import { useState, useMemo, useRef, useEffect, useLayoutEffect, useContext, createContext, Fragment } from "react";
import { apiJSON, setUnauthorizedHandler } from "./lib/api.js";
import { useI18n } from "./lib/i18n.js";
import { isManager, usePermission, refreshRolePermissions } from "./lib/permissions.js";
import { decodeBarcodePhoto, cleanCode as cleanBarcodeCode } from "./lib/barcodeDecode.js";
import { compressImageFile, compressImageFiles } from "./lib/imageCompress.js";
import { fmtMinutes as azlFmtMinutes, fmtHoursDe as azlFmtHoursDe } from "./lib/arbeitszeitlisteSvg.js";
import { createSignaturePad } from "./lib/signaturePad.js";
import { initPushIfAlreadyGranted, enablePush, disablePush, fetchPushStatus, sendTestPush as sendRealTestPush } from "./lib/push.js";
import { startRegistration, startAuthentication, browserSupportsWebAuthn, platformAuthenticatorIsAvailable } from "@simplewebauthn/browser";
// jspdf/html2canvas/@mmote/niimbluelib chargés en dynamique (import() au point d'usage,
// jamais en haut de fichier) — retour utilisateur explicite (2026-09-13/14, "L'app met
// pratiquement 10 secondes avant de s'ouvrir", confirmé "à chaque ouverture" donc pas un
// simple artefact de déploiement) : ces 3 bibliothèques + leurs dépendances transitives
// pesaient à elles seules ~1,7 Mo (sur ~4,9 Mo total avant minification, mesuré via le
// metafile esbuild) alors qu'elles ne servent QUE à l'export PDF et à l'impression Niimbot —
// jamais au chargement initial de l'app. jsbarcode reste en import statique : son seul
// usage (buildEanBarcodeSvg) est appelé depuis buildLabelBodyHtml, une fonction SYNCHRONE
// consommée aussi par l'aperçu live (dangerouslySetInnerHTML pendant la frappe) — la rendre
// async casserait cet aperçu pour un gain minime (126,8 Ko) par rapport au risque.
// Impression Niimbot via Bluetooth (chantier 2026-08-28, étape 1 — écran de test isolé,
// pas encore branché sur les vrais templates d'étiquettes). Bibliothèque communautaire
// reverse-engineered du protocole Niimbot (MIT, aucune app Niimbot nécessaire) — voir
// C:\Users\enoch\.claude\plans\delightful-soaring-shell.md pour le contexte complet.
// Version EXACTE épinglée (package.json) — le README de la lib le demande explicitement,
// projet encore en état alpha, l'API peut changer entre versions.
// Génération de vrai code-barres scannable (jamais juste les chiffres en texte) — MIT,
// mature (v3.12+, pas alpha comme niimbluelib). Aucune génération de code-barres n'existait
// dans l'app avant ce chantier (seul le DÉCODAGE existait, voir lib/barcodeDecode.js).
import JsBarcode from "jsbarcode";

// Export PDF réel — jamais window.print() : la boîte de dialogue système iOS (AirPrint/
// Aperçu du partage) ignore les dimensions @page personnalisées et impose ses propres
// marges par défaut, quelle que soit la CSS (!important compris, vérifié en conditions
// réelles, retour utilisateur explicite 2026-08-26 sur le module Étiquettes : "cela ne
// couvre pas du tout la feuille... toujours une deuxième page"). On génère donc un fichier
// PDF nous-mêmes (rendu hors-écran → html2canvas → image haute résolution → jsPDF, page aux
// dimensions EXACTES demandées, marge 0 — jamais soumis au moteur d'impression du système)
// puis on le partage via l'API Web Share (fichier réel, pas juste un lien) avec repli sur un
// téléchargement classique si le partage de fichier n'est pas supporté. Fonction générique
// réutilisée par tout module qui a besoin d'un document imprimable exact (Étiquettes,
// Warenaustausch) — un seul endroit à corriger si le comportement doit encore changer.
// heightMm est optionnel : omis (documents Warenaustausch — nombre de lignes d'articles
// variable), la hauteur de page se déduit du ratio réel du canvas capturé à widthMm fixe —
// jamais une hauteur fixe qui écraserait/étirerait le contenu si la liste déborde d'une A4.
// Fourni explicitement (étiquettes) quand le format physique est contraint et non négociable.
// Extrait d'exportHtmlToPdf (chantier impression Niimbot, 2026-08-28) — montage hors-écran +
// rasterisation, sans rien de spécifique au PDF. Réutilisé tel quel par exportHtmlToPdf
// ci-dessous ET par le futur chemin d'impression Bluetooth (printLabelViaBluetooth) : le
// canvas produit ici est exactement la matière première dont une imprimante thermique a
// besoin, jamais la peine de regénérer le rendu deux fois.
// Rastérise UN élément déjà monté dans le DOM (img déjà chargées, fonts déjà prêtes —
// responsabilité de l'appelant) avec le même filet de sécurité pour les deux : image pas
// encore chargée (voir urlToDataUri) et canvas trop grand pour WebKit (voir MAX_CANVAS_
// DIMENSION plus bas). Extrait de renderHtmlToCanvas le 2026-08-29 pour être réutilisable
// PAR PAGE dans exportHtmlToMultiPagePdf (voir ce commentaire pour le pourquoi).
async function rasterizeElement(target, scale = 4) {
  await Promise.all(Array.from(target.querySelectorAll("img")).map((img) => {
    if (img.complete) return Promise.resolve();
    return new Promise((resolve) => {
      img.addEventListener("load", resolve, { once: true });
      img.addEventListener("error", resolve, { once: true });
    });
  }));
  // Plafond de sécurité (retour utilisateur réel, 2026-08-28 : export Bewertungsbogen/
  // Lieferschein Test échouant avec "The index is not in the allowed range" — formulation
  // WebKit/JavaScriptCore, jamais reproduite en test Chromium) : Safari/WKWebView refuse
  // un canvas dont une dimension dépasse ~4096px sur certains appareils/versions iOS
  // (limite non documentée officiellement mais largement connue). On mesure la taille RÉELLE
  // avant rasterisation et on réduit le scale si besoin — jamais l'inverse (agrandir).
  const MAX_CANVAS_DIMENSION = 4096;
  const naturalWidth = target.scrollWidth || target.offsetWidth || 1;
  const naturalHeight = target.scrollHeight || target.offsetHeight || 1;
  const safeScale = Math.min(scale, MAX_CANVAS_DIMENSION / naturalWidth, MAX_CANVAS_DIMENSION / naturalHeight);
  const attempts = [...new Set([safeScale, 2, 1, 0.6])].filter((s) => s > 0 && s <= scale);
  const { default: html2canvas } = await import("html2canvas");
  let lastErr;
  for (const attemptScale of attempts) {
    try {
      return await html2canvas(target, { scale: attemptScale });
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr;
}

async function renderHtmlToCanvas({ bodyHtml, css, targetSelector, scale = 4 }) {
  const host = document.createElement("div");
  // Régression trouvée le 2026-08-29 : un essai d'opacity:0 ici a cassé TOUS les exports
  // (GR et compte normal confondus, retour utilisateur immédiat) — html2canvas reproduit
  // fidèlement l'opacité CSS de la hiérarchie parente, donc un hôte à opacity:0 produit un
  // canvas transparent/vide. Retour à left:-99999px (position hors-écran, opacité pleine),
  // seule approche confirmée fonctionnelle en production.
  host.style.cssText = "position:fixed;left:-99999px;top:0;pointer-events:none;";
  const styleEl = document.createElement("style");
  styleEl.textContent = css;
  host.appendChild(styleEl);
  const mount = document.createElement("div");
  mount.innerHTML = bodyHtml;
  host.appendChild(mount);
  document.body.appendChild(host);
  try {
    if (document.fonts && document.fonts.ready) await document.fonts.ready;
    const target = (targetSelector && mount.querySelector(targetSelector)) || mount;
    return await rasterizeElement(target, scale);
  } finally {
    document.body.removeChild(host);
  }
}

// Contournement (2026-08-29) — le PLU Quiz (seul export d'un formulaire signé qui n'a
// jamais échoué) intègre sa signature en data: URL ; Lieferschein Test/Testkauf/Warenaustausch/
// Rapport de filiale référencent une PHOTO déjà uploadée (<img src="/uploads/…"> ou
// "/api/…/signature">) que le navigateur doit charger par le réseau pendant la rasterisation
// hors-écran — l'attente explicite du chargement (`img.complete`) n'a pas suffi à éliminer
// "The index is not in the allowed range" en test réel. Plutôt que de continuer à deviner la
// cause exacte côté html2canvas, on supprime le chargement réseau du problème : l'image est
// téléchargée et convertie en data: URI AVANT de construire le HTML imprimé, donc html2canvas
// ne voit plus jamais qu'une image déjà en mémoire — exactement le cas qui marche pour PLU.
async function urlToDataUri(url) {
  if (!url || url.startsWith("data:")) return url;
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) return url;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return url; // échec réseau isolé : on garde l'URL d'origine plutôt que de bloquer tout l'export
  }
}

// Bug réel trouvé en lisant le code source de @mmote/niimbluelib (image_encoder.js,
// CanvasImageSource.isPixelNonWhite) — chantier Niimbot, 2026-08-28. La lib convertit
// chaque pixel du canvas en noir/blanc pour l'imprimante thermique avec une règle brutale :
// "pas blanc pur (255,255,255) → point noir plein", SANS aucun vrai seuil. Or le texte
// anti-crénelé (lissé) d'un canvas normal a des pixels de bord gris clair (dégradé doux
// noir→blanc) — TOUS ces gris deviennent noir plein avec cette règle, ce qui épaissit
// fortement le rendu imprimé par rapport à l'aperçu écran (retour utilisateur réel : "sur
// l'écran ce n'est pas gras mais à l'impression c'est gras" — exactement ce mécanisme).
// Fix : binariser nous-mêmes le canvas avec un VRAI seuil de luminance AVANT de le passer à
// ImageEncoder.encodeCanvas, pour qu'un pixel de bord gris clair (anti-crénelage) devienne
// franchement blanc au lieu de noir. threshold plus bas que le milieu (128) exprès : on
// préfère perdre un peu de gris clair (contour plus net, plus fin) plutôt que fabriquer un
// texte plus épais qu'à l'écran.
function thresholdCanvasForThermalPrint(canvas, threshold = 110) {
  const ctx = canvas.getContext("2d");
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const luminance = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    const v = luminance > threshold ? 255 : 0;
    data[i] = v; data[i + 1] = v; data[i + 2] = v;
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

// Partagé par exportHtmlToPdf et exportHtmlToMultiPagePdf — le blob PDF fini n'a jamais
// besoin de savoir comment il a été construit (une page ou plusieurs).
//
// Bug réel signalé (2026-10-14, capture à l'appui) : sur PC, Chrome/Windows répond VRAI à
// navigator.canShare({files}) (partage de fichier natif disponible via le "Freigeben" de
// Windows) — déclenchait donc le panneau de partage OS (Teams/Outlook/WhatsApp…) au lieu de
// simplement ouvrir/télécharger le PDF, sans aucune option évidente pour juste l'imprimer ou
// l'enregistrer. navigator.share() n'a de sens que sur mobile (partager vers une appli,
// AirDrop…). Même seuil que isWide/WIDE_BREAKPOINT (900px, "PC, tablette paysage" — voir plus
// bas) : pas de nouvelle notion, juste réutilisé ici où isWide (state React) n'est pas
// accessible (fonction hors composant).
//
// Deuxième retour utilisateur le même jour, après ce premier fix : le téléchargement direct
// marche mais "ne propose aucune possibilité d'imprimer directement dans l'app". Sur desktop,
// en plus du téléchargement (gardé — utile pour classer/renommer le fichier), on ouvre
// maintenant AUSSI le PDF dans un nouvel onglet : la visionneuse PDF intégrée de Chrome/Edge
// a sa propre icône Imprimer (Ctrl+P marche aussi), ce qui couvre "imprimer directement" sans
// réinventer un flux d'impression custom. Pas de révocation à 5s ici (contrairement au
// téléchargement) : l'onglet garde besoin de l'URL tant qu'il reste ouvert.
async function shareOrDownloadBlob(blob, filename) {
  const file = new File([blob], filename, { type: "application/pdf" });
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= WIDE_BREAKPOINT;
  if (!isDesktop && navigator.canShare && navigator.canShare({ files: [file] })) {
    await navigator.share({ files: [file], title: filename });
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
  if (isDesktop) {
    // Nouvel onglet distinct de l'URL de téléchargement (même blob, URL séparée) : la
    // révoquer après le téléchargement ne doit pas couper l'onglet encore ouvert.
    const viewUrl = URL.createObjectURL(blob);
    window.open(viewUrl, "_blank"); // popup bloqué → tant pis, le téléchargement a déjà eu lieu
  }
}

async function exportHtmlToPdf({ bodyHtml, css, targetSelector, widthMm, heightMm, filename }) {
  const canvas = await renderHtmlToCanvas({ bodyHtml, css, targetSelector, scale: 4 });
  const imgData = canvas.toDataURL("image/jpeg", 0.97);
  const pageHeightMm = heightMm || (widthMm * canvas.height / canvas.width);
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ unit: "mm", format: [widthMm, pageHeightMm], orientation: widthMm > pageHeightMm ? "landscape" : "portrait" });
  pdf.addImage(imgData, "JPEG", 0, 0, widthMm, pageHeightMm, undefined, "FAST");
  await shareOrDownloadBlob(pdf.output("blob"), filename);
}

// Étiquettes physiques hors-A4 (50×50, 150×50, 150×65…) — un PDF à la taille EXACTE du format
// (exportHtmlToPdf ci-dessus) produit un fichier dimensionnellement correct, mais la plupart
// des imprimantes/AirPrint n'ont pas ce papier chargé et réduisent automatiquement ("ajuster
// à la page") pour tenir sur le papier réellement chargé (A4) — la taille physique imprimée
// ne correspond alors plus du tout au format demandé (retour utilisateur explicite,
// 2026-09-11 : "seul le format A4 est bien géré... les autres formats sont imprimés avec des
// tailles qui ne correspondent pas du tout à leur dimension"). Fix : la PAGE du PDF est
// toujours A4 (correspond au papier réellement chargé, aucun ajustement automatique
// possible), et l'étiquette est dessinée dedans à sa vraie taille physique, en haut à gauche
// avec une marge — à découper après impression. Jamais utilisé pour aktionA4 (voir
// printLabel) : ce format EST déjà A4 pleine page, y ajouter une marge rognerait/décalerait
// l'affiche existante pour rien.
async function exportLabelToPdf({ bodyHtml, css, targetSelector, widthMm, heightMm, filename }) {
  const canvas = await renderHtmlToCanvas({ bodyHtml, css, targetSelector, scale: 4 });
  const imgData = canvas.toDataURL("image/jpeg", 0.97);
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const marginMm = 10;
  pdf.addImage(imgData, "JPEG", marginMm, marginMm, widthMm, heightMm, undefined, "FAST");
  // Pointillés de découpe (retour utilisateur explicite, 2026-09-12 : "pour que sur papier A4
  // on puisse le découper facilement") — exactement les bords de l'étiquette à sa vraie
  // taille, jamais un cadre décoratif décalé : c'est la limite précise à suivre au ciseau.
  pdf.setDrawColor(150);
  pdf.setLineWidth(0.15);
  pdf.setLineDashPattern([1.5, 1.2], 0);
  pdf.rect(marginMm, marginMm, widthMm, heightMm, "S");
  await shareOrDownloadBlob(pdf.output("blob"), filename);
}

// 2026-08-29 — trouvé en isolant tout le pipeline dans un test hors-app (Playwright, sans
// dépendre de l'appareil) : un export basé sur un long tableau (MHD Kühlung, Réductions)
// produisait UNE SEULE page géante à la hauteur exacte du contenu — confirmé par un vrai
// export à 150 lignes : page de 210 × 1303mm (plus d'1 mètre de haut), mathématiquement
// correcte (le MediaBox du PDF généré correspondait exactement aux dimensions calculées,
// aucune image mal centrée) mais totalement inexploitable — l'aperçu PDF d'iOS réduit une
// page hors-format pour la faire tenir à l'écran, rendant le tableau minuscule (exactement
// le symptôme remonté). Cause racine : ces exports rendaient TOUT le tableau, quelle que
// soit sa longueur, dans un seul <div> — jamais paginé en feuilles A4 normales.
// Fix : le HTML imprimé doit maintenant découper son contenu en plusieurs <div class="…">
// (un par page, voir buildMhdExportPrintHtml/buildReductionsPrintHtml), et cette fonction
// rastérise CHAQUE page séparément (jamais un seul canvas géant puis découpé en pixels —
// perdrait en résolution sur un tableau très long à cause du plafond WebKit) pour produire
// un vrai PDF multi-pages A4, chaque page à pleine qualité indépendamment du nombre total
// de lignes.
async function exportHtmlToMultiPagePdf({ bodyHtml, css, pageSelector, widthMm, filename }) {
  const host = document.createElement("div");
  host.style.cssText = "position:fixed;left:-99999px;top:0;pointer-events:none;";
  const styleEl = document.createElement("style");
  styleEl.textContent = css;
  host.appendChild(styleEl);
  const mount = document.createElement("div");
  mount.innerHTML = bodyHtml;
  host.appendChild(mount);
  document.body.appendChild(host);
  try {
    if (document.fonts && document.fonts.ready) await document.fonts.ready;
    const pages = Array.from(mount.querySelectorAll(pageSelector));
    if (pages.length === 0) return;
    const { jsPDF } = await import("jspdf");
    let pdf;
    for (let i = 0; i < pages.length; i++) {
      const canvas = await rasterizeElement(pages[i], 4);
      const pageHeightMm = widthMm * canvas.height / canvas.width;
      const imgData = canvas.toDataURL("image/jpeg", 0.97);
      if (i === 0) {
        pdf = new jsPDF({ unit: "mm", format: [widthMm, pageHeightMm], orientation: "portrait" });
      } else {
        pdf.addPage([widthMm, pageHeightMm], "portrait");
      }
      pdf.addImage(imgData, "JPEG", 0, 0, widthMm, pageHeightMm, undefined, "FAST");
    }
    await shareOrDownloadBlob(pdf.output("blob"), filename);
  } finally {
    document.body.removeChild(host);
  }
}

/* ============================================================
   NEXORA — PHASE 2 · V6 : LOGIN + ACCUEIL + MODULE CONTRÔLE
   Nouveautés V6 :
   - Écran de connexion complet (démarrage, formulaire Pro-ID +
     mot de passe + filiale, choix de thème et de langue) fidèle
     au flux de login.html — inscription / mot de passe oublié
     présents en boutons (simulés)
   - Accueil : salutation selon l'heure, date du jour, pilule
     filiale, avatar + menu déconnexion, carte Action Live
     repliable, grille des modules de l'app
   - Le module Contrôle V5 complet reste branché derrière
   Démo : Pro-ID libre + mot de passe "nexora"
   ============================================================ */

const THEMES = {
  creme: {
    name: "Crème", icon: "🌾",
    bg: "#f4ecdd", card: "#fffdf8", ink: "#1c1913", muted: "#93876f",
    line: "rgba(33,29,22,.08)", soft: "rgba(33,29,22,.05)",
    tabBg: "rgba(255,253,248,.82)", tabBorder: "rgba(255,255,255,.85)",
    shadow: "0 14px 36px rgba(30,42,70,.10)",
    deepShadow: "0 22px 50px rgba(30,42,70,.18)",
  },
  blanc: {
    name: "Clair", icon: "☀️",
    bg: "#ffffff", card: "#f5f6f8", ink: "#15181f", muted: "#7d8497",
    line: "rgba(21,24,31,.08)", soft: "rgba(21,24,31,.045)",
    tabBg: "rgba(255,255,255,.85)", tabBorder: "rgba(21,24,31,.06)",
    shadow: "0 14px 36px rgba(21,24,31,.08)",
    deepShadow: "0 22px 50px rgba(21,24,31,.16)",
  },
  sombre: {
    name: "Sombre", icon: "🌑",
    bg: "#0a0a0b", card: "#161618", ink: "#f5f6fa", muted: "#8f96a8",
    line: "rgba(255,255,255,.09)", soft: "rgba(255,255,255,.06)",
    tabBg: "rgba(24,24,27,.88)", tabBorder: "rgba(255,255,255,.1)",
    shadow: "0 14px 36px rgba(0,0,0,.5)",
    deepShadow: "0 22px 50px rgba(0,0,0,.65)",
  },
  nuit: {
    name: "Nuit", icon: "🌙",
    bg: "#000000", card: "#0c0c0e", ink: "#ffffff", muted: "#7a8099",
    line: "rgba(255,255,255,.07)", soft: "rgba(255,255,255,.045)",
    tabBg: "rgba(8,8,10,.9)", tabBorder: "rgba(255,255,255,.08)",
    shadow: "0 14px 36px rgba(0,0,0,.65)",
    deepShadow: "0 22px 50px rgba(0,0,0,.8)",
  },
  // 2 thèmes d'essai (2026-08-17, demande utilisatrice "que je vois le rendu dans
  // l'app") — gris-bleuté froid clair (Givre), charbon chaud/ambre (Braise). "Noir"
  // (3e thème, façon Revolut) retiré à la demande utilisatrice. Voir artefact de
  // comparaison https://claude.ai/code/artifact/b134d27e-9e72-4ed6-a9f5-29d3ac2c0a11
  givre: {
    // Retour utilisateur 2026-08-17 : version précédente (#eef2f5) trop proche de
    // "Clair" (#ffffff), thème pas assez marqué — fond bleu-glacé nettement plus
    // saturé pour se distinguer au premier coup d'œil, cartes blanches pour garder
    // le contraste/la profondeur "givre sur vitre".
    name: "Givre", icon: "❄️",
    bg: "#dfe9f0", card: "#ffffff", ink: "#0d1b26", muted: "#4f6672",
    line: "rgba(13,27,38,.1)", soft: "rgba(13,27,38,.055)",
    tabBg: "rgba(255,255,255,.85)", tabBorder: "rgba(13,27,38,.08)",
    shadow: "0 14px 36px rgba(13,27,38,.1)",
    deepShadow: "0 22px 50px rgba(13,27,38,.18)",
  },
  braise: {
    name: "Braise", icon: "🔥",
    bg: "#181310", card: "#231c17", ink: "#f7ede4", muted: "#b09484",
    line: "rgba(255,208,170,.12)", soft: "rgba(255,208,170,.07)",
    tabBg: "rgba(35,28,23,.88)", tabBorder: "rgba(255,208,170,.12)",
    shadow: "0 14px 36px rgba(0,0,0,.5)",
    deepShadow: "0 22px 50px rgba(0,0,0,.65)",
  },
};
const THEME_ORDER = ["creme", "blanc", "sombre", "nuit", "givre", "braise"];

/* Ossature desktop/tablette (Phase 1) — un seul point de bascule global. En dessous de
   WIDE_BREAKPOINT, comportement téléphone strictement inchangé (y compris tablette
   portrait, ex. iPad 768-820px, encore trop étroite pour une nav latérale confortable).
   À partir de WIDE_BREAKPOINT (PC, tablette paysage) : nav latérale, Sheet centrées,
   shell élargi — voir isWide/WideContext plus bas et leurs points d'usage. */
const WIDE_BREAKPOINT = 900;
const SIDEBAR_WIDTH = 220;
const WideContext = createContext(false);

/* ============================================================
   ICÔNES — retour aux emoji d'origine (préférés à la bibliothèque
   d'icônes au trait, jugée trop simple/plate). EmojiIcon reste en
   place comme point d'entrée unique pour ne pas retoucher tous
   les appels déjà câblés dans l'app — il affiche juste l'emoji.
   ============================================================ */
const EmojiIcon = ({ e, size = 22 }) => <span style={{ fontSize: `calc(${(size)}px * var(--nx-font-scale, 1))`, lineHeight: 1 }}>{e}</span>;

// Icône "Contrôle" (nav bas + nav latérale desktop) — retour utilisateur explicite
// (2026-08-19, capture à l'appui) : le 🛡️ natif rend rouge/gris sur iOS (couleur imposée
// par la police emoji du système, non stylable), alors que l'onglet est bleu partout
// ailleurs (A.frost — bulle de sélection, libellé actif). Remplacé UNIQUEMENT pour cet
// onglet par un SVG plein (pas un jeu d'icônes au trait générique — ce choix avait déjà
// été tenté app-wide et rejeté comme "trop simple/plate", voir commentaire ICÔNES
// ci-dessus) : même silhouette de bouclier, coloré en bleu, avec une bande plus claire
// pour garder un peu de relief plutôt qu'un aplat totalement plat.
const ShieldIcon = ({ size = 19, color = A.frost }) => (
  <svg viewBox="0 0 32 32" style={{ display: "block", width: `calc(${size}px * var(--nx-font-scale, 1))`, height: `calc(${size}px * var(--nx-font-scale, 1))` }}>
    <path d="M16 2.5 27 6.5v8.2c0 8-4.6 13.4-11 14.8C9.6 28.1 5 22.7 5 14.7V6.5L16 2.5z" fill={color} />
    <path d="M16 2.5v27C9.6 28.1 5 22.7 5 14.7V6.5L16 2.5z" fill="#fff" opacity=".16" />
  </svg>
);

const A = {
  blue: "#364fe7", frost: "#2d7ff0", purple: "#8b2fd6", orange: "#e78a12",
  green: "#1eb85f", red: "#ef4444", brown: "#a16207", navy: "#0d1730", teal: "#0d9488",
  amber: "#d97706", yellow: "#ca8a04",
};

// Fenêtre des graphiques d'évolution de Pilotage réseau > Analyse (Umsatz, Stundenleistung,
// Pertes) — demande explicite du Verkaufsleiter (2026-08-14) de voir une vraie tendance,
// pas juste 6 semaines. Constante partagée par loadPilotHistory (fetch) et le rendu des 3
// graphes (xFor/hover), jamais un "6"/"12" en dur séparé des deux côtés.
const PILOT_HISTORY_WEEKS = 12;
// Cap par défaut des classements pleine liste (Analyse > Filiales) — voir
// pilotWorkRowsExpanded/pilotUmsatzRowsExpanded. Abaissé de 20 à 10 (retour utilisateur
// 2026-08-23 : "le classement Umsatz aussi est très long" même après le premier cap — les
// cartes Umsatz sont plus hautes que les barres Stundenleistung, 20 cartes défilait encore
// beaucoup sur mobile).
const PILOT_RANKING_CAP = 10;
// Ancien seuil d'alerte fixe — remplacé le 2026-08-15 (demande utilisatrice "on se base
// toujours sur le plan Stundenleistung hebdomadaire et mensuel") par une vraie cible dérivée
// du Plan Tag réel (voir stundenleistungPlan/stundPlanSumsPerWeek). Ne sert plus QUE de
// repli d'échelle pour l'axe Y des 2 graphes Stundenleistung quand AUCUNE semaine du
// périmètre n'a de plan d'heures connu (évite un axe vide/dégénéré) — jamais comme repère
// affiché ou comparé à une vraie valeur.
const STUNDENLEISTUNG_ALERT_THRESHOLD = 180;

// "Épuration visuelle premium colorée" — dégradé à 3 tons + reflet façon verre + ombre
// profonde à partir d'une seule couleur de module, plutôt que la teinte plate d'origine.
// Validé sur la maquette https://claude.ai/code/artifact/f99d2c39-1bde-44d4-9329-0db172e19b4c
// (direction explicitement choisie : garder les couleurs vives, jamais les désaturer).
function nxHexToRgb(hex) {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function nxMixHex(hex, targetHex, amount) {
  const c = nxHexToRgb(hex);
  const t = nxHexToRgb(targetHex);
  const r = Math.round(c.r + (t.r - c.r) * amount);
  const g = Math.round(c.g + (t.g - c.g) * amount);
  const b = Math.round(c.b + (t.b - c.b) * amount);
  return `rgb(${r}, ${g}, ${b})`;
}
// Retour utilisateur 2026-08-17 : "un vrai flottement" puis, après un premier essai
// calqué sur la maquette validée (mix blanc 18%/42%), "je trouve les couleurs fades"
// — capture d'écran réelle en production : le mix blanc à 42% pâlissait quasi la
// moitié haute de chaque tuile, l'œil retient cette zone pâle plutôt que la couleur
// pleine. Un seul ton clair, bien plus léger (10%), atteint la couleur pure dès 60% —
// l'essentiel de la tuile reste saturé au maximum, le reflet reste discret (juste le
// haut), l'ombre colorée est montée en opacité pour compenser en profondeur/pop.
function premiumTileStyle(hex) {
  const c1 = nxMixHex(hex, "#ffffff", 0.1);
  return {
    background: `linear-gradient(160deg, ${c1} 0%, ${hex} 60%)`,
    boxShadow: `inset 0 1.5px 0 rgba(255,255,255,.6), inset 0 -10px 16px rgba(0,0,0,.16), 0 4px 10px rgba(0,0,0,.12), 0 14px 26px ${hex}80`,
    textShadow: "0 2px 4px rgba(0,0,0,.18)",
  };
}

// Commande Kühlung (module Merchandise/"Bestellung") — moteur de recommandation basé sur un
// Faktor paramétrable par commande (jamais codé en dur, voir écran merchScreen==="faktor").
// Bedarf = Abverkauf moyen × Faktor, en pièces (Stück) ; besoin restant = Bedarf - IST-Bestand,
// converti en unité de commande via la VE (arrondi au Karton supérieur). `abverkauf` n'existe
// dans AUCUNE donnée réelle aujourd'hui (aucun historique de vente dans l'app) — cette fonction
// retourne systématiquement null tant que ce champ n'est pas fourni : jamais de recommandation
// inventée, l'écran article affiche alors "—" et la saisie manuelle reste seule maîtresse.
function computeKuhlungRecommendation({ abverkauf, faktor, istBestand, ve }) {
  const abvRaw = Number(abverkauf);
  const fak = Number(faktor);
  const veNum = Number(ve);
  if (!Number.isFinite(abvRaw) || !Number.isFinite(fak) || !veNum) return null;
  // Retour utilisateur explicite (2026-08-25) — l'Abverkauf peut arriver décimal (moyenne
  // glissante) : arrondi standard (≥ ,5 vers l'entier supérieur, en-dessous vers
  // l'inférieur) AVANT le calcul du Bedarf, jamais un Abverkauf fractionnaire dans la
  // formule. Distinct de l'arrondi du Karton final ci-dessous, qui reste volontairement
  // TOUJOURS au carton supérieur (Math.ceil, jamais cette même règle d'arrondi standard).
  const abv = Math.round(abvRaw);
  const bedarf = abv * fak;
  const besoinRestant = Math.max(0, bedarf - (Number(istBestand) || 0));
  return { bedarf, recommendedQty: Math.ceil(besoinRestant / veNum) };
}

// Pilotage réseau · Analyse — graphique "rythme Umsatz" (courbe double semaine/mois),
// mockup validé par l'owner (Style B, https://claude.ai/code/artifact/beab8649-2855-48ea-
// b185-14e8035fcdef). Deux couleurs FIXES (jamais l'accent de marque qui varie par thème) —
// teal = courbe semaine, rose/magenta = courbe mois — pour que "quelle courbe est laquelle"
// ne dépende jamais du thème actif. Valeurs copiées telles quelles du CSS du mockup
// (--accent/--accent2 par thème) : la TEINTE reste fixe (même famille) mais la CLARTÉ varie
// entre thèmes clairs (creme/blanc) et sombres (sombre/nuit), sinon illisible sur fond
// quasi noir — mêmes deux familles que le mockup, jamais un 3e/4e réglage inventé.
const PILOT_RHYTHM_ACCENT = {
  creme: { week: { c: "#0d8a7a", soft: "#dcefe9", ink: "#0a6259" }, month: { c: "#c23a72", soft: "#f5e0e9", ink: "#8f2a54" } },
  blanc: { week: { c: "#0d9488", soft: "#dcf4f1", ink: "#0a6b62" }, month: { c: "#db2777", soft: "#fce4ef", ink: "#9d1857" } },
  sombre: { week: { c: "#2dd4bf", soft: "#0f2e2b", ink: "#7de8da" }, month: { c: "#f472b6", soft: "#3a1830", ink: "#fbb6d9" } },
  nuit: { week: { c: "#2dd4bf", soft: "#0a2624", ink: "#7de8da" }, month: { c: "#f472b6", soft: "#2e1226", ink: "#fbb6d9" } },
};

// Formatage manuel (jamais toISOString()) — piège réel trouvé le 2026-07-30 : construire un
// Date depuis "YYYY-MM-DDT00:00:00" (sans "Z") le parse en heure LOCALE, donc en CEST/CET
// (UTC+1/+2, le fuseau réel des utilisateurs) minuit local tombe la veille en UTC —
// .toISOString().slice(0,10) renvoyait alors systématiquement la date d'HIER (ex.
// addDaysISO("2026-07-30", -3) rendait "2026-07-26" au lieu de "2026-07-27", cassant le
// calcul du lundi de la semaine courante dans tout Planning — jamais de round-trip par UTC.
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const addDaysISO = (dateStr, n) => {
  const [y, m, day] = dateStr.split("-").map(Number);
  const d = new Date(y, m - 1, day);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
// Accepte une date seule ("YYYY-MM-DD", l'usage d'origine) ET un ISO datetime complet
// ("YYYY-MM-DDTHH:mm:ss.sssZ", ex. Réclamations createdAt/sentAt) — retour utilisateur
// explicite (2026-08-19, capture à l'appui) : sans le split sur "T", un datetime complet
// donnait "19T19:10:12.058Z.08.2026" au lieu de "19.08.2026". Ne touche jamais aux appels
// déjà corrects (une date seule ne contient jamais "T").
const fmtFR = (iso) => { if (!iso) return "--"; const [y, m, d] = iso.split("T")[0].split("-"); return `${d}.${m}.${y}`; };
const fmtDay = (iso) => {
  if (!iso) return "";
  try { return new Date(iso + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "short" }); } catch { return ""; }
};
const daysLeft = (iso) => Math.ceil((new Date(iso) - new Date(todayISO())) / 86400000);
const urgency = (iso) => { const d = daysLeft(iso); return d <= 2 ? "red" : d <= 5 ? "orange" : "green"; };

// Calendrier Missions réseau (2026-08-31) — semaine ISO (norme allemande/européenne, celle
// utilisée par le vrai Rundschreiben — "KW 34") et grille du mois en ISO strings (jamais des
// objets Date qui traînent, même convention que addDaysISO ci-dessus — évite tout piège de
// fuseau horaire déjà rencontré dans ce projet).
function isoWeekNumber(dateISO) {
  const [y, m, d] = dateISO.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  const dayNum = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const diff = (date - firstThursday) / 86400000;
  return 1 + Math.round(diff / 7);
}
// Lundi→Dimanche, toujours un multiple de 7 jours (5 ou 6 rangées selon le mois) — month
// 1-indexé pour rester lisible avec le reste du fichier (getMonth()+1 partout ailleurs).
function buildMonthGridISO(year, month) {
  const first = new Date(year, month - 1, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month, 0).getDate();
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;
  const startISO = `${year}-${String(month).padStart(2, "0")}-01`;
  const cells = [];
  for (let i = 0; i < totalCells; i++) cells.push(addDaysISO(startISO, i - startOffset));
  return cells;
}
const urgLabel = { red: "Urgent", orange: "Bientôt", green: "OK" };
const urgColor = { red: A.red, orange: A.orange, green: A.green };
const FONT = '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
// Réglages d'affichage (2026-08-17, demande utilisatrice) — taille de texte + police +
// gras, appliqués à toute l'app via des CSS custom properties postées sur <html> (voir
// applyDisplaySettings plus bas), jamais via transform:scale() sur la racine (footgun déjà
// vécu sur cette app, casse position:sticky — voir header/bottomnav shy). Tout fontSize
// codé en dur dans l'app est déjà passé par un codemod en `calc(Npx * var(--nx-font-scale,
// 1))`, donc ce mécanisme s'applique uniformément partout (texte ET icônes emoji, qui
// utilisent le même fontSize — voir EmojiIcon) sans avoir à toucher chaque site d'appel.
const FONT_SCALES = {
  petit: { label: "Petit", value: 0.9 },
  normal: { label: "Normal", value: 1 },
  grand: { label: "Grand", value: 1.15 },
  tresGrand: { label: "Très grand", value: 1.3 },
};
const FONT_SCALE_ORDER = ["petit", "normal", "grand", "tresGrand"];
const FONT_CHOICES = {
  classique: { label: "Classique", stack: FONT },
  systeme: { label: "Système", stack: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif' },
  moderne: { label: "Moderne", stack: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif' },
};
const FONT_CHOICE_ORDER = ["classique", "systeme", "moderne"];
// Écran de verrouillage par inactivité — décidé après un retour utilisateur réel : une session
// serveur ne se termine quasiment jamais (cookie 30 jours + rolling, jamais revérifiée par ce
// client qui restaure depuis localStorage, voir le commentaire "--- Auth ---" plus haut), donc
// un téléphone resterait connecté indéfiniment. lastActivityAt persiste en localStorage (pas
// juste un timer JS) car un setTimeout ne survit pas à un rechargement ni ne tourne de façon
// fiable app en arrière-plan sur mobile — Date.now() - lastActivityAt reste correct même après
// fermeture/réouverture de l'app.
const IDLE_LOCK_MS = 30 * 60 * 1000; // 30 min (retour utilisateur explicite 2026-08-16, était 10 min)
const IDLE_ACTIVITY_KEY = "nexora_last_activity";
// La vraie session serveur est désormais alignée sur la même durée (SESSION_MAX_AGE_MS côté
// server.js, rolling: true) — sans un battement de coeur, une app activement utilisée mais qui
// n'aurait pas fait d'appel API "métier" depuis un moment verrait sa session s'éteindre sous
// elle malgré une vraie activité récente. Marge large sous les 30 min pour ne jamais rater la
// fenêtre même avec de la latence réseau.
const HEARTBEAT_INTERVAL_MS = 3 * 60 * 1000;
// Locale BCP-47 utilisée pour Intl.toLocale*String (date/heure) selon la langue du
// compte (fr/de/en/es/it, voir src/locales/*.json) — jamais "fr-FR" en dur ailleurs.
const INTL_LOCALE_BY_LANG = { fr: "fr-FR", de: "de-DE", en: "en-GB", es: "es-ES", it: "it-IT" };

// Seuils EXACTS de public/modules/mobile.js getRunUrgencyClass (≤0 rouge, ≤3 orange) —
// volontairement distincts de urgency() ci-dessus (≤2/≤5, seuils d'affichage écran déjà
// établis ailleurs dans nexora-app) : l'export doit rester fidèle au filtre réel du
// vanilla, jamais recalé sur l'affichage écran de cette app.
const mhdExportUrgencyClass = (iso) => { const d = daysLeft(iso); return d <= 0 ? "red" : d <= 3 ? "orange" : "green"; };
const MHD_EXPORT_FILTER_LABELS = { all: "Toutes les urgences", red: "Urgent", orange: "Bientôt", green: "OK" };
// Construit le contenu imprimable de l'export MHD (même contenu/tri que l'ancien
// printMhdExportPdf de public/modules/shared.js) mais SANS window.open/document.write :
// en PWA/WKWebView standalone iOS, window.open("","_blank") ne crée pas de vrai nouvel
// onglet — il navigue la fenêtre courante hors de l'app React, sans aucun moyen d'y
// revenir sans fermer l'app. Retourne juste le HTML interne, injecté via le même
// portail d'impression in-app que HACCP (#hpPrintRoot/#mexPrintRoot, voir plus bas) —
// jamais de navigation, window.print() s'exécute sur le document courant.
// 2026-08-29 — un seul <div class="mex-page"> avec TOUT le tableau produisait une page PDF
// géante (hauteur = celle du contenu entier, parfois plus d'1m) au lieu de vraies pages A4 —
// voir le commentaire d'exportHtmlToMultiPagePdf. On découpe maintenant en plusieurs pages
// de MHD_EXPORT_ROWS_PER_PAGE lignes, chacune répétant l'en-tête (titre/filtre/tableau) pour
// rester lisible indépendamment — même volume de données, juste imprimable normalement.
const MHD_EXPORT_ROWS_PER_PAGE = 35;
const buildMhdExportPrintHtml = (rows, labels) => {
  const { reportTitle, filterLabel, artNrLabel, articleLabel, dateLabel, formatDate, printedOnLabel, todayLabel, pageTemplate } = labels;
  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const sorted = rows.slice().sort((a, b) => new Date(a.mhd).getTime() - new Date(b.mhd).getTime());
  const chunks = [];
  for (let i = 0; i < sorted.length; i += MHD_EXPORT_ROWS_PER_PAGE) chunks.push(sorted.slice(i, i + MHD_EXPORT_ROWS_PER_PAGE));
  if (chunks.length === 0) chunks.push([]);
  return chunks.map((chunk, idx) => {
    const tableRows = chunk.map((r) => `<tr><td>${esc(r.artNr)}</td><td>${esc(r.name || "")}</td><td>${formatDate(r.mhd)}</td></tr>`).join("");
    return `<div class="mex-page">
      <h1>${esc(reportTitle)}</h1>
      <p class="mex-sub">${esc(filterLabel)} · ${esc(printedOnLabel)} ${esc(todayLabel)}${chunks.length > 1 ? ` · ${esc(pageTemplate(idx + 1, chunks.length))}` : ""}</p>
      <table class="mex-table"><thead><tr><th>${esc(artNrLabel)}</th><th>${esc(articleLabel)}</th><th>${esc(dateLabel)}</th></tr></thead><tbody>${tableRows}</tbody></table>
    </div>`;
  }).join("");
};

/* Scanner — les 4 vraies zones (scannerZones() côté serveur), toutes
   également actives (pas de "bientôt" artificiel) : seule Kühlung a des
   articles dans ce jeu de données réel, les 3 autres restent ouvrables
   mais vides tant qu'aucun catalogue réel n'existe pour elles. */
const SCANNER_ZONES = [
  { id: "kuehlung", label: "Kühlung", emoji: "❄️", bg: A.frost },
  { id: "fleisch", label: "Frisch Fleisch", emoji: "🥩", bg: A.red },
  { id: "trocken", label: "Trocken", emoji: "📦", bg: A.orange },
  { id: "brot", label: "Frisch Brot", emoji: "🍞", bg: A.brown },
];
let RID = 100;
const QUICK_DAYS = [1, 2, 3, 5, 7, 10, 14, 21, 30];
/* CA (Umsatz), Leistung et pertes de la semaine — tous réels, voir loadCaCompare/weekCA/
   monthTotal/wasteByCat dans le Dashboard (daily-entries "stundenleistung"/"manual_waste"). */
/* Planning — équipe, plan de travail, demandes, congés */
const TEAM = ["Démo", "Sarah", "Marco", "Lena"];
const EMP_COLORS = { "Démo": "#364fe7", "Sarah": "#8b2fd6", "Marco": "#1eb85f", "Lena": "#e78a12" };
const WEEK_LABELS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
const WEEK_LABELS_SHORT = ["Lu", "Ma", "Me", "Je", "Ve", "Sa"];
// Clés de jour réelles du vrai plan serveur (SF_PLANNING_DAYS côté server.js) — l'index
// dans ce tableau EST l'ancien "day" numérique 0-5 déjà utilisé partout dans le JSX.
const PLAN_DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat"];
let SID = 500;
/* Mineurs (< 18 ans) — normalement dérivé de la date de naissance côté
   admin (isMinorEmployee), simulé ici pour démontrer la règle de pause
   spécifique du Jugendarbeitsschutzgesetz. */
const EMP_MINOR = { "Lena": true };
/* Rôles — un Azubi compte pour moitié dans les totaux d'heures (Leistung),
   règle réelle azubiNetFactor : 2h travaillées = 1h comptée. Les heures
   affichées sur le créneau restent les vraies heures ; seul l'agrégat
   (Ist semaine, sync Stundenleistung) applique le facteur ×0,5. */
const EMP_ROLES = { "Démo": "Filialleiter", "Sarah": "Kassiererin", "Marco": "Azubi", "Lena": "Aushilfe" };
const isAzubi = (emp) => EMP_ROLES[emp] === "Azubi";
/* Soll horaire préchargé depuis Stundenleistung (Reporting) — somme des
   jours Lu→Sa ayant une valeur cible. Dans le vrai app, une filiale qui n'a
   pas encore saisi ses valeurs Stundenleistung voit un Soll à 0 (ce n'est
   pas un bug) ; ici, valeurs de démo pour illustrer le mécanisme complet. */
const DEMO_STUNDENLEISTUNG_SOLL = [36, 34, 38, 36, 32, 40];
const THIS_YEAR = new Date().getFullYear();
const DEMO_SHIFTS = [
  { id: ++SID, day: 0, emp: "Sarah", start: "06:00", end: "12:30", pauseMin: 30 },
  { id: ++SID, day: 0, emp: "Démo", start: "12:00", end: "18:30", pauseMin: 30 },
  { id: ++SID, day: 1, emp: "Marco", start: "06:00", end: "12:30", pauseMin: 30 },
  { id: ++SID, day: 1, emp: "Lena", start: "12:00", end: "18:30", pauseMin: 0 },
  { id: ++SID, day: 2, emp: "Démo", start: "06:00", end: "12:30", pauseMin: 30 },
  { id: ++SID, day: 2, emp: "Sarah", start: "12:00", end: "18:30", pauseMin: 30 },
  { id: ++SID, day: 3, emp: "Lena", start: "06:00", end: "12:30", pauseMin: 30 },
  { id: ++SID, day: 3, emp: "Marco", start: "12:00", end: "18:30", pauseMin: 30 },
  { id: ++SID, day: 4, emp: "Démo", start: "06:00", end: "12:30", pauseMin: 30 },
  { id: ++SID, day: 4, emp: "Lena", start: "12:00", end: "18:30", pauseMin: 0 },
  { id: ++SID, day: 5, emp: "Sarah", start: "07:00", end: "13:00", pauseMin: 30 },
  { id: ++SID, day: 5, emp: "Marco", start: "12:30", end: "18:00", pauseMin: 30 },
];

/* Historique des publications du plan de travail — alimenté à chaque
   publication (voir publishWeek). */
const DEMO_PUBLISH_HISTORY = [];

/* ============================================================
   TÂCHES — Missions réseau (envoyées par la direction à plusieurs
   filiales, lecture + accomplir seulement) et Tâches filiale
   (to-do interne, créée sur place). Types : Standard, Cuisson
   boulangerie (quantités par article), Examen PLU (lié au module
   PLU Quiz). Priorité immédiate = échéance auto +15 min + rappel.
   ============================================================ */
let TID = 1000;

/* ============================================================
   HACCP — Filialcheckliste. Contrôles quotidiens par rayon (temp.
   ou case à cocher), auto-conforme/anomalie selon plage, remarque
   obligatoire + photo optionnelle en cas d'anomalie. Contrôles
   hebdomadaires séparés. Historique des modifications = journal.
   ============================================================ */
// Config HACCP (groupes, libellés, seuils) chargée exclusivement depuis
// GET /api/haccp/config — jamais recopiée en dur ici (voir haccpConfig state).
// Emoji/couleurs par groupe (absents de la réponse serveur, décoratifs) :
const HACCP_GROUP_STYLE = {
  kuehlung: { emoji: "❄️", color: A.frost }, tiefkuehlung: { emoji: "🧊", color: A.purple },
  frischfleisch: { emoji: "🥩", color: A.red }, obst: { emoji: "🍎", color: A.green },
  prebake: { emoji: "🥐", color: A.amber }, brot: { emoji: "🍞", color: A.brown },
  eier: { emoji: "🥚", color: A.yellow }, sauberkeit: { emoji: "🧼", color: A.teal },
  sauberkeit_hebdo: { emoji: "🧼", color: A.teal }, schaedlinge: { emoji: "🐜", color: A.navy },
};

/* ============================================================
   RÉCLAMATIONS — défaut de livraison signalé à la centrale.
   Fruits & légumes (avec liste d'articles propre aux réclamations,
   pas le catalogue Kühlung) ou Food/Non-Food (toujours saisie
   manuelle). Pièces OU kilos, jamais les deux. Historique en 3
   onglets : Ouvertes / Envoyées / Corbeille. Destination d'envoi
   (e-mail/fax/autre) modifiable à tout moment.
   ============================================================ */
// Repli local si /api/claims/settings est injoignable — le vrai serveur renvoie TOUJOURS
// des listes non vides (normalizeSettings retombe lui-même sur les vrais défauts DE), donc
// ce repli FR ne sert qu'en cas de coupure réseau, jamais en usage normal.
const CLAIM_DEFECTS_DEFAULT = {
  fresh: ["Pourri / moisi", "Écrasé", "Sous-calibre", "Trop mûr", "Emballage endommagé"],
  food: ["DLC dépassée", "Emballage percé", "Produit cassé", "Erreur de livraison", "Température non respectée"],
};
let CID = 2000;

/* ============================================================
   PLU QUIZ — Training libre (répétition espacée : les articles
   ratés reviennent plus souvent) + Examen officiel chronométré
   (seuil de réussite 80 %, aucune correction avant la fin).
   Question = donner le nom depuis le code PLU, ou l'inverse,
   à choix multiple (4 propositions).
   ============================================================ */
// Catégories/articles/liste chargés exclusivement depuis GET /api/plu-quiz/lists/active
// (voir pluActiveList state) — jamais recopiés en dur ici (l'ancien jeu de codes PLU
// factices, format US à 4 chiffres, n'avait aucun rapport avec le vrai catalogue interne
// NORMA à 2-3 chiffres réellement semé côté serveur, seedPluListsIfEmpty()).

/* ============================================================
   KRANKMELDUNG — Déclaration d'absence maladie, complémentaire
   aux appels obligatoires (ne les remplace pas). Assistant en
   5 étapes : informations, services concernés, contacts
   obligatoires, attestation médicale (AU), envoi définitif.
   ============================================================ */
/* Krankmeldung — 6 vrais statuts d'appel (KRANK_CONTACT_STATUSES) et 5 vrais statuts
   d'AU (KRANK_AU_STATUSES), fidèles à server.js/krankmeldung.js. */
const CONTACT_STATUS_LABELS = {
  pending: "Pas encore contacté", attempted: "Appel tenté", reached: "Joint par téléphone",
  message_left: "Message laissé", unreachable: "Injoignable", not_in_system: "Contact non enregistré dans le système",
};
const AU_STATUS_LABELS = {
  NOT_REQUIRED: "AU non requise", PENDING: "AU en attente", UPLOADED: "AU déposée", VERIFIED: "AU vérifiée", REJECTED: "AU refusée",
};
const KRANK_GENERAL_STATUS_LABELS = { DRAFT: "Brouillon", ACTIVE: "En cours", CLOSED: "Clôturée", CANCELLED: "Annulée" };
const KRANK_HISTORY_LABELS = {
  created: "Déclaration créée", edited: "Modifiée", submitted: "Envoyée", extended: "Prolongée",
  early_return_requested: "Retour anticipé annoncé", contact_status: "Contact mis à jour",
  au_uploaded: "AU déposée", au_accessed: "AU consultée", au_verified: "AU vérifiée",
  au_reminder_sent: "Rappel AU envoyé", corrected: "Dates corrigées", closed: "Dossier clôturé", cancelled: "Déclaration annulée",
};

/* ============================================================
   COMMANDE MATÉRIEL — NORMA, bons de commande filiale. Deux
   catalogues à destination différente (fax service technique /
   mail service administratif), quantités au stepper (jamais au
   clavier), certains articles "sur demande BL" uniquement.
   ============================================================ */
// Amorce/repli local — "bureau" n'a aucun équivalent réel côté serveur (seul "technique"
// existe, voir GET /api/material-orders/catalog) — plus de repli "bureau" fictif (retiré,
// aucune vraie donnée derrière — voir feedback utilisatrice : aucun élément d'exemple ne
// doit rester affiché sans vraie donnée réelle). Ce repli local ne sert que le temps très
// bref avant que loadMaterialCatalog() ne le remplace par la vraie réponse serveur.
const MATERIAL_CATALOGS_SEED = {
  technique: {
    id: "technique", label: "Matériel technique", destination: "Service technique NORMA",
    destinationNote: "Envoyé par fax au service technique régional.",
    groups: [
      { label: "Étiquetage", articles: [
        { id: "t1", name: "Rouleau étiquettes prix", artNr: "T-1042", ve: "10 rlx" },
        { id: "t2", name: "Cartouche imprimante étiquettes", artNr: "T-1088", ve: "2 pcs" },
      ]},
      { label: "Caisse", articles: [
        { id: "t3", name: "Rouleau papier caisse", artNr: "T-2011", ve: "20 rlx" },
        { id: "t4", name: "Scanner douchette de secours", artNr: "T-2099", ve: "1 pc", restricted: true },
      ]},
    ],
    excludedNote: "Le matériel de nettoyage n'est pas commandé ici — voir HACCP / Sauberkeit.",
  },
};

/* INFOS IMPORTANTES — canal officiel de consignes & rappels, vraies données
   (server.js important-info.*) — voir loadInfos()/openInfoDetail() etc. plus
   bas, remplace l'ancien DEMO_INFOS/IID entièrement locaux. */

/* Resturlaub / Leistungsfrei — vrai grand livre crédit/débit. Les congés
   sont attribués par année (avec report d'une année sur l'autre) ; chaque
   prise de congé approuvée crée un débit automatique. Tout est historisé
   et supprimable — rien n'atterrit tout seul dans l'app. */

/* Feiertage — remplacé par le vrai référentiel serveur (GET/POST/DELETE
   /api/public-holidays), calculé dynamiquement par Land (Pâques compris) et
   généré automatiquement une fois par (Land, année) — voir loadPublicHolidays()
   plus bas. L'ancien jeu figé sur 2026 (GERMAN_STATES/NATIONAL_HOLIDAYS_2026/
   REGIONAL_HOLIDAYS_2026/stateHolidays) divergeait du vrai référentiel
   (Weltkindertag TH inventé, Weltfrauentag BE/MV oublié, Mariä Himmelfahrt BY
   oublié) et ne fonctionnait que pour l'année 2026. */
const isoWeek = (d = new Date()) => {
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = x.getUTCDay() || 7;
  x.setUTCDate(x.getUTCDate() + 4 - day);
  const y0 = new Date(Date.UTC(x.getUTCFullYear(), 0, 1));
  return Math.ceil((((x - y0) / 86400000) + 1) / 7);
};

// Clé de dossier hebdomadaire pour l'archive Inventurmaßnahme (Lieferschein Test +
// Bewertungsbogen + PLU Quiz réunis) — "2026-W33", triable telle quelle (ordre
// alphabétique = ordre chronologique). Année civile de la date elle-même plutôt que
// l'année ISO exacte (isoWeek() ne renvoie que le numéro) — écart cosmétique possible
// uniquement à cheval fin déc./début janv., accepté pour rester simple.
const imWeekKeyFromDate = (dateStr) => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-W${String(isoWeek(d)).padStart(2, "0")}`;
};

/* ============================================================
   REPORTING — "les rapports se remplissent automatiquement".
   Modèle fidèle au vrai Abschriften KW : 9 rayons (Bereich), dont
   Prebake et Frischbrot/Brot sont automatiques (remplis depuis la
   saisie quotidienne par article du catalogue), les 7 autres sont
   manuels. CHAQUE rayon a deux lignes : PÜ/20% (démarque prix) et
   entsorgt (jeté). Journée et semaine se verrouillent une fois
   saisies, avec un bouton "Modifier" pour rouvrir.
   ============================================================ */
const REPORT_CATEGORIES = [
  { id: "obst_gemuese", label: "Obst/Gemüse", source: "manual" },
  { id: "schnittblumen", label: "Schnittblumen/Pflanzen", source: "manual" },
  { id: "frischfleisch", label: "Frischfleisch", source: "manual" },
  { id: "prebake", label: "Prebake", source: "auto_prebake" },
  { id: "frischbrot_brot", label: "Frischbrot / Brot", source: "auto_brot" },
  { id: "frischfisch", label: "Frischfisch", source: "manual" },
  { id: "kuehlung", label: "Kühlung", source: "manual" },
  { id: "h_bereich", label: "H-Bereich", source: "manual" },
  { id: "trocken", label: "Trocken", source: "manual" },
];
const MANUAL_CATEGORIES = REPORT_CATEGORIES.filter((c) => c.source === "manual");
// Identité couleur par rayon — bande verticale sur la ligne du tableau Wochenbericht
// (maquette validée par l'utilisateur, 2026-09-20 : voir storeflow_wochenbericht_table_
// redesign_mockup). Palette adaptée au contexte, pas une reprise littérale d'un autre écran.
const REPORT_CATEGORY_COLORS = {
  obst_gemuese: "#4CAF6D", schnittblumen: "#D65FA0", frischfleisch: "#A8395A",
  frischfisch: "#3E8FBF", kuehlung: "#6FB8D6", h_bereich: "#C9A227",
  trocken: "#8B6F47", frischbrot_brot: "#D9822B", prebake: "#C2571F",
};
const WASTE_TYPES = [{ id: "pu20", label: "PÜ/20%" }, { id: "entsorgt", label: "entsorgt" }];
const WEEK_DAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa"];
// Jours fériés allemands générés automatiquement (server.js SF_HOLIDAYS_NATIONAL/
// SF_REGIONAL_HOLIDAY_RULES, source:"auto") — décision utilisateur 2026-08-17 : traduire
// le nom dans chaque langue (contrairement à Kühlung/MHD, ces fêtes ont un équivalent
// direct). Les jours fériés PERSONNALISÉS créés par un utilisateur (source !== "auto")
// n'apparaissent jamais ici — la clé générée ne matche rien, t() retombe sur le libellé
// brut tel que saisi, jamais retraduit.
const HOLIDAY_NAME_KEYS = {
  "Neujahr": "neujahr", "Karfreitag": "karfreitag", "Ostermontag": "ostermontag",
  "Tag der Arbeit": "tagDerArbeit", "Christi Himmelfahrt": "christiHimmelfahrt",
  "Pfingstmontag": "pfingstmontag", "Tag der Deutschen Einheit": "tagDerDeutschenEinheit",
  "1. Weihnachtstag": "weihnachtstag1", "2. Weihnachtstag": "weihnachtstag2",
  "Heilige Drei Könige": "heiligeDreiKoenige", "Weltfrauentag": "weltfrauentag",
  "Fronleichnam": "fronleichnam", "Mariä Himmelfahrt": "mariaHimmelfahrt",
  "Reformationstag": "reformationstag", "Allerheiligen": "allerheiligen",
  "Buß- und Bettag": "bussUndBettag",
};
// Retour utilisateur (2026-08-09) : plus aucun arrondi dans Reporting — toujours la vraie
// valeur calculée, tronquée (jamais arrondie) à 2 décimales. Math.round()/toFixed()/
// toLocaleString() arrondissent tous par nature ; Math.trunc() coupe sans arrondir.
// Bug réel corrigé (2026-09-20, retour utilisateur : "j'entre 19,56 et c'est 19,55 qui est
// enregistré") : `19.56 * 100` vaut `1955.9999999999998` en JS (imprécision binaire des
// flottants, pas une faute de frappe) — Math.trunc coupait alors vers 1955 au lieu de 1956,
// soit un centime perdu à CHAQUE valeur dont la représentation binaire tombe juste sous son
// centième exact. `+1e-9` corrige ce bruit de calcul (bien plus petit que n'importe quelle
// vraie 3e décimale réelle, ex. 19,565 → 1956.4999999999998+1e-9 reste < 1956.5, toujours
// tronqué vers 19,56, jamais arrondi) sans jamais réintroduire le moindre arrondi — la règle
// du 2026-08-09 reste intacte, seule l'erreur de précision flottante est neutralisée.
const truncate2 = (v) => { const n = Number(v) || 0; return Math.trunc(n * 100 + Math.sign(n) * 1e-9) / 100; };
const fmt2 = (v) => (v == null ? "—" : truncate2(v).toFixed(2));
const fmtDe2 = (v) => (v == null ? "—" : truncate2(v).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
let RPID = 8000;

/* Catalogue Prebake/Brot (vraies données, /api/reporting/articles) et saisie
   quotidienne entsorgt (/api/reporting/daily-entries) — voir loadReportingCatalog/
   loadCatalogDaily et TODAY_IDX plus bas dans le composant (jour réel calculé,
   plus un index de démo figé). */

/* Grille hebdomadaire manuelle — { [categoryId]: { [jourIdx 0-5]: {pu20, entsorgt} } }.
   frischbrot_brot n'a que pu20 ici (son entsorgt vient de la saisie Brot). État initial
   réel : {} (vide), remplie par loadManualWasteWeek() — jamais une semaine inventée. */

const WIPE_TARGETS = [
  { id: "prebake", label: "Prebake Verluste (saisies quotidiennes)" },
  { id: "brot", label: "Brot Verluste (saisies quotidiennes)" },
  { id: "manual", label: "Autres Abschriften (Woche Abschriften)" },
  { id: "stundenIst", label: "Stundenleistung — saisies Ist quotidiennes" },
  { id: "krank", label: "Krank (heures maladie)" },
  { id: "plan", label: "Plan du mois (Stundenleistung)" },
  { id: "stundenFile", label: "Fichier Stundenleistung importé" },
  { id: "reports", label: "Rapports" },
];

/* ============================================================
   ADMINISTRATION — comptes, rôles & permissions. Hiérarchie
   fidèle : Employé < FLA < FL < Bereichsleiter < Verkaufsleiter
   < Niederlassungsleiter.
   ============================================================ */
// Même liste que ROLE_ORDER côté serveur (server.js /api/important-info/my-scope,
// security-engine.js) — confirmée en direct cette session, jamais un choix arbitraire.
// "Employé" retiré définitivement (demande utilisatrice 2026-07-31) : rôle générique
// inutile puisqu'il englobe tous les autres — un vrai compte doit toujours porter un
// rôle précis (Kassierer/Aushilfe/Azubi/...), jamais ce fourre-tout. Reste un alias de
// canonicalisation légitime côté serveur (server.js, mappe d'anciens intitulés comme
// "Mitarbeiter"/"EMPLOYEE" — infrastructure de production partagée avec l'app vanilla,
// hors du périmètre demandé ici) — seule la liste assignable côté nexora-app est purgée.
const ROLE_HIERARCHY = ["Kassierer", "Kassiererin", "Azubi", "Aushilfe", "Stellvertreter", "Stellvertreterin", "EQJ", "FLA", "FL", "Bereichsleiter", "Verkaufsleiter", "Niederlassungsleiter"];
// Catalogue RÉEL des permissions — porté verbatim de public/modules/security-engine.js
// (PERMISSION_GROUPS, ~90 permissions sur 24 modules), jamais la petite taxonomie
// inventée d'avant (6 groupes, "control.Voir" etc. — de simples libellés français, pas
// de vrais identifiants). Chaque permission.id EST la clé stockée telle quelle dans
// role_permissions côté serveur (ex. "control.view", "important-info.create.network").
const PERMISSION_GROUPS = [
  { id: "home", label: "Accueil", permissions: [
    { id: "home.view", label: "Voir l'accueil" },
  ] },
  { id: "admin", label: "Administration", permissions: [
    { id: "admin.view", label: "Voir administration" },
  ] },
  { id: "employees", label: "Personnel", permissions: [
    { id: "employees.view", label: "Voir personnel" },
    { id: "employees.create", label: "Ajouter personnel" },
    { id: "employees.edit", label: "Modifier personnel" },
    { id: "employees.delete", label: "Supprimer personnel" },
  ] },
  { id: "articles", label: "Articles", permissions: [
    { id: "articles.view", label: "Voir articles" },
    { id: "articles.create", label: "Ajouter articles" },
    { id: "articles.edit", label: "Modifier articles" },
    { id: "articles.delete", label: "Supprimer articles" },
    { id: "control.articlesImport", label: "Importer un catalogue (vision IA)" },
  ] },
  { id: "control", label: "Contrôle Kühlung", permissions: [
    { id: "control.view", label: "Voir le contrôle" },
    { id: "control.check", label: "Contrôler les articles" },
    { id: "control.edit_mhd", label: "Modifier MHD" },
    { id: "control.cancel", label: "Annuler contrôle" },
    { id: "control.delete_expired", label: "Supprimer les contrôles expirés" },
    { id: "control.mhdImport", label: "Importer un PDF MHD (vision IA)" },
  ] },
  { id: "reductions", label: "Réductions", permissions: [
    { id: "reductions.view", label: "Voir réductions" },
    { id: "reductions.create", label: "Créer réduction" },
    { id: "reductions.restore", label: "Restaurer" },
    { id: "reductions.validate", label: "Valider" },
  ] },
  { id: "dashboard", label: "Dashboard", permissions: [
    { id: "dashboard.view", label: "Voir dashboard" },
  ] },
  { id: "claims", label: "Réclamations", permissions: [
    { id: "claims.view", label: "Voir réclamations" },
    { id: "claims.create", label: "Créer" },
    { id: "claims.edit", label: "Modifier" },
    { id: "claims.delete", label: "Supprimer" },
  ] },
  { id: "messages", label: "Messages", permissions: [
    { id: "messages.access", label: "Accéder à la messagerie" },
    { id: "messages.send", label: "Envoyer des messages" },
    { id: "messages.private.create", label: "Créer une conversation privée" },
    { id: "messages.group.create", label: "Créer un groupe" },
    { id: "messages.group.manage", label: "Gérer un groupe (membres, nom, description)" },
    { id: "messages.filiale.channel.access", label: "Accéder au canal de sa filiale" },
    { id: "messages.crossBranch.create", label: "Créer une communication multi-filiale" },
    { id: "messages.attachments.upload", label: "Envoyer des pièces jointes" },
    { id: "messages.message.editOwn", label: "Modifier ses propres messages" },
    { id: "messages.message.deleteOwn", label: "Supprimer ses propres messages" },
    { id: "messages.message.deleteAny", label: "Supprimer n'importe quel message (modération)" },
    { id: "messages.conversation.archive", label: "Archiver une conversation" },
    { id: "messages.members.manage", label: "Ajouter/retirer des membres" },
    { id: "messages.search", label: "Rechercher dans les messages" },
    { id: "messages.moderate", label: "Modérer les conversations" },
  ] },
  { id: "users", label: "Utilisateurs", permissions: [
    { id: "users.view", label: "Voir utilisateurs" },
    { id: "users.create", label: "Ajouter" },
    { id: "users.edit", label: "Modifier" },
    { id: "users.delete", label: "Supprimer" },
  ] },
  { id: "stores", label: "Filiales", permissions: [
    { id: "stores.view", label: "Voir filiales" },
    { id: "stores.create", label: "Ajouter" },
    { id: "stores.edit", label: "Modifier" },
    // Vraie permission vérifiée par server/stores/routes.js (créer/modifier/supprimer une
    // filiale OU un Bezirk, les 6 routes de mutation) — manquait ici (2026-09-01, audit
    // suite à "vérifie que les permissions sont toutes à jour"), donc jamais assignable
    // depuis Rôles & permissions malgré le commentaire serveur prévoyant "Bereichsleiter+".
    { id: "stores.manage", label: "Créer/modifier/supprimer une filiale ou un Bezirk" },
  ] },
  { id: "roles", label: "Rôles & Permissions", permissions: [
    { id: "roles.view", label: "Voir permissions" },
    { id: "roles.edit", label: "Modifier permissions" },
  ] },
  { id: "system", label: "Paramètres système", permissions: [
    { id: "system.edit", label: "Modifier paramètres" },
  ] },
  { id: "reporting", label: "Reporting", permissions: [
    { id: "reporting.view", label: "Voir reporting" },
    { id: "reporting.manage", label: "Modifier entrées, importer Stundenplan/Stunden-file" },
  ] },
  { id: "inventur", label: "Inventur", permissions: [
    { id: "inventur.view", label: "Voir un inventaire" },
    { id: "inventur.create", label: "Créer un inventaire" },
    { id: "inventur.edit", label: "Modifier un inventaire" },
    { id: "inventur.category.manage", label: "Gérer les catégories" },
    { id: "inventur.task.assign", label: "Affecter une tâche" },
    { id: "inventur.entry.create", label: "Saisir des articles" },
    { id: "inventur.entry.edit", label: "Modifier une saisie" },
    { id: "inventur.category.finish", label: "Terminer une catégorie" },
    { id: "inventur.category.verify", label: "Vérifier une catégorie" },
    { id: "inventur.category.reopen", label: "Rouvrir une catégorie" },
    { id: "inventur.category.lock", label: "Verrouiller une catégorie" },
    { id: "inventur.close", label: "Clôturer un inventaire" },
    { id: "inventur.export", label: "Imprimer et exporter" },
    { id: "inventur.kontrolle.create", label: "Créer une Bestandskontrolle" },
    { id: "inventur.kontrolle.edit", label: "Modifier une Bestandskontrolle" },
    { id: "inventur.kontrolle.close", label: "Clôturer une Bestandskontrolle" },
    { id: "inventur.history.view", label: "Consulter les historiques" },
  ] },
  { id: "tasks", label: "Tâches", permissions: [
    { id: "tasks.view", label: "Voir les tâches" },
    { id: "tasks.create", label: "Créer une tâche" },
    { id: "tasks.complete", label: "Marquer terminé / traiter" },
    { id: "tasks.delete", label: "Supprimer / archiver" },
  ] },
  { id: "planning", label: "Planning", permissions: [
    { id: "planning.view", label: "Voir le planning" },
    { id: "planning.publish", label: "Publier / dépublier le planning" },
    { id: "planning.requests.manage", label: "Gérer les Planungsanfragen (valider/refuser)" },
    { id: "planning.requests.delete", label: "Supprimer des Anfragen" },
    { id: "planning.leistungsfrei.manage", label: "Gérer les comptes Leistungsfrei" },
    { id: "planning.vacation.manage", label: "Gérer les comptes Urlaub" },
    { id: "planning.aushilfe.manage", label: "Voir toutes les disponibilités Aushilfe" },
    { id: "planning.holidays.manage", label: "Gérer les jours fériés" },
  ] },
  // Anciennement groupe "PLU Quiz" à part — PLU Quiz est maintenant fusionné dans le
  // module Inventurmaßnahme (retour utilisateur explicite, 2026-08-11/12), donc ce groupe
  // porte désormais son nom. Les 2 identifiants de permission restent inchangés
  // (plu-quiz.view/plu-quiz.manage) — le serveur les vérifie tels quels
  // (requireDynamicPermission), les renommer casserait la vérification déjà en place.
  // Lieferschein Test + Bewertungsbogen (l'autre moitié du module) restent volontairement
  // ouverts à tout compte connecté, aucune permission dédiée pour eux (même choix que
  // Missions réseau/Associer EAN, voir server/inventurmassnahme/routes.js).
  { id: "inventurmassnahme", label: "Inventurmaßnahme", permissions: [
    { id: "plu-quiz.view", label: "Voir PLU Quiz (Training + Examen)" },
    { id: "plu-quiz.manage", label: "Gérer les listes PLU et voir les rapports de l'équipe" },
  ] },
  { id: "material-orders", label: "Commande de matériel", permissions: [
    { id: "material-orders.view", label: "Commander du matériel et voir l'historique de la filiale" },
    { id: "material-orders.manage", label: "Marquer une commande comme envoyée" },
  ] },
  { id: "merchandise-orders", label: "Commandes de marchandises", permissions: [
    { id: "merchandiseOrders.prepare", label: "Préparer une commande (remplir les quantités)" },
    { id: "merchandiseOrders.modify", label: "Modifier une commande existante (quantité, unité, date, remarque, suppression de ligne)" },
    { id: "merchandiseOrders.validate", label: "Envoyer et annuler une commande" },
    { id: "merchandiseOrders.reorder", label: "Réorganiser l'ordre des articles du rayon pour sa filiale" },
    { id: "merchandiseOrders.manageCatalog", label: "Gérer les catégories (jours) et les unités des articles" },
  ] },
  { id: "krankmeldung", label: "Krankmeldung", permissions: [
    { id: "krankmeldung.create", label: "Déclarer un arrêt maladie" },
    { id: "krankmeldung.view.own", label: "Voir ses propres déclarations" },
    { id: "krankmeldung.view.store", label: "Voir les déclarations de sa filiale" },
    { id: "krankmeldung.view.area", label: "Voir les déclarations de son périmètre" },
    { id: "krankmeldung.au.upload", label: "Déposer son AU" },
    { id: "krankmeldung.au.view", label: "Voir le statut des AU" },
    { id: "krankmeldung.au.download", label: "Télécharger/consulter une AU" },
    { id: "krankmeldung.au.verify", label: "Vérifier une AU" },
    { id: "krankmeldung.manage", label: "Gérer les dossiers (contacts, commentaire)" },
    { id: "krankmeldung.close", label: "Clôturer un dossier" },
    { id: "krankmeldung.cancel", label: "Annuler une déclaration" },
  ] },
  { id: "lieferschein", label: "Lieferschein", permissions: [
    { id: "lieferschein.view", label: "Voir les bons de livraison" },
    { id: "lieferschein.create", label: "Importer / créer un bon" },
    { id: "lieferschein.control", label: "Contrôler les articles (quantités, statuts, photos)" },
    { id: "lieferschein.validate", label: "Valider définitivement un bon (Kontrolle abschließen)" },
    { id: "lieferschein.manage", label: "Archiver, supprimer, générer une réclamation" },
  ] },
  { id: "document-intel", label: "Centre documentaire", permissions: [
    { id: "document-intel.view", label: "Voir le centre documentaire" },
    { id: "document-intel.upload", label: "Déposer des documents" },
    { id: "document-intel.manage", label: "Gérer/router les documents de la filiale" },
  ] },
  { id: "haccp", label: "Contrôle HACCP", permissions: [
    { id: "haccp.view", label: "Voir le contrôle HACCP" },
    { id: "haccp.fill", label: "Remplir les contrôles du jour" },
    { id: "haccp.edit", label: "Corriger une saisie existante" },
    { id: "haccp.validate.daily", label: "Valider la journée" },
    { id: "haccp.validate.weekly", label: "Valider la semaine (validation finale)" },
    { id: "haccp.export", label: "Exporter le PDF, voir les statistiques" },
  ] },
  { id: "important-info", label: "Informations importantes", permissions: [
    { id: "important-info.view", label: "Voir ses informations, lire, certifier, signer" },
    { id: "important-info.create.store", label: "Créer/publier pour sa propre filiale" },
    { id: "important-info.create.multi", label: "Créer/publier pour plusieurs filiales de son périmètre" },
    { id: "important-info.create.network", label: "Diffuser à tout le réseau" },
    { id: "important-info.manage", label: "Gérer (nouvelle version, relance, annulation, archivage)" },
    { id: "important-info.reports.own", label: "Consulter le suivi de ses propres publications" },
    { id: "important-info.reports.area", label: "Consulter le suivi de son périmètre" },
    { id: "important-info.export", label: "Exporter le PDF de suivi / consulter une preuve individuelle" },
  ] },
  { id: "article-finder", label: "Artikelfinder", permissions: [
    { id: "article-finder.view", label: "Photographier/rechercher, voir les fiches validées" },
    { id: "article-finder.create", label: "Proposer une nouvelle fiche" },
    { id: "article-finder.edit", label: "Compléter/modifier une fiche non validée, gérer ses photos" },
    { id: "article-finder.report", label: "Signaler un problème sur une fiche" },
    { id: "article-finder.validate", label: "Valider/refuser, modifier un prix validé, archiver/restaurer, gérer les signalements" },
    { id: "article-finder.manage", label: "Gérer les catégories, le délai de vérification, le moteur de reconnaissance" },
  ] },
  { id: "arbeitszeitliste", label: "Arbeitszeitliste", permissions: [
    { id: "arbeitszeitliste.view", label: "Consulter ses propres heures et confirmer" },
    { id: "arbeitszeitliste.manage", label: "Consulter et contrôler le document de sa filiale" },
    { id: "arbeitszeitliste.validate", label: "Valider les filiales de son périmètre (Bereichsleiter)" },
  ] },
  { id: "labels", label: "Fabrication d'étiquettes", permissions: [
    { id: "labels.view", label: "Voir les étiquettes enregistrées, prévisualiser, imprimer" },
    { id: "labels.manage", label: "Créer, modifier, supprimer des étiquettes" },
  ] },
  { id: "kassenpost", label: "Kassenpost", permissions: [
    { id: "kassenpost.view", label: "Voir les messages Kassenpost enregistrés" },
    { id: "kassenpost.manage", label: "Saisir, modifier, répondre, supprimer un message" },
  ] },
  { id: "warenaustausch", label: "Warenaustausch", permissions: [
    { id: "warenaustausch.prepare", label: "Créer/modifier/supprimer un transfert sortant, l'envoyer, l'annuler" },
    { id: "warenaustausch.receive", label: "Vérifier et valider la réception d'un transfert" },
    { id: "warenaustausch.view", label: "Voir les transferts et le Kontoauszug (lecture seule)" },
  ] },
  { id: "filialeReport", label: "Rapport de filiale", permissions: [
    { id: "filialeReport.create", label: "Créer, construire et envoyer un rapport de visite" },
    { id: "filialeReport.process", label: "Ouvrir un rapport reçu, traiter sa to-do, le marquer traité" },
  ] },
  // Vertretung (2026-08-22, voir resolveVertretungNotifyTargets, server.js) — conçue dès le
  // départ pour être "gérée depuis Admin > Rôles & Permissions comme n'importe quelle autre
  // permission dynamique" (commentaire serveur explicite), mais jamais ajoutée ici :
  // vertretung.notify n'était donc assignable à AUCUN rôle, la notification ne partait
  // jamais pour personne. Trouvé lors de l'audit "vérifie que les permissions sont toutes à
  // jour" (2026-09-01).
  { id: "vertretung", label: "Vertretung", permissions: [
    { id: "vertretung.notify", label: "Recevoir les notifications Vertretung de sa filiale" },
  ] },
  // Rundschreiben (2026-08-31) — même moteur qu'Infos importantes (server/importantInfo/),
  // mais permission VOLONTAIREMENT séparée (retour utilisateur explicite : "afficher ou pas
  // du tout, ou avec accès restreint ou pas du tout, ce sera en fonction du réglage") —
  // jamais héritée d'important-info.*, pour rester réglable indépendamment par rôle.
  { id: "rundschreiben", label: "Rundschreiben", permissions: [
    { id: "rundschreiben.view", label: "Voir, lire et signer les Rundschreiben reçus" },
    { id: "rundschreiben.manage", label: "Publier un Rundschreiben, suivre la réception par filiale, créer une mission liée" },
  ] },
];

// Icône + accent par groupe — utilisé par l'écran Admin > Rôles & permissions (fidélité
// littérale à l'artefact validé admin_redesign.html, qui montre un chip d'icône par
// module). Réutilise l'emoji déjà établi ailleurs dans l'app quand ce module y existe déjà
// (HOME_MODULES, rubriques fiche filiale) — jamais une iconographie inventée à part quand
// un équivalent existe déjà. Couleur = A.xxx, jamais une valeur ad hoc.
const PERMISSION_GROUP_ICON = {
  home: { emoji: "🏠", color: "blue" }, admin: { emoji: "🔐", color: "navy" },
  employees: { emoji: "👤", color: "green" }, articles: { emoji: "📦", color: "amber" },
  control: { emoji: "🛡️", color: "blue" }, reductions: { emoji: "🏷️", color: "purple" },
  dashboard: { emoji: "📊", color: "purple" }, claims: { emoji: "⚠️", color: "red" },
  messages: { emoji: "💬", color: "frost" }, users: { emoji: "👥", color: "green" },
  stores: { emoji: "🏬", color: "blue" }, roles: { emoji: "🔑", color: "purple" },
  system: { emoji: "⚙️", color: "navy" }, reporting: { emoji: "📈", color: "green" },
  inventur: { emoji: "🧮", color: "brown" }, tasks: { emoji: "✅", color: "green" },
  planning: { emoji: "📅", color: "teal" },
  "material-orders": { emoji: "🧰", color: "brown" }, "merchandise-orders": { emoji: "📥", color: "amber" },
  krankmeldung: { emoji: "🤒", color: "orange" }, lieferschein: { emoji: "📄", color: "blue" },
  "document-intel": { emoji: "🗂️", color: "purple" }, haccp: { emoji: "📋", color: "navy" },
  "important-info": { emoji: "📌", color: "red" }, "article-finder": { emoji: "🔎", color: "purple" },
  arbeitszeitliste: { emoji: "⏱️", color: "navy" }, labels: { emoji: "🖨️", color: "yellow" },
  inventurmassnahme: { emoji: "🧾", color: "navy" },
  warenaustausch: { emoji: "🚚", color: "amber" },
  filialeReport: { emoji: "🗒️", color: "teal" },
  rundschreiben: { emoji: "📰", color: "navy" },
  vertretung: { emoji: "🔄", color: "navy" },
};

/* ARTIKELFINDER — vraies données (server/articleFinder/, déjà extrait en Phase 6) —
   voir loadFinderCategories()/runManualSearch()/etc. plus bas. Catégories réelles,
   fiches réelles (photo obligatoire côté serveur pour valider — voir la note dans
   validateFinderItem). */

/* ============================================================
   LIEFERSCHEIN — import d'un bon multi-pages (PDF, photos ou
   saisie manuelle), analysé page par page puis vérifié par
   l'employé avant création. Signalement direct vers Réclamations.
   ============================================================ */
/* Lieferschein — les vrais statuts de contrôle par article (identiques aux 10 vraies
   valeurs LIEFERSCHEIN_ITEM_STATUSES côté serveur — server.js confirmé, pas un choix
   fictif). LIEFERSCHEIN_CLAIMABLE_STATUSES (7 des 10) sert uniquement à filtrer quelles
   lignes peuvent alimenter une réclamation (voir generateLfClaim), garder les 10 ici. */
const LF_ITEM_STATUS = {
  pending: { label: "À contrôler", color: "#64748b", emoji: "⏳" },
  complete: { label: "Conforme", color: "#16803d", emoji: "✅" },
  partial: { label: "Partiel", color: "#92400e", emoji: "🟠" },
  missing: { label: "Manquant", color: "#b91c1c", emoji: "❌" },
  excess: { label: "Excédent", color: "#0e7490", emoji: "➕" },
  damaged: { label: "Endommagé", color: "#b91c1c", emoji: "💥" },
  wrong_item: { label: "Mauvais article", color: "#b91c1c", emoji: "🔀" },
  unordered: { label: "Non commandé", color: "#7e22ce", emoji: "❓" },
  to_verify: { label: "À vérifier", color: "#92400e", emoji: "🔍" },
  other: { label: "Autre anomalie", color: "#64748b", emoji: "⚠️" },
};
const LIEFERSCHEIN_CLAIMABLE_STATUSES = ["missing", "partial", "damaged", "wrong_item", "unordered", "excess", "other"];
// snake_case (clés LF_ITEM_STATUS) -> camelCase (clés dict lieferschein.itemStatus.*) — même
// mapping que celui déjà utilisé en JSX (voir stKey plus bas), factorisé ici pour les
// usages hors-JSX (toasts/confirms) qui lisent LF_ITEM_STATUS[status].label directement.
const LF_STATUS_KEY_MAP = { pending: "pending", complete: "complete", partial: "partial", missing: "missing", excess: "excess", damaged: "damaged", wrong_item: "wrongItem", unordered: "unordered", to_verify: "toVerify", other: "other" };

const LANGS = [
  { id: "fr", flag: "🇫🇷" }, { id: "de", flag: "🇩🇪" }, { id: "en", flag: "🇬🇧" },
  { id: "es", flag: "🇪🇸" }, { id: "it", flag: "🇮🇹" },
];
/* Plus / Réglages — sessions actives de l'équipe (Supervision), vraies données via
   GET /api/admin/sessions (adminSessions state, réservé au super-admin côté serveur). */
/* Centre de notifications — deux natures bien distinctes, fidèles au vrai
   /api/notifications : des notifications persistées individuelles (lues/
   non lues, supprimables) ET un onglet "Urgent" qui n'est pas une vraie
   liste mais l'agrégat cross-module déjà utilisé par l'assistant/Action
   Live (dayTasks) — dismissible pour aujourd'hui seulement, jamais pour
   toujours si le problème persiste. */
const NOTIF_ICON = {
  messages: "💬", planning_published: "🗓️", planning_request: "🙋", planning_decision: "✅", planning_message: "💬",
  tasks: "✅", claims: "⚠️", haccp_daily: "🌡️", haccp_weekly: "🌡️", krankmeldung: "🤒",
  "plu-quiz": "🎯", "material-order": "🧰", leistungsfrei: "🏖️", lieferschein: "📄", availability: "🕐", "account-approval": "🛡️", "password-reset-request": "🔑",
  inventur_appointment: "📅",
  "article-report": "📸",
};
const HOME_MODULES = [
  { id: "notifications", label: "Notifications", emoji: "🔔", color: A.orange, live: true },
  { id: "control", label: "Contrôle", emoji: "🛡️", color: A.frost, live: true },
  { id: "reductions", label: "Réductions", emoji: "🏷️", color: A.orange, live: true },
  { id: "articles", label: "Articles", emoji: "📦", color: A.green, live: true },
  { id: "dashboard", label: "Dashboard", emoji: "📊", color: A.purple, live: true },
{ id: "haccp", label: "HACCP", emoji: "🌡️", color: A.blue, live: true },
  { id: "planning", label: "Planning", emoji: "📅", color: A.teal, live: true },
  { id: "tasks", label: "Tâches", emoji: "✅", color: A.green, live: true },
  { id: "missions", label: "Missions réseau", emoji: "🎯", color: A.purple, live: true },
{ id: "claims", label: "Réclamations", emoji: "⚠️", color: A.red, live: true },
{ id: "krank", label: "Krankmeldung", emoji: "🤒", color: A.orange, live: true },
{ id: "liefer", label: "Lieferschein", emoji: "📄", color: A.blue, live: true },
{ id: "finder", label: "Artikelfinder", emoji: "🔎", color: A.purple, live: true },
{ id: "scanner", label: "Associer EAN", emoji: "📷", color: A.navy, live: true },
{ id: "messages", label: "Messages", emoji: "💬", color: A.frost, live: true },
{ id: "infos", label: "Infos importantes", emoji: "📌", color: A.red, live: true },
{ id: "rundschreiben", label: "Rundschreiben", emoji: "📰", color: A.navy, live: true },
{ id: "material", label: "Commande matériel", emoji: "🧰", color: A.brown, live: true },
{ id: "reporting", label: "Reporting", emoji: "📈", color: A.green, live: true },
{ id: "admin", label: "Admin", emoji: "🔐", color: A.navy, live: true },
{ id: "docintel", label: "Centre documentaire", emoji: "🗂️", color: A.purple, live: true },
{ id: "azl", label: "Arbeitszeitliste", emoji: "📋", color: A.navy, live: true },
{ id: "inventur", label: "Inventur", emoji: "🧮", color: A.brown, live: true },
{ id: "merchandise", label: "Commandes marchandises", emoji: "📥", color: A.amber, live: true },
{ id: "labels", label: "Fabrication d'étiquettes", emoji: "🖨️", color: A.yellow, live: true },
{ id: "kassenpost", label: "Kassenpost", emoji: "📬", color: A.red, live: true },
{ id: "inventurmassnahme", label: "Inventurmaßnahme", emoji: "🧾", color: A.navy, live: true },
{ id: "preisanderung", label: "Preisänderung", emoji: "💶", color: A.amber, live: true },
{ id: "warenaustausch", label: "Warenaustausch", emoji: "🚚", color: A.amber, live: true },
{ id: "filialeReport", label: "Rapport de filiale", emoji: "🗒️", color: A.teal, live: true },
{ id: "rr", label: "RR", emoji: "🧮", color: A.amber, live: true },
{ id: "kontoauszug", label: "Kontoauszug", emoji: "🏦", color: A.navy, live: true },
];

// Contrôle d'accès par module (2026-08-11, retour utilisateur explicite : "brancher les
// permissions à toutes les fonctionnalités de l'app... quand on décoche, accès refusé").
// Clé = HOME_MODULES.id (le seul point d'entrée central de navigation, voir le onClick
// unique de la grille "Modules" plus bas) ; valeur = l'id du groupe correspondant dans
// PERMISSION_GROUPS. hasModuleAccess() vérifie ensuite si le rôle a AU MOINS UNE permission
// de tout le groupe (pas seulement un ".view" choisi à la main) — vérifié sur les vraies
// données serveur (FLA a "arbeitszeitliste.manage" mais PAS "arbeitszeitliste.view" :
// un gate limité au seul ".view" aurait bloqué à tort ce compte réel du module AZL).
// "notifications", "missions" et "scanner" n'ont volontairement AUCUNE entrée ici : ce sont
// de vrais écrans mais ils n'ont pas de groupe dans PERMISSION_GROUPS (rien à cocher/
// décocher pour eux dans Rôles & permissions) — les gater sans bouton pour les autoriser
// reviendrait à les couper pour tout le monde. Ceci est un contrôle d'expérience côté
// client (quel module proposer), jamais le vrai rempart de sécurité — celui-ci reste et
// reste UNIQUEMENT côté serveur sur chaque route /api/*.
// "preisanderung" et "rr" sont AUSSI volontairement absents d'ici, pour une raison
// différente (confirmé par audit, 2026-09-01) : preisanderung est strictement admin-only
// côté serveur (isAdminCaller, jamais assignable à un rôle) et rr est scopé par
// filiale/créateur, jamais par une permission role_permissions — aucun des deux n'a de
// permission à cocher, donc rien à relier ici non plus.
const MODULE_TO_PERMISSION_GROUP = {
  control: "control", reductions: "reductions", articles: "articles", dashboard: "dashboard",
  haccp: "haccp", planning: "planning", tasks: "tasks", claims: "claims", krank: "krankmeldung",
  liefer: "lieferschein", finder: "article-finder", messages: "messages", infos: "important-info",
  material: "material-orders", reporting: "reporting", admin: "admin",
  docintel: "document-intel", azl: "arbeitszeitliste", inventur: "inventur",
  merchandise: "merchandise-orders", labels: "labels", kassenpost: "kassenpost",
  // Trouvé lors de l'audit "vérifie que les permissions sont toutes à jour" (2026-09-01) —
  // le groupe existait déjà dans PERMISSION_GROUPS (plu-quiz.view/manage) mais jamais relié
  // ici : hasModuleAccess("inventurmassnahme") retombait sur `if (!groupId) return true`,
  // rendant la tuile visible pour tout rôle quel que soit le réglage réel.
  inventurmassnahme: "inventurmassnahme",
  // Warenaustausch (retour utilisateur explicite, 2026-08-23 : "désactivant complètement la
  // vue pour FL... avec notre permission module par rôle") — c'est CETTE entrée qui fait
  // dépendre la tuile d'accueil du groupe warenaustausch : tant que le rôle FL n'a AUCUNE
  // permission warenaustausch.* dans role_permissions (jamais accordée par défaut), la
  // tuile n'apparaît pas sur son accueil — aucun flag séparé, exactement le mécanisme
  // standard de l'app.
  warenaustausch: "warenaustausch",
  // Rapport de filiale (2026-08-26) — même mécanisme exact que warenaustausch juste
  // au-dessus : un FL n'a AUCUNE permission filialeReport.* par défaut, donc la tuile
  // n'apparaît pas sur son accueil tant qu'un responsable ne la lui accorde pas.
  filialeReport: "filialeReport",
  // Kontoauszug (2026-08-30) — réutilise la MÊME permission que Reporting (retour
  // utilisateur explicite sur la sensibilité financière du module : "ce module gère en gros
  // les finances de la filiale") plutôt qu'une nouvelle permission dédiée qui démarrerait
  // accordée à personne — quiconque a déjà accès à Reporting aujourd'hui (FL/FLA/BL/VL/NL)
  // voit aussi ce module sans reconfiguration Admin nécessaire.
  kontoauszug: "reporting",
  // Rundschreiben — sa PROPRE permission, jamais héritée d'important-info (voir son
  // commentaire dans PERMISSION_GROUPS) : tant qu'un rôle n'a aucune permission
  // rundschreiben.*, la tuile n'apparaît pas, exactement le mécanisme standard de l'app.
  rundschreiben: "rundschreiben",
};

// Modules Accueil GR — retour utilisateur explicite (2026-08-09) : mêmes icônes/couleurs que
// HOME_MODULES (compte normal) partout où le concept existe déjà côté employé (Missions
// réseau/Messages/Infos importantes reprennent EXACTEMENT leur entrée HOME_MODULES ; Article
// promo réseau ↔ Réductions, RDV inventaire ↔ Planning, Analyse ↔ Dashboard réutilisent le
// même emoji+couleur que leur équivalent conceptuel) — jamais une iconographie inventée à part.
const NETWORK_HOME_MODULES = [
  { id: "netFiliales", label: "Filiales", emoji: "🏬", color: A.blue },
  { id: "netAnalyse", label: "Analyse", emoji: "📊", color: A.purple },
  { id: "netMissions", label: "Missions réseau", emoji: "🎯", color: A.purple },
  { id: "netAlertes", label: "Alertes", emoji: "🚨", color: A.red },
  { id: "netPromo", label: "Article promo réseau", emoji: "🏷️", color: A.orange },
  { id: "netInfos", label: "Infos importantes", emoji: "📌", color: A.red },
  // Rundschreiben (2026-08-31) — même patron que netInfos juste au-dessus : reprend
  // EXACTEMENT l'entrée HOME_MODULES ("rundschreiben"), écran "infos" partagé avec
  // Infos importantes (voir infoCategoryFilter), jamais un écran dupliqué.
  { id: "netRundschreiben", label: "Rundschreiben", emoji: "📰", color: A.navy },
  { id: "netMessages", label: "Nouvelle communication", emoji: "💬", color: A.frost },
  { id: "netAppointments", label: "Rendez-vous d'inventaire", emoji: "📅", color: A.teal },
  // Vertretung entre grands responsables (2026-08-22, concept validé en conversation
  // complète) — écran autonome (setScreen), pas un onglet de Pilotage réseau, même patron
  // que netInfos/netMessages ci-dessus.
  { id: "netVertretung", label: "Vertretung", emoji: "🔄", color: A.navy },
  // Rapport de filiale (2026-08-26) — CÔTÉ CRÉATION : un GR (Bereichsleiter/Verkaufsleiter/
  // Niederlassungsleiter) n'a jamais accès à la grille HOME_MODULES (compte normal), donc
  // le point d'entrée pour CRÉER un rapport doit vivre ici, pas seulement dans HOME_MODULES
  // (qui, lui, reste le point d'entrée RÉCEPTION pour un compte normal — FL/FLA/BL/VKL/NL en
  // tant que destinataire). Même patron que netVertretung juste au-dessus : écran autonome
  // (setScreen), aucun onglet de Pilotage réseau, aucune permission supplémentaire ici (un
  // compte réseau EST par nature Bereichsleiter/Verkaufsleiter/Niederlassungsleiter).
  { id: "netFilialeReport", label: "Rapport de filiale", emoji: "🗒️", color: A.teal },
];

// Tic sonore synthétisé (Web Audio, aucun fichier) — palliatif pour iOS Safari/WKWebView
// où navigator.vibrate n'est jamais exposé (restriction Apple, aucune alternative
// publique côté PWA) ; validé sur la maquette
// https://claude.ai/code/artifact/f99d2c39-1bde-44d4-9329-0db172e19b4c. Respecte le mode
// silencieux iOS par design de la plateforme (Web Audio obéit au loquet sonnerie/silence),
// ce n'est pas un bug. resume() étant asynchrone, on attend sa fin avant de jouer le son
// pour ne pas le perdre silencieusement.
let nxAudioCtx = null;
function nxFireTick() {
  const osc = nxAudioCtx.createOscillator();
  const gain = nxAudioCtx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(920, nxAudioCtx.currentTime);
  gain.gain.setValueAtTime(0.16, nxAudioCtx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, nxAudioCtx.currentTime + 0.05);
  osc.connect(gain).connect(nxAudioCtx.destination);
  osc.start();
  osc.stop(nxAudioCtx.currentTime + 0.06);
}
function playTick() {
  try {
    // Bug réel signalé (2026-08-16, "quand je sors de l'app et reviens le son disparaît
    // totalement au toucher, il faut fermer complètement l'app pour le remettre") — quirk
    // WebKit connu : après une mise en arrière-plan (iOS PWA/WKWebView), l'AudioContext peut
    // rester coincé "muet" sans jamais repasser par l'état "suspended" (donc resume() ne se
    // déclenche jamais) — .state seul ne suffit pas à détecter ce cas. Voir aussi l'écouteur
    // visibilitychange plus bas, qui ferme proactivement le contexte au retour au premier
    // plan pour forcer une recréation propre ici, au lieu d'attendre un état cassé silencieux.
    if (!nxAudioCtx || nxAudioCtx.state === "closed") nxAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (nxAudioCtx.state === "suspended") nxAudioCtx.resume().then(nxFireTick).catch(() => {});
    else nxFireTick();
  } catch { /* pas de Web Audio sur ce navigateur */ }
}
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && nxAudioCtx) {
      try { nxAudioCtx.close(); } catch { /* déjà fermé/invalide, sans conséquence */ }
      nxAudioCtx = null;
    }
  });
}

// Retour haptique court pour les boutons +/- de quantité et de navigation ‹ › (Web
// Vibration API, vibre réellement sur Chrome/Android) — sur iOS, où navigator.vibrate
// n'existe pas, joue le tic synthétisé ci-dessus à la place. Jamais appelé depuis
// Pressable par défaut (déclencherait un tic/vibration sur CHAQUE bouton de l'app) —
// appelé explicitement dans HoldButton et via la prop `feedback` de Pressable.
const hapticTap = () => {
  try {
    if (navigator.vibrate) navigator.vibrate(10);
    else playTick();
  } catch { /* ignore */ }
};

// `feedback` — actif PAR DÉFAUT sur toute l'app (retour utilisateur explicite, 2026-08-16 :
// "Active le son pour toute l'app", reversant le choix précédent qui réservait le flash+tic
// à la navigation principale). Seul `feedback={false}` explicite désactive le son/flash au
// tap — utilisé pour les cas où il ferait vraiment doublon (ex. HoldButton gère déjà son
// propre hapticTap à chaque tick, voir plus bas, jamais besoin de le déclencher deux fois).
const Pressable = ({ style, children, feedback, onClick, className, ...p }) => {
  const [down, setDown] = useState(false);
  const [flash, setFlash] = useState(false);
  const feedbackEnabled = feedback !== false;
  const handleClick = feedbackEnabled
    ? (e) => {
        hapticTap();
        setFlash(false);
        requestAnimationFrame(() => setFlash(true));
        onClick?.(e);
      }
    : onClick;
  return (
    <button {...p} onClick={handleClick}
      onPointerDown={() => setDown(true)} onPointerUp={() => setDown(false)} onPointerLeave={() => setDown(false)}
      className={[className, flash ? "nx-tap-flash" : null].filter(Boolean).join(" ") || undefined}
      onAnimationEnd={feedbackEnabled ? () => setFlash(false) : undefined}
      style={{
        border: "none", background: "none", padding: 0, cursor: "pointer", textAlign: "left",
        fontFamily: `var(--nx-font, ${FONT})`, transition: "transform .16s cubic-bezier(.34,1.4,.64,1)",
        transform: down ? "scale(.96)" : "scale(1)", ...style,
      }}
    >{children}</button>
  );
};

/* Suppression par glissement vers la gauche, façon iPhone — révèle un
   bouton "Supprimer" rouge derrière la ligne ; glisser au-delà de la
   moitié de la largeur du bouton l'ouvre en position stable, sinon la
   ligne revient à sa place. Basé sur pointer events (même pattern que
   la bulle de la tab bar). */
const SwipeToDelete = ({ onDelete, children }) => {
  const [dragX, setDragX] = useState(0);
  const [flying, setFlying] = useState(false);
  const openRef = useRef(false);
  const startXRef = useRef(null);
  const draggingRef = useRef(false);
  const REVEAL = 76;
  const AUTO = REVEAL * 2.3;
  const pastAuto = dragX < -AUTO;
  const onPointerDown = (e) => {
    startXRef.current = e.clientX;
    draggingRef.current = true;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {}
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    const dx = e.clientX - startXRef.current;
    const base = openRef.current ? -REVEAL : 0;
    const next = Math.max(-AUTO - 40, Math.min(0, base + dx));
    setDragX(next);
  };
  const endDrag = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (dragX < -AUTO) {
      setFlying(true);
      setDragX(-window.innerWidth);
      setTimeout(onDelete, 200);
    } else if (dragX < -REVEAL / 2) { setDragX(-REVEAL); openRef.current = true; }
    else { setDragX(0); openRef.current = false; }
  };
  return (
    <div style={{ position: "relative", overflow: "hidden", borderRadius: 16, marginBottom: 8 }}>
      <div style={{
        position: "absolute", inset: 0, display: "flex", justifyContent: "flex-end", alignItems: "stretch",
        background: `linear-gradient(90deg, ${pastAuto ? "#dc2626" : A.red}00, ${pastAuto ? "#dc2626" : A.red})`,
      }}>
        <Pressable onClick={() => { onDelete(); setDragX(0); openRef.current = false; }} aria-label="Supprimer" style={{
          width: REVEAL, display: "grid", placeItems: "center", borderRadius: 0,
        }}>
          <span style={{
            fontSize: `calc(${(pastAuto ? 21 : 17)}px * var(--nx-font-scale, 1))`, color: "#fff", transition: "font-size .15s ease",
            filter: "drop-shadow(0 1px 2px rgba(0,0,0,.25))",
          }}>🗑️</span>
        </Pressable>
      </div>
      <div
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerCancel={endDrag}
        style={{
          transform: `translateX(${dragX}px)`, position: "relative",
          transition: draggingRef.current ? "none" : flying ? "transform .2s ease-in" : "transform .22s cubic-bezier(.34,1.2,.64,1)",
          touchAction: "pan-y",
        }}
      >{children}</div>
    </div>
  );
};

// Bascule afficher/masquer — les 10 champs mot de passe de l'app (connexion, inscription,
// mot de passe oublié, changement de mot de passe, écran de verrouillage) partagent ce
// composant plutôt que de dupliquer le bouton œil à chaque site d'appel. Visibilité locale
// à CHAQUE champ (jamais partagée entre 2 champs du même écran, ex. nouveau mot de passe +
// confirmation) — état interne, pas de prop `visible` remontée au parent.
// Icônes œil/œil-barré en SVG trait fin (2026-08-22, retour utilisateur explicite : "les
// emojis... ne fait pas pro mais plutôt enfantin", à propos de 👁️/🙈) — remplace les deux
// emojis par des icônes vectorielles sobres (patron Feather/Lucide, courant sur tout
// formulaire de connexion pro), currentColor pour hériter la couleur déjà posée sur le
// Pressable parent (P.muted), un seul point de correction ici cascade vers les 12 usages de
// PasswordInput dans l'app.
const EyeIcon = ({ off }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {off ? (
      <>
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68" />
        <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.26 9.26 0 0 0 5.39-1.61" />
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    ) : (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);
const PasswordInput = ({ style, P, t, ...rest }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input {...rest} type={visible ? "text" : "password"} style={{ ...style, paddingRight: 42 }} />
      <Pressable
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? t("auth.hidePasswordAria", "Masquer le mot de passe") : t("auth.showPasswordAria", "Afficher le mot de passe")}
        style={{
          position: "absolute", right: 3, top: "50%", transform: "translateY(-50%)",
          width: 34, height: 34, display: "grid", placeItems: "center", borderRadius: 99, color: P.muted,
        }}
      ><EyeIcon off={visible} /></Pressable>
    </div>
  );
};

const Ring = ({ pct, P, size = 66 }) => (
  <div style={{
    width: size, height: size, borderRadius: 999, display: "grid", placeItems: "center", flex: "0 0 auto",
    background: `conic-gradient(${A.green} ${pct}%, ${P.soft} 0)`, transition: "background .7s ease",
  }}>
    <div style={{
      width: size - 14, height: size - 14, borderRadius: 999, background: P.card,
      display: "grid", placeItems: "center", textAlign: "center", lineHeight: 1.02,
    }}>
      <div>
        <strong style={{ fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", display: "block", color: P.ink }}>{pct == null ? "--" : `${pct}%`}</strong>
        <span style={{ fontSize: `calc(8px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 800, letterSpacing: ".12em" }}>FAIT</span>
      </div>
    </div>
  </div>
);

// Chaque <Sheet> de l'app (30+, un par formulaire/liste dans les 20 modules) était
// monté INCONDITIONNELLEMENT — seul le style CSS (transform/opacity) changeait avec
// `open`, jamais le contenu réel : `{children}` était donc évalué et présent dans le DOM
// en permanence, même pour une sheet jamais ouverte de la session, sur CHAQUE écran.
// Mesuré en direct : 1338 <button> déjà dans le DOM sur l'accueil fraîchement connecté,
// AVANT toute navigation — la cause dominante trouvée du vrai crash natif iOS/PWA par
// manque de mémoire (voir storeflow_pwa_stale_bundle_crash, confirmé via l'inspecteur
// distant : aucune erreur JS, "la page a quitté"). `hasOpenedRef` ne monte les enfants
// qu'à partir de la première ouverture réelle — reste monté ensuite (pas de
// démontage/remontage à chaque fermeture, l'animation de fermeture doit toujours voir le
// contenu glisser hors écran).
const Sheet = ({ open, onClose, P, children, maxH = "84%" }) => {
  const hasOpenedRef = useRef(false);
  if (open) hasOpenedRef.current = true;
  const isWide = useContext(WideContext);
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
        opacity: open ? 1 : 0, pointerEvents: open ? "auto" : "none", transition: "opacity .25s ease", zIndex: 40,
        display: isWide ? "flex" : "block", alignItems: "center", justifyContent: "center",
      }}>
      <aside onClick={(e) => e.stopPropagation()} style={isWide ? {
        // Ossature desktop (Phase 1) : fenêtre centrée (fade + scale) au lieu d'une
        // feuille ancrée en bas — même précaution que le mode téléphone ci-dessous
        // (boxShadow ET opacity retirés à l'état fermé, pas seulement le transform) pour
        // ne pas réintroduire la "tache noire" sous une autre forme.
        position: "relative", zIndex: 50, maxHeight: maxH, width: "100%",
        background: P.card, border: `1px solid ${P.line}`,
        borderRadius: 24, boxShadow: open ? "0 30px 80px rgba(0,0,0,.35)" : "none",
        display: "flex", flexDirection: "column",
        transform: open ? "scale(1)" : "scale(.96)",
        opacity: open ? 1 : 0, pointerEvents: open ? "auto" : "none",
        transition: "transform .25s ease, opacity .25s ease, box-shadow .25s ease",
        maxWidth: 560, margin: "0 auto", color: P.ink,
      } : {
        position: "fixed", left: 10, right: 10, bottom: 10, zIndex: 50, maxHeight: maxH,
        background: P.card, border: `1px solid ${P.line}`,
        // boxShadow retiré à l'état fermé (open:false) — la "tache noire" persistante en
        // bas de l'app (tous appareils, pas seulement iOS) venait d'ici : translateY(112%)
        // ne déplace la boîte que de 112% de SA PROPRE hauteur, quasi nulle avant la
        // première ouverture (children pas encore montés, cf. hasOpenedRef) — un flou de
        // 80px sur une boîte à peine décalée déborde en permanence dans l'écran visible.
        borderRadius: 28, boxShadow: open ? "0 30px 80px rgba(0,0,0,.35)" : "none",
        display: "flex", flexDirection: "column",
        overscrollBehavior: "contain",
        transform: open ? "translateY(0)" : "translateY(112%)",
        transition: "transform .34s cubic-bezier(.32,.72,.33,1), box-shadow .34s ease",
        maxWidth: 500, margin: "0 auto", color: P.ink,
      }}>
        {/* Bouton ✕ TOUJOURS accessible, HORS de la zone qui défile (2026-08-17, retour
            utilisateur explicite — module Bestandskontrolle, formulaire à 3 cycles) : l'ancien
            ✕ vivait à l'intérieur du contenu défilant, en position:sticky pour tenter de
            rester visible — mécanisme documenté fragile sur WebKit (voir ci-dessous), qui
            lâchait sur un contenu assez long et rendait le popup impossible à fermer autrement
            qu'en tuant l'app. Ce ✕-ci est un vrai frère du contenu défilant, jamais affecté
            par son scroll interne, quelle que soit sa hauteur. SheetHead (plus bas dans le
            fichier) ne rend plus son propre ✕ pour éviter un doublon — celui-ci est
            désormais LE point de fermeture unique de toute sheet. */}
        {/* aria-label en dur (pas de t()) — Sheet est un composant hors du composant
            principal, sans accès au hook useI18n/t(). Purement accessibilité (jamais visible
            à l'écran), pas critique de le traduire ici. */}
        <Pressable onClick={onClose} aria-label="Fermer" style={{
          position: "absolute", top: isWide ? 16 : 14, right: isWide ? 16 : 14, zIndex: 6,
          width: 32, height: 32, borderRadius: 99, background: P.soft, color: P.muted,
          display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
          boxShadow: "0 2px 6px rgba(0,0,0,.15)", flexShrink: 0,
        }}>✕</Pressable>
        <div style={isWide ? {
          overflowY: "auto", overscrollBehavior: "contain", padding: "22px 24px", minHeight: 0,
        } : {
          overflowY: "auto", overscrollBehavior: "contain", minHeight: 0,
          padding: "10px 18px calc(76px + env(safe-area-inset-bottom))",
          // WebkitOverflowScrolling:"touch" retiré (2026-08-13) : bug WebKit connu — combiné à
          // position:sticky sur un header interne, il cessait de rester collé en haut sur un
          // contenu assez long pour défiler. N'a plus d'importance pour la fermeture depuis
          // que le ✕ ci-dessus vit hors de cette zone, mais laissé retiré (scroll momentum
          // natif sur iOS moderne même sans cette propriété).
        }}>
          {!isWide && <div style={{ width: 40, height: 5, borderRadius: 99, background: P.line, margin: "4px auto 14px" }} />}
          {hasOpenedRef.current ? children : null}
        </div>
      </aside>
      </div>
    </>
  );
};

/* ============================================================
   FABRICATION D'ÉTIQUETTES — moteur de modèles (nouveau module).

   Chaque modèle est une entrée INDÉPENDANTE de LABEL_TEMPLATES : format physique exact
   (mm), champs génériques qu'il utilise parmi les 9 possibles, et son propre bloc de rendu
   HTML (buildBody). Ajouter un futur modèle = ajouter une entrée ici, jamais toucher au
   moteur (écrans de saisie/aperçu/impression, plus bas dans le composant) ni aux 3 autres
   modèles — exigence explicite de l'utilisateur ("chaque modèle doit être indépendant").

   Fidélité impression = fidélité aperçu : le MÊME buildBody() (une chaîne HTML) alimente
   à la fois l'aperçu écran et le portail d'impression (#lblPrintRoot, voir plus bas dans le
   composant) — jamais deux implémentations de rendu qui pourraient diverger. Les unités CSS
   "mm" sont natives (1mm = 1/25.4 pouce, reconnu directement par tous les navigateurs) : le
   même balisage produit donc une taille écran proportionnellement exacte ET une taille
   d'impression physique exacte quand @page reprend les mêmes dimensions (voir
   buildLabelPrintHtml plus bas dans le composant).
   ============================================================ */

// Formats d'impression exacts demandés — jamais un choix laissé au pilote d'impression
// ("sans ajuster à la page", voir @page dans buildLabelPrintHtml).
const LABEL_FORMATS = {
  flat5x5: { widthMm: 50, heightMm: 50 },
  flat15x5: { widthMm: 150, heightMm: 50 },
  // Retour utilisateur explicite (2026-08-26) : "il n'y a que 2 formats de Aktion : A4 et
  // 15x5" / "le 21x15 n'existe pas" — l'ancien aktion21x15 (150×210mm, portrait) était une
  // erreur (photo TRIMM Sirup mal réorientée, prise pour un format haut). Le vrai second
  // format Aktion est 150×50mm, PAYSAGE — mêmes dimensions que flat15x5, mesuré directement
  // sur une seconde photo TRIMM Sirup correctement recadrée.
  aktion15x5: { widthMm: 150, heightMm: 50 },
  aktionA4: { widthMm: 210, heightMm: 297 },
  // Pancarte séparateur de rayon (mesurée sur "Internationale Weine", photo réelle NORMA,
  // 2026-08-26) — paysage, contrairement à toutes les autres étiquettes de ce module
  // (portrait) : ce n'est pas une étiquette produit, jamais de texte dans la largeur ici.
  rayon15x7: { widthMm: 150, heightMm: 65 },
  // Chantier impression Niimbot (2026-08-28) — SEUL format physiquement imprimable sur
  // l'imprimante Niimbot de l'utilisateur (rouleau chargé) ; aucun des formats ci-dessus ne
  // convient. Retour utilisateur explicite : "créer le format 5x3... vu qu'il ne peut
  // qu'imprimer sous ce format".
  niimbot5x3: { widthMm: 50, heightMm: 30 },
};

// Les 9 champs génériques — chaque modèle choisit lesquels afficher/éditer (LABEL_TEMPLATES
// [id].fields) ; le state (labelDraft) garde toujours la même forme, jamais de champs
// dynamiquement ajoutés/retirés de l'objet lui-même (plus simple, plus sûr).
function emptyLabelDraft() {
  return {
    marque: "", designation: "", description: "", conditionnement: "",
    prix: "", prixDeBase: "", ve: "", numeroArticle: "", dateDebut: "",
    couleur: "jaune", // fond au choix (LABEL_COLOR_SWATCHES) — jaune = défaut mesuré (LABEL_FLAT_COLOR)
    aktionBanner: true, // bandeau "Aktion! Mehr fürs Geld." (modèles aktion*), activé par défaut
    logos: [],           // logos de certification optionnels (modèles aktion*), ex. ["bio","dlg"]
    rayonTitle: "",       // pancarte séparateur de rayon (modèle rayon15x7)
    ean: "",              // code-barres scannable (modèle niimbot5x3, chantier impression Niimbot 2026-08-28)
  };
}

// Les étiquettes sont imprimées (dangerouslySetInnerHTML, portail #lblPrintRoot) — jamais
// injecter la saisie utilisateur sans échappement (seul point de tout App.jsx qui construit
// du HTML depuis un texte 100% libre plutôt que des données déjà internes/fiables).
function escapeLabelHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
// conditionnement/description acceptent plusieurs lignes (saisie libre) — un retour à la
// ligne devient <br>, jamais une balise imbriquée qui casserait l'échappement ci-dessus.
function escapeLabelMultiline(value) {
  return escapeLabelHtml(value).replace(/\n/g, "<br>");
}

// Code-barres réellement scannable (chantier impression Niimbot, 2026-08-28) — jamais juste
// les chiffres en texte. JsBarcode rend DANS un élément DOM fourni (jamais de retour "chaîne
// pure" natif) : un <svg> détaché suffit (pas besoin d'être attaché au document pour que le
// rendu fonctionne), on en récupère ensuite le balisage sérialisé. EAN13 attendu (13 chiffres,
// le format le plus courant en retail) — repli sur CODE128 (accepte n'importe quelle longueur/
// alphabet) si la valeur scannée n'est pas un EAN13 valide, plutôt que de faire échouer tout
// le rendu de l'étiquette pour un code-barres au format inattendu.
function buildEanBarcodeSvg(ean) {
  const value = String(ean ?? "").trim();
  if (!value) return "";
  // JsBarcode valide vraiment la somme de contrôle EAN13/EAN8 (vérifié en test réel,
  // 2026-08-28) et lève une simple CHAÎNE (pas une vraie Error, jamais .message dessus) sur
  // une valeur invalide — un EAN13 à 13 chiffres mais somme de contrôle fausse (saisie
  // manuelle, faute de frappe) ferait donc échouer EAN13 alors que CODE128 encoderait la
  // même chaîne sans problème (aucune sémantique de somme de contrôle produit). Toujours
  // essayer EAN13 d'abord (format standard retail, deux lignes de texte lisibles en moins
  // sous le code) mais ne jamais renoncer complètement si le scan réel donne un EAN valide
  // avec une somme de contrôle que JsBarcode refuse pour une autre raison.
  function tryFormat(format) {
    const svgEl = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    // margin:0 (choix initial pour gagner de la place sur le petit 5×3) cassait la lecture
    // par un vrai lecteur MDE (retour utilisateur réel, 2026-08-28) : sans zone de silence
    // (espace blanc) de part et d'autre des barres, un scanner professionnel n'arrive
    // souvent pas à détecter le début/fin du code, même quand il "a l'air" correct à l'œil
    // ou via la caméra d'un téléphone (bien plus tolérante). On revient à la marge par
    // défaut de JsBarcode (10) — non négociable, plus important que maximiser la largeur
    // des barres dans le format réduit.
    // Seule la zone de silence HORIZONTALE (gauche/droite) compte pour la lecture d'un
    // code-barres 1D — un lecteur scanne une ligne horizontale, la marge haut/bas n'a aucun
    // effet sur la détection. marginTop/marginBottom à 0 pour ne pas gaspiller la hauteur
    // déjà très contrainte du format 5×3 (30mm total, désignation + prix à caser autour).
    // height/fontSize réduits (2026-08-28, retour utilisateur réel : le prix chevauchait le
    // texte du code-barres) — le format 5×3 ne laisse que ~13mm de hauteur disponible pour
    // le code-barres une fois désignation + prix réservés ; les valeurs par défaut de
    // JsBarcode (56 unités de haut au total) débordaient sur les voisins.
    JsBarcode(svgEl, value, { format, width: 2, height: 24, displayValue: true, fontSize: 8, marginLeft: 10, marginRight: 10, marginTop: 0, marginBottom: 0 });
    // JsBarcode fixe width/height en px sur l'attribut racine (ex. 214x56) — sans ça le SVG
    // déborde de tout conteneur plus étroit (constaté en test réel : code-barres tronqué à
    // droite sur l'étiquette niimbot5x3, 2026-08-28). On force le scale au conteneur via le
    // viewBox déjà posé par JsBarcode, en pixels non requis pour l'impression.
    svgEl.setAttribute("style", "display:block;width:100%;height:auto;");
    svgEl.removeAttribute("width");
    svgEl.removeAttribute("height");
    return svgEl.outerHTML;
  }
  // EAN-8 (8 chiffres, format standard petits emballages) jamais géré avant — un EAN à 8
  // chiffres tombait directement en CODE128, une symbologie DIFFÉRENTE que beaucoup de
  // lecteurs MDE retail n'acceptent pas par défaut (configurés EAN/UPC uniquement). Retour
  // utilisateur réel (2026-08-28) : impression OK visuellement, lecteur MDE incapable de
  // scanner — cause trouvée ici, pas côté impression.
  const formats = /^\d{13}$/.test(value) ? ["EAN13", "CODE128"]
    : /^\d{8}$/.test(value) ? ["EAN8", "CODE128"]
    : ["CODE128"];
  for (const format of formats) {
    try {
      return tryFormat(format);
    } catch (err) {
      console.warn(`Code-barres non généré (${format}) :`, err);
    }
  }
  return "";
}

// TRIMM Sirup (photo réelle "Aktion") : fond BLANC + bandeau rouge/jaune fixe — jamais un
// fond plein coloré au choix (l'ancien LABEL_AKTION_COLORS n'était mesuré sur AUCUNE photo
// réelle, contrairement à LABEL_FLAT_COLOR ci-dessous, mesuré sur Sinulind).
const LABEL_AKTION_BANNER = { bg: "#E2231A", edge: "#FFCC00" };
const LABEL_AKTION_BANNER_WIDTH_FRAC = 0.19; // fraction de la LARGEUR de l'étiquette
const LABEL_FLAT_COLOR = { bg: "#F5B700", ink: "#1a1400" };

// Palette de fond au choix (retour utilisateur explicite, 2026-08-26 : "je veux aussi
// pouvoir changer de couleur de fond pour toutes étiquettes dont le blanc") — s'applique
// aux 4 modèles ; jamais/rarement une couleur inventée : jaune = LABEL_FLAT_COLOR mesuré
// (Sinulind/Ferrero), vert/or = bandColor mesurés sur les vraies photos NORMA (Bio Sonne
// Zucchini, Pfiff Chips DLG-Prämiert). Blanc est la seule couleur ajoutée sans mesure
// directe (demande explicite de l'utilisateur, pas une déduction photo).
const LABEL_COLOR_SWATCHES = {
  jaune: { bg: LABEL_FLAT_COLOR.bg, ink: LABEL_FLAT_COLOR.ink },
  blanc: { bg: "#ffffff", ink: "#141414" },
  vert: { bg: "#8FA668", ink: "#141414" },
  or: { bg: "#C9A961", ink: "#141414" },
};

// Police condensée très grasse façon étiquette de prix réelle — EMBARQUÉE (base64, jamais
// une police système) : retour utilisateur explicite ("la police sur PC n'est pas conforme
// à la police du téléphone") — Haettenschweiler n'existe que sous Windows, donc le
// téléphone retombait sur une police système différente. Anton (Google Fonts, licence
// OFL, un seul poids très gras) est le candidat le plus proche des photos ; embarquée en
// base64 directement dans le bundle JS (jamais un fichier séparé à mettre en cache PWA à
// part) pour un rendu IDENTIQUE partout : aperçu, impression, PC, téléphone, en ligne ou
// non. "font-weight: 100 900" déclare que cette seule face couvre toute la plage de
// graisse demandée par le balisage (400 à 900) sans que le navigateur ne synthétise un
// faux gras ou ne retombe sur une police système faute de correspondance exacte.
const LABEL_FONT_BASE64 = "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";
const LABEL_FONT_FACE_CSS = `
  @font-face {
    font-family: "LabelFont";
    src: url(data:font/woff2;charset=utf-8;base64,${LABEL_FONT_BASE64}) format("woff2");
    font-weight: 100 900;
    font-style: normal;
    font-display: block;
  }
`;
const LABEL_FONT = `"LabelFont", "Haettenschweiler", Impact, "Arial Narrow", sans-serif`;

// Tailles de police calibrées par MESURE directe sur les photos fournies (analyse pixel des
// photos IMG_FFD8537F [Ferrero, famille "flat"] et IMG_120E046A [Heidelbeeren, famille
// "aktion"], hauteur de casse rapportée à la hauteur totale de l'étiquette ÷ 0,72 pour
// remonter à la taille de police) — jamais des valeurs choisies "à l'oeil", qui avaient
// produit un rendu 2 à 2,5× trop petit avec un immense vide (retour utilisateur explicite :
// "police différente, taille de texte différente, emplacement des textes différents").
// Exprimées en fraction de la hauteur du modèle, donc directement en mm de la hauteur réelle
// — proportions garanties identiques à l'aperçu ET à l'impression, quel que soit le format.
// flat15x5 (prix à côté du texte, jamais empilé dessous — mesuré sur Ferrero) vs flat5x5
// (carré, TOUT empilé verticalement — mesuré séparément sur Sinulind, IMG_740FF466) :
// mêmes proportions ne marchent PAS pour les deux, un carré empilé déborde sinon (bug réel
// trouvé : prix et cases du bas coupés en dehors de l'étiquette 5×5).
// price relevé de .38 à .44 (2026-09-12, retour utilisateur explicite avec photo réelle
// GELATO D'ITALIA : "l'emplacement du prix... ne pas semblable, la taille de la police est
// aussi différente") : à .38 le prix ne dominait qu'avec un nom de produit court sur une
// ligne (le cas testé au calibrage initial Ferrero) — sur cette photo réelle, la
// désignation ET la description tiennent chacune sur 2 lignes, et le prix reste malgré
// tout visuellement quasi aussi haut que ce bloc de texte cumulé. Vérifié par un rendu réel
// AVEC LA VRAIE POLICE embarquée (jamais une police de secours pour mesurer un
// retour-à-la-ligne — une police de secours générique est bien plus large par caractère et
// fait ligne trop tôt, comme démontré lors du premier essai raté ici). Voir aussi le
// commentaire dans buildLabelBodyHtml (flat15x5) : un bug de débordement DÉJÀ PRÉSENT à .38
// a été trouvé et corrigé pendant cette même vérification (marges/interlignes resserrés),
// sans lien avec ce changement de taille du prix.
const LABEL_FLAT15_SIZES = { brand: .08, name: .16, text: .10, price: .44, bottom: .07 };
const LABEL_FLAT5_SIZES = { brand: .065, name: .105, text: .065, price: .29, priceUnit: .05, bottom: .045 };
// Famille "aktion" (21x15 et A4) recalibrée sur 3 vraies photos NORMA (Bio Sonne Zucchini,
// Saubermax WC-Farbspüler, Pfiff Chips Paprika DLG-Prämiert, 2026-08-26) — l'ancien bandeau
// vertical "Aktion! Mehr fürs Geld." mesuré sur TRIMM Sirup ne correspondait à AUCUNE de ces
// 3 étiquettes réelles (retour utilisateur explicite : "ne respecte en rien le véritable
// format de Norma"). Structure réelle : bandeau coloré HORIZONTAL en haut (couleur du badge
// actif) avec badge(s) de certification quand il y en a, sinon aplat plein jaune sans
// bandeau ; texte toujours CENTRÉ ; prix énorme aligné à GAUCHE, poussé en bas (marge auto) ;
// pied de page 3 colonnes (date / prix au kilo / VE+article) partagé avec flat5x5/flat15x5.
// banner/bannerSub mesurés sur TRIMM Sirup kalorienarm (photo réelle NORMA, 2026-08-26) —
// bandeau rouge/jaune "Aktion! Mehr fürs Geld." horizontal en haut de l'étiquette, jamais
// l'ancien bandeau vertical (mesuré sur une photo qui ne correspondait à aucune vraie
// étiquette NORMA, retiré). Le champ aktionBanner existait déjà en base (défaut true) mais
// n'était plus rendu faute de référence — cette photo confirme le rendu réel.
// banner/bannerSub/a4Badge recalibrés sur la photo DAUNASOFT en haute résolution
// (2026-09-10, cas SANS badge de certification) : le mot "Aktion!" reste nettement plus
// petit que le prix (toujours le plus gros élément) ; a4Badge = diamètre du médaillon rond
// qui déborde du coin du bandeau (jamais un cercle inline dans le texte, rendu précédent
// incorrect). name réduit de .09 à .076 (vérifié par rendu réel) : à .09, un nom de produit
// d'un seul mot un peu long ("Taschentücher") déborde du cadre — .lbl-page a overflow:hidden,
// jamais de réduction automatique de police, donc la taille doit rester sûre par construction.
const LABEL_AKTION_SIZES = { brand: .035, name: .076, text: .028, price: .26, bottom: .026, badge: .11, banner: .052, bannerSub: .024, a4Badge: .08 };
// aktion15x5 (150×50mm, paysage — mesuré sur TRIMM Sirup kalorienarm, 2e photo recadrée,
// 2026-08-26) : même disposition que flat15x5 (texte à gauche empilé, prix à droite, bande
// du bas) mais avec le ruban "Aktion!" en haut qui réduit l'espace disponible pour le reste
// — tailles réduites par rapport à LABEL_FLAT15_SIZES en conséquence.
const LABEL_AKTION15_SIZES = { banner: .19, bannerSub: .075, brand: .07, name: .13, text: .08, price: .30, bottom: .06 };
// rayon15x7 (150×65mm, paysage — "Internationale Weine", photo réelle NORMA, corrigé le
// 2026-08-26 : ce n'est PAS une pancarte séparateur pure, c'est une étiquette de prix comme
// les autres, juste photographiée vide sur le rayon — même champs que flat15x5 dans le
// corps, avec en plus un petit bandeau titre/rayon en haut). Bandeau réduit à ~22% de la
// hauteur (mesure initiale à 56% jugée "trop grande" par retour utilisateur) pour laisser la
// place au vrai contenu produit.
const LABEL_RAYON_SIZES = { brand: .06, name: .12, text: .075, price: .27, bottom: .052 };
const mm = (fraction, heightMm) => (fraction * heightMm).toFixed(2) + "mm";

// Bloc CSS partagé aux 4 modèles (classes .lbl-*) — une seule fois dans buildLabelPrintHtml/
// l'aperçu (voir plus bas dans le composant), jamais dupliqué par modèle.
const LABEL_ENGINE_CSS = `
  ${LABEL_FONT_FACE_CSS}
  .lbl-page { box-sizing: border-box; font-family: ${LABEL_FONT}; -webkit-print-color-adjust: exact; print-color-adjust: exact; overflow: hidden; }
  .lbl-page * { box-sizing: border-box; }
  .lbl-bottom-row { width: 100%; display: flex; align-items: flex-end; justify-content: space-between; gap: 3mm; }
  .lbl-bottom-row > span:empty { visibility: hidden; }
  /* Bande de cases ACCOLÉES (un seul cadre continu, jamais 2 pastilles séparées avec un
     espace entre elles) — c'est la vraie structure mesurée sur les photos (Ferrero,
     Sinulind : "VE 28 | 33239" partage une seule bordure, pas deux cases distinctes). */
  .lbl-strip { display: inline-flex; border: 0.35mm solid currentColor; }
  .lbl-strip.lbl-strip-full { display: flex; width: 100%; }
  .lbl-strip > span { padding: 0.4mm 2mm; white-space: nowrap; }
  .lbl-strip.lbl-strip-full > span { flex: 1; text-align: center; }
  .lbl-strip > span + span { border-left: 0.35mm solid currentColor; }
  .lbl-bottom-row.lbl-left-row { justify-content: flex-start; gap: 8mm; }
  /* Famille aktionA4 SANS badge de certification — mesurée sur une vraie photo NORMA
     (DAUNASOFT Taschentücher Triobox, 2026-09-10, relecture en haute résolution après un
     premier rendu jugé "ne ressemble en rien à la photo" par l'utilisateur). Structure
     réelle à 2 couches, pas une simple page blanche : un CADRE gris foncé (fait partie du
     visuel imprimé, pas juste un présentoir physique — les 2 codes du bas sont imprimés
     DANS ce gris) contenant un panneau blanc inséré avec marge. Le bandeau "Aktion! Mehr
     fürs Geld." ne fait PAS toute la largeur du panneau blanc (~80%, aligné à gauche) et un
     médaillon rond séparé (cercle blanc, anneau gris, "!" rouge) déborde du coin haut-droit
     du bandeau — jamais un simple cercle inline dans le texte (rendu précédent incorrect). */
  .lbl-aktion-a4-card { position: relative; }
  .lbl-aktion-a4-white {
    position: relative; background: #fff; border-radius: 2.5mm;
    box-shadow: 0 0.6mm 1.8mm rgba(0,0,0,.25);
    display: flex; flex-direction: column;
  }
  .lbl-aktion-banner {
    position: relative; flex-shrink: 0; align-self: flex-start; display: flex; flex-direction: column;
    align-items: center; justify-content: center; text-align: center;
    background: ${LABEL_AKTION_BANNER.bg}; border: 0.9mm solid ${LABEL_AKTION_BANNER.edge};
    border-radius: 1mm; color: #fff;
  }
  .lbl-aktion-banner .lbl-aktion-title { font-weight: 900; line-height: .95; text-transform: capitalize; }
  .lbl-aktion-banner .lbl-aktion-sub { font-weight: 700; line-height: 1.25; margin-top: 4%; }
  .lbl-aktion-a4-badge {
    position: absolute; border-radius: 50%; background: #fff; color: ${LABEL_AKTION_BANNER.bg};
    border: 0.4mm solid #ccc; box-shadow: 0 0.4mm 1mm rgba(0,0,0,.3);
    display: flex; align-items: center; justify-content: center; font-weight: 900; line-height: 1;
  }
  .lbl-aktion-a4-bottomcodes { display: flex; justify-content: space-between; color: #1c1c1a; font-weight: 700; }
  /* Bande de logos de certification (Bio/DLG), optionnelle — badges génériques (jamais une
     reproduction des vrais logos, protégés). */
  .lbl-logo-rail { position: absolute; top: 0; right: 0; display: flex; flex-direction: column; gap: 2.5mm; align-items: center; }
  .lbl-logo-badge { border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  /* Bandeau badges famille "aktion" (mesuré Bio Sonne Zucchini / Pfiff Chips DLG-Prämiert) —
     horizontal, pleine largeur, badge(s) centrés + légende sous le badge. */
  .lbl-akt2-band { display: flex; flex-direction: column; align-items: center; justify-content: center; flex-shrink: 0; color: #fff; }
  .lbl-akt2-badgerow { display: flex; gap: 3mm; align-items: center; justify-content: center; }
  .lbl-akt2-caption { text-transform: uppercase; letter-spacing: .04em; margin-top: 1.5mm; }
  /* Bandeau "Aktion! Mehr fürs Geld." (mesuré sur TRIMM Sirup) — rectangle rouge liseré
     jaune, pleine largeur, texte aligné à gauche ; le jaune déborde légèrement en dessous
     du rouge et forme une pointe en biseau vers la droite (effet ruban), reproduit via
     clip-path sur la couche jaune, le rouge restant un simple rectangle par-dessus. */
  .lbl-akt2-ribbon { position: relative; flex-shrink: 0; }
  .lbl-akt2-ribbon-yellow {
    position: absolute; inset: 0; background: ${LABEL_AKTION_BANNER.edge};
    clip-path: polygon(0 0, 100% 0, 100% 55%, 82% 100%, 0 100%);
  }
  .lbl-akt2-ribbon-red {
    position: relative; background: ${LABEL_AKTION_BANNER.bg};
    display: flex; flex-direction: column; justify-content: center; color: #fff; text-align: left;
  }
  .lbl-akt2-ribbon .lbl-akt2-title { font-weight: 900; line-height: .95; }
  .lbl-akt2-ribbon .lbl-akt2-sub { font-weight: 700; line-height: 1.2; margin-top: 1mm; }
  /* Pancarte séparateur de rayon (mesurée sur "Internationale Weine", photo réelle NORMA) —
     bandeau titre/rayon réduit en haut (titre libre en script doré, décors raisin/feuille de
     vigne dans les coins), corps = vraie étiquette de prix (mêmes champs que flat15x5) avec
     une bande texturée bois conservée en accent à gauche du corps — retour utilisateur
     explicite (2026-08-26) : bandeau initial à 56% "trop grande", et le corps doit porter les
     mêmes champs produit que les autres modèles, pas rester vide. */
  .lbl-rayon-page { display: flex; flex-direction: column; background: #fff; overflow: hidden; }
  .lbl-rayon-header {
    position: relative; flex: 0 0 22%; display: flex; align-items: center; justify-content: center;
    background: linear-gradient(180deg, #4a3624 0%, #2a1c12 100%);
    padding: 0 12mm;
  }
  .lbl-rayon-title {
    margin: 0; color: #f0d4a0; font-family: Georgia, "Times New Roman", serif; font-style: italic;
    font-weight: 700; font-size: 5mm; letter-spacing: .02em; text-align: center; line-height: 1.1;
    text-shadow: 0 1px 2px rgba(0,0,0,.45);
  }
  .lbl-rayon-deco { position: absolute; top: 50%; transform: translateY(-50%); font-size: 4.5mm; opacity: .9; filter: drop-shadow(0 1px 1px rgba(0,0,0,.4)); }
  .lbl-rayon-deco.lbl-rayon-grapes { left: 4mm; }
  .lbl-rayon-deco.lbl-rayon-leaf { right: 4mm; transform: translateY(-50%) scaleX(-1); }
  .lbl-rayon-body { position: relative; flex: 1; display: flex; background: #fff; color: #141414; }
  .lbl-rayon-wood {
    width: 10mm; height: 100%; flex-shrink: 0;
    background: repeating-linear-gradient(180deg, #8a5a34 0mm 3mm, #79492a 3mm 6mm);
    border-right: 0.4mm solid #5c3a20;
  }
`;

// Documents Warenaustausch (formulaire de suivi + étiquette d'envoi) — même moteur PDF que
// les étiquettes (exportHtmlToPdf) mais contenu très différent (document A4 texte/tableau,
// pas une étiquette produit) : feuille de style autonome dédiée, style document interne
// classique (mêmes conventions visuelles que les autres exports de l'app — voir .im-page/
// .im-fieldtable ailleurs dans le composant — jamais la palette produit crème/couleurs vives
// des autres modules, ce document reste utilitaire).
const WA_DOC_CSS = `
  .wadoc-page { box-sizing: border-box; width: 210mm; min-height: 297mm; padding: 16mm; font-family: Arial, sans-serif; color: #111827; background: #fff; }
  .wadoc-page * { box-sizing: border-box; }
  .wadoc-header { display: flex; align-items: flex-end; justify-content: space-between; border-bottom: 2px solid #111827; padding-bottom: 8px; margin-bottom: 16px; }
  .wadoc-eyebrow { margin: 0; font-size: 10px; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; color: #6b7280; }
  .wadoc-header h1 { margin: 2px 0 0; font-size: 20px; }
  .wadoc-meta { font-size: 12px; color: #6b7280; font-weight: 700; }
  .wadoc-note { font-size: 12px; color: #374151; margin: 0 0 10px; }
  .wadoc-infotable { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 14px; }
  .wadoc-infotable th, .wadoc-infotable td { border: 1px solid #d1d5db; padding: 7px 8px; text-align: left; }
  .wadoc-infotable th { background: #0f172a; color: #fff; width: 160px; }
  .wadoc-artable { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 14px; }
  .wadoc-artable th, .wadoc-artable td { border: 1px solid #d1d5db; padding: 6px 7px; text-align: left; }
  .wadoc-artable th { background: #0f172a; color: #fff; font-size: 10px; text-transform: uppercase; letter-spacing: .03em; }
  .wadoc-artable td.num, .wadoc-artable th.num { text-align: right; font-variant-numeric: tabular-nums; }
  .wadoc-diff { color: #b91c1c; font-weight: 800; }
  .wadoc-total-row td { font-weight: 800; border-top: 2px solid #111827; }
  .wadoc-sigrow { display: flex; justify-content: space-between; gap: 20px; margin-top: 24px; }
  .wadoc-sigbox { flex: 1; }
  .wadoc-sigbox .lbl { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .05em; color: #6b7280; margin: 0 0 6px; }
  .wadoc-sigbox img { height: 60px; object-fit: contain; display: block; }
  .wadoc-sigbox .name { font-size: 12px; font-weight: 700; margin-top: 4px; }
  .wadoc-sticker { box-sizing: border-box; width: 210mm; height: 297mm; padding: 24mm 18mm; font-family: Arial, sans-serif; color: #111827; background: #fff; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }
  .wadoc-sticker .kind { font-size: 22px; font-weight: 900; letter-spacing: .06em; text-transform: uppercase; color: #6b7280; margin: 0 0 26px; }
  .wadoc-sticker .route { font-size: 46px; font-weight: 900; line-height: 1.25; margin: 0; }
  .wadoc-sticker .meta { margin-top: 34px; font-size: 15px; color: #374151; }
`;

// Rapport de filiale — export PDF réel (exportHtmlToPdf, même moteur que WA_DOC_CSS
// ci-dessus et l'Étiquettes) : retour utilisateur explicite (2026-08-26, capture annotée) —
// le bouton imprimante utilisait encore window.print(), qui ajoute l'en-tête/pied de page du
// navigateur (URL, date) sur la feuille. Feuille de style autonome dédiée, reprise à
// l'identique des règles .frpx-* qui vivaient avant dans le stylesheet d'impression partagé.
const FRPX_DOC_CSS = `
  .frpx-page { box-sizing: border-box; width: 210mm; min-height: 297mm; padding: 24mm 16mm; font-family: Arial, sans-serif; color: #111827; background: #fff; }
  .frpx-page * { box-sizing: border-box; }
  .frpx-header { border-bottom: 2px solid #111827; padding-bottom: 8px; margin-bottom: 18px; }
  .frpx-eyebrow { margin: 0; font-size: 10px; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; color: #6b7280; }
  .frpx-header h1 { margin: 2px 0 0; font-size: 20px; }
  .frpx-meta { font-size: 12px; color: #6b7280; font-weight: 700; }
  .frpx-table { width: 100%; border-collapse: collapse; font-size: 12px; margin: 8px 0 16px; }
  .frpx-table th, .frpx-table td { border: 1px solid #d1d5db; padding: 7px 8px; text-align: left; }
  .frpx-table th { background: #0f172a; color: #fff; }
  .frpx-sig { margin-top: 24px; }
  .frpx-sig img { max-width: 220px; max-height: 80px; border-bottom: 1px solid #111827; }
`;

// Chantier "vrais PDF partout" (retour utilisateur explicite, 2026-08-26 : "on n'a pas plus
// de http sur la feuille" + confirmation "Oui" pour migrer le reste de l'app) — même moteur
// exportHtmlToPdf que Étiquettes/Warenaustausch/Rapport de filiale ci-dessus, pour chacun des
// générateurs d'impression restants. Chaque feuille de style ci-dessous reprend à l'identique
// les règles .xxx-* qui vivaient avant dans le stylesheet d'impression partagé (voir
// clearOtherPrintPortals, désormais supprimé), avec une largeur physique explicite (mm) sur
// la page racine — remplace l'ancien `@page { size: A4 landscape/portrait }` qui ne
// s'appliquait qu'au vrai dialogue d'impression système, jamais utilisé ici.

const MEX_DOC_CSS = `
  .mex-page { box-sizing: border-box; width: 210mm; padding: 24mm 16mm; font-family: Arial, sans-serif; color: #111827; background: #fff; }
  .mex-page * { box-sizing: border-box; }
  .mex-page h1 { margin: 0 0 4px; font-size: 20px; }
  .mex-sub { margin: 0 0 20px; color: #64748b; font-size: 13px; }
  .mex-table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .mex-table th, .mex-table td { border: 1px solid #d1d5db; padding: 8px; text-align: left; }
  .mex-table th { background: #0f172a; color: #fff; }
`;

const RPX_DOC_CSS = `
  .rpx-page { box-sizing: border-box; width: 210mm; padding: 24mm 16mm; font-family: Arial, sans-serif; color: #111827; background: #fff; }
  .rpx-page * { box-sizing: border-box; }
  .rpx-header { display: flex; align-items: flex-end; justify-content: space-between; border-bottom: 2px solid #111827; padding-bottom: 8px; margin-bottom: 18px; }
  .rpx-eyebrow { margin: 0; font-size: 10px; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; color: #6b7280; }
  .rpx-header h1 { margin: 2px 0 0; font-size: 20px; }
  .rpx-meta { font-size: 12px; color: #6b7280; font-weight: 700; }
  .rpx-cat { font-size: 14px; margin: 18px 0 8px; }
  .rpx-count { color: #6b7280; font-weight: 400; }
  .rpx-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 6px; }
  .rpx-table th, .rpx-table td { border: 1px solid #d1d5db; padding: 7px 8px; text-align: left; }
  .rpx-table th { background: #0f172a; color: #fff; }
  .rpx-empty { color: #6b7280; font-size: 13px; }
`;

const IM_DOC_CSS = `
  .im-page { box-sizing: border-box; width: 210mm; padding: 24mm 16mm; font-family: Arial, sans-serif; color: #111827; background: #fff; }
  .im-page * { box-sizing: border-box; }
  .im-header { display: flex; align-items: flex-end; justify-content: space-between; border-bottom: 2px solid #111827; padding-bottom: 8px; margin-bottom: 16px; }
  .im-eyebrow { margin: 0; font-size: 10px; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; color: #6b7280; }
  .im-header h1 { margin: 2px 0 0; font-size: 20px; }
  .im-meta { font-size: 12px; color: #6b7280; font-weight: 700; }
  .im-section { font-size: 14px; margin: 18px 0 8px; }
  .im-fieldtable, .im-qtable { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 10px; }
  .im-fieldtable th, .im-fieldtable td, .im-qtable td { border: 1px solid #d1d5db; padding: 7px 8px; text-align: left; }
  .im-fieldtable th { background: #0f172a; color: #fff; width: 220px; }
  .im-qtable td:first-child { width: 70%; }
  .im-ok { color: #15803d; font-weight: 800; }
  .im-bad { color: #b91c1c; font-weight: 800; }
  .im-note { font-size: 12px; color: #374151; margin: 0 0 10px; }
  .im-artlist { font-size: 12px; margin: 0 0 10px 18px; padding: 0; }
  .im-sigrow { display: flex; gap: 24px; margin-top: 20px; }
  .im-sigbox { flex: 1; }
  .im-sigbox span { display: block; font-size: 11px; color: #6b7280; font-weight: 700; margin-bottom: 6px; }
  .im-sigbox img { max-width: 100%; max-height: 70px; border-bottom: 1px solid #111827; }
  .im-sigempty { height: 50px; border-bottom: 1px solid #111827; }
`;

const IIP_DOC_CSS = `
  .iip-page { box-sizing: border-box; width: 210mm; font-family: Arial, sans-serif; color: #0a0a0a; padding: 24mm 16mm; background: #fff; }
  .iip-page * { box-sizing: border-box; }
  .iip-header { display: flex; align-items: flex-end; justify-content: space-between; border-bottom: 2px solid #0a0a0a; padding-bottom: 8px; margin-bottom: 12px; }
  .iip-eyebrow { margin: 0; font-size: 10px; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; color: #555; }
  .iip-header h1 { margin: 3px 0 0; font-size: 20px; font-weight: 900; max-width: 480px; }
  .iip-badge { display: inline-block; padding: 5px 14px; border-radius: 99px; color: #fff; font-weight: 900; font-size: 11px; }
  .iip-infotable { width: 100%; border-collapse: collapse; margin-bottom: 14px; font-size: 11px; }
  .iip-infotable th { text-align: left; background: #f3f3f1; font-weight: 800; padding: 6px 10px; width: 15%; border: 1px solid #ddd; }
  .iip-infotable td { padding: 6px 10px; border: 1px solid #ddd; width: 35%; }
  .iip-content { border: 1px solid #ddd; border-radius: 4px; padding: 14px 16px; margin-bottom: 16px; font-size: 12.5px; line-height: 1.6; background: #fafaf8; }
  .iip-content p { margin: 0 0 8px; }
  .iip-content p:last-child { margin-bottom: 0; }
  .iip-section-title { margin: 0 0 6px; font-size: 12px; font-weight: 900; text-transform: uppercase; letter-spacing: .04em; }
  .iip-table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
  .iip-table th, .iip-table td { border: 1px solid #ccc; padding: 5px 9px; text-align: left; }
  .iip-table thead th { background: #0f172a; color: #fff; }
  .iip-table tbody tr:nth-child(even) { background: #f7f7f5; }
  .iip-footer-note { margin-top: 10px; font-size: 8.5px; color: #666; }
`;

const WP_DOC_CSS = `
  .wp-page { box-sizing: border-box; width: 297mm; font-family: Arial, sans-serif; padding: 12mm; color: #0a0a0a; background: #fff; }
  .wp-page * { box-sizing: border-box; }
  .wp-header { display: flex; align-items: flex-end; justify-content: space-between; border-bottom: 2px solid #0a0a0a; padding-bottom: 6px; margin-bottom: 10px; }
  .wp-eyebrow { margin: 0; font-size: 9px; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; color: #555; }
  .wp-header h1 { margin: 2px 0 0; font-size: 17px; font-weight: 900; }
  .wp-meta { display: flex; align-items: center; gap: 10px; font-size: 11px; font-weight: 700; }
  .wp-badge { padding: 3px 10px; border-radius: 99px; background: #fde68a; color: #7c4a00; font-weight: 900; font-size: 9.5px; }
  .wp-badge.published { background: #bbf7d0; color: #14532d; }
  .wp-table { width: 100%; border-collapse: collapse; font-size: 9.5px; table-layout: fixed; }
  .wp-table th, .wp-table td { border: 1px solid #999; padding: 4px 3px; text-align: center; vertical-align: middle; }
  .wp-table thead th { background: #0f172a; color: #fff; font-size: 9px; }
  .wp-table tbody th { text-align: left; padding-left: 6px; font-size: 9.5px; }
  .wp-table tbody th small { display: block; font-weight: 400; color: #555; }
  .wp-table tfoot th { text-align: left; padding-left: 6px; white-space: normal; word-break: break-word; line-height: 1.15; }
  .wp-cell { line-height: 1.3; }
  .wp-time { display: block; font-weight: 800; }
  .wp-break { display: block; font-size: 8px; color: #555; }
  .wp-hours-row { display: flex; align-items: baseline; justify-content: center; gap: 4px; }
  .wp-hours { font-size: 8px; color: #555; }
  .wp-lf-badge { font-size: 8px; font-weight: 800; color: #d97706; }
  .wp-azubi-badge { font-size: 7px; font-weight: 700; color: #666; }
  .wp-lf { font-weight: 700; color: #d97706; }
  .wp-total { font-weight: 900; }
  .wp-footer-note { margin-top: 8px; font-size: 8px; color: #666; }
  .wp-summary { display: flex; gap: 8px; margin: 0 0 8px; }
  .wp-summary-card { flex: 1; border-radius: 8px; padding: 5px 8px; text-align: center; }
  .wp-summary-card strong { display: block; font-size: 13px; line-height: 1.2; }
  .wp-summary-card span { font-size: 6.5px; text-transform: uppercase; letter-spacing: .03em; font-weight: 700; }
  .wp-legend { display: flex; flex-wrap: wrap; gap: 6px 12px; margin: 0 0 8px; font-size: 7px; }
  .wp-legend-item { display: flex; align-items: center; gap: 4px; }
  .wp-legend-swatch { width: 9px; height: 9px; border-radius: 3px; display: inline-block; flex: 0 0 auto; }
  .wp-rules { margin-top: 6px; font-size: 7px; color: #444; line-height: 1.5; }
  .wp-page[data-density="lg"] .wp-table { font-size: 8px; }
  .wp-page[data-density="lg"] .wp-table th, .wp-page[data-density="lg"] .wp-table td { padding: 2px 2px; }
  .wp-page[data-density="lg"] .wp-cell .wp-time { font-size: 7.5px; }
  .wp-page[data-density="xl"] .wp-table { font-size: 6.8px; }
  .wp-page[data-density="xl"] .wp-table th, .wp-page[data-density="xl"] .wp-table td { padding: 1px 2px; }
  .wp-page[data-density="xl"] .wp-cell .wp-time { font-size: 6.5px; }
  .wp-page[data-density="xl"] .wp-break, .wp-page[data-density="xl"] .wp-hours, .wp-page[data-density="xl"] .wp-lf-badge, .wp-page[data-density="xl"] .wp-azubi-badge { font-size: 6px; }
`;

const HEP_DOC_CSS = `
  .hep-page { box-sizing: border-box; width: 297mm; padding: 10mm; font-family: ${FONT}; color: #0a0a0a; background: #fff; }
  .hep-page * { box-sizing: border-box; }
  .hep-header { display: flex; align-items: flex-end; justify-content: space-between; border-bottom: 2px solid #0a0a0a; padding-bottom: 4px; margin-bottom: 6px; }
  .hep-eyebrow { margin: 0; font-size: 8px; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; color: #555; }
  .hep-header h1 { margin: 1px 0 0; font-size: 15px; font-weight: 900; }
  .hep-store { font-size: 10px; font-weight: 800; }
  .hep-section-title { margin: 8px 0 3px; font-size: 8.5px; font-weight: 900; text-transform: uppercase; letter-spacing: .06em; }
  .hep-table { width: 100%; border-collapse: collapse; font-size: 6px; table-layout: fixed; line-height: 1; }
  .hep-table th, .hep-table td { border: 1px solid #999; padding: 0 2px; text-align: center; line-height: 1; overflow-wrap: break-word; word-break: break-word; hyphens: auto; }
  .hep-group-th { writing-mode: vertical-rl; text-orientation: mixed; font-weight: 900; background: #ececec; width: 9px; padding: 2px 0; font-size: 6px; }
  .hep-item-label { text-align: left; font-size: 5.7px; width: 225px; line-height: 1.15; padding: 1px 3px; white-space: normal; }
  .hep-max { font-weight: 400; opacity: .7; }
  .hep-daycol { height: 12px; }
  .hep-daycol span { display: block; font-size: 4.5px; text-transform: uppercase; opacity: .7; line-height: 1; }
  .hep-daycol strong { display: block; font-size: 6.5px; font-variant-numeric: tabular-nums; line-height: 1.1; }
  .hep-daycol.hep-closed { background: #ececec; }
  .hep-cell { font-variant-numeric: tabular-nums; height: 7px; }
  .hep-cell.hep-ok { color: #16803d; font-weight: 900; }
  .hep-cell.hep-bad { color: #b91c1c; font-weight: 900; background: #fde2e2; }
  .hep-cell.hep-closed { background: #ececec; }
  .hep-cell.hep-dash { color: #999; }
  .hep-table-weekly { table-layout: auto; }
  .hep-table-weekly .hep-item-label { width: auto; }
  .hep-signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 6px; }
  .hep-sig-row { display: flex; justify-content: space-between; gap: 8px; font-size: 7px; padding: 1.5px 0; border-bottom: 1px solid #ddd; }
  .hep-sig-empty { font-size: 7px; color: #777; margin: 2px 0; }
`;

// Reporting/PLU Quiz/Inventur/Bestandskontrolle/Stundenleistung/Arbeitszeitliste — TOUS le
// même patron .rep-page (voir printReportWithTitle) + Arbeitszeitliste réutilise .rep-page/
// .rep-mm-table en plus de ses propres .azl-* (voir printAzlDocument, portail séparé mais
// mêmes classes CSS) — une seule feuille de style pour cette famille entière.
const REP_DOC_CSS = `
  .rep-page { box-sizing: border-box; width: 297mm; font-family: Arial, sans-serif; color: #0a0a0a; padding: 10mm; background: #fff; }
  .rep-page * { box-sizing: border-box; }
  .rep-header { display: flex; align-items: flex-end; justify-content: space-between; border-bottom: 2px solid #0a0a0a; padding-bottom: 8px; margin-bottom: 14px; }
  .rep-eyebrow { margin: 0; font-size: 10px; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; color: #555; }
  .rep-header h1 { margin: 3px 0 0; font-size: 19px; font-weight: 900; }
  .rep-badge { padding: 5px 14px; border-radius: 99px; background: #1eb85f; color: #fff; font-weight: 900; font-size: 11px; }
  .rep-table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 14px; }
  .rep-table th, .rep-table td { border: 1px solid #ccc; padding: 6px 9px; text-align: center; }
  .rep-table thead th { background: #0f172a; color: #fff; }
  .rep-table tbody th { text-align: left; }
  .rep-table tbody tr:nth-child(even) { background: #f7f7f5; }
  .rep-total { font-weight: 800; }
  .rep-grand-total { font-size: 14px; font-weight: 900; margin: 0 0 8px; }
  .rep-section-title { margin: 4px 0 6px; font-size: 12px; font-weight: 900; text-transform: uppercase; letter-spacing: .04em; }
  .rep-plu-table { table-layout: fixed; font-size: 8.5px; margin-bottom: 10px; }
  .rep-plu-table th, .rep-plu-table td { padding: 2px 5px; border: 1px solid #ccc; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .rep-plu-table thead th { background: #0f172a; color: #fff; }
  .rep-plu-table tbody tr:nth-child(even) { background: #f7f7f5; }
  .rep-mm-page { padding: 6mm; }
  .rep-mm-table { font-size: 8px; table-layout: fixed; }
  .rep-mm-table th, .rep-mm-table td { border: 1px solid #999; padding: 3px 2px; text-align: center; }
  .rep-mm-table thead th { background: #0f172a; color: #fff; font-size: 7.5px; font-weight: 700; }
  .rep-mm-kw { font-weight: 900; background: #f3f3f1; }
  .rep-mm-empty { padding: 16px; font-size: 11px; color: #666; text-align: center; }
  .rep-row-closed td { background: #f0f0ee; color: #777; font-style: italic; }
  .rep-footer-note { margin-top: 10px; font-size: 8.5px; color: #666; }
  .rep-table-weekly tbody th { vertical-align: middle; }
  .rep-table-weekly .rep-subrow { text-align: left; font-size: 9.5px; font-weight: 700; color: #666; text-transform: uppercase; letter-spacing: .03em; }
  .rep-table-weekly tbody tr:nth-child(even) { background: none; }
  /* Fond appliqué CELLULE PAR CELLULE, jamais sur le <tr> (retour utilisateur réel,
     2026-08-28) — html2canvas mesure mal la cellule <th rowspan="2"> quand SA LIGNE porte
     un background : le nom de rayon ressortait tronqué en haut, uniquement sur les lignes
     grisées (bug reproduit et confirmé : jamais sur les lignes blanches). Même rendu visuel
     de bandes alternées, sans le bug de mesure. */
  .rep-table-weekly tbody tr:nth-child(4n+1) > td, .rep-table-weekly tbody tr:nth-child(4n+1) > th,
  .rep-table-weekly tbody tr:nth-child(4n+2) > td, .rep-table-weekly tbody tr:nth-child(4n+2) > th
  { background: #f7f7f5; }
  /* Ligne de total PAR JOUR (retour utilisateur 2026-09-08, "Summe Tag in Euro" du
     formulaire papier) — distincte visuellement des lignes d'articles (fond sombre, comme
     l'en-tête, jamais confondue avec une simple ligne de données). */
  .rep-tfoot-row th, .rep-tfoot-row td { background: #0f172a !important; color: #fff; font-weight: 900; }
  /* Format "presque identique à Norma" (retour utilisateur 2026-09-08) — colonnes fixes
     (Nr/Art-Nr/Artikel/Backprogramm/Inhalt/VE/Preis) + Stück|€ CÔTE À CÔTE par jour (jamais
     empilés en 2 lignes, seul moyen de tenir ~24 articles sur 1 page A4 paysage sans page
     multiple — voir naturalHeight/scrollHeight dans rasterizeElement : aucun découpage
     automatique n'existe pour ce pipeline mono-page, donc la densité doit suffire seule). */
  .rep-page-norma { padding: 8mm; }
  .rep-table-norma { font-size: 9px; }
  .rep-table-norma th, .rep-table-norma td { border: 1px solid #bbb; padding: 3px 3.5px; text-align: center; white-space: nowrap; }
  .rep-table-norma thead th { background: #0f172a; color: #fff; font-size: 8.3px; font-weight: 700; }
  .rep-table-norma tbody th { text-align: left; font-weight: 700; white-space: normal; max-width: 60mm; }
  .rep-table-norma tbody tr:nth-child(even) td, .rep-table-norma tbody tr:nth-child(even) th { background: #f7f7f5; }
  .azl-mm-table th:first-child, .azl-mm-table td:first-child { width: 74px; text-align: left; }
  .azl-personalnr { font-size: 6.5px; font-weight: 400; color: #666; }
  .azl-cell { display: flex; flex-direction: column; align-items: center; line-height: 1.15; min-height: 18px; justify-content: center; }
  .azl-cell-top { font-size: 6px; font-weight: 700; color: #b45300; min-height: 6px; }
  .azl-cell-mid { font-size: 8px; font-weight: 700; }
  .azl-cell-bot { font-size: 6px; color: #666; min-height: 6px; }
  .azl-cell-sun { background-image: repeating-linear-gradient(135deg, rgba(0,0,0,.1) 0px, rgba(0,0,0,.1) 1px, transparent 1px, transparent 6px); }
  .azl-legend { display: flex; flex-wrap: wrap; gap: 4px 14px; font-size: 10px; margin: 0 0 12px; }
  .azl-legend-item strong { display: inline-block; min-width: 22px; }
`;

// Badges de certification GÉNÉRIQUES (feuille pour "Bio", médaille pour "Qualité") — jamais
// une reproduction des vrais logos Bio UE / marque bio du magasin / DLG-Prämiert (marques
// déposées, hors de portée sans les fichiers officiels) : deux SVG inline simples, activables
// par l'utilisateur, qui jouent le même rôle visuel (bande de badges empilés) vue sur les
// photos réelles (Bio Zucchini, Chips Pfiff DLG-Prämiert).
const LABEL_LOGO_BADGES = {
  // bandColor : couleur du bandeau supérieur de la famille "aktion" quand ce badge est
  // actif — mesurée sur les vraies photos de référence NORMA (Bio Sonne Zucchini → vert
  // olive du bandeau ; Pfiff Chips Paprika, DLG-Prämiert → or/tan du bandeau). Jamais les
  // vrais logos Bio-Siegel EU / DLG (trademarks) : icône générique de la même famille de
  // couleur, comme déjà pratiqué ici avant ce chantier.
  bio: {
    labelKey: "labels.field.logoBio", label: "Bio", bandColor: "#8FA668",
    render: (h, sizeFraction) => `<div class="lbl-logo-badge" style="width:${mm(sizeFraction, h)};height:${mm(sizeFraction, h)};background:#1EB85F;">
      <svg viewBox="0 0 24 24" width="60%" height="60%" fill="none"><path d="M4 20c8 0 14-6 14-14 0 0-14 0-14 14z" fill="#fff"/><path d="M5.5 18.5c3.5-3.5 7-7 12-11.5" stroke="#1EB85F" stroke-width="1.4"/></svg>
    </div>`,
  },
  dlg: {
    labelKey: "labels.field.logoDlg", label: "Qualité (DLG)", bandColor: "#C9A961",
    render: (h, sizeFraction) => `<div class="lbl-logo-badge" style="width:${mm(sizeFraction, h)};height:${mm(sizeFraction, h)};background:#D97706;">
      <svg viewBox="0 0 24 24" width="62%" height="62%" fill="#fff"><path d="M12 4.2l1.4 2.9 3.2.5-2.3 2.3.5 3.2-2.8-1.5-2.8 1.5.5-3.2-2.3-2.3 3.2-.5z"/></svg>
    </div>`,
  },
};

function buildLabelBodyHtml(templateId, draft) {
  const d = draft || emptyLabelDraft();
  const marque = escapeLabelHtml(d.marque);
  const designation = escapeLabelHtml(d.designation);
  const description = escapeLabelMultiline(d.description);
  const conditionnement = escapeLabelMultiline(d.conditionnement);
  const prix = escapeLabelHtml(d.prix);
  const prixDeBase = escapeLabelHtml(d.prixDeBase);
  const ve = escapeLabelHtml(d.ve);
  const numeroArticle = escapeLabelHtml(d.numeroArticle);
  const dateDebut = escapeLabelHtml(d.dateDebut);
  const rayonTitle = escapeLabelHtml(d.rayonTitle);
  const ean = String(d.ean ?? "").trim(); // jamais échappé via escapeLabelHtml : consommé par buildEanBarcodeSvg, pas injecté tel quel dans le HTML

  if (templateId === "niimbot5x3") {
    // Format Niimbot (chantier 2026-08-28) — le SEUL que l'imprimante physique de
    // l'utilisateur peut imprimer. Volontairement minimal (pas les 9 champs génériques) :
    // à 50×30mm, tout le contenu doit rester lisible ET le code-barres doit rester assez
    // grand pour rester scannable après impression thermique N&B (voir printLabelViaBluetooth).
    const h = LABEL_FORMATS.niimbot5x3.heightMm;
    const barcodeSvg = buildEanBarcodeSvg(ean);
    return `<div class="lbl-page" style="width:50mm;height:30mm;background:#fff;color:#000;padding:1.5mm;box-sizing:border-box;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;overflow:hidden;">
      ${designation ? `<div style="font-size:${mm(.13, h)};font-weight:500;line-height:1.05;max-height:${mm(.28, h)};overflow:hidden;flex-shrink:0;margin-bottom:0.4mm;">${designation}</div>` : ""}
      ${barcodeSvg ? `<div style="width:100%;max-width:100%;min-height:0;overflow:hidden;">${barcodeSvg}</div>` : (ean ? `<div style="font-size:${mm(.11, h)};font-weight:500;flex-shrink:0;">${escapeLabelHtml(ean)}</div>` : "")}
      ${prix ? `<div style="font-size:${mm(.16, h)};font-weight:700;line-height:1;flex-shrink:0;margin-top:0.4mm;">${prix}</div>` : ""}
    </div>`;
  }

  if (templateId === "rayon15x7") {
    const w = LABEL_FORMATS.rayon15x7.widthMm; const h = LABEL_FORMATS.rayon15x7.heightMm; const s = LABEL_RAYON_SIZES;
    // "Internationale Weine" (photo réelle NORMA) : c'est une VRAIE étiquette de prix (comme
    // flat15x5), juste photographiée vide sur le rayon — retour utilisateur explicite
    // (2026-08-26) : "C'est juste qu'elle est vide et pas rempli mais tu dois l'implémenter
    // comme les autres et non titre de rayon uniquement". Bandeau titre/rayon réduit en haut
    // (grappe/feuille de vigne), corps = mêmes champs que flat15x5 (marque/désignation/
    // description/conditionnement à gauche, prix à droite, pied de page), bande "bois" à
    // gauche du corps conservée comme accent décoratif mesuré sur la photo.
    return `<div class="lbl-page lbl-rayon-page" style="width:${w}mm;height:${h}mm;">
      <div class="lbl-rayon-header">
        <span class="lbl-rayon-deco lbl-rayon-grapes">🍇</span>
        <h2 class="lbl-rayon-title">${rayonTitle || "&nbsp;"}</h2>
        <span class="lbl-rayon-deco lbl-rayon-leaf">🍃</span>
      </div>
      <div class="lbl-rayon-body">
        <div class="lbl-rayon-wood"></div>
        <div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:space-between;padding:2.5mm 4mm;">
          <div style="display:flex;justify-content:space-between;align-items:center;gap:4mm;">
            <div style="flex:1;min-width:0;">
              ${marque ? `<div style="font-size:${mm(s.brand, h)};font-weight:800;letter-spacing:.05em;text-transform:uppercase;margin:0 0 0.8mm;">${marque}</div>` : ""}
              <div style="font-size:${mm(s.name, h)};font-weight:900;line-height:1.0;margin:0 0 1mm;">${designation || "&nbsp;"}</div>
              ${description ? `<div style="font-size:${mm(s.text, h)};font-weight:700;line-height:1.2;margin:0 0 0.4mm;">${description}</div>` : ""}
              ${conditionnement ? `<div style="font-size:${mm(s.text, h)};font-weight:700;line-height:1.2;">${conditionnement}</div>` : ""}
            </div>
            <div style="font-size:${mm(s.price, h)};font-weight:900;line-height:.9;white-space:nowrap;flex:0 0 auto;">${prix || "&nbsp;"}</div>
          </div>
          <div style="text-align:left;">
            ${dateDebut ? `<div style="font-size:${mm(s.bottom, h)};font-weight:700;margin:0 0 0.5mm;">ab: ${dateDebut}</div>` : ""}
            ${prixDeBase ? `<div style="font-size:${mm(s.bottom, h)};font-weight:700;margin:0 0 0.5mm;">${prixDeBase}</div>` : ""}
            ${(ve || numeroArticle) ? `<div class="lbl-strip" style="font-size:${mm(s.bottom, h)};font-weight:800;">
              ${ve ? `<span>VE ${ve}</span>` : ""}
              ${numeroArticle ? `<span>${numeroArticle}</span>` : ""}
            </div>` : ""}
          </div>
        </div>
      </div>
    </div>`;
  }

  if (templateId === "flat5x5") {
    const c = LABEL_COLOR_SWATCHES[d.couleur] || LABEL_FLAT_COLOR; const h = LABEL_FORMATS.flat5x5.heightMm; const s = LABEL_FLAT5_SIZES;
    // Sinulind (IMG_740FF466) : prix de base CENTRÉ au-dessus, une bande de cases ACCOLÉES
    // pleine largeur en-dessous (jamais 2 pastilles séparées) — mesuré directement, voir
    // LABEL_ENGINE_CSS .lbl-strip.
    return `<div class="lbl-page" style="width:50mm;height:50mm;background:${c.bg};color:${c.ink};padding:2.5mm;display:flex;flex-direction:column;align-items:center;text-align:center;">
      ${marque ? `<div style="font-size:${mm(s.brand, h)};font-weight:800;letter-spacing:.05em;text-transform:uppercase;margin:0 0 0.8mm;">${marque}</div>` : ""}
      <div style="font-size:${mm(s.name, h)};font-weight:900;line-height:.98;margin:0 0 0.8mm;">${designation || "&nbsp;"}</div>
      ${conditionnement ? `<div style="font-size:${mm(s.text, h)};font-weight:700;line-height:1.1;margin:0 0 0.8mm;">${conditionnement}</div>` : ""}
      <div style="font-size:${mm(s.price, h)};font-weight:900;line-height:.85;margin-top:auto;">${prix || "&nbsp;"}</div>
      ${prixDeBase ? `<div style="font-size:${mm(s.priceUnit, h)};font-weight:700;margin:0.8mm 0;">${prixDeBase}</div>` : `<div style="height:1mm;"></div>`}
      ${(ve || numeroArticle) ? `<div class="lbl-strip lbl-strip-full" style="font-size:${mm(s.bottom, h)};font-weight:800;">
        ${ve ? `<span>VE ${ve}</span>` : ""}
        ${numeroArticle ? `<span>${numeroArticle}</span>` : ""}
      </div>` : ""}
    </div>`;
  }

  if (templateId === "flat15x5") {
    const c = LABEL_COLOR_SWATCHES[d.couleur] || LABEL_FLAT_COLOR; const h = LABEL_FORMATS.flat15x5.heightMm; const s = LABEL_FLAT15_SIZES;
    // Ferrero (IMG_FFD8537F) : prix de base ET bande de cases accolées, tous deux alignés à
    // GAUCHE et empilés (jamais éclatés à gauche/droite) — mesuré directement.
    // Marges/interlignes resserrés (2026-09-12, bug réel trouvé en mesurant scrollHeight vs
    // clientHeight du vrai rendu avec la vraie police embarquée — jamais avec une police de
    // secours, qui donne des largeurs de caractère totalement différentes) : dès qu'une
    // désignation ET une description tiennent chacune sur 2 lignes (cas réel GELATO
    // D'ITALIA, retour utilisateur explicite avec photo), le bloc de texte dépassait déjà les
    // 50mm de hauteur AVANT même le changement de taille du prix ci-dessous — la bande
    // VE/Art.Nr du bas se faisait purement et simplement rogner par overflow:hidden. Preuve
    // vérifiée : à l'ancien réglage, scrollHeight (218px) > clientHeight (189px) sur ce texte,
    // peu importe la taille du prix.
    return `<div class="lbl-page" style="width:150mm;height:50mm;background:${c.bg};color:${c.ink};padding:4mm 5mm;display:flex;flex-direction:column;justify-content:space-between;">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:5mm;">
        <div style="flex:1;min-width:0;">
          ${marque ? `<div style="font-size:${mm(s.brand, h)};font-weight:800;letter-spacing:.05em;text-transform:uppercase;line-height:1.0;margin:0 0 0.4mm;">${marque}</div>` : ""}
          <div style="font-size:${mm(s.name, h)};font-weight:900;line-height:1.0;margin:0 0 0.6mm;">${designation || "&nbsp;"}</div>
          ${description ? `<div style="font-size:${mm(s.text, h)};font-weight:700;line-height:1.0;margin:0 0 0.2mm;">${description}</div>` : ""}
          ${conditionnement ? `<div style="font-size:${mm(s.text, h)};font-weight:700;line-height:1.0;">${conditionnement}</div>` : ""}
        </div>
        <div style="font-size:${mm(s.price, h)};font-weight:900;line-height:.9;white-space:nowrap;flex:0 0 auto;">${prix || "&nbsp;"}</div>
      </div>
      <div style="text-align:left;">
        ${dateDebut ? `<div style="font-size:${mm(s.bottom, h)};font-weight:700;line-height:1.0;margin:0 0 0.25mm;">ab: ${dateDebut}</div>` : ""}
        ${prixDeBase ? `<div style="font-size:${mm(s.bottom, h)};font-weight:700;line-height:1.0;margin:0 0 0.25mm;">${prixDeBase}</div>` : ""}
        ${(ve || numeroArticle) ? `<div class="lbl-strip" style="font-size:${mm(s.bottom, h)};font-weight:800;">
          ${ve ? `<span>VE ${ve}</span>` : ""}
          ${numeroArticle ? `<span>${numeroArticle}</span>` : ""}
        </div>` : ""}
      </div>
    </div>`;
  }

  if (templateId === "flatA4") {
    // Demande explicite (2026-09-17, avec capture à l'appui de flat5x5) : "je veux un
    // formulaire aussi vierge comme celui-ci mais en A4 avec les couleurs" — la famille
    // "aktionA4" est TOUJOURS le poster promotionnel avec bandeau/cadre (voir plus haut,
    // jamais togglable), donc un vrai modèle séparé, pas une variante de celui-ci. Même
    // famille visuelle que flat5x5/flat15x5 (fond uni, centré, sans bandeau "Aktion!"),
    // juste mis à l'échelle d'une feuille A4 entière — repose sur LABEL_AKTION_SIZES (déjà
    // calibrées pour une hauteur de 297mm) faute de photo réelle d'un tel format vierge à
    // mesurer : pas une mesure directe comme les autres tailles de ce fichier, à corriger si
    // une vraie photo de référence apparaît un jour.
    const w = LABEL_FORMATS.aktionA4.widthMm; const h = LABEL_FORMATS.aktionA4.heightMm; const s = LABEL_AKTION_SIZES;
    const c = LABEL_COLOR_SWATCHES[d.couleur] || LABEL_FLAT_COLOR;
    return `<div class="lbl-page" style="width:${w}mm;height:${h}mm;background:${c.bg};color:${c.ink};padding:14mm 16mm;display:flex;flex-direction:column;align-items:center;text-align:center;">
      ${marque ? `<div style="font-size:${mm(s.brand, h)};font-weight:800;letter-spacing:.05em;text-transform:uppercase;margin:0 0 3mm;">${marque}</div>` : ""}
      <div style="font-size:${mm(s.name, h)};font-weight:900;line-height:1.0;margin:0 0 3mm;">${designation || "&nbsp;"}</div>
      ${description ? `<div style="font-size:${mm(s.text, h)};font-weight:700;line-height:1.2;margin:0 0 1.5mm;">${description}</div>` : ""}
      ${conditionnement ? `<div style="font-size:${mm(s.text, h)};font-weight:700;line-height:1.2;margin:0 0 1.5mm;">${conditionnement}</div>` : ""}
      <div style="font-size:${mm(s.price, h)};font-weight:900;line-height:.85;margin-top:auto;">${prix || "&nbsp;"}</div>
      ${prixDeBase ? `<div style="font-size:${mm(s.bottom, h)};font-weight:700;margin:3mm 0 1.5mm;">${prixDeBase}</div>` : `<div style="height:4mm;"></div>`}
      ${dateDebut ? `<div style="font-size:${mm(s.bottom, h)};font-weight:700;margin:0 0 1.5mm;">ab: ${dateDebut}</div>` : ""}
      ${(ve || numeroArticle) ? `<div class="lbl-strip lbl-strip-full" style="font-size:${mm(s.bottom, h)};font-weight:800;">
        ${ve ? `<span>VE ${ve}</span>` : ""}
        ${numeroArticle ? `<span>${numeroArticle}</span>` : ""}
      </div>` : ""}
    </div>`;
  }

  if (templateId === "aktion15x5") {
    // TRIMM Sirup kalorienarm (2e photo, correctement recadrée en paysage, 2026-08-26) —
    // même disposition que flat15x5 (texte empilé à gauche, prix énorme à droite, bande du
    // bas), avec le ruban "Aktion! Mehr fürs Geld." en haut, TOUJOURS présent (c'est ce qui
    // définit ce format — pas un champ togglable ici, contrairement à une hypothèse
    // précédente sans photo à l'appui). Fond blanc (jamais jaune plein comme flat15x5).
    const w = LABEL_FORMATS.aktion15x5.widthMm; const h = LABEL_FORMATS.aktion15x5.heightMm; const s = LABEL_AKTION15_SIZES;
    return `<div class="lbl-page" style="width:${w}mm;height:${h}mm;background:#fff;color:#141414;display:flex;flex-direction:column;">
      <div class="lbl-akt2-ribbon" style="height:${mm(s.banner + s.bannerSub + .04, h)};">
        <div class="lbl-akt2-ribbon-yellow"></div>
        <div class="lbl-akt2-ribbon-red" style="position:absolute;inset:1mm 2.4mm 2.4mm 1mm;clip-path:polygon(0 0, 100% 0, 100% 50%, 82% 100%, 0 100%);padding-left:5mm;">
          <span class="lbl-akt2-title" style="font-size:${mm(s.banner, h)};">Aktion!</span>
          <span class="lbl-akt2-sub" style="font-size:${mm(s.bannerSub, h)};">Mehr fürs Geld.</span>
        </div>
      </div>
      <div style="flex:1;min-height:0;padding:2mm 5mm;display:flex;justify-content:space-between;align-items:center;gap:4mm;">
        <div style="flex:1;min-width:0;">
          ${marque ? `<div style="font-size:${mm(s.brand, h)};font-weight:800;letter-spacing:.05em;text-transform:uppercase;margin:0 0 0.5mm;">${marque}</div>` : ""}
          <div style="font-size:${mm(s.name, h)};font-weight:900;line-height:1.0;margin:0 0 0.5mm;">${designation || "&nbsp;"}</div>
          ${description ? `<div style="font-size:${mm(s.text, h)};font-weight:700;line-height:1.2;">${description}</div>` : ""}
          ${conditionnement ? `<div style="font-size:${mm(s.text, h)};font-weight:700;line-height:1.2;">${conditionnement}</div>` : ""}
        </div>
        <div style="font-size:${mm(s.price, h)};font-weight:900;line-height:.9;white-space:nowrap;flex:0 0 auto;">${prix || "&nbsp;"}</div>
      </div>
      <div style="text-align:left;padding:0 5mm 1.5mm;">
        ${dateDebut ? `<div style="font-size:${mm(s.bottom, h)};font-weight:700;margin:0 0 0.4mm;">ab: ${dateDebut}</div>` : ""}
        ${prixDeBase ? `<div style="font-size:${mm(s.bottom, h)};font-weight:700;margin:0 0 0.4mm;">${prixDeBase}</div>` : ""}
        ${(ve || numeroArticle) ? `<div class="lbl-strip" style="font-size:${mm(s.bottom, h)};font-weight:800;">
          ${ve ? `<span>VE ${ve}</span>` : ""}
          ${numeroArticle ? `<span>${numeroArticle}</span>` : ""}
        </div>` : ""}
      </div>
    </div>`;
  }

  if (templateId === "aktionA4") {
    const w = LABEL_FORMATS.aktionA4.widthMm; const h = LABEL_FORMATS.aktionA4.heightMm; const s = LABEL_AKTION_SIZES;
    // Couleur réintroduite (2026-09-17, retour utilisateur explicite : "je veux que tu
    // ramènes le blanc, jaune et autres couleurs qui existait") — le cadre gris foncé reste
    // fixe (mesuré sur une vraie photo, voir plus haut), seul le panneau intérieur suit le
    // choix de couleur, comme pour flat5x5/flat15x5. Blanc reste la valeur par défaut
    // (dernier état photo-vérifié) si aucune couleur n'a encore été choisie.
    const c = LABEL_COLOR_SWATCHES[d.couleur] || LABEL_COLOR_SWATCHES.blanc;
    const activeLogos = Array.isArray(d.logos) ? d.logos.filter((k) => LABEL_LOGO_BADGES[k]) : [];
    const hasLogos = activeLogos.length > 0;
    const bandColor = hasLogos ? LABEL_LOGO_BADGES[activeLogos[0]].bandColor : "";
    const bodyPad = "18mm 16mm";
    const bandPad = `${mm(.04, h)} 16mm ${mm(.035, h)}`;
    // Bio Sonne Zucchini / Saubermax WC-Farbspüler / Pfiff Chips Paprika DLG-Prämiert (3
    // vraies photos NORMA, 2026-08-26) : bandeau coloré HORIZONTAL en haut avec badge(s) de
    // certification quand il y en a (couleur = celle du 1er badge actif) — structure INCHANGÉE.
    if (hasLogos) {
      return `<div class="lbl-page" style="width:${w}mm;height:${h}mm;background:${c.bg};color:${c.ink};display:flex;flex-direction:column;">
        <div class="lbl-akt2-band" style="background:${bandColor};padding:${bandPad};">
          <div class="lbl-akt2-badgerow">${activeLogos.length > 1 ? activeLogos.map((k) => LABEL_LOGO_BADGES[k].render(h, s.badge)).join("") : Array(3).fill(activeLogos[0]).map((k) => LABEL_LOGO_BADGES[k].render(h, s.badge)).join("")}</div>
        </div>
        <div style="flex:1;min-height:0;display:flex;flex-direction:column;text-align:center;padding:${bodyPad};">
          ${marque ? `<div style="font-size:${mm(s.brand, h)};font-weight:800;letter-spacing:.05em;text-transform:uppercase;">${marque}</div>` : ""}
          <div style="font-size:${mm(s.name, h)};font-weight:900;line-height:1.02;margin-top:${mm(.015, h)};">${designation || "&nbsp;"}</div>
          ${description ? `<div style="font-size:${mm(s.text, h)};font-weight:700;line-height:1.3;margin-top:${mm(.015, h)};">${description}</div>` : ""}
          ${conditionnement ? `<div style="font-size:${mm(s.text, h)};font-weight:700;line-height:1.3;margin-top:${mm(.006, h)};">${conditionnement}</div>` : ""}
          <div style="font-size:${mm(s.price, h)};font-weight:900;line-height:.85;margin-top:auto;margin-bottom:${mm(.05, h)};">${prix || "&nbsp;"}</div>
          <div class="lbl-bottom-row" style="font-size:${mm(s.bottom, h)};font-weight:700;">
            <span>${dateDebut ? `ab: ${dateDebut}` : ""}</span>
            <span>${prixDeBase}</span>
            <span style="display:flex;gap:3mm;font-weight:800;">
              ${ve ? `<span>VE ${ve}</span>` : ""}
              ${numeroArticle ? `<span>${numeroArticle}</span>` : ""}
            </span>
          </div>
        </div>
      </div>`;
    }
    // SANS badge : cadre gris foncé (fait partie du visuel imprimé — les 2 codes du bas sont
    // imprimés DEDANS, jamais un simple support physique) contenant un panneau blanc inséré,
    // bandeau "Aktion!" à ~80% de largeur aligné à gauche avec un médaillon rond qui déborde
    // du coin haut-droit (mesuré sur DAUNASOFT Taschentücher Triobox, 2026-09-10).
    const cardPad = `${mm(.015, h)} ${mm(.038, w)} 0`;
    const whitePad = `${mm(.02, h)} ${mm(.04, w)} ${mm(.03, h)}`;
    const badgeSize = mm(s.a4Badge, h);
    return `<div class="lbl-page lbl-aktion-a4-card" style="width:${w}mm;height:${h}mm;background:#4c4a45;padding:${cardPad};display:flex;flex-direction:column;">
      <div class="lbl-aktion-a4-white" style="flex:1;min-height:0;display:flex;flex-direction:column;padding:${whitePad};background:${c.bg};color:${c.ink};">
        <div class="lbl-aktion-banner" style="align-self:flex-start;width:78%;padding:${mm(.014, h)} ${mm(.022, w)};">
          <span class="lbl-aktion-title" style="font-size:${mm(s.banner, h)};">Aktion!</span>
          <span class="lbl-aktion-sub" style="font-size:${mm(s.bannerSub, h)};">Mehr fürs Geld.</span>
          <div class="lbl-aktion-a4-badge" style="width:${badgeSize};height:${badgeSize};font-size:${mm(s.a4Badge * .55, h)};top:${mm(s.a4Badge * -.4, h)};right:${mm(s.a4Badge * -.35, h)};">!</div>
        </div>
        <div style="flex:1;min-height:0;display:flex;flex-direction:column;text-align:center;justify-content:flex-end;">
          ${marque ? `<div style="font-size:${mm(s.brand, h)};font-weight:800;letter-spacing:.05em;text-transform:uppercase;margin-top:${mm(.05, h)};">${marque}</div>` : ""}
          <div style="font-size:${mm(s.name, h)};font-weight:900;line-height:1.02;margin-top:${mm(.015, h)};">${designation || "&nbsp;"}</div>
          ${description ? `<div style="font-size:${mm(s.text, h)};font-weight:700;line-height:1.3;margin-top:${mm(.015, h)};">${description}</div>` : ""}
          ${conditionnement ? `<div style="font-size:${mm(s.text, h)};font-weight:700;line-height:1.3;margin-top:${mm(.006, h)};">${conditionnement}</div>` : ""}
          <div style="font-size:${mm(s.price, h)};font-weight:900;line-height:.85;color:#2b2b2b;margin-top:auto;margin-bottom:${mm(.02, h)};">${prix || "&nbsp;"}</div>
        </div>
      </div>
      <div class="lbl-aktion-a4-bottomcodes" style="font-size:${mm(s.bottom, h)};padding:${mm(.014, h)} ${mm(.01, w)} 0;">
        <span>${dateDebut ? `ab: ${dateDebut}` : (prixDeBase || "")}</span>
        <span>${[ve ? `VE ${ve}` : "", numeroArticle].filter(Boolean).join("   ")}</span>
      </div>
    </div>`;
  }

  return "";
}

// Registre — SEUL endroit qui connaît la liste des modèles disponibles (hub de choix,
// formulaire dynamique) ; ajouter un modèle futur = une entrée de plus ici.
const LABEL_TEMPLATES = [
  {
    id: "flat15x5", label: "Étiquette jaune 15×5 cm", format: LABEL_FORMATS.flat15x5,
    swatch: LABEL_FLAT_COLOR.bg,
    fields: ["marque", "designation", "description", "conditionnement", "prix", "prixDeBase", "ve", "numeroArticle", "dateDebut", "couleur"],
  },
  {
    id: "flat5x5", label: "Étiquette jaune 5×5 cm", format: LABEL_FORMATS.flat5x5,
    swatch: LABEL_FLAT_COLOR.bg,
    fields: ["marque", "designation", "conditionnement", "prix", "prixDeBase", "ve", "numeroArticle", "couleur"],
  },
  {
    id: "flatA4", label: "Étiquette jaune A4", format: LABEL_FORMATS.aktionA4,
    swatch: LABEL_FLAT_COLOR.bg,
    // Même famille "vierge" que flat15x5/flat5x5 (jamais le bandeau "Aktion!") — demande
    // explicite 2026-09-17 : "je veux un formulaire aussi vierge comme celui-ci mais en A4
    // avec les couleurs".
    fields: ["marque", "designation", "description", "conditionnement", "prix", "prixDeBase", "ve", "numeroArticle", "dateDebut", "couleur"],
  },
  {
    id: "aktion15x5", label: "Affiche Aktion 15×5 cm", format: LABEL_FORMATS.aktion15x5,
    swatch: LABEL_AKTION_BANNER.bg,
    fields: ["marque", "designation", "description", "conditionnement", "prix", "prixDeBase", "ve", "numeroArticle", "dateDebut"],
  },
  {
    id: "aktionA4", label: "Affiche Aktion A4", format: LABEL_FORMATS.aktionA4,
    // Rouge (LABEL_AKTION_BANNER, même couleur que le bandeau réel) — jamais le vert badge
    // par défaut, qui ne représente que le cas AVEC logo de certification (minoritaire).
    swatch: LABEL_AKTION_BANNER.bg,
    // "couleur" réintroduit (2026-09-17, retour utilisateur explicite après le retrait du
    // 2026-09-10 : "je veux que tu ramènes le blanc, jaune et autres couleurs qui
    // existait") — blanc reste la valeur par défaut (dernier état photo-vérifié), mais
    // l'utilisateur doit pouvoir choisir une autre couleur pour le panneau intérieur.
    fields: ["marque", "designation", "description", "conditionnement", "prix", "prixDeBase", "ve", "numeroArticle", "dateDebut", "logos", "couleur"],
  },
  {
    id: "rayon15x7", label: "Pancarte séparateur de rayon", format: LABEL_FORMATS.rayon15x7,
    swatch: "#2b241f",
    fields: ["rayonTitle", "marque", "designation", "description", "conditionnement", "prix", "prixDeBase", "ve", "numeroArticle", "dateDebut"],
  },
  {
    // Chantier impression Niimbot (2026-08-28) — seul format imprimable sur l'imprimante
    // physique de l'utilisateur ; volontairement minimal (voir buildLabelBodyHtml).
    id: "niimbot5x3", label: "Étiquette Niimbot 5×3 cm", format: LABEL_FORMATS.niimbot5x3,
    swatch: A.blue,
    fields: ["designation", "ean", "prix"],
  },
];
function getLabelTemplate(id) { return LABEL_TEMPLATES.find((t) => t.id === id) || LABEL_TEMPLATES[0]; }

// Libellés/placeholders du formulaire dynamique — seul point qui traduit un id de champ
// générique en texte affiché à l'écran de saisie (jamais dans le moteur de rendu lui-même).
// keyboard : quel clavier mobile s'ouvre pour ce champ (retour utilisateur explicite —
// numérique pour tout ce qui n'est QUE des chiffres/décimales, alphabétique sinon, jamais le
// clavier générique par défaut). "decimal" ouvre le pavé numérique avec le séparateur
// décimal (prix "3,29", date courte "23.07.") ; "numeric" un pavé chiffres purs (VE,
// numéro d'article) ; absent = clavier alphabétique standard (marque/désignation/
// description/conditionnement/prix de base, qui mélange lettres, €, = et /).
const LABEL_FIELD_META = {
  marque: { key: "marque", labelKey: "labels.field.marque", label: "Marque", placeholder: "ex. FERRERO" },
  designation: { key: "designation", labelKey: "labels.field.designation", label: "Désignation", placeholder: "ex. Duplo Riegel" },
  description: { key: "description", labelKey: "labels.field.description", label: "Description", placeholder: "ex. Blau, Lila oder Türkis", multiline: true },
  conditionnement: { key: "conditionnement", labelKey: "labels.field.conditionnement", label: "Conditionnement", placeholder: "ex. 10er Pack / 182 g Packung", multiline: true },
  prix: { key: "prix", labelKey: "labels.field.prix", label: "Prix", placeholder: "ex. 3,29", keyboard: "decimal" },
  prixDeBase: { key: "prixDeBase", labelKey: "labels.field.prixDeBase", label: "Prix de base", placeholder: "ex. 182g=18,08€/1kg" },
  ve: { key: "ve", labelKey: "labels.field.ve", label: "VE", placeholder: "ex. 28", keyboard: "numeric" },
  numeroArticle: { key: "numeroArticle", labelKey: "labels.field.numeroArticle", label: "Numéro d'article", placeholder: "ex. 33239", keyboard: "numeric" },
  ean: { key: "ean", labelKey: "labels.field.ean", label: "Code-barres (EAN)", placeholder: "ex. 4008400123456", keyboard: "numeric" },
  dateDebut: { key: "dateDebut", labelKey: "labels.field.dateDebut", label: "Date de début", placeholder: "ex. 23.07.", keyboard: "decimal" },
  couleur: { key: "couleur", labelKey: "labels.field.couleur", label: "Couleur", swatches: true },
  aktionBanner: { key: "aktionBanner", labelKey: "labels.field.aktionBanner", label: "Bandeau \"Aktion!\"", toggle: true },
  logos: { key: "logos", labelKey: "labels.field.logos", label: "Logos de certification", multiSwatches: true },
  rayonTitle: { key: "rayonTitle", labelKey: "labels.field.rayonTitle", label: "Titre du rayon", placeholder: "ex. Internationale Weine" },
};

// Document complet du portail d'impression (#lblPrintRoot, voir le composant) — @page
// dynamique aux dimensions EXACTES du modèle (margin:0), déclaré APRÈS le @page A4 paysage
// partagé par les 6 autres portails (voir le bloc <style> global du composant) : par ordre
// de déclaration CSS, cette règle l'emporte pour la propriété `size`/`margin` UNIQUEMENT
// quand ce portail contient du contenu (sinon vide, donc sans effet — voir
// clearOtherPrintPortals) — jamais de conflit avec HACCP/MHD/Arbeitsplan/AZL/Infos/Reporting
// qui restent en A4 paysage. C'est ce qui permet "Imprimer"/"Exporter PDF" à 100%, sans
// "ajuster à la page" (retour utilisateur explicite).
function buildLabelPrintHtml(templateId, draft) {
  const format = getLabelTemplate(templateId).format;
  return `<style>${LABEL_ENGINE_CSS}
    @page { size: ${format.widthMm}mm ${format.heightMm}mm !important; margin: 0 !important; }
  </style>${buildLabelBodyHtml(templateId, draft)}`;
}

/* ============================================================ */
// Thème — persisté (retour utilisateur 2026-08-04) : "Clair" (blanc) est le vrai défaut
// tant qu'aucun choix n'a jamais été enregistré ; un choix explicite (y compris "Système")
// survit à la fermeture/réouverture de l'app, lu ici une seule fois au montage.
const NEXORA_THEME_CACHE_KEY = "nexora_theme";
function readCachedTheme() {
  try {
    const raw = localStorage.getItem(NEXORA_THEME_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch { return null; }
}
// Réglages d'affichage (taille/police/gras) — même patron de persistance que le thème,
// clé séparée (indépendant du choix de thème).
const NEXORA_DISPLAY_CACHE_KEY = "nexora_display";
function readCachedDisplay() {
  try {
    const raw = localStorage.getItem(NEXORA_DISPLAY_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch { return null; }
}
/* Ossature desktop/tablette (Phase 1) — wrapper fin : calcule isWide une seule fois ici
   (jamais dans NexoraAppInner, dont les 3 retours JSX distincts — login/porte/app
   connectée — rendraient un Provider imbriqué à chaque branche fastidieux et fragile à
   maintenir) et le fournit à la fois en prop directe (nav latérale, largeur du shell) et
   via WideContext (pour Sheet, composant module-level rendu profondément dans l'arbre,
   sans avoir à faire passer isWide en prop à chaque site d'appel). */
export default function NexoraApp() {
  const [isWide, setIsWide] = useState(() => window.innerWidth >= WIDE_BREAKPOINT);
  useEffect(() => {
    const onResize = () => setIsWide(window.innerWidth >= WIDE_BREAKPOINT);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return (
    <WideContext.Provider value={isWide}>
      <NexoraAppInner isWide={isWide} />
    </WideContext.Provider>
  );
}

// Heures d'ouverture/fermeture (2026-08-31, retour utilisateur explicite) — pure, sans
// dépendance React, réutilisée par la pilule Accueil, l'échéance urgente Réductions et le
// gate du popup Tâches. openTime/closeTime au format "HH:MM" ; "" = filiale sans horaires
// configurés, tous les appelants doivent alors garder leur comportement actuel (jamais
// planter, jamais inventer un horaire par défaut). holidayDatesSet = dates ISO déjà scopées
// à CETTE filiale (voir allHolidayDatesSet, /api/public-holidays?storeId= déjà filtré côté
// serveur) — dimanche + jours fériés fermés, même règle que sfIsStoreOpenDay (server.js),
// jamais dupliquée/divergente ici.
function computeStoreOpenStatus(openTime, closeTime, holidayDatesSet, at = new Date()) {
  if (!/^\d{1,2}:\d{2}$/.test(openTime || "") || !/^\d{1,2}:\d{2}$/.test(closeTime || "")) return null;
  const pad = (n) => String(n).padStart(2, "0");
  const dateISOOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const isOpenDay = (d) => d.getDay() !== 0 && !holidayDatesSet.has(dateISOOf(d));
  const [oh, om] = openTime.split(":").map(Number);
  const [ch, cm] = closeTime.split(":").map(Number);
  const todayOpen = new Date(at); todayOpen.setHours(oh, om, 0, 0);
  const todayClose = new Date(at); todayClose.setHours(ch, cm, 0, 0);

  if (isOpenDay(at) && at >= todayOpen && at < todayClose) return { isOpen: true, target: todayClose };
  if (isOpenDay(at) && at < todayOpen) return { isOpen: false, target: todayOpen };

  // Fermé maintenant (jour non ouvré, ou après l'heure de fermeture) — cherche le prochain
  // jour ouvré à partir de demain, garde-fou 30 jours (même patron que sfRollForwardToOpenDay).
  let d = new Date(at); d.setDate(d.getDate() + 1); d.setHours(0, 0, 0, 0);
  let guard = 0;
  while (!isOpenDay(d) && guard < 30) { d.setDate(d.getDate() + 1); guard++; }
  const nextOpen = new Date(d); nextOpen.setHours(oh, om, 0, 0);
  return { isOpen: false, target: nextOpen };
}

function formatStoreCountdown(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}min`;
  if (m > 0) return `${m}min ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

// Compte à rebours d'ouverture/fermeture — composant à part avec son PROPRE intervalle 1s
// (retour utilisateur : "l'heure va décrémenter" en direct), pour que seul CE petit composant
// se re-rende chaque seconde plutôt que tout l'arbre géant de l'app (le ticker `now` général
// de l'app tourne à 30s, bien trop grossier pour un vrai compte à rebours). `render` reçoit
// {isOpen, remaining} et décide de l'affichage — réutilisé par la pilule Accueil ET
// l'échéance urgente Réductions, chacun avec son propre habillage.
function StoreHoursCountdown({ openTime, closeTime, holidayDatesSet, render }) {
  const [tick, setTick] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setTick(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const status = computeStoreOpenStatus(openTime, closeTime, holidayDatesSet, tick);
  if (!status) return null;
  const remaining = formatStoreCountdown(status.target.getTime() - tick.getTime());
  return render({ isOpen: status.isOpen, remaining });
}

function NexoraAppInner({ isWide }) {
  const [themeId, setThemeId] = useState(() => readCachedTheme()?.themeId || "blanc");
  const [useSystemTheme, setUseSystemTheme] = useState(() => readCachedTheme()?.useSystemTheme === true);
  const P = THEMES[themeId];
  /* Thème "Système" — suit la préférence claire/sombre de l'appareil et
     réagit en direct si elle change pendant que l'app est ouverte. */
  useEffect(() => {
    if (!useSystemTheme || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => setThemeId(mq.matches ? "sombre" : "blanc");
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [useSystemTheme]);
  useEffect(() => {
    try { localStorage.setItem(NEXORA_THEME_CACHE_KEY, JSON.stringify({ themeId, useSystemTheme })); } catch { /* stockage indisponible — pas bloquant */ }
  }, [themeId, useSystemTheme]);
  // La zone Dynamic Island (safe-area-inset-top, viewport-fit=cover dans index.html)
  // est peinte par le fond de <body>/<html>, jamais par le div racine de l'app — index.html
  // ne fixe aucune background-color (juste margin:0), donc cette bande restait blanche par
  // défaut et ne suivait jamais le thème choisi. On la synchronise ici à chaque changement
  // de thème plutôt que de toucher viewport-fit/standalone (cf. commentaire dans index.html :
  // une tentative précédente sur ces balises PWA a cassé la position de la nav du bas).
  //
  // theme-color retiré le 2026-08-19 : vérifié sur iPhone réel (capture d'écran), la vraie
  // bande d'état iOS (horloge/réseau/batterie) reste blanche quel que soit le thème — en
  // onglet Safari normal (pas ajouté à l'écran d'accueil, pas standalone), cette bande est
  // dessinée par iOS lui-même et AUCUNE balise web ne peut la teindre, theme-color inclus.
  // theme-color n'agit que sur le chrome propre de Safari (barre d'adresse), jamais sur
  // cette bande. Seul apple-mobile-web-app-status-bar-style (black-translucent) le
  // pourrait, mais seulement en mode standalone (lancé depuis l'icône écran d'accueil) —
  // hors sujet ici tant que l'app n'est pas réinstallable proprement (voir index.html).
  useEffect(() => {
    document.documentElement.style.background = P.bg;
    document.body.style.background = P.bg;
  }, [P.bg]);
  const pickTheme = (id) => { setUseSystemTheme(false); setThemeId(id); };
  const pickSystemTheme = () => setUseSystemTheme(true);

  // Réglages d'affichage — taille de texte / police / gras (2026-08-17, demande
  // utilisatrice). Persistés indépendamment du thème, appliqués au montage ET à chaque
  // changement via des CSS custom properties sur <html> (voir FONT_SCALES/FONT_CHOICES).
  const [fontScaleId, setFontScaleId] = useState(() => {
    const cached = readCachedDisplay()?.fontScaleId;
    return FONT_SCALES[cached] ? cached : "normal";
  });
  const [fontChoiceId, setFontChoiceId] = useState(() => {
    const cached = readCachedDisplay()?.fontChoiceId;
    return FONT_CHOICES[cached] ? cached : "classique";
  });
  const [boldText, setBoldText] = useState(() => readCachedDisplay()?.boldText === true);
  // "Plus" — cartes dépliantes (retour utilisateur explicite, 2026-08-18, artefact validé
  // point par point) : remplace l'ancien long défilement de ~15 sections par 5 cartes
  // repliées par défaut, une seule ouverte à la fois (jamais plusieurs en même temps,
  // sinon l'écran redevient aussi long qu'avant).
  const [settingsOpenSection, setSettingsOpenSection] = useState(null);
  // Réclamations > Destination d'envoi — même patron accordéon que "Plus" ci-dessus
  // (retour utilisateur 2026-08-19, maquette validée : "4 sujets différents empilés sans
  // aucune séparation" — méthode d'envoi, 2 listes de défauts, catalogue d'articles).
  // Section "method" ouverte par défaut, jamais plusieurs à la fois.
  const [claimsSettingsOpenSection, setClaimsSettingsOpenSection] = useState("method");
  useEffect(() => {
    document.documentElement.style.setProperty("--nx-font-scale", String(FONT_SCALES[fontScaleId].value));
    document.documentElement.style.setProperty("--nx-font", FONT_CHOICES[fontChoiceId].stack);
    if (boldText) document.documentElement.setAttribute("data-bold-text", "true");
    else document.documentElement.removeAttribute("data-bold-text");
  }, [fontScaleId, fontChoiceId, boldText]);
  useEffect(() => {
    try { localStorage.setItem(NEXORA_DISPLAY_CACHE_KEY, JSON.stringify({ fontScaleId, fontChoiceId, boldText })); } catch { /* stockage indisponible — pas bloquant */ }
  }, [fontScaleId, fontChoiceId, boldText]);

  /* --- Auth ---
     StoreFlowProV2 (le vrai backend, proxifié via /api/*, voir Phase 0 du plan de
     migration) n'expose aucune route "GET /api/me" pour restaurer l'identité depuis le
     seul cookie de session au rechargement — son propre frontend vanilla ne s'en sert
     jamais non plus, il relit un cache client (sessionStorage + miroir localStorage,
     security-engine.js). On reprend exactement ce principe ici plutôt que d'ajouter une
     route au vrai backend : localStorage garde le dernier utilisateur connecté, restauré
     optimistiquement au chargement (si le cookie de session a expiré entre-temps, le
     premier appel API protégé échouera en 401 et ramènera à l'écran de connexion — géré
     par apiJSON()/le code appelant, pas ici). */
  const NEXORA_USER_CACHE_KEY = "nexora_user";
  const mapServerUser = (serverUser) => ({
    id: serverUser.id,
    proId: serverUser.employeeId || serverUser.login || serverUser.username || "",
    firstname: serverUser.firstname || "",
    lastname: serverUser.lastname || "",
    store: serverUser.storeId || (Array.isArray(serverUser.stores) ? serverUser.stores[0] : "") || "",
    role: serverUser.role || "",
    stores: serverUser.stores || [],
    language: serverUser.language || "fr",
    photoUrl: serverUser.photoUrl || "",
    // Manquait ici (bug réel trouvé 2026-08-04) : genderTitle (salutation "Bonjour
    // M./Mme") lit user?.gender, jamais rempli par ce mapping — la salutation genrée
    // n'avait donc jamais fonctionné, quel que soit le compte, malgré le champ existant
    // côté serveur.
    gender: serverUser.gender || "",
    // Manquait ici aussi (bug réel trouvé 2026-08-15, même piège whitelist que gender
    // ci-dessus — 2e fois sur cette même fonction) : isModuleHiddenForUser lit
    // user?.hiddenModules, jamais rempli par ce mapping — un module caché par l'admin
    // restait donc TOUJOURS visible dans la grille (seul l'ancien gate par rôle bloquait
    // le clic, jamais le retrait réel demandé). Rafraîchi en plus à chaque battement de
    // cœur (GET /api/auth/ping) pour un compte déjà connecté, pas seulement au login.
    hiddenModules: Array.isArray(serverUser.hiddenModules) ? serverUser.hiddenModules : [],
    // Manquait ici aussi (bug réel trouvé 2026-08-17, MÊME piège whitelist que gender/
    // hiddenModules ci-dessus — 3e fois sur cette même fonction) : isNexoraAdmin lit
    // user?.isSuperAdmin, jamais rempli par ce mapping — le bypass super-admin (déjà
    // renvoyé par le login, safeServerUser + isSuperAdminServer) n'atteignait donc jamais
    // le state client, cachant toutes les sections admin à un compte super-admin dont le
    // rôle affiché n'est pas NEXORA_ADMIN, alors que le serveur accepte déjà ce bypass.
    isSuperAdmin: serverUser.isSuperAdmin === true,
  });

  // Bug réel trouvé en test (2026-08-28, "fond blanc" persistant même après réinstallation
  // de l'icône écran d'accueil) : si un rendu plante AVANT que l'effet de nettoyage
  // (localStorage.removeItem sur user===null, voir plus bas) ait pu s'exécuter, l'ancien
  // objet reste en cache indéfiniment — et sur iOS, le stockage d'une icône déjà installée
  // survit parfois même à "Effacer les données de site" fait depuis Safari (constaté en
  // conditions réelles, pas juste en théorie). Plutôt que de dépendre d'un nettoyage manuel
  // qui peut ne jamais atteindre ce stockage-là, on valide ici la FORME de l'objet mis en
  // cache avant de lui faire confiance — un objet sans proId/id exploitable est traité
  // comme "pas de cache", jamais transmis tel quel au reste de l'app.
  const readCachedUser = () => {
    try {
      const cached = localStorage.getItem(NEXORA_USER_CACHE_KEY);
      if (!cached) return null;
      const parsed = JSON.parse(cached);
      if (!parsed || typeof parsed !== "object" || !(parsed.proId || parsed.id)) {
        try { localStorage.removeItem(NEXORA_USER_CACHE_KEY); } catch { /* best-effort */ }
        return null;
      }
      return parsed;
    } catch { return null; }
  };
  const [user, setUser] = useState(readCachedUser);
  // Faille de sécurité réelle corrigée (retour utilisateur 2026-08-10) : jusqu'ici, un
  // utilisateur restauré depuis le cache localStorage (readCachedUser, voir commentaire
  // "--- Auth ---" ci-dessus) faisait immédiatement afficher toute l'app authentifiée —
  // navigation et écrans compris, selon le rôle mis en cache — AVANT toute vérification
  // serveur. Si le cookie de session avait expiré ou n'existait pas sur ce navigateur/
  // appareil (session serveur à 10 min, voir SESSION_MAX_AGE_MS côté server.js) mais que
  // le cache localStorage persistait (ex. appareil partagé, PWA fermée puis rouverte),
  // n'importe qui ouvrant l'URL voyait directement l'interface du dernier rôle connecté —
  // aucune donnée protégée ne fuyait réellement (sfSessionUser revalide tout côté serveur
  // à chaque appel /api), mais la STRUCTURE de l'interface (navigation, écrans réservés à
  // un rôle) s'affichait à quiconque, sans authentification, et l'app restait ensuite
  // "à moitié connectée" indéfiniment (aucune donnée ne charge jamais, aucun retour à
  // l'écran de connexion). sessionValidating bloque maintenant tout rendu de l'app tant
  // qu'un utilisateur en cache n'a pas été reconfirmé par le serveur (voir l'effet plus
  // bas, GET /api/auth/ping — déjà protégé par requireAuth, déjà utilisé pour le battement
  // de coeur, aucune route serveur nouvelle nécessaire). Vaut `true` uniquement s'il y a
  // effectivement un utilisateur en cache à vérifier — jamais de faux écran de chargement
  // pour un premier lancement sans session.
  const [sessionValidating, setSessionValidating] = useState(() => !!readCachedUser());
  // Durée d'affichage minimale de l'écran "NEXORA" ci-dessus (retour utilisateur explicite,
  // 2026-08-21 : "il disparaît trop rapidement") — sur une connexion rapide, /api/auth/ping
  // répondait parfois en quelques dizaines de ms, trop court pour être vu. Horodatage pris
  // UNE FOIS au montage (avant même le premier rendu), jamais recalculé — voir l'effet de
  // revalidation plus bas qui l'utilise pour retarder setSessionValidating(false).
  const sessionValidationStartRef = useRef(Date.now());
  // Écran de verrouillage par inactivité (voir IDLE_LOCK_MS) — état séparé du logout complet :
  // l'app reste ouverte exactement là où elle en était, seul un écran par-dessus tout bloque
  // l'interaction jusqu'au bon mot de passe.
  const [locked, setLocked] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [authScreen, setAuthScreen] = useState("start"); // start | login | register | registerPending | initAdmin | forgotPassword
  // Langue mémorisée SUR CET APPAREIL, indépendamment de la session (retour utilisateur
  // explicite, 2026-08-25 : "sinon la personne devra configurer la langue à chaque
  // première connexion"). Avant ce correctif, `lang` ne lisait QUE le cache utilisateur
  // (NEXORA_USER_CACHE_KEY) — or ce cache est effacé à chaque logout ET à chaque
  // expiration de session (401 sur /api/auth/ping, voir l'effet de revalidation plus bas),
  // ce qui remettait silencieusement l'écran de connexion en français par défaut à la
  // prochaine ouverture de l'app, même pour un compte qui avait déjà choisi une autre
  // langue. Même patron que NEXORA_LAST_BRANCH_KEY juste au-dessus : une clé DÉDIÉE qui
  // survit exprès au logout — écrite à chaque changeLang(), jamais effacée par logout()/
  // la revalidation de session. Ordre de repli : clé dédiée (ce device) → langue du compte
  // en cache (si la clé dédiée n'existe pas encore, ex. mise à jour de l'app) → "fr".
  const NEXORA_LANG_KEY = "nexora_lang";
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem(NEXORA_LANG_KEY) || readCachedUser()?.language || "fr"; } catch { return readCachedUser()?.language || "fr"; }
  });
  // Face ID / Touch ID / Windows Hello (retour utilisateur explicite, 2026-08-18) — la
  // clé WebAuthn contient volontairement le Pro-ID (contrairement à NEXORA_LAST_BRANCH_KEY
  // plus bas, qui ne mémorise QUE la filiale) : un enregistrement biométrique est par
  // nature déjà lié à CET appareil précis (Face ID/Touch ID vérifient physiquement que
  // c'est le même visage/doigt qu'à l'enregistrement) — même si un collègue tapait "Se
  // connecter avec Face ID" sur l'appareil de quelqu'un d'autre, la vérification
  // biométrique échouerait simplement (jamais une brèche de sécurité), il ne reste donc
  // qu'une repli vers le formulaire classique.
  const NEXORA_WEBAUTHN_HINT_KEY = "nexora_webauthn_hint";
  const readWebauthnHint = () => {
    try {
      const raw = localStorage.getItem(NEXORA_WEBAUTHN_HINT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };
  const [webauthnHint, setWebauthnHint] = useState(readWebauthnHint);
  const [webauthnAvailable, setWebauthnAvailable] = useState(false);
  const [webauthnBusy, setWebauthnBusy] = useState(false);
  useEffect(() => {
    if (!browserSupportsWebAuthn()) return;
    platformAuthenticatorIsAvailable().then((ok) => { if (ok) setWebauthnAvailable(true); }).catch(() => {});
  }, []);
  // Gestion des appareils Face ID/Touch ID depuis Réglages (retour utilisateur explicite,
  // 2026-08-29) — voir GET/DELETE /api/webauthn/credentials (server/webauthn/routes.js,
  // toujours scopé au compte connecté, jamais un autre compte).
  const [webauthnDevices, setWebauthnDevices] = useState([]);
  const [webauthnDevicesLoading, setWebauthnDevicesLoading] = useState(false);
  const loadWebauthnDevices = () => {
    setWebauthnDevicesLoading(true);
    apiJSON("/api/webauthn/credentials")
      .then((d) => setWebauthnDevices(Array.isArray(d.credentials) ? d.credentials : []))
      .catch(() => setWebauthnDevices([]))
      .finally(() => setWebauthnDevicesLoading(false));
  };
  const removeWebauthnDevice = (credentialId) => {
    apiJSON(`/api/webauthn/credentials/${encodeURIComponent(credentialId)}`, { method: "DELETE" })
      .then(() => { showToast(t("settings.webauthn.removedToast", "✓ Appareil retiré")); loadWebauthnDevices(); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const [loginId, setLoginId] = useState(() => readWebauthnHint()?.login || "");
  const [loginPw, setLoginPw] = useState("");
  // Déverrouillage offline (retour utilisateur explicite, 2026-08-28 : "cela ne gêne pas les
  // sessions offline de 8h qu'on a faites ?") — l'écran de verrouillage par inactivité
  // (IDLE_LOCK_MS) fonctionne déjà 100% hors-ligne (timestamp local), mais submitUnlock
  // vérifiait TOUJOURS le mot de passe via /api/auth/verify-password : un employé bloqué
  // sans réseau au moment où le verrouillage se déclenche (ex. 30 min sans toucher son
  // téléphone en plein rangement, en pleine session offline) ne pouvait plus jamais
  // déverrouiller avant le retour du réseau. Ce ref garde le mot de passe de la DERNIÈRE
  // connexion réussie EN MÉMOIRE UNIQUEMENT (jamais localStorage/sessionStorage, jamais
  // envoyé nulle part d'autre, perdu à la fermeture de l'onglet) — submitUnlock l'utilise
  // pour vérifier localement si hors-ligne, jamais si en ligne (vraie vérification serveur
  // toujours prioritaire dans ce cas). Vidé à chaque déconnexion/expiration de session (voir
  // les mêmes points que setLocked(false) ci-dessous). Reste vide pour une connexion
  // WebAuthn/Face ID (aucun mot de passe en clair disponible) — limitation connue.
  const sessionPasswordRef = useRef("");
  // Mémorisation de la filiale (retour utilisateur explicite, 2026-08-18 : "le numéro de
  // filiale est un identifiant à part entière... entrer 3 données différentes est
  // toujours beaucoup") — clé DISTINCTE de NEXORA_USER_CACHE_KEY, survit exprès à
  // logout() (voir plus bas) puisque le but est de ne plus la ressaisir à CHAQUE
  // connexion, pas seulement de restaurer une session déjà active. Ne mémorise QUE la
  // filiale, jamais le Pro-ID : sur un appareil de filiale partagé entre plusieurs
  // employés, mémoriser QUI s'est connecté en dernier révélerait cette information à la
  // personne suivante — la filiale est une donnée d'appareil/contexte, pas une donnée
  // personnelle. Un compte réseau (Bereichsleiter/Verkaufsleiter/Niederlassungsleiter)
  // ne soumet jamais de filiale au login (voir sanitizeBranchOnBlur/safeBranchId côté
  // doLogin) et n'écrit donc jamais cette clé.
  // Signature à distance (?sign=<token>) — voir le early-return dédié plus bas et
  // requestRemoteSignature/renderRemoteSigButton. Lu UNE FOIS au montage (comme
  // NEXORA_LAST_BRANCH_KEY ci-dessous) : ce paramètre n'a de sens qu'à l'ouverture initiale
  // du lien, jamais recalculé pendant la session.
  const [publicSignToken] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("sign"); } catch { return null; }
  });
  const [publicSignData, setPublicSignData] = useState(null);
  const [publicSignError, setPublicSignError] = useState("");
  const [publicSignSubmitting, setPublicSignSubmitting] = useState(false);
  const [publicSignDone, setPublicSignDone] = useState(false);
  const [publicSigReady, setPublicSigReady] = useState(false);
  const publicSigMountRef = useRef(null);
  const publicSigPadRef = useRef(null);
  useEffect(() => {
    if (!publicSignToken) return;
    fetch(`/api/sign/${publicSignToken}?_=${Date.now()}`, { cache: "no-store" })
      .then((res) => res.json())
      .then((data) => { if (data.success) setPublicSignData(data); else setPublicSignError(data.error || "Lien invalide."); })
      .catch(() => setPublicSignError("Vérification impossible — vérifie ta connexion."));
  }, [publicSignToken]);
  useEffect(() => {
    if (!publicSignToken || !publicSignData || publicSignData.status !== "PENDING" || publicSignDone) return;
    const mount = publicSigMountRef.current;
    if (!mount) return;
    publicSigPadRef.current = createSignaturePad(mount, {
      hintText: "Signe ici avec le doigt ou la souris",
      clearLabel: "Effacer",
      strokeColor: "#1a1a1a", borderColor: "#e2e2e2", softBg: "#f7f7f7", inkColor: "#1a1a1a",
      onChange: (valid) => setPublicSigReady(valid),
    });
    return () => { publicSigPadRef.current?.destroy(); publicSigPadRef.current = null; };
  }, [publicSignToken, publicSignData, publicSignDone]);
  const submitPublicSignature = async () => {
    if (!publicSigPadRef.current?.hasValidSignature()) return;
    setPublicSignSubmitting(true);
    try {
      const dataUrl = publicSigPadRef.current.toDataURL();
      const blob = await (await fetch(dataUrl)).blob();
      const formData = new FormData();
      formData.append("signature", blob, "signature.png");
      const res = await fetch(`/api/sign/${publicSignToken}`, { method: "POST", body: formData });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Envoi impossible.");
      setPublicSignDone(true);
    } catch (err) {
      setPublicSignError(err.message || "Envoi impossible.");
    } finally {
      setPublicSignSubmitting(false);
    }
  };

  const NEXORA_LAST_BRANCH_KEY = "nexora_last_branch";
  const [loginBranchId, setLoginBranchId] = useState(() => {
    try { return readWebauthnHint()?.branchId || localStorage.getItem(NEXORA_LAST_BRANCH_KEY) || ""; } catch { return ""; }
  });
  const [loginMsg, setLoginMsg] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  // Mot de passe oublié (retour utilisateur 2026-08-05 — bouton pré-connexion jusqu'ici
  // factice, "simulé dans l'artefact") — deux étapes dans un seul écran : fpStep "request"
  // (POST /api/auth/reset-request) puis "confirm" (POST /api/auth/reset-confirm, une fois
  // qu'un responsable a communiqué le code en personne).
  const [fpStep, setFpStep] = useState("request");
  const [fpProId, setFpProId] = useState("");
  const [fpBranchId, setFpBranchId] = useState("");
  const [fpCode, setFpCode] = useState("");
  const [fpNew1, setFpNew1] = useState("");
  const [fpNew2, setFpNew2] = useState("");
  const [fpMsg, setFpMsg] = useState("");
  // Distinct de fpMsg lui-même (2026-08-20) : jusqu'ici le message de confirmation "demande
  // transmise" s'affichait en rouge, comme une vraie erreur — trompeur pour un message
  // rassurant qui explique justement qu'il n'y a rien d'inquiétant à attendre.
  const [fpMsgType, setFpMsgType] = useState("error");
  const [fpBusy, setFpBusy] = useState(false);
  const openForgotPassword = () => {
    setFpStep("request"); setFpProId(""); setFpBranchId(""); setFpCode(""); setFpNew1(""); setFpNew2(""); setFpMsg(""); setFpMsgType("error");
    setAuthScreen("forgotPassword");
  };
  const submitResetRequest = () => {
    if (!fpProId.trim()) { setFpMsgType("error"); setFpMsg(t("auth.forgotPw.needProId", "Saisis ton Pro-ID.")); return; }
    setFpBusy(true); setFpMsg("");
    // Même filet de sécurité que doLogin (voir sanitizeBranchOnBlur) — jamais une filiale
    // orpheline qui ferait échouer une demande par ailleurs légitime.
    const safeBranchId = storesCatalog.some((s) => s.id === fpBranchId) ? fpBranchId : "";
    apiJSON("/api/auth/reset-request", { method: "POST", body: JSON.stringify({ professionalId: fpProId.trim(), branchId: safeBranchId }) })
      // Retour utilisateur explicite (2026-08-20) : jusqu'ici ce message ne disait pas
      // qu'il est inutile de rester sur place — la personne se sentait obligée d'attendre
      // ici un responsable qui n'est pas toujours immédiatement disponible, sans savoir
      // qu'elle peut fermer l'app et revenir n'importe quand (le code, lui, expire 30 min
      // après sa génération PAR le responsable — pas avant, donc jamais pendant l'attente).
      .then((data) => {
        setFpStep("confirm"); setFpMsgType("info");
        // Bug réel trouvé (retour utilisateur explicite, 2026-08-20, capture d'écran compte
        // allemand) : data.message vient du serveur, TOUJOURS en français (§12, réponse
        // volontairement générique/identique que le compte existe ou non — jamais data-
        // dépendante), donc jamais traduit pour un compte DE/EN/ES/IT. Ce message n'a
        // d'ailleurs jamais besoin d'être data-dépendant ici : toujours ignorer data.message,
        // toujours la propre traduction du client.
        setFpMsg(t("auth.forgotPw.requestSentText", "Demande transmise à un responsable. Tu peux fermer cette page et revenir ici plus tard — pas besoin d'attendre."));
      })
      .catch((err) => { setFpMsgType("error"); setFpMsg(err.message || t("err.actionImpossible", "Action impossible.")); })
      .finally(() => setFpBusy(false));
  };
  const submitResetConfirm = () => {
    setFpMsgType("error");
    if (!fpCode.trim() || !fpNew1 || !fpNew2) { setFpMsg(t("auth.forgotPw.allFieldsRequired", "Tous les champs sont obligatoires.")); return; }
    if (fpNew1.length < 8) { setFpMsg(t("auth.forgotPw.minLength8", "Le nouveau mot de passe doit contenir au moins 8 caractères.")); return; }
    if (fpNew1 !== fpNew2) { setFpMsg(t("auth.forgotPw.mismatch", "Les deux mots de passe ne sont pas identiques.")); return; }
    setFpBusy(true); setFpMsg("");
    apiJSON("/api/auth/reset-confirm", {
      method: "POST",
      body: JSON.stringify({ professionalId: fpProId.trim(), code: fpCode.trim(), newPassword: fpNew1, confirmPassword: fpNew2 }),
    })
      .then(() => {
        showToast(t("toast.passwordChanged", "✓ Mot de passe modifié"));
        setLoginId(fpProId.trim()); setLoginPw(""); setAuthScreen("login");
      })
      .catch((err) => setFpMsg(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setFpBusy(false));
  };

  /* Inscription réelle (§6/§7 du cahier des charges) — un compte ne naît QUE par
     auto-inscription (POST /api/auth/register), jamais créé "à la volée" par un
     responsable (voir le commentaire sur adminScreen plus bas). Le rôle n'est PAS
     demandé ici (retrait délibéré 2026-07-27 côté vanilla) — le compte naît role:"PENDING",
     c'est l'admin qui pose le vrai rôle en validant (voir approveUser). */
  const REGISTER_FORM_EMPTY = { branchId: "", isNetwork: false, proId: "", firstname: "", lastname: "", gender: "", isMinor: false, password: "", confirmPassword: "" };
  const [registerForm, setRegisterForm] = useState(REGISTER_FORM_EMPTY);
  const [registerMsg, setRegisterMsg] = useState("");
  const [registering, setRegistering] = useState(false);
  const [registerPendingText, setRegisterPendingText] = useState("");
  // Suivi du statut d'une demande d'inscription — retour utilisateur explicite (2026-08-20) :
  // jusqu'ici, une fois l'écran "Demande envoyée" fermé, aucun moyen de revenir savoir si le
  // compte a été validé sans retenter une connexion qui échoue silencieusement. Champs
  // séparés de registerForm (jamais réutilisés après un submitRegister réussi qui vide déjà
  // ce dernier) — pré-remplis juste après une inscription, mais aussi atteignables à froid
  // depuis l'écran de connexion (lien dédié) des jours plus tard.
  const [statusCheckProId, setStatusCheckProId] = useState("");
  const [statusCheckBranchId, setStatusCheckBranchId] = useState("");
  const [statusCheckResult, setStatusCheckResult] = useState(null);
  const [statusCheckMsg, setStatusCheckMsg] = useState("");
  const [statusCheckBusy, setStatusCheckBusy] = useState(false);
  const checkRegistrationStatus = async () => {
    const proId = statusCheckProId.trim();
    if (!proId) { setStatusCheckMsg(t("auth.statusCheck.proIdRequired", "Saisis ton Pro-ID.")); return; }
    setStatusCheckMsg(""); setStatusCheckResult(null); setStatusCheckBusy(true);
    try {
      const params = new URLSearchParams({ professionalId: proId });
      if (statusCheckBranchId.trim()) params.set("branchId", statusCheckBranchId.trim());
      const data = await apiJSON(`/api/auth/registration-status?${params.toString()}`);
      setStatusCheckResult(data.status || "NOT_FOUND");
    } catch (err) {
      setStatusCheckMsg(err.message || t("auth.statusCheck.error", "Vérification impossible."));
    } finally {
      setStatusCheckBusy(false);
    }
  };
  const submitRegister = async () => {
    const f = registerForm;
    if (f.password !== f.confirmPassword) { setRegisterMsg(t("auth.register.passwordMismatch", "Les mots de passe ne correspondent pas.")); return; }
    if (!f.proId.trim() || !f.firstname.trim() || !f.lastname.trim() || !f.password) {
      setRegisterMsg(t("auth.register.fieldsRequired", "Identifiant professionnel, prénom, nom et mot de passe obligatoires.")); return;
    }
    if (!f.isNetwork && !f.branchId.trim()) { setRegisterMsg(t("auth.register.branchRequired", "Filiale principale obligatoire.")); return; }
    setRegisterMsg("");
    setRegistering(true);
    try {
      const data = await apiJSON("/api/auth/register", {
        method: "POST",
        body: JSON.stringify({
          branchId: f.branchId.trim(), accountScope: f.isNetwork ? "network" : "branch",
          professionalId: f.proId.trim(), firstname: f.firstname.trim(), lastname: f.lastname.trim(),
          gender: f.gender, isMinor: f.isMinor, password: f.password, confirmPassword: f.confirmPassword, language: lang,
        }),
      });
      // Bug réel trouvé (retour utilisateur explicite, 2026-08-20, capture d'écran compte
      // allemand) : ce texte était en dur en français, jamais traduit — s'affichait tel
      // quel quelle que soit la langue du compte. Même famille de bug que le message serveur
      // de reset-request corrigé juste en dessous (submitResetRequest).
      setRegisterPendingText(
        t("auth.register.pendingTextTemplate", { role: data.requestedRole || "" },
          `Demande envoyée. Statut : en attente de validation. Rôle demandé : ${data.requestedRole || ""}. Un responsable doit valider ton compte avant que tu puisses te connecter.`)
      );
      setStatusCheckProId(f.proId.trim());
      setStatusCheckBranchId(f.isNetwork ? "" : f.branchId.trim());
      setStatusCheckResult(null);
      setStatusCheckMsg("");
      setRegisterForm(REGISTER_FORM_EMPTY);
      setAuthScreen("registerPending");
    } catch (err) {
      setRegisterMsg(err.message || "Impossible de créer le profil.");
    } finally {
      setRegistering(false);
    }
  };

  /* Configuration du tout premier administrateur (POST /api/auth/init-admin) — jamais un
     bouton découvrable dans l'écran de connexion normal (même choix délibéré que côté
     vanilla, commentaire 2026-07-25g de login.html : "ce chemin ne doit plus jamais être
     visible/découvrable dans l'UI normale"), atteignable UNIQUEMENT via l'URL secrète
     ?nexora-setup=admin, elle-même revérifiée contre GET /api/auth/bootstrap-status —
     jamais la seule présence du paramètre d'URL qui décide. */
  const INIT_ADMIN_FORM_EMPTY = { proId: "", firstname: "", lastname: "", storeId: "", password: "", confirmPassword: "" };
  const [initAdminForm, setInitAdminForm] = useState(INIT_ADMIN_FORM_EMPTY);
  const [initAdminMsg, setInitAdminMsg] = useState("");
  const [initAdminBusy, setInitAdminBusy] = useState(false);
  useEffect(() => {
    if (user) return;
    let params;
    try { params = new URLSearchParams(window.location.search); } catch { return; }
    if (params.get("nexora-setup") !== "admin") return;
    apiJSON("/api/auth/bootstrap-status")
      .then((data) => { if (data && data.needsBootstrap) setAuthScreen("initAdmin"); })
      .catch(() => {});
  }, [user]);
  const submitInitAdmin = async () => {
    const f = initAdminForm;
    if (f.password !== f.confirmPassword) { setInitAdminMsg(t("auth.register.passwordMismatch", "Les mots de passe ne correspondent pas.")); return; }
    if (!f.proId.trim() || !f.firstname.trim() || !f.lastname.trim() || !f.password) {
      setInitAdminMsg(t("auth.register.fieldsRequired", "Identifiant professionnel, prénom, nom et mot de passe obligatoires.")); return;
    }
    setInitAdminMsg("");
    setInitAdminBusy(true);
    try {
      await apiJSON("/api/auth/init-admin", {
        method: "POST",
        body: JSON.stringify({
          professionalId: f.proId.trim(), firstname: f.firstname.trim(), lastname: f.lastname.trim(),
          storeId: f.storeId.trim(), password: f.password, confirmPassword: f.confirmPassword, language: lang,
        }),
      });
      setInitAdminForm(INIT_ADMIN_FORM_EMPTY);
      showToast(t("toast.adminCreated", "✓ Administrateur créé — tu peux maintenant te connecter"));
      setAuthScreen("start");
    } catch (err) {
      setInitAdminMsg(err.message || "Impossible de créer l'administrateur.");
    } finally {
      setInitAdminBusy(false);
    }
  };

  useEffect(() => {
    try {
      if (user) localStorage.setItem(NEXORA_USER_CACHE_KEY, JSON.stringify(user));
      else localStorage.removeItem(NEXORA_USER_CACHE_KEY);
    } catch { /* stockage indisponible (navigation privée...) — tant pis, pas bloquant */ }
  }, [user]);

  // Revalidation de la session au démarrage (voir sessionValidating ci-dessus) — une seule
  // fois, jamais à chaque changement de `user` (sinon reboucle sur elle-même après logout/
  // login). /api/auth/ping est déjà protégé par requireAuth côté serveur (session SQLite
  // revalidée contre la vraie table users à chaque requête, jamais un cookie de confiance
  // aveugle) et déjà appelé pour le battement de coeur — aucune route nouvelle. Un 401/403
  // ici signifie une session réellement morte : on efface le cache et on retombe sur
  // l'écran de connexion, jamais une app à moitié authentifiée qui n'affiche jamais aucune
  // donnée. Toute AUTRE erreur (hors-ligne, coupure réseau, 5xx passager) fait confiance au
  // cache tel quel — l'app doit rester utilisable hors-ligne (PWA), pas se déconnecter
  // simplement parce que le réseau est absent au démarrage.
  useEffect(() => {
    if (!sessionValidating) return;
    // finishValidating : ferme l'écran "NEXORA" au plus tôt SESSION_VALIDATION_MIN_MS après
    // son affichage réelle (voir sessionValidationStartRef ci-dessus), jamais avant — une
    // connexion rapide ne doit pas le faire clignoter.
    const SESSION_VALIDATION_MIN_MS = 700;
    const finishValidating = () => {
      const elapsed = Date.now() - sessionValidationStartRef.current;
      const remaining = Math.max(0, SESSION_VALIDATION_MIN_MS - elapsed);
      setTimeout(() => setSessionValidating(false), remaining);
    };
    apiJSON("/api/auth/ping")
      .then(() => finishValidating())
      .catch((err) => {
        if (err && (err.status === 401 || err.status === 403)) {
          // Même correctif de timing que logout() (voir son commentaire, chantier 2026-08-28
          // "fond blanc") : basculer screen/sheet vers un état neutre PENDANT que user est
          // encore renseigné, ne vider user qu'au tick suivant — jamais les deux à la fois.
          setSheet(null); setScreen("home");
          setLocked(false); setUnlockPassword(""); setUnlockError("");
          sessionPasswordRef.current = "";
          showToast(t("auth.sessionExpiredToast", "Session expirée — reconnecte-toi."));
          setTimeout(() => setUser(null), 0);
        }
        finishValidating();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filet de sécurité pour le RESTE de la session (pas seulement le démarrage) — voir
  // lib/api.js:setUnauthorizedHandler. Sans ceci, un cookie qui expire pendant que l'app
  // reste ouverte (les dizaines de useEffect qui chargent des données font tous un simple
  // .catch(() => {}) local) laissait l'app affichée indéfiniment sans plus jamais rien
  // charger — exactement le symptôme "je ferme et rouvre la PWA, l'app s'affiche mais rien
  // ne charge" signalé par l'utilisateur. userRef/firedRef en refs (pas de dépendance
  // useEffect) pour enregistrer le handler UNE SEULE FOIS sans jamais lire un `user` figé
  // dans une closure périmée. /api/auth/ping et /api/auth/verify-password sont exclus : ils
  // gèrent déjà explicitement leur propre 401 juste au-dessus, un double traitement ferait
  // doublon (deux `setUser(null)` sans conséquence, mais un toast en double).
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);
  const unauthorizedFiredRef = useRef(false);
  useEffect(() => {
    setUnauthorizedHandler((url) => {
      if (!userRef.current) return;
      if (url.includes("/api/auth/ping") || url.includes("/api/auth/verify-password")) return;
      if (unauthorizedFiredRef.current) return;
      unauthorizedFiredRef.current = true;
      // Même correctif de timing que logout() (chantier 2026-08-28 "fond blanc") — ce
      // handler peut se déclencher depuis N'IMPORTE QUEL écran/sheet ouvert au moment où le
      // cookie expire (le plus imprévisible des 3 chemins qui vident user) : neutraliser
      // screen/sheet D'ABORD, pendant que user est encore renseigné.
      setSheet(null); setScreen("home");
      setLocked(false); setUnlockPassword(""); setUnlockError(""); // voir commentaire équivalent plus haut (double authentification à la reconnexion)
      sessionPasswordRef.current = "";
      showToast(t("auth.sessionExpiredToast", "Session expirée — reconnecte-toi."));
      setTimeout(() => setUser(null), 0);
      setTimeout(() => { unauthorizedFiredRef.current = false; }, 5000);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --- Navigation --- */
  const [screen, setScreen] = useState("home");        // home | hub | mhdHub | kuhlung | soon | run | store | newArticles | other
  // openModule(m) — extrait tel quel (aucun changement de comportement) de l'onClick du
  // Modules grid d'Accueil, pour être réutilisable depuis la restauration d'écran après un
  // rechargement de page (voir l'effet de restauration plus bas, retour utilisateur explicite
  // 2026-10-14 : "quand je lance l'actualisation d'une page que l'app ne revienne pas sur
  // l'accueil mais plutôt sur la page en cours") — sans ça, restaurer juste `screen` sans
  // rejouer les mêmes chargements (loadStundenleistung, loadRrList, etc.) laisserait l'écran
  // restauré vide, ses données jamais chargées. Les identifiants référencés ici (HOME_MODULES,
  // hasModuleAccess, les setXxxScreen/loadXxx…) sont tous définis plus bas dans ce même
  // composant : safe en JS (fermeture évaluée à l'appel, jamais à la déclaration) tant que
  // openModule() n'est lui-même appelé qu'après le premier rendu complet (clic utilisateur,
  // ou l'effet de restauration ci-dessous qui tourne après montage).
  const openModule = (m) => {
    if (!hasModuleAccess(m.id)) { setDeniedModule(m); setScreen("accessDenied"); return; }
    m.id === "notifications" ? setScreen("notifications") : m.id === "control" ? setScreen("hub") : m.id === "reductions" ? setScreen("reductions") : m.id === "dashboard" ? (setReportingWeekOffset(0), setPlanWeekOffset(0), loadStundenleistung(0), loadCaCompare(), loadDashboardExtras(), setScreen("dashboard")) : m.id === "planning" ? (setPlanScreen("hub"), loadStundenleistung(), loadStundenleistungComparison(planWeekOffset), setScreen("planning")) : m.id === "tasks" ? (setTasksScreen("list"), setTaskFilter("mine"), setTaskScope("store"), setTaskTab("open"), setScreen("tasks")) : m.id === "haccp" ? (setHaccpScreen("hub"), setScreen("haccp")) : m.id === "claims" ? (setClaimsScreen("hub"), setScreen("claims")) : m.id === "krank" ? (setKrankScreen("hub"), setScreen("krank")) : m.id === "material" ? (setMoScreen("hub"), setScreen("material")) : m.id === "scanner" ? (setScanScreen("hub"), setScreen("scanner")) : m.id === "messages" ? (setMsgScreen("list"), setScreen("messages")) : m.id === "infos" ? (setInfoCategoryFilter("general"), setInfoScreen("hub"), setScreen("infos")) : m.id === "rundschreiben" ? (setInfoCategoryFilter("rundschreiben"), setInfoScreen("hub"), setScreen("infos")) : m.id === "reporting" ? (setRepScreen("hub"), setScreen("reporting")) : m.id === "admin" ? (setAdminScreen("hub"), setScreen("admin")) : m.id === "finder" ? (setFinderScreen("hub"), setScreen("finder")) : m.id === "liefer" ? (setLfScreen("hub"), setScreen("liefer")) : m.id === "docintel" ? setScreen("docintel") : m.id === "azl" ? setScreen("azl") : m.id === "missions" ? setScreen("missions") : m.id === "articles" ? (setArtScreen("hub"), setScreen("articles")) : m.id === "inventur" ? (setInventurScreen("hub"), setScreen("inventur")) : m.id === "merchandise" ? (setMerchScreen("hub"), setScreen("merchandise")) : m.id === "labels" ? (setLabelsScreen("hub"), loadLabels(), setScreen("labels")) : m.id === "kassenpost" ? (setKassenpostScreen("hub"), loadKassenpost(), setScreen("kassenpost")) : m.id === "inventurmassnahme" ? (setImScreen("hub"), setScreen("inventurmassnahme")) : m.id === "preisanderung" ? (setPaScreen("hub"), loadPreisanderungen(), setScreen("preisanderung")) : m.id === "warenaustausch" ? (setWaTab("outgoing"), loadWarenaustauschOutgoing(), loadWarenaustauschInbox(), loadKontoauszug(), setScreen("warenaustausch")) : m.id === "filialeReport" ? (setFrTab("mine"), loadFrMine(), loadFrReceived(), setScreen("filialeReport")) : m.id === "rr" ? (setRrScreen("hub"), loadRrList(), setScreen("rr")) : m.id === "kontoauszug" ? (setKontoScreen("hub"), loadKontoChecks(), setScreen("kontoauszug")) : showToast(t("toast.nextArtifactPhaseTemplate", { label: m.label }, `${m.label} — prochaine phase de l'artefact`));
  };
  // Persistance + restauration de l'écran courant au rechargement (retour utilisateur
  // explicite 2026-10-14 : "quand je lance l'actualisation d'une page que l'app ne revienne
  // pas sur l'accueil mais plutôt sur la page en cours"). sessionStorage (pas localStorage —
  // onglet/session, pas un choix permanent cross-session comme nexora_user) : écrit à chaque
  // changement d'écran, lu une seule fois après connexion confirmée (screenRestoredRef) pour
  // rejouer openModule() si l'écran sauvegardé correspond à un module connu — jamais un
  // setScreen() brut, qui laisserait l'écran restauré sans ses données chargées. "hub" est
  // mappé vers le module "control" (seul cas où id de module ≠ valeur de `screen`, voir
  // openModule ci-dessus) ; tout le reste correspond déjà 1:1. Aucune correspondance trouvée
  // (écran profondément imbriqué, accessDenied…) → repli silencieux sur Accueil, jamais d'erreur.
  //
  // Bug trouvé en test local : l'effet d'écriture tourne dès le montage avec `screen` encore à
  // sa valeur par défaut "home" (avant toute restauration) — sans précaution, il écrase la
  // valeur sauvegardée du rechargement précédent AVANT que l'effet de restauration ait pu la
  // lire. lastScreenRef capture donc la valeur sauvegardée une seule fois, à l'initialisation
  // du composant (évalué pendant le rendu, donc strictement avant le premier effet) — la
  // restauration lit CE ref, jamais sessionStorage directement, immunisé contre la course.
  const lastScreenRef = useRef(null);
  if (lastScreenRef.current === null) {
    try { lastScreenRef.current = sessionStorage.getItem("nexora_last_screen") || ""; } catch { lastScreenRef.current = ""; }
  }
  const screenRestoredRef = useRef(false);
  useEffect(() => {
    try { sessionStorage.setItem("nexora_last_screen", screen); } catch {}
  }, [screen]);
  useEffect(() => {
    if (!user || screenRestoredRef.current) return;
    screenRestoredRef.current = true;
    try {
      const saved = lastScreenRef.current;
      if (saved && saved !== "home") {
        const moduleId = saved === "hub" ? "control" : saved;
        const m = HOME_MODULES.find((x) => x.id === moduleId);
        if (m) openModule(m);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  // Pile d'historique générique pour le geste de retour par glissement (edge-swipe) — pas
  // de routeur dans l'app, et les ~32 boutons "Retour" existants ont chacun leur propre
  // logique de destination (pas une seule fonction goBack centralisée, contrairement à ce
  // qu'on pouvait supposer) ; plutôt que de dupliquer cette logique, on empile chaque
  // valeur précédente de `screen` et le glissement fait un vrai "précédent", cohérent avec
  // tous les boutons Retour existants sans y toucher.
  const screenHistoryRef = useRef([]);
  const prevScreenRef = useRef("home");
  useEffect(() => {
    if (prevScreenRef.current !== screen) {
      screenHistoryRef.current.push(prevScreenRef.current);
      if (screenHistoryRef.current.length > 30) screenHistoryRef.current.shift();
      prevScreenRef.current = screen;
    }
  }, [screen]);
  const swipeBackScreen = () => {
    const prev = screenHistoryRef.current.pop();
    if (prev) setScreen(prev);
  };
  // Geste de glissement de bord (edge-swipe), façon iOS — démarre seulement près du bord
  // gauche, suit le doigt en direct, déclenche swipeBackScreen() au-delà du seuil. Inactif
  // sur Accueil (rien à quitter) et en layout desktop/large (nav latérale déjà présente).
  const edgeSwipeRef = useRef({ active: false, startX: 0, startY: 0, dx: 0 });
  const [edgeDragX, setEdgeDragX] = useState(0);
  const EDGE_SWIPE_ZONE = 24;
  const EDGE_SWIPE_THRESHOLD = 90;
  const onRootPointerDown = (e) => {
    if (screen === "home" || isWide) return;
    if (e.clientX > EDGE_SWIPE_ZONE) return;
    edgeSwipeRef.current = { active: true, startX: e.clientX, startY: e.clientY, dx: 0 };
  };
  const onRootPointerMove = (e) => {
    if (!edgeSwipeRef.current.active) return;
    const dx = e.clientX - edgeSwipeRef.current.startX;
    const dy = e.clientY - edgeSwipeRef.current.startY;
    if (Math.abs(dy) > Math.abs(dx) + 20) { edgeSwipeRef.current.active = false; setEdgeDragX(0); return; }
    edgeSwipeRef.current.dx = Math.max(0, dx);
    setEdgeDragX(Math.min(edgeSwipeRef.current.dx, 160));
  };
  const onRootPointerUp = () => {
    if (!edgeSwipeRef.current.active) return;
    const dx = edgeSwipeRef.current.dx;
    edgeSwipeRef.current.active = false;
    setEdgeDragX(0);
    if (dx > EDGE_SWIPE_THRESHOLD) swipeBackScreen();
  };
  const [soonCat, setSoonCat] = useState(null);
  const [otherTab, setOtherTab] = useState(null);
  const tabBarRef = useRef(null);
  const [tabDrag, setTabDrag] = useState(null); // { x, width } en pixels, uniquement pendant le glissement
  const [liveOpen, setLiveOpen] = useState(false);

  /* Assistant Accueil — replié par défaut, invite à glisser vers le bas
     pour révéler les tâches du jour, se referme tout seul après un délai.
     dragY suit le doigt en direct pendant le geste ; assistantOpen est
     l'état final une fois le geste terminé (ou après un tap sur la
     flèche, pour l'accessibilité). */
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantOpenedAt, setAssistantOpenedAt] = useState(0);
  const [assistantHeld, setAssistantHeld] = useState(false);
  const [assistantDragY, setAssistantDragY] = useState(0);
  const assistantDragState = useRef({ dragging: false, startY: 0 });
  const assistantCloseTimer = useRef(null);
  const assistantTimerState = useRef({ startedAt: 0, remainingMs: 0 });
  const assistantAutoCloseMs = 8000;
  const clearAssistantTimer = () => { if (assistantCloseTimer.current) { clearTimeout(assistantCloseTimer.current); assistantCloseTimer.current = null; } };
  // Même esprit "l'assistant rédige" que le Rapport intelligent (vue GR), mais déclenché
  // au geste (ouverture de la bannière) plutôt qu'au montage — retour utilisateur explicite
  // 2026-08-03.
  const [assistantTyping, setAssistantTyping] = useState(false);
  const assistantTypingTimer = useRef(null);
  const clearAssistantTypingTimer = () => { if (assistantTypingTimer.current) { clearTimeout(assistantTypingTimer.current); assistantTypingTimer.current = null; } };
  const openAssistant = () => {
    clearAssistantTimer();
    setAssistantOpen(true);
    setAssistantHeld(false);
    setAssistantOpenedAt(Date.now());
    assistantTimerState.current = { startedAt: Date.now(), remainingMs: assistantAutoCloseMs };
    assistantCloseTimer.current = setTimeout(() => setAssistantOpen(false), assistantAutoCloseMs);
    clearAssistantTypingTimer();
    setAssistantTyping(true);
    assistantTypingTimer.current = setTimeout(() => setAssistantTyping(false), 700);
  };
  const closeAssistant = () => { clearAssistantTimer(); clearAssistantTypingTimer(); setAssistantOpen(false); setAssistantHeld(false); };
  /* Maintenir le doigt sur l'assistant ouvert met le compte à rebours en
     pause (on retient le temps déjà écoulé) ; le relâcher reprend le
     minuteur pour le temps RESTANT — jamais un redémarrage complet. */
  const pauseAssistantTimer = () => {
    if (!assistantOpen || assistantHeld) return;
    const elapsed = Date.now() - assistantTimerState.current.startedAt;
    assistantTimerState.current.remainingMs = Math.max(400, assistantTimerState.current.remainingMs - elapsed);
    clearAssistantTimer();
    setAssistantHeld(true);
  };
  const resumeAssistantTimer = () => {
    if (!assistantOpen || !assistantHeld) return;
    setAssistantHeld(false);
    assistantTimerState.current.startedAt = Date.now();
    assistantCloseTimer.current = setTimeout(() => setAssistantOpen(false), assistantTimerState.current.remainingMs);
  };
  useEffect(() => () => { clearAssistantTimer(); clearAssistantTypingTimer(); }, []);

  // Assistant réseau (GR) — même grammaire que l'Assistant compte normal ci-dessus (bannière
  // repliée + expansion avec effet de frappe + minuteur de fermeture auto 8s), mais état
  // dédié : un NEXORA_ADMIN peut basculer entre vue GR et vue normale dans la même session
  // (voir storeflow_admin_self_registration_correction), donc jamais partager l'état avec
  // l'assistant employé sous peine de contamination croisée entre les deux vues. Remplace
  // l'ancien Accueil GR "dashboard" (bande stats + tuile Analyse + flux urgences à plat) —
  // retour utilisateur explicite (2026-08-09) : l'Accueil ne doit jamais dupliquer Analyse,
  // qui reste le seul dashboard réseau détaillé.
  const [netAssistantOpen, setNetAssistantOpen] = useState(false);
  const [netAssistantTyping, setNetAssistantTyping] = useState(false);
  const netAssistantCloseTimer = useRef(null);
  const netAssistantTypingTimer = useRef(null);
  const netAssistantAutoCloseMs = 8000;
  const openNetAssistant = () => {
    if (netAssistantCloseTimer.current) clearTimeout(netAssistantCloseTimer.current);
    setNetAssistantOpen(true);
    netAssistantCloseTimer.current = setTimeout(() => setNetAssistantOpen(false), netAssistantAutoCloseMs);
    if (netAssistantTypingTimer.current) clearTimeout(netAssistantTypingTimer.current);
    setNetAssistantTyping(true);
    netAssistantTypingTimer.current = setTimeout(() => setNetAssistantTyping(false), 700);
  };
  const closeNetAssistant = () => {
    if (netAssistantCloseTimer.current) clearTimeout(netAssistantCloseTimer.current);
    if (netAssistantTypingTimer.current) clearTimeout(netAssistantTypingTimer.current);
    setNetAssistantOpen(false);
  };
  useEffect(() => () => {
    if (netAssistantCloseTimer.current) clearTimeout(netAssistantCloseTimer.current);
    if (netAssistantTypingTimer.current) clearTimeout(netAssistantTypingTimer.current);
  }, []);

  /* Catalogue + contrôle MHD Kühlung — vraies données StoreFlowProV2 (Phase 1). articles
     remplace articles (GET /api/articles, zone Kühlung, déjà trié par sortOrder côté
     serveur). records = GET /api/records?source=KUEHLUNG. touched = GET /api/touches (la
     vraie source de "déjà passé en revue aujourd'hui", PAS dérivée de records). */
  const [articles, setArticles] = useState([]);
  const [records, setRecords] = useState([]);
  // Bug réel trouvé (retour utilisateur explicite, 2026-08-12) : l'onglet Expirées de
  // Réductions est censé rester voir les MHD Kühlung retirées du Contrôle à minuit
  // (removedFromControlAt, voir sfRemoveExpiredFromControl côté serveur — le commentaire du
  // serveur affirme explicitement que Réductions passe ?includeExpiredRemoved=1), mais
  // `records` ci-dessus (loadKuhlungRecords, utilisé aussi par le Contrôle) ne le passe
  // jamais — donc une MHD expirée disparaissait des DEUX écrans à la fois dès le premier
  // minuit passé, pas seulement du Contrôle. Cet état séparé ne contient QUE le surplus
  // (les records ayant réellement removedFromControlAt, jamais présents dans `records` par
  // construction — mutuellement exclusifs) ; redSources (Réductions) fusionne les deux,
  // records reste inchangé pour le Contrôle et tout le reste de l'app.
  const [kuhlungExpiredRemoved, setKuhlungExpiredRemoved] = useState([]);
  const [storeRecords, setStoreRecords] = useState([]);
  const [storeTrash, setStoreTrash] = useState([]);
  // Réductions a besoin de TOUTES les MHD Magasin actives (une réduction J-30 peut porter
  // sur un article saisi des semaines avant) — storeRecords est volontairement limité au
  // jour même (Contrôle > Magasin, "Saisies du jour"), donc jamais réutilisable ici sans
  // faire disparaître les articles promo magasin plus anciens de Réductions (bug réel
  // trouvé — leurs cartes n'apparaissaient jamais). État séparé, chargé sans filtre date.
  const [redStoreRecords, setRedStoreRecords] = useState([]);
  // Écran "DLC Articles promotionnels kühlung" : vrais articles de la zone spéciale
  // "Kühlung-Nouveaux" (forme API brute {id,artNr,name,zone,...}) — leurs MHD/DLC vivent
  // dans `records` (source KUEHLUNG), pas ici. Voir loadNewArticles()/openNewArtSheet().
  const [newArticles, setNewArticles] = useState([]);
  // Même mécanisme, zone spéciale "Frisch Fleisch-Nouveaux" — MHD/DLC dans `czRecords`
  // (source FRISCHFLEISCH), pas dans `records` (réservé Kühlung). Voir
  // loadNewArticlesFF()/openNewArtSheetFF() plus bas.
  const [newArticlesFF, setNewArticlesFF] = useState([]);

  /* Module Articles — vraie base articles (Phase 1). articlesCatalog = GET
     /api/articles?status=all (catalogue complet, y compris inactifs — le Contrôle
     Kühlung ci-dessus ne charge QUE la zone Kühlung active, ce module a besoin de
     tout, d'où un state et un chargement dédiés plutôt que de réutiliser `articles`).
     articlesMhdCounts = nombre de records par artNr (badge "MHD" sur la carte),
     lecture de /api/records?includeExpiredRemoved=1&includeDoneRecords=1 (même
     historique complet que le vanilla). La 5e catégorie du hub, Prebake, n'est PAS
     dans ce catalogue — elle réutilise repCatalog/loadReportingCatalog/
     openArticleModal/saveArticleModal/setArticleStatus déjà construits pour
     Reporting (server: /api/reporting/articles, family=prebake) — voir la
     justification dans public/modules/articles.js (PREBAKE_FAMILY). */
  const [articlesCatalog, setArticlesCatalog] = useState([]);
  const [articlesMhdCounts, setArticlesMhdCounts] = useState({});
  const [artScreen, setArtScreen] = useState("hub"); // hub|list|reports|reportForm
  const [artZone, setArtZone] = useState(null); // null = tous les rayons
  const [artQuery, setArtQuery] = useState("");
  const [artFilter, setArtFilter] = useState("all"); // all|active|inactive|missing-price
  // Retour utilisateur explicite (2026-08-12) : le plafond fixe à 180 (sans façon de charger
  // le reste) faisait croire à un désordre — un catalogue Kühlung de 380 articles s'arrêtait
  // net à la position 180 avec juste un texte "affine ta recherche", aucun moyen de
  // parcourir le reste dans l'ordre. Même patron "Afficher plus" déjà utilisé et validé par
  // Scanner (scanListBatches) — batches de 180 pour garder la même densité d'écran initiale.
  const [artListBatches, setArtListBatches] = useState(1);
  useEffect(() => { if (artScreen === "list") setArtListBatches(1); }, [artScreen, artZone, artQuery, artFilter]);
  // Signalement nouveaux articles — retour utilisateur explicite (2026-08-12), artefact
  // https://claude.ai/code/artifact/cc4f3822-a2af-4f10-b0e4-b8fd19880e9a approuvé à
  // reproduire à l'identique. Historique résolu = entrée supprimée côté serveur (voir
  // server/articleReports/), pas un état "archivé" séparé à garder ici.
  const [articleReports, setArticleReports] = useState([]);
  const [articleReportForm, setArticleReportForm] = useState({ photoUrl: "", comment: "", rayon: "" });
  const [articleReportPhotoUploading, setArticleReportPhotoUploading] = useState(false);
  const [articleReportSubmitting, setArticleReportSubmitting] = useState(false);
  const articleReportPhotoInputRef = useRef(null);
  const [artPriceVisible, setArtPriceVisible] = useState(() => {
    try { return localStorage.getItem("storeflow_articles_price_visible") !== "0"; } catch { return true; }
  });
  const [artForm, setArtForm] = useState(null); // sheet "articleEditor"
  const [artMoveState, setArtMoveState] = useState(null); // sheet "articleMove"
  // Raccourci Nom/Art.Nr/VE depuis Run Kühlung (2026-08-31, retour utilisateur explicite :
  // corriger une fiche sans quitter le contrôle, tap = rechercher un article, double-tap =
  // éditer) — réutilise PATCH /api/articles/:id (donc syncRecordsForArticleChange) au lieu
  // d'une logique séparée. Voir handleRunFieldTap/commitRunHeroEdit/commitRunJump/
  // commitRunVeEdit, même mécanique de ref{key,timer} déjà établie pour le double/triple-tap
  // de Planning (shiftTapRef/shiftTripleTapRef).
  const [runHeroEdit, setRunHeroEdit] = useState(null); // { field: "name"|"artNr", value } | null
  const [runHeroBusy, setRunHeroBusy] = useState(false);
  const [runVeEdit, setRunVeEdit] = useState(null); // string | null — simple tap, pas de double-tap (champ non identifiant)
  const [runJumpValue, setRunJumpValue] = useState(null); // string | null — recherche "aller à un article"
  const runFieldTapRef = useRef({ key: "", timer: null });

  /* Livraison Kühlung — réception du jour, scan en boucle (retour utilisateur explicite,
     2026-08-11 : "l'idée n'est pas d'afficher la liste kühlung dans la réception mais
     plutôt de scanner chaque article reçu... et ajouter tout ces mhd directement dans le
     contrôle kuhlung"). Avant, saveDelivery postait source:"DELIVERY" — un silo à part que
     Contrôle Kühlung et Réductions ne voyaient JAMAIS (bug d'architecture confirmé en
     lisant le code, records=source KUEHLUNG uniquement). deliveryReceived n'est donc plus
     un état chargé séparément : c'est juste `records` (déjà chargé par loadKuhlungRecords
     pour tout le monde) filtré sur la date du jour — une seule vraie source de données.
     Non conservé au-delà d'un rechargement de page pour les MHD entrées via Contrôle "run"
     le même jour : c'est voulu, "aujourd'hui" doit refléter toute l'activité MHD Kühlung
     du jour, pas seulement celle passée par le scan de réception. */
  const [delSearch, setDelSearch] = useState("");
  const [delManualSearchOpen, setDelManualSearchOpen] = useState(false);
  const [delArt, setDelArt] = useState(null);
  const [delQty, setDelQty] = useState(1);
  const [delMhd, setDelMhd] = useState("");
  // Onglet En attente/Reçus (retour utilisateur explicite, 2026-08-16 : "toujours avoir le
  // bouton pour scanner à bonne hauteur du doigt... tout en ayant la liste des articles reçu
  // et non reçu") — remplace la sheet delMissing comme seul accès à "non reçus", devient la
  // vue principale à côté du scan, pas juste une liste secondaire dans une sheet.
  const [delTab, setDelTab] = useState("pending");

  /* Association EAN ↔ article : les codes vivent DIRECTEMENT sur l'article
     réel (article.ean/barcode/barcodes/eans, PATCH /api/articles/:artNr) —
     même contrat que public/modules/barcode-decode.js
     (linkCodeToArticle/unlinkCodeFromArticle/articleCodes), pas un registre
     local séparé qui ne persisterait jamais. Voir articleCodesFor/
     articleForCode/linkCodeToArticle/unlinkCodeFromArticle plus bas. */
  const [pendingCode, setPendingCode] = useState("");
  const [scanCtx, setScanCtx] = useState("run");
  const [lastScanCode, setLastScanCode] = useState("");
  const [linkSearch, setLinkSearch] = useState("");
  const scanFileInputRef = useRef(null); // <input type=file capture> réel — voir doScan/handleScanFileChange
  const scanCtxRef = useRef("run");

  /* Réductions — règle J-3 avant la MHD (l'exception J-2 par article et les
     jours fériés se branchent à la conversion, la liste J-2 vit côté serveur).
     Les cartes viennent des MHD saisies dans Contrôle (Kühlung/Livraison)
     et des DLC Magasin — rien n'est dupliqué, tout est dérivé. */
  // Vraies données (Phase 1) : reduced = GET /api/done, redTrash = GET /api/records?trashed=1
  // (mécanisme générique de corbeille, pas propre à Réductions — le même que .../:id/trash
  // déjà utilisé côté Contrôle pour storeRecords/storeTrash).
  const [reduced, setReduced] = useState([]);     // { recId, doneId, doneAt, source, artNr, name, mhd, quantity, zone }
  const [redTab, setRedTab] = useState("todo");   // todo | done | expired | trash
  // Hub par catégorie (retour utilisateur explicite) — null = hub, sinon "KUEHLUNG" | "STORE"
  // (les deux seules provenances réelles aujourd'hui, voir redSources ; une future 3e
  // catégorie n'aurait qu'à ajouter sa carte dans REDUCTION_CATEGORIES, rien d'autre à
  // changer dans la logique de filtrage/tri ci-dessous).
  const [redCategory, setRedCategory] = useState(null);
  const [redTrash, setRedTrash] = useState([]);   // records supprimés, restaurables
  /* Dérogation de règle de réduction par article : artNr → une des règles disponibles de
     l'app (voir ruleDefinitions ci-dessous). Généralisé le 2026-08-05 depuis l'ancienne
     "fenêtre" arbitraire (2 à 30 jours, purement locale, jamais persistée ni lue par le
     serveur ou reductions.js vanilla) — retour utilisateur explicite : "implémente les 3
     règles qu'on a actuellement dans la roue, pour que l'utilisateur puisse assigner
     lui-même une règle à un article". Persisté via GET/POST/DELETE
     /api/reductions/rule-overrides, partagé avec le serveur (notifications d'accueil) et
     reductions.js vanilla (même table).
     ruleDefinitions (2026-08-17, retour utilisateur explicite : "plusieurs choix au cas où
     les règles changeraient dans l'entreprise") — l'ENSEMBLE des règles disponibles était
     figé en dur à exactement 3 (J2_OPEN/J3_OPEN/J30_CALENDAR), devient une vraie liste
     chargée depuis GET /api/reductions/rule-definitions (gérable via Réductions > NEXORA_ADMIN
     uniquement, voir openReductionRulesAdmin). Les 3 ID historiques restent utilisés tels
     quels par le serveur (ensemencement identique) et par sourceDefaults (2026-08-17,
     voir loadSourceDefaults/saveSourceDefault — remplace l'ancien objet REDUCTION_SOURCE_
     DEFAULT_RULE figé en dur, désormais GET/PUT /api/reductions/source-defaults) ci-dessous
     — aucune régression sur les dérogations déjà enregistrées. */
  const [ruleDefinitions, setRuleDefinitions] = useState([]);
  // Repli sûr (mêmes valeurs que l'ancien objet en dur) tant que le chargement réseau
  // n'a pas encore abouti, ou si un id référencé n'existe plus — jamais planter l'écran
  // Réductions pour autant.
  const ruleDefinitionsMap = {
    J2_OPEN: { days: 2, calendar: false }, J3_OPEN: { days: 3, calendar: false }, J30_CALENDAR: { days: 30, calendar: true },
    ...Object.fromEntries(ruleDefinitions.map((d) => [d.id, d])),
  };
  const [ruleOverrides, setRuleOverrides] = useState({});
  const [ruleSearch, setRuleSearch] = useState("");
  const [ruleArt, setRuleArt] = useState(null);
  // Sélecteur de règle "rapide", accessible directement depuis les 3 écrans Contrôle MHD
  // Aktion (Kühlung/Frisch Fleisch/Magasin) — contrairement à ruleArt (recherche dans
  // articlesCatalog, le catalogue officiel), ces articles promotionnels n'y figurent
  // JAMAIS, donc l'écran Réductions > Règles existant était inatteignable pour eux. Même
  // moteur (setArticleRule), juste un point d'entrée direct par (artNr, name) déjà connus.
  const [quickRuleArt, setQuickRuleArt] = useState(null); // { artNr, name } | null
  // Défauts de règle PAR GROUPE (2026-08-17, retour utilisateur explicite : "au lieu qu'il
  // charge directement la liste des articles kühlung par défaut [...] d'abord donner le
  // choix à l'utilisateur de choisir quel groupe d'article [...] et appliquer [une règle à]
  // ce groupe") — GET/PUT /api/reductions/source-defaults, remplace REDUCTION_SOURCE_
  // DEFAULT_RULE figé en dur ci-dessus. redRulesMode pilote l'écran d'ouverture de la sheet
  // "redRules" : null (choix initial) | "group" (choisir puis éditer un groupe) | "article"
  // (recherche/scan un article précis, mécanisme déjà existant) | "admin" (gestion des
  // définitions J-X, déplacée hors de la liste d'articles qui la rendait quasi inatteignable
  // — retour utilisateur, capture à l'appui : "il est complètement en bas de la liste
  // kühlung parce que la liste charge par défaut").
  const [redRulesMode, setRedRulesMode] = useState(null);
  const [redRulesGroup, setRedRulesGroup] = useState(null);
  const [sourceDefaults, setSourceDefaults] = useState({});
  // Réutilisé à l'ouverture (retour à l'écran de choix, jamais un état figé d'une session
  // précédente) ET à la fermeture de la sheet "redRules".
  const resetRedRulesNav = () => { setRuleArt(null); setRuleSearch(""); setRedRulesMode(null); setRedRulesGroup(null); };
  // Gestion des définitions de règles J-X (NEXORA_ADMIN uniquement, 2026-08-17) — formulaire
  // d'ajout + édition en place, voir la section admin dans la sheet "redRules".
  const emptyRuleDefForm = { label: "", days: "", calendar: false };
  const [ruleDefForm, setRuleDefForm] = useState(emptyRuleDefForm);
  const [ruleDefEditingId, setRuleDefEditingId] = useState(null);
  // Bug réel corrigé (2026-08-17, retour utilisateur : "au clic il n'y a rien qui se
  // produit") : le formulaire d'ajout se décidait auparavant sur `ruleDefForm.label ||
  // ruleDefForm.days`, or le bouton "Ajouter" posait justement ces deux champs à leur
  // valeur VIDE (emptyRuleDefForm) — la condition restait donc fausse après le clic,
  // aucun re-rendu visible. État dédié, jamais déduit des valeurs du formulaire.
  const [addingRuleDef, setAddingRuleDef] = useState(false);

  // "+ Ajouter une règle" inline, depuis N'IMPORTE QUEL sélecteur de règle (création
  // d'article Aktion, ⚙️ sur une carte, dérogation par article) — retour utilisateur
  // explicite (2026-08-21) : "je veux pouvoir ajouter à J-X au besoin", sans passer par
  // l'écran "Gérer les règles disponibles" (NEXORA_ADMIN). Même endpoint que ce dernier
  // (POST /api/reductions/rule-definitions) — la règle créée rejoint immédiatement la
  // liste partagée, jamais une règle "locale" à cette carte. État séparé de
  // ruleDefForm/addingRuleDef ci-dessus (écran admin plein CRUD) pour ne jamais les faire
  // interférer si les deux étaient ouverts en même temps. inlineRuleAddKey identifie quel
  // sélecteur (parmi les 4 endroits qui partagent ce patron) a son formulaire déplié.
  const emptyInlineRuleForm = { label: "", days: "", calendar: false };
  const [inlineRuleForm, setInlineRuleForm] = useState(emptyInlineRuleForm);
  const [inlineRuleAddKey, setInlineRuleAddKey] = useState(null);

  /* Contrôle MHD — Trocken / Frisch Fleisch / Frisch Brot (2026-08-05, retour utilisateur
     explicite : "comme Kühlung", catalogue dédié + écran de saisie MHD via Contrôle). Le
     catalogue existe déjà (ARTICLE_ZONES contient ces 3 zones depuis le module Articles) —
     seul manquait un écran de saisie MHD dédié et le rattachement à Réductions. Version
     volontairement plus légère que le "run" séquentiel Kühlung (recherche + ajout MHD par
     article, pas de scan code-barres position-par-position) — le scan/run complet pourra
     être ajouté plus tard si besoin, ceci débloque déjà les 3 catégories vides de
     Réductions. `source` = valeur exacte utilisée par REDUCTION_CATEGORIES (aucune
     conversion nécessaire côté Réductions) ; `zone` = valeur exacte de ARTICLE_ZONES/
     RAYON_ZONES (fait fonctionner /api/control-state et l'alerte d'accueil sans rien
     changer côté serveur). */
  const CONTROL_ZONES = {
    trocken: { id: "trocken", zone: "Trocken", source: "TROCKEN", label: "Trocken", emoji: "📦", bg: A.orange },
    fleisch: { id: "fleisch", zone: "Frisch Fleisch", source: "FRISCHFLEISCH", label: "Frisch Fleisch", emoji: "🥩", bg: A.red },
    brot: { id: "brot", zone: "Frisch Brot", source: "FRISCHBROT_BROT", label: "Frisch Brot", emoji: "🍞", bg: A.brown },
  };
  const [czZone, setCzZone] = useState(null);
  const [czRecords, setCzRecords] = useState([]);
  // Même mécanisme que kuhlungExpiredRemoved (voir plus haut) — Trocken/Frisch Fleisch/Frisch
  // Brot avaient le même bug jamais corrigé le 2026-08-12 : une MHD retirée du Contrôle à
  // minuit disparaissait aussi de Réductions au lieu de passer dans l'onglet Expirées.
  const [czExpiredRemoved, setCzExpiredRemoved] = useState([]);
  const [czQuery, setCzQuery] = useState("");
  const [czMhdArt, setCzMhdArt] = useState(null);
  const [czMhdForm, setCzMhdForm] = useState({ mhd: "", qty: "1", note: "", photoUrl: "" });
  const [czEditId, setCzEditId] = useState(null);
  const [czUploading, setCzUploading] = useState(false);
  const czPhotoInputRef = useRef(null);

  /* Planning — plan de travail (shifts) vraies données, GET/POST /api/plannings.
     weekPlan = le vrai plan normalisé de la semaine courante (planWeekOffset), rechargé à
     chaque changement de semaine ; publishedWeeksList = résumé réel pour l'écran
     "published" (GET /api/plannings/published). Le serveur ne connaît qu'un weekStart
     (lundi ISO), jamais un "offset" — voir mondayOfWeekOffset(). */
  const [planScreen, setPlanScreen] = useState("hub");  // hub | week | requests | vacation | leistungsfrei | holidays
  const [planWeekOffset, setPlanWeekOffset] = useState(0);
  const [weekPlan, setWeekPlan] = useState(null);
  // Cache local du dernier total RÉEL synchronisé (POST .../sync-stundenleistung renvoie
  // juste updatedMonths, pas de total à afficher) — consommé par la carte Stundenleistung
  // du Dashboard (screen==="dashboard"), qui n'est pas encore migrée (Reporting, phase
  // ultérieure) mais lisait déjà cet état avant cette migration.
  const [syncedHours, setSyncedHours] = useState({});
  const [publishedWeeksList, setPublishedWeeksList] = useState([]);
  // Date de naissance réelle par employé (GET /api/users, la seule route qui l'expose —
  // /api/tasks/store-employees ne la porte pas) : seule vraie source pour la règle de
  // pause spécifique mineur (Jugendarbeitsschutzgesetz).
  const [planUsersBirthdate, setPlanUsersBirthdate] = useState({});
  // Filet de sécurité (retour utilisateur 2026-08-05, "comportement faux pour un azubi de
  // moins de 18 ans") : la case "mineur" cochée à l'inscription (users.isMinor) est un
  // signal DÉCLARÉ, distinct de la date de naissance — un employé peut être un vrai mineur
  // sans que sa date de naissance ait jamais été renseignée par la suite. isMinorEmployeeId
  // ignorait ce champ et retombait silencieusement sur "majeur par défaut". La date de
  // naissance reste prioritaire quand elle existe (elle se met à jour toute seule le jour
  // des 18 ans, contrairement à ce booléen figé) ; ce flag n'est qu'un filet si elle manque.
  const [planUsersIsMinorFlag, setPlanUsersIsMinorFlag] = useState({});
  // Soll Stundenleistung réel par date (GET /api/reporting/stunden-plan, gated
  // reporting.view — échec silencieux si l'employé connecté n'a pas ce droit, jamais
  // bloquant, même principe que côté vanilla planning.js loadStundenleistungComparison).
  const [stundenleistungCache, setStundenleistungCache] = useState({});
  // Planungsanfragen — vraies données, GET/POST /api/planning-requests. Le serveur filtre
  // déjà lui-même (un non-manager ne voit jamais que ses propres demandes, un FL/FLA voit
  // toute la filiale) — jamais reproduit ce filtre côté client. "week"/"requestedDay"
  // (Freiwunsch) sont dérivés d'une seule date choisie (voir submitRequest) : le vrai
  // formulaire vanilla utilise directement ces champs, pas de date range pour ce type.
  const [planRequests, setPlanRequests] = useState([]);
  const [reqForm, setReqForm] = useState({ type: "freiwunsch", date: "", endDate: "", comment: "" });
  // { id?, employeeId, emp, day, status, start, end, pauseMin, leistungsfreiHours, note }
  const [shiftForm, setShiftForm] = useState(null);
  // Soldes en direct affichés dans la sheet créneau (jamais mis en cache entre deux
  // ouvertures — voir openShiftSheet) : { text, available }.
  const [shiftLfHint, setShiftLfHint] = useState({ text: "", available: Infinity });
  const [shiftVacationHint, setShiftVacationHint] = useState({ text: "", available: Infinity });
  // Vrai (pas juste désactivé) si une tentative de publication vient d'être bloquée par un
  // dépassement de disponibilité Aushilfe — le bouton propose alors explicitement de forcer.
  const [publishForcePending, setPublishForcePending] = useState(false);
  const [planWeekView, setPlanWeekView] = useState("days"); // days | grid
  // Employé ciblé par le bouton "+" de la vue Jours — sans ça, le bouton devine (premier
  // sans créneau ce jour) et pouvait tomber sur un Aushilfe indisponible ce jour-là,
  // obligeant un double-tap pour confirmer "planifier quand même" (retour utilisateur
  // explicite, 2026-08-13). "" = pas de sélection, la devinette reste le repli.
  const [planDayFocusEmp, setPlanDayFocusEmp] = useState("");
  // Un plan déjà publié est consultable uniquement par défaut (jamais de sheet au clic
  // d'une case, jamais de bouton copier/vider) — seuls FL/FLA peuvent explicitement le
  // déverrouiller via "Modifier le plan" pour le rééditer, puis le republier (le
  // republiement rafraîchit publishedAt, donc réapparaît naturellement en tête de la
  // liste "Plans publiés" avec une nouvelle date de publication). Se reverrouille dès
  // qu'on change de semaine consultée, pour ne jamais laisser un déverrouillage traîner
  // sur la mauvaise semaine.
  const [planEditUnlocked, setPlanEditUnlocked] = useState(false);
  useEffect(() => { setPlanEditUnlocked(false); }, [planWeekOffset]);
  const [calYear, setCalYear] = useState(THIS_YEAR);
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calViewMode, setCalViewMode] = useState("month"); // month | year
  // Aushilfe-Verfügbarkeit — vraies données GET/POST /api/aushilfe-availability. Deux
  // sources distinctes : planWeekAvailability suit la semaine du PLAN DE TRAVAIL consulté
  // (isUnavailable ci-dessous), l'écran dédié a sa propre semaine INDÉPENDANTE
  // (availWeekOffset, jamais synchronisée — vanilla paWeek).
  const [planWeekAvailability, setPlanWeekAvailability] = useState([]);
  const [availWeekOffset, setAvailWeekOffset] = useState(0);
  const [ownAvailDays, setOwnAvailDays] = useState(null);
  const [ownAvailRecordId, setOwnAvailRecordId] = useState("");
  const [availabilityList, setAvailabilityList] = useState([]);
  const [availDayEdit, setAvailDayEdit] = useState(null); // { day } — index PLAN_DAY_KEYS, édition soi-même

  /* Tâches — vraies données (Phase 1). Le vrai serveur stocke type/category/statuses par
     filiale (pas un simple status global) — mapServerTask() reconstruit la forme locale
     {scope,type,status,assignee...} déjà consommée par le JSX, sans le toucher. */
  const [tasks, setTasks] = useState([]);
  const [taskEmployees, setTaskEmployees] = useState([]); // [{employeeId,name,role}] — GET /api/tasks/store-employees
  // Plus de "hub" intermédiaire (retour utilisateur explicite, 2026-08-02 : "supprime
  // mission reseau dans taches et la page intermediaire pour qu'au clic sur tache qu'on
  // accede directement a taches filiale") — Missions réseau a de toute façon son propre
  // écran dédié (screen==="missions", server/missions/*), plus besoin de ce doublon.
  // Plus d'écran "hub" séparé depuis le 2026-08-07 (retour utilisateur explicite : page
  // "Tâches filiale" supprimée, son contenu devenu des chips de catégorie sur "list" même).
  const [tasksScreen, setTasksScreen] = useState("list");   // list | new
  const [taskScope, setTaskScope] = useState("store");     // store | network
  // 3 tuiles d'entrée — retour utilisateur explicite (2026-08-06) : "chacune des
  // possibilités qui existent déjà [dans le module] doit être implémentée dans chaque
  // tuile" — les 3 recipients possibles existaient déjà côté données (targetEmployeeId/
  // createdById, jamais exploités pour catégoriser la LISTE elle-même). null = pas de
  // filtre (deep-link notification, "voir tout" implicite, comportement inchangé).
  // mine = je suis LE destinataire spécifique. team = équipe entière, sauf les tâches que
  // j'ai moi-même créées (le donneur d'ordre ne se voit pas sa propre tâche à faire).
  // delegated = j'ai créé une tâche ciblée sur quelqu'un d'autre (suivi de délégation).
  const [taskFilter, setTaskFilter] = useState(null);
  // Second niveau pour la tuile "Tâches de l'équipe" uniquement (retour utilisateur
  // explicite, 2026-08-07) : "received" = tâches équipe créées par quelqu'un d'autre
  // (comportement historique) ; "mine" = mes propres tâches équipe — remplace la tuile
  // "Historique" supprimée (voir commentaire sur TASK_FILTER_PREDICATES).
  const [teamSubView, setTeamSubView] = useState("received");
  const [taskTab, setTaskTab] = useState("open");          // open | done | archived
  // Design porté depuis l'artefact "Tableau de service" (retour utilisateur explicite,
  // implémentation à 100%) — "En cours" toujours affichée, Terminées/Archivées repliées par
  // défaut mais cliquables pour se déplier (remplace les 3 onglets qui forçaient à choisir
  // une seule vue à la fois). Remis à false à chaque changement de tuile/sous-vue (voir les
  // handlers de chips) pour ne jamais laisser une section dépliée "fuiter" d'une catégorie à
  // l'autre.
  const [taskSectionsOpen, setTaskSectionsOpen] = useState({ done: false, archived: false });
  // bakeryQty : { articleId: quantité } — l'utilisateur défile le vrai catalogue Prebake
  // (catalogArticlesFor, /api/reporting/articles) et tape +/- directement sur chaque
  // article, plus de ligne à ajouter manuellement via un menu déroulant.
  // assignees[] (pluriel) sert UNIQUEMENT à la tuile "Tâches confiées" — sélection
  // multi-employés directe (retour utilisateur explicite, 2026-08-06) ; assignee (singulier,
  // pré-existant) reste utilisé pour "Mes tâches" (toujours moi-même) et le formulaire
  // générique hors-tuile (deep-link). Voir submitTask() pour la boucle titres×assignees.
  const emptyTaskForm = { type: "standard", recipient: "team", assignee: TEAM[0], assignees: [], date: "", time: "18:00", priority: "normal", immediate: false, lines: [{ id: 1, title: "" }], note: "", bakeryMode: "team", bakeryQty: {} };
  const [taskForm, setTaskForm] = useState(emptyTaskForm);
  // Panier de brouillons — permet de composer plusieurs tâches de TYPES différents
  // (standard + cuisson + plu, chacune avec sa propre assignation pour "confiée") avant un
  // seul enregistrement groupé, au lieu de forcer une création séparée par type (retour
  // utilisateur explicite, 2026-08-07). Chaque entrée porte déjà ses requêtes calculées
  // (voir buildTaskRequests) : figées au moment de l'ajout, jamais recalculées après coup.
  const [taskQueue, setTaskQueue] = useState([]);
  const [taskDetail, setTaskDetail] = useState(null);       // tâche affichée en grand (sheet "taskDetail")
  const [pluTask, setPluTask] = useState(null);            // tâche PLU en cours de confirmation
  const [pluStep, setPluStep] = useState("confirm");        // confirm | delay

  /* HACCP — vraies données. Config chargée depuis GET /api/haccp/config (jamais recopiée en
     dur), haccpRecord/haccpWeeklyRecord = exactement le `record`/`weeklyRecord` serveur
     (entries/delivery/validation/history), jamais un state à plat local. */
  const [haccpConfig, setHaccpConfig] = useState({ dailyGroups: [], weeklyGroups: [], capabilities: { fill: false, validateDaily: false, validateWeekly: false, export: false } });
  const [haccpStatus, setHaccpStatus] = useState("open");    // open | closed_sunday | closed_holiday
  const [haccpRecord, setHaccpRecord] = useState(null);
  const [haccpWeeklyRecord, setHaccpWeeklyRecord] = useState(null);
  const [haccpTempDrafts, setHaccpTempDrafts] = useState({}); // { "groupId.itemId": "texte en cours de saisie" }
  const [haccpDeliveryPending, setHaccpDeliveryPending] = useState({}); // { "groupId.itemId": true } — bascule locale "Oui" avant saisie de la température
  const [haccpUploading, setHaccpUploading] = useState(false);
  const [haccpScreen, setHaccpScreen] = useState("hub");     // hub | weekly | month
  const [haccpBulkRunning, setHaccpBulkRunning] = useState(false); // confirmation groupée des "check" en cours
  const [openWeeklyGroupId, setOpenWeeklyGroupId] = useState(null); // zone hebdo ouverte dans la sheet haccpWeeklyZone
  const [haccpWeeklyBulkRunning, setHaccpWeeklyBulkRunning] = useState(false);
  // Saisie clavier de la sheet haccpZone (façon "Ajouter de l'argent") : chiffres tapés
  // décalés avec le dixième toujours actif (43 -> 4.3°C), jamais de touche "." séparée.
  // Reset à chaque changement d'item affiché (voir useEffect plus bas, juste après leur
  // déclaration commune).
  const [haccpKpDigits, setHaccpKpDigits] = useState("");
  const [haccpKpNeg, setHaccpKpNeg] = useState(false);
  const [openGroupId, setOpenGroupId] = useState(null);
  const [haccpNoteEdit, setHaccpNoteEdit] = useState(null);  // { scope, groupId, item, value, hasDelivery }
  const [haccpNoteDraft, setHaccpNoteDraft] = useState({ text: "", photoUrl: "" });
  const haccpPhotoInputRef = useRef(null);
  // Date consultée pour l'écran "hub" — toujours aujourd'hui par défaut, mais un jour du
  // mois peut être ouvert individuellement depuis la vue mensuelle pour consultation/
  // correction (un dossier quotidien HACCP est atomique par jour côté serveur : consulter
  // un jour passé ne "continue" jamais un autre calendrier, chaque jour/mois est indépendant).
  const [haccpViewDate, setHaccpViewDate] = useState(() => todayISO());
  const [haccpMonthYear, setHaccpMonthYear] = useState(() => Number(todayISO().slice(0, 4)));
  const [haccpMonthNum, setHaccpMonthNum] = useState(() => Number(todayISO().slice(5, 7)));
  const [haccpMonthData, setHaccpMonthData] = useState(null); // { storeId, year, month, days, weeks }
  const [haccpExporting, setHaccpExporting] = useState(false);

  /* Réclamations — vraies données (Phase 1). Champs renommés au mapping serveur↔local :
     deleted(bool réel)↔trashed(local), pieces+kilos séparés(réel)↔qtyType+qty(local). */
  const [claims, setClaims] = useState([]);
  const [claimsScreen, setClaimsScreen] = useState("hub");   // hub | new | history | settings
  const [claimsHistoryTab, setClaimsHistoryTab] = useState("open"); // open | sent | trash
  const [claimDefects, setClaimDefects] = useState(CLAIM_DEFECTS_DEFAULT);
  const [freshArticles, setFreshArticles] = useState([]);
  const [newDefectInput, setNewDefectInput] = useState({ fresh: "", food: "" });
  const [newArticleForm, setNewArticleForm] = useState({ artNr: "", name: "" });
  const [articlesImportText, setArticlesImportText] = useState("");
  const [newDefectDraft, setNewDefectDraft] = useState("");
  const [claimDestination, setClaimDestination] = useState({ method: null, value: "" });
  // Preisänderung (2026-08-16, cahier des charges explicite) — LA CENTRALE DÉCIDE, NEXORA
  // CALCULE, LE SALARIÉ COMPTE ET SAISIT. Le seul champ jamais éditable côté filiale est
  // `bestand` — tout le reste (artNr/alterPreis/neuerPreis/preisdifferenz/art) vient du
  // serveur et n'est jamais renvoyé en écriture (voir savePaBestand). paBestandDraft garde
  // la saisie en cours (texte libre pendant la frappe) séparée de la valeur déjà
  // sauvegardée, pour ne jamais déclencher un PATCH à chaque frappe.
  const [paScreen, setPaScreen] = useState("hub"); // hub | detail
  const [preisanderungen, setPreisanderungen] = useState([]);
  const [paActiveId, setPaActiveId] = useState(null);
  const [paBestandDraft, setPaBestandDraft] = useState({});
  const [paSummarySheet, setPaSummarySheet] = useState(false);
  const [paSaving, setPaSaving] = useState({});
  // Admin > Preisänderung — écran de création (NEXORA_ADMIN uniquement, voir gate serveur
  // POST /api/preisanderungen). "list" = suivi des batches déjà envoyés, "create" = nouveau
  // formulaire. Une ligne vide de départ pour ne jamais présenter un formulaire sans rien à
  // remplir.
  const [paAdminScreen, setPaAdminScreen] = useState("list"); // list | create
  const [paAdminList, setPaAdminList] = useState([]);
  const emptyPaLine = { artNr: "", name: "", alterPreis: "", neuerPreis: "", art: "gutschrift" };
  const emptyPaCreateForm = { reference: "", targetStoreIds: [], lines: [{ ...emptyPaLine }] };
  const [paCreateForm, setPaCreateForm] = useState(emptyPaCreateForm);
  const [paCreateBusy, setPaCreateBusy] = useState(false);
  const [paDemoBusy, setPaDemoBusy] = useState(false);
  // Warenaustausch (2026-08-23) — transferts de marchandises filiale↔filiale et
  // filiale↔Niederlassung. "outgoing" = mes transferts sortants (brouillons + envoyés +
  // reçus/annulés, voir GET /api/warenaustausch/outgoing) ; "inbox" = ce qui arrive chez moi
  // à vérifier (GET /api/warenaustausch/inbox). Onglets calqués sur le patron déjà établi
  // (Missions, Tâches) : Sortants/Réception/Kontoauszug.
  const [waTab, setWaTab] = useState("outgoing"); // outgoing | inbox | konto
  const [waOutgoing, setWaOutgoing] = useState([]);
  const [waInbox, setWaInbox] = useState([]);
  const [waKonto, setWaKonto] = useState(null); // { stores: [...], soldeTotal }
  const emptyWaForm = { kind: "filiale_transfer", category: "", toStoreId: "" };
  const [waNewForm, setWaNewForm] = useState(emptyWaForm);
  const [waDetailId, setWaDetailId] = useState(null); // id du transfert ouvert (sortant OU entrant)
  const [waScanCode, setWaScanCode] = useState("");
  const [waScanQty, setWaScanQty] = useState("1");
  const [waScanBusy, setWaScanBusy] = useState(false);
  const [waVerifyDraft, setWaVerifyDraft] = useState({}); // { [lineId]: "quantité saisie" }
  const waScanPhotoInputRef = useRef(null);
  // Refonte écrans (2026-08-26) — document de suivi + étiquette d'envoi, signature,
  // cartons/palettes : mêmes 3 Sheets remplacées par de vrais écrans (patron Rapport de
  // filiale). waScreen : "hub" | "new" | "receive".
  const [waScreen, setWaScreen] = useState("hub");
  const [waCartonsDraft, setWaCartonsDraft] = useState("0");
  const [waPalletsDraft, setWaPalletsDraft] = useState("0");
  const [waSigReady, setWaSigReady] = useState(false);
  const [waBusy, setWaBusy] = useState(false);
  const [waExporting, setWaExporting] = useState(false);
  const waSigMountRef = useRef(null);
  const waSigPadRef = useRef(null);
  // RR (2026-08-29) — bon de vérification de stock Niederlassung → Filiale, voir le
  // commentaire de tête de server/rr/routes.js pour le pourquoi. Un seul écran "detail"
  // (contrairement à Warenaustausch qui a "new"/"receive" séparés) car RR n'a que 2 étapes
  // de saisie humaine (filiale puis Niederlassung) sur le MÊME document, jamais un
  // brouillon éditable ligne-par-ligne comme un scan Warenaustausch — la Niederlassung
  // compose sa liste en une fois depuis un vrai bon papier déjà complet.
  const [rrList, setRrList] = useState([]);
  const [rrLoading, setRrLoading] = useState(false);
  const emptyRrLine = { artNr: "", name: "", expectedQty: "", unitPrice: "" };
  const emptyRrCreateForm = { toStoreId: "", lines: [{ ...emptyRrLine }] };
  const [rrCreateForm, setRrCreateForm] = useState(emptyRrCreateForm);
  const [rrCreateBusy, setRrCreateBusy] = useState(false);
  const [rrScreen, setRrScreen] = useState("hub"); // hub | create | detail
  const [rrDetailId, setRrDetailId] = useState(null);
  const [rrFoundDraft, setRrFoundDraft] = useState({}); // { [lineId]: "valeur saisie" }
  const [rrCartonsDraft, setRrCartonsDraft] = useState("0");
  const [rrPalletsDraft, setRrPalletsDraft] = useState("0");
  const [rrSigReady, setRrSigReady] = useState(false);   // signature filiale
  const [rrNlSigReady, setRrNlSigReady] = useState(false); // signature Niederlassung
  const [rrBusy, setRrBusy] = useState(false);
  const [rrExporting, setRrExporting] = useState(false);
  // Mode démo RR (2026-08-30, même patron que merchDemoMode) : jamais persisté (retombe à
  // false au rechargement), bons 100% en mémoire, jamais un appel apiJSON tant qu'actif.
  const [rrDemoMode, setRrDemoMode] = useState(false);
  const rrSigMountRef = useRef(null);
  const rrSigPadRef = useRef(null);
  const rrNlSigMountRef = useRef(null);
  const rrNlSigPadRef = useRef(null);
  // Kontoauszug — contrôle de conformité (2026-08-30). Statut JAMAIS déduit automatiquement
  // d'un écart de montant — la filiale déclare elle-même conforme/non-conforme après avoir
  // comparé ses propres bons, `calculatedAmount` n'est qu'une aide affichée à côté.
  const [kontoChecks, setKontoChecks] = useState([]);
  const [kontoLoading, setKontoLoading] = useState(false);
  // declaredByCategory : une valeur saisie par catégorie (RR/Warenaustausch/Preisänderung/
  // Retour marchandise), jamais un seul total à l'aveugle (retour utilisateur explicite).
  const KONTO_CATEGORIES = ["rr", "warenaustausch", "preisanderung", "warenrueckfuehrung"];
  // verdictsByEntry/notesByEntry (2026-08-30, retour utilisateur explicite : "le bouton
  // conforme oui/non apposé sur chaque ligne... et non un bouton d'ensemble") — jamais de
  // conforme/comment global, tout part de la vérification bon par bon.
  const emptyKontoCreateForm = { periodStart: "", periodEnd: "", declaredByEntry: {}, verdictsByEntry: {}, notesByEntry: {}, pdfUrl: "" };
  const [kontoCreateForm, setKontoCreateForm] = useState(emptyKontoCreateForm);
  const [kontoCreateBusy, setKontoCreateBusy] = useState(false);
  const [kontoUploadBusy, setKontoUploadBusy] = useState(false);
  const [kontoScreen, setKontoScreen] = useState("hub"); // hub | create | detail
  const [kontoDetailId, setKontoDetailId] = useState(null);
  const [kontoNoteDraft, setKontoNoteDraft] = useState("");
  const [kontoBusy, setKontoBusy] = useState(false);
  // Mode démo Kontoauszug (2026-08-30, même patron que rrDemoMode/merchDemoMode) : jamais
  // persisté, contrôles 100% en mémoire, jamais un appel apiJSON tant qu'actif.
  const [kontoDemoMode, setKontoDemoMode] = useState(false);
  // Aperçu calculé de la période (2026-08-30, retour utilisateur explicite : le choix
  // conforme/anomalie doit se faire APRÈS avoir vu le détail, jamais à l'aveugle avant) —
  // chargé dès que periodStart+periodEnd sont renseignés sur l'écran de création, jamais
  // persisté (repart de zéro à chaque nouvelle période saisie).
  const [kontoPreview, setKontoPreview] = useState(null); // { calculatedAmount, entriesSnapshot }
  const [kontoPreviewLoading, setKontoPreviewLoading] = useState(false);
  // Rapport de filiale (2026-08-26) — visite GR : sections texte + photos + to-do
  // optionnelle + signature, envoyé à la chaîne de gestion réelle de la filiale visitée
  // (voir GET /api/filiale-reports/:id/recipients-candidates, resolveStoreManagementChain
  // côté serveur). "mine" = mes rapports créés (brouillons + envoyés), "received" = reçus
  // à traiter — même patron d'onglets que Warenaustausch (outgoing/inbox) juste au-dessus.
  const [frTab, setFrTab] = useState("mine"); // mine | received
  const [frMine, setFrMine] = useState([]);
  const [frReceived, setFrReceived] = useState([]);
  const [frScreen, setFrScreen] = useState("hub"); // hub | new | detail
  const [frDraft, setFrDraft] = useState(null); // rapport brouillon en cours de construction
  const [frDetail, setFrDetail] = useState(null); // rapport ouvert (créateur ou destinataire)
  const [frNewStoreId, setFrNewStoreId] = useState("");
  // Correctif #8 (retour utilisateur explicite, 2026-08-26) — plus un champ libre : la
  // filiale visitée se choisit UNIQUEMENT parmi celles réellement attribuées à ce GR (+
  // celles couvertes par une Vertretung active), voir GET /api/filiale-reports/my-stores.
  const [frMyStores, setFrMyStores] = useState([]);
  // Distingue "vraiment 0 filiale attribuée" de "la requête a échoué" (2026-09-01, retour
  // utilisateur explicite : "Impossible de créer un rapport filiale, je reçois aucune
  // filiale disponible") — avant, un échec réseau/timeout tombait dans le même .catch(() =>
  // setFrMyStores([])) qu'un compte réellement sans filiale propre, affichant le même
  // message trompeur "Aucune filiale disponible" dans les deux cas. Même correctif que
  // loadManagerOverview (re-essai auto + vraie erreur affichée si ça persiste).
  const [frMyStoresError, setFrMyStoresError] = useState("");
  const [frSectionDraft, setFrSectionDraft] = useState("");
  const [frPhotoCaptionDraft, setFrPhotoCaptionDraft] = useState("");
  // Correctif #3 — une photo peut être rattachée à une tâche précise ("" = photo générale).
  const [frPhotoLinkedTodoId, setFrPhotoLinkedTodoId] = useState("");
  const [frTodoDraft, setFrTodoDraft] = useState({ text: "", dueDate: "" });
  const [frCandidates, setFrCandidates] = useState([]);
  const [frSelectedTargets, setFrSelectedTargets] = useState([]);
  const [frBusy, setFrBusy] = useState(false);
  const frPhotoInputRef = useRef(null);
  const frSigMountRef = useRef(null);
  const frSigPadRef = useRef(null);
  const [frSigReady, setFrSigReady] = useState(false);
  const emptyClaimItem = { id: null, artNr: "", name: "", qtyType: "pieces", qty: "", chargeLossNr: "", defect: "", note: "", photos: [] };
  const [draftClaimType, setDraftClaimType] = useState("fresh");
  const [draftClaimItems, setDraftClaimItems] = useState([]);
  const [claimItemForm, setClaimItemForm] = useState(null);  // item en cours d'édition dans la sheet
  // Erreur de validation de la sheet "Ajouter un article" — retour utilisateur explicite
  // (2026-08-19) : un simple toast passait inaperçu ("Défaut qualité obligatoire"), l'article
  // n'était jamais ajouté et donnait l'impression qu'il "disparaissait tout seul". Le champ
  // en faute encadre maintenant sa SECTION en rouge (voir sectionTitle/formSection dans la
  // sheet claimItem), en plus du toast — jamais silencieux. Réinitialisée à chaque
  // ouverture/changement de champ concerné.
  const [claimItemError, setClaimItemError] = useState(null); // "artNr" | "defect" | "qty" | null
  // Fiche complète d'une réclamation créée — retour utilisateur explicite (2026-08-19) :
  // cliquer une réclamation dans l'Historique n'ouvrait rien, seuls les tags "nom · défaut"
  // étaient visibles (jamais la quantité, le n° charge/perte, la remarque ou les photos).
  const [claimDetail, setClaimDetail] = useState(null);
  const claimPhotoInputRef = useRef(null);
  const uploadClaimPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const compressed = await compressImageFiles(files);
    const formData = new FormData();
    compressed.forEach((f) => formData.append("photos", f));
    apiJSON("/api/claims/photos", { method: "POST", body: formData })
      .then((data) => setClaimItemForm((f) => f ? { ...f, photos: [...f.photos, ...(data.urls || [])] } : f))
      .catch((err) => showToast(err.message || t("err.photoSendImpossible", "Envoi de photo impossible.")));
  };

  /* PLU Quiz — vraies données. pluActiveList = exactement GET /api/plu-quiz/lists/active
     (catégories/articles/durée/nb questions réels, jamais recopiés en dur). pluProgress =
     cache local de GET /api/plu-quiz/progress, mais le POIDS n'est JAMAIS recalculé côté
     client — uniquement celui renvoyé par le serveur (POST /api/plu-quiz/progress), pour
     ne jamais diverger de la vraie formule de pondération serveur. */
  const [pluActiveList, setPluActiveList] = useState(null);
  // Distingue "vraiment aucune liste active" (404) de "ce rôle n'a pas plu-quiz.view"
  // (403) — retour utilisateur explicite (2026-08-12) : avant, les deux tombaient dans le
  // même message trompeur "Aucune liste PLU active", qui ne dit pas la vraie raison à un
  // rôle dont on vient précisément de retirer le droit depuis Rôles & permissions.
  const [pluAccessDenied, setPluAccessDenied] = useState(false);
  const [pluAttempts, setPluAttempts] = useState([]);         // GET /api/plu-quiz/attempts (mes 5 derniers)
  // Sessions de training — retour utilisateur explicite (2026-08-19) : "connaître son
  // niveau" nécessite un historique, pas juste un score vu une fois puis perdu. Collection
  // séparée de pluAttempts (Examen), voir server/pluQuiz/repository.js.
  const [pluTrainingAttempts, setPluTrainingAttempts] = useState([]);
  const [pluProgress, setPluProgress] = useState({});         // { [plu]: { weight, attempts, correctStreak } }
  const [pluLists, setPluLists] = useState([]);               // admin : GET /api/plu-quiz/lists (toutes, manager)
  const [pluListDraft, setPluListDraft] = useState(null);     // admin : liste en cours d'édition/création
  const [pluWeeklySummary, setPluWeeklySummary] = useState([]); // GET /api/plu-quiz/weekly-summary (supervision)
  const [pluScreen, setPluScreen] = useState("hub");          // hub | trainingHub | quiz | examIntro | report | admin
  const [quizMode, setQuizMode] = useState("training");       // training | exam
  const [quizPace, setQuizPace] = useState("free");           // free | timed | simulation
  const [quizCategory, setQuizCategory] = useState("all");
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizSelected, setQuizSelected] = useState(null);
  const [quizTextAnswer, setQuizTextAnswer] = useState("");
  const [quizStart, setQuizStart] = useState(null);
  // Fin absolue (timestamp), pas juste un compte à rebours local — persistée en brouillon
  // localStorage (voir saveExamDraft/loadExamDraft) pour survivre à un rechargement de
  // page, fidèle au vrai vanilla (plu-quiz.js) : un examen ne doit jamais silencieusement
  // "perdre" son minuteur si l'onglet se recharge.
  const [quizEndAt, setQuizEndAt] = useState(null);
  const [quizTimeLeft, setQuizTimeLeft] = useState(null);
  const [lastReport, setLastReport] = useState(null);
  const [hasSignature, setHasSignature] = useState(false); // trait dessiné dans le canvas (avant confirmation)
  /* Infos importantes a son PROPRE pavé de signature (canvas distinct) —
     celui du PLU Quiz reste toujours monté dans sa Sheet même fermée, donc
     partager une seule ref entre les deux modules créait un conflit. */
  const [infoHasSignature, setInfoHasSignature] = useState(false);
  const infoSigCanvasRef = useRef(null);
  const infoSigDrawingRef = useRef(false);
  // Distance de trait cumulée réelle — le serveur exige strokeLength >= 24 (voir
  // POST /api/important-info/:id/sign) comme garde-fou anti-canvas-jamais-dessiné.
  const infoSigStrokeLenRef = useRef(0);
  const infoSigLastPtRef = useRef(null);
  const sigCanvasRef = useRef(null);
  const sigDrawingRef = useRef(false);
  const [examSignature, setExamSignature] = useState(null); // { dataUrl, at } — certification sur l'honneur, obligatoire, envoyée avec l'attempt

  /* Krankmeldung — vraies données. mine = mes déclarations (self-service), storeList =
     tout ce que le compte voit (managers : filiale, ou secteur entier si plusieurs
     filiales visibles — sfKrankVisibleForIdentity côté serveur fait déjà ce filtrage,
     storeList est simplement TOUT ce que /api/krankmeldungen renvoie). */
  const [krankmeldungen, setKrankmeldungen] = useState([]);
  const [krankStoreList, setKrankStoreList] = useState([]);
  const [krankScreen, setKrankScreen] = useState("hub");     // hub | new | mine | store
  const [krankStep, setKrankStep] = useState(1);
  const emptyKrankForm = { startDate: "", expectedEndDate: "", endDateUnknown: false, absenceScope: "FULL_DAY", partialStartTime: "08:00", employeeComment: "", auChoice: "later", auFiles: [] };
  const [krankForm, setKrankForm] = useState(emptyKrankForm);
  const [krankDraftId, setKrankDraftId] = useState(null);       // id du DRAFT créé dès l'étape 1
  const [krankPlanImpact, setKrankPlanImpact] = useState([]);   // affectedShifts réels (étape 2, dry-run)
  const [krankContactsPreview, setKrankContactsPreview] = useState([]); // contacts réels (étape 3)
  const [krankSubmitting, setKrankSubmitting] = useState(false);
  const [krankDetail, setKrankDetail] = useState(null);         // objet Krankmeldung complet affiché dans le sheet
  const [krankReplacementFor, setKrankReplacementFor] = useState(null);   // entryId du service en cours de remplacement
  const [krankReplacementCandidates, setKrankReplacementCandidates] = useState([]);
  const [krankExtendForm, setKrankExtendForm] = useState(null);      // { newExpectedEndDate, endDateUnknown, employeeComment }
  const [krankEarlyReturnForm, setKrankEarlyReturnForm] = useState(null); // { actualReturnDate, employeeComment }
  const [krankCorrectForm, setKrankCorrectForm] = useState(null);    // manager : { startDate, expectedEndDate, endDateUnknown }
  const krankAuFileInputRef = useRef(null);

  /* Commande matériel — vraies données (Phase 1). Le serveur n'a pas de champ "status" :
     status se déduit de sentAt (vide = pending, renseigné = sent). */
  const [materialOrders, setMaterialOrders] = useState([]);
  const [materialCatalogs, setMaterialCatalogs] = useState(MATERIAL_CATALOGS_SEED);
  const [moScreen, setMoScreen] = useState("hub");        // hub | form | history
  const [moActiveCatalog, setMoActiveCatalog] = useState(null);
  const [moQuantities, setMoQuantities] = useState({});   // { [articleId]: qty }
  const [moNote, setMoNote] = useState("");
  const [moDetail, setMoDetail] = useState(null);

  /* Commandes de marchandises (nouveau module, 2026-08-07) — un article à la fois, plein
     écran sans scroll (voir le plan complet dans delightful-soaring-shell.md). merchScreen :
     hub | date | article | summary | manage | reorder. Le "cœur" du module navigue par index
     local (merchIndex) dans merchSession.articles ; chaque Valider/Suivant écrit
     IMMÉDIATEMENT côté serveur (règle "live update everywhere" du projet — jamais un gros
     payload accumulé en local puis envoyé d'un coup). */
  const [merchScreen, setMerchScreen] = useState("hub");
  const [merchCategories, setMerchCategories] = useState([]);
  const [merchActiveOrders, setMerchActiveOrders] = useState({});  // { [zone]: order } — brouillon/en_cours le plus récent par catégorie, pour le badge "reprendre" du Hub
  // Rappel de commande du jour (2026-08-31) — voir loadMerchOrderReminder + le popup qui s'en
  // sert plus bas. zones = rayons planifiés aujourd'hui (orderDays) pas encore commandés.
  const [merchOrderReminder, setMerchOrderReminder] = useState({ zones: [], weekday: null, date: "" });
  const [merchActiveZone, setMerchActiveZone] = useState(null);
  const [merchDeliveryDateInput, setMerchDeliveryDateInput] = useState("");
  const [merchSession, setMerchSession] = useState(null);        // { articles, history, order }
  const [merchIndex, setMerchIndex] = useState(0);
  const [merchQty, setMerchQty] = useState(0);
  const [merchUnit, setMerchUnit] = useState("");
  // Faktor Kühlung (voir computeKuhlungRecommendation) — merchFaktorInput porte la saisie de
  // l'écran "faktor" (démarrage d'une commande neuve uniquement, jamais au resume) ;
  // merchIstBestand/merchIstBestandApprox sont primés par article comme merchQty/merchUnit
  // (voir primeMerchLineInputs). merchDemoMode : mode démo local au module (task #712), ne
  // touche jamais le serveur tant qu'il est actif.
  const [merchFaktorInput, setMerchFaktorInput] = useState("");
  // Retour utilisateur explicite (2026-08-25) — "commander sans facteur" est maintenant la
  // seule échappatoire pour une commande sans recommandation (jamais un Faktor à 0, qui
  // produirait un Bedarf/Empfehlung toujours nul et donc trompeur) — coché, l'écran
  // "faktor" saute directement à startMerchOrder(null), aucune valeur Faktor envoyée.
  const [merchNoFaktor, setMerchNoFaktor] = useState(false);
  const [merchIstBestand, setMerchIstBestand] = useState("");
  const [merchIstBestandApprox, setMerchIstBestandApprox] = useState(false);
  const [merchFaktorEditing, setMerchFaktorEditing] = useState(false);
  const [merchFaktorEditDraft, setMerchFaktorEditDraft] = useState("");
  // Retour utilisateur explicite (2026-08-25) — la quantité proposée (Empfehlung) se
  // pré-remplit désormais directement dans le champ, jamais besoin de la ressaisir à la
  // main : ce ref suit "est-ce que cet article suit encore la recommandation en direct, ou
  // l'utilisateur a-t-il repris la main en tapant une valeur lui-même" — réarmé à chaque
  // arrivée sur un article (primeMerchLineInputs), désarmé dès la première saisie manuelle
  // (onChange du champ quantité). Un ref, pas un state : ne doit jamais provoquer de
  // re-render à lui seul, seulement piloter l'effet de suivi ci-dessous.
  const merchQtyAutoRef = useRef(true);
  const [merchDemoMode, setMerchDemoMode] = useState(false);
  const [merchOrders, setMerchOrders] = useState([]);
  const [merchOrderDetail, setMerchOrderDetail] = useState(null);
  const [merchManageStatusFilter, setMerchManageStatusFilter] = useState("");
  const [merchReorderZone, setMerchReorderZone] = useState(null);
  const [merchReorderList, setMerchReorderList] = useState([]);
  // Design porté depuis l'artefact validé (style Contrôle Kühlung) — Art.Nr éditable en
  // ligne sur la tuile, menu déroulant d'unité (Trocken seulement, voir merchAllowedUnits),
  // liste de commande accessible via la Sheet "merchArticleList" (☰ dans la topbar article).
  const [merchArtNrEditing, setMerchArtNrEditing] = useState(false);
  const [merchArtNrDraft, setMerchArtNrDraft] = useState("");
  const [merchUnitMenuOpen, setMerchUnitMenuOpen] = useState(false);

  /* Fabrication d'étiquettes (nouveau module) — saisie 100% manuelle, moteur de modèles
     LABEL_TEMPLATES (voir plus bas, hors composant). labelsScreen : hub | template | form |
     preview. Parcours simple demandé par l'utilisateur : Nouveau → choisir le modèle →
     saisir les données → prévisualisation → imprimer/exporter. labelDraft porte toujours les
     9 champs "génériques" (marque/désignation/description/conditionnement/prix/prixDeBase/
     ve/numeroArticle/dateDebut/couleur) — chaque modèle choisit lesquels il affiche/édite,
     le state ne change jamais de forme entre modèles (voir LABEL_TEMPLATES[id].fields). */
  /* Inventurmaßnahme — nouveau module (2026-08-11, artefact validé point par point) : deux
     vrais formulaires papier NORMA (Lieferschein Test + Bewertungsbogen für Testkäufe,
     transcrits verbatim depuis les PDF fournis par l'utilisateur, Stand 11/2025 pour le
     second). PLU Quiz désormais fusionné dans ce module (retour utilisateur explicite,
     2026-08-11) — son propre écran interne (pluScreen) devient un sous-écran de
     imScreen === "pluquiz", moteur d'examen/training/admin inchangé. Plus de flux "derniers
     remplis" à plat sur le hub (retour utilisateur explicite) : imScreen === "archive"
     regroupe les 3 types de documents par semaine (KW), voir imGroupIntoWeeks — même vue
     réutilisée en lecture seule dans la rubrique fiche filiale GR. Pas de groupe de
     permissions dédié pour les 2 formulaires propres à ce module (même choix que Missions
     réseau/Associer EAN) — ouvert à tout compte connecté ; PLU Quiz garde sa propre
     permission plu-quiz.manage pour son écran admin. */
  const [imScreen, setImScreen] = useState("hub"); // hub | lieferschein | testkauf | archive | pluquiz
  // Vrai seulement quand on est entré dans lieferschein/testkauf depuis la Fiche filiale
  // GR (Pilotage réseau) plutôt que depuis le hub Inventurmassnahme lui-même — le bouton
  // retour du header doit alors ramener direct à la Fiche filiale (screen "home" + pilotScreen
  // "filiale-detail", pilotFilialeId/pilotFilialeSection jamais touchés donc toujours corrects)
  // au lieu de suivre la chaîne normale lieferschein/testkauf → hub → home.
  const [imReturnToPilot, setImReturnToPilot] = useState(false);
  const [imArchiveWeeks, setImArchiveWeeks] = useState([]);
  const [imArchiveLoading, setImArchiveLoading] = useState(false);
  const [imArchiveExpandedWeek, setImArchiveExpandedWeek] = useState(null);
  const [imSaving, setImSaving] = useState(false);
  const emptyLsForm = { id: "", rayon: "Trocken", lieferscheinnummer: "", datum: todayISO(), mitarbeiterId: "", mitarbeiterName: "", testBestanden: true, verlust: "", sigMitarbeiter: "", sigFl: "", sigBl: "", belegUrl: "", belegMimeType: "", ermahnungMuendlich: false, ermahnung: false, abmahnung: false };
  const [lsForm, setLsForm] = useState(emptyLsForm);
  const [lsMitSearch, setLsMitSearch] = useState("");
  const emptyBwForm = {
    id: "", datum: todayISO(), mitarbeiterId: "", mitarbeiterName: "", azubi: false, arbeitskleidung: "ordentlich", bemerkungen: "",
    begruessung: true, wareAufBand: true, blickEinkaufswagen: true, anhebenWare: true, warenbewegung: true,
    wechselgeldBestueckt: true, plusAppGefragt: true, kundeWechselgeldGefragt: true, pluSicherheit: true, allgemeineAngaben: "",
    fehlerhaftArtikel: [], probewagenBetrag: "", richtigerBetrag: "", testkaufBestanden: true,
    sigTestkaeufer: "", sigBereichsleitung: "", belegUrl: "", belegMimeType: "",
    ermahnungMuendlich: false, ermahnungDatum: "", ermahnung: false, abmahnung: false, bemerkung: "",
  };
  const [bwForm, setBwForm] = useState(emptyBwForm);
  const [bwMitSearch, setBwMitSearch] = useState("");
  // Pads de signature, même patron que azlSignPadRef (voir son propre useEffect) : montés
  // seulement pendant que le bon écran est affiché. FL et BL désormais séparés (retour
  // utilisateur explicite) — le pad BL n'est rendu que pour un compte GR (isNetworkLeader),
  // le BL signant depuis son propre compte plutôt que sur l'appareil de la filiale.
  const lsSigMitMountRef = useRef(null); const lsSigMitPadRef = useRef(null);
  const lsSigFlMountRef = useRef(null); const lsSigFlPadRef = useRef(null);
  const lsSigBlMountRef = useRef(null); const lsSigBlPadRef = useRef(null);
  const bwSigTkMountRef = useRef(null); const bwSigTkPadRef = useRef(null);
  const bwSigBlMountRef = useRef(null); const bwSigBlPadRef = useRef(null);
  const [lsSigMitReady, setLsSigMitReady] = useState(false);
  const [lsSigFlReady, setLsSigFlReady] = useState(false);
  const [lsSigBlReady, setLsSigBlReady] = useState(false);
  const [bwSigTkReady, setBwSigTkReady] = useState(false);
  const [bwSigBlReady, setBwSigBlReady] = useState(false);
  const [imBelegUploading, setImBelegUploading] = useState(false);
  const lsBelegInputRef = useRef(null);
  const bwBelegInputRef = useRef(null);
  // Signature à distance (retour utilisateur explicite, 2026-08-20) : un testeur pas
  // physiquement présent envoie un lien à usage unique (24h) à la personne concernée,
  // qui signe sur SON PROPRE téléphone sans compte Nexora — voir requestRemoteSignature.
  const [signatureRequestBusy, setSignatureRequestBusy] = useState(false);
  const [signatureRequestLink, setSignatureRequestLink] = useState(null); // { url, fieldLabel }

  const [labelsScreen, setLabelsScreen] = useState("hub");
  const [labelsList, setLabelsList] = useState([]);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [labelActiveTemplateId, setLabelActiveTemplateId] = useState(null);
  const [labelEditingId, setLabelEditingId] = useState(null);
  const [labelDraft, setLabelDraft] = useState(() => emptyLabelDraft());
  const [labelSaving, setLabelSaving] = useState(false);
  const [labelExporting, setLabelExporting] = useState(false);
  const [labelPendingDeleteId, setLabelPendingDeleteId] = useState(null);

  /* Kassenpost (nouveau module, 2026-09-18) — signalements reçus de la Niederlassung
     (jamais créés par la filiale). Deux catégories : "qm" (Qualitätsmeldung — Art.Nr/MHD/
     quantité/photos probantes obligatoires) et "infos" (Infos importantes — texte libre
     simple). Kassenpost reste le vrai canal officiel de réponse ; ce module sert à
     centraliser/suivre côté filiale, pas à le remplacer. */
  const [kassenpostScreen, setKassenpostScreen] = useState("hub"); // hub | form | detail
  const [kassenpostList, setKassenpostList] = useState([]);
  const [kassenpostLoading, setKassenpostLoading] = useState(false);
  const [kassenpostCategoryFilter, setKassenpostCategoryFilter] = useState("all"); // all | qm | infos
  const [kassenpostActiveId, setKassenpostActiveId] = useState(null);
  const [kassenpostForm, setKassenpostForm] = useState(null);
  const [kassenpostSaving, setKassenpostSaving] = useState(false);
  const [kassenpostPendingDeleteId, setKassenpostPendingDeleteId] = useState(null);

  /* Scanner — Associer EAN (mêmes helpers articleForCode/linkCodeToArticle que Contrôle) */
  const [scanScreen, setScanScreen] = useState("hub");     // hub | list
  const [scanZone, setScanZone] = useState("kuehlung");
  const [scanListSearch, setScanListSearch] = useState("");
  // "Voir plus" plutôt qu'un plafond fixe (retour utilisateur explicite) — nombre de lots
  // de CAP articles affichés, remis à 1 à chaque changement de rayon/écran pour ne jamais
  // garder une pagination avancée d'un rayon précédent.
  const [scanListBatches, setScanListBatches] = useState(1);
  useEffect(() => { if (scanScreen === "list") setScanListBatches(1); }, [scanScreen, scanZone]);
  const [scanLinkArticle, setScanLinkArticle] = useState(null);
  const [scanLinkInput, setScanLinkInput] = useState("");
  const [scanLinkAllowUnlink, setScanLinkAllowUnlink] = useState(true);

  /* Messages — vraies données (server/messages/*, 20 routes déjà réelles et déployées).
     AVANT : `conversations` restait initialisé à DEMO_CONVERSATIONS (Sarah/Marco/Lena)
     pour toujours — aucun chargement réel ne l'écrasait jamais, seulement des map/filter
     locaux sur ce seed. C'était la vraie source des "notifications du mode démo" vues
     partout dans l'app (Assistant du jour, badge Messages) — jamais de la fausse donnée
     dans les notifications elles-mêmes, mais un module entier jamais migré. */
  const [conversations, setConversations] = useState([]);
  const [msgThreadMessages, setMsgThreadMessages] = useState([]);
  const [msgRecipients, setMsgRecipients] = useState([]);
  const [msgConvMembers, setMsgConvMembers] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgThreadLoading, setMsgThreadLoading] = useState(false);
  const [msgScreen, setMsgScreen] = useState("list");      // list | thread | new
  const [msgFilter, setMsgFilter] = useState("all");       // all | unread | private | group | branch | archived
  const [archivedConversations, setArchivedConversations] = useState([]);
  const [msgActiveId, setMsgActiveId] = useState(null);
  const [msgDraft, setMsgDraft] = useState("");
  const [msgReplyTo, setMsgReplyTo] = useState(null); // { id, from, preview }
  const [msgEditId, setMsgEditId] = useState(null);
  const [newConvType, setNewConvType] = useState("private");
  const [newConvMembers, setNewConvMembers] = useState([]);
  const [newConvTitle, setNewConvTitle] = useState("");
  // Partage d'un Rundschreiben déjà publié vers une NOUVELLE conversation (2026-09-12,
  // retour utilisateur explicite : un GR n'écrit jamais le contenu, il relaie le PDF reçu de
  // la Niederlassung — "l'enregistrer ou le partager via la messagerie interne... à qui il
  // veut"). Posé ici juste avant d'ouvrir l'écran "new" ; consommé une seule fois dans
  // createConversation() dès que la conversation existe (openThread), jamais pour un message
  // "nouveau" ordinaire (null dans tous les autres cas).
  const [pendingInfoShare, setPendingInfoShare] = useState(null);
  // Audit du 2026-08-02 : /api/messages/recipients renvoie déjà stores/canCreateGroup/
  // canCreateCrossBranch (scope réseau entier pour un responsable, LEADER_ROLES côté
  // serveur) — jamais capturés par loadRecipients avant, donc jamais exploités. C'est
  // précisément ce qui manquait pour qu'un grand responsable puisse ouvrir une
  // communication multi-filiale (POST /api/messages/multi-branch, déjà réel côté serveur).
  const [msgAvailableStores, setMsgAvailableStores] = useState([]);
  const [msgCanCreateGroup, setMsgCanCreateGroup] = useState(true);
  const [msgCanCreateCrossBranch, setMsgCanCreateCrossBranch] = useState(false);
  const [newConvStores, setNewConvStores] = useState([]);
  // Retour utilisateur explicite (2026-08-18) : sur "Nouveau", le bouton "Créer" était
  // en toute fin d'écran — fallait défiler toute la liste des destinataires pour
  // l'atteindre. Recherche pour naviguer directement vers une personne dans une longue
  // liste, plutôt que défiler.
  const [newConvQuery, setNewConvQuery] = useState("");
  const [newConvCreatingId, setNewConvCreatingId] = useState(null);
  const [msgListQuery, setMsgListQuery] = useState("");
  const [msgSearchQuery, setMsgSearchQuery] = useState("");
  const [msgSearchResults, setMsgSearchResults] = useState([]);
  const [msgSearchLoading, setMsgSearchLoading] = useState(false);
  const [msgRenameForm, setMsgRenameForm] = useState(null); // { title, description }
  const [msgAddMemberQuery, setMsgAddMemberQuery] = useState("");
  const [msgPendingAttachments, setMsgPendingAttachments] = useState([]); // [{id,originalName,mimeType,size}]
  const [msgUploadingAttachment, setMsgUploadingAttachment] = useState(false);
  const msgAttachmentInputRef = useRef(null);

  /* Module Inventur — Inventurerfassung (inventaires physiques réels, jamais de stock
     théorique/connexion Norma simulée) + Bestandskontrolle (contrôle ciblé d'un article
     sur une période), deux sections indépendantes, mêmes pages pour tout le monde —
     jamais de "vue responsable" séparée, seules les actions visibles changent selon les
     permissions (canXInventur ci-dessus). Backend réel : /api/inventur/*. */
  const [inventurScreen, setInventurScreen] = useState("hub");
  // hub | erfassung_list | erfassung_create | erfassung_detail | erfassung_category |
  // erfassung_my_tasks | erfassung_history | kontrolle_list | kontrolle_create |
  // kontrolle_detail | kontrolle_history
  const [inventurListMode, setInventurListMode] = useState("active");
  const [inventurInventories, setInventurInventories] = useState([]);
  const [inventurCategoryTemplates, setInventurCategoryTemplates] = useState([]);
  const [inventurActiveInventory, setInventurActiveInventory] = useState(null);
  const [inventurActiveCategoryId, setInventurActiveCategoryId] = useState(null);
  const [inventurCategoryEntries, setInventurCategoryEntries] = useState([]);
  const [inventurMyTasks, setInventurMyTasks] = useState([]);
  const [inventurHistory, setInventurHistory] = useState([]);

  const emptyInventurCreateForm = { type: "inventur", name: "", date: todayISO(), plannedStartTime: "", mainUserId: "", mainUserName: "", participantIds: [], participantNames: [], categoryIds: [], note: "" };
  const [inventurCreateForm, setInventurCreateForm] = useState(emptyInventurCreateForm);
  const [inventurNewCategoryForm, setInventurNewCategoryForm] = useState(null); // { name, description, entryType, scope }
  const [inventurAssignForm, setInventurAssignForm] = useState(null); // { catId, assignedUserIds:[], assignedUserNames:[] }
  const emptyInventurEntryForm = { artNr: "", name: "", entryMethod: "direct", cartons: "", piecesPerCarton: "", singlePieces: "", totalQty: "", price: "", note: "" };
  const [inventurEntryForm, setInventurEntryForm] = useState(emptyInventurEntryForm);
  const [inventurEditEntry, setInventurEditEntry] = useState(null); // ligne existante en cours d'édition
  const [inventurDuplicateState, setInventurDuplicateState] = useState(null); // { existingEntry, payload }
  const [inventurReopenTarget, setInventurReopenTarget] = useState(null); // { kind: "category"|"inventory", catId? }
  const [inventurReopenReason, setInventurReopenReason] = useState("");
  const [inventurEntrySearch, setInventurEntrySearch] = useState("");
  const inventurArtNrInputRef = useRef(null);

  const [kontrolleListMode, setKontrolleListMode] = useState("active");
  const [inventurKontrollen, setInventurKontrollen] = useState([]);
  const [inventurKontrolleHistory, setInventurKontrolleHistory] = useState([]);
  const [inventurKontrolleHistorySummary, setInventurKontrolleHistorySummary] = useState(null);
  const [kontrolleHistoryFilters, setKontrolleHistoryFilters] = useState({ artNr: "", reason: "", negativeOnly: false });
  // Refonte accordéon (2026-08-09) — "déplier une carte EST l'écran de détail" : plus de
  // navigation kontrolle_detail séparée. Un seul contrôle déplié à la fois (id), et un seul
  // cycle déplié dans sa timeline (id) — la source de vérité reste l'entrée dans
  // inventurKontrollen elle-même, jamais une copie séparée type inventurActiveKontrolle.
  const [kontrolleExpandedId, setKontrolleExpandedId] = useState(null);
  const [kontrolleExpandedCycleId, setKontrolleExpandedCycleId] = useState(null);
  const [kontrolleExportIds, setKontrolleExportIds] = useState(null); // null=fermé, [] ou [id,...] = ouvert
  const [kontrolleBlankFormOpen, setKontrolleBlankFormOpen] = useState(false);
  // Retour utilisateur (2026-08-09) : aucune option pour corriger les infos de l'article
  // (désignation/catégorie/assigné/motif/note) une fois le contrôle créé — vrai manque,
  // le nouveau design (accordéon) ne montrait plus que les cycles, jamais ces champs.
  const [kontrolleEditInfoOpen, setKontrolleEditInfoOpen] = useState(false);
  // Retour utilisateur (2026-08-09) : dès qu'un cycle est clôturé, la carte doit se
  // refermer toute seule (au lieu de rester ouverte sur des données désormais figées) et
  // le PROCHAIN article de la liste doit surligner brièvement pour guider l'oeil vers le
  // suivant à compter, sans avoir à le chercher soi-même.
  const [kontrolleHighlightId, setKontrolleHighlightId] = useState(null);
  // Visibilité Bestandskontrolle par employé, réservée FL/FLA (retour utilisateur
  // 2026-08-09) : liste de BLOCAGE (hiddenEmployeeIds) par filiale — jamais une liste
  // d'autorisation, pour que l'absence de réglage reste équivalente à "tout le monde
  // voit", comme avant ce chantier.
  const [kontrolleVisibility, setKontrolleVisibility] = useState(null);
  const [kontrolleVisibilitySheetOpen, setKontrolleVisibilitySheetOpen] = useState(false);
  // "articles" : plusieurs lignes possibles pour créer plusieurs contrôles d'un coup
  // (retour utilisateur 2026-08-02 : "on doit pouvoir le faire sur plusieurs articles
  // à la fois") — un document Kontrolle par ligne, champs partagés (catégorie/
  // affectation/motif/note) appliqués à chacun.
  const emptyKontrolleArticleRow = { artNr: "", name: "", anfangsbestand: "" };
  const emptyKontrolleCreateForm = { articles: [{ ...emptyKontrolleArticleRow }], categoryId: "", categoryName: "", plannedEndAt: "", assignedUserId: "", assignedUserName: "", otherUserIds: [], reason: "regelmaessige_kontrolle", note: "" };
  const [kontrolleCreateForm, setKontrolleCreateForm] = useState(emptyKontrolleCreateForm);
  const [kontrolleDeliveryForm, setKontrolleDeliveryForm] = useState({ date: todayISO(), qty: "", note: "" });
  // Correction d'une livraison déjà enregistrée (retour utilisateur 2026-08-09) : null =
  // le sheet "Lieferung" sert à AJOUTER ; un id = il sert à CORRIGER cette livraison-là.
  const [kontrolleDeliveryEditId, setKontrolleDeliveryEditId] = useState(null);
  // Nouveau cycle dans le MÊME contrôle ("débuter un nouveau contrôle quand on le
  // souhaite dans le même contrôle, sans en créer un nouveau, pour un véritable suivi").
  // startAt : correction apportée sur retour utilisateur — le Startdatum d'un cycle est
  // maintenant un vrai champ saisi, plus jamais deviné depuis la date de création serveur.
  const emptyCycleStartForm = { startAt: todayISO(), anfangsbestand: "", plannedEndAt: "" };
  const [inventurCycleStartForm, setInventurCycleStartForm] = useState(emptyCycleStartForm);
  // Roster réel de la filiale pour les sélecteurs d'affectation (participants/équipe) —
  // Admin exclu (même principe déjà appliqué à Messages : "admin n'a rien à voir avec la
  // gestion du magasin").
  const [inventurTeamRoster, setInventurTeamRoster] = useState([]);
  const loadInventurTeamRoster = () => {
    apiJSON("/api/users").then((list) => {
      const arr = Array.isArray(list) ? list : [];
      setInventurTeamRoster(arr.filter((u) => !u.disabled && u.role !== "NEXORA_ADMIN" && (Array.isArray(u.stores) ? u.stores.includes(user?.store) : u.storeId === user?.store)));
    }).catch(() => setInventurTeamRoster([]));
  };

  /* Infos importantes — vraies données (server.js important-info.*). Ciblage
     simplifié à "ma filiale" (targetType:"store", targetBranches:[user?.store])
     pour cette passe — pas de sélecteur réseau/rôles/utilisateurs nommés, la
     permission de créer étant de toute façon réservée au rôle FL (voir
     infoScope.canCreate, vérifié réel via /api/important-info/my-scope). */
  const [infos, setInfos] = useState([]);
  const [infoScope, setInfoScope] = useState({ canCreate: false, canCreateNetwork: false, allowedStores: [], isNetworkWide: false });
  const [infoScreen, setInfoScreen] = useState("hub");     // hub | detail | manage | manageDetail | editor
  const [infoManageList, setInfoManageList] = useState([]);
  const [infoManageDetailId, setInfoManageDetailId] = useState(null);
  const [infoManageDetail, setInfoManageDetail] = useState(null);       // { info, version }
  const [infoManageRecipients, setInfoManageRecipients] = useState(null); // { type, rows, summary }
  const [infoTab, setInfoTab] = useState("toread");        // toread | done | archived
  const [infoDetailId, setInfoDetailId] = useState(null);
  const [infoDetail, setInfoDetail] = useState(null);      // { info, version, recipient, signature, versionLocked }
  // Rundschreiben (2026-08-31) — même écran/état qu'Infos importantes, juste filtré par
  // cette valeur ("general" par défaut = comportement inchangé). Positionné par la tuile
  // tapée (HOME_MODULES "rundschreiben"/NETWORK_HOME_MODULES "netRundschreiben" vs "infos"/
  // "netInfos"), jamais par l'utilisateur depuis l'intérieur de l'écran — deux points
  // d'entrée, un seul écran, voir le commentaire sur MODULE_TO_PERMISSION_GROUP.rundschreiben.
  const [infoCategoryFilter, setInfoCategoryFilter] = useState("general"); // "general" | "rundschreiben"
  // Retour utilisateur explicite (2026-10-14) : "le suivi des personnes qui l'ont lu devrait
  // être... dans un onglet caché et non sur la page avec une centaine de travailleurs. Mieux
  // vaut trié par filiale" — pour un Rundschreiben (audience potentiellement tout le réseau),
  // la liste nominative à plat (déjà existante, "Suivi des destinataires") n'est plus montrée
  // par défaut sur la fiche ; "Réception par filiale" (déjà existante elle aussi) reste la vue
  // principale, chaque filiale dépliable individuellement (jamais les ~100 personnes d'un
  // coup). rundschreibenShowFullList = échappatoire volontaire pour qui a vraiment besoin de
  // la liste complète, repliée par défaut. Jamais pour "Infos importantes" générales (pas
  // demandé, audience habituellement bien plus restreinte) — scope strictement category
  // === "rundschreiben" au point d'usage.
  const [rundschreibenExpandedStoreId, setRundschreibenExpandedStoreId] = useState(null);
  const [rundschreibenShowFullList, setRundschreibenShowFullList] = useState(false);
  // Suite — retour utilisateur explicite (2026-10-14, 2e tour) : même pour un GR autorisé
  // (rundschreiben.manage, déjà correctement réservé à Bereichsleiter/Verkaufsleiter/
  // Niederlassungsleiter côté permissions — vérifié en base, aucun souci là), le suivi ne
  // doit pas être la vue affichée par défaut en ouvrant un Rundschreiben — "onglet caché",
  // jamais la première chose vue. "content" = lecture normale (identique à ce que voit
  // n'importe quel destinataire), "tracking" = bascule explicite pour voir la réception.
  const [rundschreibenDetailTab, setRundschreibenDetailTab] = useState("content");
  const emptyInfoForm = { title: "", summary: "", content: "", level: "info", mandatory: false, expiresAt: "", targetBranches: [], category: "general", rundschreibenNr: "", rundschreibenWeek: "" };
  const [infoForm, setInfoForm] = useState(emptyInfoForm);
  const [infoEditingId, setInfoEditingId] = useState(null); // id du brouillon en édition, null = nouvelle création
  // Rundschreiben — PDF joint au brouillon en cours d'édition (2026-08-31). Séparé
  // d'infoForm (qui ne porte que les champs texte) — se peuple à l'ouverture de l'éditeur
  // sur un brouillon existant, et après un upload réussi (voir uploadInfoAttachment).
  const [infoEditingAttachments, setInfoEditingAttachments] = useState([]);
  const [infoAttachmentUploading, setInfoAttachmentUploading] = useState(false);
  const infoAttachmentInputRef = useRef(null);

  /* Reporting */
  const [repScreen, setRepScreen] = useState("hub"); // hub|daily|weekly|stunden|month|history|supervision|catalog
  // Navigation entre semaines — retour utilisateur explicite (2026-08-06) : "Saisies
  // quotidiennes" n'avait AUCUNE navigation (toujours la semaine calendaire en cours,
  // aucun moyen de rattraper un jour manqué la semaine passée), "Rapport hebdomadaire"
  // n'avait qu'une liste de puces limitée aux semaines déjà clôturées (reportingHistory) —
  // impossible d'atteindre une semaine jamais commencée. Même patron que planWeekOffset/
  // availWeekOffset (mondayOfWeekOffset) — jamais au-delà de la semaine réelle en cours
  // (0), une saisie de pertes future n'a pas de sens.
  const [reportingWeekOffset, setReportingWeekOffset] = useState(0);
  const [dailyFamily, setDailyFamily] = useState("prebake"); // prebake|brot|manual_waste
  // Vraies données /api/reporting/daily-entries (chargées par loadCatalogDaily) —
  // { prebake: {dayIdx: {articleId: entsorgtPieces}}, brot: {...} }, dayIdx dérivé de
  // la vraie date via reportingWeekDates, plus un index de démo figé.
  const [catalogDaily, setCatalogDaily] = useState({ prebake: {}, brot: {} });
  const [weeklyGrid, setWeeklyGrid] = useState({}); // { categoryId: { dayIdx: {pu20, entsorgt} } } — réel, chargé par loadManualWasteWeek
  // { prebake: { dayIdx: bool }, brot: {...}, manual_waste: {...} } — par JOUR, pas par
  // famille seule (retour utilisateur explicite 2026-08-02 : "aucun sélecteur de date",
  // corrigé avec le rattrapage Prebake/Brot ci-dessous — dailyLockOverride[family][dayIdx]
  // true = déverrouillé manuellement pour CE jour précis).
  const [dailyLockOverride, setDailyLockOverride] = useState({});
  const [dailySaved, setDailySaved] = useState({ prebake: {}, brot: {}, manual_waste: {} });
  // Jour sélectionné dans "Saisie quotidienne" — null = jour réel (TODAY_IDX, calculé plus
  // bas). Permet de revenir sur un jour manqué de la semaine en cours (jamais un jour futur).
  const [dailyDayIdx, setDailyDayIdx] = useState(null);
  // Glisser la liste pour changer de jour, effet carrousel (retour utilisateur explicite
  // 2026-08-21, en plus des chevrons ‹ › déjà ajoutés — puis reconstruit en V2 : "le
  // comportement qu'on avait sur ces Dashboard [ancien ring]... plus premium... je ne vois
  // rien de tel", la V1 ne bougeait rien visuellement pendant le geste). dailyContentRef =
  // noeud DOM manipulé directement en pointermove (jamais de setState par pixel, voir
  // onDailySwipeMove) ; dailyDragActiveRef = devient true seulement après avoir tranché
  // horizontal vs vertical (10px de mouvement), jamais avant. dailySwipeHint = petit aperçu
  // du jour qui s'affiche ~550ms puis disparaît tout seul.
  const dailySwipeStartRef = useRef(null);
  const dailyDragActiveRef = useRef(false);
  const dailyDirLockRef = useRef(null); // null (indécis) | "x" | "y" — voir onDailySwipeMove
  const dailyContentRef = useRef(null);
  const dailySwipeHintTimerRef = useRef(null);
  const [dailySwipeHint, setDailySwipeHint] = useState(null);
  // Réorganiser Prebake/Brot depuis l'écran de saisie quotidienne — même patron que
  // Commandes marchandises (openMerchReorder/moveMerchReorderItem/saveMerchReorder,
  // boutons ↑/↓ jamais de drag tactile) : liste locale éditée en mémoire, un seul
  // enregistrement explicite à la fin (jamais un appel réseau par tape ↑/↓).
  const [dailyReorderActive, setDailyReorderActive] = useState(false);
  const [dailyReorderList, setDailyReorderList] = useState([]);
  const [weekLockOverride, setWeekLockOverride] = useState(false);
  const [weekSaved, setWeekSaved] = useState(false);
  // gridEditTarget/gridEditDraft — réintroduits (2026-09-20, maquette validée) après une
  // première suppression le même soir. La 1re version (avant suppression) avait déjà été
  // corrigée pour POSTer réellement (voir saveGridEdit plus bas) suite à un incident de
  // perte de données — jamais un simple setState local qui affiche un faux succès.
  const [gridEditTarget, setGridEditTarget] = useState(null); // { categoryId, dayIdx }
  const [gridEditDraft, setGridEditDraft] = useState({ pu20: "", entsorgt: "" });
  const [krankHours, setKrankHours] = useState(0);
  // Garde contre le même bug que loadManualWasteWeek/loadCatalogDaily (2026-09-19,
  // voir storeflow_reporting_data_loss_race_condition_fix) : loadKrankHours() peut être
  // déclenché par le sondage Dashboard 15s pendant qu'un fetch précédent est encore en vol
  // au moment où l'utilisateur navigue vers l'écran Clôture mensuelle et tape une valeur —
  // une réponse tardive de ce fetch écraserait sinon la saisie non enregistrée. krankHours
  // est un scalaire (pas un objet à fusionner comme weeklyGrid/catalogDaily), donc la garde
  // se fait via une ref "en cours d'édition", jamais via une fermeture (stale closure).
  const krankHoursDirtyRef = useRef(false);
  // Nombre réel de personnes en arrêt cette semaine — GET /api/reporting/krank-persons-weekly,
  // calculé côté serveur depuis les vraies entrées Planning (entry.status === "Krank"), jamais
  // une valeur figée. Corrige un bug réel : c'était auparavant `useState(1)` constant, jamais
  // recalculé, alors que le texte affiché affirmait "calculé depuis Planning" (retour
  // utilisateur explicite 2026-08-02 — audit du module Reporting).
  const [krankPersons, setKrankPersons] = useState(0);
  // Historique réel (semaines/mois passés) — remplace DEMO_WEEK_HISTORY. Une entrée par
  // semaine ayant réellement des saisies (prebake/brot/manual_waste/krank), jamais la
  // semaine en cours (déjà affichée en direct via weekTotal()/krankHours/krankPersons).
  const [reportingHistory, setReportingHistory] = useState([]);
  // Sélecteur de semaine sur "Rapport hebdomadaire" — null = semaine en cours (vivante,
  // éditable, weeklyGrid/catalogDaily). Un numéro = semaine passée consultée en LECTURE
  // SEULE (pastWeekSrc), jamais mélangée à l'état d'édition de la semaine en cours (retour
  // utilisateur explicite 2026-08-02 : "il manque également le sélecteur de date").
  const [weeklyViewWeek, setWeeklyViewWeek] = useState(null);
  const [pastWeekSrc, setPastWeekSrc] = useState(null); // { week, dates, value(catId,dayIdx), rowTotal(catId), total }
  const [pastWeekLoading, setPastWeekLoading] = useState(false);
  // Vraies données /api/reporting/articles (chargées par loadReportingCatalog) —
  // shape client-side : { id, artNr, name, category:"Prebake"|"Brot", program, content,
  // ve, price, status, displayOrder }, family serveur mappé vers category ici.
  const [repCatalog, setRepCatalog] = useState([]);
  const [catalogTab, setCatalogTab] = useState("all"); // all|Prebake|Brot
  const [catalogStatusFilter, setCatalogStatusFilter] = useState("active"); // active|suspended|archived
  const [articleModalForm, setArticleModalForm] = useState(null); // { id?, name, category, program, price, status }
  const [wipeSelections, setWipeSelections] = useState([]);
  const [wipeConfirmText, setWipeConfirmText] = useState("");
  const [stundenDays, setStundenDays] = useState([]);
  const [stundenIstForm, setStundenIstForm] = useState({ umsatz: "", heures: "" });
  const [stundenImported, setStundenImported] = useState(false);
  const [stundenMonthOffset, setStundenMonthOffset] = useState(0); // 0 = mois en cours ; jamais au-delà (import futur impossible)
  const [stundenIstEditDate, setStundenIstEditDate] = useState(null); // "YYYY-MM-DD" du jour en cours de saisie/correction, ou null
  const stundenFileInputRef = useRef(null);
  const [caCompare, setCaCompare] = useState({ lastWeekTotal: 0, lastMonthTotal: 0, lastYearMonthTotal: 0, lastWeekLeistung: null, lastWeekWasteTotal: null });
  const [weekTrend, setWeekTrend] = useState([]); // [{week, total}] — 4 dernières semaines (Umsatz Ist), pour la tendance Dashboard
  const [nextMonthPlanMissing, setNextMonthPlanMissing] = useState(false); // Plan du mois suivant pas encore importé (utile seulement en fin de mois)
  const [historyView, setHistoryView] = useState("weekly"); // weekly|monthly

  /* Admin — vraies données. Pas de route de création utilisateur côté serveur (retirée
     le 2026-07-25e, cahier des charges §6/§7 : un compte ne naît plus que par
     auto-inscription réelle POST /api/auth/register, jamais créé "à la volée" par un
     responsable) — confirmé en direct : POST /api/users renvoie un vrai 404. Le vrai
     vanilla users-admin.js appelle pourtant encore cette route à la création (code mort
     jamais nettoyé côté vanilla) — ne PAS reproduire ce bouton cassé ici. */
  const [adminScreen, setAdminScreen] = useState("hub"); // hub|users|roles|stores
  const [adminUsers, setAdminUsers] = useState([]);
  // "Ton responsable réseau" (2026-08-22) — remplace l'ancien calcul 100% client
  // (adminUsers.find(...), qui ne marchait JAMAIS pour un compte normal : GET /api/users
  // est un endpoint admin, adminUsers restait vide pour lui — bug réel trouvé en concevant
  // Vertretung). Résolu côté serveur (GET /api/me/leader), conscient de la Vertretung.
  const [myLeaderInfo, setMyLeaderInfo] = useState(null);
  const loadMyLeader = () => apiJSON("/api/me/leader").then((data) => setMyLeaderInfo(data.leader || null)).catch(() => setMyLeaderInfo(null));

  // Vertretung entre grands responsables (2026-08-22) — écran réseau autonome
  // (screen === "vertretung"). "hub" = mes demandes + celles où je remplace + boîte à
  // valider ; "create" = formulaire "Déclarer mon absence".
  const [vertretungScreen, setVertretungScreen] = useState("hub");
  const [vertretungList, setVertretungList] = useState([]);
  const [vertretungPeers, setVertretungPeers] = useState([]);
  const [vertretungBusy, setVertretungBusy] = useState(false);
  const emptyVertretungForm = { deputyEmployeeId: "", reason: "urlaub", startDate: "", endDate: "", endDateUnknown: false };
  const [vertretungForm, setVertretungForm] = useState(emptyVertretungForm);
  const loadVertretungList = () => apiJSON("/api/vertretung").then((data) => setVertretungList(data.vertretungen || [])).catch(() => {});
  const loadVertretungPeers = () => apiJSON("/api/vertretung/peers").then((data) => setVertretungPeers(data.peers || [])).catch(() => setVertretungPeers([]));
  // Retour utilisateur explicite (2026-10-14, capture à l'appui) : "une demande de Vertretung
  // validée doit être automatique chez le demandeur et ne pas attendre une actualisation de la
  // page" — loadVertretungList() n'était rechargé qu'aux points d'action (entrée sur l'écran,
  // après approve/reject/cancel), jamais en arrière-plan ; le statut "En attente" restait figé
  // tant que le DEMANDEUR ne rechargeait pas lui-même après que l'APPROBATEUR ait validé
  // ailleurs. Même patron déjà établi partout (loadAdminSessions/loadNotifications/
  // loadConversations…, 15s, voir feedback_live_update_everywhere_no_refresh) — gated sur
  // l'écran Vertretung, jamais un polling global.
  useEffect(() => {
    if (screen !== "vertretung") return;
    loadVertretungList();
    const poll = setInterval(loadVertretungList, 15000);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);
  const submitVertretungRequest = () => {
    if (!vertretungForm.deputyEmployeeId) return showToast(t("vertretung.needDeputyToast", "Choisis un pair pour te remplacer."));
    if (!vertretungForm.startDate) return showToast(t("vertretung.needStartToast", "Choisis une date de début."));
    if (vertretungForm.reason === "urlaub" && !vertretungForm.endDate) return showToast(t("vertretung.needEndToast", "Choisis une date de fin."));
    // Retour utilisateur explicite (2026-08-25) — même règle que Krankmeldung : jamais une
    // date de début dans le passé, date de fin toujours après la date de début.
    if (vertretungForm.startDate < todayISO()) return showToast(t("vertretung.toast.startDateInPast", "La date de début ne peut pas être dans le passé."));
    if (!(vertretungForm.reason === "krankheit" && vertretungForm.endDateUnknown) && vertretungForm.endDate && vertretungForm.endDate < vertretungForm.startDate) {
      return showToast(t("vertretung.toast.endBeforeStart", "La date de fin ne peut pas être antérieure à la date de début."));
    }
    setVertretungBusy(true);
    apiJSON("/api/vertretung", {
      method: "POST",
      body: JSON.stringify({
        deputyEmployeeId: vertretungForm.deputyEmployeeId, reason: vertretungForm.reason,
        startDate: vertretungForm.startDate,
        endDate: vertretungForm.reason === "krankheit" && vertretungForm.endDateUnknown ? "" : vertretungForm.endDate,
        endDateUnknown: vertretungForm.reason === "krankheit" && vertretungForm.endDateUnknown,
      }),
    })
      .then(() => {
        showToast(t("toast.vertretungSent", "✓ Demande envoyée pour validation"));
        setVertretungForm(emptyVertretungForm);
        setVertretungScreen("hub");
        loadVertretungList();
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setVertretungBusy(false));
  };
  const decideVertretung = (id, action) => {
    apiJSON(`/api/vertretung/${id}/${action}`, { method: "POST" })
      .then(() => { showToast(action === "approve" ? t("toast.vertretungApproved", "✓ Vertretung approuvée") : t("toast.vertretungRejected", "Demande refusée")); loadVertretungList(); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const cancelVertretung = (id) => {
    apiJSON(`/api/vertretung/${id}/cancel`, { method: "POST" })
      .then(() => { showToast(t("toast.vertretungCancelled", "Vertretung annulée")); loadVertretungList(); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const [adminPending, setAdminPending] = useState([]); // GET /api/auth/pending — vide si non habilité à voir/valider
  // Demandes "mot de passe oublié" (retour utilisateur 2026-08-05 : le bouton pré-connexion
  // était factice, "simulé dans l'artefact" — le mécanisme réel (code à usage unique généré
  // par un responsable, jamais le mot de passe lui-même) existait déjà côté serveur depuis
  // le §11/§12 de la refonte auth, jamais câblé côté client. Même périmètre/scoping que
  // adminPending (voir GET /api/auth/reset-requests).
  const [adminResetRequests, setAdminResetRequests] = useState([]);
  const [resetCodeReveal, setResetCodeReveal] = useState(null); // { request, code, expiresInMinutes } — affiché UNE fois, jamais rechargeable
  // "Mon équipe filiale" (retour utilisateur explicite, 2026-08-29) — écran dédié FL/FLA,
  // volontairement séparé de tout l'état Admin ci-dessus (adminScreen/adminUsers/...) : ne
  // réutilise QUE les 3 listes déjà scopées serveur pour ce rôle (adminPending,
  // adminResetRequests via loadAdminPending/loadAdminResetRequests, déjà définis ci-dessus) +
  // sa propre liste d'équipe/formulaire d'édition, jamais l'écran Admin lui-même.
  const [flTeamTab, setFlTeamTab] = useState("pending"); // pending | workers | resets
  const [flTeamWorkers, setFlTeamWorkers] = useState([]);
  const [flTeamWorkersLoading, setFlTeamWorkersLoading] = useState(false);
  const [flWorkerEditForm, setFlWorkerEditForm] = useState(null); // { id, firstname, lastname, phone, email, role } | null si fermé
  const [flWorkerEditBusy, setFlWorkerEditBusy] = useState(false);
  const [adminSessions, setAdminSessions] = useState([]); // GET /api/admin/sessions — super-admin uniquement
  const [adminWebauthnDevices, setAdminWebauthnDevices] = useState([]); // GET /api/admin/webauthn-devices — super-admin/NEXORA_ADMIN uniquement
  const [rolePerms, setRolePerms] = useState({});
  const [rolePermsUpdatedAt, setRolePermsUpdatedAt] = useState(null);
  // Bug réel trouvé (retour utilisateur, module Warenaustausch juste après connexion) :
  // rolePerms démarre à {} et se peuple de façon ASYNCHRONE (loadRolePermissions, appelée
  // dans le bootstrap post-connexion) — si l'utilisateur tape une tuile de module avant que
  // ce premier chargement ait fini, hasModuleAccess lit un rolePerms encore vide et refuse
  // l'accès à TORT ("Accès refusé"), alors même que le rôle a bien la permission — décision
  // qui reste figée une fois prise (rien ne rouvre le module automatiquement une fois les
  // permissions arrivées). Ce drapeau distingue "pas encore chargé" de "chargé et vide" :
  // hasModuleAccess ne refuse jamais tant que le premier chargement n'est pas terminé
  // (le vrai rempart de sécurité reste côté serveur sur chaque route /api/*, voir le
  // commentaire au-dessus de hasModuleAccess).
  const [rolePermsLoaded, setRolePermsLoaded] = useState(false);
  const [activeRole, setActiveRole] = useState("FL");
  const [userEditForm, setUserEditForm] = useState(null); // { id, role, storeId, firstname, lastname, employeeId, phone, email, birthdate, weeklyContractedHours, ausbildungsbeauftragter }
  // Recherche dans la checklist "Filiales gérées" (2026-08-22, retour utilisateur explicite :
  // "et si on devait rechercher parmi 1200 filiales ??") — Nexora vise 1200+ filiales
  // (voir project_nexora_multi_store_scale), une simple liste défilante sans filtre y serait
  // inutilisable. Réinitialisée à chaque ouverture de fiche (voir openUserEdit).
  const [userEditStoreSearch, setUserEditStoreSearch] = useState("");
  const [pendingApprovalRole, setPendingApprovalRole] = useState({}); // { [requestId]: role } — le rôle réel est choisi par l'admin, jamais par la personne qui s'inscrit
  // Recherche + filtre filiale (retour utilisateur explicite, artefact validé) — filtre
  // purement client sur adminUsers, déjà scopé par le serveur (admin = toutes filiales, GR =
  // son périmètre, employé = la sienne, voir GET /api/users) : aucune nouvelle donnée n'est
  // exposée, on filtre uniquement ce que le compte pouvait déjà voir. Le chip filiale ne
  // s'affiche que si plusieurs filiales existent réellement dans les données reçues — jamais
  // pour un FL/FLA qui n'en a qu'une seule.
  const [adminUserSearch, setAdminUserSearch] = useState("");
  const [adminUserFilialeFilter, setAdminUserFilialeFilter] = useState("all");
  const [adminUserStatusFilter, setAdminUserStatusFilter] = useState("all"); // all | active | disabled
  // Recherche dédiée filiale + cartes dépliables (retour utilisateur 2026-08-16) — jamais une
  // simple rangée de puces à l'échelle réseau (voir storeflow_nexora_multi_store_scale,
  // CRITIQUE : construire pour 1200+ filiales, jamais pour "seulement N aujourd'hui" — un
  // scroll horizontal de puces ne tient pas à cette échelle, un champ de recherche si).
  // adminUsersExpandedStores : Set des storeId actuellement dépliés — tout replié par défaut
  // (une liste de 1200 filiales toutes ouvertes serait illisible).
  const [adminUserFilialeSearch, setAdminUserFilialeSearch] = useState("");
  const [adminUsersExpandedStores, setAdminUsersExpandedStores] = useState(() => new Set());
  const toggleAdminStoreExpanded = (storeId) => setAdminUsersExpandedStores((prev) => {
    const next = new Set(prev);
    if (next.has(storeId)) next.delete(storeId); else next.add(storeId);
    return next;
  });
  // Pilotage réseau > Analyse > Équipe (vue VKL, 2026-08-16) : carte BL dépliable pour
  // voir le détail par filiale — même patron replié-par-défaut que adminUsersExpandedStores.
  const [pilotTeamExpandedBL, setPilotTeamExpandedBL] = useState(() => new Set());
  const togglePilotTeamBL = (employeeId) => setPilotTeamExpandedBL((prev) => {
    const next = new Set(prev);
    if (next.has(employeeId)) next.delete(employeeId); else next.add(employeeId);
    return next;
  });
  // "Remplacer ce responsable" (2026-08-16, retour utilisateur explicite) — un GR qui part
  // (succession) : choisir la personne qui reprend le poste, hériter son périmètre de
  // filiales par défaut MAIS rester librement modifiable (ajouter/retirer des filiales, "le
  // responsable peut hériter des filiales d'un autre mais en recevoir plus ou moins"),
  // hérite aussi la même position dans la chaîne (reportsTo) et récupère automatiquement
  // les subordonnés de l'ancien GR (leur reportsTo est repointé), ancien compte désactivé
  // automatiquement (jamais deux comptes actifs sur le même poste). null = sheet fermée.
  const [replaceLeaderDraft, setReplaceLeaderDraft] = useState(null); // { source, targetId, targetSearch, storeSearch, stores: string[] }
  const openReplaceLeader = (source) => {
    setReplaceLeaderDraft({
      source, targetId: null, targetSearch: "", storeSearch: "",
      stores: (source.stores && source.stores.length ? source.stores : [source.storeId]).filter(Boolean),
    });
    loadStoresCatalog();
    setSheet("replaceLeader");
  };
  const toggleReplaceLeaderStore = (storeId) => setReplaceLeaderDraft((d) => {
    if (!d) return d;
    const has = d.stores.includes(storeId);
    return { ...d, stores: has ? d.stores.filter((s) => s !== storeId) : [...d.stores, storeId] };
  });
  const confirmReplaceLeader = () => {
    const d = replaceLeaderDraft;
    if (!d || !d.targetId || d.stores.length === 0) return;
    const target = adminUsers.find((u) => u.id === d.targetId);
    if (!target) return;
    // Les subordonnés directs de l'ancien GR (BL sous un VKL remplacé, VKL sous un NL
    // remplacé...) doivent suivre le nouveau titulaire du poste — sinon la pyramide les
    // affiche encore rattachés à un compte désormais désactivé.
    const subordinates = adminUsers.filter((u) => u.reportsTo && u.reportsTo === d.source.employeeId);
    Promise.all([
      apiJSON(`/api/users/${encodeURIComponent(target.id)}`, { method: "PATCH", body: JSON.stringify({
        role: d.source.role, stores: d.stores, reportsTo: d.source.reportsTo || null,
      }) }),
      apiJSON(`/api/users/${encodeURIComponent(d.source.id)}`, { method: "DELETE" }),
      ...subordinates.map((s) => apiJSON(`/api/users/${encodeURIComponent(s.id)}`, { method: "PATCH", body: JSON.stringify({ reportsTo: target.employeeId }) })),
    ])
      .then(() => {
        showToast(t("toast.leaderReplaced", "✓ Responsable remplacé"));
        setSheet(null); setReplaceLeaderDraft(null);
        loadAdminUsers();
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const [adminPermSearch, setAdminPermSearch] = useState("");
  // Onglet "Par utilisateur" de Rôles & permissions (cacher/afficher un module, admin-only,
  // voir isModuleHiddenForUser) — "role" = comportement actuel inchangé pour tout le monde
  // (PERMISSION_GROUPS ordinaires, assignables à n'importe qui — SANS RAPPORT avec
  // "roleModules" ci-dessous, malgré le nom proche, piège de confusion réel repéré en
  // lisant ce code : ne jamais fusionner les deux).
  const [rolesScreenMode, setRolesScreenMode] = useState("role"); // role | user | roleModules
  const [hiddenModulesUserSearch, setHiddenModulesUserSearch] = useState("");
  const [hiddenModulesTargetUserId, setHiddenModulesTargetUserId] = useState(null);
  // Modules cachés PAR RÔLE (2026-08-15, retour utilisateur explicite — comble le trou
  // "première connexion" du contrôle par utilisateur ci-dessus, qui ne peut viser qu'un
  // compte déjà existant) — { [role]: string[] }, gate strict NEXORA_ADMIN/super-admin
  // identique, fusionné côté SERVEUR uniquement (voir normalizeServerUser, server.js) avec
  // hiddenModules par utilisateur — le client ne lit jamais roleHiddenModules pour décider
  // si un module est caché, seulement pour l'écran d'édition Admin lui-même.
  const [roleHiddenModules, setRoleHiddenModules] = useState({});
  const [hiddenModulesTargetRole, setHiddenModulesTargetRole] = useState(null);

  // Modules mis en avant sur l'Accueil (2026-11-02, retour utilisateur explicite : "très peu
  // de personnes utilisent l'app... un favoris pour tout les utilisateurs groupée") —
  // affiché en tête d'Accueil pour TOUS les comptes normaux. Liste PAR FILIALE côté serveur
  // (server/homeHighlights/routes.js) : GET renvoie déjà celle de la filiale de CE compte
  // (repli sur le réglage réseau NEXORA_ADMIN si la filiale n'a encore rien choisi) — ce
  // client n'a jamais à connaître de storeId, juste à afficher ce que le serveur renvoie.
  // Modifiable ici même par FL/FLA/Stellvertreter·in (2e retour utilisateur explicite,
  // "donner accès à FL, FLA et Stellvertreter aussi") via homeHighlightsEditOpen plus bas —
  // le serveur refuse/scope déjà tout seul, ce client n'a qu'à proposer le bouton au bon
  // rôle. Jamais lu ni modifié par l'écran GR (NETWORK_HOME_MODULES, voir plus bas) — pas
  // concerné. Repli volontairement identique au défaut serveur
  // (DEFAULT_HIGHLIGHTED) pour qu'une app hors-ligne au premier chargement affiche la même
  // chose qu'une fois connectée.
  const [highlightedModuleIds, setHighlightedModuleIds] = useState(["control", "reductions", "finder", "tasks", "planning", "reporting"]);
  const [homeAllModulesOpen, setHomeAllModulesOpen] = useState(false);
  const [homeHighlightsEditOpen, setHomeHighlightsEditOpen] = useState(false);
  useEffect(() => {
    if (!user) return;
    apiJSON("/api/home/highlighted-modules")
      .then((data) => { if (Array.isArray(data?.moduleIds)) setHighlightedModuleIds(data.moduleIds); })
      .catch(() => {}); // hors-ligne ou erreur réseau : garde le repli déjà affiché, jamais un Accueil vide.
  }, [user]);

  /* Centre documentaire (Document Intelligence Center) — vraies données
     (server.js /api/document-intel/*, jamais extrait en module). Le moteur de lecture
     (Claude vision) n'est déclenché qu'explicitement par article ("Analyser"), jamais
     automatique sur tout un batch d'upload — chaque appel a un vrai coût API. */
  const [docIntelDocs, setDocIntelDocs] = useState([]);
  const [docIntelSummary, setDocIntelSummary] = useState({ todayCount: 0, pending: 0, unsupported: 0 });
  const [docIntelAnalyzingIds, setDocIntelAnalyzingIds] = useState([]);
  const [docIntelError, setDocIntelError] = useState("");
  const docIntelFileInputRef = useRef(null);
  const docIntelCameraInputRef = useRef(null);

  /* Arbeitszeitliste — vraies données (server/arbeitszeitliste/*, AZL-1 à AZL-6 déjà
     livrés côté serveur, module absent de nexora-app jusqu'ici). Document réel recalculé
     à chaque GET tant que non validé ; dès la 3e signature (employé/FL-FLA/Bereichsleiter),
     le serveur gèle un instantané et l'archive en PDF officiel — jamais un doublon de cette
     logique côté client. Rendu du document lui-même porté depuis le vrai SVG A3 du vanilla
     (lib/arbeitszeitlisteSvg.js), pas une réimplémentation JSX qui pourrait diverger. */
  const [azlStoreId, setAzlStoreId] = useState("");
  const [azlYear, setAzlYear] = useState(new Date().getFullYear());
  const [azlMonth, setAzlMonth] = useState(new Date().getMonth() + 1);
  const [azlData, setAzlData] = useState(null);
  const [azlLoading, setAzlLoading] = useState(false);
  const [azlError, setAzlError] = useState("");
  const [frExporting, setFrExporting] = useState(false);
  const [azlDayDetail, setAzlDayDetail] = useState(null);
  const [azlDayEditForm, setAzlDayEditForm] = useState(null);
  const [azlDaySaving, setAzlDaySaving] = useState(false);
  const [azlAnomalyList, setAzlAnomalyList] = useState([]);
  const [azlValidationRole, setAzlValidationRole] = useState(null);
  const [azlSignBusy, setAzlSignBusy] = useState(false);
  const [azlSignError, setAzlSignError] = useState("");
  const [azlSignReady, setAzlSignReady] = useState(false);
  const [azlExternalList, setAzlExternalList] = useState([]);
  const [azlExternalSearch, setAzlExternalSearch] = useState("");
  const [azlExternalResults, setAzlExternalResults] = useState([]);
  const [azlExternalSelected, setAzlExternalSelected] = useState(null);
  const [azlExternalForm, setAzlExternalForm] = useState({ start: "", end: "", reason: "" });
  const [azlExternalError, setAzlExternalError] = useState("");
  const [azlHistoryVersions, setAzlHistoryVersions] = useState([]);
  const [azlReopenReason, setAzlReopenReason] = useState("");
  const [azlReopenError, setAzlReopenError] = useState("");
  const [azlReopenBusy, setAzlReopenBusy] = useState(false);
  const azlSignPadMountRef = useRef(null);
  const azlSignPadRef = useRef(null);
  const azlExternalSearchTimer = useRef(null);

  /* Pilotage réseau — vraie vue responsable (Bereichsleiter/Verkaufsleiter/
     Niederlassungsleiter/NEXORA_ADMIN). Aujourd'hui/Alertes réels via GET
     /api/manager/overview (déjà réel côté serveur, jamais consommé avant). Missions =
     vrai système de tâches réseau (tasks state, scope "network"), pas de duplication.
     Nexora est construite pour un réseau de plus de 1200 filiales (voir
     project_nexora_multi_store_scale) — jamais une hiérarchie ou des filiales inventées,
     le regroupement par Bezirk vient du vrai registre server/stores/*. */
  const [pilotScreen, setPilotScreen] = useState("today"); // today | alertes | missions
  const [managerOverview, setManagerOverview] = useState(null); // { stores, perStore, totals, feed }
  const [managerOverviewError, setManagerOverviewError] = useState("");
  const [storesRegistry, setStoresRegistry] = useState([]);
  const [bezirkeRegistry, setBezirkeRegistry] = useState([]);
  const [alertesSeverityFilter, setAlertesSeverityFilter] = useState("all"); // all | danger | warning | info
  // Bascule vue GR / vue normale — réservée à NEXORA_ADMIN (retour utilisateur explicite :
  // "le role admin doit pouvoir permettre de switcher entre la vue GR et la vue normale"),
  // pour tester l'app exactement comme un employé de filiale la voit. Jamais persistée —
  // repart sur la vue GR par défaut à la reconnexion, pour ne jamais rester "coincé" sans
  // s'en rendre compte.
  const [adminViewAsEmployee, setAdminViewAsEmployee] = useState(false);
  const isNetworkLeader = ["Bereichsleiter", "Verkaufsleiter", "Niederlassungsleiter", "NEXORA_ADMIN"].includes(String(user?.role || "").trim())
    && !(user?.role === "NEXORA_ADMIN" && adminViewAsEmployee);
  // Filialleiter/FLA — responsable d'UNE seule filiale (contrairement à isNetworkLeader,
  // plusieurs filiales). Ma Fiche > Performance (retour utilisateur 2026-08-15) : les deux
  // tiers ont besoin du volet filiale (CA/Stundenleistung/Pertes/Réactivité/Réclamations),
  // mais FL/FLA garde EN PLUS ses cartes individuelles (Assiduité/Tâches perso/PLU Quiz —
  // il reste aussi un employé), alors que BL/VL/NL les remplace entièrement (aucune n'a de
  // sens à ce niveau).
  const isFlOrFlaRole = ["FL", "FLA"].includes(String(user?.role || "").trim());
  // Phase 3 — Filiales (tri) + Analyse & rapports. Chiffres réels agrégés par vraie
  // filiale depuis Reporting (CA réel/planifié, Stundenleistung, pertes manual_waste) —
  // jamais de Bezirk/hiérarchie inventés, jamais de "progression vs objectif" tant qu'il
  // n'y a pas de vraie donnée derrière (voir pilotPeriodRange/loadPilotStoreStats).
  const [pilotFilialeSort, setPilotFilialeSort] = useState("perf"); // perf | pertes | objectif | stundenleistung
  // Recherche filiale — Pilotage réseau > Filiales (retour utilisateur explicite 2026-08-16 :
  // "pour un VKL qui dirige plusieurs BL... la liste deviendra très longue, idem pour un NL"
  // — un VKL/NL peut hériter de dizaines/centaines de filiales, jamais un simple défilement
  // à cette échelle, voir [[project_nexora_multi_store_scale]]). Toujours affiché dès qu'il y
  // a au moins une filiale, jamais un gate ">1" (même principe que le champ équivalent
  // d'Admin > Utilisateurs).
  const [pilotFilialeSearch, setPilotFilialeSearch] = useState("");
  // Cap d'affichage des 2 classements pleine liste (Analyse > Filiales : Travail et
  // productivité, Classement Umsatz) — retour utilisateur 2026-08-23, capture d'écran à
  // l'appui, "cette liste devient très longue avec 100 filiales" (même principe que
  // pilotFilialeSearch juste au-dessus, mais ces 2 listes n'ont pas de recherche, un
  // classement se lit surtout par ses extrêmes) : replié par défaut sur les
  // PILOT_RANKING_CAP cas les plus parlants (pires Stundenleistung / meilleurs CA, déjà
  // l'ordre de tri existant), "Voir tout" déplie sans jamais perdre de donnée.
  const [pilotWorkRowsExpanded, setPilotWorkRowsExpanded] = useState(false);
  const [pilotUmsatzRowsExpanded, setPilotUmsatzRowsExpanded] = useState(false);
  const [pilotPeriod, setPilotPeriod] = useState("mois"); // jour | semaine | mois | trimestre | annee
  // Décalage arrière/avant de la période choisie (0 = en cours) — demande utilisatrice
  // (2026-08-15), maquette https://claude.ai/code/artifact/c627bbf9-3964-42c1-b42b-51ea6b143937.
  // Remis à 0 à chaque changement de granularité (jamais hériter un décalage d'un autre
  // niveau — "3 mois avant" n'a aucun sens une fois repassé en vue Semaine).
  const [periodOffset, setPeriodOffset] = useState(0);
  useEffect(() => { setPeriodOffset(0); }, [pilotPeriod]);
  // Décalage (en semaines) de la fenêtre glissante de PILOT_HISTORY_WEEKS semaines de
  // l'onglet Évolution — distinct de periodOffset (Résumé/Filiales décalent une PÉRIODE
  // discrète, ici on décale une FENÊTRE continue). 0 = fenêtre actuelle se terminant
  // aujourd'hui.
  const [evolutionWeekOffset, setEvolutionWeekOffset] = useState(0);
  const [pilotDashFiliale, setPilotDashFiliale] = useState("all"); // "all" ou un storeId
  // Restructuration Analyse en 3 onglets (demande utilisatrice, 2026-08-14, maquette
  // https://claude.ai/code/artifact/5f298b70-c203-4b44-8840-a2e14a953f06) — regroupe par
  // INTENTION (résumé rapide / tendance dans le temps / comparaison entre filiales) plutôt
  // que par ordre d'ajout historique, qui dispersait les 3 graphes d'évolution.
  const [analyseTab, setAnalyseTab] = useState("resume"); // resume | evolution | filiales | comparaison
  const [pilotStoreStats, setPilotStoreStats] = useState({}); // { [storeId]: {umsatzIst,umsatzPlan,stundenIst,stundenPlan,pertes,stundenleistung,stundenleistungPlan} }
  const [pilotStatsLoading, setPilotStatsLoading] = useState(false);
  // Onglet Comparaison (demande utilisatrice, 2026-08-17) : "comparer les données d'une
  // filiale sur des périodes qu'il souhaite et entre filiales sur période définie aussi" —
  // deux modes indépendants, avec leurs propres period/offset LOCAUX à l'onglet (jamais
  // pilotPeriod/periodOffset, qui pilotent Résumé/Filiales — même précédent que
  // ficheWeekStats/ficheMonthStats, voir commentaire fetchStoreStatsFor). Jamais écrit dans
  // pilotStoreStats non plus, pour la même raison.
  const [compareMode, setCompareMode] = useState("periods"); // periods (1 filiale, N périodes) | stores (N filiales, 1 période)
  const [compareFiliale, setCompareFiliale] = useState(null); // storeId ou null (aucune sélection = état vide)
  // Mode Périodes : nombre de périodes LIBRE (retour utilisateur explicite, 2026-08-17 —
  // "pas juste une comparaison entre 2" — jamais borné en dur), chacune avec un id stable
  // (compteur, jamais l'index du tableau — nécessaire pour que + Ajouter/🗑️ Retirer ne
  // fasse pas sauter les inputs d'une autre carte pendant le re-render).
  const [comparePeriods, setComparePeriods] = useState([{ id: 1, period: "mois", offset: -1 }, { id: 2, period: "mois", offset: 0 }]);
  const nextComparePeriodId = useRef(3);
  const [comparePeriodsStats, setComparePeriodsStats] = useState({}); // { [id]: mêmes champs que pilotStoreStats, ou null si pas encore chargé }
  const [compareStoresSelected, setCompareStoresSelected] = useState([]); // storeId[], min 2 pour un vrai comparatif
  const [compareStoresPeriod, setCompareStoresPeriod] = useState("mois");
  const [compareStoresOffset, setCompareStoresOffset] = useState(0);
  const [compareStoresStats, setCompareStoresStats] = useState({}); // { [storeId]: mêmes champs que pilotStoreStats }
  const [compareLoading, setCompareLoading] = useState(false);
  // Ma Fiche > Performance (FL/FLA/BL/VL/NL, retour utilisateur 2026-08-15) — jamais
  // partagés avec pilotStoreStats : un chargement de Ma Fiche ne doit jamais écraser ce que
  // Pilotage réseau affiche (et inversement) si les deux écrans ont été visités dans la
  // même session. Semaine/Mois toujours offset 0 (en cours) — pas de navigation temporelle
  // sur cette fiche, contrairement à l'écran Analyse.
  const [ficheWeekStats, setFicheWeekStats] = useState({}); // { [storeId]: mêmes champs que pilotStoreStats, period="semaine" }
  const [ficheMonthStats, setFicheMonthStats] = useState({}); // idem, period="mois"
  const [ficheReactivity, setFicheReactivity] = useState(null); // { perStore, totals } — sous-ensemble de GET /api/manager/overview
  const [ficheClaims, setFicheClaims] = useState(null); // { fresh, food } agrégé sur le périmètre, mois en cours
  const [fichePerfLoading, setFichePerfLoading] = useState(false);
  const [pilotHistory, setPilotHistory] = useState({}); // { [storeId]: [umsatzIst des HIST_WEEKS dernières semaines, la plus ancienne en premier] }
  // Objectif RÉEL par semaine (somme des vrais Plan Tag Lu-Sa de la semaine, jamais
  // agg.umsatzPlan re-découpé linéairement selon pilotPeriod) — corrige le bug d'origine du
  // graphique "CA réel vs objectif" (comparaison hebdo contre un objectif mois/trimestre/
  // année sans rapport). Alimenté par loadPilotHistory, même fan-out par filiale × semaine.
  const [pilotHistoryPlan, setPilotHistoryPlan] = useState({}); // { [storeId]: [objectif des HIST_WEEKS dernières semaines] }
  // Heures planifiées BRUTES par semaine (mêmes Plan Tag que pilotHistoryPlan, mais côté
  // stundenPlan plutôt que umsatzPlan) — demande utilisatrice 2026-08-15, "toujours se baser
  // sur le plan Stundenleistung". Sert à calculer Σ umsatzPlan / Σ stundenPlan APRÈS somme
  // sur le périmètre (jamais moyenner un ratio par filiale, voir commentaire loadPilotHistory),
  // remplace le seuil fixe STUNDENLEISTUNG_ALERT_THRESHOLD partout où Stundenleistung est
  // comparée à une cible.
  const [pilotStundenPlanHistory, setPilotStundenPlanHistory] = useState({}); // { [storeId]: [heures planifiées par semaine] }
  // Demande Verkaufsleiter (2026-08-14) : suivre l'ÉVOLUTION de Stundenleistung et Pertes,
  // pas juste un instantané — mêmes fetches hebdo que pilotHistory (stundenleistung déjà
  // récupéré pour Umsatz contient aussi stundenIst, jamais sommé jusqu'ici ; Pertes ajoute
  // un fetch manual_waste/prebake/brot par semaine, même familles que loadPilotStoreStats).
  const [pilotStundenHistory, setPilotStundenHistory] = useState({}); // { [storeId]: [stundenleistung €/h ou null par semaine] }
  const [pilotPertesHistory, setPilotPertesHistory] = useState({}); // { [storeId]: [pertes € par semaine] }
  const [pilotPertesCategoryHistory, setPilotPertesCategoryHistory] = useState({}); // { [storeId]: [{[categoryId]: €} par semaine] }
  // Rythme Umsatz (Analyse, filiale unique) — jours du mois affiché pour la filiale
  // actuellement sélectionnée (pilotDashFiliale), résolution JOURNALIÈRE réelle (Plan +
  // Ist), même construction que stundenDays/pilotStundenDays (buildStundenDaysArray) mais
  // navigable par mois (contrairement à pilotStundenDays, "jamais navigable" côté fiche
  // filiale) — jamais au-delà du mois en cours (offset > 0 interdit).
  const [pilotRhythmMonthOffset, setPilotRhythmMonthOffset] = useState(0);
  const [pilotRhythmDays, setPilotRhythmDays] = useState([]);
  // Lecture au doigt des 2 graphiques Analyse (retour utilisateur explicite : "interagir"
  // veut dire toucher le graphique, pas juste qu'il se mette à jour tout seul) — jour
  // survolé/touché sur le graphique journalier filiale unique, et index de semaine
  // survolé/touché sur le graphique hebdo agrégé "Toutes les filiales".
  const [pilotChartHoverDay, setPilotChartHoverDay] = useState(null);
  const [pilotAggHoverIdx, setPilotAggHoverIdx] = useState(null);
  // Graphe "Vue d'ensemble" (Analyse > Évolution) — retour utilisateur explicite
  // (2026-09-01, "impossible d'interagir avec le doigt avec ce diagramme") : contrairement
  // aux graphes "Détail" juste en dessous (pilotAggHoverIdx, updateAggHover), celui-ci
  // n'avait jamais eu de survol tactile — un oubli au moment de sa construction, pas un
  // choix voulu. État dédié (pas pilotAggHoverIdx partagé) pour ne jamais faire réagir les
  // deux graphes en même temps l'un à l'autre.
  const [pilotOverviewHoverIdx, setPilotOverviewHoverIdx] = useState(null);
  // Même mécanique que pilotAggHoverIdx, pour les 2 nouveaux graphes d'évolution
  // (Stundenleistung, Pertes) — index de semaine survolé/touché, plus l'index "sélectionné"
  // (tap) pour Pertes qui pilote le détail par rayon affiché sous le graphe.
  const [pilotStundHoverIdx, setPilotStundHoverIdx] = useState(null);
  const [pilotPertesHoverIdx, setPilotPertesHoverIdx] = useState(null);
  const [pilotPertesSelectedIdx, setPilotPertesSelectedIdx] = useState(null);
  // Le rapport narré se "rédige" brièvement (3 points qui rebondissent) puis révèle ses
  // phrases une à une, à chaque fois qu'on arrive sur Analyse ou qu'on change de filtre —
  // porté fidèlement depuis nexora-app_1.zip (prototype de référence, retour utilisateur
  // explicite 2026-08-03 : le texte s'affichait ici instantanément, jamais "rédigé").
  const [dashTyping, setDashTyping] = useState(true);
  useEffect(() => {
    if (!(screen === "home" && isNetworkLeader && pilotScreen === "analyse")) return;
    setDashTyping(true);
    const timer = setTimeout(() => setDashTyping(false), 700);
    return () => clearTimeout(timer);
  }, [screen, isNetworkLeader, pilotScreen, pilotPeriod, pilotDashFiliale]);
  // Fiche filiale — drill-down réel par filiale (13 rubriques en accordéon), fidèle à
  // nexora-app_1.zip (pilotScreen "filiale-detail"). Les rubriques croisent le vrai feed
  // /api/manager/overview (claims/krankmeldung/reporting) et les vraies Missions ciblant
  // cette filiale ; les rubriques sans signal réel aujourd'hui (azl/bestellung/haccp/
  // kuhlung/liefer/inventaire/equipe/planning/performance) affichent honnêtement
  // "Rien à signaler" plutôt qu'une donnée inventée.
  const [pilotFilialeId, setPilotFilialeId] = useState(null);
  const [pilotFilialeSection, setPilotFilialeSection] = useState(null);
  // Retour utilisateur (2026-10-14, suite au fix reload) : "tu dois aussi le faire sur la vue
  // GR entière" — pour un compte encadrement réseau (isNetworkLeader), `screen` reste "home"
  // en permanence ; c'est `pilotScreen` qui distingue le vrai sous-écran Pilotage affiché, et
  // le 1er passage (voir lastScreenRef/screenRestoredRef plus haut) ignorait toute
  // restauration dès que `screen` valait "home". Bloc séparé, placé ICI (après la déclaration
  // de pilotScreen/pilotFilialeId) et non avec le reste plus haut : un useEffect(fn, [dep])
  // évalue son tableau de dépendances PENDANT le rendu, pas à l'appel — contrairement au
  // corps de openModule() (une fonction, jamais exécutée avant d'être appelée) — y référencer
  // pilotScreen avant sa déclaration plus bas levait "Cannot access before initialization" à
  // l'exécution réelle (trouvé en test local, jamais en relecture de code seule). Pas de
  // loader à rejouer ici (contrairement à openModule) : navigateToTabIndex ne charge jamais
  // rien de plus en changeant d'onglet GR, tout est déjà chargé au montage Accueil.
  const lastPilotScreenRef = useRef(null);
  if (lastPilotScreenRef.current === null) {
    try { lastPilotScreenRef.current = sessionStorage.getItem("nexora_last_pilot_screen") || ""; } catch { lastPilotScreenRef.current = ""; }
  }
  const lastPilotFilialeRef = useRef(null);
  if (lastPilotFilialeRef.current === null) {
    try { lastPilotFilialeRef.current = sessionStorage.getItem("nexora_last_pilot_filiale") || ""; } catch { lastPilotFilialeRef.current = ""; }
  }
  const pilotScreenRestoredRef = useRef(false);
  useEffect(() => {
    try { sessionStorage.setItem("nexora_last_pilot_screen", pilotScreen || ""); } catch {}
  }, [pilotScreen]);
  useEffect(() => {
    try { sessionStorage.setItem("nexora_last_pilot_filiale", pilotFilialeId || ""); } catch {}
  }, [pilotFilialeId]);
  useEffect(() => {
    if (!user || pilotScreenRestoredRef.current) return;
    pilotScreenRestoredRef.current = true;
    try {
      // Rien à restaurer si un autre module a déjà été restauré (voir lastScreenRef plus
      // haut, même valeur sauvegardée) — la vue GR "home" n'est pertinente que lorsque
      // `screen` est resté "home" (absent de sessionStorage, ou explicitement "home").
      const savedScreen = lastScreenRef.current;
      if (savedScreen && savedScreen !== "home") return;
      if (!isNetworkLeader) return;
      const savedPilot = lastPilotScreenRef.current;
      if (!savedPilot || savedPilot === "today") return;
      if (savedPilot === "filiale-detail") {
        if (!lastPilotFilialeRef.current) return; // jamais sans filiale réellement connue
        setPilotFilialeId(lastPilotFilialeRef.current);
      }
      setPilotScreen(savedPilot);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  // Rubrique "Équipe" de la Fiche filiale — vrai effectif GET /api/stores/:storeId/team
  // (voir server.js). pilotTeamContactId = employeeId dont la fiche contact est dépliée
  // (téléphone/email), un seul à la fois — jamais de sheet séparée pour un simple contact.
  const [pilotTeam, setPilotTeam] = useState([]);
  const [pilotTeamContactId, setPilotTeamContactId] = useState(null);
  // Rubrique "Planning" de la Fiche filiale — semaine en cours + dernières semaines
  // publiées (GET /api/plannings + /api/plannings/published, déjà réels, déjà utilisés
  // par le module Planning lui-même). pilotPlanningWeeks = résumé tapable ; ouvrir une
  // ligne charge le plan complet (entries+employees) dans pilotPlanningDetail — jamais
  // l'éditeur Arbeitsplan lui-même (lecture seule pour un GR sur une filiale qui n'est
  // pas la sienne, voir buildPilotPlanningPrintHtml qui ne dépend d'aucun état d'édition).
  const [pilotPlanningWeeks, setPilotPlanningWeeks] = useState([]);
  const [pilotPlanningDetail, setPilotPlanningDetail] = useState(null);
  const [pilotPlanningDetailLoading, setPilotPlanningDetailLoading] = useState(false);
  // Recherche d'un plan antérieur (retour utilisateur explicite, 2026-08-24 : "toujours
  // afficher le dernier plan et donner la possibilité au GR de chercher un plan antérieur
  // et de le recharger au besoin") — n'importe quelle date fait charger le plan de SA
  // semaine (lundi calculé côté client, mondayOfDateISO), ajouté à pilotPlanningWeeks s'il
  // n'y est pas déjà, réutilisant telle quelle la même ligne/le même détail dépliable que
  // la semaine la plus récente.
  const [pilotPlanningSearchDate, setPilotPlanningSearchDate] = useState("");
  const [pilotPlanningSearchBusy, setPilotPlanningSearchBusy] = useState(false);
  // Rubrique "Krankmeldung" de la Fiche filiale — vrais arrêts en cours (GET
  // /api/krankmeldungen, déjà scopé côté serveur pour un GR — sfKrankVisibleForIdentity,
  // "area" = ses filiales), filtré côté client sur cette filiale + generalStatus==="ACTIVE".
  // Remplace le simple compteur de feed générique par une vraie liste exploitable.
  const [pilotKrank, setPilotKrank] = useState([]);
  // Rubrique "AZL" (Arbeitszeitliste) de la Fiche filiale — mois en cours + 2 précédents
  // (GET /api/arbeitszeitliste/:storeId/:year/:month, déjà scopé par filiale côté serveur —
  // isStoreAllowed). pilotAzlOpenKey = "year-month" du mois dont les anomalies sont dépliées.
  const [pilotAzlMonths, setPilotAzlMonths] = useState([]);
  const [pilotAzlOpenKey, setPilotAzlOpenKey] = useState(null);
  // Rubrique "HACCP" de la Fiche filiale — calendrier réel du mois en cours (GET
  // /api/haccp/month, déjà réel — DONE Phase 5). Un jour "closed" (dimanche/férié) n'a
  // jamais de contrôle attendu, jamais compté comme manquant.
  const [pilotHaccpMonth, setPilotHaccpMonth] = useState(null);
  // Rubrique "Réclamations" — vraies réclamations non closes (GET /api/claims, déjà
  // scopé identity.stores côté serveur), filtrées sur cette filiale. Le badge/la couleur
  // du badge restent dérivés du feed générique existant (claimsOpen/claimsOverdue déjà
  // calculés par /api/manager/overview) — seul le CONTENU de la liste change ici.
  const [pilotClaims, setPilotClaims] = useState([]);
  const [pilotFilialeReports, setPilotFilialeReports] = useState([]);
  const [pilotFilialeWA, setPilotFilialeWA] = useState([]);
  const [pilotClaimOpenId, setPilotClaimOpenId] = useState(null);
  // Rubrique "Inventaires" — GET /api/inventur/inventories + /kontrollen, déjà scopés
  // identity.stores côté serveur (même garantie que Krankmeldung/Réclamations ci-dessus,
  // vérifié dans server.js avant de construire cette rubrique) ; filtrage par filiale
  // fait ici, même patron que pilotKrank/pilotClaims.
  const [pilotInventurInventories, setPilotInventurInventories] = useState([]);
  const [pilotInventurKontrollen, setPilotInventurKontrollen] = useState([]);
  const [pilotInventurOpenId, setPilotInventurOpenId] = useState(null);
  // Rendez-vous d'inventaire (2026-08-03, retour utilisateur explicite) — un responsable
  // réseau propose UNE date par filiale (jamais un lot visible par plusieurs filiales,
  // voir server.js) depuis l'accueil Aujourd'hui ; GET /api/inventur/appointments est déjà
  // scopé identity.stores côté serveur, donc renvoie directement tout le périmètre du BL.
  const [pilotAppointments, setPilotAppointments] = useState([]);
  const [pilotAppointmentStoreId, setPilotAppointmentStoreId] = useState(null);
  const [pilotAppointmentDateInput, setPilotAppointmentDateInput] = useState("");
  const [pilotAppointmentChatInput, setPilotAppointmentChatInput] = useState("");
  const [pilotAppointmentBusy, setPilotAppointmentBusy] = useState(false);
  // Champ séparé pour "proposer un NOUVEAU rendez-vous indépendant" quand celui en cours
  // est déjà confirmé (retour utilisateur explicite, 2026-08-06) — ne doit jamais partager
  // pilotAppointmentDateInput, qui sert au "changement de plan" de l'existant : les deux
  // actions peuvent être visibles ensemble dans la même sheet, avec 2 dates différentes.
  const [pilotAppointmentNewDateInput, setPilotAppointmentNewDateInput] = useState("");
  // Même flux côté filiale (Inventur > Rendez-vous) — un seul appel partagé, le serveur ne
  // renvoie de toute façon jamais que le rendez-vous de SA PROPRE filiale à un compte normal.
  const [inventurAppointments, setInventurAppointments] = useState([]);
  const [inventurAppointmentOpenId, setInventurAppointmentOpenId] = useState(null);
  const [inventurAppointmentCounterDate, setInventurAppointmentCounterDate] = useState("");
  const [inventurAppointmentChatInput, setInventurAppointmentChatInput] = useState("");
  const [inventurAppointmentBusy, setInventurAppointmentBusy] = useState(false);
  // Rubrique "PLU Quiz" — GET /api/plu-quiz/attempts?storeId= (déjà réservé aux managers
  // côté serveur, voir server.js) + GET /api/plu-quiz/weekly-summary (déjà scopé
  // identity.stores, filtré ici sur cette filiale précise — même patron que les autres
  // rubriques). Les deux appels existaient déjà côté client (pluWeeklySummary) mais
  // n'étaient jamais consommés nulle part — premier vrai usage.
  const [pilotPluAttempts, setPilotPluAttempts] = useState([]);
  const [pilotPluWeekly, setPilotPluWeekly] = useState(null);
  // Rubrique "Inventurmaßnahme" (remplace l'ancienne rubrique "pluquiz") — archive
  // hebdomadaire complète (Lieferschein Test + Bewertungsbogen + PLU Quiz) en lecture
  // seule pour la filiale consultée, voir loadImArchive/imGroupIntoWeeks.
  const [pilotImArchiveWeeks, setPilotImArchiveWeeks] = useState([]);
  const [pilotImArchiveLoading, setPilotImArchiveLoading] = useState(false);
  const [pilotImArchiveExpandedWeek, setPilotImArchiveExpandedWeek] = useState(null);
  // Rubrique "Reporting" de la Fiche filiale — détail hebdomadaire + les 3 vrais documents
  // déjà téléchargeables par un compte normal pour SA filiale (Stundenleistung/
  // Monatsmeldung/Woche Abschriften), re-scopés à pilotFilialeId (storeId explicite —
  // buildWeeklyReportPrintHtml/monthlyStundenByWeek/buildMonthClosurePrintHtml acceptent
  // désormais un objet opts optionnel, voir plus haut). pilotRepWeekView pilote le
  // toggle "semaine précédente/en cours" ; pilotRepWeekSrcs = { [weekNum]: srcObject },
  // même forme que pastWeekSrc, pour les 2 semaines à la fois (toujours disponibles,
  // jamais bloqué sur une saisie pas encore terminée). pilotStundenDays/
  // pilotReportingHistory/pilotKrankHoursCurrent = mois en cours uniquement (le GR doit
  // toujours pouvoir télécharger l'état actuel, même incomplet).
  const [pilotRepWeekView, setPilotRepWeekView] = useState("current"); // current | previous
  const [pilotRepWeekSrcs, setPilotRepWeekSrcs] = useState({});
  const [pilotRepWeekLoading, setPilotRepWeekLoading] = useState(false);
  const [pilotStundenDays, setPilotStundenDays] = useState([]);
  const [pilotReportingHistory, setPilotReportingHistory] = useState([]);
  const [pilotKrankHoursCurrent, setPilotKrankHoursCurrent] = useState(0);
  // "Mes filiales" (Plus, responsable réseau uniquement) — GET/POST /api/stores +
  // PATCH /api/me/stores. storesCatalog = TOUT le registre réel (pour chercher/choisir une
  // filiale existante), myStoresQuery = recherche en cours dans le sheet "Ajouter".
  const [storesCatalog, setStoresCatalog] = useState([]);
  const [myStoresQuery, setMyStoresQuery] = useState("");
  const [myStoresAdding, setMyStoresAdding] = useState(false);
  const [myStoresBusy, setMyStoresBusy] = useState(false);
  // Périmètre Niederlassung (2026-08-22, retour utilisateur explicite : "jamais hors
  // périmètre de son Niederlassung Leiter") — remplace le filtrage sur storesCatalog (TOUT
  // le registre) par la liste RÉELLEMENT ajoutable renvoyée par le serveur (voir
  // GET /api/me/addable-stores) : impossible de proposer/taper une filiale hors périmètre,
  // jamais un simple refus après coup. myStoreConfirm = confirmation explicite avant tout
  // ajout (retour utilisateur : utile notamment pour couvrir un pair de sa Niederlassung
  // pendant son absence — un geste volontaire, jamais un tap accidentel).
  const [myAddableStores, setMyAddableStores] = useState([]);
  const [myStoresNlResolved, setMyStoresNlResolved] = useState(true);
  const [myStoreConfirm, setMyStoreConfirm] = useState(null); // { id, label }
  // Admin > Filiales — création/édition réelle, réservée à NEXORA_ADMIN (voir
  // storeflow_filiale_lifecycle_zero_inheritance : un BL ne crée jamais de filiale, il se la
  // voit attribuer). germanLander = { code: label } pour le sélecteur de Land (résout les
  // jours fériés régionaux, PUT /api/store-regions/:storeId, mécanisme déjà réel/existant).
  const [storeRegionsMap, setStoreRegionsMap] = useState({});
  const [germanLander, setGermanLander] = useState({});
  const [adminStoreForm, setAdminStoreForm] = useState(null); // {id, label, address, land, isNew}
  const [adminStoreBusy, setAdminStoreBusy] = useState(false);
  // Missions réseau — vraie entité dédiée (server/missions/*), pas une réutilisation de
  // Tasks : cible plusieurs filiales, destinataires libres, preuve requise, et un vrai
  // statut PAR FILIALE (chaque filiale a son propre cycle en_attente→validee, la soumission
  // employé EST le résultat final — plus de validation manuelle GR, retour utilisateur
  // explicite 2026-08-02, voir server/missions/repository.js). Jamais de donnée de démo —
  // vide tant qu'aucune vraie mission n'existe. Le formulaire (création ET édition) ET la
  // fiche détail vivent tous les deux dans un vrai popup premium (sheet==="missionForm" /
  // "missionDetail") — retour utilisateur explicite : le clic sur une mission doit ouvrir
  // un popup, jamais une page plein écran séparée.
  const [networkMissions, setNetworkMissions] = useState([]);
  const [missionDetailId, setMissionDetailId] = useState(null);
  const [missionEditId, setMissionEditId] = useState(null); // null = création, sinon id en édition
  // Partage explicite — verrouillage réseau (retour utilisateur explicite, 2026-08-23 :
  // "Un BL envoie une mission à une filiale ni VKL et NL peuvent voir sauf si BL autorise.")
  // Une mission n'est visible par défaut que du créateur + qui a la filiale directement la
  // sienne (voir server/missions/routes.js, visibleMissions) — ce sheet est le SEUL moyen
  // d'ouvrir la visibilité à d'autres GR (VKL/NL/BL/NEXORA_ADMIN), jamais un ajout implicite.
  const [missionShareId, setMissionShareId] = useState(null);
  const [missionShareDraft, setMissionShareDraft] = useState([]);
  const [missionShareBusy, setMissionShareBusy] = useState(false);
  const [missionShareSearch, setMissionShareSearch] = useState("");
  // Création d'un subordonné réseau (VKL crée un BL, NL crée un VKL) — POST /api/leaders,
  // retour utilisateur explicite 2026-08-23. tempPasswordResult n'est affiché QU'UNE FOIS
  // (jamais re-consultable ensuite, même règle qu'un vrai mot de passe côté serveur) —
  // sheet dédiée distincte du formulaire pour que la fermeture accidentelle du clavier ne
  // fasse jamais disparaître le mot de passe avant que le créateur ait pu le noter.
  const [createLeaderForm, setCreateLeaderForm] = useState({ firstname: "", lastname: "", gender: "", storeIds: [] });
  const [createLeaderBusy, setCreateLeaderBusy] = useState(false);
  const [createLeaderResult, setCreateLeaderResult] = useState(null);
  // Réassignation de filiale entre subordonnés (retour utilisateur 2026-08-24) —
  // PATCH /api/leaders/reassign-store, voir openReassignStore.
  const [reassignStoreDraft, setReassignStoreDraft] = useState(null); // { storeId, fromEmployeeId }
  const [reassignStoreBusy, setReassignStoreBusy] = useState(false);
  // Terminées/Archivées repliées par défaut, même patron que taskSectionsOpen (retour
  // utilisateur explicite, 2026-08-20 : "Refaire l'interface comme le module Tâches").
  const [missionSectionsOpen, setMissionSectionsOpen] = useState({ done: false, archived: false });
  // Calendrier Missions réseau (2026-08-31) — mois affiché (toujours ramené au 1er du mois)
  // + jour sélectionné, indépendant du mois affiché (naviguer un mois ne doit pas perdre la
  // sélection si le jour choisi y est encore visible en tête/fin de grille).
  const [pilotMissionsCalMonth, setPilotMissionsCalMonth] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [pilotMissionsCalSelectedDate, setPilotMissionsCalSelectedDate] = useState(() => todayISO());
  const [missionsCalendarView, setMissionsCalendarView] = useState(false); // liste (défaut) vs calendrier
  // Onglet de premier niveau — Responsables ciblés vs Filiales ciblées (retour utilisateur
  // explicite, 2026-08-24, capture d'écran à l'appui : "mélange des tâches terminées
  // confiées à un GR et confiées... à une filiale" — deux natures de mission totalement
  // différentes qui se retrouvaient mélangées dans le même En cours/Terminées/Archivées).
  // Chaque onglet garde sa PROPRE structure En cours/Terminées/Archivées (missionSectionsOpen
  // reste partagé entre les deux — rouvrir "Terminées" en changeant d'onglet reste attendu,
  // c'est un repli d'affichage, pas un filtre de données).
  const [missionTypeTab, setMissionTypeTab] = useState("filiales"); // "filiales" | "leader" — mêmes valeurs que missionForm.targetMode
  const [missionEmployeeOptions, setMissionEmployeeOptions] = useState([]);
  const [missionThreadDraft, setMissionThreadDraft] = useState("");
  const [missionReplyStoreId, setMissionReplyStoreId] = useState(null);
  // Dialogue d'une mission ciblée sur des responsables (targetLeaderIds, retour utilisateur
  // explicite 2026-08-23 : "on va implémenter le dialogue sur les missions... et fais
  // pareil dans la vue compte normal parce que tu ne l'as jamais implanté" — jamais
  // implémenté pour ce mode, seul le mode filiale l'avait) — missionReplyLeaderId (créateur,
  // même patron que missionReplyStoreId, réutilise missionThreadDraft) et leaderThread* (le
  // responsable ciblé lui-même pose sa question/anomalie sur SA part, même patron que
  // myMissionThread* côté compte normal mais affiché ici dans la fiche GR puisqu'un
  // responsable ciblé est toujours un compte GR).
  const [missionReplyLeaderId, setMissionReplyLeaderId] = useState(null);
  const [leaderThreadDraft, setLeaderThreadDraft] = useState("");
  const [leaderThreadType, setLeaderThreadType] = useState("question");
  const [leaderThreadPhotoFile, setLeaderThreadPhotoFile] = useState(null);
  const [leaderThreadPhotoPreview, setLeaderThreadPhotoPreview] = useState("");
  const [leaderThreadBusy, setLeaderThreadBusy] = useState(false);
  const emptyMissionForm = {
    title: "", filiales: [], priority: "normal", recipientMode: "team", specificEmployeeIds: [],
    destinataires: "Filialleiter", echeanceDate: "", echeanceTime: "", preuveRequise: false, note: "",
    // sourceType/échéance de synthèse (2026-08-31, retour utilisateur + vrai Rundschreiben
    // joint en exemple) — voir leur commentaire complet dans normalizeMission
    // (server/missions/repository.js). "rundschreiben" par défaut (cas majoritaire).
    sourceType: "rundschreiben", echeanceSyntheseDate: "", echeanceSyntheseTime: "",
    // Ciblage direct de responsables (retour utilisateur 2026-08-24, "un NL doit pouvoir
    // assigner des tâches à VKL et BL", puis "on ne peut pas sélectionner plusieurs
    // personnes à la fois") — mode ALTERNATIF au ciblage par filiales, jamais les deux à la
    // fois (voir POST /api/missions, server/missions/routes.js). Plusieurs responsables
    // possibles, même principe que filiales (tableau, jamais un champ singulier).
    targetLeaderIds: [],
    // Onglet actif, INDÉPENDANT de targetLeaderIds.length (retour utilisateur explicite
    // 2026-08-23 : "un destinataire ne doit jamais être sélectionné par défaut") — avant,
    // le mode "responsables" était déduit de targetLeaderIds.length > 0, ce qui forçait à
    // pré-cocher le premier candidat rien que pour garder l'onglet affiché en le
    // sélectionnant. Avec ce champ, l'onglet peut rester actif à 0 sélection.
    targetMode: "filiales",
    // Lien de traçabilité vers le Rundschreiben d'origine (2026-08-31, voir
    // openMissionFromRundschreiben) — jamais utilisé pour dériver quoi que ce soit
    // automatiquement (titre/échéance restent toujours tapés à la main), seulement affiché
    // sur la carte mission pour retrouver le document source.
    relatedRundschreibenId: "", relatedRundschreibenTitle: "",
  };
  // Pont côté compte normal — missions réellement reçues (team ou moi précisément, voir
  // GET /api/missions/mine), jamais construit avant cette demande explicite ("envoie de
  // mission depuis GR, reception de la mission dans Compte normal").
  const [myNetworkMissions, setMyNetworkMissions] = useState([]);
  const [myMissionDetailId, setMyMissionDetailId] = useState(null);
  const [myMissionThreadDraft, setMyMissionThreadDraft] = useState("");
  const [myMissionThreadType, setMyMissionThreadType] = useState("question"); // question | anomaly
  // 3 onglets côté employé (mirroring GR missionTab) — retour utilisateur explicite,
  // 2026-08-02 : "le mieux ce sera de creer 3 section en cours, terminee, archives".
  const [myMissionTab, setMyMissionTab] = useState("en_cours"); // en_cours | terminees | archivees
  // Photo de preuve — retour utilisateur explicite, 2026-08-02 : "quand on coche envoie de
  // photo pour preuve... il n'y a aucun moyen de prendre des photos pour joindre directe en
  // cliquant sur mission accomplie ou pour question et anomalie". Deux emplacements
  // distincts (preuve d'accomplissement vs pièce jointe de dialogue) donc deux jeux d'état.
  const [myMissionProofFile, setMyMissionProofFile] = useState(null);
  const [myMissionProofPreview, setMyMissionProofPreview] = useState("");
  const [myMissionThreadPhotoFile, setMyMissionThreadPhotoFile] = useState(null);
  const [myMissionThreadPhotoPreview, setMyMissionThreadPhotoPreview] = useState("");
  const [myMissionPhotoBusy, setMyMissionPhotoBusy] = useState(false);
  const myMissionProofInputRef = useRef(null);
  const myMissionThreadPhotoInputRef = useRef(null);
  // Lightbox photo — retour utilisateur explicite, 2026-08-02 : "la photo... n'est pas
  // consultable par le GR". window.open(url,"_blank") est un pattern déjà connu pour
  // échouer silencieusement en PWA installée (voir le fix équivalent sur l'export MHD,
  // qui l'avait remplacé par un portail in-app) — remplacé ici par un vrai visualiseur
  // plein écran, jamais de nouvel onglet.
  const [missionPhotoLightbox, setMissionPhotoLightbox] = useState("");
  // Visionneuse Beleg (Inventurmaßnahme) — image OU PDF, jamais window.open (même piège
  // PWA que missionPhotoLightbox juste au-dessus) : un PDF s'affiche dans un iframe
  // in-app, avec un vrai lien de téléchargement à côté (l'attribut download déclenche le
  // téléchargement natif, jamais bloqué comme une nouvelle fenêtre).
  const [imBelegViewer, setImBelegViewer] = useState(null); // { url, mimeType } | null
  const openImBeleg = (url, mimeType) => { if (url) setImBelegViewer({ url, mimeType: mimeType || "" }); };
  const [missionForm, setMissionForm] = useState(emptyMissionForm);
  // Alertes — panneau d'action réel par alerte du feed /api/manager/overview (assigner,
  // transformer en mission, clôturer). Le feed lui-même reste recalculé à chaque GET
  // (jamais persisté), MAIS "résolu"/"attribué à" sont maintenant de vrais champs
  // renvoyés par le serveur (network_alert_states, retour utilisateur explicite,
  // 2026-08-02 : l'ancien état purement client disparaissait à chaque rechargement et
  // n'était jamais visible d'un autre appareil) — f.resolved/f.assignedToName directement
  // sur chaque item du feed, plus de registre local.
  const [alertActionId, setAlertActionId] = useState(null);

  /* Plus / Réglages — Web Push réel (service worker + VAPID, voir lib/push.js). pushEnabled
     reflète le VRAI abonnement serveur (GET /api/push/status), jamais un simple booléen
     local — un abonnement peut échouer silencieusement (clé VAPID, réseau, permission
     refusée) sans que ça ait de rapport avec un état local optimiste. */
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  // Bannière popup en haut de l'écran à l'arrivée d'une notification (distincte de la cloche/
  // du centre de notifications, qui restent inchangés) — retour utilisateur explicite : utile
  // parfois, envahissante la plupart du temps. Réglage local, désactivé par défaut plutôt que
  // supprimé du code : réversible en un clic sans redemander à reconstruire le mécanisme.
  const [bannersEnabled, setBannersEnabled] = useState(() => {
    try { return localStorage.getItem("nexora_banners_enabled") === "true"; } catch { return false; }
  });
  const toggleBanners = () => {
    setBannersEnabled((prev) => {
      const next = !prev;
      try { localStorage.setItem("nexora_banners_enabled", String(next)); } catch {}
      return next;
    });
  };
  const [pwForm, setPwForm] = useState({ old: "", new1: "", new2: "" });
  const [pwMessage, setPwMessage] = useState("");
  const [krankRetentionDays, setKrankRetentionDays] = useState(730);
  const [krankRetentionSaved, setKrankRetentionSaved] = useState(false);

  /* Centre de notifications — vraies données (Phase 1, module Accueil+Notifications).
     Chargées/mutées via /api/notifications* (StoreFlowProV2) : ces routes lisent
     l'identité depuis des query-params (jamais la session cookie), voir identityQuery(). */
  const [notifications, setNotifications] = useState([]);
  const [liveActions, setLiveActions] = useState([]); // remplace DEMO_LIVE — vraies actions récentes (GET /api/actions)
  const [notifTab, setNotifTab] = useState("unread"); // unread|urgent|all
  const [dismissedUrgent, setDismissedUrgent] = useState([]); // labels dismissés "pour aujourd'hui"



  /* Artikelfinder — vraies données (server/articleFinder/, déjà extrait). Catégories
     réelles (gérées par un admin, pas une liste figée) et fiches réelles (statuts
     draft/pending_review/validated/price_review_required/rejected/archived) —
     remplace DEMO_FINDER_ITEMS/FINDER_CATEGORIES. */
  const [finderItems, setFinderItems] = useState([]);
  const [finderCategories, setFinderCategories] = useState([]); // [{id,label,archived}]
  const [finderAccess, setFinderAccess] = useState({ hasValidateTier: false, hasManageTier: false });
  const [finderAdminItems, setFinderAdminItems] = useState([]);
  const [finderScreen, setFinderScreen] = useState("hub"); // hub|results|detail|admin|create
  const [finderQuery, setFinderQuery] = useState("");
  const [finderCategory, setFinderCategory] = useState("all");
  const [finderResults, setFinderResults] = useState([]);
  const [finderPhotoScan, setFinderPhotoScan] = useState(false);
  const [finderDetailId, setFinderDetailId] = useState(null);
  const [finderDetailBackScreen, setFinderDetailBackScreen] = useState("results");
  // Création de fiche (retour utilisateur 2026-08-04 : "termine complètement le module") —
  // le serveur exigeait déjà désignation+catégorie+prix+photo pour valider (POST /items,
  // POST /items/:id/photos, voir server/articleFinder/routes.js) mais nexora-app n'avait
  // aucun écran pour ça, donc toute fiche restait bloquée en "draft" pour toujours.
  const [finderCreateForm, setFinderCreateForm] = useState({ designation: "", category: "", price: "", ean: "", articleNumber: "" });
  // Plusieurs photos possibles dès la création (retour utilisateur explicite : "je veux
  // pouvoir ajouter plusieurs photos si je veux a un Nouvel article directement dans la
  // creation") — [{file, preview}], jusqu'à MAX_IMAGES_PER_ITEM (5, même limite serveur).
  const [finderCreatePhotos, setFinderCreatePhotos] = useState([]);
  const [finderCreateSaving, setFinderCreateSaving] = useState(false);
  const finderCreatePhotoInputRef = useRef(null);
  // Visualiseur photo plein écran (Artikelfinder) — retour utilisateur explicite : "je veux
  // pouvoir agrandir les photos d'un article ajouter et les consulter toutes si plusieurs".
  // { urls: [...], index } ; distinct de missionPhotoLightbox (celui-ci gère une vraie
  // galerie avec navigation ‹ › entre plusieurs photos, l'autre est mono-image).
  const [finderPhotoViewer, setFinderPhotoViewer] = useState(null);
  // Reconnaissance photo réelle — moteur CLIP auto-hébergé déjà présent côté serveur
  // (POST /api/article-finder/recognize, server/articleFinder/recognitionService.js) mais
  // jamais appelé : runPhotoRecognition simulait avec un setTimeout + shuffleArr. Même
  // patron d'input caméra que le Centre documentaire (capture="environment").
  const finderRecognizeInputRef = useRef(null);
  // Catalogue — retour utilisateur 2026-08-04 : "une fois l'article validé... il disparaît,
  // on devrait avoir une section où on peut les trouver et les supprimer ou les actualiser".
  // GET /api/article-finder/items existait déjà côté serveur (retourne les fiches
  // validated/price_review_required pour un employé normal, toutes pour un responsable —
  // voir sfArticleFinderCanSeeItem) mais jamais appelé côté client.
  const [finderCatalog, setFinderCatalog] = useState([]);
  const [finderCatalogLoading, setFinderCatalogLoading] = useState(false);
  const [finderEditingPrice, setFinderEditingPrice] = useState(false);
  const [finderPriceDraft, setFinderPriceDraft] = useState("");
  const finderAddPhotoInputRef = useRef(null);

  /* Lieferschein — vraies données (Phase 1). trashed(local)↔deleted(réel). */
  const [lieferscheine, setLieferscheine] = useState([]);
  const [lfScreen, setLfScreen] = useState("hub"); // hub|import|preview|detail
  const [lfStatusTarget, setLfStatusTarget] = useState(null); // { noteId, itemId }
  const [lfTab, setLfTab] = useState("active"); // active|archived|trash
  const [lfImportMethod, setLfImportMethod] = useState(null); // pdf|photos|manual
  const [lfAnalyzing, setLfAnalyzing] = useState(false);
  const emptyLfPreview = { supplier: "", deliveryDate: "", deliveryNumber: "", items: [] };
  const [lfPreview, setLfPreview] = useState(emptyLfPreview);
  const [lfDetailId, setLfDetailId] = useState(null);
  const lfFileInputRef = useRef(null);

  /* Import PDF MHD (Contrôle Kühlung) — vision Claude uniquement, patron Lieferschein
     (upload -> preview éditable -> confirmation), voir server/mhdImport/routes.js. */
  const [mhdImportStep, setMhdImportStep] = useState("upload"); // upload|preview
  const [mhdImportAnalyzing, setMhdImportAnalyzing] = useState(false);
  const [mhdImportCommitting, setMhdImportCommitting] = useState(false);
  const emptyMhdImportPreview = { rows: [], excluded: [], skippedPast: [], skippedExisting: [], newArticles: [], veUpdates: [], veUpdatesChecked: {}, fileName: "" };
  const [mhdImportPreview, setMhdImportPreview] = useState(emptyMhdImportPreview);
  const mhdImportFileInputRef = useRef(null);

  /* Import catalogue Articles (démarrage d'une nouvelle filiale) — même patron que l'import
     PDF MHD ci-dessus, voir server/articlesImport/routes.js. */
  const [artImportStep, setArtImportStep] = useState("upload"); // upload|preview
  const [artImportAnalyzing, setArtImportAnalyzing] = useState(false);
  const [artImportCommitting, setArtImportCommitting] = useState(false);
  const emptyArtImportPreview = { rows: [], skippedExisting: [], fileName: "" };
  const [artImportPreview, setArtImportPreview] = useState(emptyArtImportPreview);
  const artImportFileInputRef = useRef(null);





  /* Resturlaub (jours) / Leistungsfrei (heures) — vrais comptes serveur, voir
     GET/POST /api/vacation/* et /api/leistungsfrei/*. Resturlaub est réservé FL/FLA côté
     serveur (même l'employé concerné n'y a pas accès) ; Leistungsfrei s'auto-filtre côté
     serveur sur le compte de l'employé courant pour un non-manager. */
  const canManageVacation = usePermission(user, "planning.vacation.manage");
  const canManageLf = usePermission(user, "planning.leistungsfrei.manage");
  const canManageAushilfeAvailability = usePermission(user, "planning.aushilfe.manage");
  const canViewUsers = usePermission(user, "users.view");
  const canManageRoles = usePermission(user, "roles.edit");
  // Retour utilisateur explicite (2026-08-12) : le bouton "Administration" de PLU Quiz
  // restait visible pour tout compte FL/FLA/Bereichsleiter/etc. (isManager(user), un
  // contrôle de RÔLE) même quand on lui retire explicitement plu-quiz.manage depuis
  // Rôles & permissions (un contrôle de PERMISSION) — il voyait le bouton puis se
  // prenait un refus serveur en cliquant dessus. isManager() reste utilisé ailleurs
  // (contexte général manager, pas spécifique à PLU Quiz) — seul ce bouton précis
  // doit suivre la vraie permission.
  const canManagePluQuiz = usePermission(user, "plu-quiz.manage");
  const isAushilfe = String(user?.role || "").trim() === "Aushilfe";
  // NEXORA_ADMIN a un bypass total côté serveur (isNexoraAdminRole, server.js) — role_permissions
  // ne le liste pas forcément explicitement, donc usePermission seul y renverrait false (même
  // piège déjà trouvé sur canPublishPlanning, voir storeflow_planning_nexora_admin_publish_gate).
  // user?.isSuperAdmin (2026-08-17, bug réel trouvé : retour utilisateur "je ne vois rien" sur
  // la nouvelle gestion des règles J-X) — le login renvoie déjà ce champ (safeServerUser +
  // isSuperAdminServer), et le serveur accepte systématiquement ce bypass en OR avec
  // NEXORA_ADMIN sur toutes les routes admin (`isSuperAdminServer(req.user) ||
  // identity.role === "NEXORA_ADMIN"`, partout dans server.js) — mais ce drapeau côté client
  // ne le reconnaissait jamais, cachant les sections admin à un compte super-admin dont le
  // rôle affiché est autre chose (ex. FLA) tout en acceptant ses appels API.
  const isNexoraAdmin = String(user?.role || "").trim() === "NEXORA_ADMIN" || user?.isSuperAdmin === true;
  // Compte réseau/GR (retour utilisateur explicite, 2026-08-28 : "les tâches filiales n'ont
  // rien à voir avec l'interface GR... le popup ne doit jamais être existant sur l'interface
  // GR") — même liste de rôles déjà établie ailleurs (NETWORK_LEADER_ROLES, doWebauthnLogin)
  // pour distinguer un compte de pilotage réseau d'un compte de filiale. Réutilisée pour
  // couper l'auto-ouverture du popup "Tâches à faire" (scope store) sur ces comptes — voir
  // l'effet juste après actionableTasks.
  // Bug réel trouvé le 2026-08-29 (retour utilisateur : "le popup ne s'affiche plus sur un
  // compte normal", vérifié en direct avec de vrais identifiants) : le `|| isSuperAdmin`
  // excluait aussi les comptes admin/propriétaire qui opèrent au quotidien comme un simple
  // compte filiale (ex. rôle FLA avec isSuperAdmin activé pour d'autres raisons) — hors de
  // toute intention initiale ("jamais sur l'interface GR", jamais "jamais sur un compte
  // admin"). Uniquement basé sur le rôle réseau désormais, seul signal qui correspond
  // réellement à "vue GR/pilotage réseau" — usage unique de cette const, voir l'effet juste
  // après actionableTasks, donc aucun autre endroit à vérifier pour ce changement.
  const isNetworkLeaderAccount = ["Bereichsleiter", "Verkaufsleiter", "Niederlassungsleiter", "NEXORA_ADMIN"].includes(String(user?.role || "").trim());
  // Module Administration "gelé" (2026-08-13, retour utilisateur explicite) — retiré de
  // tous les rôles configurables dans role_permissions (donnée réelle corrigée en même
  // temps que ce fichier), sauf pour ce compte précis (voir la même règle dans
  // lib/permissions.js, ADMIN_OVERRIDE_EMPLOYEE_IDS — gardé synchronisé ici volontairement,
  // hasModuleAccess ne passe pas par usePermission, voir commentaire juste en dessous).
  const isAdminModuleOverride = String(user?.employeeId || "") === "17002";
  // Contrôle d'accès module — voir MODULE_ACCESS_PERMS. rolePerms est déjà chargé en
  // synchrone (loadRolePermissions, chargement global post-connexion), donc pas besoin de
  // repasser par le hook usePermission asynchrone de lib/permissions.js pour ce garde.
  const hasModuleAccess = (moduleId) => {
    const groupId = MODULE_TO_PERMISSION_GROUP[moduleId];
    if (!groupId) return true;
    if (isNexoraAdmin) return true;
    if (moduleId === "admin" && isAdminModuleOverride) return true;
    // Tant que le premier chargement de rolePerms n'est pas terminé (voir
    // loadRolePermissions/rolePermsLoaded), ne jamais refuser à tort — le vrai rempart de
    // sécurité reste côté serveur sur chaque route /api/*, ce contrôle-ci n'est qu'un
    // confort d'affichage (quel module proposer).
    if (!rolePermsLoaded) return true;
    const group = PERMISSION_GROUPS.find((g) => g.id === groupId);
    if (!group) return true;
    const mine = rolePerms[user?.role] || [];
    return group.permissions.some((p) => mine.includes(p.id));
  };
  // Modules cachés PAR UTILISATEUR (2026-08-13) — distinct de hasModuleAccess ci-dessus
  // (gating par rôle, laisse la tuile visible et bloque au clic). Ici la tuile disparaît
  // vraiment de la grille (voir HOME_MODULES.filter/NETWORK_HOME_MODULES.filter plus bas) —
  // réglable uniquement par un vrai NEXORA_ADMIN, voir l'onglet "Par utilisateur" de
  // l'écran Rôles & permissions (PATCH /api/users/:id/hidden-modules côté serveur, gate
  // strict indépendant de "roles.edit"/"users.edit").
  const isModuleHiddenForUser = (moduleId) => (user?.hiddenModules || []).includes(moduleId);
  const [deniedModule, setDeniedModule] = useState(null);
  useEffect(() => {
    if (screen !== "accessDenied") return;
    const timer = setTimeout(() => { setScreen("home"); setDeniedModule(null); }, 2500);
    return () => clearTimeout(timer);
  }, [screen]);
  const canManageAzl = usePermission(user, "arbeitszeitliste.manage") || isNexoraAdmin;
  const canValidateAzl = usePermission(user, "arbeitszeitliste.validate") || isNexoraAdmin;
  const canImportMhdPdf = usePermission(user, "control.mhdImport") || isNexoraAdmin;
  const canImportArticlesCatalog = usePermission(user, "control.articlesImport") || isNexoraAdmin;
  // Même piège pour Articles (bug réel trouvé après coup, retour utilisateur 2026-08-02 :
  // "tu n'as pas ajoutee toutes les fonctionnalites... deplacer, suprimer, ajouter" — le
  // vrai compte NEXORA_ADMIN ne voyait AUCUN de ces boutons sans ce bypass).
  const canViewArticles = usePermission(user, "articles.view") || isNexoraAdmin;
  const canCreateArticles = usePermission(user, "articles.create") || isNexoraAdmin;
  const canEditArticles = usePermission(user, "articles.edit") || isNexoraAdmin;
  const canDeleteArticles = usePermission(user, "articles.delete") || isNexoraAdmin;
  const canManageMsgGroup = usePermission(user, "messages.group.manage") || isNexoraAdmin;
  const canManageMsgMembers = usePermission(user, "messages.members.manage") || isNexoraAdmin;
  // Commandes de marchandises (nouveau module, 2026-08-07) — même bypass NEXORA_ADMIN que
  // partout ailleurs (piège déjà frappé 3x, voir storeflow_usepermission_needs_nexora_admin_bypass).
  const canPrepareMerch = usePermission(user, "merchandiseOrders.prepare") || isNexoraAdmin;
  const canModifyMerch = usePermission(user, "merchandiseOrders.modify") || isNexoraAdmin;
  const canValidateMerch = usePermission(user, "merchandiseOrders.validate") || isNexoraAdmin;
  const canReorderMerch = usePermission(user, "merchandiseOrders.reorder") || isNexoraAdmin;
  const canManageMerchCatalog = usePermission(user, "merchandiseOrders.manageCatalog") || isNexoraAdmin;
  // Fabrication d'étiquettes (nouveau module) — même bypass NEXORA_ADMIN systématique.
  const canViewLabels = usePermission(user, "labels.view") || isNexoraAdmin;
  const canManageLabels = usePermission(user, "labels.manage") || isNexoraAdmin;
  const canViewKassenpost = usePermission(user, "kassenpost.view") || isNexoraAdmin;
  const canManageKassenpost = usePermission(user, "kassenpost.manage") || isNexoraAdmin;
  const canViewInventur = usePermission(user, "inventur.view") || isNexoraAdmin;
  const canCreateInventur = usePermission(user, "inventur.create") || isNexoraAdmin;
  const canEditInventur = usePermission(user, "inventur.edit") || isNexoraAdmin;
  const canManageInventurCategories = usePermission(user, "inventur.category.manage") || isNexoraAdmin;
  const canAssignInventurTask = usePermission(user, "inventur.task.assign") || isNexoraAdmin;
  const canCreateInventurEntry = usePermission(user, "inventur.entry.create") || isNexoraAdmin;
  const canEditInventurEntry = usePermission(user, "inventur.entry.edit") || isNexoraAdmin;
  const canFinishInventurCategory = usePermission(user, "inventur.category.finish") || isNexoraAdmin;
  const canVerifyInventurCategory = usePermission(user, "inventur.category.verify") || isNexoraAdmin;
  const canReopenInventurCategory = usePermission(user, "inventur.category.reopen") || isNexoraAdmin;
  const canLockInventurCategory = usePermission(user, "inventur.category.lock") || isNexoraAdmin;
  const canCloseInventur = usePermission(user, "inventur.close") || isNexoraAdmin;
  const canExportInventur = usePermission(user, "inventur.export") || isNexoraAdmin;
  const canCreateKontrolle = usePermission(user, "inventur.kontrolle.create") || isNexoraAdmin;
  const canEditKontrolle = usePermission(user, "inventur.kontrolle.edit") || isNexoraAdmin;
  const canCloseKontrolle = usePermission(user, "inventur.kontrolle.close") || isNexoraAdmin;
  // Réservé FL/FLA (retour utilisateur 2026-08-09) — délibérément PAS le reste de
  // MANAGEMENT_ROLES (Bereichsleiter etc.), c'est une décision locale à la filiale.
  const canManageKontrolleVisibility = ["FL", "FLA"].includes(String(user?.role || "").trim()) || isNexoraAdmin;
  const kontrolleVisibleForMe = !(kontrolleVisibility?.hiddenEmployeeIds || []).includes(String(user?.proId || user?.id || ""));
  const canViewInventurHistory = usePermission(user, "inventur.history.view") || isNexoraAdmin;
  const [vacationAccounts, setVacationAccounts] = useState([]);
  const [vacationTransactions, setVacationTransactions] = useState([]);
  const [lfAccounts, setLfAccounts] = useState([]);
  const [lfTransactions, setLfTransactions] = useState([]);
  const [allocForm, setAllocForm] = useState(null);      // { employeeId, emp, amount, mode: "credit"|"restore" }
  const [leiForm, setLeiForm] = useState(null);          // { employeeId, emp, type: "credit"|"debit", amount, note }
  const [historyEmpId, setHistoryEmpId] = useState("");
  const [historyEmpName, setHistoryEmpName] = useState("");
  const [historyKind, setHistoryKind] = useState("urlaub"); // urlaub | leistungsfrei

  /* Feiertage par État */
  // Vrais jours fériés — GET /api/public-holidays?storeId= (Land de la filiale résolu
  // côté serveur, jamais choisi ici). Entrées avec source:"auto" = générées par le
  // serveur (non supprimables sans droit planning.holidays.manage) ; sans source = jour
  // personnalisé ajouté via POST (voir saveCustomHoliday/deleteCustomHoliday).
  const [publicHolidays, setPublicHolidays] = useState([]);
  // Heures d'ouverture/fermeture de la filiale de l'utilisateur (2026-08-31) — voir
  // loadMyStoreHours ci-dessous. { openTime: "", closeTime: "" } = non configurées.
  const [myStoreHours, setMyStoreHours] = useState({ openTime: "", closeTime: "" });
  const [holidayForm, setHolidayForm] = useState({ date: "", name: "" });

  /* Discussion liée à une demande de planning (pas le module Messages) — les messages
     vivent directement sur la demande (req.messages, réel), plus de state local séparé. */
  const [activeReqId, setActiveReqId] = useState(null);
  const [reqMsgDraft, setReqMsgDraft] = useState("");
  // Contre-proposition de date (retour utilisateur 2026-08-09) — null = formulaire fermé,
  // { date, endDate } = formulaire ouvert en cours de saisie.
  const [reqProposeForm, setReqProposeForm] = useState(null);

  const [toast, setToast] = useState("");
  const toastT = useRef();
  const showToast = (m) => { setToast(m); clearTimeout(toastT.current); toastT.current = setTimeout(() => setToast(""), 2400); };

  /* Vraies notifications façon iPhone — une bannière glisse depuis le
     haut de l'écran à intervalle régulier (simulateur de "nouvelle
     notification qui arrive"), au-dessus de tout le reste. Une seule
     à la fois, comme sur un vrai téléphone. La file part des notifications
     non lues existantes ; chacune n'apparaît qu'une fois — VRAIMENT une
     fois, jamais plus, pas seulement "une fois par montage du composant".
     BUG RÉEL trouvé (retour utilisateur : "je reçois toujours toujours des
     notifications fictives") : "une fois par session" était en réalité "une
     fois par montage de l'app" — notifSeenIdsRef repart à null à CHAQUE
     rechargement de page / relance de la PWA, donc TOUT le stock de
     notifications non lues (accumulées sur des jours, jamais explicitement
     lues) repassait en bannière à chaque réouverture de l'app. Ce n'étaient
     pas de fausses notifications, mais de VRAIES notifications, déjà vues,
     qui revenaient sans cesse — d'où l'impression de notifications
     "fictives". Fix : un registre persistant (localStorage, par compte) des
     ids déjà montrés EN BANNIÈRE, qui survit au rechargement — seules les
     notifications jamais encore montrées en bannière (peu importe l'ancien
     "notifSeenIdsRef" qui ne vit qu'en mémoire) entrent dans la file. */
  const bannerShownStorageKey = (u) => `nx_banner_shown_${u?.proId || u?.id || "anon"}`;
  const loadBannerShownIds = (u) => {
    try { return new Set(JSON.parse(localStorage.getItem(bannerShownStorageKey(u)) || "[]")); }
    catch { return new Set(); }
  };
  const persistBannerShownIds = (u, idsSet) => {
    try {
      // Borné à 300 ids les plus récents pour ne jamais grossir indéfiniment.
      const arr = [...idsSet].slice(-300);
      localStorage.setItem(bannerShownStorageKey(u), JSON.stringify(arr));
    } catch {}
  };
  const bannerShownIdsRef = useRef(null);
  const [activeBanner, setActiveBanner] = useState(null);
  const [bannerLeaving, setBannerLeaving] = useState(false);
  const notificationsRef = useRef(notifications);
  useEffect(() => { notificationsRef.current = notifications; }, [notifications]);
  const bannerQueueRef = useRef(null);
  // IDs déjà vus au moins une fois (bannière montrée ou pas) — sans ça, chaque poll de
  // loadNotifications() re-proposerait indéfiniment les mêmes anciennes notifications non
  // lues en bannière plutôt que seulement les VRAIMENT nouvelles arrivées depuis.
  const notifSeenIdsRef = useRef(null);
  const bannerLeaveTimer = useRef(null);
  const dismissBanner = () => {
    clearTimeout(bannerLeaveTimer.current);
    setBannerLeaving(true);
    bannerLeaveTimer.current = setTimeout(() => { setActiveBanner(null); setBannerLeaving(false); }, 320);
  };
  useEffect(() => {
    if (!user || !bannersEnabled) return;
    // La file (bannerQueueRef) est alimentée par loadNotifications() elle-même (au premier
    // chargement ET à chaque poll suivant, voir son diff par rapport à notifSeenIdsRef) —
    // cet effet ne fait plus que consommer la file à intervalle régulier, jamais l'amorcer
    // lui-même (l'amorcer ici tournait avant la résolution du premier fetch async, donc
    // toujours sur un tableau encore vide — jamais corrigé depuis, aucune bannière ne
    // pouvait plus jamais apparaître de toute la session).
    const tick = () => {
      if (!bannerQueueRef.current || !bannerQueueRef.current.length) return;
      const nextId = bannerQueueRef.current.shift();
      const n = notificationsRef.current.find((x) => x.id === nextId);
      if (!n || n.read) return;
      setBannerLeaving(false);
      setActiveBanner(n);
      // Marquée "montrée" au moment RÉEL de l'affichage (pas seulement mise en file) —
      // persisté tout de suite pour qu'un rechargement pendant que la bannière est visible
      // ne la refasse pas repartir depuis la file.
      if (!bannerShownIdsRef.current) bannerShownIdsRef.current = loadBannerShownIds(user);
      bannerShownIdsRef.current.add(n.id);
      persistBannerShownIds(user, bannerShownIdsRef.current);
    };
    const first = setTimeout(tick, 3500);
    const loop = setInterval(tick, 15000);
    return () => { clearTimeout(first); clearInterval(loop); };
  }, [user, bannersEnabled]);
  /* Auto-fermeture liée directement au cycle de vie React de la bannière
     affichée (plutôt qu'à un minuteur géré à la main dans l'effet du
     dessus) — se nettoie et se relance correctement à chaque nouvelle
     bannière, quelle que soit la navigation entre écrans pendant ce temps. */
  useEffect(() => {
    if (!activeBanner) return;
    const t = setTimeout(() => dismissBanner(), 5000);
    return () => clearTimeout(t);
  }, [activeBanner]);
  const openBanner = () => {
    if (!activeBanner) return;
    const n = activeBanner;
    dismissBanner();
    openNotification(n);
  };

  const [runIndex, setRunIndex] = useState(0);
  const [touched, setTouched] = useState(() => new Set());
  const [finish, setFinish] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ mhd: "", qty: "", note: "" });
  const [search, setSearch] = useState("");
  // "Voir plus" plutôt qu'un plafond fixe (même retour utilisateur que Scanner ci-dessus) —
  // remis à 1 à chaque ouverture de la sheet "Liste Kühlung".
  const [kuhlungListBatches, setKuhlungListBatches] = useState(1);
  useEffect(() => { if (sheet === "list") setKuhlungListBatches(1); }, [sheet]);
  const [scanning, setScanning] = useState(false);

  const [saveFlash, setSaveFlash] = useState(false);

  const article = articles[runIndex];
  const artRecords = records.filter((r) => r.artNr === article?.artNr);
  const checkedCount = touched.size;
  /* La barre avance à mesure qu'on parcourt les articles (position), et
     jamais en arrière si des articles plus loin ont déjà été cochés. */
  const pct = Math.round((Math.max(runIndex + 1, checkedCount) / articles.length) * 100);

  useEffect(() => { document.body.style.background = P.bg; }, [P.bg]);

  /* Détection du clavier virtuel — corrige le bug où la tab bar reste
     visible (souvent coincée au-dessus ou à travers le clavier) dès
     qu'on tape dans un champ de texte. window.visualViewport rétrécit
     nettement quand le clavier s'ouvre sur mobile ; on s'en sert pour
     masquer la tab bar tant qu'on saisit du texte, comme un vrai clavier
     de chat qui prend toute la place disponible. */
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const mainScrollRef = useRef(null);
  // En-tête + nav du bas "shy" façon app native (retour utilisateur explicite, 2026-08-16 :
  // rétrécit au défilement vers le bas, reprend sa forme normale en remontant — voulu
  // CONTINU/proportionnel à la position de scroll, pas un binaire qui "claque" entre 2 états,
  // jugé "trop mécanique" dans un essai précédent). Piloté en dehors de React (mutation DOM
  // directe d'une custom property CSS depuis la boucle rAF plus bas, voir mainAppRef) plutôt
  // qu'un state React — sur un composant de cette taille, un setState à chaque frame pendant
  // tout un geste de scroll aurait forcé un re-render complet 60x/s, potentiellement plus
  // saccadé que l'ancien binaire, pas plus fluide.
  // Bug réel signalé (2026-08-06) après DEUX palliatifs ratés basés sur position:fixed +
  // calcul JS de la hauteur du clavier ("la barre est haute et peut bouger vers le haut
  // comme le bas" — jitter, probablement les deux effets keyboardOpen/scroll-lock qui se
  // relançaient l'un l'autre). Abandon complet de cette approche au profit du comportement
  // NATIF (celui de WhatsApp/toute vraie app de chat) : sur l'écran Messages > fil, ne plus
  // figer --nx-vh pendant la frappe (voir maybeSetNxVh plus bas, exception via cette ref) —
  // la colonne flex de l'app rétrécit alors réellement à la vraie zone visible quand le
  // clavier s'ouvre, et le composer (converti de position:fixed à un simple élément flex
  // normal juste après <main>, voir plus bas) se retrouve NATURELLEMENT juste au-dessus du
  // clavier, sans aucun calcul JS de position à maintenir.
  const messagesThreadActiveRef = useRef(false);
  useEffect(() => {
    messagesThreadActiveRef.current = screen === "messages" && msgScreen === "thread";
  }, [screen, msgScreen]);
  useEffect(() => {
    if (!window.visualViewport) return;
    const vv = window.visualViewport;
    // Bug réel trouvé et reproduit (retour utilisateur 2026-08-06 : "la navigation...
    // est toujours présente même quand le clavier est ouvert") : l'ancienne comparaison
    // `window.innerHeight - vv.height` lisait innerHeight EN DIRECT — sur certains
    // Android/WebView, ouvrir le clavier redimensionne innerHeight ET vv.height ENSEMBLE
    // (contrairement à iOS Safari classique où seul vv.height rétrécit), donc l'écart
    // restait proche de 0 et keyboardOpen ne passait jamais à true sur ces appareils.
    // Fige maintenant une référence "au repos" AVANT toute ouverture de clavier, jamais
    // mise à jour tant qu'un champ de saisie a le focus, comparée à vv.height EN DIRECT —
    // fiable quelle que soit la plateforme, puisqu'on ne dépend plus de innerHeight du tout.
    const isTextEntryFocused = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      if (tag === "TEXTAREA" || tag === "SELECT") return true;
      if (tag === "INPUT") {
        const type = (el.getAttribute("type") || "text").toLowerCase();
        return !["checkbox", "radio", "range", "file", "button", "submit", "color"].includes(type);
      }
      return false;
    };
    let restingHeight = vv.height;
    const evaluate = () => setKeyboardOpen(restingHeight - vv.height > 120);
    const onResize = () => {
      if (!isTextEntryFocused()) restingHeight = vv.height;
      evaluate();
    };
    // Filet de sécurité indépendant du resize : sur le WebView Android déjà identifié
    // comme pouvant ne pas redéclencher visualViewport.resize proprement à la fermeture
    // du clavier (voir commentaire plus bas, "TAB BAR iOS PREMIUM"), re-baseline et
    // réévalue après la perte de focus d'un champ, quoi qu'il arrive.
    const onFocusOut = () => setTimeout(() => { restingHeight = vv.height; evaluate(); }, 250);
    vv.addEventListener("resize", onResize);
    document.addEventListener("focusout", onFocusOut, true);
    onResize();
    return () => {
      vv.removeEventListener("resize", onResize);
      document.removeEventListener("focusout", onFocusOut, true);
    };
  }, []);

  // Bug réel signalé (2026-08-06), toujours présent après un premier palliatif ciblé sur
  // <main> ("la page remonte complètement vers le haut" persiste sur téléphone réel malgré
  // la restauration de main.scrollTop) : la vraie cible du saut est très probablement
  // window/document, pas <main> — html/body sont verrouillés en overflow:hidden partout
  // dans l'app (voir plus bas, "html, body { height:100%; overflow:hidden }"), donc
  // window.scrollY doit TOUJOURS rester à 0 quel que soit l'écran. Certains navigateurs
  // mobiles (Safari iOS notamment) sont connus pour scroller quand même le document au
  // focus d'un champ position:fixed sans ancêtre scrollable (le composer Messages, hors de
  // <main>, voir plus bas), en ignorant overflow:hidden — un bug WebKit documenté, pas une
  // conséquence du CSS de cette app. Filet de sécurité actif seulement sur le fil Messages
  // (là où le composer position:fixed existe) : ramène immédiatement tout scroll
  // window/document parasite à 0, sans jamais toucher au scroll interne légitime de <main>.
  useEffect(() => {
    if (!(screen === "messages" && msgScreen === "thread")) return;
    const resetDocScroll = () => {
      if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo(0, 0);
      if (document.documentElement.scrollTop !== 0) document.documentElement.scrollTop = 0;
      if (document.body.scrollTop !== 0) document.body.scrollTop = 0;
    };
    window.addEventListener("scroll", resetDocScroll, true);
    document.addEventListener("scroll", resetDocScroll, true);
    resetDocScroll();
    return () => {
      window.removeEventListener("scroll", resetDocScroll, true);
      document.removeEventListener("scroll", resetDocScroll, true);
    };
  }, [screen, msgScreen]);

  /* isWide vient en prop du wrapper NexoraApp (voir plus haut) — isWideRef en plus :
     le handler du geste de swipe-back (effect à dépendance [user] plus bas) doit lire
     la valeur COURANTE sans que cet effect dépende de isWide et se réabonne à chaque
     resize. */
  const isWideRef = useRef(isWide);
  useEffect(() => { isWideRef.current = isWide; }, [isWide]);

  /* Hauteur réellement fixe de la page — même patron que --sf-vh côté StoreFlowProV2
     (confirmé sur appareil réel là-bas) : 100vh/minHeight:100vh ne suit pas la vraie
     zone visible sur mobile (barre d'adresse qui apparaît/disparaît), ce qui peut rendre
     TOUTE la page défilable et emporter la nav position:fixed avec elle. --nx-vh est
     calculée en JS à partir de la zone RÉELLEMENT visible et gelée tant qu'un champ de
     saisie a le focus (le clavier ne doit pas rétrécir tout le contenu, juste couvrir
     son propre espace en bas). */
  useEffect(() => {
    function setNxVh() {
      const h = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      document.documentElement.style.setProperty("--nx-vh", (h * 0.01) + "px");
    }
    function isTextEntryFocused() {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      if (tag === "TEXTAREA" || tag === "SELECT") return true;
      if (tag === "INPUT") {
        const type = (el.getAttribute("type") || "text").toLowerCase();
        return !["checkbox", "radio", "range", "file", "button", "submit", "color"].includes(type);
      }
      return false;
    }
    function maybeSetNxVh() {
      // Exception Messages > fil (voir messagesThreadActiveRef plus haut) : sur cet écran,
      // --nx-vh doit au contraire SUIVRE le clavier en direct (comportement natif façon
      // WhatsApp — voir le commentaire complet à la déclaration de messagesThreadActiveRef),
      // pas rester gelée comme sur les autres écrans avec formulaire.
      //
      // Le gel pendant la frappe ne doit bloquer QU'un RÉTRÉCISSEMENT (ouverture clavier) —
      // jamais un agrandissement (retour à la normale). Bug réel signalé avec capture,
      // 2026-08-30 : "écran qui se divise... quand je reçois une notification ou quand je
      // rabats l'écran pour consulter un message sur écran verrouillé" — une bannière de
      // notification ou le Centre de notifications tiré depuis l'écran verrouillé
      // rétrécit puis ragrandit visualViewport SANS jamais déclencher visibilitychange/
      // focus/pageshow (l'app ne passe jamais vraiment en arrière-plan), donc seuls ces
      // écouteurs resize/visualViewport.resize peuvent recapturer la vraie hauteur au
      // retour. Si `document.activeElement` pointe encore sur un champ (focus resté actif
      // pendant tout l'aller-retour), l'ancien code ignorait CE resize de retour aussi,
      // laissant --nx-vh figée sur la valeur rétrécie captée pendant la bannière — d'où le
      // vide en bas de l'écran observé. Un agrandissement ne peut jamais correspondre à
      // une ouverture de clavier (qui rétrécit), donc le laisser passer ne réintroduit
      // aucun cas déjà géré.
      const h = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const current = parseFloat(document.documentElement.style.getPropertyValue("--nx-vh")) * 100 || 0;
      if (isTextEntryFocused() && !messagesThreadActiveRef.current && h <= current + 1) return;
      setNxVh();
    }
    // Reprise après mise en arrière-plan (retour utilisateur explicite, 2026-08-21, captures
    // d'écran : "la moitié de l'écran disparaît... il faut fermer l'app et rouvrir") — aucun
    // des écouteurs ci-dessus (resize/orientationchange/visualViewport.resize/focusout) ne se
    // redéclenche de façon fiable quand iOS suspend puis relance l'app (changement d'app,
    // verrouillage écran) : --nx-vh restait figée sur sa DERNIÈRE valeur d'avant la mise en
    // veille (parfois mesurée pendant une transition de barre d'adresse ou un clavier encore
    // ouvert), trop petite pour la vraie zone visible au retour — tout container calc(var(
    // --nx-vh,1vh)*100) laissait alors un vide blanc en dessous jusqu'au prochain vrai
    // rechargement de page (fermer/rouvrir). setNxVh() directement (jamais maybeSetNxVh) :
    // au retour d'arrière-plan, la vraie mesure prime toujours, même si document.activeElement
    // pointe encore sur un champ obsolète.
    const onVisible = () => { if (!document.hidden) setNxVh(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", setNxVh);
    window.addEventListener("focus", setNxVh);

    setNxVh();
    window.addEventListener("resize", maybeSetNxVh);
    window.addEventListener("orientationchange", setNxVh);
    if (window.visualViewport) window.visualViewport.addEventListener("resize", maybeSetNxVh);
    const onFocusOut = () => setTimeout(maybeSetNxVh, 80);
    document.addEventListener("focusout", onFocusOut, true);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", setNxVh);
      window.removeEventListener("focus", setNxVh);
      window.removeEventListener("resize", maybeSetNxVh);
      window.removeEventListener("orientationchange", setNxVh);
      if (window.visualViewport) window.visualViewport.removeEventListener("resize", maybeSetNxVh);
      document.removeEventListener("focusout", onFocusOut, true);
    };
  }, []);

  /* Retour à l'écran précédent par glissement depuis le bord gauche
     (edge-swipe-back, comme sur iPhone) — capté uniquement tout près du
     bord gauche (comme iOS distingue ce geste d'un simple défilement),
     fait glisser l'écran actuel vers la droite pendant le geste, et au
     relâchement au-delà du seuil déclenche le VRAI bouton "Retour" du
     header actuellement affiché — jamais une logique de retour dupliquée
     ou différente de celle déjà correcte pour chaque écran. */
  const mainAppRef = useRef(null);
  useEffect(() => {
    const mainEl = mainAppRef.current;
    if (!mainEl) return;
    let startX = null, startY = null, dragging = false, decided = false;
    const THRESHOLD = 80;
    const onPointerDown = (e) => {
      // Ossature desktop (Phase 1) : affordance tactile iOS-style, inutile à la souris
      // et le bord gauche est désormais occupé par le dock de nav en mode large.
      if (isWideRef.current) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      if (e.clientX > 28) return;
      startX = e.clientX; startY = e.clientY; dragging = true; decided = false;
    };
    const onPointerMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      const dy = Math.abs(e.clientY - startY);
      if (!decided) {
        if (dy > 18 && dy > Math.abs(dx)) { dragging = false; return; }
        if (Math.abs(dx) > 4) decided = true;
      }
      if (!decided) return;
      e.preventDefault();
      const clamped = Math.max(0, Math.min(dx, 340));
      mainEl.style.transition = "none";
      mainEl.style.transform = `translateX(${clamped}px)`;
      mainEl.style.boxShadow = clamped > 0 ? "-30px 0 60px rgba(0,0,0,.25)" : "none";
    };
    const onPointerUp = (e) => {
      if (!dragging) return;
      dragging = false;
      if (!decided) return;
      const dx = Math.max(0, (e.clientX ?? startX) - startX);
      mainEl.style.transition = "transform .22s ease";
      if (dx > THRESHOLD) {
        mainEl.style.transform = "translateX(340px)";
        setTimeout(() => {
          document.querySelector(`[aria-label="${t("common.back", "Retour")}"]`)?.click();
          mainEl.style.transition = "none";
          mainEl.style.transform = "translateX(0)";
          mainEl.style.boxShadow = "none";
        }, 180);
      } else {
        mainEl.style.transform = "translateX(0)";
        mainEl.style.boxShadow = "none";
      }
    };
    document.addEventListener("pointerdown", onPointerDown, { passive: true });
    document.addEventListener("pointermove", onPointerMove, { passive: false });
    document.addEventListener("pointerup", onPointerUp, { passive: true });
    document.addEventListener("pointercancel", onPointerUp, { passive: true });
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);
    };
  }, [user]);

  /* VERROU DE SCROLL DU FOND — cause réelle du "contenu qui se déplace tout
     seul" : sur iOS, même avec une sheet en position:fixed par-dessus, la
     page de fond (plus haute que l'écran à cause de tout le contenu qu'elle
     contient) reste scrollable en dessous et "fuit" au moindre geste, ce qui
     donne l'impression que tout bouge dans tous les sens. On bloque le
     scroll du fond tant qu'une sheet est ouverte, et on restaure la position
     exacte à la fermeture. */
  useEffect(() => {
    if (!sheet) return;
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = { position: body.style.position, top: body.style.top, width: body.style.width, overflow: body.style.overflow };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [sheet]);
  /* Le bouton "retirer l'association" ne concerne que l'article atteint par
     le DERNIER scan — navigation manuelle = on efface le contexte de scan
     (même règle que runLastScannedCode dans mobile.js). */
  const lastScanArt = useRef(null);
  useEffect(() => {
    const target = lastScanCode ? articleForCode(lastScanCode)?.artNr : null;
    if (target && articles[runIndex]?.artNr === target) { lastScanArt.current = target; return; }
    if (lastScanCode && lastScanArt.current && articles[runIndex]?.artNr !== lastScanArt.current) {
      setLastScanCode(""); lastScanArt.current = null;
    }
  }, [runIndex, lastScanCode, articles]);

  const cycleTheme = () => {
    const next = THEME_ORDER[(THEME_ORDER.indexOf(themeId) + 1) % THEME_ORDER.length];
    setThemeId(next);
    const nextName = t(`theme.${next}`, THEMES[next].name);
    showToast(t("toast.themeSwitchedTemplate", { name: nextName, icon: THEMES[next].icon }, `Thème ${nextName} ${THEMES[next].icon}`));
  };

  // Bug réel trouvé en vérification (2026-08-05) : un identifiant de filiale partiellement
  // tapé (ex. "N2" au lieu de "N236", jamais complété via la datalist) survivait à une
  // navigation loin de l'écran puis retour, et provoquait un faux "Identifiant ou mot de
  // passe incorrect" au moment de se connecter — alors que les identifiants étaient
  // corrects, seule la filiale ne correspondait à rien. On efface la saisie dès qu'elle
  // quitte le champ sans correspondre exactement à une vraie filiale du registre — jamais
  // silencieusement envoyée telle quelle au serveur.
  const sanitizeBranchOnBlur = (value, setter) => {
    if (value && !storesCatalog.some((s) => s.id === value)) setter("");
  };
  /* --- Auth actions --- */
  const doLogin = async () => {
    const id = loginId.trim();
    if (!id) { setLoginMsg(t("auth.login.needProId", "Saisis ton Pro-ID.")); return; }
    if (!loginPw.trim()) { setLoginMsg(t("auth.login.needPassword", "Saisis ton mot de passe.")); return; }
    setLoginMsg("");
    setLoggingIn(true);
    // Filet de sécurité final : jamais envoyer une filiale qui ne correspond à aucune
    // vraie entrée du registre (voir sanitizeBranchOnBlur) — mieux vaut l'ignorer que de
    // faire échouer une connexion par ailleurs correcte à cause d'une saisie orpheline.
    const safeBranchId = storesCatalog.some((s) => s.id === loginBranchId) ? loginBranchId : "";
    try {
      const serverUser = await apiJSON("/api/login", {
        method: "POST",
        body: JSON.stringify({ login: id, password: loginPw, branchId: safeBranchId }),
      });
      const mapped = mapServerUser(serverUser);
      setUser(mapped);
      setLang(mapped.language);
      try { localStorage.setItem(NEXORA_LANG_KEY, mapped.language); } catch { /* pas bloquant */ }
      sessionPasswordRef.current = loginPw; // voir le commentaire sur sessionPasswordRef — déverrouillage offline
      setLoginPw("");
      setScreen("home");
      // Mémorise la filiale pour la prochaine connexion sur CET appareil (voir
      // NEXORA_LAST_BRANCH_KEY plus haut) — uniquement pour un compte de filiale
      // (safeBranchId non vide) ; un compte réseau n'en soumet jamais, on ne touche donc
      // jamais cette clé pour lui (la valeur mémorisée reste disponible pour le prochain
      // employé de filiale qui utilisera cet appareil).
      if (safeBranchId) { try { localStorage.setItem(NEXORA_LAST_BRANCH_KEY, safeBranchId); } catch {} }
      // Filet de sécurité : une connexion qui vient de réussir ne doit jamais hériter d'un
      // verrouillage par inactivité laissé par la session précédente (voir les 2 resets
      // équivalents sur les chemins d'invalidation de session ci-dessus).
      setLocked(false); setUnlockPassword(""); setUnlockError("");
      try { localStorage.setItem(IDLE_ACTIVITY_KEY, String(Date.now())); } catch {}
      showToast(t("toast.welcomeTemplate", { name: mapped.firstname || mapped.proId }, `Bienvenue, ${mapped.firstname || mapped.proId} 👋`));
      // Propose Face ID juste après une connexion par mot de passe réussie — jamais après
      // une connexion déjà biométrique (aucun sens), jamais si cet appareil a déjà un
      // enregistrement pour CE compte précis (webauthnHint.login), jamais si l'appareil ne
      // supporte tout simplement pas de biométrie (webauthnAvailable). Toujours un choix
      // (bouton "Plus tard" dans le sheet), jamais forcé.
      if (webauthnAvailable && webauthnHint?.login !== mapped.proId) setSheet("webauthnOffer");
    } catch (err) {
      setLoginMsg(err.message || "Connexion impossible.");
    } finally {
      setLoggingIn(false);
    }
  };
  // Connexion biométrique — utilise l'indice du dernier enregistrement réussi sur CET
  // appareil (webauthnHint). NotAllowedError = l'utilisateur a annulé le prompt natif
  // (bouton "Annuler"/Échap) : jamais un message d'erreur pour ce cas précis, juste un
  // retour silencieux au formulaire classique déjà affiché en dessous.
  const doWebauthnLogin = async () => {
    if (!webauthnHint || webauthnBusy) return;
    setWebauthnBusy(true); setLoginMsg("");
    try {
      const { options } = await apiJSON("/api/webauthn/login-options", {
        method: "POST",
        body: JSON.stringify({ login: webauthnHint.login, branchId: webauthnHint.branchId || "" }),
      });
      const response = await startAuthentication({ optionsJSON: options });
      const serverUser = await apiJSON("/api/webauthn/login-verify", {
        method: "POST",
        body: JSON.stringify({ login: webauthnHint.login, branchId: webauthnHint.branchId || "", response }),
      });
      const mapped = mapServerUser(serverUser);
      setUser(mapped);
      setLang(mapped.language);
      try { localStorage.setItem(NEXORA_LANG_KEY, mapped.language); } catch { /* pas bloquant */ }
      setScreen("home");
      if (webauthnHint.branchId) { try { localStorage.setItem(NEXORA_LAST_BRANCH_KEY, webauthnHint.branchId); } catch {} }
      setLocked(false); setUnlockPassword(""); setUnlockError("");
      try { localStorage.setItem(IDLE_ACTIVITY_KEY, String(Date.now())); } catch {}
      showToast(t("toast.welcomeTemplate", { name: mapped.firstname || mapped.proId }, `Bienvenue, ${mapped.firstname || mapped.proId} 👋`));
    } catch (err) {
      if (err?.name !== "NotAllowedError") setLoginMsg(err.message || t("auth.webauthn.loginFailed", "Connexion Face ID impossible."));
    } finally {
      setWebauthnBusy(false);
    }
  };
  // Activation Face ID pour CET appareil — appelée uniquement pour un compte déjà connecté
  // (register-options exige une session, voir server/webauthn/routes.js).
  const registerWebauthn = async () => {
    if (webauthnBusy) return;
    setWebauthnBusy(true);
    try {
      const { options } = await apiJSON("/api/webauthn/register-options", { method: "POST" });
      const response = await startRegistration({ optionsJSON: options });
      await apiJSON("/api/webauthn/register-verify", {
        method: "POST",
        body: JSON.stringify({ response, deviceLabel: navigator.platform || navigator.userAgent?.slice(0, 40) || "" }),
      });
      // Un compte réseau (voir LEADER_ROLES côté serveur, server.js) ne soumet jamais de
      // filiale au login classique — bug réel trouvé en vérification staging (2026-08-18) :
      // ce hint gardait user?.store (ex. "N236", sa filiale d'affichage) même pour ce type de
      // compte, envoyé tel quel par doWebauthnLogin à login-options/login-verify, qui
      // échouaient alors à retrouver le compte (même règle stricte que sfFindLoginCandidate).
      const WEBAUTHN_LEADER_ROLES = ["Bereichsleiter", "Verkaufsleiter", "Niederlassungsleiter", "Admin", "ADMIN", "NEXORA_ADMIN"];
      const hint = { login: user?.proId, branchId: WEBAUTHN_LEADER_ROLES.includes(user?.role) ? "" : (user?.store || "") };
      try { localStorage.setItem(NEXORA_WEBAUTHN_HINT_KEY, JSON.stringify(hint)); } catch {}
      setWebauthnHint(hint);
      showToast(t("toast.webauthnRegistered", "✓ Face ID activé pour cet appareil"));
    } catch (err) {
      if (err?.name !== "NotAllowedError") showToast(err.message || t("err.webauthnRegisterFailed", "Impossible d'activer Face ID."));
    } finally {
      setWebauthnBusy(false);
      setSheet(null);
    }
  };
  const logout = () => {
    apiJSON("/api/logout", { method: "POST" }).catch(() => {}); // best-effort — le client oublie l'utilisateur dans tous les cas
    // Bug réel trouvé en test (2026-08-28, "fond blanc à la déconnexion" — TypeError "null is
    // not an object (evaluating 'F.proId')" via ErrorBoundary) : de nombreux écrans/sections
    // (fiche profil, listes filtrées sur user?.proId, etc.) déréférencent `user?.xxx` sans garde
    // et restent montés le temps d'un rendu si `screen`/`sheet` n'ont pas ENCORE fini de
    // basculer loin d'eux. setUser(null) EN MÊME TEMPS que le reste risquait donc de rendre
    // ces sections une dernière fois avec user déjà nul → crash → écran blanc (aucun error
    // boundary avant ce chantier). On bascule d'abord screen/sheet vers un état neutre (ce
    // rendu-là voit encore le VRAI user, donc rien ne casse), puis on ne vide `user` qu'au
    // tick suivant, une fois les sections concernées déjà démontées.
    setSheet(null); setAuthScreen("start");
    setLoginPw(""); setScreen("home");
    setLocked(false); setUnlockPassword(""); setUnlockError("");
    sessionPasswordRef.current = "";
    setTimeout(() => setUser(null), 0);
  };
  const userInitials = user ? ((user?.firstname?.[0] || "") + (user?.lastname?.[0] || "")).toUpperCase() || "?" : "?";

  /* i18n réelle (Phase 0) — dictionnaire chargé depuis /locales/<lang>.json (voir
     lib/i18n.js). changeLang() est LE seul endroit qui doit changer la langue : bascule
     l'état local ET persiste sur le vrai compte (PATCH /api/me/language, ignoré si pas
     encore connecté — la langue reste alors un simple choix local jusqu'à la connexion). */
  const { t } = useI18n(lang);
  // 2026-08-29 (chantier traduction de tous les formulaires imprimables) — revient sur le
  // forçage allemand posé le 2026-08-28 ("tout doit être en allemand"), à la demande
  // explicite de l'utilisatrice lors de ce chantier : le Rapport hebdomadaire suit
  // maintenant la langue du compte comme tous les autres documents traduits de l'app.
  // tRepDe/weekLabelShortDe/reportCategoryLabelDe/wasteTypeLabelDe (forçage "de") supprimés —
  // buildWeeklyReportPrintHtml utilise directement t()/weekLabelShort/reportCategoryLabel/
  // wasteTypeLabel comme le reste de l'écran Reporting.
  // Jours de la semaine traduits — remplace l'accès direct à WEEK_LABELS/WEEK_LABELS_SHORT/
  // WEEK_DAY_LABELS (constantes module-scope, définies avant que `t` existe, donc figées
  // dans une seule langue) — WEEK_DAY_LABELS en particulier était du pur allemand codé en
  // dur (Mo/Di/Mi/Do/Fr/Sa) affiché dans Reporting quelle que soit la langue choisie, un
  // vrai bug corrigé au passage : les 3 constantes représentent les 6 mêmes jours (Lu→Sa),
  // désormais unifiées sur les 2 fonctions ci-dessous. Repli = valeur française déjà en
  // place (jamais de régression visuelle pendant la conversion des sites d'appel).
  const WEEKDAY_I18N_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat"];
  const weekLabel = (day) => t(`weekday.long.${WEEKDAY_I18N_KEYS[day]}`, WEEK_LABELS[day]);
  const weekLabelShort = (day) => t(`weekday.short.${WEEKDAY_I18N_KEYS[day]}`, WEEK_LABELS_SHORT[day]);
  // Catégories de rapport traduites — même principe (REPORT_CATEGORIES est module-scope,
  // défini avant que `t` existe, donc figé en français/allemand mixte). Repli = label
  // français/allemand déjà en place.
  const reportCategoryLabel = (id) => t(`reportCategory.${id}`, REPORT_CATEGORIES.find((c) => c.id === id)?.label || id);
  // PERMISSION_GROUPS n'a pas besoin d'un helper dédié : l'écran Admin > Rôles &
  // permissions (screen === "admin" && adminScreen === "roles") appelle déjà
  // t(`permGroup.${g.id}`, g.label) / t(`perm.${p.id}`, p.label) inline à chaque site
  // d'usage — seules les clés locales manquaient, ajoutées dans locales/*.json.
  const scannerZoneLabel = (id) => t(`scannerZone.${id}`, SCANNER_ZONES.find((z) => z.id === id)?.label || id);
  const wasteTypeLabel = (id) => t(`wasteType.${id}`, WASTE_TYPES.find((w) => w.id === id)?.label || id);
  const wipeTargetLabel = (id) => t(`wipeTarget.${id}`, WIPE_TARGETS.find((w) => w.id === id)?.label || id);
  // ROLE_HIERARCHY — la VALEUR (ex. "Kassierer", "FLA") est un identifiant réel de rôle
  // envoyé au serveur (user?.role, comparaisons de permissions ailleurs dans l'app) : ne
  // JAMAIS la traduire. Seul roleLabel() habille l'AFFICHAGE ; la valeur stockée/comparée
  // reste inchangée à chaque site d'usage (setUserEditForm/setActiveRole/
  // setPendingApprovalRole continuent de recevoir `r` brut, jamais roleLabel(r)).
  const roleLabel = (role) => t(`role.${role}`, role);
  // Légende AZL (Arbeitszeitliste) — le CODE (K, KK, U, UU, SU, F, V, MS, ERZ, Z) est un
  // vrai code légal/administratif allemand de droit du travail (paie, absences) : ne
  // JAMAIS le traduire, dans aucune langue (confirmé explicitement par l'utilisatrice).
  // Seul le libellé qui l'explique est traduit ici. Même carte utilisée aux 2 sites qui la
  // dupliquaient (écran interactif + export imprimé), pour rester cohérent partout.
  const AZL_LEGEND_LABEL_KEYS = {
    K: "krank", KK: "kinderkrank", U: "urlaub", UU: "unbezUrlaub", SU: "sonderurlaub",
    F: "feiertag", V: "vertretung", MS: "mutterschutz", ERZ: "erziehungsurlaub", Z: "zuschlagspflichtig",
  };
  const azlLegendLabel = (code, fallback) => t(`arbeitszeitliste.legend.item.${AZL_LEGEND_LABEL_KEYS[code] || code}`, fallback);
  const changeLang = (id) => {
    setLang(id);
    try { localStorage.setItem(NEXORA_LANG_KEY, id); } catch { /* stockage indisponible — pas bloquant */ }
    if (user) {
      apiJSON("/api/me/language", { method: "PATCH", body: JSON.stringify({ language: id }) })
        .catch(() => showToast(t("toast.langChangedOfflineOnly", "Langue changée localement — pas synchronisée avec ton compte (hors-ligne ?)")));
    }
  };

  /* Horloge vivante : l'accueil affiche date ET heure, mises à jour
     toutes les 30 s — le soleil/la lune et la salutation suivent. */
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);
  const hour = now.getHours();
  const isDay = hour >= 6 && hour < 20;
  const intlLocale = INTL_LOCALE_BY_LANG[lang] || "fr-FR";
  // Calendrier annuel (Planning) — remplace l'accès direct à months_fr (constante
  // module-scope, figée en français, jamais traduite malgré son nom — bug réel trouvé en
  // vérification 2026-08-18, photo utilisateur). Intl gère nativement les 5 langues sans
  // clés i18n dédiées ; capitalisé comme le reste des libellés de mois déjà affichés
  // ailleurs dans l'app (voir buildStundenleistungPrintHtml plus haut, même motif charAt).
  const monthLabelShort = (m) => {
    const s = new Date(2000, m, 1).toLocaleDateString(intlLocale, { month: "short" });
    return s.charAt(0).toUpperCase() + s.slice(1);
  };
  const timeLabel = now.toLocaleTimeString(intlLocale, { hour: "2-digit", minute: "2-digit" });
  const greetPeriod = hour < 5 ? "Night" : hour < 12 ? "Morning" : hour < 18 ? "Afternoon" : "Evening";
  const greetFallback = { Night: "Bonne nuit", Morning: "Bonjour", Afternoon: "Bon après-midi", Evening: "Bonsoir" }[greetPeriod];
  const greetWord = t(`home.assistant.greeting${greetPeriod}`, greetFallback);
  const greetEmoji = hour < 5 ? "🌙" : hour < 12 ? "☀️" : hour < 18 ? "🌤️" : "🌆";
  // Salutation genrée (2026-08-03, retour utilisateur) : "Bonjour M./Mme {nom}" si le sexe
  // est renseigné sur le compte, sinon repli sur le prénom (comportement historique) —
  // jamais bloquant, "" = pas encore renseigné.
  const genderTitle = user?.gender === "homme" ? t("common.titleMr", "M.") : user?.gender === "femme" ? t("common.titleMrs", "Mme") : null;
  const greetName = genderTitle && user?.lastname ? `${genderTitle} ${user?.lastname}` : (user?.firstname || "");
  const greetLine = genderTitle ? `${greetWord} ${greetName}` : `${greetWord}, ${greetName}`;
  const todayLabel = now.toLocaleDateString(intlLocale, { weekday: "long", day: "numeric", month: "long" });

  // Police adaptative de la salutation Accueil (retour utilisateur explicite : "je veux
  // pouvoir afficher la salutation en entier quelque soit la longueur, je ne veut plus de
  // ...") — au lieu de tronquer avec l'ellipse, on réduit la taille de police jusqu'à ce que
  // le texte complet tienne sur la ligne disponible, quelle que soit la longueur du nom.
  // scrollWidth/clientWidth forcent une lecture de mise en page à chaque itération (peu
  // coûteux ici : au plus 13 itérations, seulement au changement de texte ou de largeur
  // d'écran, jamais à chaque frame).
  // Ref-callback (pas un simple useRef) : la salutation est rendue derrière un écran de
  // chargement/porte (gateItems === null au tout premier rendu, voir plus bas) — sur CE
  // premier rendu, le useLayoutEffect s'exécute bien mais le span réel n'existe pas encore
  // (bug réel constaté : greetLine était déjà correct dès ce premier passage, donc une fois
  // la porte levée et le vrai span monté, aucune dépendance ne changeait plus jamais pour
  // redéclencher la mesure — le nom restait tronqué en permanence pour tout nom un peu long).
  // Un ref-callback notifie exactement au moment où le nœud DOM réel apparaît.
  const [greetEl, setGreetEl] = useState(null);
  const [greetFontSize, setGreetFontSize] = useState(21);
  useLayoutEffect(() => {
    const el = greetEl;
    if (!el) return;
    // Taille réduite (2026-08-22, retour utilisateur explicite après le nouvel en-tête à 3
    // rangées : "la salutation... elle est trop imposante") — 26px convenait à l'ancienne
    // disposition sur une seule ligne partagée avec l'emoji/boutons, mais devenait
    // disproportionné une fois seule sur sa propre ligne pleine largeur.
    const MAX_SIZE = 21, MIN_SIZE = 12;
    const fit = () => {
      let size = MAX_SIZE;
      el.style.fontSize = size + "px";
      while (el.scrollWidth > el.clientWidth && size > MIN_SIZE) {
        size -= 1;
        el.style.fontSize = size + "px";
      }
      setGreetFontSize(size);
    };
    fit();
    window.addEventListener("resize", fit);
    // Un simple "resize" de fenêtre ne suffit pas : la largeur disponible pour la salutation
    // peut aussi changer parce qu'un élément voisin de l'en-tête (badge, avatar...) se charge
    // ou change de taille après ce premier rendu, sans que la fenêtre elle-même bouge.
    // ResizeObserver capte tout changement de largeur du span ou de son parent, quelle qu'en
    // soit la cause.
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    return () => { window.removeEventListener("resize", fit); ro.disconnect(); };
  }, [greetLine, greetEl]);

  const startFresh = () => { setRunIndex(0); setFinish(false); setScreen("run"); };
  const resumeRun = () => {
    const idx = articles.findIndex((a) => !touched.has(a.artNr));
    setRunIndex(idx === -1 ? 0 : idx); setFinish(idx === -1); setScreen("run");
  };
  // Export MHD Kühlung — port direct de exportMhdPdf (public/modules/mobile.js) : mêmes
  // filtres/seuils réels, mais réutilise articles/records déjà en state (déjà tenus à jour
  // par loadArticles/loadKuhlungRecords) au lieu d'un nouveau fetch, contrairement au
  // vanilla qui recharge à chaque export.
  const exportMhdKuhlungPdf = async (filter) => {
    setSheet(null);
    const byArtNr = new Map(articles.filter((a) => String(a.zone || "Kühlung").trim() === "Kühlung").map((a) => [String(a.artNr), a]));
    let rows = records.filter((r) => !r.deletedAt && r.source === "KUEHLUNG" && byArtNr.has(String(r.artNr)));
    if (filter !== "all") rows = rows.filter((r) => mhdExportUrgencyClass(r.mhd) === filter);
    rows = rows.map((r) => ({ artNr: r.artNr, name: byArtNr.get(String(r.artNr))?.name || "", mhd: r.mhd }));
    if (!rows.length) { showToast(t("toast.noMhdMatchesFilter", "Aucune MHD ne correspond à ce filtre.")); return; }
    const bodyHtml = buildMhdExportPrintHtml(rows, {
      reportTitle: t("mhdExport.reportTitle", "Export MHD Kühlung"),
      filterLabel: t(`mhdExport.filter.${filter}`, MHD_EXPORT_FILTER_LABELS[filter] || MHD_EXPORT_FILTER_LABELS.all),
      printedOnLabel: t("mhdExport.printedOnLabel", "Impression"),
      artNrLabel: t("common.artNrLabel", "Art.Nr"),
      articleLabel: t("reductionsExport.article", "Article"),
      dateLabel: t("mhdExport.dateLabel", "Date MAD"),
      formatDate: fmtFR,
      todayLabel: fmtFR(todayISO()),
      pageTemplate: (n, total) => t("common.pageOfTotalTemplate", { n, total }, `Page ${n}/${total}`),
    });
    try {
      await exportHtmlToMultiPagePdf({ bodyHtml, css: MEX_DOC_CSS, pageSelector: ".mex-page", widthMm: 210, filename: `Export-MHD-Kuhlung-${filter}.pdf` });
    } catch (err) {
      if (err?.name !== "AbortError") showToast(err.message || t("err.actionImpossible", "Action impossible."));
    }
  };
  const nextArticle = () => {
    if (runIndex >= articles.length - 1) { setFinish(true); return; }
    setRunIndex((i) => i + 1);
  };
  const prevArticle = () => { setFinish(false); setRunIndex((i) => Math.max(0, i - 1)); };

  // POST /api/actions est best-effort (fil "Action Live") — jamais bloquant si ça échoue.
  const logAction = (type, details) => {
    apiJSON("/api/actions", {
      method: "POST",
      body: JSON.stringify({ user: `${user?.firstname || ""} ${user?.lastname || ""}`.trim(), type, details: details || {} }),
    }).catch(() => {});
  };
  const markTouched = (artNr, actionType) => {
    apiJSON("/api/touches", {
      method: "POST",
      body: JSON.stringify({ artNr, employee: user?.firstname || "", role: user?.role || "" }),
    }).catch(() => {});
    if (actionType) logAction(actionType, { artNr });
  };
  const nothingToReport = () => {
    setTouched((t) => new Set(t).add(article.artNr));
    markTouched(article.artNr, "ARTICLE_CONTROLE");
    showToast(t("toast.articleCheckedNothingToReport", "✓ Article contrôlé — rien à signaler"));
    setTimeout(nextArticle, 320);
  };

  const openEditSheet = (rec) => {
    setEditId(rec ? rec.id : null);
    setEditForm(rec ? { mhd: rec.mhd, qty: rec.quantity, note: rec.note } : { mhd: "", qty: "1", note: "" });
    setSheet("edit");
  };
  const saveEdit = () => {
    if (!editForm.mhd) { showToast(t("controlRun.chooseMhdDate", "Choisis une date MAD.")); return; }
    if (editForm.mhd < todayISO()) { showToast(t("controlRun.mhdBeforeTodayError", "Impossible d'enregistrer une MHD avant aujourd'hui.")); return; }
    const dup = artRecords.find((r) => r.mhd === editForm.mhd && r.id !== editId);
    if (dup) { showToast(t("toast.mhdDateAlreadyRegistered", "Cette date MHD est déjà enregistrée pour cet article.")); return; }
    if (editId) {
      setRecords((rs) => rs.map((r) => r.id === editId ? { ...r, mhd: editForm.mhd, quantity: editForm.qty, note: editForm.note } : r));
      apiJSON(`/api/records/${editId}`, {
        method: "PATCH",
        body: JSON.stringify({ mhd: editForm.mhd, quantity: editForm.qty, note: editForm.note }),
      })
        .then(() => showToast(t("toast.mhdUpdated", "MHD mise à jour ✓")))
        .catch((err) => { loadKuhlungRecords(); showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")); });
    } else {
      const tempId = "tmp" + Date.now();
      setRecords((rs) => [...rs, {
        id: tempId, artNr: article.artNr, mhd: editForm.mhd, quantity: editForm.qty, note: editForm.note.trim(),
        employee: user?.firstname || "Toi", controlDate: todayISO(),
      }]);
      setTouched((t) => new Set(t).add(article.artNr));
      // Le toast de succès ne partait AVANT que dans tous les cas, même sur un échec serveur
      // (POST rejeté) — l'article disparaissait alors silencieusement de la liste au prochain
      // rechargement, sans qu'aucune erreur n'ait jamais été montrée (retour utilisateur
      // explicite, 2026-08-19 : des MHD "Aktion" saisies la veille absentes le lendemain).
      apiJSON("/api/records", {
        method: "POST",
        body: JSON.stringify({
          artNr: article.artNr, name: article.name, mhd: editForm.mhd, quantity: editForm.qty,
          note: editForm.note.trim(), source: "KUEHLUNG", employee: user?.firstname || "Toi", controlDate: todayISO(),
        }),
      })
        .then((created) => {
          setRecords((rs) => rs.map((r) => r.id === tempId ? created : r));
          showToast(t("toast.mhdQtySavedTemplate", { date: fmtFR(editForm.mhd), qty: editForm.qty || "?" }, `✓ ${fmtFR(editForm.mhd)} · Qté ${editForm.qty || "?"} enregistrée`));
        })
        .catch((err) => {
          setRecords((rs) => rs.filter((r) => r.id !== tempId));
          showToast(err.message || t("err.savingImpossible", "Enregistrement impossible."));
        });
      markTouched(article.artNr, "MHD_ENREGISTREE");
      setSaveFlash(true); setTimeout(() => setSaveFlash(false), 500);
    }
    setSheet(null);
  };
  const deleteEdit = () => {
    setRecords((rs) => rs.filter((r) => r.id !== editId));
    apiJSON(`/api/records/${editId}`, { method: "DELETE" }).catch(() => loadKuhlungRecords());
    setSheet(null); showToast(t("toast.mhdDeleted", "MHD supprimée"));
  };
  // Suppression directe depuis la carte "MHD enregistrées" (sans passer par la sheet
  // d'édition) — bug réel trouvé en la construisant : l'ancien bouton ✕ inline ne
  // retirait la ligne que du state client (setRecords), jamais côté serveur
  // (contrairement à deleteEdit ci-dessus) — la MHD "supprimée" réapparaissait après
  // le prochain rechargement (loadKuhlungRecords). Même effet que deleteEdit, juste
  // appelable avec un record arbitraire plutôt que le seul editId courant.
  const deleteRecordDirect = (r) => {
    setRecords((rs) => rs.filter((x) => x.id !== r.id));
    apiJSON(`/api/records/${r.id}`, { method: "DELETE" }).catch(() => loadKuhlungRecords());
    showToast(t("toast.mhdDeleted", "MHD supprimée"));
  };

  /* Scan EAN — caméra simulée (cycle sur DEMO_EANS ; vrai html5-qrcode/
     BarcodeDetector à brancher séparément, hors périmètre de cette passe —
     iOS Safari ne supporte pas BarcodeDetector nativement), mais
     l'association code→article est réelle et persistante côté serveur. */
  const articleCodesFor = (article) => {
    if (!article) return [];
    const values = [article.ean, article.barcode];
    if (Array.isArray(article.barcodes)) values.push(...article.barcodes);
    if (Array.isArray(article.eans)) values.push(...article.eans);
    // Dédupliqué : ean/barcode dupliquent volontairement le "code principal" déjà présent
    // dans eans[]/barcodes[] — sans ce dédoublonnage, unlinkCodeFromArticle (qui filtre
    // cette liste sans jamais la déduplique lui-même) réinjecte les doublons dans
    // eans[]/barcodes[] à chaque retrait, et leur nombre croît sans borne au fil des
    // ajouts/retraits successifs (bug réel constaté en vérification, jamais visible avant
    // le multi-EAN car aucun appelant ne chaînait plusieurs opérations sur le même article).
    return Array.from(new Set(
      values.filter((v) => v !== undefined && v !== null && String(v).trim() !== "").map((v) => String(v).trim())
    ));
  };
  const articleForCode = (code) => {
    const c = String(code || "").trim();
    if (!c) return null;
    return articles.find((a) => articleCodesFor(a).includes(c)) || null;
  };
  const linkCodeToArticle = (code, article) => {
    const cleanedCode = String(code || "").trim();
    if (!cleanedCode || !article?.artNr) return Promise.reject(new Error("Code ou article invalide."));
    const nextCodes = Array.from(new Set([...articleCodesFor(article), cleanedCode]));
    return apiJSON(`/api/articles/${encodeURIComponent(article.artNr)}`, {
      method: "PATCH",
      body: JSON.stringify({
        ean: article.ean || cleanedCode,
        barcode: article.barcode || cleanedCode,
        barcodes: nextCodes,
        eans: nextCodes,
      }),
    }).then(({ article: updated }) => {
      setArticles((as) => as.map((a) => (a.artNr === updated.artNr ? updated : a)));
      return updated;
    });
  };
  const unlinkCodeFromArticle = (article, code = null) => {
    if (!article?.artNr) return Promise.reject(new Error("Article invalide."));
    const cleanedCode = code ? String(code).trim() : "";
    const existing = articleCodesFor(article);
    const nextCodes = cleanedCode ? existing.filter((c) => c !== cleanedCode) : [];
    const nextEan = !cleanedCode ? "" : (String(article.ean || "").trim() === cleanedCode ? (nextCodes[0] || "") : (article.ean || ""));
    const nextBarcode = !cleanedCode ? "" : (String(article.barcode || "").trim() === cleanedCode ? (nextCodes[0] || "") : (article.barcode || ""));
    return apiJSON(`/api/articles/${encodeURIComponent(article.artNr)}`, {
      method: "PATCH",
      body: JSON.stringify({ ean: nextEan, barcode: nextBarcode, barcodes: nextCodes, eans: nextCodes }),
    }).then(({ article: updated }) => {
      setArticles((as) => as.map((a) => (a.artNr === updated.artNr ? updated : a)));
      return updated;
    });
  };
  const applyScanMatch = (ctx, code, art) => {
    if (ctx === "run") {
      const idx = articles.findIndex((a) => a.artNr === art.artNr);
      if (idx !== -1) { setRunIndex(idx); setFinish(false); }
      setLastScanCode(code);
    } else if (ctx === "delivery") {
      selectDelArt(art);
    } else if (ctx === "store") {
      // Même quand le code correspond à un article du catalogue officiel, on garde le
      // VRAI EAN scanné sur la fiche promo (jamais seulement l'Art.Nr) — voir la branche
      // "non trouvé" dans handleScanFileChange pour le cas, plus fréquent ici, où l'article
      // promotionnel n'existe dans AUCUN catalogue.
      setStoreForm((f) => ({ ...f, name: art.name, artNr: art.artNr, ean: code }));
    } else if (ctx === "inventur") {
      setInventurEntryForm((f) => ({ ...f, artNr: art.artNr, name: art.name }));
    } else if (ctx === "finder") {
      // Artikelfinder est une base collaborative séparée du catalogue officiel — un match
      // trouvé sert juste à pré-remplir (numéro d'article + désignation si vide), jamais une
      // liaison forcée comme pour run/delivery.
      setFinderCreateForm((f) => ({ ...f, ean: code, articleNumber: art.artNr || f.articleNumber, designation: f.designation || art.name || "" }));
    } else if (ctx === "newPromoArt") {
      // Formulaire "Nouvel article promotionnel" (Kühlung/Frisch Fleisch) — un vrai match
      // catalogue est rare ici (le but est justement un article HORS catalogue) mais reste
      // géré : pré-remplit Art.Nr + nom, jamais de liaison forcée. Cas plus fréquent (non
      // trouvé) dans la branche dédiée de handleScanFileChange ci-dessous.
      setNewPromoArtForm((f) => ({ ...f, artNr: art.artNr, name: f.name || art.name || "" }));
    } else if (ctx === "cz") {
      // Saisie MHD Trocken/Frisch Fleisch/Frisch Brot — même comportement que le clic sur
      // un résultat de recherche (openCzMhdForm), catalogue réel donc liaison directe.
      openCzMhdForm(art, null);
    }
  };
  // Déclenche la vraie capture (l'input file capture="environment" ouvre l'appareil
  // photo natif sur mobile) — le décodage réel se fait dans handleScanFileChange une
  // fois la photo choisie/prise.
  const doScan = (ctx) => {
    scanCtxRef.current = ctx;
    if (scanFileInputRef.current) { scanFileInputRef.current.value = ""; scanFileInputRef.current.click(); }
  };
  const handleScanFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ctx = scanCtxRef.current;
    setScanning(true);
    decodeBarcodePhoto(file)
      .then(({ value }) => {
        setScanning(false);
        if (!value) { showToast(t("toast.noBarcodeDetected", "Aucun code-barres détecté — réessaie avec un cadrage plus net.")); return; }
        const code = cleanBarcodeCode(value);
        if (ctx === "scanLink") { setScanLinkInput(code); showToast(t("toast.codeDetectedTemplate", { code: code.slice(-5) }, `📷 Code détecté : …${code.slice(-5)}`)); return; }
        // Règles de réduction (2026-08-17) : "peu importe son groupe" — cherche dans le
        // catalogue COMPLET (articlesCatalog, toutes zones), pas le seul catalogue Kühlung
        // qu'utilise articleForCode() pour les autres contextes de scan.
        if (ctx === "redRule") {
          const found = articlesCatalog.find((a) => articleCodesFor(a).includes(code));
          if (found) { setRuleArt(found); showToast(t("toast.eanRecognizedTemplate", { code: code.slice(-5), name: found.name }, `✓ EAN ${code.slice(-5)} reconnu → ${found.name}`)); }
          else showToast(t("toast.eanNotFoundInCatalog", "Aucun article ne correspond à ce code."));
          return;
        }
        const art = articleForCode(code);
        if (art) {
          applyScanMatch(ctx, code, art);
          showToast(t("toast.eanRecognizedTemplate", { code: code.slice(-5), name: art.name }, `✓ EAN ${code.slice(-5)} reconnu → ${art.name}`));
        } else if (ctx === "store") {
          // Magasin (articles promotionnels) : la plupart n'ont AUCUNE fiche catalogue —
          // contrairement à run/delivery où un code inconnu doit être lié à un article
          // existant, ici on capture le VRAI EAN directement sur la fiche promo, jamais de
          // sheet de liaison forcée.
          setStoreForm((f) => ({ ...f, ean: code }));
          showToast(t("toast.eanCapturedTemplate", { code: code.slice(-5) }, `📷 EAN capturé : …${code.slice(-5)}`));
        } else if (ctx === "inventur") {
          // Inventur compte des articles physiques quelle que soit leur présence dans un
          // catalogue en ligne (contrairement à run/delivery) — jamais de sheet de liaison
          // forcée ici non plus, même logique que "store" : le code scanné devient
          // directement l'Art.Nr, modifiable si ce n'est pas le bon.
          setInventurEntryForm((f) => ({ ...f, artNr: code }));
          showToast(t("toast.eanCapturedTemplate", { code: code.slice(-5) }, `📷 EAN capturé : …${code.slice(-5)}`));
        } else if (ctx === "finder") {
          // Même logique que store/inventur : la plupart des articles Artikelfinder n'ont
          // aucune fiche dans le catalogue officiel (c'est justement le but du module) —
          // le code scanné devient directement l'EAN de la nouvelle fiche, jamais de sheet
          // de liaison forcée.
          setFinderCreateForm((f) => ({ ...f, ean: code }));
          showToast(t("toast.eanCapturedTemplate", { code: code.slice(-5) }, `📷 EAN capturé : …${code.slice(-5)}`));
        } else if (ctx === "labels") {
          // Scanner un EAN depuis le champ "Code-barres (EAN)" du formulaire d'étiquette
          // (chantier 2026-08-28, repositionné suite retour utilisateur — pas un bouton du
          // hub, un scan inline sur le champ concerné, saisie manuelle toujours possible
          // aussi). Reste sur l'écran courant (form), remplit juste le champ ean — même
          // logique que store/inventur/finder pour le préremplissage désignation/Art.Nr si
          // l'EAN correspond à un article du catalogue (jamais de prix : aucun champ prix
          // fiable à cet endroit, jamais de donnée inventée), sans écraser une saisie déjà
          // en cours.
          const found = articleForCode(code);
          setLabelDraft((d) => ({
            ...d,
            ean: code,
            numeroArticle: d.numeroArticle || found?.artNr || "",
            designation: d.designation || found?.name || "",
          }));
          showToast(found
            ? t("toast.eanRecognizedTemplate", { code: code.slice(-5), name: found.name }, `✓ EAN ${code.slice(-5)} reconnu → ${found.name}`)
            : t("toast.eanCapturedTemplate", { code: code.slice(-5) }, `📷 EAN capturé : …${code.slice(-5)}`));
        } else if (ctx === "warenaustausch") {
          // Warenaustausch (2026-08-23) — le code scanné remplit juste le champ, l'ajout
          // réel de la ligne (résolution article + prix, POST /:id/lines) se fait au clic
          // sur "Ajouter" une fois la quantité confirmée — jamais un ajout automatique dès
          // le scan, la quantité doit toujours être une saisie consciente.
          setWaScanCode(code);
          showToast(t("toast.eanCapturedTemplate", { code: code.slice(-5) }, `📷 EAN capturé : …${code.slice(-5)}`));
        } else if (ctx === "newPromoArt") {
          // Même logique que store/inventur/finder : le but même de ce formulaire est un
          // article HORS catalogue, donc un code inconnu est le cas normal — devient
          // directement l'Art.Nr, modifiable si ce n'est pas le bon.
          setNewPromoArtForm((f) => ({ ...f, artNr: code }));
          showToast(t("toast.eanCapturedTemplate", { code: code.slice(-5) }, `📷 EAN capturé : …${code.slice(-5)}`));
        } else {
          setPendingCode(code); setScanCtx(ctx); setLinkSearch("");
          setSheet("linkCode");
        }
      })
      .catch((err) => { setScanning(false); showToast(err.message || t("err.decodingImpossible", "Décodage impossible.")); });
  };
  const linkPendingCode = (art) => {
    const code = pendingCode;
    linkCodeToArticle(code, art)
      .then((updated) => {
        applyScanMatch(scanCtx, code, updated);
        setSheet(null);
        showToast(t("toast.eanLinkedTemplate", { code: code.slice(-5), name: updated.name }, `🔗 EAN …${code.slice(-5)} associé à ${updated.name} — reconnu au prochain scan`));
        setPendingCode("");
      })
      .catch((err) => showToast(err.message || t("err.associationImpossible", "Association impossible.")));
  };
  const unlinkLastScan = () => {
    if (!lastScanCode) return;
    const art = articleForCode(lastScanCode);
    if (!art) { setLastScanCode(""); return; }
    unlinkCodeFromArticle(art, lastScanCode)
      .then(() => {
        showToast(t("toast.eanLinkRemovedTemplate", { code: lastScanCode.slice(-5) }, `Association EAN …${lastScanCode.slice(-5)} retirée`));
        setLastScanCode("");
      })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  const emptyStore = { name: "", artNr: "", ean: "", photo: "", mhd: "", qty: "", note: "", rule: "AUTO" };
  const [storeForm, setStoreForm] = useState(emptyStore);
  const [storePhotoUploading, setStorePhotoUploading] = useState(false);
  const [articleCardRecord, setArticleCardRecord] = useState(null);
  // Nom du Sheet à rouvrir en fermant la fiche article, quand elle a été ouverte DEPUIS un
  // autre Sheet (ex. "storeHistory") plutôt que depuis un `screen` — `sheet` n'est qu'une
  // seule valeur, jamais une pile, donc ouvrir "articleCard" par-dessus écrasait purement et
  // simplement le Sheet précédent : fermer la fiche article renvoyait à l'écran de dessous
  // au lieu de rester dans l'historique (retour utilisateur explicite). null = comportement
  // d'origine (ouverture depuis Réductions, un `screen`, jamais un Sheet).
  const [articleCardReturnSheet, setArticleCardReturnSheet] = useState(null);
  const storePhotoInputRef = useRef(null);
  const handleStorePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStorePhotoUploading(true);
    const body = new FormData();
    body.append("photo", file);
    apiJSON("/api/store-photos", { method: "POST", body })
      .then(({ url }) => setStoreForm((f) => ({ ...f, photo: url })))
      .catch((err) => showToast(err.message || t("err.photoSendImpossible", "Envoi de la photo impossible.")))
      .finally(() => setStorePhotoUploading(false));
  };
  const saveStore = () => {
    if (!storeForm.name.trim()) { showToast(t("toast.articleNameRequired", "Nom de l'article requis.")); return; }
    if (!storeForm.artNr.trim() && !storeForm.ean.trim()) { showToast(t("toast.scanEanOrEnterArtNr", "Scanne l'EAN ou saisis un Art.Nr.")); return; }
    if (!storeForm.mhd) { showToast(t("toast.chooseDlcDate", "Choisis une date DLC.")); return; }
    if (storeForm.mhd < todayISO()) { showToast(t("controlStore.cannotSaveBeforeToday", "Impossible d'enregistrer une DLC avant aujourd'hui.")); return; }
    const tempId = "tmp" + Date.now();
    const optimistic = {
      id: tempId, name: storeForm.name.trim(), artNr: storeForm.artNr.trim() || "—", ean: storeForm.ean.trim(), photo: storeForm.photo,
      mhd: storeForm.mhd, quantity: storeForm.qty, note: storeForm.note, controlDate: todayISO(),
    };
    setStoreRecords((rs) => [optimistic, ...rs]);
    setRedStoreRecords((rs) => [optimistic, ...rs]);
    const pickedRule = storeForm.rule; // capturé avant setStoreForm(emptyStore) plus bas
    const artNrForRule = storeForm.artNr.trim();
    const nameForRule = storeForm.name.trim();
    apiJSON("/api/records", {
      method: "POST",
      body: JSON.stringify({
        artNr: storeForm.artNr.trim() || undefined, ean: storeForm.ean.trim() || undefined, name: storeForm.name.trim(), mhd: storeForm.mhd,
        quantity: storeForm.qty, note: storeForm.note, photo: storeForm.photo, source: "STORE", employee: user?.firstname || "Toi", controlDate: todayISO(),
      }),
    })
      .then((created) => {
        setStoreRecords((rs) => rs.map((r) => r.id === tempId ? created : r)); setRedStoreRecords((rs) => rs.map((r) => r.id === tempId ? created : r));
        showToast(t("toast.promoArticleSaved", "Article promotionnel enregistré ✓"));
        // Règle de réduction facultative choisie dans le même formulaire (retour utilisateur
        // explicite, 2026-08-19) — appliquée seulement après confirmation réelle de l'article,
        // jamais avant (sinon une dérogation pourrait pointer vers un artNr jamais créé si le
        // POST échoue). Sans Art.Nr (identifié par EAN seul), pas de dérogation possible : le
        // moteur de règles est indexé par Art.Nr, jamais par EAN.
        if (pickedRule && pickedRule !== "AUTO" && artNrForRule) setArticleRule(artNrForRule, pickedRule, nameForRule);
      })
      // Le toast de succès ne partait AVANT que jusqu'ici, quel que soit le résultat réel de
      // la requête — un échec serveur (ex. date déjà enregistrée, validation) retirait
      // l'article silencieusement de la liste sans jamais avertir : l'utilisateur croyait
      // avoir enregistré, l'article disparaissait au prochain rechargement. Retour utilisateur
      // explicite (2026-08-19) : des articles Aktion "enregistrés" la veille avaient disparu
      // de l'historique le lendemain — cause racine trouvée ici.
      .catch((err) => {
        setStoreRecords((rs) => rs.filter((r) => r.id !== tempId)); setRedStoreRecords((rs) => rs.filter((r) => r.id !== tempId));
        showToast(err.message || t("err.savingImpossible", "Enregistrement impossible."));
      });
    logAction("STORE_MAD_ENREGISTREE", { artNr: storeForm.artNr, name: storeForm.name });
    setStoreForm(emptyStore);
  };
  const deliveryReceived = useMemo(() => records
    .filter((r) => r.controlDate === todayISO())
    .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || "") || String(b.id).localeCompare(String(a.id))),
  [records]);
  const delMissing = articles.filter((a) => !deliveryReceived.some((r) => r.artNr === a.artNr));
  const delTotal = articles.length;
  const delProgressPct = delTotal ? Math.round((deliveryReceived.length / delTotal) * 100) : 0;

  // Kühlung/Frisch Fleisch mélangeaient catalogue ET articles promotionnels ("-Nouveaux")
  // sous la même source — retour utilisateur explicite : chaque doit avoir sa PROPRE carte
  // dans Réductions, avec ses propres valeurs. On retague donc en KUEHLUNG_PROMO/
  // FRISCHFLEISCH_PROMO tout enregistrement dont l'artNr appartient à la liste des articles
  // promotionnels (newArticles/newArticlesFF, zone "*-Nouveaux") — même règle de réduction
  // que leur famille (voir sourceDefaults/redRuleFor), juste une carte séparée.
  const kuhlungPromoArtNrs = useMemo(() => new Set(newArticles.map((a) => a.artNr)), [newArticles]);
  const ffPromoArtNrs = useMemo(() => new Set(newArticlesFF.map((a) => a.artNr)), [newArticlesFF]);
  const redSources = useMemo(() => ([
    // [...records, ...kuhlungExpiredRemoved] : voir kuhlungExpiredRemoved plus haut — sans
    // ce merge, une MHD Kühlung expirée retirée du Contrôle à minuit disparaissait aussi de
    // l'onglet Expirées de Réductions, alors qu'elle doit y rester visible.
    ...[...records, ...kuhlungExpiredRemoved].map((r) => ({
      ...r, source: kuhlungPromoArtNrs.has(r.artNr) ? "KUEHLUNG_PROMO" : "KUEHLUNG",
      name: (articles.find((a) => a.artNr === r.artNr) || {}).name || r.name || "Art." + r.artNr,
    })),
    ...redStoreRecords.map((r) => ({ ...r, source: "STORE" })),
    // Trocken/Frisch Fleisch/Frisch Brot portent déjà leur vrai `source` depuis le serveur
    // (posé à la création par saveCzMhd) — jamais réécrit ici, contrairement à KUEHLUNG/STORE
    // ci-dessus qui n'ont pas ce champ fiable dans leur réponse brute. Seule exception :
    // Frisch Fleisch promotionnel, même retag que Kühlung ci-dessus.
    // [...czRecords, ...czExpiredRemoved] : voir czExpiredRemoved plus haut — même correctif
    // que kuhlungExpiredRemoved, pour Trocken/Frisch Fleisch/Frisch Brot.
    ...[...czRecords, ...czExpiredRemoved].map((r) => (r.source === "FRISCHFLEISCH" && ffPromoArtNrs.has(r.artNr) ? { ...r, source: "FRISCHFLEISCH_PROMO" } : r)),
  ]), [records, kuhlungExpiredRemoved, redStoreRecords, czRecords, czExpiredRemoved, kuhlungPromoArtNrs, ffPromoArtNrs, articles]);
  const redActive = redSources.filter((r) => !reduced.some((d) => d.recId === r.id));
  const redExpired = redActive.filter((r) => r.mhd < todayISO()).sort((a, b) => a.mhd.localeCompare(b.mhd));
  /* Moteur de date de réduction fidèle au vrai reductions.js — 3 règles réelles, jamais
     un nombre de jours arbitraire :
     - J30_CALENDAR (Magasin par défaut) : jours CALENDAIRES, jamais de notion de jour ouvré.
     - J3_OPEN (Kühlung/Livraison par défaut) / J2_OPEN (exception) : jours OUVRÉS, en
       ignorant dimanches et jours fériés. Une MHD tombant un jour fermé doit d'abord
       "rejoindre" le dernier jour ouvré avant elle (rollback), pour fusionner avec la
       réduction de ce jour-là au lieu de repartir sur son propre compte à rebours.
     Une dérogation par article (ruleOverrides) gagne TOUJOURS sur la règle par défaut de
     la source, quelle que soit cette source — voir la roue "Règles de réduction".
     Jours fériés réutilisés depuis Planning (vrai référentiel serveur, Land de la
     filiale déjà résolu par /api/public-holidays — voir loadPublicHolidays()). */
  const allHolidayDatesSet = useMemo(() => new Set(publicHolidays.map((h) => h.date)), [publicHolidays]);
  const isClosedDay = (dateStr) => new Date(dateStr + "T00:00:00").getDay() === 0 || allHolidayDatesSet.has(dateStr);
  const rollBackToOpenDay = (dateStr) => { let cur = dateStr; while (isClosedDay(cur)) cur = addDaysISO(cur, -1); return cur; };
  const subtractOpenDays = (dateStr, n) => {
    let cur = dateStr, count = 0;
    while (count < n) { cur = addDaysISO(cur, -1); if (!isClosedDay(cur)) count++; }
    return cur;
  };
  // Étiquette/icône par provenance pour les cartes Réductions (actif/faites/corbeille) —
  // sans ceci, tout ce qui n'est pas STORE retombait sur le libellé "Kühlung" en dur,
  // ce qui affichait à tort "Kühlung" pour les nouvelles zones Trocken/Frisch Fleisch/
  // Frisch Brot (trouvé en vérifiant la fonctionnalité avec de vraies données).
  const REDUCTION_SOURCE_META = {
    STORE: { label: t("control.storeTag", "Magasin"), emoji: "🏷️", bg: "rgba(139,47,214,.13)" },
    TROCKEN: { label: "Trocken", emoji: "📦", bg: "rgba(231,138,18,.13)" },
    FRISCHFLEISCH: { label: "Frisch Fleisch", emoji: "🥩", bg: "rgba(239,68,68,.13)" },
    FRISCHFLEISCH_PROMO: { label: t("reductions.sourceFrischfleischPromo", "Frisch Fleisch (Aktion)"), emoji: "🏷️", bg: "rgba(239,68,68,.13)" },
    FRISCHBROT_BROT: { label: "Frisch Brot", emoji: "🍞", bg: "rgba(161,98,7,.13)" },
    KUEHLUNG_PROMO: { label: t("reductions.sourceKuhlungPromo", "Kühlung (Aktion)"), emoji: "🏷️", bg: "rgba(45,127,240,.13)" },
  };
  const redSourceMeta = (source) => REDUCTION_SOURCE_META[String(source || "").toUpperCase()] || { label: "Kühlung", emoji: "❄️", bg: "rgba(45,127,240,.13)" };
  const redRuleFor = (artNr, source) => ruleOverrides[artNr] || sourceDefaults[String(source || "").toUpperCase()] || "J3_OPEN";
  const redRuleDefFor = (artNr, source) => ruleDefinitionsMap[redRuleFor(artNr, source)] || ruleDefinitionsMap.J3_OPEN;
  const redWindow = (artNr, source) => redRuleDefFor(artNr, source).days;
  const reductionDate = (mhd, artNr, source) => {
    const rule = redRuleDefFor(artNr, source);
    return rule.calendar ? addDaysISO(mhd, -rule.days) : subtractOpenDays(rollBackToOpenDay(mhd), rule.days);
  };
  // r.artNr || r.ean — un article Magasin en saisie libre (source STORE) n'a souvent qu'un
  // EAN, jamais d'Art.Nr (retour utilisateur explicite 2026-08-21) ; sans ce repli, sa
  // dérogation de règle ne serait jamais retrouvée ici et la date de réduction affichée
  // resterait celle du groupe, malgré une règle spécifique enregistrée sur cet article.
  const redTodo = redActive
    .filter((r) => r.mhd >= todayISO() && reductionDate(r.mhd, r.artNr || r.ean, r.source) <= todayISO())
    .sort((a, b) => reductionDate(a.mhd, a.artNr || a.ean, a.source).localeCompare(reductionDate(b.mhd, b.artNr || b.ean, b.source)) || a.mhd.localeCompare(b.mhd) || a.name.localeCompare(b.name));
  const redDoneToday = reduced
    .filter((d) => d.doneAt.slice(0, 10) === todayISO())
    // Une fois réduit, le vrai enregistrement disparaît de records/storeRecords (le serveur
    // exclut par défaut tout ce qui a une entrée /api/done) — on reconstruit rec à partir
    // des champs déjà renvoyés par /api/done plutôt que de dépendre de redSources.
    .map((d) => ({ ...d, rec: redSources.find((r) => r.id === d.recId) || { id: d.recId, artNr: d.artNr, name: d.name, mhd: d.mhd, quantity: d.quantity, source: d.source } }))
    .filter((d) => d.rec)
    .sort((a, b) => b.doneAt.localeCompare(a.doneAt));
  const redPct = (redTodo.length + redDoneToday.length) === 0 ? 100
    : Math.round((redDoneToday.length / (redTodo.length + redDoneToday.length)) * 100);
  // Ordre catalogue Kühlung — "articles" arrive déjà trié par sortOrder serveur (voir
  // GET /api/articles), on réutilise directement cette position plutôt que de retrier par
  // date/nom (retour utilisateur explicite : la catégorie Kühlung doit toujours suivre
  // l'ordre de la vraie liste catalogue, jamais un autre tri).
  const kuhlungOrderIndex = useMemo(() => new Map(articles.map((a, i) => [a.artNr, i])), [articles]);
  const sortForCategory = (list, category, keyOf = (x) => x) => category !== "KUEHLUNG" ? list : [...list].sort((a, b) =>
    (kuhlungOrderIndex.get(keyOf(a).artNr) ?? Infinity) - (kuhlungOrderIndex.get(keyOf(b).artNr) ?? Infinity)
  );
  // KUEHLUNG et STORE restent les 2 seules provenances RÉELLES (voir redSources) — les 3
  // autres (retour utilisateur explicite, 2026-08-02 : "on garde kuhlung, articles
  // promotionnels du magasin, fleish, brot, trocken") n'ont encore aucune source de
  // données de réduction câblée : redCategoryStats(id) retombe honnêtement sur {todo:0,
  // doneToday:0, expired:0} pour elles (aucun redSources.source ne matche leur id), donc
  // "Rien à traiter" plutôt qu'un chiffre inventé. Chaque catégorie n'a qu'à gagner sa
  // vraie source plus tard (même patron que Kühlung/Magasin), aucune autre logique à
  // changer — mêmes ids/couleurs que REPORT_CATEGORIES (Reporting pertes) pour rester
  // cohérent avec le reste de l'app.
  // 5 rayons formalisés (port du vanilla, 2026-07-13) — "Obst & Gemüse" reste une
  // valeur assignable côté serveur mais n'a délibérément pas de carte hub ici (pas
  // de contrôle MHD pour le frais en vrac, même choix que public/modules/articles.js).
  const ARTICLE_ZONES = [
    { id: "Trocken", label: "Trocken", subtitle: t("articles.catTrockenSub", "Épicerie sèche, conserves"), emoji: "🥫", color: "#c9a13b" },
    { id: "Frisch Fleisch", label: "Frisch Fleisch", subtitle: t("articles.catFleischSub", "Viande fraîche"), emoji: "🥩", color: A.red },
    { id: "Kühlung", label: "Kühlung", subtitle: t("articles.catKuhlungSub", "Frais, contrôle MHD quotidien"), emoji: "❄️", color: A.blue },
    { id: "Frisch Brot", label: "Frisch Brot", subtitle: t("articles.catBrotSub", "Boulangerie fraîche"), emoji: "🍞", color: "#b98b4e" },
    { id: "Prebake", label: "Prebake", subtitle: t("articles.catPrebakeSub", "Cuisson boulangerie (Reporting)"), emoji: "🥐", color: A.teal },
    // 2 nouvelles zones (module Commandes de marchandises, 2026-08-07) — aucun article
    // encore rattaché, à remplir progressivement via cet éditeur, comme les autres zones.
    { id: "Obst & Gemüse", label: "Obst & Gemüse", subtitle: t("articles.catObstGemuseSub", "Fruits et légumes frais"), emoji: "🥬", color: A.green },
    { id: "Tiefkühlung", label: "Tiefkühlung", subtitle: t("articles.catTiefkuhlungSub", "Surgelés"), emoji: "🧊", color: A.frost },
  ];
  const INVENTUR_STATUS_META = {
    vorbereitung: { label: t("inventur.status.vorbereitung", "Préparation"), color: A.navy },
    bereit: { label: t("inventur.status.bereit", "Prêt"), color: A.blue },
    in_bearbeitung: { label: t("inventur.status.in_bearbeitung", "En cours"), color: A.orange },
    pruefung: { label: t("inventur.status.pruefung", "Vérification"), color: A.purple },
    abgeschlossen: { label: t("inventur.status.abgeschlossen", "Terminé"), color: A.green },
    archiviert: { label: t("inventur.status.archiviert", "Archivé"), color: A.teal },
  };
  const INVENTUR_CATEGORY_STATUS_META = {
    nicht_zugewiesen: { label: t("inventur.categoryStatus.nicht_zugewiesen", "Non attribué"), color: P.muted },
    zugewiesen: { label: t("inventur.categoryStatus.zugewiesen", "Attribué"), color: A.blue },
    in_bearbeitung: { label: t("inventur.categoryStatus.in_bearbeitung", "En cours"), color: A.orange },
    pausiert: { label: t("inventur.categoryStatus.pausiert", "En pause"), color: A.brown },
    fertig: { label: t("inventur.categoryStatus.fertig", "Terminé"), color: A.green },
    geprueft: { label: t("inventur.categoryStatus.geprueft", "Vérifié"), color: A.purple },
    gesperrt: { label: t("inventur.categoryStatus.gesperrt", "Verrouillé"), color: A.navy },
  };
  const KONTROLLE_STATUS_META = {
    vorbereitet: { label: t("kontrolle.status.vorbereitet", "Préparé"), color: A.navy },
    aktiv: { label: t("kontrolle.status.aktiv", "Actif"), color: A.blue },
    wartet_auf_verkaufsmenge: { label: t("kontrolle.status.wartet_auf_verkaufsmenge", "En attente de la quantité vendue"), color: A.orange },
    wartet_auf_endzaehlung: { label: t("kontrolle.status.wartet_auf_endzaehlung", "En attente du comptage final"), color: A.orange },
    auswertung: { label: t("kontrolle.status.auswertung", "Analyse"), color: A.purple },
    abgeschlossen: { label: t("kontrolle.status.abgeschlossen", "Terminé"), color: A.green },
    archiviert: { label: t("kontrolle.status.archiviert", "Archivé"), color: A.teal },
  };
  const KONTROLLE_REASON_META = {
    diebstahlverdacht: t("kontrolle.reason.diebstahlverdacht", "Suspicion de vol"),
    bestandsauffaelligkeit: t("kontrolle.reason.bestandsauffaelligkeit", "Anomalie de stock"),
    hoher_warenwert: t("kontrolle.reason.hoher_warenwert", "Valeur marchande élevée"),
    regelmaessige_kontrolle: t("kontrolle.reason.regelmaessige_kontrolle", "Contrôle régulier"),
    ungewoehnlich_hoher_verkauf: t("kontrolle.reason.ungewoehnlich_hoher_verkauf", "Vente anormalement élevée"),
    lieferkontrolle: t("kontrolle.reason.lieferkontrolle", "Contrôle de livraison"),
    sonstiges: t("kontrolle.reason.sonstiges", "Autre"),
  };
  // Cumul Differenz = somme des Differenz de tous les cycles déjà CLÔTURÉS (jamais le cycle
  // actif, encore incomplet) — même règle utilisée dans l'accordéon, l'export et le
  // formulaire papier, une seule source de vérité pour ce calcul.
  const kontrolleCumulDiff = (k) => k.cycles.filter((c) => c.status === "abgeschlossen" && c.differenz != null).reduce((s, c) => s + c.differenz, 0);
  // Teinte légère de la carte selon le vrai statut/résultat — jamais une couleur inventée
  // indépendamment des données : "Diebstahlverdacht" est toujours un signal fort, sinon la
  // couleur suit le Cumul Differenz réel (négatif=manque, positif=surplus inhabituel).
  const kontrolleTint = (k) => {
    if (k.archived) {
      const forceClosed = (k.history || []).some((h) => h.action === "FORCE_CLOSED");
      if (forceClosed) return "muted";
      const cd = kontrolleCumulDiff(k);
      return cd === 0 ? "good" : cd < 0 ? "bad" : "warn";
    }
    if (k.reason === "diebstahlverdacht") return "bad";
    const cd = kontrolleCumulDiff(k);
    if (cd < 0) return "bad";
    if (cd > 0) return "warn";
    return "active";
  };
  // Couleur + fond léger (~9-15% d'opacité) par teinte — mêmes hex que les couleurs
  // sémantiques déjà utilisées ailleurs dans l'app (A.red/A.orange/A.green/A.blue), pas une
  // palette inventée. Pas de color-mix() (support Safari/WKWebView pas garanti) : des
  // valeurs rgba() explicites, comme le reste du fichier (ex. FILIALE_STATUS_META).
  const KONTROLLE_TINT = {
    bad: { c: A.red, bg: "rgba(239,68,68,.09)", chipBg: "rgba(239,68,68,.13)" },
    warn: { c: A.orange, bg: "rgba(231,138,18,.10)", chipBg: "rgba(231,138,18,.15)" },
    good: { c: A.green, bg: "rgba(30,184,95,.09)", chipBg: "rgba(30,184,95,.13)" },
    active: { c: A.blue, bg: "rgba(54,79,231,.08)", chipBg: "rgba(54,79,231,.12)" },
    muted: { c: P.muted, bg: P.soft, chipBg: P.soft },
  };
  // Libellé de durée à partir du VRAI Startdatum du premier cycle — jamais deviné.
  const kontrolleDurationLabel = (k) => {
    const start = new Date((k.cycles[0]?.startAt || "").slice(0, 10));
    if (isNaN(start.getTime())) return "—";
    const end = k.archived && k.archivedAt ? new Date(k.archivedAt.slice(0, 10)) : new Date();
    const days = Math.max(0, Math.round((end - start) / 86400000));
    if (days < 14) return t("kontrolle.duration.daysTemplate", { n: days }, `${days} j`);
    if (days < 60) return t("kontrolle.duration.weeksTemplate", { n: Math.round(days / 7) }, `${Math.round(days / 7)} sem.`);
    return t("kontrolle.duration.monthsTemplate", { n: Math.round(days / 30) }, `${Math.round(days / 30)} mois`);
  };

  // Kühlung/Frischfleisch/Frischbrot/Trocken sont du vocabulaire métier allemand réel
  // (Norma), jamais traduit dans aucune langue (voir project_storeflow_overview) — seul
  // "Magasin" (désigne le rayon, pas un terme Norma) varie réellement d'une langue à
  // l'autre.
  const REDUCTION_CATEGORIES = [
    { id: "KUEHLUNG", label: t("reductions.catKuhlungCatalog", "Kühlung (Sortiment)"), emoji: "❄️", color: A.blue },
    { id: "KUEHLUNG_PROMO", label: t("reductions.catKuhlungPromo", "Kühlung (Aktion)"), emoji: "🏷️", color: A.blue },
    { id: "STORE", label: t("reductions.catStorePromo", "Magasin (Aktion)"), emoji: "🏷️", color: A.purple },
    { id: "FRISCHFLEISCH", label: t("reductions.catFrischfleischCatalog", "Frischfleisch (Sortiment)"), emoji: "🥩", color: A.red },
    { id: "FRISCHFLEISCH_PROMO", label: t("reductions.catFrischfleischPromo", "Frischfleisch (Aktion)"), emoji: "🏷️", color: A.red },
    { id: "FRISCHBROT_BROT", label: t("reductions.catFrischbrotBrot", "Frischbrot / Brot"), emoji: "🍞", color: "#b98b4e" },
    { id: "TROCKEN", label: t("reductions.catTrocken", "Trocken"), emoji: "🥫", color: "#c9a13b" },
  ];
  // Commandes de marchandises (nouveau module, 2026-08-07) — id = valeur EXACTE de zone
  // côté catalogue `articles` (voir ARTICLE_ZONES ci-dessus), pour rester la même donnée que
  // le Contrôle Kühlung/MHD sur les 4 catégories qui se recoupent (aucune re-saisie). Mêmes
  // couples emoji/couleur que REDUCTION_CATEGORIES/ARTICLE_ZONES pour rester cohérent
  // visuellement dans toute l'app ; "Obst & Gemüse"/"Tiefkühlung" sont les 2 seules zones
  // réellement nouvelles (aucun article pour l'instant, à remplir via l'éditeur d'article).
  const MERCH_CATEGORIES = [
    { id: "Kühlung", label: "Kühlung", emoji: "❄️", color: A.blue },
    { id: "Trocken", label: "Trocken", emoji: "🥫", color: "#c9a13b" },
    { id: "Obst & Gemüse", label: "Obst & Gemüse", emoji: "🥬", color: A.green },
    { id: "Tiefkühlung", label: "Tiefkühlung", emoji: "🧊", color: A.frost },
    { id: "Frisch Fleisch", label: "Frischfleisch", emoji: "🥩", color: A.red },
    { id: "Frisch Brot", label: "Frischbrot", emoji: "🍞", color: "#b98b4e" },
  ];
  const MERCH_UNITS = ["Karton", "Lage", "Düsseldorfer Palette", "Europalette"];
  // Lundi→Samedi, ordre "naturel" — l'écran article fait tourner ce tableau pour que le jour
  // actuel apparaisse toujours en premier (voir mergedWeekdayOrder plus bas).
  const MERCH_WEEKDAYS = [1, 2, 3, 4, 5, 6].map((d) => t(`haccp.dow.${d}`, ["Lu", "Ma", "Me", "Je", "Ve", "Sa"][d - 1]));
  const merchCategoryMeta = (zone) => MERCH_CATEGORIES.find((c) => c.id === zone) || { id: zone, label: zone, emoji: "📦", color: A.navy };
  const MERCH_STATUS_META = {
    brouillon: { label: t("merch.status.brouillon", "Brouillon"), color: P.muted, bg: P.soft },
    en_cours: { label: t("merch.status.enCours", "En cours"), color: A.orange, bg: "rgba(231,138,18,.13)" },
    terminee: { label: t("merch.status.terminee", "Terminée"), color: A.blue, bg: "rgba(54,79,231,.1)" },
    envoyee: { label: t("merch.status.envoyee", "Envoyée"), color: A.teal, bg: "rgba(13,148,136,.13)" },
    livree: { label: t("merch.status.livree", "Livrée"), color: A.green, bg: "rgba(30,184,95,.13)" },
    annulee: { label: t("merch.status.annulee", "Annulée"), color: A.red, bg: "rgba(239,68,68,.12)" },
  };
  // Fait tourner MERCH_WEEKDAYS pour que le jour d'aujourd'hui soit toujours en premier
  // (retour utilisateur explicite : "l'ordre doit tourner automatiquement afin que le jour
  // actuel soit toujours affiché en premier"). getDay() : 0=dimanche..6=samedi ; dimanche
  // (jamais un jour ouvré ici) retombe sur lundi en tête par convention.
  const merchWeekdayOrder = () => {
    const jsDay = new Date().getDay();
    const todayIndex = jsDay === 0 ? 0 : jsDay - 1;
    return [...MERCH_WEEKDAYS.slice(todayIndex), ...MERCH_WEEKDAYS.slice(0, todayIndex)];
  };
  // Retour utilisateur explicite : "on maintiendra que Karton pour toutes les catégories sauf
  // Trocken" — seule Trocken (denrées non périssables, vraiment commandées en gros volumes)
  // garde le choix des 4 unités ; les 5 autres catégories restent strictement Karton, quel
  // que soit unitesAutorisees en base (le champ catalogue reste géré par l'admin mais n'est
  // plus consulté ici, simplification demandée explicitement).
  const merchAllowedUnits = (zone) => zone === "Trocken" ? MERCH_UNITS : ["Karton"];
  // Mode démo (task séparée, 2026-08-13) — commandes fictives 100% en mémoire, jamais
  // écrites au serveur (l'app n'est pas encore branchée à de vraies données de vente).
  // Fixture Kühlung : chaque article porte un historique "Vendu" fictif jour par jour
  // (soldHistory) — abverkauf = moyenne RÉELLE de ces 6 valeurs, jamais un nombre isolé
  // choisi à part, pour que la simulation applique fidèlement "Bedarf = durchschnittlicher
  // Abverkauf × Faktor" (retour utilisateur explicite : "il doit se reposer sur le principe
  // du Faktor Bestellung", "façon d'apprendre à utiliser le module"). Les autres rayons
  // gardent le comportement manuel actuel (pas d'Abverkauf, comme en usage réel).
  const buildMerchDemoSession = (zone) => {
    // Kühlung (2026-10-15, retour utilisateur explicite : "utilise dans le mode démo la liste
    // kühlung ce sera plus explicite") — pioche dans le VRAI catalogue Kühlung déjà chargé
    // (articles, filtré zone Kühlung/actif) plutôt qu'une courte liste fictive codée en dur :
    // noms/Art.Nr/VE réels, seul l'historique "Vendu" (soldHistory/abverkauf) reste simulé,
    // comme avant — aucune donnée de vente réelle n'existe encore pour ce module. Repli sur
    // l'ancienne petite liste fictive si le catalogue n'est pas encore chargé (jamais un écran
    // vide).
    const realKuhlung = zone === "Kühlung"
      ? articles.filter((a) => a.zone === "Kühlung" && a.active !== false && !a.disabled && String(a.artNr || "").trim())
      : [];
    const bases = zone === "Kühlung"
      ? (realKuhlung.length
          ? [...realKuhlung].sort(() => Math.random() - 0.5).slice(0, 8).map((a) => ({
              name: a.name, artNr: a.artNr, ve: Number(a.ve) > 0 ? Number(a.ve) : 6, base: 4 + Math.floor(Math.random() * 12),
            }))
          : [{ name: "Frischkäse Natur 200g", ve: 6, base: 9 }, { name: "Butter 250g", ve: 12, base: 14 }, { name: "Joghurt Erdbeere 4x125g", ve: 8, base: 6 }, { name: "Schlagsahne 200ml", ve: 10, base: 3 }, { name: "Gouda Scheiben 150g", ve: 6, base: 11 }])
      : [{ name: `Article démo ${zone} 1`, ve: 6 }, { name: `Article démo ${zone} 2`, ve: 12 }, { name: `Article démo ${zone} 3`, ve: 8 }];
    const articlesOut = bases.map((n, i) => {
      let soldHistory = null;
      let abverkauf = null;
      if (n.base) {
        soldHistory = {};
        let sum = 0;
        MERCH_WEEKDAYS.forEach((day) => {
          const v = Math.max(0, Math.round(n.base * (0.7 + Math.random() * 0.6))); // ±30% autour de la base
          soldHistory[day] = v;
          sum += v;
        });
        abverkauf = Math.round((sum / MERCH_WEEKDAYS.length) * 10) / 10; // moyenne réelle, jamais la base directement
      }
      return {
        id: `demo-${zone}-${i}`, artNr: n.artNr || `9${String(i + 1).padStart(4, "0")}`, name: n.name,
        ve: n.ve, brand: "", price: null, uniteParDefaut: "Karton", unitesAutorisees: ["Karton"],
        abverkauf, soldHistory,
      };
    });
    return {
      articles: articlesOut, history: {},
      order: { id: "DEMO", storeId: user?.store || "", zone, deliveryDate: merchDeliveryDateInput, status: "brouillon", faktor: null, cursor: 0, lines: [] },
    };
  };
  const redCategoryStats = (id) => ({
    todo: redTodo.filter((r) => r.source === id).length,
    doneToday: redDoneToday.filter((d) => d.rec.source === id).length,
    expired: redExpired.filter((r) => r.source === id).length,
  });
  // Export PDF de la liste À traiter, groupée par catégorie dans l'ordre de REDUCTION_CATEGORIES
  // (même patron de portail que HACCP/AZL/Arbeitsplan/Infos/Rep/Label, voir clearOtherPrintPortals).
  // 2026-08-29 — même correctif que buildMhdExportPrintHtml (voir le commentaire
  // d'exportHtmlToMultiPagePdf) : un seul <div class="rpx-page"> avec toutes les catégories
  // produisait une page PDF géante à la hauteur du contenu entier, illisible dans l'aperçu
  // iOS. On aplatit toutes les lignes (toutes catégories confondues, ordre inchangé) puis on
  // découpe en pages de REDUCTIONS_ROWS_PER_PAGE lignes ; une catégorie qui déborde sur la
  // page suivante répète simplement son en-tête (comportement normal d'un rapport paginé).
  const REDUCTIONS_ROWS_PER_PAGE = 30;
  const buildReductionsPrintHtml = () => {
    const flat = [];
    REDUCTION_CATEGORIES.forEach((cat) => {
      const items = sortForCategory(redTodo.filter((r) => r.source === cat.id), cat.id);
      items.forEach((r) => flat.push({ cat, r, catTotal: items.length }));
    });
    const pageChunks = [];
    for (let i = 0; i < flat.length; i += REDUCTIONS_ROWS_PER_PAGE) pageChunks.push(flat.slice(i, i + REDUCTIONS_ROWS_PER_PAGE));
    if (pageChunks.length === 0) pageChunks.push([]);
    const rowHtml = (r) => {
      const dl = daysLeft(r.mhd);
      return `<tr><td>${haccpEscHtml(r.name)}</td><td>${r.artNr ? haccpEscHtml(String(r.artNr)) : ""}</td><td>${fmtFR(r.mhd)}</td><td>${dl === 0 ? t("reductionsExport.lastDay", "dernier jour") : `J-${dl}`}</td><td>${r.quantity != null && r.quantity !== "" ? haccpEscHtml(String(r.quantity)) : ""}</td></tr>`;
    };
    return pageChunks.map((chunk, pageIdx) => {
      let blocksHtml = "";
      let i = 0;
      while (i < chunk.length) {
        const cat = chunk[i].cat;
        const catTotal = chunk[i].catTotal;
        const rowsForCat = [];
        let j = i;
        while (j < chunk.length && chunk[j].cat === cat) { rowsForCat.push(chunk[j].r); j++; }
        blocksHtml += `<h2 class="rpx-cat">${cat.emoji} ${haccpEscHtml(cat.label)} <span class="rpx-count">(${catTotal})</span></h2>
          <table class="rpx-table"><thead><tr><th>${t("reductionsExport.article", "Article")}</th><th>${t("kontrolle.export.number", "Numéro")}</th><th>MHD</th><th>${t("reductionsExport.deadline", "Échéance")}</th><th>${t("reductionsExport.qty", "Qté")}</th></tr></thead><tbody>${rowsForCat.map(rowHtml).join("")}</tbody></table>`;
        i = j;
      }
      return `<div class="rpx-page">
        <header class="rpx-header"><div><p class="rpx-eyebrow">${t("reductionsExport.eyebrow", "Réductions")}</p><h1>${t("reductionsExport.title", "Liste des réductions à traiter")}</h1></div><div class="rpx-meta">${t("reductionsExport.storeTemplate", { store: user?.store || "" }, `Filiale ${user?.store || ""}`)} · ${fmtFR(todayISO())} · ${t("reductionsExport.articleCountTemplate", { n: redTodo.length }, `${redTodo.length} article${redTodo.length > 1 ? "s" : ""}`)}${pageChunks.length > 1 ? ` · ${t("common.pageOfTotalTemplate", { n: pageIdx + 1, total: pageChunks.length }, `Page ${pageIdx + 1}/${pageChunks.length}`)}` : ""}</div></header>
        ${blocksHtml || `<p class="rpx-empty">${t("reductionsExport.emptyText", "Aucun article à réduire pour l'instant.")}</p>`}
      </div>`;
    }).join("");
  };
  const exportReductionsPdf = async () => {
    try {
      await exportHtmlToMultiPagePdf({ bodyHtml: buildReductionsPrintHtml(), css: RPX_DOC_CSS, pageSelector: ".rpx-page", widthMm: 210, filename: `Reductions-${user?.store || ""}-${todayISO()}.pdf` });
    } catch (err) {
      if (err?.name !== "AbortError") showToast(err.message || t("err.actionImpossible", "Action impossible."));
    }
  };
  const markReduced = (rec) => {
    const optimistic = { recId: rec.id, doneId: null, doneAt: new Date().toISOString(), source: rec.source, artNr: rec.artNr, name: rec.name, mhd: rec.mhd, quantity: rec.quantity, employee: user?.firstname || "" };
    setReduced((ds) => [...ds, optimistic]);
    apiJSON("/api/done", {
      method: "POST",
      body: JSON.stringify({ recordId: rec.id, user: user?.firstname || "" }),
    })
      .then((done) => setReduced((ds) => ds.map((d) => d.recId === rec.id && !d.doneId ? { ...d, doneId: done.id, doneAt: done.doneAt, zone: done.zone, employee: done.user } : d)))
      .catch(() => setReduced((ds) => ds.filter((d) => d.recId !== rec.id)));
    showToast(t("toast.discountDoneTemplate", { name: rec.name }, `🏷️ ${rec.name} — réduction faite ✓`));
  };
  const undoReduced = (recId) => {
    const entry = reduced.find((d) => d.recId === recId);
    setReduced((ds) => ds.filter((d) => d.recId !== recId));
    if (entry?.doneId) apiJSON("/api/restore", { method: "POST", body: JSON.stringify({ recordId: entry.doneId }) }).catch(() => loadReduced());
    showToast(t("toast.discountCancelledBackToTodo", "Réduction annulée — l'article revient dans À traiter"));
  };
  /* Suppression d'un expiré = passage en corbeille, jamais une perte : tout est
     restaurable — vrai mécanisme générique PATCH /api/records/:id/trash (le même que
     storeRecords/storeTrash côté Contrôle, pas propre à Réductions). */
  // Fiche article — au clic depuis Réductions (toutes catégories) ou l'Historique DLC
  // Magasin, montre toutes les vraies infos de l'article (photo, EAN, Art.Nr, DLC,
  // quantité, note, provenance). Volontairement PAS dans Contrôle pour l'instant (portée
  // confirmée par l'utilisateur).
  const openArticleCard = (r, returnSheet = null) => { setArticleCardRecord(r); setArticleCardReturnSheet(returnSheet); setSheet("articleCard"); };
  // Retiré (2026-08-20, retour utilisateur explicite) : jusqu'ici uniquement appelable
  // depuis l'onglet Expirées (dropExpired ne dépendait d'aucun état "expiré" en interne,
  // juste d'un id présent dans redSources) — maintenant aussi bouton dans "À traiter", donc
  // le nom garde son historique mais la fonction gère déjà n'importe quel statut.
  const dropExpired = (recId) => {
    const rec = redSources.find((r) => r.id === recId);
    if (!rec) return;
    setRecords((rs) => rs.filter((r) => r.id !== recId));
    setKuhlungExpiredRemoved((rs) => rs.filter((r) => r.id !== recId));
    setStoreRecords((rs) => rs.filter((r) => r.id !== recId));
    setRedStoreRecords((rs) => rs.filter((r) => r.id !== recId));
    // czRecords/czExpiredRemoved (Trocken/Frisch Fleisch/Frisch Brot) manquaient ici — une
    // suppression sur ces catégories fonctionnait bien côté serveur mais l'article restait
    // affiché côté client jusqu'au prochain rechargement complet.
    setCzRecords((rs) => rs.filter((r) => r.id !== recId));
    setCzExpiredRemoved((rs) => rs.filter((r) => r.id !== recId));
    setRedTrash((t) => [{ ...rec, trashedAt: new Date().toISOString() }, ...t]);
    apiJSON(`/api/records/${recId}/trash`, { method: "PATCH" }).catch(() => { loadKuhlungRecords(); loadKuhlungExpiredRemoved(); loadStoreRecords(); loadRedStoreRecords(); loadCzRecords(); loadCzExpiredRemoved(); loadRedTrash(); });
    showToast(t("toast.movedToTrashRestorable", "⛔ Déplacé vers la corbeille — restaurable"));
  };
  const CZ_SOURCES = ["TROCKEN", "FRISCHFLEISCH", "FRISCHFLEISCH_PROMO", "FRISCHBROT_BROT"];
  const restoreFromTrash = (recId) => {
    const rec = redTrash.find((r) => r.id === recId);
    if (!rec) return;
    setRedTrash((t) => t.filter((r) => r.id !== recId));
    const { source, name, trashedAt, ...base } = rec;
    if (source === "STORE") {
      setStoreRecords((rs) => [{ ...base, name, source }, ...rs]); setRedStoreRecords((rs) => [{ ...base, name, source }, ...rs]);
    } else if (CZ_SOURCES.includes(source)) {
      // czRecords garde toujours la source BRUTE (jamais "_PROMO", un simple retag calculé
      // dans redSources) — restaurer avec "_PROMO" tel quel casserait le filtre `r.source
      // === czZone.source` des écrans Contrôle et `r.source === redCategory` de Réductions.
      const rawSource = source === "FRISCHFLEISCH_PROMO" ? "FRISCHFLEISCH" : source;
      setCzRecords((rs) => [...rs, { ...base, name, source: rawSource }]);
    } else {
      setRecords((rs) => [...rs, { ...base, name }]);
    }
    apiJSON(`/api/records/${recId}/restore`, { method: "PATCH" }).catch(() => { loadKuhlungRecords(); loadKuhlungExpiredRemoved(); loadStoreRecords(); loadRedStoreRecords(); loadCzRecords(); loadCzExpiredRemoved(); loadRedTrash(); });
    showToast(t("toast.itemRestoredTemplate", { name }, `↩︎ ${name} restauré`));
  };
  const deletePermanently = (recId) => {
    setRedTrash((t) => t.filter((r) => r.id !== recId));
    apiJSON(`/api/records/${recId}`, { method: "DELETE" }).catch(() => loadRedTrash());
    showToast(t("toast.deletedPermanently", "🗑️ Supprimé définitivement"));
  };
  // Vide la corbeille D'UNE SEULE catégorie — DELETE /api/records/trash est un vidage
  // global sans filtre (vérifié côté serveur), donc jamais appelable depuis un onglet
  // scopé à une catégorie sous peine de vider aussi l'autre catégorie sans le dire ;
  // suppression un par un via la route individuelle à la place.
  const emptyRedTrash = (category) => {
    const toDelete = redTrash.filter((r) => r.source === category);
    setRedTrash((t) => t.filter((r) => r.source !== category));
    toDelete.forEach((r) => apiJSON(`/api/records/${r.id}`, { method: "DELETE" }).catch(() => loadRedTrash()));
    showToast(t("toast.trashEmptiedPermanently", "Corbeille vidée définitivement"));
  };
  // Vide TOUTES les catégories — retour utilisateur explicite (2026-08-12) : la Corbeille
  // globale (bouton du hub Réductions) est maintenant une vraie vue toutes catégories, ce
  // bouton doit donc pouvoir tout vider d'un coup, pas juste une catégorie à la fois.
  const emptyAllRedTrash = () => {
    const toDelete = redTrash;
    setRedTrash([]);
    toDelete.forEach((r) => apiJSON(`/api/records/${r.id}`, { method: "DELETE" }).catch(() => loadRedTrash()));
    showToast(t("toast.trashEmptiedPermanently", "Corbeille vidée définitivement"));
  };
  // Résout un prénom choisi dans un formulaire (Planning/Tâches, pas d'id réel) vers un
  // vrai employeeId via la liste chargée depuis /api/tasks/store-employees — best-effort :
  // si aucune correspondance, l'action part sans destinataire précis plutôt que d'échouer.
  const resolveEmployeeId = (firstName) => {
    const found = taskEmployees.find((e) => e.name.split(" ")[0].toLowerCase() === String(firstName || "").toLowerCase());
    return found ? found.employeeId : "";
  };
  const resolveEmployeeName = (employeeId) => {
    const found = taskEmployees.find((e) => e.employeeId === employeeId);
    return found ? found.name.split(" ")[0] : employeeId;
  };
  // Équipe réelle de la filiale pour Planning/Vacation/Leistungsfrei/Aushilfe — dérivée de
  // taskEmployees (déjà chargé pour Tâches), jamais du TEAM factice (4 prénoms fixes,
  // encore utilisé ailleurs par Messages/Rôles, hors périmètre de cette migration). Les
  // vraies routes serveur ont besoin de l'employeeId (resolveEmployeeId ci-dessus) et du
  // vrai rôle canonique (planEmpRole), pas d'un prénom seul.
  const planTeam = taskEmployees.map((e) => e.name.split(" ")[0]);
  const planEmpRole = (firstName) => {
    const found = taskEmployees.find((e) => e.name.split(" ")[0].toLowerCase() === String(firstName || "").toLowerCase());
    return found ? found.role : "";
  };
  // Civilité abrégée (M./Mme, retour utilisateur 2026-08-04) — dérivée du champ gender déjà
  // existant côté compte (voir genderTitle plus haut, jusqu'ici seulement utilisé pour la
  // salutation Accueil). Pas de gender/lastname renseigné → repli sur le prénom seul, jamais
  // bloquant (même philosophie que genderTitle).
  const planEmpDisplayLabel = (firstName) => {
    const found = taskEmployees.find((e) => e.name.split(" ")[0].toLowerCase() === String(firstName || "").toLowerCase());
    if (!found) return firstName;
    const title = found.gender === "homme" ? t("common.titleMr", "M.") : found.gender === "femme" ? t("common.titleMrs", "Mme") : null;
    return title && found.lastname ? `${title} ${found.lastname}` : firstName;
  };

  /* Planning : plan de travail — vraies données, GET/POST /api/plannings (voir tâches
     migration #442 puis rattrapage #446-454). Le serveur ne connaît qu'un weekStart
     (lundi ISO), jamais un "offset" — mondayOfWeekOffset() fait la conversion à chaque
     appel API. */
  const mondayOfWeekOffset = (offset) => {
    const dow = new Date().getDay(); // 0=Dim..6=Sam
    const diffToMonday = dow === 0 ? -6 : 1 - dow;
    return addDaysISO(todayISO(), diffToMonday + offset * 7);
  };
  // Même conversion que mondayOfWeekOffset, mais ancrée sur une date ARBITRAIRE plutôt que
  // "aujourd'hui" — nécessaire pour la recherche d'un plan antérieur (Fiche filiale >
  // Planning, retour utilisateur explicite 2026-08-24) : le GR choisit n'importe quelle
  // date via un input[type=date], on résout toujours le lundi de SA semaine.
  const mondayOfDateISO = (dateISO) => {
    const dow = new Date(`${dateISO}T00:00:00`).getDay();
    const diffToMonday = dow === 0 ? -6 : 1 - dow;
    return addDaysISO(dateISO, diffToMonday);
  };
  // 8 vrais statuts (fidèle à SF_PLANNING_DAYS/shiftKind côté vanilla planning.js) — Arbeit
  // et BS+Arbeit gardent des horaires réels, 1/2 Frei aussi ; les autres n'en ont jamais.
  const SHIFT_STATUSES = ["Arbeit", "BS+Arbeit", "1/2 Frei", "Frei", "Urlaub", "Krank", "BS", "Schulung", "Krankheit Kind", "Unbezahlter Urlaub", "Sonderurlaub", "Mutterschutz", "Erziehungsurlaub"];
  const SHIFT_STATUS_LABELS = { "Arbeit": "Arbeit", "BS+Arbeit": t("planning.shiftStatus.bsArbeit", "École + Travail"), "1/2 Frei": "1/2 Frei", "Frei": "Frei", "Urlaub": "Urlaub", "Krank": "Krank", "BS": t("planning.shiftStatus.bs", "École (BS)"), "Schulung": "Schulung", "Krankheit Kind": "Krankheit Kind", "Unbezahlter Urlaub": "Unbezahlter Urlaub", "Sonderurlaub": "Sonderurlaub", "Mutterschutz": "Mutterschutz", "Erziehungsurlaub": "Erziehungsurlaub" };
  const shiftStatusKeepsHours = (status) => status === "Arbeit" || status === "BS+Arbeit" || status === "1/2 Frei";
  const weekShifts = useMemo(() => (weekPlan?.entries || [])
    .filter((e) => e.status && e.status !== "Leer")
    .map((e) => ({
      id: e.id, day: PLAN_DAY_KEYS.indexOf(e.day),
      emp: e.employeeName ? e.employeeName.split(" ")[0] : resolveEmployeeName(e.employeeId),
      employeeId: e.employeeId, role: e.role || "", status: e.status,
      start: e.start, end: e.end, pauseMin: e.pauseMinutes || 0, pause: e.pause || "", hours: e.hours,
      hoursOverride: (e.hoursOverride === null || e.hoursOverride === undefined) ? null : Number(e.hoursOverride),
      leistungsfreiHours: e.leistungsfreiHours || 0, note: e.note || "", relatedRequestId: e.relatedRequestId || null,
    }))
    .filter((s) => s.day !== -1), [weekPlan]);
  const isPublished = weekPlan?.status === "published";
  const planWeekReadOnly = isPublished && !planEditUnlocked;

  // Détection mineur réelle (date de naissance via /api/users, jamais simulée) — voir
  // isMinorEmployee côté vanilla planning.js. Sans date de naissance renseignée, retombe
  // sur le flag isMinor déclaré à l'inscription (planUsersIsMinorFlag) plutôt que de
  // supposer majeur par défaut — un vrai mineur sans date de naissance saisie doit quand
  // même déclencher les règles de pause/couvre-feu.
  const isMinorEmployeeId = (employeeId) => {
    const bdRaw = planUsersBirthdate[employeeId];
    if (!bdRaw) return Boolean(planUsersIsMinorFlag[employeeId]);
    const bd = new Date(String(bdRaw).slice(0, 10) + "T00:00:00");
    if (Number.isNaN(bd.getTime())) return Boolean(planUsersIsMinorFlag[employeeId]);
    const today = new Date();
    let age = today.getFullYear() - bd.getFullYear();
    const monthDiff = today.getMonth() - bd.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < bd.getDate())) age--;
    return age < 18;
  };
  // Règle Azubi réelle (2h travaillées = 1h comptée dans les totaux Leistung) — purement
  // client, jamais bloquant, restaurée fidèlement (vanilla azubiNetFactor) : je l'avais à
  // tort retirée en pensant qu'elle n'existait pas côté serveur, alors qu'elle existe bien
  // côté client, dans TOUT frontend qui consomme ce plan. BS+Arbeit ne compte JAMAIS, même
  // à 50% (heures après la Berufsschule, précision reçue après coup côté vanilla).
  const isAzubiRole = (role) => String(role || "").trim() === "Azubi";
  const azubiNetFactor = (entry, role) => entry?.status === "BS+Arbeit" ? 0 : (isAzubiRole(role) ? 0.5 : 1);
  // Rendu par statut (fidèle à shiftKind/shiftLabel côté vanilla) — pour "Arbeit", la
  // sous-couleur dépend de l'heure de début (Früh/Mitte/Spät) comme sur le vrai Arbeitsplan.
  const shiftStatusMeta = (s) => {
    if (!s || !s.status) return { label: "–", badge: "—", fg: P.muted, bg: P.soft };
    if (s.status !== "Arbeit") {
      const map = {
        "Frei": { label: "Frei", badge: "Libre", fg: P.muted, bg: P.soft },
        "Urlaub": { label: "Urlaub", badge: "Congé", fg: A.green, bg: `${A.green}1f` },
        "Krank": { label: "Krank", badge: "Maladie", fg: A.red, bg: `${A.red}1f` },
        "BS": { label: "BS", badge: "École", fg: A.teal, bg: `${A.teal}1f` },
        "BS+Arbeit": { label: "BS", badge: "École + Travail", fg: A.teal, bg: `${A.teal}1f` },
        "Schulung": { label: "Schulung", badge: "Formation", fg: A.teal, bg: `${A.teal}1f` },
        "1/2 Frei": { label: "1/2 Frei", badge: "Demi", fg: A.blue, bg: `${A.blue}1f` },
        "Krankheit Kind": { label: "Krankheit Kind", badge: "Enfant malade", fg: A.red, bg: `${A.red}1f` },
        "Unbezahlter Urlaub": { label: "Unbez. Urlaub", badge: "Congé non payé", fg: A.orange, bg: `${A.orange}1f` },
        "Sonderurlaub": { label: "Sonderurlaub", badge: "Congé spécial", fg: A.orange, bg: `${A.orange}1f` },
        "Mutterschutz": { label: "Mutterschutz", badge: "Maternité", fg: A.purple, bg: `${A.purple}1f` },
        "Erziehungsurlaub": { label: "Erziehungsurlaub", badge: "Congé parental", fg: A.purple, bg: `${A.purple}1f` },
      };
      return map[s.status] || { label: s.status, badge: "Spécial", fg: A.teal, bg: `${A.teal}1f` };
    }
    const hours = shiftHours(s);
    const start = s.start ? toMin(s.start) : null;
    const end = s.end ? toMin(s.end) : null;
    const compact = s.start && s.end ? `${s.start} – ${s.end}` : "Travail";
    if (hours > 0 && hours <= 5.25) return { label: compact, badge: t("planning.shift.short", "Court"), fg: A.teal, bg: `${A.teal}1f` };
    if (start === null) return { label: compact, badge: t("planning.shift.work", "Travail"), fg: A.green, bg: `${A.green}1f` };
    if (start <= 8 * 60) return { label: compact, badge: t("planning.shift.early", "Tôt"), fg: A.blue, bg: `${A.blue}1f` };
    if (start >= 11 * 60 || (end !== null && end >= 20 * 60)) return { label: compact, badge: t("planning.shift.late", "Tard"), fg: A.orange, bg: `${A.orange}1f` };
    return { label: compact, badge: t("planning.shift.mid", "Milieu"), fg: A.purple, bg: `${A.purple}1f` };
  };
  const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
  const shiftHours = (s) => s.hours ?? Math.max(0, Math.round(((toMin(s.end) - toMin(s.start) - (s.pauseMin || 0)) / 60) * 100) / 100);
  // Bug réel corrigé (retour utilisateur 2026-08-05) : plafonnait la pause mineur à 30 min
  // quelle que soit la durée — un mineur en shift du soir 8h (ex. 11h–20h) n'obtenait donc
  // que 30 min au lieu des 60 min légalement requis au-delà de 6h (JArbSchG §11). Palier
  // manquant ajouté, même structure que la règle majeur juste en dessous.
  const requiredBreak = (isMinor, hours) => isMinor
    ? (hours > 6 ? 60 : hours >= 4.5 ? 30 : 0)
    : (hours > 9 ? 45 : hours >= 6 ? 30 : 0);
  // Un mineur ne travaille jamais après 20h (retour utilisateur 2026-08-05, ex. shift du
  // soir 8h : 11h–20h avec 1h de pause hors temps de travail) — utilisé à la fois dans le
  // rappel live de la sheet créneau et dans weekCompliance (bloquant à la publication).
  const MINOR_LATEST_END_MIN = 20 * 60;
  // Pause = vraie plage horaire "HH:MM - HH:MM" côté serveur (sfPlanningPauseMinutes),
  // jamais juste une durée — même regex (points ou deux-points acceptés) pour rester
  // compatible avec les entrées créées côté vanilla.
  const minutesToTime = (total) => {
    const clamped = Math.max(0, Math.round(total)) % (24 * 60);
    return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
  };
  const parsePauseRange = (pauseStr) => {
    const m = String(pauseStr || "").replaceAll(".", ":").match(/(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/);
    if (!m) return null;
    return { start: `${m[1].padStart(2, "0")}:${m[2]}`, end: `${m[3].padStart(2, "0")}:${m[4]}` };
  };
  const pauseRangeLabel = (s) => {
    const r = parsePauseRange(s?.pause);
    if (r) return `${r.start}–${r.end}`;
    return (s?.pauseMin || 0) > 0 ? `${s.pauseMin} min` : "";
  };
  // Suggestion : pause centrée dans le créneau, arrondie au 1/4h le plus proche, toujours
  // à l'intérieur de [start, end] — juste un point de départ, modifiable par le manager.
  const suggestPauseStart = (start, end, pauseMin) => {
    if (!start || !end || !pauseMin) return "";
    const s = toMin(start);
    let e = toMin(end);
    if (e <= s) e += 24 * 60;
    const mid = s + (e - s - pauseMin) / 2;
    const snapped = Math.min(Math.max(Math.round(mid / 15) * 15, s), e - pauseMin);
    return minutesToTime(snapped);
  };
  // "Ist semaine" = total NET (hors Leistungsfrei, facteur Azubi appliqué, surcharge
  // manuelle par employé prioritaire si posée) — jamais planning.totals.hours, qui reste
  // le total BRUT (badge "Heures" en haut d'écran, inchangé).
  const weekNetTotal = useMemo(() => {
    const byEmployee = {};
    weekShifts.forEach((s) => {
      const net = Math.max((s.hours || 0) - (s.leistungsfreiHours || 0), 0) * azubiNetFactor(s, s.role);
      byEmployee[s.employeeId] = (byEmployee[s.employeeId] || 0) + net;
    });
    let total = 0;
    const seen = new Set();
    Object.entries(byEmployee).forEach(([empId, computed]) => {
      const override = weekPlan?.totalOverrides?.[empId];
      total += (typeof override === "number" && Number.isFinite(override)) ? override : computed;
      seen.add(empId);
    });
    Object.entries(weekPlan?.totalOverrides || {}).forEach(([empId, override]) => {
      if (seen.has(empId)) return;
      if (typeof override === "number" && Number.isFinite(override)) total += override;
    });
    return Math.round(total * 100) / 100;
  }, [weekShifts, weekPlan]);
  const weekTotalHours = weekNetTotal;
  // Soll semaine réel — GET /api/reporting/stunden-plan (gated reporting.view), somme des
  // 6 jours affichés, jours sans valeur saisie ignorés (jamais comptés comme 0 de force).
  const weekSollHours = useMemo(() => {
    let sum = 0;
    for (let i = 0; i < 6; i++) {
      const d = addDaysISO(mondayOfWeekOffset(planWeekOffset), i);
      if (stundenleistungCache[d] != null) sum += stundenleistungCache[d];
    }
    return Math.round(sum * 100) / 100;
  }, [stundenleistungCache, planWeekOffset]);
  const weekDiffHours = Math.round((weekTotalHours - weekSollHours) * 10) / 10;
  const weekCompliance = useMemo(() => {
    const violations = [];
    planTeam.forEach((emp) => {
      const empShifts = weekShifts
        .filter((s) => s.emp === emp && s.status === "Arbeit" && s.start && s.end)
        .sort((a, b) => a.day - b.day || a.start.localeCompare(b.start));
      const isMinor = isMinorEmployeeId(empShifts[0]?.employeeId);
      let prevEnd = null;
      empShifts.forEach((s) => {
        const hours = shiftHours(s);
        const minorTag = isMinor ? " (mineur)" : "";
        if (hours >= 10) {
          violations.push({ emp, day: s.day, type: "duration", msg: `${emp}${minorTag} · ${weekLabel(s.day)} : ${hours}h, au-delà de 10h/jour` });
        }
        const req = requiredBreak(isMinor, hours);
        if (req && (s.pauseMin || 0) < req) {
          violations.push({ emp, day: s.day, type: "break", msg: `${emp}${minorTag} · ${weekLabel(s.day)} : pause insuffisante (${req} min requises pour ${hours}h, ${s.pauseMin || 0} min prévues)` });
        }
        if (isMinor && toMin(s.end) > MINOR_LATEST_END_MIN) {
          violations.push({ emp, day: s.day, type: "minorCurfew", msg: `${emp}${minorTag} · ${weekLabel(s.day)} : termine à ${s.end}, un mineur ne doit pas travailler après 20h` });
        }
        if (prevEnd) {
          const restMin = (s.day - prevEnd.day) * 24 * 60 - prevEnd.minutes + toMin(s.start);
          if (restMin < 11 * 60) {
            violations.push({ emp, day: s.day, type: "rest", msg: `${emp}${minorTag} · repos de ${(restMin / 60).toFixed(1)}h avant ${weekLabel(s.day)}, en dessous de 11h` });
          }
        }
        prevEnd = { day: s.day, minutes: toMin(s.end) };
      });
    });
    return violations;
  }, [weekShifts, planTeam, planUsersBirthdate, planUsersIsMinorFlag]);
  const loadWeekPlan = (offset) => {
    setPublishForcePending(false);
    apiJSON(`/api/plannings?storeId=${encodeURIComponent(user?.store)}&weekStart=${mondayOfWeekOffset(offset)}`)
      .then((data) => setWeekPlan(data.planning || null))
      .catch(() => setWeekPlan(null));
  };
  const loadPublishedWeeks = () => {
    apiJSON(`/api/plannings/published?storeId=${encodeURIComponent(user?.store)}`)
      .then((data) => setPublishedWeeksList(data.plannings || []))
      .catch(() => {});
  };
  const loadPlanUsersBirthdate = () => {
    apiJSON("/api/users")
      .then((list) => {
        const bdMap = {}; const minorFlagMap = {};
        (Array.isArray(list) ? list : []).forEach((u) => {
          if (!u.employeeId) return;
          bdMap[u.employeeId] = u.birthdate || "";
          minorFlagMap[u.employeeId] = Boolean(u.isMinor);
        });
        setPlanUsersBirthdate(bdMap);
        setPlanUsersIsMinorFlag(minorFlagMap);
      })
      .catch(() => {});
  };
  const loadStundenleistungComparison = (offset) => {
    const days = [0, 1, 2, 3, 4, 5].map((i) => addDaysISO(mondayOfWeekOffset(offset), i));
    const months = Array.from(new Set(days.map((d) => d.slice(0, 7))));
    Promise.all(months.map((ym) => {
      const [year, month] = ym.split("-").map(Number);
      return apiJSON(`/api/reporting/stunden-plan?storeId=${encodeURIComponent(user?.store)}&year=${year}&month=${month}`).catch(() => null);
    })).then((results) => {
      const map = {};
      results.forEach((data) => { if (data?.days) Object.entries(data.days).forEach(([date, v]) => { map[date] = Number(v?.stundenPlan || 0); }); });
      setStundenleistungCache(map);
    });
  };
  const syncStundenleistung = () => {
    if (!weekPlan?.id) return;
    apiJSON(`/api/plannings/${weekPlan.id}/sync-stundenleistung`, { method: "POST" })
      .then((data) => {
        setSyncedHours((h) => ({ ...h, [planWeekOffset]: weekTotalHours }));
        loadStundenleistungComparison(planWeekOffset);
        showToast(t("toast.stundenleistungUpdatedTemplate", { n: data.updatedMonths?.length || 0 }, `🔄 Stundenleistung mis à jour (${data.updatedMonths?.length || 0} mois)`));
      })
      .catch((err) => showToast(err.message || t("err.syncImpossible", "Synchronisation impossible.")));
  };
  const savePlanning = (nextEntries, extra = {}) => apiJSON("/api/plannings", {
    method: "POST",
    body: JSON.stringify({
      storeId: user?.store, weekStart: mondayOfWeekOffset(planWeekOffset),
      status: "draft", notes: weekPlan?.notes || "",
      employees: taskEmployees.map((e) => ({ employeeId: e.employeeId, name: e.name, role: e.role })),
      totalOverrides: weekPlan?.totalOverrides || {},
      entries: nextEntries,
      createdBy: weekPlan?.createdBy, createdByRole: weekPlan?.createdByRole,
      updatedBy: user?.firstname || "Toi",
      ...extra,
    }),
  }).then((data) => { setWeekPlan(data.planning); return data; });
  const saveShift = () => {
    if (!shiftForm) return;
    const keepsHours = shiftStatusKeepsHours(shiftForm.status);
    if (keepsHours && (!shiftForm.start || !shiftForm.end || shiftForm.end <= shiftForm.start)) { showToast(t("toast.invalidHoursEndAfterStart", "Heures invalides — fin après début.")); return; }
    const employeeId = resolveEmployeeId(shiftForm.emp);
    if (!employeeId) { showToast(t("toast.chooseStoreEmployee", "Choisis un employé de la filiale.")); return; }
    const emp = taskEmployees.find((e) => e.employeeId === employeeId);
    // FL/FLA ne sont jamais bloqués par le solde LF crédité ici (retour utilisateur
    // explicite : "je ne peux pas mettre de leistungsfrei sur un compte FL ou FLA... ce
    // n'est pas normal") — même principe déjà retenu pour l'auto-application calendrier
    // (voir mémoire "FL/FLA peuvent s'auto-appliquer un Leistungsfrei à solde nul sans
    // négatif"), étendu ici au formulaire de créneau du Plan de travail : leur temps de
    // compensation n'est pas géré via le même système de crédit d'heures que le reste de
    // l'équipe. Un employé non-FL/FLA reste soumis au solde réellement crédité.
    const isFlOrFla = ["FL", "FLA"].includes(String(emp?.role || "").trim());
    const lf = Number(shiftForm.leistungsfreiHours) || 0;
    if (!isFlOrFla && lf > shiftLfHint.available + 0.004) {
      showToast(t("toast.leistungsfreiInsufficientTemplate", { requested: lf, available: shiftLfHint.available.toLocaleString("fr-FR") }, `Leistungsfrei insuffisant — ${lf}h demandées, solde disponible ${shiftLfHint.available.toLocaleString("fr-FR")}h.`));
      return;
    }
    const existingForVacation = (weekPlan?.entries || []).find((e) => e.employeeId === employeeId && e.day === PLAN_DAY_KEYS[shiftForm.day]);
    if (shiftForm.status === "Urlaub" && !existingForVacation?.relatedRequestId && 1 > shiftVacationHint.available + 0.004) {
      showToast(t("toast.vacationInsufficientTemplate", { available: shiftVacationHint.available.toLocaleString("fr-FR") }, `Congé insuffisant — solde disponible ${shiftVacationHint.available.toLocaleString("fr-FR")}j.`));
      return;
    }
    const dayKey = PLAN_DAY_KEYS[shiftForm.day];
    const relatedRequestId = shiftForm.status === "Urlaub" ? (existingForVacation?.relatedRequestId || null) : null;
    // Clé fonctionnelle réelle = (employeeId, day) — un seul créneau par employé et par
    // jour côté serveur, jamais un id client à part.
    const pauseMinutes = keepsHours ? (shiftForm.pauseMin || 0) : 0;
    const pauseStr = pauseMinutes && shiftForm.pauseStart
      ? `${shiftForm.pauseStart} - ${minutesToTime(toMin(shiftForm.pauseStart) + pauseMinutes)}`
      : "";
    const nextEntries = [
      ...(weekPlan?.entries || []).filter((e) => !(e.employeeId === employeeId && e.day === dayKey)),
      {
        employeeId, employeeName: emp?.name || shiftForm.emp, role: emp?.role || "", day: dayKey,
        status: shiftForm.status || "Arbeit",
        start: keepsHours ? shiftForm.start : "", end: keepsHours ? shiftForm.end : "",
        pause: pauseStr, pauseMinutes,
        leistungsfreiHours: lf, note: shiftForm.note || "", relatedRequestId,
        zuschlagspflichtig: Boolean(shiftForm.zuschlagspflichtig),
      },
    ];
    setSheet(null); setShiftForm(null);
    savePlanning(nextEntries)
      .then((data) => {
        showToast(t("toast.slotSavedPlanBackToDraft", "Créneau enregistré — plan repassé en brouillon"));
        if (data.availabilityWarnings?.length) showToast(t("toast.aushilfeConflictsTemplate", { n: data.availabilityWarnings.length }, `⚠️ ${data.availabilityWarnings.length} conflit(s) de disponibilité Aushilfe`));
        // L'enregistrement réconcilie automatiquement Leistungsfrei (réservation) et
        // Resturlaub (débit immédiat si Urlaub posé à la main) côté serveur — voir
        // sfLeistungsfreiReconcilePlan/sfVacationReconcileFromPlan. Recharger les soldes
        // ici pour que l'écran Resturlaub/Leistungsfrei ne reste jamais figé sur une
        // valeur périmée tant que l'utilisateur ne navigue pas manuellement dessus.
        loadLfAccounts();
        if (canManageVacation) loadVacationAccounts();
      })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  const deleteShift = () => {
    if (!shiftForm?.id) { setSheet(null); setShiftForm(null); return; }
    const nextEntries = (weekPlan?.entries || []).filter((e) => e.id !== shiftForm.id);
    setSheet(null); setShiftForm(null);
    savePlanning(nextEntries)
      .then(() => {
        showToast(t("toast.slotDeleted", "Créneau supprimé"));
        loadLfAccounts();
        if (canManageVacation) loadVacationAccounts();
      })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  // Rôles autorisés à publier — même liste que vanilla PLANNING_PUBLISH_ROLES (le serveur
  // reste le vrai garde-fou via la permission dynamique planning.publish, ceci n'est qu'un
  // blocage informatif côté client, cohérent avec ce que voit réellement l'utilisateur).
  // NEXORA_ADMIN ajouté explicitement (retour utilisateur 2026-07-31 : bouton "+" absent
  // sur une semaine déjà publiée une fois connecté avec le nouveau compte admin réel) —
  // ce rôle n'existait pas encore quand cette liste a été écrite ; server.js le fait déjà
  // passer TOUJOURS (requireDynamicPermission : isNexoraAdminRole → next() sans vérifier
  // aucune permission), donc le bloquer côté client ici était devenu une incohérence, pas
  // un vrai garde-fou.
  const canPublishPlanning = () => ["FL", "FLA", "NEXORA_ADMIN"].includes(String(user?.role || "").trim());
  const publishWeek = () => {
    if (!weekPlan?.id) return;
    if (!canPublishPlanning()) { showToast(t("toast.onlyFlFlaCanPublish", "Seuls les rôles FL ou FLA peuvent publier ce planning.")); return; }
    if (weekCompliance.length > 0) { showToast(t("toast.fixLegalAlertsBeforePublish", "Corrige les alertes légales ci-dessus avant de publier.")); return; }
    apiJSON(`/api/plannings/${weekPlan.id}/publish`, { method: "POST" })
      .then((data) => {
        setWeekPlan(data.planning);
        setPublishForcePending(false);
        loadPublishedWeeks();
        // Publier encaisse les réservations Leistungsfrei en "usage" (sfLeistungsfreiReconcilePlan)
        // — le solde disponible ne change pas visuellement mais l'historique si, et une
        // publication peut aussi avoir ajouté/retiré des jours Urlaub entre-temps.
        loadLfAccounts();
        if (canManageVacation) loadVacationAccounts();
        showToast(t("toast.planPublishedToTeamTemplate", { kw: (data.planning.weekKey || "").split("-W")[1] || "" }, `📢 Plan KW ${(data.planning.weekKey || "").split("-W")[1] || ""} publié à l'équipe`));
      })
      .catch((err) => {
        const warnings = err.data?.availabilityWarnings;
        if (Array.isArray(warnings) && warnings.length) {
          setPublishForcePending(true);
          showToast(t("toast.publishBlockedAushilfeTemplate", { n: warnings.length }, `Publication bloquée — ${warnings.length} Aushilfe hors disponibilité. Tape à nouveau pour forcer.`));
          return;
        }
        showToast(err.message || t("err.publishingImpossible", "Publication impossible."));
      });
  };
  // Publication forcée malgré les dépassements de disponibilité Aushilfe (porte de sortie
  // explicite, jamais automatique) — même principe que vanilla publishPlanning() catch.
  const publishWeekForced = () => {
    if (!weekPlan?.id) return;
    apiJSON(`/api/plannings/${weekPlan.id}/publish?force=true`, { method: "POST", body: JSON.stringify({ force: true }) })
      .then((data) => {
        setWeekPlan(data.planning);
        setPublishForcePending(false);
        loadPublishedWeeks();
        loadLfAccounts();
        if (canManageVacation) loadVacationAccounts();
        showToast(t("toast.planPublishedDespiteAvailability", "📢 Plan publié malgré les dépassements de disponibilité."));
      })
      .catch((err) => showToast(err.message || t("err.publishingImpossible", "Publication impossible.")));
  };
  const openPublishedWeek = (weekStart) => {
    const thisMonday = mondayOfWeekOffset(0);
    const offset = Math.round((new Date(weekStart) - new Date(thisMonday)) / (7 * 86400000));
    setPlanWeekOffset(offset); setPlanScreen("week");
  };
  // "Supprimer" un plan publié = le dépublier (retour à l'état brouillon) — jamais une
  // vraie suppression des créneaux déjà saisis, voir le commentaire serveur sur
  // /api/plannings/:id/unpublish (endpoint déjà existant, jamais câblé côté client jusqu'ici,
  // retour utilisateur 2026-08-05). Il redevient éditable/re-publiable depuis "Plan de
  // travail", et disparaît de cette liste d'historique. Les 3 clés i18n unpublish* étaient
  // déjà traduites dans les 5 langues mais orphelines (aucun appelant) — réutilisées ici
  // plutôt que d'en inventer de nouvelles.
  const deletePublishedWeek = (h) => {
    if (!window.confirm(t("planning.published.unpublishConfirm", { week: h.weekKey || h.weekStart }, `Retirer ce planning de l'archive ? Il redevient un brouillon — les créneaux sont conservés, tu le retrouveras dans le plan de travail de la semaine ${h.weekKey || h.weekStart} et pourras le republier depuis là.`))) return;
    apiJSON(`/api/plannings/${h.id}/unpublish`, { method: "POST" })
      .then(() => {
        setPublishedWeeksList((list) => list.filter((x) => x.id !== h.id));
        showToast(t("planning.published.unpublishedToast", "Repassé en brouillon."));
      })
      .catch((err) => showToast(err.message || t("err.publishingImpossible", "Publication impossible.")));
  };
  const resetWeekPlanning = () => {
    // Retour utilisateur 2026-08-09 : "vider le planning" ne doit JAMAIS toucher un
    // Krankmeldung ou un Urlaub déjà posé dans la grille — un vidage complet effaçait ces
    // jours (le Krank revenait ensuite via la réconciliation serveur, mais l'Urlaub, lui,
    // disparaissait pour de bon et son solde était remboursé à tort). On ne repart jamais
    // de zéro : seuls les créneaux qui ne sont ni Krank ni Urlaub sont vidés.
    const preserved = (weekPlan?.entries || []).filter((e) => e.status === "Krank" || e.status === "Urlaub");
    savePlanning(preserved, { totalOverrides: {} })
      .then(() => {
        showToast(t("toast.planningCleared", "Planning vidé"));
        loadLfAccounts();
        if (canManageVacation) loadVacationAccounts();
      })
      .catch((err) => showToast(err.message || t("err.resetImpossible", "Réinitialisation impossible.")));
  };
  const copyPreviousWeek = () => {
    apiJSON("/api/plannings/copy-previous", {
      method: "POST",
      body: JSON.stringify({ storeId: user?.store, weekStart: mondayOfWeekOffset(planWeekOffset), updatedBy: user?.firstname || "Toi", createdBy: user?.firstname || "Toi", createdByRole: user?.role || "" }),
    })
      .then((data) => {
        setWeekPlan(data.planning);
        showToast(t("toast.previousWeekCopied", "Semaine précédente copiée"));
        loadLfAccounts();
        if (canManageVacation) loadVacationAccounts();
      })
      .catch((err) => showToast(err.message || t("err.copyPreviousWeekImpossible", "Impossible de copier la semaine précédente.")));
  };
  const setTotalOverride = (employeeId, value) => {
    const totalOverrides = { ...(weekPlan?.totalOverrides || {}) };
    if (value === null) delete totalOverrides[employeeId];
    else totalOverrides[employeeId] = value;
    savePlanning(weekPlan?.entries || [], { totalOverrides }).catch((err) => showToast(err.message || t("err.savingErrorShort", "Erreur enregistrement.")));
  };
  // Hiérarchie d'affichage réelle du planning (fidèle à PLANNING_ROLE_ORDER/
  // planningRoleRank côté vanilla planning.js:56-64) : FL, FLA, Stellvertreter/in,
  // Kassierer/in, Azubi, EQJ, Aushilfe — un rôle non listé passe en dernier. Les rôles
  // d'encadrement supérieur (Bereichsleiter/Verkaufsleiter/Niederlassungsleiter) ne sont
  // jamais des employés du magasin et ne doivent jamais apparaître dans un plan de travail.
  const PLANNING_EXCLUDED_ROLES = ["Bereichsleiter", "Verkaufsleiter", "Niederlassungsleiter"];
  const isPlanningEligibleRole = (role) => !PLANNING_EXCLUDED_ROLES.includes(String(role || "").trim());
  const PLANNING_ROLE_ORDER = ["FL", "FLA", "Stellvertreter", "Stellvertreterin", "Kassierer", "Kassiererin", "Azubi", "EQJ", "Aushilfe"];
  const planningRoleRank = (role) => { const i = PLANNING_ROLE_ORDER.indexOf(String(role || "").trim()); return i === -1 ? PLANNING_ROLE_ORDER.length : i; };
  // Réordonnancement de l'équipe (glisser-déposer sur la poignée ⠿, vue grille) —
  // employeeOrder persisté sur le plan, prioritaire sur l'ordre naturel, mais seulement pour
  // les employés déjà connus de cet ordre : un employé pas encore glissé une fois atterrit à
  // sa place naturelle (triée par rôle), jamais en fin de liste par défaut (fidèle à
  // getRoster/enableRosterDrag, vanilla planning.js:359-376). Adapté à une grille CSS (pas un
  // <table>) : pas de transform pixel-perfect suivant le doigt, la ligne saute directement à
  // sa nouvelle position (le flux "auto" de la grille se charge du réarrangement des
  // voisines) — reste fidèle à la fonctionnalité réelle (ordre persisté) sans reproduire la
  // mécanique exacte de mesure de <tr> du vanilla, non applicable ici.
  const orderedPlanTeam = useMemo(() => {
    const roleSorted = planTeam
      .filter((emp) => isPlanningEligibleRole(planEmpRole(emp)))
      .slice()
      .sort((a, b) => {
        const rankDiff = planningRoleRank(planEmpRole(a)) - planningRoleRank(planEmpRole(b));
        return rankDiff !== 0 ? rankDiff : a.localeCompare(b, "fr");
      });
    const order = weekPlan?.employeeOrder;
    if (!Array.isArray(order) || !order.length) return roleSorted;
    const rank = new Map(order.map((id, i) => [id, i]));
    return [...roleSorted].sort((a, b) => {
      const ra = rank.has(resolveEmployeeId(a)) ? rank.get(resolveEmployeeId(a)) : Infinity;
      const rb = rank.has(resolveEmployeeId(b)) ? rank.get(resolveEmployeeId(b)) : Infinity;
      return ra - rb;
    });
  }, [planTeam, weekPlan]);
  // Réordonnancement équipe — RECONSTRUIT (2026-07-31) : l'ancienne version utilisait un
  // glisser-déposer tactile (touch-action:none + setPointerCapture, dans une ligne posée
  // sur un conteneur overflow-x:auto) — combinaison documentée comme cause réelle de
  // plantages moteur sur WebKit (conflit de reconnaissance de geste scroll vs glisser),
  // confirmée en conditions réelles sur iPhone en PWA installée (plantage systématique,
  // uniquement cette vue, jamais reproduit ailleurs — 4 correctifs ciblés successifs sans
  // effet avant cette reconstruction, voir memory storeflow_plan_de_travail_crash_local_storage).
  // Remplacé par de simples boutons ↑/↓ par ligne — zéro geste tactile personnalisé, zéro
  // surface de conflit avec le défilement horizontal.
  const moveEmployeeInOrder = (emp, direction) => {
    const list = orderedPlanTeam;
    const curIdx = list.indexOf(emp);
    if (curIdx === -1) return;
    const targetIdx = curIdx + direction;
    if (targetIdx < 0 || targetIdx >= list.length) return;
    const next = [...list];
    [next[curIdx], next[targetIdx]] = [next[targetIdx], next[curIdx]];
    const finalOrder = next.map((e) => resolveEmployeeId(e)).filter(Boolean);
    savePlanning(weekPlan?.entries || [], { employeeOrder: finalOrder }).catch((err) => showToast(err.message || t("err.savingErrorShort", "Erreur enregistrement.")));
  };
  // Glisser-déposer restauré (2026-08-04), reconstruit pour éviter précisément le pattern
  // documenté comme cause réelle du crash iOS (setPointerCapture + touchAction:none posés
  // sur une ligne elle-même DESCENDANTE d'un conteneur overflow-x:auto — conflit de
  // reconnaissance de geste horizontal, voir memory storeflow_plan_de_travail_crash_local_storage).
  // La poignée vit maintenant dans une colonne noms SÉPARÉE, jamais imbriquée dans le
  // conteneur qui défile horizontalement (celui-ci ne porte plus aucun geste personnalisé).
  // Réordonnancement par seuil d'index (la ligne saute de case en case, pas de suivi
  // pixel-perfect), throttlé en rAF — même philosophie déjà retenue pour ↑/↓.
  const PLAN_GRID_ROW_H = 62;
  const PLAN_GRID_GAP = 5;
  const PLAN_GRID_HEADER_H = 21;
  const PLAN_GRID_FOOTER_H = 20;
  const PLAN_GRID_STEP = PLAN_GRID_ROW_H + PLAN_GRID_GAP;
  const [dragRowState, setDragRowState] = useState(null);
  const dragRafRef = useRef(null);
  const startRowDrag = (emp, e) => {
    if (planWeekReadOnly) return;
    const startIndex = orderedPlanTeam.indexOf(emp);
    if (startIndex === -1) return;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {}
    setDragRowState({ emp, startY: e.clientY, startIndex, currentIndex: startIndex });
  };
  const moveRowDrag = (e) => {
    if (!dragRowState) return;
    if (dragRafRef.current) return;
    const clientY = e.clientY;
    dragRafRef.current = requestAnimationFrame(() => {
      dragRafRef.current = null;
      setDragRowState((s) => {
        if (!s) return s;
        const deltaSlots = Math.round((clientY - s.startY) / PLAN_GRID_STEP);
        const nextIndex = Math.max(0, Math.min(orderedPlanTeam.length - 1, s.startIndex + deltaSlots));
        return nextIndex === s.currentIndex ? s : { ...s, currentIndex: nextIndex };
      });
    });
  };
  const endRowDrag = () => {
    if (!dragRowState) return;
    const { emp, startIndex, currentIndex } = dragRowState;
    setDragRowState(null);
    if (dragRafRef.current) { cancelAnimationFrame(dragRafRef.current); dragRafRef.current = null; }
    if (currentIndex === startIndex) return;
    const next = orderedPlanTeam.slice();
    next.splice(startIndex, 1);
    next.splice(currentIndex, 0, emp);
    const finalOrder = next.map((e2) => resolveEmployeeId(e2)).filter(Boolean);
    savePlanning(weekPlan?.entries || [], { employeeOrder: finalOrder }).catch((err) => showToast(err.message || t("err.savingErrorShort", "Erreur enregistrement.")));
  };
  // Ordre visuel pendant le glisser (preview live, jamais persisté avant relâchement du doigt)
  const dragPreviewTeam = useMemo(() => {
    if (!dragRowState || dragRowState.currentIndex === dragRowState.startIndex) return orderedPlanTeam;
    const next = orderedPlanTeam.slice();
    const [moved] = next.splice(dragRowState.startIndex, 1);
    next.splice(dragRowState.currentIndex, 0, moved);
    return next;
  }, [orderedPlanTeam, dragRowState]);
  // Export/impression Arbeitsplan — même patron que HACCP/MHD (portail #wpPrintRoot,
  // jamais window.open). Construit à partir des données déjà en state (roster ordonné,
  // weekShifts, weekPlan), fidèle à la structure du vrai document (renderPrintWorkplan
  // côté vanilla) : employé×jour, colonne L.F., colonne Total (surcharge comprise).
  // Légende — mêmes couleurs que shiftStatusMeta (celles réellement utilisées dans le
  // tableau), jamais une palette séparée qui pourrait diverger.
  const ARBEITSPLAN_LEGEND = [
    { label: "Früh (avant 8h)", color: A.blue },
    { label: "Mitte", color: A.purple },
    { label: "Spät (après 11h / fin après 20h)", color: A.orange },
    { label: "Kurz (≤ 5h15) / École / Formation", color: A.teal },
    { label: "Arbeit / Urlaub", color: A.green },
    { label: "Krank", color: A.red },
    { label: "1/2 Frei", color: A.blue },
    { label: "Frei / Sans créneau", color: P.muted },
  ];
  const buildArbeitsplanPrintHtml = () => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const monday = mondayOfWeekOffset(planWeekOffset);
    const weekKey = weekPlan?.weekKey || `KW${isoWeek(now) + planWeekOffset}`;
    const weekNumber = (weekKey || "").split("-W")[1] || weekKey;
    const dateRangeLabel = `${fmtFR(monday)} – ${fmtFR(addDaysISO(monday, 5))}`;
    const headCells = WEEK_LABELS_SHORT.map((_, i) => `<th>${esc(weekLabelShort(i))}<br/><small>${esc(fmtFR(addDaysISO(monday, i)).slice(0, 5))}</small></th>`).join("");
    let weekNetTotalPrint = 0;
    const dayNetTotals = [0, 0, 0, 0, 0, 0];
    const rows = orderedPlanTeam.map((emp) => {
      const empId = resolveEmployeeId(emp);
      const role = planEmpRole(emp);
      const empShifts = weekShifts.filter((s) => s.emp === emp);
      let totalNet = 0, lfTotal = 0;
      const dayCells = WEEK_LABELS.map((_, day) => {
        const s = empShifts.find((x) => x.day === day);
        if (!s) return `<td class="wp-free"></td>`;
        const net = Math.max((s.hours || 0) - (s.leistungsfreiHours || 0), 0) * azubiNetFactor(s, role);
        totalNet += net; dayNetTotals[day] += net;
        if (s.leistungsfreiHours) lfTotal += s.leistungsfreiHours;
        const meta = shiftStatusMeta(s);
        const timeLine = shiftStatusKeepsHours(s.status) && s.start ? `${s.start}–${s.end}` : (SHIFT_STATUS_LABELS[s.status] || s.status);
        const pauseLine = shiftStatusKeepsHours(s.status) && pauseRangeLabel(s) ? `P ${pauseRangeLabel(s)}` : "";
        const hoursText = s.hours ? `${s.hours}h` : "";
        const lfDayText = s.leistungsfreiHours ? `LF ${s.leistungsfreiHours}h` : "";
        // Retour utilisateur 2026-08-06 : le facteur Azubi ×0,5 n'apparaissait jamais sur
        // le PDF imprimé, seulement dans l'app en direct — même hint "→Xh Ist" qu'en ligne.
        const azubiIstText = shiftStatusKeepsHours(s.status) && isAzubiRole(role) && s.status !== "BS+Arbeit"
          ? `→${Math.round(shiftHours(s) * 0.5 * 100) / 100}h Ist` : "";
        const hoursRow = (hoursText || lfDayText || azubiIstText) ? `<span class="wp-hours-row">${hoursText ? `<span class="wp-hours">${esc(hoursText)}</span>` : ""}${azubiIstText ? `<span class="wp-azubi-badge">${esc(azubiIstText)}</span>` : ""}${lfDayText ? `<span class="wp-lf-badge">${esc(lfDayText)}</span>` : ""}</span>` : "";
        return `<td class="wp-cell" style="background:${meta.bg};color:${meta.fg};"><span class="wp-time">${esc(timeLine)}</span>${pauseLine ? `<span class="wp-break">${esc(pauseLine)}</span>` : ""}${hoursRow}</td>`;
      }).join("");
      const override = weekPlan?.totalOverrides?.[empId];
      const totalDisplay = (typeof override === "number" && Number.isFinite(override)) ? override : totalNet;
      weekNetTotalPrint += totalDisplay;
      return `<tr><th><small>${esc(role)}</small>${esc(planEmpDisplayLabel(emp))}</th>${dayCells}<td class="wp-lf">${lfTotal ? `${lfTotal.toLocaleString("fr-FR")}h` : "–"}</td><td class="wp-total">${Math.round(totalDisplay * 100) / 100}h</td></tr>`;
    }).join("");
    const footCells = dayNetTotals.map((v) => `<td>${Math.round(v * 100) / 100}h</td>`).join("");
    const createdLine = weekPlan?.createdBy
      ? `Créé par ${esc(weekPlan.createdBy)}${weekPlan.createdByRole ? ` (${esc(weekPlan.createdByRole)})` : ""}${weekPlan.createdAt ? ` · ${esc(fmtFR(weekPlan.createdAt.slice(0, 10)))} ${esc(weekPlan.createdAt.slice(11, 16))}` : ""}`
      : "";
    const legendHtml = ARBEITSPLAN_LEGEND.map((l) => `<span class="wp-legend-item"><i class="wp-legend-swatch" style="background:${l.color};"></i>${esc(l.label)}</span>`).join("");
    const diffSign = weekDiffHours >= 0 ? "+" : "";
    const density = orderedPlanTeam.length > 15 ? "xl" : orderedPlanTeam.length > 11 ? "lg" : "normal";
    return `<div class="wp-page" data-density="${density}">
      <header class="wp-header">
        <div><p class="wp-eyebrow">ARBEITSPLAN FILIALE ${esc(user?.store)}</p><h1>Semaine ${esc(weekNumber)}</h1></div>
        <div class="wp-meta"><span class="wp-badge ${weekPlan?.status === "published" ? "published" : ""}">${weekPlan?.status === "published" ? "PUBLIÉ" : "BROUILLON"}</span><span>${esc(dateRangeLabel)}</span></div>
      </header>
      <div class="wp-summary">
        <div class="wp-summary-card" style="background:${A.blue}12;border:1px solid ${A.blue}40;"><strong style="color:${A.blue};">${weekSollHours}h</strong><span>Soll semaine</span></div>
        <div class="wp-summary-card" style="background:${A.frost}12;border:1px solid ${A.frost}40;"><strong style="color:${A.frost};">${weekTotalHours}h</strong><span>Ist semaine</span></div>
        <div class="wp-summary-card" style="background:${weekDiffHours >= 0 ? A.green : A.red}12;border:1px solid ${weekDiffHours >= 0 ? A.green : A.red}40;"><strong style="color:${weekDiffHours >= 0 ? A.green : A.red};">${diffSign}${weekDiffHours}h</strong><span>Differenz (Ist − Soll)</span></div>
      </div>
      <div class="wp-legend">${legendHtml}</div>
      <table class="wp-table">
        <thead><tr><th>Mitarbeiter / Funktion</th>${headCells}<th>L.F.</th><th>Gesamt</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr><th>Stundenverbrauch</th>${footCells}<td></td><td class="wp-total">${Math.round(weekNetTotalPrint * 100) / 100}h</td></tr></tfoot>
      </table>
      <p class="wp-rules">
        Règles NORMA : plan à afficher au plus tard le vendredi de la semaine précédente · modèle par équipes obligatoire, repos ≥ 11h entre deux créneaux ·
        journée &lt; 10h ; pause 30 min dès 6h de travail (45 min au-delà de 9h), mineur : 30 min dès 4h30 (60 min au-delà de 6h), jamais après 20h ·
        Azubi = heures comptées ×0,5 dans le Ist (sauf École + Travail, jamais compté) ·
        grossesse (info) : journée ≤ 8,5h, jamais avant 6h ni après 20h, ≤ 90h par quinzaine, jamais un service isolé entre deux jours de repos.
      </p>
      <p class="wp-footer-note">${createdLine}</p>
    </div>`;
  };
  const exportArbeitsplan = async () => {
    try {
      await exportHtmlToPdf({ bodyHtml: buildArbeitsplanPrintHtml(), css: WP_DOC_CSS, targetSelector: ".wp-page", widthMm: 297, filename: `Arbeitsplan-${user?.store}.pdf` });
    } catch (err) {
      if (err?.name !== "AbortError") showToast(err.message || t("err.actionImpossible", "Action impossible."));
    }
  };
  // Fiche filiale — rubrique Planning : impression d'un plan CONSULTÉ (pas édité) pour une
  // filiale qui n'est pas forcément celle du GR connecté. Volontairement indépendant de
  // buildArbeitsplanPrintHtml (qui lit weekPlan/planWeekOffset/orderedPlanTeam — tous liés
  // à L'ÉDITEUR de la propre filiale de l'utilisateur connecté) : réutiliser cette fonction
  // telle quelle aurait imprimé le plan de la MAUVAISE filiale. Réutilise en revanche telles
  // quelles les classes CSS .wp-* déjà déclarées globalement (même portail #wpPrintRoot) —
  // seul le balisage HTML est reconstruit, à partir du plan explicitement passé en argument.
  // Volontairement NON traduite (i18n Groupe 2, chantier print/export) — même document
  // physique que buildArbeitsplanPrintHtml (même state buildé/consommé via
  // setArbeitsplanPrintHtml, mêmes classes CSS .wp-page/.wp-table, voir commentaire
  // "Jamais traduit (document physique)" sur buildHaccpPrintHtml qui documente
  // explicitement cette même règle pour l'Arbeitsplan) — juste la variante "Pilotage
  // réseau" (consultation d'une autre filiale) du même document NORMA à mise en page fixe.
  const buildPilotPlanningPrintHtml = (plan, storeId) => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const monday = plan.weekStart;
    const weekNumber = (plan.weekKey || "").split("-W")[1] || plan.weekKey || "";
    const dateRangeLabel = `${fmtFR(monday)} – ${fmtFR(addDaysISO(monday, 5))}`;
    const headCells = WEEK_LABELS_SHORT.map((_, i) => `<th>${esc(weekLabelShort(i))}<br/><small>${esc(fmtFR(addDaysISO(monday, i)).slice(0, 5))}</small></th>`).join("");
    const employees = (plan.employees || []).slice().sort((a, b) => a.name.localeCompare(b.name, "de"));
    const rows = employees.map((emp) => {
      const empEntries = (plan.entries || []).filter((e) => e.employeeId === emp.employeeId);
      let totalHours = 0;
      const dayCells = PLAN_DAY_KEYS.map((dayKey) => {
        const e = empEntries.find((x) => x.day === dayKey);
        if (!e || e.status === "Leer") return `<td class="wp-free"></td>`;
        totalHours += Number(e.hours || 0);
        const meta = shiftStatusMeta(e);
        const timeLine = e.status === "Arbeit" && e.start ? `${e.start}–${e.end}` : (SHIFT_STATUS_LABELS[e.status] || e.status);
        const azubiIstText = e.status === "Arbeit" && isAzubiRole(emp.role) ? `<span class="wp-azubi-badge">→${Math.round(Number(e.hours || 0) * 0.5 * 100) / 100}h Ist</span>` : "";
        const hoursText = (e.hours || azubiIstText) ? `<span class="wp-hours-row">${e.hours ? `<span class="wp-hours">${esc(e.hours)}h</span>` : ""}${azubiIstText}</span>` : "";
        return `<td class="wp-cell" style="background:${meta.bg};color:${meta.fg};"><span class="wp-time">${esc(timeLine)}</span>${hoursText}</td>`;
      }).join("");
      const override = plan.totalOverrides?.[emp.employeeId];
      const totalDisplay = (typeof override === "number" && Number.isFinite(override)) ? override : totalHours;
      return `<tr><th>${esc(emp.name)}<small>${esc(emp.role)}</small></th>${dayCells}<td class="wp-total">${Math.round(totalDisplay * 100) / 100}h</td></tr>`;
    }).join("");
    return `<div class="wp-page">
      <header class="wp-header">
        <div><p class="wp-eyebrow">ARBEITSPLAN FILIALE ${esc(storeId)}</p><h1>Semaine ${esc(weekNumber)}</h1></div>
        <div class="wp-meta"><span class="wp-badge ${plan.status === "published" ? "published" : ""}">${plan.status === "published" ? "PUBLIÉ" : "BROUILLON"}</span><span>${esc(dateRangeLabel)}</span></div>
      </header>
      <table class="wp-table">
        <thead><tr><th>Mitarbeiter / Funktion</th>${headCells}<th>Gesamt</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p class="wp-footer-note">Consulté depuis Pilotage réseau — ${esc(plan.totals?.employees ?? employees.length)} employé(s), ${esc(plan.totals?.hours ?? 0)}h au total.</p>
    </div>`;
  };
  const exportPilotPlanningWeek = async (plan, storeId) => {
    try {
      await exportHtmlToPdf({ bodyHtml: buildPilotPlanningPrintHtml(plan, storeId), css: WP_DOC_CSS, targetSelector: ".wp-page", widthMm: 297, filename: `Arbeitsplan-${storeId}.pdf` });
    } catch (err) {
      if (err?.name !== "AbortError") showToast(err.message || t("err.actionImpossible", "Action impossible."));
    }
  };
  // Rapport PLU Quiz complet de la semaine en cours, filiale de la Fiche filiale (retour
  // utilisateur explicite) — recharge tout (jamais les 8 dernières tentatives déjà
  // affichées, tronquées) puis filtre sur le lundi de la semaine ISO en cours, même calcul
  // que côté serveur (sfPlanningMonday) pour rester cohérent avec le badge "minimum atteint".
  // Réutilise printReportWithTitle/repPrintHtml, même portail que Inventur/Reporting —
  // jamais un nouveau #xxxPrintRoot pour un simple tableau de plus.
  const exportPilotPluWeekly = () => {
    if (!pilotFilialeId) return;
    const weekStart = pilotPluWeekly?.weekStart || mondayOfWeekOffset(0);
    apiJSON(`/api/plu-quiz/attempts?storeId=${encodeURIComponent(pilotFilialeId)}`)
      .then((data) => {
        const weekAttempts = (data.attempts || [])
          .filter((a) => a.finishedAt && a.finishedAt.slice(0, 10) >= weekStart)
          .sort((a, b) => String(b.finishedAt).localeCompare(String(a.finishedAt)));
        const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
        const rows = weekAttempts.map((a) => `<tr><td style="text-align:left;">${esc(a.employeeName)}</td><td>${esc(a.role)}</td><td>${fmtFR((a.finishedAt || "").slice(0, 10))}</td><td>${a.correctCount}/${a.totalQuestions}</td><td>${a.percentage}%</td><td>${a.passed ? "Réussi" : "Non réussi"}</td></tr>`).join("");
        const passCount = weekAttempts.filter((a) => a.passed).length;
        const minimum = pilotPluWeekly?.minimum ?? 3;
        const met = weekAttempts.length >= minimum;
        const html = `<div class="rep-page">
          <header class="rep-header">
            <div><p class="rep-eyebrow">NEXORA · PLU Quiz</p><h1>Rapport hebdomadaire — Filiale ${esc(pilotFilialeId)}</h1></div>
            <span class="rep-badge">Semaine du ${fmtFR(weekStart)}</span>
          </header>
          <p class="rep-footer-note">${weekAttempts.length}/${minimum} examen(s) passé(s) — ${passCount} réussi(s) — minimum ${met ? "atteint" : "non atteint"}</p>
          <table class="rep-table"><thead><tr><th>Employé</th><th>Rôle</th><th>Date</th><th>Score</th><th>%</th><th>Statut</th></tr></thead><tbody>${rows || `<tr><td colspan="6" style="text-align:center;color:#888;">Aucun examen cette semaine.</td></tr>`}</tbody></table>
          <p class="rep-footer-note">Document généré le ${fmtFR(todayISO())} — ${esc(user?.firstname || "")} ${esc(user?.lastname || "")}</p>
        </div>`;
        printReportWithTitle(html, `Rapport PLU Quiz - Filiale ${pilotFilialeId} - Semaine du ${weekStart}`);
      })
      .catch(() => showToast(t("deliveryNotes.actionImpossible", "Action impossible.")));
  };
  // Fiche détaillée d'UN test PLU (retour utilisateur explicite, priorisé devant le rapport
  // hebdomadaire agrégé ci-dessus) — toutes les infos possibles : identité/filiale/date/KW,
  // score+%, décompte correct/incorrect/sans réponse, durée utilisée/allouée, chrono dépassé,
  // le détail question par question (answers[], voir finishQuiz) et la signature si présente.
  // Même portail printReportWithTitle/.rep-page/.rep-table que exportPilotPluWeekly — aucune
  // fetch réseau ici, l'attempt passé en argument porte déjà tout (GET /api/plu-quiz/attempts
  // renvoie l'objet complet, jamais tronqué côté serveur pour un manager).
  const buildPluAttemptPrintHtml = (a) => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const resultLabel = { correct: t("pluQuiz.attempt.correct", "Correct"), incorrect: t("pluQuiz.attempt.incorrect", "Incorrect"), unanswered: t("pluQuiz.attempt.unanswered", "Sans réponse") };
    const rows = (a.answers || []).map((q, i) => `<tr>
      <td>${i + 1}</td>
      <td style="text-align:left;" title="${esc(q.category)}">${esc(q.category)}</td>
      <td style="text-align:left;" title="${esc(q.article)}">${esc(q.article)}</td>
      <td>${esc(q.plu)}</td>
      <td style="text-align:left;" title="${esc(q.givenAnswer ?? "—")}">${esc(q.givenAnswer ?? "—")}</td>
      <td style="color:${q.result === "correct" ? "#1b8a4c" : q.result === "incorrect" ? "#c22" : "#888"};font-weight:700;">${resultLabel[q.result] || esc(q.result)}</td>
    </tr>`).join("");
    const mm = (sec) => `${Math.floor((sec || 0) / 60)}min${String((sec || 0) % 60).padStart(2, "0")}`;
    // Retour utilisateur explicite (2026-08-12) : tout le test + résultat sur UNE seule
    // page, comme le vrai document papier — jamais plusieurs pages. En-tête et méta
    // compactés sur une seule ligne dense, table de détail en .rep-plu-table (voir CSS,
    // colonnes largeur fixe + nowrap/ellipsis), signature réduite.
    return `<div class="rep-page">
      <header class="rep-header" style="margin-bottom:8px;padding-bottom:5px;">
        <div><p class="rep-eyebrow" style="margin:0;">NEXORA · PLU Quiz</p><h1 style="font-size:15px;margin:2px 0 0;">${esc(a.employeeName)} — ${t("pluQuiz.attempt.examTitle", "Examen PLU")}</h1></div>
        <span class="rep-badge" style="background:${a.passed ? "#1b8a4c" : "#c22"};">${a.passed ? t("pluQuiz.recent.passed", "Réussi") : t("pluQuiz.recent.failed", "Non réussi")} — ${a.percentage}%</span>
      </header>
      <table class="rep-table" style="margin-bottom:8px;font-size:9.5px;">
        <tbody>
          <tr>
            <td style="text-align:left;padding:3px 6px;"><b>${t("pluQuiz.attempt.employee", "Employé")}</b> ${esc(a.employeeName)} — ${esc(a.role)}</td>
            <td style="text-align:left;padding:3px 6px;"><b>${t("pluQuiz.attempt.store", "Filiale")}</b> ${esc(a.storeId)}</td>
            <td style="text-align:left;padding:3px 6px;"><b>${t("pluQuiz.attempt.date", "Date")}</b> ${fmtFR((a.finishedAt || "").slice(0, 10))} (${esc(a.kw ? `KW${a.kw}` : "—")})</td>
          </tr>
          <tr>
            <td style="text-align:left;padding:3px 6px;"><b>${t("pluQuiz.attempt.score", "Score")}</b> ${a.correctCount}/${a.totalQuestions} (${t("pluQuiz.attempt.correct", "Correct")}: ${a.correctCount} · ${t("pluQuiz.attempt.incorrect", "Incorrect")}: ${a.incorrectCount} · ${t("pluQuiz.attempt.unanswered", "Sans réponse")}: ${a.unansweredCount})</td>
            <td colspan="2" style="text-align:left;padding:3px 6px;"><b>${t("pluQuiz.attempt.duration", "Durée")}</b> ${mm(a.durationUsedSec)} / ${mm(a.durationAllowedSec)}${a.timedOut ? ` — ${t("pluQuiz.attempt.timedOut", "chrono dépassé")}` : ""}</td>
          </tr>
        </tbody>
      </table>
      <span style="display:block;font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:#888;margin:0 0 4px;">${t("pluQuiz.attempt.detailTitle", "Détail question par question")}</span>
      <table class="rep-plu-table">
        <colgroup><col style="width:3%;"/><col style="width:16%;"/><col style="width:30%;"/><col style="width:8%;"/><col style="width:25%;"/><col style="width:18%;"/></colgroup>
        <thead><tr><th>#</th><th>${t("pluQuiz.attempt.category", "Catégorie")}</th><th>${t("pluQuiz.attempt.article", "Article")}</th><th>PLU</th><th>${t("pluQuiz.attempt.givenAnswer", "Réponse donnée")}</th><th>${t("pluQuiz.attempt.result", "Résultat")}</th></tr></thead>
        <tbody>${rows || `<tr><td colspan="6" style="text-align:center;color:#888;">—</td></tr>`}</tbody>
      </table>
      <div style="display:flex;align-items:center;justify-content:space-between;gap:14px;">
        ${a.signature?.dataUrl ? `<div><span style="display:block;font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:#888;margin-bottom:3px;">${t("pluQuiz.attempt.signature", "Signature")}</span><img src="${a.signature.dataUrl}" style="max-width:140px;max-height:44px;border:1px solid #ddd;border-radius:4px;" /></div>` : "<div></div>"}
        <p class="rep-footer-note" style="margin:0;">${t("pluQuiz.attempt.generatedOn", "Document généré le")} ${fmtFR(todayISO())} — ${esc(user?.firstname || "")} ${esc(user?.lastname || "")}</p>
      </div>
    </div>`;
  };
  const printPluAttempt = (a) => {
    printReportWithTitle(buildPluAttemptPrintHtml(a), `PLU ${a.employeeName} - ${a.storeId} - ${(a.finishedAt || "").slice(0, 10)}`);
  };
  const openPilotPlanningWeek = (weekStart) => {
    if (pilotPlanningDetail?.weekStart === weekStart) { setPilotPlanningDetail(null); return; }
    setPilotPlanningDetailLoading(true);
    apiJSON(`/api/plannings?storeId=${encodeURIComponent(pilotFilialeId)}&weekStart=${encodeURIComponent(weekStart)}`)
      .then((data) => setPilotPlanningDetail(data.planning))
      .catch(() => setPilotPlanningDetail(null))
      .finally(() => setPilotPlanningDetailLoading(false));
  };
  // Recherche d'un plan antérieur (Fiche filiale > Planning) — n'importe quelle date de la
  // semaine visée fait charger SON plan (lundi calculé côté client), ajouté à
  // pilotPlanningWeeks s'il n'y figure pas déjà, puis ouvert directement (même détail
  // dépliable que la semaine la plus récente, voir openPilotPlanningWeek ci-dessus).
  const searchOlderPilotPlanningWeek = () => {
    if (!pilotPlanningSearchDate || !pilotFilialeId) return;
    const weekStart = mondayOfDateISO(pilotPlanningSearchDate);
    if (pilotPlanningWeeks.some((w) => w.weekStart === weekStart)) { openPilotPlanningWeek(weekStart); return; }
    setPilotPlanningSearchBusy(true);
    apiJSON(`/api/plannings?storeId=${encodeURIComponent(pilotFilialeId)}&weekStart=${encodeURIComponent(weekStart)}`)
      .then((data) => {
        if (!data.planning) { showToast(t("toast.noPlanningForWeek", "Aucun plan trouvé pour cette semaine.")); return; }
        const p = data.planning;
        setPilotPlanningWeeks((list) => [...list, { id: p.id, weekStart: p.weekStart, weekKey: p.weekKey, publishedAt: p.publishedAt || null, totals: p.totals, status: p.status, isCurrent: false }]);
        setPilotPlanningDetail(p);
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setPilotPlanningSearchBusy(false));
  };
  // Solde Leistungsfrei/congés en direct — availableForEdit = solde actuel + ce qui est
  // déjà réservé pour CETTE cellule (sinon rouvrir la même case bloquerait à tort une
  // modification valide). Voir updateShiftLfBalanceHint/updateShiftVacationBalanceHint
  // côté vanilla — jamais mis en cache entre deux ouvertures de la sheet.
  const openShiftSheet = (employeeId, dayIdx) => {
    const emp = taskEmployees.find((e) => e.employeeId === employeeId);
    if (!emp) return;
    const dayKey = PLAN_DAY_KEYS[dayIdx];
    const entry = (weekPlan?.entries || []).find((e) => e.employeeId === employeeId && e.day === dayKey);
    const entryPauseMin = entry?.pauseMinutes || 0;
    const parsedPause = parsePauseRange(entry?.pause);
    setShiftForm({
      id: entry?.id || null, employeeId, emp: emp.name.split(" ")[0], day: dayIdx,
      status: entry?.status || "Arbeit", start: entry?.start || "", end: entry?.end || "",
      pauseMin: entryPauseMin,
      pauseStart: parsedPause?.start || (entryPauseMin ? suggestPauseStart(entry?.start, entry?.end, entryPauseMin) : ""),
      leistungsfreiHours: entry?.leistungsfreiHours || 0, note: entry?.note || "",
      zuschlagspflichtig: Boolean(entry?.zuschlagspflichtig),
    });
    setShiftLfHint({ text: "Solde LF disponible : …", available: Infinity });
    apiJSON("/api/leistungsfrei/accounts")
      .then((data) => {
        const acc = (data.accounts || []).find((a) => a.employeeId === employeeId);
        const available = Math.round(((acc ? Number(acc.availableHours || 0) : 0) + Number(entry?.leistungsfreiHours || 0)) * 100) / 100;
        setShiftLfHint({ text: `Solde LF disponible : ${available.toLocaleString("fr-FR")} h`, available });
      })
      .catch(() => setShiftLfHint({ text: "Solde LF disponible : indisponible (vérifié à l'enregistrement)", available: Infinity }));
    const originalVacation = (entry?.status === "Urlaub" && !entry?.relatedRequestId) ? 1 : 0;
    setShiftVacationHint({ text: "Solde congés disponible : …", available: Infinity });
    apiJSON("/api/vacation/accounts")
      .then((data) => {
        const acc = (data.accounts || []).find((a) => a.employeeId === employeeId);
        const available = Math.round(((acc ? Math.max(Number(acc.creditedDays || 0) - Number(acc.usedDays || 0), 0) : 0) + originalVacation) * 100) / 100;
        setShiftVacationHint({ text: `Solde congés disponible : ${available.toLocaleString("fr-FR")} j`, available });
      })
      .catch(() => setShiftVacationHint({ text: "Solde congés disponible : indisponible (vérifié à l'enregistrement)", available: Infinity }));
    setSheet("shift");
  };
  // Un jour où l'Aushilfe a déclaré indisponible, ou une case déjà marquée Krank (retour
  // utilisateur 2026-08-09 : une case malade ne doit plus s'ouvrir au tap normal, pour éviter
  // une modification accidentelle d'un arrêt maladie déclaré), ne s'ouvre plus au premier tap —
  // il faut taper deux fois de suite (fenêtre 450ms) pour confirmer explicitement (vanilla
  // handleShiftCellTap).
  const shiftTapRef = useRef({ key: "", timer: null });
  // Triple-tap FL/FLA (2026-08-17, retour utilisateur explicite : pouvoir forcer à la main
  // n'importe quelle donnée d'une case — heures, pause, début/fin, LF — "sans que l'app ne
  // la prenne comme une erreur et la corrige"). Réservé à FL/FLA et seulement sur une case
  // déjà remplie (rien à forcer sur une case vide) — pour ne changer AUCUN comportement pour
  // les autres rôles ou les cases vides, le tap simple normal (openShiftSheet immédiat, ou
  // le double-tap Krank/indisponibilité ci-dessous, inchangés) n'est retardé que dans ce cas
  // précis, le temps de voir si un 2e/3e tap arrive.
  const shiftTripleTapRef = useRef({ key: "", count: 0, timer: null });
  const handleShiftCellTap = (employeeId, dayIdx) => {
    const emp = taskEmployees.find((e) => e.employeeId === employeeId);
    const dayKey = PLAN_DAY_KEYS[dayIdx];
    const existing = weekShifts.find((x) => x.employeeId === employeeId && x.day === dayIdx);
    const isKrank = existing?.status === "Krank";

    const runNormalTap = () => {
      if (!isKrank && (!emp || !isUnavailable(emp.name.split(" ")[0], dayIdx))) { openShiftSheet(employeeId, dayIdx); return; }
      const tapKey = `${employeeId}|${dayKey}`;
      if (shiftTapRef.current.key === tapKey) {
        clearTimeout(shiftTapRef.current.timer);
        shiftTapRef.current = { key: "", timer: null };
        openShiftSheet(employeeId, dayIdx);
        return;
      }
      clearTimeout(shiftTapRef.current.timer);
      const timer = setTimeout(() => { shiftTapRef.current = { key: "", timer: null }; }, 450);
      shiftTapRef.current = { key: tapKey, timer };
      showToast(isKrank
        ? t("toast.krankCellTapAgainToEdit", "Case malade — tape à nouveau pour modifier")
        : t("toast.notAvailableTapAgainAnyway", "Non disponible — tape à nouveau pour planifier quand même"));
    };

    if (isFlOrFlaRole && existing) {
      const tripleKey = `${employeeId}|${dayKey}`;
      const ref = shiftTripleTapRef.current;
      const count = ref.key === tripleKey && ref.timer ? ref.count + 1 : 1;
      clearTimeout(ref.timer);
      if (count >= 3) {
        shiftTripleTapRef.current = { key: "", count: 0, timer: null };
        openShiftOverridePrompt(existing);
        return;
      }
      const timer = setTimeout(() => { shiftTripleTapRef.current = { key: "", count: 0, timer: null }; runNormalTap(); }, 260);
      shiftTripleTapRef.current = { key: tripleKey, count, timer };
      return;
    }

    runNormalTap();
  };
  // Même garde double-tap, pour l'entrée déjà affichée en vue Jours (qui appelait
  // openShiftSheet directement sans passer par handleShiftCellTap).
  const handleShiftEntryTap = (s) => {
    if (s.status !== "Krank") { openShiftSheet(s.employeeId, s.day); return; }
    const tapKey = `${s.employeeId}|${PLAN_DAY_KEYS[s.day]}`;
    if (shiftTapRef.current.key === tapKey) {
      clearTimeout(shiftTapRef.current.timer);
      shiftTapRef.current = { key: "", timer: null };
      openShiftSheet(s.employeeId, s.day);
      return;
    }
    clearTimeout(shiftTapRef.current.timer);
    const timer = setTimeout(() => { shiftTapRef.current = { key: "", timer: null }; }, 450);
    shiftTapRef.current = { key: tapKey, timer };
    showToast(t("toast.krankCellTapAgainToEdit", "Case malade — tape à nouveau pour modifier"));
  };
  // Double-tap sur la case Total (comme le double-tap indisponibilité ci-dessus) pour
  // saisir une surcharge manuelle du total hebdomadaire d'un employé (vanilla
  // promptTotalOverride/handleTotalCellTap) — mobile n'a pas de vrai dblclick fiable.
  const totalTapRef = useRef({ key: "", timer: null });
  const handleTotalCellTap = (employeeId, computedValue) => {
    const tapKey = employeeId;
    if (totalTapRef.current.key === tapKey) {
      clearTimeout(totalTapRef.current.timer);
      totalTapRef.current = { key: "", timer: null };
      const current = weekPlan?.totalOverrides?.[employeeId];
      const input = window.prompt(`Total manuel (en heures) — laisser vide pour revenir au calcul automatique (${computedValue.toLocaleString("fr-FR")}h)`, typeof current === "number" ? String(current).replace(".", ",") : "");
      if (input === null) return;
      const trimmed = input.trim();
      if (trimmed === "") { setTotalOverride(employeeId, null); return; }
      const parsed = Number(trimmed.replace(",", "."));
      if (!Number.isFinite(parsed) || parsed < 0) { showToast(t("toast.invalidValue", "Valeur invalide.")); return; }
      setTotalOverride(employeeId, Math.round(parsed * 100) / 100);
      return;
    }
    clearTimeout(totalTapRef.current.timer);
    const timer = setTimeout(() => { totalTapRef.current = { key: "", timer: null }; }, 450);
    totalTapRef.current = { key: tapKey, timer };
  };
  // Éditeur de surcharge manuelle (triple-tap FL/FLA, voir handleShiftCellTap) — même
  // logique de prompts séquentiels que handleTotalCellTap (déjà le langage UX établi de
  // l'app pour "forcer une valeur manuellement" ; vide = revenir au calcul automatique).
  // "Heures comptées" est demandé/affiché dans le résultat FINAL déjà visible sur la case
  // (donc déjà ×0,5 pour un Azubi) — jamais la valeur brute — puis reconverti en interne
  // vers l'équivalent brut stocké (s.hoursOverride), pour que shiftHours()/le Total hebdo
  // (qui réappliquent le facteur Azubi sur s.hours) retombent exactement sur la valeur
  // demandée sans double comptage. Annuler (Échap/Annuler) à n'importe quelle étape
  // abandonne tout, sans rien enregistrer.
  const openShiftOverridePrompt = (s) => {
    const keepsHours = shiftStatusKeepsHours(s.status);
    const isAzubiFactored = isAzubiRole(s.role) && s.status !== "BS+Arbeit";
    const factor = isAzubiFactored ? 0.5 : 1;
    let start = s.start || "", end = s.end || "", pause = pauseRangeLabel(s) || "";
    let hoursOverride = s.hoursOverride;
    let lf = s.leistungsfreiHours || 0;

    if (keepsHours) {
      const startInput = window.prompt("Heure de début (HH:MM)", start);
      if (startInput === null) return;
      if (/^\d{1,2}:\d{2}$/.test(startInput.trim())) start = startInput.trim();

      const endInput = window.prompt("Heure de fin (HH:MM)", end);
      if (endInput === null) return;
      if (/^\d{1,2}:\d{2}$/.test(endInput.trim())) end = endInput.trim();

      const pauseInput = window.prompt('Pause (ex. "12:00 - 12:30") — laisser vide pour aucune pause', pause);
      if (pauseInput === null) return;
      pause = pauseInput.trim();

      const currentDisplay = Math.round(shiftHours(s) * factor * 100) / 100;
      const prefillHours = hoursOverride != null ? Math.round(hoursOverride * factor * 100) / 100 : currentDisplay;
      const hoursInput = window.prompt(`Heures comptées — laisser vide pour revenir au calcul automatique (${currentDisplay.toLocaleString("fr-FR")}h)`, hoursOverride != null ? String(prefillHours).replace(".", ",") : "");
      if (hoursInput === null) return;
      const trimmedHours = hoursInput.trim();
      if (trimmedHours === "") { hoursOverride = null; }
      else {
        const parsed = Number(trimmedHours.replace(",", "."));
        if (!Number.isFinite(parsed) || parsed < 0) { showToast(t("toast.invalidValue", "Valeur invalide.")); return; }
        hoursOverride = Math.round((parsed / factor) * 100) / 100;
      }
    }

    const lfInput = window.prompt("Leistungsfrei (heures) — laisser vide pour 0", lf ? String(lf).replace(".", ",") : "");
    if (lfInput === null) return;
    const trimmedLf = lfInput.trim();
    if (trimmedLf === "") { lf = 0; }
    else {
      const parsedLf = Number(trimmedLf.replace(",", "."));
      if (!Number.isFinite(parsedLf) || parsedLf < 0) { showToast(t("toast.invalidValue", "Valeur invalide.")); return; }
      lf = Math.round(parsedLf * 100) / 100;
    }

    const pauseParsed = parsePauseRange(pause);
    const dayKey = PLAN_DAY_KEYS[s.day];
    const nextEntries = (weekPlan?.entries || []).map((e) => {
      if (e.employeeId !== s.employeeId || e.day !== dayKey) return e;
      const pauseMinutes = !keepsHours ? e.pauseMinutes : (pause === "" ? 0 : (pauseParsed ? Math.max(0, toMin(pauseParsed.end) - toMin(pauseParsed.start)) : e.pauseMinutes));
      return {
        ...e,
        start: keepsHours ? start : e.start,
        end: keepsHours ? end : e.end,
        pause: keepsHours ? pause : e.pause,
        pauseMinutes,
        hoursOverride: keepsHours ? hoursOverride : e.hoursOverride,
        leistungsfreiHours: lf,
      };
    });
    savePlanning(nextEntries)
      .then(() => {
        showToast(t("toast.manualOverrideSaved", "Case forcée manuellement — enregistrée"));
        loadLfAccounts();
      })
      .catch((err) => showToast(err.message || t("err.savingErrorShort", "Erreur enregistrement.")));
  };

  /* Calendrier annuel — congés Urlaub approuvés, vraie vue calendrier mensuelle (grille
     Lu→Di) avec tous les employés superposés, pas un résumé par employé. */
  const yearApproved = useMemo(() => planRequests.filter((r) =>
    r.type === "urlaub" && r.status === "approved" && !r.deleted &&
    (new Date(r.startDate).getFullYear() === calYear || new Date(r.endDate).getFullYear() === calYear)
  ), [planRequests, calYear]);
  const changeCalMonth = (delta) => {
    let m = calMonth + delta, y = calYear;
    if (m < 0) { m = 11; y -= 1; } else if (m > 11) { m = 0; y += 1; }
    setCalMonth(m); setCalYear(y);
  };
  // Généralisé (year/month en paramètres, pas seulement calYear/calMonth) — réutilisé pour
  // les 12 mini-calendriers de la vue Année, pas seulement la grille pleine de la vue Mois.
  const monthGridDays = (year, month) => {
    const first = new Date(year, month, 1);
    const startOffset = (first.getDay() + 6) % 7; // lundi = 0
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = new Array(startOffset).fill(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  };
  const dateISO = (year, month, day) => `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const calMonthDays = useMemo(() => monthGridDays(calYear, calMonth), [calYear, calMonth]);
  const calDayISO = (day) => dateISO(calYear, calMonth, day);
  const vacationsOnDate = (iso) => yearApproved.filter((r) => r.startDate <= iso && r.endDate >= iso);
  const vacationsOnDay = (day) => day ? vacationsOnDate(calDayISO(day)) : [];
  const monthHasVacation = (year, month, day) => day ? vacationsOnDate(dateISO(year, month, day)).length > 0 : false;
  const calMonthApproved = useMemo(() => {
    const first = calDayISO(1);
    const last = calDayISO(new Date(calYear, calMonth + 1, 0).getDate());
    return yearApproved.filter((r) => r.startDate <= last && r.endDate >= first).sort((a, b) => a.startDate.localeCompare(b.startDate));
  }, [yearApproved, calYear, calMonth]);

  /* Aushilfe-Verfügbarkeit — vraies données GET/POST /api/aushilfe-availability (mêmes
     routes que public/modules/planning-aushilfe.js) ; verrou mercredi 12:00 répliqué
     côté client à titre indicatif, le serveur reste la seule source qui bloque réellement. */
  const defaultAvailDays = () => {
    const days = {};
    PLAN_DAY_KEYS.forEach((k) => { days[k] = { mode: "unavailable", ranges: [] }; });
    return days;
  };
  const availabilityEditLocked = (week) => {
    const wednesday = addDaysISO(week, 2);
    const deadline = new Date(`${wednesday}T12:00:00`);
    if (Number.isNaN(deadline.getTime())) return false;
    return new Date() > deadline;
  };
  // Dispos Aushilfe pour la semaine du PLAN DE TRAVAIL consulté (jamais celle de l'écran
  // dédié, indépendante) — best-effort, un rôle sans accès n'affiche simplement rien.
  const loadPlanWeekAvailability = (offset) => {
    apiJSON(`/api/aushilfe-availability?storeId=${encodeURIComponent(user?.store)}&week=${mondayOfWeekOffset(offset)}`)
      .then((data) => setPlanWeekAvailability(data.availability || []))
      .catch(() => setPlanWeekAvailability([]));
  };
  // Aucune dispo déclarée = indisponible (fidèle à shiftAvailabilityDayFor côté vanilla,
  // pas juste "pas encore renseigné") — ne s'applique qu'aux Aushilfen.
  const isUnavailable = (emp, day) => {
    if (planEmpRole(emp) !== "Aushilfe") return false;
    const employeeId = resolveEmployeeId(emp);
    const dayKey = PLAN_DAY_KEYS[day];
    const record = planWeekAvailability.find((a) => String(a.employeeId) === String(employeeId));
    const d = record?.days?.[dayKey] || { mode: "unavailable", ranges: [] };
    return d.mode === "unavailable";
  };
  const loadOwnAvailability = (offset) => {
    apiJSON(`/api/aushilfe-availability?storeId=${encodeURIComponent(user?.store)}&week=${mondayOfWeekOffset(offset)}`)
      .then((data) => {
        const mine = (data.availability || [])[0];
        setOwnAvailDays(mine ? mine.days : defaultAvailDays());
        setOwnAvailRecordId(mine ? mine.id : "");
      })
      .catch(() => { setOwnAvailDays(defaultAvailDays()); setOwnAvailRecordId(""); });
  };
  const loadAvailabilityList = (offset) => {
    apiJSON(`/api/aushilfe-availability?storeId=${encodeURIComponent(user?.store)}&week=${mondayOfWeekOffset(offset)}`)
      .then((data) => setAvailabilityList(data.availability || []))
      .catch(() => setAvailabilityList([]));
  };
  const setAvailDayMode = (dayIdx, mode) => {
    const key = PLAN_DAY_KEYS[dayIdx];
    setOwnAvailDays((days) => {
      const current = (days || {})[key] || { mode: "unavailable", ranges: [] };
      const ranges = mode === "custom" ? (current.ranges.length ? current.ranges : [{ start: "09:00", end: "17:00" }]) : current.ranges;
      return { ...days, [key]: { mode, ranges } };
    });
  };
  const setAvailDayRange = (dayIdx, field, value) => {
    const key = PLAN_DAY_KEYS[dayIdx];
    setOwnAvailDays((days) => {
      const current = (days || {})[key] || { mode: "custom", ranges: [{ start: "09:00", end: "17:00" }] };
      const range = { ...(current.ranges[0] || { start: "09:00", end: "17:00" }), [field]: value };
      return { ...days, [key]: { ...current, ranges: [range] } };
    });
  };
  const saveOwnAvailability = () => {
    const week = mondayOfWeekOffset(availWeekOffset);
    if (availabilityEditLocked(week)) { showToast(t("toast.lockedEditUntilWednesdayNoon", "Verrouillé — modifications possibles jusqu'au mercredi 12:00 de cette semaine.")); return; }
    apiJSON("/api/aushilfe-availability", { method: "POST", body: JSON.stringify({ week, days: ownAvailDays }) })
      .then(() => { showToast(t("toast.availabilitySaved", "Disponibilité enregistrée.")); loadOwnAvailability(availWeekOffset); })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  const copyPreviousAvailability = () => {
    const week = mondayOfWeekOffset(availWeekOffset);
    if (availabilityEditLocked(week)) { showToast(t("toast.lockedEditUntilWednesdayNoon", "Verrouillé — modifications possibles jusqu'au mercredi 12:00 de cette semaine.")); return; }
    apiJSON("/api/aushilfe-availability/copy-previous", { method: "POST", body: JSON.stringify({ week }) })
      .then(() => { showToast(t("toast.previousWeekCopied", "Semaine précédente copiée.")); loadOwnAvailability(availWeekOffset); })
      .catch((err) => showToast(err.message || t("err.noAvailabilityFoundPreviousWeek", "Aucune disponibilité trouvée pour la semaine précédente.")));
  };
  const openAvailClearChoice = () => {
    const week = mondayOfWeekOffset(availWeekOffset);
    if (availabilityEditLocked(week)) { showToast(t("toast.lockedEditUntilWednesdayNoon", "Verrouillé — modifications possibles jusqu'au mercredi 12:00 de cette semaine.")); return; }
    if (!ownAvailRecordId) { setOwnAvailDays(defaultAvailDays()); return; }
    setSheet("availClearChoice");
  };
  // 2 sorties réelles (voir sheet availClearChoice) : suppression définitive de
  // l'enregistrement, ou remise à "non disponible" partout (l'enregistrement reste,
  // interrogeable plus tard) — même logique que la corbeille Planungsanfragen.
  const clearAvailabilityWeek = (choice) => {
    const week = mondayOfWeekOffset(availWeekOffset);
    if (choice === "full") {
      apiJSON(`/api/aushilfe-availability/${encodeURIComponent(ownAvailRecordId)}`, { method: "DELETE" })
        .then(() => { showToast(t("toast.entryDeleted", "Enregistrement supprimé.")); loadOwnAvailability(availWeekOffset); })
        .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
    } else {
      const days = defaultAvailDays();
      setOwnAvailDays(days);
      apiJSON("/api/aushilfe-availability", { method: "POST", body: JSON.stringify({ week, days }) })
        .then(() => { showToast(t("toast.resetDone", "Réinitialisé.")); loadOwnAvailability(availWeekOffset); })
        .catch((err) => showToast(err.message || t("err.resetImpossible", "Réinitialisation impossible.")));
    }
    setSheet(null);
  };

  /* Tâches — filtres et actions */
  const myTaskId = String(user?.proId || user?.id || "");
  // Modèle final, posé pièce par pièce le 2026-08-07 (retour utilisateur explicite,
  // "on va faire autrement" — reconstruction tuile par tuile) :
  // - "To Do liste personnelle" (mine) : créateur ET cible == moi. 4 sections (+Nouvelle).
  // - "Mes tâches" (assigned) : cible == moi, créée par quelqu'un d'autre — "je dois
  //   l'accomplir". 3 sections, jamais de création (ce n'est pas moi qui crée).
  // - "Tâches confiées" (delegated) : créateur == moi, cible == quelqu'un d'autre — vue
  //   créateur, je délègue. 3 sections + création.
  // - "Tâches de l'équipe" (team) : recipient="team", créée par quelqu'un d'autre — jamais
  //   ma propre consigne me revenant comme lecture à faire. 3 sections + création. Un
  //   second niveau (teamSubView "received"/"mine") bascule vers MES PROPRES tâches équipe
  //   (teamMine) — remplace la tuile "Historique" supprimée (retour utilisateur explicite :
  //   Historique faisait doublon avec "To Do"/"Confiées" une fois que toutes les tuiles ont
  //   eu leurs 3 onglets ; seul le cas "mes tâches équipe à moi" n'avait nulle part où
  //   vivre, il vit maintenant ICI plutôt que dans une tuile à part).
  const TASK_FILTER_PREDICATES = {
    mine: (t) => t.recipient === "specific" && String(t.targetEmployeeId) === myTaskId && String(t.createdById || "") === myTaskId,
    assigned: (t) => t.recipient === "specific" && String(t.targetEmployeeId) === myTaskId && String(t.createdById || "") !== myTaskId,
    team: (t) => t.recipient === "team" && String(t.createdById || "") !== myTaskId,
    teamMine: (t) => t.recipient === "team" && String(t.createdById || "") === myTaskId,
    delegated: (t) => t.recipient === "specific" && String(t.createdById || "") === myTaskId && String(t.targetEmployeeId) !== myTaskId,
  };
  // "late" (2026-08-30) reste une tâche à faire — l'onglet "En cours", les compteurs des
  // tuiles du hub et le bouton "Terminer" doivent continuer à la traiter comme telle. Seul
  // le popup "Tâches à faire" (voir actionableTasks plus bas) exclut spécifiquement le late.
  const isTaskPending = (t) => t.status === "open" || t.status === "late";
  const scopedTasks = tasks.filter((t) => t.scope === taskScope && (
    !taskFilter ? true : taskFilter === "team" && teamSubView === "mine" ? TASK_FILTER_PREDICATES.teamMine(t) : TASK_FILTER_PREDICATES[taskFilter](t)
  ));
  const openTasks = scopedTasks.filter(isTaskPending);
  const doneTasks = scopedTasks.filter((t) => t.status === "done");
  const archivedTasks = scopedTasks.filter((t) => t.status === "archived");
  const currentTaskList = taskTab === "open" ? openTasks : taskTab === "done" ? doneTasks : archivedTasks;
  // Compteurs pour les 3 tuiles du hub — toujours sur TOUTES les tâches store ouvertes,
  // indépendamment du taskFilter/taskTab actuellement sélectionné ailleurs.
  const openStoreTasksAll = tasks.filter((t) => t.scope === "store" && isTaskPending(t));
  const taskFilterCounts = {
    mine: openStoreTasksAll.filter(TASK_FILTER_PREDICATES.mine).length,
    assigned: openStoreTasksAll.filter(TASK_FILTER_PREDICATES.assigned).length,
    team: openStoreTasksAll.filter(TASK_FILTER_PREDICATES.team).length,
    teamMine: openStoreTasksAll.filter(TASK_FILTER_PREDICATES.teamMine).length,
    delegated: openStoreTasksAll.filter(TASK_FILTER_PREDICATES.delegated).length,
  };
  const openStoreTeamTasks = tasks.filter((t) => t.scope === "store" && isTaskPending(t) && t.recipient !== "specific");
  // Route une tâche précise vers sa vraie tuile (retour utilisateur explicite, 2026-08-07 :
  // relecture du clic sur une notification de tâche) — avant, TOUTE notification liée à une
  // tâche atterrissait sur taskFilter=null (vue générale non filtrée), qui montrait bien la
  // tâche mais ne routait jamais vers sa catégorie réelle, maintenant que les 4 tuiles ont un
  // sens précis. `tasks` est déjà chargé au montage de l'app (loadTasks, indépendant du
  // module Tâches), donc disponible même si on clique une notification sans jamais avoir
  // ouvert Tâches cette session.
  const routeForTaskId = (taskId) => {
    const tk = tasks.find((t) => String(t.id) === String(taskId));
    if (!tk) return { filter: null, teamSub: "received" };
    if (TASK_FILTER_PREDICATES.mine(tk)) return { filter: "mine", teamSub: "received" };
    if (TASK_FILTER_PREDICATES.assigned(tk)) return { filter: "assigned", teamSub: "received" };
    if (TASK_FILTER_PREDICATES.delegated(tk)) return { filter: "delegated", teamSub: "received" };
    if (TASK_FILTER_PREDICATES.team(tk)) return { filter: "team", teamSub: "received" };
    if (TASK_FILTER_PREDICATES.teamMine(tk)) return { filter: "team", teamSub: "mine" };
    return { filter: null, teamSub: "received" };
  };

  const priorityMeta = {
    normal: { label: t("tasks.priorityNormal", "Normal"), color: P.muted, bg: P.soft },
    high: { label: t("tasks.priorityHigh", "Haute"), color: A.orange, bg: "rgba(231,138,18,.13)" },
    urgent: { label: t("tasks.priorityUrgent", "Urgente"), color: A.red, bg: "rgba(239,68,68,.13)" },
  };
  const typeMeta = {
    standard: { emoji: "📋", label: t("tasks.type.standard", "Standard") },
    bakery: { emoji: "🥐", label: t("tasks.type.bakery", "Cuisson boulangerie") },
    pluExam: { emoji: "🎯", label: t("tasks.type.pluExam", "Examen PLU") },
  };
  // Design porté depuis l'artefact "Tableau de service" validé par l'utilisatrice — la
  // puce-icône se colore désormais selon le TYPE (pas la priorité, qui a sa propre pilule),
  // et chaque carte reçoit une coloration complète discrète selon sa PRIORITÉ (voir
  // TASK_PRIORITY_WASH, utilisé à la place de l'ancien fond vert/rouge par statut).
  const TASK_TYPE_COLOR = {
    standard: { bg: "rgba(54,79,231,.1)" },
    bakery: { bg: "rgba(161,98,7,.13)" },
    pluExam: { bg: "rgba(139,47,214,.13)" },
  };
  // "coloration complete de la carte avec la couleur de la priorite... pas trop forte
  // visuellement mais discernable a l'oeil nu" (retour utilisateur explicite) — "normal"
  // reste neutre (comme "Faible" dans l'artefact), seules Haute/Urgente attirent l'œil.
  const TASK_PRIORITY_WASH = {
    urgent: { background: "rgba(239,68,68,.055)", border: "1px solid rgba(239,68,68,.22)" },
    high: { background: "rgba(231,138,18,.06)", border: "1px solid rgba(231,138,18,.22)" },
    normal: {},
  };
  // Rôles responsable réseau (GR) — jamais FL/FLA (personnel de filiale à part entière,
  // légitimement candidats à un examen PLU). Utilisé pour exclure les GR de la liste des
  // employés sélectionnables pour un examen PLU (retour utilisateur explicite, 2026-08-06).
  const NETWORK_LEADER_TASK_ROLES = ["Bereichsleiter", "Verkaufsleiter", "Niederlassungsleiter", "NEXORA_ADMIN"];

  // Retraduit un titre de tâche AUTO-GÉNÉRÉ (bakery/pluExam) pour le VIEWER courant, quelle
  // que soit la langue dans laquelle il a été créé (retour utilisateur explicite, 2026-09-12,
  // photo à l'appui : "il y a toujours du français à certains endroits alors que l'app est
  // en allemand" — le titre était un littéral figé en base dans la langue du créateur,
  // jamais retraduit pour un viewer différent). Ces titres ne sont PAS du texte libre
  // (contrairement à un titre de tâche standard tapé à la main, jamais touché ici) — les
  // reconnaître et les réafficher dans la langue du viewer est donc correct et sûr.
  // Reconnaît les 5 variantes déjà connues (une par langue) plutôt que de dépendre d'un
  // marqueur stocké en base : fonctionne aussi sur les tâches déjà créées avant ce correctif,
  // sans migration.
  const AUTO_TASK_TITLE_VARIANTS = {
    personalBakery: ["Ma liste de cuisson", "Meine Backliste", "My baking list", "Mi lista de horneado", "La mia lista di cottura"],
    teamBakery: ["Commande de cuisson", "Backbestellung", "Baking order", "Pedido de horneado", "Ordine di cottura"],
    pluExamPrefix: ["Examen PLU ", "PLU-Prüfung ", "PLU Exam ", "Esame PLU "],
  };
  const translateAutoTaskTitle = (title) => {
    if (!title) return title;
    if (AUTO_TASK_TITLE_VARIANTS.personalBakery.includes(title)) return t("tasks.personalBakeryListTitle", "Ma liste de cuisson");
    if (AUTO_TASK_TITLE_VARIANTS.teamBakery.includes(title)) return t("tasks.teamBakeryOrderTitle", "Commande de cuisson");
    for (const prefix of AUTO_TASK_TITLE_VARIANTS.pluExamPrefix) {
      if (title.startsWith(prefix)) return t("tasks.pluExamTitleTemplate", { label: title.slice(prefix.length) }, title);
    }
    return title;
  };

  // Carte tâche — extraite en fonction réutilisable (retour utilisateur explicite : implémenter
  // l'artefact "Tableau de service" à 100%) pour être appelée depuis les 3 sections du
  // "board" (En cours toujours visible, Terminées/Archivées repliables) sans tripler la JSX.
  // Aucune donnée ni action n'est différente de l'ancienne version à 3 onglets — seule la
  // présentation (coloration priorité, icône type, repli) a changé.
  const renderTaskCard = (tk) => {
    const tm = typeMeta[tk.type];
    const tc = TASK_TYPE_COLOR[tk.type] || TASK_TYPE_COLOR.standard;
    const pm = priorityMeta[tk.priority];
    const overdue = tk.status === "late";
    const cardTint = TASK_PRIORITY_WASH[tk.priority] || {};
    const doneFade = !isTaskPending(tk) ? { opacity: .62 } : {};
    return (
      <div key={tk.id} style={{ ...card, ...cardTint, ...doneFade, padding: 14, marginBottom: isWide ? 0 : 9, minWidth: 0 }}>
        {/* Zone cliquable = tâche affichée en grand (sheet taskDetail) — séparée de la
            rangée d'actions ci-dessous pour ne jamais capter ses clics (pas de bouton
            imbriqué dans un bouton). */}
        <Pressable onClick={() => { setTaskDetail(tk); setSheet("taskDetail"); }} style={{ display: "flex", gap: 14, alignItems: "flex-start", width: "100%" }}>
          <span style={{ flex: "0 0 auto", width: 42, height: 42, borderRadius: 14, background: tc.bg, display: "grid", placeItems: "center", fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>{tm.emoji}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", textDecoration: !isTaskPending(tk) ? "line-through" : "none" }}>{translateAutoTaskTitle(tk.title)}</strong>
            <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 2 }}>
              {tk.scope === "network" ? `${tk.createdBy} · ${t("tasks.allStoresSuffix", "toutes filiales")}` : tk.recipient === "specific" ? t("tasks.forAssigneeTemplate", { name: tk.assignee }, `Pour ${tk.assignee}`) : t("tasks.forTeamBtn", "Pour l'équipe")}
              {" · "}{tm.label}
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 7 }}>
              <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: pm.color, background: pm.bg, borderRadius: 99, padding: "3px 9px" }}>{pm.label}</span>
              <span style={{
                fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 700, borderRadius: 99, padding: "3px 9px",
                color: overdue ? A.red : P.muted, background: overdue ? "rgba(239,68,68,.13)" : P.soft,
              }}>{overdue ? t("tasks.overdueLabel", "Pas fait · ") : t("tasks.deadlinePrefix", "Échéance ")}{fmtFR(tk.deadline.slice(0, 10))} {tk.deadline.slice(11, 16)}</span>
              {tk.type === "bakery" && tk.bakeryItems && (
                <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.brown, background: "rgba(180,110,60,.12)", borderRadius: 99, padding: "3px 9px" }}>
                  🥐 {t("tasks.articlesCountViewDetail", { n: tk.bakeryItems.length }, `${tk.bakeryItems.length} article${tk.bakeryItems.length > 1 ? "s" : ""} · voir le détail`)}
                </span>
              )}
            </div>
            {tk.status === "done" && tk.completedBy && (
              <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: "#15803d", marginTop: 6, fontWeight: 700 }}>{t("tasks.completedByTemplate", { name: tk.completedBy }, `✓ Terminée par ${tk.completedBy}`)}</div>
            )}
          </div>
        </Pressable>
        <div style={{ display: "flex", gap: 7, marginTop: 9, flexWrap: "wrap" }}>
          {isTaskPending(tk) && tk.type === "pluExam" && (
            <Pressable onClick={() => { setPluTask(tk); setPluStep("confirm"); setSheet("pluConfirm"); }} style={{
              padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: A.blue, color: "#fff",
            }}>{t("tasks.openExamBtn", "🎯 Ouvrir l'examen")}</Pressable>
          )}
          {/* "Tâches confiées" (delegated) = vue CRÉATEUR — le serveur réserve
              POST /:id/complete au seul employé assigné (targetEmployeeId), jamais
              au créateur (bug réel trouvé par vérification, 2026-08-07 : bouton
              affiché mais 403 systématique). Cohérent avec le principe explicite
              de l'utilisateur : "cette personne doit la faire et signaler que c'est
              fait" — le créateur suit, il n'accomplit pas à la place de l'assigné. */}
          {isTaskPending(tk) && tk.type !== "pluExam" && taskFilter !== "delegated" && (
            <Pressable onClick={() => completeTask(tk.id)} style={{
              padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: A.green, color: "#fff",
            }}>{t("tasks.finishBtn", "✓ Terminer")}</Pressable>
          )}
          {tk.status === "done" && (
            <Pressable onClick={() => archiveTask(tk.id)} style={{
              padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink,
            }}>{t("tasks.archiveBtn", "📦 Archiver")}</Pressable>
          )}
          {tk.status === "archived" && (
            <Pressable onClick={() => restoreTask(tk.id)} style={{
              padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: "rgba(54,79,231,.12)", color: A.blue,
            }}>{t("reductions.restoreBtn", "↩︎ Restaurer")}</Pressable>
          )}
          {/* Réservé au créateur côté serveur (403 sinon, cf. canDeleteTask côté
              vanilla) — masqué ici pour un non-créateur (retour utilisateur
              explicite, 2026-08-07 : "par rapport au bouton supprimer... on doit
              pouvoir savoir pour qui l'afficher"). Contrôle PAR TÂCHE, pas par
              tuile, pour rester correct même dans la vue générale non filtrée où
              des tâches de créateurs différents se mélangent. */}
          {String(tk.createdById || "") === myTaskId && (
            <Pressable onClick={() => deleteTask(tk.id)} style={{
              padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: "rgba(239,68,68,.12)", color: A.red,
            }}>{t("tasks.deleteBtn", "🗑️ Supprimer")}</Pressable>
          )}
        </div>
      </div>
    );
  };

  const addTaskLine = () => setTaskForm((f) => ({ ...f, lines: [...f.lines, { id: Date.now(), title: "" }] }));
  const removeTaskLine = (id) => setTaskForm((f) => ({ ...f, lines: f.lines.length > 1 ? f.lines.filter((l) => l.id !== id) : f.lines }));
  const updateTaskLine = (id, title) => setTaskForm((f) => ({ ...f, lines: f.lines.map((l) => l.id === id ? { ...l, title } : l) }));
  const setBakeryQty = (articleId, delta) => setTaskForm((f) => ({
    ...f, bakeryQty: { ...f.bakeryQty, [articleId]: Math.max(0, (f.bakeryQty[articleId] || 0) + delta) },
  }));
  // Formulaire par défaut selon la tuile d'origine — extrait du handler "+Nouvelle tâche"
  // pour être réutilisable après chaque ajout au panier (queueCurrentTask), qui doit remettre
  // le formulaire dans le même état de départ, pas juste le vider (retour utilisateur
  // explicite, 2026-08-07 : "équipe" force recipient=team, "mes tâches" force recipient=
  // specific sur soi, "confiée" force recipient=specific avec sélection multi-employés).
  const presetFormForTile = (filter) => {
    const base = { ...emptyTaskForm };
    if (filter === "mine") { base.recipient = "specific"; base.assignee = user?.firstname || ""; base.bakeryMode = "personal"; }
    else if (filter === "team") { base.recipient = "team"; }
    else if (filter === "delegated") { base.recipient = "specific"; base.assignees = []; }
    return base;
  };
  // Un formulaire "rempli" (par opposition à un formulaire fraîchement réinitialisé après un
  // ajout au panier) — sert uniquement à décider si le contenu du formulaire ENCORE affiché
  // doit être inclus automatiquement au moment de l'enregistrement final (voir submitTask),
  // pour ne pas obliger un "+ Ajouter à la liste" explicite sur la toute dernière entrée.
  const taskFormIsFilled = (form) => {
    if (form.type === "standard") return form.lines.some((l) => l.title.trim());
    if (form.type === "bakery") return catalogArticlesFor("Prebake").some((a) => (form.bakeryQty[a.id] || 0) > 0);
    if (form.type === "pluExam") return taskFilter === "delegated" ? form.assignees.length > 0 : !!resolveEmployeeId(form.assignee);
    return false;
  };
  // Mêmes contrôles qu'avant (retour utilisateur explicite pour chacun) — extraits pour être
  // rejoués aussi bien sur un ajout au panier que sur l'enregistrement direct sans panier.
  const validateTaskForm = (form) => {
    const isPersonalBakery = form.type === "bakery" && form.bakeryMode === "personal";
    const isDelegatedMulti = taskFilter === "delegated";
    if (form.type === "standard" && !form.lines.some((l) => l.title.trim())) { showToast(t("toast.addAtLeastOneTaskLine", "Ajoute au moins une ligne à la tâche.")); return false; }
    if (!isPersonalBakery && !form.immediate && form.type !== "bakery" && !form.date) { showToast(t("toast.chooseDeadlineDate", "Choisis une date limite.")); return false; }
    const bakerySelected = catalogArticlesFor("Prebake").filter((a) => (form.bakeryQty[a.id] || 0) > 0);
    if (form.type === "bakery" && bakerySelected.length === 0) { showToast(t("toast.indicateQtyAtLeastOneArticle", "Indique une quantité pour au moins un article.")); return false; }
    if (form.type === "pluExam" && !pluActiveList) { showToast(t("toast.noActivePluListCantCreateExam", "Aucune liste PLU active — impossible de créer cet examen.")); return false; }
    if (isDelegatedMulti && form.recipient === "specific" && form.assignees.length === 0) { showToast(t("toast.chooseAtLeastOneEmployee", "Choisis au moins un employé.")); return false; }
    if (!isDelegatedMulti && form.type === "pluExam" && !resolveEmployeeId(form.assignee)) { showToast(t("toast.chooseEmployeeForExam", "Choisis l'employé qui doit passer l'examen.")); return false; }
    return true;
  };
  // Calcule les requêtes POST /api/tasks pour UN formulaire — jamais fired ici (juste
  // construites), pour être réutilisable telle quelle par le panier (figées au moment de
  // l'ajout) ET par l'enregistrement direct sans panier.
  const buildTaskRequests = (form) => {
    const isPersonalBakery = form.type === "bakery" && form.bakeryMode === "personal";
    // Tuile "confiée" — sélection multi-employés directe (retour utilisateur explicite,
    // 2026-08-06) : une tâche par employé sélectionné, jamais form.assignee (singulier,
    // resté réservé à "Mes tâches"/formulaire générique).
    const isDelegatedMulti = taskFilter === "delegated";
    const bakerySelected = catalogArticlesFor("Prebake").filter((a) => (form.bakeryQty[a.id] || 0) > 0);
    const linesTrimmed = form.lines.map((l) => l.title.trim()).filter(Boolean);
    const priority = form.type === "bakery" ? (isPersonalBakery ? "normal" : "urgent") : (form.immediate ? "urgent" : form.priority);
    const deadline = isPersonalBakery ? ""
      : (form.immediate || form.type === "bakery")
        ? new Date(Date.now() + 15 * 60000).toISOString().slice(0, 16)
        : `${form.date}T${form.time || "18:00"}`;
    const category = form.type === "bakery" ? "bakery" : form.type === "pluExam" ? "plu_exam" : "general";
    const basePayload = {
      type: taskScope === "network" ? "network" : "store", priority, deadline, category,
      description: form.note.trim(),
      ...(form.type === "bakery" ? { bakeryItems: bakerySelected.map((a) => ({ artNr: a.artNr || "", name: a.name, quantity: form.bakeryQty[a.id], unit: "" })) } : {}),
      ...(form.type === "pluExam" ? { pluListId: pluActiveList?.id } : {}),
    };
    // Le vrai serveur crée UNE tâche PAR LIGNE pour le type standard (jamais un tableau
    // "lines" imbriqué) — voir submitTasks côté vanilla tasks.js.
    // Titres auto-générés (bakery/pluExam) traduits dans la langue du CRÉATEUR au moment de
    // la création — corrigé pour le VIEWER par translateAutoTaskTitle() partout où un titre
    // de tâche est affiché (retour utilisateur explicite 2026-09-12 : "il y a toujours du
    // français à certains endroits alors que l'app est en allemand" — l'ancien titre en dur
    // restait figé dans la langue du créateur pour tous les viewers futurs, quelle que soit
    // leur propre langue).
    const titles = form.type === "standard" ? linesTrimmed
      : [form.type === "bakery" ? (isPersonalBakery ? t("tasks.personalBakeryListTitle", "Ma liste de cuisson") : t("tasks.teamBakeryOrderTitle", "Commande de cuisson")) : t("tasks.pluExamTitleTemplate", { label: pluActiveList?.label || `KW ${pluActiveList?.kw || ""}` }, `Examen PLU ${pluActiveList?.label || `KW ${pluActiveList?.kw || ""}`}`)];
    // Depuis "confiée" (recipient=specific), une requête par COMBINAISON employé×titre ;
    // sinon comportement inchangé (targetEmployeeId unique calculé une fois).
    if (isDelegatedMulti && form.recipient === "specific") {
      return form.assignees.map(resolveEmployeeId).filter(Boolean).flatMap((targetEmployeeId) =>
        titles.map((title) => ({ url: "/api/tasks", body: { ...basePayload, targetEmployeeId, title } })));
    }
    // 2 bugs réels trouvés le même jour (retour utilisateur explicite, 2026-08-07), même
    // famille : ne JAMAIS passer par une recherche par prénom pour se cibler SOI-MÊME depuis
    // "To Do liste personnelle" (taskFilter==="mine") — myTaskId est déjà connu avec
    // certitude, aucune résolution nécessaire. Voir storeflow_tasks_ambient_chip_destination_bug
    // en mémoire pour l'historique complet des 2 bugs + du piège d'état ambiant qui a suivi.
    const targetEmployeeId = taskFilter === "mine" ? myTaskId : form.recipient === "specific" ? resolveEmployeeId(form.assignee) : "";
    return titles.map((title) => ({ url: "/api/tasks", body: { ...basePayload, targetEmployeeId, title } }));
  };
  // Résumé lisible d'une entrée du panier (type + contenu + destinataires pour "confiée").
  const buildQueueLabel = (form) => {
    const meta = typeMeta[form.type];
    let detail = "";
    if (form.type === "standard") {
      const titles = form.lines.map((l) => l.title.trim()).filter(Boolean);
      detail = titles.length > 1 ? t("tasks.queueLinesCountTemplate", { n: titles.length }, `${titles.length} lignes`) : (titles[0] || "");
    } else if (form.type === "bakery") {
      const n = catalogArticlesFor("Prebake").filter((a) => (form.bakeryQty[a.id] || 0) > 0).length;
      detail = t("tasks.queueArticlesCountTemplate", { n }, `${n} article${n > 1 ? "s" : ""}`);
    } else if (form.type === "pluExam") {
      detail = pluActiveList?.label || `KW ${pluActiveList?.kw || ""}`;
    }
    const who = taskFilter === "delegated" && form.assignees.length ? ` → ${form.assignees.join(", ")}` : "";
    return `${meta.emoji} ${meta.label}${detail ? " — " + detail : ""}${who}`;
  };
  // Ajoute le formulaire actuel au panier SANS l'envoyer, puis réinitialise le formulaire
  // (même préréglage que l'entrée dans la tuile) pour composer l'entrée suivante — permet de
  // mélanger standard/cuisson/plu (et, depuis "confiée", des employés différents par entrée)
  // avant un seul enregistrement groupé (retour utilisateur explicite, 2026-08-07).
  const queueCurrentTask = () => {
    if (!validateTaskForm(taskForm)) return;
    setTaskQueue((q) => [...q, { id: Date.now() + Math.random(), label: buildQueueLabel(taskForm), requests: buildTaskRequests(taskForm) }]);
    setTaskForm(presetFormForTile(taskFilter));
    showToast(t("toast.taskAddedToQueue", "➕ Ajoutée à la liste"));
  };
  const removeQueuedTask = (id) => setTaskQueue((q) => q.filter((item) => item.id !== id));
  const submitTask = () => {
    // Panier vide (cas le plus courant, un seul type) : comportement identique à avant, un
    // clic direct sur "Créer la tâche" valide et envoie le formulaire affiché. Panier non
    // vide : le formulaire affiché n'est réinjecté QUE s'il contient encore du contenu réel
    // (taskFormIsFilled) — sinon c'est juste le formulaire vierge laissé après le dernier
    // "+ Ajouter à la liste", à ne pas re-soumettre en silence.
    let finalRequests = taskQueue.flatMap((q) => q.requests);
    if (taskQueue.length === 0) {
      if (!validateTaskForm(taskForm)) return;
      finalRequests = buildTaskRequests(taskForm);
    } else if (taskFormIsFilled(taskForm)) {
      finalRequests = finalRequests.concat(buildTaskRequests(taskForm));
    }
    const isPersonalBakery = taskForm.type === "bakery" && taskForm.bakeryMode === "personal";
    const totalCount = finalRequests.length;
    Promise.all(finalRequests.map((r) => apiJSON(r.url, { method: "POST", body: JSON.stringify(r.body) })))
      .then(() => loadTasks())
      .catch((err) => showToast(err.message || t("err.taskCreationImpossible", "Impossible de créer la tâche.")));
    setTaskQueue([]);
    setTaskForm(emptyTaskForm);
    // Reste sur la tuile d'où on vient (taskFilter inchangé) — avant, revenait toujours à
    // la vue non filtrée après création, ce qui n'avait plus de sens depuis que chaque
    // tuile est une vue de premier niveau à part entière (retour utilisateur explicite,
    // 2026-08-07, effet de bord repéré en travaillant sur ce chantier).
    setTasksScreen("list"); setTaskScope("store"); setTaskTab("open");
    showToast(totalCount > 1 ? t("tasks.tasksCreatedTemplate", { n: totalCount }, `${totalCount} tâches créées.`)
      : isPersonalBakery ? "✓ Liste enregistrée" : taskForm.immediate || taskForm.type === "bakery" ? "⚡ Tâche envoyée en priorité immédiate" : "✓ Tâche créée");
  };
  const completeTask = (id) => {
    setTasks((ts) => ts.map((t) => t.id === id ? { ...t, status: "done", completedBy: user?.firstname || "Toi" } : t));
    const task = tasks.find((t) => t.id === id);
    apiJSON(`/api/tasks/${id}/complete`, {
      method: "POST",
      body: JSON.stringify({ storeId: task?.scope === "store" ? task.storeId : user?.store }),
    }).catch((err) => { showToast(err.message || t("err.actionRefused", "Action refusée.")); loadTasks(); });
    showToast(t("toast.taskMarkedDone", "✓ Tâche marquée accomplie"));
  };
  const archiveTask = (id) => {
    setTasks((ts) => ts.map((t) => t.id === id ? { ...t, status: "archived" } : t));
    apiJSON(`/api/tasks/${id}/archive`, { method: "POST", body: JSON.stringify({ archived: true }) })
      .catch((err) => { showToast(err.message || t("err.actionRefused", "Action refusée.")); loadTasks(); });
    showToast(t("toast.taskArchived", "📦 Tâche archivée"));
  };
  const restoreTask = (id) => {
    setTasks((ts) => ts.map((t) => t.id === id ? { ...t, status: "done" } : t));
    apiJSON(`/api/tasks/${id}/archive`, { method: "POST", body: JSON.stringify({ archived: false }) })
      .catch((err) => { showToast(err.message || t("err.actionRefused", "Action refusée.")); loadTasks(); });
    showToast(t("toast.taskRestored", "↩︎ Tâche restaurée"));
  };
  const deleteTask = (id) => {
    setTasks((ts) => ts.filter((t) => t.id !== id));
    // Réservé au créateur côté serveur (403 sinon, cf. canDeleteTask côté vanilla) — le
    // bouton est maintenant masqué côté JSX pour un non-créateur (retour utilisateur
    // explicite, 2026-08-07), ce catch reste un filet de sécurité, pas le mécanisme principal.
    apiJSON(`/api/tasks/${id}`, { method: "DELETE" }).catch((err) => { showToast(err.message || t("toast.deletionRefused", "Suppression refusée.")); loadTasks(); });
    showToast(t("toast.taskDeleted", "🗑️ Tâche supprimée"));
  };
  const startPluNow = (task) => {
    // pluTask reste défini (jamais remis à null ici) : finishQuiz() en a besoin pour
    // envoyer sourceTaskId à POST /api/plu-quiz/attempts et terminer la tâche liée.
    setSheet(null);
    setPluScreen("examIntro"); setImScreen("pluquiz"); setScreen("inventurmassnahme");
    apiJSON(`/api/tasks/${task.id}/start`, { method: "POST" }).then(() => loadTasks()).catch(() => {});
    showToast(t("toast.pluExamStartedTemplate", { title: translateAutoTaskTitle(task.title) }, `🎯 Examen PLU « ${translateAutoTaskTitle(task.title)} »`));
  };
  const savePluDelay = (minutes) => {
    const task = pluTask;
    setSheet(null); setPluTask(null); setPluStep("confirm");
    if (task?.id) {
      apiJSON(`/api/tasks/${task.id}/plu-remind-later`, { method: "POST", body: JSON.stringify({ delayMinutes: minutes }) })
        .then(() => loadTasks())
        .catch((err) => showToast(err.message || t("err.reminderImpossible", "Rappel impossible.")));
    }
    showToast(t("toast.reminderScheduledInTemplate", { duration: minutes < 60 ? minutes + " min" : (minutes / 60) + " h" }, `⏰ Rappel programmé dans ${minutes < 60 ? minutes + " min" : (minutes / 60) + " h"}`));
  };

  /* HACCP — progression, saisie, validation, journal (vraies données serveur) */
  const allDailyItems = haccpConfig.dailyGroups.flatMap((g) => g.items.map((it) => ({ ...it, groupId: g.id })));
  const allWeeklyItems = haccpConfig.weeklyGroups.flatMap((g) => g.items.map((it) => ({ ...it, groupId: g.id })));
  const haccpItemLookup = (groupId, itemId) => (haccpConfig.dailyGroups.find((g) => g.id === groupId)?.items || []).find((i) => i.id === itemId);
  const haccpEntryFor = (groupId, itemId) => {
    if (!haccpRecord) return null;
    const key = `${groupId}.${itemId}`;
    const item = haccpItemLookup(groupId, itemId);
    return item?.type === "delivery_temp" ? (haccpRecord.delivery[key] || null) : (haccpRecord.entries[key] || null);
  };
  // Miroir exact de haccpMissingDailyKeys côté serveur (server.js) — jamais une simple
  // vérité locale, sinon la barre de progression pourrait mentir par rapport à ce que
  // "Valider la journée" acceptera réellement.
  const isDailyFilled = (groupId, itemId) => {
    const item = haccpItemLookup(groupId, itemId);
    const e = haccpEntryFor(groupId, itemId);
    if (!item) return false;
    if (item.type === "delivery_temp") {
      if (!e || e.hasDelivery === null || e.hasDelivery === undefined) return false;
      if (e.hasDelivery === true && (e.temp === null || e.temp === undefined)) return false;
      return true;
    }
    return !!e && e.value !== null && e.value !== undefined;
  };
  const isWeeklyFilled = (groupId, itemId) => {
    const e = haccpWeeklyRecord?.entries?.[`${groupId}.${itemId}`];
    return !!e && e.value !== null && e.value !== undefined;
  };
  const dailyFilledCount = allDailyItems.filter((it) => isDailyFilled(it.groupId, it.id)).length;
  const dailyPct = allDailyItems.length ? Math.round((dailyFilledCount / allDailyItems.length) * 100) : 0;
  const groupFilledCount = (g) => g.items.filter((it) => isDailyFilled(g.id, it.id)).length;
  const groupComplete = (g) => groupFilledCount(g) === g.items.length;
  const weeklyFilledCount = allWeeklyItems.filter((it) => isWeeklyFilled(it.groupId, it.id)).length;
  const weeklyPct = allWeeklyItems.length ? Math.round((weeklyFilledCount / allWeeklyItems.length) * 100) : 0;
  const weeklyGroupFilledCount = (g) => g.items.filter((it) => isWeeklyFilled(g.id, it.id)).length;
  const weeklyGroupComplete = (g) => weeklyGroupFilledCount(g) === g.items.length;
  // Contrôles hebdomadaires restants — tous de type "check" (aucun temp/livraison côté
  // hebdo, voir HACCP_WEEKLY_GROUPS server.js), donc jamais besoin de filtrer par type ici
  // contrairement à haccpRemainingChecks (quotidien).
  const haccpRemainingWeeklyChecks = allWeeklyItems.filter((it) => !isWeeklyFilled(it.groupId, it.id));
  const haccpWeeklyZoneGroup = haccpConfig.weeklyGroups.find((g) => g.id === openWeeklyGroupId) || null;
  const haccpWeeklyZoneItems = haccpWeeklyZoneGroup ? haccpWeeklyZoneGroup.items : [];
  const haccpWeeklyZoneIdx = haccpWeeklyZoneGroup ? haccpWeeklyZoneItems.findIndex((it) => !isWeeklyFilled(haccpWeeklyZoneGroup.id, it.id)) : -1;
  const haccpWeeklyZoneItem = haccpWeeklyZoneIdx >= 0 ? haccpWeeklyZoneItems[haccpWeeklyZoneIdx] : null;
  const haccpCanValidateWeekly = haccpConfig.capabilities.validateDaily; // même garde que /api/haccp/weekly (PUT), FLA/FL/admin — cf. section weekly existante
  const haccpAnomalyCount = allDailyItems.filter((it) => haccpEntryFor(it.groupId, it.id)?.ok === false).length;
  // Contrôles simples (type "check") encore à faire, toutes zones confondues — alimente le
  // bouton de confirmation groupée (App.jsx, hub HACCP) : jamais les items temp/livraison,
  // qui exigent une vraie mesure et gardent leur propre flux dans la sheet haccpZone.
  const haccpRemainingChecks = allDailyItems.filter((it) => it.type === "check" && !isDailyFilled(it.groupId, it.id));
  // Item courant de la sheet haccpZone — dérivé en direct de haccpRecord (premier item non
  // rempli de la zone ouverte), jamais un index local qui pourrait désynchroniser de la
  // vraie donnée serveur après un enregistrement.
  const haccpZoneGroup = haccpConfig.dailyGroups.find((g) => g.id === openGroupId) || null;
  const haccpZoneItems = haccpZoneGroup ? haccpZoneGroup.items : [];
  const haccpZoneIdx = haccpZoneGroup ? haccpZoneItems.findIndex((it) => !isDailyFilled(haccpZoneGroup.id, it.id)) : -1;
  const haccpZoneItem = haccpZoneIdx >= 0 ? haccpZoneItems[haccpZoneIdx] : null;
  const haccpCanFill = haccpConfig.capabilities.fill && haccpStatus === "open";
  useEffect(() => { setHaccpKpDigits(""); setHaccpKpNeg(false); }, [haccpZoneGroup?.id, haccpZoneItem?.id]);
  // Bande de pastilles température : ±3° autour du seuil réel par pas de 0.2° — jamais une
  // plage inventée arbitrairement, toujours centrée sur la vraie limite HACCP de l'item.
  const haccpChipsFor = (item) => {
    const step = 0.2, span = 3, vals = [];
    for (let v = item.max - span; v <= item.max + span + 1e-9; v += step) vals.push(+v.toFixed(1));
    return vals;
  };
  const haccpKpValue = haccpKpDigits ? (parseInt(haccpKpDigits, 10) / 10) * (haccpKpNeg ? -1 : 1) : null;
  const haccpKpPressDigit = (d) => { if (haccpKpDigits.length < 4) setHaccpKpDigits(haccpKpDigits + d); };
  const haccpKpBackspace = () => { const nd = haccpKpDigits.slice(0, -1); setHaccpKpDigits(nd); if (!nd) setHaccpKpNeg(false); };
  const haccpKpToggleNeg = () => setHaccpKpNeg((n) => !n);
  const haccpCommitZoneTemp = (raw) => {
    if (!haccpZoneGroup || !haccpZoneItem) return;
    if (haccpZoneItem.type === "delivery_temp") commitDeliveryTemp(haccpZoneGroup.id, haccpZoneItem, raw);
    else commitTempValue(haccpZoneGroup.id, haccpZoneItem, raw);
  };
  const haccpConfirmZoneTemp = () => {
    if (haccpKpValue == null) { showToast(t("haccp.zone.enterValueToast", "Tape ou choisis la valeur relevée")); return; }
    haccpCommitZoneTemp(String(haccpKpValue));
  };

  // date optionnelle : consulter/corriger un jour du mois autre qu'aujourd'hui (ouvert
  // depuis la vue mensuelle) — sinon toujours aujourd'hui. Chaque jour reste un dossier
  // atomique côté serveur (id = storeId_date), donc ouvrir un jour passé ne "continue"
  // jamais un autre calendrier ni ne mélange les mois.
  const loadHaccp = (date) => {
    if (!user?.store) return;
    const d = date || haccpViewDate || todayISO();
    setHaccpViewDate(d);
    apiJSON("/api/haccp/config").then((cfg) => setHaccpConfig({
      dailyGroups: cfg.dailyGroups || [], weeklyGroups: cfg.weeklyGroups || [], capabilities: cfg.capabilities || {},
    })).catch(() => {});
    apiJSON(`/api/haccp/daily?storeId=${encodeURIComponent(user?.store)}&date=${d}`)
      .then((data) => {
        setHaccpStatus(data.status); setHaccpRecord(data.record);
        setHaccpConfig((c) => ({ ...c, capabilities: data.capabilities || c.capabilities }));
        if (data.record?.year && data.record?.week) {
          return apiJSON(`/api/haccp/weekly?storeId=${encodeURIComponent(user?.store)}&year=${data.record.year}&week=${data.record.week}`);
        }
        return null;
      })
      .then((weeklyData) => { if (weeklyData) setHaccpWeeklyRecord(weeklyData.record); })
      .catch(() => {});
  };
  const openHaccpDay = (date) => { loadHaccp(date); setHaccpScreen("hub"); setOpenGroupId(null); };
  // Sheet "haccpZone" — flux pas-à-pas d'une zone (contrôle éclair). openGroupId sert à la
  // fois d'identifiant de zone ouverte ET de déclencheur d'avancement (advanceToNextGroup,
  // déjà existant, y écrit directement) — l'item courant est TOUJOURS dérivé en direct de
  // haccpRecord au rendu (jamais un index local dupliqué qui pourrait désynchroniser de la
  // vraie donnée serveur).
  const openHaccpZone = (g) => { setOpenGroupId(g.id); setSheet("haccpZone"); };
  const closeHaccpZone = () => { setSheet(null); setOpenGroupId(null); };
  // Confirmation groupée des contrôles "check" restants — jamais les temp/livraison, qui
  // exigent une vraie mesure. Séquentiel (pas Promise.all) : chaque PUT renvoie le dossier
  // complet, un enchaînement parallèle risquerait d'écraser localement une réponse plus
  // récente par une plus ancienne arrivée après.
  const bulkConfirmHaccpChecks = async () => {
    setHaccpBulkRunning(true);
    try {
      for (const it of haccpRemainingChecks) {
        await putHaccpDaily(it.groupId, it.id, { value: true });
      }
      setSheet(null);
      showToast(t("toast.haccpChecksConfirmed", "✓ Contrôles confirmés"));
    } catch (err) {
      showToast(err.message || t("err.savingImpossible", "Enregistrement impossible."));
    } finally {
      setHaccpBulkRunning(false);
    }
  };
  const openHaccpWeeklyZone = (g) => { setOpenWeeklyGroupId(g.id); setSheet("haccpWeeklyZone"); };
  const closeHaccpWeeklyZone = () => { setSheet(null); setOpenWeeklyGroupId(null); };
  // Tous les items hebdo sont de type "check" (voir HACCP_WEEKLY_GROUPS server.js) — la
  // confirmation groupée couvre donc la totalité du contrôle hebdomadaire, pas seulement un
  // sous-ensemble comme haccpRemainingChecks côté quotidien.
  const bulkConfirmHaccpWeeklyChecks = async () => {
    setHaccpWeeklyBulkRunning(true);
    try {
      for (const it of haccpRemainingWeeklyChecks) {
        await putHaccpWeekly(it.groupId, it.id, { value: true });
      }
      setSheet(null);
      showToast(t("toast.haccpChecksConfirmed", "✓ Contrôles confirmés"));
    } catch (err) {
      showToast(err.message || t("err.savingImpossible", "Enregistrement impossible."));
    } finally {
      setHaccpWeeklyBulkRunning(false);
    }
  };
  const loadHaccpMonth = (year, month) => {
    if (!user?.store) return;
    setHaccpMonthYear(year); setHaccpMonthNum(month);
    apiJSON(`/api/haccp/month?storeId=${encodeURIComponent(user?.store)}&year=${year}&month=${month}`)
      .then((data) => setHaccpMonthData(data))
      .catch((err) => showToast(err.message || t("err.loadingImpossible", "Chargement impossible.")));
  };
  const openHaccpMonth = () => { loadHaccpMonth(haccpMonthYear, haccpMonthNum); setHaccpScreen("month"); };
  const changeHaccpMonth = (delta) => {
    let m = haccpMonthNum + delta, y = haccpMonthYear;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    loadHaccpMonth(y, m);
  };

  const advanceToNextGroup = (fromGroupId) => {
    const idx = haccpConfig.dailyGroups.findIndex((g) => g.id === fromGroupId);
    const next = haccpConfig.dailyGroups.slice(idx + 1).find((g) => !groupComplete(g));
    if (next) setOpenGroupId(next.id);
  };
  const isLastItemOfGroup = (groupId, itemId) => {
    const g = haccpConfig.dailyGroups.find((gr) => gr.id === groupId);
    return g.items.every((it) => it.id === itemId || isDailyFilled(groupId, it.id));
  };
  const advanceToNextWeeklyGroup = (fromGroupId) => {
    const idx = haccpConfig.weeklyGroups.findIndex((g) => g.id === fromGroupId);
    const next = haccpConfig.weeklyGroups.slice(idx + 1).find((g) => !weeklyGroupComplete(g));
    if (next) setOpenWeeklyGroupId(next.id);
  };
  const isLastItemOfWeeklyGroup = (groupId, itemId) => {
    const g = haccpConfig.weeklyGroups.find((gr) => gr.id === groupId);
    return g.items.every((it) => it.id === itemId || isWeeklyFilled(groupId, it.id));
  };
  const putHaccpDaily = (groupId, itemId, body) => apiJSON("/api/haccp/daily", {
    method: "PUT", body: JSON.stringify({ storeId: user?.store, date: haccpViewDate, key: `${groupId}.${itemId}`, ...body }),
  }).then((data) => {
    setHaccpRecord(data.record);
    setHaccpConfig((c) => ({ ...c, capabilities: data.capabilities || c.capabilities }));
    return data;
  });
  const putHaccpWeekly = (groupId, itemId, body) => apiJSON("/api/haccp/weekly", {
    method: "PUT", body: JSON.stringify({ storeId: user?.store, year: haccpWeeklyRecord?.year, week: haccpWeeklyRecord?.week, key: `${groupId}.${itemId}`, ...body }),
  }).then((data) => {
    setHaccpWeeklyRecord(data.record);
    setHaccpConfig((c) => ({ ...c, capabilities: data.capabilities || c.capabilities }));
    return data;
  });

  const commitTempValue = (groupId, item, raw) => {
    const dKey = `${groupId}.${item.id}`;
    if (raw === "" || raw == null) return;
    const v = Number(raw);
    if (!Number.isFinite(v)) return;
    if (v <= item.max) {
      const willComplete = isLastItemOfGroup(groupId, item.id);
      putHaccpDaily(groupId, item.id, { value: v })
        .then(() => { setHaccpTempDrafts((d) => { const c = { ...d }; delete c[dKey]; return c; }); if (willComplete) advanceToNextGroup(groupId); })
        .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
    } else {
      // returnSheet capture la sheet d'où vient l'anomalie (haccpZone ou haccpQuickChecks) —
      // confirmHaccpAnomaly doit y revenir, jamais fermer inconditionnellement (sheet unique
      // partagée avec le flux de zone depuis le passage de l'accordéon à la sheet pas-à-pas).
      setHaccpNoteEdit({ scope: "daily", groupId, item, value: v, returnSheet: sheet });
      setHaccpNoteDraft({ text: "", photoUrl: "" });
      setSheet("haccpNote");
    }
  };
  const toggleCheck = (groupId, item, done) => {
    if (!done) { setHaccpNoteEdit({ scope: "daily", groupId, item, value: null, returnSheet: sheet }); setHaccpNoteDraft({ text: "", photoUrl: "" }); setSheet("haccpNote"); return; }
    const willComplete = isLastItemOfGroup(groupId, item.id);
    putHaccpDaily(groupId, item.id, { value: true })
      .then(() => { if (willComplete) advanceToNextGroup(groupId); })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  const setDeliveryNo = (groupId, item) => {
    putHaccpDaily(groupId, item.id, { hasDelivery: false }).catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  const setDeliveryYes = (groupId, item) => {
    setHaccpDeliveryPending((p) => ({ ...p, [`${groupId}.${item.id}`]: true }));
  };
  const commitDeliveryTemp = (groupId, item, raw) => {
    if (raw === "" || raw == null) return;
    const v = Number(raw);
    if (!Number.isFinite(v)) return;
    if (v <= item.max) {
      const willComplete = isLastItemOfGroup(groupId, item.id);
      putHaccpDaily(groupId, item.id, { hasDelivery: true, temp: v })
        .then(() => { if (willComplete) advanceToNextGroup(groupId); })
        .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
    } else {
      setHaccpNoteEdit({ scope: "daily", groupId, item, value: v, hasDelivery: true, returnSheet: sheet });
      setHaccpNoteDraft({ text: "", photoUrl: "" });
      setSheet("haccpNote");
    }
  };
  const toggleWeekly = (groupId, item, done) => {
    if (!done) { setHaccpNoteEdit({ scope: "weekly", groupId, item, value: null, returnSheet: sheet }); setHaccpNoteDraft({ text: "", photoUrl: "" }); setSheet("haccpNote"); return; }
    const willComplete = isLastItemOfWeeklyGroup(groupId, item.id);
    putHaccpWeekly(groupId, item.id, { value: true })
      .then(() => { if (willComplete) advanceToNextWeeklyGroup(groupId); })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  const uploadHaccpPhoto = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setHaccpUploading(true);
    const compressed = await compressImageFile(file);
    const formData = new FormData();
    formData.append("photo", compressed);
    apiJSON("/api/store-photos", { method: "POST", body: formData })
      .then((data) => setHaccpNoteDraft((d) => ({ ...d, photoUrl: data.url })))
      .catch((err) => showToast(err.message || t("err.photoSendImpossible", "Envoi de la photo impossible.")))
      .finally(() => setHaccpUploading(false));
  };
  const confirmHaccpAnomaly = () => {
    if (!haccpNoteDraft.text.trim()) { showToast(`${t("haccp.noteRequired", "Une remarque est obligatoire")} ${t("haccp.anomalySuffixShort", "pour enregistrer une anomalie")}.`); return; }
    const { scope, groupId, item, value, hasDelivery, returnSheet } = haccpNoteEdit;
    const note = haccpNoteDraft.text.trim();
    const photoUrl = haccpNoteDraft.photoUrl;
    if (scope === "daily") {
      const willComplete = isLastItemOfGroup(groupId, item.id);
      const body = item.type === "delivery_temp" ? { hasDelivery: true, temp: value, note, photoUrl }
        : item.type === "install_temp" ? { value, note, photoUrl }
        : { value: false, note, photoUrl };
      putHaccpDaily(groupId, item.id, body)
        .then(() => { if (willComplete) advanceToNextGroup(groupId); setSheet(returnSheet || null); setHaccpNoteEdit(null); showToast(t("toast.anomalySaved", "⚠️ Anomalie enregistrée")); })
        .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
    } else {
      const willComplete = isLastItemOfWeeklyGroup(groupId, item.id);
      putHaccpWeekly(groupId, item.id, { value: false, note, photoUrl })
        .then(() => { if (willComplete) advanceToNextWeeklyGroup(groupId); setSheet(returnSheet || null); setHaccpNoteEdit(null); showToast(t("toast.anomalySaved", "⚠️ Anomalie enregistrée")); })
        .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
    }
  };
  const validateDaily = () => {
    apiJSON("/api/haccp/daily/validate", { method: "POST", body: JSON.stringify({ storeId: user?.store, date: haccpViewDate }) })
      .then((data) => { setHaccpRecord(data.record); showToast(t("toast.haccpDayValidated", "✓ Journée HACCP validée")); })
      .catch((err) => showToast(err.message || t("err.validationImpossible", "Validation impossible.")));
  };
  const validateWeekly = () => {
    apiJSON("/api/haccp/weekly/validate", { method: "POST", body: JSON.stringify({ storeId: user?.store, year: haccpWeeklyRecord?.year, week: haccpWeeklyRecord?.week }) })
      .then((data) => { setHaccpWeeklyRecord(data.record); showToast(t("toast.haccpWeekValidated", "✓ Semaine HACCP validée")); })
      .catch((err) => showToast(err.message || t("err.validationImpossible", "Validation impossible.")));
  };

  // Export imprimable du mois — grille A3/A4 paysage fidèle au document papier NORMA,
  // même patron que l'app vanilla (public/modules/haccp-month.js, renderPrintExport) :
  // jours en colonnes groupées par semaine, contrôles en lignes groupées par rayon.
  // Jamais traduit (document physique), voir même règle que l'Arbeitsplan Planning.
  const haccpEscHtml = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const buildHaccpPrintHtml = (data) => {
    const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
    const DOW = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
    const cols = data.days.filter((d) => new Date(d.date + "T00:00:00").getDay() !== 0);
    const weekGroups = [];
    cols.forEach((d) => {
      const last = weekGroups[weekGroups.length - 1];
      if (!last || last.week !== d.week) weekGroups.push({ week: d.week, days: [d] });
      else last.days.push(d);
    });
    const weekHeaderRow1 = weekGroups.map((g) => `<th colspan="${g.days.length}">KW ${g.week}</th>`).join("");
    const weekHeaderRow2 = cols.map((d) => {
      const dow = new Date(d.date + "T00:00:00").getDay();
      return `<th class="hep-daycol ${d.status !== "open" ? "hep-closed" : ""}"><span>${DOW[dow]}</span><strong>${Number(d.date.slice(8, 10))}</strong></th>`;
    }).join("");

    const renderDailyCell = (day, item, key) => {
      if (day.status !== "open") return `<td class="hep-cell hep-closed"></td>`;
      if (item.type === "delivery_temp") {
        const dv = day.delivery[key];
        if (!dv || dv.hasDelivery === undefined || dv.hasDelivery === null) return `<td class="hep-cell"></td>`;
        if (dv.hasDelivery === false) return `<td class="hep-cell hep-dash">–</td>`;
        return `<td class="hep-cell ${dv.ok === false ? "hep-bad" : ""}">${dv.temp}</td>`;
      }
      const e = day.entries[key];
      if (!e || e.value === undefined || e.value === null) return `<td class="hep-cell"></td>`;
      if (item.type === "install_temp") return `<td class="hep-cell ${e.ok === false ? "hep-bad" : ""}">${e.value}</td>`;
      return `<td class="hep-cell ${e.ok === false ? "hep-bad" : "hep-ok"}">${e.value === true ? "✓" : "✗"}</td>`;
    };
    const renderWeeklyCell = (week, key) => {
      const e = week.entries[key];
      if (!e || e.value === undefined || e.value === null) return `<td class="hep-cell"></td>`;
      return `<td class="hep-cell ${e.value === false ? "hep-bad" : "hep-ok"}">${e.value === true ? "✓" : "✗"}</td>`;
    };

    const dailyRows = [];
    data.dailyGroups.forEach((group) => {
      group.items.forEach((item, idx) => {
        const key = `${group.id}.${item.id}`;
        dailyRows.push(`<tr>${idx === 0 ? `<th rowspan="${group.items.length}" class="hep-group-th">${haccpEscHtml(group.label)}</th>` : ""}<td class="hep-item-label">${haccpEscHtml(item.label)}${item.max !== undefined ? ` <span class="hep-max">(max. ${item.max}°C)</span>` : ""}</td>${cols.map((d) => renderDailyCell(d, item, key)).join("")}</tr>`);
      });
    });

    const weeklyHeaderRow = data.weeks.map((w) => `<th>KW ${w.week}</th>`).join("");
    const weeklyRows = [];
    data.weeklyGroups.forEach((group) => {
      group.items.forEach((item, idx) => {
        const key = `${group.id}.${item.id}`;
        weeklyRows.push(`<tr>${idx === 0 ? `<th rowspan="${group.items.length}" class="hep-group-th">${haccpEscHtml(group.label)}</th>` : ""}<td class="hep-item-label">${haccpEscHtml(item.label)}</td>${data.weeks.map((w) => renderWeeklyCell(w, key)).join("")}</tr>`);
      });
    });

    const dailySignatures = cols.filter((d) => d.validation).map((d) => `<div class="hep-sig-row"><span>${Number(d.date.slice(8, 10))}/${data.month}</span><strong>${haccpEscHtml(d.validation.validatedByName)}</strong><span>${haccpEscHtml(d.validation.validatedByRole)}</span></div>`).join("") || `<p class="hep-sig-empty">Aucune journée validée ce mois-ci.</p>`;
    const weeklySignatures = data.weeks.filter((w) => w.validation).map((w) => `<div class="hep-sig-row"><span>KW ${w.week}</span><strong>${haccpEscHtml(w.validation.validatedByName)}</strong><span>${haccpEscHtml(w.validation.ergebnis || "")}</span></div>`).join("") || `<p class="hep-sig-empty">Aucune semaine validée ce mois-ci.</p>`;

    const monthLabel = `${MONTHS[data.month - 1]} ${data.year}`;
    return `<div class="hep-page">
      <header class="hep-header"><div><p class="hep-eyebrow">HACCP · Filialcheckliste</p><h1>${haccpEscHtml(monthLabel)}</h1></div><div class="hep-store">Filiale ${haccpEscHtml(data.storeId)}</div></header>
      <p class="hep-section-title">Contrôles quotidiens</p>
      <table class="hep-table"><colgroup><col style="width:9px;" /><col style="width:225px;" />${cols.map(() => `<col />`).join("")}</colgroup><thead><tr><th class="hep-group-th"></th><th></th>${weekHeaderRow1}</tr><tr><th class="hep-group-th"></th><th></th>${weekHeaderRow2}</tr></thead><tbody>${dailyRows.join("")}</tbody></table>
      <p class="hep-section-title">Contrôles hebdomadaires</p>
      <table class="hep-table hep-table-weekly"><thead><tr><th class="hep-group-th"></th><th></th>${weeklyHeaderRow}</tr></thead><tbody>${weeklyRows.join("")}</tbody></table>
      <div class="hep-signatures"><div><p class="hep-section-title">Signatures — validation quotidienne</p>${dailySignatures}</div><div><p class="hep-section-title">Signatures — validation hebdomadaire</p>${weeklySignatures}</div></div>
    </div>`;
  };
  const exportHaccpMonth = () => {
    setHaccpExporting(true);
    apiJSON(`/api/haccp/month/export?storeId=${encodeURIComponent(user?.store)}&year=${haccpMonthYear}&month=${haccpMonthNum}`)
      .then((data) => exportHtmlToPdf({ bodyHtml: buildHaccpPrintHtml(data), css: HEP_DOC_CSS, targetSelector: ".hep-page", widthMm: 297, filename: `HACCP-${user?.store}-${haccpMonthYear}-${haccpMonthNum}.pdf` }))
      .catch((err) => { if (err?.name !== "AbortError") showToast(err.message || t("err.exportImpossible", "Export impossible.")); })
      .finally(() => setHaccpExporting(false));
  };
  // Fiche filiale (Pilotage réseau) — export du mois HACCP d'une filiale consultée, pas
  // forcément la propre filiale du GR connecté. buildHaccpPrintHtml(data) est déjà pur
  // (tout vient de `data`, jamais de user?.store/haccpMonthYear) — contrairement à
  // buildArbeitsplanPrintHtml, réutilisable tel quel sans risque d'imprimer la mauvaise
  // filiale (voir storeflow_fiche_filiale_rubrique_planning).
  const exportPilotHaccpMonth = (storeId, year, month) => {
    apiJSON(`/api/haccp/month/export?storeId=${encodeURIComponent(storeId)}&year=${year}&month=${month}`)
      .then((data) => exportHtmlToPdf({ bodyHtml: buildHaccpPrintHtml(data), css: HEP_DOC_CSS, targetSelector: ".hep-page", widthMm: 297, filename: `HACCP-${storeId}-${year}-${month}.pdf` }))
      .catch(() => {});
  };
  const haccpGroupLabel = (scope, groupId) => {
    const label = (scope === "weekly" ? haccpConfig.weeklyGroups : haccpConfig.dailyGroups).find((g) => g.id === groupId)?.label;
    return label ? t(`haccp.group.${groupId}.label`, label) : groupId;
  };
  const haccpItemLabelIn = (scope, groupId, itemId) => {
    const label = (scope === "weekly" ? haccpConfig.weeklyGroups : haccpConfig.dailyGroups).find((g) => g.id === groupId)?.items.find((i) => i.id === itemId)?.label;
    return label ? t(`haccp.group.${groupId}.item.${itemId}.label`, label) : itemId;
  };
  const haccpJournalEntries = () => {
    const dailyH = (haccpRecord?.history || []).map((h) => ({ ...h, scope: "daily" }));
    const weeklyH = (haccpWeeklyRecord?.history || []).map((h) => ({ ...h, scope: "weekly" }));
    return [...dailyH, ...weeklyH].sort((a, b) => new Date(b.at) - new Date(a.at));
  };

  /* Réclamations */
  const currentDefects = claimDefects[draftClaimType];
  const addDefectToList = () => {
    if (!newDefectDraft.trim()) return;
    setClaimDefects((d) => ({ ...d, [draftClaimType]: [...d[draftClaimType], newDefectDraft.trim()] }));
    setNewDefectDraft("");
  };
  const saveClaimItem = () => {
    if (!claimItemForm.artNr.trim()) { setClaimItemError("artNr"); showToast(t("toast.articleNumberRequired", "Numéro d'article obligatoire.")); return; }
    if (!claimItemForm.defect) { setClaimItemError("defect"); showToast(t("toast.qualityDefectRequired", "Défaut qualité obligatoire.")); return; }
    if (!claimItemForm.qty) { setClaimItemError("qty"); showToast(t("toast.indicatePiecesOrWeight", "Indique un nombre de pièces ou un poids en kilos.")); return; }
    setClaimItemError(null);
    const item = { ...claimItemForm, id: claimItemForm.id || "i" + (++CID) };
    setDraftClaimItems((its) => claimItemForm.id ? its.map((x) => x.id === item.id ? item : x) : [...its, item]);
    setSheet(null); setClaimItemForm(null);
  };
  const removeDraftItem = (id) => setDraftClaimItems((its) => its.filter((x) => x.id !== id));
  const submitClaim = () => {
    if (draftClaimItems.length === 0) { showToast(t("toast.addAtLeastOneArticle", "Ajoute au moins un article.")); return; }
    const items = draftClaimItems.map((it) => ({
      artNr: it.artNr, name: it.name,
      pieces: it.qtyType === "pieces" ? Number(it.qty) || 0 : "",
      kilos: it.qtyType === "kilos" ? Number(it.qty) || 0 : "",
      cartons: it.qtyType === "cartons" ? Number(it.qty) || 0 : "",
      chargeLossNr: it.chargeLossNr, defect: it.defect, note: it.note, photos: it.photos,
    }));
    apiJSON("/api/claims", { method: "POST", body: JSON.stringify({ type: draftClaimType, items }) })
      .then(() => loadClaims())
      .catch((err) => showToast(err.message || t("err.claimCreationImpossible", "Impossible de créer la réclamation.")));
    setDraftClaimItems([]); setDraftClaimType("fresh");
    setClaimsScreen("history"); setClaimsHistoryTab("open");
    showToast(t("toast.claimCreated", "✓ Réclamation créée"));
  };
  const sendClaim = (id) => {
    if (!claimDestination.method) { showToast(t("toast.configureDestinationFirst", "Configure d'abord la destination d'envoi.")); setClaimsScreen("settings"); return; }
    setClaims((cs) => cs.map((c) => c.id === id ? { ...c, status: "sent", sentAt: new Date().toISOString() } : c));
    apiJSON(`/api/claims/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status: "sent", sentVia: claimDestination.method, sentTo: claimDestination.value }),
    }).catch((err) => { showToast(err.message || t("err.sendingRefused", "Envoi refusé.")); loadClaims(); });
    showToast(t("toast.claimSentToHq", "📤 Réclamation envoyée à la centrale"));
  };
  // Envoi groupé — retour utilisateur explicite (2026-08-19) : cliquer "Envoyer" une par
  // une devenait pénible avec plusieurs réclamations ouvertes. Même règle que sendClaim
  // (destination obligatoire), un seul PATCH par réclamation (pas de route bulk dédiée
  // côté serveur — inutile pour ce volume, jamais des centaines de réclamations à la fois).
  const sendAllOpenClaims = () => {
    if (!claimDestination.method) { showToast(t("toast.configureDestinationFirst", "Configure d'abord la destination d'envoi.")); setClaimsScreen("settings"); return; }
    const openIds = claims.filter((c) => c.status === "open" && !c.trashed).map((c) => c.id);
    if (openIds.length === 0) return;
    const sentAt = new Date().toISOString();
    setClaims((cs) => cs.map((c) => openIds.includes(c.id) ? { ...c, status: "sent", sentAt } : c));
    Promise.all(openIds.map((id) => apiJSON(`/api/claims/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status: "sent", sentVia: claimDestination.method, sentTo: claimDestination.value }),
    }))).catch((err) => { showToast(err.message || t("err.sendingRefused", "Envoi refusé.")); loadClaims(); });
    showToast(t("toast.allClaimsSentToHq", { n: openIds.length }, `📤 ${openIds.length} réclamation(s) envoyée(s) à la centrale`));
  };
  const markClaimDone = (id) => {
    setClaims((cs) => cs.map((c) => c.id === id ? { ...c, status: "done" } : c));
    apiJSON(`/api/claims/${id}`, { method: "PATCH", body: JSON.stringify({ status: "done" }) })
      .catch((err) => { showToast(err.message || t("err.actionRefused", "Action refusée.")); loadClaims(); });
    showToast(t("toast.claimMarkedProcessed", "✓ Réclamation marquée traitée"));
  };
  const trashClaim = (id) => {
    setClaims((cs) => cs.map((c) => c.id === id ? { ...c, trashed: true } : c));
    apiJSON(`/api/claims/${id}`, { method: "DELETE" }).catch((err) => { showToast(err.message || t("err.actionRefused", "Action refusée.")); loadClaims(); });
    showToast(t("toast.movedToTrashF", "Déplacée vers la corbeille"));
  };
  const restoreClaim = (id) => {
    setClaims((cs) => cs.map((c) => c.id === id ? { ...c, trashed: false } : c));
    // PATCH /:id/restore ajouté côté serveur spécifiquement pour ce bouton — l'ancien
    // frontend vanilla n'a pas d'équivalent, jamais appelé avant cette migration.
    apiJSON(`/api/claims/${id}/restore`, { method: "PATCH" }).catch((err) => { showToast(err.message || t("err.restoreRefused", "Restauration refusée.")); loadClaims(); });
    showToast(t("toast.claimRestored", "↩︎ Réclamation restaurée"));
  };
  const deleteClaimForever = (id) => {
    setClaims((cs) => cs.filter((c) => c.id !== id));
    apiJSON(`/api/claims/${id}/purge`, { method: "DELETE" }).catch((err) => { showToast(err.message || t("toast.deletionRefused", "Suppression refusée.")); loadClaims(); });
    showToast(t("toast.permanentlyDeletedF", "Supprimée définitivement"));
  };
  const saveDestination = () => {
    if (claimDestination.method !== "other" && !claimDestination.value.trim()) { showToast(t("toast.indicateDestinationValue", "Indique la valeur de destination.")); return; }
    apiJSON("/api/claims/settings", { method: "POST", body: JSON.stringify({ method: claimDestination.method, value: claimDestination.value }) })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
    showToast(t("toast.destinationSaved", "✓ Destination d'envoi enregistrée"));
  };
  const openClaimsCount = claims.filter((c) => c.status === "open" && !c.trashed).length;

  /* Réclamations — gestion des défauts et de la liste d'articles Fruits & légumes */
  const addDefectEntry = (kind) => {
    const value = newDefectInput[kind].trim();
    if (!value) return;
    setClaimDefects((cd) => {
      if (cd[kind].some((d) => d.toLowerCase() === value.toLowerCase())) return cd;
      const next = { ...cd, [kind]: [...cd[kind], value] };
      saveClaimDefects(next);
      return next;
    });
    setNewDefectInput({ ...newDefectInput, [kind]: "" });
  };
  const removeDefectEntry = (kind, idx) => setClaimDefects((cd) => {
    const next = { ...cd, [kind]: cd[kind].filter((_, i) => i !== idx) };
    saveClaimDefects(next);
    return next;
  });
  const saveClaimDefects = (next) => {
    apiJSON("/api/claims/settings", { method: "POST", body: JSON.stringify({ defectsFresh: next.fresh, defectsFood: next.food }) })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  const addFreshArticleEntry = () => {
    if (!newArticleForm.artNr.trim() || !newArticleForm.name.trim()) { showToast(t("toast.numberAndNameRequired", "Numéro et désignation obligatoires.")); return; }
    apiJSON("/api/claims/fresh-articles", { method: "POST", body: JSON.stringify({ artNr: newArticleForm.artNr.trim(), name: newArticleForm.name.trim() }) })
      .then(() => loadFreshArticles())
      .catch((err) => showToast(err.message || t("err.additionImpossible", "Ajout impossible.")));
    setNewArticleForm({ artNr: "", name: "" });
    showToast(t("toast.articleAdded", "✓ Article ajouté"));
  };
  const removeFreshArticle = (id) => {
    setFreshArticles((fa) => fa.filter((a) => a.id !== id));
    apiJSON(`/api/claims/fresh-articles/${id}`, { method: "DELETE" }).catch((err) => { showToast(err.message || t("toast.deletionRefused", "Suppression refusée.")); loadFreshArticles(); });
  };
  const importFreshArticlesFromText = () => {
    if (!articlesImportText.trim()) { showToast(t("claims.toast.pasteAtLeastOneLine", "Colle au moins une ligne.")); return; }
    apiJSON("/api/claims/fresh-articles/import", { method: "POST", body: JSON.stringify({ text: articlesImportText }) })
      .then((data) => { loadFreshArticles(); showToast(t("toast.articlesImportedTemplate", { n: data.count }, `✓ ${data.count} article${data.count > 1 ? "s" : ""} importé${data.count > 1 ? "s" : ""}`)); })
      .catch((err) => showToast(err.message || t("err.importImpossibleFormatTemplate", "Import impossible — format attendu : Art.Nr ; Désignation.")));
    setArticlesImportText("");
  };

  /* PLU Quiz — chargement des vraies données. loadPluActiveList/loadPluProgress/
     loadPluAttempts sont appelés depuis l'effet principal (avec les autres loadX()) ;
     loadPluLists/loadPluWeeklySummary sont appelés à la demande (écrans admin/supervision,
     réservés managers) pour ne jamais faire un appel 403 inutile aux autres employés. */
  const loadPluActiveList = () => {
    apiJSON("/api/plu-quiz/lists/active").then((data) => { setPluActiveList(data.list); setPluAccessDenied(false); })
      .catch((err) => { setPluActiveList(null); setPluAccessDenied(err.status === 403); });
  };
  const loadPluProgress = () => {
    apiJSON("/api/plu-quiz/progress").then((data) => {
      const map = {};
      (data.entries || []).forEach((e) => { map[e.plu] = e; });
      setPluProgress(map);
    }).catch(() => {});
  };
  const loadPluAttempts = () => {
    apiJSON("/api/plu-quiz/attempts").then((data) => setPluAttempts((data.attempts || []).slice(0, 5)))
      .catch(() => setPluAttempts([]));
  };
  const loadPluTrainingAttempts = () => {
    apiJSON("/api/plu-quiz/training-attempts").then((data) => setPluTrainingAttempts((data.attempts || []).slice(0, 5)))
      .catch(() => setPluTrainingAttempts([]));
  };
  const loadPluLists = () => {
    apiJSON("/api/plu-quiz/lists").then((data) => setPluLists(data.lists || []))
      .catch((err) => showToast(err.message || t("err.loadingImpossible", "Chargement impossible.")));
  };
  const loadPluWeeklySummary = () => {
    apiJSON("/api/plu-quiz/weekly-summary").then((data) => setPluWeeklySummary(data.summary || []))
      .catch(() => setPluWeeklySummary([]));
  };
  const activatePluList = (id) => {
    apiJSON(`/api/plu-quiz/lists/${id}/activate`, { method: "POST" })
      .then(() => { loadPluLists(); loadPluActiveList(); showToast(t("toast.listActivated", "✓ Liste activée")); })
      .catch((err) => showToast(err.message || t("err.activationImpossible", "Activation impossible.")));
  };
  const deletePluList = (id) => {
    if (!window.confirm(t("confirm.deletePluListPermanently", "Supprimer définitivement cette liste PLU ?"))) return;
    apiJSON(`/api/plu-quiz/lists/${id}`, { method: "DELETE" })
      .then(() => { loadPluLists(); showToast(t("toast.listDeleted", "🗑️ Liste supprimée")); })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  const savePluListDraft = () => {
    if (!pluListDraft) return;
    apiJSON("/api/plu-quiz/lists", { method: "POST", body: JSON.stringify(pluListDraft) })
      .then(() => { loadPluLists(); loadPluActiveList(); setPluListDraft(null); showToast(t("toast.listSaved", "✓ Liste enregistrée")); })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  const startNewPluListDraft = () => setPluListDraft({
    kw: isoWeek(new Date()) + 1, year: new Date().getFullYear(), label: "", status: "archived",
    examDurationMinutes: 15, examQuestionCount: 20, categories: [],
  });
  // Dupliquer la semaine active : préremplit KW+1 avec les mêmes catégories/articles,
  // aucun id (POST /api/plu-quiz/lists sans id force une création, jamais un écrasement).
  const duplicateActivePluList = () => {
    if (!pluActiveList) { showToast(t("toast.noActiveListToDuplicate", "Aucune liste active à dupliquer.")); return; }
    setPluListDraft({
      kw: pluActiveList.kw + 1, year: pluActiveList.year, label: pluActiveList.label,
      status: "archived", examDurationMinutes: pluActiveList.examDurationMinutes,
      examQuestionCount: pluActiveList.examQuestionCount, categories: pluActiveList.categories,
    });
  };

  /* PLU Quiz — génération de questions (locale, logique pure), mais catalogue/pondération/
     examens/envoi réels, câblés sur GET/POST /api/plu-quiz/* (voir loadPluActiveList,
     loadPluProgress, loadPluAttempts plus bas, appelés depuis l'effet principal). */
  const shuffleArr = (arr) => { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[a[i], a[j]] = [a[j], a[i]]; } return a; };
  const pickDistractors = (article, pool, n) => shuffleArr(pool.filter((a) => a.plu !== article.plu)).slice(0, n);
  const normalizePlu = (v) => String(v ?? "").trim().replace(/^0+(?=\d)/, "");
  const normalizeText = (v) => String(v ?? "").trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const coreName = (v) => normalizeText(String(v ?? "").replace(/\([^)]*\)/g, "").replace(/"[^"]*"/g, ""));
  const QUESTION_TYPES = ["article-to-plu", "plu-to-article", "multiple-choice", "free-text", "true-false"];
  /* 5 vraies formes de question (fidèle à plu-quiz.js) : 2 en QCM (choisir le PLU
     ou l'article parmi 4), 2 en texte libre (taper le PLU ou le nom d'article),
     1 en vrai/faux (le PLU montré est-il le bon, 50/50). */
  const generateQuestion = (article, pool) => {
    const type = QUESTION_TYPES[Math.floor(Math.random() * QUESTION_TYPES.length)];
    const base = { article: article.name, plu: article.plu, categoryLabel: article.categoryLabel, type };
    if (type === "article-to-plu") {
      return { ...base, mode: "text", inputMode: "numeric", prompt: article.name, placeholder: "Code PLU", correctAnswer: article.plu };
    }
    if (type === "plu-to-article") {
      const choices = shuffleArr([article.name, ...pickDistractors(article, pool, 3).map((a) => a.name)]);
      return { ...base, mode: "choice", prompt: `PLU ${article.plu}`, choices, correctAnswer: article.name };
    }
    if (type === "free-text") {
      return { ...base, mode: "text", prompt: `Quel article correspond au PLU ${article.plu} ?`, placeholder: "Nom de l'article", correctAnswer: article.name };
    }
    if (type === "true-false") {
      const wrongCandidate = pickDistractors(article, pool, 1)[0];
      const isTrue = Math.random() < 0.5 || !wrongCandidate;
      const shownPlu = isTrue ? article.plu : wrongCandidate.plu;
      return { ...base, mode: "truefalse", prompt: `${article.name} = PLU ${shownPlu} ?`, correctAnswer: shownPlu === article.plu ? "vrai" : "faux" };
    }
    const choices = shuffleArr([article.plu, ...pickDistractors(article, pool, 3).map((a) => a.plu)]);
    return { ...base, mode: "choice", prompt: article.name, choices, correctAnswer: article.plu };
  };
  const weightedShuffle = (pool) => {
    const remaining = [...pool]; const out = [];
    while (remaining.length) {
      const weights = remaining.map((a) => pluProgress[a.plu]?.weight ?? 1);
      const total = weights.reduce((s, w) => s + w, 0);
      let r = Math.random() * total; let idx = 0;
      for (; idx < weights.length - 1; idx++) { r -= weights[idx]; if (r <= 0) break; }
      out.push(remaining.splice(idx, 1)[0]);
    }
    return out;
  };
  // Aplatit la vraie liste active (catégories/articles réels) — reconstruit à chaque
  // rendu à partir de pluActiveList, jamais recopié en dur.
  const pluAllArticles = (pluActiveList?.categories || []).flatMap((c) => c.articles.map((a) => ({ ...a, categoryId: c.id, categoryLabel: c.label })));
  const articlesForCategory = (catId) => catId === "all" ? pluAllArticles : pluAllArticles.filter((a) => a.categoryId === catId);
  const currentQ = quizQuestions[quizIndex];
  const [revealed, setRevealed] = useState(false);

  const startTraining = (catId, pace) => {
    const pool = articlesForCategory(catId);
    if (!pool.length) { showToast(t("toast.noArticleInCategory", "Aucun article dans cette catégorie.")); return; }
    const questions = weightedShuffle(pool).map((a) => generateQuestion(a, pool));
    setQuizCategory(catId); setQuizMode("training"); setQuizPace(pace);
    setQuizQuestions(questions); setQuizIndex(0); setQuizAnswers([]); setQuizSelected(null); setQuizTextAnswer(""); setRevealed(false);
    const now = Date.now();
    setQuizStart(now);
    const durSec = pace === "timed" ? 15 : pace === "simulation" ? (pluActiveList?.examDurationMinutes || 15) * 60 : null;
    setQuizEndAt(durSec != null ? now + durSec * 1000 : null);
    setQuizTimeLeft(durSec);
    setPluScreen("quiz");
  };
  const startExam = () => {
    // L'examen couvre TOUS les articles de la liste active réelle (mélangés), jamais
    // un sous-ensemble — examQuestionCount reste informatif (fidèle au vanilla).
    const chosen = shuffleArr(pluAllArticles);
    const questions = chosen.map((a) => generateQuestion(a, pluAllArticles));
    setQuizMode("exam"); setQuizPace("timed");
    setQuizQuestions(questions); setQuizIndex(0); setQuizAnswers([]); setQuizSelected(null); setQuizTextAnswer(""); setRevealed(false);
    const now = Date.now();
    const durSec = (pluActiveList?.examDurationMinutes || 15) * 60;
    setQuizStart(now); setQuizEndAt(now + durSec * 1000); setQuizTimeLeft(durSec);
    setPluScreen("quiz");
  };
  const recordAnswer = (given) => {
    const q = currentQ;
    let correct = false;
    if (given != null) {
      if (q.mode === "choice" || q.mode === "truefalse") correct = String(given).trim() === String(q.correctAnswer).trim();
      else if (q.type === "article-to-plu") correct = normalizePlu(given) === normalizePlu(q.correctAnswer);
      else if (q.type === "free-text") correct = coreName(given) === coreName(q.correctAnswer);
    }
    setQuizAnswers((a) => [...a, { plu: q.plu, article: q.article, category: q.categoryLabel, questionType: q.type, given, correct }]);
    if (quizMode === "training") {
      // Poids JAMAIS recalculé côté client — seule la formule réelle du serveur (streak,
      // bornes .2-8) fait foi, pour ne jamais diverger d'un employé à l'autre.
      apiJSON("/api/plu-quiz/progress", { method: "POST", body: JSON.stringify({ plu: q.plu, correct }) })
        .then((data) => setPluProgress((p) => ({ ...p, [q.plu]: data.entry })))
        .catch(() => {});
    }
  };
  const nextQuestion = () => {
    setQuizSelected(null); setRevealed(false); setQuizTextAnswer("");
    if (quizIndex + 1 >= quizQuestions.length) { finishQuiz(false); return; }
    setQuizIndex((i) => i + 1);
    if (quizPace === "timed") { const now = Date.now(); setQuizEndAt(now + 15000); setQuizTimeLeft(15); }
  };
  const validateAnswer = () => {
    const given = currentQ.mode === "text" ? quizTextAnswer.trim() : quizSelected;
    if (!given) { showToast(currentQ.mode === "text" ? "Tape une réponse." : "Choisis une réponse."); return; }
    recordAnswer(given);
    if (quizMode === "training" && quizPace === "free") setRevealed(true); else nextQuestion();
  };
  const finishQuiz = (timedOut) => {
    const totalQ = quizQuestions.length;
    const correctCount = quizAnswers.filter((a) => a.correct).length;
    const incorrectCount = quizAnswers.filter((a) => !a.correct && a.given != null).length;
    const unansweredCount = totalQ - quizAnswers.length;
    const percentage = totalQ > 0 ? Math.round((correctCount / totalQ) * 100) : 0;
    const durationSec = Math.round((Date.now() - quizStart) / 1000);
    const report = { percentage, passed: percentage >= 80, correct: correctCount, incorrect: incorrectCount, unanswered: unansweredCount, durationSec, timedOut, detail: quizAnswers, mode: quizMode };
    if (quizMode === "exam") {
      const payload = {
        listId: pluActiveList?.id, kw: pluActiveList?.kw,
        startedAt: new Date(quizStart).toISOString(), finishedAt: new Date().toISOString(),
        durationAllowedSec: (pluActiveList?.examDurationMinutes || 15) * 60, durationUsedSec: durationSec,
        totalQuestions: totalQ, correctCount, incorrectCount, unansweredCount, percentage,
        passed: percentage >= 80, timedOut,
        answers: quizAnswers.map((a) => ({
          category: a.category, article: a.article, plu: a.plu, questionType: a.questionType,
          givenAnswer: a.given, result: a.correct ? "correct" : (a.given != null ? "incorrect" : "unanswered"),
        })),
        signature: examSignature,
        ...(pluTask?.id ? { sourceTaskId: pluTask.id } : {}),
      };
      apiJSON("/api/plu-quiz/attempts", { method: "POST", body: JSON.stringify(payload) })
        .then((data) => {
          setLastReport({ ...report, attemptId: data.attempt.id, sentToManagerAt: data.attempt.sentToManagerAt });
          setPluAttempts((a) => [data.attempt, ...a].slice(0, 5));
          setPluTask(null);
          loadTasks();
        })
        .catch((err) => { showToast(err.message || t("err.examSendImpossible", "Envoi de l'examen impossible.")); setLastReport(report); });
    } else {
      setLastReport(report);
      // Historique training — retour utilisateur explicite (2026-08-19) : sauvegarde
      // silencieuse en arrière-plan (n'affecte jamais l'affichage du rapport, déjà montré
      // ci-dessus via setLastReport quel que soit le résultat de cet appel).
      const categoryLabel = quizCategory === "all"
        ? t("articles.filterAll", "Tous")
        : (pluActiveList?.categories || []).find((c) => c.id === quizCategory)?.label || quizCategory;
      apiJSON("/api/plu-quiz/training-attempts", {
        method: "POST",
        body: JSON.stringify({
          listId: pluActiveList?.id, kw: pluActiveList?.kw, category: quizCategory, categoryLabel, pace: quizPace,
          finishedAt: new Date().toISOString(), durationSec, totalQuestions: totalQ, correctCount, incorrectCount, unansweredCount, percentage,
        }),
      }).then((data) => setPluTrainingAttempts((a) => [data.attempt, ...a].slice(0, 5))).catch(() => {});
    }
    setPluScreen("report");
  };
  useEffect(() => {
    if (pluScreen !== "quiz" || quizEndAt == null) return;
    const tick = () => {
      const left = Math.max(0, Math.round((quizEndAt - Date.now()) / 1000));
      setQuizTimeLeft(left);
      if (left <= 0) {
        if (quizPace === "timed" && quizMode === "training") { recordAnswer(null); nextQuestion(); }
        else finishQuiz(true);
      }
    };
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [pluScreen, quizEndAt]);
  const sendReportToManager = () => {
    if (!lastReport?.attemptId) return;
    apiJSON(`/api/plu-quiz/attempts/${lastReport.attemptId}/send-to-manager`, { method: "POST" })
      .then((data) => { setLastReport((r) => ({ ...r, sentToManagerAt: data.attempt.sentToManagerAt })); showToast(t("toast.reportSentToManager", "📤 Rapport envoyé au responsable")); })
      .catch((err) => showToast(err.message || t("err.sendingImpossible", "Envoi impossible.")));
  };

  /* Signature manuscrite (canvas) pour certifier l'examen — remplace la
     case à cocher : le candidat doit réellement tracer sa signature. */
  const sigPointerPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    // Le canvas affiché (CSS) et sa résolution de dessin interne diffèrent :
    // on remet à l'échelle pour que le trait suive exactement le doigt.
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };
  const sigStart = (e) => {
    e.preventDefault();
    const canvas = sigCanvasRef.current; if (!canvas) return;
    const { x, y } = sigPointerPos(e, canvas);
    const ctx = canvas.getContext("2d");
    ctx.beginPath(); ctx.moveTo(x, y);
    sigDrawingRef.current = true;
  };
  const sigMove = (e) => {
    if (!sigDrawingRef.current) return;
    e.preventDefault();
    const canvas = sigCanvasRef.current; if (!canvas) return;
    const { x, y } = sigPointerPos(e, canvas);
    const ctx = canvas.getContext("2d");
    ctx.lineTo(x, y); ctx.strokeStyle = "#1c1913"; ctx.lineWidth = 2.6; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.stroke();
    if (!hasSignature) setHasSignature(true);
  };
  const sigEnd = () => { sigDrawingRef.current = false; };
  const clearSignature = () => {
    const canvas = sigCanvasRef.current;
    if (canvas) canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };
  const openExamCertify = () => { clearSignature(); setExamSignature(null); setSheet("examCertify"); };
  const signAndStartExam = () => {
    if (!hasSignature) { showToast(t("toast.traceSignatureToContinue", "Trace ta signature dans le cadre pour continuer.")); return; }
    const dataUrl = sigCanvasRef.current.toDataURL();
    setExamSignature({ dataUrl, at: new Date().toISOString() });
    setSheet(null);
    startExam();
  };

  /* Signature dédiée Infos importantes (canvas séparé de celui du PLU Quiz) */
  const infoSigPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * (canvas.width / rect.width), y: (clientY - rect.top) * (canvas.height / rect.height) };
  };
  const infoSigStart = (e) => {
    e.preventDefault();
    const canvas = infoSigCanvasRef.current; if (!canvas) return;
    const { x, y } = infoSigPos(e, canvas);
    canvas.getContext("2d").beginPath();
    canvas.getContext("2d").moveTo(x, y);
    infoSigDrawingRef.current = true;
    infoSigLastPtRef.current = { x, y };
  };
  const infoSigMove = (e) => {
    if (!infoSigDrawingRef.current) return;
    e.preventDefault();
    const canvas = infoSigCanvasRef.current; if (!canvas) return;
    const { x, y } = infoSigPos(e, canvas);
    const ctx = canvas.getContext("2d");
    ctx.lineTo(x, y); ctx.strokeStyle = "#1c1913"; ctx.lineWidth = 2.6; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.stroke();
    // Distance de trait cumulée réelle — le serveur exige strokeLength >= 24
    // (POST /:id/sign), garde-fou anti-canvas-jamais-vraiment-dessiné.
    const last = infoSigLastPtRef.current;
    if (last) infoSigStrokeLenRef.current += Math.hypot(x - last.x, y - last.y);
    infoSigLastPtRef.current = { x, y };
    if (!infoHasSignature) setInfoHasSignature(true);
  };
  const infoSigEnd = () => { infoSigDrawingRef.current = false; };
  const clearInfoSignature = () => {
    const canvas = infoSigCanvasRef.current;
    if (canvas) canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setInfoHasSignature(false);
    infoSigStrokeLenRef.current = 0;
    infoSigLastPtRef.current = null;
  };

  /* Krankmeldung — vraies données (server.js), flux fidèle à public/modules/krankmeldung.js :
     un DRAFT est créé dès l'étape 1 (POST), PATCH sur retour arrière, plan-impact et
     preview-contacts réels aux étapes 2/3 (jamais un compteur local), upload réel à
     l'étape 4, submit réel à l'étape 5. La confirmation d'appel (statuts détaillés) est
     réservée aux managers dans le détail après coup — le salarié ne confirme jamais ses
     propres appels dans l'assistant, fidèle au vrai contrat serveur (POST /:id/contacts
     hors self-service, contrairement à l'ancien comportement 100% local). */
  const canManageKrank = usePermission(user, "krankmeldung.manage");
  const canCloseKrank = usePermission(user, "krankmeldung.close");

  const loadKrankmeldungen = () => {
    apiJSON("/api/krankmeldungen").then((data) => {
      const all = data.krankmeldungen || [];
      setKrankStoreList(all);
      setKrankmeldungen(all.filter((k) => k.employeeId === user?.proId));
    }).catch(() => { setKrankStoreList([]); setKrankmeldungen([]); });
  };
  const refreshKrankOne = (updated) => {
    setKrankStoreList((ks) => ks.some((k) => k.id === updated.id) ? ks.map((k) => k.id === updated.id ? updated : k) : [updated, ...ks]);
    setKrankmeldungen((ks) => ks.some((k) => k.id === updated.id) ? ks.map((k) => k.id === updated.id ? updated : k) : (updated.employeeId === user?.proId ? [updated, ...ks] : ks));
    setKrankDetail((d) => (d && d.id === updated.id) ? updated : d);
  };

  const startKrankNew = () => {
    setKrankForm(emptyKrankForm); setKrankDraftId(null); setKrankPlanImpact([]); setKrankContactsPreview([]);
    setKrankStep(1); setKrankScreen("new");
  };

  // Étape 1→2 : crée le DRAFT (1ère fois) ou le PATCH (retour en arrière), puis charge le
  // vrai impact planning (dry-run de sfApplyKrankToPlanning) — jamais un compteur local.
  const krankGoStep2 = () => {
    if (!krankForm.startDate) { showToast(t("krankmeldung.toast.missingStartDate", "Indique le premier jour d'absence.")); return; }
    if (!krankForm.endDateUnknown && !krankForm.expectedEndDate) { showToast(t("toast.indicateEstimatedEndOrUnknown", "Indique le dernier jour estimé, ou coche « Fin encore inconnue ».")); return; }
    // Retour utilisateur explicite (2026-08-25) — "jamais de date antérieure à la date du
    // jour", "date de fin toujours après date de début". Contrôle UX ici (le serveur reste
    // la seule vraie barrière, voir POST /api/krankmeldungen).
    if (krankForm.startDate < todayISO()) { showToast(t("krankmeldung.toast.startDateInPast", "Le premier jour d'absence ne peut pas être dans le passé.")); return; }
    if (!krankForm.endDateUnknown && krankForm.expectedEndDate < krankForm.startDate) { showToast(t("krankmeldung.toast.endBeforeStart", "La date de fin ne peut pas être antérieure à la date de début.")); return; }
    const body = {
      startDate: krankForm.startDate, expectedEndDate: krankForm.endDateUnknown ? "" : krankForm.expectedEndDate,
      endDateUnknown: krankForm.endDateUnknown, absenceScope: krankForm.absenceScope,
      partialStartTime: krankForm.absenceScope === "PARTIAL" ? krankForm.partialStartTime : "",
      employeeComment: krankForm.employeeComment.trim(),
    };
    setKrankSubmitting(true);
    const call = krankDraftId
      ? apiJSON(`/api/krankmeldungen/${krankDraftId}`, { method: "PATCH", body: JSON.stringify(body) })
      : apiJSON("/api/krankmeldungen", { method: "POST", body: JSON.stringify(body) });
    call.then((data) => {
      const km = data.krankmeldung;
      setKrankDraftId(km.id);
      return apiJSON(`/api/krankmeldungen/${km.id}/plan-impact`);
    }).then((data) => { setKrankPlanImpact(data.affectedShifts || []); setKrankStep(2); })
      .catch((err) => showToast(err.message || t("err.stepSaveImpossible", "Impossible d'enregistrer cette étape.")))
      .finally(() => setKrankSubmitting(false));
  };

  // Étape 2→3 : vraie résolution des contacts par rôle (SICKNESS_CONTACT_RULES côté
  // serveur), jamais la règle fictive locale d'avant.
  const krankGoStep3 = () => {
    setKrankSubmitting(true);
    apiJSON("/api/krankmeldungen/preview-contacts").then((data) => { setKrankContactsPreview(data.contacts || []); setKrankStep(3); })
      .catch((err) => showToast(err.message || t("err.contactsLoadImpossible", "Impossible de charger les contacts.")))
      .finally(() => setKrankSubmitting(false));
  };

  const krankUploadAu = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !krankDraftId) return;
    setKrankSubmitting(true);
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    apiJSON(`/api/krankmeldungen/${krankDraftId}/attachments`, { method: "POST", body: formData })
      .then((data) => setKrankForm((f) => ({ ...f, auFiles: (data.krankmeldung.attachments || []).map((a) => a.originalName) })))
      .catch((err) => showToast(err.message || t("err.depositImpossible", "Dépôt impossible.")))
      .finally(() => setKrankSubmitting(false));
  };

  const submitKrankmeldung = () => {
    if (!krankDraftId) return;
    setKrankSubmitting(true);
    const finalize = krankForm.auChoice === "none"
      ? apiJSON(`/api/krankmeldungen/${krankDraftId}`, {
          method: "PATCH",
          body: JSON.stringify({ startDate: krankForm.startDate, expectedEndDate: krankForm.endDateUnknown ? "" : krankForm.expectedEndDate, endDateUnknown: krankForm.endDateUnknown, auStatus: "NOT_REQUIRED" }),
        })
      : Promise.resolve();
    finalize.then(() => apiJSON(`/api/krankmeldungen/${krankDraftId}/submit`, { method: "POST" }))
      .then((data) => {
        refreshKrankOne(data.krankmeldung);
        setKrankForm(emptyKrankForm); setKrankDraftId(null); setKrankStep(1);
        setKrankScreen("mine");
        showToast(t("toast.krankmeldungSent", "✓ Krankmeldung envoyée"));
      })
      .catch((err) => showToast(err.message || t("err.sendingImpossible", "Envoi impossible.")))
      .finally(() => setKrankSubmitting(false));
  };

  const openKrankDetail = (id) => {
    setKrankReplacementFor(null); setKrankCorrectForm(null); setKrankExtendForm(null); setKrankEarlyReturnForm(null);
    apiJSON(`/api/krankmeldungen/${id}`).then((data) => { setKrankDetail(data.krankmeldung); setSheet("krankDetail"); })
      .catch((err) => showToast(err.message || t("err.loadingImpossible", "Chargement impossible.")));
  };

  /* Confirmation d'appel — réservée aux managers dans le détail (le salarié ne confirme
     jamais ses propres appels dans l'assistant), 6 vrais statuts d'issue plutôt qu'un
     simple fait/pas fait, horodatés et attribuables côté serveur. */
  const [contactConfirmTarget, setContactConfirmTarget] = useState(null); // { krankId, userId, name }
  const openContactConfirm = (krankId, userId, name) => { setContactConfirmTarget({ krankId, userId, name }); setSheet("contactConfirm"); };
  // Retour utilisateur explicite (2026-08-25, capture d'écran à l'appui) : "quand elle
  // apparaît en tant que vertretung, l'app devrait le mentionner... et le code doit aussi
  // être revu pour que cela ait le même comportement" — un contact de la liste d'appel
  // Krankmeldung n'est plus jamais silencieusement "juste ajouté" : ce texte explique
  // pourquoi il y figure dès que ce n'est pas la résolution directe/normale de son rôle
  // (voir server/krankmeldung/routes.js, deputyFor/fallbackReason/unavailableToday).
  const krankContactContextText = (c) => {
    // Date de fin ajoutée le 14/10/26 (retour utilisateur explicite) — jamais affichée si
    // endDateUnknown (congé sans date de fin connue, rien à afficher plutôt qu'une fausse
    // précision) ; endDate absent (ancien dossier figé avant ce champ) retombe pareil sur le
    // texte simple sans date.
    if (c.deputyFor?.name) {
      const until = (!c.deputyFor.endDateUnknown && c.deputyFor.endDate) ? ` ${t("krankmeldung.contact.deputyForUntilSuffix", { date: fmtFR(c.deputyFor.endDate) }, `jusqu'au ${fmtFR(c.deputyFor.endDate)}`)}` : "";
      return t("krankmeldung.contact.deputyForTemplate", { name: c.deputyFor.name }, `Vertretung — remplace ${c.deputyFor.name}`) + until;
    }
    if (c.unavailableToday) return t("krankmeldung.contact.unavailableToday", "En congé aujourd'hui");
    if (c.fallbackReason) return t("krankmeldung.contact.fallbackReason", "Remplaçant — rôle habituel indisponible aujourd'hui");
    return null;
  };
  const confirmContactCall = (status) => {
    const { krankId, userId } = contactConfirmTarget;
    apiJSON(`/api/krankmeldungen/${krankId}/contacts`, { method: "POST", body: JSON.stringify({ userId, status }) })
      .then((data) => { refreshKrankOne(data.krankmeldung); showToast(`✓ ${t(`krankmeldung.contactStatus.${{ pending: "pending", attempted: "attempted", reached: "reached", message_left: "messageLeft", unreachable: "unreachable", not_in_system: "notInSystem" }[status]}`, CONTACT_STATUS_LABELS[status])}`); })
      .catch((err) => showToast(err.message || t("err.actionRefused", "Action refusée.")));
    setSheet(null); setContactConfirmTarget(null);
  };

  const setKrankAuStatus = (id, attachmentId, status) => {
    apiJSON(`/api/krankmeldungen/${id}/attachments/${attachmentId}/verify`, { method: "POST", body: JSON.stringify({ status }) })
      .then((data) => { refreshKrankOne(data.krankmeldung); showToast(status === "VERIFIED" ? "✓ AU vérifiée" : "AU refusée — à redéposer"); })
      .catch((err) => showToast(err.message || t("err.actionRefused", "Action refusée.")));
  };

  const cancelKrankmeldung = (id) => {
    if (!window.confirm(t("confirm.cancelDeclarationPlanningFreed", "Annuler cette déclaration ? Le planning déjà marqué sera libéré."))) return;
    apiJSON(`/api/krankmeldungen/${id}/cancel`, { method: "POST", body: JSON.stringify({}) })
      .then((data) => { refreshKrankOne(data.krankmeldung); setSheet(null); setKrankDetail(null); showToast(t("toast.declarationCancelled", "Déclaration annulée")); })
      .catch((err) => showToast(err.message || t("err.cancellationImpossible", "Annulation impossible.")));
  };
  // Suppression DÉFINITIVE (retour utilisateur explicite, 2026-08-18) — distincte
  // d'Annuler ci-dessus, qui garde le dossier à l'historique pour une erreur de saisie.
  // Réservée aux managers côté serveur (jamais l'employé lui-même) — le bouton n'est
  // donc affiché que si canManageKrank, jamais pour isOwner seul.
  const deleteKrankmeldung = (k) => {
    if (!window.confirm(t("confirm.deleteKrankmeldung", { name: k.employeeNameSnapshot, date: fmtFR(k.startDate) }, `Supprimer définitivement le dossier maladie de ${k.employeeNameSnapshot} (${fmtFR(k.startDate)}) ? Cette action est irréversible.`))) return;
    apiJSON(`/api/krankmeldungen/${k.id}`, { method: "DELETE" })
      .then(() => {
        setKrankStoreList((ks) => ks.filter((x) => x.id !== k.id));
        setKrankmeldungen((ks) => ks.filter((x) => x.id !== k.id));
        setSheet(null); setKrankDetail(null);
        showToast(t("toast.declarationDeleted", "🗑️ Dossier supprimé"));
      })
      .catch((err) => showToast(err.message || t("err.deletionImpossible", "Suppression impossible.")));
  };

  const closeKrankmeldung = (id) => {
    apiJSON(`/api/krankmeldungen/${id}/close`, { method: "POST", body: JSON.stringify({}) })
      .then((data) => { refreshKrankOne(data.krankmeldung); showToast(t("toast.caseClosed", "✓ Dossier clôturé")); })
      .catch((err) => showToast(err.message || t("err.closingImpossible", "Clôture impossible.")));
  };

  const submitKrankCorrect = () => {
    if (!krankCorrectForm || !krankDetail) return;
    // Volontairement pas de contrôle "pas avant aujourd'hui" ici (correction manager d'un
    // dossier déjà passé) — seul l'ordre début/fin est vérifié, comme côté serveur.
    if (!krankCorrectForm.endDateUnknown && krankCorrectForm.expectedEndDate && krankCorrectForm.expectedEndDate < krankCorrectForm.startDate) {
      showToast(t("krankmeldung.toast.endBeforeStart", "La date de fin ne peut pas être antérieure à la date de début.")); return;
    }
    apiJSON(`/api/krankmeldungen/${krankDetail.id}/correct`, { method: "POST", body: JSON.stringify(krankCorrectForm) })
      .then((data) => { refreshKrankOne(data.krankmeldung); setKrankCorrectForm(null); showToast(t("toast.datesCorrected", "✓ Dates corrigées")); })
      .catch((err) => showToast(err.message || t("err.correctionImpossible", "Correction impossible.")));
  };

  const submitKrankExtend = () => {
    if (!krankExtendForm || !krankDetail) return;
    if (!krankExtendForm.endDateUnknown) {
      const previousEnd = krankDetail.expectedEndDate || krankDetail.startDate;
      if (krankExtendForm.newExpectedEndDate < previousEnd) {
        showToast(t("krankmeldung.toast.extendMustBeLater", "La nouvelle date de fin doit être postérieure à l'ancienne.")); return;
      }
    }
    apiJSON(`/api/krankmeldungen/${krankDetail.id}/extend`, { method: "POST", body: JSON.stringify(krankExtendForm) })
      .then((data) => { refreshKrankOne(data.krankmeldung); setKrankExtendForm(null); showToast(t("toast.extensionSaved", "✓ Prolongation enregistrée")); })
      .catch((err) => showToast(err.message || t("err.extensionImpossible", "Prolongation impossible.")));
  };

  const submitKrankEarlyReturn = () => {
    if (!krankEarlyReturnForm || !krankDetail) return;
    if (krankEarlyReturnForm.actualReturnDate < krankDetail.startDate) {
      showToast(t("krankmeldung.toast.returnBeforeStart", "La date de retour ne peut pas être antérieure au premier jour d'absence.")); return;
    }
    apiJSON(`/api/krankmeldungen/${krankDetail.id}/early-return`, { method: "POST", body: JSON.stringify(krankEarlyReturnForm) })
      .then((data) => { refreshKrankOne(data.krankmeldung); setKrankEarlyReturnForm(null); showToast(t("toast.earlyReturnAnnounced", "✓ Retour anticipé annoncé")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };

  // Remplacement d'un service (Planänderung) — jamais automatique, une décision manager
  // explicite par service concerné (assigner OU laisser explicitement non couvert).
  const openKrankReplacement = (entryId) => {
    setKrankReplacementFor(entryId); setKrankReplacementCandidates([]);
    apiJSON(`/api/krankmeldungen/${krankDetail.id}/replacement-candidates?entryId=${encodeURIComponent(entryId)}`)
      .then((data) => setKrankReplacementCandidates(data.candidates || []))
      .catch((err) => showToast(err.message || t("err.loadingImpossible", "Chargement impossible.")));
  };
  const assignKrankReplacement = (replacementEmployeeId) => {
    apiJSON(`/api/krankmeldungen/${krankDetail.id}/plan-change`, { method: "POST", body: JSON.stringify({ entryId: krankReplacementFor, replacementEmployeeId }) })
      .then((data) => { refreshKrankOne(data.krankmeldung); setKrankReplacementFor(null); showToast(replacementEmployeeId ? "✓ Remplaçant assigné" : "Service marqué non couvert"); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };

  const krankAvatarColor = (name) => [A.navy, A.blue, A.teal, A.orange, "#8b2fd6"][(String(name || "").charCodeAt(0) || 0) % 5];
  const activeKrank = krankmeldungen.find((k) => k.generalStatus === "ACTIVE");
  // Dashboard consolidé secteur : dérivé des données réellement visibles (le serveur ne
  // renvoie plusieurs filiales que si le compte est un rôle secteur — sfKrankVisibleForIdentity),
  // jamais un rôle dupliqué en dur côté client.
  const krankStoreIds = [...new Set(krankStoreList.map((k) => k.storeId))];
  const krankDashboardRows = krankStoreIds.length > 1 ? krankStoreIds.map((storeId) => {
    const rows = krankStoreList.filter((k) => k.storeId === storeId);
    return {
      storeId,
      active: rows.filter((k) => k.generalStatus === "ACTIVE").length,
      planPending: rows.filter((k) => k.generalStatus === "ACTIVE" && k.planStatus === "PENDING").length,
      auMissing: rows.filter((k) => k.generalStatus === "ACTIVE" && ["PENDING", "REJECTED"].includes(k.auStatus)).length,
    };
  }) : [];

  /* Commande matériel */
  const openMoCatalog = (catalogId) => { setMoActiveCatalog(catalogId); setMoQuantities({}); setMoNote(""); setMoScreen("form"); };
  const setMoQty = (articleId, delta) => setMoQuantities((q) => ({ ...q, [articleId]: Math.max(0, (q[articleId] || 0) + delta) }));
  const moSelectedItems = () => {
    if (!moActiveCatalog) return [];
    const items = [];
    materialCatalogs[moActiveCatalog].groups.forEach((g) => g.articles.forEach((a) => {
      const qty = moQuantities[a.id] || 0;
      if (qty > 0) items.push({ id: a.id, name: a.name, artNr: a.artNr, ve: a.ve, qty });
    }));
    return items;
  };
  const submitMaterialOrder = () => {
    const items = moSelectedItems();
    if (items.length === 0) { showToast(t("toast.selectAtLeastOneArticle", "Sélectionne au moins un article.")); return; }
    apiJSON("/api/material-orders", {
      method: "POST",
      body: JSON.stringify({
        catalogId: moActiveCatalog,
        items: items.map((i) => ({ articleId: i.id, qty: i.qty })),
        note: moNote.trim(),
      }),
    })
      .then(() => loadMaterialOrders())
      .catch((err) => showToast(err.message || t("err.orderSlipSendImpossible", "Impossible d'envoyer le bon de commande.")));
    setMoScreen("history"); setMoActiveCatalog(null); setMoQuantities({}); setMoNote("");
    showToast(t("toast.orderSlipSent", "✓ Bon de commande envoyé"));
  };
  const markOrderSent = (id) => {
    setMaterialOrders((os) => os.map((o) => o.id === id ? { ...o, status: "sent", sentBy: user?.firstname || "Toi", sentAt: new Date().toISOString() } : o));
    apiJSON(`/api/material-orders/${id}/mark-sent`, { method: "POST" }).catch((err) => { showToast(err.message || t("err.actionRefused", "Action refusée.")); loadMaterialOrders(); });
    setSheet(null); setMoDetail(null);
    showToast(t("toast.markedAsSentF", "📤 Marquée comme envoyée"));
  };
  const pendingOrdersCount = materialOrders.filter((o) => o.status === "pending").length;

  /* Commandes de marchandises (nouveau module) — voir le bloc d'état plus haut pour le
     contexte général. */
  const loadMerchCategories = () => {
    apiJSON("/api/merchandise-orders/categories")
      .then((data) => setMerchCategories(data.categories || []))
      .catch(() => {});
    // Commande brouillon/en_cours la plus récente par catégorie — alimente le badge "Commande
    // en cours" sur chaque tuile du Hub (voir resumeMerchOrder). Déjà trié par updatedAt desc
    // côté serveur, donc la 1ère rencontrée par zone est la bonne.
    apiJSON("/api/merchandise-orders")
      .then((data) => {
        const map = {};
        (data.orders || []).forEach((o) => {
          if ((o.status === "brouillon" || o.status === "en_cours") && !map[o.zone]) map[o.zone] = o;
        });
        setMerchActiveOrders(map);
      })
      .catch(() => {});
  };
  const loadMerchOrders = (zone) => {
    apiJSON(`/api/merchandise-orders${zone ? `?zone=${encodeURIComponent(zone)}` : ""}`)
      .then((data) => setMerchOrders(data.orders || []))
      .catch(() => {});
  };
  // Rappel de commande du jour (2026-08-31) — voir GET /reminder-today (server), le popup
  // dédié plus bas (mêmes mécanique/cooldown que "Tâches à faire", voir son commentaire) et
  // l'écran de configuration (grille rayon × jour + heure, Hub Commande de marchandises).
  const loadMerchOrderReminder = () => {
    apiJSON("/api/merchandise-orders/reminder-today")
      .then((data) => setMerchOrderReminder({ zones: data.zones || [], weekday: data.weekday || null, date: data.date || "" }))
      .catch(() => {});
  };
  // Identifiants de jour STABLES (2026-08-31, écran de planning ci-dessous) — jamais
  // MERCH_WEEKDAYS/merchWeekdayOrder (labels déjà traduits, ex. "Mo" en allemand) : le serveur
  // compare orderDays à weekdayIdFromISODate, qui renvoie TOUJOURS "Lu".."Sa" quelle que soit
  // la langue de l'interface — envoyer un libellé traduit casserait la comparaison pour tout
  // compte non francophone. MERCH_SCHEDULE_DAY_LABELS = affichage uniquement.
  const MERCH_SCHEDULE_DAY_IDS = ["Lu", "Ma", "Me", "Je", "Ve", "Sa"];
  const MERCH_SCHEDULE_DAY_LABELS = [1, 2, 3, 4, 5, 6].map((d) => t(`haccp.dow.${d}`, MERCH_SCHEDULE_DAY_IDS[d - 1]));
  // Enregistre immédiatement (pas de bouton "Enregistrer" séparé — écran dédié à ce seul
  // réglage) et recharge le rappel du jour tout de suite (un changement peut faire apparaître/
  // disparaître le rayon du jour même en restant sur cet écran).
  const patchMerchCategoryDays = (zone, patch) => {
    const current = merchCategories.find((c) => c.zone === zone) || { zone, orderDays: [], deliveryDays: [], orderHour: null, articleCount: 0 };
    const next = { ...current, ...patch };
    setMerchCategories((list) => {
      const idx = list.findIndex((c) => c.zone === zone);
      if (idx === -1) return [...list, next];
      const copy = [...list]; copy[idx] = next; return copy;
    });
    apiJSON(`/api/merchandise-orders/categories/${encodeURIComponent(zone)}`, {
      method: "PATCH",
      body: JSON.stringify({ orderDays: next.orderDays, deliveryDays: next.deliveryDays, orderHour: next.orderHour }),
    }).then(() => loadMerchOrderReminder()).catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  const toggleMerchOrderDay = (zone, day) => {
    const days = (merchCategories.find((c) => c.zone === zone)?.orderDays) || [];
    patchMerchCategoryDays(zone, { orderDays: days.includes(day) ? days.filter((d) => d !== day) : [...days, day] });
  };
  // Pré-remplit la zone Commande avec la ligne déjà enregistrée pour cet article (reprise
  // d'un brouillon interrompu) — sinon 0 tant qu'aucun Bestand réel n'a été saisi (retour
  // utilisateur explicite, 2026-08-31, capture à l'appui : "il doit être toujours à 0 tant
  // que l'utilisateur n'a encore entré aucune valeur de bestand" — corrige le comportement du
  // 2026-08-25 qui proposait directement la quantité en supposant Bestand=0 à l'arrivée,
  // avant même que l'humain ait regardé le rayon). La recommandation Nexora ne se pré-remplit
  // désormais QUE via l'effet de suivi juste après, déclenché par une vraie saisie de
  // merchIstBestand — jamais ici, à l'arrivée sur l'article. Une ligne DÉJÀ enregistrée reste
  // seule maîtresse — jamais écrasée par une recommandation, même si elle a changé depuis.
  const primeMerchLineInputs = (article, order, zone = merchActiveZone) => {
    if (!article) return;
    const existingLine = order?.lines?.find((l) => l.articleId === article.id);
    merchQtyAutoRef.current = !existingLine;
    setMerchQty(existingLine ? existingLine.quantity : 0);
    const allowed = merchAllowedUnits(zone);
    const fallbackUnit = allowed.includes(article.uniteParDefaut) ? article.uniteParDefaut : allowed[0];
    setMerchUnit(existingLine && allowed.includes(existingLine.unit) ? existingLine.unit : fallbackUnit);
    setMerchArtNrEditing(false);
    setMerchUnitMenuOpen(false);
    // IST-Bestand Kühlung — reprend la valeur déjà saisie pour cet article sur cette
    // commande (même logique de reprise que merchQty/merchUnit ci-dessus).
    setMerchIstBestand(existingLine && existingLine.istBestand != null ? String(existingLine.istBestand) : "");
    setMerchIstBestandApprox(existingLine ? existingLine.istBestandApprox === true : false);
    setMerchFaktorEditing(false);
  };
  // Suit la recommandation en direct tant que l'utilisateur n'a pas repris la main sur la
  // quantité (merchQtyAutoRef, désarmé par l'onChange du champ quantité) — se déclenche
  // UNIQUEMENT une fois qu'un vrai Bestand a été saisi (merchIstBestand non vide) : tant que
  // le champ est vide, ne touche jamais merchQty, qui reste à 0 tel que posé par
  // primeMerchLineInputs (2026-08-31, retour utilisateur explicite — voir son commentaire).
  // Se réévalue aussi quand le Faktor est édité en place, une fois un Bestand déjà saisi.
  useEffect(() => {
    if (merchScreen !== "article" || !merchQtyAutoRef.current) return;
    if (merchIstBestand === "") return;
    const order = merchSession?.order;
    const article = merchSession?.articles?.[merchIndex];
    if (!article || order?.faktor == null) return;
    const rec = computeKuhlungRecommendation({
      abverkauf: article.abverkauf, faktor: order.faktor,
      istBestand: Number(merchIstBestand), ve: article.ve,
    });
    if (rec) setMerchQty(rec.recommendedQty);
  }, [merchIstBestand, merchScreen, merchIndex, merchSession?.order?.faktor]);
  // Taper la catégorie depuis le Hub = TOUJOURS pouvoir démarrer une NOUVELLE commande
  // (retour utilisateur explicite : "quand je clique sur kuhlung que je puisse toujours
  // faire une nouvelle commande, valable pour toutes les catégories") — jamais détourné vers
  // la reprise d'une commande existante. La reprise d'une commande en cours passe par son
  // propre badge sur la tuile (voir resumeMerchOrder + merchActiveOrders plus bas).
  const openMerchCategory = (zone) => { setMerchActiveZone(zone); setMerchDeliveryDateInput(""); setMerchScreen("date"); };
  // Tap sur le badge "Commande en cours" d'une tuile du Hub (retour utilisateur explicite :
  // "au clic d'une commande en cours que je me retrouve encore dans l'interface de commande
  // pour une commande deja debutee") — rentre DIRECTEMENT dans l'écran article, au curseur
  // exact, sans repasser par le choix de date.
  const resumeMerchOrder = (order) => { setMerchActiveZone(order.zone); loadMerchSession(order.zone, order.deliveryDate, order); };
  const loadMerchSession = (zone, deliveryDate, fallbackOrder) => {
    apiJSON(`/api/merchandise-orders/session?zone=${encodeURIComponent(zone)}&deliveryDate=${encodeURIComponent(deliveryDate)}`)
      .then((data) => {
        const order = data.order || fallbackOrder || null;
        const articles = data.articles || [];
        setMerchSession({ ...data, order });
        const startIndex = order ? Math.min(order.cursor || 0, Math.max(0, articles.length - 1)) : 0;
        setMerchIndex(startIndex);
        primeMerchLineInputs(articles[startIndex], order);
        setMerchScreen(articles.length ? "article" : "summary");
      })
      .catch((err) => showToast(err.message || t("err.loadingImpossible", "Chargement impossible.")));
  };
  // "Nexora ne doit jamais proposer automatiquement une date" (retour utilisateur explicite) —
  // merchDeliveryDateInput part toujours vide (voir openMerchCategory), l'utilisateur choisit.
  // `faktor` optionnel, valable pour n'importe quel rayon depuis le retour utilisateur du
  // 2026-08-25 ("implémenter la règle dans tout le module", auparavant Kühlung uniquement) —
  // absent (null) pour une commande "sans facteur" (voir merchNoFaktor/écran "faktor").
  const startMerchOrder = (faktor) => {
    if (!merchDeliveryDateInput) { showToast(t("toast.chooseDeliveryDate", "Choisis une date de livraison.")); return; }
    if (merchDemoMode) {
      const session = buildMerchDemoSession(merchActiveZone);
      if (faktor !== undefined && faktor !== null) session.order.faktor = faktor;
      setMerchSession(session);
      setMerchIndex(0);
      primeMerchLineInputs(session.articles[0], session.order);
      setMerchScreen(session.articles.length ? "article" : "summary");
      return;
    }
    const body = { zone: merchActiveZone, deliveryDate: merchDeliveryDateInput };
    if (faktor !== undefined && faktor !== null) body.faktor = faktor;
    apiJSON("/api/merchandise-orders", { method: "POST", body: JSON.stringify(body) })
      .then((data) => loadMerchSession(merchActiveZone, merchDeliveryDateInput, data.order))
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  // Bouton "Continuer" de l'écran Date : passe désormais par l'étape Faktor pour TOUS les
  // rayons (retour utilisateur explicite, 2026-08-25 — auparavant Kühlung uniquement),
  // jamais interposée à la reprise d'une commande (voir resumeMerchOrder). merchNoFaktor
  // repart toujours décoché à chaque nouvelle commande — jamais mémorisé d'une commande à
  // l'autre, contrairement au dernier Faktor saisi.
  const MERCH_FAKTOR_STORAGE_KEY = "nexora_merch_last_faktor";
  const continueFromMerchDate = () => {
    if (!merchDeliveryDateInput) { showToast(t("toast.chooseDeliveryDate", "Choisis une date de livraison.")); return; }
    let lastFaktor = "";
    try { lastFaktor = localStorage.getItem(MERCH_FAKTOR_STORAGE_KEY) || ""; } catch { /* stockage indisponible — pas bloquant */ }
    setMerchFaktorInput(lastFaktor);
    setMerchNoFaktor(false);
    setMerchScreen("faktor");
  };
  // "sinon créer une case à cocher pour un Bestellung sans facteur" (retour utilisateur
  // explicite) — un Faktor à 0 produirait un Bedarf/Empfehlung toujours nul, donc trompeur
  // (semblerait dire "n'en commande aucun" plutôt que "pas de recommandation") ; la case à
  // cocher est la seule échappatoire propre, jamais 0 comme valeur de Faktor valide.
  const confirmMerchFaktor = () => {
    if (merchNoFaktor) { startMerchOrder(null); return; }
    const faktor = Number(merchFaktorInput);
    if (!Number.isFinite(faktor) || faktor <= 0) { showToast(t("merch.faktor.invalidToast", "Indique un Faktor valide.")); return; }
    try { localStorage.setItem(MERCH_FAKTOR_STORAGE_KEY, String(faktor)); } catch { /* stockage indisponible — pas bloquant */ }
    startMerchOrder(faktor);
  };
  // Puce "Faktor" éditable sur l'écran article (commande déjà créée) — jamais réinterposé
  // via l'écran "faktor" (voir resumeMerchOrder), reste modifiable en place.
  const commitMerchFaktorEdit = () => {
    const faktor = Number(merchFaktorEditDraft);
    const order = merchSession?.order;
    setMerchFaktorEditing(false);
    if (!order || !Number.isFinite(faktor) || faktor <= 0) return;
    try { localStorage.setItem(MERCH_FAKTOR_STORAGE_KEY, String(faktor)); } catch { /* stockage indisponible — pas bloquant */ }
    setMerchSession((s) => ({ ...s, order: { ...s.order, faktor } }));
    if (merchDemoMode) return;
    apiJSON(`/api/merchandise-orders/${order.id}`, { method: "PATCH", body: JSON.stringify({ faktor }) })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const goToMerchArticle = (index) => {
    const articles = merchSession?.articles || [];
    if (index >= articles.length) { setMerchScreen("summary"); return; }
    setMerchIndex(index);
    primeMerchLineInputs(articles[index], merchSession?.order);
  };
  // "‹" à l'article 0 quitte le parcours (retour utilisateur explicite : plus de barre de
  // navigation sur cet écran, donc plus de flèche retour dans un en-tête — le chevron gauche
  // en bas d'écran assume ce rôle une fois qu'il n'y a plus d'article précédent).
  const goMerchBack = () => {
    if (merchIndex <= 0) { setMerchScreen("hub"); loadMerchCategories(); return; }
    goToMerchArticle(merchIndex - 1);
  };
  // "✕" ramène au Hub (mode pause — rien n'est perdu, chaque ligne validée est déjà
  // enregistrée côté serveur) — retour utilisateur explicite : ce clic doit TOUJOURS
  // annoncer où la commande se retrouve, pour que l'utilisateur sache où revenir.
  const pauseMerchOrder = () => {
    const articles = merchSession?.articles || [];
    const order = merchSession?.order;
    const meta = merchCategoryMeta(merchActiveZone);
    const done = (order?.lines || []).filter((l) => !l.skipped && l.quantity > 0).length;
    const msg = merchIndex >= articles.length
      ? t("merch.article.pauseToastDoneTemplate", { zone: meta.label, done }, `⏸ ${meta.label} en pause — déjà entièrement parcouru (${done} commandé${done > 1 ? "s" : ""}). Rouvre ${meta.label} depuis le Hub pour voir le résumé.`)
      : t("merch.article.pauseToastTemplate", { zone: meta.label, index: merchIndex + 1, total: articles.length, done }, `⏸ ${meta.label} en pause à l'article ${merchIndex + 1}/${articles.length} (${done} commandé${done > 1 ? "s" : ""} jusqu'ici). Rouvre ${meta.label} depuis le Hub pour reprendre exactement ici.`);
    showToast(msg);
    setMerchScreen("hub"); loadMerchCategories();
  };
  // Numéro d'article éditable sur la tuile (retour utilisateur explicite : "il doit faire
  // changer directement le nom de l'article en haut progressivement...") — sert de recherche
  // rapide dans le catalogue COMPLET du rayon (merchSession.articles = tout le catalogue de
  // la zone, voir server/merchandiseOrders/routes.js /session), pas un simple renommage du
  // champ artNr de l'article courant (ancien comportement, bug signalé "il ne fonctionne
  // pas" : ça renommait sans jamais chercher ni changer d'article).
  const findMerchArticleByArtNr = (query) => {
    const q = String(query || "").trim();
    if (!q) return null;
    const articles = merchSession?.articles || [];
    return articles.find((a) => a.artNr === q) || articles.find((a) => a.artNr && a.artNr.startsWith(q)) || null;
  };
  // Numéro complet et exact → bascule directement sur cet article (retour utilisateur :
  // "si le numero d'article est completement ecrit alors c'est l'article cherché").
  const selectMerchArticleByArtNr = (matched) => {
    const articles = merchSession?.articles || [];
    const idx = articles.findIndex((a) => a.id === matched.id);
    if (idx === -1) return;
    goToMerchArticle(idx);
    setMerchArtNrDraft("");
  };
  const startMerchArtNrEdit = () => {
    const article = merchSession?.articles?.[merchIndex];
    if (!article) return;
    setMerchArtNrDraft(article.artNr || "");
    setMerchArtNrEditing(true);
  };
  const commitMerchArtNrEdit = () => {
    const exact = findMerchArticleByArtNr(merchArtNrDraft);
    if (exact) { selectMerchArticleByArtNr(exact); return; }
    setMerchArtNrEditing(false);
    setMerchArtNrDraft("");
  };
  // Un seul bouton "›" fait tout le travail désormais (retour utilisateur explicite : "le clic
  // sur suivant valide directement la quantité, donc plus besoin du bouton valider") — skip
  // dérivé de la quantité affichée (0 = jamais touché = comportement identique à l'ancien
  // "Suivant (0)"), pour que l'historique "Commandé" et le résumé (ignorés) restent exacts.
  const advanceMerchArticle = () => saveMerchLine(merchQty <= 0);
  const saveMerchLine = (skip) => {
    const article = merchSession?.articles?.[merchIndex];
    const order = merchSession?.order;
    if (!article || !order) return;
    const nextIndex = merchIndex + 1;
    // Champs de recommandation — seulement calculés/envoyés pour une commande AVEC facteur
    // (retour utilisateur 2026-08-25 : la règle vaut pour tout le module, plus seulement
    // Kühlung ; `merchNoFaktor` reste la seule échappatoire) ; `quantity` ci-dessous reste
    // la seule valeur définitive de commande, jamais recalculée à partir de la
    // recommandation (voir computeKuhlungRecommendation).
    const hasFaktor = order.faktor != null;
    const istBestandNum = merchIstBestand === "" ? null : Number(merchIstBestand);
    const rec = hasFaktor ? computeKuhlungRecommendation({ abverkauf: article.abverkauf, faktor: order.faktor, istBestand: istBestandNum, ve: article.ve }) : null;
    const line = {
      articleId: article.id, artNr: article.artNr, name: article.name, ve: article.ve,
      quantity: skip ? 0 : merchQty, unit: merchUnit, skipped: skip, cursor: nextIndex,
      ...(hasFaktor ? {
        istBestand: istBestandNum, istBestandApprox: merchIstBestandApprox,
        abverkauf: article.abverkauf ?? null, bedarf: rec ? rec.bedarf : null, recommendedQty: rec ? rec.recommendedQty : null,
      } : {}),
    };
    if (merchDemoMode) {
      setMerchSession((s) => {
        const lines = [...(s.order.lines || [])];
        const idx = lines.findIndex((l) => l.articleId === line.articleId);
        if (idx === -1) lines.push(line); else lines[idx] = line;
        return { ...s, order: { ...s.order, lines, cursor: Math.max(s.order.cursor || 0, nextIndex) } };
      });
      goToMerchArticle(nextIndex);
      return;
    }
    apiJSON(`/api/merchandise-orders/${order.id}/line`, { method: "PATCH", body: JSON.stringify(line) })
      .then((data) => { setMerchSession((s) => ({ ...s, order: data.order })); goToMerchArticle(nextIndex); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const merchSummaryStats = () => {
    const order = merchSession?.order;
    const articles = merchSession?.articles || [];
    const linesById = new Map((order?.lines || []).map((l) => [l.articleId, l]));
    const traites = articles.filter((a) => linesById.has(a.id)).length;
    const commandes = articles.filter((a) => { const l = linesById.get(a.id); return l && !l.skipped && l.quantity > 0; }).length;
    return { traites, total: articles.length, commandes, ignores: traites - commandes };
  };
  const merchModifyOrder = () => goToMerchArticle(0);
  // "Enregistrer" (reste sur le résumé) et "Envoyer plus tard" (revient au hub) amènent tous
  // deux au statut terminee — voir le plan pour l'interprétation de ces 2 boutons.
  const merchConclude = (exitAfter) => {
    const order = merchSession?.order;
    if (!order) return;
    if (merchDemoMode) {
      setMerchSession((s) => ({ ...s, order: { ...s.order, status: "terminee" } }));
      showToast(exitAfter ? t("toast.merchOrderSavedLater", "✓ Commande enregistrée — à envoyer plus tard") : t("toast.merchOrderSaved", "✓ Commande enregistrée"));
      if (exitAfter) setMerchScreen("hub");
      return;
    }
    apiJSON(`/api/merchandise-orders/${order.id}`, { method: "PATCH", body: JSON.stringify({ status: "terminee" }) })
      .then((data) => {
        setMerchSession((s) => ({ ...s, order: data.order }));
        showToast(exitAfter ? t("toast.merchOrderSavedLater", "✓ Commande enregistrée — à envoyer plus tard") : t("toast.merchOrderSaved", "✓ Commande enregistrée"));
        if (exitAfter) { setMerchScreen("hub"); loadMerchCategories(); }
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const merchSend = () => {
    const order = merchSession?.order;
    if (!order) return;
    if (merchDemoMode) {
      setMerchSession((s) => ({ ...s, order: { ...s.order, status: "envoyee" } }));
      showToast(t("toast.merchOrderSent", "📤 Commande envoyée"));
      setMerchScreen("hub");
      return;
    }
    apiJSON(`/api/merchandise-orders/${order.id}/send`, { method: "POST" })
      .then((data) => { setMerchSession((s) => ({ ...s, order: data.order })); showToast(t("toast.merchOrderSent", "📤 Commande envoyée")); setMerchScreen("hub"); loadMerchCategories(); })
      .catch((err) => showToast(err.message || t("err.actionRefused", "Action refusée.")));
  };

  /* Commandes de marchandises — écran Gestion */
  const openMerchManage = () => { setMerchManageStatusFilter(""); setMerchScreen("manage"); loadMerchOrders(); };
  const openMerchOrderDetail = (order) => { setMerchOrderDetail(order); setSheet("merchOrderDetail"); };
  const saveMerchOrderField = (patch, toastMsg) => {
    if (!merchOrderDetail) return;
    apiJSON(`/api/merchandise-orders/${merchOrderDetail.id}`, { method: "PATCH", body: JSON.stringify(patch) })
      .then((data) => { setMerchOrderDetail(data.order); loadMerchOrders(); if (toastMsg) showToast(toastMsg); })
      .catch((err) => showToast(err.message || t("err.actionRefused", "Action refusée.")));
  };
  const updateMerchOrderLine = (line) => {
    if (!merchOrderDetail) return;
    apiJSON(`/api/merchandise-orders/${merchOrderDetail.id}/line`, {
      method: "PATCH",
      body: JSON.stringify({ articleId: line.articleId, artNr: line.artNr, name: line.name, ve: line.ve, quantity: line.quantity, unit: line.unit, skipped: line.skipped, cursor: merchOrderDetail.cursor }),
    })
      .then((data) => { setMerchOrderDetail(data.order); loadMerchOrders(); showToast(t("toast.merchLineUpdated", "✓ Ligne mise à jour")); })
      .catch((err) => showToast(err.message || t("err.actionRefused", "Action refusée.")));
  };
  const removeMerchOrderLine = (articleId) => saveMerchOrderField({ removeArticleId: articleId }, t("toast.merchLineRemoved", "🗑️ Ligne supprimée"));
  const sendMerchOrderFromManage = (order) => {
    apiJSON(`/api/merchandise-orders/${order.id}/send`, { method: "POST" })
      .then((data) => { setMerchOrderDetail(data.order); loadMerchOrders(); showToast(t("toast.merchOrderSent", "📤 Commande envoyée")); })
      .catch((err) => showToast(err.message || t("err.actionRefused", "Action refusée.")));
  };
  const markMerchOrderDelivered = (order) => {
    apiJSON(`/api/merchandise-orders/${order.id}/mark-delivered`, { method: "POST" })
      .then((data) => { setMerchOrderDetail(data.order); loadMerchOrders(); showToast(t("toast.merchOrderDelivered", "📦 Marquée reçue")); })
      .catch((err) => showToast(err.message || t("err.actionRefused", "Action refusée.")));
  };
  const cancelMerchOrder = (order) => {
    apiJSON(`/api/merchandise-orders/${order.id}/cancel`, { method: "POST" })
      .then((data) => { setMerchOrderDetail(data.order); loadMerchOrders(); showToast(t("toast.merchOrderCancelled", "✕ Commande annulée")); })
      .catch((err) => showToast(err.message || t("err.actionRefused", "Action refusée.")));
  };
  // Suppression DÉFINITIVE (retour utilisateur explicite : "je veux pouvoir completement
  // supprimer une commande") — distincte d'Annuler (qui garde la commande à l'historique
  // avec le statut annulee). Irréversible, donc confirmation explicite avant l'appel.
  const deleteMerchOrder = (order) => {
    if (!window.confirm(t("confirm.deleteMerchOrder", { date: fmtFR(order.deliveryDate) }, `Supprimer définitivement cette commande (livraison ${fmtFR(order.deliveryDate)}) ? Cette action est irréversible.`))) return;
    apiJSON(`/api/merchandise-orders/${order.id}`, { method: "DELETE" })
      .then(() => { setSheet(null); setMerchOrderDetail(null); loadMerchOrders(); showToast(t("toast.merchOrderDeleted", "🗑️ Commande supprimée")); })
      .catch((err) => showToast(err.message || t("err.actionRefused", "Action refusée.")));
  };

  /* Commandes de marchandises — réorganiser le rayon (Stellvertreter/FL de la filiale,
     boutons ↑/↓ — pattern sûr, jamais le drag tactile qui a déjà causé des crashs GPU
     ailleurs dans l'app, voir le plan). */
  const openMerchReorder = (zone) => {
    setMerchReorderZone(zone);
    apiJSON(`/api/merchandise-orders/session?zone=${encodeURIComponent(zone)}&deliveryDate=`)
      .then((data) => { setMerchReorderList(data.articles || []); setMerchScreen("reorder"); })
      .catch((err) => showToast(err.message || t("err.loadingImpossible", "Chargement impossible.")));
  };
  const moveMerchReorderItem = (index, direction) => {
    setMerchReorderList((list) => {
      const target = index + direction;
      if (target < 0 || target >= list.length) return list;
      const next = [...list];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };
  const saveMerchReorder = () => {
    apiJSON(`/api/merchandise-orders/store-order/${encodeURIComponent(merchReorderZone)}`, {
      method: "PATCH",
      body: JSON.stringify({ articleIds: merchReorderList.map((a) => a.id) }),
    })
      .then(() => { showToast(t("toast.merchOrderSaved2", "✓ Ordre du rayon enregistré")); setMerchScreen("hub"); loadMerchCategories(); })
      .catch((err) => showToast(err.message || t("err.actionRefused", "Action refusée.")));
  };

  /* Inventurmaßnahme — Lieferschein Test + Bewertungsbogen für Testkäufe + PLU Quiz
     (fusionné). Archive hebdomadaire : un seul flux mêlant les 3 types, groupé par KW —
     réutilisé tel quel côté filiale (propres documents) et côté rubrique fiche filiale GR
     (lecture seule, storeId=filiale consultée). */
  const imBuildWeeklyItem = (r) => {
    if (r.type === "plu_quiz") return {
      id: r.id, kind: "plu", dateKey: r.finishedAt, ok: r.passed, raw: r,
      title: t("plu.eyebrow", "PLU Quiz"), subtitle: `${r.employeeName} · ${r.percentage}%`,
    };
    if (r.type === "testkauf") return {
      id: r.id, kind: "bw", dateKey: r.datum, ok: r.testkaufBestanden, raw: r,
      title: t("inventurmassnahme.hub.bwTitle", "Bewertungsbogen für Testkäufe"),
      subtitle: `${r.mitarbeiterName} · ${r.testkaufBestanden ? t("inventurmassnahme.hub.passed", "Test réussi") : t("inventurmassnahme.hub.failed", "Non réussi")}`,
    };
    return {
      id: r.id, kind: "ls", dateKey: r.datum, ok: r.testBestanden, raw: r,
      title: `${t("inventurmassnahme.hub.lsTitle", "Lieferschein Test")} · ${r.rayon}`,
      subtitle: `${r.mitarbeiterName} · ${r.testBestanden ? t("inventurmassnahme.hub.passed", "Test réussi") : t("inventurmassnahme.hub.failed", "Non réussi")}`,
    };
  };
  const imGroupIntoWeeks = (imRecords, pluAttempts) => {
    const items = [...imRecords, ...pluAttempts.map((a) => ({ ...a, type: "plu_quiz" }))].map(imBuildWeeklyItem);
    const byWeek = new Map();
    items.forEach((it) => {
      const wk = imWeekKeyFromDate(it.dateKey);
      if (!wk) return;
      if (!byWeek.has(wk)) byWeek.set(wk, []);
      byWeek.get(wk).push(it);
    });
    return [...byWeek.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([weekKey, weekItems]) => ({
        weekKey,
        label: t("inventurmassnahme.archive.weekLabelTemplate", { kw: weekKey.split("-W")[1], year: weekKey.split("-W")[0] }, `KW ${weekKey.split("-W")[1]} · ${weekKey.split("-W")[0]}`),
        items: weekItems.sort((a, b) => String(b.dateKey).localeCompare(String(a.dateKey))),
      }));
  };
  // setWeeks/setLoading paramétrables — même chargeur pour l'archive filiale (imScreen ===
  // "archive", setImArchiveWeeks/setImArchiveLoading) ET la rubrique fiche filiale GR
  // (setPilotImArchiveWeeks/setPilotImArchiveLoading, storeId = filiale consultée).
  const loadImArchive = (storeId, setWeeks = setImArchiveWeeks, setLoading = setImArchiveLoading) => {
    setLoading(true);
    const targetStore = storeId || user?.store;
    // PLU Quiz : storeId dans la requête = historique COMPLET de la filiale (réservé aux
    // managers côté serveur) ; sans storeId = seulement les propres tentatives de
    // l'appelant (comportement déjà existant de la route, jamais touché) — un simple
    // employé consultant sa propre archive ne voit donc que ses propres examens PLU, un
    // manager (FL/FLA/GR) voit tout l'historique de la filiale.
    const pluQ = (storeId || isManager(user)) ? `?storeId=${encodeURIComponent(targetStore)}` : "";
    Promise.all([
      apiJSON(`/api/inventurmassnahme/recent?storeId=${encodeURIComponent(targetStore)}&limit=300`).then((d) => d.records || []).catch(() => []),
      apiJSON(`/api/plu-quiz/attempts${pluQ}`).then((d) => d.attempts || []).catch(() => []),
    ]).then(([records, attempts]) => setWeeks(imGroupIntoWeeks(records, attempts)))
      .finally(() => setLoading(false));
  };
  // Rendu partagé — écran filiale (imScreen === "archive", édition/suppression actives) ET
  // rubrique fiche filiale GR (lecture seule, juste l'export). readOnly masque ✎/🗑.
  const imRenderWeekFolders = (weeks, opts) => {
    if (weeks.length === 0) return <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "20px 0" }}>{t("inventurmassnahme.archive.empty", "Aucun document pour l'instant.")}</p>;
    // Grille desktop Phase 2 (2026-08-13) — helper partagé par l'archive Inventurmaßnahme
    // et la rubrique fiche filiale GR équivalente (voir les 2 sites d'appel), un seul
    // point à corriger couvre les deux. align-items:"start" gère le dossier ouvert plus
    // haut que ses voisins sans les étirer.
    return (
      <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(360px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 10 : 0, alignItems: "start" }}>
      {weeks.map((w, wIdx) => {
      const open = opts.expandedWeek === w.weekKey;
      const okCount = w.items.filter((it) => it.ok).length;
      return (
        <div key={w.weekKey} className="nx-list-item-in" style={{ ...card, marginBottom: isWide ? 0 : 10, overflow: "hidden", animationDelay: `${Math.min(wIdx, 14) * 28}ms` }}>
          <Pressable onClick={() => opts.setExpandedWeek(open ? null : w.weekKey)} style={{ display: "flex", alignItems: "center", gap: 11, padding: 14, width: "100%" }}>
            <span style={{ width: 38, height: 38, borderRadius: 11, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`, flexShrink: 0 }}>📁</span>
            <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
              <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{w.label}</strong>
              <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("inventurmassnahme.archive.itemCountTemplate", { n: w.items.length }, `${w.items.length} document(s)`)} · {okCount}/{w.items.length} ✓</span>
            </div>
            <span style={{ color: P.muted, fontSize: `calc(14px * var(--nx-font-scale, 1))`, transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}>›</span>
          </Pressable>
          {open && (
            <div style={{ padding: "0 12px 12px" }}>
              {w.items.map((it) => (
                <div key={`${it.kind}-${it.id}`} style={{ display: "flex", alignItems: "center", gap: 8, background: P.soft, borderRadius: 12, padding: "9px 11px", marginBottom: 6 }}>
                  <span style={{ width: 7, height: 7, borderRadius: 99, flexShrink: 0, background: it.ok ? A.green : A.red }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ display: "block", fontSize: `calc(12px * var(--nx-font-scale, 1))`, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.title}</strong>
                    <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>{it.subtitle}</span>
                  </div>
                  {it.raw.belegUrl && <Pressable onClick={() => openImBeleg(it.raw.belegUrl, it.raw.belegMimeType)} aria-label={t("inventurmassnahme.belegViewAria", "Voir le fichier")} style={{ width: 28, height: 28, borderRadius: 99, background: P.card, display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, flexShrink: 0 }}>📎</Pressable>}
                  {opts.onExportItem && <Pressable onClick={() => opts.onExportItem(it)} aria-label={t("inventurmassnahme.exportAria", "Exporter le PDF")} style={{ width: 28, height: 28, borderRadius: 99, background: P.card, display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, flexShrink: 0 }}>📄</Pressable>}
                  {!opts.readOnly && it.kind !== "plu" && opts.onEditItem && <Pressable onClick={() => opts.onEditItem(it)} aria-label={t("inventurmassnahme.editAria", "Modifier")} style={{ width: 28, height: 28, borderRadius: 99, background: P.card, display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, flexShrink: 0 }}>✎</Pressable>}
                  {!opts.readOnly && it.kind !== "plu" && opts.onDeleteItem && <Pressable onClick={() => opts.onDeleteItem(it)} aria-label={t("inventurmassnahme.deleteAria", "Supprimer")} style={{ width: 28, height: 28, borderRadius: 99, background: P.card, display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: A.red, flexShrink: 0 }}>🗑</Pressable>}
                </div>
              ))}
            </div>
          )}
        </div>
      );
      })}
      </div>
    );
  };
  // Convertit le PNG du pad de signature en vraie photo uploadée (même route que les
  // photos HACCP/Contrôle, /api/store-photos) — jamais de base64 stocké tel quel dans une
  // collection (même règle que les photos de réclamation, voir server/claims/repository.js).
  const uploadImSignature = async (dataUrl) => {
    if (!dataUrl) return "";
    const blob = await (await fetch(dataUrl)).blob();
    const formData = new FormData();
    formData.append("photo", blob, "signature.png");
    const data = await apiJSON("/api/store-photos", { method: "POST", body: formData });
    return data.url || "";
  };
  // Génère le lien à usage unique (POST /api/signature-requests, voir server/
  // signatureRequests/routes.js) — nécessite un enregistrement déjà sauvegardé (recordId),
  // jamais un brouillon en cours : la signature reçue patche directement le champ visé sur
  // la fiche réelle, il faut donc qu'elle existe déjà côté serveur.
  const requestRemoteSignature = async (targetType, recordId, fieldKey, fieldLabel) => {
    if (!recordId) { showToast(t("inventurmassnahme.remoteSig.needSaveFirst", "Enregistre d'abord la fiche avant de demander une signature à distance.")); return; }
    setSignatureRequestBusy(true);
    try {
      const data = await apiJSON("/api/signature-requests", {
        method: "POST",
        body: JSON.stringify({ targetType, recordId, fieldKey, fieldLabel }),
      });
      setSignatureRequestLink({ url: `${window.location.origin}/?sign=${data.token}`, fieldLabel });
      setSheet("remoteSignatureLink");
    } catch (err) {
      showToast(err.message || t("err.actionImpossible", "Action impossible."));
    } finally {
      setSignatureRequestBusy(false);
    }
  };
  // Bouton réutilisable — 3 emplacements (Testkauf: Testkäufer ; Lieferschein Test:
  // Mitarbeiter + FL), jamais sur un champ signé "depuis son propre compte" (BL, voir les
  // 2 blocs isNetworkLeader juste à côté — mécanisme distinct, déjà correct).
  const renderRemoteSigButton = (targetType, recordId, fieldKey, fieldLabel, hasSignature) => (
    !hasSignature && recordId ? (
      <Pressable onClick={() => requestRemoteSignature(targetType, recordId, fieldKey, fieldLabel)} style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%",
        padding: "10px 0", borderRadius: 12, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
        background: "rgba(54,79,231,.08)", border: `1.5px solid ${A.blue}`, color: A.blue, marginBottom: 10,
        opacity: signatureRequestBusy ? .6 : 1,
      }}>
        📤 {t("inventurmassnahme.remoteSig.requestBtn", "Demander une signature à distance")}
      </Pressable>
    ) : null
  );
  // Photo ou PDF du vrai bon — sans lui les deux formulaires ne sont pas valides (retour
  // utilisateur explicite). Route dédiée (accepte aussi le PDF, contrairement à
  // /api/store-photos qui refuse tout ce qui n'est pas une image).
  const uploadImBeleg = (file, setForm) => {
    if (!file) return;
    setImBelegUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    apiJSON("/api/inventurmassnahme/upload-beleg", { method: "POST", body: formData })
      .then((data) => setForm((f) => ({ ...f, belegUrl: data.url || "", belegMimeType: data.mimeType || "" })))
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")))
      .finally(() => setImBelegUploading(false));
  };
  // Aperçu + bouton d'ajout/remplacement du Beleg — même fonction pour les deux
  // formulaires (juste un inputRef différent par écran, comme les inputs fichiers cachés
  // établis ailleurs dans l'app, ex. stundenFileInputRef).
  const imBelegField = (form, setForm, inputRef) => (
    <div style={{ marginBottom: 14 }}>
      {form.belegUrl ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: P.soft, border: `1.5px solid ${P.line}`, borderRadius: 14, padding: "10px 12px" }}>
          <Pressable onClick={() => openImBeleg(form.belegUrl, form.belegMimeType)} aria-label={t("inventurmassnahme.belegViewAria", "Voir le fichier")} style={{ flexShrink: 0 }}>
            {form.belegMimeType.startsWith("image/") ? (
              <img src={form.belegUrl} alt="" style={{ width: 44, height: 44, borderRadius: 10, objectFit: "cover", display: "block" }} />
            ) : (
              <span style={{ width: 44, height: 44, borderRadius: 10, background: P.card, display: "grid", placeItems: "center", fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>📄</span>
            )}
          </Pressable>
          <span style={{ flex: 1, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: A.green, fontWeight: 700 }}>{t("inventurmassnahme.belegAttachedNote", "✓ Justificatif joint")}</span>
          <Pressable onClick={() => inputRef.current?.click()} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, padding: "6px 8px" }}>{t("messages.changeBtn", "Changer")}</Pressable>
          <Pressable onClick={() => setForm((f) => ({ ...f, belegUrl: "", belegMimeType: "" }))} aria-label={t("claims.action.remove", "Retirer")} style={{ width: 28, height: 28, borderRadius: 99, background: P.card, display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: A.red, flexShrink: 0 }}>✕</Pressable>
        </div>
      ) : (
        <Pressable onClick={() => inputRef.current?.click()} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "13px 0", borderRadius: 14, border: `1.5px dashed ${P.line}`, background: P.soft, color: P.muted, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, opacity: imBelegUploading ? .6 : 1 }}>
          {imBelegUploading ? t("common.saving", "Enregistrement…") : t("inventurmassnahme.belegAddBtn", "📎 Ajouter une photo ou un PDF")}
        </Pressable>
      )}
    </div>
  );
  const editImRecord = (r) => {
    if (r.type === "testkauf") {
      setBwForm({ ...emptyBwForm, ...r });
      setBwMitSearch("");
      setImScreen("testkauf");
    } else {
      setLsForm({ ...emptyLsForm, ...r });
      setLsMitSearch("");
      setImScreen("lieferschein");
    }
  };
  const deleteImRecord = (id) => {
    if (!window.confirm(t("inventurmassnahme.confirmDelete", "Supprimer définitivement ce formulaire ?"))) return;
    apiJSON(`/api/inventurmassnahme/${id}`, { method: "DELETE" })
      .then(() => {
        setImArchiveWeeks((weeks) => weeks
          .map((w) => ({ ...w, items: w.items.filter((it) => it.id !== id) }))
          .filter((w) => w.items.length > 0));
        showToast(t("inventurmassnahme.deletedToast", "✓ Supprimé"));
      })
      .catch((err) => showToast(err.message || t("err.actionRefused", "Action refusée.")));
  };
  const saveLieferscheinTest = async () => {
    if (!lsForm.lieferscheinnummer.trim()) { showToast(t("inventurmassnahme.ls.needNumberToast", "Numéro de bon de livraison obligatoire.")); return; }
    if (!lsForm.mitarbeiterName.trim()) { showToast(t("inventurmassnahme.needEmployeeToast", "Choisis un collaborateur.")); return; }
    if (!lsForm.belegUrl) { showToast(t("inventurmassnahme.needBelegToast", "Ajoute une photo ou un PDF du bon de livraison.")); return; }
    setImSaving(true);
    try {
      // Un pad laissé vierge pendant une modification ne doit jamais effacer une
      // signature déjà enregistrée — seul un nouveau tracé remplace l'image existante.
      // sigBl : le pad n'est même pas monté pour un compte FL/FLA (voir isNetworkLeader
      // dans le useEffect de montage) — hasValidSignature() est alors toujours falsy,
      // donc le fallback sur lsForm.sigBl s'applique naturellement, jamais d'écrasement.
      const sigMitarbeiter = lsSigMitPadRef.current?.hasValidSignature() ? await uploadImSignature(lsSigMitPadRef.current.toDataURL()) : (lsForm.sigMitarbeiter || "");
      const sigFl = lsSigFlPadRef.current?.hasValidSignature() ? await uploadImSignature(lsSigFlPadRef.current.toDataURL()) : (lsForm.sigFl || "");
      const sigBl = lsSigBlPadRef.current?.hasValidSignature() ? await uploadImSignature(lsSigBlPadRef.current.toDataURL()) : (lsForm.sigBl || "");
      const isEdit = Boolean(lsForm.id);
      const data = await apiJSON(isEdit ? `/api/inventurmassnahme/lieferschein-test/${lsForm.id}` : "/api/inventurmassnahme/lieferschein-test", {
        method: isEdit ? "PUT" : "POST", body: JSON.stringify({ ...lsForm, sigMitarbeiter, sigFl, sigBl }),
      });
      showToast(t("inventurmassnahme.savedToast", "✓ Enregistré"));
      // Retour utilisateur explicite (2026-08-20) : reste sur la fiche après enregistrement
      // (jamais de sortie/export PDF automatique, jamais professionnel de kicker l'employé
      // vers les archives dès qu'il clique Enregistrer) — recharge lsForm avec le vrai
      // enregistrement serveur (id désormais présent -> déverrouille "signature à
      // distance"), pads remis à zéro (signature déjà uploadée, visible via sa vignette).
      setLsForm(data.record);
      lsSigMitPadRef.current?.clear(); lsSigFlPadRef.current?.clear(); lsSigBlPadRef.current?.clear();
    } catch (err) {
      showToast(err.message || t("err.savingImpossible", "Enregistrement impossible."));
    } finally {
      setImSaving(false);
    }
  };
  const addBwFehlerArtikel = () => { if (bwForm.fehlerhaftArtikel.length < 6) setBwForm((f) => ({ ...f, fehlerhaftArtikel: [...f.fehlerhaftArtikel, ""] })); };
  const updateBwFehlerArtikel = (idx, value) => setBwForm((f) => ({ ...f, fehlerhaftArtikel: f.fehlerhaftArtikel.map((a, i) => i === idx ? value : a) }));
  const removeBwFehlerArtikel = (idx) => setBwForm((f) => ({ ...f, fehlerhaftArtikel: f.fehlerhaftArtikel.filter((_, i) => i !== idx) }));
  const saveTestkauf = async () => {
    if (!bwForm.mitarbeiterName.trim()) { showToast(t("inventurmassnahme.needEmployeeToast", "Choisis un collaborateur.")); return; }
    if (!bwForm.belegUrl) { showToast(t("inventurmassnahme.needBelegToast", "Ajoute une photo ou un PDF du bon de livraison.")); return; }
    setImSaving(true);
    try {
      const sigTestkaeufer = bwSigTkPadRef.current?.hasValidSignature() ? await uploadImSignature(bwSigTkPadRef.current.toDataURL()) : (bwForm.sigTestkaeufer || "");
      const sigBereichsleitung = bwSigBlPadRef.current?.hasValidSignature() ? await uploadImSignature(bwSigBlPadRef.current.toDataURL()) : (bwForm.sigBereichsleitung || "");
      // Déduit du vrai rôle du collaborateur choisi (voir bwMitarbeiterIsAzubi côté rendu,
      // même calcul) — jamais une case à cocher manuelle qui pourrait diverger du rôle réel.
      const azubi = taskEmployees.find((e) => e.employeeId === bwForm.mitarbeiterId)?.role === "Azubi";
      const isEdit = Boolean(bwForm.id);
      const data = await apiJSON(isEdit ? `/api/inventurmassnahme/testkauf/${bwForm.id}` : "/api/inventurmassnahme/testkauf", {
        method: isEdit ? "PUT" : "POST", body: JSON.stringify({ ...bwForm, sigTestkaeufer, sigBereichsleitung, azubi }),
      });
      showToast(t("inventurmassnahme.savedToast", "✓ Enregistré"));
      // Voir le commentaire équivalent dans saveLieferscheinTest juste au-dessus.
      setBwForm(data.record);
      bwSigTkPadRef.current?.clear(); bwSigBlPadRef.current?.clear();
    } catch (err) {
      showToast(err.message || t("err.savingImpossible", "Enregistrement impossible."));
    } finally {
      setImSaving(false);
    }
  };
  // Export PDF — même patron de portail que HACCP/AZL/Arbeitsplan/Infos/Rep/Label/Réductions
  // (voir clearOtherPrintPortals) : deux nouveaux portails distincts #imLsPrintRoot/
  // #imBwPrintRoot, chacun vidé quand l'autre s'exporte.
  const buildLieferscheinTestPrintHtml = (r) => `<div class="im-page">
    <header class="im-header"><div><p class="im-eyebrow">Inventurmaßnahme</p><h1>${t("imBw.lieferscheinTest.title", "Lieferschein Test")}</h1></div><div class="im-meta">${t("home.store.label", { id: haccpEscHtml(r.storeId || user?.store || "") }, `Filiale ${haccpEscHtml(r.storeId || user?.store || "")}`)} · ${fmtFR(r.datum)}</div></header>
    <table class="im-fieldtable">
      <tr><th>${t("imBw.rayonLabel", "Rayon")}</th><td>${haccpEscHtml(r.rayon)}</td></tr>
      <tr><th>${t("imBw.lieferscheinnummerLabel", "Lieferscheinnummer")}</th><td>${haccpEscHtml(r.lieferscheinnummer)}</td></tr>
      <tr><th>${t("imBw.datumLabel", "Datum")}</th><td>${fmtFR(r.datum)}</td></tr>
      <tr><th>${t("imBw.mitarbeiterLabel", "Mitarbeiter")}</th><td>${haccpEscHtml(r.mitarbeiterName)}</td></tr>
      <tr><th>${t("imBw.testPassedLabel", "Test bestanden")}</th><td class="${r.testBestanden ? "im-ok" : "im-bad"}">${r.testBestanden ? t("common.yes", "Oui") : t("common.no", "Non")}</td></tr>
      ${r.verlust !== "" && r.verlust != null ? `<tr><th>${t("imBw.verlustLabel", "Verlust")}</th><td>${fmt2(Number(r.verlust))} €</td></tr>` : ""}
      <tr><th>${t("imBw.ermahnungOralLabel", "Ermahnung durch BL mündlich")}</th><td>${r.ermahnungMuendlich ? t("common.yes", "Oui") : t("common.no", "Non")}</td></tr>
      <tr><th>${t("imBw.ermahnungAbmahnungLabel", "Ermahnung / Abmahnung")}</th><td>${[r.ermahnung ? t("imBw.ermahnungWord", "Ermahnung") : "", r.abmahnung ? t("imBw.abmahnungWord", "Abmahnung (durch VKS)") : ""].filter(Boolean).join(" · ") || "—"}</td></tr>
    </table>
    <div class="im-sigrow">
      <div class="im-sigbox"><span>${t("imBw.sigMitarbeiterLabel", "Unterschrift Mitarbeiter")}</span>${r.sigMitarbeiter ? `<img src="${r.sigMitarbeiter}" />` : `<div class="im-sigempty"></div>`}</div>
      <div class="im-sigbox"><span>${t("imBw.sigFlLabel", "Unterschrift FL")}</span>${r.sigFl ? `<img src="${r.sigFl}" />` : `<div class="im-sigempty"></div>`}</div>
      <div class="im-sigbox"><span>${t("imBw.sigBlLabel", "Unterschrift BL")}</span>${r.sigBl ? `<img src="${r.sigBl}" />` : `<div class="im-sigempty"></div>`}</div>
    </div>
  </div>`;
  const exportLieferscheinTestPdf = async (r) => {
    try {
      // Contournement — voir le commentaire de urlToDataUri : les signatures sont des URLs de
      // photos uploadées (jamais des data: URL comme PLU), on les convertit AVANT de générer le
      // HTML imprimé pour retirer tout chargement réseau du chemin de rasterisation.
      const r2 = { ...r,
        sigMitarbeiter: await urlToDataUri(r.sigMitarbeiter),
        sigFl: await urlToDataUri(r.sigFl),
        sigBl: await urlToDataUri(r.sigBl),
      };
      await exportHtmlToPdf({ bodyHtml: buildLieferscheinTestPrintHtml(r2), css: IM_DOC_CSS, targetSelector: ".im-page", widthMm: 210, filename: `Lieferschein-Test-${r.storeId || user?.store || ""}-${r.datum || ""}.pdf` });
    } catch (err) {
      if (err?.name !== "AbortError") showToast(err.message || t("err.actionImpossible", "Action impossible."));
    }
  };
  const bwYesNoRow = (label, val) => `<tr><td>${haccpEscHtml(label)}</td><td class="${val ? "im-ok" : "im-bad"}">${val ? t("common.yes", "Oui") : t("common.no", "Non")}</td></tr>`;
  const buildTestkaufPrintHtml = (r) => `<div class="im-page">
    <header class="im-header"><div><p class="im-eyebrow">Inventurmaßnahme</p><h1>${t("imBw.testkauf.title", "Bewertungsbogen für Testkäufe")}</h1></div><div class="im-meta">${t("home.store.label", { id: haccpEscHtml(r.storeId || user?.store || "") }, `Filiale ${haccpEscHtml(r.storeId || user?.store || "")}`)} · ${fmtFR(r.datum)}</div></header>
    <table class="im-fieldtable">
      <tr><th>${t("imBw.mitarbeiterLabel", "Mitarbeiter")}</th><td>${haccpEscHtml(r.mitarbeiterName)}</td></tr>
      <tr><th>${t("imBw.azubiLabel", "Azubi")}</th><td>${r.azubi ? t("common.yes", "Oui") : t("common.no", "Non")}</td></tr>
      <tr><th>${t("imBw.arbeitskleidungLabel", "Arbeitskleidung")}</th><td>${{ ja: t("common.yes", "Oui"), nein: t("common.no", "Non"), ordentlich: t("imBw.arbeitskleidungOrdentlich", "Ordentlich") }[r.arbeitskleidung] || r.arbeitskleidung}</td></tr>
      ${r.bemerkungen ? `<tr><th>${t("imBw.bemerkungenLabel", "Bemerkungen")}</th><td>${haccpEscHtml(r.bemerkungen)}</td></tr>` : ""}
    </table>
    <h2 class="im-section">${t("imBw.sectionA", "A) Verhalten gegenüber Kunden")}</h2>
    <table class="im-qtable">${bwYesNoRow(t("imBw.q.begruessung", "Begrüßung und Verabschiedung durch Kassierer/in"), r.begruessung)}</table>
    <h2 class="im-section">${t("imBw.sectionB", "B) Kassiervorgang")}</h2>
    <table class="im-qtable">
      ${bwYesNoRow(t("imBw.q.wareAufBand", "Ware auf das Band"), r.wareAufBand)}
      ${bwYesNoRow(t("imBw.q.blickEinkaufswagen", "Blick in den Einkaufswagen"), r.blickEinkaufswagen)}
      ${bwYesNoRow(t("imBw.q.anhebenWare", "Anheben der Ware"), r.anhebenWare)}
      ${bwYesNoRow(t("imBw.q.warenbewegung", "Warenbewegung auf Kassenband"), r.warenbewegung)}
      ${bwYesNoRow(t("imBw.q.wechselgeldBestueckt", "Ist die Kasse ausreichend mit Wechselgeld bestückt?"), r.wechselgeldBestueckt)}
      ${bwYesNoRow(t("imBw.q.plusAppGefragt", "Wird nach der NORMA Plus-App gefragt?"), r.plusAppGefragt)}
      ${bwYesNoRow(t("imBw.q.kundeWechselgeldGefragt", "Wird der Kunde nach Wechselgeld gefragt?"), r.kundeWechselgeldGefragt)}
      ${bwYesNoRow(t("imBw.q.pluSicherheit", "PLU-Sicherheit"), r.pluSicherheit)}
    </table>
    ${r.allgemeineAngaben ? `<p class="im-note"><b>${t("imBw.allgemeineAngabenLabel", "Allgemeine Angaben:")}</b> ${haccpEscHtml(r.allgemeineAngaben)}</p>` : ""}
    <h2 class="im-section">${t("imBw.sectionC", "C) Wertmäßige Fehler beim Abkassieren")}</h2>
    ${r.fehlerhaftArtikel.length ? `<ol class="im-artlist">${r.fehlerhaftArtikel.map((a) => `<li>${haccpEscHtml(a)}</li>`).join("")}</ol>` : `<p class="im-note">${t("imBw.noFehlerhaftArtikel", "Keine fehlerhaften Artikel gemeldet.")}</p>`}
    <table class="im-fieldtable">
      ${r.probewagenBetrag !== "" ? `<tr><th>${t("imBw.probewagenBetragLabel", "Betrag des Probewagens")}</th><td>${fmt2(Number(r.probewagenBetrag))} €</td></tr>` : ""}
      ${r.richtigerBetrag !== "" ? `<tr><th>${t("imBw.richtigerBetragLabel", "Richtiger Betrag")}</th><td>${fmt2(Number(r.richtigerBetrag))} €</td></tr>` : ""}
      ${r.differenz !== "" ? `<tr><th>${t("imBw.differenzLabel", "Differenz")}</th><td>${r.differenz >= 0 ? "+" : ""}${fmt2(r.differenz)} €</td></tr>` : ""}
      <tr><th>${t("imBw.testkaufPassedLabel", "Testkauf bestanden")}</th><td class="${r.testkaufBestanden ? "im-ok" : "im-bad"}">${r.testkaufBestanden ? t("common.yes", "Oui") : t("common.no", "Non")}</td></tr>
      <tr><th>${t("imBw.ermahnungOralLabel", "Ermahnung durch BL mündlich")}</th><td>${r.ermahnungMuendlich ? t("common.yes", "Oui") : t("common.no", "Non")}${r.ermahnungDatum ? ` · ${fmtFR(r.ermahnungDatum)}` : ""}</td></tr>
      <tr><th>${t("imBw.ermahnungAbmahnungLabel", "Ermahnung / Abmahnung")}</th><td>${[r.ermahnung ? t("imBw.ermahnungWord", "Ermahnung") : "", r.abmahnung ? t("imBw.abmahnungWord", "Abmahnung (durch VKS)") : ""].filter(Boolean).join(" · ") || "—"}</td></tr>
      ${r.bemerkung ? `<tr><th>${t("imBw.bemerkungLabel", "Bemerkung")}</th><td>${haccpEscHtml(r.bemerkung)}</td></tr>` : ""}
    </table>
    <div class="im-sigrow">
      <div class="im-sigbox"><span>${t("imBw.sigTestkaeuferLabel", "Name und Unterschrift des Testkäufers")}</span>${r.sigTestkaeufer ? `<img src="${r.sigTestkaeufer}" />` : `<div class="im-sigempty"></div>`}</div>
      <div class="im-sigbox"><span>${t("imBw.sigBereichsleitungLabel", "Unterschrift der Bereichsleitung")}</span>${r.sigBereichsleitung ? `<img src="${r.sigBereichsleitung}" />` : `<div class="im-sigempty"></div>`}</div>
    </div>
  </div>`;
  const exportTestkaufPdf = async (r) => {
    try {
      const r2 = { ...r,
        sigTestkaeufer: await urlToDataUri(r.sigTestkaeufer),
        sigBereichsleitung: await urlToDataUri(r.sigBereichsleitung),
      };
      await exportHtmlToPdf({ bodyHtml: buildTestkaufPrintHtml(r2), css: IM_DOC_CSS, targetSelector: ".im-page", widthMm: 210, filename: `Testkauf-${r.storeId || user?.store || ""}-${r.datum || ""}.pdf` });
    } catch (err) {
      if (err?.name !== "AbortError") showToast(err.message || t("err.actionImpossible", "Action impossible."));
    }
  };

  /* Fabrication d'étiquettes (nouveau module) — parcours Nouveau → modèle → formulaire →
     aperçu → imprimer/exporter, voir LABEL_TEMPLATES/buildLabelBodyHtml/buildLabelPrintHtml
     (moteur, hors composant, juste après Sheet). Saisie 100% manuelle (aucun lien avec le
     catalogue articles, décision explicite de l'utilisateur pour cette V1). */
  const loadLabels = () => {
    setLabelsLoading(true);
    apiJSON("/api/labels")
      .then((data) => setLabelsList(data.labels || []))
      .catch((err) => showToast(err.message || t("err.loadingImpossible", "Chargement impossible.")))
      .finally(() => setLabelsLoading(false));
  };
  const startNewLabel = () => { setLabelEditingId(null); setLabelActiveTemplateId(null); setLabelsScreen("template"); };
  const pickLabelTemplate = (id) => {
    setLabelActiveTemplateId(id);
    // aktionA4 démarre sur "blanc" (dernier état photo-vérifié, 2026-09-10), jamais le
    // "jaune" par défaut partagé par les autres modèles (emptyLabelDraft) — la couleur
    // reste un choix disponible (voir buildLabelBodyHtml), juste pas le point de départ ici.
    setLabelDraft({ ...emptyLabelDraft(), ...(id === "aktionA4" ? { couleur: "blanc" } : {}) });
    setLabelEditingId(null);
    setLabelsScreen("form");
  };
  const updateLabelField = (key, value) => setLabelDraft((d) => ({ ...d, [key]: value }));
  // Conversion de format (chantier Niimbot Étape 2, Partie C, 2026-08-28) — contrairement à
  // pickLabelTemplate, NE réinitialise JAMAIS labelDraft : le draft est déjà indépendant du
  // template actif (mêmes 13 clés quel que soit le modèle, voir emptyLabelDraft), donc
  // changer seulement labelActiveTemplateId suffit — buildLabelBodyHtml ignore déjà sans
  // erreur les clés que le nouveau modèle n'utilise pas. Avertit avant de convertir si des
  // champs REMPLIS deviendraient invisibles/non imprimés dans le nouveau format (perte
  // silencieuse jamais acceptable) — les données restent enregistrées dans le draft, juste
  // plus affichées, donc une reconversion arrière les fait réapparaître.
  const convertLabelToTemplate = (newTemplateId) => {
    const currentTpl = getLabelTemplate(labelActiveTemplateId);
    const newTpl = getLabelTemplate(newTemplateId);
    const droppedFields = currentTpl.fields.filter((f) => {
      if (newTpl.fields.includes(f)) return false;
      const v = labelDraft[f];
      return Array.isArray(v) ? v.length > 0 : (v !== "" && v !== false && v != null);
    });
    if (droppedFields.length > 0) {
      const names = droppedFields.map((f) => t(LABEL_FIELD_META[f]?.labelKey, LABEL_FIELD_META[f]?.label || f)).join(", ");
      const ok = window.confirm(t("labels.convert.warnDropTemplate", { fields: names }, `Le modèle « ${t(`labels.templateName.${newTpl.id}`, newTpl.label)} » n'affiche pas : ${names}. Ces informations resteront enregistrées mais n'apparaîtront pas sur cette étiquette. Continuer ?`));
      if (!ok) return;
    }
    setLabelActiveTemplateId(newTemplateId);
    setSheet(null);
    setLabelsScreen("preview");
  };
  const goToLabelPreview = () => {
    if (!labelDraft.designation.trim()) { showToast(t("toast.labelNeedsDesignation", "Indique au moins une désignation.")); return; }
    setLabelsScreen("preview");
  };
  const openSavedLabel = (label) => {
    setLabelActiveTemplateId(label.templateId);
    setLabelDraft({ ...emptyLabelDraft(), ...label.data });
    setLabelEditingId(label.id);
    setLabelsScreen("preview");
  };
  const editCurrentLabel = () => setLabelsScreen("form");
  const saveLabel = () => {
    // Retour à l'écran d'échec silencieux (2026-09-17, "enregistrements essayés sans
    // succès") : ce garde-fou ne devrait normalement jamais se déclencher (le bouton lui-
    // même est déjà masqué si !canManageLabels), mais s'il se déclenchait quand même — état
    // de permission périmé après un changement de rôle en cours de session, par exemple —
    // un clic qui ne fait STRICTEMENT rien, sans le moindre message, est indiscernable d'un
    // bug côté app pour l'utilisateur. Un vrai message vaut mieux qu'un silence, même pour
    // un cas qui ne devrait "jamais" arriver.
    if (!canManageLabels) { showToast(t("toast.notAllowedToDoThis", "Tu n'as pas le droit de faire ça.")); return; }
    setLabelSaving(true);
    const payload = { templateId: labelActiveTemplateId, data: labelDraft };
    const req = labelEditingId
      ? apiJSON(`/api/labels/${labelEditingId}`, { method: "PATCH", body: JSON.stringify(payload) })
      : apiJSON("/api/labels", { method: "POST", body: JSON.stringify(payload) });
    req
      .then((data) => {
        setLabelEditingId(data.label.id);
        showToast(t("toast.labelSaved", "✓ Étiquette enregistrée"));
        loadLabels();
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setLabelSaving(false));
  };
  const deleteLabelConfirmed = (id) => {
    apiJSON(`/api/labels/${id}`, { method: "DELETE" })
      .then(() => {
        setLabelPendingDeleteId(null);
        showToast(t("toast.labelDeleted", "Étiquette supprimée"));
        if (labelEditingId === id) { setLabelsScreen("hub"); setLabelEditingId(null); }
        loadLabels();
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };

  // Kassenpost (2026-09-18) — mêmes patrons déjà éprouvés que labels (CRUD manuel) et
  // claims (upload photo) ci-dessus, jamais réinventés.
  const emptyKassenpostForm = (category) => ({
    category, numero: "", receivedAt: "", sender: "", title: "", body: "",
    artNr: "", artName: "", mhd: "", defectType: "", quantityPieces: "", deadline: "",
    photos: [], status: "pending", answerNote: "",
  });
  const loadKassenpost = () => {
    setKassenpostLoading(true);
    apiJSON("/api/kassenpost")
      .then((data) => setKassenpostList(data.items || []))
      .catch((err) => showToast(err.message || t("err.loadingImpossible", "Chargement impossible.")))
      .finally(() => setKassenpostLoading(false));
  };
  const startNewKassenpost = (category) => {
    setKassenpostActiveId(null);
    setKassenpostForm(emptyKassenpostForm(category));
    setKassenpostScreen("form");
  };
  const updateKassenpostField = (key, value) => setKassenpostForm((f) => (f ? { ...f, [key]: value } : f));
  const openKassenpostDetail = (item) => {
    setKassenpostActiveId(item.id);
    setKassenpostForm({ ...item });
    setKassenpostScreen("form");
  };
  const saveKassenpost = () => {
    if (!canManageKassenpost || !kassenpostForm) return;
    if (!kassenpostForm.title.trim()) { showToast(t("toast.kassenpostTitleRequired", "Indique au moins un titre.")); return; }
    setKassenpostSaving(true);
    const payload = { ...kassenpostForm };
    const req = kassenpostActiveId
      ? apiJSON(`/api/kassenpost/${kassenpostActiveId}`, { method: "PATCH", body: JSON.stringify(payload) })
      : apiJSON("/api/kassenpost", { method: "POST", body: JSON.stringify(payload) });
    req
      .then((data) => {
        setKassenpostActiveId(data.item.id);
        setKassenpostForm(data.item);
        showToast(t("toast.kassenpostSaved", "✓ Message enregistré"));
        loadKassenpost();
      })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")))
      .finally(() => setKassenpostSaving(false));
  };
  // "Marqué comme répondu" — retour utilisateur explicite (rappel Kassenpost : "Eine
  // Zusendung von aussagekräftigen Bildern ist zwingend notwendig") : pour une
  // Qualitätsmeldung précisément, jamais clôturable sans au moins une photo, pour ne pas
  // perdre silencieusement la preuve exigée par la Niederlassung.
  const markKassenpostAnswered = () => {
    if (!kassenpostForm || !kassenpostActiveId) return;
    if (kassenpostForm.category === "qm" && (!kassenpostForm.photos || kassenpostForm.photos.length === 0)) {
      showToast(t("toast.kassenpostPhotoRequired", "Ajoute au moins une photo avant de marquer comme répondu."));
      return;
    }
    // Envoie tout le formulaire courant (pas seulement status/answerNote/quantityPieces) —
    // bug réel trouvé en test : une photo tout juste ajoutée ne vit QUE dans l'état local
    // (uploadKassenpostPhotos ne persiste rien tant que "Enregistrer" n'est pas rappelé) ;
    // un PATCH partiel qui omettait "photos" ici écrasait silencieusement la preuve photo
    // pourtant déjà vérifiée juste au-dessus (kassenpostForm.photos.length === 0 aurait dû
    // rester impossible à contourner).
    apiJSON(`/api/kassenpost/${kassenpostActiveId}`, {
      method: "PATCH",
      body: JSON.stringify({ ...kassenpostForm, status: "answered" }),
    })
      .then((data) => { setKassenpostForm(data.item); showToast(t("toast.kassenpostAnswered", "✓ Marqué comme répondu")); loadKassenpost(); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const deleteKassenpostConfirmed = (id) => {
    apiJSON(`/api/kassenpost/${id}`, { method: "DELETE" })
      .then(() => {
        setKassenpostPendingDeleteId(null);
        showToast(t("toast.kassenpostDeleted", "Message supprimé"));
        if (kassenpostActiveId === id) { setKassenpostScreen("hub"); setKassenpostActiveId(null); }
        loadKassenpost();
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const kassenpostPhotoInputRef = useRef(null);
  const uploadKassenpostPhotos = async (fileList) => {
    const filesToUpload = Array.from(fileList || []);
    if (!filesToUpload.length) return;
    const compressed = await compressImageFiles(filesToUpload);
    const formData = new FormData();
    compressed.forEach((f) => formData.append("photos", f));
    apiJSON("/api/kassenpost/photos", { method: "POST", body: formData })
      .then((data) => setKassenpostForm((f) => (f ? { ...f, photos: [...f.photos, ...(data.urls || [])] } : f)))
      .catch((err) => showToast(err.message || t("err.photoSendImpossible", "Envoi de photo impossible.")));
  };

  // Export PDF réel — jamais window.print() (voir exportHtmlToPdf, fonction module-level
  // ci-dessus, pour le pourquoi détaillé). aktionA4 (déjà au format A4 pleine page) passe par
  // exportHtmlToPdf tel quel, comme avant ; tous les autres formats (physiquement plus petits
  // qu'A4) passent par exportLabelToPdf — page PDF toujours A4, étiquette dessinée à sa vraie
  // taille dedans (voir le commentaire d'exportLabelToPdf pour le pourquoi : sans ça,
  // AirPrint/l'imprimante réduit le PDF pour l'ajuster au papier réellement chargé, faussant
  // la taille physique — retour utilisateur explicite 2026-09-11).
  const printLabel = async () => {
    if (labelExporting) return;
    setLabelExporting(true);
    const tpl = getLabelTemplate(labelActiveTemplateId);
    const { widthMm, heightMm } = tpl.format;
    const filename = `${labelDraft.designation ? labelDraft.designation.replace(/[^a-z0-9]+/gi, "-").toLowerCase() : "etiquette"}.pdf`;
    const isFullA4 = widthMm === LABEL_FORMATS.aktionA4.widthMm && heightMm === LABEL_FORMATS.aktionA4.heightMm;
    try {
      await (isFullA4 ? exportHtmlToPdf : exportLabelToPdf)({
        bodyHtml: buildLabelBodyHtml(labelActiveTemplateId, labelDraft),
        css: LABEL_ENGINE_CSS, targetSelector: ".lbl-page", widthMm, heightMm, filename,
      });
    } catch (err) {
      if (err?.name !== "AbortError") showToast(err.message || t("err.actionImpossible", "Action impossible."));
    } finally {
      setLabelExporting(false);
    }
  };

  // Impression Niimbot — ÉTAPE 1 du chantier (2026-08-28) : écran de test isolé, PAS ENCORE
  // branché sur les vrais templates d'étiquettes (voir le plan). But unique ici : valider la
  // connexion Bluetooth réelle + une impression simple avec l'imprimante physique de
  // l'utilisateur (aucun accès matériel de mon côté, donc rien de plus avancé tant que ce
  // palier n'est pas confirmé). Visible seulement si navigator.bluetooth existe (jamais sur
  // Safari iOS) — voir la garde dans le JSX plus bas.
  const [niimbotClient, setNiimbotClient] = useState(null);
  const [niimbotDeviceName, setNiimbotDeviceName] = useState("");
  const [niimbotBusy, setNiimbotBusy] = useState(false);
  const [niimbotStatus, setNiimbotStatus] = useState("");

  const connectNiimbot = async () => {
    setNiimbotBusy(true); setNiimbotStatus(t("labels.niimbotTest.connecting", "Recherche de l'imprimante…"));
    try {
      const { NiimbotBluetoothClient } = await import("@mmote/niimbluelib");
      const client = new NiimbotBluetoothClient();
      const info = await client.connect();
      await client.fetchPrinterInfo();
      setNiimbotClient(client);
      setNiimbotDeviceName(info.deviceName || t("labels.niimbotTest.unknownDevice", "Imprimante Niimbot"));
      setNiimbotStatus(t("labels.niimbotTest.connected", "Connecté."));
    } catch (err) {
      setNiimbotStatus(t("labels.niimbotTest.connectError", "Connexion impossible : {message}", { message: err.message || String(err) }));
    } finally {
      setNiimbotBusy(false);
    }
  };

  const disconnectNiimbot = async () => {
    try { await niimbotClient?.disconnect(); } catch { /* best-effort */ }
    setNiimbotClient(null); setNiimbotDeviceName(""); setNiimbotStatus("");
  };

  const testPrintNiimbot = async () => {
    if (!niimbotClient || niimbotBusy) return;
    setNiimbotBusy(true); setNiimbotStatus(t("labels.niimbotTest.printing", "Impression test en cours…"));
    try {
      const { ImageEncoder, findPrintTask } = await import("@mmote/niimbluelib");
      const meta = niimbotClient.getModelMetadata();
      if (!meta) throw new Error(t("labels.niimbotTest.modelUnknown", "Modèle d'imprimante non reconnu par la bibliothèque."));
      const taskName = findPrintTask(meta.model);
      if (!taskName) throw new Error(t("labels.niimbotTest.noPrintTask", "Aucune méthode d'impression connue pour ce modèle."));
      // Étiquette de test minimale — jamais liée aux vrais templates tant que cette étape
      // n'est pas validée avec l'imprimante réelle (voir le plan, Étape 1 vs Étape 2).
      const canvas = await renderHtmlToCanvas({
        bodyHtml: `<div style="width:240px;height:240px;display:flex;align-items:center;justify-content:center;font-size:26px;font-weight:800;font-family:sans-serif;border:5px solid #000;box-sizing:border-box;">TEST</div>`,
        css: "", targetSelector: null, scale: 2,
      });
      const encoded = ImageEncoder.encodeCanvas(thresholdCanvasForThermalPrint(canvas), meta.printDirection);
      const printTask = niimbotClient.abstraction.newPrintTask(taskName, { totalPages: 1 });
      await printTask.printInit();
      await printTask.printPage(encoded, 1);
      await printTask.waitForPageFinished();
      await printTask.waitForFinished();
      await niimbotClient.abstraction.printEnd();
      setNiimbotStatus(t("labels.niimbotTest.printSuccess", "Impression test envoyée."));
    } catch (err) {
      setNiimbotStatus(t("labels.niimbotTest.printError", "Erreur impression : {message}", { message: err.message || String(err) }));
    } finally {
      setNiimbotBusy(false);
    }
  };

  // Impression Niimbot — ÉTAPE 2, Partie B (2026-08-28) : le VRAI contenu de l'étiquette
  // active (buildLabelBodyHtml, même moteur exact que l'aperçu et l'export PDF), plus le
  // même enchaînement Bluetooth déjà validé en réel à l'Étape 1 (testPrintNiimbot). Connecte
  // automatiquement si besoin — un seul bouton, jamais deux étapes manuelles pour l'usage
  // courant. La connexion reste ensuite réutilisée pour les impressions suivantes tant que
  // l'onglet reste ouvert (niimbotClient géré au niveau composant).
  //
  // Dimensionnement du canvas — point critique trouvé en lisant le code source de la lib
  // (@mmote/niimbluelib, B1PrintTask.js) : ImageEncoder.encodeCanvas lit le canvas PIXEL PAR
  // PIXEL, sans aucun redimensionnement automatique côté lib — la largeur/hauteur du canvas
  // devient directement le nombre de points envoyés à l'imprimante. Deux contraintes à
  // respecter soi-même, jamais géré ailleurs :
  //  1) la résolution doit correspondre au DPI réel de l'imprimante connectée (meta.dpi,
  //     203 pour B1/B21/B18 vérifié dans printer_models.js) pour que l'étiquette sorte à sa
  //     vraie taille physique — sans ça, échelle par défaut du navigateur (96 CSS px/pouce)
  //     donnerait une étiquette imprimée ~2x trop petite ;
  //  2) le nombre de points ne doit jamais dépasser printheadPixels (384 pour B1/B21 — à
  //     203 dpi la largeur imprimable réelle est donc ~48mm, pas 50mm plein cadre ; B18 est
  //     encore plus étroit, 96 points, et tourne l'image de 90° — printDirection="left").
  //     D'où le plafond ci-dessous sur la dimension réellement contrainte par le sens
  //     d'impression, jamais un simple scale fixe : sans ce plafond, n'importe quel autre
  //     format (150mm de large, formats existants réutilisables via la conversion Partie C)
  //     dépasserait largement le printhead et produirait une image tronquée/corrompue.
  const printLabelViaBluetooth = async () => {
    if (niimbotBusy) return;
    setNiimbotBusy(true);
    setNiimbotStatus(t("labels.niimbotPrint.starting", "Préparation…"));
    try {
      const { NiimbotBluetoothClient, ImageEncoder, findPrintTask } = await import("@mmote/niimbluelib");
      let client = niimbotClient;
      if (!client) {
        setNiimbotStatus(t("labels.niimbotTest.connecting", "Recherche de l'imprimante…"));
        client = new NiimbotBluetoothClient();
        const info = await client.connect();
        await client.fetchPrinterInfo();
        setNiimbotClient(client);
        setNiimbotDeviceName(info.deviceName || t("labels.niimbotTest.unknownDevice", "Imprimante Niimbot"));
      }
      const meta = client.getModelMetadata();
      if (!meta) throw new Error(t("labels.niimbotTest.modelUnknown", "Modèle d'imprimante non reconnu par la bibliothèque."));
      const taskName = findPrintTask(meta.model);
      if (!taskName) throw new Error(t("labels.niimbotTest.noPrintTask", "Aucune méthode d'impression connue pour ce modèle."));

      setNiimbotStatus(t("labels.niimbotPrint.rendering", "Génération de l'image…"));
      const tpl = getLabelTemplate(labelActiveTemplateId);
      const { widthMm, heightMm } = tpl.format;
      const naturalWidthPx = widthMm * (96 / 25.4);
      const naturalHeightPx = heightMm * (96 / 25.4);
      const dpiScale = meta.dpi / 96;
      const constrainedNaturalPx = meta.printDirection === "left" ? naturalHeightPx : naturalWidthPx;
      const maxScale = meta.printheadPixels ? meta.printheadPixels / constrainedNaturalPx : dpiScale;
      const scale = Math.min(dpiScale, maxScale);

      const canvas = await renderHtmlToCanvas({
        bodyHtml: buildLabelBodyHtml(labelActiveTemplateId, labelDraft),
        css: LABEL_ENGINE_CSS, targetSelector: ".lbl-page", scale,
      });
      const encoded = ImageEncoder.encodeCanvas(thresholdCanvasForThermalPrint(canvas), meta.printDirection);

      setNiimbotStatus(t("labels.niimbotPrint.printing", "Impression en cours…"));
      const printTask = client.abstraction.newPrintTask(taskName, { totalPages: 1 });
      await printTask.printInit();
      await printTask.printPage(encoded, 1);
      await printTask.waitForPageFinished();
      await printTask.waitForFinished();
      await client.abstraction.printEnd();
      setNiimbotStatus(t("labels.niimbotPrint.success", "Étiquette imprimée."));
      showToast(t("labels.niimbotPrint.successToast", "🖨️ Étiquette envoyée à l'imprimante."));
    } catch (err) {
      const message = err?.message || String(err);
      setNiimbotStatus(t("labels.niimbotTest.printError", "Erreur impression : {message}", { message }));
      showToast(message);
    } finally {
      setNiimbotBusy(false);
    }
  };

  /* Scanner — Associer EAN (même contrat réel article.ean/barcode/barcodes/
     eans que le scan de Contrôle, via articleCodesFor/linkCodeToArticle/
     unlinkCodeFromArticle définis plus haut) */
  const scanArticleEan = (artNr) => {
    const a = articles.find((x) => x.artNr === artNr);
    return a ? (articleCodesFor(a)[0] || null) : null;
  };
  const scanLinkedCount = articles.filter((a) => scanArticleEan(a.artNr)).length;
  // allowUnlink=false depuis Livraison (retour utilisateur explicite : "une fois liée ils
  // doivent être reconnus au prochain scan et ne plus proposer d'être liée ou de se délier.
  // Pour délier il faudrait retourner dans le module scanner.") — délier reste réservé au
  // module Scanner, jamais accessible depuis un écran de réception/consultation.
  const openScanLink = (article, allowUnlink = true) => { setScanLinkArticle(article); setScanLinkInput(""); setScanLinkAllowUnlink(allowUnlink); setSheet("scannerLink"); };
  const saveScanLink = () => {
    const code = scanLinkInput.trim();
    if (!code) { showToast(t("toast.enterOrScanEan", "Saisis ou scanne un code EAN.")); return; }
    const liveArticle = articles.find((x) => x.artNr === scanLinkArticle?.artNr) || scanLinkArticle;
    const conflict = articleForCode(code);
    const hasConflict = conflict && conflict.artNr !== liveArticle.artNr;
    linkCodeToArticle(code, liveArticle)
      .then((updated) => {
        setScanLinkArticle(updated);
        setScanLinkInput("");
        showToast(hasConflict
          ? t("toast.eanLinkedWithConflictTemplate", { article: updated.name, code: code.slice(-5), other: conflict.name },
              `⚠️ …${code.slice(-5)} était déjà lié à ${conflict.name} — ajouté quand même à ${updated.name}`)
          : t("toast.linkedToEanTemplate", { name: updated.name, code: code.slice(-5) }, `🔗 ${updated.name} associé à …${code.slice(-5)}`));
      })
      .catch((err) => showToast(err.message || t("err.associationImpossible", "Association impossible.")));
  };
  const removeScanLinkCode = (code) => {
    const liveArticle = articles.find((x) => x.artNr === scanLinkArticle?.artNr);
    if (!liveArticle) return;
    unlinkCodeFromArticle(liveArticle, code)
      .then((updated) => {
        setScanLinkArticle(updated);
        showToast(t("toast.eanLinkRemovedTemplate", { code: code.slice(-5) }, `Association EAN …${code.slice(-5)} retirée`));
      })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  const simulateScanForLink = () => doScan("scanLink");

  /* Messages — vraie API server/messages/*. Le résumé de conversation renvoyé par
     GET /api/messages/conversations porte déjà `unread`/`lastMessagePreview`/
     `lastMessageAt`/`muted` calculés côté serveur — jamais recalculés côté client comme
     avant (convUnread() sur un tableau de messages embarqué qui n'existait que dans le
     seed démo). Écart de permissions déjà corrigé (voir storeflow_messages_demo_data_never_migrated) —
     tous les rôles ont maintenant les messages.* attendus, pas seulement "Employé". */
  const totalUnread = conversations.reduce((s, c) => s + (c.unread || 0), 0);
  // Bug réel trouvé à l'audit (2026-08-06) : archiver une conversation était un aller simple
  // — le serveur excluait déjà toute conversation masquée (hiddenAt) de TOUS les filtres,
  // et rien côté client ne demandait jamais la liste archivée (setArchived(false) existait
  // déjà côté serveur mais personne ne pouvait jamais l'appeler). filter=archived (voir
  // listConversations) + onglet dédié ci-dessous rouvrent ce chemin.
  const filteredConversations = (msgFilter === "archived" ? archivedConversations : conversations)
    .filter((c) => msgFilter === "all" || msgFilter === "archived" ? true : msgFilter === "unread" ? (c.unread || 0) > 0 : c.type === msgFilter)
    .filter((c) => !msgListQuery.trim() || c.title.toLowerCase().includes(msgListQuery.trim().toLowerCase()))
    .sort((a, b) => String(b.lastMessageAt || "").localeCompare(String(a.lastMessageAt || "")));

  const loadConversations = () => {
    if (!user) return;
    setMsgLoading(true);
    apiJSON("/api/messages/conversations")
      .then((data) => setConversations(data.conversations || []))
      .catch(() => setConversations([]))
      .finally(() => setMsgLoading(false));
  };
  const loadArchivedConversations = () => {
    if (!user) return;
    setMsgLoading(true);
    apiJSON("/api/messages/conversations?filter=archived")
      .then((data) => setArchivedConversations(data.conversations || []))
      .catch(() => setArchivedConversations([]))
      .finally(() => setMsgLoading(false));
  };
  const unarchiveConv = (id) => {
    apiJSON(`/api/messages/${encodeURIComponent(id)}/archive`, { method: "PATCH", body: JSON.stringify({ archived: false }) })
      .then(() => { loadArchivedConversations(); showToast(t("toast.conversationUnarchived", "Conversation désarchivée")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const loadThreadMessages = (chatId) => {
    setMsgThreadLoading(true);
    apiJSON(`/api/messages/${encodeURIComponent(chatId)}/messages`)
      .then((data) => setMsgThreadMessages(data.messages || []))
      .catch(() => setMsgThreadMessages([]))
      .finally(() => setMsgThreadLoading(false));
  };
  // Variante silencieuse pour le poll — bug réel trouvé à l'audit (2026-08-06) : le fil
  // ouvert n'était chargé qu'une fois à l'ouverture, jamais rafraîchi ensuite (contrairement
  // à la liste des conversations, pollée toutes les 15s) — un message reçu pendant que le
  // fil est ouvert n'apparaissait qu'en quittant/rouvrant. Pas de setMsgThreadLoading ici :
  // ne doit jamais faire clignoter le fil déjà affiché à chaque tick.
  const pollThreadMessages = (chatId) => {
    apiJSON(`/api/messages/${encodeURIComponent(chatId)}/messages`)
      .then((data) => setMsgThreadMessages(data.messages || []))
      .catch(() => {});
  };
  const loadRecipients = (query) => {
    apiJSON(`/api/messages/recipients${query ? `?search=${encodeURIComponent(query)}` : ""}`)
      .then((data) => {
        setMsgRecipients(data.recipients || []);
        setMsgAvailableStores(data.stores || []);
        setMsgCanCreateGroup(!!data.canCreateGroup);
        setMsgCanCreateCrossBranch(!!data.canCreateCrossBranch);
      })
      .catch(() => { setMsgRecipients([]); setMsgAvailableStores([]); setMsgCanCreateGroup(false); setMsgCanCreateCrossBranch(false); });
  };

  const openThread = (id) => {
    setMsgActiveId(id); setMsgDraft(""); setMsgScreen("thread"); setMsgThreadMessages([]);
    setMsgPendingAttachments([]);
    loadThreadMessages(id);
    apiJSON(`/api/messages/${encodeURIComponent(id)}/read`, { method: "POST" })
      .then(loadConversations)
      .catch(() => {});
  };
  const sendMessage = () => {
    const text = msgDraft.trim();
    if ((!text && !msgPendingAttachments.length) || !msgActiveId) return;
    if (msgEditId) {
      if (!text) return;
      apiJSON(`/api/messages/${encodeURIComponent(msgActiveId)}/messages/${encodeURIComponent(msgEditId)}`, {
        method: "PATCH", body: JSON.stringify({ text }),
      }).then((data) => {
        setMsgThreadMessages((ms) => ms.map((m) => m.id === msgEditId ? data.message : m));
      }).catch((err) => showToast(err.message || t("err.modificationImpossible", "Modification impossible.")));
      setMsgEditId(null); setMsgDraft("");
      return;
    }
    const replyToId = msgReplyTo ? msgReplyTo.id : undefined;
    const attachmentIds = msgPendingAttachments.map((a) => a.id);
    apiJSON(`/api/messages/${encodeURIComponent(msgActiveId)}/messages`, {
      method: "POST", body: JSON.stringify({ text, replyToId, attachmentIds }),
    }).then((data) => {
      setMsgThreadMessages((ms) => [...ms, data.message]);
      loadConversations();
    }).catch((err) => showToast(err.message || t("err.sendingImpossible", "Envoi impossible.")));
    setMsgDraft(""); setMsgReplyTo(null); setMsgPendingAttachments([]);
  };
  // Upload immédiat (avant l'envoi du message, comme le veut le contrat serveur : POST
  // .../attachments renvoie des ids indépendants, sendMessage les référence ensuite via
  // attachmentIds) — file d'attente visible dans la barre de composition, retirable avant
  // envoi. Jamais de bouton 📎 auparavant malgré la route déjà réelle côté serveur.
  const uploadMsgAttachment = (files) => {
    if (!msgActiveId || !files || !files.length) return;
    setMsgUploadingAttachment(true);
    const form = new FormData();
    Array.from(files).forEach((f) => form.append("files", f));
    apiJSON(`/api/messages/${encodeURIComponent(msgActiveId)}/attachments`, { method: "POST", body: form })
      .then((data) => setMsgPendingAttachments((list) => [...list, ...(data.attachments || [])]))
      .catch((err) => showToast(err.message || t("err.attachmentSendImpossible", "Envoi de la pièce jointe impossible.")))
      .finally(() => setMsgUploadingAttachment(false));
  };
  const removePendingAttachment = (id) => setMsgPendingAttachments((list) => list.filter((a) => a.id !== id));
  const renameConversation = () => {
    if (!msgRenameForm || !msgActiveId) return;
    const title = msgRenameForm.title.trim();
    if (!title) { showToast(t("toast.nameCannotBeEmpty", "Le nom ne peut pas être vide.")); return; }
    apiJSON(`/api/messages/${encodeURIComponent(msgActiveId)}`, {
      method: "PATCH", body: JSON.stringify({ title, description: msgRenameForm.description || "" }),
    }).then(() => { loadConversations(); setMsgRenameForm(null); showToast(t("toast.conversationRenamed", "Conversation renommée.")); })
      .catch((err) => showToast(err.message || t("err.renamingImpossible", "Renommage impossible.")));
  };
  const addConvMember = (employeeId) => {
    if (!msgActiveId) return;
    apiJSON(`/api/messages/${encodeURIComponent(msgActiveId)}/members`, {
      method: "POST", body: JSON.stringify({ employeeIds: [employeeId] }),
    }).then(() => {
      apiJSON(`/api/messages/${encodeURIComponent(msgActiveId)}`).then((data) => setMsgConvMembers(data.members || []));
      showToast(t("toast.memberAdded", "Membre ajouté."));
    }).catch((err) => showToast(err.message || t("err.additionImpossible", "Ajout impossible.")));
  };
  const removeConvMember = (employeeId) => {
    if (!msgActiveId) return;
    // Confirmation manquante trouvée à l'audit (2026-08-06) — même patron que les 15+
    // autres actions destructives de l'app (suppression utilisateur/mission/planning…).
    if (!window.confirm(t("messages.removeMemberConfirm", "Retirer ce membre de la conversation ?"))) return;
    apiJSON(`/api/messages/${encodeURIComponent(msgActiveId)}/members/${encodeURIComponent(employeeId)}`, { method: "DELETE" })
      .then(() => setMsgConvMembers((ms) => ms.filter((m) => String(m.userId) !== String(employeeId))))
      .catch((err) => showToast(err.message || t("err.removalImpossible", "Retrait impossible.")));
  };
  const runMessageSearch = (q) => {
    setMsgSearchQuery(q);
    if (!q.trim()) { setMsgSearchResults([]); return; }
    setMsgSearchLoading(true);
    apiJSON(`/api/messages/search?q=${encodeURIComponent(q.trim())}`)
      .then((data) => setMsgSearchResults(data.results || []))
      .catch(() => setMsgSearchResults([]))
      .finally(() => setMsgSearchLoading(false));
  };
  const startReply = (m) => {
    setMsgEditId(null);
    setMsgReplyTo({ id: m.id, from: m.authorName, preview: m.deleted ? "" : (m.text || "") });
  };
  const startEditMessage = (m) => { setMsgReplyTo(null); setMsgEditId(m.id); setMsgDraft(m.text || ""); };
  const cancelComposerMode = () => { setMsgReplyTo(null); setMsgEditId(null); setMsgDraft(""); };
  const deleteMessage = (convId, msgId) => {
    apiJSON(`/api/messages/${encodeURIComponent(convId)}/messages/${encodeURIComponent(msgId)}`, { method: "DELETE" })
      .then((data) => setMsgThreadMessages((ms) => ms.map((m) => m.id === msgId ? data.message : m)))
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  const toggleMute = (id) => {
    const c = conversations.find((x) => x.id === id);
    apiJSON(`/api/messages/${encodeURIComponent(id)}/mute`, { method: "PATCH", body: JSON.stringify({ muted: !c?.muted }) })
      .then(loadConversations).catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const archiveConv = (id) => {
    // Bug réel trouvé en vérification (2026-08-18, retour utilisateur avec capture d'écran) :
    // la sheet "convInfo" (d'où ce bouton est appelé) n'était jamais fermée ici — seul l'écran
    // repassait sur la liste. La conversation archivée disparaît de `conversations` au
    // rechargement, donc conversations.find(...) dans le rendu de la sheet retombe à `null` et
    // affiche une sheet VIDE toujours ouverte par-dessus la liste, au lieu de disparaître.
    apiJSON(`/api/messages/${encodeURIComponent(id)}/archive`, { method: "PATCH", body: JSON.stringify({ archived: true }) })
      .then(() => { loadConversations(); setMsgScreen("list"); setSheet(null); showToast(t("toast.conversationArchived", "Conversation archivée")); })
      .catch((err) => showToast(err.message || t("err.archivingImpossible", "Archivage impossible.")));
  };
  const leaveConv = (id) => {
    // Confirmation manquante trouvée à l'audit (2026-08-06) — quitter n'est pas trivialement
    // réversible (il faut être réinvité) et n'avait, seul de toutes les actions destructives
    // de l'app, aucun window.confirm.
    // Retour utilisateur explicite 2026-08-24 : "il manque le bouton pour supprimer les
    // messages, archiver existe mais définitivement supprimer non" — la route serveur
    // /leave existait déjà (désactive ma propre adhésion, définitif tant que je ne suis pas
    // réinvité) mais le bouton était réservé aux groupes (conv.type !== "private") ; pour
    // une conversation privée c'est EXACTEMENT la sémantique "supprimer pour moi" voulue —
    // jamais un vrai effacement partagé qui retirerait aussi la conversation chez l'autre
    // personne, juste ma propre visibilité, définitivement (contrairement à Archiver,
    // réversible via Désarchiver).
    const conv = conversations.find((c) => c.id === id);
    const isPrivate = conv?.type === "private";
    const confirmText = isPrivate
      ? t("messages.deleteConfirm", "Supprimer définitivement cette conversation ? Elle disparaîtra de ta liste ; l'autre personne pourra toujours t'écrire de nouveau.")
      : t("messages.leaveConfirm", "Quitter cette conversation ? Tu devras être réinvité(e) pour la retrouver.");
    if (!window.confirm(confirmText)) return;
    apiJSON(`/api/messages/${encodeURIComponent(id)}/leave`, { method: "POST" })
      .then(() => { loadConversations(); setMsgScreen("list"); setSheet(null); showToast(isPrivate ? t("toast.conversationDeleted", "🗑️ Conversation supprimée") : t("toast.youLeftConversation", "Tu as quitté la conversation")); })
      .catch((err) => showToast(err.message || t("err.leaveGroupImpossible", "Impossible de quitter le groupe.")));
  };
  const toggleNewConvMember = (employeeId) => setNewConvMembers((ms) => ms.includes(employeeId) ? ms.filter((m) => m !== employeeId) : [...ms, employeeId]);
  const toggleNewConvStore = (storeId) => setNewConvStores((ss) => ss.includes(storeId) ? ss.filter((s) => s !== storeId) : [...ss, storeId]);
  // Retour utilisateur explicite (2026-08-18) : pour un message privé, taper sur une
  // personne doit ouvrir directement la conversation — plus d'étape de sélection puis
  // bouton "Créer" tout en bas de la liste des destinataires. newConvCreatingId
  // désactive les autres lignes le temps de la requête (jamais 2 conversations créées
  // par un double-tap).
  const createPrivateConversationWith = (employeeId) => {
    if (newConvCreatingId) return;
    setNewConvCreatingId(employeeId);
    apiJSON("/api/messages/private", { method: "POST", body: JSON.stringify({ targetEmployeeId: employeeId }) })
      .then((data) => {
        loadConversations();
        setNewConvType("private"); setNewConvMembers([]); setNewConvTitle(""); setNewConvStores([]); setNewConvQuery("");
        openThread(data.conversation.id);
      })
      .catch((err) => showToast(err.message || t("err.creationImpossible", "Création impossible.")))
      .finally(() => setNewConvCreatingId(null));
  };
  const createConversation = () => {
    if (newConvType === "group" && newConvMembers.length < 2) { showToast(t("messages.pickAtLeastTwoPeople", "Choisis au moins 2 personnes.")); return; }
    if (newConvType === "group" && !newConvTitle.trim()) { showToast(t("messages.giveGroupName", "Donne un nom au groupe.")); return; }
    if (newConvType === "multi-branch" && !newConvStores.length && !newConvMembers.length) { showToast(t("messages.pickBranchOrRecipient", "Choisis au moins une filiale ou un destinataire.")); return; }
    const request = newConvType === "group"
      ? apiJSON("/api/messages/group", { method: "POST", body: JSON.stringify({ title: newConvTitle.trim(), memberIds: newConvMembers }) })
      : apiJSON("/api/messages/multi-branch", { method: "POST", body: JSON.stringify({ title: newConvTitle.trim(), storeIds: newConvStores, memberIds: newConvMembers }) });
    request.then((data) => {
      loadConversations();
      setNewConvType("private"); setNewConvMembers([]); setNewConvTitle(""); setNewConvStores([]); setNewConvQuery("");
      openThread(data.conversation.id);
      // Partage d'un Rundschreiben (voir pendingInfoShare) — consommé une seule fois, jamais
      // pour une conversation "nouveau message" ordinaire.
      if (pendingInfoShare) {
        const share = pendingInfoShare;
        setPendingInfoShare(null);
        finalizeInfoShareInConversation(data.conversation.id, share);
      }
    }).catch((err) => showToast(err.message || t("err.creationImpossible", "Création impossible.")));
  };
  // Relaie le PDF déjà joint à un Rundschreiben publié dans une conversation qui vient
  // d'être créée (2026-09-12) — deux systèmes de pièces jointes distincts (important-info vs
  // messages), donc un vrai relais : télécharge le fichier déjà stocké côté important-info,
  // le réupload tel quel côté messages (mêmes octets, jamais régénéré), puis envoie un
  // message texte court avec le titre pour donner le contexte.
  const finalizeInfoShareInConversation = async (convId, share) => {
    try {
      let attachmentIds = [];
      if (share.attachment) {
        const res = await fetch(`/api/important-info/${encodeURIComponent(share.infoId)}/attachment/${encodeURIComponent(share.attachment.id)}`, { credentials: "include" });
        if (!res.ok) throw new Error(t("err.attachmentSendImpossible", "Envoi de la pièce jointe impossible."));
        const blob = await res.blob();
        const file = new File([blob], share.attachment.fileName || "rundschreiben.pdf", { type: "application/pdf" });
        const form = new FormData();
        form.append("files", file);
        const uploadData = await apiJSON(`/api/messages/${encodeURIComponent(convId)}/attachments`, { method: "POST", body: form });
        attachmentIds = (uploadData.attachments || []).map((a) => a.id);
      }
      await apiJSON(`/api/messages/${encodeURIComponent(convId)}/messages`, {
        method: "POST", body: JSON.stringify({ text: `📰 ${share.title}`, attachmentIds }),
      });
      loadThreadMessages(convId);
      loadConversations();
    } catch (err) {
      showToast(err.message || t("err.sendingImpossible", "Envoi impossible."));
    }
  };

  /* Infos importantes — vraies données. La LISTE (GET /api/important-info) ne
     contient jamais le contenu complet (c'est une propriété de VERSION, pas de
     l'info) — le détail lecteur/auteur refait toujours un GET /:id dédié. */
  const levelMeta = {
    info: { label: t("importantInfo.level.info", "Information"), color: A.blue },
    attention: { label: t("importantInfo.level.attention", "Attention"), color: A.orange },
    important: { label: t("importantInfo.level.important", "Important"), color: A.purple },
    urgent: { label: t("importantInfo.level.urgent", "Urgent"), color: A.red },
  };
  const loadInfos = () => apiJSON("/api/important-info").then((d) => setInfos(d.items || [])).catch(() => {});
  const loadInfoScope = () => apiJSON("/api/important-info/my-scope").then(setInfoScope).catch(() => {});
  const loadInfoManage = () => apiJSON("/api/important-info/manage").then((d) => setInfoManageList(d.items || [])).catch((err) => showToast(err.message || t("err.myPublicationsLoadImpossible", "Impossible de charger tes publications.")));
  // "Terminé" pour CE destinataire : lecture simple pour une info normale, signature
  // pour une obligatoire — jamais le même critère (§6/§19 du vrai contrat serveur).
  const infoIsDone = (i) => i.type === "mandatory" ? i.signed : (i.readStatus === "read" || i.readStatus === "certified" || i.readStatus === "signed");
  const infosByTab = (tab) => infos.filter((i) => {
    if ((i.category || "general") !== infoCategoryFilter) return false;
    if (tab === "archived") return i.status === "archived" || i.status === "cancelled" || i.expired;
    if (i.status !== "published" || i.expired) return false;
    return tab === "done" ? infoIsDone(i) : !infoIsDone(i);
  });
  const infoMandatoryPending = infos.filter((i) => (i.category || "general") === "general" && i.type === "mandatory" && i.status === "published" && !i.expired && !i.signed).length;
  const rundschreibenMandatoryPending = infos.filter((i) => i.category === "rundschreiben" && i.type === "mandatory" && i.status === "published" && !i.expired && !i.signed).length;
  const openInfoDetail = (id) => {
    setInfoDetailId(id); setInfoDetail(null); clearInfoSignature(); setInfoScreen("detail");
    apiJSON(`/api/important-info/${id}`)
      .then((d) => { setInfoDetail(d); loadInfos(); })
      .catch((err) => { showToast(err.message || t("err.infoNotFound", "Information introuvable.")); setInfoScreen("hub"); });
  };
  // onSuccess optionnel — le flux normal retombe sur le hub "Infos importantes", mais le
  // gate obligatoire (voir plus haut) doit au contraire avancer sa propre file d'attente
  // sans jamais naviguer ailleurs dans l'app (l'utilisateur reste bloqué tant qu'il en
  // reste). Même fonction réelle des deux côtés — jamais un second endpoint dupliqué.
  const markInfoDone = (id, requireSig, onSuccess, versionNumberOverride) => {
    const finish = (msg) => {
      showToast(msg); loadInfos();
      if (onSuccess) onSuccess(); else { setInfoScreen("hub"); setInfoTab("done"); }
    };
    if (!requireSig) {
      apiJSON(`/api/important-info/${id}/read`, { method: "POST" })
        .then(() => finish("✓ Information marquée lue"))
        .catch((err) => showToast(err.message || t("err.readSaveImpossible", "Impossible d'enregistrer la lecture.")));
      return;
    }
    if (!infoHasSignature || infoSigStrokeLenRef.current < 24) { showToast(t("toast.signToCertifyReading", "Signe pour certifier ta lecture.")); return; }
    const canvas = infoSigCanvasRef.current;
    apiJSON(`/api/important-info/${id}/sign`, {
      method: "POST",
      body: JSON.stringify({
        versionNumber: versionNumberOverride ?? infoDetail?.info?.currentVersionNumber,
        fullyRead: true, certificationAccepted: true,
        strokeLength: infoSigStrokeLenRef.current,
        signatureDataUrl: canvas.toDataURL("image/png"),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
        appVersion: "nexora-app", sessionId: String(Date.now()), language: lang,
      }),
    })
      .then(() => finish("✓ Signature enregistrée"))
      .catch((err) => showToast(err.message || t("err.signatureSaveImpossible", "Impossible d'enregistrer la signature.")));
  };
  /* Cycle de vie côté auteur (Mes publications) — fidèle au vrai
     draft→published→cancelled/archived. Ciblage réel selon le périmètre serveur
     (infoScope.allowedStores, GET /my-scope) : un Filialleiter (create.store) n'a
     qu'une seule filiale possible — jamais de sélecteur, comportement inchangé
     (targetType:"store", targetBranches:[user?.store]). Un responsable réseau
     (create.multi, Bereichsleiter/Verkaufsleiter) choisit parmi les filiales de son
     rayon (retour utilisateur explicite 2026-08-02 : "un GR puisse poster des infos
     et la diffuser selon son choix aux filiales choisies") — voir le sélecteur dans
     l'écran editor. sfImportantInfoMatchesUser exclut déjà GR/Admin de l'audience
     (targetRoles vide), donc l'auteur GR n'est jamais lui-même un destinataire requis. */
  const infoMultiTarget = infoScope.allowedStores.length > 1;
  const publishInfo = (asDraft) => {
    const isRundschreiben = infoForm.category === "rundschreiben";
    // Un Rundschreiben n'a jamais de titre/contenu RÉDIGÉ (retour utilisateur explicite,
    // 2026-09-12 : "il ne crée jamais le contenu du Rundschreiben") — le vrai contenu, c'est
    // le PDF reçu de la Niederlassung, joint juste après ce brouillon. title/content restent
    // néanmoins exigés par le serveur (POST /api/important-info) : dérivés ici du N°/de la
    // semaine, jamais tapés par le GR. On exige donc au moins l'un des deux à la place.
    if (isRundschreiben) {
      if (!infoForm.rundschreibenNr.trim() && !infoForm.rundschreibenWeek.trim()) { showToast(t("toast.giveRundschreibenNrOrWeek", "Indique au moins le N° ou la semaine.")); return; }
    } else {
      if (!infoForm.title.trim()) { showToast(t("toast.giveTitleToInfo", "Donne un titre à l'information.")); return; }
      if (!infoForm.content.trim()) { showToast(t("toast.writeFullContent", "Rédige le contenu complet.")); return; }
    }
    if (infoMultiTarget && infoForm.targetBranches.length === 0) { showToast(t("toast.chooseAtLeastOneDestBranch", "Choisis au moins une filiale destinataire.")); return; }
    // Rundschreiben est toujours "obligatoire" — jamais laissé au choix du toggle (le
    // serveur le force de toute façon, voir POST /api/important-info, mais on le reflète
    // ici pour ne jamais afficher un état incohérent côté GR).
    const type = isRundschreiben ? "mandatory" : (infoForm.mandatory ? "mandatory" : "normal");
    const targetBranches = infoMultiTarget ? infoForm.targetBranches : [user?.store].filter(Boolean);
    const rundschreibenTitle = [infoForm.rundschreibenNr.trim() ? `N° ${infoForm.rundschreibenNr.trim()}` : "", infoForm.rundschreibenWeek.trim()].filter(Boolean).join(" · ");
    const body = {
      title: isRundschreiben ? t("rundschreiben.autoTitleTemplate", { label: rundschreibenTitle }, `Rundschreiben ${rundschreibenTitle}`) : infoForm.title.trim(),
      summary: isRundschreiben ? "" : infoForm.summary.trim(),
      content: isRundschreiben ? t("rundschreiben.autoContentText", "Voir le document PDF joint.") : infoForm.content.trim(),
      importanceLevel: infoForm.level, expiresAt: infoForm.expiresAt || "", targetType: "store", targetBranches,
      category: isRundschreiben ? "rundschreiben" : "general",
      rundschreibenNr: infoForm.rundschreibenNr || "", rundschreibenWeek: infoForm.rundschreibenWeek || "",
    };
    const save = infoEditingId
      ? apiJSON(`/api/important-info/${infoEditingId}`, { method: "PATCH", body: JSON.stringify({ ...body, type }) })
      : apiJSON("/api/important-info", { method: "POST", body: JSON.stringify({ ...body, type }) });
    save
      .then((d) => asDraft ? d : apiJSON(`/api/important-info/${d.info.id}/publish`, { method: "POST" }))
      .then(() => {
        setInfoForm(emptyInfoForm); setInfoEditingId(null);
        setInfoScreen("manage");
        loadInfoManage(); loadInfos();
        showToast(asDraft ? "✓ Brouillon enregistré" : "📢 Information publiée");
      })
      .catch((err) => showToast(err.message || t("err.publishingImpossible", "Publication impossible.")));
  };
  // Rundschreiben — le vrai PDF joint au brouillon en cours (2026-08-31). Réservé aux
  // brouillons côté serveur (POST /:id/attachment) — jamais possible après publication,
  // même règle que le reste du contenu (passe par une nouvelle version sinon).
  const uploadInfoAttachment = (file) => {
    if (!file || !infoEditingId) return;
    if (file.type !== "application/pdf") { showToast(t("rundschreiben.editor.pdfOnlyError", "Seul un fichier PDF est accepté.")); return; }
    setInfoAttachmentUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      apiJSON(`/api/important-info/${infoEditingId}/attachment`, { method: "POST", body: JSON.stringify({ dataUrl: reader.result, fileName: file.name }) })
        .then((d) => { showToast(t("rundschreiben.editor.pdfAttachedToast", "✓ PDF joint")); setInfoEditingAttachments(d.version?.attachments || []); })
        .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")))
        .finally(() => setInfoAttachmentUploading(false));
    };
    reader.onerror = () => { setInfoAttachmentUploading(false); showToast(t("err.savingImpossible", "Enregistrement impossible.")); };
    reader.readAsDataURL(file);
  };
  const openManageDetail = (id) => {
    setInfoManageDetailId(id); setInfoManageDetail(null); setInfoManageRecipients(null); setInfoScreen("manageDetail");
    setRundschreibenExpandedStoreId(null); setRundschreibenShowFullList(false); setRundschreibenDetailTab("content");
    apiJSON(`/api/important-info/${id}`).then(setInfoManageDetail).catch((err) => showToast(err.message || t("err.publicationNotFound", "Publication introuvable.")));
    const item = infoManageList.find((i) => i.id === id);
    if (item && item.status !== "draft") {
      apiJSON(`/api/important-info/${id}/recipients`).then(setInfoManageRecipients).catch(() => {});
    }
  };
  const publishDraftInfo = (id) => {
    apiJSON(`/api/important-info/${id}/publish`, { method: "POST" })
      .then(() => { showToast(t("toast.infoPublished", "📢 Information publiée")); loadInfoManage(); loadInfos(); openManageDetail(id); })
      .catch((err) => showToast(err.message || t("err.publishingImpossible", "Publication impossible.")));
  };
  const cancelInfoManage = (id) => {
    apiJSON(`/api/important-info/${id}/cancel`, { method: "POST" })
      .then(() => { setInfoScreen("manage"); showToast(t("toast.infoCancelled", "Information annulée")); loadInfoManage(); loadInfos(); })
      .catch((err) => showToast(err.message || t("err.cancellationImpossible", "Annulation impossible.")));
  };
  const archiveInfoManage = (id) => {
    apiJSON(`/api/important-info/${id}/archive`, { method: "POST" })
      .then(() => { setInfoScreen("manage"); showToast(t("toast.archivedF", "📦 Archivée")); loadInfoManage(); loadInfos(); })
      .catch((err) => showToast(err.message || t("err.archivingImpossible", "Archivage impossible.")));
  };
  const deleteInfoManage = (id) => {
    apiJSON(`/api/important-info/${id}`, { method: "DELETE" })
      .then(() => { setInfoScreen("manage"); showToast(t("toast.deletedF", "🗑️ Supprimée")); loadInfoManage(); loadInfos(); })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  // Export imprimable — contenu complet + tableau de suivi nominatif des signatures,
  // retour utilisateur explicite : "le fichier imprimable doit vraiment avoir une
  // structure adequate" (pas juste une photo/liste brute). Construit à partir des vraies
  // données déjà chargées par la Fiche filiale/manageDetail (info+version+rows), aucun
  // nouvel endpoint nécessaire. Même portail d'impression que HACCP/AZL/Arbeitsplan.
  const buildInfoPrintHtml = (info, version, rows) => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const lm = levelMeta[info.importanceLevel];
    const scopeLabel = info.targetType === "network" ? t("infoExport.wholeNetwork", "Tout le réseau") : ((info.targetBranches || []).join(", ") || "—");
    const RECIPIENT_LABEL = {
      not_opened: t("infoExport.status.notOpened", "Non ouverte"), opened: t("infoExport.status.opened", "Ouverte"),
      read: t("infoExport.status.read", "Lue"), certified: t("infoExport.status.certified", "Certifiée"), signed: t("infoExport.status.signed", "Signée"),
    };
    const doneCount = rows.filter((r) => r.status === "signed" || r.status === "read").length;
    const rowsHtml = rows.map((r) => `<tr>
        <td>${esc(r.userName)}${r.isAuthor ? ` (${t("infoExport.authorSuffix", "Auteur")})` : ""}</td>
        <td>${esc(r.userRole)}</td>
        <td>${esc(r.storeId)}</td>
        <td>${esc(RECIPIENT_LABEL[r.status] || r.status)}</td>
        <td>${(r.signedAt || r.readAt) ? esc(fmtFR((r.signedAt || r.readAt).slice(0, 10))) : "—"}</td>
      </tr>`).join("");
    const contentHtml = String(version?.content || "").split("\n").map((l) => `<p>${l.trim() ? esc(l) : "&nbsp;"}</p>`).join("");
    // Bug réel corrigé au passage : `{t(...)}` (syntaxe JSX) était utilisé DANS un
    // template literal JS, où seule `${...}` interpole — le texte "{t(...)}" s'imprimait
    // donc littéralement sur le document au lieu d'être traduit.
    return `<div class="iip-page">
      <header class="iip-header">
        <div><p class="iip-eyebrow">NEXORA · ${info.type === "mandatory" ? t("infoExport.mandatoryLabel", "Information obligatoire") : t("infoExport.generalLabel", "Information générale")}</p><h1>${esc(info.title)}</h1></div>
        <span class="iip-badge" style="background:${lm.color};">${esc(lm.label)}</span>
      </header>
      <table class="iip-infotable">
        <tr><th>${t("infoExport.authorLabel", "Auteur")}</th><td>${esc(info.authorName)}${info.authorRole ? ` (${esc(info.authorRole)})` : ""}</td><th>${t("infoExport.publishedOnLabel", "Publiée le")}</th><td>${info.publishedAt ? esc(fmtFR(info.publishedAt.slice(0, 10))) : "—"}</td></tr>
        <tr><th>${t("infoExport.scopeLabel", "Portée")}</th><td>${esc(scopeLabel)}</td><th>${t("common.typeLabel", "Type")}</th><td>${info.type === "mandatory" ? t("infoExport.mandatoryFullText", "Obligatoire — lecture + signature") : t("infoExport.simpleInfoText", "Information simple")}</td></tr>
      </table>
      <div class="iip-content">${contentHtml}</div>
      <p class="iip-section-title">${t("infoExport.recipientsTrackingTemplate", { done: doneCount, total: rows.length }, `Suivi des destinataires (${doneCount}/${rows.length} terminé${doneCount > 1 ? "s" : ""})`)}</p>
      <table class="iip-table">
        <thead><tr><th>${t("common.nameLabel", "Nom")}</th><th>${t("admin.roleFieldLabel", "Rôle")}</th><th>${t("common.storeLabel", "Filiale")}</th><th>${t("common.statusLabel", "Statut")}</th><th>${t("reporting.dateLabel", "Date")}</th></tr></thead>
        <tbody>${rowsHtml || `<tr><td colspan="5" style="text-align:center;color:#777;">${t("infoExport.noRecipients", "Aucun destinataire.")}</td></tr>`}</tbody>
      </table>
      <p class="iip-footer-note">${t("infoExport.generatedOnTemplate", { date: esc(new Date().toLocaleString("fr-FR")), id: esc(info.id) }, `Document généré le ${esc(new Date().toLocaleString("fr-FR"))} — ID ${esc(info.id)}`)}</p>
    </div>`;
  };
  const exportInfoPrint = async () => {
    if (!infoManageDetail) return;
    const rows = infoManageRecipients?.rows || [];
    try {
      await exportHtmlToPdf({ bodyHtml: buildInfoPrintHtml(infoManageDetail.info, infoManageDetail.version, rows), css: IIP_DOC_CSS, targetSelector: ".iip-page", widthMm: 210, filename: `Info-${(infoManageDetail.info.title || "").replace(/[\\/:*?"<>|]+/g, "-")}.pdf` });
    } catch (err) {
      if (err?.name !== "AbortError") showToast(err.message || t("err.actionImpossible", "Action impossible."));
    }
  };

  /* Reporting — Prebake/Brot auto (vrai catalogue + vraie saisie quotidienne
     entsorgt, /api/reporting/articles et /api/reporting/daily-entries) + 7 rayons
     manuels réels (weeklyGrid, même famille daily-entries "manual_waste" — voir
     loadManualWasteWeek/saveDailyFamily/saveWeek), chacun en 2 lignes PÜ/20% et
     entsorgt. frischbrot_brot (auto_brot) porte en plus un pu20Amount saisi à la
     main, jamais persisté seul — seulement via la sauvegarde hebdomadaire groupée
     (fidèle à saveWeeklyManualEntries côté vanilla). Verrouillage jour et semaine
     avec override "Modifier", fidèle au vrai Abschriften KW.
     TODAY_IDX était figé (index 2, "mercredi de démo") — remplacé par le vrai jour
     de la semaine (Lu=0..Sa=5, Dimanche replié sur Sa puisque ce module n'a que 6
     colonnes) via les mêmes helpers déjà établis pour Planning (mondayOfWeekOffset/
     addDaysISO/todayISO, bug de fuseau horaire déjà corrigé ailleurs). */
  const reportingWeekMonday = mondayOfWeekOffset(reportingWeekOffset);
  const reportingWeekDates = [0, 1, 2, 3, 4, 5].map((i) => addDaysISO(reportingWeekMonday, i));
  const reportingTodayIdxRaw = reportingWeekDates.indexOf(todayISO());
  const TODAY_IDX = reportingTodayIdxRaw === -1 ? 5 : reportingTodayIdxRaw;
  // Semaine/année RÉELLES de la semaine affichée (peut différer de isoWeek(now)/
  // now.getFullYear() dès que reportingWeekOffset !== 0) — jamais "now" en dur dans les
  // appels réseau ci-dessous, sans quoi naviguer vers une semaine passée continuerait de
  // charger/écraser la semaine calendaire du jour.
  const reportingWeekNumber = isoWeek(new Date(reportingWeekMonday + "T00:00:00"));
  const reportingWeekYear = Number(reportingWeekMonday.slice(0, 4));
  const loadReportingCatalog = () => {
    apiJSON(`/api/reporting/articles?storeId=${encodeURIComponent(user?.store)}`)
      .then((list) => setRepCatalog((Array.isArray(list) ? list : []).map((a) => ({
        id: a.id, artNr: a.artNr || "", name: a.name || "",
        category: a.family === "brot" ? "Brot" : "Prebake",
        program: a.program || "", content: a.content || "", ve: a.ve ?? "",
        price: Number(a.price || 0), status: a.status || "active",
        displayOrder: Number(a.displayOrder || 999),
      }))))
      .catch(() => setRepCatalog([]));
  };
  // Charge TOUTE la semaine (pas juste aujourd'hui) — le rollup hebdomadaire
  // (Abschriften KW, categoryDayValue plus bas) affiche familyDayTotal pour
  // chaque jour, pas seulement TODAY_IDX.
  const loadCatalogDaily = () => {
    const week = reportingWeekNumber;
    const year = reportingWeekYear;
    Promise.all(["prebake", "brot"].map((family) =>
      apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(user?.store)}&family=${family}&week=${week}&year=${year}`)
        .then((entries) => ({ family, entries: Array.isArray(entries) ? entries : [] }))
        .catch(() => ({ family, entries: [] }))
    )).then((results) => {
      const nextCatalogDaily = { prebake: {}, brot: {} };
      const nextSaved = { prebake: {}, brot: {} };
      results.forEach(({ family, entries }) => {
        entries.forEach((entry) => {
          const dayIdx = reportingWeekDates.indexOf(entry.date);
          if (dayIdx === -1) return;
          const dayMap = {};
          (entry.items || []).forEach((item) => { if (item.articleId) dayMap[item.articleId] = Number(item.entsorgtPieces || 0); });
          nextCatalogDaily[family][dayIdx] = dayMap;
          nextSaved[family][dayIdx] = true;
        });
      });
      // Fusion, jamais un remplacement complet — même correctif et même raison que
      // loadManualWasteWeek juste en dessous (retour utilisateur explicite, 2026-09-19) :
      // ce chemin partage le même risque (sondage Dashboard 15s + rechargements de semaine)
      // même s'il ne s'est pas manifesté cette fois-ci pour prebake/brot.
      setCatalogDaily((prev) => ({
        prebake: { ...prev.prebake, ...nextCatalogDaily.prebake },
        brot: { ...prev.brot, ...nextCatalogDaily.brot },
      }));
      setDailySaved((d) => ({ ...d, prebake: { ...d.prebake, ...nextSaved.prebake }, brot: { ...d.brot, ...nextSaved.brot } }));
    });
  };
  // "Autres Abschriften" (manual_waste) — même famille réelle que prebake/brot
  // (/api/reporting/daily-entries), mais SANS catalogue d'articles : chaque entrée
  // porte directement un item par rayon manuel (categoryId/pu20Amount/entsorgtAmount),
  // fidèle à public/modules/reporting.js (saveCurrentDailyEntry/saveWeeklyManualEntries).
  // frischbrot_brot n'est PAS un rayon manuel (source "auto_brot") mais porte quand
  // même un pu20Amount saisi à la main (démarque prix du pain, jamais calculée) —
  // rejoint items UNIQUEMENT dans la sauvegarde hebdomadaire groupée (saveWeek), jamais
  // dans la saisie quotidienne "Autres Abschriften" seule (comportement réel identique).
  const loadManualWasteWeek = () => {
    const week = reportingWeekNumber;
    const year = reportingWeekYear;
    apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(user?.store)}&family=manual_waste&week=${week}&year=${year}`)
      .then((entries) => {
        const next = {};
        const nextSavedDays = {};
        (Array.isArray(entries) ? entries : []).forEach((entry) => {
          const dayIdx = reportingWeekDates.indexOf(entry.date);
          if (dayIdx === -1) return;
          (entry.items || []).forEach((item) => {
            if (!item.categoryId) return;
            next[item.categoryId] = { ...next[item.categoryId], [dayIdx]: { pu20: Number(item.pu20Amount || 0), entsorgt: Number(item.entsorgtAmount || 0) } };
          });
          nextSavedDays[dayIdx] = true;
        });
        // Fusion, jamais un remplacement complet (2026-09-19, retour utilisateur explicite :
        // "j'ai entré plusieurs valeurs ce soir mais elles ne s'affichent pas") — cette
        // fonction est aussi appelée par un sondage en arrière-plan (Dashboard, toutes les
        // 15s) et par les rechargements de semaine/écran : un setWeeklyGrid(next) en
        // remplacement PUR effaçait silencieusement toute case tapée localement mais pas
        // encore enregistrée si une de ces relectures se terminait entre-temps — aucune
        // erreur visible, aucune trace, la valeur tapée disparaissait juste. On ne réécrit
        // donc que les catégories/jours que le serveur connaît réellement (vraies données
        // confirmées, utile si une autre personne a enregistré depuis un autre poste),
        // jamais les cases locales absentes de la réponse serveur.
        setWeeklyGrid((prev) => {
          const merged = { ...prev };
          Object.keys(next).forEach((categoryId) => {
            merged[categoryId] = { ...merged[categoryId], ...next[categoryId] };
          });
          return merged;
        });
        setDailySaved((d) => ({ ...d, manual_waste: nextSavedDays }));
      })
      .catch(() => {});
  };
  // Krank — semaine en cours (clôture mensuelle). Personnes réellement calculées depuis
  // Planning (GET /krank-persons-weekly, réservé aux rôles de management côté serveur —
  // échoue silencieusement à 0 pour un compte non-management, cohérent avec le fait que
  // cet écran est pensé pour la clôture, un geste de responsable). Heures réellement
  // persistées via daily-entries famille "krank" (déjà prévue côté serveur, jamais
  // utilisée par le client jusqu'ici) — une entrée par semaine, datée au lundi.
  const loadKrankPersons = () => {
    const year = now.getFullYear();
    apiJSON(`/api/reporting/krank-persons-weekly?storeId=${encodeURIComponent(user?.store)}&year=${year}`)
      .then((d) => setKrankPersons(d?.weeks?.[isoWeek(now)]?.count || 0))
      .catch(() => setKrankPersons(0));
  };
  const loadKrankHours = () => {
    const week = isoWeek(now);
    const year = now.getFullYear();
    apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(user?.store)}&family=krank&week=${week}&year=${year}`)
      .then((entries) => {
        if (krankHoursDirtyRef.current) return;
        setKrankHours(Number((Array.isArray(entries) ? entries : [])[0]?.items?.[0]?.hours || 0));
      })
      .catch(() => { if (!krankHoursDirtyRef.current) setKrankHours(0); });
  };
  const saveKrankHours = () => {
    apiJSON("/api/reporting/daily-entries", {
      method: "POST",
      body: JSON.stringify({
        storeId: user?.store, date: reportingWeekMonday, week: isoWeek(now), year: now.getFullYear(), month: now.getMonth() + 1,
        family: "krank", totalEuro: 0, items: [{ hours: krankHours }], updatedBy: user?.firstname || "",
      }),
    }).then(() => { krankHoursDirtyRef.current = false; })
      .catch((err) => showToast(err.message || t("err.krankHoursSaveImpossible", "Enregistrement des heures Krank impossible.")));
  };

  // Historique réel (clôture mensuelle + Historique) — un seul GET sans filtre "family"
  // (toutes familles pour l'année de la filiale), regroupée par (semaine, mois) côté client
  // — une semaine à cheval sur deux mois donne deux entrées distinctes ("KWxxa"/"KWxxb",
  // texte déjà annoncé par l'écran mais jamais implémenté avant ce chantier), chacune ne
  // portant que le total RÉEL des jours de la semaine tombant dans SON mois.
  const MONTH_NAMES_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
  const loadReportingHistory = () => {
    const year = now.getFullYear();
    // reportingWeekNumber (la semaine NAVIGUÉE, voir reportingWeekOffset) plutôt que
    // isoWeek(now) figé — cette semaine est déjà affichée "en direct" (catalogDaily/
    // weeklyGrid), jamais dupliquée dans l'historique, quelle que soit la semaine réelle
    // du calendrier.
    const currentWeek = reportingWeekNumber;
    Promise.all([
      apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(user?.store)}&year=${year}`).catch(() => []),
      apiJSON(`/api/reporting/krank-persons-weekly?storeId=${encodeURIComponent(user?.store)}&year=${year}`).catch(() => null),
    ]).then(([entries, krank]) => {
      // Une entrée par (semaine, mois) — un jour ne compte jamais que pour SON propre mois.
      // byCategory : répartition PÜ/20% vs entsorgt PAR RAYON, nécessaire au vrai format
      // Monatsmeldung (voir buildMonthClosurePrintHtml) — jamais recalculée depuis le
      // catalogue au prix actuel, toujours depuis les montants € réellement enregistrés à
      // l'époque (entsorgtEuro/pu20Amount déjà figés dans chaque entrée).
      const buckets = {};
      const addCat = (bucket, catId, pu20, entsorgt) => {
        bucket.byCategory[catId] = bucket.byCategory[catId] || { pu20: 0, entsorgt: 0 };
        bucket.byCategory[catId].pu20 += pu20;
        bucket.byCategory[catId].entsorgt += entsorgt;
      };
      (Array.isArray(entries) ? entries : []).forEach((e) => {
        if (!["prebake", "brot", "manual_waste", "krank"].includes(e.family)) return;
        const wk = Number(e.week);
        const mo = Number(e.month);
        if (!Number.isFinite(wk) || wk === currentWeek || !mo) return; // semaine en cours = affichée en direct, jamais dupliquée ici
        const key = `${wk}-${mo}`;
        if (!buckets[key]) buckets[key] = { week: wk, month: mo, total: 0, krankHours: 0, byCategory: {} };
        const bucket = buckets[key];
        if (e.family === "krank") { bucket.krankHours += Number(e.items?.[0]?.hours || 0); return; }
        bucket.total += Number(e.totalEuro || 0);
        if (e.family === "manual_waste") {
          (e.items || []).forEach((item) => {
            if (!item.categoryId) return;
            addCat(bucket, item.categoryId, Number(item.pu20Amount || 0), Number(item.entsorgtAmount || 0));
          });
        } else {
          const catId = e.family === "prebake" ? "prebake" : "frischbrot_brot";
          const entsorgtSum = (e.items || []).reduce((s, it) => s + Number(it.entsorgtEuro || 0), 0);
          addCat(bucket, catId, 0, entsorgtSum);
        }
      });
      // Pour chaque semaine ayant des jours dans 2 mois différents : le plus tôt = "a", le
      // suivant = "b" (ordre chronologique réel, jamais un simple tri numérique — couvre
      // aussi le cas d'une semaine à cheval sur décembre/janvier).
      const weekMonths = {};
      Object.values(buckets).forEach((b) => {
        if (!weekMonths[b.week]) weekMonths[b.week] = [];
        if (!weekMonths[b.week].includes(b.month)) weekMonths[b.week].push(b.month);
      });
      Object.values(weekMonths).forEach((arr) => arr.sort((a, b) => a - b));
      const list = Object.values(buckets)
        .sort((a, b) => b.week - a.week || b.month - a.month)
        .map((b) => {
          const months = weekMonths[b.week];
          const suffix = months.length > 1 ? (months.indexOf(b.month) === 0 ? "a" : "b") : "";
          return {
            kw: b.week, kwLabel: `${b.week}${suffix}`, year, total: truncate2(b.total),
            krankHours: b.krankHours, krankPersons: krank?.weeks?.[b.week]?.count || 0, month: MONTH_NAMES_FR[b.month - 1],
            byCategory: b.byCategory,
          };
        });
      setReportingHistory(list);
    });
  };

  // Consultation en LECTURE SEULE d'une semaine passée sur "Rapport hebdomadaire" (sélecteur
  // de semaine) — jamais mélangée à catalogDaily/weeklyGrid (état d'édition de la semaine en
  // cours). Réutilise mondayOfWeekOffset (déjà réel, basé sur "aujourd'hui" + offset) plutôt
  // qu'un nouveau calcul ISO absolu — offset = semaine visée − semaine actuelle.
  const datesForWeekNumber = (weekNum) => {
    const offset = weekNum - isoWeek(now);
    const monday = mondayOfWeekOffset(offset);
    return [0, 1, 2, 3, 4, 5].map((i) => addDaysISO(monday, i));
  };
  // Extrait de loadPastWeek — construit le même objet { week, dates, value, rowTotal, total }
  // pour N'IMPORTE QUELLE filiale (storeId explicite). Fonction pure côté résultat (aucun
  // état écrit), réutilisée telle quelle par loadPastWeek (filiale du compte connecté) ET
  // par la fiche filiale (Pilotage réseau, pilotRepWeekSrcs plus bas) pour une AUTRE
  // filiale — même calcul, jamais dupliqué. "Live" par construction : relit toujours
  // /api/reporting/daily-entries, aussi bien pour la semaine en cours (encore en train de
  // se remplir) que pour la semaine précédente.
  const fetchWeekSrcForStore = (storeId, weekNum) => {
    const dates = datesForWeekNumber(weekNum);
    const year = now.getFullYear();
    return Promise.all(["prebake", "brot", "manual_waste"].map((family) =>
      apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(storeId)}&family=${family}&week=${weekNum}&year=${year}`)
        .then((entries) => ({ family, entries: Array.isArray(entries) ? entries : [] }))
        .catch(() => ({ family, entries: [] }))
    )).then((results) => {
      // Toujours les vrais € déjà enregistrés à l'époque (entsorgtEuro/pu20Amount), jamais
      // qté × prix catalogue ACTUEL — un prix a pu changer depuis.
      const byCategory = {};
      const ensure = (catId, dayIdx) => {
        byCategory[catId] = byCategory[catId] || {};
        byCategory[catId][dayIdx] = byCategory[catId][dayIdx] || { pu20: 0, entsorgt: 0 };
        return byCategory[catId][dayIdx];
      };
      results.forEach(({ family, entries }) => {
        entries.forEach((entry) => {
          const dayIdx = dates.indexOf(entry.date);
          if (dayIdx === -1) return;
          if (family === "manual_waste") {
            (entry.items || []).forEach((item) => {
              if (!item.categoryId) return;
              const cell = ensure(item.categoryId, dayIdx);
              cell.pu20 = Number(item.pu20Amount || 0);
              cell.entsorgt = Number(item.entsorgtAmount || 0);
            });
          } else {
            const catId = family === "prebake" ? "prebake" : "frischbrot_brot";
            const entsorgtSum = (entry.items || []).reduce((s, it) => s + Number(it.entsorgtEuro || 0), 0);
            ensure(catId, dayIdx).entsorgt = truncate2(entsorgtSum);
          }
        });
      });
      const value = (catId, dayIdx) => byCategory[catId]?.[dayIdx] || { pu20: 0, entsorgt: 0 };
      const rowTotal = (catId) => dates.reduce((s, _, i) => { const v = value(catId, i); return s + v.pu20 + v.entsorgt; }, 0);
      const total = REPORT_CATEGORIES.reduce((s, cat) => s + rowTotal(cat.id), 0);
      return { week: weekNum, dates, value, rowTotal, total: truncate2(total) };
    });
  };
  const loadPastWeek = (weekNum) => {
    setPastWeekLoading(true);
    fetchWeekSrcForStore(user?.store, weekNum).then((src) => {
      setPastWeekSrc(src);
      setPastWeekLoading(false);
    });
  };

  const catalogArticlesFor = (cat) => repCatalog.filter((a) => a.category === cat && a.status === "active");
  // Génériques par JOUR (dayIdx explicite) — plus jamais figées sur TODAY_IDX, pour
  // permettre le rattrapage d'un jour manqué de la semaine en cours (voir sélecteur de
  // jour sur l'écran "Saisie quotidienne").
  const dayQty = (family, dayIdx, articleId) => catalogDaily[family]?.[dayIdx]?.[articleId] || 0;
  const setDayQty = (family, dayIdx, articleId, delta) => setCatalogDaily((cd) => ({
    ...cd, [family]: { ...cd[family], [dayIdx]: { ...cd[family]?.[dayIdx], [articleId]: Math.max(0, (cd[family]?.[dayIdx]?.[articleId] || 0) + delta) } },
  }));
  const familyDayTotal = (family, dayIdx) => catalogArticlesFor(family === "prebake" ? "Prebake" : "Brot")
    .reduce((s, a) => s + (catalogDaily[family]?.[dayIdx]?.[a.id] || 0) * a.price, 0);
  const isFamilyDayLocked = (family, dayIdx) => Boolean(dailySaved[family]?.[dayIdx]) && !dailyLockOverride[family]?.[dayIdx];

  const startDailyReorder = () => {
    setDailyReorderList(catalogArticlesFor(dailyFamily === "prebake" ? "Prebake" : "Brot").slice());
    setDailyReorderActive(true);
  };
  const moveDailyReorderItem = (index, direction) => {
    setDailyReorderList((list) => {
      const target = index + direction;
      if (target < 0 || target >= list.length) return list;
      const next = [...list];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };
  const saveDailyReorder = () => {
    Promise.all(dailyReorderList.map((a, i) =>
      apiJSON(`/api/reporting/articles/${encodeURIComponent(a.id)}`, { method: "PATCH", body: JSON.stringify({ displayOrder: (i + 1) * 10, updatedBy: user?.firstname || "" }) })
    )).then(() => {
      showToast(t("toast.reportingOrderSaved", "✓ Ordre enregistré"));
      setDailyReorderActive(false);
      loadReportingCatalog();
    }).catch((err) => showToast(err.message || t("err.actionRefused", "Action refusée.")));
  };
  // Export par KW (retour utilisateur explicite : "L'export ne doit pas être
  // journalière mais plutôt par KW") — une colonne par jour de la semaine affichée
  // (reportingWeekNumber/Year, même semaine que la navigation ‹ KW N › de l'écran),
  // jamais juste le jour sélectionné. catalogDaily couvre déjà TOUTE la semaine
  // (voir loadCatalogDaily), donc aucun appel réseau supplémentaire nécessaire.
  const buildWeeklyReportingPrintHtml = (family) => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const familyLabel = family === "prebake" ? "Prebake" : "Brot";
    const list = catalogArticlesFor(family === "prebake" ? "Prebake" : "Brot");
    // Retour utilisateur (2026-09-08) : "presque identique à celui de Norma", tout sur une
    // seule page paysage — reprend la structure RÉELLE du formulaire papier "Prebake/Brot -
    // Abschriften" (Nr/Art-Nr/Art-Bez/Backprogramm/Inhalt/VE/Preis fixes, puis Stück+Euro
    // COLONNE À COLONNE pour chaque jour, jamais empilés en 2 lignes comme le premier essai
    // — une seule ligne par article, sinon ~24 articles × 2 lignes ne tient plus sur une
    // page A4 paysage). Densité calquée sur .rep-mm-table (Monatsmeldung), déjà la seule
    // autre table de ce fichier pensée pour reproduire un vrai document Norma sur 1 page.
    const rows = list.map((a, idx) => {
      const qtys = [0, 1, 2, 3, 4, 5].map((i) => dayQty(family, i, a.id));
      const dayCells = qtys.map((q) => `<td>${q || ""}</td><td>${q > 0 ? (q * a.price).toFixed(2) : ""}</td>`).join("");
      const totalQty = qtys.reduce((s, q) => s + q, 0);
      const totalEuro = totalQty * a.price;
      return `<tr>
          <td>${idx + 1}</td><td>${esc(a.artNr)}</td><th>${esc(a.name)}</th><td>${esc(a.program)}</td><td>${esc(a.content)}</td><td>${esc(a.ve)}</td><td>${a.price.toFixed(2)}</td>
          ${dayCells}<td class="rep-total">${totalQty || ""}</td><td class="rep-total">${totalEuro > 0 ? totalEuro.toFixed(2) : ""}</td>
        </tr>`;
    }).join("");
    const dayTotals = [0, 1, 2, 3, 4, 5].map((i) => familyDayTotal(family, i));
    const grandTotal = dayTotals.reduce((s, v) => s + v, 0);
    // Retour utilisateur (2026-09-08) : "il manque le Summe Tag in Euro pour chaque
    // colonne" — ligne de total PAR JOUR (somme verticale de tous les articles), distincte
    // du total PAR ARTICLE (dernière colonne) et du total semaine (déjà sous le tableau).
    // Réutilise familyDayTotal, déjà la source de vérité pour "Total du jour" dans l'écran
    // de saisie lui-même. colspan=2 fusionne Stück+€ de chaque jour (une seule vraie valeur
    // à afficher ici, le total en pièces n'a pas de sens business propre).
    const dayTotalCells = dayTotals.map((v) => `<td colspan="2" class="rep-total">${v.toFixed(2)} €</td>`).join("");
    const dayHeaders = [0, 1, 2, 3, 4, 5].map((i) => `<th colspan="2">${esc(weekLabelShort(i))}</th>`).join("");
    const subHeaders = [0, 1, 2, 3, 4, 5].map(() => `<th>Stück</th><th>€</th>`).join("");
    return `<div class="rep-page rep-page-norma">
      <header class="rep-header">
        <div><p class="rep-eyebrow">NEXORA · Reporting</p><h1>${t("reporting.daily.printWeekTitleTemplate", { family: familyLabel, week: reportingWeekNumber, year: reportingWeekYear }, `Saisie quotidienne ${familyLabel} — KW ${reportingWeekNumber}/${reportingWeekYear}`)}</h1></div>
        <span class="rep-badge">${t("home.store.label", { id: esc(user?.store) }, `Filiale ${esc(user?.store)}`)}</span>
      </header>
      <table class="rep-table rep-table-norma">
        <thead>
          <tr>
            <th rowspan="2">Nr</th><th rowspan="2">Art.-Nr</th><th rowspan="2">Artikel</th>
            <th rowspan="2">Backprogramm</th><th rowspan="2">Inhalt</th><th rowspan="2">VE</th><th rowspan="2">Preis</th>
            ${dayHeaders}
            <th colspan="2">Gesamt</th>
          </tr>
          <tr>${subHeaders}<th>Stück</th><th>€</th></tr>
        </thead>
        <tbody>${rows}</tbody>
        <tfoot><tr class="rep-tfoot-row"><th colspan="7">${t("reporting.daily.totalOfDayShort", "Total du jour")}</th>${dayTotalCells}<td colspan="2" class="rep-total">${grandTotal.toFixed(2)} €</td></tr></tfoot>
      </table>
      <p class="rep-grand-total">${t("weeklyReport.weekTotalTemplate", { amount: grandTotal.toFixed(2) }, `Total semaine : ${grandTotal.toFixed(2)} €`)}</p>
      <p class="rep-footer-note">${t("common.documentGeneratedOnTemplate", { date: fmtFR(todayISO()), name: `${esc(user?.firstname || "")} ${esc(user?.lastname || "")}` }, `Document généré le ${fmtFR(todayISO())} — ${esc(user?.firstname || "")} ${esc(user?.lastname || "")}`)}</p>
    </div>`;
  };
  const exportDailyReporting = () => {
    const familyLabel = dailyFamily === "prebake" ? "Prebake" : "Brot";
    // Pas de heightMm forcé (retour utilisateur 2026-09-08) : Prebake (~27 articles) atteint
    // déjà ~208mm naturellement, quasi-A4 pile — mais Brot (~16 articles) ne fait que ~150mm.
    // Forcer 210mm aurait étiré Brot de 40% (texte/lignes disproportionnés) pour gagner un
    // remplissage artificiel. La densité de police/cellules (voir .rep-table-norma) est déjà
    // la même pour les deux familles — une page plus courte à contenu égal est correcte,
    // jamais un défaut à corriger en écrasant le ratio naturel.
    printReportWithTitle(buildWeeklyReportingPrintHtml(dailyFamily), `${familyLabel} - KW${reportingWeekNumber} ${reportingWeekYear} - Filiale ${user?.store}`);
  };
  const saveDailyFamily = (family, dayIdx) => {
    // Semaine/année/mois dérivés de la vraie date DU JOUR CHOISI, jamais de "now" — une
    // semaine à cheval sur deux mois (voir monthWeeks/loadReportingHistory, KWxxa/KWxxb)
    // aurait sinon étiqueté un jour de fin de mois précédent avec le mois en cours.
    const date = reportingWeekDates[dayIdx];
    // Bug réel trouvé en câblant la navigation entre semaines (2026-08-06) : le
    // commentaire ci-dessus promettait déjà "jamais now", mais `week` restait
    // `isoWeek(now)` en dur malgré year/month déjà correctement dérivés de `date` —
    // sans conséquence tant que Saisies quotidiennes ne montrait QUE la semaine
    // calendaire réelle, mais aurait enregistré une semaine sous le mauvais numéro dès
    // qu'on navigue vers une semaine passée.
    const week = isoWeek(new Date(date + "T00:00:00"));
    const year = Number(date.slice(0, 4));
    const month = Number(date.slice(5, 7));
    if (family === "manual_waste") {
      const items = MANUAL_CATEGORIES.map((cat) => {
        const v = manualCellValue(cat.id, dayIdx);
        return { categoryId: cat.id, label: reportCategoryLabel(cat.id), pu20Amount: v.pu20 || 0, entsorgtAmount: v.entsorgt || 0, amount: (v.pu20 || 0) + (v.entsorgt || 0) };
      });
      // Retour utilisateur (2026-08-09) : Prebake et Frischbrot/Brot doivent aussi
      // apparaître dans la saisie quotidienne "Autres Abschriften", pas seulement à la
      // clôture hebdomadaire groupée (saveWeek ci-dessous, même construction d'items —
      // categoryDayValue reste la SEULE source de vérité pour ces 2 lignes automatiques).
      const brotPu20 = parseCommaAmount(weeklyGrid.frischbrot_brot?.[dayIdx]?.pu20);
      const brotEntsorgt = truncate2(familyDayTotal("brot", dayIdx));
      items.push({ categoryId: "frischbrot_brot", label: "Frischbrot / Brot", pu20Amount: brotPu20, entsorgtAmount: brotEntsorgt, amount: brotPu20 + brotEntsorgt });
      const prebakeEntsorgt = truncate2(familyDayTotal("prebake", dayIdx));
      items.push({ categoryId: "prebake", label: "Prebake", pu20Amount: 0, entsorgtAmount: prebakeEntsorgt, amount: prebakeEntsorgt });
      const totalEuro = truncate2(items.reduce((s, it) => s + it.amount, 0));
      apiJSON("/api/reporting/daily-entries", {
        method: "POST",
        body: JSON.stringify({ storeId: user?.store, date, week, year, month, family: "manual_waste", totalEuro, items, updatedBy: user?.firstname || "" }),
      })
        .then(() => {
          setDailySaved((d) => ({ ...d, manual_waste: { ...d.manual_waste, [dayIdx]: true } }));
          setDailyLockOverride((d) => ({ ...d, manual_waste: { ...d.manual_waste, [dayIdx]: false } }));
          showToast(t("toast.entrySavedDayClosed", "✓ Saisie enregistrée — journée clôturée"));
        })
        .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
      return;
    }
    const articles = catalogArticlesFor(family === "prebake" ? "Prebake" : "Brot");
    const items = articles.map((a) => {
      const entsorgtPieces = catalogDaily[family]?.[dayIdx]?.[a.id] || 0;
      const priceUsed = Number(a.price || 0);
      const entsorgtEuro = truncate2(entsorgtPieces * priceUsed);
      return {
        articleId: a.id, artNr: a.artNr || "", name: a.name, family,
        program: a.program || "", content: a.content || "", ve: a.ve ?? "",
        pu20Pieces: 0, entsorgtPieces, pieces: entsorgtPieces,
        priceUsed, pu20Euro: 0, entsorgtEuro, totalEuro: entsorgtEuro,
      };
    });
    const totalEuro = truncate2(items.reduce((s, it) => s + it.totalEuro, 0));
    apiJSON("/api/reporting/daily-entries", {
      method: "POST",
      body: JSON.stringify({ storeId: user?.store, date, week, year, month, family, totalEuro, items, updatedBy: user?.firstname || "" }),
    })
      .then(() => {
        setDailySaved((d) => ({ ...d, [family]: { ...d[family], [dayIdx]: true } }));
        setDailyLockOverride((d) => ({ ...d, [family]: { ...d[family], [dayIdx]: false } }));
        showToast(t("toast.entrySavedDayClosed", "✓ Saisie enregistrée — journée clôturée"));
      })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  const unlockDailyFamily = (family, dayIdx) => setDailyLockOverride((d) => ({ ...d, [family]: { ...d[family], [dayIdx]: true } }));

  // weeklyGrid stocke les saisies pu20/entsorgt EN TEXTE BRUT tel que tapé (virgule
  // comprise) — jamais un nombre déjà arrondi/parsé — pour que le champ affiche fidèlement
  // ce que l'utilisateur tape (retour utilisateur explicite : "le 0,x ne fonctionne pas" —
  // un input qui réaffiche à chaque frappe la valeur DÉJÀ parsée efface la virgule en cours
  // de saisie). Le parsing virgule→point n'a lieu qu'ici, au point de lecture unique.
  const parseCommaAmount = (v) => {
    if (v === undefined || v === null || v === "") return 0;
    const n = parseFloat(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  };
  const manualCellValue = (categoryId, dayIdx) => {
    const raw = weeklyGrid[categoryId]?.[dayIdx] || {};
    return { pu20: parseCommaAmount(raw.pu20), entsorgt: parseCommaAmount(raw.entsorgt) };
  };
  const categoryDayValue = (category, dayIdx) => {
    if (category.source === "auto_prebake") return { pu20: 0, entsorgt: truncate2(familyDayTotal("prebake", dayIdx)) };
    if (category.source === "auto_brot") return { pu20: parseCommaAmount(weeklyGrid.frischbrot_brot?.[dayIdx]?.pu20), entsorgt: truncate2(familyDayTotal("brot", dayIdx)) };
    return manualCellValue(category.id, dayIdx);
  };
  const categoryRowTotal = (category) => WEEK_DAY_LABELS.reduce((s, _, i) => { const v = categoryDayValue(category, i); return s + v.pu20 + v.entsorgt; }, 0);
  const weekTotal = () => truncate2(REPORT_CATEGORIES.reduce((s, c) => s + categoryRowTotal(c), 0));
  // Portion du total de la semaine EN COURS qui tombe dans un mois donné — jamais le total
  // entier de la semaine (weekTotal() reste la vraie somme Abschriften KW, utilisée pour le
  // rapport hebdomadaire). Nécessaire pour la Clôture mensuelle quand la semaine en cours
  // chevauche deux mois (KWxxa/KWxxb, voir loadReportingHistory pour les semaines passées).
  const weekTotalForMonth = (monthNum) => {
    let total = 0;
    reportingWeekDates.forEach((date, i) => {
      if (Number(date.slice(5, 7)) !== monthNum) return;
      REPORT_CATEGORIES.forEach((cat) => { const v = categoryDayValue(cat, i); total += v.pu20 + v.entsorgt; });
    });
    return truncate2(total);
  };
  // Répartition PÜ/20% vs entsorgt de la semaine EN COURS, restreinte au mois donné — même
  // principe que weekTotalForMonth, nécessaire au vrai format Monatsmeldung (une ligne
  // PÜ/20% + une ligne entsorgt par rayon, jamais un total fusionné).
  const categoryTotalForMonth = (cat, monthNum) => {
    let pu20 = 0, entsorgt = 0;
    reportingWeekDates.forEach((date, i) => {
      if (Number(date.slice(5, 7)) !== monthNum) return;
      const v = categoryDayValue(cat, i);
      pu20 += v.pu20; entsorgt += v.entsorgt;
    });
    return { pu20: truncate2(pu20), entsorgt: truncate2(entsorgt) };
  };
  const weekByCategory = () => REPORT_CATEGORIES.map((c) => ({ category: reportCategoryLabel(c.id), total: truncate2(categoryRowTotal(c)) })).filter((x) => x.total > 0);
  const isWeekLocked = () => weekSaved && !weekLockOverride;
  const openGridEdit = (categoryId, dayIdx) => {
    const v = manualCellValue(categoryId, dayIdx);
    setGridEditTarget({ categoryId, dayIdx });
    setGridEditDraft({ pu20: v.pu20 ? String(v.pu20) : "", entsorgt: v.entsorgt ? String(v.entsorgt) : "" });
    setSheet("gridEdit");
  };
  // Réintroduite (2026-09-20, maquette validée — popup central depuis la vue Semaine
  // fusionnée dans "Saisie quotidienne") après une suppression le même soir. Toujours un
  // vrai POST — jamais un simple setState local (c'est exactement ce qui avait causé
  // l'incident de perte de données la première fois : "✓ Case mise à jour" affiché sans
  // rien envoyer au serveur). Même construction exacte que saveDailyFamily("manual_waste",
  // ...) — jamais un second calcul — avec la valeur juste tapée substituée pour la seule
  // catégorie éditée (weeklyGrid n'est pas encore à jour au moment du POST, setState étant
  // asynchrone).
  const saveGridEdit = () => {
    const { categoryId, dayIdx } = gridEditTarget;
    const pu20 = parseFloat(String(gridEditDraft.pu20).replace(",", ".")) || 0;
    const entsorgt = categoryId === "frischbrot_brot" ? 0 : (parseFloat(String(gridEditDraft.entsorgt).replace(",", ".")) || 0);
    const date = reportingWeekDates[dayIdx];
    const week = isoWeek(new Date(date + "T00:00:00"));
    const year = Number(date.slice(0, 4));
    const month = Number(date.slice(5, 7));
    const items = MANUAL_CATEGORIES.map((cat) => {
      const v = cat.id === categoryId ? { pu20, entsorgt } : manualCellValue(cat.id, dayIdx);
      return { categoryId: cat.id, label: reportCategoryLabel(cat.id), pu20Amount: v.pu20 || 0, entsorgtAmount: v.entsorgt || 0, amount: (v.pu20 || 0) + (v.entsorgt || 0) };
    });
    const brotPu20 = categoryId === "frischbrot_brot" ? pu20 : parseCommaAmount(weeklyGrid.frischbrot_brot?.[dayIdx]?.pu20);
    const brotEntsorgt = truncate2(familyDayTotal("brot", dayIdx));
    items.push({ categoryId: "frischbrot_brot", label: "Frischbrot / Brot", pu20Amount: brotPu20, entsorgtAmount: brotEntsorgt, amount: brotPu20 + brotEntsorgt });
    const prebakeEntsorgt = truncate2(familyDayTotal("prebake", dayIdx));
    items.push({ categoryId: "prebake", label: "Prebake", pu20Amount: 0, entsorgtAmount: prebakeEntsorgt, amount: prebakeEntsorgt });
    const totalEuro = truncate2(items.reduce((s, it) => s + it.amount, 0));
    apiJSON("/api/reporting/daily-entries", {
      method: "POST",
      body: JSON.stringify({ storeId: user?.store, date, week, year, month, family: "manual_waste", totalEuro, items, updatedBy: user?.firstname || "" }),
    })
      .then(() => {
        setWeeklyGrid((g) => ({ ...g, [categoryId]: { ...g[categoryId], [dayIdx]: { pu20, ...(categoryId === "frischbrot_brot" ? {} : { entsorgt }) } } }));
        setDailySaved((d) => ({ ...d, manual_waste: { ...d.manual_waste, [dayIdx]: true } }));
        setSheet(null); setGridEditTarget(null);
        showToast(t("toast.cellUpdated", "✓ Case mise à jour"));
      })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  // Sauvegarde hebdomadaire groupée réelle (fidèle à saveWeeklyManualEntries côté
  // vanilla) : un POST /daily-entries par jour de la semaine, 8 items — les 7 rayons
  // manuels + frischbrot_brot (pu20 saisi à la main, entsorgt = photo de l'auto-total
  // boulangerie au moment de l'enregistrement — jamais recalculé côté serveur).
  const saveWeek = () => {
    Promise.all(reportingWeekDates.map((date, dayIdx) => {
      const week = isoWeek(new Date(date + "T00:00:00"));
      const year = Number(date.slice(0, 4));
      const items = MANUAL_CATEGORIES.map((cat) => {
        const v = manualCellValue(cat.id, dayIdx);
        return { categoryId: cat.id, label: reportCategoryLabel(cat.id), pu20Amount: v.pu20 || 0, entsorgtAmount: v.entsorgt || 0, amount: (v.pu20 || 0) + (v.entsorgt || 0) };
      });
      const brotPu20 = parseCommaAmount(weeklyGrid.frischbrot_brot?.[dayIdx]?.pu20);
      const brotEntsorgt = truncate2(familyDayTotal("brot", dayIdx));
      items.push({ categoryId: "frischbrot_brot", label: "Frischbrot / Brot", pu20Amount: brotPu20, entsorgtAmount: brotEntsorgt, amount: brotPu20 + brotEntsorgt });
      // Bug réel trouvé (retour utilisateur 2026-08-06) : Prebake (source auto_prebake,
      // entsorgt = familyDayTotal("prebake", ...), jamais de pu20 — voir categoryDayValue)
      // n'était JAMAIS inclus dans les items envoyés à la clôture de semaine, contrairement
      // à frischbrot_brot juste au-dessus. La grille EN COURS l'affichait correctement (lu
      // en direct depuis catalogDaily), mais une fois la semaine clôturée/consultée dans
      // l'historique (pastWeekSrc, reconstruit depuis ces daily-entries persistées),
      // Prebake retombait à 0 — la ligne semblait "manquante" pour une semaine passée.
      const prebakeEntsorgt = truncate2(familyDayTotal("prebake", dayIdx));
      items.push({ categoryId: "prebake", label: "Prebake", pu20Amount: 0, entsorgtAmount: prebakeEntsorgt, amount: prebakeEntsorgt });
      const totalEuro = truncate2(items.reduce((s, it) => s + it.amount, 0));
      return apiJSON("/api/reporting/daily-entries", {
        method: "POST",
        body: JSON.stringify({ storeId: user?.store, date, week, year, month: Number(date.slice(5, 7)), family: "manual_waste", totalEuro, items, updatedBy: user?.firstname || "" }),
      });
    }))
      // Clôture réelle côté serveur (2026-09-20, retour utilisateur explicite : "la semaine
      // redevient normale comme si on ne l'avait jamais fermée" après un redémarrage complet
      // de l'app) — jusqu'ici setWeekSaved(true) n'était qu'un état local, jamais persisté.
      // Ce POST échoue en silence côté UI si le réseau lâche ici : les saisies du dessus sont
      // déjà enregistrées (le plus important), la clôture peut être retentée en retapant le
      // bouton — jamais bloquant pour l'utilisateur.
      .then(() => apiJSON("/api/reporting/week-lock", {
        method: "POST",
        body: JSON.stringify({ storeId: user?.store, year: reportingWeekYear, week: reportingWeekNumber, closedBy: user?.firstname || "" }),
      }).catch(() => {}))
      .then(() => {
        setWeekSaved(true); setWeekLockOverride(false);
        showToast(t("toast.weekSavedAbschriftenClosed", "✓ Semaine enregistrée — Abschriften KW clôturée"));
      })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  const loadWeekLock = () => {
    apiJSON(`/api/reporting/week-lock?storeId=${encodeURIComponent(user?.store)}&year=${reportingWeekYear}&week=${reportingWeekNumber}`)
      .then((lock) => setWeekSaved(!!lock))
      .catch(() => setWeekSaved(false));
  };
  // Déverrouille réellement côté serveur (même correctif que saveWeek ci-dessus) — avant,
  // "Modifier" ne posait qu'un override local temporaire : la semaine réapparaissait
  // clôturée au prochain chargement, jamais vraiment rouverte.
  const unlockWeek = () => {
    setWeekLockOverride(true);
    apiJSON(`/api/reporting/week-lock?storeId=${encodeURIComponent(user?.store)}&year=${reportingWeekYear}&week=${reportingWeekNumber}`, { method: "DELETE" })
      .then(() => setWeekSaved(false))
      .catch(() => {});
  };
  const monthWeeks = () => {
    const currentMonthNum = now.getMonth() + 1;
    const currentMonthLabel = MONTH_NAMES_FR[now.getMonth()];
    // La semaine en cours peut chevaucher 2 mois (ex. semaine du 27 juillet au 1er août) —
    // seule la portion RÉELLE de jours tombant dans le mois affiché compte ici, jamais le
    // total entier de la semaine. Suffixe a/b si les 2 mois de la semaine ont réellement des
    // jours (ordre chronologique, pas un tri numérique — couvre déc./janv.).
    const weekMonthsOrdered = [];
    reportingWeekDates.forEach((d) => { const m = Number(d.slice(5, 7)); if (!weekMonthsOrdered.includes(m)) weekMonthsOrdered.push(m); });
    const suffix = weekMonthsOrdered.length > 1 ? (weekMonthsOrdered.indexOf(currentMonthNum) === 0 ? "a" : "b") : "";
    const currentWeek = isoWeek(new Date());
    const currentByCategory = {};
    REPORT_CATEGORIES.forEach((cat) => { currentByCategory[cat.id] = categoryTotalForMonth(cat, currentMonthNum); });
    return [
      { kw: currentWeek, kwLabel: `${currentWeek}${suffix}`, total: weekTotalForMonth(currentMonthNum), current: true, krankHours, krankPersons, byCategory: currentByCategory },
      ...reportingHistory.filter((w) => w.month === currentMonthLabel),
    ];
  };

  // Export imprimable — rapport hebdomadaire (Abschriften KW) et clôture mensuelle, même
  // patron de portail que HACCP/AZL/Arbeitsplan/Infos importantes (#repPrintRoot, voir plus
  // bas) — remplace les 2 boutons "Export (simulé)" trouvés à l'audit du 2026-08-02.
  // Format réel Woche Abschriften : PÜ/20% et entsorgt sont deux lignes comptables
  // distinctes (jamais fusionnées) — corrigé sur retour utilisateur explicite 2026-08-02,
  // l'export imprimait jusqu'ici le cumul des deux par jour, perdant la répartition
  // nécessaire au vrai document. Chaque rayon porte donc 2 lignes (PÜ/20% puis Entsorgt),
  // fidèle au tableau Wochenbericht (colonnes PÜ/20% et Entsorgt distinctes) et à
  // l'écran "Autres Abschriften" (2 champs distincts par rayon).
  // opts.src : objet { week, value(catId,dayIdx), total } de même forme que pastWeekSrc —
  // permet à la fiche filiale (Pilotage réseau) d'imprimer la semaine d'UNE AUTRE filiale
  // (voir fetchWeekSrcForStore/exportPilotWeeklyReport plus bas) sans jamais toucher à
  // weeklyGrid/pastWeekSrc (état de la filiale du compte connecté). Aucun appel existant ne
  // passe opts → comportement 100% identique tant qu'aucun override n'est fourni.
  const buildWeeklyReportPrintHtml = (opts = {}) => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const storeIdForPrint = opts.storeId || user?.store;
    let weekLabel, dayValueOf, totalOf;
    if (opts.src) {
      weekLabel = opts.src.week;
      dayValueOf = (cat, i) => opts.src.value(cat.id, i);
      totalOf = opts.src.total;
    } else {
      // Semaine passée sélectionnée (sélecteur de semaine) → données de consultation réelles
      // (pastWeekSrc, jamais recalculées au prix catalogue actuel) ; sinon la semaine en cours
      // vivante, comme avant.
      const viewingPast = weeklyViewWeek !== null && pastWeekSrc;
      weekLabel = viewingPast ? weeklyViewWeek : isoWeek(now);
      dayValueOf = (cat, i) => viewingPast ? pastWeekSrc.value(cat.id, i) : categoryDayValue(cat, i);
      totalOf = viewingPast ? pastWeekSrc.total : weekTotal();
    }
    const rowsHtml = REPORT_CATEGORIES.map((cat) => {
      const dayValues = [0, 1, 2, 3, 4, 5].map((i) => dayValueOf(cat, i));
      const entsorgtCells = dayValues.map((v) => `<td>${v.entsorgt > 0 ? v.entsorgt.toFixed(2) + " €" : "—"}</td>`).join("");
      const entsorgtTotal = dayValues.reduce((s, v) => s + v.entsorgt, 0);
      // Prebake n'a jamais de ligne PÜ/20% (categoryDayValue force pu20:0 pour ce rayon —
      // seule la ligne entsorgt existe réellement pour lui, voir Abschriften KW/l'écran
      // Wochenbericht qui masque déjà cette ligne, showPu ligne ~33319) — retour utilisateur
      // explicite (2026-09-24) : le PDF l'affichait quand même, toujours à "—", une ligne qui
      // "n'existe pas de toute façon". Une seule ligne ici, jamais de rowspan=2 vide.
      if (cat.source === "auto_prebake") {
        return `<tr>
          <th>${esc(reportCategoryLabel(cat.id))}</th>
          <td class="rep-subrow">${esc(wasteTypeLabel("entsorgt"))}</td>${entsorgtCells}<td class="rep-total">${entsorgtTotal.toFixed(2)} €</td>
        </tr>`;
      }
      const pu20Cells = dayValues.map((v) => `<td>${v.pu20 > 0 ? v.pu20.toFixed(2) + " €" : "—"}</td>`).join("");
      const pu20Total = dayValues.reduce((s, v) => s + v.pu20, 0);
      return `<tr>
          <th rowspan="2">${esc(reportCategoryLabel(cat.id))}</th>
          <td class="rep-subrow">${esc(wasteTypeLabel("pu20"))}</td>${pu20Cells}<td class="rep-total">${pu20Total.toFixed(2)} €</td>
        </tr>
        <tr class="rep-subrow-row">
          <td class="rep-subrow">${esc(wasteTypeLabel("entsorgt"))}</td>${entsorgtCells}<td class="rep-total">${entsorgtTotal.toFixed(2)} €</td>
        </tr>`;
    }).join("");
    // "Total jour" — même calcul que la colonne "Total jour" de l'écran (ligne ~33341,
    // REPORT_CATEGORIES.reduce pu20+entsorgt par jour) — retour utilisateur explicite
    // (2026-09-24) : le PDF n'affichait qu'un total par rayon (Gesamt) et un total de
    // semaine, jamais ce total par jour pourtant visible dans le tableau à l'écran.
    const dayGrandTotals = [0, 1, 2, 3, 4, 5].map((i) => REPORT_CATEGORIES.reduce((s, cat) => { const v = dayValueOf(cat, i); return s + v.pu20 + v.entsorgt; }, 0));
    const dayGrandTotalCells = dayGrandTotals.map((v) => `<td class="rep-total">${v.toFixed(2)} €</td>`).join("");
    return `<div class="rep-page">
      <header class="rep-header">
        <div><p class="rep-eyebrow">NEXORA · Reporting</p><h1>${t("weeklyReport.titleTemplate", { week: weekLabel, year: now.getFullYear() }, `Wochenbericht — KW ${weekLabel}/${now.getFullYear()}`)}</h1></div>
        <span class="rep-badge">${t("home.store.label", { id: esc(storeIdForPrint) }, `Filiale ${esc(storeIdForPrint)}`)}</span>
      </header>
      <table class="rep-table rep-table-weekly">
        <thead><tr><th>${t("imBw.rayonLabel", "Abteilung")}</th><th>${t("common.typeLabel", "Typ")}</th>${[0, 1, 2, 3, 4, 5].map((i) => `<th>${esc(weekLabelShort(i))}</th>`).join("")}<th>${t("common.total", "Gesamt")}</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot><tr class="rep-tfoot-row"><th colspan="2">${t("reporting.daily.totalOfDayShort", "Total du jour")}</th>${dayGrandTotalCells}<td class="rep-total">${totalOf.toFixed(2)} €</td></tr></tfoot>
      </table>
      <p class="rep-grand-total">${t("weeklyReport.weekTotalTemplate", { amount: totalOf.toFixed(2) }, `Wochensumme: ${totalOf.toFixed(2)} €`)}</p>
      <p class="rep-footer-note">${t("common.documentGeneratedOnTemplate", { date: fmtFR(todayISO()), name: `${esc(user?.firstname || "")} ${esc(user?.lastname || "")}` }, `Document généré le ${fmtFR(todayISO())} — ${esc(user?.firstname || "")} ${esc(user?.lastname || "")}`)}</p>
    </div>`;
  };
  // Export PDF réel (exportHtmlToPdf, voir REP_DOC_CSS) — jamais window.print() : générait
  // l'en-tête/pied de page du navigateur (URL, date) sur la feuille (retour utilisateur
  // explicite, 2026-08-26, chantier "vrais PDF partout"). `title` devient directement le nom
  // du fichier proposé (déjà le rôle qu'il jouait via document.title + "Enregistrer en PDF"
  // du navigateur, voir l'historique de ce correctif du 2026-08-02 ci-dessous conservé pour
  // contexte) — un seul point de conversion pour les 13 exports qui partagent ce patron
  // (Reporting, PLU Quiz, Inventur, Bestandskontrolle, Stundenleistung).
  const printReportWithTitle = async (html, title) => {
    try {
      await exportHtmlToPdf({ bodyHtml: html, css: REP_DOC_CSS, targetSelector: ".rep-page", widthMm: 297, filename: `${title.replace(/[\\/:*?"<>|]+/g, "-")}.pdf` });
    } catch (err) {
      if (err?.name !== "AbortError") showToast(err.message || t("err.actionImpossible", "Action impossible."));
    }
  };
  const exportWeeklyReport = () => {
    const label = weeklyViewWeek !== null ? weeklyViewWeek : isoWeek(now);
    printReportWithTitle(buildWeeklyReportPrintHtml(), `Rapport hebdomadaire KW${label} ${now.getFullYear()} - Filiale ${user?.store}`);
  };
  // Fiche filiale (Pilotage réseau) — Woche Abschriften d'UNE AUTRE filiale, semaine
  // sélectionnée par le toggle pilotRepWeekView (voir pilotRepWeekSrcs plus haut).
  const exportPilotWeeklyReport = () => {
    if (!pilotFilialeId) return;
    const wk = pilotRepWeekView === "previous" ? isoWeek(now) - 1 : isoWeek(now);
    const src = pilotRepWeekSrcs[wk];
    if (!src) return;
    printReportWithTitle(
      buildWeeklyReportPrintHtml({ storeId: pilotFilialeId, src }),
      `Rapport hebdomadaire KW${wk} ${now.getFullYear()} - Filiale ${pilotFilialeId}`
    );
  };

  // Format réel "Wochen-/Monatsmeldung BL" — photo du vrai document NORMA fournie par
  // l'utilisateur 2026-08-02 ("regarde dans downloads c'est le vrai monatsmeldung"). Ce
  // N'EST PAS une déclinaison rayon par rayon comme le rapport hebdomadaire (erreur du
  // premier essai, corrigée ici) : c'est le suivi Umsatz/Stunden/Leistung/Krank déjà
  // utilisé par l'écran Stundenleistung, mais regroupé par SEMAINE (pas par jour) et
  // cumulé sur le mois — une ligne par KW, Plan vs Ist, "Woche" (juste cette semaine) et
  // "aufgelaufen" (cumulé depuis le 1er du mois).
  // opts : { stundenDays, reportingHistory, krankHours, monthLabel } — mêmes noms/formes que
  // les états qu'ils remplacent, tous optionnels. Utilisé par la fiche filiale (Pilotage
  // réseau) pour recalculer la Monatsmeldung d'UNE AUTRE filiale (pilotStundenDays/
  // pilotReportingHistory/pilotKrankHoursCurrent, voir plus bas) sans jamais toucher aux
  // états de la filiale du compte connecté. Aucun appel existant ne passe opts →
  // comportement 100% identique tant qu'aucun override n'est fourni.
  const monthlyStundenByWeek = (opts = {}) => {
    const days = stundenComputed(opts.stundenDays ? { stundenDays: opts.stundenDays } : {});
    const historySrc = opts.reportingHistory || reportingHistory;
    const krankHoursSrc = opts.krankHours != null ? opts.krankHours : krankHours;
    const currentMonthLabel = opts.monthLabel || MONTH_NAMES_FR[now.getMonth()];
    const currentWeek = isoWeek(now);
    const weekday = (dateStr) => new Date(`${dateStr}T12:00:00`).getDay(); // 0=Di...6=Sa (Fr.: Do=4)
    const byWeek = {};
    days.forEach((d) => {
      const wk = isoWeek(new Date(d.date + "T12:00:00"));
      if (!byWeek[wk]) byWeek[wk] = { week: wk, umsatzPlan: 0, umsatzIst: 0, stundenPlan: 0, stundenIst: 0, hasIst: false, firstDate: d.date, lastDate: d.date };
      const w = byWeek[wk];
      w.lastDate = d.date; // days traités dans l'ordre du mois (1..N) : lastDate = dernier jour VU pour cette semaine dans CE mois
      // Dimanche (isClosed) appartient à la même semaine ISO que le Lu-Sa qui précède —
      // son Plan Tag est déjà le sous-total de ces 6 jours (imprimé par Norma), jamais un
      // 7e jour à additionner en plus (sinon double-comptage de la semaine).
      if (!d.isClosed) {
        if (d.umsatzPlanTag != null) w.umsatzPlan += d.umsatzPlanTag;
        if (d.stundenPlanTag != null) w.stundenPlan += d.stundenPlanTag;
      }
      if (d.umsatzIstTag != null) { w.umsatzIst += d.umsatzIstTag; w.hasIst = true; }
      if (d.stundenIstTag != null) w.stundenIst += d.stundenIstTag;
    });
    const weeksList = Object.values(byWeek).sort((a, b) => a.week - b.week);
    let cumUP = 0, cumSP = 0, cumUI = 0, cumSI = 0, cumKrank = 0;
    return weeksList.map((w) => {
      cumUP += w.umsatzPlan; cumSP += w.stundenPlan;
      if (w.hasIst) { cumUI += w.umsatzIst; cumSI += w.stundenIst; }
      const histEntry = historySrc.find((h) => h.kw === w.week && h.month === currentMonthLabel);
      const krankWoche = truncate2(w.week === currentWeek ? krankHoursSrc : (histEntry?.krankHours || 0));
      cumKrank += krankWoche;
      // Semaine à cheval sur 2 mois : le mois qui finit en plein milieu de semaine = "a"
      // (il manque la fin, Ve/Sa passent dans le mois suivant) ; le mois qui commence en
      // plein milieu de semaine = "b" (il manque le début, Lu passe dans le mois précédent).
      // Une semaine complète dans CE mois va de Lundi (1) à Dimanche (0, jour fermé inclus).
      const missingStart = weekday(w.firstDate) !== 1;
      const missingEnd = weekday(w.lastDate) !== 0;
      const kwSuffix = missingStart ? "b" : missingEnd ? "a" : "";
      return {
        week: w.week,
        kwLabel: `${w.week}${kwSuffix}`,
        umsatzAufPlan: truncate2(cumUP),
        umsatzAufIst: w.hasIst ? truncate2(cumUI) : null,
        stundenWochePlan: truncate2(w.stundenPlan),
        stundenWocheIst: w.hasIst ? truncate2(w.stundenIst) : null,
        stundenAufPlan: truncate2(cumSP),
        stundenAufIst: w.hasIst ? truncate2(cumSI) : null,
        leistungWochePlan: w.stundenPlan ? truncate2(w.umsatzPlan / w.stundenPlan) : null,
        leistungWocheIst: (w.hasIst && w.stundenIst) ? truncate2(w.umsatzIst / w.stundenIst) : null,
        leistungAufPlan: cumSP ? truncate2(cumUP / cumSP) : null,
        leistungAufIst: (w.hasIst && cumSI) ? truncate2(cumUI / cumSI) : null,
        krankWoche, krankAuf: truncate2(cumKrank),
      };
    });
  };
  // opts : mêmes clés que monthlyStundenByWeek plus storeId — voir commentaire ci-dessus.
  const buildMonthClosurePrintHtml = (opts = {}) => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const storeIdForPrint = opts.storeId || user?.store;
    const monthLabel = opts.monthLabel || MONTH_NAMES_FR[now.getMonth()];
    const weeks = opts.weeks || monthlyStundenByWeek(opts);
    const fmtNum = (v) => fmtDe2(v);
    // NOTE i18n (Groupe 2) : ce tableau reproduit EXACTEMENT la mise en page du vrai
    // document papier NORMA "Wochen-/Monatsmeldung BL" (voir commentaire plus haut dans ce
    // fichier — photo du document réel fournie par l'utilisatrice) — mêmes abréviations
    // comptables allemandes compressées (KW, geplant, Ist, aufgelaufen, lfd. Wo.) que
    // l'original papier, pour rester croisable avec lui. Traduit le texte générique autour
    // (badges, pied de page, état vide) mais volontairement PAS les en-têtes du tableau
    // lui-même, par analogie avec buildHaccpPrintHtml/buildArbeitsplanPrintHtml (documents
    // physiques à mise en page fixe) — à reconfirmer explicitement avec l'utilisatrice si
    // elle veut aussi ces en-têtes traduits malgré la rupture avec le document papier réel.
    const rowsHtml = weeks.length === 0
      ? `<tr><td colspan="13" class="rep-mm-empty">${t("monthClosure.emptyText", "Aucune donnée Stundenleistung importée pour ce mois — voir l'écran \"Stundenleistung\".")}</td></tr>`
      : weeks.map((w) => `<tr>
          <td class="rep-mm-kw">${w.kwLabel}</td>
          <td>${fmtNum(w.umsatzAufPlan)}</td><td>${fmtNum(w.umsatzAufIst)}</td>
          <td>${fmtNum(w.stundenWochePlan)}</td><td>${fmtNum(w.stundenWocheIst)}</td>
          <td>${fmtNum(w.stundenAufPlan)}</td><td>${fmtNum(w.stundenAufIst)}</td>
          <td>${fmtNum(w.leistungWochePlan)}</td><td>${fmtNum(w.leistungWocheIst)}</td>
          <td>${fmtNum(w.leistungAufPlan)}</td><td>${fmtNum(w.leistungAufIst)}</td>
          <td>${fmtNum(w.krankWoche)}</td><td>${fmtNum(w.krankAuf)}</td>
        </tr>`).join("");
    return `<div class="rep-page rep-mm-page">
      <header class="rep-header">
        <div><p class="rep-eyebrow">NEXORA · Wochen-/Monatsmeldung BL</p><h1>${esc(monthLabel)} ${now.getFullYear()}</h1></div>
        <span class="rep-badge">${t("home.store.label", { id: esc(storeIdForPrint) }, `Filiale ${esc(storeIdForPrint)}`)}</span>
      </header>
      <table class="rep-table rep-mm-table">
        <thead>
          <tr>
            <th rowspan="2">KW</th>
            <th colspan="2">${t("monthClosure.header.umsatzAufgelaufen", "Umsatz — aufgelaufen")}</th>
            <th colspan="4">${t("reporting.stundenCol", "Stunden")}</th>
            <th colspan="4">${t("monthClosure.header.leistung", "Leistung (€/h)")}</th>
            <th colspan="2">${t("monthClosure.header.krank", "Krank (h)")}</th>
          </tr>
          <tr>
            <th>${t("kontrolle.plannedWord", "geplant")}</th><th>${t("reporting.istSuffix", "Ist")}</th>
            <th>${t("monthClosure.header.woche", "Woche")} ${t("kontrolle.plannedWord", "geplant")}</th><th>${t("monthClosure.header.woche", "Woche")} ${t("reporting.istSuffix", "Ist")}</th><th>${t("monthClosure.header.aufgelaufenWord", "aufgelaufen")} ${t("kontrolle.plannedWord", "geplant")}</th><th>${t("monthClosure.header.aufgelaufenWord", "aufgelaufen")} ${t("reporting.istSuffix", "Ist")}</th>
            <th>${t("monthClosure.header.woche", "Woche")} ${t("kontrolle.plannedWord", "geplant")}</th><th>${t("monthClosure.header.woche", "Woche")} ${t("reporting.istSuffix", "Ist")}</th><th>${t("monthClosure.header.aufgelaufenWord", "aufgelaufen")} ${t("kontrolle.plannedWord", "geplant")}</th><th>${t("monthClosure.header.aufgelaufenWord", "aufgelaufen")} ${t("reporting.istSuffix", "Ist")}</th>
            <th>${t("monthClosure.header.lfdWo", "lfd. Wo.")}</th><th>${t("monthClosure.header.aufgelaufenWord", "aufgelaufen")}</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <p class="rep-footer-note">${t("common.documentGeneratedOnTemplate", { date: fmtFR(todayISO()), name: `${esc(user?.firstname || "")} ${esc(user?.lastname || "")}` }, `Document généré le ${fmtFR(todayISO())} — ${esc(user?.firstname || "")} ${esc(user?.lastname || "")}`)}</p>
    </div>`;
  };
  const exportMonthClosure = () => {
    printReportWithTitle(buildMonthClosurePrintHtml(), `Monatsmeldung ${MONTH_NAMES_FR[now.getMonth()]} ${now.getFullYear()} - Filiale ${user?.store}`);
  };
  // Fiche filiale (Pilotage réseau) — Monatsmeldung d'UNE AUTRE filiale, toujours le mois
  // en cours (au stade actuel de la saisie, jamais bloqué sur un mois "pas encore fini") :
  // pilotStundenDays/pilotReportingHistory/pilotKrankHoursCurrent (voir plus haut).
  const exportPilotMonatsmeldung = () => {
    if (!pilotFilialeId) return;
    const monthLabel = MONTH_NAMES_FR[now.getMonth()];
    printReportWithTitle(
      buildMonthClosurePrintHtml({
        storeId: pilotFilialeId, monthLabel,
        stundenDays: pilotStundenDays, reportingHistory: pilotReportingHistory, krankHours: pilotKrankHoursCurrent,
      }),
      `Monatsmeldung ${monthLabel} ${now.getFullYear()} - Filiale ${pilotFilialeId}`
    );
  };

  /* Catalogue — jamais fixe : actif / suspendu / archivé, par famille */
  const catalogFiltered = () => repCatalog.filter((a) =>
    (catalogTab === "all" || a.category === catalogTab) && a.status === catalogStatusFilter
  );
  const openArticleModal = (article) => {
    setArticleModalForm(article ? { ...article } : { id: null, artNr: "", name: "", category: "Prebake", program: "", content: "", ve: "", price: "", status: "active" });
    setSheet("articleModal");
  };
  const saveArticleModal = () => {
    if (!String(articleModalForm.artNr || "").trim()) { showToast(t("toast.indicateArticleNumber", "Indique le numéro d'article.")); return; }
    if (!articleModalForm.name.trim()) { showToast(t("toast.indicateArticleName", "Indique le nom de l'article.")); return; }
    const price = parseFloat(String(articleModalForm.price).replace(",", "."));
    if (!price || price <= 0) { showToast(t("toast.indicateValidPrice", "Indique un prix valide.")); return; }
    const family = articleModalForm.category === "Brot" ? "brot" : "prebake";
    const payload = {
      storeId: user?.store, family, artNr: String(articleModalForm.artNr).trim(), name: articleModalForm.name.trim(),
      program: articleModalForm.program || "", content: articleModalForm.content || "", ve: articleModalForm.ve ?? "",
      price, status: articleModalForm.status || "active", updatedBy: user?.firstname || "",
    };
    const req = articleModalForm.id
      ? apiJSON(`/api/reporting/articles/${encodeURIComponent(articleModalForm.id)}`, { method: "PATCH", body: JSON.stringify(payload) })
      : apiJSON("/api/reporting/articles", { method: "POST", body: JSON.stringify(payload) });
    req.then(() => {
      showToast(articleModalForm.id ? t("toast.articleModified", "✓ Article modifié") : t("toast.articleAddedToCatalog", "✓ Article ajouté au catalogue"));
      loadReportingCatalog();
      setSheet(null); setArticleModalForm(null);
    }).catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  const setArticleStatus = (id, status) => {
    apiJSON(`/api/reporting/articles/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ status, updatedBy: user?.firstname || "" }) })
      .then(() => loadReportingCatalog())
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };

  /* Module Articles — logique (port de public/modules/articles.js). Le tri par
     sortOrder est un espace numérique GLOBAL au catalogue (pas par rayon) — même
     comportement que le vanilla, calculateArticleMoveOrder ci-dessous. Prebake
     réutilise repCatalog/loadReportingCatalog/setArticleStatus définis juste
     au-dessus (même collection que le catalogue Reporting, family=prebake). */
  const loadArticlesCatalog = () => {
    apiJSON("/api/articles?status=all")
      .then((list) => setArticlesCatalog(Array.isArray(list) ? list : []))
      .catch(() => setArticlesCatalog([]));
  };
  // Rafraîchit TOUT ce qui dépend du catalogue articles, après n'importe quelle mutation
  // réelle (créer/modifier/supprimer/activer-désactiver/déplacer) — retour utilisateur
  // explicite (2026-08-19) : "toutes les listes se mettent à jour", pas seulement l'édition
  // de nom/Art.Nr déjà couverte jusqu'ici. articles/newArticles/newArticlesFF sont des états
  // SÉPARÉS de articlesCatalog (Contrôle Kühlung + DLC Aktion Kühlung/FF-Nouveaux, voir
  // saveArticleMoveReal plus bas) — jusqu'ici seul un déplacement de zone les rechargeait ;
  // un ajout, une désactivation ou une suppression pouvaient laisser ces 3 écrans obsolètes
  // jusqu'au prochain sondage 15s. Jamais appelé pour Prebake (catalogue Reporting séparé).
  const refreshArticleDependentLists = () => {
    loadArticlesCatalog(); loadArticles(); loadNewArticles(); loadNewArticlesFF();
    loadArticlesMhdCounts(); loadKuhlungRecords(); loadRuleOverrides(); loadReduced();
    loadCzRecords(); loadCzExpiredRemoved(); loadKuhlungExpiredRemoved(); loadStoreRecords(); loadRedStoreRecords();
    loadTouched();
  };
  const loadArticlesMhdCounts = () => {
    apiJSON("/api/records?includeExpiredRemoved=1&includeDoneRecords=1")
      .then((list) => {
        const counts = {};
        (Array.isArray(list) ? list : []).forEach((r) => { if (r.artNr) counts[r.artNr] = (counts[r.artNr] || 0) + 1; });
        setArticlesMhdCounts(counts);
      })
      .catch(() => setArticlesMhdCounts({}));
  };

  // Signalement nouveaux articles — voir server/articleReports/. Chargé au mount (comme
  // loadArticlesCatalog juste au-dessus) pour que le badge de la tuile Articles de l'accueil
  // soit correct sans avoir à ouvrir le module d'abord, et re-poll (voir useEffect dédié plus
  // bas) pour qu'il décrémente en direct dès qu'un FL/FLA résout un signalement ailleurs.
  const loadArticleReports = () => {
    apiJSON("/api/article-reports")
      .then((d) => setArticleReports(Array.isArray(d.records) ? d.records : []))
      .catch(() => setArticleReports([]));
  };
  const uploadArticleReportPhoto = (file) => {
    if (!file) return;
    setArticleReportPhotoUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    apiJSON("/api/article-reports/upload-photo", { method: "POST", body: formData })
      .then((d) => setArticleReportForm((f) => ({ ...f, photoUrl: d.url })))
      .catch((err) => showToast(err.message || t("err.photoUploadImpossible", "Envoi de la photo impossible.")))
      .finally(() => setArticleReportPhotoUploading(false));
  };
  const submitArticleReport = () => {
    if (!articleReportForm.photoUrl) return showToast(t("articles.reports.form.missingPhoto", "Photo obligatoire."));
    if (!articleReportForm.comment.trim()) return showToast(t("articles.reports.form.missingComment", "Commentaire obligatoire."));
    setArticleReportSubmitting(true);
    apiJSON("/api/article-reports", { method: "POST", body: JSON.stringify(articleReportForm) })
      .then(() => {
        showToast(t("articles.reports.form.sentToast", "✓ Signalement envoyé"));
        setArticleReportForm({ photoUrl: "", comment: "", rayon: "" });
        loadArticleReports();
        setArtScreen("reports");
      })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")))
      .finally(() => setArticleReportSubmitting(false));
  };
  const resolveArticleReport = (id) => {
    apiJSON(`/api/article-reports/${id}`, { method: "DELETE" })
      .then(() => { showToast(t("articles.reports.resolvedToast", "✓ Marqué inséré")); setArticleReports((list) => list.filter((r) => r.id !== id)); })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  const isValidArtPrice = (v) => Number.isFinite(Number(v)) && Number(v) > 0;
  const artZoneLabel = (zone) => ARTICLE_ZONES.find((z) => z.id === zone)?.label || zone || t("articles.noZone", "Aucune zone");
  const prebakeAsArticles = () => repCatalog.filter((a) => a.category === "Prebake").map((a) => ({
    id: a.id, artNr: a.artNr, name: a.name, price: a.price, regularPrice: null,
    sortOrder: a.displayOrder, zone: "Prebake", unit: a.ve, note: "",
    active: a.status === "active", isPrebake: true,
  }));
  const getArticlesForZone = (zone) => {
    const catalog = articlesCatalog.map((a) => ({ ...a, active: a.status !== "inactive" && a.disabled !== true && a.active !== false }));
    if (!zone) return [...catalog, ...prebakeAsArticles()];
    if (zone === "Prebake") return prebakeAsArticles();
    return catalog.filter((a) => a.zone === zone);
  };
  const getArticleStatsFor = (list) => ({
    total: list.length,
    active: list.filter((a) => a.active).length,
    priced: list.filter((a) => isValidArtPrice(a.price)).length,
    missingPrice: list.filter((a) => !isValidArtPrice(a.price)).length,
  });
  const sortArtByOrder = (a, b) => {
    const oa = Number(a.sortOrder ?? 999999), ob = Number(b.sortOrder ?? 999999);
    if (oa !== ob) return oa - ob;
    return String(a.name).localeCompare(String(b.name), "fr");
  };
  const getVisibleArticlesList = () => {
    const q = artQuery.trim().toLowerCase();
    return getArticlesForZone(artZone)
      .filter((a) => {
        if (artFilter === "active" && !a.active) return false;
        if (artFilter === "inactive" && a.active) return false;
        if (artFilter === "missing-price" && isValidArtPrice(a.price)) return false;
        return true;
      })
      .filter((a) => !q || [a.artNr, a.name, a.zone, a.unit, a.note, a.sortOrder, a.price, a.regularPrice].join(" ").toLowerCase().includes(q))
      // Retour utilisateur explicite (2026-08-12) : ordre EXACT du Kühlung (sortOrder), plus
      // de regroupement "actifs d'abord" qui casserait cet ordre dès qu'un article est
      // inactif — le filtre artFilter au-dessus gère déjà la visibilité actif/inactif.
      .sort(sortArtByOrder);
  };

  const openCreateArticleSheet = () => {
    setArtForm({
      mode: "create", id: null, artNr: "", name: "", sortOrder: "", price: "", regularPrice: "",
      unit: "", zone: artZone === "Prebake" ? "Prebake" : (artZone || "Kühlung"), status: "active", note: "",
      isPrebake: artZone === "Prebake",
      uniteParDefaut: "Karton", unitesAutorisees: ["Karton"],
    });
    setSheet("articleEditor");
  };
  const openEditArticleSheet = (article) => {
    setArtForm({
      mode: "edit", id: article.id, artNr: article.artNr, name: article.name,
      sortOrder: article.sortOrder ?? "", price: article.price ? String(article.price) : "",
      regularPrice: article.regularPrice ? String(article.regularPrice) : "",
      unit: article.unit || "", zone: article.zone || "Kühlung", status: article.active ? "active" : "inactive",
      note: article.note || "", isPrebake: !!article.isPrebake,
      uniteParDefaut: article.uniteParDefaut || "Karton", unitesAutorisees: Array.isArray(article.unitesAutorisees) && article.unitesAutorisees.length ? article.unitesAutorisees : ["Karton"],
    });
    setSheet("articleEditor");
  };
  // "Insérer au rayon" — retour utilisateur explicite (2026-08-12, 2e passe) : un article
  // promo Kühlung (zone "Kühlung-Nouveaux") qui s'avère faire partie du vrai assortiment
  // doit pouvoir être inséré directement à la position souhaitée, pas juste basculer de
  // zone avec un numéro de position arbitraire — réutilise donc le sheet "Déplacer"
  // existant (avant/après un article repère cherché par nom, zone forcée sur "Kühlung")
  // plutôt que le formulaire d'édition simple. Le changement de zone+position suffit à
  // tout reclasser automatiquement (voir le refresh ciblé dans saveArticleMoveReal) :
  // disparaît de DLC Aktion, ses MHD (déjà source:"KUEHLUNG", jamais touchées ici)
  // apparaissent dans le Contrôle normal, Réductions bascule de "Kühlung (Aktion)" à
  // "Kühlung (Sortiment)" — aucune de ces 3 conséquences n'est un cas spécial à coder,
  // elles découlent toutes du même champ zone déjà utilisé partout ailleurs.
  const openInsertPromoArtSheet = (article) => {
    if (!canEditArticles) { showToast(t("toast.notAllowedMoveArticle", "Tu n'as pas le droit de déplacer un article.")); return; }
    setArtMoveState({ article, mode: "after", query: "", referenceArtNr: null, zone: "Kühlung" });
    setSheet("articleMove");
  };
  const isDuplicateArtArtNr = (artNr, excludeArtNr, isPrebake) => {
    const pool = isPrebake ? prebakeAsArticles() : articlesCatalog;
    return pool.some((a) => String(a.artNr) === String(artNr) && String(a.artNr) !== String(excludeArtNr || ""));
  };
  const saveArticleForm = () => {
    if (!artForm) return;
    if ((artForm.mode === "create" && !canCreateArticles) || (artForm.mode === "edit" && !canEditArticles)) {
      showToast(t("toast.notAllowedToDoThis", "Tu n'as pas le droit de faire ça.")); return;
    }
    const artNr = artForm.artNr.trim();
    const name = artForm.name.trim();
    if (!artNr || !name) { showToast(t("articles.artNrNameRequired", "Art.Nr et nom de l'article sont obligatoires.")); return; }
    if (isDuplicateArtArtNr(artNr, artForm.mode === "edit" ? artForm.artNr : null, artForm.isPrebake)) {
      showToast(t("toast.artNrAlreadyExists", "Cet Art.Nr existe déjà.")); return;
    }
    const price = parseFloat(String(artForm.price).replace(",", "."));

    if (artForm.isPrebake) {
      const payload = {
        artNr, name, price: Number.isFinite(price) ? price : 0, ve: artForm.unit,
        status: artForm.status, storeId: user?.store, family: "prebake", updatedBy: user?.firstname || "",
      };
      const req = artForm.mode === "create"
        ? apiJSON("/api/reporting/articles", { method: "POST", body: JSON.stringify(payload) })
        : apiJSON(`/api/reporting/articles/${encodeURIComponent(artForm.id)}`, { method: "PATCH", body: JSON.stringify(payload) });
      req.then(() => {
        showToast(t("toast.articleSaved", "✓ Article enregistré")); loadReportingCatalog(); setSheet(null); setArtForm(null);
      }).catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
      return;
    }

    const regularPrice = parseFloat(String(artForm.regularPrice).replace(",", "."));
    const payload = {
      artNr, name, sortOrder: artForm.sortOrder === "" ? undefined : Number(artForm.sortOrder),
      price: Number.isFinite(price) ? price : undefined,
      regularPrice: Number.isFinite(regularPrice) ? regularPrice : undefined,
      unit: artForm.unit, zone: artForm.zone || "Kühlung",
      status: artForm.status, disabled: artForm.status === "inactive", active: artForm.status !== "inactive",
      note: artForm.note, updatedAt: new Date().toISOString(),
      ...(artForm.mode === "create" ? { createdBy: user?.firstname || "" } : {}),
      // Commandes de marchandises — seulement si le formulaire a réellement affiché ces 2
      // champs (canManageMerchCatalog) ; sinon on ne les envoie jamais, pour ne rien écraser
      // par erreur avec des valeurs vides pour quelqu'un qui ne les voit pas.
      ...(canManageMerchCatalog ? { uniteParDefaut: artForm.uniteParDefaut || "Karton", unitesAutorisees: artForm.unitesAutorisees || ["Karton"] } : {}),
    };
    const req = artForm.mode === "create"
      ? apiJSON("/api/articles", { method: "POST", body: JSON.stringify(payload) })
      : apiJSON(`/api/articles/${encodeURIComponent(artForm.id || artForm.artNr)}`, { method: "PATCH", body: JSON.stringify(payload) });
    req.then(() => {
      showToast(t("toast.articleSaved", "✓ Article enregistré")); setSheet(null); setArtForm(null);
      // Un changement d'Art.Nr est désormais répercuté côté serveur vers records/
      // reduction_j2_articles/done_records (voir syncRecordsForArticleChange, server.js,
      // 2026-08-19) — refreshArticleDependentLists() recharge tout ce qui en dépend, création
      // comme édition (une création peut immédiatement apparaître dans Contrôle/DLC Aktion
      // selon sa zone — voir son commentaire pour le détail des écrans concernés).
      refreshArticleDependentLists();
    }).catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  const duplicateArticleForm = () => {
    if (!artForm || artForm.mode !== "edit") return;
    if (!canCreateArticles) { showToast(t("articles.noRightToDuplicate", "Tu n'as pas le droit de dupliquer un article.")); return; }
    setArtForm({ ...artForm, mode: "create", id: null, artNr: "" });
  };
  const toggleArticleStatusReal = (article) => {
    if (!canDeleteArticles) { showToast(t("toast.notAllowedDeactivateArticle", "Tu n'as pas le droit de désactiver un article.")); return; }
    const nextStatus = article.active ? "inactive" : "active";
    if (!confirm(article.active ? `Désactiver ${article.name} ? Il ne sera plus considéré comme actif.` : `Réactiver ${article.name} ?`)) return;
    if (article.isPrebake) {
      apiJSON(`/api/reporting/articles/${encodeURIComponent(article.id)}`, { method: "PATCH", body: JSON.stringify({ status: nextStatus, updatedBy: user?.firstname || "" }) })
        .then(() => loadReportingCatalog())
        .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
      return;
    }
    apiJSON(`/api/articles/${encodeURIComponent(article.id || article.artNr)}`, {
      method: "PATCH", body: JSON.stringify({ status: nextStatus, disabled: nextStatus === "inactive", active: nextStatus === "active", updatedAt: new Date().toISOString() }),
    }).then(() => refreshArticleDependentLists())
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  // Tap simple → ouvre la recherche "aller à un article" (runJumpValue) ; double-tap sur le
  // MÊME champ dans la fenêtre de 450ms → ouvre l'édition de CE champ (runHeroEdit). Ref
  // {key,timer} unique, même mécanique que shiftTapRef/totalTapRef (Planning) — jamais un
  // vrai dblclick, peu fiable sur mobile.
  const handleRunFieldTap = (field) => {
    const tapKey = `${article.artNr}|${field}`;
    if (runFieldTapRef.current.key === tapKey) {
      clearTimeout(runFieldTapRef.current.timer);
      runFieldTapRef.current = { key: "", timer: null };
      setRunHeroEdit({ field, value: field === "artNr" ? article.artNr : article.name });
      return;
    }
    clearTimeout(runFieldTapRef.current.timer);
    const timer = setTimeout(() => { runFieldTapRef.current = { key: "", timer: null }; setRunJumpValue(""); }, 450);
    runFieldTapRef.current = { key: tapKey, timer };
  };
  // Recherche par Art.Nr exact d'abord, sinon sous-chaîne du nom — reste dans la liste déjà
  // chargée pour ce rayon (articles, zone Kühlung), jamais un nouvel appel serveur.
  const commitRunJump = () => {
    const q = String(runJumpValue || "").trim();
    setRunJumpValue(null);
    if (!q) return;
    let idx = articles.findIndex((a) => String(a.artNr) === q);
    if (idx === -1) idx = articles.findIndex((a) => String(a.name || "").toLowerCase().includes(q.toLowerCase()));
    if (idx === -1) { showToast(t("controlRun.quickJump.notFoundTemplate", { q }, `Aucun article trouvé pour "${q}".`)); return; }
    setRunIndex(idx);
  };
  // Réutilise PATCH /api/articles/:id — donc syncRecordsForArticleChange côté serveur
  // (records/reduction_j2_articles/done_records mis à jour en cascade, voir 2026-08-19) —
  // jamais une écriture séparée qui contournerait ce garde-fou. Confirmation explicite
  // systématique ici (retour utilisateur : "message box de confirmation" pour nom ET
  // numéro) — VE seul (commitRunVeEdit ci-dessous) reste un aller-retour direct sans popup.
  // Verrou synchrone (ref, pas un state) — confirm() vole le focus de l'input dès qu'il
  // s'ouvre, ce qui met un blur en file d'attente ; ce blur ne s'exécute qu'APRÈS le retour
  // de confirm() (JS bloqué pendant la boîte native), donc sans ce verrou onKeyDown(Enter)
  // ET onBlur appelleraient tous les deux commitRunHeroEdit avec le même state pas encore
  // vidé → double confirm() coup sur coup. Un state ne suffirait pas (mise à jour asynchrone,
  // pas encore visible au moment du second appel synchrone).
  const runHeroCommitLockRef = useRef(false);
  const commitRunHeroEdit = () => {
    if (runHeroCommitLockRef.current) return;
    if (!runHeroEdit) return;
    const { field, value } = runHeroEdit;
    const cleanValue = String(value || "").trim();
    if (!cleanValue) { showToast(t("articles.mandatoryNameArtNr", "Nom et Art.Nr sont obligatoires.")); setRunHeroEdit(null); return; }
    const originalArtNr = article.artNr;
    const originalValue = field === "artNr" ? article.artNr : article.name;
    if (cleanValue === String(originalValue)) { setRunHeroEdit(null); return; }
    runHeroCommitLockRef.current = true;
    const confirmMsg = field === "artNr"
      ? t("controlRun.quickEdit.artNrChangeConfirmTemplate", { old: originalArtNr, new: cleanValue }, `Renuméroter l'article ${originalArtNr} → ${cleanValue} ? Ses MHD déjà enregistrées et ses règles de réduction suivront automatiquement.`)
      : t("controlRun.quickEdit.nameChangeConfirmTemplate", { old: originalValue, new: cleanValue }, `Renommer "${originalValue}" en "${cleanValue}" ?`);
    if (!confirm(confirmMsg)) { runHeroCommitLockRef.current = false; setRunHeroEdit(null); return; }
    const patch = field === "artNr" ? { artNr: cleanValue } : { name: cleanValue };
    setRunHeroBusy(true);
    apiJSON(`/api/articles/${encodeURIComponent(article.id || originalArtNr)}`, {
      method: "PATCH", body: JSON.stringify({ ...patch, updatedAt: new Date().toISOString() }),
    }).then(() => {
      showToast(t("toast.articleSaved", "✓ Article enregistré"));
      // Reflet local immédiat (même index, jamais dépendant d'un nouveau tri serveur qui
      // pourrait déplacer la position pendant le contrôle en cours) — refreshArticleDependentLists()
      // resynchronise tout le reste en tâche de fond (records/réductions/compteurs MHD).
      setArticles((prev) => prev.map((a) => (String(a.artNr) === String(originalArtNr) ? { ...a, ...patch } : a)));
      refreshArticleDependentLists();
      setRunHeroEdit(null);
    }).catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")))
      .finally(() => { setRunHeroBusy(false); runHeroCommitLockRef.current = false; });
  };
  // VE — simple tap→édition, jamais de double-tap (champ non identifiant, pas de cascade à
  // risquer) ni de confirmation, cohérent avec le principe "correctif mineur = direct".
  const commitRunVeEdit = () => {
    if (runVeEdit === null) return;
    const cleanVe = String(runVeEdit).replace(/\D/g, "");
    setRunVeEdit(null);
    if (cleanVe === String(article.ve ?? "")) return;
    const originalArtNr = article.artNr;
    apiJSON(`/api/articles/${encodeURIComponent(article.id || originalArtNr)}`, {
      method: "PATCH", body: JSON.stringify({ ve: cleanVe === "" ? "" : Number(cleanVe), updatedAt: new Date().toISOString() }),
    }).then(() => {
      setArticles((prev) => prev.map((a) => (String(a.artNr) === String(originalArtNr) ? { ...a, ve: cleanVe === "" ? "" : Number(cleanVe) } : a)));
      refreshArticleDependentLists();
    }).catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  // Suppression DÉFINITIVE — distincte de "Désactiver" ci-dessus, qui garde la fiche
  // (visible en filtre "Inactifs"). Cas d'usage réel qui l'a motivée (retour utilisateur
  // explicite, 2026-08-19) : fiche article en double créée par une importation, qu'il faut
  // pouvoir retirer pour libérer son Art.Nr et corriger l'autre fiche. Jamais pour Prebake
  // (catalogue Reporting séparé, hors périmètre de cette action).
  const deleteArticleReal = (article) => {
    if (!canDeleteArticles) { showToast(t("toast.notAllowedToDoThis", "Tu n'as pas le droit de faire ça.")); return; }
    if (!confirm(t("articles.confirmDeleteTemplate", { name: article.name, artNr: article.artNr }, `Supprimer définitivement "${article.name}" (Art.Nr ${article.artNr}) ? Cette action est irréversible.`))) return;
    apiJSON(`/api/articles/${encodeURIComponent(article.id || article.artNr)}`, { method: "DELETE" })
      .then(() => { showToast(t("toast.articleDeleted", "🗑️ Article supprimé")); refreshArticleDependentLists(); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };

  const artMoveCandidateSource = (movingArticle) => movingArticle?.isPrebake
    ? prebakeAsArticles()
    : articlesCatalog.map((a) => ({ ...a, active: a.status !== "inactive" && a.disabled !== true && a.active !== false }));
  const openMoveArticleSheet = (article) => {
    if (!canEditArticles) { showToast(t("toast.notAllowedMoveArticle", "Tu n'as pas le droit de déplacer un article.")); return; }
    setArtMoveState({ article, mode: "after", query: "", referenceArtNr: null, zone: article.zone || "Kühlung" });
    setSheet("articleMove");
  };
  const artMoveCandidates = () => {
    if (!artMoveState) return [];
    const q = artMoveState.query.trim().toLowerCase();
    return artMoveCandidateSource(artMoveState.article)
      .filter((a) => String(a.artNr) !== String(artMoveState.article.artNr))
      .filter((a) => !q || [a.artNr, a.name, a.zone, a.sortOrder].join(" ").toLowerCase().includes(q))
      .sort(sortArtByOrder)
      .slice(0, q ? 20 : 10);
  };
  const calculateArticleMoveOrder = () => {
    if (!artMoveState) return { ok: false };
    const ordered = artMoveCandidateSource(artMoveState.article)
      .filter((a) => String(a.artNr) !== String(artMoveState.article.artNr))
      .sort(sortArtByOrder);
    if (!ordered.length) return { ok: true, sortOrder: 10 };
    let targetIndex = ordered.length;
    if (artMoveState.mode === "start") targetIndex = 0;
    else if (artMoveState.mode === "end") targetIndex = ordered.length;
    else {
      const refIndex = ordered.findIndex((a) => String(a.artNr) === String(artMoveState.referenceArtNr));
      if (refIndex === -1) return { ok: false, message: "Article repère introuvable." };
      targetIndex = artMoveState.mode === "before" ? refIndex : refIndex + 1;
    }
    const previous = ordered[targetIndex - 1] || null;
    const next = ordered[targetIndex] || null;
    const prevOrder = previous ? (Number.isFinite(Number(previous.sortOrder)) ? Number(previous.sortOrder) : 999999) : null;
    const nextOrder = next ? (Number.isFinite(Number(next.sortOrder)) ? Number(next.sortOrder) : 999999) : null;
    let newOrder;
    if (prevOrder == null && nextOrder != null) newOrder = nextOrder - 10;
    else if (prevOrder != null && nextOrder == null) newOrder = prevOrder + 10;
    else if (prevOrder != null && nextOrder != null) newOrder = (prevOrder + nextOrder) / 2;
    else newOrder = 10;
    if (!Number.isFinite(newOrder)) return { ok: false, message: "Position invalide." };
    return { ok: true, sortOrder: Math.round(newOrder * 1000) / 1000 };
  };
  const saveArticleMoveReal = () => {
    if (!artMoveState) return;
    if ((artMoveState.mode === "before" || artMoveState.mode === "after") && !artMoveState.referenceArtNr) {
      showToast(t("toast.chooseReferenceArticleFirst", "Choisis d'abord un article repère.")); return;
    }
    const destination = calculateArticleMoveOrder();
    if (!destination.ok) { showToast(destination.message || t("err.cantComputeNewPosition", "Impossible de calculer la nouvelle position.")); return; }
    const { article } = artMoveState;
    if (article.isPrebake) {
      apiJSON(`/api/reporting/articles/${encodeURIComponent(article.id)}`, { method: "PATCH", body: JSON.stringify({ displayOrder: destination.sortOrder, updatedBy: user?.firstname || "" }) })
        .then(() => { showToast(t("toast.articleMoved", "✓ Article déplacé")); loadReportingCatalog(); setSheet(null); setArtMoveState(null); })
        .catch((err) => showToast(err.message || t("err.movingImpossible", "Déplacement impossible.")));
      return;
    }
    apiJSON(`/api/articles/${encodeURIComponent(article.id || article.artNr)}`, {
      method: "PATCH", body: JSON.stringify({ sortOrder: destination.sortOrder, zone: artMoveState.zone || article.zone, updatedAt: new Date().toISOString() }),
    }).then(() => {
      showToast(t("toast.articleMoved", "✓ Article déplacé")); setSheet(null); setArtMoveState(null);
      // Retour utilisateur explicite (2026-08-12) : un déplacement qui change aussi la zone
      // (ex. article promo Kühlung-Nouveaux -> Kühlung) doit se propager immédiatement à
      // TOUT ce qui dépend de la zone catalogue — Contrôle/Scanner (`articles`, filtré
      // zone==="Kühlung") et l'écran DLC Aktion (`newArticles`/`newArticlesFF`, filtré
      // zone==="*-Nouveaux"). refreshArticleDependentLists() couvre désormais ces 3 écrans
      // dans TOUS les cas (2026-08-19) — même un simple réordonnancement dans la même zone
      // change l'ordre affiché dans ces listes, pas seulement un changement de zone.
      refreshArticleDependentLists();
    })
      .catch((err) => showToast(err.message || t("err.movingImpossible", "Déplacement impossible.")));
  };

  /* ---------------------------------------------------------
     Inventur — Inventurerfassung
  --------------------------------------------------------- */
  const loadInventurCategoryTemplates = () => {
    apiJSON(`/api/inventur/categories?storeId=${encodeURIComponent(user?.store)}`)
      .then((d) => setInventurCategoryTemplates(d.categories || []))
      .catch(() => setInventurCategoryTemplates([]));
  };
  const loadInventurInventories = (mode) => {
    const m = mode || inventurListMode;
    setInventurListMode(m);
    apiJSON(`/api/inventur/inventories?mode=${m}`)
      .then((d) => setInventurInventories(d.inventories || []))
      .catch(() => setInventurInventories([]));
  };
  const openInventurInventory = (id) => {
    return apiJSON(`/api/inventur/inventories/${encodeURIComponent(id)}`)
      .then((d) => { setInventurActiveInventory(d.inventory); setInventurScreen("erfassung_detail"); return d.inventory; })
      .catch((err) => { showToast(err.message || t("inventur.notFoundToast", "Inventaire introuvable.")); return null; });
  };
  const refreshActiveInventory = () => {
    if (!inventurActiveInventory) return;
    apiJSON(`/api/inventur/inventories/${encodeURIComponent(inventurActiveInventory.id)}`)
      .then((d) => setInventurActiveInventory(d.inventory)).catch(() => {});
  };
  const createInventurInventory = () => {
    if (!inventurCreateForm.date) { showToast(t("inventur.toast.needDate", "Indique une date.")); return; }
    apiJSON("/api/inventur/inventories", {
      method: "POST",
      body: JSON.stringify({ ...inventurCreateForm, storeId: user?.store }),
    }).then((d) => {
      showToast(`✓ ${t("inventur.toast.created", "Inventaire créé")}`);
      setInventurCreateForm(emptyInventurCreateForm);
      setInventurActiveInventory(d.inventory);
      setInventurScreen("erfassung_detail");
      loadInventurInventories();
    }).catch((err) => showToast(err.message || t("deliveryNotes.creationImpossible", "Création impossible.")));
  };
  const patchInventurInventoryStatus = (status, force) => {
    if (!inventurActiveInventory) return;
    apiJSON(`/api/inventur/inventories/${encodeURIComponent(inventurActiveInventory.id)}/status`, {
      method: "PATCH", body: JSON.stringify({ status, force }),
    }).then((d) => { setInventurActiveInventory(d.inventory); showToast(`✓ ${t("inventur.toast.statusUpdated", "Statut mis à jour")}`); loadInventurInventories(); })
      .catch((err) => {
        // Clôture bloquée par des catégories non confirmées (409, voir server.js) — offre
        // au responsable (qui a déjà le droit inventur.close, revérifié serveur) de forcer
        // la clôture plutôt qu'un blocage sans recours, retour utilisateur explicite 2026-08-03.
        if (status === "abgeschlossen" && !force && err.status === 409) {
          if (confirm(t("inventur.confirmForceCloseTemplate", { detail: err.message }, `${err.message}\n\nClôturer quand même ? Les catégories non confirmées seront verrouillées automatiquement.`))) {
            patchInventurInventoryStatus(status, true);
            return;
          }
        }
        showToast(err.message || t("deliveryNotes.actionImpossible", "Action impossible."));
      });
  };
  const reopenInventurInventory = () => {
    if (!inventurActiveInventory || !inventurReopenReason.trim()) { showToast(t("inventur.toast.needReason", "Indique un motif.")); return; }
    apiJSON(`/api/inventur/inventories/${encodeURIComponent(inventurActiveInventory.id)}/reopen`, {
      method: "POST", body: JSON.stringify({ reason: inventurReopenReason.trim() }),
    }).then((d) => { setInventurActiveInventory(d.inventory); setSheet(null); setInventurReopenTarget(null); setInventurReopenReason(""); showToast(`✓ ${t("inventur.toast.reopened", "Inventaire rouvert")}`); loadInventurInventories(); })
      .catch((err) => showToast(err.message || t("inventur.toast.reopenError", "Réouverture impossible.")));
  };
  const addInventurCategoryToInventory = (payload) => {
    if (!inventurActiveInventory) return;
    apiJSON(`/api/inventur/inventories/${encodeURIComponent(inventurActiveInventory.id)}/categories`, {
      method: "POST", body: JSON.stringify(payload),
    }).then((d) => { setInventurActiveInventory(d.inventory); setInventurNewCategoryForm(null); setSheet(null); showToast(`✓ ${t("inventur.toast.categoryAdded", "Catégorie ajoutée")}`); })
      .catch((err) => showToast(err.message || t("inventur.toast.addError", "Ajout impossible.")));
  };
  const removeInventurCategoryFromInventory = (catId) => {
    if (!inventurActiveInventory) return;
    if (!confirm(t("inventur.confirmRemoveCategory", "Retirer cette catégorie de l'inventaire ?"))) return;
    apiJSON(`/api/inventur/inventories/${encodeURIComponent(inventurActiveInventory.id)}/categories/${encodeURIComponent(catId)}`, { method: "DELETE" })
      .then(() => { refreshActiveInventory(); showToast(t("inventur.toast.categoryRemoved", "Catégorie retirée.")); })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  const saveInventurAssignment = () => {
    if (!inventurActiveInventory || !inventurAssignForm) return;
    apiJSON(`/api/inventur/inventories/${encodeURIComponent(inventurActiveInventory.id)}/categories/${encodeURIComponent(inventurAssignForm.catId)}/assign`, {
      method: "PATCH", body: JSON.stringify({ assignedUserIds: inventurAssignForm.assignedUserIds, assignedUserNames: inventurAssignForm.assignedUserNames }),
    }).then((d) => { setInventurActiveInventory(d.inventory); setInventurAssignForm(null); setSheet(null); showToast(`✓ ${t("inventur.toast.assignmentSaved", "Affectation enregistrée")}`); })
      .catch((err) => showToast(err.message || t("inventur.toast.assignError", "Affectation impossible.")));
  };
  const setInventurCategoryStatus = (catId, status, reason) => {
    if (!inventurActiveInventory) return;
    apiJSON(`/api/inventur/inventories/${encodeURIComponent(inventurActiveInventory.id)}/categories/${encodeURIComponent(catId)}/status`, {
      method: "PATCH", body: JSON.stringify({ status, reason }),
    }).then((d) => { setInventurActiveInventory(d.inventory); showToast(`✓ ${t("inventur.toast.statusUpdated", "Statut mis à jour")}`); })
      .catch((err) => showToast(err.message || t("deliveryNotes.actionImpossible", "Action impossible.")));
  };
  const loadInventurCategoryEntries = (catId, invId) => {
    const id = invId || inventurActiveInventory?.id;
    if (!id) return;
    apiJSON(`/api/inventur/inventories/${encodeURIComponent(id)}/categories/${encodeURIComponent(catId)}/entries`)
      .then((d) => setInventurCategoryEntries(d.entries || []))
      .catch(() => setInventurCategoryEntries([]));
  };
  const openInventurCategory = (catId, invId) => {
    setInventurActiveCategoryId(catId);
    setInventurEntrySearch("");
    setInventurEntryForm(emptyInventurEntryForm);
    loadInventurCategoryEntries(catId, invId);
    setInventurScreen("erfassung_category");
    setTimeout(() => inventurArtNrInputRef.current?.focus(), 100);
  };
  const activeInventurCategory = () => inventurActiveInventory?.categories.find((c) => c.id === inventurActiveCategoryId) || null;

  const submitInventurEntry = (forceSeparate) => {
    const cat = activeInventurCategory();
    if (!cat || !inventurActiveInventory) return;
    const artNr = inventurEntryForm.artNr.trim();
    if (!artNr) { showToast(t("inventur.toast.needArtNr", "Indique un Art.Nr.")); return; }
    const isBooks = cat.entryType === "buecher";
    if (isBooks && (!inventurEntryForm.price || Number(inventurEntryForm.totalQty) <= 0)) { showToast(t("inventur.toast.needPriceQty", "Indique un prix et une quantité.")); return; }
    if (!isBooks && inventurEntryForm.entryMethod === "direct" && !inventurEntryForm.totalQty) { showToast(t("inventur.toast.needTotalQty", "Indique la quantité totale.")); return; }

    const payload = {
      artNr, name: inventurEntryForm.name, note: inventurEntryForm.note,
      forceSeparate: Boolean(forceSeparate),
    };
    if (isBooks) {
      payload.price = parseFloat(String(inventurEntryForm.price).replace(",", "."));
      payload.totalQty = Number(inventurEntryForm.totalQty || 0);
    } else if (inventurEntryForm.entryMethod === "cartons") {
      payload.entryMethod = "cartons";
      payload.cartons = Number(inventurEntryForm.cartons || 0);
      payload.piecesPerCarton = Number(inventurEntryForm.piecesPerCarton || 0);
      payload.singlePieces = Number(inventurEntryForm.singlePieces || 0);
    } else {
      payload.totalQty = Number(inventurEntryForm.totalQty || 0);
    }

    apiJSON(`/api/inventur/inventories/${encodeURIComponent(inventurActiveInventory.id)}/categories/${encodeURIComponent(cat.id)}/entries`, {
      method: "POST", body: JSON.stringify(payload),
    }).then((d) => {
      setInventurActiveInventory(d.inventory);
      setInventurCategoryEntries((list) => [...list, d.entry]);
      setInventurEntryForm(emptyInventurEntryForm);
      showToast(`✓ ${t("inventur.toast.lineSaved", "Ligne enregistrée")}`);
      inventurArtNrInputRef.current?.focus();
    }).catch((err) => {
      if (err.data?.duplicate) { setInventurDuplicateState({ existingEntry: err.data.existingEntry, payload }); return; }
      showToast(err.message || t("settings.krankRetention.saveError", "Enregistrement impossible."));
    });
  };
  const resolveInventurDuplicate = (choice) => {
    if (!inventurDuplicateState) return;
    const { existingEntry, payload } = inventurDuplicateState;
    if (choice === "cancel") { setInventurDuplicateState(null); return; }
    if (choice === "add") {
      apiJSON(`/api/inventur/entries/${encodeURIComponent(existingEntry.id)}`, { method: "PATCH", body: JSON.stringify({ addQty: Number(payload.totalQty || 0) }) })
        .then(() => { setInventurDuplicateState(null); setInventurEntryForm(emptyInventurEntryForm); loadInventurCategoryEntries(inventurActiveCategoryId); refreshActiveInventory(); showToast(`✓ ${t("inventur.toast.qtyAdded", "Quantité ajoutée")}`); inventurArtNrInputRef.current?.focus(); })
        .catch((err) => showToast(err.message || t("deliveryNotes.actionImpossible", "Action impossible.")));
      return;
    }
    if (choice === "edit") {
      setInventurEditEntry(existingEntry);
      setInventurDuplicateState(null);
      setSheet("inventurEditEntry");
      return;
    }
    if (choice === "separate") {
      setInventurDuplicateState(null);
      submitInventurEntry(true);
    }
  };
  const saveInventurEntryEdit = () => {
    if (!inventurEditEntry) return;
    apiJSON(`/api/inventur/entries/${encodeURIComponent(inventurEditEntry.id)}`, {
      method: "PATCH",
      body: JSON.stringify({
        name: inventurEditEntry.name, note: inventurEditEntry.note, price: inventurEditEntry.price,
        entryMethod: inventurEditEntry.entryMethod, cartons: inventurEditEntry.cartons,
        piecesPerCarton: inventurEditEntry.piecesPerCarton, singlePieces: inventurEditEntry.singlePieces,
        totalQty: inventurEditEntry.totalQty,
      }),
    }).then(() => { setSheet(null); setInventurEditEntry(null); loadInventurCategoryEntries(inventurActiveCategoryId); refreshActiveInventory(); showToast(`✓ ${t("inventur.toast.lineEdited", "Ligne modifiée")}`); })
      .catch((err) => showToast(err.message || t("inventur.toast.editError", "Modification impossible.")));
  };
  const deleteInventurEntry = (entryId) => {
    if (!confirm(t("inventur.confirmDeleteLine", "Supprimer cette ligne ?"))) return;
    apiJSON(`/api/inventur/entries/${encodeURIComponent(entryId)}`, { method: "DELETE" })
      .then(() => { loadInventurCategoryEntries(inventurActiveCategoryId); refreshActiveInventory(); showToast(t("inventur.toast.lineDeleted", "Ligne supprimée.")); })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  const loadInventurMyTasks = () => {
    apiJSON("/api/inventur/my-tasks").then((d) => setInventurMyTasks(d.tasks || [])).catch(() => setInventurMyTasks([]));
  };
  const loadInventurHistory = (id) => {
    apiJSON(`/api/inventur/inventories/${encodeURIComponent(id)}/history`).then((d) => setInventurHistory(d.history || [])).catch(() => setInventurHistory([]));
  };
  const createInventurCategoryTemplate = () => {
    if (!inventurNewCategoryForm?.name?.trim()) { showToast(t("inventur.toast.needName", "Indique un nom.")); return; }
    if (inventurNewCategoryForm.scope === "shared" && !inventurNewCategoryForm.persistOnly) {
      // Catégorie "Für die Filiale speichern" : créée directement en persistée, PUIS rattachée à l'inventaire actif si un inventaire est ouvert.
      if (inventurActiveInventory) {
        addInventurCategoryToInventory({ name: inventurNewCategoryForm.name, description: inventurNewCategoryForm.description, entryType: inventurNewCategoryForm.entryType, scope: "shared" });
      } else {
        apiJSON("/api/inventur/categories", { method: "POST", body: JSON.stringify({ storeId: user?.store, name: inventurNewCategoryForm.name, description: inventurNewCategoryForm.description, entryType: inventurNewCategoryForm.entryType }) })
          .then((d) => { loadInventurCategoryTemplates(); setInventurNewCategoryForm(null); setSheet(null); showToast(d.warning || `✓ ${t("inventur.toast.categoryCreated", "Catégorie créée")}`); })
          .catch((err) => showToast(err.message || t("deliveryNotes.creationImpossible", "Création impossible.")));
      }
    } else if (inventurActiveInventory) {
      addInventurCategoryToInventory({ name: inventurNewCategoryForm.name, entryType: inventurNewCategoryForm.entryType, scope: "inventory_only" });
    }
  };
  const patchInventurCategoryTemplate = (id, patch) => {
    apiJSON(`/api/inventur/categories/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) })
      .then(() => { loadInventurCategoryTemplates(); showToast(`✓ ${t("inventur.toast.categoryUpdated", "Catégorie mise à jour")}`); })
      .catch((err) => showToast(err.message || t("deliveryNotes.actionImpossible", "Action impossible.")));
  };
  const deleteInventurCategoryTemplate = (id) => {
    if (!confirm(t("inventur.confirmDeleteCategory", "Supprimer cette catégorie ?"))) return;
    apiJSON(`/api/inventur/categories/${encodeURIComponent(id)}`, { method: "DELETE" })
      .then(() => { loadInventurCategoryTemplates(); showToast(t("inventur.toast.categoryDeleted", "Catégorie supprimée.")); })
      .catch((err) => showToast(err.message || t("inventur.toast.deleteInUse", "Suppression impossible (déjà utilisée).")));
  };

  /* ---------------------------------------------------------
     Inventur — Bestandskontrolle (indépendant de l'Erfassung)
  --------------------------------------------------------- */
  const loadInventurKontrollen = (mode) => {
    const m = mode || kontrolleListMode;
    setKontrolleListMode(m);
    apiJSON(`/api/inventur/kontrollen?mode=${m}`).then((d) => setInventurKontrollen(d.kontrollen || [])).catch(() => setInventurKontrollen([]));
  };
  // L'entrée de la liste EST la source de vérité (plus d'état "actif" séparé qui pourrait
  // diverger) — chaque mutation réécrit juste l'élément correspondant dans inventurKontrollen.
  const updateKontrolleInList = (updated) => {
    setInventurKontrollen((list) => {
      const idx = list.findIndex((k) => k.id === updated.id);
      if (idx === -1) return [updated, ...list];
      const next = list.slice();
      next[idx] = updated;
      return next;
    });
  };
  const kontrolleExpanded = inventurKontrollen.find((k) => k.id === kontrolleExpandedId) || null;
  // Depuis l'historique (ou tout autre point d'entrée par id) : récupère le contrôle réel,
  // l'insère/rafraîchit dans la liste, puis le déplie — jamais une navigation vers un autre
  // écran (l'accordéon EST le détail).
  const openInventurKontrolle = (id) => {
    apiJSON(`/api/inventur/kontrollen/${encodeURIComponent(id)}`)
      .then((d) => {
        updateKontrolleInList(d.kontrolle);
        setKontrolleExpandedId(d.kontrolle.id);
        setKontrolleExpandedCycleId(d.kontrolle.currentCycle.id);
        setInventurScreen("kontrolle_list");
      })
      .catch((err) => showToast(err.message || t("kontrolle.notFoundToast", "Contrôle introuvable.")));
  };
  const createInventurKontrolle = () => {
    const articles = kontrolleCreateForm.articles.filter((a) => a.artNr.trim());
    if (!articles.length) { showToast(t("kontrolle.toast.needArtNr", "Indique au moins un Art.Nr.")); return; }
    apiJSON("/api/inventur/kontrollen", { method: "POST", body: JSON.stringify({ ...kontrolleCreateForm, articles, storeId: user?.store }) })
      .then((d) => {
        const list = d.kontrollen || (d.kontrolle ? [d.kontrolle] : []);
        showToast(list.length > 1 ? `✓ ${t("kontrolle.toast.createdManyTemplate", { n: list.length }, `${list.length} contrôles créés`)}` : `✓ ${t("kontrolle.toast.createdOne", "Contrôle créé")}`);
        setKontrolleCreateForm(emptyKontrolleCreateForm);
        loadInventurKontrollen();
        setInventurScreen("kontrolle_list");
        if (list.length === 1) { setKontrolleExpandedId(list[0].id); setKontrolleExpandedCycleId(list[0].currentCycle.id); }
      })
      .catch((err) => showToast(err.message || t("deliveryNotes.creationImpossible", "Création impossible.")));
  };
  const patchInventurKontrolle = (patch) => {
    if (!kontrolleExpandedId) return;
    apiJSON(`/api/inventur/kontrollen/${encodeURIComponent(kontrolleExpandedId)}`, { method: "PATCH", body: JSON.stringify(patch) })
      .then((d) => { updateKontrolleInList(d.kontrolle); showToast(`✓ ${t("inventur.toast.saved", "Enregistré")}`); })
      .catch((err) => showToast(err.message || t("deliveryNotes.actionImpossible", "Action impossible.")));
  };
  const addKontrolleDelivery = () => {
    if (!kontrolleDeliveryForm.qty) { showToast(t("kontrolle.toast.needQty", "Indique une quantité.")); return; }
    if (kontrolleDeliveryEditId) {
      patchInventurKontrolle({ updateDelivery: { id: kontrolleDeliveryEditId, ...kontrolleDeliveryForm } });
    } else {
      patchInventurKontrolle({ addDelivery: kontrolleDeliveryForm });
    }
    setKontrolleDeliveryForm({ date: todayISO(), qty: "", note: "" });
    setKontrolleDeliveryEditId(null);
    setSheet(null);
  };
  const openKontrolleDeliveryEdit = (d) => {
    setKontrolleDeliveryEditId(d.id);
    setKontrolleDeliveryForm({ date: d.date, qty: String(d.qty), note: d.note || "" });
    setSheet("kontrolleDelivery");
  };
  const deleteKontrolleDelivery = (d) => {
    if (!confirm(t("kontrolle.delivery.confirmDeleteTemplate", { qty: d.qty }, `Supprimer la livraison de ${d.qty} ?`))) return;
    patchInventurKontrolle({ deleteDelivery: d.id });
  };
  const setKontrolleStatus = (status) => {
    if (!kontrolleExpandedId) return;
    const closingId = kontrolleExpandedId;
    apiJSON(`/api/inventur/kontrollen/${encodeURIComponent(closingId)}/status`, { method: "PATCH", body: JSON.stringify({ status }) })
      .then((d) => {
        updateKontrolleInList(d.kontrolle);
        showToast(`✓ ${t("inventur.toast.statusUpdated", "Statut mis à jour")}`);
        // Retour utilisateur (2026-08-09) : une fois le cycle clôturé, la carte n'a plus
        // besoin de rester ouverte (ses données sont figées) — elle se referme seule, et
        // le prochain article de la liste surligne 3s pour guider directement dessus.
        if (status === "abgeschlossen") {
          const idx = inventurKontrollen.findIndex((k) => k.id === closingId);
          const next = idx >= 0 ? inventurKontrollen[idx + 1] : null;
          setKontrolleExpandedId(null);
          setKontrolleExpandedCycleId(null);
          setKontrolleEditInfoOpen(false);
          if (next) {
            setKontrolleHighlightId(next.id);
            setTimeout(() => setKontrolleHighlightId((cur) => (cur === next.id ? null : cur)), 3000);
          }
        }
      })
      .catch((err) => showToast(err.message || t("deliveryNotes.actionImpossible", "Action impossible.")));
  };
  const startInventurKontrolleCycle = () => {
    if (!kontrolleExpandedId) return;
    apiJSON(`/api/inventur/kontrollen/${encodeURIComponent(kontrolleExpandedId)}/cycles`, { method: "POST", body: JSON.stringify(inventurCycleStartForm) })
      .then((d) => {
        updateKontrolleInList(d.kontrolle);
        setKontrolleExpandedCycleId(d.kontrolle.currentCycle.id);
        setInventurCycleStartForm(emptyCycleStartForm);
        setSheet(null);
        showToast(`✓ ${t("kontrolle.toast.cycleStarted", "Nouveau cycle démarré")}`);
      })
      .catch((err) => showToast(err.message || t("kontrolle.toast.cycleStartError", "Impossible de démarrer un nouveau cycle.")));
  };
  // Clôture anticipée (GR) — arrête tout le contrôle même si le cycle en cours n'est pas
  // terminé (Verkaufsmenge/Endbestand potentiellement absents ⇒ Differenz reste null côté
  // serveur, jamais fabriquée côté client).
  const forceCloseKontrolle = (k) => {
    if (!confirm(t("kontrolle.forceClose.confirmTemplate", { artNr: k.artNr }, `Clôturer définitivement le contrôle "${k.artNr}" ?\n\nLe cycle en cours s'arrête immédiatement, même si sa période prévue n'est pas terminée. Action irréversible.`))) return;
    apiJSON(`/api/inventur/kontrollen/${encodeURIComponent(k.id)}/force-close`, { method: "POST" })
      .then((d) => { updateKontrolleInList(d.kontrolle); showToast(`⛔ ${t("kontrolle.forceClose.toast", "Contrôle clôturé anticipativement")}`); })
      .catch((err) => showToast(err.message || t("deliveryNotes.actionImpossible", "Action impossible.")));
  };
  const deleteInventurKontrolle = (k) => {
    if (!confirm(t("kontrolle.confirmDeleteTemplate", { artNr: k.artNr }, `Supprimer définitivement le contrôle "${k.artNr}" ?`))) return;
    apiJSON(`/api/inventur/kontrollen/${encodeURIComponent(k.id)}`, { method: "DELETE" })
      .then(() => {
        showToast(t("kontrolle.toast.deleted", "Contrôle supprimé."));
        setInventurKontrollen((list) => list.filter((x) => x.id !== k.id));
        if (kontrolleExpandedId === k.id) setKontrolleExpandedId(null);
      })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  const loadKontrolleVisibility = () => {
    apiJSON(`/api/inventur/kontrolle-visibility?storeId=${encodeURIComponent(user?.store)}`)
      .then((d) => setKontrolleVisibility(d.visibility))
      .catch(() => setKontrolleVisibility(null));
  };
  const toggleKontrolleVisibility = (employeeId) => {
    const current = kontrolleVisibility?.hiddenEmployeeIds || [];
    const next = current.includes(employeeId) ? current.filter((id) => id !== employeeId) : [...current, employeeId];
    apiJSON("/api/inventur/kontrolle-visibility", { method: "PUT", body: JSON.stringify({ storeId: user?.store, hiddenEmployeeIds: next }) })
      .then((d) => { setKontrolleVisibility(d.visibility); showToast(`✓ ${t("inventur.toast.saved", "Enregistré")}`); })
      .catch((err) => showToast(err.message || t("deliveryNotes.actionImpossible", "Action impossible.")));
  };
  const deleteInventurInventory = () => {
    if (!inventurActiveInventory) return;
    if (!confirm(t("inventur.confirmDeleteTemplate", { name: inventurActiveInventory.name || inventurActiveInventory.id }, `Supprimer définitivement l'inventaire "${inventurActiveInventory.name || inventurActiveInventory.id}" ?`))) return;
    apiJSON(`/api/inventur/inventories/${encodeURIComponent(inventurActiveInventory.id)}`, { method: "DELETE" })
      .then(() => { showToast(t("inventur.toast.deleted", "Inventaire supprimé.")); setInventurActiveInventory(null); setInventurScreen("erfassung_list"); loadInventurInventories(); })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  const loadKontrolleHistory = () => {
    const params = new URLSearchParams();
    if (kontrolleHistoryFilters.artNr) params.set("artNr", kontrolleHistoryFilters.artNr);
    if (kontrolleHistoryFilters.reason) params.set("reason", kontrolleHistoryFilters.reason);
    if (kontrolleHistoryFilters.negativeOnly) params.set("negativeOnly", "1");
    apiJSON(`/api/inventur/kontrollen-history?${params.toString()}`)
      .then((d) => { setInventurKontrolleHistory(d.kontrollen || []); setInventurKontrolleHistorySummary(d.summary || null); })
      .catch(() => { setInventurKontrolleHistory([]); setInventurKontrolleHistorySummary(null); });
  };
  const buildInventurPrintHtml = (inv) => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const categoriesHtml = inv.categories.slice().sort((a, b) => a.order - b.order).map((cat) => {
      const isBooks = cat.entryType === "buecher";
      const entries = (cat.entries || []).slice().sort((a, b) => isBooks ? (a.artNr === b.artNr ? Number(a.price) - Number(b.price) : a.artNr.localeCompare(b.artNr)) : a.artNr.localeCompare(b.artNr));
      const rows = entries.map((e) => `<tr><td>${esc(e.artNr)}</td><td>${esc(e.name || "")}</td>${isBooks ? `<td>${Number(e.price).toFixed(2)} €</td>` : ""}<td class="rep-total">${e.totalQty}</td></tr>`).join("");
      return `<h2 style="font-size:14px;margin:18px 0 6px;">${esc(cat.name)} <span style="font-weight:400;color:#888;">(${t("inventurExport.lineCountTemplate", { n: entries.length }, `${entries.length} Zeile(n)`)})</span></h2>
        <table class="rep-table"><thead><tr><th>${t("kontrolle.export.number", "Numéro")}</th><th>${t("inventurExport.designationHeader", "Bezeichnung")}</th>${isBooks ? `<th>${t("inventurExport.priceHeader", "Preis")}</th>` : ""}<th>${t("inventurExport.quantityHeader", "Menge")}</th></tr></thead><tbody>${rows || `<tr><td colspan="${isBooks ? 4 : 3}" style="text-align:center;color:#888;">—</td></tr>`}</tbody></table>`;
    }).join("");
    return `<div class="rep-page">
      <header class="rep-header">
        <div><p class="rep-eyebrow">NEXORA · Inventur</p><h1>${esc(inv.name || inv.id)}</h1></div>
        <span class="rep-badge">${t("home.store.label", { id: esc(inv.storeId) }, `Filiale ${esc(inv.storeId)}`)}</span>
      </header>
      <p class="rep-footer-note">${inv.type === "jahresinventur" ? t("inventurExport.jahresinventurLabel", "Jahresinventur") : t("inventurExport.inventurLabel", "Inventur")} · ${fmtFR(inv.date)}</p>
      ${categoriesHtml}
      <p class="rep-footer-note">${t("common.documentGeneratedOnTemplate", { date: fmtFR(todayISO()), name: `${esc(user?.firstname || "")} ${esc(user?.lastname || "")}` }, `Document généré le ${fmtFR(todayISO())} — ${esc(user?.firstname || "")} ${esc(user?.lastname || "")}`)}</p>
    </div>`;
  };

  // Export Bestandskontrolle (un ou plusieurs articles) — même patron .rep-page/.rep-table
  // que le reste de l'app. Une colonne PAR cycle (jamais un texte fusionné) + Cumul
  // Differenz dans sa propre colonne finale — chaque nombre a sa case.
  const buildKontrollePrintHtml = (list) => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const maxCycles = list.reduce((m, k) => Math.max(m, k.cycles.length), 0);
    const cycleHeaders = Array.from({ length: maxCycles }, (_, i) => `<th>${t("kontrolle.cycleTemplate", { n: i + 1 }, `Zyklus ${i + 1}`)}</th>`).join("");
    const rows = list.map((k) => {
      const startAt = fmtFR((k.cycles[0].startAt || "").slice(0, 10));
      const lastCycle = k.cycles[k.cycles.length - 1];
      const period = `${startAt} – ${k.archived && lastCycle.closedAt ? fmtFR(lastCycle.closedAt.slice(0, 10)) : t("kontrolle.export.ongoing", "en cours")}`;
      const cycleCells = Array.from({ length: maxCycles }, (_, i) => {
        const c = k.cycles[i];
        if (!c) return "<td>—</td>";
        const label = c.status !== "abgeschlossen" ? t("kontrolle.export.ongoing", "en cours") : (c.differenz != null ? `${c.differenz > 0 ? "+" : ""}${c.differenz}` : "—");
        return `<td>${esc(label)}</td>`;
      }).join("");
      const cumul = kontrolleCumulDiff(k);
      return `<tr><td>${esc(k.artNr)}</td><td>${esc(k.name || "")}</td><td>${period}</td>${cycleCells}<td class="rep-total">${cumul > 0 ? "+" : ""}${cumul}</td></tr>`;
    }).join("");
    return `<div class="rep-page">
      <header class="rep-header">
        <div><p class="rep-eyebrow">NEXORA · Bestandskontrolle</p><h1>${list.length === 1 ? esc(list[0].artNr + (list[0].name ? " · " + list[0].name : "")) : t("kontrolle.export.allTitleTemplate", { n: list.length }, `Export — ${list.length} articles`)}</h1></div>
        <span class="rep-badge">${t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`)}</span>
      </header>
      <table class="rep-table"><thead><tr><th>${t("kontrolle.export.number", "Numéro")}</th><th>${t("kontrolle.export.designation", "Désignation")}</th><th>${t("kontrolle.export.period", "Période")}</th>${cycleHeaders}<th>${t("kontrolle.export.cumul", "Cumul Differenz")}</th></tr></thead><tbody>${rows}</tbody></table>
      <p class="rep-footer-note">${t("common.documentGeneratedOnTemplate", { date: fmtFR(todayISO()), name: `${esc(user?.firstname || "")} ${esc(user?.lastname || "")}` }, `Document généré le ${fmtFR(todayISO())} — ${esc(user?.firstname || "")} ${esc(user?.lastname || "")}`)}</p>
    </div>`;
  };

  // Formulaire papier totalement vierge — pour un article suivi par quelqu'un sans accès à
  // l'application. 3 blocs de cycle vierges (le même dossier papier peut donc courir sur
  // plusieurs mois, comme dans l'app) + les formules imprimées en rappel + Cumul Differenz
  // séparé, exactement comme l'aperçu à l'écran (openKontrolleBlankFormPreview).
  const buildKontrolleBlankFormHtml = () => {
    const blankRow = (label, colspan) => `<td style="font-size:9.5px;text-transform:uppercase;letter-spacing:.03em;color:#6a7085;padding:7px 8px 3px;width:22%;vertical-align:bottom;white-space:nowrap;">${label}</td><td colspan="${colspan || 1}" style="border-bottom:1px solid #15181f;padding:7px 8px 9px;"></td>`;
    const deliveryRow = () => `<tr><td style="border-bottom:1px solid #eceef2;height:20px;"></td><td style="border-bottom:1px solid #eceef2;height:20px;"></td><td style="border-bottom:1px solid #eceef2;height:20px;"></td></tr>`;
    const cycleBlock = (n) => `
      <div style="border:1px solid #d8dae2;border-radius:6px;padding:10px 12px 4px;margin-bottom:12px;page-break-inside:avoid;">
        <h2 style="font-size:12.5px;margin:0 0 8px;">${t("kontrolle.cycleTemplate", { n }, `Zyklus ${n}`)}</h2>
        <table style="width:100%;border-collapse:collapse;margin-bottom:10px;">
          <tr>${blankRow(t("kontrolle.startdatumLabel", "Startdatum"))}${blankRow(t("kontrolleBlankFormPrint.enddatumPrevu", "Enddatum (prévu)"))}</tr>
          <tr>${blankRow(t("kontrolleBlankFormPrint.anfangsbestand", "Anfangsbestand"))}${blankRow(t("kontrolleBlankFormPrint.liefermengeTotal", "Liefermenge (total)"))}</tr>
        </table>
        <table style="width:100%;border-collapse:collapse;margin-bottom:10px;font-size:10.5px;">
          <thead><tr>${[t("kontrolleBlankFormPrint.datumHeader", "Datum"), t("kontrolleBlankFormPrint.mengeHeader", "Menge"), t("kontrolleBlankFormPrint.bemerkungHeader", "Bemerkung")].map((h) => `<th style="text-align:left;font-size:8.5px;text-transform:uppercase;color:#6a7085;border-bottom:1px solid #d8dae2;padding:0 6px 4px;">${h}</th>`).join("")}</tr></thead>
          <tbody>${deliveryRow()}${deliveryRow()}${deliveryRow()}</tbody>
        </table>
        <table style="width:100%;border-collapse:collapse;margin-bottom:10px;">
          <tr>${blankRow(t("kontrolleBlankFormPrint.verkaufsmengeNorma", "Verkaufsmenge (Norma)"))}${blankRow(t("kontrolleBlankFormPrint.endbestandGezaehlt", "Endbestand (gezählt)"))}</tr>
        </table>
        <div style="font-size:10.5px;color:#3a3f4d;border:1px dashed #b7bcc9;border-radius:6px;padding:7px 10px;margin-bottom:7px;">${t("kontrolleBlankFormPrint.erwarteterFormula", "Erwarteter Endbestand = Anfangsbestand + Liefermenge − Verkaufsmenge = ________________")}</div>
        <div style="font-size:10.5px;font-weight:700;color:#3a3f4d;border:1px dashed #b7bcc9;border-radius:6px;padding:7px 10px;margin-bottom:7px;">${t("kontrolleBlankFormPrint.differenzFormulaTemplate", "Differenz (ce cycle) = Endbestand (gezählt) − Erwarteter Endbestand = ________________")}</div>
      </div>`;
    return `<div class="rep-page">
      <header class="rep-header">
        <div><p class="rep-eyebrow">NEXORA · Bestandskontrolle</p><h1>${t("kontrolle.blankForm.title", "Formulaire papier vierge")}</h1></div>
        <span class="rep-badge">${t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`)}</span>
      </header>
      <p class="rep-footer-note" style="margin-bottom:14px;">${t("kontrolle.blankForm.hint", "À remplir à la main pour un article suivi sans accès à l'application.")}</p>
      <table style="width:100%;border-collapse:collapse;margin-bottom:14px;">
        <tr>${blankRow(t("kontrolleBlankFormPrint.numeroArticle", "Numéro d'article"))}${blankRow(t("kontrolleBlankFormPrint.designation", "Désignation"))}</tr>
        <tr>${blankRow(t("kontrolle.categoryOptionalLabel", "Catégorie"))}${blankRow(t("kontrolle.assignedEmployeeLabel", "Attribué à"))}</tr>
        <tr>${blankRow(t("kontrolleBlankFormPrint.motifControle", "Motif du contrôle"), 3)}</tr>
      </table>
      ${[1, 2, 3].map(cycleBlock).join("")}
      <div style="border:1.5px solid #15181f;border-radius:6px;padding:11px 13px;margin:14px 0;font-weight:700;">${t("kontrolleBlankFormPrint.cumulDifferenzFormula", "Cumul Differenz (somme des Differenz de tous les cycles déjà clôturés) = ________________")}</div>
      <table style="width:100%;border-collapse:collapse;">
        <tr>${blankRow(t("kontrolle.blankForm.filledBy", "Rempli par"))}${blankRow(t("reporting.dateLabel", "Date"))}${blankRow(t("kontrolle.blankForm.signature", "Signature"))}</tr>
      </table>
    </div>`;
  };

  /* Zone sensible — vider des saisies (jamais le catalogue lui-même) */
  const toggleWipeTarget = (id) => setWipeSelections((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const confirmWipe = () => {
    if (wipeSelections.length === 0) { showToast(t("toast.checkAtLeastOneCategoryToWipe", "Coche au moins une catégorie à vider.")); return; }
    if (wipeConfirmText.trim().toUpperCase() !== "SUPPRIMER") { showToast(t("reporting.wipeModal.typeSupprimerToast", 'Tape "SUPPRIMER" pour confirmer.')); return; }
    const month = now.getMonth() + 1;
    const year = now.getFullYear();
    ["prebake", "brot"].forEach((family) => {
      if (!wipeSelections.includes(family)) return;
      apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(user?.store)}&month=${month}&year=${year}&family=${family}`, { method: "DELETE" })
        .then(() => loadCatalogDaily())
        .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
      setDailySaved((d) => ({ ...d, [family]: false }));
    });
    if (wipeSelections.includes("manual")) {
      apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(user?.store)}&month=${month}&year=${year}&family=manual_waste`, { method: "DELETE" })
        .then(() => loadManualWasteWeek())
        .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
      setDailySaved((d) => ({ ...d, manual_waste: false })); setWeekSaved(false);
    }
    if (wipeSelections.includes("krank")) {
      apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(user?.store)}&month=${month}&year=${year}&family=krank`, { method: "DELETE" })
        .then(() => setKrankHours(0))
        .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
    }
    if (wipeSelections.includes("stundenIst")) {
      apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(user?.store)}&month=${month}&year=${year}&family=stundenleistung`, { method: "DELETE" })
        .then(() => loadStundenleistung())
        .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
    }
    if (wipeSelections.includes("plan") || wipeSelections.includes("stundenFile")) {
      const targets = [];
      if (wipeSelections.includes("plan")) targets.push("stunden-plan");
      if (wipeSelections.includes("stundenFile")) targets.push("stunden-file");
      apiJSON("/api/reporting/wipe-entries", { method: "DELETE", body: JSON.stringify({ targets }) })
        .then(() => { setStundenDays([]); setStundenImported(false); })
        .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
    }
    setSheet(null); setWipeSelections([]); setWipeConfirmText("");
    showToast(t("toast.selectedEntriesWiped", "🗑️ Saisies sélectionnées vidées"));
  };

  /* Stundenleistung — Umsatz ÷ heures travaillées, Plan importé (fichier NORMA, vraie
     lecture IA vision serveur, POST /api/reporting/stunden-plan/ai-import) vs Ist saisi
     chaque jour (vraie famille daily-entries "stundenleistung"), cumulé sur le mois.
     Plan réel stocké par date ({umsatzPlan,stundenPlan}, mêmes clés que le vrai
     planDays[date] côté vanilla public/modules/reporting.js — même backend, même
     table). Simplifié vs le vrai module : seules les valeurs "Tag" (jour) sont
     conservées, jamais les colonnes "aufgelaufen" imprimées sur le document ni le
     tableau de validation croisée (validateStundenExtraction) — juste le nécessaire
     pour ce que l'écran affiche déjà (Plan/Ist jour + cumul recalculé ici). N'affiche
     que les jours du 1er au jour réel du mois (jamais les jours futurs, qui n'ont
     jamais d'Ist par construction) — "dernier jour du tableau" reste donc toujours
     "aujourd'hui", condition dont dépend le formulaire de saisie Ist plus bas. */
  const parseGermanNumber = (value) => {
    if (value === null || value === undefined) return null;
    const raw = String(value).trim();
    if (!raw || raw === "-" || raw.toLowerCase() === "null") return null;
    const cleaned = raw.replace(/\s/g, "").replace(/€/g, "").replace(/[^\d,.\-]/g, "");
    if (!cleaned) return null;
    const hasComma = cleaned.includes(",");
    const hasDot = cleaned.includes(".");
    let normalized = cleaned;
    if (hasComma && hasDot) normalized = cleaned.replace(/\./g, "").replace(",", ".");
    else if (hasComma && !hasDot) normalized = cleaned.replace(",", ".");
    const num = Number(normalized);
    return Number.isFinite(num) ? num : null;
  };
  // Extrait de loadStundenleistung — reconstruit les lignes 1..N du mois depuis
  // planDays/istByDate déjà chargés. Fonction pure (aucun état) réutilisée telle quelle
  // par loadPilotStundenDays (fiche filiale, voir plus bas) pour une AUTRE filiale — même
  // construction ligne par ligne, jamais dupliquée.
  const buildStundenDaysArray = (planDays, istByDate, year, month) => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const arr = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const date = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const p = planDays[date] || {};
      const ist = istByDate[date] || {};
      arr.push({
        day: d, date,
        // Dimanche : le document Norma imprime la somme Lu-Sa de la semaine dans la
        // case "Plan Tag" (vérifié : correspond exactement à la somme des jours
        // ouvrés), jamais une vraie cible journalière — isClosed le signale pour que
        // rien (Dashboard, CA du jour) ne traite ce nombre comme une estimation d'un
        // seul jour (retour utilisateur 2026-08-09).
        isClosed: new Date(`${date}T12:00:00`).getDay() === 0,
        umsatzPlanTag: Number.isFinite(p.umsatzPlan) ? p.umsatzPlan : null,
        stundenPlanTag: Number.isFinite(p.stundenPlan) ? p.stundenPlan : null,
        umsatzIstTag: Number.isFinite(ist.umsatzIst) ? ist.umsatzIst : null,
        stundenIstTag: Number.isFinite(ist.stundenIst) ? ist.stundenIst : null,
      });
    }
    return arr;
  };
  const loadStundenleistung = (monthOffset = stundenMonthOffset) => {
    const base = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    const year = base.getFullYear();
    const month = base.getMonth() + 1;
    // Toutes les lignes du mois (Plan connu dès l'import, comme le document Norma) —
    // pas seulement jusqu'à aujourd'hui ; les jours futurs restent "à saisir" pour
    // l'Ist. Les calculs Dashboard (CA réalisé) filtrent séparément aux jours écoulés.
    Promise.all([
      apiJSON(`/api/reporting/stunden-plan?storeId=${encodeURIComponent(user?.store)}&year=${year}&month=${month}`).catch(() => null),
      apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(user?.store)}&family=stundenleistung&month=${month}&year=${year}`).catch(() => []),
    ]).then(([plan, istEntries]) => {
      const planDays = plan?.days || {};
      const istByDate = {};
      (Array.isArray(istEntries) ? istEntries : []).forEach((e) => { istByDate[e.date] = e.items?.[0] || {}; });
      setStundenDays(buildStundenDaysArray(planDays, istByDate, year, month));
      setStundenImported(Boolean(plan));
    });
  };
  // opts.stundenDays : override (mêmes clés que stundenDays state) — utilisé par la fiche
  // filiale (pilotStundenDays, voir plus bas) pour recalculer les cumuls d'UNE AUTRE
  // filiale sans jamais toucher à l'état de la filiale du compte connecté. Aucun appel
  // existant ne passe opts → comportement 100% identique tant qu'aucun override n'est fourni.
  const stundenComputed = (opts = {}) => {
    const days = opts.stundenDays || stundenDays;
    // Dernier jour où le travailleur a réellement saisi un Ist — au-delà, l'app arrête
    // le calcul (cumuls, Stundenleistung Ist) au lieu de continuer à "traîner" la
    // dernière valeur sur des jours pas encore vécus (retour utilisateur 2026-08-09).
    const lastIstDay = days.reduce((max, d) => (d.umsatzIstTag != null || d.stundenIstTag != null) ? Math.max(max, d.day) : max, 0);
    let cumUP = 0, cumSP = 0, cumUI = 0, cumSI = 0;
    return days.map((d) => {
      // Dimanche (isClosed) : son Plan Tag est déjà le sous-total Lu-Sa de sa semaine
      // (imprimé par Norma) — l'ajouter au cumul du mois le compterait une 2e fois par-
      // dessus les 6 jours déjà cumulés (retour utilisateur 2026-08-09).
      if (!d.isClosed) {
        if (d.umsatzPlanTag != null) cumUP += d.umsatzPlanTag;
        if (d.stundenPlanTag != null) cumSP += d.stundenPlanTag;
      }
      if (d.umsatzIstTag != null) cumUI += d.umsatzIstTag;
      if (d.stundenIstTag != null) cumSI += d.stundenIstTag;
      const pastLastIst = d.day > lastIstDay;
      return {
        ...d,
        umsatzPlanCum: truncate2(cumUP),
        umsatzIstCum: pastLastIst ? null : truncate2(cumUI),
        stundenPlanCum: truncate2(cumSP),
        stundenIstCum: pastLastIst ? null : truncate2(cumSI),
        leistungPlanTag: d.umsatzPlanTag != null && d.stundenPlanTag ? truncate2(d.umsatzPlanTag / d.stundenPlanTag) : null,
        leistungIstTag: d.umsatzIstTag != null && d.stundenIstTag ? truncate2(d.umsatzIstTag / d.stundenIstTag) : null,
        leistungPlanCum: cumSP > 0 ? truncate2(cumUP / cumSP) : null,
        leistungIstCum: pastLastIst || cumSI <= 0 ? null : truncate2(cumUI / cumSI),
      };
    });
  };
  // Export imprimable — disponible à tout moment (retour utilisateur), pas seulement en
  // fin de mois : reprend fidèlement les 13 colonnes de l'écran, mois actuellement affiché
  // (respecte stundenMonthOffset).
  // opts : { storeId, stundenDays, monthLabel } — voir commentaire sur stundenComputed.
  const buildStundenleistungPrintHtml = (opts = {}) => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const storeIdForPrint = opts.storeId || user?.store;
    const rows = stundenComputed(opts.stundenDays ? { stundenDays: opts.stundenDays } : {});
    const monthLabel = opts.monthLabel || (() => {
      const viewedMonth = new Date(now.getFullYear(), now.getMonth() + stundenMonthOffset, 1);
      return viewedMonth.toLocaleDateString(intlLocale, { month: "long", year: "numeric" });
    })();
    const fmtNum = (v) => fmtDe2(v);
    // NOTE i18n (Groupe 2) : mêmes abréviations compressées (Plan/Ist/∑) que
    // buildMonthClosurePrintHtml — voir la note à cet endroit, même famille de document,
    // même traitement (chrome générique traduit, en-têtes de tableau volontairement
    // laissés tels quels).
    const rowsHtml = rows.length === 0
      ? `<tr><td colspan="13" class="rep-mm-empty">${t("stundenleistungExport.emptyText", "Aucune donnée Stundenleistung importée pour ce mois.")}</td></tr>`
      : rows.map((d) => `<tr${d.isClosed ? ` class="rep-row-closed"` : ""}>
          <td class="rep-mm-kw">${d.day}${d.isClosed ? ` · ${t("stundenleistungExport.closedSuffix", "fermé")}` : ""}</td>
          <td>${fmtNum(d.umsatzPlanTag)}</td><td>${fmtNum(d.umsatzIstTag)}</td><td>${fmtNum(d.umsatzPlanCum)}</td><td>${fmtNum(d.umsatzIstCum)}</td>
          <td>${fmtNum(d.stundenPlanTag)}</td><td>${fmtNum(d.stundenIstTag)}</td><td>${fmtNum(d.stundenPlanCum)}</td><td>${fmtNum(d.stundenIstCum)}</td>
          <td>${fmtNum(d.leistungPlanTag)}</td><td>${fmtNum(d.leistungIstTag)}</td><td>${fmtNum(d.leistungPlanCum)}</td><td>${fmtNum(d.leistungIstCum)}</td>
        </tr>`).join("");
    return `<div class="rep-page rep-mm-page">
      <header class="rep-header">
        <div><p class="rep-eyebrow">NEXORA · Stundenleistung</p><h1>${esc(monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1))}</h1></div>
        <span class="rep-badge">${t("home.store.label", { id: esc(storeIdForPrint) }, `Filiale ${esc(storeIdForPrint)}`)}</span>
      </header>
      <table class="rep-table rep-mm-table">
        <thead>
          <tr>
            <th rowspan="2">${t("stundenleistungExport.dayHeader", "Jour")}</th>
            <th colspan="4">${t("reporting.umsatzCol", "Umsatz")}</th>
            <th colspan="4">${t("reporting.stundenCol", "Stunden")}</th>
            <th colspan="4">${t("reporting.stundenleistungCol", "Stundenleistung")}</th>
          </tr>
          <tr>
            <th>${t("reporting.planTagCol", "Plan")}</th><th>${t("reporting.istTagCol", "Ist")}</th><th>${t("reporting.planCumCol", "Plan ∑")}</th><th>${t("reporting.istCumCol", "Ist ∑")}</th>
            <th>${t("reporting.planTagCol", "Plan")}</th><th>${t("reporting.istTagCol", "Ist")}</th><th>${t("reporting.planCumCol", "Plan ∑")}</th><th>${t("reporting.istCumCol", "Ist ∑")}</th>
            <th>${t("reporting.planTagCol", "Plan")}</th><th>${t("reporting.istTagCol", "Ist")}</th><th>${t("reporting.planCumCol", "Plan ∑")}</th><th>${t("reporting.istCumCol", "Ist ∑")}</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <p class="rep-footer-note">${t("common.documentGeneratedOnTemplate", { date: fmtFR(todayISO()), name: `${esc(user?.firstname || "")} ${esc(user?.lastname || "")}` }, `Document généré le ${fmtFR(todayISO())} — ${esc(user?.firstname || "")} ${esc(user?.lastname || "")}`)}</p>
    </div>`;
  };
  const exportStundenleistung = () => {
    const viewedMonth = new Date(now.getFullYear(), now.getMonth() + stundenMonthOffset, 1);
    const monthLabel = viewedMonth.toLocaleDateString(intlLocale, { month: "long", year: "numeric" });
    printReportWithTitle(buildStundenleistungPrintHtml(), `Stundenleistung ${monthLabel} - Filiale ${user?.store}`);
  };
  // Fiche filiale (Pilotage réseau) — Stundenleistung d'UNE AUTRE filiale, toujours le
  // mois en cours, à l'état actuel de la saisie (pilotStundenDays, voir plus haut et
  // l'effet de chargement plus bas) — jamais navigable comme stundenMonthOffset.
  const exportPilotStundenleistung = () => {
    if (!pilotFilialeId) return;
    const base = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthLabel = base.toLocaleDateString(intlLocale, { month: "long", year: "numeric" });
    printReportWithTitle(
      buildStundenleistungPrintHtml({ storeId: pilotFilialeId, stundenDays: pilotStundenDays, monthLabel }),
      `Stundenleistung ${monthLabel} - Filiale ${pilotFilialeId}`
    );
  };
  // Comparaisons "vs semaine/mois dernier" du Dashboard — vraies données Ist réelles
  // (même famille daily-entries "stundenleistung"), jamais une base de comparaison
  // inventée à côté d'un vrai total (aurait pu induire une manageuse en erreur avec
  // un badge +/-X % complètement fictif).
  const loadCaCompare = () => {
    const year = now.getFullYear();
    const lastWeek = isoWeek(now) - 1;
    const month = now.getMonth() + 1;
    const lastMonthDate = new Date(year, month - 2, 1);
    const lastMonth = lastMonthDate.getMonth() + 1;
    const lastMonthYear = lastMonthDate.getFullYear();
    Promise.all([
      apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(user?.store)}&family=stundenleistung&week=${lastWeek}&year=${year}`).catch(() => []),
      apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(user?.store)}&family=stundenleistung&month=${lastMonth}&year=${lastMonthYear}`).catch(() => []),
      // Semaine dernière, famille manual_waste — même source réelle que "Pertes de la
      // semaine" (weeklyGrid), jamais DEMO_LASTWEEK_WASTE_COUNT (bug réel trouvé et
      // corrigé le 2026-08-02, retour utilisateur : dashboard sans données de test).
      apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(user?.store)}&family=manual_waste&week=${lastWeek}&year=${year}`).catch(() => []),
      // Même mois, année précédente — comparaison YoY demandée par l'utilisateur
      // (2026-08-09) : même famille/logique que lastMonthTotal, juste year-1 au lieu de mois-1.
      apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(user?.store)}&family=stundenleistung&month=${month}&year=${year - 1}`).catch(() => []),
    ]).then(([lastWeekEntries, lastMonthEntries, lastWeekWasteEntries, lastYearMonthEntries]) => {
      const sumUmsatzIst = (entries) => (Array.isArray(entries) ? entries : []).reduce((s, e) => s + Number(e.items?.[0]?.umsatzIst || 0), 0);
      const sumStundenIst = (entries) => (Array.isArray(entries) ? entries : []).reduce((s, e) => s + Number(e.items?.[0]?.stundenIst || 0), 0);
      const weekUmsatz = sumUmsatzIst(lastWeekEntries);
      const weekStunden = sumStundenIst(lastWeekEntries);
      const wasteTotal = (Array.isArray(lastWeekWasteEntries) ? lastWeekWasteEntries : [])
        .reduce((s, e) => s + (e.items || []).reduce((si, it) => si + Number(it.pu20Amount || 0) + Number(it.entsorgtAmount || 0), 0), 0);
      setCaCompare({
        lastWeekTotal: weekUmsatz,
        lastMonthTotal: sumUmsatzIst(lastMonthEntries),
        lastYearMonthTotal: sumUmsatzIst(lastYearMonthEntries),
        lastWeekLeistung: weekStunden > 0 ? truncate2(weekUmsatz / weekStunden) : null,
        lastWeekWasteTotal: wasteTotal,
      });
    });
  };
  // Dashboard — tendance 4 semaines (Umsatz Ist réel, jamais une projection) + alerte
  // "Plan du mois suivant pas encore importé" (seulement utile en fin de mois, jamais
  // affichée en plein milieu d'un mois où ça n'a pas de sens de s'en inquiéter).
  const loadDashboardExtras = () => {
    const year = now.getFullYear();
    const thisWeek = isoWeek(now);
    const weeks = [thisWeek - 3, thisWeek - 2, thisWeek - 1, thisWeek].filter((w) => w >= 1);
    Promise.all(weeks.map((wk) =>
      apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(user?.store)}&family=stundenleistung&week=${wk}&year=${year}`)
        .then((entries) => ({ week: wk, total: (Array.isArray(entries) ? entries : []).reduce((s, e) => s + Number(e.items?.[0]?.umsatzIst || 0), 0) }))
        .catch(() => ({ week: wk, total: 0 }))
    )).then((results) => setWeekTrend(results));

    const daysInThisMonth = new Date(year, now.getMonth() + 1, 0).getDate();
    if (daysInThisMonth - now.getDate() <= 5) {
      const nextMonthDate = new Date(year, now.getMonth() + 1, 1);
      apiJSON(`/api/reporting/stunden-plan?storeId=${encodeURIComponent(user?.store)}&year=${nextMonthDate.getFullYear()}&month=${nextMonthDate.getMonth() + 1}`)
        .then((plan) => setNextMonthPlanMissing(!plan?.days || Object.keys(plan.days).length === 0))
        .catch(() => setNextMonthPlanMissing(false));
    } else {
      setNextMonthPlanMissing(false);
    }
  };
  const importStundenFile = () => stundenFileInputRef.current?.click();
  const handleStundenFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base = new Date(now.getFullYear(), now.getMonth() + stundenMonthOffset, 1);
    const year = base.getFullYear();
    const month = base.getMonth() + 1;
    showToast(t("toast.analyzingNormaFile", "📥 Analyse du fichier NORMA..."));
    const compressed = await compressImageFile(file);
    const fd = new FormData();
    fd.append("file", compressed);
    fd.append("year", String(year));
    fd.append("month", String(month));
    apiJSON("/api/reporting/stunden-plan/ai-import", { method: "POST", body: fd })
      .then((extraction) => {
        const days = {};
        (extraction.days || []).forEach((d) => {
          const dayNum = Number(d.day);
          if (!Number.isFinite(dayNum)) return;
          const date = `${year}-${String(month).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
          days[date] = { umsatzPlan: parseGermanNumber(d.umsatzPlanTag?.raw), stundenPlan: parseGermanNumber(d.stundenPlanTag?.raw) };
        });
        return apiJSON("/api/reporting/stunden-plan", {
          method: "POST",
          body: JSON.stringify({ storeId: user?.store, month, year, days, updatedBy: user?.firstname || "" }),
        });
      })
      .then(() => {
        showToast(t("toast.fileImportedMonthPlanUpdated", "✓ Fichier importé — plan du mois à jour"));
        loadStundenleistung();
      })
      .catch((err) => showToast(err.message || t("err.importImpossible", "Import impossible.")));
  };
  // date: "YYYY-MM-DD" du jour à saisir/corriger — jamais figé sur "aujourd'hui" (retour
  // utilisateur : besoin de rattraper un jour manqué). week/month/year recalculés depuis
  // cette date, jamais depuis `now`, sinon un jour d'un autre mois serait mal classé.
  const saveStundenIst = (date = todayISO()) => {
    const u = parseFloat(String(stundenIstForm.umsatz).replace(",", "."));
    const h = parseFloat(String(stundenIstForm.heures).replace(",", "."));
    if (!u || !h) { showToast(t("toast.indicateUmsatzAndHours", "Indique l'Umsatz et les heures du jour.")); return; }
    const d = new Date(`${date}T12:00:00`);
    apiJSON("/api/reporting/daily-entries", {
      method: "POST",
      body: JSON.stringify({
        storeId: user?.store, date, week: isoWeek(d), year: d.getFullYear(), month: d.getMonth() + 1,
        family: "stundenleistung", totalEuro: u, items: [{ umsatzIst: u, stundenIst: h }], updatedBy: user?.firstname || "",
      }),
    })
      .then(() => {
        setStundenIstForm({ umsatz: "", heures: "" });
        setStundenIstEditDate(null);
        showToast(t("toast.dailyIstSaved", "✓ Ist du jour enregistré"));
        loadStundenleistung();
      })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };

  /* Historique — vue hebdomadaire ou mensuelle (Monatsmeldung : un mois
     = sa dernière semaine, depuis janvier). */
  const monthlyHistory = () => {
    const all = [{ kw: isoWeek(new Date()), kwLabel: String(isoWeek(new Date())), total: weekTotal(), month: "En cours" }, ...reportingHistory.filter((w) => w.month)];
    const byMonth = {};
    all.forEach((w) => { if (!byMonth[w.month]) byMonth[w.month] = w; }); // la première rencontrée = la plus récente (déjà triée)
    return Object.values(byMonth);
  };


  /* Admin — comptes, rôles & permissions, tout réel */
  const loadAdminUsers = () => apiJSON("/api/users").then(setAdminUsers).catch(() => setAdminUsers([]));
  // Onglet "Par utilisateur" (cacher/afficher un module) — endpoint dédié, gate strict
  // NEXORA_ADMIN/super-admin côté serveur (jamais "users.edit", voir
  // PATCH /api/users/:id/hidden-modules, server.js). Mise à jour optimiste de adminUsers
  // pour un retour visuel immédiat (règle "live update everywhere" du projet).
  // Lit/écrit TOUJOURS personalHiddenModules (jamais hiddenModules, qui est la valeur déjà
  // FUSIONNÉE rôle∪utilisateur — bug réel corrigé le 2026-08-16 : décocher un module ici
  // n'avait aucun effet quand il n'était en fait caché que par le rôle, puisque ça écrivait
  // dans le champ personnel sans jamais toucher au champ rôle réellement responsable).
  const toggleHiddenModuleForUser = (targetUser, moduleId) => {
    const current = targetUser.personalHiddenModules || [];
    const next = current.includes(moduleId) ? current.filter((id) => id !== moduleId) : [...current, moduleId];
    setAdminUsers((list) => list.map((u) => (u.id === targetUser.id ? { ...u, personalHiddenModules: next } : u)));
    apiJSON(`/api/users/${targetUser.id}/hidden-modules`, { method: "PATCH", body: JSON.stringify({ hiddenModules: next }) })
      .catch((err) => {
        setAdminUsers((list) => list.map((u) => (u.id === targetUser.id ? { ...u, personalHiddenModules: current } : u)));
        showToast(err.message || t("err.actionImpossible", "Action impossible."));
      });
  };
  // Onglet "Modules par rôle" (2026-08-15) — même patron optimiste que
  // toggleHiddenModuleForUser ci-dessus, endpoint dédié PATCH /api/roles/:role/hidden-modules,
  // même gate strict serveur. Comble le trou "première connexion" — voir normalizeServerUser
  // côté serveur pour la fusion réelle avec hiddenModules par utilisateur.
  const loadRoleHiddenModules = () => apiJSON("/api/roles/hidden-modules").then((data) => setRoleHiddenModules(data?.roleHiddenModules || {})).catch(() => setRoleHiddenModules({}));
  const toggleHiddenModuleForRole = (role, moduleId) => {
    const current = roleHiddenModules[role] || [];
    const next = current.includes(moduleId) ? current.filter((id) => id !== moduleId) : [...current, moduleId];
    setRoleHiddenModules((map) => ({ ...map, [role]: next }));
    apiJSON(`/api/roles/${encodeURIComponent(role)}/hidden-modules`, { method: "PATCH", body: JSON.stringify({ hiddenModules: next }) })
      .catch((err) => {
        setRoleHiddenModules((map) => ({ ...map, [role]: current }));
        showToast(err.message || t("err.actionImpossible", "Action impossible."));
      });
  };
  // Modules mis en avant sur l'Accueil (compte normal, voir le commentaire complet à la
  // déclaration de highlightedModuleIds plus haut) — même patron optimiste que
  // toggleHiddenModuleForRole ci-dessus : reflet immédiat côté client, revert + toast si le
  // serveur refuse (droit insuffisant, réseau).
  const toggleHighlightedModule = (moduleId) => {
    const current = highlightedModuleIds;
    const next = current.includes(moduleId) ? current.filter((id) => id !== moduleId) : [...current, moduleId];
    setHighlightedModuleIds(next);
    apiJSON("/api/home/highlighted-modules", { method: "PATCH", body: JSON.stringify({ moduleIds: next }) })
      .catch((err) => {
        setHighlightedModuleIds(current);
        showToast(err.message || t("err.actionImpossible", "Action impossible."));
      });
  };
  // GET /api/auth/pending est scopé côté serveur (sfAuthApproverSeesRequest) — un
  // compte sans droit de validation reçoit une liste simplement vide, jamais une
  // erreur à afficher (comportement normal, pas un échec réseau).
  const loadAdminPending = () => apiJSON("/api/auth/pending").then((d) => setAdminPending(Array.isArray(d) ? d : [])).catch(() => setAdminPending([]));
  // Même principe que loadAdminPending — GET /api/auth/reset-requests est scopé côté
  // serveur (sfAuthApproverSeesRequest, même fonction/périmètre que les inscriptions).
  const loadAdminResetRequests = () => apiJSON("/api/auth/reset-requests").then((d) => setAdminResetRequests(Array.isArray(d) ? d : [])).catch(() => setAdminResetRequests([]));
  // Le code n'est renvoyé qu'UNE seule fois par le serveur (jamais journalisé, §14) —
  // affiché ici en évidence pour que le responsable le communique de vive voix/en
  // personne, jamais par écrit (message/email) : le formulaire de confirmation employé
  // ne redemande le code qu'une fois, pas de second essai possible après échec/expiration
  // sans regénérer.
  const generateResetCode = (request) => {
    apiJSON(`/api/auth/reset-generate-code/${encodeURIComponent(request.id)}`, { method: "POST" })
      .then((data) => { setResetCodeReveal({ request, code: data.code, expiresInMinutes: data.expiresInMinutes }); loadAdminResetRequests(); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  // "Mon équipe filiale" — GET /api/users n'a aucun garde de rôle et scope déjà en interne
  // sur la seule filiale de l'appelant pour un compte non-réseau (dont FL/FLA, voir
  // server.js) : même route que loadAdminUsers, jamais dupliquée côté serveur, juste son
  // propre état pour ne dépendre d'aucun effet lié à l'écran Admin.
  // Filtre storeId RE-appliqué ici (bug réel trouvé en testant, 2026-08-29) : un compte
  // FL/FLA qui a AUSSI isSuperAdmin (cas réel, voir mapServerUser) déclenche la branche
  // "voit tout le réseau" de GET /api/users — correcte pour cette route générale, mais
  // jamais l'intention de CET écran, qui doit toujours rester borné à une seule filiale
  // quels que soient les autres privilèges du compte.
  const loadFlTeamWorkers = () => {
    setFlTeamWorkersLoading(true);
    apiJSON("/api/users")
      .then((d) => setFlTeamWorkers((Array.isArray(d) ? d : []).filter((w) => (w.stores || []).includes(user?.store) || w.storeId === user?.store)))
      .catch(() => setFlTeamWorkers([]))
      .finally(() => setFlTeamWorkersLoading(false));
  };
  const openFlWorkerEdit = (u) => setFlWorkerEditForm({
    id: u.id, firstname: u.firstname || "", lastname: u.lastname || "",
    phone: u.phone || "", email: u.email || "", role: u.role || "",
  });
  // PATCH /api/team/workers/:id (server/teamManage/routes.js) — jamais /api/users/:id, qui
  // reste bloqué pour FL/FLA par le gel volontaire du cluster admin (voir le commentaire en
  // tête de ce fichier de routes). Le serveur revalide tout (périmètre filiale + rôle
  // attribuable) — ce formulaire reste volontairement minimal côté client.
  const commitFlWorkerEdit = () => {
    if (!flWorkerEditForm) return;
    setFlWorkerEditBusy(true);
    const { id, ...body } = flWorkerEditForm;
    apiJSON(`/api/team/workers/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) })
      .then(() => { showToast(t("flTeam.savedToast", "✓ Enregistré")); setFlWorkerEditForm(null); loadFlTeamWorkers(); })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")))
      .finally(() => setFlWorkerEditBusy(false));
  };
  const toggleFlWorkerStatus = (u) => {
    const nextActive = Boolean(u.disabled || u.status === "disabled");
    apiJSON(`/api/team/workers/${encodeURIComponent(u.id)}/status`, { method: "PATCH", body: JSON.stringify({ active: nextActive }) })
      .then(() => { showToast(nextActive ? t("flTeam.reactivatedToast", "✓ Compte réactivé") : t("flTeam.disabledToast", "Compte désactivé")); loadFlTeamWorkers(); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  // DELETE /api/team/workers/:id (server/teamManage/routes.js) — même effet définitif que
  // deleteUserPermanently (Admin), jamais /api/users/:id/permanent qui reste bloqué pour
  // FL/FLA par le gel volontaire du cluster admin.
  const deleteFlWorker = (u) => {
    apiJSON(`/api/team/workers/${encodeURIComponent(u.id)}`, { method: "DELETE" })
      .then(() => { showToast(t("toast.accountDeletedPermanently", "🗑️ Compte supprimé définitivement")); loadFlTeamWorkers(); })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  // GET /api/admin/sessions renvoie un vrai 403 pour tout compte non super-admin
  // (isSuperAdminServer — un accès personnel, pas une permission du système de rôles) ;
  // là aussi on avale l'erreur, l'onglet Supervision reste juste vide.
  const loadAdminSessions = () => apiJSON("/api/admin/sessions").then((d) => setAdminSessions(d.sessions || [])).catch(() => setAdminSessions([]));
  // Vue supervision Face ID (retour utilisateur explicite, 2026-08-29) — jamais visible ni
  // appelable en dehors du Hub Administration (gate rôle client ci-dessous, ET gate serveur
  // indépendant, voir GET /api/admin/webauthn-devices).
  const loadAdminWebauthnDevices = () => apiJSON("/api/admin/webauthn-devices").then((d) => setAdminWebauthnDevices(d.accounts || [])).catch(() => setAdminWebauthnDevices([]));
  // Suppression d'un appareil Face ID d'un autre compte (2026-08-31, retour utilisateur
  // explicite : "Admin doit pouvoir supprimer les Face ID des utilisateurs sur cette page")
  // — revient sur "lecture seule" posé le 2026-08-29. Même gate NEXORA_ADMIN que l'écran.
  const deleteAdminWebauthnDevice = (acc, device) => {
    if (!confirm(t("admin.webauthnDevices.confirmDeleteTemplate", { name: acc.name, device: device.deviceLabel || t("settings.webauthn.unnamedDevice", "Appareil") }, `Supprimer "${device.deviceLabel || "Appareil"}" de ${acc.name} ? La personne devra se reconnecter avec son mot de passe.`))) return;
    apiJSON(`/api/admin/webauthn-devices/${acc.userId}/${encodeURIComponent(device.credentialId)}`, { method: "DELETE" })
      .then(() => { showToast(t("admin.webauthnDevices.deletedToast", "✓ Appareil supprimé")); loadAdminWebauthnDevices(); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  // Retour utilisateur explicite (2026-08-18) : "Sessions actives" ne se rechargeait
  // qu'une seule fois, au login — un statut en ligne/hors ligne périmé si le tiroir était
  // rouvert longtemps après. Même cadence que le battement de cœur (refreshLiveUserFields,
  // 15s) puisque c'est justement lui qui fait bouger le vrai statut côté serveur
  // (SESSION_ONLINE_THRESHOLD_MS = 90s) — rafraîchir moins souvent laisserait un statut
  // "en ligne" affiché après qu'il soit redevenu faux côté serveur.
  useEffect(() => {
    if (sheet !== "sessions") return;
    loadAdminSessions();
    const poll = setInterval(loadAdminSessions, 15000);
    return () => clearInterval(poll);
  }, [sheet]);
  // 2026-09-01, correctif suite à un incident réel : un échec silencieux ici (réseau,
  // timeout du service worker, ou — la vraie cause cette fois — une donnée serveur
  // temporairement invalide) laissait rolePerms vide sans jamais le signaler, indissociable
  // à l'écran d'un rôle réellement sans permission ("accès refusé" partout, ou 0% dans
  // Rôles & permissions). Re-essai une fois avant d'abandonner silencieusement — même
  // patron que loadManagerOverview/loadFrMyStores.
  const loadRolePermissions = (isRetry) => {
    apiJSON("/api/roles-permissions")
      .then((d) => { setRolePerms(d.roles || {}); setRolePermsUpdatedAt(d.updatedAt || null); setRolePermsLoaded(true); })
      .catch(() => { if (!isRetry) setTimeout(() => loadRolePermissions(true), 1500); });
  };
  const toggleUserStatus = (u) => {
    const isActive = !u.disabled && u.status !== "disabled";
    const req = isActive
      ? apiJSON(`/api/users/${encodeURIComponent(u.id)}`, { method: "DELETE" })
      : apiJSON(`/api/users/${encodeURIComponent(u.id)}`, { method: "PATCH", body: JSON.stringify({ disabled: false, active: true, status: "active" }) });
    req.then(() => { showToast(isActive ? "Compte désactivé" : "✓ Compte réactivé"); loadAdminUsers(); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const approveUser = (requestId, role) => {
    apiJSON(`/api/auth/approve/${encodeURIComponent(requestId)}`, { method: "POST", body: JSON.stringify(role ? { role } : {}) })
      .then(() => { showToast(t("toast.accountApproved", "✓ Compte approuvé")); loadAdminPending(); loadAdminUsers(); })
      .catch((err) => showToast(err.message || t("err.validationImpossible", "Validation impossible.")));
  };
  const rejectUser = (requestId) => {
    apiJSON(`/api/auth/reject/${encodeURIComponent(requestId)}`, { method: "POST", body: JSON.stringify({}) })
      .then(() => { showToast(t("toast.requestRejected", "Demande refusée")); loadAdminPending(); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const deleteUserPermanently = (id) => {
    apiJSON(`/api/users/${encodeURIComponent(id)}/permanent`, { method: "DELETE" })
      .then(() => { showToast(t("toast.accountDeletedPermanently", "🗑️ Compte supprimé définitivement")); loadAdminUsers(); })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  const openUserEdit = (u) => {
    setUserEditForm({
      id: u.id, role: u.role, storeId: u.storeId || u.store || "",
      firstname: u.firstname || "", lastname: u.lastname || "", gender: u.gender || "", employeeId: u.employeeId || "",
      phone: u.phone || "", email: u.email || "", birthdate: (u.birthdate || "").slice(0, 10),
      weeklyContractedHours: u.weeklyContractedHours != null ? String(u.weeklyContractedHours) : "",
      ausbildungsbeauftragter: u.ausbildungsbeauftragter === true,
      reportsTo: u.reportsTo || "",
      // Périmètre multi-filiale — éditable directement ici pour un rôle GR (2026-08-22,
      // retour utilisateur explicite : "il faudrait un vrai écran assigner des filiales" —
      // avant ça, seul le détournement de "Remplacer ce responsable" permettait de poser un
      // tableau `stores` à plusieurs entrées, ce qui a laissé le compte d'un responsable avec
      // stores:[] malgré un champ Filiale affichant une valeur). Pour un rôle non-GR, reste un
      // simple filet de sécurité (voir saveUserEdit) — jamais affiché/éditable dans ce cas.
      stores: Array.isArray(u.stores) ? u.stores : [],
    });
    setUserEditStoreSearch("");
    loadStoresCatalog();
    setSheet("userEdit");
  };
  const saveUserEdit = () => {
    if (!userEditForm) return;
    const f = userEditForm;
    if (!f.firstname.trim() || !f.lastname.trim() || !f.employeeId.trim()) {
      showToast(t("toast.firstLastEmployeeIdRequired", "Prénom, nom et ID employé obligatoires.")); return;
    }
    // Un rôle GR gère son périmètre via la checklist multi-filiale (voir plus bas dans le
    // JSX) — jamais le simple champ texte "Filiale", qui reste réservé aux comptes de
    // filiale (une seule filiale, jamais plusieurs). storeId devient juste "la première
    // filiale cochée" pour un GR, cohérent avec le repli déjà utilisé partout ailleurs
    // (sfRequestIdentity, etc.) — jamais vide en pratique une fois au moins une filiale
    // cochée, mais peut légitimement l'être pour un GR tout juste créé (0 filiale).
    const isGrRole = ["Bereichsleiter", "Verkaufsleiter", "Niederlassungsleiter"].includes(f.role);
    let storeId, stores;
    if (isGrRole) {
      stores = Array.isArray(f.stores) ? f.stores : [];
      storeId = stores[0] || "";
    } else {
      storeId = f.storeId.trim().toUpperCase();
      if (!storeId) { showToast(t("toast.storeRequired", "Filiale obligatoire.")); return; }
      stores = [storeId];
    }
    const hours = Number(f.weeklyContractedHours);
    const commitUserEdit = () => apiJSON(`/api/users/${encodeURIComponent(f.id)}`, {
      method: "PATCH",
      body: JSON.stringify({
        role: f.role, storeId, stores,
        firstname: f.firstname.trim(), lastname: f.lastname.trim(), gender: f.gender || "", employeeId: f.employeeId.trim(),
        phone: f.phone.trim(), email: f.email.trim(), birthdate: f.birthdate,
        weeklyContractedHours: Number.isFinite(hours) && hours > 0 ? hours : null,
        ausbildungsbeauftragter: f.ausbildungsbeauftragter === true,
        reportsTo: ["Bereichsleiter", "Verkaufsleiter"].includes(f.role) ? (f.reportsTo || null) : null,
      }),
    })
      .then(() => {
        showToast(t("toast.accountEdited", "✓ Compte modifié"));
        setSheet(null); setUserEditForm(null);
        loadAdminUsers();
        // Retour utilisateur 2026-08-04 : la civilité (et le reste de la fiche) doit se
        // refléter immédiatement, pas seulement après un rechargement de page — taskEmployees
        // alimente Planning/Tâches (planEmpDisplayLabel), donc un rechargement ici couvre les
        // deux d'un coup, jamais besoin de revisiter l'écran pour le déclencher.
        loadTaskEmployees();
        // Si le compte modifié est celui de la session en cours (un admin qui édite sa
        // propre fiche), la salutation Accueil (genderTitle) doit changer tout de suite —
        // sans ça, il faudrait se déconnecter/reconnecter pour la voir à jour.
        if (user && f.id === user?.id) {
          setUser((u) => ({ ...u, firstname: f.firstname.trim(), lastname: f.lastname.trim(), gender: f.gender || "" }));
        }
      })
      .catch((err) => showToast(err.message || t("err.modificationImpossible", "Modification impossible.")));

    // Une filiale tapée ici et absente du registre (voir GET /api/stores) doit devenir
    // réelle immédiatement — plus jamais une filiale "fantôme" comme N397, référencée
    // uniquement dans le profil d'un utilisateur sans exister nulle part ailleurs. Un GR
    // choisit toujours parmi le registre existant (checklist, jamais de saisie libre) —
    // cette création à la volée ne concerne donc que le champ texte réservé aux comptes de
    // filiale.
    if (isGrRole || storesCatalog.some((s) => s.id === storeId)) {
      commitUserEdit();
    } else {
      apiJSON("/api/stores", { method: "POST", body: JSON.stringify({ id: storeId, label: storeId }) })
        .then(commitUserEdit)
        .catch((err) => showToast(err.message || t("err.storeCreationImpossible", "Création de la filiale impossible.")));
    }
  };
  // PATCH /api/roles-permissions/:role/toggle — bascule UNE permission, lue et modifiée
  // atomiquement CÔTÉ SERVEUR (2026-09-01, correctif suite à un incident réel : l'ancien
  // POST envoyait TOUT le document roles depuis rolePerms côté client à chaque clic — un
  // téléphone resté en arrière-plan (poll 15s suspendu par iOS) gardait un état local
  // périmé, et le moindre clic sur N'IMPORTE QUEL rôle réécrivait ce snapshot entier,
  // effaçant silencieusement toute correction faite entre-temps côté serveur). Optimiste
  // localement pour un affichage instantané, mais la vérité vient toujours de la réponse
  // serveur (roles complet renvoyé), jamais de ce que le client pensait avant l'appel.
  const togglePermission = (role, permId) => {
    setRolePerms((rp) => {
      const current = rp[role] || [];
      const has = current.includes(permId);
      return { ...rp, [role]: has ? current.filter((p) => p !== permId) : [...current, permId] };
    });
    apiJSON(`/api/roles-permissions/${encodeURIComponent(role)}/toggle`, { method: "PATCH", body: JSON.stringify({ permId }) })
      .then((d) => { setRolePerms(d.roles || {}); setRolePermsUpdatedAt(new Date().toISOString()); refreshRolePermissions(); })
      .catch((err) => { showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")); loadRolePermissions(); });
  };
  const activeUsersCount = adminUsers.filter((u) => !u.disabled && u.status !== "disabled" && u.approvalStatus !== "PENDING_APPROVAL").length;
  const disabledUsersCount = adminUsers.filter((u) => u.disabled || u.status === "disabled").length;

  const loadDocIntel = () => {
    Promise.all([apiJSON("/api/document-intel/documents"), apiJSON("/api/document-intel/summary")])
      .then(([docsData, summaryData]) => {
        setDocIntelDocs(Array.isArray(docsData.documents) ? docsData.documents : []);
        setDocIntelSummary(summaryData || { todayCount: 0, pending: 0, unsupported: 0 });
      })
      .catch(() => {});
  };
  const uploadDocIntelFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setDocIntelError("");
    const compressed = await compressImageFiles(files);
    const formData = new FormData();
    compressed.forEach((f) => formData.append("files", f));
    showToast(files.length > 1 ? t("documentIntel.toast.uploadingManyTemplate", { n: files.length }, `Envoi de ${files.length} fichiers...`) : t("documentIntel.toast.uploadingOne", "Envoi du fichier..."));
    apiJSON("/api/document-intel/upload", { method: "POST", body: formData })
      .then(() => {
        showToast(files.length > 1 ? t("documentIntel.toast.uploadedManyTemplate", { n: files.length }, `${files.length} documents déposés.`) : t("documentIntel.toast.uploadedOne", "Document déposé."));
        loadDocIntel();
      })
      .catch((err) => setDocIntelError(err.message || t("documentIntel.uploadError.default", "Import impossible.")));
  };
  const analyzeDocIntelDoc = (id) => {
    if (docIntelAnalyzingIds.includes(id)) return;
    setDocIntelAnalyzingIds((ids) => [...ids, id]);
    apiJSON(`/api/document-intel/${encodeURIComponent(id)}/analyze`, { method: "POST" })
      .then(() => showToast(t("documentIntel.toast.analyzed", "Document analysé.")))
      .catch((err) => showToast(err.message || t("documentIntel.analyzeError", "Analyse impossible.")))
      .finally(() => {
        setDocIntelAnalyzingIds((ids) => ids.filter((x) => x !== id));
        loadDocIntel();
      });
  };
  const DOC_INTEL_STATUS_META = {
    pending: { label: t("documentIntel.status.pending", "En attente"), color: P.muted, emoji: "⏳" },
    classified: { label: t("documentIntel.status.classified", "Classé"), color: A.green, emoji: "✅" },
    routed: { label: t("documentIntel.status.routed", "Envoyé au module"), color: A.frost, emoji: "➡️" },
    unsupported: { label: t("documentIntel.status.unsupported", "Non pris en charge"), color: A.orange, emoji: "🚫" },
    rejected: { label: t("documentIntel.status.rejected", "Rejeté"), color: A.red, emoji: "❌" },
  };

  /* Arbeitszeitliste — chargement/mutations réelles (GET renvoie déjà TOUT : dates,
     employés+totaux, pages, anomalies, QR, validation — un seul point de vérité, jamais
     recalculé côté client). */
  const loadAzl = () => {
    if (!azlStoreId || !azlYear || !azlMonth) return;
    setAzlLoading(true);
    setAzlError("");
    apiJSON(`/api/arbeitszeitliste/${encodeURIComponent(azlStoreId)}/${azlYear}/${azlMonth}`)
      .then((data) => setAzlData(data))
      .catch((err) => { setAzlError(err.message || "Impossible de charger l'Arbeitszeitliste."); setAzlData(null); })
      .finally(() => setAzlLoading(false));
  };
  const azlShiftMonth = (delta) => {
    const d = new Date(azlYear, azlMonth - 1 + delta, 1);
    setAzlYear(d.getFullYear());
    setAzlMonth(d.getMonth() + 1);
  };
  const AZL_MONTH_DE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
  const azlMonthLabel = `${AZL_MONTH_DE[azlMonth - 1]} ${azlYear}`;
  // Même vocabulaire de badges que l'ancien renderer SVG (BADGE_FILL/BADGE_COLOR,
  // arbeitszeitlisteSvg.js) — porté en couleurs React pour le nouveau tableau HTML.
  // Phase AZL Phase 1 a rendu KK/UU/SU/MS/ERZ réels (plus seulement des entrées de légende
  // jamais produites) — couleurs alignées sur les mêmes familles que shiftStatusMeta
  // (Planning) : rouge pour les arrêts maladie, orange pour les congés non payés/spéciaux,
  // violet pour maternité/parental.
  const AZL_BADGE_FILL = { LF: "#eaf7ef", V: "#f1ebff", K: "#ffebe9", U: "#eaf2ff", F: "#fff4e5", KK: "#ffebe9", UU: "#fff4e5", SU: "#fff4e5", MS: "#f1ebff", ERZ: "#f1ebff" };
  const AZL_BADGE_COLOR = { LF: A.green, V: A.purple, K: A.red, U: A.blue, F: A.orange, KK: A.red, UU: A.orange, SU: A.orange, MS: A.purple, ERZ: A.purple };

  // Document imprimable "brouillon" — retour utilisateur explicite (2026-08-03) : plus
  // jamais un clone du SVG A3 cramponné, un vrai document HTML neuf comme tous les autres
  // exports de l'app (.rep-page/.rep-mm-table, mêmes classes que Reporting). N'existe que
  // pour un mois pas encore archivé — le document officiel (validé+signé) est déjà réel et
  // hash-vérifié, jamais régénéré ici (voir downloadAzlArchive).
  // Phase 7 (fidélité impression/PDF finale) — reprend EXACTEMENT le même modèle 3-lignes/
  // légende 10 codes/colonnes Rest-Urlaub+Übertrag/signature par employé que l'écran
  // interactif (voir azlDayCellContent et la carte Validierung, ~L16511 et ~L16705) —
  // jamais une logique séparée qui pourrait diverger du rendu à l'écran.
  const buildAzlPrintHtml = () => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const sigByRole = new Map((azlData.validation?.signatures || []).map((s) => [s.validationRole, s]));
    const AZL_FULL_DAY_CODES = ["K", "KK", "U", "UU", "SU", "F", "MS", "ERZ"];
    const AZL_BADGE_COLOR_PRINT = { K: "#dc2626", KK: "#dc2626", U: "#1d4ed8", UU: "#1d4ed8", SU: "#1d4ed8", F: "#6b7280", V: "#7c3aed", MS: "#db2777", ERZ: "#db2777" };
    const hoursDecimal = (minutes) => (minutes > 0 ? azlFmtHoursDe(minutes / 60) : "");
    const dayCellHtml = (day) => {
      if (!day) return "";
      const code = day.absenceCode;
      const topText = day.vertretungsgeldNote || (day.lfMinutes > 0 ? `${hoursDecimal(day.lfMinutes)}*` : "");
      const isFullDayCode = AZL_FULL_DAY_CODES.includes(code);
      const middleText = isFullDayCode ? code : (code === "V" ? hoursDecimal(day.vertretungMinutes) : hoursDecimal(day.workedMinutes));
      const middleColor = isFullDayCode || code === "V" ? (AZL_BADGE_COLOR_PRINT[code] || "#0a0a0a") : "#0a0a0a";
      const bottomText = day.pauseMinutes > 0 ? hoursDecimal(day.pauseMinutes) : "";
      return `<div class="azl-cell">
        <span class="azl-cell-top">${esc(topText)}</span>
        <span class="azl-cell-mid" style="color:${middleColor};">${esc(middleText)}${day.zuschlagspflichtig && middleText ? "<sup>Z</sup>" : ""}</span>
        <span class="azl-cell-bot">${esc(bottomText)}</span>
      </div>`;
    };
    const isSundayISO = (dateISO) => new Date(dateISO + "T00:00:00").getDay() === 0;
    const azlDowShort = (jsDay) => (jsDay === 0 ? t("weekday.short.sun", "So") : weekLabelShort(jsDay - 1));
    const dayHeaders = azlData.dates.map((dateISO) => {
      const d = new Date(dateISO + "T00:00:00");
      return `<th>${d.getDate()}.<br>${esc(azlDowShort(d.getDay()))}</th>`;
    }).join("");
    const externalBadge = t("arbeitszeitliste.externalBadge", "EXTERN");
    const personalNrLabel = t("arbeitszeitliste.table.personalNr", "Personal-Nr");
    const empRows = azlData.employees.map((emp) => `<tr>
      <th>${esc(emp.displayName)}${emp.isExternal ? ` (${esc(externalBadge)} ${esc(emp.homeStoreId)})` : ""}<br><span class="azl-personalnr">${esc(personalNrLabel)} ${esc(emp.employeeId)}</span></th>
      ${emp.days.map((day, idx) => `<td class="${isSundayISO(azlData.dates[idx]) ? "azl-cell-sun" : ""}">${dayCellHtml(day)}</td>`).join("")}
      <td>${azlFmtMinutes(emp.workedMinutes)}</td>
      <td>${azlFmtMinutes(emp.lfMinutes)}</td>
      <td>${azlFmtMinutes(emp.vertretungMinutes)}</td>
      <td>${emp.krankDays}</td>
      <td>${emp.urlaubDays}</td>
      <td>${emp.weeklyContractedHours != null ? `${emp.weeklyContractedHours} h` : "—"}</td>
      <td>${emp.restUrlaubDays != null ? `${emp.restUrlaubDays} j` : "—"}</td>
      <td style="${emp.hourCarryoverMinutes ? "color:#dc2626;font-weight:700;" : ""}">${emp.hourCarryoverMinutes ? `${emp.hourCarryoverMinutes >= 0 ? "+" : "-"}${azlFmtMinutes(Math.abs(emp.hourCarryoverMinutes))}` : "—"}</td>
    </tr>`).join("");
    // Signature par ligne employé (Phase 5) + 2 emplacements fixes — jamais la liste
    // statique à 3 rôles remplacée en Phase 5 côté écran interactif, désormais alignée ici.
    const allRoles = azlData.validation?.requiredRoles || [];
    const employeeRoles = allRoles.filter((r) => r.startsWith("employee:"));
    const fixedRoles = allRoles.filter((r) => !r.startsWith("employee:"));
    const employeesById = new Map((azlData.validation?.employees || []).map((e) => [e.employeeId, e.name]));
    const validationRoleLabel = (role) => {
      if (role === "branchManager") return t("arbeitszeitliste.validation.roleBranchManager", "Filialleiter / FLA");
      if (role === "areaManager") return t("arbeitszeitliste.validation.roleAreaManager", "Bereichsleiter");
      const empId = role.startsWith("employee:") ? role.slice("employee:".length) : null;
      return empId ? (employeesById.get(empId) || empId) : role;
    };
    const validationRows = [...employeeRoles, ...fixedRoles].map((role) => {
      const sig = sigByRole.get(role);
      return `<tr><td style="text-align:left;">${esc(validationRoleLabel(role))}</td><td>${sig ? esc(sig.userName) : "—"}</td><td>${sig ? fmtFR(sig.signedAt.slice(0, 10)) : "—"}</td></tr>`;
    }).join("");
    const anomalyRows = azlData.anomalies.length
      ? azlData.anomalies.map((a) => `<li>${esc(a.message)}</li>`).join("")
      : `<li style="color:#1eb85f;font-weight:700;">${esc(t("arbeitszeitliste.anomalies.none", "Aucune anomalie détectée"))}</li>`;
    // Légende — les 10 vrais codes du document papier (même liste que l'écran interactif,
    // voir azlLegendLabel ~ligne 3560), jamais l'ancien "EZ" inventé. Le CODE (K, KK, U...)
    // reste toujours identique dans les 5 langues (code légal allemand) ; seul son libellé
    // explicatif est traduit via azlLegendLabel.
    const LEGEND = [["K", "Krank"], ["KK", "Kinderkrank"], ["U", "Urlaub"], ["UU", "Unbez. Urlaub"], ["SU", "Sonderurlaub"], ["F", "Feiertag"], ["V", "Vertretung"], ["MS", "Mutterschutz"], ["ERZ", "Erziehungsurlaub"], ["Z", "Zuschlagspflichtig"]];
    const legendHtml = LEGEND.map(([code, label]) => `<span class="azl-legend-item"><strong>${code}</strong> ${esc(azlLegendLabel(code, label))}</span>`).join("");
    const statusLabel = azlData.document.status === "archived" ? t("azlPrint.status.archived", "Archivé")
      : azlData.document.status === "validated" ? t("azlPrint.status.validated", "Validé")
      : t("azlPrint.status.draft", "Brouillon");
    const naLabel = t("arbeitszeitliste.performance.unavailable", "Non disponible");
    return `<div class="rep-page rep-mm-page">
      <header class="rep-header">
        <div><p class="rep-eyebrow">NEXORA · Arbeitszeitliste</p><h1>${esc(t("home.store.label", { id: azlStoreId }, `Filiale ${azlStoreId}`))} — ${esc(azlMonthLabel)}</h1></div>
        <span class="rep-badge" style="background:${azlData.document.status === "archived" ? "#1eb85f" : azlData.document.status === "validated" ? "#e78a12" : "#364fe7"};">${esc(statusLabel)}</span>
      </header>
      <table class="rep-table rep-mm-table azl-mm-table"><thead><tr><th>${esc(t("arbeitszeitliste.table.employee", "Mitarbeiter"))}</th>${dayHeaders}<th>${esc(t("arbeitszeitliste.table.sumWork", "Σ Arbeit"))}</th><th>${esc(t("arbeitszeitliste.table.sumLf", "Σ LF"))}</th><th>${esc(t("arbeitszeitliste.table.sumV", "Σ V"))}</th><th>${esc(t("arbeitszeitliste.table.sumK", "Σ K"))}</th><th>${esc(t("arbeitszeitliste.table.sumU", "Σ U"))}</th><th>${esc(t("arbeitszeitliste.table.contract", "Vertrag"))}</th><th>${esc(t("arbeitszeitliste.table.restUrlaub", "Rest-Urlaub"))}</th><th>${esc(t("arbeitszeitliste.table.carryover", "Übertrag"))}</th></tr></thead><tbody>${empRows}</tbody></table>
      <p class="rep-section-title">${esc(t("arbeitszeitliste.legend.title", "Legende"))}</p>
      <p class="azl-legend">${legendHtml}</p>
      <p class="rep-section-title">${esc(t("arbeitszeitliste.summary.title", "Zusammenfassung"))}</p>
      <p style="font-size:11px;margin:0 0 12px;">${esc(t("arbeitszeitliste.summary.sumWork", "Σ Arbeit"))} ${azlFmtMinutes(azlData.branchTotals.workedMinutes)} · ${esc(t("arbeitszeitliste.summary.sumLf", "Σ LF"))} ${azlFmtMinutes(azlData.branchTotals.lfMinutes)} · ${esc(t("arbeitszeitliste.summary.sumV", "Σ Vertretung"))} ${azlFmtMinutes(azlData.branchTotals.vertretungMinutes)} · ${esc(t("arbeitszeitliste.summary.sumK", "Σ Krank"))} ${azlData.branchTotals.krankDays} · ${esc(t("arbeitszeitliste.summary.sumU", "Σ Urlaubstage"))} ${azlData.branchTotals.urlaubDays} · ${esc(t("arbeitszeitliste.summary.sumExt", "Σ Einsatz extern"))} ${azlData.branchTotals.externalCount}</p>
      <p class="rep-section-title">${esc(t("arbeitszeitliste.performance.title", "Performance"))}</p>
      <p style="font-size:11px;margin:0 0 12px;">${esc(t("arbeitszeitliste.performance.umsatz", "Σ Umsatz"))} ${azlData.performance.umsatzTotal === null ? esc(naLabel) : `${azlData.performance.umsatzTotal.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`} · ${esc(t("arbeitszeitliste.performance.leistungsstunden", "Σ Leistungsstunden"))} ${azlData.performance.leistungsstundenTotal === null ? esc(naLabel) : azlFmtMinutes(azlData.performance.leistungsstundenTotal * 60)} · ${esc(t("arbeitszeitliste.performance.stundenleistung", "Ø Stundenleistung"))} ${azlData.performance.stundenleistung === null ? esc(naLabel) : `${azlData.performance.stundenleistung.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/h`}</p>
      <p class="rep-section-title">${esc(t("arbeitszeitliste.anomalies.title", "Anomalien"))}</p>
      <ul style="font-size:11px;margin:0 0 12px;padding-left:18px;">${anomalyRows}</ul>
      <p class="rep-section-title">${esc(t("arbeitszeitliste.validation.title", "Validierung"))}</p>
      <table class="rep-table"><thead><tr><th>${esc(t("azlPrint.locationLabel", "Emplacement"))}</th><th>${esc(t("common.nameLabel", "Nom"))}</th><th>${esc(t("reporting.dateLabel", "Date"))}</th></tr></thead><tbody>${validationRows}</tbody></table>
      <p class="rep-footer-note">${esc(t("azlPrint.footerDraftNote", { date: fmtFR(todayISO()) }, `Document généré le ${fmtFR(todayISO())} — brouillon de travail, pas le document officiel archivé.`))} ${esc(t("azlPrint.footerCentralNote", "Les heures supplémentaires, l'indemnité de remplacement (total), les mutations et le bloc Centrale sont renseignés par le siège (pas dans cette vue)."))}</p>
    </div>`;
  };
  // Impression — retour utilisateur explicite (2026-08-03) : si le mois est déjà archivé,
  // télécharge le VRAI PDF officiel hash-vérifié (déjà réel, voir downloadAzlArchive) au
  // lieu de rouvrir un aperçu — c'est déjà le document final. Sinon, nouveau document
  // brouillon (voir buildAzlPrintHtml ci-dessus), même portail #azlPrintRoot +
  // window.print() que HACCP/MHD/Arbeitsplan (jamais window.open, qui navigue hors de
  // l'app en PWA/WKWebView standalone iOS).
  const printAzlDocument = async () => {
    if (!azlData) { showToast(t("toast.nothingToPrint", "Rien à imprimer.")); return; }
    if (azlData.document.status === "archived") { downloadAzlArchive(); return; }
    try {
      await exportHtmlToPdf({ bodyHtml: buildAzlPrintHtml(), css: REP_DOC_CSS, targetSelector: ".rep-page", widthMm: 297, filename: `Arbeitszeitliste-${azlStoreId}-${azlMonthLabel}.pdf`.replace(/[\\/:*?"<>|]+/g, "-") });
    } catch (err) {
      if (err?.name !== "AbortError") showToast(err.message || t("err.actionImpossible", "Action impossible."));
    }
  };

  // Formulaire d'édition (Phase AZL Phase 3) — décimal en heures côté UI (cohérent avec le
  // reste de l'app, ex. shiftForm.leistungsfreiHours), converti en minutes seulement à
  // l'envoi. Pré-rempli avec l'état EFFECTIF de la case (déjà corrigé si overridden), jamais
  // avec une valeur vide qui ferait perdre une correction existante en cas de re-soumission
  // partielle.
  const azlMinutesToHoursStr = (m) => (m ? String(Math.round((m / 60) * 100) / 100) : "");
  const openAzlDay = (emp, dateISO, day, anomalyMessage) => {
    setAzlDayDetail({ emp, dateISO, day, anomalyMessage });
    setAzlDayEditForm({
      workedHours: azlMinutesToHoursStr(day?.workedMinutes),
      lfHours: azlMinutesToHoursStr(day?.lfMinutes),
      pauseHours: azlMinutesToHoursStr(day?.pauseMinutes),
      absenceCode: day?.absenceCode || "",
      vertretungsgeldNote: day?.vertretungsgeldNote || "",
      reason: "",
    });
    setSheet("azlDay");
  };
  const AZL_DAY_ABSENCE_OPTIONS = ["", "K", "KK", "U", "UU", "SU", "MS", "ERZ"];
  const saveAzlDayOverride = () => {
    if (!azlDayDetail || !azlDayEditForm) return;
    setAzlDaySaving(true);
    const toMinutesOrNull = (str) => {
      const n = Number(String(str || "").replace(",", "."));
      return str === "" || !Number.isFinite(n) ? null : Math.round(n * 60);
    };
    apiJSON(`/api/arbeitszeitliste/${encodeURIComponent(azlStoreId)}/${azlYear}/${azlMonth}/days/${encodeURIComponent(azlDayDetail.emp.employeeId)}/${azlDayDetail.dateISO}`, {
      method: "PATCH",
      body: JSON.stringify({
        workedMinutes: toMinutesOrNull(azlDayEditForm.workedHours),
        lfMinutes: toMinutesOrNull(azlDayEditForm.lfHours),
        pauseMinutes: toMinutesOrNull(azlDayEditForm.pauseHours),
        absenceCode: azlDayEditForm.absenceCode || null,
        vertretungsgeldNote: azlDayEditForm.vertretungsgeldNote || "",
        reason: azlDayEditForm.reason || "",
      }),
    })
      .then(() => {
        showToast(t("toast.savingSuccessShort", "✓ Enregistré"));
        setSheet(null); setAzlDayDetail(null); setAzlDayEditForm(null);
        loadAzl();
      })
      .catch((err) => showToast(err.message || t("err.savingErrorShort", "Erreur enregistrement.")))
      .finally(() => setAzlDaySaving(false));
  };
  const openAzlAnomalyDetail = (anomaly) => {
    if (!anomaly.date) { showToast(anomaly.message); return; }
    const emp = azlData?.employees.find((e) => String(e.employeeId) === String(anomaly.employeeId));
    if (!emp) { showToast(anomaly.message); return; }
    const dayIndex = azlData.dates.indexOf(anomaly.date);
    const day = dayIndex >= 0 ? emp.days[dayIndex] : null;
    openAzlDay(emp, anomaly.date, day, anomaly.message);
  };
  const openAzlAnomalyList = (anomalies) => { setAzlAnomalyList(anomalies); setSheet("azlAnomalies"); };

  const openAzlValidation = (role) => {
    setAzlValidationRole(role); setAzlSignError(""); setAzlSignReady(false);
    setSheet("azlValidate");
  };
  const submitAzlSignature = () => {
    const pad = azlSignPadRef.current;
    if (!pad || !pad.hasValidSignature()) return;
    setAzlSignBusy(true); setAzlSignError("");
    apiJSON(`/api/arbeitszeitliste/${encodeURIComponent(azlStoreId)}/${azlYear}/${azlMonth}/sign`, {
      method: "POST",
      body: JSON.stringify({ validationRole: azlValidationRole, signatureDataUrl: pad.toDataURL(), strokeLength: pad.getStrokeLength() }),
    })
      .then((result) => {
        setSheet(null);
        loadAzl();
        showToast(result.allSigned ? "Les 3 validations sont réunies — le document est désormais figé et archivé." : "✓ Signature enregistrée");
      })
      .catch((err) => setAzlSignError(err.message || "Signature impossible."))
      .finally(() => setAzlSignBusy(false));
  };

  // Téléchargement du PDF officiel — jamais window.open (voir buildMhdExportPrintHtml plus
  // haut : navigue hors de l'app en PWA/WKWebView standalone iOS sans moyen d'y revenir).
  // fetch+blob+<a download> reste dans l'app quelle que soit la plateforme.
  const azlDownloadFile = async (url, filename) => {
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || "Téléchargement impossible."); }
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 4000);
    } catch (err) {
      showToast(err.message || t("err.downloadImpossible", "Téléchargement impossible."));
    }
  };
  const downloadAzlArchive = () => azlDownloadFile(
    `/api/arbeitszeitliste/${encodeURIComponent(azlStoreId)}/${azlYear}/${azlMonth}/archive`,
    `${azlData?.document?.id || "arbeitszeitliste"}.pdf`
  );
  const downloadAzlVersion = (versionNumber) => azlDownloadFile(
    `/api/arbeitszeitliste/${encodeURIComponent(azlStoreId)}/${azlYear}/${azlMonth}/versions/${versionNumber}/archive`,
    `${azlData?.document?.id || "arbeitszeitliste"}-v${versionNumber}.pdf`
  );
  const retryAzlArchive = () => {
    apiJSON(`/api/arbeitszeitliste/${encodeURIComponent(azlStoreId)}/${azlYear}/${azlMonth}/archive`, { method: "POST" })
      .then(() => { showToast(t("toast.archivingRetried", "✓ Archivage réessayé")); loadAzl(); })
      .catch((err) => showToast(err.message || t("err.archivingImpossible", "Archivage impossible.")));
  };

  const openAzlExternal = () => {
    setAzlExternalForm({ start: "", end: "", reason: "" });
    setAzlExternalSelected(null); setAzlExternalSearch(""); setAzlExternalResults([]); setAzlExternalError("");
    setSheet("azlExternal");
    loadAzlExternalList();
  };
  const loadAzlExternalList = () => {
    apiJSON(`/api/arbeitszeitliste/temp-assignments/${encodeURIComponent(azlStoreId)}`)
      .then((data) => setAzlExternalList(data.assignments || []))
      .catch(() => setAzlExternalList([]));
  };
  const scheduleAzlExternalSearch = (q) => {
    setAzlExternalSearch(q);
    setAzlExternalSelected(null);
    clearTimeout(azlExternalSearchTimer.current);
    if (!q.trim()) { setAzlExternalResults([]); return; }
    azlExternalSearchTimer.current = setTimeout(() => {
      apiJSON(`/api/arbeitszeitliste/employees-search?excludeStoreId=${encodeURIComponent(azlStoreId)}&q=${encodeURIComponent(q.trim())}`)
        .then((data) => setAzlExternalResults(data.employees || []))
        .catch(() => setAzlExternalResults([]));
    }, 250);
  };
  const submitAzlExternal = () => {
    setAzlExternalError("");
    if (!azlExternalSelected) { setAzlExternalError(t("arbeitszeitliste.external.errorNoEmployee", "Sélectionnez un employé dans la liste.")); return; }
    if (!azlExternalForm.start) { setAzlExternalError(t("arbeitszeitliste.external.errorNoStart", "La date de début est obligatoire.")); return; }
    apiJSON("/api/arbeitszeitliste/temp-assignments", {
      method: "POST",
      body: JSON.stringify({
        employeeId: azlExternalSelected.employeeId, targetBranchId: azlStoreId,
        startDate: azlExternalForm.start, endDate: azlExternalForm.end, reason: azlExternalForm.reason,
      }),
    })
      .then(() => {
        setAzlExternalForm({ start: "", end: "", reason: "" });
        setAzlExternalSelected(null); setAzlExternalSearch(""); setAzlExternalResults([]);
        loadAzlExternalList(); loadAzl();
        showToast(`✓ ${t("arbeitszeitliste.external.addedToast", "Affectation ajoutée")}`);
      })
      .catch((err) => setAzlExternalError(err.message || t("arbeitszeitliste.external.createError", "Impossible de créer l'affectation.")));
  };
  const deleteAzlExternal = (id) => {
    if (!window.confirm(t("arbeitszeitliste.external.confirmDelete", "Supprimer cette affectation ?"))) return;
    apiJSON(`/api/arbeitszeitliste/temp-assignments/${encodeURIComponent(id)}`, { method: "DELETE" })
      .then(() => { loadAzlExternalList(); loadAzl(); })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };

  const openAzlHistory = () => {
    setAzlReopenReason(""); setAzlReopenError("");
    setSheet("azlHistory");
    apiJSON(`/api/arbeitszeitliste/${encodeURIComponent(azlStoreId)}/${azlYear}/${azlMonth}/versions`)
      .then((data) => setAzlHistoryVersions(data.versions || []))
      .catch(() => setAzlHistoryVersions([]));
  };
  const submitAzlReopen = () => {
    const reason = azlReopenReason.trim();
    if (!reason) { setAzlReopenError(t("arbeitszeitliste.history.reasonRequired", "Le motif est obligatoire.")); return; }
    if (!window.confirm(t("arbeitszeitliste.history.reopenConfirm", "Rouvrir ce document ? Les 3 signatures actuelles resteront visibles dans l'historique, mais un nouveau cycle de validation sera nécessaire."))) return;
    setAzlReopenBusy(true);
    apiJSON(`/api/arbeitszeitliste/${encodeURIComponent(azlStoreId)}/${azlYear}/${azlMonth}/reopen`, {
      method: "POST", body: JSON.stringify({ reason }),
    })
      .then(() => { setSheet(null); loadAzl(); showToast(`✓ ${t("arbeitszeitliste.history.reopenedToast", "Document rouvert")}`); })
      .catch((err) => setAzlReopenError(err.message || t("arbeitszeitliste.history.reopenError", "Réouverture impossible.")))
      .finally(() => setAzlReopenBusy(false));
  };

  /* Pilotage réseau — chargement réel. Un seul essai suivi d'un abandon immédiat affichait
     "Hors-ligne : aucune donnée en cache" (fallback du service worker, voir
     service-worker.js staleWhileRevalidate) même en étant réellement connecté — cette
     route agrège tout le réseau (potentiellement 100+ filiales pour un NEXORA_ADMIN) sans
     cache serveur, un premier appel un peu lent (ex. juste après un redémarrage) suffisait
     à dépasser le délai de 8s du service worker (retour utilisateur explicite, 2026-09-01 :
     "je reçois souvent ce type d'erreur à l'ouverture"). Un seul re-essai automatique après
     1.5s avant d'afficher l'erreur — pas de boucle infinie, juste absorber les lenteurs
     ponctuelles transitoires. */
  const loadManagerOverview = (isRetry) => {
    if (!isRetry) setManagerOverviewError("");
    apiJSON("/api/manager/overview")
      .then((data) => { setManagerOverview(data); setManagerOverviewError(""); })
      .catch((err) => {
        if (!isRetry) { setTimeout(() => loadManagerOverview(true), 1500); return; }
        setManagerOverviewError(err.message || "Impossible de charger le pilotage réseau."); setManagerOverview(null);
      });
  };
  const loadStoresRegistry = () => {
    apiJSON("/api/stores").then((data) => setStoresRegistry(data.stores || [])).catch(() => {});
  };
  const loadBezirkeRegistry = () => {
    apiJSON("/api/bezirke").then((data) => setBezirkeRegistry(data.bezirke || [])).catch(() => {});
  };
  const storeLabelFor = (storeId) => storesRegistry.find((s) => s.id === storeId)?.label || storeId;
  // Périmètre EFFECTIF (hérité) d'un compte réseau, calculé côté client depuis adminUsers
  // déjà chargé — miroir du BFS descendant côté serveur (sfRequestIdentity, server.js).
  // Un Bereichsleiter a son propre champ `stores` ; un Verkaufsleiter/Niederlassungsleiter
  // n'a typiquement AUCUNE filiale propre, seulement l'union de ce que ses subordonnés
  // directs possèdent (récursif : un NL hérite de ses VKL, qui héritent eux-mêmes de leurs
  // BL). Sert à regrouper une liste de filiales par propriétaire réel (BL pour un VKL, VKL
  // pour un NL) au lieu d'une liste plate — voir Pilotage réseau > Filiales et > Équipe.
  const effectiveStoresFor = (u) => {
    if (u.role === "Bereichsleiter") return (u.stores && u.stores.length ? u.stores : [u.storeId]).filter(Boolean);
    const children = adminUsers.filter((c) => c.reportsTo === u.employeeId);
    if (!children.length) return (u.stores && u.stores.length ? u.stores : [u.storeId]).filter(Boolean);
    return [...new Set(children.flatMap(effectiveStoresFor))];
  };
  // "Filiale {id}" au singulier affichait toujours user?.store SEUL, même pour un GR
  // multi-filiales (bug réel signalé : un Bereichsleiter restait affiché avec sa
  // seule filiale d'origine malgré plusieurs filiales réelles) — jamais silencieux,
  // toutes les filiales du périmètre courant s'affichent désormais. Recalculé à chaque
  // rendu depuis `user?.stores` (tenu à jour en direct, voir le poll 15s ci-dessous) — pas
  // besoin d'un état séparé.
  // Plafonné à MY_STORES_LABEL_CAP (retour utilisateur 2026-08-23, capture d'écran à
  // l'appui : "Profil NL trop long" — un Niederlassungsleiter avec une centaine de filiales, envahissait tout l'écran
  // Plus/Ma fiche en une seule liste à virgules) — au-delà, "+N autres" plutôt que tout
  // écrire, jamais un simple troncage silencieux du COMPTE (n reste le vrai total).
  const MY_STORES_LABEL_CAP = 4;
  const myStoresLabel = (user?.stores && user?.stores.length > 1)
    ? (() => {
        const shown = user?.stores.slice(0, MY_STORES_LABEL_CAP).map((id) => storeLabelFor(id)).join(", ");
        const remaining = user?.stores.length - MY_STORES_LABEL_CAP;
        return remaining > 0
          ? t("home.storesLabelCappedTemplate", { list: shown, n: remaining }, `Filiales ${shown} +${remaining} autre${remaining > 1 ? "s" : ""}`)
          : t("home.storesLabelTemplate", { list: shown }, `Filiales ${shown}`);
      })()
    : t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`);
  /* Missions réseau — vraies routes /api/missions (server/missions/*). */
  const loadMissions = () => {
    apiJSON("/api/missions").then((data) => setNetworkMissions(data.missions || [])).catch(() => {});
  };
  // Employés réels des filiales déjà sélectionnées — recalculé à chaque changement de
  // filiales pour que le sélecteur "Employé spécifique" ne propose jamais quelqu'un hors
  // périmètre. Vide silencieusement si aucune filiale choisie (pas d'erreur affichée).
  const loadMissionEmployees = (storeIds) => {
    if (!storeIds || !storeIds.length) { setMissionEmployeeOptions([]); return; }
    apiJSON(`/api/missions/employees?storeIds=${storeIds.map(encodeURIComponent).join(",")}`)
      .then((data) => setMissionEmployeeOptions(data.employees || []))
      .catch(() => setMissionEmployeeOptions([]));
  };
  const openMissionNew = (presetMode) => {
    setMissionForm(presetMode ? { ...emptyMissionForm, targetMode: presetMode } : emptyMissionForm);
    setMissionEditId(null); setMissionEmployeeOptions([]); setSheet("missionForm");
  };
  // Partager un Rundschreiben publié via la messagerie interne (2026-09-12, retour
  // utilisateur explicite : "un grand responsable peut l'enregistrer ou le partager via la
  // messagerie interne... pour ses filiales ou à qui il veut" — il ne rédige jamais le
  // contenu, il relaie le PDF déjà reçu). Type "multi-branch" par défaut (jamais "private"
  // ni "group") : seul type qui permet de choisir librement des FILIALES ET des personnes
  // dans la même conversation, exactement le "à qui il veut" demandé. Le vrai envoi du PDF a
  // lieu dans finalizeInfoShareInConversation, une fois la conversation créée.
  const shareInfoViaMessage = (info, version) => {
    setNewConvType("multi-branch"); setNewConvMembers([]); setNewConvTitle(info.title); setNewConvStores([]); setNewConvQuery("");
    setPendingInfoShare({ infoId: info.id, title: info.title, attachment: (version?.attachments || [])[0] || null });
    setMsgScreen("new");
    setScreen("messages");
  };
  // Pont Rundschreiben → mission (2026-08-31, retour utilisateur explicite : "un pont entre
  // la Niederlassung et les filiales") — AUCUNE extraction automatique du PDF, juste un
  // raccourci + un lien de traçabilité : le GR vient de LIRE le Rundschreiben et sait déjà
  // ce qu'il veut en faire, il tape/choisit tout lui-même comme d'habitude. Ouvrable depuis
  // n'importe quel écran (Sheet global, voir <Sheet open={sheet === "missionForm"}>).
  const openMissionFromRundschreiben = (info) => {
    setMissionForm({
      ...emptyMissionForm, targetMode: "filiales",
      title: t("rundschreiben.mission.titlePrefixTemplate", { title: info.title }, `Rundschreiben — ${info.title}`),
      relatedRundschreibenId: info.id, relatedRundschreibenTitle: info.title,
    });
    setMissionEditId(null); setMissionEmployeeOptions([]); setSheet("missionForm");
  };
  const openMissionEdit = (m) => {
    setMissionForm({
      title: m.title, filiales: [...m.filiales], priority: m.priority || "normal",
      recipientMode: m.recipientMode || "team", specificEmployeeIds: Array.isArray(m.specificEmployeeIds) ? [...m.specificEmployeeIds] : [],
      destinataires: m.destinataires, echeanceDate: m.echeanceDate || "", echeanceTime: m.echeanceTime || "",
      sourceType: m.sourceType === "initiative" ? "initiative" : "rundschreiben",
      echeanceSyntheseDate: m.echeanceSyntheseDate || "", echeanceSyntheseTime: m.echeanceSyntheseTime || "",
      preuveRequise: m.preuveRequise, note: m.note,
      targetLeaderIds: Array.isArray(m.targetLeaderIds) ? [...m.targetLeaderIds] : [],
      targetMode: Array.isArray(m.targetLeaderIds) && m.targetLeaderIds.length ? "leader" : "filiales",
      relatedRundschreibenId: m.relatedRundschreibenId || "", relatedRundschreibenTitle: m.relatedRundschreibenTitle || "",
    });
    setMissionEditId(m.id);
    loadMissionEmployees(m.filiales);
    setSheet("missionForm");
  };
  const openMissionDetail = (id) => {
    setMissionDetailId(id); setMissionReplyStoreId(null); setMissionThreadDraft("");
    setMissionReplyLeaderId(null); setLeaderThreadDraft(""); setLeaderThreadType("question");
    setLeaderThreadPhotoFile(null); setLeaderThreadPhotoPreview("");
    setSheet("missionDetail");
    markMissionRead(id);
  };
  const toggleMissionFiliale = (storeId) => setMissionForm((f) => {
    const filiales = f.filiales.includes(storeId) ? f.filiales.filter((x) => x !== storeId) : [...f.filiales, storeId];
    loadMissionEmployees(filiales);
    return { ...f, filiales };
  });
  const createMission = () => {
    if (!missionForm.title.trim()) { showToast(t("toast.giveTitleToMission", "Donne un titre à la mission.")); return; }
    if (missionForm.targetMode === "leader") {
      if (missionForm.targetLeaderIds.length === 0) { showToast(t("toast.chooseAtLeastOneLeader", "Choisis au moins un responsable.")); return; }
    } else {
      if (missionForm.filiales.length === 0) { showToast(t("toast.chooseAtLeastOneStore", "Choisis au moins une filiale.")); return; }
      if (missionForm.recipientMode === "specific" && missionForm.specificEmployeeIds.length === 0) { showToast(t("toast.chooseAtLeastOneRecipient", "Choisis au moins un employé destinataire.")); return; }
    }
    const isEdit = Boolean(missionEditId);
    const req = isEdit
      ? apiJSON(`/api/missions/${encodeURIComponent(missionEditId)}`, { method: "PATCH", body: JSON.stringify(missionForm) })
      : apiJSON("/api/missions", { method: "POST", body: JSON.stringify(missionForm) });
    req
      .then(() => { setSheet(null); setMissionEditId(null); loadMissions(); showToast(isEdit ? t("toast.missionUpdated", "✓ Mission modifiée") : t("toast.missionSent", "🎯 Mission envoyée")); })
      .catch((err) => showToast(err.message || t("err.missionSaveImpossible", "Impossible d'enregistrer la mission.")));
  };
  // Ciblage direct d'un responsable (retour utilisateur 2026-08-24) — le responsable ciblé
  // marque lui-même sa mission accomplie, POST /:id/submit-leader (miroir de
  // /:id/submit pour une filiale, voir server/missions/routes.js).
  const submitLeaderMission = (id) => {
    apiJSON(`/api/missions/${encodeURIComponent(id)}/submit-leader`, { method: "POST" })
      .then(() => { loadMissions(); showToast(t("toast.missionDone", "✓ Mission marquée comme faite")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const relanceMission = (id) => {
    apiJSON(`/api/missions/${encodeURIComponent(id)}/relance`, { method: "POST" })
      .then(() => { loadMissions(); showToast(t("toast.recipientsReminded", "🔔 Destinataires relancés")); })
      .catch((err) => showToast(err.message || t("err.reminderPingImpossible", "Relance impossible.")));
  };
  const archiveMission = (id, archived) => {
    apiJSON(`/api/missions/${encodeURIComponent(id)}/archive`, { method: "POST", body: JSON.stringify({ archived }) })
      .then(() => { loadMissions(); showToast(archived ? t("toast.missionArchived", "🗄️ Mission archivée") : t("toast.missionRestored", "↩️ Mission restaurée")); })
      .catch((err) => showToast(err.message || t("err.archivingImpossible", "Archivage impossible.")));
  };
  const deleteMission = (m) => {
    if (!window.confirm(t("confirm.deleteMissionTemplate", { title: m.title }, `Supprimer définitivement la mission "${m.title}" ?`))) return;
    apiJSON(`/api/missions/${encodeURIComponent(m.id)}`, { method: "DELETE" })
      .then(() => {
        loadMissions();
        if (missionDetailId === m.id) { setSheet(null); setMissionDetailId(null); }
        showToast(t("toast.missionDeleted", "🗑️ Mission supprimée"));
      })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  // Retrait personnel de la vue GR (retour utilisateur explicite, 2026-08-24 : "chaque
  // destinataire peut retirer SA PROPRE copie") — jamais le créateur (qui a déjà Supprimer/
  // Archiver), réservé à quiconque voit la mission sans la gérer, une fois SA part
  // terminée. N'affecte jamais la mission des autres ni du créateur.
  const dismissMission = (m) => {
    apiJSON(`/api/missions/${encodeURIComponent(m.id)}/dismiss`, { method: "POST" })
      .then(() => { loadMissions(); showToast(t("toast.missionDismissed", "🗑️ Retirée de ta liste")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const openMissionShare = (m) => {
    setMissionShareId(m.id);
    setMissionShareDraft(Array.isArray(m.sharedWith) ? [...m.sharedWith] : []);
    setSheet("missionShare");
  };
  const toggleMissionShareId = (employeeId) => setMissionShareDraft((list) =>
    list.includes(employeeId) ? list.filter((id) => id !== employeeId) : [...list, employeeId]);
  const saveMissionShare = () => {
    if (!missionShareId) return;
    setMissionShareBusy(true);
    apiJSON(`/api/missions/${encodeURIComponent(missionShareId)}/share`, { method: "PATCH", body: JSON.stringify({ employeeIds: missionShareDraft }) })
      .then(() => { loadMissions(); setSheet("missionDetail"); showToast(t("toast.missionShareSaved", "✓ Partage mis à jour")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setMissionShareBusy(false));
  };
  const openCreateLeader = () => {
    setCreateLeaderForm({ firstname: "", lastname: "", gender: "", storeIds: [] });
    setSheet("createLeader");
  };
  const toggleCreateLeaderStore = (storeId) => setCreateLeaderForm((f) => ({
    ...f, storeIds: f.storeIds.includes(storeId) ? f.storeIds.filter((id) => id !== storeId) : [...f.storeIds, storeId],
  }));
  const toggleLeaderDisabled = (leader) => {
    const nextDisabled = !leader.disabled;
    const name = `${leader.firstname} ${leader.lastname}`;
    if (!window.confirm(nextDisabled
      ? t("confirm.disableLeaderTemplate", { name }, `Désactiver le compte de ${name} ? Il ne pourra plus se connecter.`)
      : t("confirm.enableLeaderTemplate", { name }, `Réactiver le compte de ${name} ?`))) return;
    apiJSON(`/api/leaders/${encodeURIComponent(leader.id)}/disable`, { method: "PATCH", body: JSON.stringify({ disabled: nextDisabled }) })
      .then(() => { loadAdminUsers(); showToast(nextDisabled ? t("toast.accountDisabled", "Compte désactivé") : t("toast.accountReactivated", "✓ Compte réactivé")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const openReassignStore = (storeId, fromEmployeeId) => { setReassignStoreDraft({ storeId, fromEmployeeId }); setSheet("reassignStore"); };
  const submitReassignStore = (toEmployeeId) => {
    if (!reassignStoreDraft) return;
    setReassignStoreBusy(true);
    apiJSON("/api/leaders/reassign-store", { method: "PATCH", body: JSON.stringify({ storeId: reassignStoreDraft.storeId, toEmployeeId }) })
      .then(() => { loadAdminUsers(); setSheet(null); setReassignStoreDraft(null); showToast(t("toast.storeReassigned", "✓ Filiale déplacée")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setReassignStoreBusy(false));
  };
  const submitCreateLeader = () => {
    if (!createLeaderForm.firstname.trim() || !createLeaderForm.lastname.trim()) { showToast(t("toast.giveFirstLastName", "Donne un prénom et un nom.")); return; }
    if (!createLeaderForm.storeIds.length) { showToast(t("toast.chooseAtLeastOneStore", "Choisis au moins une filiale.")); return; }
    setCreateLeaderBusy(true);
    apiJSON("/api/leaders", { method: "POST", body: JSON.stringify(createLeaderForm) })
      .then((data) => {
        loadAdminUsers();
        setCreateLeaderResult({ employeeId: data.employeeId, tempPassword: data.tempPassword, name: `${createLeaderForm.firstname} ${createLeaderForm.lastname}` });
        setSheet("createLeaderResult");
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setCreateLeaderBusy(false));
  };
  const deleteAllArchivedMissions = () => {
    if (!window.confirm(t("confirm.deleteAllArchivedMissions", "Supprimer définitivement TOUTES les missions archivées ?"))) return;
    apiJSON("/api/missions/archived/all", { method: "DELETE" })
      .then((data) => { loadMissions(); showToast(t("toast.missionsDeletedTemplate", { n: data.count || 0 }, `🗑️ ${data.count || 0} mission${(data.count || 0) > 1 ? "s" : ""} supprimée${(data.count || 0) > 1 ? "s" : ""}`)); })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  // Réponse du GR à UNE filiale précise (missionReplyStoreId choisi dans la fiche détail —
  // un dialogue par filiale, jamais un fourre-tout commun à toute la mission).
  const postMissionReply = () => {
    if (!missionThreadDraft.trim() || !missionDetailId || !missionReplyStoreId) return;
    apiJSON(`/api/missions/${encodeURIComponent(missionDetailId)}/thread`, {
      method: "POST", body: JSON.stringify({ storeId: missionReplyStoreId, text: missionThreadDraft.trim() })
    })
      .then(() => { setMissionThreadDraft(""); setMissionReplyStoreId(null); loadMissions(); showToast(t("toast.replySent", "✓ Réponse envoyée")); })
      .catch((err) => showToast(err.message || t("err.sendingImpossible", "Envoi impossible.")));
  };
  // Équivalent de postMissionReply pour une mission ciblée sur des responsables — le
  // créateur répond à UN responsable ciblé précis (missionReplyLeaderId), jamais un
  // fourre-tout partagé entre co-cibles.
  const postMissionLeaderReply = () => {
    if (!missionThreadDraft.trim() || !missionDetailId || !missionReplyLeaderId) return;
    apiJSON(`/api/missions/${encodeURIComponent(missionDetailId)}/thread`, {
      method: "POST", body: JSON.stringify({ leaderId: missionReplyLeaderId, text: missionThreadDraft.trim() })
    })
      .then(() => { setMissionThreadDraft(""); setMissionReplyLeaderId(null); loadMissions(); showToast(t("toast.replySent", "✓ Réponse envoyée")); })
      .catch((err) => showToast(err.message || t("err.sendingImpossible", "Envoi impossible.")));
  };
  // Le responsable ciblé lui-même pose sa question/anomalie sur SA part (jamais de leaderId
  // envoyé — le serveur résout l'identité et vérifie l'appartenance à targetLeaderIds, voir
  // POST /:id/thread). Même patron que postMyMissionThread côté compte normal, mais affiché
  // ici dans la fiche GR (missionDetail) puisqu'un responsable ciblé (VKL/BL) est toujours
  // un compte GR, jamais un compte normal.
  const pickLeaderThreadPhoto = (file) => {
    if (!file) return;
    setLeaderThreadPhotoFile(file);
    setLeaderThreadPhotoPreview(URL.createObjectURL(file));
  };
  const postLeaderThread = () => {
    if (!leaderThreadDraft.trim() || !missionDetailId) return;
    setLeaderThreadBusy(true);
    (leaderThreadPhotoFile ? uploadMissionPhoto(leaderThreadPhotoFile) : Promise.resolve(""))
      .then((photoUrl) => apiJSON(`/api/missions/${encodeURIComponent(missionDetailId)}/thread`, {
        method: "POST", body: JSON.stringify({ type: leaderThreadType, text: leaderThreadDraft.trim(), photoUrl })
      }))
      .then(() => {
        setLeaderThreadDraft(""); setLeaderThreadPhotoFile(null); setLeaderThreadPhotoPreview("");
        loadMissions(); showToast(leaderThreadType === "anomaly" ? "⚠️ Anomalie signalée" : "✓ Question envoyée");
      })
      .catch((err) => showToast(err.message || t("err.sendingImpossible", "Envoi impossible.")))
      .finally(() => setLeaderThreadBusy(false));
  };
  // Badge "non lu" (retour utilisateur explicite : "il faut des notifications et des badges
  // pour qu'on soit avertis à chaque fois") — marque cette mission comme lue POUR MOI dès
  // l'ouverture de sa fiche détail, côté GR comme côté compte normal (voir les deux appels
  // ci-dessous). Best-effort, jamais bloquant pour l'ouverture de la fiche elle-même.
  const markMissionRead = (id, { forEmployee = false } = {}) => {
    if (!id) return;
    apiJSON(`/api/missions/${encodeURIComponent(id)}/mark-read`, { method: "POST" })
      .then(() => (forEmployee ? loadMyMissions() : loadMissions()))
      .catch(() => {});
  };

  /* Pont côté compte normal — réception des missions, "accomplie", dialogue (question /
     anomalie), voir server/missions/routes.js GET /mine + POST /:id/thread. */
  const loadMyMissions = () => {
    apiJSON("/api/missions/mine").then((data) => setMyNetworkMissions(data.missions || [])).catch(() => {});
  };
  const openMyMissionDetail = (id) => {
    setMyMissionDetailId(id); setMyMissionThreadDraft(""); setMyMissionThreadType("question");
    setMyMissionProofFile(null); setMyMissionProofPreview(""); setMyMissionThreadPhotoFile(null); setMyMissionThreadPhotoPreview("");
    setSheet("myMissionDetail");
    markMissionRead(id, { forEmployee: true });
  };
  // Upload réel disque (POST /api/missions/photos, même patron que Claims) — jamais de
  // base64 stocké tel quel. Compression avant envoi (compressImageFile, même lib que
  // Réclamations/Centre documentaire) : une photo prise directement à l'appareil peut peser
  // plusieurs Mo.
  const uploadMissionPhoto = async (file) => {
    const compressed = await compressImageFile(file);
    const body = new FormData();
    body.append("photo", compressed);
    const data = await apiJSON("/api/missions/photos", { method: "POST", body });
    return data.url;
  };
  const pickMyMissionProofPhoto = (file) => {
    if (!file) return;
    setMyMissionProofFile(file);
    setMyMissionProofPreview(URL.createObjectURL(file));
  };
  const pickMyMissionThreadPhoto = (file) => {
    if (!file) return;
    setMyMissionThreadPhotoFile(file);
    setMyMissionThreadPhotoPreview(URL.createObjectURL(file));
  };
  const completeMyMission = (missionId, preuveRequise) => {
    if (preuveRequise && !myMissionProofFile) { showToast(t("toast.proofRequiredBeforeValidate", "Preuve requise — prends une photo avant de valider.")); return; }
    setMyMissionPhotoBusy(true);
    (myMissionProofFile ? uploadMissionPhoto(myMissionProofFile) : Promise.resolve(""))
      .then((photoUrl) => apiJSON(`/api/missions/${encodeURIComponent(missionId)}/submit`, {
        method: "POST", body: JSON.stringify({ photoUrl })
      }))
      .then(() => {
        setMyMissionProofFile(null); setMyMissionProofPreview("");
        loadMyMissions(); showToast(t("toast.missionAccomplished", "✅ Mission accomplie"));
      })
      .catch((err) => showToast(err.message || t("err.missionCompleteImpossible", "Impossible de marquer la mission accomplie.")))
      .finally(() => setMyMissionPhotoBusy(false));
  };
  const postMyMissionThread = () => {
    if (!myMissionThreadDraft.trim() || !myMissionDetailId) return;
    setMyMissionPhotoBusy(true);
    (myMissionThreadPhotoFile ? uploadMissionPhoto(myMissionThreadPhotoFile) : Promise.resolve(""))
      .then((photoUrl) => apiJSON(`/api/missions/${encodeURIComponent(myMissionDetailId)}/thread`, {
        method: "POST", body: JSON.stringify({ type: myMissionThreadType, text: myMissionThreadDraft.trim(), photoUrl })
      }))
      .then(() => { setMyMissionThreadDraft(""); setMyMissionThreadPhotoFile(null); setMyMissionThreadPhotoPreview(""); loadMyMissions(); showToast(myMissionThreadType === "anomaly" ? "⚠️ Anomalie signalée" : "✓ Question envoyée"); })
      .catch((err) => showToast(err.message || t("err.sendingImpossible", "Envoi impossible.")))
      .finally(() => setMyMissionPhotoBusy(false));
  };
  // Archivage/suppression côté EMPLOYÉ — retour utilisateur explicite, 2026-08-02 : "le
  // travailleur doit avoir la possibilite de supprimer, archivee ou tout supprimer". Scopé
  // à SA seule filiale côté serveur (POST /:id/employee-archive, DELETE /:id/mine) — ne
  // touche jamais la mission pour le GR ni les autres filiales.
  const archiveMyMission = (id, archived) => {
    apiJSON(`/api/missions/${encodeURIComponent(id)}/employee-archive`, { method: "POST", body: JSON.stringify({ archived }) })
      .then(() => { loadMyMissions(); showToast(archived ? "🗄️ Mission archivée" : "↩️ Mission restaurée"); })
      .catch((err) => showToast(err.message || t("err.archivingImpossible", "Archivage impossible.")));
  };
  const deleteMyMission = (m) => {
    if (!window.confirm(t("missions.confirmDeleteFromMineTemplate", { title: m.title }, `Supprimer "${m.title}" de tes missions ?`))) return;
    apiJSON(`/api/missions/${encodeURIComponent(m.id)}/mine`, { method: "DELETE" })
      .then(() => { loadMyMissions(); showToast(t("toast.missionDeleted", "🗑️ Mission supprimée")); })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  const deleteAllMyArchivedMissions = () => {
    if (!window.confirm(t("confirm.deleteAllMyArchivedMissions", "Supprimer définitivement TOUTES tes missions archivées ?"))) return;
    apiJSON("/api/missions/mine/archived/all", { method: "DELETE" })
      .then((data) => { loadMyMissions(); showToast(t("toast.missionsDeletedTemplate", { n: data.count || 0 }, `🗑️ ${data.count || 0} mission${(data.count || 0) > 1 ? "s" : ""} supprimée${(data.count || 0) > 1 ? "s" : ""}`)); })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };

  /* Alertes — actions réelles, persistées côté serveur (POST /api/manager/alerts/:key/
     resolve|assign, retour utilisateur explicite 2026-08-02). alertKeyFor doit rester
     identique à la clé calculée côté serveur (storeId:type) pour matcher f.key. */
  const alertKeyFor = (f) => `${f.storeId}:${f.type}`;
  const assignAlert = (f, assigned = true) => {
    apiJSON(`/api/manager/alerts/${encodeURIComponent(alertKeyFor(f))}/assign`, {
      method: "POST", body: JSON.stringify({ assigned })
    })
      .then(() => { loadManagerOverview(); showToast(assigned ? "👤 Alerte attribuée" : "Attribution retirée"); })
      .catch((err) => showToast(err.message || t("err.attributionImpossible", "Attribution impossible.")));
  };
  const resolveAlert = (f, resolved = true) => {
    apiJSON(`/api/manager/alerts/${encodeURIComponent(alertKeyFor(f))}/resolve`, {
      method: "POST", body: JSON.stringify({ resolved })
    })
      .then(() => { loadManagerOverview(); setAlertActionId(null); showToast(resolved ? "✓ Alerte clôturée" : "↩️ Alerte rouverte"); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const alertToMission = (f) => {
    // Bug réel trouvé, retour utilisateur explicite 2026-08-02 : "le bouton transformer
    // en mission... envoi sur une page d'erreur" — setMissionScreen("list") appelait un
    // état jamais défini nulle part (ReferenceError au runtime). L'écran missions n'a
    // plus de sous-navigation séparée depuis sa conversion en vrai screen (voir
    // [[storeflow_missions_photo_lightbox_dialogue_hide_employee_archive]]) : screen +
    // sheet suffisent, plus besoin de ce 3ᵉ état.
    setMissionForm({ ...emptyMissionForm, title: f.title, filiales: [f.storeId], preuveRequise: true, note: "Créée depuis une alerte du Pilotage réseau." });
    setMissionEditId(null);
    setAlertActionId(null);
    setPilotScreen("missions"); setSheet("missionForm");
  };
  // Vue VKL — onglet Équipe (2026-08-16, retour utilisateur "il doit tout au moins hériter
  // de tout ce que possède un BL et plus que ça") : créer une mission pré-remplie avec
  // TOUTES les filiales d'un BL en un clic, même patron que alertToMission ci-dessus.
  const createTeamMission = (bl) => {
    setMissionForm({
      ...emptyMissionForm,
      title: t("pilotage.team.missionTitleTemplate", { name: `${bl.firstname} ${bl.lastname}` }, `Mission équipe — ${bl.firstname} ${bl.lastname}`),
      filiales: (bl.stores && bl.stores.length ? bl.stores : [bl.storeId]).filter(Boolean),
      preuveRequise: true,
      note: t("pilotage.team.missionNoteText", "Créée depuis Pilotage réseau > Équipe."),
    });
    setMissionEditId(null);
    setPilotScreen("missions"); setSheet("missionForm");
  };

  // Bornes réelles de la période choisie — un seul point de vérité pour les 3 sources
  // (Ist, Plan, pertes), jamais un calcul différent par écran. `months` liste les vraies
  // requêtes storeId+year+month à faire (GET /api/reporting/daily-entries/stunden-plan ne
  // filtrent pas par plage de dates arbitraire) ; startISO/endISO bornent ensuite chaque
  // jour reçu, y compris pour ne jamais compter un jour futur du mois/trimestre/année en
  // cours (un plan déjà saisi pour un jour pas encore vécu ne doit jamais compter comme un
  // objectif "manqué").
  // `offset` (0 par défaut = période en cours, -1 = précédente, -2 = encore avant...)
  // décale la date de référence AVANT de calculer les bornes — demande utilisatrice
  // (2026-08-15) de pouvoir naviguer Jour/Semaine/Mois/Trimestre/Année dans le passé.
  // Différence clé avec avant : `endISO` n'est clampé à `todayISO()` QUE pour la période
  // EN COURS (offset===0) — une période passée doit toujours renvoyer sa vraie date de fin
  // naturelle (dernier jour du mois/trimestre/année, dimanche de la semaine), jamais la
  // date du jour réel qui n'a aucun sens pour un mois déjà terminé.
  const pilotPeriodRange = (period, offset = 0) => {
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const today = todayISO();

    if (period === "jour") {
      const d = new Date(); d.setDate(d.getDate() + offset);
      const dISO = iso(d);
      return { startISO: dISO, endISO: dISO, months: [{ year: d.getFullYear(), month: d.getMonth() + 1 }] };
    }
    if (period === "semaine") {
      const now = new Date(); now.setDate(now.getDate() + offset * 7);
      const dow = now.getDay() || 7;
      const monday = new Date(now); monday.setDate(now.getDate() - dow + 1);
      const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
      const monthsMap = new Map();
      [monday, sunday].forEach((d) => monthsMap.set(`${d.getFullYear()}-${d.getMonth() + 1}`, { year: d.getFullYear(), month: d.getMonth() + 1 }));
      const naturalEnd = iso(sunday);
      return { startISO: iso(monday), endISO: offset === 0 && naturalEnd > today ? today : naturalEnd, months: [...monthsMap.values()] };
    }
    if (period === "mois") {
      // setDate(1) AVANT setMonth : sinon un jour-du-mois qui n'existe pas dans le mois
      // cible (ex. "31" en décalant de mai vers avril) fait déborder setMonth au mois
      // suivant — piège classique des Date JS, jamais un `now.setMonth()` seul ici.
      const now = new Date(); now.setDate(1); now.setMonth(now.getMonth() + offset);
      const y = now.getFullYear(), m = now.getMonth();
      const naturalEnd = iso(new Date(y, m + 1, 0));
      return { startISO: `${y}-${String(m + 1).padStart(2, "0")}-01`, endISO: offset === 0 && naturalEnd > today ? today : naturalEnd, months: [{ year: y, month: m + 1 }] };
    }
    if (period === "trimestre") {
      const now = new Date(); now.setDate(1); now.setMonth(now.getMonth() + offset * 3);
      const y = now.getFullYear();
      const firstMonth = Math.floor(now.getMonth() / 3) * 3 + 1;
      const months = (offset === 0 ? [0, 1, 2].filter((i) => firstMonth + i <= new Date().getMonth() + 1) : [0, 1, 2]).map((i) => ({ year: y, month: firstMonth + i }));
      const naturalEnd = iso(new Date(y, firstMonth + 2, 0));
      return { startISO: `${y}-${String(firstMonth).padStart(2, "0")}-01`, endISO: offset === 0 && naturalEnd > today ? today : naturalEnd, months };
    }
    // année (branche implicite finale, même convention que le code d'origine)
    const now = new Date(); now.setFullYear(now.getFullYear() + offset);
    const y = now.getFullYear();
    const monthCount = offset === 0 ? new Date().getMonth() + 1 : 12;
    const months = Array.from({ length: monthCount }, (_, i) => ({ year: y, month: i + 1 }));
    return { startISO: `${y}-01-01`, endISO: offset === 0 ? today : `${y}-12-31`, months };
  };

  // Agrège les vraies données Reporting (CA réel/planifié, pertes) par vraie filiale du
  // périmètre — une boucle par filiale, seule façon de faire tant qu'aucun endpoint réseau
  // consolidé n'existe côté serveur pour ce domaine. Se réévalue à chaque changement de
  // période ou de périmètre de filiales.
  // Cœur d'agrégation Reporting par filiale — extrait de loadPilotStoreStats (retour
  // utilisateur 2026-08-15, fiche Performance FL/FLA/BL/VL/NL) pour être appelable avec un
  // period/offset explicite (ex. "semaine" + "mois" en parallèle pour Ma Fiche) SANS toucher
  // au state pilotPeriod/periodOffset de l'écran Pilotage réseau — jamais de clignotement de
  // la période affichée là-bas pendant qu'un autre écran charge ses propres chiffres.
  // Retourne juste la Promise du résultat, aucun setState ici (chaque appelant décide où
  // écrire : pilotStoreStats pour Pilotage réseau, ficheWeekStats/ficheMonthStats pour Ma
  // Fiche) — logique de calcul strictement identique, aucun comportement changé.
  // Batch réseau (retour utilisateur 2026-08-23, incident réel "0 FILIALES"/timeout puis
  // Analyse & rapports qui ne charge plus du tout sur un NL à 100 filiales) — AVANT : un
  // appel par filiale × mois × famille (jusqu'à ~3 300 requêtes en parallèle pour 100
  // filiales/8 mois, invivable sur connexion mobile faible, voir screenshot "2 barres" —
  // et chaque appel relisait toute la table côté serveur). MAINTENANT : un seul appel par
  // mois/année couvrant TOUTES les filiales du périmètre (server.js, GET
  // /api/reporting/daily-entries/network et /api/reporting/stunden-plan/network), le
  // filtrage par famille qui se faisait côté serveur (family=...) se fait ici — logique de
  // calcul strictement identique, seule la façon d'obtenir les données change.
  const fetchStoreStatsFor = (stores, period, offset) => {
    if (!stores || !stores.length) return Promise.resolve({});
    const range = pilotPeriodRange(period, offset);
    const years = [...new Set(range.months.map((m) => m.year))];
    const storeIdsParam = stores.map(encodeURIComponent).join(",");

    return Promise.all([
      Promise.all(range.months.map((m) =>
        apiJSON(`/api/reporting/daily-entries/network?storeIds=${storeIdsParam}&year=${m.year}&month=${m.month}`).catch(() => [])
      )),
      Promise.all(years.map((y) =>
        apiJSON(`/api/reporting/stunden-plan/network?storeIds=${storeIdsParam}&year=${y}`).catch(() => [])
      )),
    ]).then(([entriesByMonth, plansByYear]) => {
      const entriesByStore = new Map();
      entriesByMonth.flat().forEach((e) => {
        if (!entriesByStore.has(e.storeId)) entriesByStore.set(e.storeId, []);
        entriesByStore.get(e.storeId).push(e);
      });
      const plansByStore = new Map();
      plansByYear.flat().forEach((rec) => {
        if (!rec) return;
        if (!plansByStore.has(rec.storeId)) plansByStore.set(rec.storeId, []);
        plansByStore.get(rec.storeId).push(rec);
      });

      return Object.fromEntries(stores.map((storeId) => {
        let umsatzIst = 0, stundenIst = 0, pertes = 0, umsatzPlan = 0, stundenPlan = 0;
        const pertesByCategory = {};
        // Bug réel trouvé à la vérification (2026-08-06) : prebake/frischbrot_brot ne
        // doivent JAMAIS être comptés deux fois (une fois via leur item manual_waste, une
        // fois via leur famille dédiée) — ici chaque entrée n'a qu'UNE SEULE family, la
        // structure même du if/else empêche le double-comptage (plus robuste que les deux
        // tableaux séparés d'avant).
        (entriesByStore.get(storeId) || []).forEach((e) => {
          if (!e.date || e.date < range.startISO || e.date > range.endISO) return;
          if (e.family === "stundenleistung") {
            (e.items || []).forEach((it) => { umsatzIst += Number(it.umsatzIst) || 0; stundenIst += Number(it.stundenIst) || 0; });
          } else if (e.family === "manual_waste") {
            (e.items || []).forEach((it) => {
              if (it.categoryId === "prebake" || it.categoryId === "frischbrot_brot") return;
              const amount = Number(it.amount) || 0;
              pertes += amount;
              if (it.categoryId) pertesByCategory[it.categoryId] = (pertesByCategory[it.categoryId] || 0) + amount;
            });
          } else if (e.family === "prebake" || e.family === "brot") {
            const categoryId = e.family === "prebake" ? "prebake" : "frischbrot_brot";
            const amount = Number(e.totalEuro) || 0;
            pertes += amount;
            pertesByCategory[categoryId] = (pertesByCategory[categoryId] || 0) + amount;
          }
        });
        (plansByStore.get(storeId) || []).forEach((rec) => {
          if (!rec || !rec.days) return;
          Object.entries(rec.days).forEach(([dateISO, d]) => {
            if (dateISO < range.startISO || dateISO > range.endISO) return;
            umsatzPlan += Number(d.umsatzPlan) || 0;
            stundenPlan += Number(d.stundenPlan) || 0;
          });
        });
        // stundenleistungPlan = vrai plan sur plan (jamais l'Ist), demande utilisatrice
        // 2026-08-15 "on se base toujours sur le plan Stundenleistung" — remplace le seuil
        // fixe STUNDENLEISTUNG_ALERT_THRESHOLD par la vraie cible de la filiale/période, null
        // si aucun plan d'heures connu (jamais une fausse cible à 0€/h).
        return [storeId, {
          umsatzIst, umsatzPlan, stundenIst, stundenPlan, pertes, pertesByCategory,
          stundenleistung: stundenIst > 0 ? umsatzIst / stundenIst : null,
          stundenleistungPlan: stundenPlan > 0 ? umsatzPlan / stundenPlan : null,
        }];
      }));
    });
  };

  const loadPilotStoreStats = () => {
    const stores = managerOverview?.stores || [];
    if (!stores.length) return;
    setPilotStatsLoading(true);
    fetchStoreStatsFor(stores, pilotPeriod, periodOffset)
      .then((stats) => setPilotStoreStats(stats))
      .finally(() => setPilotStatsLoading(false));
  };

  // Historique réel PILOT_HISTORY_WEEKS semaines (CA réel par semaine ISO, la plus
  // ancienne en premier) — alimente la courbe et la détection de tendance du rapport
  // narré, PLUS (depuis la demande Verkaufsleiter du 2026-08-14) l'évolution de la
  // Stundenleistung (dérivée des mêmes entrées stundenleistung, stundenIst n'était jamais
  // sommé jusqu'ici) et des Pertes (nouveau fetch manual_waste/prebake/brot par semaine,
  // même familles/garde anti-double-comptage que loadPilotStoreStats). Une requête par
  // filiale × semaine × famille (même limite que loadPilotStoreStats tant qu'aucun
  // endpoint réseau consolidé n'existe côté serveur).
  // `weekOffset` (0 par défaut) décale l'ancrage de la fenêtre de PILOT_HISTORY_WEEKS
  // semaines dans le passé — demande utilisatrice (2026-08-15) de pouvoir faire glisser
  // toute la fenêtre de l'onglet Évolution, pas seulement naviguer dedans (déjà possible
  // via le glisser-déposer sur les 3 graphes de Détail). setDate() gère nativement les
  // débordements de mois/année, aucun piège comme setMonth() (voir pilotPeriodRange).
  const loadPilotHistory = (weekOffset = 0) => {
    const stores = managerOverview?.stores || [];
    if (!stores.length) return;
    const now = new Date(); now.setDate(now.getDate() + weekOffset * 7);
    const weeks = Array.from({ length: PILOT_HISTORY_WEEKS }, (_, i) => {
      const d = new Date(now); d.setDate(d.getDate() - (PILOT_HISTORY_WEEKS - 1 - i) * 7);
      return { year: d.getFullYear(), week: isoWeek(d) };
    });
    // Objectif RÉEL par semaine (voir pilotHistoryPlan) — mêmes années que les semaines
    // couvertes (généralement 1, parfois 2 à cheval sur un 1er janvier).
    // Batch réseau (retour utilisateur 2026-08-23, même incident que fetchStoreStatsFor
    // ci-dessus : un NL à 100 filiales déclenchait ~5 000 requêtes — filiale × semaine ×
    // famille — pour cet onglet Évolution seul) — un seul appel par ANNÉE couvrant TOUTES
    // les filiales et TOUTES les familles (le filtrage semaine/famille qui se faisait côté
    // serveur se fait maintenant ici, via les champs week/year déjà présents sur chaque
    // entrée), au lieu d'un appel par filiale × semaine × famille.
    const planYears = [...new Set(weeks.map((w) => w.year))];
    Promise.all([
      Promise.all(planYears.map((y) =>
        apiJSON(`/api/reporting/daily-entries/network?storeIds=${stores.map(encodeURIComponent).join(",")}&year=${y}`).catch(() => [])
      )),
      Promise.all(planYears.map((y) =>
        apiJSON(`/api/reporting/stunden-plan/network?storeIds=${stores.map(encodeURIComponent).join(",")}&year=${y}`).catch(() => [])
      )),
    ]).then(([entriesByYear, plansByYear]) => {
      const entriesByStore = new Map();
      entriesByYear.flat().forEach((e) => {
        if (!entriesByStore.has(e.storeId)) entriesByStore.set(e.storeId, []);
        entriesByStore.get(e.storeId).push(e);
      });
      const plansByStore = new Map();
      plansByYear.flat().forEach((rec) => {
        if (!rec) return;
        if (!plansByStore.has(rec.storeId)) plansByStore.set(rec.storeId, []);
        plansByStore.get(rec.storeId).push(rec);
      });

      const tuples = stores.map((storeId) => {
        // Regroupe les entrées de cette filiale par semaine ISO (clé "année-semaine",
        // déjà portée par chaque entrée) — équivalent exact des tableaux weeklyEntries[i]/
        // wasteByWeek[i]/... d'avant, reconstruit ici plutôt que par le serveur.
        const byWeekKey = new Map();
        (entriesByStore.get(storeId) || []).forEach((e) => {
          const key = `${e.year}-${e.week}`;
          if (!byWeekKey.has(key)) byWeekKey.set(key, []);
          byWeekKey.get(key).push(e);
        });
        const weekEntriesOf = (w, family) => (byWeekKey.get(`${w.year}-${w.week}`) || []).filter((e) => e.family === family);

        // Dimanche exclu (isClosed) — son Plan Tag est déjà le sous-total Lu-Sa de la
        // semaine (même convention que buildStundenDaysArray/stundenComputed), l'additionner
        // compterait la semaine deux fois.
        const weekPlanIndex = {};
        const weekStundenPlanIndex = {};
        (plansByStore.get(storeId) || []).forEach((rec) => {
          if (!rec || !rec.days) return;
          Object.entries(rec.days).forEach(([dateISO, d]) => {
            const dt = new Date(`${dateISO}T12:00:00`);
            if (dt.getDay() === 0) return;
            const key = `${dt.getFullYear()}-${isoWeek(dt)}`;
            weekPlanIndex[key] = (weekPlanIndex[key] || 0) + (Number(d.umsatzPlan) || 0);
            weekStundenPlanIndex[key] = (weekStundenPlanIndex[key] || 0) + (Number(d.stundenPlan) || 0);
          });
        });
        const umsatzIstPerWeek = weeks.map((w) => weekEntriesOf(w, "stundenleistung").reduce((sum, e) => sum + (e.items || []).reduce((a, it) => a + (Number(it.umsatzIst) || 0), 0), 0));
        const stundenIstPerWeek = weeks.map((w) => weekEntriesOf(w, "stundenleistung").reduce((sum, e) => sum + (e.items || []).reduce((a, it) => a + (Number(it.stundenIst) || 0), 0), 0));
        const stundenleistungPerWeek = umsatzIstPerWeek.map((u, i) => (u > 0 && stundenIstPerWeek[i] > 0) ? u / stundenIstPerWeek[i] : null);
        // Heures planifiées BRUTES par semaine (jamais un ratio par filiale ici — voir
        // pilotStundenPlanHistory) : le périmètre "Toutes les filiales" doit sommer les
        // heures et le CA planifiés de chaque filiale AVANT de diviser, sinon moyenner des
        // Stundenleistung-cibles filiale par filiale fausse le résultat réseau (biais de
        // pondération). Même logique que weekPlanIndex/pilotHistoryPlan pour umsatzPlan.
        const stundenPlanPerWeek = weeks.map((w) => weekStundenPlanIndex[`${w.year}-${w.week}`] || 0);

        // Même garde anti-double-comptage que loadPilotStoreStats : prebake/frischbrot_brot
        // sont sommés depuis leur famille dédiée (totalEuro), jamais depuis leurs items
        // manual_waste s'ils y apparaissent aussi.
        const pertesPerWeek = weeks.map((w) => {
          let total = 0; const byCat = {};
          weekEntriesOf(w, "manual_waste").forEach((e) => {
            (e.items || []).forEach((it) => {
              if (it.categoryId === "prebake" || it.categoryId === "frischbrot_brot") return;
              const amount = Number(it.amount) || 0;
              total += amount;
              if (it.categoryId) byCat[it.categoryId] = (byCat[it.categoryId] || 0) + amount;
            });
          });
          weekEntriesOf(w, "prebake").forEach((e) => { const amount = Number(e.totalEuro) || 0; total += amount; byCat.prebake = (byCat.prebake || 0) + amount; });
          weekEntriesOf(w, "brot").forEach((e) => { const amount = Number(e.totalEuro) || 0; total += amount; byCat.frischbrot_brot = (byCat.frischbrot_brot || 0) + amount; });
          return { total, byCat };
        });

        return [
          storeId,
          umsatzIstPerWeek,
          weeks.map((w) => weekPlanIndex[`${w.year}-${w.week}`] || 0),
          stundenleistungPerWeek,
          pertesPerWeek.map((p) => p.total),
          pertesPerWeek.map((p) => p.byCat),
          stundenPlanPerWeek,
        ];
      });
      setPilotHistory(Object.fromEntries(tuples.map(([id, ist]) => [id, ist])));
      setPilotHistoryPlan(Object.fromEntries(tuples.map(([id, , plan]) => [id, plan])));
      setPilotStundenHistory(Object.fromEntries(tuples.map(([id, , , stund]) => [id, stund])));
      setPilotPertesHistory(Object.fromEntries(tuples.map(([id, , , , pertes]) => [id, pertes])));
      setPilotPertesCategoryHistory(Object.fromEntries(tuples.map(([id, , , , , cats]) => [id, cats])));
      setPilotStundenPlanHistory(Object.fromEntries(tuples.map(([id, , , , , , stundPlan]) => [id, stundPlan])));
    });
  };

  /* Plus / Réglages — Web Push réel */
  const sendTestPush = () => {
    sendRealTestPush()
      .then(() => showToast(t("toast.testNotifSentCheckPhone", "🔔 Notification test envoyée — vérifie ton téléphone")))
      .catch((err) => showToast(err.message || t("err.testSendImpossible", "Envoi du test impossible.")));
  };
  const togglePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (pushEnabled) {
        const ok = await disablePush();
        setPushEnabled(!ok ? pushEnabled : false);
        if (ok) showToast(t("toast.notifsDisabled", "🔕 Notifications désactivées"));
      } else {
        // Doit rester le tout premier appel async de ce clic (voir lib/push.js) — aucun
        // await avant sur iOS/Safari, sinon la demande de permission échoue silencieusement.
        const result = await enablePush();
        if (result === "granted") { setPushEnabled(true); showToast(t("toast.notifsEnabled", "🔔 Notifications activées")); }
        else if (result === "denied") showToast(t("toast.permissionDeniedReenableInSettings", "Permission refusée — réactive-la dans les réglages de ton téléphone."));
        else if (result === "unsupported") showToast(t("toast.notAvailableOnThisDevice", "Non disponible sur cet appareil/navigateur."));
      }
    } finally {
      setPushBusy(false);
    }
  };
  const openPasswordSheet = () => { setPwForm({ old: "", new1: "", new2: "" }); setPwMessage(""); setSheet("password"); };
  const changePassword = () => {
    if (!pwForm.old || !pwForm.new1 || !pwForm.new2) { setPwMessage("Tous les champs sont obligatoires."); return; }
    if (pwForm.new1.length < 4) { setPwMessage("Le nouveau mot de passe doit contenir au moins 4 caractères."); return; }
    if (pwForm.new1 !== pwForm.new2) { setPwMessage("Les deux nouveaux mots de passe ne sont pas identiques."); return; }
    apiJSON("/api/users/change-password", {
      method: "POST",
      body: JSON.stringify({ employeeId: user?.proId, oldPassword: pwForm.old, newPassword: pwForm.new1 }),
    })
      .then(() => { setSheet(null); showToast(t("toast.passwordChanged", "✓ Mot de passe modifié")); })
      .catch((err) => setPwMessage(err.message || "Erreur."));
  };
  const saveKrankRetention = () => {
    if (krankRetentionDays < 30 || krankRetentionDays > 3650) { showToast(t("toast.invalidDuration30to3650", "Durée invalide (30 à 3650 jours).")); return; }
    apiJSON("/api/app-settings", { method: "PUT", body: JSON.stringify({ krankRetentionDays }) })
      .then(() => {
        setKrankRetentionSaved(true);
        showToast(t("toast.savedGeneric", "✓ Enregistré"));
        setTimeout(() => setKrankRetentionSaved(false), 2000);
      })
      .catch((err) => showToast(err.message || t("err.savingRefused", "Enregistrement refusé.")));
  };
  // Photo de profil — réelle des deux côtés (POST/DELETE /api/users/:id/photo, multer +
  // dossier public/uploads/avatars, déjà là côté serveur) mais jamais branchée côté client
  // (retour utilisateur explicite : "tu dois implementer tout ce qui concerne une photo de
  // profil") — removeProfilePhoto affichait un simple toast de succès sans RIEN faire de
  // réel. mapServerUser transportait déjà tous les autres champs mais pas photoUrl, ajouté
  // au passage.
  const [profilePhotoUploading, setProfilePhotoUploading] = useState(false);
  const profilePhotoInputRef = useRef(null);
  // Fiche employé du compte connecté (retour utilisateur 2026-08-04 : "sa fiche" au tap sur
  // le badge/photo Accueil, pas juste un menu de réglages) — GET /api/me/profile, self-service,
  // chargée à l'ouverture de la sheet "account" (voir openAccountSheet).
  const [myProfile, setMyProfile] = useState(null);
  const loadMyProfile = () => {
    apiJSON("/api/me/profile").then((data) => setMyProfile(data.profile)).catch(() => setMyProfile(null));
  };
  const openAccountSheet = () => { loadMyProfile(); setSheet("account"); };
  // Module Performance Employé (retour utilisateur 2026-08-04) — écran dédié plein écran
  // ("Ma fiche" devient un vrai screen avec onglets Infos/Performance, pas juste la sheet
  // compacte ci-dessus, qui reste inchangée pour l'accès rapide depuis le badge Accueil).
  // ficheScreen = sous-onglet interne, même patron que planScreen/repScreen/adminScreen.
  const [ficheScreen, setFicheScreen] = useState("infos");
  // Bug réel signalé (2026-08-06) : <main> est un seul nœud DOM persistant (mainScrollRef)
  // dont le scrollTop n'est jamais réinitialisé par le navigateur au changement d'écran —
  // ouvrir une page après avoir défilé sur la précédente atterrissait au milieu/bas du
  // nouveau contenu au lieu du haut. Remonte à 0 à CHAQUE changement de sous-écran, sur
  // toute l'app — sauf le fil Messages (msgScreen==="thread"), qui a son propre
  // comportement "toujours en bas" géré séparément juste après.
  useEffect(() => {
    const el = mainScrollRef.current;
    if (!el) return;
    if (screen === "messages" && msgScreen === "thread") return;
    el.scrollTop = 0;
    if (mainAppRef.current) mainAppRef.current.style.setProperty("--nx-shy-scale", "1");
    // Bug réel signalé (2026-10-14, capture à l'appui) : en layout large (nav latérale fixe
    // avec backdrop-filter, absente en mobile — voir SIDEBAR_WIDTH plus bas), le contenu de
    // l'écran précédent (ex. une carte Articles) reste visuellement peint par-dessus le
    // nouvel écran après un changement de `screen`, alors que le DOM/React est déjà correct
    // (confirmé : aucune fuite de rendu trouvée dans le JSX, toujours gardé par
    // `screen === "articles"`) — un gel de composition GPU, pas un bug d'état. Même famille
    // que le `transform` déjà retiré de mainAppRef (commentaire plus bas, 2026-08-13) pour un
    // symptôme similaire sur la zone Dynamic Island, mais la nav latérale (apparue après ce
    // fix, Phase 1 desktop) introduit sa propre couche de composition (position:fixed +
    // backdrop-filter) qui n'existait pas encore à l'époque. Correctif : forcer un repaint du
    // compositeur sur <main> à chaque changement d'écran — un aller-retour transitoire de
    // `transform` (jamais laissé posé, donc aucun risque pour le `position: sticky` des
    // Sheets, contrairement à un transform permanent sur mainAppRef). Scope isWide uniquement
    // : la nav mobile (pilule du bas, pas de sidebar fixe) n'a jamais reproduit ce bug.
    if (isWide) {
      el.style.transform = "translateZ(0)";
      requestAnimationFrame(() => { el.style.transform = ""; });
    }
  }, [screen, authScreen, artScreen, planScreen, tasksScreen, haccpScreen, claimsScreen, pluScreen, krankScreen, moScreen, scanScreen, msgScreen, inventurScreen, infoScreen, repScreen, adminScreen, pilotScreen, finderScreen, lfScreen, ficheScreen, redCategory, paScreen, isWide]);
  // En-tête + nav du bas "shy" — CONTINU, proportionnel à scrollTop (retour utilisateur,
  // 2026-08-16 : "ça marche mais trop mécanique" sur la première version à 2 états qui
  // "claquait"). Pas de seuil, pas de sens de défilement à détecter : l'échelle suit
  // directement la distance défilée (0 en haut, minimum atteint à SHY_RANGE px) — descendre
  // rétrécit progressivement, remonter agrandit progressivement, symétrique par construction.
  // Écrit directement en style DOM (--nx-shy-scale sur mainAppRef) plutôt que du state React :
  // sur un composant de cette taille, un setState à chaque frame pendant le scroll forcerait
  // un re-render complet 60x/s. Attente active de <main> comme avant (même bug de timing
  // deps:[] déjà corrigé une fois — <main> n'existe pas encore au tout premier rendu, avant
  // la connexion).
  useEffect(() => {
    let rafId = null, waitId = null;
    const SHY_RANGE = 70;
    const SHY_MIN_SCALE = 0.86;
    const startWatching = (mainEl, rootEl) => {
      const tick = () => {
        const y = mainEl.scrollTop;
        const progress = Math.max(0, Math.min(1, y / SHY_RANGE));
        const scale = 1 - progress * (1 - SHY_MIN_SCALE);
        rootEl.style.setProperty("--nx-shy-scale", scale.toFixed(4));
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    };
    const waitForMain = () => {
      const mainEl = mainScrollRef.current;
      const rootEl = mainAppRef.current;
      if (mainEl && rootEl) { startWatching(mainEl, rootEl); return; }
      waitId = requestAnimationFrame(waitForMain);
    };
    waitForMain();
    return () => { if (rafId) cancelAnimationFrame(rafId); if (waitId) cancelAnimationFrame(waitId); };
  }, []);
  // Fil Messages ouvert — comportement natif façon WhatsApp (retour utilisateur explicite,
  // 2026-08-06) : pointe toujours sur les DERNIERS messages (bas), jamais en haut. Se
  // redéclenche à chaque changement de msgThreadMessages (ouverture, envoi, poll 15s) —
  // s'exécute après le rendu DOM, donc el.scrollHeight reflète déjà le contenu à jour.
  useEffect(() => {
    if (!(screen === "messages" && msgScreen === "thread")) return;
    const el = mainScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [msgThreadMessages, screen, msgScreen]);
  const [myPerformance, setMyPerformance] = useState(null);
  const [myAzlSummary, setMyAzlSummary] = useState(null);
  const loadMyPerformance = () => {
    apiJSON("/api/me/performance").then((data) => setMyPerformance(data)).catch(() => setMyPerformance(null));
    // Assiduité AZL — pas de nouvelle route serveur : réutilise l'endpoint déjà public de
    // l'écran Arbeitszeitliste (mêmes données que l'onglet AZL lui-même) et n'en extrait que
    // la ligne de l'employé connecté, jamais un second calcul côté serveur.
    const now = new Date();
    const myId = String(user?.proId || user?.id || "");
    apiJSON(`/api/arbeitszeitliste/${encodeURIComponent(user?.store)}/${now.getFullYear()}/${now.getMonth() + 1}`)
      .then((data) => setMyAzlSummary(data.employees?.find((e) => String(e.employeeId) === myId) || null))
      .catch(() => setMyAzlSummary(null));
  };
  // Ma Fiche > Performance, volet filiale (FL/FLA/BL/VL/NL, retour utilisateur 2026-08-15) —
  // un seul fetch à l'ouverture, pas de poll 15s comme Pilotage réseau (fiche consultée
  // ponctuellement, même pattern que loadMyPerformance ci-dessus). GET /api/manager/overview
  // scope déjà lui-même correctement selon QUI appelle (identity.stores pour un GR,
  // [identity.storeId] pour un FL/FLA) — jamais besoin de repasser un périmètre côté client,
  // et ses totaux (Σ/Σ, jamais une moyenne de taux par filiale) servent directement.
  const loadFichePerfStats = () => {
    setFichePerfLoading(true);
    apiJSON("/api/manager/overview")
      .then((data) => {
        const scope = data?.stores || [];
        setFicheReactivity(data?.totals ? {
          tasksOnTimeRate: data.totals.tasksOnTimeRate, tasksTotal: data.totals.tasksReactivityTotal,
          missionsOnTimeRate: data.totals.missionsOnTimeRate, missionsTotal: data.totals.missionsReactivityTotal,
        } : null);
        setFicheClaims(data?.totals ? { fresh: data.totals.claimsFresh, food: data.totals.claimsFood } : null);
        return Promise.all([fetchStoreStatsFor(scope, "semaine", 0), fetchStoreStatsFor(scope, "mois", 0)]);
      })
      .then(([weekStats, monthStats]) => { setFicheWeekStats(weekStats); setFicheMonthStats(monthStats); })
      .catch(() => { setFicheReactivity(null); setFicheClaims(null); setFicheWeekStats({}); setFicheMonthStats({}); })
      .finally(() => setFichePerfLoading(false));
  };
  const openMyFiche = () => {
    loadMyProfile(); loadMyPerformance();
    // FL/FLA rejoint isNetworkLeader ici — les deux tiers ont besoin du volet filiale, seule
    // la mise en page (ajouté vs remplace les cartes individuelles) diffère dans le rendu.
    if (isNetworkLeader || isFlOrFlaRole) loadFichePerfStats();
    setFicheScreen("infos"); setSheet(null); setScreen("myFiche");
  };
  const uploadProfilePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProfilePhotoUploading(true);
    const body = new FormData();
    body.append("photo", file);
    apiJSON(`/api/users/${encodeURIComponent(user?.id)}/photo`, { method: "POST", body })
      .then((updated) => { setUser((u) => ({ ...u, photoUrl: updated.photoUrl || "" })); showToast(t("toast.photoUpdated", "✓ Photo mise à jour")); })
      .catch((err) => showToast(err.message || t("err.photoSendImpossible", "Envoi de la photo impossible.")))
      .finally(() => { setProfilePhotoUploading(false); if (profilePhotoInputRef.current) profilePhotoInputRef.current.value = ""; });
  };
  const removeProfilePhoto = () => {
    apiJSON(`/api/users/${encodeURIComponent(user?.id)}/photo`, { method: "DELETE" })
      .then(() => { setUser((u) => ({ ...u, photoUrl: "" })); showToast(t("toast.profilePhotoDeleted", "Photo de profil supprimée")); })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  const formatLastSeen = (min) => {
    if (min == null) return t("settings.sessions.never", "Jamais connecté");
    if (min < 60) return t("settings.sessions.minutesAgo", { n: min }, `Il y a ${min} min`);
    const h = Math.floor(min / 60);
    if (h < 24) return t("settings.sessions.hoursAgo", { n: h }, `Il y a ${h} h`);
    return t("settings.sessions.daysAgo", { n: Math.floor(h / 24) }, `Il y a ${Math.floor(h / 24)} j`);
  };

  /* Centre de notifications — vraies routes StoreFlowProV2. identityQuery() reproduit
     userQuery() côté vanilla (notifications.js/home-notifications-final.js) : ces routes
     lisent l'identité depuis la query string, jamais depuis la session cookie. */
  const identityQuery = (u) => {
    if (!u) return "";
    const name = `${u.firstname || ""} ${u.lastname || ""}`.trim();
    return `userId=${encodeURIComponent(u.id || "")}&employeeId=${encodeURIComponent(u.proId || "")}&userName=${encodeURIComponent(name)}`;
  };
  // Dérive l'écran de destination depuis le type/les relatedXxxId d'une vraie notification
  // serveur — le champ href renvoyé par le serveur est une URL .html vanilla, inutilisable
  // ici (voir sfNotifHrefFor côté server.js).
  const deriveNotifGo = (n) => {
    if (n.relatedClaimId) return "claims";
    if (n.relatedChatId) return "messages";
    if (n.relatedTaskId) return "tasks";
    if (n.relatedRequestId) return "planning";
    if (n.relatedKrankmeldungId) return "krank";
    if (n.relatedMaterialOrderId) return "material";
    if (n.relatedPluAttemptId) return "inventurmassnahme";
    if (n.relatedLieferscheinId) return "liefer";
    if (n.relatedAccountApprovalRequestId) return "admin";
    if (n.relatedPasswordResetRequestId) return "admin";
    if (n.relatedImportantInfoId) return "infos";
    if (n.relatedMissionId) return "missions";
    if (n.relatedPreisanderungId) return "preisanderung";
    if (n.relatedInventurId) return "inventur";
    if (n.relatedMerchandiseOrderId) return "merchandise";
    if (n.relatedInventurmassnahmeId) return "inventurmassnahme";
    if (n.relatedFilialeReportId) return "filialeReport";
    if (n.relatedRrId) return "rr";
    if (n.relatedKontoauszugCheckId) return "kontoauszug";
    // Retour utilisateur explicite (2026-08-18) : "le même système sur toutes les
    // notifications" — types couverts jusqu'ici uniquement par relatedXxxId (donc
    // silencieusement absents de la liste ci-dessus s'ils n'ont AUCUN related id, ex.
    // haccp_daily/planning_published/leistungsfrei/reductions_mhd_complete/article-finder,
    // retombaient tous sur le cas générique "notifications" sans jamais atterrir sur le
    // bon module).
    if (n.type === "claims") return "claims";
    if (n.type === "messages") return "messages";
    if (n.type === "tasks") return "tasks";
    if (n.type === "article-report") return "articles";
    if (n.type === "haccp_daily" || n.type === "haccp_weekly") return "haccp";
    if (n.type === "planning_published" || n.type === "leistungsfrei") return "planning";
    if (n.type === "reductions_mhd_complete" || n.type === "reductions") return "reductions";
    if (n.type === "article-finder") return "finder";
    return "notifications";
  };
  const mapServerNotification = (n) => ({
    id: n.id,
    type: n.type,
    title: n.title,
    // titleKey/messageKey/params (2026-08-17, audit i18n) : bug réel trouvé en câblant ce
    // chantier — le serveur posait déjà titleKey sur certaines notifications, mais ce
    // mapping ne le reportait jamais, donc notifTitle() retombait TOUJOURS sur le texte
    // français brut malgré son mécanisme de retraduction déjà écrit (jamais branché bout en
    // bout). Même piège pour messageKey/params, ajoutés ici pour la première fois.
    titleKey: n.titleKey || null,
    messageKey: n.messageKey || null,
    params: n.params || null,
    body: n.message,
    read: !!n.read,
    createdAt: n.createdAt,
    go: deriveNotifGo(n),
    // Ces trois-là sont relues par openNotification APRÈS le mapping (pas seulement par
    // deriveNotifGo côté brut ci-dessus) — sans les reporter ici, "ouvrir le bon fil", "aller
    // sur le bon rendez-vous" et "router vers la bonne tuile de tâche" retombent toujours sur
    // le cas générique, silencieusement (bug préexistant trouvé en câblant les notifs
    // Rendez-vous d'inventaire, 2026-08-03 : relatedChatId n'était en fait jamais transmis
    // malgré le commentaire de l'audit 2026-08-02 qui affirmait le fil précis déjà ouvert ;
    // MÊME bug retrouvé le 2026-08-07 pour relatedTaskId en vérifiant routeForTaskId — jamais
    // câblé ici depuis le début, routeForTaskId retombait donc toujours sur taskFilter=null).
    relatedChatId: n.relatedChatId,
    relatedInventurAppointmentId: n.relatedInventurAppointmentId,
    relatedTaskId: n.relatedTaskId,
    relatedImportantInfoId: n.relatedImportantInfoId,
    relatedKrankmeldungId: n.relatedKrankmeldungId,
    relatedLieferscheinId: n.relatedLieferscheinId,
    relatedMissionId: n.relatedMissionId,
    relatedPreisanderungId: n.relatedPreisanderungId,
    relatedInventurId: n.relatedInventurId,
    relatedMerchandiseOrderId: n.relatedMerchandiseOrderId,
    relatedInventurmassnahmeId: n.relatedInventurmassnahmeId,
    relatedFilialeReportId: n.relatedFilialeReportId,
    relatedRrId: n.relatedRrId,
    relatedKontoauszugCheckId: n.relatedKontoauszugCheckId,
  });

  const loadNotifications = () => {
    if (!user) return;
    apiJSON(`/api/notifications?${identityQuery(user)}`)
      .then((data) => {
        const mapped = (data.notifications || []).map(mapServerNotification);
        setNotifications(mapped);
        // Fait apparaître en bannière les notifications VRAIMENT nouvelles depuis le
        // dernier chargement (pas seulement celles présentes au tout premier montage) —
        // sans ce diff, un poll ultérieur ne remettrait jamais rien en file (bannerQueueRef
        // ne se remplit qu'une fois, jamais rechargé), donc toute notification créée APRÈS
        // l'ouverture de l'app (ex. une nouvelle inscription à valider) ne serait jamais
        // signalée avant un rechargement complet de la page.
        if (notifSeenIdsRef.current === null) {
          notifSeenIdsRef.current = new Set(mapped.map((n) => n.id));
          if (bannerShownIdsRef.current === null) bannerShownIdsRef.current = loadBannerShownIds(user);
          // Seules les notifications JAMAIS encore montrées en bannière (registre persistant,
          // survit au rechargement) entrent dans la file au premier montage — pas tout le
          // stock de non-lues, sinon un simple refresh de page les refait toutes défiler.
          bannerQueueRef.current = mapped.filter((n) => !n.read && !bannerShownIdsRef.current.has(n.id)).map((n) => n.id);
        } else {
          const freshUnread = mapped.filter((n) => !n.read && !notifSeenIdsRef.current.has(n.id));
          mapped.forEach((n) => notifSeenIdsRef.current.add(n.id));
          if (freshUnread.length) {
            bannerQueueRef.current = [...(bannerQueueRef.current || []), ...freshUnread.map((n) => n.id)];
          }
        }
      })
      .catch(() => {});
  };

  // Même table que ACTION_META_EXACT côté vanilla (home-cockpit-final.js) — les entrées
  // exactes d'abord, puis un repli par sous-chaîne du type, jamais une action muette.
  const ACTION_META_EXACT = {
    MHD_ENREGISTREE: { emoji: "🛡️", text: "a enregistré un contrôle MHD." },
    STORE_MAD_ENREGISTREE: { emoji: "🏷️", text: "a ajouté une MAD magasin." },
    ARTICLE_CONTROLE: { emoji: "🛡️", text: "a contrôlé un article." },
    REDUCTION_TRAITEE: { emoji: "🏷️", text: "a traité une réduction." },
    TASK_CREEE: { emoji: "✅", text: "a créé une tâche." },
    TASK_TERMINEE: { emoji: "✅", text: "a terminé une tâche." },
    CLAIM_CREEE: { emoji: "📣", text: "a créé une réclamation." },
    CLAIM_ENVOYEE: { emoji: "📣", text: "a envoyé une réclamation." },
    CLAIM_TRAITEE: { emoji: "📣", text: "a traité une réclamation." },
    LIEFERSCHEIN_CREE: { emoji: "📦", text: "a créé un bon de livraison." },
    LIEFERSCHEIN_VALIDEE: { emoji: "📦", text: "a validé un bon de livraison." },
    PLANNING_SAVED: { emoji: "🗓️", text: "a modifié le planning." },
    PLANNING_COPIED: { emoji: "🗓️", text: "a dupliqué une semaine de planning." },
    PLANNING_PUBLISHED: { emoji: "🗓️", text: "a publié le planning." },
    PLANNING_UNPUBLISHED: { emoji: "🗓️", text: "a dépublié le planning." },
    PLANNING_SYNCED_STUNDENLEISTUNG: { emoji: "🗓️", text: "a synchronisé le relevé d'heures." },
    MATERIAL_ORDER_CREE: { emoji: "📦", text: "a passé une commande de matériel." },
  };
  const actionMeta = (action) => {
    const type = String(action?.type || "").toUpperCase();
    if (ACTION_META_EXACT[type]) return ACTION_META_EXACT[type];
    if (type.includes("STORE")) return { emoji: "🏷️", text: "a ajouté une MAD magasin." };
    if (type.includes("REDUCTION")) return { emoji: "🏷️", text: "a traité une réduction." };
    if (type.includes("DELIVERY") || type.includes("LIEFERSCHEIN")) return { emoji: "📦", text: "a mis à jour un bon de livraison." };
    if (type.includes("MHD") || type.includes("MAD")) return { emoji: "🛡️", text: "a enregistré une MAD." };
    if (type.includes("TASK")) return { emoji: "✅", text: "a mis à jour une tâche." };
    if (type.includes("CLAIM")) return { emoji: "📣", text: "a mis à jour une réclamation." };
    if (type.startsWith("PLANNING")) return { emoji: "🗓️", text: "a mis à jour le planning." };
    if (type.includes("MATERIAL")) return { emoji: "📦", text: "a passé une commande de matériel." };
    return { emoji: "✅", text: "a effectué une action." };
  };
  const loadLiveActions = () => {
    if (!user) return;
    apiJSON("/api/actions")
      .then((list) => {
        const today = new Date().toISOString().slice(0, 10);
        const todays = (Array.isArray(list) ? list : []).filter((a) => a.date === today).slice(-8).reverse();
        setLiveActions(todays.map((a) => {
          const meta = actionMeta(a);
          const name = (a.user || a.employee || "Employé").split(" ")[0].split(".")[0];
          return { emoji: meta.emoji, text: `${name} ${meta.text}`, time: a.time || "" };
        }));
      })
      .catch(() => {});
  };

  // Web Push réel — réabonne silencieusement si déjà autorisé (jamais une demande de
  // permission automatique, voir lib/push.js) et synchronise pushEnabled sur le VRAI
  // abonnement serveur, pas un optimisme local.
  useEffect(() => {
    if (!user) { setPushEnabled(false); return; }
    initPushIfAlreadyGranted().finally(() => {
      fetchPushStatus().then(setPushEnabled);
    });
  }, [user?.id]);

  // Gate obligatoire "lire + signer avant d'accéder à l'app" (retour utilisateur explicite,
  // 2026-08-02 : "je ne vois pas du tout la contrainte") — le backend existe déjà en
  // entier (GET /api/important-info/pending-signatures, importantInfoGateEnabled côté
  // /api/important-info/gate-toggle) mais n'était jamais consulté côté nexora-app. null =
  // pas encore vérifié (ne rien afficher, ni bloquer ni laisser passer — évite un flash de
  // l'app avant la vraie réponse serveur), [] = rien à signer.
  const [gateItems, setGateItems] = useState(null);
  const checkImportantInfoGate = () => {
    if (!user) { setGateItems(null); return; }
    apiJSON("/api/important-info/pending-signatures")
      .then((d) => setGateItems(d.gateEnabled === false ? [] : (d.items || [])))
      .catch(() => setGateItems([]));
  };
  useEffect(() => { checkImportantInfoGate(); }, [user?.id]);

  // Deep-link Web Push (retour utilisateur explicite, 2026-08-18 : "le même système sur
  // toutes les notifications de l'app") — service-worker.js encode juste l'id de LA
  // notification dans la query string ("?notif=<id>", payload.tag = notif.id), lue ici une
  // fois l'utilisateur identifié (le fetch a besoin d'une session valide) : on retrouve la
  // notification par id dans la vraie liste, on la mappe (mapServerNotification) puis on
  // rejoue openNotification() dessus — EXACTEMENT la même fonction que le tap sur une
  // notification depuis l'écran in-app, jamais une deuxième logique de routage. Retirée de
  // l'URL ensuite pour ne jamais se redéclencher à chaque re-render ni rester visible dans
  // la barre d'adresse.
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const notifId = params.get("notif");
    if (!notifId) return;
    window.history.replaceState(null, "", window.location.pathname);
    apiJSON(`/api/notifications?${identityQuery(user)}`)
      .then((data) => {
        const raw = (data.notifications || []).find((item) => String(item.id) === String(notifId));
        if (raw) openNotification(mapServerNotification(raw));
      })
      .catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (!user) { setNotifications([]); setLiveActions([]); notifSeenIdsRef.current = null; bannerQueueRef.current = null; bannerShownIdsRef.current = null; return; }
    loadNotifications();
    loadLiveActions();
    // Vraie notification "live" : sans ce poll, une notification créée après l'ouverture
    // de l'app (ex. une nouvelle inscription à approuver, retour utilisateur réel
    // 2026-07-31) n'apparaissait jamais avant un rechargement complet de la page —
    // loadNotifications() n'était appelée qu'une seule fois, au montage. Même cadence que
    // la boucle de consommation des bannières (15s) juste au-dessus.
    const poll = setInterval(() => { loadNotifications(); loadLiveActions(); }, 15000);
    return () => clearInterval(poll);
  }, [user?.id]);

  // Permissions par rôle — retour utilisateur explicite (2026-08-12) : un changement de
  // permission décidé par un admin (togglePermission) doit s'appliquer à tous les comptes
  // du rôle concerné sans qu'ils aient à recharger l'app. rolePerms (hasModuleAccess +
  // écran Rôles & permissions lui-même) n'était chargé qu'une fois au login — même cadence
  // que le poll de notifications juste au-dessus. Le cache séparé utilisé par
  // usePermission() (lib/permissions.js, la plupart des canXxx ailleurs dans l'app) a son
  // propre poll interne, voir refreshRolePermissions/ensurePolling là-bas.
  useEffect(() => {
    if (!user) return;
    const poll = setInterval(loadRolePermissions, 15000);
    return () => clearInterval(poll);
  }, [user?.id]);

  // Visibilité Bestandskontrolle : un FL/FLA masque/rétablit un employé pendant que ce
  // dernier a l'app ouverte (retour utilisateur 2026-08-09, "aucun rafraîchissement ne
  // doit être nécessaire") — sans ce poll, kontrolleVisibility n'était chargée qu'une
  // fois au montage, donc un blocage/déblocage décidé par le manager restait invisible
  // pour l'employé concerné jusqu'à ce qu'il recharge l'app lui-même. Continue en tâche
  // de fond quel que soit l'écran affiché, comme le poll notifications ci-dessus.
  useEffect(() => {
    if (!user) return;
    loadKontrolleVisibility();
    const poll = setInterval(loadKontrolleVisibility, 15000);
    return () => clearInterval(poll);
  }, [user?.id, user?.store]);

  // Fiche employé (rôle, date de naissance, flag mineur...) chargée une seule fois au
  // montage (loadTaskEmployees/loadPlanUsersBirthdate) sans ce poll — retour utilisateur
  // 2026-08-05, "mise à jour d'une fiche employé ne se répercute nulle part sans recharger
  // la page" : violait la règle "live update everywhere". Consommé par Planning (règle
  // mineur/Azubi), Tâches, et tout écran qui lit taskEmployees. Même cadence que le poll
  // notifications juste au-dessus, tourne en continu quel que soit l'écran affiché.
  useEffect(() => {
    if (!user) return;
    const poll = setInterval(() => { loadTaskEmployees(); loadPlanUsersBirthdate(); }, 20000);
    return () => clearInterval(poll);
  }, [user?.id]);

  // Verrouillage par inactivité (voir IDLE_LOCK_MS plus haut) — vérifié au montage, au retour
  // au premier plan (visibilitychange) et toutes les 20s en filet de sécurité si peu
  // d'interactions ; réinitialisé sur toute interaction réelle.
  useEffect(() => {
    if (!user) return;
    let lastHeartbeatAt = 0;
    const markActivity = () => { try { localStorage.setItem(IDLE_ACTIVITY_KEY, String(Date.now())); } catch {} };
    const heartbeat = () => {
      lastHeartbeatAt = Date.now();
      // Rafraîchit hiddenModules à chaque battement (retour utilisateur 2026-08-15) — un
      // module caché/réaffiché par l'admin doit disparaître/réapparaître de l'Accueil d'un
      // compte déjà connecté, jamais seulement après un nouveau login (voir mapServerUser).
      // best-effort — un échec ne doit rien casser côté UI, jamais de setUser(null) ici (déjà
      // géré par le handler 401 global, voir plus haut).
      apiJSON("/api/auth/ping")
        .then((data) => {
          if (!Array.isArray(data?.hiddenModules)) return;
          setUser((u) => (u ? { ...u, hiddenModules: data.hiddenModules } : u));
        })
        .catch(() => {});
    };
    const checkIdle = () => {
      let last = 0;
      try { last = Number(localStorage.getItem(IDLE_ACTIVITY_KEY)) || 0; } catch {}
      if (last && Date.now() - last >= IDLE_LOCK_MS) { setLocked(true); return; }
      // Activité récente et pas encore verrouillé : prolonge la vraie session serveur (voir
      // HEARTBEAT_INTERVAL_MS) — inutile une fois verrouillé, la session peut redevenir
      // "juste" expirée si personne ne revient la déverrouiller.
      if (last && Date.now() - lastHeartbeatAt >= HEARTBEAT_INTERVAL_MS) heartbeat();
    };
    // Vérifie AVANT de marquer une activité — sinon on écraserait un timestamp déjà périmé
    // (ex. app rouverte après 20 min) avec "maintenant" avant même d'avoir pu le comparer,
    // et le verrouillage ne se déclencherait jamais au retour. Si aucune activité n'a jamais
    // été enregistrée (premier chargement après connexion, où doLogin() vient de la poser),
    // `last` est 0/falsy et le garde-fou ci-dessous ne verrouille jamais à tort.
    checkIdle();
    document.addEventListener("pointerdown", markActivity, { passive: true });
    document.addEventListener("keydown", markActivity);
    document.addEventListener("touchstart", markActivity, { passive: true });
    const onVisibility = () => { if (document.visibilityState === "visible") checkIdle(); };
    document.addEventListener("visibilitychange", onVisibility);
    const idlePoll = setInterval(checkIdle, 20000);
    return () => {
      document.removeEventListener("pointerdown", markActivity);
      document.removeEventListener("keydown", markActivity);
      document.removeEventListener("touchstart", markActivity);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(idlePoll);
    };
  }, [user?.id]);

  // Modules cachés + périmètre de filiales — propagation EN DIRECT, sans rafraîchissement
  // (retour utilisateur explicite 2026-08-16 : "cela doit être automatique et instantané" ;
  // puis 2026-08-16 pour les filiales : "cette infos doit toujours se mettre a jour des
  // qu'un GR recoit une nouvelle filiale, cette filiale doit toujours s'ajouter et non
  // rester silencieuse" — un Bereichsleiter multi-filiales restait affiché avec sa
  // seule filiale d'origine tant que le compte ne se reconnectait pas). Séparé du battement de
  // cœur ci-dessus (HEARTBEAT_INTERVAL_MS = 3 min, sert la prolongation de session, pas la
  // réactivité UI) — cadence 15s, même idiome que les autres polls "live" du projet
  // (loadPilotStoreStats, missions, notifications). GET /api/auth/ping est déjà appelé
  // partout ailleurs dans l'app, aucune route nouvelle, aucun coût réseau notable.
  useEffect(() => {
    if (!user) return;
    const refreshLiveUserFields = () => {
      apiJSON("/api/auth/ping")
        .then((data) => {
          setUser((u) => {
            if (!u) return u;
            let next = u;
            if (Array.isArray(data?.hiddenModules)) {
              const same = next.hiddenModules?.length === data.hiddenModules.length && next.hiddenModules?.every((id) => data.hiddenModules.includes(id));
              if (!same) next = { ...next, hiddenModules: data.hiddenModules };
            }
            if (Array.isArray(data?.stores)) {
              const same = next.stores?.length === data.stores.length && next.stores?.every((id) => data.stores.includes(id));
              if (!same) next = { ...next, stores: data.stores };
            }
            if (typeof data?.storeId === "string" && data.storeId && data.storeId !== next.store) {
              next = { ...next, store: data.storeId };
            }
            return next === u ? u : next;
          });
        })
        .catch(() => {}); // best-effort — un échec (hors-ligne...) ne doit rien casser côté UI
    };
    refreshLiveUserFields();
    const poll = setInterval(refreshLiveUserFields, 15000);
    return () => clearInterval(poll);
  }, [user?.id]);

  // Vérification locale partagée (retour utilisateur 2026-08-28, "qu'est-ce qui est inutile
  // maintenant ?" — factorisation d'un doublon, jamais un changement de comportement) : les
  // DEUX chemins qui retombent sur sessionPasswordRef (hors-ligne explicite, et panne réseau
  // franche malgré navigator.onLine=true) faisaient exactement les 4 mêmes lignes. Renvoie
  // true si déverrouillé, false sinon — l'appelant décide seul du message d'erreur/busy.
  const tryLocalUnlock = () => {
    if (!(sessionPasswordRef.current && unlockPassword === sessionPasswordRef.current)) return false;
    setLocked(false);
    setUnlockPassword("");
    try { localStorage.setItem(IDLE_ACTIVITY_KEY, String(Date.now())); } catch {}
    return true;
  };

  const submitUnlock = async () => {
    if (!unlockPassword.trim()) { setUnlockError(t("auth.lockScreen.needPasswordError", "Saisis ton mot de passe.")); return; }
    setUnlockBusy(true);
    setUnlockError("");
    // Hors-ligne : jamais essayer /api/auth/verify-password (échouerait de toute façon, sans
    // réseau) — vérifie directement contre sessionPasswordRef (mot de passe de la dernière
    // connexion réussie, voir son commentaire). En ligne, la vraie vérification serveur reste
    // TOUJOURS prioritaire, jamais contournée — ce chemin local est un filet de secours pour
    // le hors-ligne uniquement, jamais un raccourci de confort.
    if (!navigator.onLine) {
      setUnlockBusy(false);
      if (!tryLocalUnlock()) setUnlockError(t("auth.lockScreen.wrongPasswordError", "Mot de passe incorrect."));
      return;
    }
    try {
      await apiJSON("/api/auth/verify-password", { method: "POST", body: JSON.stringify({ password: unlockPassword }) });
      setLocked(false);
      setUnlockPassword("");
      try { localStorage.setItem(IDLE_ACTIVITY_KEY, String(Date.now())); } catch {}
    } catch (err) {
      if (err.status === 401 && err.data?.error !== "Mot de passe incorrect") {
        // Session réellement expirée entretemps (pas juste un mauvais mot de passe) — pas la
        // peine de redemander en boucle, retour direct à l'écran de connexion. Message
        // explicite (retour utilisateur 2026-08-10) : avant, ce retour à la connexion
        // n'était jamais expliqué, ce qui ressemblait à un bug plutôt qu'à une session
        // réellement expirée.
        logout();
        showToast(t("auth.sessionExpiredToast", "Session expirée — reconnecte-toi."));
        return;
      }
      // navigator.onLine peut mentir (Wi-Fi associé mais sans vraie sortie internet) — un
      // échec réseau franc (aucun statut HTTP du tout, ou délai dépassé, voir lib/api.js
      // isTimeout) retombe sur la même vérification locale que le chemin hors-ligne explicite
      // ci-dessus, plutôt que d'afficher à tort "mot de passe incorrect" pour un problème de
      // réseau. Un vrai 401 serveur (err.status === 401, mot de passe réellement faux) ne
      // passe JAMAIS par ici — jamais court-circuité par le local.
      if ((!err.status || err.isTimeout) && tryLocalUnlock()) {
        setUnlockBusy(false);
        return;
      }
      setUnlockError(err.message || t("auth.lockScreen.wrongPasswordError", "Mot de passe incorrect."));
    } finally {
      setUnlockBusy(false);
    }
  };

  // Déverrouillage Face ID / Touch ID (retour utilisateur explicite, 2026-08-28, suite au
  // filet mot de passe offline ci-dessus : "Peux-tu faire cela avec Face ID ?") — contraire
  // au mot de passe, WebAuthn n'a PAS d'équivalent "comparer localement" : le protocole exige
  // TOUJOURS un défi émis par le serveur pour la vraie vérification cryptographique (aucune
  // façon de faire ça hors-ligne sans serveur, par conception du protocole — vérifié dans
  // server/webauthn/routes.js, generateAuthenticationOptions()). Deux chemins différents :
  //  - En ligne : réutilise la VRAIE vérification serveur (mêmes routes que doWebauthnLogin,
  //    login-options + login-verify) — aussi rigoureux qu'une vraie connexion, jamais un
  //    raccourci.
  //  - Hors-ligne : bascule sur un défi généré localement (jamais envoyé nulle part) et
  //    déclenche directement le capteur biométrique de la plateforme via l'API WebAuthn du
  //    navigateur — celle-ci reste utilisable sans réseau (Face ID/Touch ID vérifient
  //    physiquement le visage/doigt EN LOCAL sur l'appareil, avant même de songer à un
  //    serveur). Un succès ici ne prouve PAS cryptographiquement l'identité au serveur
  //    (impossible sans lui) — c'est une porte de réouverture LOCALE, exactement le même
  //    niveau de garantie que le filet mot de passe hors-ligne juste au-dessus, jamais une
  //    vraie ré-authentification à distance. residentKey:"preferred" côté serveur
  //    (register-options) rend ce credential découvrable, donc allowCredentials peut rester
  //    vide ici — jamais besoin de connaître/mettre en cache l'identifiant du credential.
  const unlockWithFaceId = async () => {
    if (webauthnBusy || !webauthnHint) return;
    setWebauthnBusy(true);
    setUnlockError("");
    try {
      if (navigator.onLine) {
        const { options } = await apiJSON("/api/webauthn/login-options", {
          method: "POST",
          body: JSON.stringify({ login: webauthnHint.login, branchId: webauthnHint.branchId || "" }),
        });
        const response = await startAuthentication({ optionsJSON: options });
        await apiJSON("/api/webauthn/login-verify", {
          method: "POST",
          body: JSON.stringify({ login: webauthnHint.login, branchId: webauthnHint.branchId || "", response }),
        });
      } else {
        const challengeBytes = crypto.getRandomValues(new Uint8Array(32));
        let bin = ""; for (const b of challengeBytes) bin += String.fromCharCode(b);
        const localChallenge = btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
        await startAuthentication({ optionsJSON: { challenge: localChallenge, timeout: 60000, userVerification: "required" } });
      }
      setLocked(false);
      setUnlockPassword("");
      try { localStorage.setItem(IDLE_ACTIVITY_KEY, String(Date.now())); } catch {}
    } catch (err) {
      if (err?.name !== "NotAllowedError") setUnlockError(err.message || t("auth.webauthn.loginFailed", "Connexion Face ID impossible."));
    } finally {
      setWebauthnBusy(false);
    }
  };

  /* Messages — chargement réel au montage (totalUnread alimente l'Assistant du jour et le
     badge Messages partout dans l'app, pas seulement l'écran Messages lui-même) + un poll
     30s pour les messages reçus pendant que l'app est ouverte ailleurs. Sur 403 (lacune de
     permission réelle connue, voir plus haut), reste honnêtement vide plutôt que de montrer
     quoi que ce soit — jamais de repli sur une donnée inventée. */
  useEffect(() => {
    if (!user) { setConversations([]); return; }
    loadConversations();
    // 15s comme les autres flux "live" de l'app (missions, Krankmeldung GR) — 30s donnait
    // l'impression que les messages reçus n'arrivaient "qu'après un rafraîchissement de la
    // page" (retour utilisateur explicite, 2026-08-02).
    const poll = setInterval(loadConversations, 15000);
    return () => clearInterval(poll);
  }, [user?.id]);

  // Rafraîchi aussi immédiatement à chaque entrée sur la liste (pas seulement le poll) —
  // pour que revenir sur Messages après avoir envoyé un message ailleurs (ou après une
  // notification) montre l'état à jour tout de suite, sans attendre le prochain tick.
  useEffect(() => {
    if (!user || screen !== "messages" || msgScreen !== "list") return;
    if (msgFilter === "archived") loadArchivedConversations();
    else loadConversations();
  }, [user?.id, screen, msgScreen, msgFilter]);

  // Poll du fil ouvert — voir pollThreadMessages ci-dessus (bug réel trouvé à l'audit
  // Messages du 2026-08-06). Même cadence (15s) que la liste des conversations.
  useEffect(() => {
    if (!user || screen !== "messages" || msgScreen !== "thread" || !msgActiveId) return;
    const poll = setInterval(() => pollThreadMessages(msgActiveId), 15000);
    return () => clearInterval(poll);
  }, [user?.id, screen, msgScreen, msgActiveId]);

  useEffect(() => {
    if (!user || screen !== "messages") return;
    // Chargé aussi quand la sheet "convInfo" est ouverte, pas seulement sur l'écran
    // "Nouveau" — nécessaire pour la recherche "Ajouter un membre" (audit du 2026-08-02).
    if (msgScreen !== "new" && sheet !== "convInfo") return;
    loadRecipients("");
  }, [user?.id, screen, msgScreen, sheet]);

  // "Mon équipe filiale" — charge les 3 sources en une fois à l'entrée sur l'écran (jamais
  // au montage du composant, ni couplé à un quelconque effet de l'écran Admin) :
  // adminPending/adminResetRequests sont déjà scopés serveur pour FL/FLA (voir
  // loadAdminPending/loadAdminResetRequests, réutilisés tels quels), flTeamWorkers via sa
  // propre route.
  useEffect(() => {
    if (!user || screen !== "flTeam") return;
    loadAdminPending();
    loadAdminResetRequests();
    loadFlTeamWorkers();
  }, [user?.id, screen]);

  // Bug réel trouvé (2026-08-22, retour utilisateur : "j'ai essayé d'envoyer un message
  // depuis un compte grand responsable à un compte normal... jamais reçu") : la recherche
  // ne réinterrogeait JAMAIS le serveur — msgRecipients restait le snapshot chargé une
  // seule fois à l'ouverture (search="", plafonné à 40 côté serveur, ordre d'insertion
  // brut), et la barre de recherche ne faisait que refiltrer localement CE snapshot. Pour
  // un GR (pool réseau entier, jamais limité à une filiale), tout employé au-delà de la
  // position 40 était structurellement introuvable, même en tapant son nom exact. Le
  // serveur (searchableRecipients, server/messages/service.js) filtre AVANT de plafonner
  // à 40 — il suffit de vraiment lui envoyer la recherche tapée, avec un léger debounce
  // pour ne pas spammer une requête par frappe.
  useEffect(() => {
    if (!user || screen !== "messages" || msgScreen !== "new") return;
    const timer = setTimeout(() => loadRecipients(newConvQuery), 300);
    return () => clearTimeout(timer);
  }, [newConvQuery]);

  // Même correctif pour "Ajouter un membre" dans la sheet convInfo — même snapshot figé,
  // même piège au-delà de 40 destinataires réseau.
  useEffect(() => {
    if (!user || screen !== "messages" || sheet !== "convInfo") return;
    const timer = setTimeout(() => loadRecipients(msgAddMemberQuery), 300);
    return () => clearTimeout(timer);
  }, [msgAddMemberQuery]);

  // Admin > Utilisateurs : rechargé à chaque entrée sur l'écran + toutes les 20s tant qu'il
  // reste ouvert (retour utilisateur explicite : un nouveau compte, notamment une demande
  // d'inscription PENDING_APPROVAL, doit apparaître sans avoir à rafraîchir toute la page —
  // même patron que loadConversations ci-dessus).
  useEffect(() => {
    if (!(screen === "admin" && adminScreen === "users")) return;
    loadAdminUsers();
    loadAdminPending();
    loadAdminResetRequests();
    const poll = setInterval(() => { loadAdminUsers(); loadAdminPending(); loadAdminResetRequests(); }, 20000);
    return () => clearInterval(poll);
  }, [screen, adminScreen]);
  // Onglet "Par utilisateur" (cacher/afficher un module) de Rôles & permissions —
  // admin-only — a besoin de la même liste adminUsers ; simple charge ponctuelle à
  // l'entrée sur l'écran Rôles (pas de poll, contrairement à Utilisateurs : moins
  // sensible au temps réel, un admin qui vient chercher un utilisateur précis suffit).
  useEffect(() => {
    if (!(screen === "admin" && adminScreen === "roles" && (isNexoraAdmin || isAdminModuleOverride))) return;
    if (!adminUsers.length) loadAdminUsers();
    loadRoleHiddenModules();
  }, [screen, adminScreen, isNexoraAdmin, isAdminModuleOverride]);
  // Vue VKL — onglet Équipe de Pilotage réseau (voir plus bas, pilotScreen === "analyse")
  // a besoin de la liste des comptes pour savoir quels BL rattachent à ce VKL (reportsTo)
  // — même liste qu'Admin > Utilisateurs, simple charge ponctuelle à l'entrée sur Pilotage
  // (pas de poll : un rattachement BL↔VKL change rarement, contrairement aux données
  // Reporting déjà pollées ailleurs sur cet écran).
  useEffect(() => {
    if (!(screen === "pilotage" && isNetworkLeader)) return;
    if (!adminUsers.length) loadAdminUsers();
  }, [screen, isNetworkLeader]);


  useEffect(() => {
    if (!isNetworkLeader || pilotScreen !== "filiale-detail" || !pilotFilialeId) { setPilotTeam([]); return; }
    apiJSON(`/api/stores/${encodeURIComponent(pilotFilialeId)}/team`)
      .then((data) => setPilotTeam(data.team || []))
      .catch(() => setPilotTeam([]));
  }, [isNetworkLeader, pilotScreen, pilotFilialeId]);

  useEffect(() => {
    setPilotPlanningDetail(null);
    setPilotPlanningSearchDate("");
    if (!isNetworkLeader || pilotScreen !== "filiale-detail" || !pilotFilialeId) { setPilotPlanningWeeks([]); return; }
    const currentMonday = mondayOfWeekOffset(0);
    Promise.all([
      apiJSON(`/api/plannings/published?storeId=${encodeURIComponent(pilotFilialeId)}`),
      apiJSON(`/api/plannings?storeId=${encodeURIComponent(pilotFilialeId)}&weekStart=${encodeURIComponent(currentMonday)}`),
    ]).then(([pubData, curData]) => {
      const published = (pubData.plannings || []).map((p) => ({ ...p, status: "published" }));
      const current = curData.planning;
      const alreadyIncluded = current && published.some((p) => p.weekStart === current.weekStart);
      const weeks = (!current || alreadyIncluded) ? published : [
        { id: current.id, weekStart: current.weekStart, weekKey: current.weekKey, publishedAt: null, totals: current.totals, status: current.status },
        ...published,
      ];
      // Seule la dernière semaine s'affiche par défaut (retour utilisateur explicite,
      // 2026-08-24) — les précédentes restent consultables via la recherche par date
      // ci-dessous (searchOlderPilotPlanningWeek), jamais un mur de 4 semaines d'un coup.
      setPilotPlanningWeeks(weeks.slice(0, 1).map((w) => ({ ...w, isCurrent: w.weekStart === current?.weekStart })));
    }).catch(() => setPilotPlanningWeeks([]));
  }, [isNetworkLeader, pilotScreen, pilotFilialeId]);

  useEffect(() => {
    if (!isNetworkLeader || pilotScreen !== "filiale-detail" || !pilotFilialeId) { setPilotKrank([]); return; }
    const loadPilotKrank = () => {
      apiJSON("/api/krankmeldungen")
        .then((data) => setPilotKrank((data.krankmeldungen || []).filter((k) => k.storeId === pilotFilialeId && k.generalStatus === "ACTIVE")))
        .catch(() => setPilotKrank([]));
    };
    loadPilotKrank();
    // Une nouvelle déclaration doit apparaître ici sans que le GR ait à recharger la page
    // (retour utilisateur explicite) — même cadence que le poll Missions/Notifications (15s).
    const poll = setInterval(loadPilotKrank, 15000);
    return () => clearInterval(poll);
  }, [isNetworkLeader, pilotScreen, pilotFilialeId]);

  useEffect(() => {
    setPilotAzlOpenKey(null);
    if (!isNetworkLeader || pilotScreen !== "filiale-detail" || !pilotFilialeId) { setPilotAzlMonths([]); return; }
    const base = new Date();
    const months = [0, -1, -2].map((offset) => {
      const d = new Date(base.getFullYear(), base.getMonth() + offset, 1);
      return { year: d.getFullYear(), month: d.getMonth() + 1 };
    });
    Promise.all(months.map(({ year, month }) =>
      apiJSON(`/api/arbeitszeitliste/${encodeURIComponent(pilotFilialeId)}/${year}/${month}`)
        .then((data) => ({
          year, month, status: data.document.status, hasArchivedPdf: data.document.hasArchivedPdf,
          anomalies: data.anomalies || [], signatures: data.validation.signatures || [], requiredRoles: data.validation.requiredRoles || [],
        }))
        .catch(() => ({ year, month, status: "draft", hasArchivedPdf: false, anomalies: [], signatures: [], requiredRoles: [] }))
    )).then(setPilotAzlMonths);
  }, [isNetworkLeader, pilotScreen, pilotFilialeId]);

  useEffect(() => {
    if (!isNetworkLeader || pilotScreen !== "filiale-detail" || !pilotFilialeId) { setPilotHaccpMonth(null); return; }
    const d = new Date();
    apiJSON(`/api/haccp/month?storeId=${encodeURIComponent(pilotFilialeId)}&year=${d.getFullYear()}&month=${d.getMonth() + 1}`)
      .then(setPilotHaccpMonth)
      .catch(() => setPilotHaccpMonth(null));
  }, [isNetworkLeader, pilotScreen, pilotFilialeId]);

  useEffect(() => {
    if (!isNetworkLeader || pilotScreen !== "filiale-detail" || !pilotFilialeId) { setPilotClaims([]); return; }
    apiJSON("/api/claims")
      .then((data) => setPilotClaims((data.claims || [])
        .filter((c) => c.storeId === pilotFilialeId && c.status !== "done")
        .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))))
      .catch(() => setPilotClaims([]));
  }, [isNetworkLeader, pilotScreen, pilotFilialeId]);

  // Rapport de filiale (2026-08-26) — même patron que pilotClaims juste au-dessus. Visibles
  // ici seulement pour un GR qui a réellement accès au module (canSeeReport côté serveur —
  // créateur, cible, ou NEXORA_ADMIN) : un GR sans lien avec le rapport reçoit une liste
  // vide, jamais une 403 bruyante dans cette vue de synthèse.
  useEffect(() => {
    if (!isNetworkLeader || pilotScreen !== "filiale-detail" || !pilotFilialeId) { setPilotFilialeReports([]); return; }
    apiJSON(`/api/filiale-reports/by-store/${encodeURIComponent(pilotFilialeId)}`)
      .then((data) => setPilotFilialeReports(data.reports || []))
      .catch(() => setPilotFilialeReports([]));
  }, [isNetworkLeader, pilotScreen, pilotFilialeId]);

  // Rubrique "Warenaustausch" (2026-08-27, retour utilisateur explicite) — même patron que
  // pilotFilialeReports juste au-dessus. /outgoing et /inbox sont déjà scopés au périmètre
  // RÉSEAU de l'appelant (identity.stores, voir server/warenaustausch/routes.js —
  // warenaustausch.view existe déjà explicitement pour "un oversight réseau BL/VKL/NL"),
  // filtrés ici sur la SEULE filiale consultée. Peuple aussi waOutgoing/waInbox (état global
  // du module) — sinon openWarenaustauschDetail (qui cherche dans CES listes, pas dans
  // pilotFilialeWA) échouerait avec "Transfert introuvable" pour un GR qui n'a jamais ouvert
  // le module Warenaustausch lui-même avant de passer par la Fiche filiale.
  useEffect(() => {
    if (!isNetworkLeader || pilotScreen !== "filiale-detail" || !pilotFilialeId) { setPilotFilialeWA([]); return; }
    Promise.all([
      apiJSON("/api/warenaustausch/outgoing").catch(() => ({ transfers: [] })),
      apiJSON("/api/warenaustausch/inbox").catch(() => ({ transfers: [] })),
    ]).then(([out, inb]) => {
      setWaOutgoing(out.transfers || []);
      setWaInbox(inb.transfers || []);
      const merged = new Map();
      [...(out.transfers || []), ...(inb.transfers || [])].forEach((tr) => merged.set(tr.id, tr));
      // Retour utilisateur explicite (2026-08-27) : "le brouillon ne doit pas être
      // téléchargeable... reste juste visible pour la filiale de départ parce qu'elle n'a
      // pas encore validé le WA" — un brouillon n'existe pas encore pour l'oversight GR,
      // seulement pour son créateur (voir /outgoing côté serveur, qui renvoie les brouillons
      // pour que LA FILIALE puisse continuer à éditer les siens — jamais destiné à fuiter
      // ici). Exclu de la Fiche filiale entièrement, pas juste de l'export.
      const list = [...merged.values()]
        .filter((tr) => tr.status !== "brouillon" && (tr.fromStoreId === pilotFilialeId || tr.toStoreId === pilotFilialeId))
        .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
      setPilotFilialeWA(list);
    }).catch(() => setPilotFilialeWA([]));
  }, [isNetworkLeader, pilotScreen, pilotFilialeId]);

  useEffect(() => {
    if (!isNetworkLeader || pilotScreen !== "filiale-detail" || !pilotFilialeId) { setPilotInventurInventories([]); setPilotInventurKontrollen([]); return; }
    const loadPilotInventur = () => {
      apiJSON("/api/inventur/inventories?mode=active")
        .then((data) => setPilotInventurInventories((data.inventories || []).filter((inv) => inv.storeId === pilotFilialeId)))
        .catch(() => setPilotInventurInventories([]));
      apiJSON("/api/inventur/kontrollen?mode=active")
        .then((data) => setPilotInventurKontrollen((data.kontrollen || []).filter((k) => k.storeId === pilotFilialeId)))
        .catch(() => setPilotInventurKontrollen([]));
    };
    loadPilotInventur();
    // Une clôture d'inventaire/cycle par l'équipe doit apparaître ici sans que le GR ait à
    // rouvrir la fiche (retour utilisateur explicite) — même cadence que le poll Krankmeldung.
    const poll = setInterval(loadPilotInventur, 15000);
    return () => clearInterval(poll);
  }, [isNetworkLeader, pilotScreen, pilotFilialeId]);

  useEffect(() => {
    if (!isNetworkLeader || pilotScreen !== "filiale-detail" || !pilotFilialeId) { setPilotPluAttempts([]); setPilotPluWeekly(null); return; }
    // Priorité explicite de l'utilisateur (2026-08-03) : ce que le GR doit voir/télécharger
    // en premier, c'est CHAQUE test PLU passé durant la semaine en cours (pas juste les 8
    // derniers tous temps confondus) — même calcul de lundi ISO que exportPilotPluWeekly,
    // pour rester cohérent avec le badge "minimum atteint" juste au-dessus.
    const weekStart = mondayOfWeekOffset(0);
    apiJSON(`/api/plu-quiz/attempts?storeId=${encodeURIComponent(pilotFilialeId)}`)
      .then((data) => setPilotPluAttempts((data.attempts || [])
        .filter((a) => a.finishedAt && a.finishedAt.slice(0, 10) >= weekStart)
        .sort((a, b) => String(b.finishedAt).localeCompare(String(a.finishedAt)))))
      .catch(() => setPilotPluAttempts([]));
    apiJSON("/api/plu-quiz/weekly-summary")
      .then((data) => setPilotPluWeekly((data.summary || []).find((s) => s.storeId === pilotFilialeId) || null))
      .catch(() => setPilotPluWeekly(null));
  }, [isNetworkLeader, pilotScreen, pilotFilialeId]);

  useEffect(() => {
    if (!isNetworkLeader || pilotScreen !== "filiale-detail" || !pilotFilialeId) { setPilotImArchiveWeeks([]); return; }
    loadImArchive(pilotFilialeId, setPilotImArchiveWeeks, setPilotImArchiveLoading);
  }, [isNetworkLeader, pilotScreen, pilotFilialeId]);

  // Rubrique "Reporting" — Woche Abschriften, semaine en cours ET semaine précédente,
  // toujours les deux à la fois (voir exportPilotWeeklyReport/pilotRepWeekView) : le GR
  // doit toujours pouvoir télécharger l'une ou l'autre, jamais bloqué sur une saisie pas
  // terminée. fetchWeekSrcForStore = même calcul que loadPastWeek, re-scopé à
  // pilotFilialeId. Même cadence de poll (15s) que Krankmeldung/Inventaires ci-dessus —
  // "live update everywhere" (des saisies peuvent arriver pendant que la fiche est ouverte).
  useEffect(() => {
    setPilotRepWeekView("current");
    if (!isNetworkLeader || pilotScreen !== "filiale-detail" || !pilotFilialeId) { setPilotRepWeekSrcs({}); return; }
    const loadPilotRepWeeks = () => {
      setPilotRepWeekLoading(true);
      const weeks = [isoWeek(now), isoWeek(now) - 1];
      Promise.all(weeks.map((wk) => fetchWeekSrcForStore(pilotFilialeId, wk).then((src) => [wk, src])))
        .then((pairs) => setPilotRepWeekSrcs(Object.fromEntries(pairs)))
        .catch(() => setPilotRepWeekSrcs({}))
        .finally(() => setPilotRepWeekLoading(false));
    };
    loadPilotRepWeeks();
    const poll = setInterval(loadPilotRepWeeks, 15000);
    return () => clearInterval(poll);
  }, [isNetworkLeader, pilotScreen, pilotFilialeId]);

  // Rubrique "Reporting" — Stundenleistung du mois EN COURS (jamais navigable ici, voir
  // exportPilotStundenleistung), à l'état actuel de la saisie : buildStundenDaysArray est
  // le même code que loadStundenleistung, re-scopé à pilotFilialeId.
  useEffect(() => {
    if (!isNetworkLeader || pilotScreen !== "filiale-detail" || !pilotFilialeId) { setPilotStundenDays([]); return; }
    const loadPilotStundenDays = () => {
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      Promise.all([
        apiJSON(`/api/reporting/stunden-plan?storeId=${encodeURIComponent(pilotFilialeId)}&year=${year}&month=${month}`).catch(() => null),
        apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(pilotFilialeId)}&family=stundenleistung&month=${month}&year=${year}`).catch(() => []),
      ]).then(([plan, istEntries]) => {
        const planDays = plan?.days || {};
        const istByDate = {};
        (Array.isArray(istEntries) ? istEntries : []).forEach((e) => { istByDate[e.date] = e.items?.[0] || {}; });
        setPilotStundenDays(buildStundenDaysArray(planDays, istByDate, year, month));
      }).catch(() => setPilotStundenDays([]));
    };
    loadPilotStundenDays();
    const poll = setInterval(loadPilotStundenDays, 15000);
    return () => clearInterval(poll);
  }, [isNetworkLeader, pilotScreen, pilotFilialeId]);

  // Rubrique "Reporting" — Krank de la semaine en cours + historique Krank par semaine du
  // mois en cours (mêmes deux besoins que krankHours/reportingHistory pour la Monatsmeldung
  // de la filiale du compte connecté, voir monthlyStundenByWeek), re-scopés à
  // pilotFilialeId. Seul family=krank est nécessaire ici (byCategory/total des autres
  // familles ne servent pas à la Monatsmeldung) — plus léger que loadReportingHistory.
  useEffect(() => {
    if (!isNetworkLeader || pilotScreen !== "filiale-detail" || !pilotFilialeId) { setPilotKrankHoursCurrent(0); setPilotReportingHistory([]); return; }
    const loadPilotKrankData = () => {
      const week = isoWeek(now);
      const year = now.getFullYear();
      apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(pilotFilialeId)}&family=krank&week=${week}&year=${year}`)
        .then((entries) => setPilotKrankHoursCurrent(Number((Array.isArray(entries) ? entries : [])[0]?.items?.[0]?.hours || 0)))
        .catch(() => setPilotKrankHoursCurrent(0));
      apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(pilotFilialeId)}&family=krank&year=${year}`)
        .then((entries) => {
          const buckets = {};
          (Array.isArray(entries) ? entries : []).forEach((e) => {
            const wk = Number(e.week); const mo = Number(e.month);
            if (!Number.isFinite(wk) || wk === week || !mo) return; // semaine en cours = affichée en direct, jamais dupliquée ici
            const key = `${wk}-${mo}`;
            if (!buckets[key]) buckets[key] = { kw: wk, month: MONTH_NAMES_FR[mo - 1], krankHours: 0 };
            buckets[key].krankHours += Number(e.items?.[0]?.hours || 0);
          });
          setPilotReportingHistory(Object.values(buckets));
        })
        .catch(() => setPilotReportingHistory([]));
    };
    loadPilotKrankData();
    const poll = setInterval(loadPilotKrankData, 15000);
    return () => clearInterval(poll);
  }, [isNetworkLeader, pilotScreen, pilotFilialeId]);

  const loadPilotAppointments = () => {
    apiJSON("/api/inventur/appointments")
      .then((data) => setPilotAppointments(data.appointments || []))
      .catch(() => setPilotAppointments([]));
  };
  useEffect(() => {
    // "today" a besoin des données pour le badge sur la tuile ; "appointments" est la
    // page dédiée qui affiche la liste complète — les deux consomment le même state.
    if (!isNetworkLeader || (pilotScreen !== "today" && pilotScreen !== "appointments")) { setPilotAppointments([]); return; }
    loadPilotAppointments();
    // Une réponse/contre-proposition de la filiale doit apparaître sans recharger la page
    // (même exigence "temps réel" que Krankmeldung/Inventaires — retour utilisateur).
    const poll = setInterval(loadPilotAppointments, 15000);
    return () => clearInterval(poll);
  }, [isNetworkLeader, pilotScreen]);

  // Badge "Rapport de filiale" — retour utilisateur explicite (2026-08-27) : la tuile module
  // doit toujours porter le nombre de rapports reçus non traités, comme les autres modules
  // "live" (Missions/Alertes/Infos). frReceived n'était chargé qu'au clic dans le module
  // (loadFrReceived, voir plus bas) — sans ce poll, le badge de l'Accueil restait toujours à
  // 0 tant qu'on n'avait pas déjà ouvert le module une fois. Marche pour un compte normal
  // (FL/FLA destinataire) ET un compte GR (transmis à son supérieur/subordonné) — un compte
  // sans la permission filialeReport.process reçoit juste une liste vide (catch silencieux).
  useEffect(() => {
    if (screen !== "home") { setFrReceived([]); return; }
    loadFrReceived();
    const poll = setInterval(loadFrReceived, 15000);
    return () => clearInterval(poll);
  }, [screen]);

  // Chargé de façon proactive (login + polling 15s, voir plus bas) pour alimenter le badge
  // de la tuile Inventur dès l'accueil — plus jamais vidé au changement d'écran (l'ancien
  // effet gardé ici l'effaçait dès qu'on quittait l'écran RDV, ce qui aurait cassé le badge).
  const loadInventurAppointments = () => {
    apiJSON("/api/inventur/appointments")
      .then((data) => setInventurAppointments(data.appointments || []))
      .catch(() => setInventurAppointments([]));
  };

  // ---- Rendez-vous d'inventaire — actions côté BL (Aujourd'hui > Rendez-vous) ----
  // Ouvre la sheet pour UNE filiale précise — préremplit la date avec la proposition en
  // cours si un rendez-vous actif existe déjà pour cette filiale (jamais vide dans ce cas,
  // sinon un clic "Enregistrer" sans avoir touché le champ renverrait une chaîne vide).
  const openPilotAppointment = (storeId) => {
    const existing = pilotAppointments.find((a) => a.storeId === storeId && !a.confirmed && !a.cancelled);
    setPilotAppointmentStoreId(storeId);
    setPilotAppointmentDateInput(existing?.proposedDate || "");
    setPilotAppointmentNewDateInput("");
    setPilotAppointmentChatInput("");
    setSheet("pilotAppointment");
  };
  const proposePilotAppointment = () => {
    if (!pilotAppointmentStoreId || !pilotAppointmentDateInput || pilotAppointmentBusy) return;
    setPilotAppointmentBusy(true);
    apiJSON("/api/inventur/appointments", {
      method: "POST",
      body: JSON.stringify({ storeId: pilotAppointmentStoreId, date: pilotAppointmentDateInput }),
    })
      .then(() => { showToast(t("pilotage.appointment.proposedToast", "✓ Date proposée à la filiale.")); loadPilotAppointments(); })
      .catch(() => showToast(t("deliveryNotes.actionImpossible", "Action impossible.")))
      .finally(() => setPilotAppointmentBusy(false));
  };
  const confirmPilotAppointment = (id) => {
    if (pilotAppointmentBusy) return;
    setPilotAppointmentBusy(true);
    apiJSON(`/api/inventur/appointments/${id}/confirm`, { method: "PATCH" })
      .then(() => { showToast(t("pilotage.appointment.confirmedToast", "✓ Rendez-vous validé définitivement.")); loadPilotAppointments(); setSheet(null); })
      .catch(() => showToast(t("deliveryNotes.actionImpossible", "Action impossible.")))
      .finally(() => setPilotAppointmentBusy(false));
  };
  // wasConfirmed change juste le texte de confirmation + le toast — le rendez-vous peut
  // maintenant être supprimé même déjà validé (retour utilisateur explicite, 2026-08-06),
  // la filiale en est alors prévenue côté serveur.
  const cancelPilotAppointment = (id, wasConfirmed) => {
    const confirmMsg = wasConfirmed
      ? t("pilotage.appointment.deleteConfirmedConfirm", "Supprimer ce rendez-vous d'inventaire déjà validé ? La filiale sera prévenue.")
      : t("pilotage.appointment.cancelConfirm", "Annuler cette proposition de rendez-vous ?");
    if (!window.confirm(confirmMsg)) return;
    apiJSON(`/api/inventur/appointments/${id}/cancel`, { method: "PATCH" })
      .then(() => { showToast(wasConfirmed ? t("pilotage.appointment.deletedToast", "Rendez-vous supprimé.") : t("pilotage.appointment.cancelledToast", "Rendez-vous annulé.")); loadPilotAppointments(); setSheet(null); })
      .catch(() => showToast(t("deliveryNotes.actionImpossible", "Action impossible.")));
  };
  // Modifie un rendez-vous DÉJÀ VALIDÉ : redevient une demande en attente, la filiale reçoit
  // une notification "changement de plan" (jamais un nouveau rendez-vous séparé).
  const reschedulePilotAppointment = () => {
    if (!pilotAppointmentStoreId || !pilotAppointmentDateInput || pilotAppointmentBusy) return;
    const confirmedApt = pilotAppointments.find((a) => a.storeId === pilotAppointmentStoreId && a.confirmed && !a.cancelled);
    if (!confirmedApt) return;
    setPilotAppointmentBusy(true);
    apiJSON(`/api/inventur/appointments/${confirmedApt.id}/reschedule`, {
      method: "PATCH",
      body: JSON.stringify({ date: pilotAppointmentDateInput }),
    })
      .then(() => { showToast(t("pilotage.appointment.rescheduledToast", "✓ Changement de plan envoyé à la filiale.")); setPilotAppointmentDateInput(""); loadPilotAppointments(); })
      .catch(() => showToast(t("deliveryNotes.actionImpossible", "Action impossible.")))
      .finally(() => setPilotAppointmentBusy(false));
  };
  // Propose un inventaire NOUVEAU/additionnel sans toucher à celui déjà validé — même route
  // que la toute première proposition, le serveur ignore les rendez-vous confirmés et crée
  // un enregistrement indépendant qui coexiste avec celui déjà validé.
  const proposeNewPilotAppointment = () => {
    if (!pilotAppointmentStoreId || !pilotAppointmentNewDateInput || pilotAppointmentBusy) return;
    setPilotAppointmentBusy(true);
    apiJSON("/api/inventur/appointments", {
      method: "POST",
      body: JSON.stringify({ storeId: pilotAppointmentStoreId, date: pilotAppointmentNewDateInput }),
    })
      .then(() => { showToast(t("pilotage.appointment.proposedToast", "✓ Date proposée à la filiale.")); setPilotAppointmentNewDateInput(""); loadPilotAppointments(); })
      .catch(() => showToast(t("deliveryNotes.actionImpossible", "Action impossible.")))
      .finally(() => setPilotAppointmentBusy(false));
  };
  const sendPilotAppointmentMessage = (id) => {
    const text = pilotAppointmentChatInput.trim();
    if (!text) return;
    setPilotAppointmentChatInput("");
    apiJSON(`/api/inventur/appointments/${id}/messages`, { method: "POST", body: JSON.stringify({ text }) })
      .then(() => loadPilotAppointments())
      .catch(() => showToast(t("deliveryNotes.actionImpossible", "Action impossible.")));
  };

  // ---- Rendez-vous d'inventaire — actions côté filiale (module Inventur > Rendez-vous) ----
  const acceptInventurAppointment = (id) => {
    if (inventurAppointmentBusy) return;
    setInventurAppointmentBusy(true);
    apiJSON(`/api/inventur/appointments/${id}/respond`, { method: "PATCH", body: JSON.stringify({ action: "accept" }) })
      .then(() => { showToast(t("inventur.appointment.acceptedToast", "✓ Date acceptée.")); loadInventurAppointments(); })
      .catch(() => showToast(t("deliveryNotes.actionImpossible", "Action impossible.")))
      .finally(() => setInventurAppointmentBusy(false));
  };
  const counterInventurAppointment = (id) => {
    if (!inventurAppointmentCounterDate || inventurAppointmentBusy) return;
    setInventurAppointmentBusy(true);
    apiJSON(`/api/inventur/appointments/${id}/respond`, {
      method: "PATCH",
      body: JSON.stringify({ action: "counter", date: inventurAppointmentCounterDate }),
    })
      .then(() => { showToast(t("inventur.appointment.counteredToast", "✓ Nouvelle date proposée.")); setInventurAppointmentCounterDate(""); loadInventurAppointments(); })
      .catch(() => showToast(t("deliveryNotes.actionImpossible", "Action impossible.")))
      .finally(() => setInventurAppointmentBusy(false));
  };
  const sendInventurAppointmentMessage = (id) => {
    const text = inventurAppointmentChatInput.trim();
    if (!text) return;
    setInventurAppointmentChatInput("");
    apiJSON(`/api/inventur/appointments/${id}/messages`, { method: "POST", body: JSON.stringify({ text }) })
      .then(() => loadInventurAppointments())
      .catch(() => showToast(t("deliveryNotes.actionImpossible", "Action impossible.")));
  };

  const loadStoresCatalog = () => apiJSON("/api/stores").then((d) => setStoresCatalog(d.stores || [])).catch(() => setStoresCatalog([]));
  const loadStoreRegions = () => apiJSON("/api/store-regions")
    .then((d) => {
      setStoreRegionsMap(Object.fromEntries((d.regions || []).map((r) => [r.storeId, r.land])));
      setGermanLander(d.lander || {});
    })
    .catch(() => {});
  // GET /api/stores reste accessible avant connexion (voir requireAuth côté serveur) —
  // le formulaire d'inscription doit pouvoir proposer les vraies filiales existantes,
  // jamais un champ texte libre (même trou que N397, corrigé ici aussi).
  useEffect(() => { if (["register", "login", "forgotPassword"].includes(authScreen)) loadStoresCatalog(); }, [authScreen]);
  const openMyStores = () => { setMyStoresAdding(false); setMyStoresQuery(""); loadStoresCatalog(); setSheet("myStores"); };
  const openAdminStoreCreate = () => { setAdminStoreForm({ id: "", label: "", address: "", land: "", openTime: "", closeTime: "", isNew: true }); setSheet("adminStoreEdit"); };
  const openAdminStoreEdit = (s) => { setAdminStoreForm({ id: s.id, label: s.label, address: s.address || "", land: storeRegionsMap[s.id] || "", openTime: s.openTime || "", closeTime: s.closeTime || "", isNew: false }); setSheet("adminStoreEdit"); };
  const saveAdminStore = () => {
    const f = adminStoreForm;
    if (!f) return;
    const id = f.id.trim().toUpperCase();
    if (!id || !f.label.trim()) { showToast(t("toast.idAndLabelRequired", "Identifiant et label obligatoires.")); return; }
    setAdminStoreBusy(true);
    const commitLand = () => f.land
      ? apiJSON(`/api/store-regions/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify({ land: f.land }) })
      : Promise.resolve();
    const req = f.isNew
      ? apiJSON("/api/stores", { method: "POST", body: JSON.stringify({ id, label: f.label.trim(), address: f.address.trim() }) })
      : apiJSON(`/api/stores/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ label: f.label.trim(), address: f.address.trim(), openTime: f.openTime || "", closeTime: f.closeTime || "" }) });
    req.then(commitLand)
      .then(() => { showToast(f.isNew ? "✓ Filiale créée" : "✓ Filiale modifiée"); setSheet(null); setAdminStoreForm(null); loadStoresCatalog(); loadStoreRegions(); })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")))
      .finally(() => setAdminStoreBusy(false));
  };
  // Retour utilisateur explicite : "on doit pouvoir supprimer une filiale enregistrer" —
  // le serveur refuse (409) tant que de vrais comptes référencent encore cette filiale,
  // pour ne jamais recréer le genre de filiale fantôme (N397) que ce chantier a corrigé.
  const deleteAdminStore = (s) => {
    if (!window.confirm(t("confirm.deleteStoreTemplate", { label: s.label || s.id, id: s.id }, `Supprimer définitivement la filiale ${s.label || s.id} (${s.id}) ?`))) return;
    apiJSON(`/api/stores/${encodeURIComponent(s.id)}`, { method: "DELETE" })
      .then(() => { showToast(t("toast.storeDeleted", "🗑️ Filiale supprimée")); loadStoresCatalog(); loadStoreRegions(); })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  const loadMyAddableStores = () => {
    apiJSON("/api/me/addable-stores")
      .then((data) => {
        setMyAddableStores(data.stores || []);
        setMyStoresNlResolved(data.niederlassungResolved !== false);
      })
      .catch(() => { setMyAddableStores([]); setMyStoresNlResolved(true); });
  };
  const addMyStore = (storeId, label) => {
    setMyStoresBusy(true);
    apiJSON("/api/me/stores", { method: "PATCH", body: JSON.stringify({ action: "add", storeId, label }) })
      .then((updated) => {
        setUser((u) => ({ ...u, store: updated.storeId, stores: updated.stores || [] }));
        setMyStoresQuery(""); setMyStoresAdding(false); setMyStoreConfirm(null);
        loadStoresCatalog();
        showToast(t("toast.storeAddedToMineTemplate", { id: storeId }, `✓ ${storeId} ajoutée à tes filiales`));
      })
      .catch((err) => showToast(err.message || t("err.additionImpossible", "Ajout impossible.")))
      .finally(() => setMyStoresBusy(false));
  };
  const removeMyStore = (storeId) => {
    setMyStoresBusy(true);
    apiJSON("/api/me/stores", { method: "PATCH", body: JSON.stringify({ action: "remove", storeId }) })
      .then((updated) => {
        setUser((u) => ({ ...u, store: updated.storeId, stores: updated.stores || [] }));
        showToast(t("toast.storeRemovedFromMineTemplate", { id: storeId }, `Filiale ${storeId} retirée`));
      })
      .catch((err) => showToast(err.message || t("err.removalImpossible", "Retrait impossible.")))
      .finally(() => setMyStoresBusy(false));
  };

  // Chargé aussi à l'ouverture même du fil (pas seulement la sheet "Infos") — nécessaire
  // pour afficher le bon sous-titre sous le nom en tête de conversation (rôle/filiale du
  // ou des autres participants, voir le header msgScreen==="thread" plus bas — bug réel
  // signalé 2026-08-06 : "Filiale N236" affiché sous le nom d'une Bereichsleiterin qui n'a
  // justement PAS UNE SEULE filiale).
  useEffect(() => {
    const active = (sheet === "convInfo" || (screen === "messages" && msgScreen === "thread")) && msgActiveId;
    if (!active) { setMsgConvMembers([]); return; }
    apiJSON(`/api/messages/${encodeURIComponent(msgActiveId)}`)
      .then((data) => setMsgConvMembers(data.members || []))
      .catch(() => setMsgConvMembers([]));
  }, [sheet, screen, msgScreen, msgActiveId]);

  /* Contrôle MHD Kühlung — vraies données (Phase 1, module Contrôle). Voir le mapping
     complet dans le plan de migration : articles = GET /api/articles (zone Kühlung, déjà
     trié par sortOrder côté serveur) ; records = GET /api/records?source=KUEHLUNG ;
     touched = GET /api/touches (source réelle du "déjà passé en revue aujourd'hui", pas
     dérivée de records) ; Magasin/STORE et Livraison/DELIVERY sont le MÊME mhd_records
     avec juste un source différent, pas des tables séparées. */
  const todayStr = () => new Date().toISOString().slice(0, 10);
  const loadArticles = () => {
    apiJSON("/api/articles")
      .then((list) => setArticles((Array.isArray(list) ? list : [])
        .filter((a) => a.zone === "Kühlung")
        // Consignes de bouteilles (EW-Pfand), jamais de vrais articles à contrôler/réduire —
        // retour utilisateur explicite (2026-08-17, capture à l'appui) : elles apparaissaient
        // dans MAD Kühlung (compteur 380/381) et le sélecteur de règles de réduction, alors
        // qu'une consigne n'a ni MHD ni notion de réduction. 7 articles réels concernés
        // aujourd'hui (Art.Nr 3791/3793/3794/3795/3796/22845/33351), tous nommés "EW-Pfand..."
        // — filtre par motif du nom (pas une liste figée d'Art.Nr) pour couvrir tout futur
        // article de consigne créé sous le même nom, sans dépendre d'un flag dédié inexistant
        // côté catalogue. Reste visible ailleurs (Articles, Bestellung) — ce filtre ne touche
        // que cette liste Kühlung dédiée au contrôle/réductions.
        .filter((a) => !/^ew-pfand/i.test((a.name || "").trim())))
      )
      .catch(() => {});
  };
  const loadKuhlungRecords = () => {
    apiJSON("/api/records?source=KUEHLUNG")
      .then((list) => setRecords(Array.isArray(list) ? list : []))
      .catch(() => {});
  };
  // Voir kuhlungExpiredRemoved plus haut — filtre côté client sur removedFromControlAt pour
  // ne garder QUE le surplus par rapport à `records` (jamais de doublon possible, les deux
  // ensembles sont mutuellement exclusifs par construction côté serveur).
  const loadKuhlungExpiredRemoved = () => {
    apiJSON("/api/records?source=KUEHLUNG&includeExpiredRemoved=1")
      .then((list) => setKuhlungExpiredRemoved((Array.isArray(list) ? list : []).filter((r) => r.removedFromControlAt)))
      .catch(() => {});
  };
  const loadTouched = () => {
    apiJSON(`/api/touches?date=${todayStr()}`)
      .then((list) => setTouched(new Set((Array.isArray(list) ? list : []).map((t) => t.artNr))))
      .catch(() => {});
  };
  const loadStoreRecords = () => {
    apiJSON(`/api/records?source=STORE&controlDate=${todayStr()}`)
      .then((list) => setStoreRecords(Array.isArray(list) ? list : []))
      .catch(() => {});
  };
  // includeExpiredRemoved=1 (2026-08-20, retour utilisateur explicite) : sans ce flag, une
  // MHD Magasin retirée du Contrôle à minuit disparaissait de Réductions ENTIÈREMENT (actif
  // ET Expirées), au lieu de rester visible dans l'onglet Expirées — même bug que Kühlung
  // avant le fix du 2026-08-12, jamais appliqué ici. Pas besoin d'un 2e état séparé comme
  // kuhlungExpiredRemoved : redStoreRecords n'est utilisé que par Réductions/Historique
  // complet, jamais par un écran Contrôle qui aurait besoin du filtre par défaut.
  const loadRedStoreRecords = () => {
    apiJSON("/api/records?source=STORE&includeExpiredRemoved=1")
      .then((list) => setRedStoreRecords(Array.isArray(list) ? list : []))
      .catch(() => {});
  };
  // Trocken/Frisch Fleisch/Frisch Brot — comme Kühlung (aucun filtre controlDate) : ce sont
  // des articles de catalogue persistants dont la MHD reste valable au-delà du jour de
  // saisie, contrairement à Magasin (loadStoreRecords, ad-hoc, controlDate=jour même).
  const loadCzRecords = () => {
    Promise.all([
      apiJSON("/api/records?source=TROCKEN").catch(() => []),
      apiJSON("/api/records?source=FRISCHFLEISCH").catch(() => []),
      apiJSON("/api/records?source=FRISCHBROT_BROT").catch(() => []),
    ]).then(([a, b, c]) => setCzRecords([
      ...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : []), ...(Array.isArray(c) ? c : []),
    ])).catch(() => {});
  };
  // Voir czExpiredRemoved plus haut — même patron que loadKuhlungExpiredRemoved, gardé
  // séparé de czRecords (utilisé tel quel par les écrans Contrôle des 3 zones) pour ne
  // jamais faire réapparaître une MHD nettoyée dans leur propre liste de travail.
  const loadCzExpiredRemoved = () => {
    Promise.all([
      apiJSON("/api/records?source=TROCKEN&includeExpiredRemoved=1").catch(() => []),
      apiJSON("/api/records?source=FRISCHFLEISCH&includeExpiredRemoved=1").catch(() => []),
      apiJSON("/api/records?source=FRISCHBROT_BROT&includeExpiredRemoved=1").catch(() => []),
    ]).then(([a, b, c]) => setCzExpiredRemoved(
      [...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : []), ...(Array.isArray(c) ? c : [])]
        .filter((r) => r.removedFromControlAt)
    )).catch(() => {});
  };
  const openCzZone = (id) => {
    const cz = CONTROL_ZONES[id];
    if (!cz) return;
    setCzZone(cz); setCzQuery(""); setCzMhdArt(null); setCzEditId(null);
    setScreen("czEntry");
  };
  const openCzMhdForm = (art, rec) => {
    setCzMhdArt(art);
    setCzEditId(rec ? rec.id : null);
    setCzMhdForm(rec ? { mhd: rec.mhd, qty: String(rec.quantity ?? "1"), note: rec.note || "", photo: rec.photo || "" } : { mhd: "", qty: "1", note: "", photo: "" });
  };
  // Trocken/Frisch Fleisch/Frisch Brot n'ont AUCUN catalogue réel importé côté serveur
  // (contrairement à Kühlung, 379 articles) — la recherche ci-dessus reste donc
  // systématiquement vide pour ces 3 zones. Avant, le seul contournement possible passait
  // par l'écran "DLC Articles promotionnels Frisch Fleisch" dont la fiche de création
  // s'ouvre sur un bouton photo/scan en premier — pas ce que l'utilisateur voulait pour un
  // simple formulaire de saisie MHD (retour utilisateur explicite). `manual: true` rend le
  // nom éditable dans la fiche ci-dessous ; saveCzMhd() n'a besoin d'aucune modification,
  // il lit déjà art.artNr/art.name tels quels.
  const openCzManualArticle = () => {
    setCzQuery("");
    setCzEditId(null);
    setCzMhdArt({ artNr: "", name: "", manual: true });
    setCzMhdForm({ mhd: "", qty: "1", note: "", photo: "" });
  };
  // Même patron que uploadHaccpPhoto : compression client, /api/store-photos générique,
  // l'URL renvoyée est stockée dans czMhdForm.photo puis persistée dans le blob JSON du
  // mhd_record sous le même nom de champ `photo` déjà utilisé côté serveur (normalizeRecord,
  // POST /api/records) — pas de nouveau nom de champ, PATCH complété pour le préserver aussi.
  const uploadCzPhoto = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setCzUploading(true);
    const compressed = await compressImageFile(file);
    const formData = new FormData();
    formData.append("photo", compressed);
    apiJSON("/api/store-photos", { method: "POST", body: formData })
      .then((data) => setCzMhdForm((f) => ({ ...f, photo: data.url })))
      .catch((err) => showToast(err.message || t("err.photoSendImpossible", "Envoi de la photo impossible.")))
      .finally(() => setCzUploading(false));
  };
  const saveCzMhd = () => {
    if (czMhdArt?.manual && !czMhdArt.name.trim()) { showToast(t("control.czEntry.manualNameRequired", "Donne un nom à l'article.")); return; }
    if (!czMhdForm.mhd) { showToast(t("controlRun.chooseMhdDate", "Choisis une date MAD.")); return; }
    if (czMhdForm.mhd < todayISO()) { showToast(t("controlRun.mhdBeforeTodayError", "Impossible d'enregistrer une MHD avant aujourd'hui.")); return; }
    const art = czMhdArt;
    if (czEditId) {
      setCzRecords((rs) => rs.map((r) => r.id === czEditId ? { ...r, mhd: czMhdForm.mhd, quantity: czMhdForm.qty, note: czMhdForm.note, photo: czMhdForm.photo } : r));
      apiJSON(`/api/records/${czEditId}`, {
        method: "PATCH",
        body: JSON.stringify({ mhd: czMhdForm.mhd, quantity: czMhdForm.qty, note: czMhdForm.note, photo: czMhdForm.photo }),
      }).catch(() => loadCzRecords());
      showToast(t("toast.mhdUpdated", "MHD mise à jour ✓"));
    } else {
      const tempId = "tmp" + Date.now();
      setCzRecords((rs) => [...rs, {
        id: tempId, artNr: art.artNr, name: art.name, mhd: czMhdForm.mhd, quantity: czMhdForm.qty,
        note: czMhdForm.note.trim(), photo: czMhdForm.photo, source: czZone.source, zone: czZone.zone, employee: user?.firstname || "Toi", controlDate: todayISO(),
      }]);
      if (art.artNr) markTouched(art.artNr, "MHD_ENREGISTREE");
      apiJSON("/api/records", {
        method: "POST",
        body: JSON.stringify({
          artNr: art.artNr, name: art.name, mhd: czMhdForm.mhd, quantity: czMhdForm.qty,
          note: czMhdForm.note.trim(), photo: czMhdForm.photo, source: czZone.source, zone: czZone.zone,
          employee: user?.firstname || "Toi", controlDate: todayISO(),
        }),
      })
        .then((created) => setCzRecords((rs) => rs.map((r) => r.id === tempId ? created : r)))
        .catch(() => setCzRecords((rs) => rs.filter((r) => r.id !== tempId)));
      showToast(t("toast.mhdQtySavedTemplate", { date: fmtFR(czMhdForm.mhd), qty: czMhdForm.qty || "?" }, `✓ ${fmtFR(czMhdForm.mhd)} · Qté ${czMhdForm.qty || "?"} enregistrée`));
    }
    setCzMhdArt(null); setCzEditId(null);
  };
  const deleteCzRecord = (rec) => {
    setCzRecords((rs) => rs.filter((r) => r.id !== rec.id));
    apiJSON(`/api/records/${rec.id}`, { method: "DELETE" }).catch(() => loadCzRecords());
    showToast(t("toast.mhdDeleted", "MHD supprimée"));
  };
  const loadStoreTrash = () => {
    apiJSON("/api/records?source=STORE&trashed=1")
      .then((list) => setStoreTrash(Array.isArray(list) ? list : []))
      .catch(() => {});
  };
  // "DLC Articles promotionnels kühlung" (écran "newArticles") — ce sont les vrais
  // articles de la zone spéciale "Kühlung-Nouveaux". Leurs MHD/DLC sont de vrais
  // mhd_records source:"KUEHLUNG" — déjà chargés dans `records` par loadKuhlungRecords()
  // (fetch non filtré par zone), donc pas besoin d'un 2e fetch ici, juste la liste
  // d'articles elle-même. Port réel de public/modules/control-new-articles.js (plusieurs
  // MHD par article, pas une seule) — voir openNewArtSheet/saveNewArtMhd/deleteNewArtMhd.
  const loadNewArticles = () => {
    apiJSON("/api/articles")
      .then((list) => setNewArticles((Array.isArray(list) ? list : []).filter((a) => a.zone === "Kühlung-Nouveaux")))
      .catch(() => {});
  };
  const loadNewArticlesFF = () => {
    apiJSON("/api/articles")
      .then((list) => setNewArticlesFF((Array.isArray(list) ? list : []).filter((a) => a.zone === "Frisch Fleisch-Nouveaux")))
      .catch(() => {});
  };
  const emptyNewArtForm = { artNr: "", recordId: "", name: "", mhd: "", qty: "", note: "" };
  const [newArtForm, setNewArtForm] = useState(emptyNewArtForm);
  const openNewArtSheet = (artNr, recordId) => {
    const article = newArticles.find((a) => a.artNr === artNr);
    if (recordId) {
      const rec = records.find((r) => r.id === recordId);
      setNewArtForm({ artNr, recordId, name: article?.name || "", mhd: rec?.mhd || "", qty: rec?.quantity || "", note: rec?.note || "" });
    } else {
      setNewArtForm({ artNr, recordId: "", name: article?.name || "", mhd: "", qty: "", note: "" });
    }
    setSheet("newArtMhd");
  };
  const saveNewArtMhd = () => {
    const { artNr, recordId, name, mhd, qty, note } = newArtForm;
    if (!mhd) { showToast(t("toast.chooseDlcDate", "Choisis une date DLC.")); return; }
    if (mhd < todayISO()) { showToast(t("controlStore.cannotSaveBeforeToday", "Impossible d'enregistrer une DLC avant aujourd'hui.")); return; }
    const duplicate = records.some((r) => r.artNr === artNr && r.mhd === mhd && r.id !== recordId);
    if (duplicate) { showToast(t("toast.dlcDateAlreadyRegistered", "Cette date DLC est déjà enregistrée pour cet article.")); return; }
    setSheet(null);
    if (recordId) {
      setRecords((rs) => rs.map((r) => r.id === recordId ? { ...r, mhd, quantity: qty, note } : r));
      apiJSON(`/api/records/${recordId}`, { method: "PATCH", body: JSON.stringify({ mhd, quantity: qty, note }) })
        .then(() => showToast(t("toast.dlcSaved", "✓ DLC enregistrée")))
        .catch((err) => { loadKuhlungRecords(); showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")); });
    } else {
      const tempId = "tmp" + Date.now();
      setRecords((rs) => [...rs, { id: tempId, artNr, name, mhd, quantity: qty, note, source: "KUEHLUNG", employee: user?.firstname || "Toi", controlDate: todayISO() }]);
      // Voir saveStore (même correctif, 2026-08-19) : le toast de succès ne devient réel
      // qu'après confirmation serveur, sinon un échec silencieux (ex. date déjà enregistrée)
      // retirait l'article de la liste "Kühlung (Aktion)" sans jamais avertir l'utilisateur.
      apiJSON("/api/records", {
        method: "POST",
        body: JSON.stringify({ artNr, name, mhd, quantity: qty, note, source: "KUEHLUNG", employee: user?.firstname || "Toi", controlDate: todayISO() }),
      })
        .then((created) => { setRecords((rs) => rs.map((r) => r.id === tempId ? created : r)); showToast(t("toast.dlcSaved", "✓ DLC enregistrée")); })
        .catch((err) => { setRecords((rs) => rs.filter((r) => r.id !== tempId)); showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")); });
    }
  };
  const deleteNewArtMhd = (recordId) => {
    setRecords((rs) => rs.filter((r) => r.id !== recordId));
    apiJSON(`/api/records/${recordId}`, { method: "DELETE" }).catch(() => loadKuhlungRecords());
    showToast(t("toast.mhdDeletedEmoji", "🗑️ MHD supprimée"));
  };
  // Même mécanisme que ci-dessus (openNewArtSheet/saveNewArtMhd/deleteNewArtMhd), version
  // Frisch Fleisch : lit/écrit czRecords (source FRISCHFLEISCH), pas records.
  const emptyNewArtFormFF = { artNr: "", recordId: "", name: "", mhd: "", qty: "", note: "" };
  const [newArtFormFF, setNewArtFormFF] = useState(emptyNewArtFormFF);
  const openNewArtSheetFF = (artNr, recordId) => {
    const article = newArticlesFF.find((a) => a.artNr === artNr);
    if (recordId) {
      const rec = czRecords.find((r) => r.id === recordId);
      setNewArtFormFF({ artNr, recordId, name: article?.name || "", mhd: rec?.mhd || "", qty: rec?.quantity ?? rec?.qty ?? "", note: rec?.note || "" });
    } else {
      setNewArtFormFF({ artNr, recordId: "", name: article?.name || "", mhd: "", qty: "", note: "" });
    }
    setSheet("newArtMhdFF");
  };
  const saveNewArtMhdFF = () => {
    const { artNr, recordId, name, mhd, qty, note } = newArtFormFF;
    if (!mhd) { showToast(t("toast.chooseDlcDate", "Choisis une date DLC.")); return; }
    if (mhd < todayISO()) { showToast(t("controlStore.cannotSaveBeforeToday", "Impossible d'enregistrer une DLC avant aujourd'hui.")); return; }
    const duplicate = czRecords.some((r) => r.source === "FRISCHFLEISCH" && r.artNr === artNr && r.mhd === mhd && r.id !== recordId);
    if (duplicate) { showToast(t("toast.dlcDateAlreadyRegistered", "Cette date DLC est déjà enregistrée pour cet article.")); return; }
    setSheet(null);
    if (recordId) {
      setCzRecords((rs) => rs.map((r) => r.id === recordId ? { ...r, mhd, quantity: qty, note } : r));
      apiJSON(`/api/records/${recordId}`, { method: "PATCH", body: JSON.stringify({ mhd, quantity: qty, note }) })
        .then(() => showToast(t("toast.dlcSaved", "✓ DLC enregistrée")))
        .catch((err) => { loadCzRecords(); showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")); });
    } else {
      const tempId = "tmp" + Date.now();
      setCzRecords((rs) => [...rs, { id: tempId, artNr, name, mhd, quantity: qty, note, source: "FRISCHFLEISCH", employee: user?.firstname || "Toi", controlDate: todayISO() }]);
      // Même correctif que saveNewArtMhd/saveStore (2026-08-19).
      apiJSON("/api/records", {
        method: "POST",
        body: JSON.stringify({ artNr, name, mhd, quantity: qty, note, source: "FRISCHFLEISCH", employee: user?.firstname || "Toi", controlDate: todayISO() }),
      })
        .then((created) => { setCzRecords((rs) => rs.map((r) => r.id === tempId ? created : r)); showToast(t("toast.dlcSaved", "✓ DLC enregistrée")); })
        .catch((err) => { setCzRecords((rs) => rs.filter((r) => r.id !== tempId)); showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")); });
    }
  };
  const deleteNewArtMhdFF = (recordId) => {
    setCzRecords((rs) => rs.filter((r) => r.id !== recordId));
    apiJSON(`/api/records/${recordId}`, { method: "DELETE" }).catch(() => loadCzRecords());
    showToast(t("toast.mhdDeletedEmoji", "🗑️ MHD supprimée"));
  };
  // Bouton "+" niveau écran (retour utilisateur explicite) : jusqu'ici impossible d'ajouter
  // un article JAMAIS vu (newArticles/newArticlesFF ne listent que des articles déjà créés
  // en base avec zone "Kühlung-Nouveaux"/"Frisch Fleisch-Nouveaux" — aucune fiche de création
  // n'existait). Crée la fiche catalogue (zone spéciale, statut actif) PUIS sa première DLC,
  // partagé entre les deux familles (même opération, seuls zone/source/état changent).
  const emptyNewPromoArtForm = { family: "KUEHLUNG", artNr: "", name: "", mhd: "", qty: "", note: "", photo: "", rule: "AUTO" };
  const [newPromoArtForm, setNewPromoArtForm] = useState(emptyNewPromoArtForm);
  const [newPromoArtPhotoUploading, setNewPromoArtPhotoUploading] = useState(false);
  const newPromoArtPhotoInputRef = useRef(null);
  const handleNewPromoArtPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNewPromoArtPhotoUploading(true);
    const body = new FormData();
    body.append("photo", file);
    apiJSON("/api/store-photos", { method: "POST", body })
      .then(({ url }) => setNewPromoArtForm((f) => ({ ...f, photo: url })))
      .catch((err) => showToast(err.message || t("err.photoSendImpossible", "Envoi de la photo impossible.")))
      .finally(() => setNewPromoArtPhotoUploading(false));
  };
  const openCreatePromoArtSheet = (family) => { setNewPromoArtForm({ ...emptyNewPromoArtForm, family }); setSheet("newPromoArt"); };
  const saveNewPromoArt = () => {
    const { family, artNr, name, mhd, qty, note, photo, rule } = newPromoArtForm;
    const artNrT = artNr.trim(), nameT = name.trim();
    if (!artNrT) { showToast(t("toast.artNrRequired", "Art.Nr requis.")); return; }
    if (!nameT) { showToast(t("toast.articleNameRequired", "Nom de l'article requis.")); return; }
    if (!mhd) { showToast(t("toast.chooseDlcDate", "Choisis une date DLC.")); return; }
    if (mhd < todayISO()) { showToast(t("controlStore.cannotSaveBeforeToday", "Impossible d'enregistrer une DLC avant aujourd'hui.")); return; }
    const isFF = family === "FRISCHFLEISCH";
    const zone = isFF ? "Frisch Fleisch-Nouveaux" : "Kühlung-Nouveaux";
    const already = (isFF ? newArticlesFF : newArticles).some((a) => a.artNr === artNrT);
    if (already) { showToast(t("toast.articleAlreadyInList", "Cet article est déjà dans la liste — ajoute une DLC depuis sa fiche.")); return; }
    setSheet(null);
    apiJSON("/api/articles", { method: "POST", body: JSON.stringify({ artNr: artNrT, name: nameT, zone, status: "active" }) })
      .then((createdArt) => {
        if (isFF) setNewArticlesFF((as) => [...as, createdArt]); else setNewArticles((as) => [...as, createdArt]);
        const tempId = "tmp" + Date.now();
        const recPayload = { artNr: artNrT, name: nameT, mhd, quantity: qty, note, photo, source: family, employee: user?.firstname || "Toi", controlDate: todayISO() };
        if (isFF) setCzRecords((rs) => [...rs, { id: tempId, ...recPayload }]);
        else setRecords((rs) => [...rs, { id: tempId, ...recPayload }]);
        // Même correctif que saveStore/saveNewArtMhd(FF) (2026-08-19) : le toast de succès
        // n'attendait AVANT la confirmation ni de la création d'article NI de celle du
        // premier enregistrement DLC — un échec de ce second appel effaçait silencieusement
        // l'entrée sans jamais avertir.
        apiJSON("/api/records", { method: "POST", body: JSON.stringify(recPayload) })
          .then((createdRec) => {
            if (isFF) setCzRecords((rs) => rs.map((r) => r.id === tempId ? createdRec : r));
            else setRecords((rs) => rs.map((r) => r.id === tempId ? createdRec : r));
            showToast(t("toast.promoArticleSaved", "Article promotionnel enregistré ✓"));
            // Règle de réduction facultative choisie dans le même formulaire (retour
            // utilisateur explicite, 2026-08-19) — appliquée seulement une fois la DLC
            // réellement confirmée en base.
            if (rule && rule !== "AUTO") setArticleRule(artNrT, rule, nameT);
          })
          .catch((err) => {
            if (isFF) setCzRecords((rs) => rs.filter((r) => r.id !== tempId));
            else setRecords((rs) => rs.filter((r) => r.id !== tempId));
            showToast(err.message || t("err.savingImpossible", "Enregistrement impossible."));
          });
      })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };

  // Réductions — vraies données. done_records (recId/doneId/...) et la corbeille
  // générique (records de N'IMPORTE quelle source mis en corbeille, pas propre à
  // Réductions ni à STORE) sont chargés ici, pas dans le bloc Contrôle ci-dessus.
  const loadReduced = () => {
    apiJSON("/api/done")
      .then((list) => setReduced((Array.isArray(list) ? list : []).map((d) => ({
        recId: d.recordId, doneId: d.id, doneAt: d.doneAt, source: d.source,
        artNr: d.artNr, name: d.name, mhd: d.mhd, quantity: d.quantity, zone: d.zone, employee: d.user,
      }))))
      .catch(() => {});
  };
  const loadRedTrash = () => {
    apiJSON("/api/records?trashed=1")
      .then((list) => setRedTrash((Array.isArray(list) ? list : []).map((r) => ({ ...r, trashedAt: r.updatedAt || r.createdAt }))))
      .catch(() => {});
  };
  // Dérogations de règle réelles (GET /api/reductions/rule-overrides) — même table que
  // reductions.js vanilla et sfReductionDateFor côté serveur, jamais une copie locale.
  const loadRuleOverrides = () => {
    apiJSON("/api/reductions/rule-overrides")
      .then((data) => {
        const list = (data && Array.isArray(data.overrides)) ? data.overrides : [];
        const map = {};
        // Clé Art.Nr en priorité, EAN sinon (article Magasin en saisie libre sans Art.Nr —
        // retour utilisateur explicite 2026-08-21) — même clé effective que le serveur
        // (sfReductionOverrideKey), jamais Art.Nr seul.
        list.forEach((a) => { const key = a.artNr || a.ean; if (key) map[key] = a.rule; });
        setRuleOverrides(map);
      })
      .catch(() => {});
  };
  // Liste gérable des règles J-X disponibles (2026-08-17) — voir ruleDefinitionsMap.
  const loadRuleDefinitions = () => {
    apiJSON("/api/reductions/rule-definitions")
      .then((data) => setRuleDefinitions((data && Array.isArray(data.definitions)) ? data.definitions : []))
      .catch(() => {});
  };
  // Défauts de règle par groupe (2026-08-17) — voir sourceDefaults/redRulesMode ci-dessus.
  const loadSourceDefaults = () => {
    apiJSON("/api/reductions/source-defaults")
      .then((data) => setSourceDefaults((data && data.defaults && typeof data.defaults === "object") ? data.defaults : {}))
      .catch(() => {});
  };
  const saveSourceDefault = (source, ruleId) => {
    apiJSON("/api/reductions/source-defaults", { method: "PUT", body: JSON.stringify({ source, ruleId }) })
      .then((data) => {
        const next = (data && data.defaults && typeof data.defaults === "object") ? data.defaults : null;
        setSourceDefaults((d) => next || { ...d, [source]: ruleId });
        showToast(t("toast.sourceDefaultUpdated", "⚙️ Règle par défaut mise à jour"));
      })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };

  // Tâches — vraies données. Le serveur stocke type/category/statuses{[storeId]} — on
  // reconstruit la forme locale {scope,type,status,assignee...} déjà consommée par le JSX.
  const mapServerTask = (t) => {
    const storeStatus = (t.statuses && (t.statuses[user?.store] || t.statuses[t.storeId])) || {};
    // "late" (2026-08-30, retour utilisateur : le popup "Tâches à faire" ne doit plus se
    // déclencher une fois l'échéance dépassée, la tâche doit alors s'afficher "Pas fait"
    // jusqu'au clic sur "Terminer") — calculé côté serveur (t.late, voir GET /api/tasks,
    // même règle sfTaskIsLateForStore que le reste de l'app, jours fériés inclus), jamais
    // recomparé ici avec une date naïve.
    const status = t.archived ? "archived" : (storeStatus.state === "done" ? "done" : t.late ? "late" : "open");
    return {
      id: t.id, scope: t.type, storeId: t.storeId,
      type: t.category === "bakery" ? "bakery" : t.category === "plu_exam" ? "pluExam" : "standard",
      title: t.title,
      recipient: t.targetEmployeeId ? "specific" : "team",
      assignee: t.targetEmployeeId ? resolveEmployeeName(t.targetEmployeeId) : null,
      targetEmployeeId: t.targetEmployeeId || "",
      stores: t.type === "network" ? "all" : undefined,
      deadline: t.deadline, priority: t.priority, status,
      createdBy: t.createdBy, createdById: t.createdById,
      completedBy: storeStatus.doneBy || undefined,
      note: t.description,
      bakeryItems: (t.bakeryItems || []).map((b) => ({ name: b.name, qty: b.quantity, artNr: b.artNr || "", unit: b.unit || "" })),
      pluListId: t.pluListId,
    };
  };
  const loadTasks = () => {
    apiJSON("/api/tasks")
      .then((data) => setTasks((data.tasks || []).map(mapServerTask)))
      .catch(() => {});
  };
  const loadTaskEmployees = () => {
    apiJSON("/api/tasks/store-employees")
      .then((data) => setTaskEmployees(data.employees || []))
      .catch(() => {});
  };
  // Jours fériés réels — le Land de la filiale est déjà résolu côté serveur
  // (sfStoreLand), jamais choisi ici (voir commentaire module-level ci-dessus).
  const loadPublicHolidays = () => {
    apiJSON(`/api/public-holidays?storeId=${encodeURIComponent(user?.store)}`)
      .then((data) => setPublicHolidays(data.holidays || []))
      .catch(() => {});
  };

  // Heures d'ouverture/fermeture (2026-08-31, retour utilisateur explicite) — GET
  // /api/stores/:id plutôt que le registre entier (storesCatalog, pas chargé pour un simple
  // employé et potentiellement 1200+ filiales) : ne récupère que la fiche de SA filiale.
  // openTime/closeTime restent "" tant que non configurés côté Admin > Filiales — tous les
  // consommateurs (pilule Accueil, échéance Réductions, gate popup Tâches) doivent alors
  // garder leur comportement actuel plutôt que planter (voir computeStoreOpenStatus).
  const loadMyStoreHours = () => {
    if (!user?.store) return;
    apiJSON(`/api/stores/${encodeURIComponent(user.store)}`)
      .then((data) => setMyStoreHours({ openTime: data.store?.openTime || "", closeTime: data.store?.closeTime || "" }))
      .catch(() => {});
  };

  // Commande de matériel — vraies données. "bureau" n'a pas d'équivalent serveur (voir
  // MATERIAL_CATALOGS_SEED) — seul "technique" est écrasé par le vrai catalogue.
  const mapServerOrder = (o) => ({
    id: o.id, catalogId: o.catalogId, status: o.sentAt ? "sent" : "pending",
    createdBy: o.employeeName, createdAt: o.createdAt, sentBy: o.sentBy, sentAt: o.sentAt,
    note: o.note, items: (o.items || []).map((i) => ({ id: i.articleId, name: i.name, artNr: i.artNr, ve: i.ve, qty: i.qty })),
  });
  const loadMaterialCatalog = () => {
    apiJSON("/api/material-orders/catalog")
      .then((data) => setMaterialCatalogs((prev) => ({ ...prev, ...(data.catalogs || {}) })))
      .catch(() => {});
  };
  const loadMaterialOrders = () => {
    apiJSON("/api/material-orders")
      .then((data) => setMaterialOrders((data.orders || []).map(mapServerOrder)))
      .catch(() => {});
  };

  // Réclamations — vraies données. deleted(réel)↔trashed(local), pieces+kilos
  // séparés(réel)↔qtyType+qty(local) — voir submitClaim pour le sens inverse.
  const mapServerClaim = (c) => ({
    id: c.id, type: c.type, status: c.status, trashed: !!c.deleted,
    createdAt: c.createdAt, createdBy: c.createdBy, sentAt: c.sentAt,
    sentVia: c.sentVia || "", sentTo: c.sentTo || "",
    items: (c.items || []).map((i) => ({
      id: i.id, artNr: i.artNr, name: i.name,
      qtyType: i.pieces !== "" && i.pieces != null ? "pieces" : i.cartons !== "" && i.cartons != null ? "cartons" : "kilos",
      qty: i.pieces !== "" && i.pieces != null ? i.pieces : i.cartons !== "" && i.cartons != null ? i.cartons : i.kilos,
      chargeLossNr: i.chargeLossNr, defect: i.defect, note: i.note, photos: i.photos || [],
    })),
  });
  const loadClaims = () => {
    Promise.all([apiJSON("/api/claims"), apiJSON("/api/claims?trash=true")])
      .then(([open, trash]) => setClaims([...(open.claims || []), ...(trash.claims || [])].map(mapServerClaim)))
      .catch(() => {});
  };
  const loadClaimSettings = () => {
    apiJSON("/api/claims/settings")
      .then((data) => {
        const s = data.settings || {};
        setClaimDefects({ fresh: s.defectsFresh || CLAIM_DEFECTS_DEFAULT.fresh, food: s.defectsFood || CLAIM_DEFECTS_DEFAULT.food });
        setClaimDestination({ method: s.method || null, value: s.value || "" });
      })
      .catch(() => {});
  };
  const loadFreshArticles = () => {
    apiJSON("/api/claims/fresh-articles")
      .then((data) => setFreshArticles(data.articles || []))
      .catch(() => {});
  };

  // Warenaustausch (2026-08-23) — transferts de marchandises. Le serveur recalcule tout
  // (prix figé, montant sur quantité VÉRIFIÉE, écritures Kontoauszug) — le client ne fait
  // jamais de calcul financier lui-même.
  const loadWarenaustauschOutgoing = () => {
    apiJSON("/api/warenaustausch/outgoing").then((data) => setWaOutgoing(data.transfers || [])).catch(() => {});
  };
  const loadWarenaustauschInbox = () => {
    apiJSON("/api/warenaustausch/inbox").then((data) => setWaInbox(data.transfers || [])).catch(() => {});
  };
  const loadKontoauszug = () => {
    apiJSON("/api/kontoauszug").then((data) => setWaKonto(data)).catch(() => {});
  };
  const waFindTransfer = (id) => waOutgoing.find((t) => t.id === id) || waInbox.find((t) => t.id === id) || null;
  const openWarenaustauschNew = () => {
    setWaNewForm(emptyWaForm); setWaDetailId(null); setWaCartonsDraft("0"); setWaPalletsDraft("0");
    setWaScreen("new");
  };
  const createWarenaustauschTransfer = () => {
    const body = waNewForm.kind === "warenrueckfuehrung"
      ? { kind: "warenrueckfuehrung", category: waNewForm.category }
      : { kind: "filiale_transfer", toStoreId: waNewForm.toStoreId };
    if (waNewForm.kind === "warenrueckfuehrung" && !waNewForm.category) { showToast(t("toast.chooseCategory", "Choisis une catégorie.")); return; }
    if (waNewForm.kind === "filiale_transfer" && !waNewForm.toStoreId) { showToast(t("toast.chooseDestinationStore", "Choisis la filiale destinataire.")); return; }
    apiJSON("/api/warenaustausch", { method: "POST", body: JSON.stringify(body) })
      .then((data) => {
        // Insère directement le transfert créé dans l'état local — jamais dépendre du
        // rechargement asynchrone (loadWarenaustauschOutgoing) pour ouvrir sa fiche
        // détail : bug réel trouvé (retour utilisateur, capture d'écran) — la fiche
        // s'ouvrait AVANT que la liste ait fini de se recharger depuis le serveur,
        // waFindTransfer ne trouvait rien et affichait "Transfert introuvable".
        setWaOutgoing((list) => [data.transfer, ...list]);
        setWaDetailId(data.transfer.id); setWaScanCode(""); setWaScanQty("1");
        loadWarenaustauschOutgoing();
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const openWarenaustauschDetail = (id, fromInbox) => {
    const tr = waOutgoing.find((t) => t.id === id) || waInbox.find((t) => t.id === id);
    setWaDetailId(id); setWaScanCode(""); setWaScanQty("1"); setWaVerifyDraft({});
    setWaCartonsDraft(String(tr?.cartonsCount ?? 0)); setWaPalletsDraft(String(tr?.palletsCount ?? 0));
    setWaScreen(fromInbox ? "receive" : "new");
  };
  const addWarenaustauschLine = () => {
    if (!waDetailId || !waScanCode.trim()) { showToast(t("toast.scanOrEnterCode", "Scanne ou saisis un code article.")); return; }
    const qty = Number(waScanQty);
    if (!Number.isFinite(qty) || qty <= 0) { showToast(t("toast.invalidQuantity", "Quantité invalide.")); return; }
    setWaScanBusy(true);
    apiJSON(`/api/warenaustausch/${encodeURIComponent(waDetailId)}/lines`, {
      method: "POST", body: JSON.stringify({ code: waScanCode.trim(), quantitySent: qty }),
    })
      .then((data) => {
        setWaOutgoing((list) => list.map((t) => t.id === data.transfer.id ? data.transfer : t));
        setWaScanCode(""); setWaScanQty("1");
        showToast(t("toast.articleAdded", "✓ Article ajouté"));
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setWaScanBusy(false));
  };
  const removeWarenaustauschLine = (transferId, lineId) => {
    apiJSON(`/api/warenaustausch/${encodeURIComponent(transferId)}/lines/${encodeURIComponent(lineId)}`, { method: "DELETE" })
      .then((data) => setWaOutgoing((list) => list.map((t) => t.id === data.transfer.id ? data.transfer : t)))
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  // Cartons/palettes utilisés (retour utilisateur explicite, 2026-08-26) — sauvegardés au
  // blocage du champ (onBlur), pas à chaque frappe, pour éviter une rafale de PATCH.
  const saveWaCartonsPallets = (id) => {
    apiJSON(`/api/warenaustausch/${encodeURIComponent(id)}`, {
      method: "PATCH", body: JSON.stringify({ cartonsCount: Number(waCartonsDraft) || 0, palletsCount: Number(waPalletsDraft) || 0 }),
    })
      .then((data) => setWaOutgoing((list) => list.map((t) => t.id === data.transfer.id ? data.transfer : t)))
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const submitWaSignature = () => {
    const pad = waSigPadRef.current;
    if (!pad || !pad.hasValidSignature() || !waDetailId) return;
    setWaBusy(true);
    apiJSON(`/api/warenaustausch/${encodeURIComponent(waDetailId)}/sign`, {
      method: "POST", body: JSON.stringify({ signatureDataUrl: pad.toDataURL(), strokeLength: pad.getStrokeLength() }),
    })
      .then((data) => { setWaOutgoing((list) => list.map((t) => t.id === data.transfer.id ? data.transfer : t)); showToast(t("toast.signatureSaved", "✓ Signature enregistrée")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setWaBusy(false));
  };
  const sendWarenaustauschTransfer = (id) => {
    apiJSON(`/api/warenaustausch/${encodeURIComponent(id)}/send`, { method: "POST" })
      .then(() => { setWaScreen("hub"); loadWarenaustauschOutgoing(); showToast(t("toast.transferSent", "📤 Transfert envoyé")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const deleteWarenaustauschTransfer = (id) => {
    if (!window.confirm(t("confirm.deleteDraftTransfer", "Supprimer ce brouillon ?"))) return;
    apiJSON(`/api/warenaustausch/${encodeURIComponent(id)}`, { method: "DELETE" })
      .then(() => { setWaScreen("hub"); loadWarenaustauschOutgoing(); showToast(t("toast.transferDeleted", "🗑️ Transfert supprimé")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const cancelWarenaustauschTransfer = (id) => {
    if (!window.confirm(t("confirm.cancelTransfer", "Annuler ce transfert ?"))) return;
    apiJSON(`/api/warenaustausch/${encodeURIComponent(id)}/cancel`, { method: "POST" })
      .then(() => { setWaScreen("hub"); loadWarenaustauschOutgoing(); showToast(t("toast.transferCancelled", "✕ Transfert annulé")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const verifyWarenaustauschLine = (transferId, lineId) => {
    const raw = waVerifyDraft[lineId];
    const qty = Number(raw);
    if (raw === undefined || raw === "" || !Number.isFinite(qty) || qty < 0) { showToast(t("toast.invalidQuantity", "Quantité invalide.")); return; }
    apiJSON(`/api/warenaustausch/${encodeURIComponent(transferId)}/verify-line`, {
      method: "POST", body: JSON.stringify({ lineId, quantityReceived: qty }),
    })
      .then((data) => setWaInbox((list) => list.map((t) => t.id === data.transfer.id ? data.transfer : t)))
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const receiveWarenaustauschTransfer = (id) => {
    apiJSON(`/api/warenaustausch/${encodeURIComponent(id)}/receive`, { method: "POST" })
      .then(() => { loadWarenaustauschInbox(); loadKontoauszug(); showToast(t("toast.transferReceived", "✅ Réception validée")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const waStoreLabel = (storeId) => storesCatalog.find((s) => s.id === storeId)?.label || storeId;

  // RR (2026-08-29) — bon de vérification de stock Niederlassung → Filiale. Le serveur
  // recalcule tout (numéro, totaux, écart, écritures Kontoauszug) — le client ne fait
  // jamais de calcul financier lui-même (même principe que Warenaustausch).
  // Totaux recalculés côté client UNIQUEMENT pour le mode démo (mêmes formules exactes
  // que repo.computeRrTotals côté serveur, server/rr/repository.js) — en usage réel le
  // serveur reste toujours seul juge (aucun calcul financier client-side hors démo).
  const rrDemoRound2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
  const rrDemoTotals = (lines) => {
    const totalExpected = rrDemoRound2(lines.reduce((s, l) => s + l.expectedQty * l.unitPrice, 0));
    const allFilled = lines.length > 0 && lines.every((l) => l.foundQty != null);
    const totalFound = allFilled ? rrDemoRound2(lines.reduce((s, l) => s + l.foundQty * l.unitPrice, 0)) : null;
    const diff = totalFound == null ? null : rrDemoRound2(totalFound - totalExpected);
    return { totalExpected, totalFound, diff, allFilled };
  };
  const RR_DEMO_SIG = "data:image/svg+xml;utf8," + encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="220" height="80"><path d="M10,60 Q30,10 50,50 T90,40 Q110,70 130,30 T170,45 Q190,20 210,55" stroke="#211d16" stroke-width="4" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`
  );
  // Fixtures 100% en mémoire, jamais écrites au serveur (mode démo — voir toggle sur le
  // hub). Un RR est toujours reçu DEPUIS une Niederlassung, jamais depuis une autre
  // filiale — fromStoreId est donc toujours "NL-DEMO" (jamais une vraie filiale), toStoreId
  // toujours la propre filiale du compte connecté, pour que le hub affiche des bons "reçus"
  // réalistes quel que soit le compte utilisé pour la démo.
  const buildRrDemoBons = () => {
    const myStore = user?.store || "N236";
    const now = Date.now();
    const iso = (daysAgo) => new Date(now - daysAgo * 86400000).toISOString();
    const mkLines = (arr) => arr.map((l, i) => ({
      id: `rrdemo-line-${i}-${l.artNr}`, artNr: l.artNr, name: l.name,
      expectedQty: l.expectedQty, foundQty: l.foundQty === undefined ? null : l.foundQty, unitPrice: l.unitPrice,
    }));
    const nl = { employeeId: "demo-nl", name: "Niederlassung (démo)", role: "Niederlassungsleiter" };
    const me = { employeeId: user?.employeeId || user?.id || "demo", name: user?.name || "Filiale (démo)" };

    const bon1Lines = mkLines([
      { artNr: "40021", name: "Milch 1L", expectedQty: 24, unitPrice: 1.09 },
      { artNr: "40088", name: "Butter 250g", expectedQty: 12, unitPrice: 2.29 },
      { artNr: "41210", name: "Joghurt Erdbeer 500g", expectedQty: 18, unitPrice: 1.49 },
      { artNr: "38765", name: "Eier 10er", expectedQty: 10, unitPrice: 2.79 },
      { artNr: "39980", name: "Käse Gouda 400g", expectedQty: 8, unitPrice: 3.49 },
    ]);
    const bon2Lines = mkLines([
      { artNr: "40021", name: "Milch 1L", expectedQty: 30, unitPrice: 1.09, foundQty: 28 },
      { artNr: "42210", name: "Orangensaft 1L", expectedQty: 16, unitPrice: 1.79, foundQty: -2 },
      { artNr: "40088", name: "Butter 250g", expectedQty: 12, unitPrice: 2.29 },
      { artNr: "44120", name: "Kaffee gemahlen 500g", expectedQty: 6, unitPrice: 4.99, foundQty: 6 },
    ]);
    const bon3Lines = mkLines([
      { artNr: "40021", name: "Milch 1L", expectedQty: 20, unitPrice: 1.09, foundQty: 20 },
      { artNr: "41500", name: "Nutella 450g", expectedQty: 10, unitPrice: 3.99, foundQty: 12 },
      { artNr: "39980", name: "Käse Gouda 400g", expectedQty: 8, unitPrice: 3.49, foundQty: 8 },
      { artNr: "45012", name: "Chips Paprika 175g", expectedQty: 15, unitPrice: 1.29, foundQty: 15 },
    ]);
    const bon4Lines = mkLines([
      { artNr: "40021", name: "Milch 1L", expectedQty: 24, unitPrice: 1.09, foundQty: 24 },
      { artNr: "46300", name: "Cola 1.5L", expectedQty: 12, unitPrice: 1.59, foundQty: 9 },
      { artNr: "41210", name: "Joghurt Erdbeer 500g", expectedQty: 18, unitPrice: 1.49, foundQty: 18 },
      { artNr: "47800", name: "Consigne EW-Pfand", expectedQty: 0, unitPrice: 0.15, foundQty: 0 },
    ]);
    const bon5Lines = mkLines([
      { artNr: "40021", name: "Milch 1L", expectedQty: 10, unitPrice: 1.09 },
      { artNr: "40088", name: "Butter 250g", expectedQty: 5, unitPrice: 2.29 },
    ]);
    // Lot supplémentaire (2026-10-15, retour utilisateur explicite : "ajoute plus de démo
    // dans tous ces modules") — même patron exact que bon1-5, juste plus de volume/variété de
    // statuts pour un échantillon démo plus riche.
    const bon6Lines = mkLines([
      { artNr: "40021", name: "Milch 1L", expectedQty: 36, unitPrice: 1.09 },
      { artNr: "48210", name: "Mehl Type 405 1kg", expectedQty: 14, unitPrice: 0.89 },
      { artNr: "48900", name: "Zucker 1kg", expectedQty: 10, unitPrice: 1.19 },
      { artNr: "41210", name: "Joghurt Erdbeer 500g", expectedQty: 24, unitPrice: 1.49 },
    ]);
    const bon7Lines = mkLines([
      { artNr: "40021", name: "Milch 1L", expectedQty: 18, unitPrice: 1.09, foundQty: 18 },
      { artNr: "49500", name: "Nudeln Spaghetti 500g", expectedQty: 20, unitPrice: 0.79, foundQty: 19 },
      { artNr: "39980", name: "Käse Gouda 400g", expectedQty: 6, unitPrice: 3.49, foundQty: 6 },
      { artNr: "50120", name: "Tomatensauce 400g", expectedQty: 12, unitPrice: 0.99, foundQty: 11 },
      { artNr: "47800", name: "Consigne EW-Pfand", expectedQty: 0, unitPrice: 0.15, foundQty: 0 },
    ]);
    const bon8Lines = mkLines([
      { artNr: "40088", name: "Butter 250g", expectedQty: 20, unitPrice: 2.29, foundQty: 20 },
      { artNr: "51200", name: "Olivenöl 500ml", expectedQty: 8, unitPrice: 4.49, foundQty: 7 },
      { artNr: "41500", name: "Nutella 450g", expectedQty: 14, unitPrice: 3.99, foundQty: 14 },
    ]);

    return [
      { id: "rrdemo-1", number: "RR-2026-DEMO1", fromStoreId: "NL-DEMO", toStoreId: myStore, status: "envoye",
        lines: bon1Lines, cartonsCount: 0, palletsCount: 0, createdBy: nl,
        createdAt: iso(1), updatedAt: iso(1), sentAt: iso(1),
        processedBy: null, processedAt: null, filialeSignatureUrl: "",
        validatedBy: null, validatedAt: null, niederlassungSignatureUrl: "",
        kontoauszugPosted: false, totals: rrDemoTotals(bon1Lines) },
      { id: "rrdemo-2", number: "RR-2026-DEMO2", fromStoreId: "NL-DEMO", toStoreId: myStore, status: "envoye",
        lines: bon2Lines, cartonsCount: 3, palletsCount: 0, createdBy: nl,
        createdAt: iso(2), updatedAt: iso(1), sentAt: iso(2),
        processedBy: null, processedAt: null, filialeSignatureUrl: "",
        validatedBy: null, validatedAt: null, niederlassungSignatureUrl: "",
        kontoauszugPosted: false, totals: rrDemoTotals(bon2Lines) },
      { id: "rrdemo-3", number: "RR-2026-DEMO3", fromStoreId: "NL-DEMO", toStoreId: myStore, status: "confirme",
        lines: bon3Lines, cartonsCount: 4, palletsCount: 1, createdBy: nl,
        createdAt: iso(4), updatedAt: iso(3), sentAt: iso(4),
        processedBy: me, processedAt: iso(3), filialeSignatureUrl: RR_DEMO_SIG,
        validatedBy: null, validatedAt: null, niederlassungSignatureUrl: "",
        kontoauszugPosted: false, totals: rrDemoTotals(bon3Lines) },
      { id: "rrdemo-4", number: "RR-2026-DEMO4", fromStoreId: "NL-DEMO", toStoreId: myStore, status: "valide",
        lines: bon4Lines, cartonsCount: 5, palletsCount: 1, createdBy: nl,
        createdAt: iso(7), updatedAt: iso(6), sentAt: iso(7),
        processedBy: me, processedAt: iso(6.5), filialeSignatureUrl: RR_DEMO_SIG,
        validatedBy: nl, validatedAt: iso(6), niederlassungSignatureUrl: RR_DEMO_SIG,
        kontoauszugPosted: true, totals: rrDemoTotals(bon4Lines) },
      { id: "rrdemo-5", number: "RR-2026-DEMO5", fromStoreId: "NL-DEMO", toStoreId: myStore, status: "annule",
        lines: bon5Lines, cartonsCount: 0, palletsCount: 0, createdBy: nl,
        createdAt: iso(10), updatedAt: iso(9), sentAt: iso(10),
        processedBy: null, processedAt: null, filialeSignatureUrl: "",
        validatedBy: null, validatedAt: null, niederlassungSignatureUrl: "",
        kontoauszugPosted: false, totals: rrDemoTotals(bon5Lines) },
      { id: "rrdemo-6", number: "RR-2026-DEMO6", fromStoreId: "NL-DEMO", toStoreId: myStore, status: "envoye",
        lines: bon6Lines, cartonsCount: 2, palletsCount: 0, createdBy: nl,
        createdAt: iso(0.3), updatedAt: iso(0.3), sentAt: iso(0.3),
        processedBy: null, processedAt: null, filialeSignatureUrl: "",
        validatedBy: null, validatedAt: null, niederlassungSignatureUrl: "",
        kontoauszugPosted: false, totals: rrDemoTotals(bon6Lines) },
      { id: "rrdemo-7", number: "RR-2026-DEMO7", fromStoreId: "NL-DEMO", toStoreId: myStore, status: "confirme",
        lines: bon7Lines, cartonsCount: 6, palletsCount: 1, createdBy: nl,
        createdAt: iso(5), updatedAt: iso(4), sentAt: iso(5),
        processedBy: me, processedAt: iso(4), filialeSignatureUrl: RR_DEMO_SIG,
        validatedBy: null, validatedAt: null, niederlassungSignatureUrl: "",
        kontoauszugPosted: false, totals: rrDemoTotals(bon7Lines) },
      { id: "rrdemo-8", number: "RR-2026-DEMO8", fromStoreId: "NL-DEMO", toStoreId: myStore, status: "valide",
        lines: bon8Lines, cartonsCount: 3, palletsCount: 0, createdBy: nl,
        createdAt: iso(14), updatedAt: iso(13), sentAt: iso(14),
        processedBy: me, processedAt: iso(13.5), filialeSignatureUrl: RR_DEMO_SIG,
        validatedBy: nl, validatedAt: iso(13), niederlassungSignatureUrl: RR_DEMO_SIG,
        kontoauszugPosted: true, totals: rrDemoTotals(bon8Lines) },
    ];
  };
  const toggleRrDemoMode = () => {
    const next = !rrDemoMode;
    setRrDemoMode(next);
    if (next) setRrList(buildRrDemoBons());
    else loadRrList();
  };
  const loadRrList = () => {
    if (rrDemoMode) { setRrList(buildRrDemoBons()); return; }
    setRrLoading(true);
    apiJSON("/api/rr").then((data) => setRrList(data.bons || [])).catch(() => setRrList([])).finally(() => setRrLoading(false));
  };
  const rrFindBon = (id) => rrList.find((b) => b.id === id) || null;
  // Pas de nouvelle route serveur pour ça — storesCatalog (GET /api/stores) renvoie déjà
  // les filiales normalisées, kind inclus. Purement informatif côté client (affiche/masque
  // le bouton "Créer" et adapte le libellé des écrans) — le vrai gate reste toujours
  // server-side (isRrCreator, server/rr/routes.js), jamais fait confiance ici seul.
  const rrIsNiederlassung = () => storesCatalog.find((s) => s.id === user?.store)?.kind === "niederlassung" || isNexoraAdmin;
  const openRrCreate = () => { setRrCreateForm(emptyRrCreateForm); setRrScreen("create"); };
  // Créé PUIS envoyé en un seul geste (jamais un brouillon à rouvrir séparément) — la
  // Niederlassung compose sa liste en une fois depuis un vrai bon papier déjà complet,
  // contrairement à Warenaustausch qui scanne article par article dans le temps.
  const createRrBon = () => {
    if (!rrCreateForm.toStoreId.trim()) { showToast(t("toast.chooseDestinationStore", "Choisis la filiale destinataire.")); return; }
    const lines = rrCreateForm.lines.filter((l) => l.artNr.trim() && l.name.trim());
    if (!lines.length) { showToast(t("rr.toast.needLines", "Ajoute au moins un article.")); return; }
    if (rrDemoMode) {
      const demoLines = lines.map((l, i) => ({ id: `rrdemo-line-new-${i}-${Date.now()}`, artNr: l.artNr.trim(), name: l.name.trim(), expectedQty: Number(l.expectedQty) || 0, foundQty: null, unitPrice: Number(l.unitPrice) || 0 }));
      const demoBon = {
        id: `rrdemo-new-${Date.now()}`, number: `RR-2026-DEMO${rrList.length + 1}`,
        fromStoreId: "NL-DEMO", toStoreId: rrCreateForm.toStoreId.trim(), status: "envoye",
        lines: demoLines, cartonsCount: 0, palletsCount: 0,
        createdBy: { employeeId: user?.employeeId || user?.id || "demo", name: user?.name || "Démo", role: user?.role || "" },
        createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), sentAt: new Date().toISOString(),
        processedBy: null, processedAt: null, filialeSignatureUrl: "",
        validatedBy: null, validatedAt: null, niederlassungSignatureUrl: "",
        kontoauszugPosted: false,
      };
      demoBon.totals = rrDemoTotals(demoLines);
      setRrList((list) => [demoBon, ...list]);
      showToast(t("rr.toast.created", { number: demoBon.number }, `📤 ${demoBon.number} créé et envoyé`));
      setRrScreen("hub");
      return;
    }
    setRrCreateBusy(true);
    apiJSON("/api/rr", {
      method: "POST",
      body: JSON.stringify({
        toStoreId: rrCreateForm.toStoreId.trim(),
        lines: lines.map((l) => ({ artNr: l.artNr.trim(), name: l.name.trim(), expectedQty: Number(l.expectedQty) || 0, unitPrice: Number(l.unitPrice) || 0 })),
      }),
    })
      .then((data) => apiJSON(`/api/rr/${encodeURIComponent(data.bon.id)}/send`, { method: "POST" }))
      .then((data) => {
        setRrList((list) => [data.bon, ...list]);
        showToast(t("rr.toast.created", { number: data.bon.number }, `📤 ${data.bon.number} créé et envoyé`));
        setRrScreen("hub");
        loadRrList();
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setRrCreateBusy(false));
  };
  const openRrDetail = (id) => {
    const bon = rrFindBon(id);
    setRrDetailId(id);
    setRrFoundDraft({});
    setRrCartonsDraft(String(bon?.cartonsCount ?? 0));
    setRrPalletsDraft(String(bon?.palletsCount ?? 0));
    setRrScreen("detail");
  };
  // Saisie des quantités trouvées — enregistrée au blocage du champ (onBlur), même patron
  // que cartons/palettes de Warenaustausch, jamais à chaque frappe (évite une rafale de
  // PATCH). Accepte les négatifs sans validation (retour utilisateur explicite).
  const saveRrLineFound = (bonId, lineId, value) => {
    const foundQty = value.trim() === "" ? NaN : Number(value);
    if (!Number.isFinite(foundQty)) return;
    if (rrDemoMode) {
      setRrList((list) => list.map((b) => {
        if (b.id !== bonId) return b;
        const lines = b.lines.map((l) => l.id === lineId ? { ...l, foundQty } : l);
        return { ...b, lines, totals: rrDemoTotals(lines), updatedAt: new Date().toISOString() };
      }));
      return;
    }
    apiJSON(`/api/rr/${encodeURIComponent(bonId)}/lines/${encodeURIComponent(lineId)}`, {
      method: "PATCH", body: JSON.stringify({ foundQty }),
    })
      .then((data) => setRrList((list) => list.map((b) => b.id === data.bon.id ? data.bon : b)))
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const saveRrLogistics = (id) => {
    if (rrDemoMode) {
      setRrList((list) => list.map((b) => b.id === id ? { ...b, cartonsCount: Number(rrCartonsDraft) || 0, palletsCount: Number(rrPalletsDraft) || 0, updatedAt: new Date().toISOString() } : b));
      return;
    }
    apiJSON(`/api/rr/${encodeURIComponent(id)}/logistics`, {
      method: "PATCH", body: JSON.stringify({ cartonsCount: Number(rrCartonsDraft) || 0, palletsCount: Number(rrPalletsDraft) || 0 }),
    })
      .then((data) => setRrList((list) => list.map((b) => b.id === data.bon.id ? data.bon : b)))
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const submitRrSignature = () => {
    const pad = rrSigPadRef.current;
    if (!pad || !pad.hasValidSignature() || !rrDetailId) return;
    if (rrDemoMode) {
      const dataUrl = pad.toDataURL();
      setRrList((list) => list.map((b) => b.id === rrDetailId ? { ...b, filialeSignatureUrl: dataUrl, updatedAt: new Date().toISOString() } : b));
      showToast(t("toast.signatureSaved", "✓ Signature enregistrée"));
      return;
    }
    setRrBusy(true);
    apiJSON(`/api/rr/${encodeURIComponent(rrDetailId)}/sign`, {
      method: "POST", body: JSON.stringify({ signatureDataUrl: pad.toDataURL(), strokeLength: pad.getStrokeLength() }),
    })
      .then((data) => { setRrList((list) => list.map((b) => b.id === data.bon.id ? data.bon : b)); showToast(t("toast.signatureSaved", "✓ Signature enregistrée")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setRrBusy(false));
  };
  const submitRrNlSignature = () => {
    const pad = rrNlSigPadRef.current;
    if (!pad || !pad.hasValidSignature() || !rrDetailId) return;
    if (rrDemoMode) {
      const dataUrl = pad.toDataURL();
      setRrList((list) => list.map((b) => b.id === rrDetailId ? { ...b, niederlassungSignatureUrl: dataUrl, updatedAt: new Date().toISOString() } : b));
      showToast(t("toast.signatureSaved", "✓ Signature enregistrée"));
      return;
    }
    setRrBusy(true);
    apiJSON(`/api/rr/${encodeURIComponent(rrDetailId)}/validate-sign`, {
      method: "POST", body: JSON.stringify({ signatureDataUrl: pad.toDataURL(), strokeLength: pad.getStrokeLength() }),
    })
      .then((data) => { setRrList((list) => list.map((b) => b.id === data.bon.id ? data.bon : b)); showToast(t("toast.signatureSaved", "✓ Signature enregistrée")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setRrBusy(false));
  };
  const cancelRrBon = (id) => {
    if (!window.confirm(t("rr.confirmCancel", "Annuler ce bon RR ?"))) return;
    if (rrDemoMode) {
      setRrList((list) => list.map((b) => b.id === id ? { ...b, status: "annule", updatedAt: new Date().toISOString() } : b));
      setRrScreen("hub");
      showToast(t("rr.toast.cancelled", "Bon RR annulé"));
      return;
    }
    apiJSON(`/api/rr/${encodeURIComponent(id)}/cancel`, { method: "POST" })
      .then(() => { setRrScreen("hub"); loadRrList(); showToast(t("rr.toast.cancelled", "Bon RR annulé")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const confirmRrBon = (id) => {
    if (rrDemoMode) {
      const me = { employeeId: user?.employeeId || user?.id || "demo", name: user?.name || "Démo" };
      setRrList((list) => list.map((b) => b.id === id ? { ...b, status: "confirme", processedBy: me, processedAt: new Date().toISOString(), updatedAt: new Date().toISOString() } : b));
      showToast(t("rr.toast.confirmed", "✓ Bon RR confirmé"));
      return;
    }
    setRrBusy(true);
    apiJSON(`/api/rr/${encodeURIComponent(id)}/confirm`, { method: "POST" })
      .then((data) => { setRrList((list) => list.map((b) => b.id === data.bon.id ? data.bon : b)); showToast(t("rr.toast.confirmed", "✓ Bon RR confirmé")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setRrBusy(false));
  };
  const validateRrBon = (id) => {
    if (rrDemoMode) {
      const nl = { employeeId: "demo-nl", name: "Niederlassung (démo)" };
      setRrList((list) => list.map((b) => b.id === id ? { ...b, status: "valide", validatedBy: nl, validatedAt: new Date().toISOString(), updatedAt: new Date().toISOString(), kontoauszugPosted: true } : b));
      showToast(t("rr.toast.validated", "✅ Bon RR validé"));
      return;
    }
    setRrBusy(true);
    apiJSON(`/api/rr/${encodeURIComponent(id)}/validate`, { method: "POST" })
      .then((data) => { setRrList((list) => list.map((b) => b.id === data.bon.id ? data.bon : b)); showToast(t("rr.toast.validated", "✅ Bon RR validé")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setRrBusy(false));
  };
  const buildRrTrackingHtml = async (bon) => {
    const totals = bon.totals || {};
    const rows = bon.lines.map((l, i) => {
      const found = l.foundQty;
      const sub = found != null ? (found * l.unitPrice) : (l.expectedQty * l.unitPrice);
      return `<tr>
        <td>${i + 1}</td>
        <td>${haccpEscHtml(l.artNr)}</td>
        <td>${haccpEscHtml(l.name)}</td>
        <td class="num">${l.unitPrice.toFixed(2)} €</td>
        <td class="num">${l.expectedQty}</td>
        <td class="num">${found != null ? found : "—"}</td>
        <td class="num">${sub.toFixed(2)} €</td>
      </tr>`;
    }).join("");
    // Les signatures sont des photos déjà uploadées (jamais des data: URL) — converties en
    // data URI AVANT de générer le HTML imprimé, même contournement que Lieferschein Test/
    // Testkauf cette même session (voir le commentaire de urlToDataUri) : retire tout
    // chargement réseau du chemin de rasterisation, seule approche confirmée fiable.
    const filialeSigSrc = bon.filialeSignatureUrl ? await urlToDataUri(`/api/rr/${bon.id}/signature/filiale`) : "";
    const nlSigSrc = bon.niederlassungSignatureUrl ? await urlToDataUri(`/api/rr/${bon.id}/signature/niederlassung`) : "";
    return `<div class="wadoc-page">
      <header class="wadoc-header">
        <div>
          <p class="wadoc-eyebrow">${t("rr.doc.eyebrow", "RR")} — ${haccpEscHtml(bon.number)}</p>
          <h1>${haccpEscHtml(waStoreLabel(bon.fromStoreId))} → ${haccpEscHtml(waStoreLabel(bon.toStoreId))}</h1>
        </div>
        <div class="wadoc-meta">${fmtFR(bon.sentAt || bon.createdAt)}</div>
      </header>
      <table class="wadoc-infotable">
        <tr><th>${t("warenaustausch.doc.createdByLabel", "Créé par")}</th><td>${haccpEscHtml(bon.createdBy.name)}</td></tr>
        <tr><th>${t("warenaustausch.doc.cartonsLabel", "Cartons")}</th><td>${bon.cartonsCount}</td></tr>
        <tr><th>${t("warenaustausch.doc.palletsLabel", "Palettes")}</th><td>${bon.palletsCount}</td></tr>
        ${bon.processedBy ? `<tr><th>${t("rr.doc.processedByLabel", "Traité par")}</th><td>${haccpEscHtml(bon.processedBy.name)} · ${fmtFR(bon.processedAt)}</td></tr>` : ""}
        ${bon.validatedBy ? `<tr><th>${t("rr.doc.validatedByLabel", "Validé par")}</th><td>${haccpEscHtml(bon.validatedBy.name)} · ${fmtFR(bon.validatedAt)}</td></tr>` : ""}
      </table>
      <table class="wadoc-artable">
        <thead><tr>
          <th>#</th><th>${t("warenaustausch.doc.artNrLabel", "Art.Nr")}</th><th>${t("warenaustausch.doc.designationLabel", "Désignation")}</th>
          <th class="num">${t("warenaustausch.doc.unitPriceLabel", "Prix unitaire")}</th>
          <th class="num">${t("rr.doc.expectedQtyLabel", "Qté attendue")}</th>
          <th class="num">${t("rr.doc.foundQtyLabel", "Qté trouvée")}</th>
          <th class="num">${t("warenaustausch.doc.totalLabel", "Total")}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr class="wadoc-total-row"><td colspan="6">${t("rr.doc.totalExpectedLabel", "Total attendu")}</td><td class="num">${(totals.totalExpected || 0).toFixed(2)} €</td></tr>
          <tr class="wadoc-total-row"><td colspan="6">${t("rr.doc.totalFoundLabel", "Total trouvé")}</td><td class="num">${totals.totalFound != null ? totals.totalFound.toFixed(2) + " €" : "—"}</td></tr>
          <tr class="wadoc-total-row"><td colspan="6">${t("rr.doc.diffLabel", "Écart")}</td><td class="num${totals.diff ? " wadoc-diff" : ""}">${totals.diff != null ? (totals.diff >= 0 ? "+" : "") + totals.diff.toFixed(2) + " €" : "—"}</td></tr>
        </tfoot>
      </table>
      <div class="wadoc-sigrow">
        <div class="wadoc-sigbox">
          <p class="lbl">${t("rr.doc.filialeSignatureLabel", "Signature filiale")}</p>
          ${filialeSigSrc ? `<img src="${filialeSigSrc}" />` : ""}
          <p class="name">${bon.processedBy ? haccpEscHtml(bon.processedBy.name) : ""}</p>
        </div>
        <div class="wadoc-sigbox">
          <p class="lbl">${t("rr.doc.niederlassungSignatureLabel", "Signature Niederlassung")}</p>
          ${nlSigSrc ? `<img src="${nlSigSrc}" />` : ""}
          <p class="name">${bon.validatedBy ? haccpEscHtml(bon.validatedBy.name) : ""}</p>
        </div>
      </div>
    </div>`;
  };
  const buildRrStickerHtml = (bon) => `<div class="wadoc-sticker">
      <p class="kind">${t("rr.doc.eyebrow", "RR")} · ${haccpEscHtml(bon.number)}</p>
      <p class="route">${t("warenaustausch.doc.storeLabelPrefix", "Filiale")} ${haccpEscHtml(bon.toStoreId)} → ${haccpEscHtml(waStoreLabel(bon.fromStoreId))}</p>
      <p class="meta">${fmtFR(bon.processedAt || bon.sentAt || bon.createdAt)}</p>
    </div>`;
  const exportRrTracking = async (bon) => {
    if (rrExporting) return;
    setRrExporting(true);
    try {
      const bodyHtml = await buildRrTrackingHtml(bon);
      await exportHtmlToPdf({ bodyHtml, css: WA_DOC_CSS, targetSelector: ".wadoc-page", widthMm: 210, filename: `${bon.number}.pdf` });
    } catch (err) {
      if (err?.name !== "AbortError") showToast(err.message || t("err.actionImpossible", "Action impossible."));
    } finally {
      setRrExporting(false);
    }
  };
  const exportRrSticker = async (bon) => {
    if (rrExporting) return;
    setRrExporting(true);
    try {
      await exportHtmlToPdf({ bodyHtml: buildRrStickerHtml(bon), css: WA_DOC_CSS, targetSelector: ".wadoc-sticker", widthMm: 210, heightMm: 297, filename: `${bon.number}-etiquette.pdf` });
    } catch (err) {
      if (err?.name !== "AbortError") showToast(err.message || t("err.actionImpossible", "Action impossible."));
    } finally {
      setRrExporting(false);
    }
  };
  // Kontoauszug — contrôle de conformité (2026-08-30). Le serveur calcule seul
  // calculatedAmount/diff (jamais le client) ; le statut vient du choix conforme:true/false
  // envoyé ici, jamais déduit de cet écart côté client non plus.
  // Fixtures 100% en mémoire, jamais écrites au serveur (mode démo — toggle sur le hub).
  // Couvre les 3 statuts + un fil de notes réaliste (question Niederlassung → réponse
  // filiale) sur le contrôle en anomalie, pour montrer le mécanisme complet sans données
  // réelles en jeu.
  const buildKontoDemoChecks = () => {
    const myStore = user?.store || "N236";
    const now = Date.now();
    const iso = (daysAgo) => new Date(now - daysAgo * 86400000).toISOString();
    const me = { employeeId: user?.employeeId || user?.id || "demo", name: user?.name || "Filiale (démo)", role: user?.role || "" };
    const nl = { employeeId: "demo-nl", name: "Niederlassung (démo)", role: "Niederlassungsleiter" };
    // verdict/entryNote (2026-08-30, retour utilisateur explicite) : chaque bon porte SA
    // PROPRE décision — "conforme"/"anomalie"/"accepte" — jamais un seul choix global pour
    // tout le contrôle. Fixtures enrichies (retour utilisateur : "plus d'éléments") : les 4
    // catégories représentées dans chaque contrôle, avec au moins un cas "accepte" (écart
    // réel mais jugé correct) en plus du cas "anomalie", pour montrer les 3 verdicts.
    const mkEntry = (i, motif, art, betrag, declaredBetrag, verdict, entryNote, daysAgo, relatedTransferId) => ({
      id: `kdemo-entry-${i}`, art, betrag, declaredBetrag, verdict, entryNote, motif, relatedTransferId, counterpartStoreId: "NL-DEMO", createdAt: iso(daysAgo),
    });
    return [
      {
        id: "kdemo-1", storeId: myStore, periodStart: "2026-07-01", periodEnd: "2026-07-31",
        declaredAmount: 926.25, calculatedAmount: 926.25, diff: 0, pdfUrl: "",
        entriesSnapshot: [
          mkEntry(1, "warenaustausch", "gutschrift", 500.00, 500.00, "conforme", "", 25, "WA-2026-0091"),
          mkEntry(2, "rr", "gutschrift", 342.50, 342.50, "conforme", "", 22, "RR-2026-0028"),
          mkEntry(3, "preisanderung", "belastung", 45.00, 45.00, "conforme", "", 24, "PA-2026-0039"),
          mkEntry(4, "warenrueckfuehrung", "gutschrift", 128.75, 128.75, "conforme", "", 23, "WR-2026-0014"),
        ],
        status: "conforme", notes: [],
        createdBy: me, createdAt: iso(28), updatedAt: iso(28), resolvedBy: null, resolvedAt: null,
      },
      {
        id: "kdemo-2", storeId: myStore, periodStart: "2026-08-01", periodEnd: "2026-08-15",
        declaredAmount: 492.40, calculatedAmount: 542.60, diff: 50.20, pdfUrl: "",
        entriesSnapshot: [
          mkEntry(5, "warenaustausch", "gutschrift", 300.00, 300.00, "conforme", "", 13, "WA-2026-0112"),
          mkEntry(6, "preisanderung", "belastung", 20.00, 20.00, "conforme", "", 12, "PA-2026-0044"),
          mkEntry(7, "rr", "gutschrift", 186.20, 142.40, "anomalie", "Surplus Nutella non repris — le bon RR-2026-0032 complet n'apparaît pas dans votre relevé.", 11, "RR-2026-0032"),
          mkEntry(8, "warenrueckfuehrung", "gutschrift", 95.00, 95.00, "conforme", "", 12, "WR-2026-0021"),
          mkEntry(9, "warenaustausch", "belastung", 18.60, 25.00, "accepte", "", 11, "WA-2026-0115"),
        ],
        status: "anomalie",
        notes: [
          { authorId: me.employeeId, authorName: me.name, role: me.role, text: "RR-2026-0032 — Surplus Nutella non repris — le bon RR-2026-0032 complet n'apparaît pas dans votre relevé.", createdAt: iso(10) },
          { authorId: nl.employeeId, authorName: nl.name, role: nl.role, text: "Vérifié de notre côté — RR-2026-0032 avait été posté sur le mauvais mois. Correction en cours, nouveau relevé sous 48h.", createdAt: iso(9) },
        ],
        createdBy: me, createdAt: iso(10), updatedAt: iso(9), resolvedBy: null, resolvedAt: null,
      },
      {
        id: "kdemo-3", storeId: myStore, periodStart: "2026-08-16", periodEnd: "2026-08-29",
        declaredAmount: 549.80, calculatedAmount: 571.10, diff: 21.30, pdfUrl: "",
        entriesSnapshot: [
          mkEntry(10, "warenaustausch", "gutschrift", 450.00, 435.20, "anomalie", "WA-2026-0118 non retrouvé sur le relevé, écart de 14,80 €.", 6, "WA-2026-0118"),
          mkEntry(11, "rr", "belastung", 12.90, 12.90, "conforme", "", 5, "RR-2026-0035"),
          mkEntry(12, "preisanderung", "gutschrift", 164.00, 164.00, "conforme", "", 4, "PA-2026-0047"),
          mkEntry(13, "warenrueckfuehrung", "belastung", 30.00, 36.50, "accepte", "", 5, "WR-2026-0026"),
        ],
        status: "resolue",
        notes: [
          { authorId: me.employeeId, authorName: me.name, role: me.role, text: "WA-2026-0118 — WA-2026-0118 non retrouvé sur le relevé, écart de 14,80 €.", createdAt: iso(3) },
          { authorId: nl.employeeId, authorName: nl.name, role: nl.role, text: "C'était un doublon de frais de transport, annulé. Montant corrigé sur le prochain relevé.", createdAt: iso(2) },
        ],
        createdBy: me, createdAt: iso(3), updatedAt: iso(1),
        resolvedBy: me, resolvedAt: iso(1),
      },
      // Lot supplémentaire (2026-10-15, retour utilisateur explicite : "ajoute plus de démo
      // dans tous ces modules") — même patron exact que kdemo-1/2/3, juste plus de volume.
      {
        id: "kdemo-4", storeId: myStore, periodStart: "2026-09-01", periodEnd: "2026-09-15",
        declaredAmount: 388.10, calculatedAmount: 388.10, diff: 0, pdfUrl: "",
        entriesSnapshot: [
          mkEntry(14, "warenaustausch", "gutschrift", 210.00, 210.00, "conforme", "", 44, "WA-2026-0134"),
          mkEntry(15, "rr", "belastung", 58.10, 58.10, "conforme", "", 43, "RR-2026-0041"),
          mkEntry(16, "preisanderung", "gutschrift", 120.00, 120.00, "conforme", "", 42, "PA-2026-0052"),
        ],
        status: "conforme", notes: [],
        createdBy: me, createdAt: iso(44), updatedAt: iso(44), resolvedBy: null, resolvedAt: null,
      },
      {
        id: "kdemo-5", storeId: myStore, periodStart: "2026-09-16", periodEnd: "2026-09-30",
        declaredAmount: 276.50, calculatedAmount: 301.90, diff: 25.40, pdfUrl: "",
        entriesSnapshot: [
          mkEntry(17, "warenaustausch", "gutschrift", 180.00, 180.00, "conforme", "", 16, "WA-2026-0141"),
          mkEntry(18, "warenrueckfuehrung", "gutschrift", 96.50, 71.10, "anomalie", "Retour palette WR-2026-0033 non recrédité en totalité sur votre relevé.", 15, "WR-2026-0033"),
          mkEntry(19, "rr", "belastung", 25.40, 25.40, "conforme", "", 14, "RR-2026-0048"),
        ],
        status: "anomalie",
        notes: [
          { authorId: me.employeeId, authorName: me.name, role: me.role, text: "WR-2026-0033 — Retour palette WR-2026-0033 non recrédité en totalité sur votre relevé.", createdAt: iso(14) },
        ],
        createdBy: me, createdAt: iso(16), updatedAt: iso(14), resolvedBy: null, resolvedAt: null,
      },
    ];
  };
  const toggleKontoDemoMode = () => {
    const next = !kontoDemoMode;
    setKontoDemoMode(next);
    if (next) setKontoChecks(buildKontoDemoChecks());
    else loadKontoChecks();
  };
  const loadKontoChecks = () => {
    if (kontoDemoMode) { setKontoChecks(buildKontoDemoChecks()); return; }
    setKontoLoading(true);
    apiJSON("/api/kontoauszug/checks").then((data) => setKontoChecks(data.checks || [])).catch(() => setKontoChecks([])).finally(() => setKontoLoading(false));
  };
  const kontoFindCheck = (id) => kontoChecks.find((c) => c.id === id) || null;
  // Confort d'affichage uniquement (montrer/masquer le champ de note, l'upload, le bouton
  // résoudre) — le vrai gate reste toujours server-side (isCheckAccessible,
  // server/kontoauszug/routes.js), jamais une confiance aveugle ici. Un compte Niederlassung
  // qui répond à une anomalie n'a pas forcément storeId===check.storeId (sa propre filiale
  // est la Niederlassung elle-même) — le serveur tranche via resolveMyNiederlassung, ici on
  // reste permissif (jamais bloquant côté client) plutôt que de deviner cette hiérarchie.
  const isCheckAccessibleClient = () => true;
  const openKontoCreate = () => { setKontoCreateForm(emptyKontoCreateForm); setKontoPreview(null); setKontoScreen("create"); };
  // Aperçu — appelé onBlur des deux champs date (même patron que les autres champs
  // "enregistrés au blocage" de ce module, jamais à chaque frappe). Ignoré tant que les
  // deux dates ne sont pas renseignées.
  const loadKontoPreview = (periodStart, periodEnd) => {
    if (!periodStart || !periodEnd) { setKontoPreview(null); return; }
    if (kontoDemoMode) {
      const demoEntries = [
        { id: "kpreview-demo-1", art: "gutschrift", betrag: 214.30, motif: "warenaustausch", relatedTransferId: "WA-2026-DEMO1", counterpartStoreId: "NL-DEMO", createdAt: new Date().toISOString() },
        { id: "kpreview-demo-2", art: "belastung", betrag: 18.50, motif: "rr", relatedTransferId: "RR-2026-DEMO2", counterpartStoreId: "NL-DEMO", createdAt: new Date().toISOString() },
        { id: "kpreview-demo-3", art: "gutschrift", betrag: 76.40, motif: "preisanderung", relatedTransferId: "PA-2026-DEMO3", counterpartStoreId: "NL-DEMO", createdAt: new Date().toISOString() },
        { id: "kpreview-demo-4", art: "belastung", betrag: 32.00, motif: "warenrueckfuehrung", relatedTransferId: "WR-2026-DEMO4", counterpartStoreId: "NL-DEMO", createdAt: new Date().toISOString() },
      ];
      setKontoPreview({ calculatedAmount: round2ClientKonto(214.30 - 18.50 + 76.40 - 32.00), entriesSnapshot: demoEntries });
      return;
    }
    setKontoPreviewLoading(true);
    apiJSON(`/api/kontoauszug/checks/preview?periodStart=${encodeURIComponent(periodStart)}&periodEnd=${encodeURIComponent(periodEnd)}`)
      .then((data) => setKontoPreview({ calculatedAmount: data.calculatedAmount, entriesSnapshot: data.entriesSnapshot || [] }))
      .catch(() => setKontoPreview(null))
      .finally(() => setKontoPreviewLoading(false));
  };
  const round2ClientKonto = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
  // Regroupe des écritures PAR CATÉGORIE, chaque bon restant une ligne distincte à
  // l'intérieur de son groupe (retour utilisateur explicite : "comparer catégorie par
  // catégorie et chaque bon de chaque catégorie, jamais mettre tous les bons ensemble") —
  // jamais une liste plate qui mélangerait RR/Warenaustausch/Preisänderung. `getDeclared`
  // abstrait la source du montant déclaré (brouillon en cours de saisie vs check déjà créé),
  // réutilisé identique par l'écran de création (éditable) et le détail (lecture seule).
  const kontoGroupByCategory = (entries, getDeclared) => {
    const buckets = {};
    KONTO_CATEGORIES.forEach((m) => { buckets[m] = []; });
    (entries || []).forEach((e) => { if (buckets[e.motif]) buckets[e.motif].push(e); });
    return KONTO_CATEGORIES.map((motif) => {
      const rows = buckets[motif];
      if (!rows.length) return null;
      const calculatedSubtotal = round2ClientKonto(rows.reduce((s, e) => s + (e.art === "gutschrift" ? e.betrag : -e.betrag), 0));
      const declaredSubtotal = round2ClientKonto(rows.reduce((s, e) => {
        const d = getDeclared(e);
        return s + (d == null || d === "" ? 0 : (e.art === "gutschrift" ? Number(d) : -Number(d)));
      }, 0));
      return { motif, rows, calculatedSubtotal, declaredSubtotal };
    }).filter(Boolean);
  };
  // Vérification bon par bon (retour utilisateur explicite, 2026-08-30) : chaque ligne du
  // tableau porte SA PROPRE décision (conforme/anomalie/accepté), jamais un bouton global.
  const setKontoVerdict = (entryId, verdict) => setKontoCreateForm((prev) => ({ ...prev, verdictsByEntry: { ...prev.verdictsByEntry, [entryId]: prev.verdictsByEntry[entryId] === verdict ? undefined : verdict } }));
  const setKontoEntryNote = (entryId, text) => setKontoCreateForm((prev) => ({ ...prev, notesByEntry: { ...prev.notesByEntry, [entryId]: text } }));
  const createKontoCheck = () => {
    const f = kontoCreateForm;
    if (!f.periodStart || !f.periodEnd) { showToast(t("toast.choosePeriod", "Choisis la période du relevé.")); return; }
    if (!kontoPreview) { showToast(t("konto.toast.needPreview", "Attends le calcul du détail avant de continuer.")); return; }
    const entries = kontoPreview.entriesSnapshot || [];
    const unverified = entries.filter((e) => !f.verdictsByEntry[e.id]);
    if (unverified.length) { showToast(t("konto.toast.needAllVerdicts", "Vérifie chaque bon (conforme, anomalie ou accepté) avant d'enregistrer.")); return; }
    const missingNote = entries.filter((e) => f.verdictsByEntry[e.id] === "anomalie" && !(f.notesByEntry[e.id] || "").trim());
    if (missingNote.length) { showToast(t("konto.toast.needEntryNote", "Décris l'anomalie pour chaque bon signalé non conforme.")); return; }

    const declaredAmount = round2ClientKonto(entries.reduce((s, e) => {
      const d = f.declaredByEntry[e.id];
      return s + (d == null || d === "" ? 0 : (e.art === "gutschrift" ? Number(d) : -Number(d)));
    }, 0));
    const conforme = entries.every((e) => f.verdictsByEntry[e.id] !== "anomalie");
    if (kontoDemoMode) {
      const entriesSnapshot = entries.map((e) => ({
        ...e,
        declaredBetrag: f.declaredByEntry[e.id] == null || f.declaredByEntry[e.id] === "" ? null : Number(f.declaredByEntry[e.id]),
        verdict: f.verdictsByEntry[e.id],
        entryNote: f.verdictsByEntry[e.id] === "anomalie" ? (f.notesByEntry[e.id] || "").trim() : "",
      }));
      const anomalieEntries = entriesSnapshot.filter((e) => e.verdict === "anomalie");
      const check = {
        id: `kdemo-new-${Date.now()}`, storeId: user?.store || "N236",
        periodStart: f.periodStart, periodEnd: f.periodEnd,
        declaredAmount, calculatedAmount: kontoPreview.calculatedAmount,
        diff: round2ClientKonto(kontoPreview.calculatedAmount - declaredAmount), pdfUrl: "", entriesSnapshot,
        status: conforme ? "conforme" : "anomalie",
        notes: anomalieEntries.map((e) => ({ authorId: user?.employeeId || user?.id || "demo", authorName: user?.name || "Démo", role: user?.role || "", text: `${e.relatedTransferId || e.id} — ${e.entryNote}`, createdAt: new Date().toISOString() })),
        createdBy: { employeeId: user?.employeeId || user?.id || "demo", name: user?.name || "Démo" },
        createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), resolvedBy: null, resolvedAt: null,
      };
      setKontoChecks((list) => [check, ...list]);
      showToast(conforme ? t("konto.toast.confirmedOk", "✓ Contrôle enregistré — conforme") : t("konto.toast.anomalySent", "⚠️ Anomalie signalée à la Niederlassung"));
      setKontoScreen("hub");
      return;
    }
    setKontoCreateBusy(true);
    apiJSON("/api/kontoauszug/checks", {
      method: "POST",
      body: JSON.stringify({
        periodStart: f.periodStart, periodEnd: f.periodEnd,
        declaredByEntry: f.declaredByEntry, verdictsByEntry: f.verdictsByEntry, notesByEntry: f.notesByEntry,
        pdfUrl: f.pdfUrl,
      }),
    })
      .then((data) => {
        setKontoChecks((list) => [data.check, ...list]);
        showToast(data.check.status === "conforme" ? t("konto.toast.confirmedOk", "✓ Contrôle enregistré — conforme") : t("konto.toast.anomalySent", "⚠️ Anomalie signalée à la Niederlassung"));
        setKontoScreen("hub");
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setKontoCreateBusy(false));
  };
  const openKontoDetail = (id) => { setKontoDetailId(id); setKontoNoteDraft(""); setKontoScreen("detail"); };
  const addKontoNote = (id) => {
    const text = kontoNoteDraft.trim();
    if (!text) return;
    if (kontoDemoMode) {
      const note = { authorId: user?.employeeId || user?.id || "demo", authorName: user?.name || "Démo", role: user?.role || "", text, createdAt: new Date().toISOString() };
      setKontoChecks((list) => list.map((c) => c.id === id ? { ...c, notes: [...c.notes, note], updatedAt: new Date().toISOString() } : c));
      setKontoNoteDraft("");
      return;
    }
    setKontoBusy(true);
    apiJSON(`/api/kontoauszug/checks/${encodeURIComponent(id)}/notes`, { method: "POST", body: JSON.stringify({ text }) })
      .then((data) => { setKontoChecks((list) => list.map((c) => c.id === data.check.id ? data.check : c)); setKontoNoteDraft(""); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setKontoBusy(false));
  };
  const resolveKontoCheck = (id) => {
    if (kontoDemoMode) {
      const me = { employeeId: user?.employeeId || user?.id || "demo", name: user?.name || "Démo" };
      setKontoChecks((list) => list.map((c) => c.id === id ? { ...c, status: "resolue", resolvedBy: me, resolvedAt: new Date().toISOString(), updatedAt: new Date().toISOString() } : c));
      showToast(t("konto.toast.resolved", "✓ Contrôle marqué résolu"));
      return;
    }
    setKontoBusy(true);
    apiJSON(`/api/kontoauszug/checks/${encodeURIComponent(id)}/resolve`, { method: "POST" })
      .then((data) => { setKontoChecks((list) => list.map((c) => c.id === data.check.id ? data.check : c)); showToast(t("konto.toast.resolved", "✓ Contrôle marqué résolu")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setKontoBusy(false));
  };
  const uploadKontoPdfToCheck = (id, file) => {
    if (!file) return;
    if (kontoDemoMode) {
      setKontoChecks((list) => list.map((c) => c.id === id ? { ...c, pdfUrl: "demo.pdf", updatedAt: new Date().toISOString() } : c));
      showToast(t("konto.toast.pdfSaved", "✓ PDF joint"));
      return;
    }
    setKontoUploadBusy(true);
    const formData = new FormData();
    formData.append("file", file);
    apiJSON(`/api/kontoauszug/checks/${encodeURIComponent(id)}/upload-pdf`, { method: "POST", body: formData })
      .then((data) => { setKontoChecks((list) => list.map((c) => c.id === data.check.id ? data.check : c)); showToast(t("konto.toast.pdfSaved", "✓ PDF joint")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setKontoUploadBusy(false));
  };
  const buildWaTrackingHtml = (tr) => {
    const isWR = tr.kind === "warenrueckfuehrung";
    // Le libellé catalogue d'une Niederlassung inclut déjà "Niederlassung" dans son nom
    // (ex. réel : "Niederlassung Test (Nom du responsable)") — jamais rajouter le préfixe ici, sinon
    // doublon (bug réel trouvé en vérifiant ce document : "Niederlassung Niederlassung X").
    const toLabel = waStoreLabel(tr.toStoreId);
    const rows = tr.lines.map((l, i) => {
      const verified = l.quantityReceived != null;
      const qty = verified ? l.quantityReceived : l.quantitySent;
      const diff = verified && l.quantityReceived !== l.quantitySent;
      const total = (qty * (l.unitPriceAtSend || 0)).toFixed(2);
      return `<tr>
        <td>${i + 1}</td>
        <td>${haccpEscHtml(l.artNr)}</td>
        <td>${haccpEscHtml(l.name)}</td>
        <td class="num">${(l.unitPriceAtSend || 0).toFixed(2)} €</td>
        <td class="num">${l.quantitySent}</td>
        <td class="num">${verified ? `${l.quantityReceived}${diff ? ` <span class="wadoc-diff">${t("warenaustausch.doc.diffLabel", "(écart)")}</span>` : ""}` : "—"}</td>
        <td class="num">${total} €</td>
      </tr>`;
    }).join("");
    const totalGeneral = tr.lines.reduce((sum, l) => {
      const qty = l.quantityReceived != null ? l.quantityReceived : l.quantitySent;
      return sum + qty * (l.unitPriceAtSend || 0);
    }, 0).toFixed(2);
    return `<div class="wadoc-page">
      <header class="wadoc-header">
        <div>
          <p class="wadoc-eyebrow">${t("warenaustausch.doc.eyebrow", "Warenaustausch")}${isWR ? ` — ${haccpEscHtml(WA_CATEGORY_LABEL[tr.category] || "")}` : ""}</p>
          <h1>${haccpEscHtml(waStoreLabel(tr.fromStoreId))} → ${haccpEscHtml(toLabel)}</h1>
        </div>
        <div class="wadoc-meta">${fmtFR(tr.sentAt || tr.createdAt)}</div>
      </header>
      <table class="wadoc-infotable">
        <tr><th>${t("warenaustausch.doc.createdByLabel", "Créé par")}</th><td>${haccpEscHtml(tr.createdBy.name)}</td></tr>
        <tr><th>${t("warenaustausch.doc.cartonsLabel", "Cartons")}</th><td>${tr.cartonsCount}</td></tr>
        <tr><th>${t("warenaustausch.doc.palletsLabel", "Palettes")}</th><td>${tr.palletsCount}</td></tr>
        ${tr.receivedBy ? `<tr><th>${t("warenaustausch.doc.controlledByLabel", "Contrôlé par")}</th><td>${haccpEscHtml(tr.receivedBy.name)}</td></tr>` : ""}
        ${tr.receivedAt ? `<tr><th>${t("warenaustausch.doc.receivedAtLabel", "Reçu le")}</th><td>${fmtFR(tr.receivedAt)}</td></tr>` : ""}
      </table>
      <table class="wadoc-artable">
        <thead><tr>
          <th>#</th><th>${t("warenaustausch.doc.artNrLabel", "Art.Nr")}</th><th>${t("warenaustausch.doc.designationLabel", "Désignation")}</th>
          <th class="num">${t("warenaustausch.doc.unitPriceLabel", "Prix unitaire")}</th>
          <th class="num">${t("warenaustausch.doc.qtySentLabel", "Qté envoyée")}</th>
          <th class="num">${t("warenaustausch.doc.qtyReceivedLabel", "Qté reçue")}</th>
          <th class="num">${t("warenaustausch.doc.totalLabel", "Total")}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr class="wadoc-total-row"><td colspan="6">${t("warenaustausch.doc.grandTotalLabel", "Total général")}</td><td class="num">${totalGeneral} €</td></tr></tfoot>
      </table>
      <div class="wadoc-sigrow">
        <div class="wadoc-sigbox">
          <p class="lbl">${t("warenaustausch.doc.signatureLabel", "Signature de l'expéditeur")}</p>
          ${tr.signatureUrl ? `<img src="/api/warenaustausch/${tr.id}/signature" />` : ""}
          <p class="name">${haccpEscHtml(tr.createdBy.name)}</p>
        </div>
      </div>
    </div>`;
  };
  const buildWaStickerHtml = (tr) => {
    const isWR = tr.kind === "warenrueckfuehrung";
    const kindLabel = isWR ? t("warenaustausch.doc.stickerKindReturn", "Retour marchandise") : t("warenaustausch.doc.stickerKindTransfer", "Warenaustausch");
    // Même correctif que buildWaTrackingHtml juste au-dessus — le libellé Niederlassung
    // contient déjà le mot, jamais de préfixe ajouté par-dessus.
    const destLabel = isWR
      ? haccpEscHtml(waStoreLabel(tr.toStoreId))
      : `${t("warenaustausch.doc.storeLabelPrefix", "Filiale")} ${haccpEscHtml(tr.toStoreId)}`;
    const routeText = `${t("warenaustausch.doc.storeLabelPrefix", "Filiale")} ${haccpEscHtml(tr.fromStoreId)} → ${destLabel}`;
    return `<div class="wadoc-sticker">
      <p class="kind">${kindLabel}</p>
      <p class="route">${routeText}</p>
      <p class="meta">${fmtFR(tr.sentAt || tr.createdAt)}</p>
    </div>`;
  };
  const exportWaTracking = async (tr) => {
    if (waExporting) return;
    setWaExporting(true);
    try {
      await exportHtmlToPdf({ bodyHtml: buildWaTrackingHtml(tr), css: WA_DOC_CSS, targetSelector: ".wadoc-page", widthMm: 210, filename: `suivi-wa-${tr.id}.pdf` });
    } catch (err) {
      if (err?.name !== "AbortError") showToast(err.message || t("err.actionImpossible", "Action impossible."));
    } finally {
      setWaExporting(false);
    }
  };
  const exportWaSticker = async (tr) => {
    if (waExporting) return;
    setWaExporting(true);
    try {
      await exportHtmlToPdf({ bodyHtml: buildWaStickerHtml(tr), css: WA_DOC_CSS, targetSelector: ".wadoc-sticker", widthMm: 210, heightMm: 297, filename: `etiquette-envoi-${tr.id}.pdf` });
    } catch (err) {
      if (err?.name !== "AbortError") showToast(err.message || t("err.actionImpossible", "Action impossible."));
    } finally {
      setWaExporting(false);
    }
  };

  // Rapport de filiale (2026-08-26) — voir les états déclarés plus haut (frTab/frMine/...).
  const loadFrMine = () => {
    apiJSON("/api/filiale-reports").then((data) => setFrMine(data.reports || [])).catch(() => {});
  };
  const loadFrReceived = () => {
    apiJSON("/api/filiale-reports/received").then((data) => setFrReceived(data.reports || [])).catch(() => {});
  };
  // Correctif #8 — filiales réellement choisissables (propres filiales + Vertretung active),
  // jamais un champ libre. Appelé au moment de démarrer un nouveau rapport, jamais au
  // montage de l'app (GR uniquement, coûterait un appel inutile à tout autre compte).
  const loadFrMyStores = (isRetry) => {
    if (!isRetry) setFrMyStoresError("");
    apiJSON("/api/filiale-reports/my-stores")
      .then((data) => { setFrMyStores(data.storeIds || []); setFrMyStoresError(""); })
      .catch((err) => {
        if (!isRetry) { setTimeout(() => loadFrMyStores(true), 1500); return; }
        setFrMyStores([]); setFrMyStoresError(err.message || t("err.actionImpossible", "Action impossible."));
      });
  };
  const startFrNew = () => {
    setFrNewStoreId(""); setFrDraft(null); setFrSectionDraft(""); setFrPhotoCaptionDraft(""); setFrPhotoLinkedTodoId("");
    setFrTodoDraft({ text: "", dueDate: "" }); setFrCandidates([]); setFrSelectedTargets([]); setFrSigReady(false);
    loadFrMyStores();
    setFrScreen("new");
  };
  // Reprend un brouillon déjà commencé (tap depuis "Mes rapports") — même écran unique de
  // construction que startFrNew, juste réamorcé avec les données déjà là.
  const resumeFrDraft = (report) => {
    setFrDraft(report); setFrSectionDraft(""); setFrPhotoCaptionDraft(""); setFrPhotoLinkedTodoId("");
    setFrTodoDraft({ text: "", dueDate: "" }); setFrSelectedTargets([]); setFrSigReady(false);
    apiJSON(`/api/filiale-reports/${report.id}/recipients-candidates`).then((data) => setFrCandidates(data.candidates || [])).catch(() => {});
    setFrScreen("new");
  };
  const createFrDraft = () => {
    if (!frNewStoreId) { showToast(t("toast.chooseVisitedStore", "Choisis la filiale visitée.")); return; }
    setFrBusy(true);
    apiJSON("/api/filiale-reports", { method: "POST", body: JSON.stringify({ storeId: frNewStoreId }) })
      .then((data) => {
        setFrDraft(data.report);
        return apiJSON(`/api/filiale-reports/${data.report.id}/recipients-candidates`);
      })
      .then((data) => setFrCandidates(data.candidates || []))
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setFrBusy(false));
  };
  // Correctif #3 — bascule le mode de date de la to-do (une date pour toute la liste, ou une
  // par tâche) ; correctif #5 — jamais une date passée, contrôlé ici avant même l'appel
  // réseau (le serveur reste la vraie barrière, voir findPastDate côté routes.js).
  const setFrTodoDateMode = (mode) => {
    if (!frDraft) return;
    apiJSON(`/api/filiale-reports/${frDraft.id}`, { method: "PATCH", body: JSON.stringify({ todoDateMode: mode }) })
      .then((data) => setFrDraft(data.report))
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const setFrSharedDueDate = (date) => {
    if (!frDraft) return;
    if (date && date < todayISO()) { showToast(t("filialeReport.toast.pastDate", "Cette date ne peut pas être antérieure à aujourd'hui.")); return; }
    apiJSON(`/api/filiale-reports/${frDraft.id}`, { method: "PATCH", body: JSON.stringify({ todoSharedDueDate: date }) })
      .then((data) => setFrDraft(data.report))
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const addFrSection = () => {
    const text = frSectionDraft.trim();
    if (!text || !frDraft) return;
    const sections = [...frDraft.sections, { text }];
    apiJSON(`/api/filiale-reports/${frDraft.id}`, { method: "PATCH", body: JSON.stringify({ sections }) })
      .then((data) => { setFrDraft(data.report); setFrSectionDraft(""); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const removeFrSection = (id) => {
    if (!frDraft) return;
    const sections = frDraft.sections.filter((s) => s.id !== id);
    apiJSON(`/api/filiale-reports/${frDraft.id}`, { method: "PATCH", body: JSON.stringify({ sections }) })
      .then((data) => setFrDraft(data.report))
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const uploadFrPhoto = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !frDraft) return;
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    formData.append("captions", frPhotoCaptionDraft.trim());
    // Correctif #3 — "les photos doivent correspondre à chaque événement" : rattachées à la
    // tâche sélectionnée juste avant la prise de vue (frPhotoLinkedTodoId), "" = photo
    // générale du rapport.
    formData.append("linkedTodoId", frPhotoLinkedTodoId);
    setFrBusy(true);
    apiJSON(`/api/filiale-reports/${frDraft.id}/photos`, { method: "POST", body: formData })
      .then((data) => { setFrDraft(data.report); setFrPhotoCaptionDraft(""); })
      .catch((err) => showToast(err.message || t("err.depositImpossible", "Dépôt impossible.")))
      .finally(() => setFrBusy(false));
  };
  const removeFrPhoto = (photoId) => {
    if (!frDraft) return;
    apiJSON(`/api/filiale-reports/${frDraft.id}/photos/${photoId}`, { method: "DELETE" })
      .then((data) => setFrDraft(data.report))
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const addFrTodo = () => {
    const text = frTodoDraft.text.trim();
    if (!text || !frDraft) return;
    // Correctif #5 — jamais une date passée (contrôle client, la vraie barrière reste
    // findPastDate côté serveur). Non applicable en mode "shared" (todoSharedDueDate porte
    // alors la seule date qui compte).
    if (frDraft.todoDateMode === "perItem" && frTodoDraft.dueDate && frTodoDraft.dueDate < todayISO()) {
      showToast(t("filialeReport.toast.pastDate", "Cette date ne peut pas être antérieure à aujourd'hui.")); return;
    }
    const todoItems = [...frDraft.todoItems, { text, dueDate: frDraft.todoDateMode === "perItem" ? frTodoDraft.dueDate : "" }];
    apiJSON(`/api/filiale-reports/${frDraft.id}`, { method: "PATCH", body: JSON.stringify({ todoItems }) })
      .then((data) => { setFrDraft(data.report); setFrTodoDraft({ text: "", dueDate: "" }); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const removeFrTodo = (id) => {
    if (!frDraft) return;
    const todoItems = frDraft.todoItems.filter((i) => i.id !== id);
    apiJSON(`/api/filiale-reports/${frDraft.id}`, { method: "PATCH", body: JSON.stringify({ todoItems }) })
      .then((data) => setFrDraft(data.report))
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const toggleFrTarget = (employeeId) => {
    setFrSelectedTargets((list) => list.includes(employeeId) ? list.filter((id) => id !== employeeId) : [...list, employeeId]);
  };
  const submitFrSignature = () => {
    const pad = frSigPadRef.current;
    if (!pad || !pad.hasValidSignature() || !frDraft) return;
    setFrBusy(true);
    apiJSON(`/api/filiale-reports/${frDraft.id}/sign`, {
      method: "POST", body: JSON.stringify({ signatureDataUrl: pad.toDataURL(), strokeLength: pad.getStrokeLength() }),
    })
      .then((data) => { setFrDraft(data.report); showToast(t("toast.signatureSaved", "✓ Signature enregistrée")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setFrBusy(false));
  };
  const sendFrReport = () => {
    if (!frDraft) return;
    if (!frDraft.signatureUrl) { showToast(t("toast.signBeforeSend", "Signe le rapport avant de l'envoyer.")); return; }
    if (!frSelectedTargets.length) { showToast(t("toast.chooseRecipient", "Choisis au moins un destinataire.")); return; }
    setFrBusy(true);
    // Filet de sécurité (bug réel trouvé, 2026-08-26 : "le texte rédigé... n'est jamais
    // affiché après avoir été envoyé, même à l'impression") — du texte tapé dans la zone
    // "Rapport écrit" mais jamais explicitement validé via "Ajouter" ne vivait que dans
    // frSectionDraft (état local), jamais persisté côté serveur : il disparaissait donc
    // silencieusement à l'envoi. On le rattache automatiquement ici avant d'envoyer, pour
    // qu'un GR qui rédige puis envoie directement (sans avoir remarqué le bouton "Ajouter")
    // ne perde jamais ce qu'il a écrit.
    const pendingText = frSectionDraft.trim();
    const flush = pendingText
      ? apiJSON(`/api/filiale-reports/${frDraft.id}`, { method: "PATCH", body: JSON.stringify({ sections: [...frDraft.sections, { text: pendingText }] }) })
      : Promise.resolve(null);
    flush
      .then(() => {
        if (pendingText) setFrSectionDraft("");
        return apiJSON(`/api/filiale-reports/${frDraft.id}/send`, { method: "POST", body: JSON.stringify({ targetIds: frSelectedTargets }) });
      })
      .then(() => {
        showToast(t("toast.reportSent", "✓ Rapport envoyé"));
        setFrDraft(null); setFrScreen("hub"); setFrTab("mine"); loadFrMine();
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setFrBusy(false));
  };
  const deleteFrDraft = () => {
    if (!frDraft || !window.confirm(t("confirm.deleteDraftReport", "Supprimer ce brouillon ?"))) return;
    apiJSON(`/api/filiale-reports/${frDraft.id}`, { method: "DELETE" })
      .then(() => { setFrDraft(null); setFrScreen("hub"); loadFrMine(); showToast(t("toast.reportDeleted", "🗑️ Rapport supprimé")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  // Correctif #7 — le créateur peut supprimer son rapport même après envoi (avant : seul un
  // brouillon pouvait l'être) ; jamais accessible à qui que ce soit d'autre — le bouton
  // lui-même n'est rendu que pour le créateur (voir écran détail), et le serveur revérifie
  // de toute façon isCreator indépendamment de ce que montre l'UI.
  const deleteFrReportFromDetail = () => {
    if (!frDetail || !window.confirm(t("confirm.deleteReport", "Supprimer définitivement ce rapport ?"))) return;
    apiJSON(`/api/filiale-reports/${frDetail.id}`, { method: "DELETE" })
      .then(() => { setFrDetail(null); setFrScreen("hub"); loadFrMine(); loadFrReceived(); showToast(t("toast.reportDeleted", "🗑️ Rapport supprimé")); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const openFrDetail = (id) => {
    apiJSON(`/api/filiale-reports/${id}`).then((data) => { setFrDetail(data.report); setFrScreen("detail"); })
      .catch((err) => showToast(err.message || t("err.loadingImpossible", "Chargement impossible.")));
  };
  const toggleFrTodoItem = (todoId) => {
    if (!frDetail) return;
    apiJSON(`/api/filiale-reports/${frDetail.id}/todo/${todoId}/toggle`, { method: "POST" })
      .then((data) => setFrDetail(data.report))
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  const markFrTraite = () => {
    if (!frDetail) return;
    apiJSON(`/api/filiale-reports/${frDetail.id}/mark-traite`, { method: "POST" })
      .then((data) => { setFrDetail(data.report); showToast(t("toast.reportProcessed", "✓ Rapport marqué traité")); loadFrReceived(); })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };
  // Retour utilisateur explicite (2026-08-26) — "c'est très important la date maximale
  // d'accomplissement des tâches" : l'échéance doit être immédiatement visible, jamais une
  // simple ligne grise discrète. Couleur d'urgence dérivée de l'écart à aujourd'hui — même
  // esprit que les seuils déjà utilisés ailleurs dans l'app (ex. J-3 Réductions) — jamais
  // pour une tâche déjà faite (le badge "fait par" prend le relais).
  const frDueUrgency = (dueDate, done) => {
    if (done || !dueDate) return null;
    const days = Math.round((new Date(`${dueDate}T00:00:00`) - new Date(`${todayISO()}T00:00:00`)) / 86400000);
    if (days < 0) return { c: A.red, bg: "rgba(239,68,68,.13)", label: t("filialeReport.todo.overdueTemplate", { n: Math.abs(days) }, `En retard de ${Math.abs(days)} j`) };
    if (days === 0) return { c: A.red, bg: "rgba(239,68,68,.13)", label: t("filialeReport.todo.dueTodayLabel", "Échéance aujourd'hui") };
    if (days <= 3) return { c: A.orange, bg: "rgba(231,138,18,.14)", label: t("filialeReport.todo.dueSoonTemplate", { n: days, date: fmtFR(dueDate) }, `Dans ${days} j — ${fmtFR(dueDate)}`) };
    return { c: A.teal, bg: "rgba(13,148,136,.13)", label: t("filialeReport.todo.dueTemplate", { date: fmtFR(dueDate) }, `Avant le ${fmtFR(dueDate)}`) };
  };
  // Correctif #9 — export/impression, même patron que tous les autres modules de cette app
  // (buildXPrintHtml + portail dédié + window.print(), voir clearOtherPrintPortals).
  const buildFrReportPrintHtml = (r) => {
    const sections = (r.sections || []).map((s, i) => `<p><strong>${i + 1}.</strong> ${haccpEscHtml(s.text)}</p>`).join("");
    const todoRows = (r.todoItems || []).map((it, i) => {
      const due = r.todoDateMode === "shared" ? r.todoSharedDueDate : it.dueDate;
      return `<tr><td>${i + 1}</td><td>${haccpEscHtml(it.text)}</td><td>${due ? fmtFR(due) : "—"}</td><td>${it.done ? "✓" : ""}</td></tr>`;
    }).join("");
    return `<div class="frpx-page">
      <header class="frpx-header">
        <p class="frpx-eyebrow">${t("filialeReport.print.eyebrowTemplate", { n: r.reportNumber }, `Visite filiale N°${r.reportNumber}`)}</p>
        <h1>${t("home.store.label", { id: r.storeId }, `Filiale ${r.storeId}`)}</h1>
        <div class="frpx-meta">${t("filialeReport.detail.byTemplate", { name: r.createdByName || "", date: fmtFR(r.sentAt || r.createdAt) }, `Par ${r.createdByName || ""} — ${fmtFR(r.sentAt || r.createdAt)}`)}</div>
      </header>
      ${sections ? `<section>${sections}</section>` : ""}
      ${todoRows ? `<h2>${t("filialeReport.print.todoSectionTitle", "To-do")}</h2><table class="frpx-table"><thead><tr><th>#</th><th>${t("filialeReport.print.taskColumnHeader", "Tâche")}</th><th>${t("reductionsExport.deadline", "Échéance")}</th><th>${t("haccp.labelDone", "Fait")}</th></tr></thead><tbody>${todoRows}</tbody></table>` : ""}
      ${r.signatureUrl ? `<div class="frpx-sig"><img src="/api/filiale-reports/${r.id}/signature" /></div>` : ""}
    </div>`;
  };
  // Export PDF réel (exportHtmlToPdf, voir FRPX_DOC_CSS) — jamais window.print() : générait
  // l'en-tête/pied de page du navigateur (URL, date) sur la feuille (retour utilisateur
  // explicite, 2026-08-26, même défaut déjà corrigé pour Étiquettes/Warenaustausch).
  const exportFrReportPdf = async (r) => {
    if (frExporting) return;
    setFrExporting(true);
    try {
      await exportHtmlToPdf({
        bodyHtml: buildFrReportPrintHtml(r),
        css: FRPX_DOC_CSS, targetSelector: ".frpx-page", widthMm: 210,
        filename: `rapport-filiale-${r.storeId}-n${r.reportNumber}.pdf`,
      });
    } catch (err) {
      if (err?.name !== "AbortError") showToast(err.message || t("err.actionImpossible", "Action impossible."));
    } finally {
      setFrExporting(false);
    }
  };

  // Preisänderung — vraies données. Le serveur recalcule déjà tout (preisdifferenz/betrag/
  // status) à chaque lecture (voir server/preisanderung/repository.js) — le client ne
  // recalcule jamais rien lui-même, seulement l'affichage des totaux Gesamt (une simple
  // somme des betrag déjà calculés serveur, jamais un second calcul divergent).
  const loadPreisanderungen = () => {
    apiJSON("/api/preisanderungen").then((data) => setPreisanderungen(data.preisanderungen || [])).catch(() => {});
  };
  const openPreisanderung = (id) => { setPaActiveId(id); setPaBestandDraft({}); setPaScreen("detail"); };
  const savePaBestand = (docId, lineId, bestandValue) => {
    // Jamais artNr/alterPreis/neuerPreis/preisdifferenz/art dans ce corps — le serveur
    // rejetterait de toute façon tout champ hors bestand pour cette route (voir routes.js),
    // mais le client ne doit même pas donner l'impression que ces champs sont éditables.
    setPaSaving((m) => ({ ...m, [lineId]: true }));
    apiJSON(`/api/preisanderungen/${encodeURIComponent(docId)}/lines/${encodeURIComponent(lineId)}`, {
      method: "PATCH", body: JSON.stringify({ bestand: bestandValue === "" ? null : Number(bestandValue) }),
    })
      .then((data) => {
        setPreisanderungen((list) => list.map((p) => p.id === docId ? data.preisanderung : p));
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setPaSaving((m) => { const next = { ...m }; delete next[lineId]; return next; }));
  };
  // Boutons +/- (retour utilisateur explicite : "toujours possibilité d'utiliser le clavier
  // numérique" — le clavier reste entièrement fonctionnel via l'input lui-même, +/- ne sont
  // qu'un raccourci tactile en plus, jamais un remplacement).
  // paBestandLiveRef (retour utilisateur explicite : "un appui long doit déclencher une
  // saisie rapide") — un appui MAINTENU déclenche des tick répétés très rapprochés
  // (HoldButton, jusqu'à 30ms d'écart) : lire `paBestandDraft` (state React) à chaque tick
  // serait périmé (la fermeture JS capturée au pointerdown ne voit jamais l'état mis à jour
  // par les tick précédents dans la MÊME pression maintenue — seul un vrai re-rendu la
  // rafraîchirait, jamais assez vite). Un ref partagé, muté de façon synchrone à chaque
  // tick (jamais seulement resynchronisé au rendu), lui, reste toujours à jour quel que soit
  // le nombre de tick déjà exécutés dans la pression en cours — même patron que
  // `valueRef` du QtyStepper déjà utilisé ailleurs dans l'app (Inventur), qui a exactement
  // le même besoin.
  const paBestandLiveRef = useRef({});
  const stepPaBestand = (docId, lineId, currentBestand, delta) => {
    const base = paBestandLiveRef.current[lineId] ?? currentBestand ?? 0;
    const next = Math.max(0, (Number.isFinite(base) ? base : 0) + delta);
    paBestandLiveRef.current[lineId] = next;
    setPaBestandDraft((m) => ({ ...m, [lineId]: String(next) }));
    savePaBestand(docId, lineId, String(next));
  };
  const completePreisanderung = (docId) => {
    apiJSON(`/api/preisanderungen/${encodeURIComponent(docId)}/complete`, { method: "POST" })
      .then((data) => {
        setPreisanderungen((list) => list.map((p) => p.id === docId ? data.preisanderung : p));
        setPaSummarySheet(false);
        showToast(t("toast.preisanderungCompleted", "✓ Preisänderung terminée"));
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")));
  };

  // Admin > Preisänderung — suivi réseau (NEXORA_ADMIN voit tous les documents, regroupés
  // par batchId côté client pour l'affichage — un batch envoyé à 5 filiales = 5 documents
  // indépendants côté serveur, jamais partagés, voir server/preisanderung/repository.js).
  const loadPaAdminList = () => {
    apiJSON("/api/preisanderungen").then((data) => setPaAdminList(data.preisanderungen || [])).catch(() => {});
  };
  const addPaLine = () => setPaCreateForm((f) => ({ ...f, lines: [...f.lines, { ...emptyPaLine }] }));
  const removePaLine = (idx) => setPaCreateForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  const updatePaLine = (idx, field, value) => setPaCreateForm((f) => ({
    ...f, lines: f.lines.map((l, i) => i === idx ? { ...l, [field]: value } : l),
  }));
  const togglePaTargetStore = (storeId) => setPaCreateForm((f) => ({
    ...f, targetStoreIds: f.targetStoreIds.includes(storeId) ? f.targetStoreIds.filter((s) => s !== storeId) : [...f.targetStoreIds, storeId],
  }));
  const submitPaCreate = () => {
    const reference = paCreateForm.reference.trim();
    if (!reference) return showToast(t("preisanderung.admin.needReferenceToast", "Ajoute une référence."));
    if (!paCreateForm.targetStoreIds.length) return showToast(t("preisanderung.admin.needStoreToast", "Choisis au moins une filiale cible."));
    const validLines = paCreateForm.lines.filter((l) => l.artNr.trim() && l.name.trim() && l.alterPreis !== "" && l.neuerPreis !== "");
    if (!validLines.length) return showToast(t("preisanderung.admin.needLineToast", "Ajoute au moins un article complet."));
    setPaCreateBusy(true);
    apiJSON("/api/preisanderungen", {
      method: "POST",
      body: JSON.stringify({
        reference, targetStoreIds: paCreateForm.targetStoreIds,
        lines: validLines.map((l) => ({ artNr: l.artNr.trim(), name: l.name.trim(), alterPreis: Number(l.alterPreis), neuerPreis: Number(l.neuerPreis), art: l.art })),
      }),
    })
      .then(() => {
        showToast(t("toast.preisanderungSent", "✓ Preisänderung envoyée"));
        setPaCreateForm(emptyPaCreateForm);
        setPaAdminScreen("list");
        loadPaAdminList();
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setPaCreateBusy(false));
  };
  // Régénérer les données démo (2026-08-21, retour utilisateur explicite : "un plusieurs
  // articles et un mode démo qui peut s'actualiser") — vrais documents persistés (isDemo:true
  // côté serveur), remplacent COMPLÈTEMENT le lot démo précédent à chaque appel, jamais les
  // vrais documents. Cible : les filiales déjà cochées dans le formulaire de création (même
  // state réutilisé) ; si aucune n'est cochée, la première filiale du registre par défaut —
  // toujours modifiable avant de régénérer à nouveau.
  const regeneratePaDemo = () => {
    const targetStoreIds = paCreateForm.targetStoreIds.length ? paCreateForm.targetStoreIds : (storesCatalog[0] ? [storesCatalog[0].id] : []);
    if (!targetStoreIds.length) return showToast(t("preisanderung.admin.needStoreToast", "Choisis au moins une filiale cible."));
    setPaDemoBusy(true);
    apiJSON("/api/preisanderungen/demo/refresh", { method: "POST", body: JSON.stringify({ targetStoreIds }) })
      .then(() => {
        showToast(t("toast.preisanderungDemoRefreshed", "🎲 Données démo régénérées"));
        loadPaAdminList();
      })
      .catch((err) => showToast(err.message || t("err.actionImpossible", "Action impossible.")))
      .finally(() => setPaDemoBusy(false));
  };

  // Lieferschein — vraies données. trashed(local)↔deleted(réel), qty(local)↔expectedQty(réel).
  const mapServerLieferschein = (n) => ({
    id: n.id, supplier: n.supplier, deliveryDate: n.deliveryDate, deliveryNumber: n.number,
    archived: !!n.archived, trashed: !!n.deleted, createdBy: n.createdBy, createdAt: n.createdAt,
    pageCount: n.pageCount, status: n.status,
    groups: (n.groups || []).map((g) => ({ id: g.id, name: g.name })),
    items: (n.items || []).map((it) => ({
      id: it.id, artNr: it.artNr, name: it.name, qty: it.expectedQty, ve: it.ve,
      groupId: it.groupId, groupName: it.groupName,
      status: it.status, needsReview: it.needsReview, catalogMatch: it.catalogMatch, reviewReason: it.reviewReason,
    })),
  });
  const loadLieferscheine = () => {
    // mode=all exclut la corbeille côté serveur (voir server.js) — appel séparé pour trash,
    // fusionné ici pour que currentLfList() (déjà locale, filtre par lfTab) continue de
    // fonctionner exactement pareil sans changement JSX.
    Promise.all([apiJSON("/api/lieferschein?mode=all"), apiJSON("/api/lieferschein?mode=trash")])
      .then(([all, trash]) => setLieferscheine([...(all.lieferscheine || []), ...(trash.lieferscheine || [])].map(mapServerLieferschein)))
      .catch(() => {});
  };

  useEffect(() => {
    if (!user) {
      setArticles([]); setRecords([]); setTouched(new Set()); setStoreRecords([]); setRedStoreRecords([]); setStoreTrash([]);
      setNewArticles([]); setNewArticlesFF([]); setReduced([]); setRedTrash([]);
      setTasks([]); setTaskEmployees([]);
      setMaterialCatalogs(MATERIAL_CATALOGS_SEED); setMaterialOrders([]);
      setMerchCategories([]); setMerchSession(null); setMerchOrders([]); setMerchOrderDetail(null);
      setClaims([]); setFreshArticles([]); setClaimDefects(CLAIM_DEFECTS_DEFAULT); setClaimDestination({ method: null, value: "" });
      setLieferscheine([]);
      setKrankmeldungen([]); setKrankStoreList([]);
      setHaccpRecord(null); setHaccpWeeklyRecord(null);
      setPluActiveList(null); setPluAttempts([]); setPluTrainingAttempts([]); setPluProgress({});
      setPublicHolidays([]);
      setWeekPlan(null); setPublishedWeeksList([]);
      setPlanRequests([]);
      setVacationAccounts([]); setLfAccounts([]);
      setInfos([]); setInfoManageList([]); setInfoScope({ canCreate: false, canCreateNetwork: false, allowedStores: [], isNetworkWide: false });
      setFinderItems([]); setFinderCategories([]); setFinderAccess({ hasValidateTier: false, hasManageTier: false }); setFinderAdminItems([]);
      setWeeklyGrid({}); setStundenDays([]); setStundenImported(false);
      setAdminUsers([]); setAdminPending([]); setAdminSessions([]); setRolePerms({}); setRolePermsUpdatedAt(null);
      return;
    }
    loadArticles();
    loadKuhlungRecords();
    loadKuhlungExpiredRemoved();
    loadTouched();
    loadStoreRecords();
    loadRedStoreRecords();
    loadStoreTrash();
    loadNewArticles();
    loadNewArticlesFF();
    loadReduced();
    loadRedTrash();
    loadRuleOverrides();
    loadRuleDefinitions();
    loadSourceDefaults();
    loadCzRecords();
    loadCzExpiredRemoved();
    loadTaskEmployees();
    loadPublicHolidays();
    loadMyStoreHours();
    loadTasks();
    loadMaterialCatalog();
    loadMaterialOrders();
    loadMerchCategories();
    loadMerchOrderReminder();
    loadClaims();
    loadClaimSettings();
    loadFreshArticles();
    loadPreisanderungen();
    loadWarenaustauschOutgoing();
    loadWarenaustauschInbox();
    loadKontoauszug();
    loadLieferscheine();
    loadKrankmeldungen();
    loadHaccp();
    loadPluActiveList();
    loadPluProgress();
    loadPluAttempts();
    loadPluTrainingAttempts();
    loadPublishedWeeks();
    loadPlanUsersBirthdate();
    loadPlanRequests();
    loadLfAccounts();
    loadReportingCatalog();
    loadCatalogDaily();
    loadArticlesCatalog();
    loadMyLeader();
    loadArticlesMhdCounts();
    loadArticleReports();
    loadInventurAppointments();
    loadInventurCategoryTemplates();
    loadInventurInventories("active");
    loadInventurMyTasks();
    loadInventurTeamRoster();
    loadManualWasteWeek();
    loadKrankPersons();
    loadKrankHours();
    loadReportingHistory();
    loadCaCompare();
    loadDashboardExtras();
    loadAdminUsers();
    loadAdminPending();
    loadAdminResetRequests();
    loadAdminSessions();
    loadRolePermissions();
    loadStoresCatalog();
    loadStoreRegions();
    loadInfos();
    loadInfoScope();
    loadFinderCategories();
    loadFinderAccess();
  }, [user?.id]);

  // Document Intelligence Center — retiré du chargement automatique au login (retour
  // utilisateur explicite 2026-10-13 : "l'app... met pratiquement 15s à s'ouvrir") : ce
  // mount-effect géant déclenchait ~58 appels à chaque ouverture, pour TOUT compte, même
  // un employé qui n'ouvre jamais cet écran. docIntelDocs/docIntelSummary ne sont lus
  // QUE dans le rendu screen==="docintel" (vérifié, aucun badge/consommateur ailleurs),
  // donc chargé maintenant seulement à l'entrée réelle sur l'écran — même patron que
  // l'Arbeitszeitliste ("document rechargé à chaque entrée sur l'écran").
  useEffect(() => {
    if (user && screen === "docintel") loadDocIntel();
  }, [user?.id, screen]);

  // Resturlaub est réservé FL/FLA côté serveur — chargé seulement une fois la permission
  // dynamique résolue à true (usePermission est asynchrone, jamais vrai au tout premier rendu).
  useEffect(() => {
    if (user?.id && canManageVacation) loadVacationAccounts();
  }, [user?.id, canManageVacation]);

  // Fiches Artikelfinder en attente : même logique, réservé aux comptes avec le tiers
  // validation (article-finder.validate) — nécessaire dès le chargement pour le badge
  // "X à valider" de l'Assistant du jour, pas seulement à l'ouverture de l'écran admin.
  useEffect(() => {
    if (user?.id && finderAccess.hasValidateTier) loadFinderAdminItems();
  }, [user?.id, finderAccess.hasValidateTier]);

  // Le plan de travail dépend de la semaine consultée (weekStart), jamais juste de
  // l'utilisateur — rechargé à chaque changement de planWeekOffset (Précédente/Suivante).
  useEffect(() => {
    if (!user) return;
    loadWeekPlan(planWeekOffset);
    loadStundenleistungComparison(planWeekOffset);
    loadPlanWeekAvailability(planWeekOffset);
  }, [user?.id, planWeekOffset]);

  // Reporting — même patron que planWeekOffset ci-dessus : Saisies quotidiennes/Rapport
  // hebdomadaire dépendent de la semaine consultée (reportingWeekOffset), rechargées à
  // chaque Précédente/Suivante. Verrous transitoires (dailyLockOverride/weekLockOverride)
  // et sélection de jour remis à zéro — n'ont de sens que pour la semaine qu'on quitte.
  useEffect(() => {
    if (!user) return;
    // Bug réel corrigé (2026-09-23, retour utilisateur explicite : "quand je switche entre
    // les semaines... les données ne correspondent plus, je n'ai que de fausses
    // informations") — catalogDaily/weeklyGrid/dailySaved sont indexés par dayIdx (0-5,
    // Lundi-Samedi DE LA SEMAINE AFFICHÉE), jamais par date réelle. loadCatalogDaily/
    // loadManualWasteWeek FUSIONNENT depuis le correctif du 2026-09-19 (pour ne jamais
    // effacer une saisie locale non enregistrée pendant un sondage arrière-plan) — mais
    // cette fusion ne réécrit que les dayIdx que le serveur renvoie pour LA NOUVELLE
    // semaine. Un dayIdx sans saisie cette semaine-là gardait donc silencieusement la
    // valeur de la semaine précédemment affichée au même dayIdx — mélange de deux semaines
    // différentes, jamais une vraie régression du serveur. Reset explicite ICI (déclenché
    // uniquement par une vraie navigation de semaine, jamais par le sondage 15s qui
    // recharge la MÊME semaine) avant de fusionner les données fraîches — la fusion reste
    // donc un remplacement complet à chaque changement de semaine, exactement l'ancien
    // comportement, tout en gardant la protection contre la perte de saisie en vol.
    setCatalogDaily({ prebake: {}, brot: {} });
    setWeeklyGrid({});
    setDailySaved({ prebake: {}, brot: {}, manual_waste: {} });
    loadCatalogDaily();
    loadManualWasteWeek();
    loadReportingHistory();
    setDailyDayIdx(null);
    setDailyLockOverride({});
    // Bug réel corrigé (2026-09-20, retour utilisateur explicite : "quand on rouvre l'app, la
    // semaine redevient normale comme si on ne l'avait jamais fermée") — avant, ce reset était
    // un simple setWeekSaved(false) local, jamais relu depuis le serveur : "Semaine terminée"
    // ne survivait donc jamais à un rechargement. loadWeekLock() lit le vrai état serveur.
    loadWeekLock();
    setWeekLockOverride(false);
  }, [user?.id, reportingWeekOffset]);

  // Stundenleistung — sélecteur de mois (retour utilisateur : aucun moyen de consulter
  // un autre mois que le mois en cours). Même patron que reportingWeekOffset ci-dessus.
  useEffect(() => {
    if (!user) return;
    loadStundenleistung(stundenMonthOffset);
    setStundenIstEditDate(null);
    setStundenIstForm({ umsatz: "", heures: "" });
  }, [user?.id, stundenMonthOffset]);

  // Dashboard (compte normal) — live sans rafraîchissement (retour utilisateur explicite) :
  // les chiffres Reporting (CA, pertes, Leistung, Krank) doivent refléter automatiquement une
  // saisie faite ailleurs (autre poste de la même filiale) pendant que l'écran est ouvert, pas
  // seulement au montage de l'app. Même cadence que les autres polls opérationnels (15s,
  // notifications/missions/kontrolle-visibility ci-dessus/ci-dessous) ; actif seulement pendant
  // qu'on regarde l'écran Dashboard — ces 7 appels réseau seraient inutiles en arrière-plan sur
  // un autre écran.
  useEffect(() => {
    if (!user || screen !== "dashboard") return;
    const pollDashboard = () => {
      loadCaCompare();
      loadDashboardExtras();
      loadStundenleistung(stundenMonthOffset);
      loadManualWasteWeek();
      loadCatalogDaily();
      loadKrankPersons();
      loadKrankHours();
    };
    const poll = setInterval(pollDashboard, 15000);
    return () => clearInterval(poll);
  }, [user?.id, screen, stundenMonthOffset]);
  // Rapport hebdomadaire — reportingWeekOffset pilote AUSSI la vue lecture-seule "Historique"
  // déjà existante (weeklyViewWeek/pastWeekSrc, prix FIGÉS au moment de la clôture — jamais
  // recalculés au prix catalogue actuel, voir loadPastWeek) : dès qu'on s'éloigne de la
  // semaine réelle en cours (offset 0), bascule automatiquement dessus pour ne jamais
  // afficher un total historique recalculé à un prix qui a pu changer depuis.
  useEffect(() => {
    if (!user) return;
    if (reportingWeekOffset === 0) { setWeeklyViewWeek(null); setPastWeekSrc(null); return; }
    setWeeklyViewWeek(reportingWeekNumber);
    loadPastWeek(reportingWeekNumber);
  }, [user?.id, reportingWeekOffset]);

  // Écran dédié Aushilfe-Verfügbarkeit — semaine indépendante (availWeekOffset), chargée
  // seulement en y entrant et selon le rôle (soi-même Aushilfe vs liste responsable).
  // canManageAushilfeAvailability est asynchrone (usePermission) — jamais vrai au tout
  // premier rendu, donc dans les deps comme pour Resturlaub.
  useEffect(() => {
    if (!user || planScreen !== "aushilfe") return;
    if (isAushilfe) loadOwnAvailability(availWeekOffset);
    else if (canManageAushilfeAvailability) loadAvailabilityList(availWeekOffset);
  }, [user?.id, planScreen, availWeekOffset, isAushilfe, canManageAushilfeAvailability]);
  useEffect(() => { setAvailWeekOffset(0); }, [planScreen]);

  // Arbeitszeitliste — filiale par défaut résolue dès que l'utilisateur est connu (première
  // du périmètre), jamais recalculée après (le store select reste maître une fois touché).
  // Document rechargé à chaque changement filiale/mois ET à chaque entrée sur l'écran.
  useEffect(() => {
    if (user && !azlStoreId) {
      const stores = user?.stores?.length ? user?.stores : [user?.store];
      setAzlStoreId(stores[0] || "");
    }
  }, [user?.id]);
  useEffect(() => {
    if (!user || screen !== "azl" || !azlStoreId) return;
    loadAzl();
  }, [user?.id, screen, azlStoreId, azlYear, azlMonth]);

  useEffect(() => {
    if (!user || !isNetworkLeader || screen !== "home") return;
    loadManagerOverview();
    loadStoresRegistry();
    loadBezirkeRegistry();
    loadMissions();
  }, [user?.id, screen, isNetworkLeader]);

  // Poll continu des missions côté GR — retour utilisateur explicite, 2026-08-02 : "de
  // statut en cours passe automatiquement a terminer sans rafraichissement de la page". Le
  // useEffect ci-dessus ne rechargeait qu'une fois à l'entrée sur l'Accueil ; un GR resté
  // sur la fiche détail d'une mission ne voyait jamais le statut ou le compteur "encore B
  // filiale(s)" bouger tant qu'une filiale soumettait pendant qu'il regardait. Même cadence
  // que le poll employé (15s), non conditionné à l'écran pour la même raison que lui.
  useEffect(() => {
    if (!user || !isNetworkLeader) return;
    const poll = setInterval(loadMissions, 15000);
    return () => clearInterval(poll);
  }, [user?.id, isNetworkLeader]);

  // Signalement nouveaux articles — même cadence que les autres badges live (15s) : le badge
  // de la tuile Articles/accueil doit décrémenter/incrémenter sans rafraîchissement, que ce
  // soit un autre compte qui signale ou un FL/FLA qui résout (voir loadArticleReports).
  useEffect(() => {
    if (!user) return;
    const poll = setInterval(loadArticleReports, 15000);
    return () => clearInterval(poll);
  }, [user?.id]);

  // RDV inventaire (côté filiale) — retour utilisateur explicite (2026-08-17) : "il manque
  // les badges sur le module inventaire lors d'interaction entre un GR et une filiale pour
  // RDV inventaire et le comportement doit être pareil des 2 côtés". Avant, inventurAppointments
  // n'était chargé QUE sur l'écran RDV lui-même (jamais dispo pour calculer un badge) — même
  // cadence 15s que les autres badges live, cohérent avec loadPilotAppointments côté GR (qui,
  // lui, chargeait déjà proactivement dès "today" pour la même raison).
  useEffect(() => {
    if (!user) return;
    const poll = setInterval(loadInventurAppointments, 15000);
    return () => clearInterval(poll);
  }, [user?.id]);

  // Planungsanfragen — retour utilisateur 2026-08-09 : "tout ce processus doit être
  // automatique et ne nécessiter aucune actualisation". Sans ce poll, un employé ne
  // voyait la décision (approuvé/refusé) ou un nouveau message qu'après avoir quitté et
  // rouvert l'écran ; un FL/FLA ne voyait une nouvelle demande ou une contre-proposition de
  // date qu'au prochain montage. Même cadence et même patron que les autres polls "toujours
  // actifs" (notifications, missions) — le badge (hub tile + tuile Modules Accueil via
  // dayTasks, tous deux dérivés de planRequests) se met donc à jour tout seul, sans écran
  // particulier requis.
  useEffect(() => {
    if (!user) return;
    const poll = setInterval(loadPlanRequests, 15000);
    return () => clearInterval(poll);
  }, [user?.id]);

  // Pont employé — un compte normal doit voir les missions reçues, et le badge doit se
  // mettre à jour EN DIRECT (retour utilisateur explicite : "si une fois la mission passe
  // au statut accomplie le badge se decremente et s'incremente si d'autres missions
  // apparaissent"). Bug réel trouvé : la 1ère version ne rechargeait qu'au montage de
  // l'Accueil (screen==="home") — un employé déjà posté sur l'Accueil au moment où le GR
  // envoie une mission ne voyait jamais rien apparaître avant un rechargement complet.
  // Même cadence que le poll notifications juste au-dessus (15s), et surtout PAS
  // conditionné à screen==="home" pour la même raison.
  useEffect(() => {
    if (!user || isNetworkLeader) { setMyNetworkMissions([]); return; }
    loadMyMissions();
    const poll = setInterval(loadMyMissions, 15000);
    return () => clearInterval(poll);
  }, [user?.id, isNetworkLeader]);

  useEffect(() => {
    if (!user || !isNetworkLeader || screen !== "home") return;
    if (!["filiales", "analyse", "filiale-detail"].includes(pilotScreen)) return;
    if (!managerOverview?.stores?.length) return;
    loadPilotStoreStats();
  }, [user?.id, screen, isNetworkLeader, pilotScreen, pilotPeriod, periodOffset, managerOverview?.stores?.join(",")]);

  useEffect(() => {
    if (!user || !isNetworkLeader || screen !== "home" || pilotScreen !== "analyse") return;
    if (!managerOverview?.stores?.length) return;
    loadPilotHistory(evolutionWeekOffset);
  }, [user?.id, screen, isNetworkLeader, pilotScreen, evolutionWeekOffset, managerOverview?.stores?.join(",")]);

  // Pilotage réseau (Filiales/Analyse) — mêmes chiffres Reporting doivent se mettre à jour en
  // direct pendant que le GR regarde l'écran (retour utilisateur explicite : dashboards compte
  // normal ET GR doivent se mettre à jour automatiquement avec les données Reporting de chaque
  // filiale), pas seulement à la navigation/au changement de période comme les 2 useEffect
  // ci-dessus. Mêmes gardes qu'eux, cadence 15s comme les autres polls opérationnels.
  useEffect(() => {
    if (!user || !isNetworkLeader || screen !== "home") return;
    if (!["filiales", "analyse", "filiale-detail"].includes(pilotScreen)) return;
    if (!managerOverview?.stores?.length) return;
    const poll = setInterval(() => {
      loadPilotStoreStats();
      if (pilotScreen === "analyse") loadPilotHistory(evolutionWeekOffset);
    }, 15000);
    return () => clearInterval(poll);
  }, [user?.id, screen, isNetworkLeader, pilotScreen, pilotPeriod, periodOffset, evolutionWeekOffset, managerOverview?.stores?.join(",")]);

  // Rythme Umsatz (Analyse, filiale unique) — jamais navigué au-delà du mois en cours ;
  // repart sur le mois en cours dès qu'on change de filiale ou qu'on quitte l'écran, pour ne
  // jamais laisser une filiale hériter du mois consulté sur la filiale précédente.
  useEffect(() => { setPilotRhythmMonthOffset(0); }, [pilotDashFiliale, pilotScreen]);
  useEffect(() => { setPilotChartHoverDay(null); }, [pilotDashFiliale, pilotScreen, pilotRhythmMonthOffset]);
  useEffect(() => { setPilotAggHoverIdx(null); }, [pilotDashFiliale, pilotScreen, pilotPeriod]);

  // Résolution journalière réelle (Plan + Ist) de la filiale sélectionnée — même construction
  // que loadStundenleistung/loadPilotStundenDays (buildStundenDaysArray), re-scopée à
  // pilotDashFiliale ET navigable par mois (pilotRhythmMonthOffset). Non applicable au
  // périmètre "Toutes les filiales" (voir bloc de rendu — reste sur pilotHistory/
  // pilotHistoryPlan hebdo, jamais de fan-out journalier réseau entier ici, cf. échelle
  // 1200+ filiales). Même cadence 15s que les autres rubriques "live" de Pilotage réseau.
  useEffect(() => {
    if (!user || !isNetworkLeader || screen !== "home" || pilotScreen !== "analyse" || pilotDashFiliale === "all") {
      setPilotRhythmDays([]);
      return;
    }
    const loadPilotRhythmDays = () => {
      const base = new Date(now.getFullYear(), now.getMonth() + pilotRhythmMonthOffset, 1);
      const year = base.getFullYear();
      const month = base.getMonth() + 1;
      Promise.all([
        apiJSON(`/api/reporting/stunden-plan?storeId=${encodeURIComponent(pilotDashFiliale)}&year=${year}&month=${month}`).catch(() => null),
        apiJSON(`/api/reporting/daily-entries?storeId=${encodeURIComponent(pilotDashFiliale)}&family=stundenleistung&month=${month}&year=${year}`).catch(() => []),
      ]).then(([plan, istEntries]) => {
        const planDays = plan?.days || {};
        const istByDate = {};
        (Array.isArray(istEntries) ? istEntries : []).forEach((e) => { istByDate[e.date] = e.items?.[0] || {}; });
        setPilotRhythmDays(buildStundenDaysArray(planDays, istByDate, year, month));
      }).catch(() => setPilotRhythmDays([]));
    };
    loadPilotRhythmDays();
    const poll = setInterval(loadPilotRhythmDays, 15000);
    return () => clearInterval(poll);
  }, [user?.id, screen, isNetworkLeader, pilotScreen, pilotDashFiliale, pilotRhythmMonthOffset]);

  // Onglet Comparaison — mode "Périodes" (1 filiale, N périodes choisies librement — retour
  // utilisateur explicite 2026-08-17, jamais borné à 2). Même cadence 15s "en direct" que le
  // reste de Pilotage réseau (voir commentaire ci-dessus).
  const comparePeriodsKey = comparePeriods.map((p) => `${p.id}:${p.period}:${p.offset}`).join(",");
  useEffect(() => {
    if (!user || !isNetworkLeader || screen !== "home" || pilotScreen !== "analyse" || analyseTab !== "comparaison" || compareMode !== "periods" || !compareFiliale || comparePeriods.length === 0) {
      return;
    }
    const loadCompareStatsPeriods = () => {
      setCompareLoading(true);
      Promise.all(comparePeriods.map((p) =>
        fetchStoreStatsFor([compareFiliale], p.period, p.offset).then((res) => [p.id, res[compareFiliale] || null])
      )).then((pairs) => setComparePeriodsStats(Object.fromEntries(pairs)))
        .finally(() => setCompareLoading(false));
    };
    loadCompareStatsPeriods();
    const poll = setInterval(loadCompareStatsPeriods, 15000);
    return () => clearInterval(poll);
  }, [user?.id, screen, isNetworkLeader, pilotScreen, analyseTab, compareMode, compareFiliale, comparePeriodsKey]);

  // Onglet Comparaison — mode "Filiales" (N filiales, 1 période commune). Repart de zéro
  // sous 2 filiales sélectionnées (une comparaison à 1 n'a pas de sens, voir garde-fou côté
  // rendu) pour ne jamais afficher un résultat obsolète après une désélection.
  useEffect(() => {
    if (!user || !isNetworkLeader || screen !== "home" || pilotScreen !== "analyse" || analyseTab !== "comparaison" || compareMode !== "stores" || compareStoresSelected.length < 2) {
      setCompareStoresStats({});
      return;
    }
    const loadCompareStatsStores = () => {
      setCompareLoading(true);
      fetchStoreStatsFor(compareStoresSelected, compareStoresPeriod, compareStoresOffset)
        .then(setCompareStoresStats)
        .finally(() => setCompareLoading(false));
    };
    loadCompareStatsStores();
    const poll = setInterval(loadCompareStatsStores, 15000);
    return () => clearInterval(poll);
  }, [user?.id, screen, isNetworkLeader, pilotScreen, analyseTab, compareMode, compareStoresSelected.join(","), compareStoresPeriod, compareStoresOffset]);

  // Repart proprement quand on quitte l'onglet/l'écran — jamais un état de comparaison
  // fantôme visible en revenant plus tard sur un autre périmètre.
  useEffect(() => {
    if (analyseTab !== "comparaison" || pilotScreen !== "analyse") {
      setComparePeriodsStats({}); setCompareStoresStats({});
    }
  }, [analyseTab, pilotScreen]);

  // Pad de signature — monté seulement pendant que le Sheet "azlValidate" est ouvert
  // (le Sheet lazy-mount ses children dès la première ouverture, voir hasOpenedRef côté
  // composant Sheet — le ref est donc déjà présent au premier passage de cet effet).
  useEffect(() => {
    if (sheet !== "azlValidate") {
      azlSignPadRef.current?.destroy();
      azlSignPadRef.current = null;
      return;
    }
    const mount = azlSignPadMountRef.current;
    if (!mount) return;
    azlSignPadRef.current = createSignaturePad(mount, {
      hintText: "Signe ici avec le doigt ou la souris",
      clearLabel: "Effacer",
      // Trait toujours foncé, jamais P.ink : le pad reste blanc quel que soit le thème
      // (voir signaturePad.js, background:#fff codé en dur) — en thème sombre, P.ink est
      // clair pour rester lisible SUR fond sombre, ce qui rendait le trait quasi invisible
      // sur ce fond blanc (retour utilisateur, capture à l'appui).
      strokeColor: "#211d16",
      borderColor: P.line,
      softBg: P.soft,
      inkColor: P.ink,
      onChange: (valid) => setAzlSignReady(valid),
    });
    return () => { azlSignPadRef.current?.destroy(); azlSignPadRef.current = null; };
  }, [sheet]);

  // Pad de signature Rapport de filiale — même patron qu'azlSignPadRef juste au-dessus,
  // monté tant que l'écran de construction est affiché ET qu'un brouillon signable existe
  // (jamais avant, le pad n'a alors nulle part où écrire).
  useEffect(() => {
    if (frScreen !== "new" || !frDraft) {
      frSigPadRef.current?.destroy();
      frSigPadRef.current = null;
      return;
    }
    const mount = frSigMountRef.current;
    if (!mount) return;
    frSigPadRef.current = createSignaturePad(mount, {
      hintText: t("common.signHereHint", "Signe ici avec le doigt ou la souris"),
      clearLabel: t("common.clearBtn", "Effacer"),
      strokeColor: "#211d16", borderColor: P.line, softBg: P.soft, inkColor: P.ink,
      onChange: (valid) => setFrSigReady(valid),
    });
    return () => { frSigPadRef.current?.destroy(); frSigPadRef.current = null; };
  }, [frScreen, frDraft?.id]);

  // Pad de signature Warenaustausch — même patron que frSigPadRef ci-dessus, monté tant que
  // l'écran de création est affiché ET que le brouillon est encore modifiable (jamais après
  // envoi, la signature est alors déjà figée côté serveur).
  useEffect(() => {
    const tr = waDetailId ? waFindTransfer(waDetailId) : null;
    if (waScreen !== "new" || !tr || tr.status !== "brouillon") {
      waSigPadRef.current?.destroy();
      waSigPadRef.current = null;
      return;
    }
    const mount = waSigMountRef.current;
    if (!mount) return;
    waSigPadRef.current = createSignaturePad(mount, {
      hintText: t("common.signHereHint", "Signe ici avec le doigt ou la souris"),
      clearLabel: t("common.clearBtn", "Effacer"),
      strokeColor: "#211d16", borderColor: P.line, softBg: P.soft, inkColor: P.ink,
      onChange: (valid) => setWaSigReady(valid),
    });
    return () => { waSigPadRef.current?.destroy(); waSigPadRef.current = null; };
  }, [waScreen, waDetailId]);

  // Pads de signature RR — même patron que Warenaustausch ci-dessus, mais DEUX pads
  // distincts (filiale confirme, Niederlassung valide) montés à des moments différents du
  // MÊME document, jamais simultanément (voir les conditions de statut/périmètre).
  useEffect(() => {
    const bon = rrDetailId ? rrFindBon(rrDetailId) : null;
    if (rrScreen !== "detail" || !bon || bon.status !== "envoye" || user?.store !== bon.toStoreId) {
      rrSigPadRef.current?.destroy();
      rrSigPadRef.current = null;
      return;
    }
    const mount = rrSigMountRef.current;
    if (!mount) return;
    rrSigPadRef.current = createSignaturePad(mount, {
      hintText: t("common.signHereHint", "Signe ici avec le doigt ou la souris"),
      clearLabel: t("common.clearBtn", "Effacer"),
      strokeColor: "#211d16", borderColor: P.line, softBg: P.soft, inkColor: P.ink,
      onChange: (valid) => setRrSigReady(valid),
    });
    return () => { rrSigPadRef.current?.destroy(); rrSigPadRef.current = null; };
  }, [rrScreen, rrDetailId, rrList]);

  useEffect(() => {
    const bon = rrDetailId ? rrFindBon(rrDetailId) : null;
    if (rrScreen !== "detail" || !bon || bon.status !== "confirme" || user?.store !== bon.fromStoreId) {
      rrNlSigPadRef.current?.destroy();
      rrNlSigPadRef.current = null;
      return;
    }
    const mount = rrNlSigMountRef.current;
    if (!mount) return;
    rrNlSigPadRef.current = createSignaturePad(mount, {
      hintText: t("common.signHereHint", "Signe ici avec le doigt ou la souris"),
      clearLabel: t("common.clearBtn", "Effacer"),
      strokeColor: "#211d16", borderColor: P.line, softBg: P.soft, inkColor: P.ink,
      onChange: (valid) => setRrNlSigReady(valid),
    });
    return () => { rrNlSigPadRef.current?.destroy(); rrNlSigPadRef.current = null; };
  }, [rrScreen, rrDetailId, rrList]);

  // Mêmes 2 pads par formulaire Inventurmaßnahme, même patron que ci-dessus — factorisé en
  // une petite fonction plutôt que 4 useEffect quasi identiques.
  const mountImSignaturePad = (active, mountRef, padRef, setReady) => {
    if (!active) { padRef.current?.destroy(); padRef.current = null; return; }
    const mount = mountRef.current;
    if (!mount) return;
    padRef.current = createSignaturePad(mount, {
      hintText: t("common.signHereHint", "Signe ici avec le doigt ou la souris"),
      clearLabel: t("common.clearBtn", "Effacer"),
      strokeColor: "#211d16", borderColor: P.line, softBg: P.soft, inkColor: P.ink,
      onChange: (valid) => setReady(valid),
    });
    return () => { padRef.current?.destroy(); padRef.current = null; };
  };
  useEffect(() => mountImSignaturePad(screen === "inventurmassnahme" && imScreen === "lieferschein", lsSigMitMountRef, lsSigMitPadRef, setLsSigMitReady), [screen, imScreen]);
  useEffect(() => mountImSignaturePad(screen === "inventurmassnahme" && imScreen === "lieferschein", lsSigFlMountRef, lsSigFlPadRef, setLsSigFlReady), [screen, imScreen]);
  useEffect(() => mountImSignaturePad(screen === "inventurmassnahme" && imScreen === "lieferschein" && isNetworkLeader, lsSigBlMountRef, lsSigBlPadRef, setLsSigBlReady), [screen, imScreen, isNetworkLeader]);
  useEffect(() => mountImSignaturePad(screen === "inventurmassnahme" && imScreen === "testkauf", bwSigTkMountRef, bwSigTkPadRef, setBwSigTkReady), [screen, imScreen]);
  useEffect(() => mountImSignaturePad(screen === "inventurmassnahme" && imScreen === "testkauf" && isNetworkLeader, bwSigBlMountRef, bwSigBlPadRef, setBwSigBlReady), [screen, imScreen, isNetworkLeader]);

  /* Centre de notifications */
  const notifUnreadCount = notifications.filter((n) => !n.read).length;
  // Titre traduit d'une notification — le serveur compose déjà un titre français final
  // (n.title, toujours présent, gardé comme fallback). Correction d'architecture réelle
  // (2026-08-17) : les titres sont en pratique de courtes chaînes QUASI-STATIQUES qui
  // varient selon le code path (plusieurs titres distincts partagent le même `type`, ex.
  // "tasks" a "Nouvelle tâche filiale"/"Tâche accomplie"/"Examen PLU commencé"...) — le
  // vrai contenu dynamique (nom, nombre, libellé...) vit dans `message` (hors périmètre
  // ici). Donc PAS de dictionnaire type->clé avec params : le serveur envoie en plus un
  // `titleKey` stable (slug par titre distinct, pas par type) quand câblé ; sinon t()
  // retombe silencieusement sur n.title — aucune régression sur les notifications
  // existantes ou les titres pas encore couverts.
  const notifTitle = (n) => n.titleKey ? t(`notifications.title.${n.titleKey}`, n.params || {}, n.title) : n.title;
  // Symétrique de notifTitle (2026-08-17, audit i18n — signalement utilisateur explicite,
  // capture à l'appui : page Notifications en allemand mais contenu toujours en français).
  // n.body porte le texte français déjà formaté par le serveur (voir mapServerNotification),
  // conservé comme fallback fidèle pour tout enregistrement sans messageKey (historique).
  const notifMessage = (n) => n.messageKey ? t(`notifications.message.${n.messageKey}`, n.params || {}, n.body) : n.body;
  const markNotifRead = (id) => {
    setNotifications((ns) => ns.map((n) => n.id === id ? { ...n, read: true } : n));
    apiJSON(`/api/notifications/${id}/read?${identityQuery(user)}`, { method: "PATCH" }).catch(() => {});
  };
  const markAllNotifsRead = () => {
    setNotifications((ns) => ns.map((n) => ({ ...n, read: true })));
    apiJSON(`/api/notifications/read-all?${identityQuery(user)}`, { method: "PATCH" }).catch(() => {});
    showToast(t("toast.allMarkedRead", "✓ Tout marqué comme lu"));
  };
  const clearReadNotifs = () => {
    setNotifications((ns) => ns.filter((n) => !n.read));
    apiJSON(`/api/notifications/read?${identityQuery(user)}`, { method: "DELETE" }).catch(() => {});
    showToast(t("toast.readNotifsCleared", "Notifications lues effacées"));
  };
  const deleteNotif = (id) => {
    setNotifications((ns) => ns.filter((n) => n.id !== id));
    apiJSON(`/api/notifications/${id}?${identityQuery(user)}`, { method: "DELETE" }).catch(() => {});
  };
  // DELETE /api/notifications/all ajouté côté serveur spécifiquement pour ce bouton —
  // aucun équivalent dans l'ancien frontend vanilla (qui n'a que "effacer les lues").
  const deleteAllNotifs = () => {
    setNotifications([]);
    apiJSON(`/api/notifications/all?${identityQuery(user)}`, { method: "DELETE" })
      .catch(() => { showToast(t("toast.deletionRefused", "Suppression refusée.")); loadNotifications(); });
  };
  const dismissUrgentItem = (label) => setDismissedUrgent((d) => [...d, label]);
  const openNotification = (n) => {
    markNotifRead(n.id);
    // Rendez-vous d'inventaire — destination différente selon le rôle (deriveNotifGo ne
    // peut pas trancher seul, la même relatedInventurAppointmentId doit amener un BL sur
    // sa page dédiée et une filiale sur l'écran Inventur > Rendez-vous) — sort tôt, avant
    // le setScreen(n.go) générique en bas de fonction qui ne connaît pas ce cas.
    if (n.relatedInventurAppointmentId) {
      if (isNetworkLeader) { setPilotScreen("appointments"); setScreen("home"); }
      else { setInventurScreen("rdv"); setScreen("inventur"); }
      return;
    }
    // Planning — même destination que le fil Action Live (goToDayTask) pour le même
    // événement conceptuel : "requests" liste réellement les Urlaubswunsch/Freiwunsch/
    // Leistungsfrei en attente, jamais juste le hub (bug réel trouvé en câblant "toutes les
    // notifications", 2026-08-18 — openNotification atterrissait sur le hub nu alors que
    // goToDayTask, pour la même famille d'événement, va déjà sur "requests"). Publication
    // d'un planning ("planning_published") reste un cas à part : sa vraie destination est
    // l'onglet "Plans publiés", pas la file de demandes.
    if (n.go === "planning") setPlanScreen(n.type === "planning_published" ? "published" : "requests");
    // Route vers la vraie tuile de la tâche (retour utilisateur explicite, 2026-08-07) —
    // avant, atterrissait toujours sur taskFilter=null (vue non filtrée), qui montrait bien
    // la tâche mais jamais sa catégorie réelle. Voir routeForTaskId.
    if (n.go === "tasks") {
      const route = routeForTaskId(n.relatedTaskId);
      setTasksScreen("list"); setTaskScope("store"); setTaskTab("open");
      setTaskFilter(route.filter); setTeamSubView(route.teamSub);
    }
    if (n.go === "haccp") setHaccpScreen("hub");
    if (n.go === "claims") setClaimsScreen("hub");
    // Krankmeldung/Lieferschein — ramène directement sur le dossier précis (openKrankDetail/
    // openLfDetail, déjà utilisés partout ailleurs pour ouvrir un dossier depuis une liste),
    // jamais juste le hub du module (même famille de bug que "toutes les notifications",
    // 2026-08-18 — relatedKrankmeldungId/relatedLieferscheinId routaient déjà vers le bon
    // module via deriveNotifGo, mais rien n'ouvrait ensuite le dossier précis).
    if (n.go === "krank") { if (n.relatedKrankmeldungId) openKrankDetail(n.relatedKrankmeldungId); else setKrankScreen("hub"); }
    if (n.go === "liefer" && n.relatedLieferscheinId) openLfDetail(n.relatedLieferscheinId);
    if (n.go === "material") setMoScreen("hub");
    // Notification de message → ouvre directement le fil concerné (relatedChatId, déjà
    // renvoyé par le serveur) plutôt que juste la liste — bug trouvé lors de l'audit du
    // 2026-08-02, obligeait un tap supplémentaire à chaque notification.
    if (n.go === "messages") { if (n.relatedChatId) openThread(n.relatedChatId); else setMsgScreen("list"); }
    if (n.go === "inventurmassnahme" && n.relatedPluAttemptId) { setPluScreen("hub"); setImScreen("pluquiz"); }
    // Lieferschein Test / Testkauf échoué — ramène sur l'archive hebdomadaire où le dossier
    // apparaît (aucun écran de détail dédié par id n'existe pour ce sous-module, voir
    // imBuildWeeklyItem), jamais juste la liste générique de notifications.
    if (n.go === "inventurmassnahme" && n.relatedInventurmassnahmeId) { setImScreen("archive"); loadImArchive(); }
    // Signalement nouveaux articles — deep-link direct vers l'historique (artefact écran 5),
    // pas juste le hub du module.
    if (n.go === "articles" && n.type === "article-report") setArtScreen("reports");
    // Fiches Artikelfinder en attente — ramène le validateur directement sur la file de
    // révision, jamais juste le hub de recherche.
    if (n.go === "finder" && n.type === "article-finder") setFinderScreen("admin");
    // Info importante — ramène directement sur la fiche de lecture/signature (retour
    // utilisateur explicite, 2026-08-18), jamais juste le hub des infos importantes. Même
    // fonction que le vrai chargement du détail (openInfoDetail, matérialise la ligne
    // destinataire + ouvre) — pas une navigation à part.
    if (n.go === "infos" && n.relatedImportantInfoId) openInfoDetail(n.relatedImportantInfoId);
    // Missions réseau / Préisänderung / Inventur — mêmes ouvreurs de détail que ceux déjà
    // utilisés depuis les listes respectives (openMissionDetail/openPreisanderung/
    // openInventurInventory), jamais juste le hub du module (retour utilisateur explicite,
    // 2026-08-18 : "le même système sur toutes les notifications").
    if (n.go === "missions" && n.relatedMissionId) openMissionDetail(n.relatedMissionId);
    if (n.go === "preisanderung" && n.relatedPreisanderungId) openPreisanderung(n.relatedPreisanderungId);
    if (n.go === "inventur" && n.relatedInventurId) openInventurInventory(n.relatedInventurId);
    if (n.go === "filialeReport" && n.relatedFilialeReportId) { setScreen("filialeReport"); openFrDetail(n.relatedFilialeReportId); }
    // RR — pas de route GET dédiée par id à part entière côté serveur au-delà de la liste
    // (même choix que Commande marchandise juste en dessous) : on recharge la liste puis on
    // ouvre le détail, rrFindBon retrouvant le bon une fois rrList peuplé.
    if (n.go === "rr" && n.relatedRrId) { loadRrList(); setRrScreen("detail"); setRrDetailId(n.relatedRrId); setScreen("rr"); }
    if (n.go === "kontoauszug" && n.relatedKontoauszugCheckId) { loadKontoChecks(); setKontoScreen("detail"); setKontoDetailId(n.relatedKontoauszugCheckId); setScreen("kontoauszug"); }
    // Commande marchandise — openMerchOrderDetail attend l'objet commande complet (pas
    // juste un id, contrairement aux autres ouvreurs de cette fonction) et aucune route
    // GET /:id dédiée n'existe côté serveur (seulement la liste) : on recharge la liste et
    // on retrouve la commande par id, plutôt que d'ajouter une route rien que pour ce cas.
    if (n.go === "merchandise" && n.relatedMerchandiseOrderId) {
      setScreen("merchandise");
      apiJSON("/api/merchandise-orders")
        .then((d) => {
          const order = (d.orders || []).find((o) => o.id === n.relatedMerchandiseOrderId);
          if (order) openMerchOrderDetail(order);
        })
        .catch(() => {});
    }
    setScreen(n.go);
  };


  /* Artikelfinder — base collaborative réelle, recherche manuelle réelle
     (GET /search, journalise même l'historique de recherche côté serveur).
     Reconnaissance photo RÉELLE (2026-08-04) : moteur CLIP auto-hébergé côté serveur
     (POST /recognize), capture via le même patron d'input caméra que le Centre
     documentaire (capture="environment" — pas de getUserMedia ici, cohérent avec le
     reste du module). */
  const loadFinderCategories = () => apiJSON("/api/article-finder/categories").then((d) => setFinderCategories((d.categories || []).filter((c) => !c.archived))).catch(() => {});
  const loadFinderAccess = () => apiJSON("/api/article-finder/my-access").then(setFinderAccess).catch(() => {});
  const loadFinderAdminItems = () => apiJSON("/api/article-finder/admin/items?status=pending_review").then((d) => setFinderAdminItems(d.items || [])).catch((err) => showToast(err.message || t("err.pendingCardsLoadImpossible", "Impossible de charger les fiches en attente.")));
  const runManualSearch = () => {
    const params = new URLSearchParams();
    if (finderQuery.trim()) params.set("q", finderQuery.trim());
    if (finderCategory !== "all") params.set("category", finderCategory);
    apiJSON(`/api/article-finder/search?${params.toString()}`)
      .then((d) => { setFinderResults((d.items || []).map((item) => ({ item, confidenceLevel: "high" }))); setFinderScreen("results"); })
      .catch((err) => showToast(err.message || t("err.searchImpossible", "Recherche impossible.")));
  };
  const runPhotoRecognition = () => { finderRecognizeInputRef.current?.click(); };
  const handleFinderRecognizePhoto = (file) => {
    if (!file) return;
    setFinderPhotoScan(true);
    const formData = new FormData();
    formData.append("photo", file);
    apiJSON("/api/article-finder/recognize", { method: "POST", body: formData })
      .then((d) => {
        if (!d.configured) { showToast(d.reason || t("finder.recognitionUnavailable", "Reconnaissance visuelle indisponible pour le moment.")); return; }
        setFinderResults(d.matches || []);
        setFinderScreen("results");
      })
      .catch((err) => showToast(err.message || t("err.recognitionImpossible", "Reconnaissance impossible.")))
      .finally(() => setFinderPhotoScan(false));
  };
  // Créer une fiche — désignation+catégorie obligatoires côté serveur (prix optionnel).
  // Retour utilisateur explicite : "une fois creer elle doit directement exister et non
  // etre encore validerr" — POST /items crée désormais directement en statut "validated"
  // (voir server/articleFinder/routes.js), plus de palier pending_review/"un responsable va
  // la valider" pour une fiche créée depuis ce formulaire. Les photos (plusieurs possibles,
  // jusqu'à MAX_IMAGES_PER_ITEM=5) restent envoyées dans un 2e appel (POST /items puis
  // POST /items/:id/photos), mais uniquement pour attacher les images — plus pour déclencher
  // une transition de statut.
  const openFinderCreate = () => {
    setFinderCreateForm({ designation: "", category: finderCategories[0]?.label || "", price: "", ean: "", articleNumber: "" });
    setFinderCreatePhotos([]);
    setFinderScreen("create");
  };
  const pickFinderCreatePhotos = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setFinderCreatePhotos((prev) => {
      const room = 5 - prev.length;
      if (room <= 0) { showToast(t("finder.create.maxPhotos", "Maximum 5 photos par fiche.")); return prev; }
      const toAdd = files.slice(0, room).map((file) => ({ file, preview: URL.createObjectURL(file) }));
      if (files.length > room) showToast(t("finder.create.maxPhotos", "Maximum 5 photos par fiche."));
      return [...prev, ...toAdd];
    });
  };
  const removeFinderCreatePhoto = (index) => {
    setFinderCreatePhotos((prev) => prev.filter((_, i) => i !== index));
  };
  const submitFinderCreate = () => {
    const designation = finderCreateForm.designation.trim();
    if (!designation) { showToast(t("finder.create.designationRequired", "Indique une désignation.")); return; }
    if (!finderCreateForm.category) { showToast(t("finder.create.categoryRequired", "Choisis une catégorie.")); return; }
    setFinderCreateSaving(true);
    const priceAmount = finderCreateForm.price ? Number(String(finderCreateForm.price).replace(",", ".")) : null;
    // Numéro d'article et EAN facultatifs (retour utilisateur 2026-08-04) — le serveur ne
    // les a jamais exigés (seules désignation+catégorie le sont, voir POST /items), il
    // manquait juste les champs + le scan dans ce formulaire.
    const body = { designation, category: finderCreateForm.category };
    if (Number.isFinite(priceAmount) && priceAmount > 0) body.price = { amount: priceAmount };
    if (finderCreateForm.ean.trim()) body.ean = finderCreateForm.ean.trim();
    if (finderCreateForm.articleNumber.trim()) body.articleNumber = finderCreateForm.articleNumber.trim();
    apiJSON("/api/article-finder/items", { method: "POST", body: JSON.stringify(body) })
      .then((d) => {
        const item = d.item;
        if (!finderCreatePhotos.length) {
          setFinderItems((items) => [...items, item]);
          showToast(t("finder.create.saved", "✓ Fiche créée."));
          setFinderCreateSaving(false);
          setFinderScreen("hub");
          return;
        }
        const formData = new FormData();
        finderCreatePhotos.forEach((p) => formData.append("photos", p.file));
        apiJSON(`/api/article-finder/items/${item.id}/photos`, { method: "POST", body: formData })
          .then((d2) => {
            setFinderItems((items) => [...items, d2.item]);
            showToast(t("finder.create.savedWithPhotos", "✓ Fiche créée."));
            setFinderCreatePhotos([]);
            setFinderScreen("hub");
          })
          .catch((err) => {
            setFinderItems((items) => [...items, item]);
            showToast(err.message || t("finder.create.photoUploadFailed", "Fiche créée, mais l'envoi des photos a échoué — ajoute-les depuis la fiche."));
            setFinderCreatePhotos([]);
            openFinderDetail(item.id, "hub");
          })
          .finally(() => setFinderCreateSaving(false));
      })
      .catch((err) => { showToast(err.message || t("err.creationImpossible", "Création impossible.")); setFinderCreateSaving(false); });
  };
  const openFinderDetail = (id, backTo = "results") => {
    setFinderDetailId(id);
    setFinderDetailBackScreen(backTo);
    setFinderScreen("detail");
    setFinderEditingPrice(false);
    apiJSON(`/api/article-finder/items/${id}`)
      .then((d) => setFinderItems((items) => items.some((i) => i.id === d.item.id) ? items.map((i) => i.id === d.item.id ? d.item : i) : [...items, d.item]))
      .catch((err) => showToast(err.message || t("err.cardNotFound", "Fiche introuvable.")));
  };
  // Catalogue — liste des fiches réellement disponibles pour l'employé (validated/
  // price_review_required) ou tout le catalogue de la filiale pour un responsable.
  const loadFinderCatalog = () => {
    setFinderCatalogLoading(true);
    const params = new URLSearchParams();
    if (finderCategory !== "all") params.set("category", finderCategory);
    apiJSON(`/api/article-finder/items${params.toString() ? `?${params.toString()}` : ""}`)
      .then((d) => setFinderCatalog(d.items || []))
      .catch(() => setFinderCatalog([]))
      .finally(() => setFinderCatalogLoading(false));
  };
  const openFinderCatalog = () => { setFinderScreen("catalog"); loadFinderCatalog(); };
  // Prix — POST /items/:id/price (distinct de /price/verify, qui ne fait que confirmer le
  // prix déjà en place). Le serveur bloque déjà lui-même la modification d'un prix validé
  // pour un non-responsable (403) — le bouton n'est de toute façon proposé qu'aux comptes
  // hasValidateTier ici, jamais un vrai garde-fou dupliqué côté client.
  const startEditFinderPrice = (item) => {
    setFinderPriceDraft(Number.isFinite(item.price?.amount) ? String(item.price.amount) : "");
    setFinderEditingPrice(true);
  };
  const saveFinderPrice = (id) => {
    const amount = Number(String(finderPriceDraft).replace(",", "."));
    if (!Number.isFinite(amount) || amount < 0) { showToast(t("finder.priceInvalid", "Montant invalide.")); return; }
    apiJSON(`/api/article-finder/items/${id}/price`, { method: "POST", body: JSON.stringify({ amount }) })
      .then((d) => {
        setFinderItems((items) => items.map((i) => i.id === id ? d.item : i));
        setFinderCatalog((items) => items.map((i) => i.id === id ? d.item : i));
        setFinderEditingPrice(false);
        showToast(t("finder.priceUpdated", "✓ Prix mis à jour"));
      })
      .catch((err) => showToast(err.message || t("err.priceUpdateImpossible", "Mise à jour du prix impossible.")));
  };
  // Nouvelle photo sur une fiche déjà existante — même route que la création (POST
  // /items/:id/photos, jusqu'à 5 par fiche), utile pour actualiser une photo obsolète.
  const addFinderPhoto = (id, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("photos", file);
    apiJSON(`/api/article-finder/items/${id}/photos`, { method: "POST", body: formData })
      .then((d) => {
        setFinderItems((items) => items.map((i) => i.id === id ? d.item : i));
        setFinderCatalog((items) => items.map((i) => i.id === id ? d.item : i));
        showToast(t("finder.photoAdded", "✓ Photo ajoutée"));
      })
      .catch((err) => showToast(err.message || t("err.photoUploadImpossible", "Envoi de la photo impossible.")));
  };
  const verifyPriceFinderItem = (id) => {
    apiJSON(`/api/article-finder/items/${id}/price/verify`, { method: "POST" })
      .then((d) => { setFinderItems((items) => items.map((i) => i.id === id ? d.item : i)); showToast(t("toast.priceConfirmedUpToDate", "✓ Prix confirmé à jour")); })
      .catch((err) => showToast(err.message || t("err.confirmationImpossible", "Confirmation impossible.")));
  };
  // Suppression DÉFINITIVE — retour utilisateur explicite : "je veux pouvoir supprimer
  // completement et non seulement archiver une fiche". Distincte d'Archiver (qui garde la
  // fiche, restaurable) : ici, la fiche + ses photos + ses signalements + son historique de
  // prix sont effacés côté serveur (DELETE /items/:id), irréversible — confirmation requise.
  const deleteFinderItem = (id) => {
    if (!window.confirm(t("confirm.deleteFinderItem", "Supprimer définitivement cette fiche ? Cette action est irréversible."))) return;
    apiJSON(`/api/article-finder/items/${id}`, { method: "DELETE" })
      .then(() => {
        setFinderItems((items) => items.filter((i) => i.id !== id));
        setFinderCatalog((items) => items.filter((i) => i.id !== id));
        setFinderAdminItems((items) => items.filter((i) => i.id !== id));
        // Retour utilisateur explicite : "quand je supprime une fiche que je revienne dans
        // la liste du catalogue et non a l'accueil du module" — cohérent avec le fait que la
        // fiche est atteinte le plus souvent depuis le Catalogue désormais (plus de fiches en
        // attente à valider séparément).
        setFinderScreen("catalog");
        loadFinderCatalog();
        showToast(t("toast.cardDeleted", "🗑️ Fiche supprimée"));
      })
      .catch((err) => showToast(err.message || t("err.deletionImpossible", "Suppression impossible.")));
  };
  const restoreFinderItem = (id) => {
    apiJSON(`/api/article-finder/items/${id}/restore`, { method: "POST" })
      .then((d) => setFinderItems((items) => items.some((i) => i.id === d.item.id) ? items.map((i) => i.id === d.item.id ? d.item : i) : [...items, d.item]))
      .then(() => showToast(t("toast.cardRestored", "↩︎ Fiche restaurée")))
      .catch((err) => showToast(err.message || t("err.restoreImpossible", "Restauration impossible.")));
  };
  // Le serveur EXIGE désignation + catégorie + prix + au moins une photo pour valider
  // (server/articleFinder/routes.js, POST /items/:id/validate) — nexora-app n'a aucun
  // écran de création/upload de fiche pour l'instant (même limite que la capture caméra
  // du Scanner), donc toute fiche existante sans photo renverra une vraie erreur 400 ici.
  const validateFinderItem = (id) => {
    apiJSON(`/api/article-finder/items/${id}/validate`, { method: "POST" })
      .then(() => { showToast(t("toast.cardValidated", "✓ Fiche validée")); loadFinderAdminItems(); })
      .catch((err) => showToast(err.message || t("err.validationRefused", "Validation refusée.")));
  };
  const rejectFinderItem = (id) => {
    apiJSON(`/api/article-finder/items/${id}/reject`, { method: "POST", body: JSON.stringify({ reason: "" }) })
      .then(() => { showToast(t("toast.cardRejected", "Fiche rejetée")); loadFinderAdminItems(); })
      .catch((err) => showToast(err.message || t("err.rejectionImpossible", "Rejet impossible.")));
  };

  /* Lieferschein — import multi-méthodes, vérification avant création,
     signalement direct d'un article vers Réclamations. */
  const currentLfList = () => lieferscheine.filter((n) => lfTab === "trash" ? n.trashed : !n.trashed && (lfTab === "archived" ? n.archived : !n.archived));
  const startLfImport = (method) => { setLfImportMethod(method); setLfScreen("import"); };
  // Saisie manuelle : formulaire vide comme avant, aucun fichier — pas de véritable
  // "extraction", légitime en local. PDF/photos : vrai POST /api/lieferschein/import
  // (extraction vision Claude côté serveur, JUSQU'À PLUSIEURS MINUTES sur un bon dense —
  // requête HTTP simple qui attend la fin, pas de polling, voir recherche Phase 1).
  const analyzeLfImport = (fileList) => {
    if (lfImportMethod === "manual") {
      setLfPreview({
        supplier: "", deliveryDate: todayISO(), deliveryNumber: "",
        items: [{ id: "n" + Date.now(), artNr: "", name: "", qty: "", ve: "", needsReview: false, catalogMatch: true }],
      });
      setLfScreen("preview");
      return;
    }
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setLfAnalyzing(true);
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    apiJSON("/api/lieferschein/import", { method: "POST", body: formData })
      .then((data) => {
        const info = data.extraction?.documentInfo || {};
        const lines = data.extraction?.lines || [];
        setLfPreview({
          supplier: info.supplier || "", deliveryDate: info.deliveryDate || todayISO(), deliveryNumber: info.deliveryNumber || "",
          items: lines.map((l, idx) => ({
            id: "n" + idx + Date.now(), artNr: l.artNr || "", name: l.name || "", qty: l.expectedQty || "", ve: l.ve || "",
            needsReview: !!l.needsReview, catalogMatch: !!l.catalogMatch, reviewReason: l.reviewReason || "",
          })),
        });
        setLfScreen("preview");
      })
      .catch((err) => showToast(err.message || t("err.extractionImpossible", "Extraction impossible.")))
      .finally(() => setLfAnalyzing(false));
  };
  const updateLfPreviewField = (field, value) => setLfPreview((p) => ({ ...p, [field]: value }));
  const updateLfPreviewItem = (id, field, value) => setLfPreview((p) => ({ ...p, items: p.items.map((it) => it.id === id ? { ...it, [field]: value } : it) }));
  const markLfLineVerified = (id) => setLfPreview((p) => ({ ...p, items: p.items.map((it) => it.id === id ? { ...it, needsReview: false } : it) }));
  const addLfPreviewLine = () => setLfPreview((p) => ({ ...p, items: [...p.items, { id: "n" + Date.now(), artNr: "", name: "", qty: "", ve: "", needsReview: false, catalogMatch: true }] }));
  const removeLfPreviewLine = (id) => setLfPreview((p) => ({ ...p, items: p.items.filter((it) => it.id !== id) }));
  const createLieferschein = () => {
    if (!lfPreview.supplier.trim()) { showToast(t("toast.indicateSupplier", "Indique le fournisseur.")); return; }
    if (lfPreview.items.length === 0) { showToast(t("toast.addAtLeastOneArticle", "Ajoute au moins un article.")); return; }
    apiJSON("/api/lieferschein", {
      method: "POST",
      body: JSON.stringify({
        supplier: lfPreview.supplier.trim(), deliveryDate: lfPreview.deliveryDate || todayISO(),
        number: lfPreview.deliveryNumber.trim(), source: lfImportMethod === "manual" ? "manual" : "ai_import",
        items: lfPreview.items.map((it) => ({ artNr: it.artNr, name: it.name, expectedQty: it.qty, ve: it.ve, needsReview: it.needsReview, catalogMatch: it.catalogMatch, reviewReason: it.reviewReason })),
      }),
    })
      .then(() => loadLieferscheine())
      .catch((err) => showToast(err.message || t("err.creationImpossible", "Création impossible.")));
    setLfPreview(emptyLfPreview); setLfImportMethod(null);
    setLfScreen("hub"); setLfTab("active");
    showToast(t("toast.deliveryNoteCreated", "✓ Bon de livraison créé"));
  };
  /* Import PDF MHD — analyse (vision Claude côté serveur, peut prendre du temps sur un
     document dense) puis preview éditable, même patron que Lieferschein ci-dessus. */
  const analyzeMhdImport = (fileList) => {
    const file = (fileList || [])[0];
    if (!file) return;
    setMhdImportAnalyzing(true);
    const formData = new FormData();
    formData.append("file", file);
    apiJSON("/api/mhd-import/analyze", { method: "POST", body: formData })
      .then((data) => {
        setMhdImportPreview({
          rows: (data.rows || []).map((r) => ({ ...r, verified: !r.needsReview })),
          excluded: data.excluded || [],
          skippedPast: data.skippedPast || [],
          skippedExisting: data.skippedExisting || [],
          newArticles: data.newArticles || [],
          veUpdates: data.veUpdates || [],
          veUpdatesChecked: Object.fromEntries((data.veUpdates || []).map((u) => [u.artNr, true])),
          fileName: file.name || "",
        });
        setMhdImportStep("preview");
      })
      .catch((err) => showToast(err.message || t("err.extractionImpossible", "Extraction impossible.")))
      .finally(() => setMhdImportAnalyzing(false));
  };
  const updateMhdImportRow = (id, field, value) => setMhdImportPreview((p) => ({ ...p, rows: p.rows.map((r) => r.id === id ? { ...r, [field]: value } : r) }));
  const markMhdImportRowVerified = (id) => setMhdImportPreview((p) => ({ ...p, rows: p.rows.map((r) => r.id === id ? { ...r, verified: true } : r) }));
  const removeMhdImportRow = (id) => setMhdImportPreview((p) => ({ ...p, rows: p.rows.filter((r) => r.id !== id) }));
  const toggleMhdImportVe = (artNr) => setMhdImportPreview((p) => ({ ...p, veUpdatesChecked: { ...p.veUpdatesChecked, [artNr]: !p.veUpdatesChecked[artNr] } }));
  const mhdImportHasUnresolved = mhdImportPreview.rows.some((r) => r.needsReview && !r.verified);
  const resetMhdImport = () => { setMhdImportPreview(emptyMhdImportPreview); setMhdImportStep("upload"); };
  const commitMhdImport = () => {
    if (mhdImportHasUnresolved) { showToast(t("mhdImport.toast.resolveAllFirst", "Vérifie d'abord toutes les lignes marquées ⚠️.")); return; }
    if (!mhdImportPreview.rows.length) { showToast(t("mhdImport.toast.noRows", "Aucune ligne à importer.")); return; }
    setMhdImportCommitting(true);
    apiJSON("/api/mhd-import/commit", {
      method: "POST",
      body: JSON.stringify({
        fileName: mhdImportPreview.fileName,
        rows: mhdImportPreview.rows,
        veUpdates: mhdImportPreview.veUpdates.filter((u) => mhdImportPreview.veUpdatesChecked[u.artNr]),
      }),
    })
      .then((data) => {
        resetMhdImport();
        setScreen("kuhlung");
        loadKuhlungRecords();
        loadArticles();
        loadNewArticles();
        showToast(t("mhdImport.toast.importedTemplate", { n: data.inserted }, `✓ ${data.inserted} enregistrement(s) MHD importé(s)`));
      })
      .catch((err) => showToast(err.message || t("mhdImport.toast.importImpossible", "Import impossible.")))
      .finally(() => setMhdImportCommitting(false));
  };
  /* Import catalogue Articles — même patron que l'import PDF MHD ci-dessus. */
  const analyzeArtImport = (fileList) => {
    const file = (fileList || [])[0];
    if (!file) return;
    setArtImportAnalyzing(true);
    const formData = new FormData();
    formData.append("file", file);
    apiJSON("/api/articles-import/analyze", { method: "POST", body: formData })
      .then((data) => {
        setArtImportPreview({
          rows: data.rows || [],
          skippedExisting: data.skippedExisting || [],
          fileName: file.name || "",
        });
        setArtImportStep("preview");
      })
      .catch((err) => showToast(err.message || t("err.extractionImpossible", "Extraction impossible.")))
      .finally(() => setArtImportAnalyzing(false));
  };
  const updateArtImportRow = (id, field, value) => setArtImportPreview((p) => ({ ...p, rows: p.rows.map((r) => r.id === id ? { ...r, [field]: value } : r) }));
  const removeArtImportRow = (id) => setArtImportPreview((p) => ({ ...p, rows: p.rows.filter((r) => r.id !== id) }));
  const resetArtImport = () => { setArtImportPreview(emptyArtImportPreview); setArtImportStep("upload"); };
  const commitArtImport = () => {
    if (!artImportPreview.rows.length) { showToast(t("artImport.toast.noRows", "Aucune ligne à importer.")); return; }
    setArtImportCommitting(true);
    apiJSON("/api/articles-import/commit", {
      method: "POST",
      body: JSON.stringify({ fileName: artImportPreview.fileName, rows: artImportPreview.rows }),
    })
      .then((data) => {
        resetArtImport();
        setArtScreen("hub");
        refreshArticleDependentLists();
        showToast(t("artImport.toast.importedTemplate", { n: data.inserted }, `✓ ${data.inserted} article(s) importé(s)`));
      })
      .catch((err) => showToast(err.message || t("artImport.toast.importImpossible", "Import impossible.")))
      .finally(() => setArtImportCommitting(false));
  };
  const openLfDetail = (id) => { setLfDetailId(id); setLfScreen("detail"); };
  const archiveLf = (id) => {
    setLieferscheine((ns) => ns.map((n) => n.id === id ? { ...n, archived: true } : n));
    apiJSON(`/api/lieferschein/${id}/archive`, { method: "PATCH", body: JSON.stringify({ archived: true }) }).catch((err) => { showToast(err.message || t("err.actionRefused", "Action refusée.")); loadLieferscheine(); });
    setLfScreen("hub"); showToast(t("toast.archivedM", "📦 Archivé"));
  };
  const restoreLf = (id) => {
    setLieferscheine((ns) => ns.map((n) => n.id === id ? { ...n, trashed: false, archived: false } : n));
    apiJSON(`/api/lieferschein/${id}/restore`, { method: "PATCH" }).catch((err) => { showToast(err.message || t("err.restoreRefused", "Restauration refusée.")); loadLieferscheine(); });
    showToast(t("toast.restoredM", "↩︎ Restauré"));
  };
  const trashLf = (id) => {
    setLieferscheine((ns) => ns.map((n) => n.id === id ? { ...n, trashed: true } : n));
    apiJSON(`/api/lieferschein/${id}`, { method: "DELETE" }).catch((err) => { showToast(err.message || t("err.actionRefused", "Action refusée.")); loadLieferscheine(); });
    setLfScreen("hub"); showToast(t("toast.movedToTrashM", "Déplacé vers la corbeille"));
  };
  const deleteLfForever = (id) => {
    setLieferscheine((ns) => ns.filter((n) => n.id !== id));
    apiJSON(`/api/lieferschein/${id}/permanent`, { method: "DELETE" }).catch((err) => { showToast(err.message || t("toast.deletionRefused", "Suppression refusée.")); loadLieferscheine(); });
    showToast(t("toast.permanentlyDeletedM", "Supprimé définitivement"));
  };
  const reportLfItemToClaims = (item) => {
    setDraftClaimType("food");
    setDraftClaimItems((its) => [...its, {
      id: "i" + Date.now(), artNr: item.artNr || "", name: item.name, qtyType: "pieces", qty: item.qty || "1",
      chargeLossNr: "", defect: "Erreur de livraison", note: "Signalé depuis Lieferschein", photos: [],
    }]);
    setScreen("claims"); setClaimsScreen("new");
    showToast(t("toast.itemAddedToNewClaimTemplate", { name: item.name }, `✓ « ${item.name} » ajouté à une nouvelle réclamation`));
  };
  const setLfItemStatus = (noteId, itemId, status) => {
    setLieferscheine((ns) => ns.map((n) => n.id !== noteId ? n : {
      ...n, items: n.items.map((it) => it.id === itemId ? { ...it, status, needsReview: status !== "complete" } : it),
    }));
    apiJSON(`/api/lieferschein/${noteId}/items/${itemId}`, { method: "PATCH", body: JSON.stringify({ status }) })
      .catch((err) => { showToast(err.message || t("err.actionRefused", "Action refusée.")); loadLieferscheine(); });
    setLfStatusTarget(null);
    showToast(`${LF_ITEM_STATUS[status].emoji} ${t(`lieferschein.itemStatus.${LF_STATUS_KEY_MAP[status]}`, LF_ITEM_STATUS[status].label)}`);
  };
  const completeLfControl = (note) => {
    const pending = note.items.filter((i) => i.status === "pending" || !i.status).length;
    const breakdown = Object.entries(LF_ITEM_STATUS).filter(([k]) => k !== "pending")
      .map(([k, meta]) => { const n = note.items.filter((i) => i.status === k).length; return n ? `${meta.emoji} ${t(`lieferschein.itemStatus.${LF_STATUS_KEY_MAP[k]}`, meta.label)} : ${n}` : null; })
      .filter(Boolean).join("\n");
    const done = note.items.length - pending;
    if (!window.confirm(t("confirm.controlDoneSummaryTemplate", { done, total: note.items.length, breakdown }, `Articles contrôlés : ${done}/${note.items.length}\n${breakdown}\n\nTerminer le contrôle ?`))) return;
    apiJSON(`/api/lieferschein/${note.id}/status`, { method: "PATCH", body: JSON.stringify({ status: "completed" }) })
      .then(() => loadLieferscheine())
      .catch((err) => showToast(err.message || t("err.actionRefusedManagersOnly", "Action refusée (réservée aux responsables ?).")));
    showToast(t("toast.controlFinished", "✓ Contrôle terminé"));
  };
  const generateLfClaim = (note) => {
    const anomalyItems = note.items.filter((i) => i.status && !["pending", "complete"].includes(i.status));
    if (anomalyItems.length === 0) { showToast(t("toast.noConfirmedAnomalyToReport", "Aucune anomalie confirmée à signaler.")); return; }
    if (!window.confirm(t("confirm.generateClaimFromAnomaliesTemplate", { n: anomalyItems.length }, `Générer une réclamation à partir de ${anomalyItems.length} ligne(s) en anomalie ?`))) return;
    setDraftClaimType("food");
    setDraftClaimItems(anomalyItems.map((item) => ({
      id: "i" + Date.now() + Math.random(), artNr: item.artNr || "", name: item.name, qtyType: "pieces", qty: item.qty || "1",
      chargeLossNr: "", defect: (item.status && t(`lieferschein.itemStatus.${LF_STATUS_KEY_MAP[item.status]}`, LF_ITEM_STATUS[item.status]?.label)) || t("claims.defectDeliveryErrorFallback", "Erreur de livraison"), note: `Signalé depuis Lieferschein ${note.deliveryNumber}`, photos: [],
    })));
    setScreen("claims"); setClaimsScreen("new");
    showToast(t("toast.claimPreparedTemplate", { n: anomalyItems.length }, `✓ Réclamation préparée (${anomalyItems.length} article${anomalyItems.length > 1 ? "s" : ""})`));
  };



  // Un FL/FLA se voit auto-approuvé à la création (isSelfApproving côté serveur) — jamais
  // de "pending" pour son propre compte. Freiwunsch n'a pas de plage de dates réelle
  // (week+requestedDay) : une seule date choisie ici, le lundi de sa semaine + le jour
  // (mon..sat) en sont dérivés — un dimanche est invalide (filiale fermée).
  const weekAndDayForDate = (dateISO) => {
    const d = new Date(dateISO + "T00:00:00");
    const dow = d.getDay();
    if (dow === 0) return null;
    const week = addDaysISO(dateISO, 1 - dow);
    return { week, requestedDay: PLAN_DAY_KEYS[dow - 1] };
  };
  const mapServerRequest = (r) => ({ ...r, emp: r.employeeName ? r.employeeName.split(" ")[0] : resolveEmployeeName(r.employeeId) });
  const loadPlanRequests = () => {
    apiJSON(`/api/planning-requests?storeId=${encodeURIComponent(user?.store)}`)
      .then((data) => setPlanRequests((data.requests || []).map(mapServerRequest)))
      .catch(() => {});
  };
  const submitRequest = () => {
    if (!reqForm.date) { showToast(t("toast.chooseADate", "Choisis une date.")); return; }
    const payload = { type: reqForm.type, comment: reqForm.comment.trim() };
    if (reqForm.type === "urlaub") {
      const endDate = reqForm.endDate || reqForm.date;
      if (endDate < reqForm.date) { showToast(t("toast.endMustBeAfterStart", "La fin doit être après le début.")); return; }
      payload.startDate = reqForm.date; payload.endDate = endDate;
    } else {
      const wd = weekAndDayForDate(reqForm.date);
      if (!wd) { showToast(t("toast.storeClosedSundayChooseOtherDay", "La filiale est fermée le dimanche — choisis un autre jour.")); return; }
      payload.week = wd.week; payload.requestedDay = wd.requestedDay;
    }
    apiJSON("/api/planning-requests", { method: "POST", body: JSON.stringify(payload) })
      .then((data) => {
        setPlanRequests((rs) => [mapServerRequest(data.request), ...rs]);
        setSheet(null); setReqForm({ type: "freiwunsch", date: "", endDate: "", comment: "" });
        showToast(data.selfApproved ? "✓ Planifié directement (FL/FLA)" : (reqForm.type === "urlaub" ? "🏖️ Demande de congés envoyée" : "🌿 Freiwunsch envoyé"));
      })
      .catch((err) => showToast(err.message || t("err.sendingImpossible", "Envoi impossible.")));
  };
  const approveRequest = (id) => {
    apiJSON(`/api/planning-requests/${id}/approve`, { method: "POST" })
      .then((data) => { setPlanRequests((rs) => rs.map((r) => r.id === id ? mapServerRequest(data.request) : r)); showToast(t("toast.requestApproved", "✓ Demande approuvée")); })
      .catch((err) => showToast(err.message || t("err.approvalImpossible", "Approbation impossible.")));
  };
  const rejectRequest = (id) => {
    apiJSON(`/api/planning-requests/${id}/reject`, { method: "POST" })
      .then((data) => { setPlanRequests((rs) => rs.map((r) => r.id === id ? mapServerRequest(data.request) : r)); showToast(t("toast.requestRejected", "Demande refusée")); })
      .catch((err) => showToast(err.message || t("err.refusalImpossible", "Refus impossible.")));
  };
  const cancelRequest = (id) => {
    apiJSON(`/api/planning-requests/${id}/cancel`, { method: "POST" })
      .then((data) => { setPlanRequests((rs) => rs.map((r) => r.id === id ? mapServerRequest(data.request) : r)); showToast(t("toast.requestCancelled", "Demande annulée")); })
      .catch((err) => showToast(err.message || t("err.cancellationImpossible", "Annulation impossible.")));
  };
  // Suppression d'un congé Urlaub déjà approuvé/posé, depuis le Calendrier annuel —
  // réservé à FL/FLA (retour utilisateur 2026-08-04). scope=full retire aussi les jours
  // Urlaub du plan de travail ET restitue le solde Resturlaub côté serveur
  // (sfRevertRequestFromPlanning), donc jamais besoin de recalculer le solde ici : un
  // simple rechargement des comptes suffit à refléter le crédit rendu partout.
  const deleteApprovedVacation = (r) => {
    if (!window.confirm(t("planning.calendar.deleteConfirmTemplate", { emp: r.emp, dates: reqDateLabel(r) }, `Supprimer le congé de ${r.emp} (${reqDateLabel(r)}) ? Les jours seront retirés du plan de travail et son solde Resturlaub sera crédité en retour.`))) return;
    apiJSON(`/api/planning-requests/${r.id}?scope=full`, { method: "DELETE" })
      .then(() => {
        setPlanRequests((rs) => rs.map((x) => x.id === r.id ? { ...x, deleted: true } : x));
        loadVacationAccounts();
        if (String(r.employeeId) === String(user?.proId || user?.id)) loadMyProfile();
        showToast(t("planning.calendar.deleteSuccess", "🏖️ Congé supprimé — solde Resturlaub restitué"));
      })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };
  const reqPending = planRequests.filter((r) => r.status === "pending" && !r.deleted);
  const reqDateLabelFor = (type, startDate, endDate, week, requestedDay) => type === "urlaub"
    ? (startDate === endDate ? fmtFR(startDate) : `${fmtFR(startDate)} → ${fmtFR(endDate)}`)
    : `${weekLabel(PLAN_DAY_KEYS.indexOf(requestedDay)) || requestedDay} · semaine du ${fmtFR(week)}`;
  const reqDateLabel = (r) => reqDateLabelFor(r.type, r.startDate, r.endDate, r.week, r.requestedDay);
  const REQ_STATUS_META = {
    pending: { label: t("planning.requests.status.pending", "En attente"), bg: "rgba(231,138,18,.16)", fg: "#b56a00" },
    approved: { label: t("planning.requests.status.approved", "Approuvé"), bg: A.green, fg: "#fff" },
    rejected: { label: t("planning.requests.status.rejected", "Refusé"), bg: A.red, fg: "#fff" },
    discussion: { label: t("planning.requests.status.discussion", "En discussion"), bg: A.blue, fg: "#fff" },
    cancelled: { label: t("planning.requests.status.cancelled", "Annulé"), bg: P.soft, fg: P.muted },
  };

  /* Discussion liée à une demande — messages réels persistés sur la demande elle-même. */
  const openReqThread = (id) => { setActiveReqId(id); setReqMsgDraft(""); setReqProposeForm(null); setSheet("reqThread"); };
  const sendReqMessage = () => {
    const text = reqMsgDraft.trim();
    if (!text || !activeReqId) return;
    apiJSON(`/api/planning-requests/${activeReqId}/reply`, { method: "POST", body: JSON.stringify({ text }) })
      .then((data) => { setPlanRequests((rs) => rs.map((r) => r.id === activeReqId ? mapServerRequest(data.request) : r)); setReqMsgDraft(""); })
      .catch((err) => showToast(err.message || t("err.sendingImpossible", "Envoi impossible.")));
  };
  // Contre-proposition de date : l'employé propose une nouvelle date après discussion —
  // écrase startDate/endDate (Urlaub) ou week/requestedDay (Freiwunsch) côté serveur ; le
  // bouton Approuver déjà existant applique ensuite cette date la plus récente au planning.
  const submitProposeDate = () => {
    const req = planRequests.find((r) => r.id === activeReqId);
    const date = reqProposeForm?.date;
    if (!req || !date) { showToast(t("toast.chooseADate", "Choisis une date.")); return; }
    let payload;
    if (req.type === "urlaub") {
      const endDate = reqProposeForm.endDate || date;
      if (endDate < date) { showToast(t("toast.endMustBeAfterStart", "La fin doit être après le début.")); return; }
      payload = { startDate: date, endDate };
    } else {
      const wd = weekAndDayForDate(date);
      if (!wd) { showToast(t("toast.storeClosedSundayChooseOtherDay", "La filiale est fermée le dimanche — choisis un autre jour.")); return; }
      payload = wd;
    }
    apiJSON(`/api/planning-requests/${req.id}/propose-date`, { method: "POST", body: JSON.stringify(payload) })
      .then((data) => { setPlanRequests((rs) => rs.map((r) => r.id === req.id ? mapServerRequest(data.request) : r)); setReqProposeForm(null); showToast(t("toast.newDateProposed", "📅 Nouvelle date proposée")); })
      .catch((err) => showToast(err.message || t("err.sendingImpossible", "Envoi impossible.")));
  };

  /* Resturlaub / Leistungsfrei — vrais comptes serveur. Resturlaub n'a AUCUN découpage par
     année côté serveur (juste creditedDays/usedDays cumulés) — contrairement à l'ancienne UI
     démo qui affichait des lignes par année avec report, jamais réel côté serveur. */
  const loadVacationAccounts = () => {
    apiJSON(`/api/vacation/accounts?storeId=${encodeURIComponent(user?.store)}`)
      .then((data) => setVacationAccounts(data.accounts || []))
      .catch(() => setVacationAccounts([]));
  };
  const loadLfAccounts = () => {
    apiJSON("/api/leistungsfrei/accounts")
      .then((data) => setLfAccounts(data.accounts || []))
      .catch(() => setLfAccounts([]));
  };
  const vacationAccountFor = (employeeId) => vacationAccounts.find((a) => a.employeeId === employeeId);
  const vacationDaysLeft = (employeeId) => {
    const a = vacationAccountFor(employeeId);
    return a ? Math.max(Math.round((a.creditedDays - a.usedDays) * 100) / 100, 0) : 0;
  };
  const lfAccountFor = (employeeId) => lfAccounts.find((a) => a.employeeId === employeeId);
  const lfBalance = (employeeId) => lfAccountFor(employeeId)?.availableHours || 0;

  // Libellé + signe lisibles pour une transaction réelle — les types serveur (reservation/
  // release/usage/cancellation côté Leistungsfrei) n'ont pas d'équivalent 1:1 dans l'ancienne
  // UI démo (juste credit/debit) ; "usage" est neutre (une réservation déjà comptée qui
  // bascule en consommée à la publication, le solde disponible ne bouge pas ce jour-là).
  const txMeta = (kind, t) => {
    if (kind === "urlaub") {
      const labels = { credit: "Attribution", restore: "Jours rétablis", usage: "Congé pris", correction: "Correction" };
      return { label: labels[t.type] || t.type, sign: t.type === "usage" ? "−" : "+", amount: t.days };
    }
    const labels = { credit: "Crédit manuel", reservation: "Réservé (créneau)", release: "Libéré", usage: "Consommé (plan publié)", cancellation: "Annulé", correction: "Correction manuelle" };
    let sign = "+";
    if (t.type === "reservation") sign = "−";
    else if (t.type === "usage") sign = "•";
    else if (t.type === "correction") sign = t.direction === "debit" ? "−" : "+";
    return { label: labels[t.type] || t.type, sign, amount: t.hours };
  };
  const openLedgerHistory = (kind, employeeId, emp) => {
    const defaultId = employeeId || resolveEmployeeId(planTeam[0]);
    const defaultName = emp || planTeam[0];
    setHistoryKind(kind); setHistoryEmpId(defaultId); setHistoryEmpName(defaultName);
    setSheet("ledgerHistory");
    if (kind === "urlaub") {
      apiJSON("/api/vacation/transactions").then((data) => setVacationTransactions(data.transactions || [])).catch(() => setVacationTransactions([]));
    } else {
      apiJSON("/api/leistungsfrei/transactions").then((data) => setLfTransactions(data.transactions || [])).catch(() => setLfTransactions([]));
    }
  };
  const saveAllocation = () => {
    if (!allocForm || !allocForm.amount || allocForm.amount <= 0) { showToast(t("toast.indicateNumberOfDays", "Indique un nombre de jours.")); return; }
    const path = allocForm.mode === "restore" ? "restore" : "credit";
    apiJSON(`/api/vacation/accounts/${allocForm.employeeId}/${path}`, {
      method: "POST",
      body: JSON.stringify({ days: allocForm.amount, comment: allocForm.mode === "restore" ? "Jours non pris rétablis" : "Attribution manuelle" }),
    })
      .then((data) => {
        setVacationAccounts((accs) => accs.some((a) => a.employeeId === data.account.employeeId) ? accs.map((a) => a.employeeId === data.account.employeeId ? data.account : a) : [...accs, data.account]);
        setSheet(null); setAllocForm(null);
        showToast(allocForm.mode === "restore" ? `🏖️ ${allocForm.amount} j rétablis pour ${allocForm.emp}` : `🏖️ ${allocForm.amount} j attribués à ${allocForm.emp}`);
      })
      .catch((err) => showToast(err.message || t("err.operationImpossible", "Opération impossible.")));
  };
  const saveLeiEntry = () => {
    if (!leiForm || !leiForm.amount || leiForm.amount <= 0) { showToast(t("toast.indicateNumberOfHours", "Indique un nombre d'heures.")); return; }
    const isCredit = leiForm.type === "credit";
    const body = isCredit
      ? { employeeId: leiForm.employeeId, hours: leiForm.amount, comment: leiForm.note || "Crédit manuel" }
      : { employeeId: leiForm.employeeId, hours: leiForm.amount, direction: "debit", comment: leiForm.note || "Utilisé" };
    apiJSON(`/api/leistungsfrei/${isCredit ? "credit" : "correct"}`, { method: "POST", body: JSON.stringify(body) })
      .then((data) => {
        setLfAccounts((accs) => accs.some((a) => a.employeeId === data.account.employeeId) ? accs.map((a) => a.employeeId === data.account.employeeId ? data.account : a) : [...accs, data.account]);
        setSheet(null); setLeiForm(null);
        showToast(isCredit ? `⏱️ +${leiForm.amount} h créditées à ${leiForm.emp}` : `⏱️ −${leiForm.amount} h retirées à ${leiForm.emp}`);
      })
      .catch((err) => showToast(err.message || t("err.operationImpossible", "Opération impossible.")));
  };
  const deleteLedgerEntry = (id, kind) => {
    apiJSON(kind === "urlaub" ? `/api/vacation/transactions/${id}` : `/api/leistungsfrei/transactions/${id}`, { method: "DELETE" })
      .then((data) => {
        if (kind === "urlaub") {
          setVacationTransactions((ts) => ts.filter((t) => t.id !== id));
          setVacationAccounts((accs) => accs.map((a) => a.employeeId === data.account.employeeId ? data.account : a));
        } else {
          setLfTransactions((ts) => ts.filter((t) => t.id !== id));
          setLfAccounts((accs) => accs.map((a) => a.employeeId === data.account.employeeId ? data.account : a));
        }
        showToast(t("toast.entryRemovedFromHistory", "Écriture supprimée de l'historique"));
      })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };

  /* Feiertage — vrai référentiel serveur, déjà résolu pour la filiale courante. */
  const activeHolidays = useMemo(() => (
    publicHolidays.filter((h) => h.date >= todayISO()).sort((a, b) => a.date.localeCompare(b.date))
  ), [publicHolidays]);
  const saveCustomHoliday = () => {
    if (!holidayForm.date || !holidayForm.name.trim()) { showToast(t("toast.indicateDateAndName", "Indique une date et un nom.")); return; }
    const holiday = { date: holidayForm.date, label: holidayForm.name.trim(), storeId: user?.store };
    setSheet(null); setHolidayForm({ date: "", name: "" });
    apiJSON("/api/public-holidays", { method: "POST", body: JSON.stringify(holiday) })
      .then(() => { loadPublicHolidays(); showToast(t("toast.holidayAdded", "🎌 Jour férié ajouté")); })
      .catch((err) => showToast(err.message || t("err.additionImpossible", "Ajout impossible.")));
  };
  const deleteCustomHoliday = (date) => {
    apiJSON(`/api/public-holidays/${encodeURIComponent(date)}?storeId=${encodeURIComponent(user?.store)}`, { method: "DELETE" })
      .then(() => { loadPublicHolidays(); showToast(t("toast.holidayRemoved", "Jour férié retiré")); })
      .catch((err) => showToast(err.message || t("tasks.deletionImpossible", "Suppression impossible.")));
  };

  // Les 3 clés i18n historiques restent la source des 3 seeds (traductions déjà faites
  // dans les 5 langues) — toute règle ajoutée depuis l'admin retombe sur son propre
  // `label` stocké (2026-08-17, ruleDefinitions désormais gérable, voir plus haut).
  const reductionRuleLabel = (rule) => ({
    J2_OPEN: t("reductions.ruleLabelJ2Open", "J-2 · jours ouvrés"),
    J3_OPEN: t("reductions.ruleLabelJ3Open", "J-3 · jours ouvrés"),
    J30_CALENDAR: t("reductions.ruleLabelJ30Calendar", "J-30 · jours calendaires")
  }[rule] || ruleDefinitions.find((d) => d.id === rule)?.label || rule);

  // rule = "AUTO" retire la dérogation (retour à la règle par défaut de la source) ;
  // sinon une des 3 vraies règles (J2_OPEN/J3_OPEN/J30_CALENDAR). Persisté côté serveur —
  // partagé avec reductions.js vanilla et les alertes d'accueil, jamais un état local isolé.
  // knownName : nom déjà connu de l'appelant (ex. article promotionnel Aktion, jamais dans
  // `articles` le catalogue officiel) — évite d'afficher/enregistrer l'Art.Nr brut en guise
  // de nom quand la recherche dans le catalogue échoue légitimement.
  // ean : repli quand l'article n'a pas d'Art.Nr (Magasin en saisie libre — retour
  // utilisateur explicite 2026-08-21, "un article créé sans règle définie doit pouvoir en
  // recevoir une depuis sa carte"). key = clé effective envoyée au serveur, même logique
  // que sfReductionOverrideKey côté serveur (Art.Nr en priorité, EAN sinon).
  const setArticleRule = (artNr, rule, knownName, ean) => {
    const key = artNr || ean;
    const name = knownName || articles.find((a) => a.artNr === artNr)?.name || key;
    const isAuto = rule === "AUTO";
    const req = isAuto
      ? apiJSON(`/api/reductions/rule-overrides/${encodeURIComponent(key)}`, { method: "DELETE" })
      : apiJSON("/api/reductions/rule-overrides", { method: "POST", body: JSON.stringify({ artNr, ean, name, rule }) });
    req.then(() => {
      setRuleOverrides((m) => {
        const c = { ...m };
        if (isAuto) delete c[key]; else c[key] = rule;
        return c;
      });
      showToast(isAuto
        ? t("toast.discountRuleReset", { name }, `⚙️ ${name} → règle automatique`)
        : t("toast.discountRuleSet", { name, rule: reductionRuleLabel(rule) }, `⚙️ ${name} → ${reductionRuleLabel(rule)}`));
    }).catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  // returnTo : sheet à ré-ouvrir en refermant quickRule — `sheet` est un état EXCLUSIF (pas
  // une pile), donc ouvrir "quickRule" depuis DANS une autre sheet (ex. "storeHistory")
  // l'écrasait sans jamais y revenir, refermant celle-ci par la même occasion (retour
  // utilisateur explicite 2026-08-21 : "pas que l'écran historique disparaisse... sauf si je
  // ferme le popup moi-même"). undefined quand ouvert depuis un écran plein (pas une sheet,
  // ex. "Saisies du jour") — closeQuickRule referme alors complètement, comme avant.
  const openQuickRule = (artNr, name, ean, returnTo) => { setQuickRuleArt({ artNr, name, ean, returnTo }); setSheet("quickRule"); };
  const closeQuickRule = () => { setSheet(quickRuleArt?.returnTo || null); setQuickRuleArt(null); };

  // CRUD des définitions de règles (NEXORA_ADMIN, 2026-08-17) — création, édition en place,
  // suppression (le serveur refuse une règle seed ou encore utilisée, message d'erreur réel
  // affiché tel quel).
  const startEditRuleDef = (d) => { setAddingRuleDef(false); setRuleDefEditingId(d.id); setRuleDefForm({ label: d.label, days: String(d.days), calendar: d.calendar }); };
  const cancelRuleDefForm = () => { setAddingRuleDef(false); setRuleDefEditingId(null); setRuleDefForm(emptyRuleDefForm); };
  const submitRuleDefForm = () => {
    const label = ruleDefForm.label.trim();
    const days = Number(ruleDefForm.days);
    if (!label) return showToast(t("reductions.rulesAdmin.errLabel", "Libellé obligatoire."));
    if (!Number.isFinite(days) || days <= 0 || !Number.isInteger(days)) return showToast(t("reductions.rulesAdmin.errDays", "Nombre de jours invalide."));
    const body = JSON.stringify({ label, days, calendar: ruleDefForm.calendar });
    const req = ruleDefEditingId
      ? apiJSON(`/api/reductions/rule-definitions/${encodeURIComponent(ruleDefEditingId)}`, { method: "PATCH", body })
      : apiJSON("/api/reductions/rule-definitions", { method: "POST", body });
    req.then(() => {
      loadRuleDefinitions();
      showToast(ruleDefEditingId ? t("toast.ruleDefUpdated", "⚙️ Règle mise à jour") : t("toast.ruleDefCreated", "⚙️ Règle créée"));
      cancelRuleDefForm();
    }).catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  const deleteRuleDef = (d) => {
    if (!window.confirm(t("reductions.rulesAdmin.confirmDelete", { label: d.label }, `Supprimer la règle "${d.label}" ?`))) return;
    apiJSON(`/api/reductions/rule-definitions/${encodeURIComponent(d.id)}`, { method: "DELETE" })
      .then(() => { loadRuleDefinitions(); showToast(t("toast.ruleDefDeleted", "🗑️ Règle supprimée")); })
      .catch((err) => showToast(err.message || t("reductions.rulesAdmin.errDeleteInUse", "Suppression impossible.")));
  };
  // Voir le commentaire sur inlineRuleAddKey plus haut — même endpoint que submitRuleDefForm
  // (POST uniquement, jamais d'édition/suppression ici, ce n'est pas l'écran admin), mais
  // sélectionne automatiquement la règle fraîchement créée via onCreated(id).
  const openInlineRuleAdd = (key) => { setInlineRuleAddKey(key); setInlineRuleForm(emptyInlineRuleForm); };
  const cancelInlineRuleAdd = () => { setInlineRuleAddKey(null); setInlineRuleForm(emptyInlineRuleForm); };
  const submitInlineRuleAdd = (onCreated) => {
    const label = inlineRuleForm.label.trim();
    const days = Number(inlineRuleForm.days);
    if (!label) return showToast(t("reductions.rulesAdmin.errLabel", "Libellé obligatoire."));
    if (!Number.isFinite(days) || days <= 0 || !Number.isInteger(days)) return showToast(t("reductions.rulesAdmin.errDays", "Nombre de jours invalide."));
    apiJSON("/api/reductions/rule-definitions", { method: "POST", body: JSON.stringify({ label, days, calendar: inlineRuleForm.calendar }) })
      .then((data) => {
        loadRuleDefinitions();
        showToast(t("toast.ruleDefCreated", "⚙️ Règle créée"));
        setInlineRuleAddKey(null); setInlineRuleForm(emptyInlineRuleForm);
        if (data && data.definition && onCreated) onCreated(data.definition.id);
      })
      .catch((err) => showToast(err.message || t("err.savingImpossible", "Enregistrement impossible.")));
  };
  // Rendu partagé du mini-formulaire (label + J-X + ouvrés/calendaires) — mêmes champs que
  // l'écran admin, jamais dupliqués visuellement différemment selon l'endroit d'où on l'ouvre.
  const renderInlineRuleAddForm = (onCreated) => (
    <div style={{ ...card, borderRadius: 16, padding: 12, marginTop: 8, flexDirection: "column", alignItems: "stretch" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <input style={inputStyle} placeholder={t("reductions.rulesAdmin.labelPlaceholder", "Libellé")} value={inlineRuleForm.label} onChange={(e) => setInlineRuleForm({ ...inlineRuleForm, label: e.target.value })} />
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input style={{ ...inputStyle, flex: 1 }} type="number" min="1" inputMode="numeric" placeholder="J-X" value={inlineRuleForm.days} onChange={(e) => setInlineRuleForm({ ...inlineRuleForm, days: e.target.value })} />
          <Pressable onClick={() => setInlineRuleForm({ ...inlineRuleForm, calendar: !inlineRuleForm.calendar })} style={{
            flex: "0 0 auto", padding: "10px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
            background: inlineRuleForm.calendar ? A.blue : P.soft, color: inlineRuleForm.calendar ? "#fff" : P.ink,
          }}>{inlineRuleForm.calendar ? t("reductions.rulesAdmin.calendarDays", "Calendaires") : t("reductions.rulesAdmin.openDays", "Ouvrés")}</Pressable>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Pressable onClick={cancelInlineRuleAdd} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.muted }}>{t("common.cancel", "Annuler")}</Pressable>
          <Pressable onClick={() => submitInlineRuleAdd(onCreated)} style={{ flex: 1, ...btnSolid(A.blue), padding: "10px 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))` }}>{t("reductions.rulesAdmin.createBtn", "Créer la règle")}</Pressable>
        </div>
      </div>
    </div>
  );
  const selectDelArt = (a) => { setDelArt(a); setDelSearch(""); setDelManualSearchOpen(false); setDelQty(1); setDelMhd(""); };
  // Écrit directement dans `records` (source KUEHLUNG) — même mécanisme que saveEdit
  // (Contrôle Kühlung "run") et saveNewArtMhd, jamais un silo "DELIVERY" séparé (voir
  // commentaire sur deliveryReceived plus haut). Une MHD reçue par scan devient donc une
  // vraie fiche Contrôle Kühlung, immédiatement visible dans Contrôle ET Réductions.
  const saveDelivery = () => {
    if (!delArt) { showToast(t("toast.selectArticleFirst", "Sélectionne d'abord un article.")); return; }
    if (!delMhd) { showToast(t("toast.chooseReceivedMhdDate", "Choisis la date MHD reçue.")); return; }
    if (delMhd < todayISO()) { showToast(t("controlRun.mhdBeforeTodayError", "Impossible d'enregistrer une MHD avant aujourd'hui.")); return; }
    const duplicate = records.some((r) => r.artNr === delArt.artNr && r.mhd === delMhd);
    if (duplicate) { showToast(t("toast.mhdDateAlreadyRegistered", "Cette date MHD est déjà enregistrée pour cet article.")); return; }
    const tempId = "tmp" + Date.now();
    setRecords((rs) => [...rs, {
      id: tempId, artNr: delArt.artNr, name: delArt.name, mhd: delMhd, quantity: delQty,
      source: "KUEHLUNG", employee: user?.firstname || "Toi", controlDate: todayISO(), createdAt: new Date().toISOString(),
    }]);
    setTouched((ts) => new Set(ts).add(delArt.artNr));
    apiJSON("/api/records", {
      method: "POST",
      body: JSON.stringify({
        artNr: delArt.artNr, name: delArt.name, mhd: delMhd, quantity: delQty,
        source: "KUEHLUNG", employee: user?.firstname || "Toi", controlDate: todayISO(),
      }),
    })
      .then((created) => setRecords((rs) => rs.map((r) => r.id === tempId ? created : r)))
      .catch(() => setRecords((rs) => rs.filter((r) => r.id !== tempId)));
    markTouched(delArt.artNr, "MHD_ENREGISTREE");
    showToast(t("delivery.addedToControlToast", { name: delArt.name }, `✓ ${delArt.name} ajouté au Contrôle Kühlung`));
    setDelArt(null); setDelQty(1); setDelMhd("");
  };
  const removeDelivery = (id) => {
    setRecords((rs) => rs.filter((r) => r.id !== id));
    apiJSON(`/api/records/${id}`, { method: "DELETE" }).catch(() => loadKuhlungRecords());
    showToast(t("toast.receptionRemoved", "Réception retirée"));
  };

  const trashStore = (id) => {
    // storeRecords seul ne suffit pas (2026-08-20, bug réel trouvé en vérification) :
    // limité à controlDate=aujourd'hui, alors que ce bouton est aussi appelé depuis
    // Historique complet qui affiche des saisies de n'importe quel jour (voir
    // redStoreRecords) — un id absent de storeRecords poussait `undefined` dans
    // storeTrash et faisait planter l'écran (page blanche).
    const rec = storeRecords.find((r) => r.id === id) || redStoreRecords.find((r) => r.id === id);
    if (!rec) return;
    setStoreRecords((rs) => rs.filter((r) => r.id !== id));
    setRedStoreRecords((rs) => rs.filter((r) => r.id !== id));
    setStoreTrash((t) => [rec, ...t]);
    apiJSON(`/api/records/${id}/trash`, { method: "PATCH" }).catch(() => { loadStoreRecords(); loadRedStoreRecords(); loadStoreTrash(); });
    showToast(t("toast.movedToTrashM", "Déplacé vers la corbeille"));
  };
  const restoreStore = (id) => {
    const rec = storeTrash.find((r) => r.id === id);
    setStoreTrash((t) => t.filter((r) => r.id !== id));
    setStoreRecords((rs) => [rec, ...rs]);
    setRedStoreRecords((rs) => [rec, ...rs]);
    apiJSON(`/api/records/${id}/restore`, { method: "PATCH" }).catch(() => { loadStoreRecords(); loadRedStoreRecords(); loadStoreTrash(); });
    showToast(t("toast.restoredCheckmark", "Restauré ✓"));
  };
  // Pendant de deletePermanently (Réductions), pour la Corbeille propre à Historique complet
  // (storeTrash) — retour utilisateur explicite (2026-08-20).
  const deleteStorePermanently = (id) => {
    setStoreTrash((t) => t.filter((r) => r.id !== id));
    apiJSON(`/api/records/${id}`, { method: "DELETE" }).catch(() => loadStoreTrash());
    showToast(t("toast.deletedPermanently", "🗑️ Supprimé définitivement"));
  };

  const goBack = () => {
    if (screen === "delivery") { setScreen("kuhlung"); return; }
    // Un responsable réseau (Bereichsleiter...) n'a JAMAIS accès au reste du module Kühlung
    // (pas de tuile Contrôle/Kühlung dans sa nav — seule sa tuile personnalisée Accueil mène
    // ici) : le faire atterrir dans "hub" (le hub employé de Kühlung, avec Contrôle/Livraison/
    // DLC promo) lui donnerait accès de fait à tout le module, contraire au retour explicite
    // "il n'a pas besoin de tout le module kuhlung". Retour direct à son Accueil réseau.
    if (screen === "store" && isNetworkLeader) { setScreen("home"); return; }
    if (screen === "run" || screen === "store" || screen === "newArticles" || screen === "newArticlesFF") { setScreen(screen === "run" ? "kuhlung" : "hub"); return; }
    if (screen === "mhdImport") {
      if (mhdImportStep === "preview") { setMhdImportStep("upload"); return; }
      setScreen("kuhlung");
      return;
    }
    if (screen === "kuhlung" || screen === "soon" || screen === "czEntry") { setScreen("mhdHub"); return; }
    if (screen === "mhdHub") { setScreen("hub"); return; }
    setScreen("home");
  };

  const title =
    screen === "mhdImport" ? (mhdImportStep === "preview" ? t("mhdImport.preview.title", "Vérification MHD") : t("mhdImport.upload.title", "Importer un PDF MHD")) :
    screen === "mhdHub" ? t("control.hub.mhdTitle", "Contrôle MHD") :
    screen === "kuhlung" ? t("control.kuhlung.title", "MHD Kühlung") :
    screen === "czEntry" ? (czZone?.label || t("control.hub.mhdTitle", "Contrôle MHD")) :
    screen === "soon" ? (soonCat?.label || t("control.categorySoon", "Bientôt")) :
    screen === "delivery" ? t("control.title.deliveryKuhlung", "Livraison Kühlung") :
    screen === "newArticles" ? t("control.hub.newArticlesTitle", "DLC Articles promotionnels kühlung") :
    screen === "newArticlesFF" ? t("control.hub.newArticlesFFTitle", "DLC Articles promotionnels Frisch Fleisch") :
    t("nav.control", "Contrôle");

  const filteredList = useMemo(() => {
    const q = search.trim().toLowerCase();
    return articles.filter((a) => !q || a.name.toLowerCase().includes(q) || a.artNr.includes(q));
  }, [search, articles]);

  /* Accueil — indicateurs et fil d'activité dérivés des VRAIES données de
     l'artefact : tout ce que tu fais dans Contrôle se reflète ici. */
  const urgentCount = records.filter((r) => urgency(r.mhd) === "red").length;
  // Bug réel trouvé (retour utilisateur 2026-08-04, "les MHD restent verrouillées en haut
  // du fil, rien de nouveau ne prend leur place") : le tri utilisait `ord: r.id` directement
  // sur des id texte type "R1782752968291_od2mdb" (voir createId(), server.js) — `b.ord -
  // a.ord` sur deux chaînes non numériques vaut NaN, donc le comparateur ne triait jamais
  // rien et l'ordre restait figé sur l'ordre d'arrivée du tableau (records en premier,
  // toujours les mêmes 4 premiers). idOrd() récupère le vrai timestamp Date.now() déjà
  // encodé dans chaque id créé par createId(), comparable entre TOUTES les catégories.
  const idOrd = (id) => { const m = String(id || "").match(/(\d{10,})/); return m ? Number(m[1]) : 0; };
  const homeActivity = useMemo(() => ([
    ...records.map((r) => ({
      key: "r" + r.id, ord: idOrd(r.id), emoji: "❄️", go: "hub",
      text: `${r.employee} · MHD ${fmtFR(r.mhd)} — ${(articles.find((a) => a.artNr === r.artNr) || {}).name || "Art." + r.artNr}`,
    })),
    ...storeRecords.map((r) => ({
      key: "s" + r.id, ord: idOrd(r.id), emoji: "🏷️", go: "store",
      text: `${r.employee ? r.employee + " · " : ""}DLC promo ${fmtFR(r.mhd)} — ${r.name}`,
    })),
    ...newArticles.map((a) => ({
      key: "n" + a.id, ord: idOrd(a.id), emoji: "🆕", go: "newArticles",
      text: `${a.createdBy ? a.createdBy + " · " : ""}Nouvel article signalé — ${a.name}`,
    })),
    ...deliveryReceived.map((r) => ({
      key: "d" + r.id, ord: idOrd(r.id), emoji: "❄️", go: "delivery",
      text: `${r.employee} · MHD Kühlung ×${r.quantity} — ${r.name}`,
    })),
    ...planRequests.filter((r) => r.status !== "pending" || r.emp === (user?.firstname || "Démo")).slice(0, 3).map((r) => ({
      key: "p" + r.id, ord: r.createdAt ? new Date(r.createdAt).getTime() : idOrd(r.id), emoji: r.type === "urlaub" ? "🏖️" : "🌿", go: "planning",
      text: `${r.emp} · ${r.type === "urlaub" ? "Urlaub" : "Freiwunsch"} ${fmtFR(r.from)} — ${r.status === "approved" ? "approuvé" : r.status === "refused" ? "refusé" : "envoyé"}`,
    })),
    ...reduced.map((d) => {
      const rec = [...records, ...storeRecords].find((r) => r.id === d.recId);
      return rec ? {
        key: "x" + d.recId, ord: d.doneAt ? new Date(d.doneAt).getTime() : idOrd(d.recId), emoji: "🏷️", go: "reductions",
        text: `${d.employee ? d.employee + " · " : ""}Réduction faite — ${(articles.find((a) => a.artNr === rec.artNr) || rec).name || rec.name}`,
      } : null;
    }).filter(Boolean),
  ].sort((a, b) => b.ord - a.ord).slice(0, 4)), [records, storeRecords, newArticles, deliveryReceived, reduced, planRequests, user]);

  /* Tâches du jour — source unique pour l'assistant ET le dashboard */
  const dayTasks = useMemo(() => {
    const list = [];
    // Retiré définitivement (retour utilisateur explicite, 2026-08-12) : "contrôler tout le
    // Kühlung" (dayTasks.controlKuhlung), "faire valider X articles" (dayTasks.validateNewArticles)
    // et "retirer les articles expirés" (dayTasks.removeExpired) ne doivent plus jamais
    // apparaître dans les tâches de l'Assistant/dashboard — retiré de la liste, pas juste
    // masqué. Les clés i18n/chip correspondantes restent dans les locales (inertes, plus
    // aucun appelant) plutôt que supprimées, pour ne pas casser une éventuelle réutilisation
    // ailleurs sans le vérifier explicitement.
    // "réceptionner la livraison du jour" (dayTasks.receiveDelivery) retiré de la même façon
    // (retour utilisateur explicite, 2026-08-16) — clé i18n laissée inerte dans les locales.
    if (urgentCount > 0) list.push({ label: t("dayTasks.urgentMhd.label", { n: urgentCount }, `traiter ${urgentCount} MHD urgente${urgentCount > 1 ? "s" : ""}`), chip: t("dayTasks.urgentMhd.chip", { n: urgentCount }, `🚨 ${urgentCount} urgente${urgentCount > 1 ? "s" : ""}`), go: "hub", priority: "urgent" });
    if (redTodo.length > 0) list.push({ label: t("dayTasks.reduceArticles.label", { n: redTodo.length }, `réduire ${redTodo.length} article${redTodo.length > 1 ? "s" : ""} (J-3)`), chip: t("dayTasks.reduceArticles.chip", { n: redTodo.length }, `🏷️ ${redTodo.length} à réduire`), go: "reductions", priority: "medium" });
    if (reqPending.length > 0) list.push({ label: t("dayTasks.planningRequests.label", { n: reqPending.length }, `traiter ${reqPending.length} demande${reqPending.length > 1 ? "s" : ""} de planning`), chip: t("dayTasks.planningRequests.chip", { n: reqPending.length }, `🙋 ${reqPending.length} demande${reqPending.length > 1 ? "s" : ""}`), go: "planning", priority: "low" });
    if (openStoreTeamTasks.length > 0) list.push({ label: t("dayTasks.storeTeamTasks.label", { n: openStoreTeamTasks.length }, `faire ${openStoreTeamTasks.length} tâche${openStoreTeamTasks.length > 1 ? "s" : ""} filiale`), chip: t("dayTasks.storeTeamTasks.chip", { n: openStoreTeamTasks.length }, `✅ ${openStoreTeamTasks.length} tâche${openStoreTeamTasks.length > 1 ? "s" : ""}`), go: "tasks", priority: "medium" });
    if (!haccpRecord?.validation) {
      const remaining = allDailyItems.length - dailyFilledCount;
      list.push(remaining > 0
        ? { label: t("dayTasks.haccpFill.label", { n: remaining }, `remplir ${remaining} contrôle${remaining > 1 ? "s" : ""} HACCP`), chip: t("dayTasks.haccpFill.chip", { n: remaining }, `🌡️ ${remaining} HACCP`), go: "haccp", priority: "medium" }
        : { label: t("dayTasks.haccpValidateDay.label", "valider la journée HACCP"), chip: t("dayTasks.haccpValidateDay.chip", "🌡️ Valider HACCP"), go: "haccp", priority: "medium" });
    }
    if (openClaimsCount > 0) list.push({ label: t("dayTasks.sendClaims.label", { n: openClaimsCount }, `envoyer ${openClaimsCount} réclamation${openClaimsCount > 1 ? "s" : ""}`), chip: t("dayTasks.sendClaims.chip", { n: openClaimsCount }, `⚠️ ${openClaimsCount} à envoyer`), go: "claims", priority: "medium" });
    if (krankmeldungen.some((k) => k.generalStatus === "ACTIVE" && ["PENDING", "REJECTED"].includes(k.auStatus))) list.push({ label: t("dayTasks.krankAuUpload.label", "déposer ton AU Krankmeldung"), chip: t("dayTasks.krankAuUpload.chip", "🤒 AU à déposer"), go: "krank", priority: "urgent" });
    if (pendingOrdersCount > 0) list.push({ label: t("dayTasks.sendMaterialOrders.label", { n: pendingOrdersCount }, `envoyer ${pendingOrdersCount} bon${pendingOrdersCount > 1 ? "s" : ""} de commande`), chip: t("dayTasks.sendMaterialOrders.chip", { n: pendingOrdersCount }, `🧰 ${pendingOrdersCount} à envoyer`), go: "material", priority: "low" });
    if (infoMandatoryPending > 0) list.push({ label: t("dayTasks.signMandatoryInfo.label", { n: infoMandatoryPending }, `signer ${infoMandatoryPending} information${infoMandatoryPending > 1 ? "s" : ""} obligatoire${infoMandatoryPending > 1 ? "s" : ""}`), chip: t("dayTasks.signMandatoryInfo.chip", { n: infoMandatoryPending }, `📌 ${infoMandatoryPending} à signer`), go: "infos", priority: "urgent" });
    if (totalUnread > 0) list.push({ label: t("dayTasks.readMessages.label", { n: totalUnread }, `lire ${totalUnread} message${totalUnread > 1 ? "s" : ""}`), chip: t("dayTasks.readMessages.chip", { n: totalUnread }, `💬 ${totalUnread} non lu${totalUnread > 1 ? "s" : ""}`), go: "messages", priority: "low" });
    if (!dailySaved.prebake?.[TODAY_IDX] && !dailySaved.brot?.[TODAY_IDX] && !dailySaved.manual_waste?.[TODAY_IDX]) list.push({ label: t("dayTasks.reportingDailyEntry.label", "saisir les pertes du jour (Reporting)"), chip: t("dayTasks.reportingDailyEntry.chip", "📈 Saisie du jour"), go: "reporting", priority: "medium" });
    if (finderAccess.hasValidateTier && finderAdminItems.length > 0) list.push({ label: t("dayTasks.validateFinderCards.label", { n: finderAdminItems.length }, `valider ${finderAdminItems.length} fiche${finderAdminItems.length > 1 ? "s" : ""} Artikelfinder`), chip: t("dayTasks.validateFinderCards.chip", { n: finderAdminItems.length }, `🔎 ${finderAdminItems.length} à valider`), go: "finder", priority: "low" });
    const lfNeedsReview = lieferscheine.filter((n) => !n.archived && !n.trashed && n.items.some((it) => it.needsReview)).length;
    if (lfNeedsReview > 0) list.push({ label: t("dayTasks.reviewLieferschein.label", { n: lfNeedsReview }, `vérifier ${lfNeedsReview} Lieferschein${lfNeedsReview > 1 ? "e" : ""} à revoir`), chip: t("dayTasks.reviewLieferschein.chip", { n: lfNeedsReview }, `📄 ${lfNeedsReview} à revoir`), go: "liefer", priority: "medium" });
    // Retour utilisateur explicite (2026-08-17) : "L'assistant sur le compte normal doit
    // toujours gérer les notifications arrivant sur tous les modules" — Inventur/RDV
    // inventaire manquait ici (le badge de tuile pendingModuleCounts.inventur a été corrigé
    // séparément, mais dayTasks est le SEUL flux qui alimente vraiment la phrase parlée de
    // l'Assistant + ses pastilles cliquables, voir goToDayTask). Même filtre que le badge :
    // le GR a proposé/contre-proposé une date, pas encore répondu par la filiale.
    const inventurPending = inventurAppointments.filter((a) => !a.confirmed && !a.cancelled && a.proposedBySide === "bl").length;
    if (inventurPending > 0) list.push({ label: t("dayTasks.inventurAppointment.label", { n: inventurPending }, `répondre à ${inventurPending} rendez-vous d'inventaire`), chip: t("dayTasks.inventurAppointment.chip", { n: inventurPending }, `🧮 ${inventurPending} RDV`), go: "inventur", priority: "medium" });
    // Même exigence (2026-08-17, "connecte tout à l'assistant") — Articles et Missions
    // réseau avaient déjà un vrai badge de tuile (pendingModuleCounts) mais jamais d'entrée
    // dayTasks, donc jamais dans la phrase parlée/pastilles de l'Assistant. Mêmes variables
    // exactes que les overrides pendingModuleCounts existants plus bas, aucune nouvelle
    // source de vérité inventée.
    if (articleReports.length > 0) list.push({ label: t("dayTasks.articleReports.label", { n: articleReports.length }, `insérer ${articleReports.length} article${articleReports.length > 1 ? "s" : ""} signalé${articleReports.length > 1 ? "s" : ""}`), chip: t("dayTasks.articleReports.chip", { n: articleReports.length }, `📦 ${articleReports.length} à insérer`), go: "articles", priority: "low" });
    const missionsPending = myNetworkMissions.filter((m) => (m.filialeStatuses[m.myStores[0]] || {}).status === "en_attente").length;
    if (missionsPending > 0) list.push({ label: t("dayTasks.networkMissions.label", { n: missionsPending }, `traiter ${missionsPending} mission${missionsPending > 1 ? "s" : ""} réseau`), chip: t("dayTasks.networkMissions.chip", { n: missionsPending }, `🎯 ${missionsPending} mission${missionsPending > 1 ? "s" : ""}`), go: "missions", priority: "medium" });
    // Même exigence (2026-08-17) — Preisänderung : GET /api/preisanderungen est déjà filtré
    // côté serveur par filiale (server/preisanderung/routes.js, scope.has(p.storeId)), donc
    // "pas terminée" = vraiment à compter par CETTE filiale, jamais une donnée d'une autre.
    const preisanderungPending = preisanderungen.filter((p) => p.status !== "abgeschlossen").length;
    if (preisanderungPending > 0) list.push({ label: t("dayTasks.preisanderung.label", { n: preisanderungPending }, `compter ${preisanderungPending} Preisänderung${preisanderungPending > 1 ? "en" : ""}`), chip: t("dayTasks.preisanderung.chip", { n: preisanderungPending }, `💶 ${preisanderungPending} Preisänderung${preisanderungPending > 1 ? "en" : ""}`), go: "preisanderung", priority: "medium" });
    return list;
  }, [urgentCount, deliveryReceived, redTodo, reqPending, openStoreTeamTasks, haccpRecord, dailyFilledCount, openClaimsCount, krankmeldungen, pendingOrdersCount, infoMandatoryPending, totalUnread, dailySaved, finderAccess, finderAdminItems, lieferscheine, inventurAppointments, articleReports, myNetworkMissions, preisanderungen, t]);

  // Tâches "réellement actionnables par moi aujourd'hui" — Mes tâches (assignées) + To Do
  // liste personnelle + Tâches de l'équipe reçues, JAMAIS Tâches confiées (celles-là sont à
  // faire par quelqu'un d'autre, pas par moi). Retour utilisateur explicite, 2026-08-07 :
  // sert le popup "Tâches à faire" ci-dessous — voir aussi son constat que l'Assistant
  // (dayTasks) n'a jamais compté que les tâches équipe (openStoreTeamTasks), jamais "mine"/
  // "assigned", d'où l'impression que l'Assistant "n'est pas du tout connecté au module tâches".
  //
  // t.status === "open" exclut volontairement le statut "late" (2026-08-30, retour
  // utilisateur : "tant que les tâches n'ont pas été effectuées [le popup s'affiche] mais si
  // la date et l'heure maximum d'exécution passent alors le popup ne déclenche plus à
  // l'ouverture et les tâches passent en statut pas fait") — une tâche dont l'échéance est
  // dépassée (mapServerTask, via t.late calculé côté serveur) sort de ce filtre et arrête de
  // rouvrir le popup, sans pour autant disparaître : elle reste visible et actionnable dans
  // l'onglet "En cours" (voir isTaskPending) jusqu'au clic sur "Terminer".
  //
  // Gate horaires filiale (2026-08-31, retour utilisateur explicite — "le popup ne devrait
  // jamais se déclencher avant l'ouverture ou après la fermeture") : storeOpenStatus === null
  // veut dire "horaires non configurés pour cette filiale" — ne JAMAIS bloquer dans ce cas,
  // comportement identique à avant (toutes les filiales n'ont pas encore leurs horaires
  // saisis dans Admin > Filiales). Seul storeOpenStatus.isOpen === false bloque vraiment.
  const storeOpenStatus = computeStoreOpenStatus(myStoreHours.openTime, myStoreHours.closeTime, allHolidayDatesSet, now);
  const actionableTasks = (storeOpenStatus && !storeOpenStatus.isOpen) ? [] : tasks.filter((t) => t.scope === "store" && t.status === "open" && (
    TASK_FILTER_PREDICATES.mine(t) || TASK_FILTER_PREDICATES.assigned(t) || TASK_FILTER_PREDICATES.team(t)
  ));
  const goToActionableTasks = () => {
    setSheet(null);
    const category = actionableTasks.some((t) => TASK_FILTER_PREDICATES.assigned(t)) ? "assigned"
      : actionableTasks.some((t) => TASK_FILTER_PREDICATES.mine(t)) ? "mine" : "team";
    setScreen("tasks"); setTasksScreen("list"); setTaskScope("store"); setTaskTab("open");
    setTaskFilter(category); setTeamSubView("received");
  };

  /* Popup "Tâches à faire" + auto-ouverture de l'Assistant à l'entrée dans l'app (retour
     utilisateur explicite, 2026-08-07) : "une sorte de popup central qui s'ouvrait sur l'app
     quand on sortait et rentrait dans l'app... pour rester focus et ne pas oublier ses
     objectifs du jour" — PAS l'Assistant (confirmé explicitement par l'utilisateur après
     une première tentative), un popup dédié listant les tâches en cours du module Tâches,
     avec "Y aller"/"Plus tard". Réutilise le composant Sheet existant (feuille du bas), pas
     un nouveau style de popup centré — décision explicite de l'utilisateur pour rester
     cohérent avec le reste de l'app plutôt qu'introduire un nouveau patron visuel.
     Priorité : s'il y a des tâches actionnables, montre CE popup. L'auto-ouverture de
     l'Assistant (repli précédent quand il n'y avait pas de tâche actionnable mais autre
     chose à faire dans dayTasks) a été DÉSACTIVÉE — retour utilisateur explicite, 2026-08-07 :
     "désactive l'assistant qui s'ouvre à l'ouverture de l'app". L'Assistant reste accessible
     manuellement (glissement sur la carte, voir openAssistant plus bas) — seul le
     déclenchement automatique est coupé. Se déclenche à l'ouverture initiale ET à chaque
     retour au premier plan (Page Visibility API), avec un cooldown de 60s pour ne pas
     harceler quelqu'un qui bascule juste rapidement entre apps. Lit tout via des refs (même
     patron que notificationsRef plus haut) pour que cet effet ne s'enregistre qu'UNE fois
     par connexion (dépend seulement de `user`). */
  const actionableTasksRef = useRef(actionableTasks);
  useEffect(() => { actionableTasksRef.current = actionableTasks; }, [actionableTasks]);
  // Rappel de commande (2026-08-31) — lu via refs dans le même tryAutoOpen ci-dessous, jamais
  // son propre effet séparé : un seul popup automatique à la fois, priorité aux Tâches (déjà
  // établie), le rappel de commande prend le relais UNIQUEMENT si aucune tâche actionnable
  // n'a pris ce créneau. Gate horaires filiale identique au popup Tâches (storeOpenStatus
  // === null = horaires non configurés, jamais bloquant ; isOpen === false = bloquant).
  const merchOrderReminderZonesRef = useRef(merchOrderReminder.zones);
  useEffect(() => { merchOrderReminderZonesRef.current = merchOrderReminder.zones; }, [merchOrderReminder.zones]);
  const canPrepareMerchRef = useRef(canPrepareMerch);
  useEffect(() => { canPrepareMerchRef.current = canPrepareMerch; }, [canPrepareMerch]);
  const storeOpenStatusRef = useRef(storeOpenStatus);
  useEffect(() => { storeOpenStatusRef.current = storeOpenStatus; }, [storeOpenStatus]);
  const lastAutoPopupAtRef = useRef(0);
  const hasTriedInitialTaskPopupRef = useRef(false);
  useEffect(() => {
    // Jamais sur un compte réseau/GR (voir isNetworkLeaderAccount) — les tâches listées ici
    // sont TOUJOURS scope:"store" (voir actionableTasks), sans rapport avec l'interface de
    // pilotage réseau, même quand ce compte a par ailleurs une visibilité incidente sur les
    // tâches d'une filiale précise.
    if (!user || isNetworkLeaderAccount) return;
    hasTriedInitialTaskPopupRef.current = false; // nouvelle connexion → nouvelle tentative permise
    const tryAutoOpen = () => {
      if (Date.now() - lastAutoPopupAtRef.current < 60000) return;
      if (actionableTasksRef.current.length > 0) {
        lastAutoPopupAtRef.current = Date.now();
        hasTriedInitialTaskPopupRef.current = true;
        setSheet("taskReminder");
        return;
      }
      const storeStatus = storeOpenStatusRef.current;
      const storeClosedNow = storeStatus && !storeStatus.isOpen;
      if (canPrepareMerchRef.current && merchOrderReminderZonesRef.current.length > 0 && !storeClosedNow) {
        lastAutoPopupAtRef.current = Date.now();
        setSheet("merchOrderReminder");
      }
    };
    const initial = setTimeout(tryAutoOpen, 1500);
    const onVisibility = () => { if (document.visibilityState === "visible") tryAutoOpen(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => { clearTimeout(initial); document.removeEventListener("visibilitychange", onVisibility); };
  }, [user, isNetworkLeaderAccount]);
  // Filet de sécurité contre une course avec le réseau (retour utilisateur réel, 2026-08-29 :
  // "le popup ne s'affiche plus sur un compte normal", vérifié en base — de vraies tâches
  // d'équipe actionnables existaient bien, donc le popup AURAIT dû s'afficher). Cause :
  // loadTasks() fait partie d'une quinzaine d'appels chargés en parallèle à la connexion ; sur
  // un réseau lent, l'essai unique à 1500ms ci-dessus peut tomber avant que les tâches soient
  // réellement arrivées — sans filet, plus aucune chance d'afficher le popup avant le prochain
  // retour au premier plan de l'app. Dès que actionableTasks devient non-vide pour la première
  // fois après connexion, retente une fois (hasTriedInitialTaskPopupRef empêche toute
  // réapparition ultérieure dans la même session quand une tâche est marquée faite/ajoutée).
  useEffect(() => {
    if (!user || isNetworkLeaderAccount || hasTriedInitialTaskPopupRef.current) return;
    if (actionableTasks.length === 0) return;
    if (Date.now() - lastAutoPopupAtRef.current < 60000) return;
    hasTriedInitialTaskPopupRef.current = true;
    lastAutoPopupAtRef.current = Date.now();
    setSheet("taskReminder");
  }, [user, isNetworkLeaderAccount, actionableTasks.length]);

  /* Point d'entrée unique pour aller vers un item d'Action Live / Urgent —
     réutilisé par l'assistant, le tableau de bord et le centre de notifications. */
  const goToDayTask = (t) => {
    if (t.go === "planning") setPlanScreen("requests");
    // Cet item est un agrégat ("faire N tâches filiale", openStoreTeamTasks) — pas une tâche
    // précise, donc pas de routeForTaskId possible ici, mais on route quand même vers la
    // bonne CATÉGORIE ("Tâches de l'équipe") plutôt que la vue non filtrée (retour
    // utilisateur explicite, 2026-08-07).
    if (t.go === "tasks") { setTasksScreen("list"); setTaskScope("store"); setTaskTab("open"); setTaskFilter("team"); setTeamSubView("received"); }
    if (t.go === "haccp") {
      setHaccpScreen("hub");
      const firstIncomplete = haccpConfig.dailyGroups.find((g) => !groupComplete(g));
      setOpenGroupId(firstIncomplete ? firstIncomplete.id : null);
    }
    if (t.go === "claims") setClaimsScreen("hub");
    if (t.go === "krank") setKrankScreen("mine");
    if (t.go === "material") setMoScreen("hub");
    if (t.go === "infos") setInfoScreen("hub");
    if (t.go === "messages") setMsgScreen("list");
    if (t.go === "reporting") setRepScreen("hub");
    if (t.go === "finder") setFinderScreen("hub");
    if (t.go === "inventur") setInventurScreen("rdv");
    if (t.go === "articles") setArtScreen("reports");
    if (t.go === "preisanderung") setPaScreen("hub");
    if (t.go === "liefer") {
      const noteNeedsReview = lieferscheine.find((n) => !n.archived && !n.trashed && n.items.some((it) => it.needsReview));
      if (noteNeedsReview) { setLfDetailId(noteNeedsReview.id); setLfScreen("detail"); }
      else setLfScreen("hub");
    }
    setScreen(t.go);
  };
  const urgentItems = dayTasks.filter((t) => !dismissedUrgent.includes(t.label));
  /* Modules "avec des notifications" — dérivé des vraies tâches en attente
     (dayTasks), pour que le point pulsé sur une tuile reflète un vrai
     signal à traiter plutôt qu'un simple "module construit". */
  const GO_TO_MODULE_ID = { kuhlung: "control", hub: "control", delivery: "control", store: "control", newArticles: "control" };
  const pendingModuleCounts = dayTasks.reduce((acc, t) => {
    const id = GO_TO_MODULE_ID[t.go] || t.go;
    acc[id] = (acc[id] || 0) + 1;
    return acc;
  }, {});
  // Badge Tâches — même défaut que Réductions/HACCP/Articles/Inventur ci-dessous : dayTasks
  // ne pousse jamais qu'UNE seule entrée "tasks" (générique, "tu as des tâches"), jamais le
  // vrai compte. Retour utilisateur explicite (2026-08-28, capture annotée) : la tuile
  // affichait "1" alors que la carte Assistant, juste au-dessus, annonçait déjà "5 tâches
  // t'attendent aujourd'hui" — même source de vérité (actionableTasks) réutilisée ici pour
  // que les deux ne se contredisent plus jamais.
  pendingModuleCounts.tasks = actionableTasks.length;
  // Badge Missions réseau — même comportement que les autres modules (retour utilisateur
  // explicite) : compte les missions dont SA propre filiale n'a pas encore accompli le
  // résultat ("en_attente" seulement — dès "Mission accomplie" cliqué, ça décrémente ;
  // une nouvelle mission reçue incrémente). Compte AUSSI le dialogue non lu (retour
  // utilisateur explicite, 2026-08-23 : "il faut des notifications et des badges... et fais
  // pareil dans la vue compte normal parce que tu ne l'as jamais implanté") — jamais
  // implémenté avant pour ce module côté compte normal.
  pendingModuleCounts.missions = myNetworkMissions.filter((m) => !m.myArchived && (((m.filialeStatuses[m.myStores[0]] || {}).status === "en_attente") || m.hasUnread)).length;
  // Badge Rapport de filiale — retour utilisateur explicite (2026-08-27) : "le module doit
  // toujours s'incrémenter... et se décrémenter au cas où les rapports diminuent OU les
  // tâches d'un rapport sont totalement accomplies". Deux façons de sortir du badge, pas
  // une seule : "Marquer le rapport traité" (mark-traite, déjà en place) OU — nouveau — la
  // to-do entièrement cochée, même si le destinataire n'a pas encore cliqué "traité" (un GR
  // pressé ne devrait pas avoir à acquitter manuellement un rapport dont il ne reste plus
  // rien à faire). Un rapport sans to-do (juste du texte libre) ne se vide que via
  // mark-traite, jamais automatiquement.
  pendingModuleCounts.filialeReport = frReceived.filter((r) => {
    const mine = r.recipientStatuses?.[user?.proId];
    if (!mine || mine.status === "traite") return false;
    const items = r.todoItems || [];
    if (items.length > 0 && items.every((i) => i.done)) return false;
    return true;
  }).length;
  // Badge Réductions — retour utilisateur explicite (2026-08-12, revient sur le choix
  // précédent) : uniquement le nombre d'articles À RÉDUIRE (redTodo), plus jamais
  // redExpired.length en plus — les expirés ne doivent pas gonfler ce badge. TOUTES
  // catégories confondues (Kühlung/Magasin/Trocken/Frisch Fleisch/Frisch Brot, déjà
  // fusionnées dans redActive/redTodo). redTodo est dérivé en direct de redActive, qui
  // exclut déjà tout article marqué réduit (state `reduced`) — décrémente donc dès qu'un
  // article est réduit, incrémente dès qu'un nouvel article entre dans sa fenêtre ou est
  // ajouté à n'importe quelle catégorie, sans action supplémentaire ici.
  pendingModuleCounts.reductions = redTodo.length;
  // Badge HACCP — même défaut que Réductions : dayTasks ne pousse qu'UNE entrée HACCP
  // ("remplir HACCP" ou "valider la journée"), jamais le vrai nombre de contrôles restants.
  // Ici : le vrai compte (allDailyItems - dailyFilledCount) tant qu'il reste des contrôles à
  // remplir ; une fois tous remplis mais la journée pas encore validée, il reste UNE action
  // réelle ("valider la journée") donc 1 ; journée déjà validée → 0.
  pendingModuleCounts.haccp = haccpRecord?.validation ? 0 : (Math.max(allDailyItems.length - dailyFilledCount, 0) || 1);
  // Badge Articles — Signalement nouveaux articles (artefact approuvé 2026-08-12) : le
  // nombre de signalements encore non insérés au rayon. Incrémente à chaque nouveau
  // signalement (POST), décrémente dès qu'un FL/FLA le marque "Inséré" (DELETE, voir
  // resolveArticleReport) — jamais un dayTask, articleReports est déjà la vraie liste.
  pendingModuleCounts.articles = articleReports.length;
  // Badge Inventur — RDV inventaire côté filiale (retour utilisateur explicite 2026-08-17,
  // "il manque les badges sur le module inventaire... le comportement doit être pareil des
  // 2 côtés"). Miroir exact de la formule déjà en place côté GR (pilotAppointments, tuile
  // "netAppointments" ~ligne 26575) : là-bas, le GR doit agir quand storeAgreed OU
  // proposedBySide==="store" ; ici symétriquement, la filiale doit agir quand le GR est à
  // l'origine de la proposition en cours ET qu'elle ne l'a pas encore acceptée — jamais les
  // deux badges allumés en même temps sur un même rendez-vous, le champ proposedBySide fait
  // toujours basculer le tour d'un côté OU de l'autre (jamais les deux, voir server.js
  // /api/inventur/appointments/:id/respond et /reschedule).
  pendingModuleCounts.inventur = inventurAppointments.filter((a) => !a.confirmed && !a.cancelled && a.proposedBySide === "bl").length;
  // Badge Preisänderung — même défaut que Réductions/HACCP/Articles : le reduce générique
  // ne pousserait qu'UNE seule entrée dayTasks donc 1, jamais le vrai nombre de Preisänderung
  // encore à compter. preisanderungen est déjà filtré par filiale côté serveur (voir
  // dayTasks ci-dessus), donc pas besoin de re-filtrer par storeId ici.
  pendingModuleCounts.preisanderung = preisanderungen.filter((p) => p.status !== "abgeschlossen").length;

  /* Pertes de la semaine — vraie saisie manuelle entsorgt (Reporting, weeklyGrid, famille
     manual_waste), jamais une base de démo mélangée à une vraie donnée (bug réel trouvé et
     corrigé 2026-08-02, retour utilisateur : dashboard sans données de test). Somme des 6
     jours réels de la semaine par rayon, en €, filtré aux rayons ayant une vraie saisie. */
  // Les 9 rayons RÉELS (7 manuels + Prebake/Frischbrot-Brot, sourcés de catalogDaily —
  // voir categoryDayValue), jamais seulement MANUAL_CATEGORIES : retour utilisateur
  // explicite (2026-08-03), "j'ai saisi les pertes du jour [boulangerie], rien ne bouge
  // sur le Dashboard" — bug réel, la carte "Pertes de la semaine" ignorait entièrement
  // Prebake et Frischbrot/Brot (sourcées de catalogDaily, jamais de weeklyGrid).
  // categoryDayValue lit catalogDaily/weeklyGrid en direct (état client, pas un nouveau
  // fetch) donc la mise à jour est déjà live dès la saisie, sans rechargement de page.
  const wasteByCat = useMemo(() => {
    return REPORT_CATEGORIES
      .map((cat) => {
        const total = WEEK_DAY_LABELS.reduce((s, _, i) => { const v = categoryDayValue(cat, i); return s + v.pu20 + v.entsorgt; }, 0);
        return [reportCategoryLabel(cat.id), truncate2(total)];
      })
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);
  }, [weeklyGrid, catalogDaily, repCatalog]);

  const colorCard = (bg) => ({
    background: `linear-gradient(155deg, rgba(255,255,255,.18), rgba(255,255,255,0) 45%), ${bg}`,
    color: "#fff", border: "1px solid rgba(255,255,255,.14)", borderRadius: 24,
    boxShadow: `0 3px 8px rgba(0,0,0,.10), 0 18px 40px ${bg}4d`,
  });
  const onColorSub = { color: "rgba(255,255,255,.82)" };
  const onColorTag = { fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".1em", color: "rgba(255,255,255,.85)" };
  const onColorChip = {
    flex: "0 0 auto", width: 48, height: 48, borderRadius: 16, color: "#fff",
    background: "linear-gradient(155deg, rgba(255,255,255,.34), rgba(255,255,255,.14) 60%)",
    border: "1px solid rgba(255,255,255,.32)",
    boxShadow: "inset 0 1px 1px rgba(255,255,255,.4), inset 0 -6px 10px rgba(0,0,0,.08), 0 6px 14px rgba(0,0,0,.14)",
    display: "grid", placeItems: "center", fontSize: `calc(22px * var(--nx-font-scale, 1))`,
  };
  const card = { background: P.card, border: `1px solid ${P.line}`, borderRadius: 24, boxShadow: `0 2px 6px rgba(20,24,38,.04), ${P.shadow}`, color: P.ink };
  const tile = { display: "flex", alignItems: "center", gap: 14, width: "100%", boxSizing: "border-box", padding: 16, marginBottom: 12 };
  const eyebrow = (color = A.blue) => ({ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color, textTransform: "uppercase", letterSpacing: ".1em" });
  // Missions réseau — deux échelles de statut distinctes et réutilisées partout (liste,
  // fiche détail, Fiche filiale) : FILIALE_STATUS_META pour le statut RÉEL d'UNE filiale
  // précise sur une mission (voir server/missions/repository.js), MISSION_OVERALL_META pour
  // le statut global dérivé (terminée seulement quand toutes les filiales sont tranchées).
  const FILIALE_STATUS_META = {
    en_attente: { l: t("missions.status.enAttente", "En attente"), c: P.muted, bg: P.soft },
    a_valider: { l: t("missions.status.aValider", "À valider"), c: A.blue, bg: "rgba(54,79,231,.12)" },
    validee: { l: t("missions.status.validee", "Validée"), c: A.green, bg: "rgba(30,184,95,.13)" },
    refusee: { l: t("missions.status.refusee", "Refusée"), c: A.red, bg: "rgba(239,68,68,.13)" },
  };
  const MISSION_OVERALL_META = {
    en_cours: { l: t("missions.overallStatus.enCours", "En cours"), c: A.orange, bg: "rgba(231,138,18,.14)" },
    terminee: { l: t("missions.overallStatus.terminee", "Terminée"), c: A.green, bg: "rgba(30,184,95,.13)" },
  };
  // Warenaustausch — statuts d'un transfert (server/warenaustausch/repository.js).
  const WA_STATUS_META = {
    brouillon: { l: t("warenaustausch.status.brouillon", "Brouillon"), c: P.muted, bg: P.soft },
    envoyee: { l: t("warenaustausch.status.envoyee", "Envoyé — à vérifier"), c: A.orange, bg: "rgba(231,138,18,.14)" },
    receptionnee: { l: t("warenaustausch.status.receptionnee", "Réceptionné"), c: A.green, bg: "rgba(30,184,95,.13)" },
    annulee: { l: t("warenaustausch.status.annulee", "Annulé"), c: A.red, bg: "rgba(239,68,68,.13)" },
  };
  const WA_CATEGORY_LABEL = {
    defekt: t("warenaustausch.category.defekt", "Marchandise défectueuse"),
    co2: t("warenaustausch.category.co2", "Cylindres CO2"),
    rr: t("warenaustausch.category.rr", "RR (manquant)"),
  };
  const TERMINAL_MISSION_STATUSES = ["validee", "refusee"];
  // Statut "vu par MOI" pour une mission ciblée sur des responsables — retour utilisateur
  // explicite 2026-08-23 : "je ne veux plus que quand je confie une mission en tant que NL
  // à VKL que cela renvoie à ses BL... une mission sélectionnée pour VKL reste juste visible
  // pour VKL et pour BL reste juste visible pour BL rien d'autre. Chacun valide de son côté
  // et c'est terminé". Aucune cascade de VISIBILITÉ n'a jamais existé (canSeeMissionDetail
  // exige déjà targetLeaderIds.includes(soi-même), voir server/missions/routes.js) — le vrai
  // problème était le STATUT affiché : overallStatus est agrégé (terminée seulement quand
  // TOUS les responsables ciblés ont validé), donc un VKL qui avait déjà validé SA part
  // voyait quand même "En cours" sur sa propre carte tant qu'un BL co-ciblé, sans rapport
  // avec lui, n'avait pas fini la sienne. Le créateur garde l'agrégat (utile pour son
  // oversight) ; un responsable ciblé qui n'est PAS le créateur voit désormais SA PROPRE
  // part uniquement, jamais tributaire d'un co-cible.
  const missionEffectiveStatus = (m) => {
    const myId = String(user?.proId || "");
    const isCreatorViewer = myId === String(m.createdBy);
    if (!isCreatorViewer && (m.targetLeaderIds || []).length) {
      const mine = (m.leaderStatuses || {})[myId];
      if (mine) return TERMINAL_MISSION_STATUSES.includes(mine.status) ? "terminee" : "en_cours";
    }
    return m.overallStatus;
  };
  // "encore B filiale(s) restante(s)" — retour utilisateur explicite, 2026-08-02 : le
  // statut "en cours" doit refléter directement combien de filiales n'ont pas encore
  // accompli leur part (B = total − filiales sur un statut terminal). null une fois
  // terminée (le badge "Terminée" suffit, pas besoin du décompte).
  const missionRemainingLabel = (m) => {
    if (m.overallStatus === "terminee") return null;
    const remaining = m.filiales.filter((f) => !["validee", "refusee"].includes((m.filialeStatuses[f] || {}).status)).length;
    if (remaining <= 0) return null;
    return t("missions.remainingLabel", { n: remaining }, `encore ${remaining} filiale${remaining > 1 ? "s" : ""} restante${remaining > 1 ? "s" : ""}`);
  };
  const btnSolid = (bg, fg = "#fff") => ({
    textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
    background: bg, color: fg, boxShadow: `0 12px 26px ${typeof bg === "string" && bg.startsWith("#") ? bg + "55" : "rgba(0,0,0,.2)"}`,
  });
  const btnGhostOnColor = { textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14px * var(--nx-font-scale, 1))`, background: "rgba(255,255,255,.2)", color: "#fff" };
  const inputStyle = {
    width: "100%", minWidth: 0, maxWidth: "100%", boxSizing: "border-box", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(15.5px * var(--nx-font-scale, 1))`, color: P.ink,
    background: P.soft, border: `1.5px solid ${P.line}`, borderRadius: 16, padding: "12px 14px",
    outline: "none", colorScheme: themeId === "sombre" ? "dark" : "light",
  };
  // input[type=date]/[type=time] sur Safari iOS réel (vérifié via Web Inspector distant
  // sur un vrai iPhone, 2026-07-29) : box-sizing:border-box n'est PAS respecté pour ces
  // contrôles natifs — getComputedStyle renvoie box-sizing:content-box malgré le style
  // inline, donc padding+border s'AJOUTENT à width:100% au lieu d'y être inclus (mesuré :
  // ~31px de débordement = 2×14px padding + 2×1.5px border). width:100% seul déborde donc
  // toujours du conteneur sur ces deux types précis, jamais sur un input texte classique.
  const dateTimeInputStyle = { ...inputStyle, width: "calc(100% - 31px)" };
  // Champ Filiale — "N" toujours préfixé, jamais tapé par l'utilisateur (2026-08-22,
  // retour utilisateur explicite : "que l'utilisateur n'ait plus jamais à entrer le N").
  // Purement une question de saisie — la valeur reconstituée par onChange reste "N236" en
  // clair, exactement le même format qu'avant partout ailleurs (aucun changement de donnée,
  // aucune migration).
  //
  // Bug réel trouvé en prod juste après déploiement (2026-08-22, retour utilisateur : "le
  // clavier a un semblant de mouvement puis se referme automatiquement") — memo sur
  // [P, inputStyle] comme copié depuis TimeSelect ci-dessous, mais `inputStyle` est un objet
  // littéral recréé à CHAQUE rendu (jamais lui-même mémoïsé) : sa référence change à chaque
  // frappe, donc useMemo ne mémoïsait en réalité RIEN, recréait ce composant à chaque
  // caractère tapé, React démontait/remontait le <input> DOM et le clavier mobile se
  // refermait aussitôt. TimeSelect a exactement le même défaut mais ne l'a jamais montré
  // (un <select> n'a pas besoin de garder le focus sur plusieurs frappes consécutives comme
  // un champ texte). Corrigé ici en ne dépendant que de `P` — stable d'un rendu à l'autre
  // (THEMES[themeId] renvoie toujours la même référence tant que le thème ne change pas),
  // et `inputStyle`/`themeId` en dérivent tous les deux, donc `P` seul suffit à invalider le
  // cache au bon moment (changement de thème) sans jamais le faire à chaque frappe.
  const StoreIdInput = useMemo(() => function StoreIdInputComp({ value, onChange, onBlur, placeholder, disabled }) {
    const digits = String(value || "").replace(/^N/i, "").replace(/\D/g, "");
    const buildValue = (raw) => "N" + raw.replace(/\D/g, "");
    return (
      <div style={{ ...inputStyle, padding: 0, display: "flex", alignItems: "center", opacity: disabled ? .6 : 1 }}>
        <span style={{ padding: "12px 0 12px 14px", fontWeight: 800, color: P.muted, userSelect: "none" }}>N</span>
        <input
          type="text" inputMode="numeric" autoCapitalize="none" autoComplete="off"
          value={digits} disabled={disabled}
          onChange={(e) => onChange(buildValue(e.target.value))}
          onBlur={onBlur ? (e) => onBlur({ target: { value: buildValue(e.target.value) } }) : undefined}
          placeholder={placeholder}
          style={{
            flex: 1, minWidth: 0, border: "none", outline: "none", background: "none",
            fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(15.5px * var(--nx-font-scale, 1))`, color: P.ink,
            padding: "12px 14px 12px 1px",
          }}
        />
      </div>
    );
  }, [P]);
  // Champ Pro-ID — purement numérique (2026-08-22, retour utilisateur explicite : "le champ
  // ID aussi doit être purement numérique"). Tous les Pro-ID réels de cette app sont des
  // suites de chiffres (17002, 44444, 7001...), jamais de préfixe lettre comme les filiales
  // — pas de badge fixe ici, juste un input filtré aux chiffres avec le clavier numérique.
  // Mémoïsé sur [P] seul, pas [P, inputStyle] — voir le commentaire détaillé au-dessus de
  // StoreIdInput (même bug de clavier qui se referme, même correctif).
  const NumericIdInput = useMemo(() => function NumericIdInputComp({ value, onChange, onKeyDown, placeholder, disabled }) {
    return (
      <input
        style={inputStyle} type="text" inputMode="numeric" autoCapitalize="none" autoComplete="off"
        value={String(value || "")} disabled={disabled} placeholder={placeholder} onKeyDown={onKeyDown}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
      />
    );
  }, [P]);
  // <input type="time" step="900"> ne suffit pas : la roue native iOS Safari/WKWebView
  // ignore `step` et défile toujours minute par minute (limitation WebKit confirmée par
  // test réel) — invisible pour l'utilisatrice tant que le pas de 15 min n'est pas visible
  // PENDANT la sélection elle-même, pas seulement arrondi après coup. Deux
  // <select> natifs (heures 00-23, minutes limitées à 00/15/30/45) remplacent le picker
  // natif sur les champs horaires de Planning : aucune roue à limitation plateforme,
  // le pas de 15 est visible et garanti par la liste d'options elle-même.
  const TimeSelect = useMemo(() => function TimeSelectComp({ value, onChange, minuteOptions }) {
    // minuteOptions — pas par défaut 00/15/30/45 (Planning). Missions réseau passe ["00",
    // "30"] (retour utilisateur explicite, 2026-08-31 : "je ne veux pas des minutes dans
    // l'heure, soit 00 ou des bonds de 30 minutes") — jamais changé ici globalement, ça
    // casserait le pas de 15 attendu par Planning/Tâches/Arbeitszeitliste.
    const opts = minuteOptions || ["00", "15", "30", "45"];
    const parts = (value || "09:00").split(":");
    const h = parts[0] || "09";
    const m = opts.includes(parts[1]) ? parts[1] : opts[0];
    const selStyle = {
      ...inputStyle, width: "auto", flex: 1, textAlign: "center", textAlignLast: "center",
      appearance: "none", WebkitAppearance: "none",
    };
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <select style={selStyle} value={h} onChange={(e) => onChange(`${e.target.value}:${m}`)}>
          {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((hh) => (
            <option key={hh} value={hh}>{hh}</option>
          ))}
        </select>
        <span style={{ fontWeight: 800, color: P.muted, fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>:</span>
        <select style={selStyle} value={m} onChange={(e) => onChange(`${h}:${e.target.value}`)}>
          {opts.map((mm) => <option key={mm} value={mm}>{mm}</option>)}
        </select>
      </div>
    );
  }, [P, inputStyle]);
  /* Field est mémoïsé sur le thème : sans ça, le composant est recréé à
     CHAQUE frappe, React remonte l'input, le focus saute et le clavier
     iOS se referme après chaque lettre (bug corrigé v8.1). */
  const Field = useMemo(() => function FieldComp({ label, children }) {
    return (
      <label style={{ display: "block", marginBottom: 12 }}>
        <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 5 }}>{label}</span>
        {children}
      </label>
    );
  }, [P]);
  // Champ quantité — saisie directe (clavier) ET boutons −/+ (retour utilisateur
  // 2026-08-02 : "je veux que les champs soit remplis par - et + et aussi par saisi").
  // Mémoïsé sur P comme Field, pour la même raison exacte (voir commentaire ci-dessus) :
  // un composant recréé à chaque frappe démonte l'input et referme le clavier iOS.
  // Maintien appuyé = défilement accéléré (retour utilisateur 2026-08-02 : "au lieu
  // d'avoir à tapoter l'écran 23 fois pour entrer 23"), comme un stepper iOS natif —
  // un simple tap fait +1/-1, un appui maintenu accélère progressivement. Passe par un
  // <button> dédié plutôt que par Pressable : Pressable pose déjà ses PROPRES
  // onPointerDown/Up/Leave APRÈS le spread de {...p} dans son JSX, donc les nôtres
  // seraient silencieusement écrasés si on les lui passait en props.
  const HoldButton = useMemo(() => function HoldButtonComp({ onTap, onRepeat, label, children, style, disabled }) {
    const [down, setDown] = useState(false);
    const [flash, setFlash] = useState(false);
    const stateRef = useRef({ timeout: null, interval: null, fired: false });
    const stop = () => {
      clearTimeout(stateRef.current.timeout);
      clearTimeout(stateRef.current.interval);
      stateRef.current.timeout = null; stateRef.current.interval = null;
      setDown(false);
    };
    const start = (e) => {
      e.preventDefault();
      if (disabled) return;
      setDown(true);
      stateRef.current.fired = false;
      // Retour tactile natif (vibration réelle ou tic iOS en secours) + flash — posé ici,
      // au point central, pour couvrir tous les +/- de l'app d'un coup (ne pas le
      // dupliquer dans onTap des sites appelants, qui ne l'appellent plus). Sauté si
      // `disabled`, pour ne jamais faire vibrer/flasher un bouton verrouillé sans effet.
      hapticTap();
      setFlash(false);
      requestAnimationFrame(() => setFlash(true));
      onTap();
      // Délai avant accélération, pour distinguer un tap simple d'un maintien.
      stateRef.current.timeout = setTimeout(() => {
        let delay = 220;
        const tick = () => {
          onRepeat();
          delay = Math.max(30, delay * 0.78); // accélération progressive
          stateRef.current.interval = setTimeout(tick, delay);
        };
        tick();
      }, 380);
    };
    useEffect(() => stop, []);
    return (
      <button
        onPointerDown={start} onPointerUp={stop} onPointerLeave={stop} onPointerCancel={stop}
        aria-label={label}
        className={flash ? "nx-tap-flash" : undefined}
        onAnimationEnd={() => setFlash(false)}
        style={{
          border: "none", cursor: disabled ? "default" : "pointer", fontFamily: `var(--nx-font, ${FONT})`, touchAction: "manipulation",
          transition: "transform .12s cubic-bezier(.34,1.4,.64,1)", transform: down ? "scale(.92)" : "scale(1)",
          ...style,
        }}
      >{children}</button>
    );
  }, []);
  const QtyStepper = useMemo(() => function QtyStepperComp({ value, onChange, step = 1, min = 0 }) {
    const valueRef = useRef(Number(value) || 0);
    valueRef.current = Number(value) || 0;
    const bump = (dir) => { const next = Math.max(min, valueRef.current + dir * step); valueRef.current = next; onChange(String(next)); };
    const btnStyle = { width: 44, height: 44, borderRadius: 14, background: P.soft, border: `1.5px solid ${P.line}`, display: "grid", placeItems: "center", fontSize: `calc(19px * var(--nx-font-scale, 1))`, fontWeight: 800, flex: "0 0 auto", color: P.ink };
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <HoldButton onTap={() => bump(-1)} onRepeat={() => bump(-1)} label={t("common.decreaseHoldAria", "Moins (maintenir pour accélérer)")} style={btnStyle}>−</HoldButton>
        <input
          style={{ width: "100%", minWidth: 0, boxSizing: "border-box", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(15.5px * var(--nx-font-scale, 1))`, color: P.ink, background: P.soft, border: `1.5px solid ${P.line}`, borderRadius: 16, padding: "12px 8px", outline: "none", textAlign: "center" }}
          inputMode="numeric" value={value} onChange={(e) => onChange(e.target.value)}
        />
        <HoldButton onTap={() => bump(1)} onRepeat={() => bump(1)} label={t("common.increaseHoldAria", "Plus (maintenir pour accélérer)")} style={btnStyle}>＋</HoldButton>
      </div>
    );
  }, [P, HoldButton]);
  // Collant en haut de la sheet — utile pour garder kicker/titre/extra visibles en scrollant,
  // mais ne porte plus SON PROPRE ✕ (2026-08-17) : ce bouton en position:sticky pouvait
  // devenir inatteignable sur un contenu assez long (bug WebKit documenté sur Sheet
  // ci-dessus) — la fermeture passe désormais uniquement par le ✕ universel de Sheet
  // lui-même, jamais affecté par le scroll interne. paddingRight réservé pour ne jamais
  // passer sous ce ✕ flottant (position absolute, coin haut-droit de la sheet).
  const SheetHead = ({ kicker, title, onClose, extra }) => (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14, position: "sticky", top: 0, zIndex: 5, background: P.card, paddingBottom: 4, paddingRight: extra ? 44 : 0 }}>
      <div>
        <span style={eyebrow()}>{kicker}</span>
        <strong style={{ display: "block", fontSize: `calc(19px * var(--nx-font-scale, 1))`, marginTop: 2, letterSpacing: "-.02em" }}>{title}</strong>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        {extra}
      </div>
    </div>
  );
  const urgRow = (u) => ({
    red: { background: "rgba(239,68,68,.13)", border: "1px solid rgba(239,68,68,.25)" },
    orange: { background: "rgba(231,138,18,.13)", border: "1px solid rgba(231,138,18,.25)" },
    green: { background: "rgba(30,184,95,.13)", border: "1px solid rgba(30,184,95,.25)" },
  }[u]);

  const EMPLOYEE_TABS = [
    { id: "home", emoji: "🏠", label: t("nav.home", "Accueil"), color: A.blue },
    { id: "control", emoji: "🛡️", label: t("nav.control", "Contrôle"), color: A.frost },
    { id: "reductions", emoji: "🏷️", label: t("nav.reductions", "Réduc."), color: A.purple },
    { id: "dashboard", emoji: "📊", label: t("nav.dashboard", "Dash"), color: A.green },
    { id: "more", emoji: "⚙️", label: t("nav.more", "Plus"), color: A.orange },
  ];
  // Nav du bas dédiée aux grands responsables réels — remplace ENTIÈREMENT la nav employé,
  // jamais un module en plus (voir le retour utilisateur qui a corrigé cette confusion :
  // "cela ne doit pas etre un module mais la vue qu'un grand responsable doit avoir").
  // "netHome" reste id "home" côté vanilla artefact mais renommé ici pour ne jamais
  // collisionner avec le calcul d'activeTabIndex de l'onglet employé.
  const NETWORK_TABS = [
    { id: "netHome", emoji: "🏠", label: t("nav.home", "Accueil"), color: A.blue },
    { id: "filiales", emoji: "🏬", label: t("nav.filiales", "Filiales"), color: A.frost },
    { id: "alertes", emoji: "🚨", label: t("nav.alertes", "Alertes"), color: A.red },
    { id: "missions", emoji: "🎯", label: t("nav.missions", "Missions"), color: A.purple },
    { id: "more", emoji: "⚙️", label: t("nav.more", "Plus"), color: A.orange },
  ];
  const TABS = isNetworkLeader ? NETWORK_TABS : EMPLOYEE_TABS;

  const inRun = screen === "run" && !finish;
  const inControl = ["hub", "mhdHub", "kuhlung", "soon", "run", "store", "newArticles", "newArticlesFF", "delivery", "czEntry", "mhdImport"].includes(screen);

  // Rafraîchissement périodique du catalogue articles + collections dérivées — même patron
  // que Sessions actives/Missions réseau/Profil multi-filiales (poll 15s scopé à l'écran
  // ouvert, jamais un poll global permanent). Retour utilisateur explicite (2026-08-19) :
  // une correction faite par un collègue sur un autre appareil doit apparaître SANS recharger
  // l'app — avant, seule la session qui avait fait la modif se rafraîchissait elle-même.
  useEffect(() => {
    if (!(screen === "articles" || screen === "reductions" || inControl)) return;
    const poll = setInterval(() => {
      refreshArticleDependentLists();
    }, 15000);
    return () => clearInterval(poll);
  }, [screen]);

  /* ============================================================
     SIGNATURE À DISTANCE (?sign=<token>) — retour utilisateur explicite (2026-08-20) : un
     testeur pas physiquement présent (Testkauf/Lieferschein Test) peut demander à la
     personne concernée de signer sur SON PROPRE téléphone, voir requestRemoteSignature plus
     haut. Page PUBLIQUE, montée avant même les gates user/sessionValidating ci-dessous —
     la personne qui signe n'a jamais de compte Nexora, seul le token protège ce lien
     (24 octets aléatoires côté serveur, voir server/signatureRequests/routes.js). Styles
     volontairement autonomes (jamais P/A, qui dépendent du thème d'un compte connecté) —
     cette page n'a aucun utilisateur, donc aucun thème à lire.
     ============================================================ */
  if (publicSignToken) {
    return (
      <div style={{
        fontFamily: FONT, color: "#1a1a1a", background: "#fafafa", height: "calc(var(--nx-vh, 1vh) * 100)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: 24, boxSizing: "border-box", textAlign: "center", overflowY: "auto",
      }}>
        <span style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-.02em", marginBottom: 24, color: "#d4a72c" }}>NEXORA</span>
        <div style={{ width: "100%", maxWidth: 420, background: "#fff", borderRadius: 22, padding: 24, boxShadow: "0 12px 30px rgba(0,0,0,.08)", boxSizing: "border-box" }}>
          {!publicSignData && !publicSignError && (
            <p style={{ fontSize: 14, color: "#888" }}>Chargement...</p>
          )}
          {publicSignError && (
            <>
              <span style={{ fontSize: 34, display: "block", marginBottom: 10 }}>⚠️</span>
              <p style={{ fontSize: 14, fontWeight: 700, color: "#e11" }}>{publicSignError}</p>
            </>
          )}
          {publicSignData && !publicSignError && (() => {
            if (publicSignDone || publicSignData.status === "SIGNED") {
              return (
                <>
                  <span style={{ fontSize: 34, display: "block", marginBottom: 10 }}>✅</span>
                  <strong style={{ display: "block", fontSize: 17, marginBottom: 6 }}>Signature envoyée</strong>
                  <p style={{ fontSize: 13.5, color: "#888", lineHeight: 1.5 }}>Merci ! Ta signature a bien été transmise.</p>
                </>
              );
            }
            if (publicSignData.status === "EXPIRED") {
              return (
                <>
                  <span style={{ fontSize: 34, display: "block", marginBottom: 10 }}>⏱️</span>
                  <strong style={{ display: "block", fontSize: 17, marginBottom: 6 }}>Ce lien a expiré</strong>
                  <p style={{ fontSize: 13.5, color: "#888", lineHeight: 1.5 }}>Demande un nouveau lien à {publicSignData.requestedByName || "la personne qui te l'a envoyé"}.</p>
                </>
              );
            }
            if (publicSignData.status === "CANCELLED") {
              return (
                <>
                  <span style={{ fontSize: 34, display: "block", marginBottom: 10 }}>🚫</span>
                  <strong style={{ display: "block", fontSize: 17 }}>Cette demande a été annulée</strong>
                </>
              );
            }
            return (
              <>
                <strong style={{ display: "block", fontSize: 17, marginBottom: 4 }}>{publicSignData.fieldLabel || "Signature demandée"}</strong>
                <p style={{ fontSize: 13, color: "#888", marginBottom: 18, lineHeight: 1.5 }}>
                  Demandé par {publicSignData.requestedByName || "un responsable"} — filiale {publicSignData.storeId}.
                </p>
                <div ref={publicSigMountRef} style={{ marginBottom: 16 }} />
                <button
                  onClick={submitPublicSignature}
                  disabled={!publicSigReady || publicSignSubmitting}
                  style={{
                    width: "100%", padding: "15px 0", borderRadius: 99, fontWeight: 800, fontSize: 15, border: "none",
                    background: publicSigReady ? "#364fe7" : "#ddd", color: "#fff",
                    opacity: publicSignSubmitting ? .6 : 1,
                  }}
                >
                  {publicSignSubmitting ? "Envoi..." : "Valider ma signature"}
                </button>
              </>
            );
          })()}
        </div>
      </div>
    );
  }

  /* ============================================================
     REVALIDATION DE SESSION (voir sessionValidating tout en haut) — jamais l'app
     authentifiée tant qu'un utilisateur restauré du cache n'a pas été reconfirmé par le
     serveur. Écran neutre, sans aucun contenu dépendant du rôle (pas de nav, pas de nom
     de module) — c'est précisément ce qu'un visiteur non authentifié doit voir sur un
     appareil/navigateur avec un cache localStorage périmé, jamais l'interface réelle.
     ============================================================ */
  if (user && sessionValidating) {
    return (
      <div style={{
        fontFamily: `var(--nx-font, ${FONT})`, color: P.ink, background: P.bg, height: "calc(var(--nx-vh, 1vh) * 100)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <span style={{ fontSize: `calc(22px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: "-.02em", opacity: .55 }}>NEXORA</span>
      </div>
    );
  }

  /* ============================================================
     ÉCRAN DE CONNEXION (plein écran, sans tab bar)
     ============================================================ */
  if (!user) {
    // Refonte structurelle "premium" (retour utilisateur explicite, 2026-08-27, capture
    // annotée du bouton Face ID/carte plate) — jamais une simple micro-animation posée
    // par-dessus l'existant (voir [[feedback_visual_redesign_vs_animation]]) : vrai fond
    // ambiant en dégradé (3 halos radiaux, un par mot de VERWALTEN·KONTROLLIEREN·LEISTEN),
    // carte de connexion élevée avec ombre teintée au lieu du `card` plat générique
    // utilisé partout ailleurs dans l'app, halo lumineux derrière le wordmark.
    const authCard = {
      background: P.card, border: `1px solid ${P.line}`, borderRadius: 28,
      boxShadow: `0 28px 64px -20px ${A.blue}40, 0 10px 30px rgba(20,24,38,.10), ${P.shadow}`,
      color: P.ink,
    };
    return (
      <div style={{
        fontFamily: `var(--nx-font, ${FONT})`, color: P.ink,
        background: P.bg,
        height: "calc(var(--nx-vh, 1vh) * 100)",
        // Vue PC (retour utilisateur 2026-08-15, capture d'écran) : cet écran n'a JAMAIS de
        // nav latérale (ni avant ni après connexion, "plein écran, sans tab bar") — reprendre
        // ici le maxWidth:1100/marginLeft:SIDEBAR_WIDTH du shell connecté (qui réserve, lui,
        // la vraie place de sa nav fixe) décalait la carte de connexion vers la gauche sans
        // raison et l'étirait sur 1100px de large. Carte de connexion = largeur de carte
        // desktop raisonnable (comme un Sheet centré, voir isWide dans le composant Sheet),
        // toujours vraiment centrée par margin auto — jamais de décalage sidebar fantôme.
        maxWidth: isWide ? 460 : 520, marginLeft: "auto", marginRight: "auto",
        display: "flex", flexDirection: "column",
        transition: "background .35s ease", overflowX: "hidden", overflowY: "auto", position: "relative",
      }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700;800&display=swap');
          /* Réglages d'affichage (taille/police/gras, 2026-08-17) — --nx-font/--nx-font-scale
             posées en JS sur <html> (voir applyDisplaySettings), valeurs par défaut ici pour
             éviter tout flash avant le premier effet. "Gras" via text-stroke/text-shadow plutôt
             que fontWeight : marche même sur du texte dont le fontWeight est déjà fixé en dur
             inline (des milliers d'occurrences dans l'app), sans avoir à toucher chacune. */
          :root { --nx-font-scale: 1; --nx-font: ${FONT}; }
          html[data-bold-text="true"] * { -webkit-text-stroke: 0.35px currentColor; text-shadow: 0 0 0.2px currentColor; }
          html, body { height: 100%; overflow: hidden; overscroll-behavior-x: none; touch-action: pan-x pan-y; }
          * { scrollbar-width: none; -ms-overflow-style: none; }
          *::-webkit-scrollbar { display: none; width: 0; height: 0; }
          @keyframes nxToastIn { from { opacity: 0; transform: translate(-50%, 10px); } to { opacity: 1; transform: translate(-50%, 0); } }
          @keyframes nxLive { 0%,100% { transform: scale(1); opacity: .9; } 50% { transform: scale(1.4); opacity: .4; } }
          @keyframes nxLogoIn { from { opacity: 0; transform: translateY(10px) scale(.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
          @keyframes nxGradientFlow { 0% { background-position: 0% 50%; } 100% { background-position: -300% 50%; } }
          @keyframes nxBrandFlow { 0% { background-position: 0% 50%; } 100% { background-position: -400% 50%; } }
          .nx-gradient-text {
            background: linear-gradient(90deg, #2d7ff0, #7c5cf2, #b13df0, #0d9488, #2d7ff0);
            background-size: 300% 100%;
            -webkit-background-clip: text; background-clip: text;
            color: transparent; -webkit-text-fill-color: transparent;
            animation: nxGradientFlow 7s linear infinite;
          }
          .nx-brand-text {
            background: linear-gradient(90deg, #364fe7, #8b2fd6, #ef4444, #e78a12, #1eb85f, #0d9488, #364fe7);
            background-size: 400% 100%;
            -webkit-background-clip: text; background-clip: text;
            color: transparent; -webkit-text-fill-color: transparent;
            animation: nxBrandFlow 8s linear infinite;
          }
          input:focus { border-color: ${A.blue} !important; box-shadow: 0 0 0 4px rgba(54,79,231,.14); }
          * { -webkit-tap-highlight-color: transparent; }
          * { -webkit-user-select: none; -moz-user-select: none; user-select: none; -webkit-touch-callout: none; }
          input, textarea, [contenteditable="true"] { -webkit-user-select: text; -moz-user-select: text; user-select: text; -webkit-touch-callout: default; }
          @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important; } }
        `}</style>

        {/* Réglages (thème/langue) — retirés du flux principal, derrière cette icône, pour
            que l'écran de connexion ne montre que l'essentiel (retour utilisateur explicite,
            2026-08-19 : le sélecteur de 7 thèmes "ressemble à un panneau développeur"). */}
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "14px 18px 0" }}>
          <Pressable onClick={() => setSheet("authSettings")} aria-label={t("auth.settingsAria", "Réglages d'affichage")} style={{
            width: 38, height: 38, borderRadius: 99, background: P.card, border: `1px solid ${P.line}`,
            display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`, boxShadow: P.shadow,
          }}>⚙️</Pressable>
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: "8px 24px 24px" }}>
          {/* Logo Nexora — fidèle à la vraie capture d'écran fournie par
              l'utilisateur : wordmark seul (pas d'icône séparée), dégradé
              vert→or, sous-titre allemand (identité de marque figée, comme
              beaucoup de marques gardent leur slogan dans la langue
              d'origine quelle que soit la langue de l'interface). */}
          <div style={{ textAlign: "center", marginBottom: 34, animation: "nxLogoIn .5s ease" }}>
            <h1 className="nx-brand-text" style={{
              margin: 0, fontSize: `calc(42px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: ".03em",
            }}>NEXORA</h1>
            <p style={{ margin: "9px 0 0", fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: ".03em" }}>
              <span style={{ color: A.blue }}>VERWALTEN</span> <span style={{ color: P.muted, fontWeight: 400 }}>·</span>{" "}
              <span style={{ color: A.green }}>KONTROLLIEREN</span> <span style={{ color: P.muted, fontWeight: 400 }}>·</span>{" "}
              <span style={{ color: A.orange }}>LEISTEN</span>
            </p>
          </div>

          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <h2 style={{ margin: "0 0 8px", fontSize: `calc(27px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: "-.02em" }}>{t("auth.title", "Connexion")}</h2>
            <p style={{ margin: "0 auto", maxWidth: 300, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
              {t("auth.subtitle", "Accède à ton espace magasin, tes contrôles MAD et tes actions du jour.")}
            </p>
          </div>

          {authScreen === "start" && (
            <div style={{ animation: "nxLogoIn .5s ease .08s both" }}>
              <Pressable onClick={() => setAuthScreen("login")} style={{
                width: "100%", textAlign: "center", padding: "16px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(15.5px * var(--nx-font-scale, 1))`, color: "#fff", marginBottom: 11,
                background: `linear-gradient(135deg, ${A.blue}, #4f6bf5)`,
                boxShadow: `0 10px 26px ${A.blue}4d, 0 3px 8px rgba(0,0,0,.12)`,
              }}>{t("auth.loginButton", "Se connecter")}</Pressable>
              <Pressable onClick={() => { setRegisterMsg(""); setAuthScreen("register"); }} style={{
                width: "100%", textAlign: "center", padding: "15px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
                background: P.card, border: `1.5px solid ${P.line}`, color: P.ink, marginBottom: 11, boxShadow: P.shadow,
              }}>{t("auth.registerButton", "Créer mon profil")}</Pressable>
              <Pressable onClick={openForgotPassword} style={{
                width: "100%", textAlign: "center", padding: "10px 0", fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: A.blue, marginBottom: 11,
              }}>{t("auth.forgotPassword", "Mot de passe oublié ?")}</Pressable>
              {/* Accessible à froid, jamais seulement juste après une inscription — voir
                  statusCheckProId/checkRegistrationStatus plus haut (2026-08-20). */}
              <Pressable onClick={() => { setRegisterPendingText(""); setStatusCheckResult(null); setStatusCheckMsg(""); setAuthScreen("registerPending"); }} style={{
                width: "100%", textAlign: "center", padding: "10px 0", fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: A.blue, marginBottom: 11,
              }}>{t("auth.trackRequestLink", "Suivre ma demande d'inscription")}</Pressable>

              <p style={{ textAlign: "center", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "18px 0 0" }}>
                {t("auth.secureFooter", "Mode filiale sécurisé · Auth Nexora")} · <span style={{ fontWeight: 800 }}>{lang.toUpperCase()}</span>
              </p>
            </div>
          )}

          {authScreen === "login" && (
            <div style={{ ...authCard, padding: 22, animation: "nxLogoIn .4s ease" }}>
              <strong style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", display: "block", marginBottom: 3 }}>{t("auth.title", "Connexion")}</strong>
              <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginBottom: 15 }}>
                {t("auth.loginFormSubtitle", "Ton Pro-ID et ton mot de passe personnel.")}
              </span>
              {/* Face ID/Touch ID/Windows Hello (retour utilisateur explicite, 2026-08-18)
                  — visible uniquement si CET appareil a déjà un enregistrement réussi
                  (webauthnHint), jamais un bouton mort pour tout le monde. Le formulaire
                  classique reste toujours juste en dessous, jamais remplacé. */}
              {webauthnHint && webauthnAvailable && (
                <>
                  {/* Badge circulaire doré (même or que le wordmark NEXORA de l'écran de
                      lancement, #d4a72c — jamais une couleur inventée) + fond dégradé navy
                      profond au lieu du navy plat précédent (retour utilisateur explicite,
                      2026-08-27 : "il n'est pas premium comme je le souhaite", capture
                      annotée pointant précisément ce bouton). */}
                  <Pressable onClick={doWebauthnLogin} style={{
                    width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                    padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: "#fff",
                    background: `linear-gradient(135deg, ${A.navy}, #1c2b52)`,
                    boxShadow: `0 16px 34px -12px rgba(13,23,48,.55), 0 4px 10px rgba(0,0,0,.15)`,
                    marginBottom: 14, opacity: webauthnBusy ? .6 : 1,
                  }}>
                    <span style={{
                      width: 25, height: 25, borderRadius: "50%", flexShrink: 0, display: "grid", placeItems: "center",
                      background: "linear-gradient(135deg, #f3d585, #d4a72c)", fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                      boxShadow: "0 2px 6px rgba(212,167,44,.5)",
                    }}>🔐</span>
                    {webauthnBusy ? t("auth.webauthn.loginBusy", "Vérification...") : t("auth.webauthn.loginButtonTemplate", { name: webauthnHint.login }, `Se connecter avec Face ID (${webauthnHint.login})`)}
                  </Pressable>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                    <div style={{ flex: 1, height: 1, background: P.line }} />
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".05em" }}>{t("common.or", "ou")}</span>
                    <div style={{ flex: 1, height: 1, background: P.line }} />
                  </div>
                </>
              )}
              <Field label={t("auth.proIdLabel", "Pro-ID")}>
                <NumericIdInput value={loginId} placeholder={t("auth.proIdPlaceholderExample", "ex. 12345")}
                  onChange={setLoginId} />
              </Field>
              {/* Retour utilisateur 2026-08-05 : un même Pro-ID peut exister dans deux
                  filiales différentes (§6, voir /api/auth/register) — sans la filiale,
                  /api/login prend le premier compte trouvé, potentiellement le mauvais.
                  Optionnelle (un compte réseau n'a pas une seule filiale fixe) mais
                  départage le doublon dès qu'elle est renseignée. */}
              <Field label={t("auth.mainStoreLabel", "Filiale principale")}>
                <StoreIdInput value={loginBranchId} placeholder={t("auth.branchIdPlaceholderExample", "Numéro")}
                  onChange={setLoginBranchId}
                  onBlur={(e) => sanitizeBranchOnBlur(e.target.value, setLoginBranchId)} />
                {/* Retour utilisateur explicite (2026-08-06) : ce champ est déjà facultatif
                    (voir commentaire ci-dessus), mais rien ne le signalait visuellement — un
                    Bereichsleiter/Verkaufsleiter/Niederlassungsleiter (plusieurs filiales,
                    aucune "principale") pensait devoir le remplir. */}
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 5 }}>
                  {t("auth.loginStoreHelp", "Facultatif — laisse vide si tu gères plusieurs filiales (Bereichsleiter, Verkaufsleiter, Niederlassungsleiter).")}
                </span>
              </Field>
              <Field label={t("auth.passwordLabel", "Mot de passe")}>
                <PasswordInput style={inputStyle} P={P} t={t} value={loginPw} placeholder="••••••••"
                  onChange={(e) => setLoginPw(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") doLogin(); }} />
              </Field>
              {loginMsg && <p style={{ margin: "0 0 11px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.red }}>{loginMsg}</p>}
              <Pressable onClick={doLogin} style={{ width: "100%", ...btnSolid(A.blue), padding: "15px 0", fontSize: `calc(15px * var(--nx-font-scale, 1))`, opacity: loggingIn ? .6 : 1 }}>
                {loggingIn ? t("auth.loginButtonLoading", "Connexion...") : t("auth.loginButton", "Se connecter")}
              </Pressable>
              <Pressable onClick={() => { setAuthScreen("start"); setLoginMsg(""); sanitizeBranchOnBlur(loginBranchId, setLoginBranchId); }} style={{
                width: "100%", textAlign: "center", padding: "12px 0", fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 4,
              }}>← {t("common.back", "Retour")}</Pressable>
            </div>
          )}

          {authScreen === "forgotPassword" && (
            <div style={{ ...authCard, padding: 22, animation: "nxLogoIn .4s ease" }}>
              <strong style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", display: "block", marginBottom: 3 }}>{t("auth.forgotPassword", "Mot de passe oublié ?")}</strong>
              {fpStep === "request" ? (
                <>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginBottom: 15, lineHeight: 1.5 }}>
                    {t("auth.forgotPw.requestSubtitle", "Un responsable de ta filiale te communiquera un code en personne pour choisir un nouveau mot de passe.")}
                  </span>
                  <Field label={t("auth.proIdLabel", "Pro-ID")}>
                    <NumericIdInput value={fpProId} placeholder={t("auth.proIdPlaceholderExample", "ex. 12345")}
                      onChange={setFpProId}
                      onKeyDown={(e) => { if (e.key === "Enter") submitResetRequest(); }} />
                  </Field>
                  {/* Retour utilisateur 2026-08-05 : un même Pro-ID peut exister dans deux
                      filiales différentes (§6) — sans la filiale, la demande pourrait viser
                      le mauvais compte. Optionnelle (un compte réseau n'a pas une seule
                      filiale) mais fortement conseillée dès qu'un doublon existe. */}
                  <Field label={t("auth.mainStoreLabel", "Filiale principale")}>
                    <StoreIdInput value={fpBranchId} placeholder={t("auth.branchIdPlaceholderExample", "Numéro")}
                      onChange={setFpBranchId}
                      onBlur={(e) => sanitizeBranchOnBlur(e.target.value, setFpBranchId)} />
                  </Field>
                  {fpMsg && <p style={{ margin: "0 0 11px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: fpMsgType === "info" ? A.blue : A.red }}>{fpMsg}</p>}
                  <Pressable onClick={submitResetRequest} style={{ width: "100%", ...btnSolid(A.blue), padding: "15px 0", fontSize: `calc(15px * var(--nx-font-scale, 1))`, opacity: fpBusy ? .6 : 1 }}>
                    {fpBusy ? t("common.sending", "Envoi...") : t("auth.forgotPw.sendRequestBtn", "Envoyer la demande")}
                  </Pressable>
                  <Pressable onClick={() => { setFpStep("confirm"); setFpMsg(""); setFpMsgType("error"); }} style={{
                    width: "100%", textAlign: "center", padding: "12px 0", fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: A.blue, marginTop: 4,
                  }}>{t("auth.forgotPw.haveCodeLink", "J'ai déjà un code")}</Pressable>
                </>
              ) : (
                <>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginBottom: 15, lineHeight: 1.5 }}>
                    {t("auth.forgotPw.confirmSubtitle", "Saisis le code communiqué en personne par ton responsable, et ton nouveau mot de passe.")}
                  </span>
                  {/* Retour utilisateur explicite (2026-08-20) : rien n'indiquait jusqu'ici
                      que le code expire — la personne pouvait le recevoir en retard sans
                      savoir qu'il fallait l'utiliser tout de suite. */}
                  <p style={{ margin: "-10px 0 15px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: A.orange, fontWeight: 700, lineHeight: 1.4 }}>
                    ⏱️ {t("auth.forgotPw.codeExpiryNote", "Ce code est valable 30 minutes une fois reçu — utilise-le rapidement.")}
                  </p>
                  <Field label={t("auth.proIdLabel", "Pro-ID")}>
                    <NumericIdInput value={fpProId} placeholder={t("auth.proIdPlaceholderExample", "ex. 12345")}
                      onChange={setFpProId} />
                  </Field>
                  <Field label={t("auth.forgotPw.codeLabel", "Code reçu")}>
                    <input style={inputStyle} value={fpCode} inputMode="numeric" placeholder="123456"
                      onChange={(e) => setFpCode(e.target.value)} />
                  </Field>
                  <Field label={t("auth.forgotPw.newPasswordLabel", "Nouveau mot de passe")}>
                    <PasswordInput style={inputStyle} P={P} t={t} value={fpNew1} placeholder="••••••••"
                      onChange={(e) => setFpNew1(e.target.value)} />
                  </Field>
                  <Field label={t("settings.password.confirm", "Confirmer")}>
                    <PasswordInput style={inputStyle} P={P} t={t} value={fpNew2} placeholder="••••••••"
                      onChange={(e) => setFpNew2(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") submitResetConfirm(); }} />
                  </Field>
                  {fpMsg && <p style={{ margin: "0 0 11px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: fpMsgType === "info" ? A.blue : A.red }}>{fpMsg}</p>}
                  <Pressable onClick={submitResetConfirm} style={{ width: "100%", ...btnSolid(A.blue), padding: "15px 0", fontSize: `calc(15px * var(--nx-font-scale, 1))`, opacity: fpBusy ? .6 : 1 }}>
                    {fpBusy ? t("auth.loginButtonLoading", "Connexion...") : t("auth.forgotPw.confirmBtn", "Choisir ce mot de passe")}
                  </Pressable>
                  <Pressable onClick={() => { setFpStep("request"); setFpMsg(""); setFpMsgType("error"); }} style={{
                    width: "100%", textAlign: "center", padding: "12px 0", fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 4,
                  }}>← {t("common.back", "Retour")}</Pressable>
                </>
              )}
              <Pressable onClick={() => { setAuthScreen("start"); setFpMsg(""); }} style={{
                width: "100%", textAlign: "center", padding: "8px 0", fontWeight: 700, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 4,
              }}>{t("common.cancel", "Annuler")}</Pressable>
            </div>
          )}

          {authScreen === "register" && (
            <div style={{ ...authCard, padding: 22, animation: "nxLogoIn .4s ease" }}>
              <strong style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", display: "block", marginBottom: 3 }}>{t("auth.registerButton", "Créer mon profil")}</strong>
              <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginBottom: 15 }}>
                {t("auth.registerFormSubtitle", "Ton compte sera en attente de validation par un responsable avant que tu puisses te connecter.")}
              </span>
              <Field label={t("auth.mainStoreLabel", "Filiale principale")}>
                <StoreIdInput value={registerForm.branchId} placeholder={t("auth.branchIdPlaceholderExample", "Numéro")}
                  disabled={registerForm.isNetwork}
                  onChange={(v) => setRegisterForm((f) => ({ ...f, branchId: v }))} />
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 5 }}>
                  {t("auth.storeHelp", "Choisis ta filiale dans la liste — introuvable ? Contacte un administrateur.")}
                </span>
              </Field>
              <Pressable onClick={() => setRegisterForm((f) => ({ ...f, isNetwork: !f.isNetwork, isMinor: f.isNetwork ? f.isMinor : false }))} style={{
                display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "9px 2px", marginBottom: 11,
              }}>
                <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{registerForm.isNetwork ? "☑️" : "⬜️"}</span>
                <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, flex: 1, textAlign: "left" }}>{t("auth.networkManagerCheckbox", "Responsable réseau / Multifiliale")}</span>
              </Pressable>
              <Field label={t("auth.proIdFullLabel", "Identifiant professionnel")}>
                <NumericIdInput value={registerForm.proId}
                  onChange={(v) => setRegisterForm((f) => ({ ...f, proId: v }))} />
              </Field>
              <Field label={t("auth.firstnameLabel", "Prénom")}>
                <input style={inputStyle} value={registerForm.firstname}
                  onChange={(e) => setRegisterForm((f) => ({ ...f, firstname: e.target.value }))} />
              </Field>
              <Field label={t("auth.lastnameLabel", "Nom")}>
                <input style={inputStyle} value={registerForm.lastname}
                  onChange={(e) => setRegisterForm((f) => ({ ...f, lastname: e.target.value }))} />
              </Field>
              <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>{t("auth.genderLabel", "Sexe")}</span>
              <div style={{ display: "flex", gap: 8, marginBottom: 11 }}>
                {[{ id: "homme", l: t("auth.genderMale", "Homme") }, { id: "femme", l: t("auth.genderFemale", "Femme") }].map((g) => (
                  <Pressable key={g.id} onClick={() => setRegisterForm((f) => ({ ...f, gender: g.id }))} style={{
                    flex: 1, textAlign: "center", padding: "11px 0", borderRadius: 14, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                    background: registerForm.gender === g.id ? A.blue : P.soft, color: registerForm.gender === g.id ? "#fff" : P.ink,
                  }}>{g.l}</Pressable>
                ))}
              </div>
              {/* Masqué pour un compte réseau — retour utilisateur explicite (2026-08-06) :
                  "aucun GR n'a moins de 18 ans donc ce champ est complètement inutile" pour
                  un Bereichsleiter/Verkaufsleiter/Niederlassungsleiter. Remis à false au
                  moment même où isNetwork passe à true (voir la case à cocher au-dessus),
                  jamais soumis avec une valeur périmée. */}
              {!registerForm.isNetwork && (
                <Pressable onClick={() => setRegisterForm((f) => ({ ...f, isMinor: !f.isMinor }))} style={{
                  display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "9px 2px", marginBottom: 11,
                }}>
                  <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{registerForm.isMinor ? "☑️" : "⬜️"}</span>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, flex: 1, textAlign: "left" }}>{t("auth.minorCheckbox", "J'ai moins de 18 ans")}</span>
                </Pressable>
              )}
              <Field label={t("auth.passwordLabel", "Mot de passe")}>
                <PasswordInput style={inputStyle} P={P} t={t} value={registerForm.password} placeholder={t("auth.passwordPlaceholder", "8 caractères minimum")}
                  onChange={(e) => setRegisterForm((f) => ({ ...f, password: e.target.value }))} />
              </Field>
              <Field label={t("auth.confirmPasswordLabel", "Confirmer le mot de passe")}>
                <PasswordInput style={inputStyle} P={P} t={t} value={registerForm.confirmPassword}
                  onChange={(e) => setRegisterForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") submitRegister(); }} />
              </Field>
              {registerMsg && <p style={{ margin: "0 0 11px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.red }}>{registerMsg}</p>}
              <Pressable onClick={submitRegister} style={{ width: "100%", ...btnSolid(A.blue), padding: "15px 0", fontSize: `calc(15px * var(--nx-font-scale, 1))`, opacity: registering ? .6 : 1 }}>
                {registering ? t("auth.registerButtonLoading", "Envoi...") : t("auth.registerButton", "Créer mon profil")}
              </Pressable>
              <Pressable onClick={() => { setAuthScreen("start"); setRegisterMsg(""); }} style={{
                width: "100%", textAlign: "center", padding: "12px 0", fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 4,
              }}>← {t("common.back", "Retour")}</Pressable>
            </div>
          )}

          {authScreen === "registerPending" && (
            <div style={{ ...authCard, padding: 22, animation: "nxLogoIn .4s ease" }}>
              {/* Ce bloc ne s'affiche que juste après une inscription réussie dans CETTE
                  session (registerPendingText vidé au retour à "start" ci-dessous) — le
                  vérificateur de statut en dessous, lui, reste accessible à froid n'importe
                  quand (voir le lien depuis l'écran "start"). */}
              {registerPendingText && (
                <div style={{ textAlign: "center", marginBottom: 18 }}>
                  <span style={{ fontSize: `calc(30px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 10 }}>⏳</span>
                  <p style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, lineHeight: 1.5, margin: 0 }}>{registerPendingText}</p>
                </div>
              )}
              <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", display: "block", marginBottom: 3 }}>{t("auth.statusCheck.title", "Suivre ma demande")}</strong>
              <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginBottom: 15, lineHeight: 1.5 }}>
                {t("auth.statusCheck.subtitle", "Reviens ici à tout moment pour savoir si ton compte a été validé.")}
              </span>
              <Field label={t("auth.proIdLabel", "Pro-ID")}>
                <NumericIdInput value={statusCheckProId} placeholder={t("auth.proIdPlaceholderExample", "ex. 12345")}
                  onChange={setStatusCheckProId}
                  onKeyDown={(e) => { if (e.key === "Enter") checkRegistrationStatus(); }} />
              </Field>
              <Field label={t("auth.mainStoreLabel", "Filiale principale")}>
                <StoreIdInput value={statusCheckBranchId} placeholder={t("auth.branchIdPlaceholderExample", "Numéro")}
                  onChange={setStatusCheckBranchId} />
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 5 }}>
                  {t("auth.loginStoreHelp", "Facultatif — laisse vide si tu gères plusieurs filiales (Bereichsleiter, Verkaufsleiter, Niederlassungsleiter).")}
                </span>
              </Field>
              {statusCheckMsg && <p style={{ margin: "0 0 11px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.red }}>{statusCheckMsg}</p>}
              {statusCheckResult && (() => {
                const meta = {
                  PENDING_APPROVAL: { emoji: "⏳", color: P.muted, bg: P.soft, text: t("auth.statusCheck.pending", "Toujours en attente de validation par un responsable.") },
                  ACTIVE: { emoji: "✅", color: A.green, bg: "rgba(30,184,95,.1)", text: t("auth.statusCheck.active", "Ton compte est validé — tu peux te connecter.") },
                  REJECTED: { emoji: "⛔", color: A.red, bg: "rgba(239,68,68,.1)", text: t("auth.statusCheck.rejected", "Cette demande n'a pas été validée. Contacte un responsable pour en savoir plus.") },
                  NOT_FOUND: { emoji: "❓", color: A.red, bg: "rgba(239,68,68,.1)", text: t("auth.statusCheck.notFound", "Aucune demande trouvée avec ces informations. Vérifie ton Pro-ID et ta filiale.") },
                }[statusCheckResult] || null;
                if (!meta) return null;
                return (
                  <div style={{ borderRadius: 16, padding: 14, marginBottom: 14, background: meta.bg, display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: `calc(20px * var(--nx-font-scale, 1))`}}>{meta.emoji}</span>
                    <span style={{ flex: 1, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: meta.color, lineHeight: 1.4 }}>{meta.text}</span>
                  </div>
                );
              })()}
              {statusCheckResult === "ACTIVE" ? (
                <Pressable onClick={() => {
                  setLoginId(statusCheckProId); setLoginBranchId(statusCheckBranchId);
                  setAuthScreen("login"); setLoginMsg("");
                }} style={{ width: "100%", ...btnSolid(A.blue), padding: "15px 0", fontSize: `calc(15px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>
                  {t("auth.loginButton", "Se connecter")}
                </Pressable>
              ) : (
                <Pressable onClick={checkRegistrationStatus} style={{ width: "100%", ...btnSolid(A.blue), padding: "15px 0", fontSize: `calc(15px * var(--nx-font-scale, 1))`, opacity: statusCheckBusy ? .6 : 1, marginBottom: 8 }}>
                  {statusCheckBusy ? t("auth.statusCheck.checking", "Vérification...") : t("auth.statusCheck.checkBtn", "Vérifier mon statut")}
                </Pressable>
              )}
              <Pressable onClick={() => { setAuthScreen("start"); setRegisterPendingText(""); setStatusCheckResult(null); setStatusCheckMsg(""); }} style={{
                width: "100%", textAlign: "center", padding: "12px 0", fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 4,
              }}>← {t("common.back", "Retour")}</Pressable>
            </div>
          )}

          {authScreen === "initAdmin" && (
            <div style={{ ...authCard, padding: 22, animation: "nxLogoIn .4s ease" }}>
              <strong style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", display: "block", marginBottom: 3 }}>{t("auth.initAdminTitle", "Configurer l'administrateur")}</strong>
              <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginBottom: 15, lineHeight: 1.5 }}>
                {t("auth.initAdminSubtitle", "Aucun administrateur Nexora n'existe encore. Configure le premier compte — cette étape ne pourra plus être refaite ensuite.")}
              </span>
              <Field label={t("auth.proIdFullLabel", "Identifiant professionnel")}>
                <NumericIdInput value={initAdminForm.proId}
                  onChange={(v) => setInitAdminForm((f) => ({ ...f, proId: v }))} />
              </Field>
              <Field label={t("auth.firstnameLabel", "Prénom")}>
                <input style={inputStyle} value={initAdminForm.firstname}
                  onChange={(e) => setInitAdminForm((f) => ({ ...f, firstname: e.target.value }))} />
              </Field>
              <Field label={t("auth.lastnameLabel", "Nom")}>
                <input style={inputStyle} value={initAdminForm.lastname}
                  onChange={(e) => setInitAdminForm((f) => ({ ...f, lastname: e.target.value }))} />
              </Field>
              <Field label={t("auth.mainStoreLabel", "Filiale principale")}>
                <StoreIdInput value={initAdminForm.storeId} placeholder="Numéro"
                  onChange={(v) => setInitAdminForm((f) => ({ ...f, storeId: v }))} />
              </Field>
              <Field label={t("auth.passwordLabel", "Mot de passe")}>
                <PasswordInput style={inputStyle} P={P} t={t} value={initAdminForm.password} placeholder={t("auth.passwordPlaceholder", "8 caractères minimum")}
                  onChange={(e) => setInitAdminForm((f) => ({ ...f, password: e.target.value }))} />
              </Field>
              <Field label={t("auth.confirmPasswordLabel", "Confirmer le mot de passe")}>
                <PasswordInput style={inputStyle} P={P} t={t} value={initAdminForm.confirmPassword}
                  onChange={(e) => setInitAdminForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") submitInitAdmin(); }} />
              </Field>
              {initAdminMsg && <p style={{ margin: "0 0 11px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.red }}>{initAdminMsg}</p>}
              <Pressable onClick={submitInitAdmin} style={{ width: "100%", ...btnSolid(A.blue), padding: "15px 0", fontSize: `calc(15px * var(--nx-font-scale, 1))`, opacity: initAdminBusy ? .6 : 1 }}>
                {initAdminBusy ? t("auth.initAdminButtonLoading", "Création...") : t("auth.initAdminTitle", "Configurer l'administrateur")}
              </Pressable>
              <Pressable onClick={() => setAuthScreen("start")} style={{
                width: "100%", textAlign: "center", padding: "12px 0", fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 4,
              }}>← {t("common.back", "Retour")}</Pressable>
            </div>
          )}
        </div>

        {toast && (
          <div style={{
            position: "fixed", bottom: 60, left: "50%", transform: "translateX(-50%)", zIndex: 60,
            background: themeId === "sombre" ? "#26262a" : A.navy, color: "#fff", padding: "11px 19px",
            borderRadius: 99, fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 600, boxShadow: P.deepShadow,
            whiteSpace: "nowrap", maxWidth: "88vw", overflow: "hidden", textOverflow: "ellipsis",
            animation: "nxToastIn .32s cubic-bezier(.34,1.4,.64,1)",
          }}>{toast}</div>
        )}

        <Sheet open={sheet === "authSettings"} onClose={() => setSheet(null)} P={P}>
          <SheetHead kicker={t("auth.settingsKicker", "Affichage")} title={t("auth.settingsTitle", "Réglages d'affichage")} onClose={() => setSheet(null)} />
          <p style={{ margin: "0 0 18px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("auth.settingsSubtitle", "Thème et langue — mémorisés sur cet appareil.")}</p>

          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: P.muted, display: "block", marginBottom: 8 }}>{t("auth.appearanceLabel", "Apparence")}</span>
          {/* Thème — fidèle à la capture d'origine : pastilles textuelles, y compris
              "Système" (suit l'appareil) et "Nuit" (noir OLED distinct de Sombre). */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 22 }}>
            <Pressable onClick={pickSystemTheme} style={{
              flex: "1 1 76px", minWidth: 0, textAlign: "center", padding: "9px 2px", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
              background: useSystemTheme ? A.blue : P.soft, color: useSystemTheme ? "#fff" : P.muted,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{t("auth.systemTheme", "Système")}</Pressable>
            {THEME_ORDER.map((id) => {
              const th = THEMES[id];
              const active = !useSystemTheme && id === themeId;
              return (
                <Pressable key={id} onClick={() => pickTheme(id)} style={{
                  flex: "1 1 76px", minWidth: 0, textAlign: "center", padding: "9px 2px", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                  background: active ? A.blue : P.soft, color: active ? "#fff" : P.muted,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{t(`theme.${id}`, th.name)}</Pressable>
              );
            })}
          </div>

          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: P.muted, display: "block", marginBottom: 8 }}>{t("auth.languageLabel", "Langue")}</span>
          {/* Langue — pastilles rondes, celle sélectionnée nettement plus grande
              (hiérarchie visuelle fidèle à la capture d'origine). */}
          <div style={{ display: "flex", gap: 9, justifyContent: "center" }}>
            {LANGS.map((l) => {
              const active = lang === l.id;
              return (
                <Pressable key={l.id} onClick={() => changeLang(l.id)}
                  style={{
                    width: active ? 58 : 48, height: active ? 58 : 48, borderRadius: 99,
                    display: "grid", placeItems: "center", fontWeight: 800, fontSize: `calc(${(active ? 14.5 : 12.5)}px * var(--nx-font-scale, 1))`,
                    color: active ? "#fff" : P.muted, background: active ? A.blue : P.soft,
                    boxShadow: active ? `0 8px 18px ${A.blue}4d` : "none", transition: "width .2s ease, height .2s ease",
                  }}>{l.id.toUpperCase()}</Pressable>
              );
            })}
          </div>
        </Sheet>
      </div>
    );
  }

  // Gate obligatoire — un utilisateur connecté avec au moins une information "obligatoire"
  // non signée ne voit RIEN d'autre tant qu'il n'a pas signé chacune (retour utilisateur
  // explicite : "un utilisateur n'a acces a l'app tant qu'il n'a pas lu et signe le message
  // important"). Early return AVANT le rendu de l'app connectée — jamais un simple overlay
  // qu'un clic sur la nav du bas pourrait contourner. gateItems===null = vérification en
  // cours (rien affiché, ni bloqué ni laissé passer, pour éviter un flash de l'app avant la
  // vraie réponse serveur).
  if (gateItems === null) {
    return (
      <div style={{
        height: "calc(var(--nx-vh, 1vh) * 100)", display: "grid", placeItems: "center",
        background: P.bg, fontFamily: `var(--nx-font, ${FONT})`,
      }} />
    );
  }
  if (gateItems.length > 0) {
    const current = gateItems[0];
    const { info, version } = current;
    const lm = levelMeta[info.importanceLevel];
    const advanceGate = () => { setGateItems((items) => items.slice(1)); clearInfoSignature(); };
    return (
      <div style={{
        fontFamily: `var(--nx-font, ${FONT})`, color: P.ink, background: P.bg, height: "calc(var(--nx-vh, 1vh) * 100)",
        // Même bug/correction que l'écran de connexion ci-dessus : cette porte de signature
        // obligatoire s'affiche AVANT le shell connecté (donc sans sa nav latérale) — jamais
        // de marginLeft:SIDEBAR_WIDTH ici non plus.
        maxWidth: isWide ? 460 : 520, marginLeft: "auto", marginRight: "auto",
        display: "flex", flexDirection: "column",
        overflowX: "hidden", overflowY: "auto", position: "relative",
      }}>
        <header style={{ padding: "24px 18px 6px" }}>
          <p style={{ margin: 0, fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.red, letterSpacing: ".04em", textTransform: "uppercase" }}>
            🔒 Accès bloqué — lecture obligatoire
          </p>
          <h1 style={{ margin: "4px 0 2px", fontSize: `calc(21px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", fontWeight: 800 }}>{t("importantInfo.signPageTitle", "Information à signer")}</h1>
          <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>
            {gateItems.length > 1 ? `${gateItems.length} informations à traiter avant de continuer.` : "Dernière information avant de continuer."}
          </p>
        </header>
        <div style={{ padding: "12px 18px 28px" }}>
          <section style={{ ...colorCard(lm.color), padding: 19, borderRadius: 18, marginBottom: 16 }}>
            <span style={onColorTag}>{lm.label} · signature requise</span>
            <h2 style={{ margin: "5px 0 6px", fontSize: `calc(18px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{info.title}</h2>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("importantInfo.publishedByTemplate", { name: info.authorName, date: fmtFR((info.publishedAt || "").slice(0, 10)) }, `Publié par ${info.authorName} · ${fmtFR((info.publishedAt || "").slice(0, 10))}`)}</span>
          </section>
          <div style={{ ...card, borderRadius: 18, padding: 16, marginBottom: 16 }}>
            <p style={{ margin: 0, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{version?.content}</p>
          </div>
          <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 7 }}>
            Signature de {user?.firstname || "Toi"} — certifie ta lecture en signant
          </span>
          <div style={{ position: "relative", borderRadius: 16, background: "#fffefb", border: `1.5px dashed ${infoHasSignature ? A.green : P.line}`, marginBottom: 10, overflow: "hidden", touchAction: "none" }}>
            <canvas ref={infoSigCanvasRef} width={520} height={180} style={{ width: "100%", height: 150, display: "block", touchAction: "none", cursor: "crosshair" }}
              onPointerDown={infoSigStart} onPointerMove={infoSigMove} onPointerUp={infoSigEnd} onPointerLeave={infoSigEnd}
              onTouchStart={infoSigStart} onTouchMove={infoSigMove} onTouchEnd={infoSigEnd} />
            {!infoHasSignature && <span style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, pointerEvents: "none" }}>{t("importantInfo.signHereText", "✍️ Signe ici")}</span>}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <Pressable onClick={clearInfoSignature} style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, background: P.soft, borderRadius: 99, padding: "8px 14px" }}>{t("common.clearBtn", "Effacer")}</Pressable>
            {infoHasSignature && <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: "#15803d", fontWeight: 700 }}>{t("importantInfo.signatureTracedText", "✓ Signature tracée")}</span>}
          </div>
          <Pressable onClick={() => markInfoDone(info.id, true, advanceGate, info.currentVersionNumber)} style={{ width: "100%", ...btnSolid(A.red), opacity: infoHasSignature ? 1 : .5 }}>
            J'ai lu et je certifie
          </Pressable>
        </div>
        {toast && (
          <div style={{
            position: "fixed", bottom: 30, left: "50%", transform: "translateX(-50%)", zIndex: 60,
            background: themeId === "sombre" ? "#26262a" : A.navy, color: "#fff", padding: "11px 19px",
            borderRadius: 99, fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 600, boxShadow: P.deepShadow,
            whiteSpace: "nowrap", maxWidth: "88vw", overflow: "hidden", textOverflow: "ellipsis",
          }}>{toast}</div>
        )}
      </div>
    );
  }

  /* ============================================================
     APP CONNECTÉE
     ============================================================ */
  return (
    <div ref={mainAppRef}
      onPointerDown={onRootPointerDown} onPointerMove={onRootPointerMove}
      onPointerUp={onRootPointerUp} onPointerCancel={onRootPointerUp}
      style={{
      fontFamily: `var(--nx-font, ${FONT})`, color: P.ink, background: P.bg, height: "calc(var(--nx-vh, 1vh) * 100)",
      maxWidth: isWide ? 1100 : 520, marginLeft: isWide ? SIDEBAR_WIDTH : "auto", marginRight: "auto",
      display: "flex", flexDirection: "column",
      // PAS de transform ici (retiré 2026-08-13) : poser `transform` sur CE conteneur —
      // ancêtre direct de tous les Sheets/toasts de l'app — cassait le `position: sticky`
      // du header des Sheets longs (ex. formulaire vierge Bestandskontrolle : bouton ✕
      // devenait inaccessible après scroll) et pourrait aussi expliquer le gel de rendu
      // observé sur la zone Dynamic Island (bug WebKit connu : un ancêtre avec `transform`
      // change le contexte de positionnement/composition des descendants position:fixed/
      // sticky). Le geste de glissement de bord reste pleinement fonctionnel (détection +
      // seuil + swipeBackScreen ci-dessous) — seul le suivi visuel du doigt sur TOUT
      // l'écran est retiré ; voir edgeDragX plus bas pour l'indice visuel de bord restant.
      overflowX: "hidden", overflowY: "hidden", position: "relative",
    }}>
      {edgeDragX > 0 && (
        <div style={{
          position: "fixed", left: 0, top: 0, bottom: 0, width: 4, zIndex: 60,
          background: P.ink, opacity: Math.min(edgeDragX / EDGE_SWIPE_THRESHOLD, 1) * 0.5,
          pointerEvents: "none",
        }} />
      )}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700;800&display=swap');
        /* Réglages d'affichage (taille/police/gras, 2026-08-17) — --nx-font/--nx-font-scale
           posées en JS sur <html>, valeurs par défaut ici pour éviter tout flash avant le
           premier effet. Cette règle doit exister ici ET dans le style du login (elle vit,
           pas globale : un <style> JSX disparaît avec son composant — sans elle ici, "Gras"
           s'arrêterait de fonctionner dès la connexion, ne marchant plus que sur le login). */
        :root { --nx-font-scale: 1; --nx-font: ${FONT}; }
        html[data-bold-text="true"] * { -webkit-text-stroke: 0.35px currentColor; text-shadow: 0 0 0.2px currentColor; }
        html, body { height: 100%; overflow: hidden; overscroll-behavior-x: none; touch-action: pan-x pan-y; }
        * { scrollbar-width: none; -ms-overflow-style: none; }
        *::-webkit-scrollbar { display: none; width: 0; height: 0; }
        @keyframes nxToastIn { from { opacity: 0; transform: translate(-50%, 10px); } to { opacity: 1; transform: translate(-50%, 0); } }
        @keyframes nxPulse { 0%,100% { opacity: 1; } 50% { opacity: .35; } }
        @keyframes nxLive { 0%,100% { transform: scale(1); opacity: .9; } 50% { transform: scale(1.4); opacity: .4; } }
        @keyframes nxTabPop { 0% { transform: scale(1); } 45% { transform: scale(1.22) translateY(-2px); } 100% { transform: scale(1.08) translateY(-1px); } }
        @keyframes nxSaveFlash { 0% { box-shadow: 0 0 0 0 rgba(30,184,95,.55); } 100% { box-shadow: 0 0 0 22px rgba(30,184,95,0); } }
        @keyframes nxNextArticlePulse { 0%,100% { box-shadow: 0 0 0 0 rgba(54,79,231,.4); } 50% { box-shadow: 0 0 0 9px rgba(54,79,231,.16); } }
        @keyframes nxSlideIn { from { opacity: 0; transform: translateX(14px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes nxGradientFlow { 0% { background-position: 0% 50%; } 100% { background-position: -300% 50%; } }
        @keyframes nxTapFlash { 0% { transform: scale(1); } 35% { transform: scale(1.045); } 100% { transform: scale(1); } }
        .nx-tap-flash { animation: nxTapFlash .38s cubic-bezier(.22,1,.36,1); }
        @keyframes nxScreenEnter { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .nx-screen-enter { animation: nxScreenEnter .32s cubic-bezier(.22,1,.36,1); }
        /* En-tête + nav du bas "shy" (retour utilisateur explicite, 2026-08-16 — 2e passe :
           "ça marche mais trop mécanique") — --nx-shy-scale posée en continu sur mainAppRef
           par la boucle rAF (voir l'effet correspondant), jamais un binaire "claqué" entre 2
           états. transform plutôt que padding/font-size en dur : aucun en-tête/nav ne pose de
           transform inline, rien à contourner ; margin-bottom négatif (en-tête seulement,
           calc() proportionnel à l'échelle) compense le vide visuel laissé par le scale,
           sinon <main>, flex:1 juste en dessous, ne remonte pas. Nav du bas : position:fixed,
           donc aucun voisin à recaler — un simple scale suffit, ancré vers le bas. Transition
           courte uniquement pour lisser le pas entre 2 frames (~60/s), jamais de décalage
           perceptible avec le doigt. */
        .nx-topbar { transform: scale(var(--nx-shy-scale, 1)); transition: transform .06s linear, margin-bottom .06s linear; transform-origin: top left; margin-bottom: calc((var(--nx-shy-scale, 1) - 1) * 90px); }
        .nx-bottomnav { transform: scale(var(--nx-shy-scale, 1)); transition: transform .06s linear; transform-origin: bottom center; }
        @media (prefers-reduced-motion: reduce) { .nx-topbar, .nx-bottomnav { transition: none; } }
        @keyframes nxListItemIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .nx-list-item-in { animation: nxListItemIn .32s cubic-bezier(.22,1,.36,1) both; }
        @media (prefers-reduced-motion: reduce) {
          .nx-tap-flash, .nx-screen-enter, .nx-list-item-in { animation: none; }
        }
        .nx-gradient-text {
          background: linear-gradient(90deg, #2d7ff0, #7c5cf2, #b13df0, #0d9488, #2d7ff0);
          background-size: 300% 100%;
          -webkit-background-clip: text; background-clip: text;
          color: transparent; -webkit-text-fill-color: transparent;
          animation: nxGradientFlow 7s linear infinite;
        }
        @keyframes nxSun { 0% { transform: rotate(0deg) scale(1); } 50% { transform: rotate(180deg) scale(1.08); } 100% { transform: rotate(360deg) scale(1); } }
        .nx-sun { display: inline-block; animation: nxSun 16s linear infinite; filter: drop-shadow(0 0 10px rgba(255,190,60,.85)) drop-shadow(0 0 22px rgba(255,160,30,.45)); }
        @keyframes nxMoon { 0%,100% { transform: translateY(0) rotate(-8deg); filter: drop-shadow(0 0 8px rgba(160,190,255,.75)); } 50% { transform: translateY(-3px) rotate(6deg); filter: drop-shadow(0 0 16px rgba(180,205,255,.95)); } }
        .nx-moon { display: inline-block; animation: nxMoon 4.5s ease-in-out infinite; }
        @keyframes nxWave { 0%,100% { transform: rotate(0deg); } 15% { transform: rotate(22deg); } 30% { transform: rotate(-8deg); } 45% { transform: rotate(16deg); } 60% { transform: rotate(-4deg); } 75% { transform: rotate(9deg); } }
        .nx-wave { display: inline-block; transform-origin: 75% 80%; animation: nxWave 2.2s ease-in-out infinite; filter: drop-shadow(0 0 7px rgba(255,200,90,.7)); }
        @keyframes nxBot { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        .nx-bot { display: inline-block; animation: nxBot 2.6s ease-in-out infinite; }
        @keyframes nxBotRing { 0% { box-shadow: 0 0 0 0 rgba(45,127,240,.4); } 100% { box-shadow: 0 0 0 16px rgba(45,127,240,0); } }
        @keyframes nxBolt { 0%,100% { transform: scale(1) rotate(0deg); filter: drop-shadow(0 0 5px rgba(54,79,231,.6)); } 45% { transform: scale(1.16) rotate(-6deg); filter: drop-shadow(0 0 13px rgba(124,92,242,.95)); } 60% { transform: scale(.94) rotate(4deg); filter: drop-shadow(0 0 6px rgba(54,79,231,.5)); } }
        .nx-bolt { display: inline-block; animation: nxBolt 2.4s ease-in-out infinite; }
        @keyframes nxNotifDot { 0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,.55); } 70% { box-shadow: 0 0 0 8px rgba(239,68,68,0); } }
        .nx-notif-dot { animation: nxNotifDot 1.8s ease-out infinite; }
        @keyframes nxShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        .nx-shimmer-text {
          background: linear-gradient(100deg, var(--nx-ink) 42%, #7c5cf2 48%, #2d7ff0 52%, var(--nx-ink) 58%);
          background-size: 250% 100%;
          -webkit-background-clip: text; background-clip: text;
          color: transparent; -webkit-text-fill-color: transparent;
          animation: nxShimmer 3s linear infinite;
        }
        .nx-chips { overscroll-behavior-x: contain; -webkit-overflow-scrolling: touch; touch-action: pan-x; }
        .nx-chips::-webkit-scrollbar { display: none; }
        /* Tableau Wochenbericht (repScreen==="weekly") — maquette validée 2026-09-20, voir
           storeflow_wochenbericht_table_redesign_mockup. Structure/positionnement seulement
           ici (couleurs réelles posées en inline via P/A — ce fichier a un vrai système de
           thèmes multiples, THEMES ci-dessus, jamais de couleur figée en dur dans ce bloc
           <style> global partagé par tous les thèmes). Colonnes fixes (table-layout:fixed) :
           en "auto" la colonne catégorie collée s'élargissait pour garder un libellé long sur
           une seule ligne au lieu de passer à la ligne (retour utilisateur, capture à l'appui).
           Pas de word-break/hyphens forcés — un mot entier comme "Pflanzen" doit rester entier
           sur sa propre ligne, jamais coupé en "Pflan-zen" (autre retour utilisateur explicite). */
        /* Grille CSS (pas <table>) pour le Wochenbericht — voir commentaire au site d'usage
           (App.jsx, repScreen==="weekly") : position:sticky sur des cellules <table> se
           désynchronise de sa ligne au défilement sur Safari/WebKit, jamais sur des items de
           grille CSS. min-width fixe (pas 100%) = somme réelle des pistes, pour forcer le
           scroll horizontal au lieu de tasser les colonnes.
           min-height uniforme sur TOUTES les cellules (pas juste la colonne catégorie) — retour
           utilisateur réel, capture à l'appui (2026-09-20) : "Schnittblumen/Pflanzen" passe sur
           2 lignes et chevauche la valeur du jour voisin. Cause : Safari/WebKit exclut les
           éléments position:sticky du calcul automatique de hauteur de ligne d'une grille CSS —
           la ligne se dimensionne alors sur les cellules NON collées (plus courtes), et la
           cellule catégorie collée déborde par-dessus au lieu de pousser la ligne plus haut.
           En forçant un min-height uniforme sur TOUTES les cellules de la ligne (collées ou
           non), la ligne est déjà assez haute sans dépendre du calcul automatique. Repassé de
           52px (marge pour 2 lignes de libellé) à 42px (retour utilisateur : "trop grand, je
           préfère l'ancienne taille compacte") une fois la vraie cause du passage à 2 lignes
           réglée autrement (police réduite par libellé, voir REPORT_CATEGORIES.map plus bas) —
           42px reste juste assez pour PÜ/20%+Entsorgt empilés sur 1 ligne de libellé, le cas
           normal désormais ; le filet ne sert plus qu'aux langues futures pas encore vues ici
          (jusqu'à ~24 caractères, ES "Flores cortadas/Plantas" compris). */
        .nx-wk-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .nx-wk-grid { display: grid; min-width: 574px; font-variant-numeric: tabular-nums; }
        .nx-wk-cell { padding: 6px 10px 6px 4px; text-align: right; display: flex; flex-direction: column; justify-content: center; min-height: 42px; }
        .nx-wk-head { position: sticky; top: 0; z-index: 2; font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: .05em; padding: 8px 8px 8px 4px; min-height: 30px; }
        .nx-wk-cat-h { position: sticky; left: 0; top: 0; z-index: 3; text-align: left; padding-left: 13px; }
        .nx-wk-cat { position: sticky; left: 0; z-index: 1; text-align: left; padding: 6px 10px 6px 15px; font-size: 11px; font-weight: 700; line-height: 1.24; }
        .nx-wk-cat-bar { position: absolute; left: 0; top: 6px; bottom: 6px; width: 3px; border-radius: 3px; }
        .nx-wk-cat-tag { display: block; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; margin-top: 2px; }
        .nx-wk-pu { display: block; font-size: 8.5px; font-weight: 500; line-height: 1.3; margin-bottom: 3px; }
        .nx-wk-en { display: block; font-size: 12px; font-weight: 700; line-height: 1.25; }
        .nx-wk-total-cell { position: relative; font-size: 11.5px; font-weight: 800; }
        .nx-wk-total-pill { position: absolute; inset: 4px 3px; border-radius: 8px; z-index: -1; }
        /* Retour utilisateur (2026-10-14, capture à l'appui) : grille Wochenbericht pensée
           mobile (colonnes 62px, police 8-12px) jamais adaptée au desktop — restait tout
           aussi minuscule/tassée sur un grand écran. gridTemplateColumns + catFontSize/total
           semaine passent par du JS (isWide, voir plus bas, car dépendants du contenu) ; le
           reste (padding/hauteur/police des autres cellules) passe par ce modificateur de
           classe, jamais appliqué sur mobile (comportement téléphone strictement inchangé). */
        /* Retour utilisateur (2026-10-14, 3e capture) : toujours trop haut au global — le
           tableau entier (en-tête + ~9 catégories + Total jour) doit tenir sans défiler
           verticalement. Hauteur de ligne ramenée SOUS le mobile (42px) : les cellules ne
           sont plus des cibles tactiles sur desktop (souris/trackpad), 36px suffit largement. */
        .nx-wk-wide .nx-wk-cell { padding: 4px 10px 4px 6px; min-height: 34px; }
        .nx-wk-wide .nx-wk-head { font-size: 9.5px; padding: 6px 8px 6px 6px; min-height: 24px; }
        .nx-wk-wide .nx-wk-cat-h { padding-left: 15px; }
        .nx-wk-wide .nx-wk-cat { padding: 4px 10px 4px 15px; }
        .nx-wk-wide .nx-wk-cat-tag { font-size: 8px; margin-top: 1px; }
        .nx-wk-wide .nx-wk-pu { font-size: 9px; margin-bottom: 2px; }
        .nx-wk-wide .nx-wk-en { font-size: 11.5px; }
        .nx-wk-wide .nx-wk-total-cell { font-size: 10.5px; }
        @keyframes nxChevronBounce { 0%,100% { transform: translateY(0); opacity: .55; } 50% { transform: translateY(5px); opacity: 1; } }
        @keyframes nxDailySwipeHint { 0% { opacity: 0; transform: translate(-50%,-4px) scale(.94); } 15%,70% { opacity: 1; transform: translate(-50%,0) scale(1); } 100% { opacity: 0; transform: translate(-50%,-4px) scale(.96); } }
        .nx-assistant-chevron { animation: nxChevronBounce 1.6s ease-in-out infinite; }
        @keyframes nxAutoCloseDrain { from { transform: scaleX(1); } to { transform: scaleX(0); } }
        .nx-assistant-timer { transform-origin: left center; animation: nxAutoCloseDrain linear forwards; }
        @keyframes nxAssistantGlow { 0%,100% { box-shadow: 0 0 0 0 rgba(45,127,240,0); } 50% { box-shadow: 0 0 0 6px rgba(45,127,240,.10); } }
        .nx-assistant-peek { animation: nxAssistantGlow 2.6s ease-in-out infinite; }
        @keyframes nxTypingDot { 0%,60%,100% { transform: translateY(0); opacity: .4; } 30% { transform: translateY(-5px); opacity: 1; } }
        .nx-typing-dot { animation: nxTypingDot 1.1s ease-in-out infinite; }
        @keyframes nxInsightIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .nx-insight-in { animation: nxInsightIn .45s cubic-bezier(.2,.9,.3,1) both; }
        @keyframes nxBannerIn { from { transform: translateY(-130%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes nxBannerOut { from { transform: translateY(0); opacity: 1; } to { transform: translateY(-130%); opacity: 0; } }
        .nx-banner-in { animation: nxBannerIn .45s cubic-bezier(.2,1.1,.3,1) both; }
        .nx-banner-out { animation: nxBannerOut .3s cubic-bezier(.4,0,1,1) both; }
        input:focus { border-color: ${A.blue} !important; box-shadow: 0 0 0 4px rgba(54,79,231,.14); }
        * { -webkit-tap-highlight-color: transparent; }
          * { -webkit-user-select: none; -moz-user-select: none; user-select: none; -webkit-touch-callout: none; }
          input, textarea, [contenteditable="true"] { -webkit-user-select: text; -moz-user-select: text; user-select: text; -webkit-touch-callout: default; }
        @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important; } }

      `}</style>

      {/* ---------- HEADER ACCUEIL ---------- */}
      {/* Volontairement SANS "nx-topbar" (retour utilisateur explicite, 2026-08-16 : "le
          comportement sur l'accueil n'est pas du tout agréable") — cet en-tête reste toujours
          à sa taille normale, jamais rétréci au scroll, contrairement aux autres écrans. */}
      {screen === "home" && !isNetworkLeader && (
        // Hiérarchie resserrée (2026-08-22, retour utilisateur explicite — maquette validée
        // avant implémentation) : avant, marque/météo/salutation/emoji/thème/avatar étaient
        // tous au même niveau visuel, sans point focal. Désormais 3 rangées nettes : (1) une
        // ligne utilitaire discrète (marque + horloge + réglages, jamais l'objet de
        // l'attention), (2) la salutation seule comme vrai titre — plus d'emoji 👋 qui
        // cassait le sérieux, (3) le badge filiale remonté juste en dessous avec un point de
        // statut plutôt qu'un mot en majuscules. L'icône ☀️/🌙 du bouton de thème (P.icon)
        // suffit déjà à porter la touche visuelle — plus besoin d'un second glyphe météo
        // redondant (isDay) à côté de l'heure.
        <header style={{ padding: "20px 18px 6px", position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <span className="nx-gradient-text" style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 900, letterSpacing: ".2em", textTransform: "uppercase" }}>Nexora</span>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, textTransform: "capitalize", fontVariantNumeric: "tabular-nums" }}>
                {todayLabel} · {timeLabel}
              </span>
              <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
                width: 30, height: 30, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, flexShrink: 0,
              }}>{P.icon}</Pressable>
              <Pressable onClick={openAccountSheet} aria-label={t("common.userMenuAria", "Menu utilisateur")} style={{
                width: 30, height: 30, borderRadius: 99, color: "#fff", overflow: "hidden", flexShrink: 0,
                background: user?.photoUrl ? "none" : `linear-gradient(155deg, rgba(255,255,255,.28), rgba(255,255,255,0) 55%), linear-gradient(135deg, ${A.blue}, #6f5ef0)`,
                border: "2px solid " + P.bg,
                display: "grid", placeItems: "center", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: "-.02em",
                boxShadow: `0 3px 8px rgba(54,79,231,.2), 0 8px 18px rgba(54,79,231,.35)`,
              }}>{user?.photoUrl ? <img src={user?.photoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : userInitials}</Pressable>
            </div>
          </div>
          <h1 ref={setGreetEl} style={{
            margin: "0 0 12px", color: P.ink, letterSpacing: "-.025em", fontWeight: 800,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            fontSize: `calc(${(greetFontSize)}px * var(--nx-font-scale, 1))`,
          }}>{greetLine}</h1>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "5px 12px",
            color: P.muted, letterSpacing: ".02em",
          }}>
            {myStoreHours.openTime && myStoreHours.closeTime ? (
              <StoreHoursCountdown
                openTime={myStoreHours.openTime} closeTime={myStoreHours.closeTime} holidayDatesSet={allHolidayDatesSet}
                render={({ isOpen, remaining }) => (
                  <>
                    <span style={{ width: 6, height: 6, borderRadius: 99, background: isOpen ? A.green : A.orange, flexShrink: 0 }} />
                    {t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`)} · <span style={{ color: P.ink, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                      {isOpen ? t("home.storeHours.closesIn", { time: remaining }, `Ferme dans ${remaining}`) : t("home.storeHours.opensIn", { time: remaining }, `Ouvre dans ${remaining}`)}
                    </span>
                  </>
                )}
              />
            ) : (
              <>
                <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, flexShrink: 0 }} />
                {t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`)} · <span style={{ color: P.ink, fontWeight: 800 }}>{t("common.activeStatus", "Active")}</span>
              </>
            )}
          </span>
        </header>
      )}

      {/* ---------- TOPBAR modules (hors accueil, hors run, hors "other") ---------- */}
      {inControl && !inRun && (
        <header style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={goBack} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow() }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("control.eyebrow", "Qualité & DLC")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>{title}</h1>
            <span style={{
              fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px",
              color: P.muted, letterSpacing: ".03em",
            }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>{P.icon}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR RÉDUCTIONS ---------- */}
      {screen === "reductions" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => redCategory ? setRedCategory(null) : setScreen("home")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.purple) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("reductions.eyebrow", "Qualité & fraîcheur")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {redCategory === "trash" ? `${t("reductions.topbarTitle", "Réductions")} · ${t("reductions.trashBtn", "Corbeille")}`
                : redCategory ? `${t("reductions.topbarTitle", "Réductions")} · ${REDUCTION_CATEGORIES.find((c) => c.id === redCategory)?.label}`
                : t("reductions.topbarTitle", "Réductions")}
            </h1>
            <span style={{
              fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px",
              color: P.muted, letterSpacing: ".03em",
            }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          {!redCategory && (
            <Pressable onClick={exportReductionsPdf} aria-label={t("reductions.exportPdfAria", "Exporter la liste en PDF")} style={{
              width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`,
            }}>📄</Pressable>
          )}
          <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>{P.icon}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR ARTICLES ---------- */}
      {screen === "articles" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => {
            if (artScreen === "list" || artScreen === "reports") setArtScreen("hub");
            else if (artScreen === "reportForm") setArtScreen("reports");
            else if (artScreen === "import") { if (artImportStep === "preview") setArtImportStep("upload"); else setArtScreen("hub"); }
            else setScreen("home");
          }} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.green) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {artScreen === "list" ? t("articles.rayonLabel", "Rayon") : (artScreen === "reports" || artScreen === "reportForm") ? t("articles.reports.eyebrow", "Signalement") : artScreen === "import" ? t("artImport.eyebrow", "Import IA") : t("articles.eyebrow", "Base articles")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {artScreen === "list" ? (artZone ? artZoneLabel(artZone) : t("articles.allArticlesLabel", "Tous les articles"))
                : artScreen === "reports" ? t("articles.reports.historyTitle", "Historique")
                : artScreen === "reportForm" ? t("articles.reports.formTitle", "Nouvel article")
                : artScreen === "import" ? (artImportStep === "preview" ? t("artImport.preview.title", "Vérification catalogue") : t("artImport.upload.title", "Importer un catalogue"))
                : t("articles.listTitleDefault", "Articles")}
            </h1>
            <span style={{
              fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px",
              color: P.muted, letterSpacing: ".03em",
            }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>{P.icon}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR INVENTUR ---------- */}
      {screen === "inventur" && (() => {
        const backMap = {
          hub: () => setScreen("home"),
          erfassung_list: () => setInventurScreen("hub"),
          erfassung_create: () => setInventurScreen("erfassung_list"),
          erfassung_my_tasks: () => setInventurScreen("hub"),
          erfassung_detail: () => { setInventurActiveInventory(null); setInventurScreen("erfassung_list"); loadInventurInventories(); },
          erfassung_category: () => { setInventurActiveCategoryId(null); setInventurScreen("erfassung_detail"); refreshActiveInventory(); },
          erfassung_history: () => setInventurScreen("erfassung_detail"),
          // kontrolle_detail retiré (2026-08-09) : déplier une carte dans kontrolle_list EST
          // le détail désormais, plus jamais un écran séparé à quitter.
          kontrolle_list: () => setInventurScreen("hub"),
          kontrolle_create: () => setInventurScreen("kontrolle_list"),
          kontrolle_history: () => setInventurScreen("hub"),
          rdv: () => setInventurScreen("hub"),
        };
        const titles = {
          hub: "Inventur", erfassung_list: "Inventurerfassung", erfassung_create: t("inventur.newTitle", "Nouvel inventaire"),
          erfassung_my_tasks: t("inventur.myTasks.title", "Mes tâches d'inventaire"),
          erfassung_detail: inventurActiveInventory?.name || inventurActiveInventory?.id || "Inventur",
          erfassung_category: activeInventurCategory()?.name || t("inventur.categoryFallback", "Catégorie"),
          erfassung_history: t("arbeitszeitliste.history.open", "Historique"),
          kontrolle_list: "Bestandskontrolle", kontrolle_create: t("kontrolle.newTitle", "Nouveau contrôle"),
          kontrolle_history: t("kontrolle.historyTitle", "Historique des contrôles"),
          rdv: t("inventur.appointment.hubTitle", "Rendez-vous d'inventaire"),
        };
        return (
          <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
            <Pressable onClick={backMap[inventurScreen] || (() => setInventurScreen("hub"))} aria-label={t("common.back", "Retour")} style={{
              width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
            }}>←</Pressable>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.brown) }}>
                <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
                {inventurScreen.startsWith("kontrolle") ? "Bestandskontrolle" : "Inventur"}
              </p>
              <h1 style={{ margin: "2px 0 3px", fontSize: `calc(22px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {titles[inventurScreen] || "Inventur"}
              </h1>
              <span style={{
                fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px",
                color: P.muted, letterSpacing: ".03em",
              }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
            </div>
            {inventurScreen === "erfassung_detail" && canViewInventurHistory && (
              <Pressable onClick={() => { loadInventurHistory(inventurActiveInventory.id); setInventurScreen("erfassung_history"); }} aria-label={t("materialOrder.historyAria", "Historique")} style={{
                width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`,
              }}>🕘</Pressable>
            )}
            <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
              width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
            }}>{P.icon}</Pressable>
          </header>
        );
      })()}

      {/* ---------- TOPBAR MISSIONS RÉSEAU (compte normal) — vrai écran, pas une popup
          (retour utilisateur explicite, 2026-08-02 : "mission reseau ne doit pas etre un
          module popup mais un module qu'on ouvre a clique"). La fiche d'UNE mission reste
          un popup (myMissionDetail), seule l'entrée du module change. ---------- */}
      {screen === "missions" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => setScreen("home")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.purple) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              Reçues
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>{t("tasks.networkMissions", "Missions réseau")}</h1>
            <span style={{
              fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px",
              color: P.muted, letterSpacing: ".03em",
            }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>{P.icon}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR DASHBOARD ---------- */}
      {screen === "dashboard" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => setScreen("home")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.green) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("dashboard.eyebrow", "Aujourd'hui")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>{t("home.module.dashboard", "Dashboard")}</h1>
            <span style={{
              fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px",
              color: P.muted, letterSpacing: ".03em",
            }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()} · KW {isoWeek(now)}</span>
          </div>
          <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>{P.icon}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR ARTIKELFINDER ---------- */}
      {screen === "finder" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => {
            if (finderScreen === "hub") { setScreen("home"); return; }
            if (finderScreen === "detail") { setFinderScreen(finderDetailBackScreen); return; }
            setFinderScreen("hub");
          }} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.purple) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              Artikelfinder
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {finderScreen === "results" ? t("finder.title.results", "Résultats") : finderScreen === "detail" ? t("finder.title.detail", "Fiche article") : finderScreen === "admin" ? t("finder.title.admin", "Fiches en attente") : finderScreen === "create" ? t("finder.create.title", "Nouvelle fiche") : finderScreen === "catalog" ? t("finder.catalog.title", "Catalogue") : "Artikelfinder"}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          {/* Roue "Fiches en attente" retirée (retour utilisateur explicite) — cohérent avec
              la création qui existe désormais directement (statut "validated" dès POST
              /items, voir submitFinderCreate) : plus aucune nouvelle fiche n'atteint
              "pending_review" par ce chemin, la file d'attente n'a plus lieu d'être un point
              d'entrée visible. */}
        </header>
      )}

      {/* ---------- TOPBAR CENTRE DOCUMENTAIRE ---------- */}
      {screen === "docintel" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => setScreen("home")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.purple) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("documentIntel.hero.eyebrow", "Smart Import")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>{t("documentIntel.title", "Centre documentaire")}</h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
        </header>
      )}

      {/* ---------- TOPBAR ARBEITSZEITLISTE ---------- */}
      {screen === "azl" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => setScreen("home")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.navy) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("arbeitszeitliste.eyebrow", "Récapitulatif mensuel")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>{t("arbeitszeitliste.pageTitle", "Arbeitszeitliste")}</h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: azlStoreId || user?.store }, `Filiale ${azlStoreId || user?.store}`).toUpperCase()}</span>
          </div>
          {azlData && (
            <Pressable onClick={printAzlDocument} aria-label={t("arbeitszeitliste.printAria", "Imprimer / PDF")} style={{
              width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`,
            }}>🖨️</Pressable>
          )}
          {canManageAzl && (
            <Pressable onClick={openAzlExternal} aria-label={t("arbeitszeitliste.externalManageAria", "Employés externes")} style={{
              width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`,
            }}>👥</Pressable>
          )}
        </header>
      )}

      {/* ---------- HEADER PILOTAGE RÉSEAU (= Accueil pour un grand responsable réel :
           Bereichsleiter/Verkaufsleiter/Niederlassungsleiter/NEXORA_ADMIN — jamais une
           tuile qu'on ouvre, voir project_nexora_multi_store_scale/retour utilisateur :
           "cela ne doit pas etre un module mais la vue qu'un grand responsable doit
           avoir quand il se connecte a son compte"). Même gabarit que le header Accueil
           employé (pas de bouton retour, wordmark + heure + thème + profil), jamais la
           hiérarchie Bezirk/rôles fictive de l'artefact d'origine — périmètre réel
           (nombre de vraies filiales du compte, via /api/manager/overview). ---------- */}
      {screen === "home" && isNetworkLeader && pilotScreen !== "analyse" && pilotScreen !== "filiale-detail" && pilotScreen !== "appointments" && (
        <header style={{ padding: "20px 18px 6px", position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <span className="nx-gradient-text" style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, fontWeight: 900, letterSpacing: ".2em", textTransform: "uppercase" }}>Nexora</span>
            <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, textTransform: "capitalize" }}>
              {todayLabel} · <strong style={{ color: P.ink }}>{timeLabel}</strong>
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.purple) }}>
                <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
                {t("pilotage.header.title", "Pilotage réseau")} · {user?.role}
              </p>
              <h1 style={{ margin: "3px 0 4px", fontSize: `calc(26px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
                {pilotScreen === "missions" ? t("nav.missions", "Missions") : { today: t("dashboard.eyebrow", "Aujourd'hui"), alertes: t("nav.alertes", "Alertes"), filiales: t("nav.filiales", "Filiales") }[pilotScreen]}
              </h1>
            </div>
            <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
              width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
            }}>{P.icon}</Pressable>
            <Pressable onClick={openAccountSheet} aria-label={t("common.userMenuAria", "Menu utilisateur")} style={{
              width: 42, height: 42, borderRadius: 99, color: "#fff", overflow: "hidden",
              background: user?.photoUrl ? "none" : `linear-gradient(155deg, rgba(255,255,255,.28), rgba(255,255,255,0) 55%), linear-gradient(135deg, ${A.blue}, #6f5ef0)`,
              border: "2px solid " + P.bg,
              display: "grid", placeItems: "center", fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: "-.02em",
              boxShadow: `0 3px 8px rgba(54,79,231,.2), 0 10px 22px rgba(54,79,231,.4)`,
            }}>{user?.photoUrl ? <img src={user?.photoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : userInitials}</Pressable>
          </div>
          <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>
            {t("pilotage.header.storesCountTemplate", { n: (managerOverview?.stores || []).length }, `${(managerOverview?.stores || []).length} FILIALE${(managerOverview?.stores || []).length > 1 ? "S" : ""}`)}
          </span>
        </header>
      )}

      {/* ---------- HEADER ANALYSE & RAPPORTS (Pilotage réseau) — gabarit distinct et
           plus simple que le header général : juste retour + titre, fidèle à
           nexora-app_1.zip (source lignes ~4201-4211), pas de thème/profil ici. ---------- */}
      {screen === "home" && isNetworkLeader && pilotScreen === "analyse" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => setPilotScreen("today")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.purple) }}>{t("pilotage.header.title", "Pilotage réseau")}</p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(24px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>{t("pilotage.header.analyseTitle", "Analyse & rapports")}</h1>
          </div>
        </header>
      )}

      {/* ---------- HEADER RENDEZ-VOUS D'INVENTAIRE (Pilotage réseau) — même gabarit
           simple que le header Analyse ci-dessus (retour + titre, pas de thème/profil). ---------- */}
      {screen === "home" && isNetworkLeader && pilotScreen === "appointments" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => setPilotScreen("today")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.purple) }}>{t("pilotage.header.title", "Pilotage réseau")}</p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(24px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>{t("pilotage.appointment.tileTitle", "Rendez-vous d'inventaire")}</h1>
          </div>
        </header>
      )}

      {/* ---------- HEADER FICHE FILIALE (détail) — fidèle à nexora-app_1.zip
           (source lignes ~4182-4198), badge de sévérité réel depuis le feed
           /api/manager/overview de cette filiale. ---------- */}
      {screen === "home" && isNetworkLeader && pilotScreen === "filiale-detail" && pilotFilialeId && (() => {
        const storeFeed = (managerOverview?.feed || []).filter((f) => f.storeId === pilotFilialeId);
        const meta = storeFeed.some((f) => f.severity === "danger")
          ? { c: A.red, l: t("arbeitszeitliste.anomalies.severity.critical", "Critique") }
          : storeFeed.length > 0
          ? { c: A.orange, l: t("pilotage.today.toWatch", "À surveiller") }
          : { c: A.green, l: t("haccp.labelCompliant", "Conforme") };
        return (
          <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
            <Pressable onClick={() => { setPilotScreen("filiales"); setPilotFilialeId(null); setPilotFilialeSection(null); }} aria-label={t("common.back", "Retour")} style={{
              width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
            }}>←</Pressable>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.purple) }}>{t("pilotage.header.ficheFilialeEyebrow", "Fiche filiale")}</p>
              <h1 style={{ margin: "2px 0 3px", fontSize: `calc(24px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>{storeLabelFor(pilotFilialeId)}</h1>
              <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: meta.c, borderRadius: 99, padding: "3px 10px" }}>{pilotFilialeId} · {meta.l}</span>
            </div>
          </header>
        );
      })()}

      {/* ---------- TOPBAR LIEFERSCHEIN ---------- */}
      {screen === "liefer" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => {
            if (lfScreen === "hub") { setScreen("home"); return; }
            if (lfScreen === "detail") { setLfScreen("hub"); return; }
            if (lfScreen === "preview") { setLfScreen("import"); return; }
            setLfScreen("hub");
          }} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.blue) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("lieferschein.hub.title", "Bons de livraison")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {lfScreen === "import" ? t("lieferschein.import.title", "Importer un bon") : lfScreen === "preview" ? t("lieferschein.preview.title", "Vérification avant création") : lfScreen === "detail" ? t("lieferschein.detail.title", "Détail") : t("lieferschein.hub.title", "Bons de livraison")}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
        </header>
      )}

      {/* ---------- TOPBAR REPORTING ---------- */}
      {screen === "reporting" && (
        <header style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          {/* "weekly" n'est plus atteignable que depuis "daily" (bascule Jour/Semaine, tuile
              hub retirée, 2026-09-20) — retour va donc vers "daily", pas "hub", pour rester
              cohérent avec "j'y suis arrivé depuis là" plutôt que de sauter ailleurs. */}
          <Pressable onClick={() => { if (repScreen === "daily") setDailyDayIdx(null); if (repScreen === "weekly") { setWeeklyViewWeek(null); setPastWeekSrc(null); setRepScreen("daily"); return; } repScreen === "hub" ? setScreen("home") : setRepScreen("hub"); }} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.green) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("home.module.reporting", "Reporting")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {{
                hub: t("home.module.reporting", "Reporting"),
                daily: t("reporting.hub.dailyTitle", "Saisie quotidienne"),
                weekly: t("reporting.hub.weeklyTitleShort", "Rapport hebdomadaire"),
                stunden: t("reporting.stundenleistungCol", "Stundenleistung"),
                month: t("reporting.hub.monthTitle", "Clôture mensuelle"),
                history: t("reporting.hub.historyTitle", "Historique"),
                catalog: t("reporting.catalog.title", "Catalogue Prebake & Brot"),
              }[repScreen]}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>{P.icon}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR MA FICHE ---------- */}
      {screen === "myFiche" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => ficheScreen === "infos" ? setScreen("home") : setFicheScreen("infos")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, ...eyebrow(A.blue) }}>{t("home.menu.myFiche", "Ma fiche")}</p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {{ infos: t("ficheEmployee.tabInfos", "Infos"), performance: t("ficheEmployee.tabPerformance", "Performance") }[ficheScreen]}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{user?.firstname} {user?.lastname || ""}</span>
          </div>
          <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>{P.icon}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR ADMIN ---------- */}
      {screen === "admin" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => {
            adminScreen === "hub" ? setScreen("home") : setAdminScreen("hub");
          }} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.navy) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("settings.admin.title", "Administration")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {adminScreen === "users" ? t("admin.usersTitle", "Utilisateurs") : adminScreen === "roles" ? t("admin.rolesTitle", "Rôles & permissions") : adminScreen === "stores" ? t("admin.storesTitle", "Filiales")
                : adminScreen === "webauthnDevices" ? t("admin.webauthnDevicesTitle", "Appareils Face ID")
                : t("settings.admin.title", "Administration")}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>{P.icon}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR MON ÉQUIPE FILIALE (FL/FLA) ---------- */}
      {screen === "flTeam" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => setScreen("home")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.purple) }}>
              {t("flTeam.eyebrow", "Mon équipe filiale")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {t("flTeam.title", "Gérer mon équipe")}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
        </header>
      )}

      {/* ---------- TOPBAR NOTIFICATIONS ---------- */}
      {screen === "notifications" && (
        // alignItems: flex-start (pas "center") — avec une grande taille de police, le titre
        // peut s'enrouler sur plusieurs lignes ; centré verticalement, le bouton "Tout lu" se
        // retrouvait au milieu de cette hauteur, visuellement superposé au texte du titre
        // (retour utilisateur explicite, capture à l'appui, 2026-08-17).
        <header className="nx-topbar" style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "20px 18px 10px", flexWrap: "wrap" }}>
          <Pressable onClick={() => setScreen("home")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
          }}>←</Pressable>
          <div style={{ flex: "1 1 160px", minWidth: 0 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.orange) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("notifications.globalCenterEyebrow", "Centre global · priorités")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>{t("notifications.title", "Notifications")}</h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          <Pressable onClick={markAllNotifsRead} style={{
            flex: "0 0 auto", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "9px 13px", color: P.ink, whiteSpace: "nowrap",
          }}>{t("notifications.markAllReadBtn", "Tout lu")}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR MESSAGES ---------- */}
      {screen === "messages" && (
        <header style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => {
            if (msgScreen === "list") { setScreen("home"); return; }
            if (msgScreen === "search") { setMsgSearchQuery(""); setMsgSearchResults([]); setMsgScreen("list"); return; }
            setMsgScreen("list"); setMsgActiveId(null);
          }} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.frost) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {totalUnread > 0 ? t("messages.eyebrow.unread", { count: totalUnread }, `${totalUnread} non lus`) : t("messages.eyebrow.allRead", "Tout est lu")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {msgScreen === "thread" ? (conversations.find((c) => c.id === msgActiveId)?.title || t("messages.title", "Messages")) : msgScreen === "new" ? t("messages.new.title", "Nouveau") : msgScreen === "search" ? t("common.search", "Rechercher") : t("messages.title", "Messages")}
            </h1>
            {msgScreen === "thread" ? (() => {
              // Bug réel signalé (2026-08-06) : affichait auparavant "FILIALE {user?.store}"
              // — la filiale du VIEWER, sans lien avec la personne affichée dans l'en-tête
              // (ex. "Filiale N236" sous le nom d'une Bereichsleiterin qui gère plusieurs
              // filiales et ne peut en avoir aucune comme unique filiale). Pour une
              // conversation privée, affiche maintenant le rôle du ou de la destinataire —
              // seul le rôle pour un GR (Bereichsleiter/Verkaufsleiter/Niederlassungsleiter,
              // aucune filiale à afficher), rôle + filiale pour un compte normal.
              const conv = conversations.find((c) => c.id === msgActiveId);
              // Bug réel trouvé après premier essai ("je vois toujours Filiale N236") :
              // une conversation démarrée depuis "Nouvelle communication" (vue GR) est de
              // type multi_branch, pas private, même pour un seul destinataire — exactement
              // le même cas déjà géré côté serveur (displayTitleFor/notifyNewMessage,
              // "une multi_branch retombée à 2 personnes est fonctionnellement privée").
              // Devait suivre la même règle ici, pas seulement conv.type === "private".
              const effectivelyPrivate = conv?.type === "private" || (conv?.type === "multi_branch" && msgConvMembers.length === 2);
              if (!effectivelyPrivate) return (
                <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
              );
              const myId = String(user?.proId || user?.id || "");
              const other = msgConvMembers.find((m) => String(m.userId) !== myId);
              if (!other || !other.role) return null;
              const isNetworkLeaderRole = ["Bereichsleiter", "Verkaufsleiter", "Niederlassungsleiter", "NEXORA_ADMIN"].includes(other.role);
              const label = isNetworkLeaderRole ? other.role : `${other.role}${other.storeId ? ` · Filiale ${other.storeId}` : ""}`;
              return <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{label.toUpperCase()}</span>;
            })() : (
              <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
            )}
          </div>
          {msgScreen === "list" && (
            <Pressable onClick={() => { setMsgSearchQuery(""); setMsgSearchResults([]); setMsgScreen("search"); }} aria-label={t("perm.messages.search", "Rechercher dans les messages")} style={{
              width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`,
            }}>🔎</Pressable>
          )}
          {msgScreen === "thread" && (
            <Pressable onClick={() => setSheet("convInfo")} aria-label={t("messages.convInfoAria", "Infos conversation")} style={{
              width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`,
            }}>ⓘ</Pressable>
          )}
        </header>
      )}

      {/* ---------- TOPBAR INFOS IMPORTANTES ---------- */}
      {screen === "infos" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => {
            if (infoScreen === "hub") { setScreen("home"); return; }
            if (infoScreen === "editor") { setInfoScreen("manage"); return; }
            if (infoScreen === "manageDetail") { setInfoScreen("manage"); return; }
            setInfoScreen("hub");
          }} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.red) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("importantInfo.hero.eyebrow", "Canal officiel")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {infoScreen === "manage" ? t("importantInfo.manage.pageTitle", "Mes publications") : infoScreen === "manageDetail" ? t("importantInfo.manageDetail.title", "Suivi") : infoScreen === "editor" ? t("importantInfo.editor.title", "Nouvelle information") : infoScreen === "detail" ? t("importantInfo.detail.title", "Détail") : t("importantInfo.pageTitle", "Informations importantes")}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>{P.icon}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR COMMANDE MATÉRIEL ---------- */}
      {screen === "material" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => {
            if (moScreen === "hub") { setScreen("home"); return; }
            if (moScreen === "form") { setMoScreen("hub"); setMoActiveCatalog(null); return; }
            setMoScreen("hub");
          }} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.brown) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("materialOrder.hero.eyebrow", "NORMA · Bons de commande")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {moScreen === "form" ? (moActiveCatalog ? materialCatalogs[moActiveCatalog].label : t("materialOrder.title.detail", "Commande")) : moScreen === "history" ? t("materialOrder.title.history", "Historique") : t("materialOrder.title.home", "Commande de matériel")}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>{P.icon}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR COMMANDES DE MARCHANDISES ---------- */}
      {/* L'écran "article" (cœur du module) n'a PAS de topbar — retour utilisateur explicite :
          "page plein écran sans barre de navigation", navigation assurée par les gros
          chevrons ‹ › en bas d'écran (voir plus bas), jamais une flèche de retour standard. */}
      {screen === "merchandise" && merchScreen !== "article" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => {
            if (merchScreen === "hub") { setScreen("home"); return; }
            if (merchScreen === "faktor") { setMerchScreen("date"); return; }
            if (merchScreen === "schedule") { setMerchScreen("hub"); return; }
            setMerchScreen("hub"); loadMerchCategories();
          }} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.amber) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {merchActiveZone ? merchCategoryMeta(merchActiveZone).label : t("merch.zoneByZoneEyebrow", "Rayon par rayon")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {merchScreen === "date" ? t("merch.deliveryDateTitle", "Date de livraison")
                : merchScreen === "faktor" ? t("merch.faktorTitle", "Faktor")
                : merchScreen === "summary" ? t("merch.summaryTitle", "Résumé")
                : merchScreen === "manage" ? t("merch.manageTitle", "Gestion des commandes")
                : merchScreen === "reorder" ? t("merch.reorderTitle", "Réorganiser le rayon")
                : merchScreen === "schedule" ? t("merch.schedule.title", "Rappel de commande")
                : merchScreen === "article" ? merchCategoryMeta(merchActiveZone).label
                : t("merch.hubTitle", "Commandes de marchandises")}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          {merchScreen === "hub" && canManageMerchCatalog && (
            <Pressable onClick={() => setMerchScreen("schedule")} aria-label={t("merch.schedule.aria", "Rappel de commande")} style={{
              width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
            }}>⏰</Pressable>
          )}
          {merchScreen === "hub" && canPrepareMerch && (
            <Pressable onClick={openMerchManage} aria-label={t("merch.manageAria", "Gestion des commandes")} style={{
              width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
            }}>📋</Pressable>
          )}
        </header>
      )}
      {screen === "merchandise" && merchDemoMode && (
        <div style={{ margin: "0 18px 8px", padding: "6px 12px", borderRadius: 99, background: "rgba(231,138,18,.15)", border: `1.5px solid ${A.orange}`, textAlign: "center", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.orange, letterSpacing: ".03em" }}>
          🧪 {t("merch.demo.bannerText", "MODE DÉMO — aucune donnée réelle")}
        </div>
      )}

      {/* ---------- TOPBAR FABRICATION D'ÉTIQUETTES ---------- */}
      {screen === "labels" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => {
            if (labelsScreen === "hub") { setScreen("home"); return; }
            if (labelsScreen === "template") { setLabelsScreen("hub"); return; }
            if (labelsScreen === "form") { setLabelsScreen(labelEditingId ? "preview" : "template"); return; }
            setLabelsScreen("hub"); loadLabels();
          }} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.yellow) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("labels.eyebrow", "Étiquettes")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {labelsScreen === "template" ? t("labels.templateTitle", "Choisir un modèle")
                : (labelsScreen === "form" || labelsScreen === "preview") ? t(`labels.templateName.${labelActiveTemplateId}`, getLabelTemplate(labelActiveTemplateId).label)
                : t("labels.hubTitle", "Fabrication d'étiquettes")}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
        </header>
      )}

      {/* ---------- TOPBAR KASSENPOST ---------- */}
      {screen === "kassenpost" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => {
            if (kassenpostScreen === "hub") { setScreen("home"); return; }
            setKassenpostScreen("hub"); loadKassenpost();
          }} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.red) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("kassenpost.eyebrow", "Kassenpost")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {kassenpostScreen === "form"
                ? (kassenpostActiveId
                    ? (kassenpostForm?.numero ? `#${kassenpostForm.numero}` : t("kassenpost.editTitle", "Message"))
                    : t("kassenpost.newTitle", "Nouveau message"))
                : t("kassenpost.hubTitle", "Kassenpost")}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
        </header>
      )}

      {/* ---------- TOPBAR ARTICLE COMMANDES DE MARCHANDISES ---------- */}
      {/* Design porté à l'identique de l'artefact validé (style Contrôle Kühlung) : pastille
          pause (✕, mode pause — jamais un simple retour), nom d'article tronqué, bascule prix
          (réutilise artPriceVisible, même réglage global que le module Articles), liste de
          commande (☰, badge = nb déjà commandés), compteur n/total. Toujours visible — plus
          de "page plein écran sans en-tête", l'artefact a tranché pour une topbar compacte. */}
      {screen === "merchandise" && merchScreen === "article" && merchSession && merchSession.articles[merchIndex] && (() => {
        const article = merchSession.articles[merchIndex];
        const meta = merchCategoryMeta(merchActiveZone);
        const orderedInSession = (merchSession.order?.lines || []).filter((l) => !l.skipped && l.quantity > 0).length;
        return (
          <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 8, padding: "20px 18px 8px" }}>
            <Pressable onClick={pauseMerchOrder} aria-label={t("merch.article.pauseAria", "Mettre en pause et revenir au Hub")} style={{
              width: 38, height: 38, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(15px * var(--nx-font-scale, 1))`,
            }}>✕</Pressable>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, ...eyebrow(meta.color) }}>{meta.label}</p>
              <strong style={{ display: "block", fontSize: `calc(15.5px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: "-.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {article.name.length > 26 ? article.name.slice(0, 25) + "…" : article.name}
              </strong>
            </div>
            <Pressable onClick={() => setSheet("merchArticleList")} aria-label={t("merch.article.listAria", "Liste de commande")} style={{
              width: 38, height: 38, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, position: "relative", flexShrink: 0,
            }}>
              ☰
              {orderedInSession > 0 && (
                <span style={{ position: "absolute", top: -4, right: -4, minWidth: 16, height: 16, padding: "0 3px", borderRadius: 99, background: A.green, color: "#fff", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, display: "grid", placeItems: "center" }}>{orderedInSession}</span>
              )}
            </Pressable>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "5px 10px", color: P.muted, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{merchIndex + 1}/{merchSession.articles.length}</span>
          </header>
        );
      })()}

      {/* ---------- TOPBAR INVENTURMASSNAHME ---------- */}
      {screen === "inventurmassnahme" && imScreen !== "pluquiz" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => {
            if (imReturnToPilot) { setImReturnToPilot(false); setScreen("home"); setPilotScreen("filiale-detail"); return; }
            imScreen === "hub" ? setScreen("home") : setImScreen("hub");
          }} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, ...eyebrow(A.navy) }}>{t("inventurmassnahme.eyebrow", "Inventurmaßnahme")}</p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {imScreen === "lieferschein" ? t("inventurmassnahme.ls.title", "Lieferschein Test")
                : imScreen === "testkauf" ? t("inventurmassnahme.bw.title", "Bewertungsbogen")
                : imScreen === "archive" ? t("inventurmassnahme.hub.archiveTitle", "Archives hebdomadaires")
                : t("inventurmassnahme.hub.pageTitle", "Inventurmaßnahme")}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
        </header>
      )}

      {/* ---------- TOPBAR PREISÄNDERUNG ---------- */}
      {screen === "preisanderung" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => {
            if (paScreen === "admin" && paAdminScreen === "create") { setPaAdminScreen("list"); return; }
            if (paScreen === "admin") { setPaScreen("hub"); return; }
            paScreen === "hub" ? setScreen("home") : (setPaScreen("hub"), setPaActiveId(null));
          }} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, ...eyebrow(A.amber) }}>{t("preisanderung.eyebrow", "Preisänderung")}</p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {paScreen === "detail" ? (preisanderungen.find((p) => p.id === paActiveId)?.reference || "Preisänderung")
                : paScreen === "admin" ? (paAdminScreen === "create" ? t("preisanderung.admin.newTitle", "Nouvelle Preisänderung") : t("preisanderung.admin.manageTitle", "Gérer les envois"))
                : t("preisanderung.hub.title", "Preisänderung")}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          {/* Accès gestion réseau — remplace l'ancienne tuile fixe du Hub Administration
              (retour utilisateur explicite, 2026-08-16 : "l'admin n'en a pas besoin puisqu'il
              dispose des 2 vues compte normal et compte GR" — un NEXORA_ADMIN passe par CET
              écran, comme n'importe quel compte, avec ce bouton en plus, jamais une 3e
              destination séparée dans Administration). */}
          {isNexoraAdmin && paScreen === "hub" && (
            <Pressable onClick={() => { setPaAdminScreen("list"); loadPaAdminList(); setPaScreen("admin"); }} aria-label={t("preisanderung.admin.manageAria", "Gérer les envois (admin)")} style={{
              width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`,
            }}>⚙️</Pressable>
          )}
        </header>
      )}

      {/* ---------- TOPBAR WARENAUSTAUSCH ---------- */}
      {screen === "warenaustausch" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => waScreen === "hub" ? setScreen("home") : setWaScreen("hub")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, ...eyebrow(A.amber) }}>{t("warenaustausch.eyebrow", "Transferts")}</p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {waScreen === "new" ? t("warenaustausch.new.title", "Nouveau transfert") : waScreen === "receive" ? t("warenaustausch.receiveTitle", "Réception") : t("warenaustausch.title", "Warenaustausch")}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
        </header>
      )}

      {/* ---------- TOPBAR RR ---------- */}
      {screen === "rr" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => rrScreen === "hub" ? setScreen("home") : setRrScreen("hub")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, ...eyebrow(A.amber) }}>{t("rr.eyebrow", "RR")}</p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {rrScreen === "create" ? t("rr.create.title", "Nouveau bon RR") : rrScreen === "detail" ? (rrFindBon(rrDetailId)?.number || t("rr.title", "RR")) : t("rr.title", "RR")}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
        </header>
      )}
      {screen === "rr" && rrDemoMode && (
        <div style={{ margin: "0 18px 8px", padding: "6px 12px", borderRadius: 99, background: "rgba(231,138,18,.15)", border: `1.5px solid ${A.orange}`, textAlign: "center", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.orange, letterSpacing: ".03em" }}>
          🧪 {t("rr.demo.bannerText", "MODE DÉMO — aucune donnée réelle")}
        </div>
      )}

      {screen === "kontoauszug" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => kontoScreen === "hub" ? setScreen("home") : setKontoScreen("hub")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, ...eyebrow(A.navy) }}>{t("konto.eyebrow", "Kontoauszug")}</p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {kontoScreen === "create" ? t("konto.create.title", "Nouveau contrôle") : kontoScreen === "detail" ? t("konto.detail.title", "Contrôle") : t("konto.title", "Kontoauszug")}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
        </header>
      )}
      {screen === "kontoauszug" && kontoDemoMode && (
        <div style={{ margin: "0 18px 8px", padding: "6px 12px", borderRadius: 99, background: "rgba(231,138,18,.15)", border: `1.5px solid ${A.orange}`, textAlign: "center", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.orange, letterSpacing: ".03em" }}>
          🧪 {t("konto.demo.bannerText", "MODE DÉMO — aucune donnée réelle")}
        </div>
      )}

      {/* ---------- TOPBAR RAPPORT DE FILIALE ---------- */}
      {screen === "filialeReport" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => frScreen === "hub" ? setScreen("home") : setFrScreen("hub")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, ...eyebrow(A.teal) }}>{t("filialeReport.eyebrow", "Visite filiale")}</p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {frScreen === "new" ? t("filialeReport.newTitle", "Nouveau rapport") : frScreen === "detail" ? t("filialeReport.detailTitle", "Rapport") : t("filialeReport.hubTitle", "Rapport de filiale")}
            </h1>
          </div>
        </header>
      )}

      {/* ---------- TOPBAR VERTRETUNG ---------- */}
      {screen === "vertretung" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => vertretungScreen === "hub" ? setScreen("home") : setVertretungScreen("hub")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, ...eyebrow(A.navy) }}>{t("vertretung.eyebrow", "Grands responsables")}</p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {vertretungScreen === "create" ? t("vertretung.declareBtn", "Déclarer mon absence") : t("vertretung.hubTitle", "Vertretung")}
            </h1>
          </div>
        </header>
      )}

      {/* ---------- TOPBAR SCANNER (ASSOCIER EAN) ---------- */}
      {screen === "scanner" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => scanScreen === "hub" ? setScreen("home") : setScanScreen("hub")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.navy) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("scanner.hubKicker", "Choisis un rayon")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>{t("scanner.pageTitle", "Associer EAN")}</h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>{P.icon}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR PLU QUIZ ---------- */}
      {screen === "inventurmassnahme" && imScreen === "pluquiz" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => {
            if (pluScreen === "hub") { setImScreen("hub"); return; }
            if (pluScreen === "quiz" || pluScreen === "examIntro") { setPluScreen(quizMode === "exam" ? "hub" : "trainingHub"); return; }
            if (pluScreen === "report") { setPluScreen("hub"); return; }
            setPluScreen("hub");
          }} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.teal) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("plu.eyebrow", "PLU Quiz")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {{
                hub: t("plu.title.hub", "PLU Quiz"),
                trainingHub: t("plu.title.trainingHub", "Training"),
                quiz: quizMode === "exam" ? t("plu.title.examMode", "Examen") : t("plu.title.trainingMode", "Training"),
                examIntro: t("plu.title.examIntro", "Prêt à démarrer ?"),
                report: t("plu.title.report", "Résultat"),
                admin: t("plu.title.admin", "Listes PLU"),
              }[pluScreen]}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>{P.icon}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR KRANKMELDUNG ---------- */}
      {screen === "krank" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => krankScreen === "hub" ? setScreen("home") : setKrankScreen("hub")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.orange) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              Krankmeldung
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {{
                hub: t("krank.title.hub", "Déclaration d'arrêt maladie"),
                new: t("krank.title.new", "Nouvelle déclaration"),
                mine: t("krank.title.mine", "Mes déclarations"),
                store: t("krank.title.store", "Dossiers filiale"),
              }[krankScreen]}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>{P.icon}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR HACCP ---------- */}
      {screen === "haccp" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => haccpScreen === "hub" ? setScreen("home") : setHaccpScreen("hub")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.blue) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("haccp.pageEyebrow", "HACCP · Filialcheckliste")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {haccpScreen === "weekly" ? t("haccp.weeklyControlsTitle", "Contrôles hebdomadaires") : haccpScreen === "month" ? t("haccp.monthViewTitle", "Vue mensuelle") : t("haccp.dayControlsLabel", "Contrôles du jour")}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          {haccpScreen === "month" ? (
            <Pressable onClick={exportHaccpMonth} aria-label={t("haccp.exportPdfAria", "Imprimer / exporter")} style={{
              width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`, opacity: haccpExporting ? .5 : 1,
            }}>🖨️</Pressable>
          ) : (
            <Pressable onClick={() => setSheet("haccpJournal")} aria-label={t("haccp.journalLabel", "Journal")} style={{
              width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`,
            }}>📜</Pressable>
          )}
        </header>
      )}

      {/* ---------- TOPBAR RÉCLAMATIONS ---------- */}
      {screen === "claims" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => {
            if (claimsScreen === "hub") { setScreen("home"); return; }
            setClaimsScreen("hub");
          }} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.red) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("claims.eyebrow", "Qualité livraison")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {{
                hub: t("home.module.claims", "Réclamations"),
                new: t("claims.new.title", "Signaler un défaut"),
                history: t("reporting.hub.historyTitle", "Historique"),
                settings: t("claims.menu.settings", "Destination d'envoi"),
              }[claimsScreen]}
            </h1>
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px", color: P.muted, letterSpacing: ".03em" }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>{P.icon}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR TÂCHES ---------- */}
      {screen === "tasks" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          {/* "Nouvelle tâche" est désormais une Sheet (popup), plus une sous-page — cette
              en-tête n'a donc plus besoin de gérer son propre retour, la Sheet a sa propre
              croix de fermeture (SheetHead). Retour ← ramène toujours à l'accueil. */}
          <Pressable onClick={() => setScreen("home")} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.green) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("tasks.hub.title", "Organisation")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {taskFilter === "mine" ? t("tasks.filter.mineTitle", "To Do liste personnelle") : taskFilter === "assigned" ? t("tasks.filter.assignedTitle", "Mes tâches") : taskFilter === "team" ? t("tasks.filter.teamTitle", "Tâches de l'équipe") : taskFilter === "delegated" ? t("tasks.filter.delegatedTitle", "Tâches confiées") : t("tasks.filter.allTitle", "Toutes les tâches")}
            </h1>
            <span style={{
              fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px",
              color: P.muted, letterSpacing: ".03em",
            }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>{P.icon}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR PLANNING (seul module sans en-tête/retour jusqu'ici — cul-de-sac réel : le geste
         swipe-back cherche un élément aria-label={t("common.back", "Retour")}, absent de tout ce module, donc il s'animait puis
         revenait sur place sans jamais naviguer) ---------- */}
      {screen === "planning" && (
        <header className="nx-topbar" style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 18px 10px" }}>
          <Pressable onClick={() => {
            if (planScreen === "hub") { setScreen("home"); return; }
            setPlanScreen("hub");
          }} aria-label={t("common.back", "Retour")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>←</Pressable>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, display: "flex", alignItems: "center", gap: 6, ...eyebrow(A.purple) }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
              {t("tasks.hub.title", "Organisation")}
            </p>
            <h1 style={{ margin: "2px 0 3px", fontSize: `calc(25px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>
              {
                planScreen === "week" ? t("planning.hub.arbeitsplan.title", "Plan de travail") :
                planScreen === "requests" ? t("planning.hub.requests.title", "Demandes de planning") :
                planScreen === "vacation" ? t("planning.hub.vacation.title", "Solde de congés") :
                planScreen === "leistungsfrei" ? t("planning.hub.leistungsfrei.title", "Heures compensées") :
                planScreen === "holidays" ? t("planning.hub.holidays.title", "Jours fériés") :
                planScreen === "published" ? t("planning.hub.published.title", "Plans publiés") :
                planScreen === "calendar" ? t("planning.hub.calendar.title", "Calendrier annuel") :
                planScreen === "aushilfe" ? t("planning.hub.aushilfe.title", "Disponibilités des extras") :
                "Planning"
              }
            </h1>
            <span style={{
              fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "3px 11px",
              color: P.muted, letterSpacing: ".03em",
            }}>{t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`).toUpperCase()}</span>
          </div>
          <Pressable onClick={cycleTheme} aria-label={t("common.themeToggleAria", "Changer de thème")} style={{
            width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
          }}>{P.icon}</Pressable>
        </header>
      )}

      {/* ---------- TOPBAR RUN ---------- */}
      {inRun && (
        <header className="nx-topbar" style={{ padding: "16px 18px 6px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Pressable onClick={goBack} aria-label={t("common.back", "Retour")} style={{
              width: 38, height: 38, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`,
            }}>←</Pressable>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.01em" }}>{t("controlRun.pageTitle", "MAD Réfrigération")}</strong>
                <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{runIndex + 1} / {articles.length}</span>
              </div>
              <div style={{ height: 6, borderRadius: 99, background: P.soft, overflow: "hidden", marginTop: 5 }}>
                <div style={{ width: `${pct}%`, height: "100%", borderRadius: 99, background: A.green, transition: "width .55s cubic-bezier(.34,1.2,.64,1)" }} />
              </div>
            </div>
            <Pressable onClick={() => setSheet("list")} style={{
              fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, ...card, borderRadius: 99, padding: "9px 14px",
            }}>☰ {t("controlRun.listBtnLabel", "Liste")}</Pressable>
          </div>
        </header>
      )}

      {/* Merchandise > article : 165px (pas 130px) — retour utilisateur explicite, 2026-08-31,
          capture à l'appui : la carte "Commande" passait sous la barre ‹précédent/suivant›
          fixe (position:fixed, ~94px + son offset bottom + safe-area — 130px ne laissait
          quasi aucune marge, insuffisant une fois le vrai safe-area-inset-bottom compté). */}
      <main ref={mainScrollRef} style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch", padding: inRun ? "6px 18px 100px" : (screen === "messages" && msgScreen === "thread") ? `8px 18px ${keyboardOpen ? 10 : 180}px` : (screen === "merchandise" && merchScreen === "article") ? "6px 18px 165px" : (screen === "labels" && labelsScreen === "form") ? `8px 18px ${keyboardOpen ? 16 : 140}px` : (screen === "inventurmassnahme" && imScreen !== "hub") ? `8px 18px ${keyboardOpen ? 16 : 140}px` : "8px 18px 140px" }}>

        {/* ================= MA FICHE (écran dédié, Infos + Performance) =================
             Ne remplace pas la sheet "account" (menu rapide badge Accueil, reste inchangée) —
             accessible depuis celle-ci via "Ma fiche complète" (openMyFiche). */}
        {screen === "myFiche" && (
          <div className="nx-screen-enter">
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {[{ id: "infos", l: t("ficheEmployee.tabInfos", "Infos") }, { id: "performance", l: t("ficheEmployee.tabPerformance", "Performance") }].map((tab) => (
                <Pressable key={tab.id} onClick={() => setFicheScreen(tab.id)} style={{
                  flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: ficheScreen === tab.id ? A.blue : P.soft, color: ficheScreen === tab.id ? "#fff" : P.ink,
                }}>{tab.l}</Pressable>
              ))}
            </div>

            {ficheScreen === "infos" && (
              <>
                <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 18, marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 4 }}>
                    {user?.photoUrl ? (
                      <img src={user?.photoUrl} alt="" style={{ width: 62, height: 62, borderRadius: 99, objectFit: "cover", border: `3px solid ${P.bg}` }} />
                    ) : (
                      <span style={{
                        width: 62, height: 62, borderRadius: 99, color: "#fff",
                        background: `linear-gradient(155deg, rgba(255,255,255,.3), rgba(255,255,255,0) 55%), linear-gradient(135deg, ${A.blue}, #6f5ef0)`,
                        border: `3px solid ${P.bg}`, display: "grid", placeItems: "center", fontSize: `calc(21px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: "-.02em",
                      }}>{userInitials}</span>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", display: "block" }}>{user?.firstname} {user?.lastname || ""}</strong>
                      <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{myProfile?.roleLabel || user?.role} · {myStoresLabel}</span>
                    </div>
                  </div>
                </section>
                {myProfile && (
                  <section style={{ ...card, padding: 4, marginBottom: 14 }}>
                    {[
                      [t("ficheEmployee.civility", "Civilité"), myProfile.gender === "homme" ? t("common.titleMr", "M.") : myProfile.gender === "femme" ? t("common.titleMrs", "Mme") : "—"],
                      [t("ficheEmployee.contractType", "Type de contrat"), myProfile.contractType || "—"],
                      [t("ficheEmployee.weeklyHours", "Volume horaire hebdo"), myProfile.weeklyContractedHours != null ? `${myProfile.weeklyContractedHours} h` : "—"],
                      [t("planning.lfShort", "LF"), myProfile.leistungsfreiAvailableHours != null ? `${myProfile.leistungsfreiAvailableHours} h` : "—"],
                      [t("arbeitszeitliste.table.restUrlaub", "Rest-Urlaub"), myProfile.restUrlaubDays != null ? `${myProfile.restUrlaubDays} j` : "—"],
                      [t("ficheEmployee.phone", "Téléphone"), myProfile.phone || "—"],
                      [t("ficheEmployee.email", "E-mail"), myProfile.email || "—"],
                    ].map(([label, value], i, arr) => (
                      <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", borderBottom: i < arr.length - 1 ? `1px solid ${P.line}` : "none" }}>
                        <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{label}</span>
                        <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800 }}>{value}</span>
                      </div>
                    ))}
                  </section>
                )}
              </>
            )}

            {ficheScreen === "performance" && (
              <>
                {/* ===== Volet filiale — FL/FLA (une filiale) + BL/VL/NL (réseau agrégé) =====
                     Retour utilisateur 2026-08-15 : la performance d'un responsable se lit sur
                     le CA (Semaine+Mois vs Objectif), la gestion du Stundenleistung (Ist vs
                     Plan — même calcul que Pilotage réseau, jamais une moyenne de ratios par
                     filiale, voir fetchStoreStatsFor/loadFichePerfStats), les Pertes, la
                     réactivité des équipes sur Tâches/Missions réseau (2 chiffres distincts,
                     jamais combinés) — jamais Assiduité/Tâches perso/PLU Quiz, sans rapport
                     avec ce rôle. Réclamations Fruit&Légume/Food non-Food réservées à
                     isFlOrFlaRole (2026-08-16, retour utilisateur explicite : "la performance
                     d'un GR n'a pas besoin de la tuile réclamation" — traitée au niveau
                     filiale, jamais par le réseau qui supervise). Le CA/Stundenleistung/
                     Pertes/Réactivité d'un GR (BL/VL/NL) proviennent déjà de
                     identity.stores — pour un VKL/NL, ce périmètre est maintenant hérité de
                     ses subordonnés (voir sfRequestIdentity, server.js), donc "sa"
                     performance est déjà littéralement celle du travail accompli par ses BL,
                     jamais un chiffre personnel séparé — c'est le point central de la demande
                     "un VKL doit avoir ses performances liées au travail qu'effectue ses
                     BL". */}
                {(isNetworkLeader || isFlOrFlaRole) && (() => {
                  const aggregateFicheStats = (statsObj) => Object.values(statsObj || {}).reduce((acc, s) => {
                    acc.umsatzIst += s.umsatzIst; acc.umsatzPlan += s.umsatzPlan;
                    acc.stundenIst += s.stundenIst; acc.stundenPlan += s.stundenPlan || 0;
                    acc.pertes += s.pertes;
                    return acc;
                  }, { umsatzIst: 0, umsatzPlan: 0, stundenIst: 0, stundenPlan: 0, pertes: 0 });
                  const deriveFicheKpis = (agg) => {
                    const stundenleistung = agg.stundenIst > 0 ? agg.umsatzIst / agg.stundenIst : null;
                    const stundenleistungPlan = agg.stundenPlan > 0 ? agg.umsatzPlan / agg.stundenPlan : null;
                    const ecartPct = agg.umsatzPlan > 0 ? Math.round(((agg.umsatzIst - agg.umsatzPlan) / agg.umsatzPlan) * 100) : null;
                    const stundEcartPct = stundenleistungPlan > 0 && stundenleistung !== null ? Math.round(((stundenleistung - stundenleistungPlan) / stundenleistungPlan) * 100) : null;
                    const tauxPertes = agg.umsatzIst > 0 ? (agg.pertes / agg.umsatzIst) * 100 : 0;
                    return { ...agg, stundenleistung, stundenleistungPlan, ecartPct, stundEcartPct, tauxPertes };
                  };
                  const week = deriveFicheKpis(aggregateFicheStats(ficheWeekStats));
                  const month = deriveFicheKpis(aggregateFicheStats(ficheMonthStats));
                  const fmtEuroFiche = (v) => Math.round(v).toLocaleString("de-DE") + " €";
                  const hasAnyFicheStats = Object.keys(ficheWeekStats).length > 0 || Object.keys(ficheMonthStats).length > 0;

                  const periodBlock = (label, k) => (
                    <div style={{ flex: "1 1 140px", minWidth: 130 }}>
                      <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".03em" }}>{label}</span>
                      <strong style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`, display: "block", margin: "3px 0 1px", letterSpacing: "-.02em" }}>{fmtEuroFiche(k.umsatzIst)}</strong>
                      {k.umsatzPlan > 0 ? (
                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("pilotage.kpi.objTemplate", { amount: fmtEuroFiche(k.umsatzPlan) }, `Obj. ${fmtEuroFiche(k.umsatzPlan)}`)} · <span style={{ color: k.ecartPct >= 0 ? A.green : A.red }}>{k.ecartPct >= 0 ? "+" : ""}{k.ecartPct}%</span></span>
                      ) : (
                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("pilotage.kpi.noObjectiveSet", "Objectif non saisi")}</span>
                      )}
                    </div>
                  );
                  const stundenBlock = (label, k) => (
                    <div style={{ flex: "1 1 140px", minWidth: 130 }}>
                      <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".03em" }}>{label}</span>
                      <strong style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`, display: "block", margin: "3px 0 1px", letterSpacing: "-.02em" }}>{k.stundenleistung == null ? "—" : `${k.stundenleistung.toFixed(0)} €/h`}</strong>
                      {k.stundenleistungPlan != null ? (
                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("pilotage.kpi.objTemplate", { amount: `${k.stundenleistungPlan.toFixed(0)} €/h` }, `Obj. ${k.stundenleistungPlan.toFixed(0)} €/h`)} · <span style={{ color: k.stundEcartPct >= 0 ? A.green : A.red }}>{k.stundEcartPct >= 0 ? "+" : ""}{k.stundEcartPct}%</span></span>
                      ) : (
                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("pilotage.kpi.noObjectiveSet", "Objectif non saisi")}</span>
                      )}
                    </div>
                  );
                  const pertesColor = month.pertes === 0 ? A.green : month.tauxPertes < 1.2 ? A.green : month.tauxPertes < 1.8 ? A.orange : A.red;
                  const pertesLabel = month.pertes === 0 ? t("pilotage.kpi.status.none", "Aucune") : month.tauxPertes < 1.2 ? t("pilotage.kpi.status.controlled", "Maîtrisé") : month.tauxPertes < 1.8 ? t("pilotage.kpi.status.toWatch", "À surveiller") : t("pilotage.kpi.status.high", "Élevé");

                  return (
                    <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fit, minmax(340px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 12 : 0, alignItems: "start", marginBottom: isWide ? 12 : 14 }}>
                      <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 18, marginBottom: isWide ? 0 : 14 }}>
                        <span style={eyebrow(A.teal)}>{t("ficheEmployee.perf.filiale.umsatz", "Chiffre d'affaires")}</span>
                        {fichePerfLoading && !hasAnyFicheStats ? (
                          <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "8px 0 0" }}>{t("common.loading", "Chargement...")}</p>
                        ) : (
                          <div style={{ display: "flex", gap: 14, marginTop: 8, flexWrap: "wrap" }}>
                            {periodBlock(t("pilotage.period.week", "Cette semaine"), week)}
                            {periodBlock(t("pilotage.period.month", "Ce mois"), month)}
                          </div>
                        )}
                      </section>

                      <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 18, marginBottom: isWide ? 0 : 14 }}>
                        <span style={eyebrow(A.green)}>Stundenleistung</span>
                        {fichePerfLoading && !hasAnyFicheStats ? (
                          <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "8px 0 0" }}>{t("common.loading", "Chargement...")}</p>
                        ) : (
                          <div style={{ display: "flex", gap: 14, marginTop: 8, flexWrap: "wrap" }}>
                            {stundenBlock(t("pilotage.period.week", "Cette semaine"), week)}
                            {stundenBlock(t("pilotage.period.month", "Ce mois"), month)}
                          </div>
                        )}
                      </section>

                      <section style={{ ...colorCard(pertesColor), ...tile, flexDirection: "column", alignItems: "stretch", padding: 18, marginBottom: isWide ? 0 : 14 }}>
                        <span style={onColorTag}>{t("ficheEmployee.perf.filiale.pertes", "Pertes — mois en cours")}</span>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "8px 0 4px" }}>
                          <strong style={{ fontSize: `calc(26px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em" }}>{fmtEuroFiche(month.pertes)}</strong>
                          <span style={{ ...onColorSub, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{month.umsatzIst > 0 ? t("pilotage.kpi.pctOfRevenueTemplate", { pct: month.tauxPertes.toFixed(1) }, `${month.tauxPertes.toFixed(1)}% du CA`) : ""}</span>
                        </div>
                        <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, background: "rgba(255,255,255,.16)", color: "#fff", borderRadius: 99, padding: "6px 11px", display: "inline-block" }}>{pertesLabel}</span>
                      </section>

                      <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 18, marginBottom: isWide ? 0 : 14 }}>
                        <span style={eyebrow(A.purple)}>{t("ficheEmployee.perf.filiale.reactivity", "Réactivité équipes")}</span>
                        {ficheReactivity && (ficheReactivity.tasksTotal > 0 || ficheReactivity.missionsTotal > 0) ? (
                          <div style={{ display: "flex", gap: 14, marginTop: 8, flexWrap: "wrap" }}>
                            <div style={{ flex: "1 1 120px" }}>
                              <strong style={{ fontSize: `calc(22px * var(--nx-font-scale, 1))`, display: "block" }}>{ficheReactivity.tasksOnTimeRate == null ? "—" : `${ficheReactivity.tasksOnTimeRate}%`}</strong>
                              <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("ficheEmployee.perf.filiale.tasksOnTime", "Tâches réseau à temps")}</span>
                            </div>
                            <div style={{ flex: "1 1 120px" }}>
                              <strong style={{ fontSize: `calc(22px * var(--nx-font-scale, 1))`, display: "block" }}>{ficheReactivity.missionsOnTimeRate == null ? "—" : `${ficheReactivity.missionsOnTimeRate}%`}</strong>
                              <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("ficheEmployee.perf.filiale.missionsOnTime", "Missions réseau à temps")}</span>
                            </div>
                          </div>
                        ) : (
                          <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "8px 0 0" }}>{t("ficheEmployee.perf.filiale.noReactivity", "Aucune tâche/mission récente avec échéance.")}</p>
                        )}
                      </section>

                      {/* Réclamations — retour utilisateur explicite (2026-08-16) : "la
                          performance d'un GR n'a pas besoin de la tuile réclamation". GR =
                          responsable réseau (BL/VL/NL, isNetworkLeader) — une réclamation est
                          traitée au niveau filiale par le FL/FLA sur place, jamais par le
                          réseau qui la supervise, donc cette tuile reste réservée à
                          isFlOrFlaRole (rôle filiale, volet additif) et disparaît entièrement
                          pour un GR (volet qui REMPLACE, voir commentaire plus haut). */}
                      {isFlOrFlaRole && (
                        <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 18, marginBottom: isWide ? 0 : 14 }}>
                          <span style={eyebrow(A.red)}>{t("ficheEmployee.perf.filiale.claims", "Réclamations — mois en cours")}</span>
                          {ficheClaims && (ficheClaims.fresh + ficheClaims.food) > 0 ? (
                            <div style={{ display: "flex", gap: 7, marginTop: 8, flexWrap: "wrap" }}>
                              <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, background: P.soft, color: P.ink, borderRadius: 99, padding: "6px 11px" }}>{t("ficheEmployee.perf.filiale.claimsFreshTemplate", { n: ficheClaims.fresh }, `${ficheClaims.fresh} Fruit&Légume`)}</span>
                              <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, background: P.soft, color: P.ink, borderRadius: 99, padding: "6px 11px" }}>{t("ficheEmployee.perf.filiale.claimsFoodTemplate", { n: ficheClaims.food }, `${ficheClaims.food} Food non-Food`)}</span>
                            </div>
                          ) : (
                            <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "8px 0 0" }}>{t("ficheEmployee.perf.filiale.noClaims", "Aucune réclamation ce mois-ci.")}</p>
                          )}
                        </section>
                      )}
                    </div>
                  );
                })()}

                {!isNetworkLeader && (
                <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fit, minmax(340px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 12 : 0, alignItems: "start" }}>
                {/* Assiduité — réutilise directement la ligne de l'employé connecté déjà
                    calculée par Arbeitszeitliste (mois en cours), jamais un second calcul. */}
                <section style={{ ...colorCard(A.blue), ...tile, flexDirection: "column", alignItems: "stretch", padding: 18, marginBottom: isWide ? 0 : 14 }}>
                  <span style={onColorTag}>{t("ficheEmployee.perf.attendance", "Assiduité — mois en cours")}</span>
                  {myAzlSummary ? (
                    <>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "8px 0 4px" }}>
                        <strong style={{ fontSize: `calc(26px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em" }}>{azlFmtMinutes(myAzlSummary.workedMinutes)}</strong>
                        <span style={{ ...onColorSub, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{myAzlSummary.weeklyContractedHours != null ? t("ficheEmployee.perf.contractTemplate", { h: myAzlSummary.weeklyContractedHours }, `sur base contrat ${myAzlSummary.weeklyContractedHours} h/sem.`) : ""}</span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 8 }}>
                        {[
                          myAzlSummary.lfMinutes > 0 ? `${t("planning.lfShort", "LF")} ${azlFmtMinutes(myAzlSummary.lfMinutes)}` : null,
                          myAzlSummary.vertretungMinutes > 0 ? `${t("ficheEmployee.perf.vertretung", "Vertretung")} ${azlFmtMinutes(myAzlSummary.vertretungMinutes)}` : null,
                          myAzlSummary.krankDays > 0 ? `${t("arbeitszeitliste.table.sumK", "Σ K")} ${myAzlSummary.krankDays}` : null,
                          myAzlSummary.urlaubDays > 0 ? `${t("arbeitszeitliste.table.sumU", "Σ U")} ${myAzlSummary.urlaubDays}` : null,
                        ].filter(Boolean).map((chip, i) => (
                          <span key={i} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, background: "rgba(255,255,255,.16)", color: "#fff", borderRadius: 99, padding: "6px 11px" }}>{chip}</span>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p style={{ ...onColorSub, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "8px 0 0" }}>{t("ficheEmployee.perf.noAzlThisMonth", "Aucune donnée Arbeitszeitliste ce mois-ci.")}</p>
                  )}
                </section>

                {/* Tâches assignées personnellement — jamais les tâches ouvertes à toute la
                    filiale (pas un engagement individuel), voir GET /api/me/performance. */}
                <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 18, marginBottom: isWide ? 0 : 14 }}>
                  <span style={eyebrow(A.green)}>{t("ficheEmployee.perf.tasks", "Tâches assignées")}</span>
                  {myPerformance?.tasks && myPerformance.tasks.assignedTotal > 0 ? (
                    <>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "8px 0 8px" }}>
                        <strong style={{ fontSize: `calc(26px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em" }}>{myPerformance.tasks.onTimeRate != null ? `${myPerformance.tasks.onTimeRate}%` : "—"}</strong>
                        <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("ficheEmployee.perf.onTimeRate", "terminées à temps")}</span>
                      </div>
                      <div style={{ height: 9, borderRadius: 99, background: P.soft, overflow: "hidden", marginBottom: 10 }}>
                        <div style={{ width: `${myPerformance.tasks.onTimeRate || 0}%`, height: "100%", borderRadius: 99, background: A.green }} />
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                        <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, background: P.soft, color: P.ink, borderRadius: 99, padding: "6px 11px" }}>{t("ficheEmployee.perf.assignedTotalTemplate", { n: myPerformance.tasks.assignedTotal }, `${myPerformance.tasks.assignedTotal} au total`)}</span>
                        {myPerformance.tasks.pending > 0 && <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, background: P.soft, color: P.ink, borderRadius: 99, padding: "6px 11px" }}>{t("ficheEmployee.perf.pendingTemplate", { n: myPerformance.tasks.pending }, `${myPerformance.tasks.pending} en cours`)}</span>}
                        {myPerformance.tasks.overdue > 0 && <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, background: "rgba(239,68,68,.1)", color: A.red, borderRadius: 99, padding: "6px 11px" }}>{t("ficheEmployee.perf.overdueTemplate", { n: myPerformance.tasks.overdue }, `${myPerformance.tasks.overdue} en retard`)}</span>}
                      </div>
                    </>
                  ) : (
                    <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "8px 0 0" }}>{t("ficheEmployee.perf.noTasks", "Aucune tâche assignée personnellement pour l'instant.")}</p>
                  )}
                </section>

                {/* PLU Quiz — taux de réussite sur les examens réellement terminés. */}
                <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 18, marginBottom: isWide ? 0 : 14 }}>
                  <span style={eyebrow(A.orange)}>{t("ficheEmployee.perf.pluQuiz", "PLU Quiz")}</span>
                  {myPerformance?.pluQuiz && myPerformance.pluQuiz.attemptsTotal > 0 ? (
                    <>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "8px 0 8px" }}>
                        <strong style={{ fontSize: `calc(26px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em" }}>{myPerformance.pluQuiz.passRate}%</strong>
                        <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("ficheEmployee.perf.passRate", "taux de réussite")}</span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                        <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, background: P.soft, color: P.ink, borderRadius: 99, padding: "6px 11px" }}>{t("ficheEmployee.perf.attemptsTemplate", { n: myPerformance.pluQuiz.attemptsTotal }, `${myPerformance.pluQuiz.attemptsTotal} examen(s) passé(s)`)}</span>
                        {myPerformance.pluQuiz.lastAttempt && (
                          <span style={{
                            fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, borderRadius: 99, padding: "6px 11px",
                            background: myPerformance.pluQuiz.lastAttempt.passed ? "rgba(30,184,95,.12)" : "rgba(239,68,68,.1)",
                            color: myPerformance.pluQuiz.lastAttempt.passed ? A.green : A.red,
                          }}>{fmtFR(myPerformance.pluQuiz.lastAttempt.finishedAt.slice(0, 10))} · {myPerformance.pluQuiz.lastAttempt.percentage}%</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "8px 0 0" }}>{t("ficheEmployee.perf.noPlu", "Aucun examen PLU passé pour l'instant.")}</p>
                  )}
                </section>
                </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ================= ACCUEIL ================= */}
        {screen === "home" && !isNetworkLeader && (
          <div className="nx-screen-enter">
            {/* ASSISTANT — replié par défaut : juste un teaser + une flèche qui
                invite à glisser vers le bas. Un glissement (ou un tap sur la
                flèche) déplie la vraie phrase avec les tâches cliquables ;
                une fine barre draine pendant 8s puis l'assistant se referme
                tout seul — jamais besoin d'y retoucher. */}
            {(() => {
              const tasks = dayTasks;
              const moodColor = tasks.length === 0 ? A.blue : tasks.length <= 3 ? A.orange : A.red;
              const onAssistantPointerDown = (e) => {
                if (assistantOpen) return;
                assistantDragState.current = { dragging: true, startY: e.clientY };
                try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {}
              };
              const onAssistantPointerMove = (e) => {
                if (!assistantDragState.current.dragging) return;
                const dy = Math.max(0, Math.min(e.clientY - assistantDragState.current.startY, 44));
                assistantDragState.current.lastDy = dy;
                setAssistantDragY(dy);
              };
              const onAssistantPointerUp = () => {
                if (!assistantDragState.current.dragging) return;
                assistantDragState.current.dragging = false;
                const dy = assistantDragState.current.lastDy || 0;
                setAssistantDragY(0);
                if (dy > 20) openAssistant();
              };
              return (
                <section
                  onPointerDown={pauseAssistantTimer}
                  onPointerUp={resumeAssistantTimer} onPointerLeave={resumeAssistantTimer} onPointerCancel={resumeAssistantTimer}
                  style={{
                    ...card, borderRadius: 22, marginBottom: 16, overflow: "hidden",
                    transition: "box-shadow .3s ease",
                  }}>
                  <div style={{ display: "flex", gap: 13, alignItems: "flex-start", padding: 16, paddingBottom: assistantOpen ? 10 : 16 }}>
                    <span className="nx-bot" style={{
                      flex: "0 0 auto", width: 50, height: 50, borderRadius: 17,
                      display: "grid", placeItems: "center", fontSize: `calc(26px * var(--nx-font-scale, 1))`,
                      animation: "nxBot 2.6s ease-in-out infinite, nxBotRing 2.6s ease-out infinite",
                      transition: "background .4s ease",
                      ...premiumTileStyle(moodColor),
                    }}>🤖</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 6, ...eyebrow(moodColor) }}>
                        {t("home.assistant.eyebrowShort", "Ton assistant")}
                        <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
                      </span>
                      {assistantOpen && assistantTyping ? (
                        <div style={{ display: "flex", gap: 4, padding: "8px 0 2px" }}>
                          {[0, 1, 2].map((i) => <span key={i} className="nx-typing-dot" style={{ width: 7, height: 7, borderRadius: 99, background: P.muted, animationDelay: `${i * 0.15}s` }} />)}
                        </div>
                      ) : assistantOpen ? (
                        <p className="nx-insight-in" style={{ margin: "5px 0 0", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, lineHeight: 1.6, fontWeight: 600 }}>
                          {greetWord} {user?.firstname} ! {tasks.length === 0 ? (
                            t("home.assistant.doneAll", "Tout est fait pour aujourd'hui — magnifique travail 🎉 Repose-toi bien, à très bientôt !")
                          ) : (
                            <>
                              {t("home.assistant.remainingPrefix", "Aujourd'hui il te reste à")}{" "}
                              {tasks.map((t2, i) => {
                                const pColor = { urgent: A.red, medium: A.orange, low: A.blue }[t2.priority] || A.blue;
                                return (
                                  <Fragment key={t2.chip}>
                                    <Pressable onClick={() => goToDayTask(t2)} style={{
                                      display: "inline-block", color: "#fff", fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                                      background: pColor, borderRadius: 8, padding: "1.5px 8px", margin: "0 1px",
                                      lineHeight: 1.5, verticalAlign: "-1px",
                                    }}>{t2.label}</Pressable>
                                    {i < tasks.length - 2 ? ", " : i === tasks.length - 2 ? t("home.assistant.and", " et ") : ""}
                                  </Fragment>
                                );
                              })}
                              {t("home.assistant.remainingSuffix", ". Courage, à très bientôt !")} 💪
                            </>
                          )}
                        </p>
                      ) : (
                        <p style={{ margin: "5px 0 0", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, lineHeight: 1.4, fontWeight: 600 }}>
                          {tasks.length === 0 ? t("home.assistant.allUpToDate", "Tout est à jour aujourd'hui 🎉") : t("home.assistant.peekCount", { n: tasks.length }, `${tasks.length} tâche${tasks.length > 1 ? "s" : ""} t'attend${tasks.length > 1 ? "ent" : ""} aujourd'hui`)}
                        </p>
                      )}
                    </div>
                    {assistantOpen && (
                      <Pressable onClick={closeAssistant} aria-label={t("home.assistant.collapseAria", "Replier l'assistant")} style={{
                        flex: "0 0 auto", width: 26, height: 26, borderRadius: 99, background: P.soft,
                        display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 1,
                      }}>✕</Pressable>
                    )}
                  </div>

                  {assistantOpen ? (
                    <div key={assistantOpenedAt} className="nx-assistant-timer" style={{
                      height: 2.5, background: moodColor, opacity: .4, animationDuration: `${assistantAutoCloseMs}ms`,
                      animationPlayState: assistantHeld ? "paused" : "running",
                    }} />
                  ) : (
                    <div
                      className="nx-assistant-peek"
                      onPointerDown={onAssistantPointerDown} onPointerMove={onAssistantPointerMove}
                      onPointerUp={onAssistantPointerUp} onPointerCancel={onAssistantPointerUp}
                      onClick={openAssistant}
                      style={{
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                        padding: "2px 0 12px", touchAction: "none",
                        transform: `translateY(${assistantDragY * 0.4}px)`,
                      }}
                    >
                      <svg className="nx-assistant-chevron" width="18" height="9" viewBox="0 0 18 9" fill="none">
                        <path d="M1 1L9 8L17 1" stroke={P.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>{t("home.assistant.swipeHint", "Glisse pour voir tes tâches du jour")}</span>
                    </div>
                  )}
                </section>
              );
            })()}

            {/* Modules — Mis en avant (liste groupée, décidée côté Admin, voir
                server/homeHighlights/routes.js) + Tous les modules repliés (retour
                utilisateur explicite, 2026-11-02 : "très peu de personnes utilisent
                l'app... un favoris pour tout les utilisateurs groupée" plutôt qu'une
                personnalisation par utilisateur qui ne serait jamais faite). Jamais
                appliqué à l'écran GR (NETWORK_HOME_MODULES, plus bas) — pas concerné. */}
            {(() => {
              const visibleModules = HOME_MODULES.filter((m) => !isModuleHiddenForUser(m.id));
              // FL/FLA/Stellvertreter·in peuvent décider pour LEUR PROPRE filiale (retour
              // utilisateur explicite, "donner accès à FL, FLA et Stellvertreter aussi") —
              // le serveur scope déjà tout seul au storeId du compte connecté (jamais un
              // storeId choisi côté client), ce bouton n'a qu'à être visible au bon rôle.
              const canEditHighlighted = ["FL", "FLA", "Stellvertreter", "Stellvertreterin"].includes(user?.role);
              const highlightedSet = new Set(highlightedModuleIds);
              const pinnedModules = visibleModules.filter((m) => highlightedSet.has(m.id));
              const restModules = visibleModules.filter((m) => !highlightedSet.has(m.id));
              // Ossature desktop Phase 2 (écran par écran) — sur téléphone, grille 4
              // colonnes fixe strictement inchangée. Sur desktop, la grille se déploie en
              // autant de colonnes que la largeur du shell élargi le permet plutôt que de
              // rester cantonnée à 4 colonnes étriquées dans 1100px de large.
              const gridCols = isWide ? "repeat(auto-fill, minmax(110px, 1fr))" : "1fr 1fr 1fr 1fr";
              const renderModuleTile = (m, mIdx) => (
                <Pressable key={m.id} className="nx-list-item-in"
                  onClick={() => openModule(m)}
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "4px 0", position: "relative", animationDelay: `${Math.min(mIdx, 14) * 28}ms` }}>
                  <span style={{
                    width: 54, height: 54, borderRadius: 18, color: "#fff",
                    border: "1px solid rgba(255,255,255,.16)",
                    display: "grid", placeItems: "center", fontSize: `calc(24px * var(--nx-font-scale, 1))`,
                    ...premiumTileStyle(m.color),
                  }}>{m.id === "control" ? <ShieldIcon size={24} color="#fff" /> : <EmojiIcon e={m.emoji} size={24} />}</span>
                  {m.id === "notifications" && notifUnreadCount > 0 && (
                    <span className="nx-notif-dot" style={{
                      position: "absolute", top: -2, right: "calc(50% - 32px)", minWidth: 18, height: 18, padding: "0 4px",
                      borderRadius: 99, background: A.red, border: `2px solid ${P.bg}`, color: "#fff",
                      fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, display: "grid", placeItems: "center",
                    }}>{notifUnreadCount}</span>
                  )}
                  {/* Retour utilisateur explicite (2026-08-08) : badge Contrôle retiré — un
                      vrai catalogue actif a quasiment toujours au moins un des 4 signaux
                      (Kühlung à vérifier, MHD urgente, nouvel article à valider, livraison
                      pas encore réceptionnée) vrai un jour donné, donc perçu comme "toujours
                      là" plutôt que comme une vraie alerte ponctuelle. Le calcul (dayTasks)
                      reste inchangé — utilisé ailleurs (carte Assistant, popup "Tâches à
                      faire") — seul l'affichage du badge sur CETTE tuile est retiré. */}
                  {m.id !== "notifications" && m.id !== "control" && pendingModuleCounts[m.id] > 0 && (
                    <span className="nx-notif-dot" style={{
                      position: "absolute", top: -2, right: "calc(50% - 32px)", minWidth: 18, height: 18, padding: "0 4px",
                      borderRadius: 99, background: A.red, border: `2px solid ${P.bg}`, color: "#fff",
                      fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, display: "grid", placeItems: "center",
                    }}>{pendingModuleCounts[m.id]}</span>
                  )}
                  <span style={{
                    fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink, textAlign: "center", lineHeight: 1.15,
                    maxWidth: 74, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box",
                    WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                  }}>{t(`home.module.${m.id}`, m.label)}</span>
                </Pressable>
              );
              return (
                <>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", margin: "2px 2px 11px" }}>
                    <h3 className="nx-shimmer-text" style={{
                      margin: 0, fontSize: `calc(16.5px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: "-.02em", "--nx-ink": P.ink,
                    }}>{t("home.section.modules", "Modules")}</h3>
                    {/* Retour utilisateur explicite (2026-08-16, capture "26 accès" ne correspondant
                        pas au nombre réel de tuiles affichées) : ce compteur doit toujours refléter
                        le nombre EXACT de modules que CET utilisateur voit sur son Accueil, mis en
                        avant + repliés confondus — jamais le total de tous les modules définis dans
                        le code. */}
                    <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>
                        {t("home.section.accessCount", { n: visibleModules.length }, `${visibleModules.length} accès`)}
                      </span>
                      {canEditHighlighted && (
                        <Pressable onClick={() => setHomeHighlightsEditOpen((v) => !v)}
                          aria-label={t("home.highlightsEdit.toggleAria", "Choisir les modules mis en avant")}
                          style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: homeHighlightsEditOpen ? A.purple : P.muted }}>
                          ✏️
                        </Pressable>
                      )}
                    </span>
                  </div>

                  {canEditHighlighted && homeHighlightsEditOpen ? (
                    <>
                      <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 12px", lineHeight: 1.5 }}>
                        {t("home.highlightsEdit.hint", "Choisis les modules à afficher en tête de l'Accueil de ta filiale — pour tout le monde dans cette filiale, pas seulement pour toi.")}
                      </p>
                      {visibleModules.map((m) => {
                        const on = highlightedSet.has(m.id);
                        return (
                          <Pressable key={m.id} onClick={() => toggleHighlightedModule(m.id)} style={{
                            display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                            padding: "10px 13px", background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 12, marginBottom: 6,
                          }}>
                            <span style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>
                              {m.id === "control" ? <ShieldIcon size={16} color={P.ink} /> : <EmojiIcon e={m.emoji} size={16} />}
                            </span>
                            <span style={{ flex: 1, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink }}>{t(`home.module.${m.id}`, m.label)}</span>
                            <span style={{
                              width: 34, height: 20, borderRadius: 99, flex: "0 0 auto", position: "relative",
                              background: on ? A.purple : P.soft, border: on ? "none" : `1.5px solid ${P.line}`,
                              transition: "background .15s ease",
                            }}>
                              <span style={{
                                position: "absolute", top: 2, width: 14, height: 14, borderRadius: 99, background: on ? "#fff" : P.muted,
                                boxShadow: "0 1px 3px rgba(0,0,0,.25)", left: on ? 18 : 2, transition: "left .15s ease",
                              }} />
                            </span>
                          </Pressable>
                        );
                      })}
                      <Pressable onClick={() => setHomeHighlightsEditOpen(false)} style={{
                        width: "100%", textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 800,
                        fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: "#fff", background: A.purple, marginTop: 4,
                      }}>{t("home.highlightsEdit.done", "Terminé")}</Pressable>
                    </>
                  ) : (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 10 }}>
                        {pinnedModules.map(renderModuleTile)}
                      </div>
                      {restModules.length > 0 && (
                        <>
                          <Pressable onClick={() => setHomeAllModulesOpen((v) => !v)} aria-expanded={homeAllModulesOpen} style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
                            padding: "13px 2px", marginTop: 6, borderTop: `1px solid ${P.line}`,
                          }}>
                            <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink }}>{t("home.section.allModules", "Tous les modules")}</span>
                            <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                              {t("home.section.allModulesCount", { n: restModules.length }, `${restModules.length} autres`)} {homeAllModulesOpen ? "▾" : "▸"}
                            </span>
                          </Pressable>
                          {homeAllModulesOpen && (
                            <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 10, marginTop: 10 }}>
                              {restModules.map(renderModuleTile)}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              );
            })()}

            {/* Action Live — en bas de l'accueil, alimentée par les vraies actions */}
            <section style={{
              ...card, borderRadius: 22, marginTop: 16, overflow: "hidden",
              border: `1px solid ${themeId === "sombre" ? "rgba(124,92,242,.25)" : "rgba(54,79,231,.16)"}`,
              boxShadow: `${P.shadow}, 0 0 0 1px rgba(124,92,242,.05)`,
            }}>
              <Pressable onClick={() => setLiveOpen((v) => !v)} style={{
                display: "flex", alignItems: "center", gap: 12, width: "100%", boxSizing: "border-box", padding: "13px 15px", color: P.ink,
              }}>
                <span style={{
                  width: 42, height: 42, borderRadius: 15, color: "#fff",
                  border: "1px solid rgba(255,255,255,.22)",
                  display: "grid", placeItems: "center", fontSize: `calc(19px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                  ...premiumTileStyle("#364fe7"),
                }}><span className="nx-bolt">⚡</span></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span className="nx-gradient-text" style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".08em" }}>{t("home.section.live", "Action Live")}</span>
                    <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
                  </span>
                  <span style={{
                    fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", overflow: "hidden",
                    textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2,
                  }}>{homeActivity[0] ? homeActivity[0].text : (liveActions[0] ? liveActions[0].text : t("home.section.liveEmptyText", "Aucune activité enregistrée aujourd'hui."))}</span>
                </div>
                <span style={{
                  fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: P.muted, transform: liveOpen ? "rotate(90deg)" : "none", transition: "transform .25s ease",
                }}>›</span>
              </Pressable>
              {liveOpen && (
                <div style={{ padding: "0 15px 13px" }}>
                  {homeActivity.map((n) => (
                    <Pressable key={n.key} onClick={() => { if (n.go === "planning") setPlanScreen("requests"); if (n.go === "tasks") { setTasksScreen("list"); setTaskScope("store"); setTaskTab("open"); setTaskFilter(null); } if (n.go === "haccp") setHaccpScreen("hub"); if (n.go === "claims") setClaimsScreen("hub"); if (n.go === "krank") setKrankScreen("hub"); if (n.go === "material") setMoScreen("hub"); if (n.go === "infos") setInfoScreen("hub"); if (n.go === "messages") setMsgScreen("list"); if (n.go === "reporting") setRepScreen("hub"); if (n.go === "finder") setFinderScreen("hub"); if (n.go === "liefer") setLfScreen("hub"); setScreen(n.go); }} style={{
                      display: "flex", gap: 10, padding: "10px 11px", borderRadius: 14, width: "100%", boxSizing: "border-box",
                      background: P.soft, marginBottom: 7, alignItems: "flex-start", color: P.ink,
                    }}>
                      <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>{n.emoji}</span>
                      <span style={{ flex: 1, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, lineHeight: 1.4, fontWeight: 600 }}>{n.text}</span>
                      <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted }}>›</span>
                    </Pressable>
                  ))}
                  {liveActions.map((n, i) => (
                    <div key={i} style={{
                      display: "flex", gap: 10, padding: "10px 11px", borderRadius: 14,
                      background: P.soft, marginBottom: 7, alignItems: "flex-start",
                    }}>
                      <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>{n.emoji}</span>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", lineHeight: 1.4 }}>{n.text}</span>
                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{n.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {/* ================= HUB CONTRÔLE ================= */}
        {screen === "hub" && (
          <>
            <section style={{ ...card, ...tile, justifyContent: "space-between", padding: 19 }}>
              <div style={{ minWidth: 0 }}>
                <span style={eyebrow(P.muted)}>{t("control.eyebrowToday", "Aujourd'hui")}</span>
                <h2 style={{ margin: "4px 0 4px", fontSize: `calc(19px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em" }}>{t("control.heroCountTemplate", { checked: checkedCount, total: articles.length }, `${checkedCount} sur ${articles.length} articles`)}</h2>
                <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.4 }}>
                  {checkedCount >= articles.length
                    ? t("control.hub.allDone", "Tous les articles Kühlung sont contrôlés aujourd'hui.")
                    : t("control.hub.remaining", { n: articles.length - checkedCount }, `${articles.length - checkedCount} article${articles.length - checkedCount > 1 ? "s" : ""} Kühlung restant${articles.length - checkedCount > 1 ? "s" : ""} à contrôler.`)}
                </p>
              </div>
              <Ring pct={pct} P={P} />
            </section>

            {[
              { s: "mhdHub", bg: A.frost, emoji: "❄️", tag: "MHD", t: t("control.hub.mhdTitle", "Contrôle MHD"), d: t("control.hub.mhdDesc", "Kühlung, livraison, Trocken, Frisch Fleisch, Frisch Brot.") },
              { s: "store", bg: A.purple, emoji: "🏷️", tag: t("control.storeTag", "Magasin"), t: t("control.storeCardTitle", "DLC Articles promotionnels"), d: t("control.storeCardDesc", "Saisie libre des DLC pour les articles promotionnels.") },
              { s: "newArticles", bg: A.orange, emoji: "🏷️", tag: t("control.newArticlesTag", "Kühlung"), t: t("control.hub.newArticlesTitle", "DLC Articles promotionnels kühlung"), d: t("control.newArticlesCardDesc", "Articles reçus mais absents du catalogue officiel Kühlung.") },
              { s: "newArticlesFF", bg: A.red, emoji: "🥩", tag: t("control.newArticlesFFTag", "Frisch Fleisch"), t: t("control.hub.newArticlesFFTitle", "DLC Articles promotionnels Frisch Fleisch"), d: t("control.newArticlesFFCardDesc", "Articles reçus mais absents du catalogue officiel Frisch Fleisch.") },
            ].map((x) => (
              <Pressable key={x.s} onClick={() => setScreen(x.s)} style={{ ...colorCard(x.bg), ...tile }}>
                <div style={onColorChip}><EmojiIcon e={x.emoji} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={onColorTag}>{x.tag}</span>
                  <h2 style={{ margin: "3px 0 3px", fontSize: `calc(16.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{x.t}</h2>
                  <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, lineHeight: 1.35, ...onColorSub }}>{x.d}</p>
                </div>
                <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(19px * var(--nx-font-scale, 1))`, fontWeight: 300 }}>›</span>
              </Pressable>
            ))}
          </>
        )}

        {/* ================= HUB MHD ================= */}
        {screen === "mhdHub" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[
              { id: "kuhlung", label: "Kühlung", sub: t("control.mhdHub.kuhlungSub", "Contrôle quotidien du rayon"), emoji: "❄️", ready: true, bg: A.frost },
              { id: "trocken", label: "Trocken", sub: t("control.mhdHub.trockenSub", "Épicerie sèche, conserves"), emoji: "📦", ready: true, bg: A.orange },
              { id: "fleisch", label: "Frisch Fleisch", sub: t("control.mhdHub.fleischSub", "Viande fraîche"), emoji: "🥩", ready: true, bg: A.red },
              { id: "brot", label: "Frisch Brot", sub: t("control.mhdHub.brotSub", "Boulangerie fraîche"), emoji: "🍞", ready: true, bg: A.brown },
            ].map((c) => (
              <Pressable key={c.id}
                onClick={() => { if (c.id === "kuhlung") setScreen("kuhlung"); else if (CONTROL_ZONES[c.id]) openCzZone(c.id); else { setSoonCat(c); setScreen("soon"); } }}
                style={{
                  ...colorCard(c.bg), padding: 15, position: "relative", minHeight: 132,
                  display: "flex", flexDirection: "column", justifyContent: "flex-end",
                  opacity: c.ready ? 1 : .78, filter: c.ready ? "none" : "saturate(.75)",
                }}>
                <span style={{
                  position: "absolute", top: 11, right: 11, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800,
                  letterSpacing: ".08em", textTransform: "uppercase", padding: "4px 9px", borderRadius: 99,
                  background: "rgba(255,255,255,.25)", color: "#fff",
                }}>{c.ready ? t("control.categoryActive", "Actif") : t("control.categorySoon", "Bientôt")}</span>
                <div style={{ ...onColorChip, width: 44, height: 44, marginBottom: 11 }}>{c.emoji}</div>
                <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, display: "block", letterSpacing: "-.01em", color: "#fff" }}>{c.label}</strong>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, lineHeight: 1.3, ...onColorSub }}>{c.sub}</span>
              </Pressable>
            ))}
          </div>
        )}

        {/* ================= SAISIE MHD — TROCKEN / FRISCH FLEISCH / FRISCH BROT =================
            2026-08-05, retour utilisateur explicite : "comme Kühlung", catalogue dédié + écran de
            saisie MHD. Reprend le catalogue déjà existant (ARTICLE_ZONES/getArticlesForZone,
            aucun nouveau code catalogue) mais PAS le scan séquentiel position-par-position de
            Kühlung (écran délibérément simplifié — recherche+sélection, patron proche de
            "Livraison" ci-dessus) : à signaler comme réduction de périmètre si l'utilisateur en
            reparle. */}
        {screen === "czEntry" && czZone && (
          <div className="nx-screen-enter">
            <section style={{ ...colorCard(czZone.bg), ...tile, flexDirection: "column", alignItems: "stretch", padding: 19 }}>
              <div style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
                <div style={onColorChip}><EmojiIcon e={czZone.emoji} /></div>
                <div style={{ flex: 1 }}>
                  <span style={onColorTag}>{t("control.mhdHub.zoneEyebrow", "Contrôle MHD")}</span>
                  <h2 style={{ margin: "3px 0 3px", fontSize: `calc(17.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{czZone.label}</h2>
                  <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("control.czEntry.subtitle", "Rechercher un article et enregistrer sa date de MAD.")}</p>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                {[
                  { v: czRecords.filter((r) => r.source === czZone.source).length, l: t("control.czEntry.mhdSavedLabel", "MHD enregistrées") },
                  { v: getArticlesForZone(czZone.zone).length, l: t("control.czEntry.catalogLabel", "Articles au catalogue") },
                ].map((s) => (
                  <div key={s.l} style={{ flex: 1, textAlign: "center", background: "rgba(255,255,255,.18)", borderRadius: 14, padding: "9px 0" }}>
                    <strong style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, color: "#fff", display: "block", lineHeight: 1.1 }}>{s.v}</strong>
                    <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "rgba(255,255,255,.8)" }}>{s.l}</span>
                  </div>
                ))}
              </div>
            </section>

            <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 15 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <input style={{ ...inputStyle, flex: 1, boxSizing: "border-box" }}
                  placeholder={t("controlRun.searchPlaceholder", "Rechercher Art.Nr ou nom...")}
                  value={czQuery} onChange={(e) => setCzQuery(e.target.value)} />
                <Pressable onClick={() => doScan("cz")} aria-label={t("controlRun.scanBarcodeBtn", "Scanner le code-barres")} style={{
                  flex: "0 0 auto", width: 46, borderRadius: 14, display: "grid", placeItems: "center",
                  background: czZone.bg, color: "#fff", fontSize: `calc(17px * var(--nx-font-scale, 1))`, animation: scanning ? "nxPulse 1s ease infinite" : "none",
                }}>📷</Pressable>
              </div>
              {czQuery.trim() && (
                <div style={{ marginTop: 10 }}>
                  {getArticlesForZone(czZone.zone).filter((a) => {
                    const q = czQuery.trim().toLowerCase();
                    return a.name.toLowerCase().includes(q) || a.artNr.includes(q);
                  }).slice(0, 8).map((a) => (
                    <Pressable key={a.artNr} onClick={() => { openCzMhdForm(a, null); setCzQuery(""); }} style={{
                      display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                      padding: "11px 12px", borderRadius: 14, background: P.soft, marginBottom: 6, color: P.ink,
                    }}>
                      <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{czZone.emoji}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</strong>
                        <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlRun.artNrPrefix", "Art.Nr")} {a.artNr}</span>
                      </div>
                      <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted }}>›</span>
                    </Pressable>
                  ))}
                  {getArticlesForZone(czZone.zone).filter((a) => {
                    const q = czQuery.trim().toLowerCase();
                    return a.name.toLowerCase().includes(q) || a.artNr.includes(q);
                  }).length === 0 && (
                    <p style={{ margin: "10px 2px 2px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("control.czEntry.noArticleFound", "Aucun article trouvé dans ce catalogue.")}</p>
                  )}
                </div>
              )}
              {!czQuery.trim() && !czMhdArt && (
                <p style={{ margin: "10px 2px 2px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("control.czEntry.searchHint", "Tape un numéro d'article ou un nom pour commencer.")}</p>
              )}
              {!czMhdArt && (
                <Pressable onClick={openCzManualArticle} style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 7, width: "100%", boxSizing: "border-box",
                  marginTop: 10, padding: "12px 0", borderRadius: 14, background: P.soft, border: `1.5px dashed ${P.line}`,
                  fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.ink,
                }}>＋ {t("control.czEntry.addManualArticleBtn", "Ajouter un article (hors catalogue)")}</Pressable>
              )}
            </section>

            {czMhdArt && (
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 16, border: `1.5px solid ${czZone.bg}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: `calc(20px * var(--nx-font-scale, 1))`}}>{czZone.emoji}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {czMhdArt.manual ? (
                      <>
                        <input style={{ ...inputStyle, padding: "8px 10px", fontSize: `calc(14px * var(--nx-font-scale, 1))`, fontWeight: 700, marginBottom: 5 }} autoFocus
                          placeholder={t("control.czEntry.manualNamePlaceholder", "Nom de l'article")}
                          value={czMhdArt.name} onChange={(e) => setCzMhdArt((a) => ({ ...a, name: e.target.value }))} />
                        <input style={{ ...inputStyle, padding: "6px 9px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`}}
                          placeholder={t("control.czEntry.manualArtNrPlaceholder", "Art.Nr (optionnel)")}
                          value={czMhdArt.artNr} onChange={(e) => setCzMhdArt((a) => ({ ...a, artNr: e.target.value }))} />
                      </>
                    ) : (
                      <>
                        <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`, display: "block" }}>{czMhdArt.name}</strong>
                        <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlRun.artNrPrefix", "Art.Nr")} {czMhdArt.artNr}</span>
                      </>
                    )}
                  </div>
                  <Pressable onClick={() => { setCzMhdArt(null); setCzEditId(null); }} aria-label={t("common.cancelSelectionAria", "Annuler la sélection")} style={{
                    width: 30, height: 30, borderRadius: 99, background: P.soft, color: P.ink,
                    display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  }}>✕</Pressable>
                </div>

                <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 11, paddingBottom: 2 }}>
                  {QUICK_DAYS.map((n) => {
                    const iso = addDays(n);
                    const sel = czMhdForm.mhd === iso;
                    const u = urgency(iso);
                    return (
                      <Pressable key={n} onClick={() => setCzMhdForm((f) => ({ ...f, mhd: iso }))} style={{
                        flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                        background: sel ? urgColor[u] : P.soft, color: sel ? "#fff" : P.ink,
                        border: `1.5px solid ${sel ? urgColor[u] : P.line}`,
                      }}>+{n}j</Pressable>
                    );
                  })}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    display: "flex", alignItems: "center", borderRadius: 16,
                    background: P.soft, border: `1.5px solid ${P.line}`, overflow: "hidden",
                  }}>
                    <HoldButton onTap={() => { setCzMhdForm((f) => ({ ...f, qty: String(Math.max(1, Number(f.qty || 1) - 1)) })); }}
                      onRepeat={() => setCzMhdForm((f) => ({ ...f, qty: String(Math.max(1, Number(f.qty || 1) - 1)) }))}
                      label={t("common.decreaseHoldAria", "Moins (maintenir pour accélérer)")} style={{
                        width: 54, height: 54, display: "grid", placeItems: "center", fontSize: `calc(25px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink, background: "none",
                      }}>−</HoldButton>
                    <div style={{ minWidth: 52, textAlign: "center" }}>
                      <strong style={{ fontSize: `calc(21px * var(--nx-font-scale, 1))`}}>{czMhdForm.qty}</strong>
                      <span style={{ display: "block", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, letterSpacing: ".08em" }}>{t("common.qtyShortLabel", "QTÉ")}</span>
                    </div>
                    <HoldButton onTap={() => { setCzMhdForm((f) => ({ ...f, qty: String(Math.min(999, Number(f.qty || 1) + 1)) })); }}
                      onRepeat={() => setCzMhdForm((f) => ({ ...f, qty: String(Math.min(999, Number(f.qty || 1) + 1)) }))}
                      label={t("common.increaseHoldAria", "Plus (maintenir pour accélérer)")} style={{
                        width: 54, height: 54, display: "grid", placeItems: "center", fontSize: `calc(25px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink, background: "none",
                      }}>＋</HoldButton>
                  </div>
                  <label style={{ position: "relative", flex: 1, display: "block" }}>
                    <div style={{
                      textAlign: "center", padding: "12px 8px", borderRadius: 16,
                      background: P.soft, border: `1.5px solid ${czMhdForm.mhd ? urgColor[urgency(czMhdForm.mhd)] : P.line}`,
                      fontWeight: 800, fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: czMhdForm.mhd ? P.ink : P.muted,
                    }}>{czMhdForm.mhd ? `${fmtDay(czMhdForm.mhd)} ${fmtFR(czMhdForm.mhd)}` : `📅 ${t("common.otherDatePlaceholder", "Autre date")}`}</div>
                    <input type="date" min={todayISO()} value={czMhdForm.mhd} onChange={(e) => setCzMhdForm((f) => ({ ...f, mhd: e.target.value }))}
                      aria-label={t("common.receivedMhdDateAria", "Date MHD reçue")}
                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer", colorScheme: themeId === "sombre" ? "dark" : "light" }} />
                  </label>
                </div>

                <input style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginTop: 10 }}
                  placeholder={t("control.czEntry.notePlaceholder", "Note (optionnel)")}
                  value={czMhdForm.note} onChange={(e) => setCzMhdForm((f) => ({ ...f, note: e.target.value }))} />

                <input ref={czPhotoInputRef} type="file" accept="image/*" style={{ display: "none" }}
                  onChange={(e) => { uploadCzPhoto(e.target.files); e.target.value = ""; }} />
                <Pressable onClick={() => czMhdForm.photo ? setCzMhdForm((f) => ({ ...f, photo: "" })) : czPhotoInputRef.current?.click()} style={{
                  display: "flex", alignItems: "center", gap: 9, padding: "11px 13px", borderRadius: 14, marginTop: 10,
                  background: czMhdForm.photo ? "rgba(54,79,231,.1)" : P.soft, border: `1.5px solid ${czMhdForm.photo ? A.blue : P.line}`,
                }}>
                  <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{czMhdForm.photo ? "✅" : "📷"}</span>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: czMhdForm.photo ? A.blue : P.ink }}>
                    {czUploading ? t("haccp.uploadingPhoto", "Envoi de la photo...") : czMhdForm.photo ? t("haccp.photoAttachedLabel", "Photo jointe") : t("haccp.addPhotoOptional", "Ajouter une photo (optionnel)")}
                  </span>
                </Pressable>

                <Pressable onClick={saveCzMhd} style={{
                  marginTop: 12, textAlign: "center", padding: "16px 0", borderRadius: 18, fontWeight: 800, fontSize: `calc(15.5px * var(--nx-font-scale, 1))`,
                  background: czZone.bg, color: "#fff", boxShadow: "0 12px 26px rgba(0,0,0,.22)",
                }}>✓ {czEditId ? t("control.czEntry.updateBtn", "Mettre à jour la MHD") : t("control.czEntry.saveBtn", "Enregistrer la MHD")}</Pressable>
              </section>
            )}

            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", margin: "4px 2px 9px" }}>
              <h3 style={{ margin: 0, fontSize: `calc(15.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em" }}>{t("control.czEntry.savedListTitle", "MHD enregistrées")}</h3>
              <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>{czRecords.filter((r) => r.source === czZone.source).length}</span>
            </div>
            {czRecords.filter((r) => r.source === czZone.source).length === 0 && (
              <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px 10px" }}>{t("control.czEntry.emptyList", "Aucune MHD enregistrée pour l'instant dans cette catégorie.")}</p>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
            {czRecords.filter((r) => r.source === czZone.source).sort((a, b) => a.mhd.localeCompare(b.mhd)).map((r, rIdx) => {
              const u = urgency(r.mhd);
              return (
              <div key={r.id} className="nx-list-item-in" style={{ ...card, ...tile, padding: 13, marginBottom: isWide ? 0 : 8, minWidth: 0, animationDelay: `${Math.min(rIdx, 14) * 28}ms`, ...urgRow(u) }}>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: urgColor[u], flex: "0 0 auto" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</strong>
                  <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>×{r.quantity ?? r.qty ?? 1} · MHD {fmtFR(r.mhd)}{r.note ? ` · ${r.note}` : ""}{r.photo ? " · 📎" : ""}</span>
                </div>
                <Pressable onClick={() => openCzMhdForm({ artNr: r.artNr, name: r.name }, r)} aria-label={t("common.editAria", "Modifier")} style={{
                  width: 32, height: 32, borderRadius: 99, background: P.soft, color: P.ink,
                  display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                }}>✏️</Pressable>
                <Pressable onClick={() => deleteCzRecord(r)} aria-label={t("reductions.removeAria", "Retirer")} style={{
                  width: 32, height: 32, borderRadius: 99, background: "rgba(239,68,68,.13)",
                  display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                }}>🗑️</Pressable>
              </div>
              );
            })}
            </div>
          </div>
        )}

        {/* ================= SECTION KÜHLUNG ================= */}
        {screen === "kuhlung" && (
          <>
            <article style={{ ...colorCard(A.frost), ...tile, flexDirection: "column", alignItems: "stretch", padding: 19 }}>
              <div style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
                <div style={onColorChip}><EmojiIcon e="❄️" /></div>
                <div>
                  <span style={onColorTag}>{t("control.newArticlesTag", "Kühlung")}</span>
                  <h4 style={{ margin: "3px 0 3px", fontSize: `calc(16.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{t("control.kuhlung.title", "MHD Kühlung")}</h4>
                  <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("control.kuhlung.desc", "Contrôle des articles Kühlung dans l'ordre du rayon.")}</p>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                <Pressable onClick={startFresh} style={{ flex: 1, ...btnGhostOnColor }}>{t("control.restartBtn", "Recommencer")}</Pressable>
                <Pressable onClick={resumeRun} style={{ flex: 1, ...btnSolid("#ffffff", A.frost), boxShadow: "0 12px 26px rgba(0,0,0,.18)" }}>{t("control.continueBtn", "Continuer")}</Pressable>
              </div>
            </article>

            <div style={{ display: "flex", gap: 11, background: P.soft, border: `1px solid ${P.line}`, borderRadius: 16, padding: 13, marginBottom: 12, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>
              <span>ℹ️</span>
              <div>
                <strong style={{ display: "block", marginBottom: 2 }}>{t("control.resumeInfoTitle", "Reprise globale")}</strong>
                <span style={{ color: P.muted }}>{t("control.kuhlung.resumeInfoDesc", "Le bouton Continuer reprend le premier article Kühlung non contrôlé aujourd'hui, peu importe l'employé connecté.")}</span>
              </div>
            </div>

            <Pressable onClick={() => setScreen("delivery")} style={{ ...colorCard(A.green), ...tile, marginBottom: 10 }}>
              <div style={onColorChip}><EmojiIcon e="📋" /></div>
              <div style={{ flex: 1 }}>
                <span style={onColorTag}>{t("control.kuhlung.deliveryTag", "Livraison")}</span>
                <h4 style={{ margin: "3px 0 3px", fontSize: `calc(16px * var(--nx-font-scale, 1))`, color: "#fff" }}>{t("control.kuhlung.deliveryTitle", "Réception du jour")}</h4>
                <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("control.kuhlung.deliveryDesc", "Rechercher un article, saisir quantité et MHD.")}</p>
              </div>
              <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>→</span>
            </Pressable>

            <Pressable onClick={() => setSheet("mhdExport")} style={{ ...card, ...tile, padding: 15, marginBottom: canImportMhdPdf ? 10 : 0 }}>
              <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>🖨️</span>
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{t("control.kuhlung.exportTitle", "Exporter la liste des MHD")}</strong>
                <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("control.kuhlung.exportDesc", "PDF imprimable, filtrable par urgence.")}</div>
              </div>
              <span style={{ color: P.muted, fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>›</span>
            </Pressable>

            {canImportMhdPdf && (
              <Pressable onClick={() => { resetMhdImport(); setScreen("mhdImport"); }} style={{ ...card, ...tile, padding: 15 }}>
                <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>🤖</span>
                <div style={{ flex: 1 }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{t("mhdImport.hub.title", "Importer un PDF MHD")}</strong>
                  <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("mhdImport.hub.desc", "Lecture automatique par IA, à vérifier avant import.")}</div>
                </div>
                <span style={{ color: P.muted, fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>›</span>
              </Pressable>
            )}
          </>
        )}

        {/* ================= LIVRAISON KÜHLUNG — RÉCEPTION DU JOUR ================= */}
        {screen === "delivery" && (
          <div className="nx-screen-enter" style={{ paddingBottom: 96 }}>
            {/* Refonte 2026-08-16 (retour utilisateur explicite : "toujours avoir le bouton
                pour scanner à bonne hauteur du doigt pour une utilisation rapide tout en
                ayant la liste des articles reçu et non reçu") — le scan devient le FAB flottant
                tout en bas de l'écran (voir juste après la fermeture de ce bloc), et la liste
                En attente/Reçus (delTab) devient le contenu principal au lieu d'être cachée
                dans la sheet delMissing. Un scan reconnu ouvre toujours directement la carte
                de saisie MHD ci-dessous (delArt, inchangée) ; un code inconnu passe par la
                sheet générique de liaison EAN (voir handleScanFileChange, ctx "delivery"). */}
            <section style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: "14px 16px", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
                <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("delivery.progressTitle", "Réception du jour")}</strong>
                <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.green }}>{delProgressPct}%</span>
              </div>
              <div style={{ height: 8, borderRadius: 99, background: P.soft, overflow: "hidden" }}>
                <div style={{ height: "100%", borderRadius: 99, width: `${delProgressPct}%`, background: A.green, transition: "width .25s ease" }} />
              </div>
              <span style={{ display: "block", marginTop: 7, fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 600 }}>
                {t("delivery.progressSubTemplate", { n: deliveryReceived.length, total: delTotal }, `${deliveryReceived.length} sur ${delTotal} articles reçus aujourd'hui`)}
              </span>
            </section>

            <Pressable onClick={() => setDelManualSearchOpen((v) => !v)} style={{
              display: "block", textAlign: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, textDecoration: "underline", margin: "0 0 12px",
            }}>{t("delivery.manualFallbackBtn", "ou cherche manuellement")}</Pressable>

            <div style={{ display: "flex", gap: 6, marginBottom: 12, background: P.soft, padding: 4, borderRadius: 14 }}>
              <Pressable onClick={() => setDelTab("pending")} style={{
                flex: 1, textAlign: "center", padding: "8px 4px", borderRadius: 10, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800,
                background: delTab === "pending" ? P.card : "transparent", color: delTab === "pending" ? P.ink : P.muted,
                boxShadow: delTab === "pending" ? "0 4px 12px rgba(15,23,42,.08)" : "none",
              }}><span style={{ color: A.orange }}>{delMissing.length}</span> {t("delivery.tabPending", "en attente")}</Pressable>
              <Pressable onClick={() => setDelTab("received")} style={{
                flex: 1, textAlign: "center", padding: "8px 4px", borderRadius: 10, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800,
                background: delTab === "received" ? P.card : "transparent", color: delTab === "received" ? P.ink : P.muted,
                boxShadow: delTab === "received" ? "0 4px 12px rgba(15,23,42,.08)" : "none",
              }}><span style={{ color: A.green }}>{deliveryReceived.length}</span> {t("delivery.tabReceived", "reçus")}</Pressable>
            </div>

            {/* Repli manuel — replié par défaut, pour les cas où le code-barres est illisible */}
            {delManualSearchOpen && (
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 15, marginBottom: 4 }}>
                <input style={{ ...inputStyle }} placeholder={t("controlRun.searchPlaceholder", "Rechercher Art.Nr ou nom...")}
                  value={delSearch} onChange={(e) => { setDelSearch(e.target.value); }} autoFocus />
                {delSearch.trim() && (
                  <div style={{ marginTop: 10 }}>
                    {articles.filter((a) => {
                      const q = delSearch.trim().toLowerCase();
                      return a.name.toLowerCase().includes(q) || a.artNr.includes(q);
                    }).slice(0, 5).map((a) => {
                      const got = deliveryReceived.some((r) => r.artNr === a.artNr);
                      const linkedCode = scanArticleEan(a.artNr);
                      return (
                        <Pressable key={a.artNr} onClick={() => selectDelArt(a)} style={{
                          display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                          padding: "11px 12px", borderRadius: 14, background: P.soft, marginBottom: 6, color: P.ink,
                        }}>
                          <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{got ? "✅" : "📦"}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</strong>
                            <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlRun.artNrPrefix", "Art.Nr")} {a.artNr}{got ? ` · ${t("lieferschein.item.alreadyReceivedLabel", "déjà reçu")}` : ""}</span>
                          </div>
                          {linkedCode ? (
                            <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 9px", whiteSpace: "nowrap", color: "#fff", background: A.green }}>🔗</span>
                          ) : (
                            <Pressable onClick={(e) => { e.stopPropagation(); openScanLink(a, false); }} style={{
                              fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "5px 10px", whiteSpace: "nowrap",
                              color: P.muted, background: P.card, border: `1.5px solid ${P.line}`,
                            }}>{t("delivery.linkArticleBtn", "Lier")}</Pressable>
                          )}
                          <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted }}>›</span>
                        </Pressable>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            {/* Article sélectionné : saisie qté + MHD au pouce */}
            {delArt && (
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 16, border: `1.5px solid ${A.green}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: `calc(20px * var(--nx-font-scale, 1))`}}>📦</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`, display: "block" }}>{delArt.name}</strong>
                    <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlRun.artNrPrefix", "Art.Nr")} {delArt.artNr} · {t("controlRun.positionPrefix", "Position")} {delArt.position}</span>
                  </div>
                  <Pressable onClick={() => setDelArt(null)} aria-label={t("common.cancelSelectionAria", "Annuler la sélection")} style={{
                    width: 30, height: 30, borderRadius: 99, background: P.soft, color: P.ink,
                    display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  }}>✕</Pressable>
                </div>

                <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 11, paddingBottom: 2 }}>
                  {QUICK_DAYS.map((n) => {
                    const iso = addDays(n);
                    const sel = delMhd === iso;
                    const u = urgency(iso);
                    return (
                      <Pressable key={n} onClick={() => setDelMhd(iso)} style={{
                        flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                        background: sel ? urgColor[u] : P.soft, color: sel ? "#fff" : P.ink,
                        border: `1.5px solid ${sel ? urgColor[u] : P.line}`,
                      }}>+{n}j</Pressable>
                    );
                  })}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    display: "flex", alignItems: "center", borderRadius: 16,
                    background: P.soft, border: `1.5px solid ${P.line}`, overflow: "hidden",
                  }}>
                    <HoldButton onTap={() => { setDelQty((q) => Math.max(1, q - 1)); }} onRepeat={() => setDelQty((q) => Math.max(1, q - 1))} label={t("common.decreaseHoldAria", "Moins (maintenir pour accélérer)")} style={{
                      width: 54, height: 54, display: "grid", placeItems: "center", fontSize: `calc(25px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink, background: "none",
                    }}>−</HoldButton>
                    <div style={{ minWidth: 52, textAlign: "center" }}>
                      <strong style={{ fontSize: `calc(21px * var(--nx-font-scale, 1))`}}>{delQty}</strong>
                      <span style={{ display: "block", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, letterSpacing: ".08em" }}>{t("common.qtyShortLabel", "QTÉ")}</span>
                    </div>
                    <HoldButton onTap={() => { setDelQty((q) => Math.min(999, q + 1)); }} onRepeat={() => setDelQty((q) => Math.min(999, q + 1))} label={t("common.increaseHoldAria", "Plus (maintenir pour accélérer)")} style={{
                      width: 54, height: 54, display: "grid", placeItems: "center", fontSize: `calc(25px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink, background: "none",
                    }}>＋</HoldButton>
                  </div>
                  <label style={{ position: "relative", flex: 1, display: "block" }}>
                    <div style={{
                      textAlign: "center", padding: "12px 8px", borderRadius: 16,
                      background: P.soft, border: `1.5px solid ${delMhd ? urgColor[urgency(delMhd)] : P.line}`,
                      fontWeight: 800, fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: delMhd ? P.ink : P.muted,
                    }}>{delMhd ? `${fmtDay(delMhd)} ${fmtFR(delMhd)}` : `📅 ${t("common.otherDatePlaceholder", "Autre date")}`}</div>
                    <input type="date" min={todayISO()} value={delMhd} onChange={(e) => setDelMhd(e.target.value)}
                      aria-label={t("common.receivedMhdDateAria", "Date MHD reçue")}
                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer", colorScheme: themeId === "sombre" ? "dark" : "light" }} />
                  </label>
                </div>

                <Pressable onClick={saveDelivery} style={{
                  marginTop: 12, textAlign: "center", padding: "16px 0", borderRadius: 18, fontWeight: 800, fontSize: `calc(15.5px * var(--nx-font-scale, 1))`,
                  background: A.green, color: "#fff", boxShadow: "0 12px 26px rgba(30,184,95,.4)",
                }}>✓ {t("delivery.articleReceivedLabel", "Article reçu")}</Pressable>
              </section>
            )}

            {/* Liste principale — bascule En attente (delMissing, catalogue non encore reçu
                aujourd'hui) / Reçus (deliveryReceived, vraies fiches Contrôle Kühlung filtrées
                sur controlDate). Remplace l'ancienne sheet delMissing comme seul accès. */}
            {delTab === "pending" ? (
              delMissing.length === 0 ? (
                <div style={{ ...card, flexDirection: "column", alignItems: "center", textAlign: "center", padding: "26px 20px" }}>
                  <span style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`, marginBottom: 8, display: "block" }}>✅</span>
                  <p style={{ margin: 0, color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("control.allReceivedText", "Tout est réceptionné — belle livraison ✅")}</p>
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 7 }}>
                  {delMissing.map((a) => {
                    const linkedCode = scanArticleEan(a.artNr);
                    return (
                      <Pressable key={a.artNr} onClick={() => selectDelArt(a)} style={{
                        ...card, ...tile, padding: 12, minWidth: 0, marginBottom: isWide ? 0 : 0,
                      }}>
                        <span style={{ width: 7, height: 7, borderRadius: 99, background: A.orange, flex: "0 0 auto" }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</strong>
                          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlRun.artNrPrefix", "Art.Nr")} {a.artNr} · {t("controlRun.positionPrefix", "Position")} {a.position}</span>
                        </div>
                        {!linkedCode && (
                          <Pressable onClick={(e) => { e.stopPropagation(); openScanLink(a, false); }} style={{
                            fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "5px 9px", whiteSpace: "nowrap",
                            color: P.muted, background: P.soft, border: `1.5px solid ${P.line}`,
                          }}>{t("delivery.linkArticleBtn", "Lier")}</Pressable>
                        )}
                        <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "6px 11px", whiteSpace: "nowrap", color: A.green, background: "rgba(30,184,95,.12)" }}>{t("control.receiveBtn", "Réceptionner ›")}</span>
                      </Pressable>
                    );
                  })}
                </div>
              )
            ) : (
              deliveryReceived.length === 0 ? (
                <div style={{ ...card, flexDirection: "column", alignItems: "center", textAlign: "center", padding: "26px 20px" }}>
                  <span style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`, marginBottom: 8, display: "block" }}>📦</span>
                  <p style={{ margin: 0, color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("delivery.noArticleReceivedYetFull", "Rien de réceptionné pour l'instant — la liste se remplit à chaque « Article reçu ».")}</p>
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 7 }}>
                  {deliveryReceived.map((r, rIdx) => (
                    <div key={r.id} className="nx-list-item-in" style={{ ...card, ...tile, padding: 13, minWidth: 0, animationDelay: `${Math.min(rIdx, 14) * 28}ms` }}>
                      <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>✅</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</strong>
                        <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>×{r.quantity} · MHD {fmtFR(r.mhd)} · {t("delivery.byEmployeePrefix", "par")} {r.employee}</span>
                      </div>
                      <span style={{ width: 8, height: 8, borderRadius: 99, background: urgColor[urgency(r.mhd)], flex: "0 0 auto" }} />
                      <Pressable onClick={() => removeDelivery(r.id)} aria-label={t("reductions.removeAria", "Retirer")} style={{
                        width: 32, height: 32, borderRadius: 99, background: "rgba(239,68,68,.13)",
                        display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                      }}>🗑️</Pressable>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        )}

        {/* Scan flottant — toujours à la même hauteur (retour utilisateur explicite : "à
            bonne hauteur du doigt pour une utilisation rapide"), au-dessus de la nav réelle,
            jamais dans le flux de la liste (position fixed, même patron que le FAB "+"
            Tâches ci-dessus). */}
        {screen === "delivery" && (
          <Pressable onClick={() => doScan("delivery")} aria-label={t("delivery.scanBtn", "Scanner un article")} style={{
            position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: "calc(128px + env(safe-area-inset-bottom))", zIndex: 33,
            display: "flex", alignItems: "center", gap: 10, padding: "14px 24px 14px 16px", borderRadius: 99,
            background: A.green, color: "#fff", boxShadow: "0 18px 36px rgba(30,184,95,.42)",
          }}>
            <span style={{
              width: 34, height: 34, borderRadius: 99, background: "rgba(255,255,255,.22)", display: "grid", placeItems: "center", fontSize: `calc(15px * var(--nx-font-scale, 1))`,
              animation: scanning ? "nxPulse 1s ease infinite" : "none",
            }}>📷</span>
            <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800 }}>{t("delivery.scanBtn", "Scanner un article")}</span>
          </Pressable>
        )}

        {/* ================= "BIENTÔT" ================= */}
        {screen === "soon" && soonCat && (
          <article style={{ ...colorCard(soonCat.bg), ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 32 }}>
            <div style={{ ...onColorChip, width: 60, height: 60, fontSize: `calc(28px * var(--nx-font-scale, 1))`, marginBottom: 14 }}>{soonCat.emoji}</div>
            <h4 style={{ margin: "0 0 6px", fontSize: `calc(17.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{soonCat.label} — bientôt disponible</h4>
            <p style={{ margin: 0, fontSize: `calc(13px * var(--nx-font-scale, 1))`, lineHeight: 1.55, ...onColorSub }}>
              Le contrôle MHD dédié à {soonCat.label} arrive prochainement. Les articles de ce rayon sont déjà gérés dans le module Articles.
            </p>
          </article>
        )}

        {/* ================= ACCÈS REFUSÉ =================
            Voir MODULE_ACCESS_PERMS/hasModuleAccess — retour immédiat à l'accueil au bout de
            2.5s (AccessDeniedRedirect ci-dessous), + bouton explicite pour ne pas attendre.
            Contrôle d'expérience côté client : le vrai rempart reste chaque route /api/*
            côté serveur, inchangé. */}
        {screen === "accessDenied" && (
          <article style={{ ...colorCard(A.red), ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 32 }}>
            <div style={{ ...onColorChip, width: 60, height: 60, fontSize: `calc(28px * var(--nx-font-scale, 1))`, marginBottom: 14 }}>🚫</div>
            <h4 style={{ margin: "0 0 6px", fontSize: `calc(17.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{t("accessDenied.title", "Accès refusé")}</h4>
            <p style={{ margin: "0 0 18px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, lineHeight: 1.55, ...onColorSub }}>
              {deniedModule ? t("accessDenied.bodyTemplate", { module: deniedModule.label }, `Ton rôle n'a pas la permission d'accéder à "${deniedModule.label}". Demande à un responsable de te l'activer dans Rôles & permissions si besoin.`) : t("accessDenied.bodyGeneric", "Ton rôle n'a pas la permission d'accéder à ce module.")}
            </p>
            <Pressable onClick={() => { setScreen("home"); setDeniedModule(null); }} style={{
              padding: "13px 22px", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: "rgba(255,255,255,.22)", color: "#fff",
            }}>{t("accessDenied.backHomeBtn", "‹ Retour à l'accueil")}</Pressable>
          </article>
        )}

        {/* ================= RUN KÜHLUNG (ergonomie magasin) ================= */}
        {inRun && article && (
          <>
            <section key={runIndex} style={{ ...colorCard(A.frost), borderRadius: 24, padding: "20px 18px 18px", textAlign: "center", animation: "nxSlideIn .28s ease", position: "relative" }}>
              <span style={onColorTag}>{t("controlRun.positionPrefix", "Position")} {article.position} · {t("controlRun.kuhlungDeptSuffix", "Rayon Kühlung")}</span>
              {/* Raccourci fiche article (2026-08-31, retour utilisateur explicite) — un tap
                  sur le nom/Art.Nr ouvre la recherche "aller à un article" (runJumpValue),
                  un DOUBLE tap sur le même champ ouvre son édition (runHeroEdit), avec
                  confirmation avant sauvegarde. Même mécanique déjà établie dans Planning
                  (shiftTapRef/shiftTripleTapRef, handleShiftCellTap) — un ref{key,timer} par
                  fenêtre de tap, jamais un vrai dblclick (peu fiable sur mobile). VE reste un
                  simple tap→édition directe (pas de double-tap, champ non identifiant). */}
              {runJumpValue !== null ? (
                <div style={{ margin: "10px 0 4px" }}>
                  <input
                    autoFocus value={runJumpValue} placeholder={t("controlRun.quickJump.placeholder", "Art.Nr ou nom…")}
                    onChange={(e) => setRunJumpValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") commitRunJump(); if (e.key === "Escape") setRunJumpValue(null); }}
                    onBlur={commitRunJump}
                    style={{
                      width: "100%", boxSizing: "border-box", textAlign: "center", padding: "11px 14px", borderRadius: 14,
                      border: "1.5px solid rgba(255,255,255,.5)", background: "rgba(255,255,255,.16)", color: "#fff",
                      fontSize: `calc(16px * var(--nx-font-scale, 1))`, fontWeight: 700,
                    }}
                  />
                  <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: "rgba(255,255,255,.75)", display: "block", marginTop: 6 }}>{t("controlRun.quickJump.hint", "↵ pour aller à cet article")}</span>
                </div>
              ) : (
                <>
                  {runHeroEdit?.field === "name" ? (
                    <input
                      autoFocus value={runHeroEdit.value} onFocus={(e) => e.target.select()}
                      onChange={(e) => setRunHeroEdit((f) => ({ ...f, value: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") commitRunHeroEdit(); if (e.key === "Escape") setRunHeroEdit(null); }}
                      onBlur={commitRunHeroEdit}
                      style={{
                        width: "100%", boxSizing: "border-box", margin: "7px 0 9px", textAlign: "center", padding: "8px 10px", borderRadius: 12,
                        border: "1.5px solid rgba(255,255,255,.5)", background: "rgba(255,255,255,.16)", color: "#fff",
                        fontSize: `calc(20px * var(--nx-font-scale, 1))`, fontWeight: 800,
                      }}
                    />
                  ) : (
                    <h1 onClick={() => handleRunFieldTap("name")} style={{ margin: "7px 0 9px", fontSize: `calc(23px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800, lineHeight: 1.15, color: "#fff", cursor: "pointer" }}>{article.name}</h1>
                  )}
                  <div style={{ display: "flex", gap: 7, justifyContent: "center", flexWrap: "wrap", alignItems: "center" }}>
                    {runHeroEdit?.field === "artNr" ? (
                      <input
                        autoFocus inputMode="numeric" value={runHeroEdit.value} onFocus={(e) => e.target.select()}
                        onChange={(e) => setRunHeroEdit((f) => ({ ...f, value: e.target.value.replace(/\D/g, "") }))}
                        onKeyDown={(e) => { if (e.key === "Enter") commitRunHeroEdit(); if (e.key === "Escape") setRunHeroEdit(null); }}
                        onBlur={commitRunHeroEdit}
                        style={{ width: 110, textAlign: "center", padding: "5px 10px", borderRadius: 99, border: "1.5px solid rgba(255,255,255,.5)", background: "rgba(255,255,255,.16)", color: "#fff", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}
                      />
                    ) : (
                      <span onClick={() => handleRunFieldTap("artNr")} style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, background: "rgba(255,255,255,.2)", borderRadius: 99, padding: "5px 12px", color: "#fff", cursor: "pointer" }}>{t("controlRun.artNrPrefix", "Art.Nr")} {article.artNr}</span>
                    )}
                    {runVeEdit !== null ? (
                      <input
                        autoFocus inputMode="numeric" placeholder="—" value={runVeEdit} onFocus={(e) => e.target.select()}
                        onChange={(e) => setRunVeEdit(e.target.value.replace(/\D/g, ""))}
                        onKeyDown={(e) => { if (e.key === "Enter") commitRunVeEdit(); if (e.key === "Escape") setRunVeEdit(null); }}
                        onBlur={commitRunVeEdit}
                        style={{ width: 70, textAlign: "center", padding: "5px 10px", borderRadius: 99, border: "1.5px solid rgba(255,255,255,.5)", background: "rgba(255,255,255,.16)", color: "#fff", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}
                      />
                    ) : (
                      <span onClick={() => setRunVeEdit(String(article.ve ?? ""))} style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, background: "rgba(255,255,255,.2)", borderRadius: 99, padding: "5px 12px", color: "#fff", cursor: "pointer" }}>{t("merch.article.veLabel", "VE")} {article.ve || "—"}</span>
                    )}
                    <span style={{
                      fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "5px 12px",
                      background: touched.has(article.artNr) ? "rgba(255,255,255,.95)" : "rgba(255,255,255,.2)",
                      color: touched.has(article.artNr) ? A.frost : "#fff",
                    }}>{touched.has(article.artNr) ? `✓ ${t("controlRun.checkedBadge", "Contrôlé")}` : t("controlRun.pendingStatus", "En attente")}</span>
                  </div>
                  <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: "rgba(255,255,255,.65)", display: "block", marginTop: 8 }}>{t("controlRun.quickEdit.hint", "Tape pour chercher un article · double-tape pour modifier")}</span>
                </>
              )}
              {lastScanCode && articleForCode(lastScanCode)?.artNr === article.artNr && (
                <Pressable onClick={unlinkLastScan} style={{
                  margin: "10px auto 0", display: "block", padding: "8px 14px", borderRadius: 99,
                  fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: "#fff", background: "rgba(0,0,0,.18)",
                }}>{t("controlRun.unlinkEanBtn", "Ce n'est pas le bon article — retirer l'association EAN")}</Pressable>
              )}
              <Pressable onClick={() => doScan("run")} style={{
                width: "100%", marginTop: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                padding: "13px 0", borderRadius: 16, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                background: "#fff", color: A.frost, boxShadow: "0 6px 16px rgba(0,0,0,.14)",
                animation: scanning ? "nxPulse 1s ease infinite" : "none",
              }}>📷 {t("controlRun.scanBarcodeBtn", "Scanner le code-barres")}</Pressable>
            </section>

            <section style={{ ...card, borderRadius: 22, padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: artRecords.length ? 4 : 0 }}>
                <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{t("controlRun.registeredMhdTitle", "MHD enregistrées")}</strong>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: P.muted, borderRadius: 99, padding: "2px 9px" }}>{artRecords.length}</span>
              </div>
              {artRecords.length === 0 ? (
                <p style={{ margin: "6px 2px 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlRun.noMhdShort", "Aucune MHD")}</p>
              ) : (
                // Carte évolutive/scrollable — retour utilisateur explicite, 2026-08-02 :
                // "dans un futur on pourrait voir une vingtaine voir plus de mhd pour un
                // seul article", jamais fait exploser la hauteur de la carte (ni de la
                // page) au-delà de max-height — la liste défile À L'INTÉRIEUR, l'en-tête
                // "MHD enregistrées (N)" et le reste de l'écran restent stables.
                <div style={{ maxHeight: 360, overflowY: "auto", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}>
                  {[...artRecords].sort((a, b) => a.mhd.localeCompare(b.mhd)).map((r) => {
                    const u = urgency(r.mhd);
                    return (
                      <div key={r.id} style={{
                        display: "flex", alignItems: "center", gap: 8, width: "100%", boxSizing: "border-box",
                        padding: "11px 12px", borderRadius: 14, marginTop: 6, ...urgRow(u),
                      }}>
                        <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                            <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{fmtFR(r.mhd)}</strong>
                            <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: urgColor[u], borderRadius: 99, padding: "2px 8px" }}>{urgLabel[u]}</span>
                          </div>
                          <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlRun.qtyPrefix", "Qté")} {r.quantity || "—"} · {r.note || t("controlRun.noNote", "Sans remarque")}</span>
                        </div>
                        {/* Bouton Modifier explicite — retour utilisateur explicite : "il
                            manque le boutons modier mhd sur la carte" (avant, seul un
                            clic implicite sur toute la ligne ouvrait l'édition). */}
                        <Pressable onClick={() => openEditSheet(r)} aria-label={t("common.editAria", "Modifier")} style={{
                          width: 30, height: 30, borderRadius: 99, background: "rgba(54,79,231,.1)", color: A.blue,
                          display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                        }}>✏️</Pressable>
                        <Pressable onClick={() => deleteRecordDirect(r)}
                          aria-label={t("common.delete", "Supprimer")} style={{
                            width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.12)", color: A.red,
                            display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                          }}>✕</Pressable>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <Pressable onClick={nothingToReport} style={{
              display: "block", margin: "2px auto 0", padding: "8px 4px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, textAlign: "center",
            }}>{t("controlRun.nothingToReportBtn", "Rien à signaler pour cet article")}</Pressable>
          </>
        )}

        {/* ---- Écran de fin ---- */}
        {screen === "run" && finish && (
          <section style={{ ...colorCard(A.green), ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 36 }}>
            <div style={{
              width: 60, height: 60, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(27px * var(--nx-font-scale, 1))`,
              marginBottom: 14, background: "rgba(255,255,255,.25)", color: "#fff",
            }}>✓</div>
            <h2 style={{ margin: "0 0 5px", fontSize: `calc(20px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{t("controlRun.finishTitle", "Contrôle terminé")}</h2>
            <p style={{ margin: "0 0 20px", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("controlRun.finishTextKuhlung", "Tous les articles Kühlung ont été contrôlés.")}</p>
            <Pressable onClick={() => exportMhdKuhlungPdf("all")}
              style={{ width: "100%", ...btnSolid("#ffffff", A.green), boxShadow: "0 12px 26px rgba(0,0,0,.18)", marginBottom: 13 }}>
              🖨️ {t("controlRun.printFullListBtn", "Imprimer la liste complète")}
            </Pressable>
            <Pressable onClick={() => setScreen("kuhlung")} style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: "#fff" }}>{t("common.back", "Retour")}</Pressable>
          </section>
        )}

        {/* ================= DLC ARTICLES PROMO ================= */}
        {screen === "store" && (
          <>
            <section style={{ ...colorCard(A.purple), ...tile, justifyContent: "space-between", padding: 19 }}>
              <div>
                <span style={onColorTag}>{t("controlStore.heroEyebrow", "Magasin · Saisie libre")}</span>
                <h2 style={{ margin: "4px 0 0", fontSize: `calc(18.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{t("controlStore.heroTitle", "Quel article promotionnel ?")}</h2>
              </div>
              <Pressable onClick={() => setSheet("storeHistory")} style={{
                fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, background: "rgba(255,255,255,.22)", color: "#fff",
                borderRadius: 99, padding: "9px 15px", whiteSpace: "nowrap",
              }}>{t("controlStore.seeAllBtn", "Historique")}</Pressable>
            </section>

            <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 19 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 3 }}>
                <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{t("controlStore.newArticleTitle", "Nouvel article")}</strong>
                <Pressable onClick={() => doScan("store")} aria-label={t("controlRun.scanBarcodeBtn", "Scanner le code-barres")} style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "8px 13px", borderRadius: 99,
                  background: themeId === "sombre" ? "#2a2a2e" : A.navy, color: "#fff",
                  fontWeight: 700, fontSize: `calc(12px * var(--nx-font-scale, 1))`, animation: scanning ? "nxPulse 1s ease infinite" : "none",
                }}>📷 {t("common.scanAria", "Scanner")}</Pressable>
              </div>
              <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 14 }}>{t("controlStore.newArticleSubtitle", "Produits hors Kühlung")}</span>
              <Field label={t("controlStore.articleNameLabel", "Nom de l'article")}>
                <input style={inputStyle} value={storeForm.name} placeholder={t("controlStore.articleNamePlaceholderEx", "Ex. Chips paprika promo 175g")}
                  onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })} />
              </Field>
              {storeForm.ean && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: P.soft, borderRadius: 12, padding: "9px 12px", marginBottom: 10 }}>
                  <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>📷</span>
                  <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, flex: 1 }}>{t("controlStore.eanChipLabel", { code: storeForm.ean }, `EAN ${storeForm.ean}`)}</span>
                  <Pressable onClick={() => setStoreForm((f) => ({ ...f, ean: "" }))} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: A.red, fontWeight: 700 }}>{t("reductions.removeAria", "Retirer")}</Pressable>
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label={t("controlStore.artNrLabelOptionalEan", "Art.Nr (facultatif si EAN)")}>
                  <input style={inputStyle} value={storeForm.artNr} inputMode="numeric" placeholder="88210"
                    onChange={(e) => setStoreForm({ ...storeForm, artNr: e.target.value })} />
                </Field>
                <Field label={t("controlStore.fieldQuantity", "Quantité")}>
                  <input style={inputStyle} value={storeForm.qty} inputMode="numeric" placeholder={t("controlRun.qtyPrefix", "Qté")}
                    onChange={(e) => setStoreForm({ ...storeForm, qty: e.target.value })} />
                </Field>
              </div>
              <Field label={t("controlStore.fieldDateMhd", "Date DLC")}>
                <input style={dateTimeInputStyle} type="date" min={todayISO()} value={storeForm.mhd}
                  onChange={(e) => setStoreForm({ ...storeForm, mhd: e.target.value })} />
              </Field>
              <Field label={t("controlRun.remarkLabel", "Remarque")}>
                <input style={inputStyle} value={storeForm.note} placeholder={t("controlRun.remarkPlaceholder", "Remarque facultative")}
                  onChange={(e) => setStoreForm({ ...storeForm, note: e.target.value })} />
              </Field>
              {/* Règle de réduction facultative — jamais obligatoire pour enregistrer (retour
                  utilisateur explicite, 2026-08-19) : "AUTO" = règle par défaut de la source
                  (sourceDefaults.STORE), rien à faire ici pour ça. Indexée par Art.Nr — retirée
                  automatiquement si l'utilisateur n'a saisi qu'un EAN (voir saveStore). */}
              <Field label={t("reductions.optionalRuleLabel", "Règle de réduction (facultatif)")}>
                <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", paddingBottom: 2 }}>
                  {["AUTO", ...ruleDefinitions.map((d) => d.id)].map((rule) => {
                    const sel = storeForm.rule === rule;
                    const label = rule === "AUTO" ? t("reductions.autoLabel", "Automatique") : reductionRuleLabel(rule);
                    return (
                      <Pressable key={rule} onClick={() => setStoreForm({ ...storeForm, rule })} style={{
                        flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                        background: sel ? A.blue : P.soft, color: sel ? "#fff" : P.ink, border: `1.5px solid ${sel ? A.blue : P.line}`,
                      }}>{label}</Pressable>
                    );
                  })}
                  <Pressable onClick={() => openInlineRuleAdd("storeForm")} style={{
                    flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                    background: P.soft, border: `1.5px dashed ${P.line}`, color: A.blue,
                  }}>＋ {t("reductions.rulesAdmin.addBtn", "Ajouter une règle")}</Pressable>
                </div>
                {inlineRuleAddKey === "storeForm" && renderInlineRuleAddForm((newId) => setStoreForm((f) => ({ ...f, rule: newId })))}
              </Field>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                {storeForm.photo ? (
                  <img src={storeForm.photo} alt="" style={{ width: 46, height: 46, borderRadius: 12, objectFit: "cover", flex: "0 0 auto" }} />
                ) : (
                  <span style={{ width: 46, height: 46, borderRadius: 12, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>📸</span>
                )}
                <Pressable onClick={() => storePhotoInputRef.current?.click()} style={{
                  flex: 1, textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink, opacity: storePhotoUploading ? .6 : 1,
                }}>{storePhotoUploading ? t("settings.photo.uploading", "Envoi...") : storeForm.photo ? t("controlStore.changePhotoBtn", "Changer la photo") : t("controlStore.addPhotoOptionalLabel", "Ajouter une photo (facultatif)")}</Pressable>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                <Pressable onClick={() => setStoreForm(emptyStore)} style={{
                  flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
                  background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                }}>{t("controlStore.resetBtn", "Réinitialiser")}</Pressable>
                <Pressable onClick={saveStore} style={{ flex: 1, ...btnSolid(A.purple) }}>{t("common.save", "Enregistrer")}</Pressable>
              </div>
            </section>

            <strong style={{ display: "block", fontSize: `calc(14px * var(--nx-font-scale, 1))`, margin: "6px 2px 10px" }}>{t("controlStore.todayEntriesTitle", "Saisies du jour")}</strong>
            {storeRecords.length === 0 && <p style={{ color: P.muted, fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("controlStore.noEntriesTodayText", "Aucune saisie aujourd'hui — le formulaire ci-dessus n'attend que toi.")}</p>}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
            {storeRecords.map((r) => {
              const u = urgency(r.mhd);
              return (
                <div key={r.id} style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 9, minWidth: 0, ...urgRow(u), color: P.ink }}>
                  <span style={{ width: 9, height: 9, borderRadius: 99, background: urgColor[u], flex: "0 0 auto" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{r.name}</strong>
                    <div style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>
                      {t("controlRun.artNrPrefix", "Art.Nr")} {r.artNr} · {t("controlStore.dlcShortLabel", "DLC")} {fmtFR(r.mhd)}{r.quantity ? ` · ${t("controlRun.qtyPrefix", "Qté")} ${r.quantity}` : ""}
                    </div>
                    {/* Règle visible directement sur la carte (retour utilisateur explicite
                        2026-08-21 : "quand j'applique une règle que je puisse voir cela sur la
                        carte") — teintée bleu quand une dérogation est active, comme le bouton
                        ⚙️, jamais un simple libellé neutre qui masquerait l'information. */}
                    <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, color: ruleOverrides[r.artNr || r.ean] ? A.blue : P.muted, marginTop: 2 }}>
                      ⚙️ {reductionRuleLabel(redRuleFor(r.artNr || r.ean, r.source))}
                    </div>
                  </div>
                  {/* EAN en repli (retour utilisateur explicite 2026-08-21) — un article
                      Magasin en saisie libre n'a souvent qu'un EAN scanné, jamais d'Art.Nr,
                      et ne doit pas pour autant perdre l'accès à une règle de réduction. */}
                  {((r.artNr && r.artNr !== "—") || r.ean) && (
                    <Pressable onClick={() => openQuickRule(r.artNr, r.name, r.ean)} aria-label={t("reductions.ruleAria", "Règle de réduction")} style={{
                      width: 34, height: 34, borderRadius: 99, background: ruleOverrides[r.artNr || r.ean] ? "rgba(54,79,231,.15)" : P.soft, color: ruleOverrides[r.artNr || r.ean] ? A.blue : P.muted,
                      display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                    }}>⚙️</Pressable>
                  )}
                  <Pressable onClick={() => trashStore(r.id)} aria-label={t("controlStore.trashAria", "Corbeille")} style={{
                    width: 34, height: 34, borderRadius: 99, background: "rgba(239,68,68,.15)",
                    display: "grid", placeItems: "center", fontSize: `calc(15px * var(--nx-font-scale, 1))`,
                  }}>🗑️</Pressable>
                </div>
              );
            })}
            </div>
          </>
        )}

        {/* ================= IMPORT PDF MHD (vision Claude) ================= */}
        {screen === "mhdImport" && mhdImportStep === "upload" && (
          <div className="nx-screen-enter">
            <p style={{ margin: "0 2px 16px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
              {t("mhdImport.upload.intro", "Choisis la liste de stock MHD scannée (PDF ou photos). L'IA transcrit chaque ligne — tu vérifieras et corrigeras avant l'import définitif.")}
            </p>
            <input ref={mhdImportFileInputRef} type="file" accept="application/pdf,image/*" style={{ display: "none" }}
              onChange={(e) => { analyzeMhdImport(e.target.files); e.target.value = ""; }} />
            <Pressable onClick={() => mhdImportFileInputRef.current?.click()} disabled={mhdImportAnalyzing} style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
              padding: 28, borderRadius: 18, background: P.card, border: `1.5px dashed ${P.line}`, opacity: mhdImportAnalyzing ? .6 : 1,
            }}>
              <span style={{ fontSize: `calc(30px * var(--nx-font-scale, 1))`}}>📑</span>
              <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{mhdImportAnalyzing ? t("mhdImport.upload.analyzing", "Analyse en cours — lecture ligne par ligne...") : t("mhdImport.upload.chooseFile", "Choisir un fichier")}</strong>
              <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("mhdImport.upload.fileHint", "PDF ou photo, une page à la fois")}</span>
            </Pressable>
          </div>
        )}

        {screen === "mhdImport" && mhdImportStep === "preview" && (
          <div className="nx-screen-enter" style={{ paddingBottom: 96 }}>
            <p style={{ margin: "0 2px 14px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{t("mhdImport.preview.intro", "Vérification avant import — corrige ce que l'analyse aurait mal lu, puis marque chaque ligne ⚠️ comme vérifiée.")}</p>

            <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, margin: "4px 2px 9px" }}>{t("mhdImport.preview.rowsHeading", { n: mhdImportPreview.rows.length }, `Articles à importer (${mhdImportPreview.rows.length})`)}</strong>
            {mhdImportPreview.rows.map((r) => (
              <div key={r.id} style={{ ...card, borderRadius: 16, padding: 12, marginBottom: 9, border: (r.needsReview && !r.verified) ? `1.5px solid ${A.orange}` : `1px solid ${P.line}` }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8, marginBottom: 8 }}>
                  <input style={{ ...inputStyle, padding: "9px 11px", fontSize: `calc(13px * var(--nx-font-scale, 1))`}} placeholder="Art.Nr" value={r.artNr} onChange={(e) => updateMhdImportRow(r.id, "artNr", e.target.value)} />
                  <input style={{ ...inputStyle, padding: "9px 11px", fontSize: `calc(13px * var(--nx-font-scale, 1))`}} placeholder={t("claims.item.name", "Désignation")} value={r.name} onChange={(e) => updateMhdImportRow(r.id, "name", e.target.value)} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6, marginBottom: 8 }}>
                  <input style={{ ...inputStyle, padding: "9px 8px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}} inputMode="numeric" placeholder="Karton" value={r.karton} onChange={(e) => updateMhdImportRow(r.id, "karton", e.target.value)} />
                  <input style={{ ...inputStyle, padding: "9px 8px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}} inputMode="numeric" placeholder="VE" value={r.ve} onChange={(e) => updateMhdImportRow(r.id, "ve", e.target.value)} />
                  <input style={{ ...inputStyle, padding: "9px 8px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}} inputMode="numeric" placeholder="Stk" value={r.stk} onChange={(e) => updateMhdImportRow(r.id, "stk", e.target.value)} />
                  <input style={{ ...inputStyle, padding: "9px 8px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}} inputMode="decimal" placeholder="Preis" value={r.preis} onChange={(e) => updateMhdImportRow(r.id, "preis", e.target.value)} />
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ flex: 1, fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink }}>{t("mhdImport.preview.mhdLabelTemplate", { date: fmtFR(r.mhd) }, `MHD : ${fmtFR(r.mhd)}`)}</span>
                  {(r.needsReview && !r.verified) ? (
                    <Pressable onClick={() => markMhdImportRowVerified(r.id)} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: A.orange, borderRadius: 99, padding: "9px 12px", whiteSpace: "nowrap" }}>{t("lieferschein.preview.markVerified", "Marquer vérifié")}</Pressable>
                  ) : (
                    <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.green, whiteSpace: "nowrap" }}>✓ {t("lieferschein.preview.verified", "Vérifié")}</span>
                  )}
                  <Pressable onClick={() => removeMhdImportRow(r.id)} aria-label={t("common.delete", "Supprimer")} style={{ width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.13)", display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>✕</Pressable>
                </div>
                {r.needsReview && !r.verified && <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: A.orange, fontWeight: 700, display: "block", marginTop: 6 }}>⚠️ {r.reviewReason}</span>}
                {r.occurrences > 1 && <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 4 }}>{t("mhdImport.preview.occurrencesTemplate", { n: r.occurrences }, `${r.occurrences} lignes fusionnées (même Art.Nr, même MHD)`)}</span>}
              </div>
            ))}
            {!mhdImportPreview.rows.length && <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 16px" }}>{t("mhdImport.preview.noRows", "Aucune ligne exploitable détectée.")}</p>}

            {!!mhdImportPreview.newArticles.length && (
              <>
                <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, margin: "16px 2px 9px" }}>{t("mhdImport.preview.newArticlesHeading", { n: mhdImportPreview.newArticles.length }, `Nouvelles fiches Kühlung-Nouveaux à créer (${mhdImportPreview.newArticles.length})`)}</strong>
                <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 12, marginBottom: 12 }}>
                  {mhdImportPreview.newArticles.map((a) => (
                    <div key={a.artNr} style={{ display: "flex", justifyContent: "space-between", fontSize: `calc(12px * var(--nx-font-scale, 1))`, padding: "5px 0" }}>
                      <span style={{ color: P.muted }}>{a.artNr}</span><span>{a.name}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {!!mhdImportPreview.veUpdates.length && (
              <>
                <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, margin: "6px 2px 9px" }}>{t("mhdImport.preview.veUpdatesHeading", { n: mhdImportPreview.veUpdates.length }, `VE à réactualiser (${mhdImportPreview.veUpdates.length})`)}</strong>
                <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 12, marginBottom: 12 }}>
                  {mhdImportPreview.veUpdates.map((u) => (
                    <label key={u.artNr} style={{ display: "flex", alignItems: "center", gap: 9, fontSize: `calc(12px * var(--nx-font-scale, 1))`, padding: "5px 0" }}>
                      <input type="checkbox" checked={!!mhdImportPreview.veUpdatesChecked[u.artNr]} onChange={() => toggleMhdImportVe(u.artNr)} />
                      <span style={{ flex: 1 }}>{u.name} ({u.artNr})</span>
                      <span style={{ color: P.muted }}>{u.oldVe ?? "—"} → <strong style={{ color: P.ink }}>{u.newVe}</strong></span>
                    </label>
                  ))}
                </div>
              </>
            )}

            {(!!mhdImportPreview.excluded.length || !!mhdImportPreview.skippedPast.length || !!mhdImportPreview.skippedExisting.length) && (
              <details style={{ marginBottom: 16 }}>
                <summary style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, cursor: "pointer" }}>
                  {t("mhdImport.preview.ignoredHeadingTemplate", { n: mhdImportPreview.excluded.length + mhdImportPreview.skippedPast.length + mhdImportPreview.skippedExisting.length },
                    `Lignes ignorées (${mhdImportPreview.excluded.length + mhdImportPreview.skippedPast.length + mhdImportPreview.skippedExisting.length})`)}
                </summary>
                <div style={{ marginTop: 8 }}>
                  {mhdImportPreview.excluded.map((e, i) => (
                    <div key={"ex" + i} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, padding: "4px 0" }}>{e.artNr} {e.name} — {t("mhdImport.preview.reasonInvalidDate", "date invalide")}</div>
                  ))}
                  {mhdImportPreview.skippedPast.map((e, i) => (
                    <div key={"sp" + i} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, padding: "4px 0" }}>{e.artNr} {e.name} — {t("mhdImport.preview.reasonPastDate", "date déjà passée")}</div>
                  ))}
                  {mhdImportPreview.skippedExisting.map((e, i) => (
                    <div key={"se" + i} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, padding: "4px 0" }}>{e.artNr} {e.name} — {t("mhdImport.preview.reasonAlreadySaved", "déjà enregistrée")}</div>
                  ))}
                </div>
              </details>
            )}

            <Pressable onClick={commitMhdImport} disabled={mhdImportHasUnresolved || mhdImportCommitting || !mhdImportPreview.rows.length} style={{ width: "100%", ...btnSolid(A.blue), opacity: (mhdImportHasUnresolved || mhdImportCommitting || !mhdImportPreview.rows.length) ? .5 : 1 }}>
              {mhdImportCommitting ? t("mhdImport.preview.importing", "Import en cours...") : t("mhdImport.preview.importBtnTemplate", { n: mhdImportPreview.rows.length }, `Importer ${mhdImportPreview.rows.length} enregistrement(s)`)}
            </Pressable>
          </div>
        )}

        {/* ================= IMPORT CATALOGUE ARTICLES (vision Claude) ================= */}
        {screen === "articles" && artScreen === "import" && artImportStep === "upload" && (
          <div className="nx-screen-enter">
            <p style={{ margin: "0 2px 16px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
              {t("artImport.upload.intro", "Choisis le catalogue scanné (PDF ou photos) — l'ordre des lignes du document devient l'ordre du rayon. L'IA transcrit chaque ligne, tu vérifieras avant l'import définitif.")}
            </p>
            <input ref={artImportFileInputRef} type="file" accept="application/pdf,image/*" style={{ display: "none" }}
              onChange={(e) => { analyzeArtImport(e.target.files); e.target.value = ""; }} />
            <Pressable onClick={() => artImportFileInputRef.current?.click()} disabled={artImportAnalyzing} style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
              padding: 28, borderRadius: 18, background: P.card, border: `1.5px dashed ${P.line}`, opacity: artImportAnalyzing ? .6 : 1,
            }}>
              <span style={{ fontSize: `calc(30px * var(--nx-font-scale, 1))`}}>📑</span>
              <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{artImportAnalyzing ? t("artImport.upload.analyzing", "Analyse en cours — lecture ligne par ligne...") : t("artImport.upload.chooseFile", "Choisir un fichier")}</strong>
              <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("artImport.upload.fileHint", "PDF ou photo, une page à la fois")}</span>
            </Pressable>
          </div>
        )}

        {screen === "articles" && artScreen === "import" && artImportStep === "preview" && (
          <div className="nx-screen-enter" style={{ paddingBottom: 96 }}>
            <p style={{ margin: "0 2px 14px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{t("artImport.preview.intro", "Vérification avant import — corrige ce que l'analyse aurait mal lu. L'ordre d'affichage ci-dessous est celui qui sera utilisé dans le rayon.")}</p>

            <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, margin: "4px 2px 9px" }}>{t("artImport.preview.rowsHeading", { n: artImportPreview.rows.length }, `Articles à importer (${artImportPreview.rows.length})`)}</strong>
            {artImportPreview.rows.map((r) => (
              <div key={r.id} style={{ ...card, borderRadius: 16, padding: 12, marginBottom: 9, border: `1px solid ${P.line}` }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8, marginBottom: 8 }}>
                  <input style={{ ...inputStyle, padding: "9px 11px", fontSize: `calc(13px * var(--nx-font-scale, 1))`}} placeholder="Art.Nr" value={r.artNr} onChange={(e) => updateArtImportRow(r.id, "artNr", e.target.value)} />
                  <input style={{ ...inputStyle, padding: "9px 11px", fontSize: `calc(13px * var(--nx-font-scale, 1))`}} placeholder={t("claims.item.name", "Désignation")} value={r.name} onChange={(e) => updateArtImportRow(r.id, "name", e.target.value)} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 8 }}>
                  <input style={{ ...inputStyle, padding: "9px 8px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}} inputMode="decimal" placeholder="Preis" value={r.price ?? ""} onChange={(e) => updateArtImportRow(r.id, "price", e.target.value)} />
                  <input style={{ ...inputStyle, padding: "9px 8px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}} placeholder={t("artImport.preview.zonePlaceholder", "Rayon")} value={r.zone || ""} onChange={(e) => updateArtImportRow(r.id, "zone", e.target.value)} />
                  <input style={{ ...inputStyle, padding: "9px 8px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}} placeholder="VE" value={r.unit || ""} onChange={(e) => updateArtImportRow(r.id, "unit", e.target.value)} />
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ flex: 1, fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>{t("artImport.preview.orderLabelTemplate", { n: r.sortOrder }, `Position rayon : ${r.sortOrder}`)}</span>
                  <Pressable onClick={() => removeArtImportRow(r.id)} aria-label={t("common.delete", "Supprimer")} style={{ width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.13)", display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>✕</Pressable>
                </div>
              </div>
            ))}
            {!artImportPreview.rows.length && <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 16px" }}>{t("artImport.preview.noRows", "Aucune ligne exploitable détectée.")}</p>}

            {!!artImportPreview.skippedExisting.length && (
              <details style={{ marginBottom: 16 }}>
                <summary style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, cursor: "pointer" }}>
                  {t("artImport.preview.ignoredHeadingTemplate", { n: artImportPreview.skippedExisting.length }, `Lignes ignorées (${artImportPreview.skippedExisting.length})`)}
                </summary>
                <div style={{ marginTop: 8 }}>
                  {artImportPreview.skippedExisting.map((e, i) => (
                    <div key={"se" + i} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, padding: "4px 0" }}>{e.artNr} {e.name} — {e.reason}</div>
                  ))}
                </div>
              </details>
            )}

            <Pressable onClick={commitArtImport} disabled={artImportCommitting || !artImportPreview.rows.length} style={{ width: "100%", ...btnSolid(A.blue), opacity: (artImportCommitting || !artImportPreview.rows.length) ? .5 : 1 }}>
              {artImportCommitting ? t("artImport.preview.importing", "Import en cours...") : t("artImport.preview.importBtnTemplate", { n: artImportPreview.rows.length }, `Importer ${artImportPreview.rows.length} article(s)`)}
            </Pressable>
          </div>
        )}

        {/* ================= DLC ARTICLES PROMOTIONNELS KÜHLUNG ================= */}
        {screen === "newArticles" && (
          <>
            <section style={{ ...colorCard(A.orange), ...tile, padding: 18 }}>
              <div style={onColorChip}><EmojiIcon e="🆕" /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, color: "#fff" }}>{t("control.hub.newArticlesTitle", "DLC Articles promotionnels kühlung")}</strong>
                <p style={{ margin: "3px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>
                  {t("control.newArticlesFullDesc", "Articles reçus mais absents du catalogue officiel. Enregistre leur DLC ici, un article peut en avoir plusieurs.")}
                </p>
              </div>
              <Pressable onClick={() => openCreatePromoArtSheet("KUEHLUNG")} aria-label={t("controlNewArt.addArticleAria", "Ajouter un article")} style={{
                flex: "0 0 auto", width: 36, height: 36, borderRadius: 99, background: "rgba(255,255,255,.22)", color: "#fff",
                display: "grid", placeItems: "center", fontSize: `calc(19px * var(--nx-font-scale, 1))`, fontWeight: 700,
              }}>＋</Pressable>
            </section>
            {/* Un article Aktion (Kühlung-Nouveaux) qui n'a plus AUCUNE DLC active (expirée
                sans avoir été réduite, ou déjà réduite en Réductions) disparaît complètement
                de cette liste plutôt que d'afficher "Aucune DLC enregistrée" — retour
                utilisateur explicite (2026-08-16) : c'est un article promotionnel temporaire,
                pas un article vendu tous les jours, il n'a rien à faire à traîner ici sans
                DLC valable. S'il rejoint un jour l'assortiment officiel (zone catalogue
                change), il sort de newArticles de lui-même et rejoint le flux Sortiment
                normal — déjà géré par l'architecture existante, rien à faire ici pour ce cas. */}
            {(() => { const newArticlesWithMhd = newArticles.filter((a) => records.some((r) => !r.deletedAt && r.artNr === a.artNr)); return (
            <>
            {newArticlesWithMhd.length === 0 && (
              <p style={{ color: P.muted, fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("controlNewArt.emptyState", "Aucun article hors catalogue pour le moment.")}</p>
            )}
            {newArticlesWithMhd.map((a) => {
              const recs = records.filter((r) => !r.deletedAt && r.artNr === a.artNr).sort((x, y) => x.mhd.localeCompare(y.mhd));
              return (
                <section key={a.artNr} style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 14, marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: recs.length ? 10 : 2 }}>
                    <div style={{ minWidth: 0 }}>
                      <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</strong>
                      <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlRun.artNrPrefix", "Art.Nr")} {a.artNr}</span>
                    </div>
                    <div style={{ display: "flex", gap: 8, flex: "0 0 auto" }}>
                      <Pressable onClick={() => openQuickRule(a.artNr, a.name)} aria-label={t("reductions.ruleAria", "Règle de réduction")} style={{
                        width: 34, height: 34, borderRadius: 99, background: ruleOverrides[a.artNr] ? "rgba(54,79,231,.15)" : P.soft, color: ruleOverrides[a.artNr] ? A.blue : P.muted,
                        display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`,
                      }}>⚙️</Pressable>
                      <Pressable onClick={() => openNewArtSheet(a.artNr)} aria-label={t("controlRun.newMhdModeLabel", "Nouvelle DLC")} style={{
                        width: 34, height: 34, borderRadius: 99, background: P.soft, color: P.ink,
                        display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`, fontWeight: 700,
                      }}>＋</Pressable>
                    </div>
                  </div>
                  <Pressable onClick={() => openInsertPromoArtSheet(a)} style={{
                    width: "100%", boxSizing: "border-box", textAlign: "center", padding: "9px 0", borderRadius: 12,
                    marginBottom: recs.length ? 10 : 2, fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.blue, background: "rgba(54,79,231,.1)",
                  }}>📥 {t("controlNewArt.insertToRayonBtn", "Insérer au rayon")}</Pressable>
                  {recs.length === 0 ? (
                    <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlRun.noMhdForArticle", "Aucune DLC enregistrée pour cet article.")}</p>
                  ) : recs.map((r) => {
                    const u = urgency(r.mhd);
                    return (
                      <Pressable key={r.id} onClick={() => openNewArtSheet(a.artNr, r.id)} style={{
                        display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                        padding: 12, borderRadius: 14, marginBottom: 6, color: P.ink, ...urgRow(u),
                      }}>
                        <span style={{ width: 9, height: 9, borderRadius: 99, background: urgColor[u], flex: "0 0 auto" }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{t("controlStore.dlcShortLabel", "DLC")} {fmtFR(r.mhd)}</strong>
                          <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{r.quantity ? `${t("controlRun.qtyPrefix", "Qté")} ${r.quantity}` : t("controlRun.qtyDash", "Qté —")} · {r.note || t("controlRun.noNote", "Sans remarque")}</div>
                        </div>
                        <Pressable onClick={(e) => { e.stopPropagation(); deleteNewArtMhd(r.id); }} aria-label={t("controlRun.deleteThisMhdBtn", "Supprimer cette DLC")} style={{
                          width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.15)", flex: "0 0 auto",
                          display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                        }}>🗑️</Pressable>
                      </Pressable>
                    );
                  })}
                </section>
              );
            })}
            </>
            ); })()}
          </>
        )}

        {/* ================= DLC ARTICLES PROMOTIONNELS FRISCH FLEISCH ================= */}
        {screen === "newArticlesFF" && (
          <>
            <section style={{ ...colorCard(A.red), ...tile, padding: 18 }}>
              <div style={onColorChip}><EmojiIcon e="🥩" /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, color: "#fff" }}>{t("control.hub.newArticlesFFTitle", "DLC Articles promotionnels Frisch Fleisch")}</strong>
                <p style={{ margin: "3px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>
                  {t("control.newArticlesFFFullDesc", "Articles reçus mais absents du catalogue officiel Frisch Fleisch. Enregistre leur DLC ici, un article peut en avoir plusieurs.")}
                </p>
              </div>
              <Pressable onClick={() => openCreatePromoArtSheet("FRISCHFLEISCH")} aria-label={t("controlNewArt.addArticleAria", "Ajouter un article")} style={{
                flex: "0 0 auto", width: 36, height: 36, borderRadius: 99, background: "rgba(255,255,255,.22)", color: "#fff",
                display: "grid", placeItems: "center", fontSize: `calc(19px * var(--nx-font-scale, 1))`, fontWeight: 700,
              }}>＋</Pressable>
            </section>
            {/* Même règle que newArticles ci-dessus (retour utilisateur explicite,
                2026-08-16) — un article Aktion Frisch Fleisch sans DLC active disparaît
                complètement plutôt que d'afficher "Aucune DLC enregistrée". */}
            {(() => { const newArticlesFFWithMhd = newArticlesFF.filter((a) => czRecords.some((r) => r.source === "FRISCHFLEISCH" && !r.deletedAt && r.artNr === a.artNr)); return (
            <>
            {newArticlesFFWithMhd.length === 0 && (
              <p style={{ color: P.muted, fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("controlNewArt.emptyState", "Aucun article hors catalogue pour le moment.")}</p>
            )}
            {newArticlesFFWithMhd.map((a) => {
              const recs = czRecords.filter((r) => r.source === "FRISCHFLEISCH" && !r.deletedAt && r.artNr === a.artNr).sort((x, y) => x.mhd.localeCompare(y.mhd));
              return (
                <section key={a.artNr} style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 14, marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: recs.length ? 10 : 2 }}>
                    <div style={{ minWidth: 0 }}>
                      <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</strong>
                      <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlRun.artNrPrefix", "Art.Nr")} {a.artNr}</span>
                    </div>
                    <div style={{ display: "flex", gap: 8, flex: "0 0 auto" }}>
                      <Pressable onClick={() => openQuickRule(a.artNr, a.name)} aria-label={t("reductions.ruleAria", "Règle de réduction")} style={{
                        width: 34, height: 34, borderRadius: 99, background: ruleOverrides[a.artNr] ? "rgba(54,79,231,.15)" : P.soft, color: ruleOverrides[a.artNr] ? A.blue : P.muted,
                        display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`,
                      }}>⚙️</Pressable>
                      <Pressable onClick={() => openNewArtSheetFF(a.artNr)} aria-label={t("controlRun.newMhdModeLabel", "Nouvelle DLC")} style={{
                        width: 34, height: 34, borderRadius: 99, background: P.soft, color: P.ink,
                        display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`, fontWeight: 700,
                      }}>＋</Pressable>
                    </div>
                  </div>
                  {recs.length === 0 ? (
                    <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlRun.noMhdForArticle", "Aucune DLC enregistrée pour cet article.")}</p>
                  ) : recs.map((r) => {
                    const u = urgency(r.mhd);
                    return (
                      <Pressable key={r.id} onClick={() => openNewArtSheetFF(a.artNr, r.id)} style={{
                        display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                        padding: 12, borderRadius: 14, marginBottom: 6, color: P.ink, ...urgRow(u),
                      }}>
                        <span style={{ width: 9, height: 9, borderRadius: 99, background: urgColor[u], flex: "0 0 auto" }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{t("controlStore.dlcShortLabel", "DLC")} {fmtFR(r.mhd)}</strong>
                          <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{r.quantity ? `${t("controlRun.qtyPrefix", "Qté")} ${r.quantity}` : t("controlRun.qtyDash", "Qté —")} · {r.note || t("controlRun.noNote", "Sans remarque")}</div>
                        </div>
                        <Pressable onClick={(e) => { e.stopPropagation(); deleteNewArtMhdFF(r.id); }} aria-label={t("controlRun.deleteThisMhdBtn", "Supprimer cette DLC")} style={{
                          width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.15)", flex: "0 0 auto",
                          display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                        }}>🗑️</Pressable>
                      </Pressable>
                    );
                  })}
                </section>
              );
            })}
            </>
            ); })()}
          </>
        )}

        {/* ================= MISSIONS RÉSEAU (compte normal) ================= */}
        {screen === "missions" && (() => {
          const statusOf = (m) => (m.filialeStatuses[m.myStores[0]] || { status: "en_attente" }).status;
          const nonArchived = myNetworkMissions.filter((m) => !m.myArchived);
          const enCoursMine = nonArchived.filter((m) => !TERMINAL_MISSION_STATUSES.includes(statusOf(m)));
          const termineesMine = nonArchived.filter((m) => TERMINAL_MISSION_STATUSES.includes(statusOf(m)));
          const archiveesMine = myNetworkMissions.filter((m) => m.myArchived);
          const MY_TABS = [
            { id: "en_cours", l: t("tasks.tabOpen", "En cours"), n: enCoursMine.length },
            { id: "terminees", l: t("tasks.tabDone", "Terminées"), n: termineesMine.length },
            { id: "archivees", l: t("messages.tabArchived", "Archivées"), n: archiveesMine.length },
          ];
          const shownRaw = myMissionTab === "en_cours" ? enCoursMine : myMissionTab === "terminees" ? termineesMine : archiveesMine;
          const EMPTY_COPY_MINE = {
            en_cours: t("missions.emptyOngoing", "Aucune mission en cours."),
            terminees: t("missions.emptyDone", "Aucune mission terminée pour l'instant."),
            archivees: t("missions.emptyArchived", "Aucune mission archivée."),
          };
          // Tri + regroupement par échéance (2026-08-31, retour utilisateur explicite : liste
          // pas triée par urgence — préféré à un vrai calendrier, "trop de poids visuel pour
          // le volume de missions à la fois") — seulement pour "En cours", là où l'urgence
          // compte vraiment ; Terminées/Archivées gardent l'ordre existant (récence).
          const today = todayISO();
          const weekLimit = addDays(7);
          const missionDateGroup = (m) => {
            if (!m.echeanceDate) return "none";
            if (m.echeanceDate < today) return "overdue";
            if (m.echeanceDate === today) return "today";
            if (m.echeanceDate <= weekLimit) return "week";
            return "later";
          };
          const GROUP_ORDER = ["overdue", "today", "week", "later", "none"];
          const GROUP_LABELS = {
            overdue: t("missions.dateGroup.overdue", "En retard"),
            today: t("missions.dateGroup.today", "Aujourd'hui"),
            week: t("missions.dateGroup.week", "Cette semaine"),
            later: t("missions.dateGroup.later", "Plus tard"),
            none: t("missions.dateGroup.none", "Sans échéance"),
          };
          const shown = myMissionTab === "en_cours"
            ? [...shownRaw].sort((a, b) => {
                const ga = GROUP_ORDER.indexOf(missionDateGroup(a));
                const gb = GROUP_ORDER.indexOf(missionDateGroup(b));
                if (ga !== gb) return ga - gb;
                const ta = `${a.echeanceDate || ""}T${a.echeanceTime || "00:00"}`;
                const tb = `${b.echeanceDate || ""}T${b.echeanceTime || "00:00"}`;
                return ta.localeCompare(tb);
              })
            : shownRaw;
          let lastGroupSeen = null;
          return (
            <>
              <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
                {MY_TABS.map((tb) => (
                  <Pressable key={tb.id} onClick={() => setMyMissionTab(tb.id)} style={{
                    flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                    background: myMissionTab === tb.id ? A.purple : P.soft, color: myMissionTab === tb.id ? "#fff" : P.muted,
                  }}>
                    {tb.l}
                    {tb.n > 0 && (
                      <span style={{
                        fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, minWidth: 16, height: 16, padding: "0 4px", display: "grid", placeItems: "center",
                        background: myMissionTab === tb.id ? "rgba(255,255,255,.28)" : "rgba(139,47,214,.15)", color: myMissionTab === tb.id ? "#fff" : A.purple,
                      }}>{tb.n}</span>
                    )}
                  </Pressable>
                ))}
              </div>

              {myMissionTab === "archivees" && archiveesMine.length > 0 && (
                <Pressable onClick={deleteAllMyArchivedMissions} style={{
                  width: "100%", textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: "rgba(239,68,68,.1)", color: A.red, marginBottom: 14,
                }}>🗑️ {t("reductions.clearAllExpiredBtn", "Tout supprimer")}</Pressable>
              )}

              {shown.length === 0 && (
                <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "20px 0" }}>{EMPTY_COPY_MINE[myMissionTab]}</p>
              )}
              {shown.map((m) => {
                // En-tête de groupe (2026-08-31) — seulement sur "En cours" (shown déjà trié
                // dans ce cas, voir plus haut) ; une seule fois par groupe, jamais répété.
                let groupHeader = null;
                if (myMissionTab === "en_cours") {
                  const g = missionDateGroup(m);
                  if (g !== lastGroupSeen) {
                    lastGroupSeen = g;
                    groupHeader = (
                      <p key={`group-${g}`} style={{
                        margin: "16px 2px 8px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: ".04em", textTransform: "uppercase",
                        color: g === "overdue" ? A.red : P.muted,
                      }}>{GROUP_LABELS[g]}</p>
                    );
                  }
                }
                const fs = m.filialeStatuses[m.myStores[0]] || { status: "en_attente" };
                const fMeta = FILIALE_STATUS_META[fs.status];
                const pm = priorityMeta[m.priority || "normal"];
                // Coloration immédiate — retour utilisateur explicite, 2026-08-02 : vert
                // clair si validée, rouge clair si archivée (même règle que côté GR —
                // archivée prime sur validée si les deux sont vraies).
                const cardTint = m.myArchived
                  ? { background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.25)" }
                  : fs.status === "validee"
                  ? { background: "rgba(30,184,95,.10)", border: "1px solid rgba(30,184,95,.3)" }
                  : fs.status === "refusee"
                  ? { background: "rgba(239,68,68,.06)", border: "1px solid rgba(239,68,68,.18)" }
                  : {};
                // Grisé + titre barré pour toute mission TERMINALE non archivée (validée OU
                // refusée) — retour utilisateur explicite 2026-08-24 : "les missions
                // terminées et archivées doivent changer d'aspect et non le même aspect que
                // quand la mission est en cours". "refusee" est déjà dans
                // TERMINAL_MISSION_STATUSES (donc déjà dans l'onglet "Terminées") mais
                // n'avait jusqu'ici AUCUNE teinte ni aucun grisé — visuellement indiscernable
                // d'une mission en cours. Même patron que doneFade (Tâches, renderTaskCard)
                // et le grisé déjà appliqué côté vue GR (renderMissionCard).
                const doneFade = (TERMINAL_MISSION_STATUSES.includes(fs.status) && !m.myArchived) ? { opacity: .62 } : {};
                return (
                  <Fragment key={m.id}>
                  {groupHeader}
                  <div style={{
                    ...card, ...cardTint, ...doneFade, padding: 14, marginBottom: 9, display: "flex", alignItems: "flex-start", gap: 10,
                  }}>
                    <Pressable onClick={() => openMyMissionDetail(m.id)} style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
                        <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, textDecoration: doneFade.opacity ? "line-through" : "none" }}>{m.title}</strong>
                        {m.priority !== "normal" && <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: pm.color, borderRadius: 99, padding: "2px 7px" }}>{pm.label}</span>}
                        <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: fMeta.c, background: fMeta.bg, borderRadius: 99, padding: "3px 8px", whiteSpace: "nowrap" }}>{fMeta.l}</span>
                        {m.hasUnread && (
                          <span aria-label={t("missions.detail.unreadDialogueAria", "Nouveau message dans le dialogue")} title={t("missions.detail.unreadDialogueAria", "Nouveau message dans le dialogue")} style={{
                            width: 9, height: 9, borderRadius: 99, background: A.red, flex: "0 0 auto", boxShadow: "0 0 0 2px rgba(239,68,68,.2)",
                          }} />
                        )}
                      </div>
                      <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>
                        {m.echeanceDate ? `${t("tasks.deadlinePrefix", "Échéance ")}${fmtFR(m.echeanceDate)}${m.echeanceTime ? ` à ${m.echeanceTime}` : ""}` : t("missions.noDeadline", "Aucune échéance")}
                      </div>
                    </Pressable>
                    <div style={{ display: "flex", gap: 5, flex: "0 0 auto" }}>
                      <Pressable onClick={() => archiveMyMission(m.id, !m.myArchived)} aria-label={m.myArchived ? t("missions.restoreAriaTemplate", { title: m.title }, `Restaurer ${m.title}`) : t("missions.archiveAriaTemplate", { title: m.title }, `Archiver ${m.title}`)} style={{
                        width: 30, height: 30, borderRadius: 99, background: P.soft, color: P.muted, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                      }}>{m.myArchived ? "↩️" : "🗄️"}</Pressable>
                      {/* Bug réel signalé (retour utilisateur 2026-08-09) : ce bouton était
                          affiché même sur une mission "en_attente"/"a_valider", jamais
                          accomplie — un employé pouvait donc la faire disparaître de sa
                          liste sans jamais l'avoir traitée, pendant que le GR la croyait
                          toujours en attente. Réservé maintenant aux statuts terminaux
                          (mêmes que ceux qui séparent déjà les onglets En cours/Terminées),
                          garde-fou identique côté serveur (DELETE /:id/mine). */}
                      {TERMINAL_MISSION_STATUSES.includes(fs.status) && (
                        <Pressable onClick={() => deleteMyMission(m)} aria-label={t("missions.deleteAriaTemplate", { title: m.title }, `Supprimer ${m.title}`)} style={{
                          width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.1)", color: A.red, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                        }}>🗑️</Pressable>
                      )}
                    </div>
                  </div>
                  </Fragment>
                );
              })}
            </>
          );
        })()}

        {/* ================= RÉDUCTIONS ================= */}
        {/* Refonte 2026-08-11 (retour utilisateur explicite, artefact validé point par point) :
            hub en grille 2 colonnes avec l'urgence du jour visible par catégorie SANS avoir à
            ouvrir chacune d'elles ; l'écran catégorie groupe "À traiter" par vraie urgence
            (urgLabel/urgColor déjà établis : rouge/orange/vert, jamais un 4e état neutre).
            Aucune nouvelle donnée : redCategoryStats/redTodo/urgency() existaient déjà. */}
        {screen === "reductions" && redCategory === null && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 12, ...card, borderRadius: 18, padding: "13px 15px", marginBottom: 14 }}>
              <strong style={{ fontSize: `calc(24px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: "-.02em", color: A.red, minWidth: 34 }}>{redTodo.length}</strong>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("reductions.hubSummaryTitle", "Articles à traiter aujourd'hui")}</strong>
                {/* Échéance réelle plutôt qu'un "aujourd'hui" abstrait (2026-08-31, retour
                    utilisateur explicite) : tant qu'il reste des articles à traiter ET que la
                    filiale est ouverte, affiche le compte à rebours réel avant fermeture —
                    sinon (fermé, ou horaires non configurés) retombe sur le sous-titre neutre
                    d'origine, jamais un texte cassé/inventé. */}
                {redTodo.length > 0 && myStoreHours.openTime && myStoreHours.closeTime ? (
                  <StoreHoursCountdown
                    openTime={myStoreHours.openTime} closeTime={myStoreHours.closeTime} holidayDatesSet={allHolidayDatesSet}
                    render={({ isOpen, remaining }) => (
                      <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: isOpen ? A.red : P.muted, fontWeight: isOpen ? 800 : 500, fontVariantNumeric: "tabular-nums" }}>
                        {isOpen
                          ? t("reductions.hubSummaryClosesIn", { time: remaining }, `⏱ Encore ${remaining} avant fermeture`)
                          : t("reductions.hubSummarySubtitle", "Toutes catégories confondues")}
                      </span>
                    )}
                  />
                ) : (
                  <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("reductions.hubSummarySubtitle", "Toutes catégories confondues")}</span>
                )}
              </div>
              <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>🏷️</span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(200px, 1fr))" : "1fr 1fr", gap: 9 }}>
            {REDUCTION_CATEGORIES.map((cat, idx) => {
              const stats = redCategoryStats(cat.id);
              const last = idx === REDUCTION_CATEGORIES.length - 1 && REDUCTION_CATEGORIES.length % 2 === 1;
              return (
                <Pressable key={cat.id} onClick={() => { setRedCategory(cat.id); setRedTab("todo"); }} style={{
                  position: "relative", borderRadius: 18, padding: 13, minHeight: 92,
                  display: "flex", flexDirection: "column", justifyContent: "flex-end", overflow: "hidden",
                  background: `${cat.color}22`, gridColumn: last ? "1 / -1" : undefined,
                }}>
                  <span style={{
                    position: "absolute", top: 9, right: 9, fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 900, padding: "3px 8px", borderRadius: 99,
                    background: stats.todo > 0 ? (stats.expired > 0 ? A.red : cat.color) : "rgba(255,255,255,.5)",
                    color: stats.todo > 0 ? "#fff" : P.muted,
                  }}>{stats.todo}</span>
                  <span style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`, marginBottom: 6 }}>{cat.emoji}</span>
                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.01em", display: "block", lineHeight: 1.2 }}>{cat.label}</strong>
                  {stats.doneToday > 0 && (
                    <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("reductions.categoryDoneTodayCount", { n: stats.doneToday }, `${stats.doneToday} fait${stats.doneToday > 1 ? "s" : ""} aujourd'hui`)}</span>
                  )}
                </Pressable>
              );
            })}
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <Pressable onClick={() => { resetRedRulesNav(); setSheet("redRules"); }} style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                ...card, borderRadius: 14, padding: "10px 0", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted,
              }}>⚙️ {t("reductions.rulesBtn", "Règles de réduction")}</Pressable>
              <Pressable onClick={() => setRedCategory("trash")} style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                ...card, borderRadius: 14, padding: "10px 0", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted,
              }}>🗑️ {t("reductions.trashBtnTemplate", { n: redTrash.length }, `Corbeille · ${redTrash.length}`)}</Pressable>
            </div>

            {redTodo.length > 0 && (
              <Pressable onClick={exportReductionsPdf} style={{ width: "100%", ...btnSolid(A.purple), marginTop: 10 }}>
                📄 {t("reductions.exportPdfBtn", "Exporter la liste en PDF")}
              </Pressable>
            )}
          </>
        )}

        {/* Corbeille globale — retour utilisateur explicite (2026-08-12) : le bouton du hub
            annonçait un total toutes catégories mais redirigeait vers l'onglet Corbeille
            d'UNE seule catégorie (avec ses 3 autres onglets À traiter/Faites/Expirés sans
            rapport, encore visibles) — vraie vue globale ici, aucun de ces 3 onglets, juste
            la liste complète avec la catégorie de chaque article affichée inline
            (redSourceMeta(r.source).label, déjà utilisé par la corbeille par-catégorie). */}
        {screen === "reductions" && redCategory === "trash" && (
          redTrash.length === 0 ? (
            <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 30 }}>
              <span style={{ fontSize: `calc(34px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>🗑️</span>
              <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`}}>{t("reductions.trashEmptyTitle", "La corbeille est vide.")}</strong>
              <p style={{ margin: "5px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("reductions.trashEmptyText", "Les articles expirés supprimés atterrissent ici et restent restaurables.")}</p>
            </section>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                <Pressable onClick={emptyAllRedTrash} style={{
                  padding: "10px 16px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: "rgba(239,68,68,.13)", color: A.red,
                }}>{t("reductions.emptyTrashBtn", "Vider la corbeille")}</Pressable>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
              {redTrash.map((r) => (
                <div key={r.id} style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 8, minWidth: 0 }}>
                  <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, opacity: .6 }}>🗑️</span>
                  <div onClick={() => openArticleCard(r)} style={{ flex: 1, minWidth: 0, opacity: .75, cursor: "pointer" }}>
                    <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: "line-through" }}>{r.name}</strong>
                    <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                      MHD {fmtFR(r.mhd)} · {redSourceMeta(r.source).label} · {t("reductions.trashedAtInline", { time: r.trashedAt.slice(11, 16) }, `supprimé à ${r.trashedAt.slice(11, 16)}`)}
                    </span>
                  </div>
                  <Pressable onClick={() => restoreFromTrash(r.id)} style={{
                    fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.blue, background: "rgba(54,79,231,.1)",
                    borderRadius: 99, padding: "9px 14px",
                  }}>{t("reductions.restoreBtn", "↩︎ Restaurer")}</Pressable>
                  <Pressable onClick={() => deletePermanently(r.id)} style={{
                    fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.red, background: "rgba(239,68,68,.1)",
                    borderRadius: 99, padding: "9px 14px",
                  }}>{t("reductions.deletePermanentlyBtn", "Supprimer définitivement")}</Pressable>
                </div>
              ))}
              </div>
            </>
          )
        )}

        {screen === "reductions" && redCategory !== null && redCategory !== "trash" && (() => {
          const catTodo = sortForCategory(redTodo.filter((r) => r.source === redCategory), redCategory);
          const catDone = sortForCategory(redDoneToday.filter((d) => d.rec.source === redCategory), redCategory, (d) => d.rec);
          const catExpired = sortForCategory(redExpired.filter((r) => r.source === redCategory), redCategory);
          const catTrash = sortForCategory(redTrash.filter((r) => r.source === redCategory), redCategory);
          const catPct = (catTodo.length + catDone.length) === 0 ? 100
            : Math.round((catDone.length / (catTodo.length + catDone.length)) * 100);
          const catMeta = REDUCTION_CATEGORIES.find((c) => c.id === redCategory) || REDUCTION_CATEGORIES[0];
          // Groupé par la vraie urgence déjà établie (urgency()/urgLabel — rouge ≤2j ouvrés,
          // orange ≤5j, vert au-delà), jamais une liste plate triée par date seule.
          const catTodoByUrgency = { red: [], orange: [], green: [] };
          catTodo.forEach((r) => catTodoByUrgency[urgency(r.mhd)].push(r));
          return (
          <>
            {/* Héro : anneau = vraie progression du jour de la catégorie, couleur = la vraie
                couleur établie de cette catégorie (REDUCTION_CATEGORIES), jamais un violet
                générique appliqué à toutes. */}
            <section style={{
              borderRadius: 22, padding: 16, marginBottom: 13, display: "flex", alignItems: "center", gap: 14,
              background: `linear-gradient(155deg, rgba(255,255,255,.22), rgba(255,255,255,0) 55%), ${catMeta.color}`,
            }}>
              <div style={{
                width: 62, height: 62, borderRadius: 999, flex: "0 0 auto", display: "grid", placeItems: "center",
                background: `conic-gradient(#fff ${catPct}%, rgba(255,255,255,.25) 0)`,
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 999, background: catMeta.color,
                  display: "grid", placeItems: "center", color: "#fff", fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                }}>{catPct}%</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={onColorTag}>{t("reductions.heroKickerDetail", "À traiter aujourd'hui")}</span>
                <h2 style={{ margin: "3px 0 3px", fontSize: `calc(15.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>
                  {catTodo.length === 0 ? t("reductions.detailAllDoneTitle", "Tout est réduit 🎉") : t("reductions.detailTodoCount", { n: catTodo.length }, `${catTodo.length} article${catTodo.length > 1 ? "s" : ""} à réduire`)}
                </h2>
                <p style={{ margin: 0, fontSize: `calc(11px * var(--nx-font-scale, 1))`, lineHeight: 1.4, ...onColorSub }}>
                  {t("reductions.detailDoneCountTemplate", { done: catDone.length, remaining: catTodo.length, total: catTodo.length + catDone.length }, `${catDone.length} traités · ${catTodo.length} restants sur ${catTodo.length + catDone.length}`)}
                </p>
              </div>
            </section>

            {/* Règles de fenêtre J-X */}
            <div style={{ display: "flex", justifyContent: "flex-end", margin: "-4px 0 10px" }}>
              <Pressable onClick={() => { resetRedRulesNav(); setSheet("redRules"); }} style={{
                display: "flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 99,
                fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
              }}>⚙️ {t("reductions.rulesBtn", "Règles de réduction")}{Object.keys(ruleOverrides).length ? ` · ${t("reductions.rulesExceptionCount", { n: Object.keys(ruleOverrides).length }, `${Object.keys(ruleOverrides).length} exception${Object.keys(ruleOverrides).length > 1 ? "s" : ""}`)}` : ""}</Pressable>
            </div>

            {/* Segments — piste neutre, seul l'onglet actif ressort (retour utilisateur
                explicite, artefact validé) : la couleur de l'app reste portée par l'urgence
                des cartes, jamais une 2e couche de couleur sur les onglets. */}
            <div style={{ display: "flex", gap: 5, background: P.soft, borderRadius: 13, padding: 4, marginBottom: 15 }}>
              {[
                { id: "todo", label: `${t("reductions.expiredPageTodoLabel", "À traiter")} · ${catTodo.length}` },
                { id: "done", label: `${t("reductions.doneLabel", "Faites")} · ${catDone.length}` },
                { id: "expired", label: `${t("reductions.expiredLabel", "Expirés")} · ${catExpired.length}` },
                { id: "trash", label: `🗑️ ${catTrash.length}` },
              ].map((s) => (
                <Pressable key={s.id} onClick={() => setRedTab(s.id)} style={{
                  flex: 1, textAlign: "center", padding: "8px 4px", borderRadius: 10, fontWeight: 800, fontSize: `calc(10.8px * var(--nx-font-scale, 1))`,
                  background: redTab === s.id ? P.card : "none",
                  color: redTab === s.id ? P.ink : P.muted,
                  boxShadow: redTab === s.id ? P.shadow : "none",
                }}>{s.label}</Pressable>
              ))}
            </div>

            {/* À TRAITER */}
            {redTab === "todo" && (
              catTodo.length === 0 ? (
                <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 30 }}>
                  <span style={{ fontSize: `calc(38px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>🎉</span>
                  <strong style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>{t("reductions.emptyTitle", "Toutes les réductions du jour sont terminées.")}</strong>
                  <p style={{ margin: "5px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("reductions.emptyText", "Excellent travail. Aucun article normal à réduire.")}</p>
                  {catExpired.length > 0 && (
                    <Pressable onClick={() => setRedTab("expired")} style={{
                      marginTop: 14, padding: "11px 22px", borderRadius: 99, fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                      background: "rgba(239,68,68,.13)", color: A.red,
                    }}>{t("reductions.viewExpiredCount", { n: catExpired.length }, `Voir les ${catExpired.length} article${catExpired.length > 1 ? "s" : ""} expiré${catExpired.length > 1 ? "s" : ""}`)}</Pressable>
                  )}
                </section>
              ) : (
              <>
              {["red", "orange", "green"].map((u) => {
                const group = catTodoByUrgency[u];
                if (!group.length) return null;
                return (
                  <div key={u}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7, margin: "14px 2px 8px" }}>
                      <span style={{ width: 8, height: 8, borderRadius: 99, background: urgColor[u], flex: "0 0 auto" }} />
                      <strong style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: ".02em" }}>{urgLabel[u]}</strong>
                      <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, marginLeft: "auto" }}>{group.length}</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
                    {group.map((r) => {
                      const dl = daysLeft(r.mhd);
                      return (
                        <div key={r.id} style={{ ...card, ...tile, padding: 13, marginBottom: isWide ? 0 : 8, alignItems: "center", minWidth: 0, ...urgRow(u) }}>
                          <span style={{
                            flex: "0 0 auto", width: 34, height: 34, borderRadius: 11, fontSize: `calc(15px * var(--nx-font-scale, 1))`,
                            background: redSourceMeta(r.source).bg,
                            display: "grid", placeItems: "center",
                          }}>{redSourceMeta(r.source).emoji}</span>
                          <div onClick={() => openArticleCard(r)} style={{ flex: 1, minWidth: 0, cursor: "pointer" }}>
                            <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</strong>
                            <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                              {r.quantity ? `${t("reductions.fieldQty", "Qté")} ${r.quantity} · ` : ""}{t("controlRun.artNrPrefix", "Art.Nr")} {r.artNr}
                              {ruleOverrides[r.artNr] ? ` · ${reductionRuleLabel(ruleOverrides[r.artNr])}` : ""}
                            </span>
                          </div>
                          <span style={{
                            fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 900, padding: "3px 8px", borderRadius: 99, whiteSpace: "nowrap",
                            background: `${urgColor[u]}22`, color: urgColor[u],
                          }}>{dl === 0 ? t("reductions.countdownLastDay", "dernier jour") : `J-${dl}`}</span>
                          {/* Retour utilisateur explicite (2026-08-29) : le bouton Supprimer ajouté
                              ici le 2026-08-20 a été retiré — un article "À traiter" n'est PAS
                              encore réduit, le laisser supprimable ici risquait de faire disparaître
                              un vrai MHD jamais traité (perte silencieuse). Supprimer reste possible
                              UNIQUEMENT depuis l'onglet Expirées (voir dropExpired plus bas), jamais
                              tant qu'un article est encore actionnable. */}
                          <Pressable onClick={() => markReduced(r)} aria-label={t("reductions.reduceBtn", "✓ Réduit")} style={{
                            flex: "0 0 auto", width: 30, height: 30, borderRadius: 99, background: P.card,
                            border: `1.5px solid ${P.line}`, color: P.ink, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800,
                          }}>✓</Pressable>
                        </div>
                      );
                    })}
                    </div>
                  </div>
                );
              })}
              </>
              )
            )}

            {/* FAITES */}
            {redTab === "done" && (
              catDone.length === 0 ? (
                <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 30 }}>
                  <span style={{ fontSize: `calc(34px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>🕐</span>
                  <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`}}>{t("reductions.doneEmptyTitle", "Rien de réduit aujourd'hui pour l'instant.")}</strong>
                  <p style={{ margin: "5px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("reductions.doneEmptyText", "Les réductions faites apparaîtront ici au fil de la journée.")}</p>
                </section>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
                  {catDone.map((d) => (
                    <div key={d.recId} style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 8, minWidth: 0, background: "rgba(30,184,95,.09)", border: "1px solid rgba(30,184,95,.22)" }}>
                      <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>✅</span>
                      <div onClick={() => openArticleCard(d.rec)} style={{ flex: 1, minWidth: 0, cursor: "pointer" }}>
                        <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.rec.name}</strong>
                        <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                          MHD {fmtFR(d.rec.mhd)} · {t("reductions.reducedAtInline", { time: d.doneAt.slice(11, 16) }, `réduit à ${d.doneAt.slice(11, 16)}`)} · {redSourceMeta(d.rec.source).label}
                        </span>
                      </div>
                      <Pressable onClick={() => undoReduced(d.recId)} style={{
                        fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, background: P.soft,
                        borderRadius: 99, padding: "8px 12px",
                      }}>{t("common.cancel", "Annuler")}</Pressable>
                    </div>
                  ))}
                </div>
              )
            )}

            {/* CORBEILLE */}
            {redTab === "trash" && (
              catTrash.length === 0 ? (
                <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 30 }}>
                  <span style={{ fontSize: `calc(34px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>🗑️</span>
                  <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`}}>{t("reductions.trashEmptyTitle", "La corbeille est vide.")}</strong>
                  <p style={{ margin: "5px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("reductions.trashEmptyText", "Les articles expirés supprimés atterrissent ici et restent restaurables.")}</p>
                </section>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                    <Pressable onClick={() => emptyRedTrash(redCategory)} style={{
                      padding: "10px 16px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                      background: "rgba(239,68,68,.13)", color: A.red,
                    }}>{t("reductions.emptyTrashBtn", "Vider la corbeille")}</Pressable>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
                  {catTrash.map((r) => (
                    <div key={r.id} style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 8, minWidth: 0 }}>
                      <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, opacity: .6 }}>🗑️</span>
                      <div onClick={() => openArticleCard(r)} style={{ flex: 1, minWidth: 0, opacity: .75, cursor: "pointer" }}>
                        <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: "line-through" }}>{r.name}</strong>
                        <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                          MHD {fmtFR(r.mhd)} · {redSourceMeta(r.source).label} · {t("reductions.trashedAtInline", { time: r.trashedAt.slice(11, 16) }, `supprimé à ${r.trashedAt.slice(11, 16)}`)}
                        </span>
                      </div>
                      <Pressable onClick={() => restoreFromTrash(r.id)} style={{
                        fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.blue, background: "rgba(54,79,231,.1)",
                        borderRadius: 99, padding: "9px 14px",
                      }}>{t("reductions.restoreBtn", "↩︎ Restaurer")}</Pressable>
                      <Pressable onClick={() => deletePermanently(r.id)} style={{
                        fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.red, background: "rgba(239,68,68,.1)",
                        borderRadius: 99, padding: "9px 14px",
                      }}>{t("reductions.deletePermanentlyBtn", "Supprimer définitivement")}</Pressable>
                    </div>
                  ))}
                  </div>
                </>
              )
            )}

            {/* EXPIRÉS */}
            {redTab === "expired" && (
              catExpired.length === 0 ? (
                <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 30 }}>
                  <span style={{ fontSize: `calc(34px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>🛡️</span>
                  <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`}}>{t("reductions.noExpiredArticle", "Aucun article expiré.")}</strong>
                  <p style={{ margin: "5px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("reductions.expiredPageEmptyText", "Tout est sous contrôle.")}</p>
                </section>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                    <Pressable onClick={() => { catExpired.forEach((r) => dropExpired(r.id)); }} style={{
                      padding: "10px 16px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                      background: "rgba(239,68,68,.13)", color: A.red,
                    }}>{t("reductions.clearAllExpiredBtn", "Tout supprimer")}</Pressable>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
                  {catExpired.map((r) => (
                    <div key={r.id} style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 8, minWidth: 0, background: "rgba(239,68,68,.09)", border: "1px solid rgba(239,68,68,.25)" }}>
                      <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>⛔</span>
                      <div onClick={() => openArticleCard(r)} style={{ flex: 1, minWidth: 0, cursor: "pointer" }}>
                        <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</strong>
                        <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                          {t("reductions.expiredSinceCount", { n: Math.abs(daysLeft(r.mhd)) }, `Expiré depuis ${Math.abs(daysLeft(r.mhd))} j`)} · MHD {fmtFR(r.mhd)}{r.quantity ? ` · ${t("reductions.fieldQty", "Qté")} ${r.quantity}` : ""}
                        </span>
                      </div>
                      <Pressable onClick={() => dropExpired(r.id)} aria-label={t("common.delete", "Supprimer")} style={{
                        width: 34, height: 34, borderRadius: 99, background: "rgba(239,68,68,.16)",
                        display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`,
                      }}>🗑️</Pressable>
                    </div>
                  ))}
                  </div>
                </>
              )
            )}
          </>
          );
        })()}

        {/* ================= ARTICLES ================= */}
        {screen === "articles" && artScreen === "hub" && (
          <>
            <p style={{ color: P.muted, fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "2px 2px 16px", lineHeight: 1.5 }}>
              {t("articles.heroTitle", "Choisis un rayon")} — Art.Nr, prix, unité et ordre de rayon pour chaque article.
            </p>
            {/* Signalement nouveaux articles — artefact approuvé 2026-08-12
                (https://claude.ai/code/artifact/cc4f3822-a2af-4f10-b0e4-b8fd19880e9a) : tuile
                distincte en tête, hors liste des rayons, accent orange pour ne jamais se
                confondre avec une couleur de rayon existante (aucune des 7 zones n'utilise
                A.orange). Badge = même composant nx-notif-dot que le reste de l'app (accueil,
                notifications), pas un badge réinventé. */}
            <Pressable onClick={() => setArtScreen("reports")} style={{
              ...card, ...tile, padding: 18, marginBottom: 12, alignItems: "center", position: "relative",
              background: `linear-gradient(155deg, ${A.orange}24, ${A.orange}0d)`, border: `1.3px solid ${A.orange}52`,
            }}>
              {articleReports.length > 0 && (
                <span className="nx-notif-dot" style={{
                  position: "absolute", top: -6, right: -6, minWidth: 18, height: 18, padding: "0 4px",
                  borderRadius: 99, background: A.red, border: `2px solid ${P.bg}`, color: "#fff",
                  fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, display: "grid", placeItems: "center",
                }}>{articleReports.length}</span>
              )}
              <span style={{
                flex: "0 0 auto", width: 50, height: 50, borderRadius: 17, fontSize: `calc(22px * var(--nx-font-scale, 1))`,
                background: `${A.orange}22`, color: A.orange, display: "grid", placeItems: "center",
              }}>📸</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`}}>{t("articles.reports.tileTitle", "Signalement nouveaux articles")}</strong>
                <div style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 2 }}>
                  {articleReports.length > 0
                    ? t("articles.reports.tilePendingCount", { n: articleReports.length }, `${articleReports.length} en attente d'insertion`)
                    : t("articles.reports.tileEmpty", "Aucun signalement pour l'instant")}
                </div>
              </div>
              <span style={{ color: P.muted, fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>›</span>
            </Pressable>
            {canImportArticlesCatalog && (
              <Pressable onClick={() => { resetArtImport(); setArtScreen("import"); }} style={{ ...card, ...tile, padding: 15, marginBottom: 12 }}>
                <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>🤖</span>
                <div style={{ flex: 1 }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{t("artImport.hub.title", "Importer un catalogue")}</strong>
                  <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("artImport.hub.desc", "Photo ou PDF, lecture automatique par IA — pour démarrer une nouvelle filiale.")}</div>
                </div>
                <span style={{ color: P.muted, fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>›</span>
              </Pressable>
            )}
            <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".06em", color: P.muted, margin: "16px 2px 8px" }}>{t("articles.reports.rayonsSectionLabel", "Rayons")}</div>
            {ARTICLE_ZONES.map((cat) => {
              const count = cat.id === "Prebake" ? prebakeAsArticles().length : articlesCatalog.filter((a) => a.zone === cat.id).length;
              return (
                <Pressable key={cat.id} onClick={() => { setArtZone(cat.id); setArtQuery(""); setArtFilter("all"); setArtScreen("list"); }} style={{
                  ...card, ...tile, padding: 18, marginBottom: 12, alignItems: "center",
                }}>
                  <span style={{
                    flex: "0 0 auto", width: 50, height: 50, borderRadius: 17, fontSize: `calc(22px * var(--nx-font-scale, 1))`,
                    background: `${cat.color}22`, display: "grid", placeItems: "center",
                  }}>{cat.emoji}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`}}>{cat.label}</strong>
                    <div style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 2 }}>{cat.subtitle} · {count > 0 ? t("articles.articleCountShort", { n: count }, `${count} article${count > 1 ? "s" : ""}`) : t("articles.noArticleYet", "Aucun article pour l'instant")}</div>
                  </div>
                  <span style={{ color: P.muted, fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>›</span>
                </Pressable>
              );
            })}
            <Pressable onClick={() => { setArtZone(null); setArtQuery(""); setArtFilter("all"); setArtScreen("list"); }} style={{
              ...card, ...tile, padding: 16, marginBottom: 12, alignItems: "center", justifyContent: "center",
              background: P.soft, border: `1.5px dashed ${P.line}`,
            }}>
              <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: P.muted }}>📚 {t("articles.seeAllArticlesBtn", "Voir tous les articles")}</strong>
            </Pressable>
          </>
        )}

        {/* Signalement — formulaire (artefact écran 2, reproduction identique). */}
        {screen === "articles" && artScreen === "reportForm" && (
          <>
            <input ref={articleReportPhotoInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
              onChange={(e) => { uploadArticleReportPhoto(e.target.files?.[0]); e.target.value = ""; }} />
            <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink, margin: "0 0 6px 2px" }}>{t("articles.reports.form.photoLabel", "Photo de l'article")}</div>
            <Pressable onClick={() => articleReportPhotoInputRef.current?.click()} style={{
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
              width: "100%", boxSizing: "border-box", height: 150, borderRadius: 20, marginBottom: 14,
              border: `1.6px dashed ${P.line}`, background: P.card, color: P.muted, overflow: "hidden", position: "relative",
            }}>
              {articleReportPhotoUploading ? (
                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{t("articles.reports.form.uploading", "Envoi en cours…")}</span>
              ) : articleReportForm.photoUrl ? (
                <>
                  <img src={articleReportForm.photoUrl} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                  <span style={{ position: "relative", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: "rgba(0,0,0,.45)", borderRadius: 99, padding: "5px 12px" }}>{t("articles.reports.form.changePhoto", "Changer la photo")}</span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: `calc(26px * var(--nx-font-scale, 1))`}}>📷</span>
                  <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{t("articles.reports.form.takePhoto", "Prendre une photo")}</span>
                </>
              )}
            </Pressable>
            <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink, margin: "0 0 6px 2px" }}>{t("articles.reports.form.commentLabel", "Commentaire")}</div>
            <textarea value={articleReportForm.comment} onChange={(e) => setArticleReportForm((f) => ({ ...f, comment: e.target.value }))}
              placeholder={t("articles.reports.form.commentPlaceholder", "Décris l'article — nouveau produit livré, pas encore de place au rayon…")}
              style={{
                width: "100%", boxSizing: "border-box", minHeight: 78, borderRadius: 16, border: `1px solid ${P.line}`,
                background: P.card, padding: "11px 12px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.ink, fontFamily: "inherit", resize: "none", marginBottom: 16,
              }} />
            <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink, margin: "0 0 6px 2px" }}>{t("articles.reports.form.rayonLabel", "Rayon concerné (optionnel)")}</div>
            <div style={{ position: "relative", marginBottom: 20 }}>
              <select value={articleReportForm.rayon} onChange={(e) => setArticleReportForm((f) => ({ ...f, rayon: e.target.value }))} style={{
                width: "100%", boxSizing: "border-box", padding: "12px 14px", borderRadius: 16, border: `1px solid ${P.line}`,
                background: P.card, color: P.ink, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, appearance: "none", WebkitAppearance: "none", fontFamily: "inherit",
              }}>
                <option value="">{t("articles.reports.form.rayonNone", "— Aucun —")}</option>
                {ARTICLE_ZONES.map((cat) => <option key={cat.id} value={cat.id}>{cat.label}</option>)}
              </select>
              <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: P.muted, fontSize: `calc(13px * var(--nx-font-scale, 1))`, pointerEvents: "none" }}>▾</span>
            </div>
            <Pressable onClick={submitArticleReport} disabled={articleReportSubmitting} style={{
              width: "100%", boxSizing: "border-box", padding: 13, borderRadius: 16, textAlign: "center",
              background: `linear-gradient(155deg, rgba(255,255,255,.22), rgba(255,255,255,0) 50%), ${A.orange}`,
              color: "#fff", fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, border: "1px solid rgba(255,255,255,.16)",
              boxShadow: `0 3px 8px ${A.orange}40, 0 10px 20px ${A.orange}38`, opacity: articleReportSubmitting ? .6 : 1,
            }}>{articleReportSubmitting ? t("common.sending", "Envoi…") : t("articles.reports.form.submitBtn", "Envoyer le signalement")}</Pressable>
          </>
        )}

        {/* Signalement — historique (artefact écran 3), photo cliquable → lightbox partagé
            missionPhotoLightbox (artefact écran 4, même composant que Missions/Réclamations/
            Inventurmaßnahme, pas un nouveau composant). */}
        {screen === "articles" && artScreen === "reports" && (
          <>
            <Pressable onClick={() => setArtScreen("reportForm")} style={{
              width: "100%", boxSizing: "border-box", padding: 13, borderRadius: 16, textAlign: "center", marginBottom: 16,
              background: `linear-gradient(155deg, rgba(255,255,255,.22), rgba(255,255,255,0) 50%), ${A.orange}`,
              color: "#fff", fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, border: "1px solid rgba(255,255,255,.16)",
              boxShadow: `0 3px 8px ${A.orange}40, 0 10px 20px ${A.orange}38`,
            }}>+ {t("articles.reports.newBtn", "Signaler un nouvel article")}</Pressable>
            {articleReports.length === 0 && (
              <p style={{ color: P.muted, fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("articles.reports.historyEmpty", "Aucun signalement pour l'instant.")}</p>
            )}
            {articleReports.map((r) => (
              <div key={r.id} style={{ ...card, padding: 13, marginBottom: 11, display: "flex", gap: 11 }}>
                <Pressable onClick={() => setMissionPhotoLightbox(r.photoUrl)} aria-label={t("articles.reports.viewPhotoAria", "Voir la photo")} style={{
                  flex: "0 0 auto", width: 58, height: 58, borderRadius: 14, overflow: "hidden", background: P.soft,
                }}>
                  <img src={r.photoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </Pressable>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 3 }}>{r.createdBy} · {fmtFR(r.createdAt.slice(0, 10))} · {r.createdAt.slice(11, 16)}{r.rayon ? ` · ${artZoneLabel(r.rayon)}` : ""}</div>
                  <span style={{ display: "inline-block", fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, padding: "3px 8px", borderRadius: 99, background: `${A.orange}24`, color: A.orange, marginBottom: 8 }}>{t("articles.reports.pendingPill", "En attente")}</span>
                  <div style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, lineHeight: 1.4, marginBottom: 8 }}>{r.comment}</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Pressable onClick={() => setMissionPhotoLightbox(r.photoUrl)} style={{ flex: 1, textAlign: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, padding: 8, borderRadius: 12, background: P.soft, color: P.muted }}>🔍 {t("articles.reports.photoBtn", "Photo")}</Pressable>
                    {isManager(user) && (
                      <Pressable onClick={() => resolveArticleReport(r.id)} style={{ flex: 1, textAlign: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, padding: 8, borderRadius: 12, background: "rgba(30,184,95,.14)", color: A.green }}>✓ {t("articles.reports.resolveBtn", "Inséré")}</Pressable>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {screen === "articles" && artScreen === "list" && (() => {
          const zoneList = getArticlesForZone(artZone);
          const stats = getArticleStatsFor(zoneList);
          const visible = getVisibleArticlesList();
          const ART_CAP = 180;
          const shownCount = artListBatches * ART_CAP;
          const capped = visible.length > shownCount;
          const shown = capped ? visible.slice(0, shownCount) : visible;
          const remaining = visible.length - shown.length;
          return (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
                {[
                  { label: t("articles.statTotal", "Total"), value: stats.total, color: A.navy },
                  { label: t("articles.statActive", "Actifs"), value: stats.active, color: A.green },
                  { label: t("articles.statPricedOk", "Prix OK"), value: stats.priced, color: A.blue },
                  { label: t("articles.statMissingPrice", "Sans prix"), value: stats.missingPrice, color: A.red },
                ].map((s) => (
                  <div key={s.label} style={{ ...card, padding: "12px 8px", textAlign: "center" }}>
                    <strong style={{ display: "block", fontSize: `calc(18px * var(--nx-font-scale, 1))`, color: s.color }}>{s.value}</strong>
                    <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{s.label}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, ...card, padding: "10px 14px" }}>
                  <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: P.muted }}>🔎</span>
                  <input value={artQuery} onChange={(e) => setArtQuery(e.target.value)} placeholder={t("articles.searchPlaceholder", "Art.Nr, nom, zone...")} style={{
                    flex: 1, border: "none", outline: "none", background: "none", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: P.ink,
                  }} />
                  {artQuery && <Pressable onClick={() => setArtQuery("")} style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted }}>✕</Pressable>}
                </div>
                <Pressable onClick={() => { refreshArticleDependentLists(); loadReportingCatalog(); showToast(t("articles.refreshedToast", "Articles actualisés")); }} style={{
                  width: 42, ...card, borderRadius: 14, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`,
                }}>🔄</Pressable>
                {canCreateArticles && (
                  <Pressable onClick={openCreateArticleSheet} style={{
                    width: 42, borderRadius: 14, display: "grid", placeItems: "center", fontSize: `calc(18px * var(--nx-font-scale, 1))`, color: "#fff",
                    background: A.green, boxShadow: `0 8px 18px ${A.green}55`,
                  }}>＋</Pressable>
                )}
              </div>

              <div style={{ display: "flex", gap: 8, marginBottom: 13, overflowX: "auto" }}>
                {[
                  { id: "all", label: t("articles.filterAll", "Tous") },
                  { id: "active", label: t("articles.statActive", "Actifs") },
                  { id: "missing-price", label: t("articles.statMissingPrice", "Sans prix") },
                  { id: "inactive", label: t("articles.filterInactive", "Inactifs") },
                ].map((f) => (
                  <Pressable key={f.id} onClick={() => setArtFilter(f.id)} style={{
                    flex: "0 0 auto", padding: "9px 15px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                    background: artFilter === f.id ? A.green : P.soft, color: artFilter === f.id ? "#fff" : P.muted,
                    border: `1.5px solid ${artFilter === f.id ? A.green : P.line}`,
                  }}>{f.label}</Pressable>
                ))}
              </div>

              <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 10px", fontWeight: 700 }}>
                {artQuery ? t("articles.countFoundTemplate", { n: visible.length }, `${visible.length} article${visible.length > 1 ? "s" : ""} trouvé${visible.length > 1 ? "s" : ""}`) : t("articles.countDisplayedTemplate", { n: visible.length }, `${visible.length} article${visible.length > 1 ? "s" : ""} affiché${visible.length > 1 ? "s" : ""}`)}
              </div>

              {visible.length === 0 ? (
                <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 30 }}>
                  <span style={{ fontSize: `calc(34px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>{stats.total === 0 && artZone ? "📦" : "🔎"}</span>
                  <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{stats.total === 0 && artZone ? t("articles.noArticleInZoneYet", "Aucun article dans ce rayon pour l'instant") : t("articles.noArticleFound", "Aucun article trouvé")}</strong>
                  <p style={{ margin: "5px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                    {stats.total === 0 && artZone ? t("articles.addFirstArticleTemplate", { zone: artZoneLabel(artZone) }, `Ajoute le premier article de ${artZoneLabel(artZone)} avec le bouton +.`) : t("articles.tryAnotherSearch", "Essaie un autre Art.Nr, nom ou zone.")}
                  </p>
                </section>
              ) : (
                <>
                  {shown.map((a) => {
                    const mhdCount = articlesMhdCounts[a.artNr] || 0;
                    const hasPrice = isValidArtPrice(a.price);
                    const hasRegular = isValidArtPrice(a.regularPrice);
                    // Prix jamais affiché en clair (2026-08-17, retour utilisateur explicite :
                    // "cache complètement le prix... ils doivent juste ne plus être visibles") —
                    // seul l'état "existe/n'existe pas" reste visible, jamais la valeur elle-même.
                    const priceText = !hasPrice ? t("articles.priceToEnter", "À saisir") : "••••";
                    const regularText = !hasRegular ? "-" : "••••";
                    return (
                      <div key={a.isPrebake ? `pb-${a.artNr}` : a.artNr} style={{ ...card, padding: 15, marginBottom: 9, opacity: a.active ? 1 : .55 }}>
                        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                          <span style={{ flex: "0 0 auto", width: 38, height: 38, borderRadius: 12, fontSize: `calc(17px * var(--nx-font-scale, 1))`, background: P.soft, display: "grid", placeItems: "center" }}>📦</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</strong>
                            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{artZoneLabel(a.zone)} · {t("controlRun.positionPrefix", "Position")} {a.sortOrder ?? "-"}</span>
                          </div>
                          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "4px 9px", color: P.muted, flex: "0 0 auto" }}>{a.artNr}</span>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
                          <div style={{ background: hasPrice ? P.soft : "rgba(239,68,68,.1)", borderRadius: 12, padding: "8px 10px" }}>
                            <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700, display: "block" }}>{t("articles.fieldCurrentPriceShort", "Prix actuel")}</span>
                            <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{priceText}</strong>
                          </div>
                          <div style={{ background: P.soft, borderRadius: 12, padding: "8px 10px" }}>
                            <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700, display: "block" }}>{t("articles.fieldRegularPriceShort", "Prix normal")}</span>
                            <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{regularText}</strong>
                          </div>
                          <div style={{ background: P.soft, borderRadius: 12, padding: "8px 10px" }}>
                            <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700, display: "block" }}>{t("articles.mhdStatLabel", "MHD")}</span>
                            <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{mhdCount}</strong>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, padding: "4px 9px", borderRadius: 99, background: a.active ? "rgba(34,197,94,.14)" : "rgba(239,68,68,.14)", color: a.active ? A.green : A.red }}>{a.active ? t("articles.statusActive", "Actif") : t("articles.statusInactive", "Inactif")}</span>
                          {a.unit && <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, padding: "4px 9px", borderRadius: 99, background: P.soft, color: P.muted }}>{a.unit}</span>}
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          {canEditArticles && (
                            <Pressable onClick={() => openEditArticleSheet(a)} style={{ flex: 1, padding: "9px 0", borderRadius: 99, textAlign: "center", fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>✏️ {t("common.editAria", "Modifier")}</Pressable>
                          )}
                          {canEditArticles && (
                            <Pressable onClick={() => openMoveArticleSheet(a)} style={{ flex: 1, padding: "9px 0", borderRadius: 99, textAlign: "center", fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>↕️ {t("articles.moveActionBtn", "Déplacer")}</Pressable>
                          )}
                          {canDeleteArticles && (
                            <Pressable onClick={() => toggleArticleStatusReal(a)} style={{ flex: 1, padding: "9px 0", borderRadius: 99, textAlign: "center", fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: a.active ? "rgba(239,68,68,.14)" : "rgba(34,197,94,.14)", color: a.active ? A.red : A.green }}>{a.active ? `⛔ ${t("articles.deactivateBtn", "Désactiver")}` : `✓ ${t("articles.reactivateBtn", "Réactiver")}`}</Pressable>
                          )}
                          {canDeleteArticles && !a.isPrebake && (
                            <Pressable onClick={() => deleteArticleReal(a)} aria-label={t("articles.deleteAria", "Supprimer définitivement")} style={{
                              flex: "0 0 auto", width: 34, borderRadius: 99, display: "grid", placeItems: "center",
                              background: "rgba(239,68,68,.14)", color: A.red, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                            }}>🗑️</Pressable>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {capped && (
                    <>
                      <Pressable onClick={() => setArtListBatches((n) => n + 1)} style={{
                        width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, marginTop: 4,
                        background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                      }}>{t("articles.showMoreBtnTemplate", { shown: shown.length, total: visible.length }, `Afficher plus (${shown.length}/${visible.length})`)}</Pressable>
                      <p style={{ textAlign: "center", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "6px 0 0" }}>{t("articles.moreArticlesHint", { n: remaining }, `+ ${remaining} article${remaining > 1 ? "s" : ""} — ou affine ta recherche pour aller plus vite.`)}</p>
                    </>
                  )}
                </>
              )}
            </>
          );
        })()}

        {/* ================= INVENTUR — hub (2 cartes, jamais de vue "responsable") ================= */}
        {screen === "inventur" && inventurScreen === "hub" && (
          <>
            <Pressable onClick={() => { setInventurScreen("erfassung_list"); loadInventurInventories("active"); }} style={{ ...colorCard(A.brown), ...tile, flexDirection: "column", alignItems: "stretch", padding: 20, marginBottom: 14 }}>
              <span style={{ ...onColorChip, marginBottom: 10 }}><EmojiIcon e="🧮" /></span>
              <strong style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, color: "#fff" }}>Inventurerfassung</strong>
              <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, lineHeight: 1.5, ...onColorSub }}>{t("inventur.hub.erfassungDesc", "Préparer les inventaires, attribuer les tâches, saisir les quantités et imprimer les résultats.")}</p>
            </Pressable>
            {kontrolleVisibleForMe && (
              <Pressable onClick={() => { setInventurScreen("kontrolle_list"); loadInventurKontrollen("active"); }} style={{ ...colorCard(A.blue), ...tile, flexDirection: "column", alignItems: "stretch", padding: 20, marginBottom: canManageKontrolleVisibility ? 4 : 14 }}>
                <span style={{ ...onColorChip, marginBottom: 10 }}><EmojiIcon e="🔍" /></span>
                <strong style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, color: "#fff" }}>Bestandskontrolle</strong>
                <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, lineHeight: 1.5, ...onColorSub }}>{t("inventur.hub.kontrolleDesc", "Contrôler certains articles sur une période donnée et détecter d'éventuels écarts de stock.")}</p>
              </Pressable>
            )}
            {/* Réservé FL/FLA (retour utilisateur 2026-08-09) : décide qui, sur l'équipe, voit
                la tuile Bestandskontrolle — jamais un droit (les permissions inventur.kontrolle.*
                restent inchangées), juste une visibilité. Toujours affiché même si CE manager
                s'est lui-même retiré de la liste par erreur — sinon plus moyen de se rajouter. */}
            {canManageKontrolleVisibility && (
              <Pressable onClick={() => setKontrolleVisibilitySheetOpen(true)} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, marginBottom: 14, padding: "2px 2px" }}>
                <span>👁️ {t("kontrolle.visibility.manageLink", "Gérer qui voit ce module")}</span>
                <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>›</span>
              </Pressable>
            )}
            {/* Rendez-vous d'inventaire — retour utilisateur explicite (2026-08-03), voir
                GET /api/inventur/appointments (déjà scopé à la filiale connectée côté
                serveur). Toujours visible, même sans rendez-vous en cours — c'est aussi là
                que l'historique des rendez-vous passés reste consultable. Même gabarit que
                les 2 tuiles ci-dessus (retour utilisateur explicite : "une tuile comme les
                autres"), jamais une petite carte-ligne secondaire. */}
            <Pressable onClick={() => setInventurScreen("rdv")} style={{ ...colorCard(A.teal), ...tile, flexDirection: "column", alignItems: "stretch", padding: 20, marginBottom: 14 }}>
              <span style={{ ...onColorChip, marginBottom: 10 }}><EmojiIcon e="📅" /></span>
              <strong style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, color: "#fff" }}>{t("inventur.appointment.hubTitle", "Rendez-vous d'inventaire")}</strong>
              <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, lineHeight: 1.5, ...onColorSub }}>{t("inventur.appointment.hubSubtitle", "Date proposée par ton responsable réseau.")}</p>
            </Pressable>
            {inventurMyTasks.length > 0 && (
              <Pressable onClick={() => setInventurScreen("erfassung_my_tasks")} style={{ ...card, ...tile, padding: 15, marginBottom: 9 }}>
                <span style={{ flex: "0 0 auto", width: 38, height: 38, borderRadius: 12, background: `${A.orange}22`, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>📋</span>
                <div style={{ flex: 1 }}><strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{t("inventur.myTasks.title", "Mes tâches d'inventaire")}</strong><div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("inventur.hub.myTasksCountTemplate", { n: inventurMyTasks.length }, `${inventurMyTasks.length} Kategorie(n) zugewiesen`)}</div></div>
                <span style={{ color: P.muted, fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>›</span>
              </Pressable>
            )}
          </>
        )}

        {/* ================= INVENTURERFASSUNG — liste =================
             Refonte 2026-08-10, portée depuis l'artefact "Inventurerfassung — nouveau
             design" validé par l'utilisateur : cartes de session type-tag + nom + chip de
             statut (point pulsant pour "en cours"), barre de progression avec libellé
             court, sous-titre de synthèse. Statuts et libellés réels inchangés
             (INVENTUR_STATUS_META), seule l'habillage visuel change. */}
        {screen === "inventur" && inventurScreen === "erfassung_list" && (() => {
          const inProgressCount = inventurInventories.filter((inv) => ["in_bearbeitung", "pruefung"].includes(inv.status)).length;
          return (
          <div className="nx-screen-enter">
            <p style={{ margin: "0 2px 14px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>
              {t("inventur.list.subtitleTemplate", { count: inventurInventories.length, active: inProgressCount }, `${inventurInventories.length} Inventuren · ${inProgressCount} in Bearbeitung`)}
            </p>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[{ id: "active", l: t("inventur.activeShort", "Actif") }, { id: "archived", l: t("inventur.archivedShort", "Archivé") }].map((tb) => (
                <Pressable key={tb.id} onClick={() => loadInventurInventories(tb.id)} style={{
                  flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: inventurListMode === tb.id ? A.brown : P.soft, color: inventurListMode === tb.id ? "#fff" : P.ink,
                  border: `1.5px solid ${inventurListMode === tb.id ? A.brown : P.line}`,
                }}>{tb.l}</Pressable>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <Pressable onClick={() => setInventurScreen("erfassung_my_tasks")} style={{ flex: 1, padding: "11px 0", borderRadius: 99, textAlign: "center", fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>📋 {t("inventur.myTasksBtn", "Mes tâches")}</Pressable>
              {canCreateInventur && (
                <Pressable onClick={() => { setInventurCreateForm(emptyInventurCreateForm); setInventurScreen("erfassung_create"); }} style={{ flex: 1, padding: "11px 0", borderRadius: 99, textAlign: "center", fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: A.brown, color: "#fff" }}>＋ {t("inventur.newTitle", "Nouvel inventaire")}</Pressable>
              )}
            </div>
            {inventurInventories.length === 0 && (
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 30 }}>
                <span style={{ fontSize: `calc(34px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>🧮</span>
                <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{t("inventur.noneYetText", "Aucun inventaire pour l'instant.")}</strong>
              </section>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
            {inventurInventories.map((inv, invIdx) => {
              const total = inv.categories.length;
              const done = inv.categories.filter((c) => ["fertig", "geprueft", "gesperrt"].includes(c.status)).length;
              const pct = total ? Math.round((done / total) * 100) : 0;
              const meta = INVENTUR_STATUS_META[inv.status] || INVENTUR_STATUS_META.vorbereitung;
              const isActive = ["in_bearbeitung", "pruefung"].includes(inv.status);
              const isDone = ["abgeschlossen", "archiviert"].includes(inv.status);
              return (
                <Pressable key={inv.id} feedback className="nx-list-item-in" onClick={() => openInventurInventory(inv.id)} style={{ ...card, padding: 15, marginBottom: isWide ? 0 : 9, flexDirection: "column", alignItems: "stretch", animationDelay: `${Math.min(invIdx, 14) * 28}ms` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 4 }}>
                    <div style={{ minWidth: 0 }}>
                      <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em", color: P.muted }}>{inv.type === "jahresinventur" ? "Jahresinventur" : "Inventur"}</span>
                      <strong style={{ fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, display: "block", marginTop: 2 }}>{inv.name || inv.id}</strong>
                    </div>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: meta.color, borderRadius: 99, padding: "4px 10px", flexShrink: 0, whiteSpace: "nowrap" }}>
                      {isActive && <span style={{ width: 6, height: 6, borderRadius: 99, background: "#fff", animation: "nxLive 1.8s ease infinite" }} />}
                      {isDone ? "✓ " : ""}{meta.label}
                    </span>
                  </div>
                  <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 10 }}>{fmtFR(inv.date)}{inv.mainUserName ? ` · ${t("inventur.list.mainUserPrefix", "Responsable")} ${inv.mainUserName}` : ""}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1, height: 7, borderRadius: 99, background: P.soft, overflow: "hidden" }}><div style={{ height: "100%", width: `${pct}%`, background: isDone ? A.green : A.brown }} /></div>
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, whiteSpace: "nowrap" }}>{t("inventur.categoriesDoneShortTemplate", { done, total }, `${done} / ${total} Kategorien`)}</span>
                  </div>
                </Pressable>
              );
            })}
            </div>
          </div>
          );
        })()}

        {/* ================= INVENTURERFASSUNG — création ================= */}
        {screen === "inventur" && inventurScreen === "erfassung_create" && (
          <>
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>{t("importantInfo.editor.fieldType", "Type")}</span>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {[{ id: "inventur", l: "Inventur" }, { id: "jahresinventur", l: "Jahresinventur" }].map((tp) => (
                <Pressable key={tp.id} onClick={() => setInventurCreateForm({ ...inventurCreateForm, type: tp.id })} style={{
                  flex: 1, textAlign: "center", padding: "12px 0", borderRadius: 14, fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                  background: inventurCreateForm.type === tp.id ? A.brown : P.soft, color: inventurCreateForm.type === tp.id ? "#fff" : P.ink,
                  border: `1.5px solid ${inventurCreateForm.type === tp.id ? A.brown : P.line}`,
                }}>{tp.l}</Pressable>
              ))}
            </div>
            <Field label={t("inventur.nameOptionalLabel", "Name (facultatif)")}>
              <input style={inputStyle} placeholder={t("inventur.namePlaceholder", "Ex. Inventaire d'été")} value={inventurCreateForm.name} onChange={(e) => setInventurCreateForm({ ...inventurCreateForm, name: e.target.value })} />
            </Field>
            <Field label={t("reporting.dateLabel", "Date")}>
              <input type="date" style={dateTimeInputStyle} value={inventurCreateForm.date} onChange={(e) => setInventurCreateForm({ ...inventurCreateForm, date: e.target.value })} />
            </Field>
            <Field label={t("inventur.plannedStartLabel", "Heure de début prévue")}>
              <input type="time" style={dateTimeInputStyle} value={inventurCreateForm.plannedStartTime} onChange={(e) => setInventurCreateForm({ ...inventurCreateForm, plannedStartTime: e.target.value })} />
            </Field>
            <Field label={t("inventur.mainResponsibleLabel", "Responsable principal")}>
              <select style={inputStyle} value={inventurCreateForm.mainUserId} onChange={(e) => { const u = inventurTeamRoster.find((x) => x.employeeId === e.target.value); setInventurCreateForm({ ...inventurCreateForm, mainUserId: e.target.value, mainUserName: u?.name || "" }); }}>
                <option value="">—</option>
                {inventurTeamRoster.map((u) => <option key={u.employeeId} value={u.employeeId}>{u.name}</option>)}
              </select>
            </Field>
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>{t("inventur.participantsLabel", "Participants")}</span>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 16 }}>
              {inventurTeamRoster.map((u) => {
                const sel = inventurCreateForm.participantIds.includes(u.employeeId);
                return (
                  <Pressable key={u.employeeId} onClick={() => {
                    const ids = sel ? inventurCreateForm.participantIds.filter((id) => id !== u.employeeId) : [...inventurCreateForm.participantIds, u.employeeId];
                    const names = sel ? inventurCreateForm.participantNames.filter((n) => n !== u.name) : [...inventurCreateForm.participantNames, u.name];
                    setInventurCreateForm({ ...inventurCreateForm, participantIds: ids, participantNames: names });
                  }} style={{
                    padding: "8px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                    background: sel ? A.brown : P.soft, color: sel ? "#fff" : P.ink, border: `1.5px solid ${sel ? A.brown : P.line}`,
                  }}>{u.name}</Pressable>
                );
              })}
            </div>
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>{t("inventur.categoriesLabel", "Catégories")}</span>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 16 }}>
              {inventurCategoryTemplates.filter((c) => c.active && !c.archived).map((c) => {
                const sel = inventurCreateForm.categoryIds.includes(c.id);
                return (
                  <Pressable key={c.id} onClick={() => {
                    const ids = sel ? inventurCreateForm.categoryIds.filter((id) => id !== c.id) : [...inventurCreateForm.categoryIds, c.id];
                    setInventurCreateForm({ ...inventurCreateForm, categoryIds: ids });
                  }} style={{
                    padding: "8px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                    background: sel ? A.green : P.soft, color: sel ? "#fff" : P.ink, border: `1.5px solid ${sel ? A.green : P.line}`,
                  }}>{c.name}</Pressable>
                );
              })}
            </div>
            <Field label={t("inventur.noteOptionalLabel", "Remarque (facultatif)")}>
              <input style={inputStyle} value={inventurCreateForm.note} onChange={(e) => setInventurCreateForm({ ...inventurCreateForm, note: e.target.value })} />
            </Field>
            <Pressable onClick={createInventurInventory} style={{ width: "100%", ...btnSolid(A.brown), marginTop: 8 }}>{t("inventur.createBtn", "Créer l'inventaire")}</Pressable>
          </>
        )}

        {/* ================= INVENTURERFASSUNG — page générale d'un inventaire =================
             Refonte 2026-08-10, portée depuis l'artefact validé : bandeau KPI (catégories
             terminées / lignes saisies / progression), cartes de catégorie teintées selon
             leur vrai statut (réutilise KONTROLLE_TINT), et un SEUL bloc d'export en bas de
             page — correction du bug réel repéré dans l'artefact : chaque catégorie portait
             auparavant son propre bouton CSV, qui exportait déjà TOUT l'inventaire (jamais
             cette seule catégorie), ce qui prêtait à confusion. Toutes les actions réelles
             (assigner/terminer/vérifier/verrouiller/rouvrir/retirer) restent inchangées. */}
        {screen === "inventur" && inventurScreen === "erfassung_detail" && inventurActiveInventory && (() => {
          const inv = inventurActiveInventory;
          const total = inv.categories.length;
          const done = inv.categories.filter((c) => ["fertig", "geprueft", "gesperrt"].includes(c.status)).length;
          const pct = total ? Math.round((done / total) * 100) : 0;
          const totalLines = inv.categories.reduce((s, c) => s + (c.entryCount || 0), 0);
          return (
            <>
              <section style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 18, marginBottom: 16 }}>
                <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 12 }}>
                  {fmtFR(inv.date)}{inv.mainUserName ? ` · ${t("inventur.list.mainUserPrefix", "Responsable")} ${inv.mainUserName}` : ""}
                  {inv.participantNames && inv.participantNames.length > 0 ? ` · ${t("inventur.participantsLabel", "Participants")} : ${inv.participantNames.join(", ")}` : ""}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 1, background: P.line, borderRadius: 12, overflow: "hidden", border: `1px solid ${P.line}` }}>
                  {[
                    [`${done} / ${total}`, t("inventur.kpi.categoriesDone", "Catégories terminées")],
                    [String(totalLines), t("inventur.kpi.linesEntered", "Lignes saisies")],
                    [`${pct} %`, t("inventur.kpi.progress", "Progression")],
                  ].map(([v, l], i) => (
                    <div key={i} style={{ background: P.card, padding: "12px 8px", textAlign: "center" }}>
                      <strong style={{ fontSize: `calc(16.5px * var(--nx-font-scale, 1))`, display: "block", letterSpacing: "-.01em" }}>{v}</strong>
                      <span style={{ fontSize: `calc(8.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, textTransform: "uppercase", letterSpacing: ".03em", display: "block", marginTop: 2 }}>{l}</span>
                    </div>
                  ))}
                </div>
              </section>

              {inv.status === "abgeschlossen" && canCloseInventur && (
                <Pressable onClick={() => { setInventurReopenTarget({ kind: "inventory" }); setInventurReopenReason(""); setSheet("inventurReopen"); }} style={{ width: "100%", padding: "11px 0", borderRadius: 99, textAlign: "center", fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink, marginBottom: 14 }}>🔓 {t("inventur.reopenInventoryBtn", "Rouvrir l'inventaire")}</Pressable>
              )}

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "2px 2px 10px" }}>
                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".04em" }}>{t("inventur.categoriesLabel", "Catégories")}</span>
                {canManageInventurCategories && inv.status !== "abgeschlossen" && (
                  <Pressable onClick={() => { setInventurNewCategoryForm({ name: "", description: "", entryType: "standard", scope: "shared" }); setSheet("inventurNewCategory"); }} style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.brown }}>＋ {t("inventur.categoryFallback", "Catégorie")}</Pressable>
                )}
              </div>

              {inv.categories.slice().sort((a, b) => a.order - b.order).map((cat) => {
                const cmeta = INVENTUR_CATEGORY_STATUS_META[cat.status] || INVENTUR_CATEGORY_STATUS_META.nicht_zugewiesen;
                const locked = ["fertig", "geprueft", "gesperrt"].includes(cat.status);
                const tintId = ["gesperrt", "geprueft"].includes(cat.status) ? "good" : ["fertig", "pausiert"].includes(cat.status) ? "warn" : ["in_bearbeitung", "zugewiesen"].includes(cat.status) ? "active" : "muted";
                const tc = KONTROLLE_TINT[tintId];
                return (
                  <div key={cat.id} style={{ ...card, background: tc.bg, padding: 14, marginBottom: 9 }}>
                    <Pressable onClick={() => openInventurCategory(cat.id)} style={{ display: "flex", width: "100%", alignItems: "center", gap: 10, marginBottom: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{cat.name}</strong>
                          {cat.status === "gesperrt" && <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`}}>🔒</span>}
                        </div>
                        <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 2 }}>{cat.assignedUserNames.join(", ") || t("inventur.categoryStatus.nicht_zugewiesen", "Non attribué")} · {t("inventur.linesCountShort", { n: cat.entryCount }, `${cat.entryCount} Zeile(n)`)}</div>
                      </div>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: tc.c, background: tc.chipBg, borderRadius: 99, padding: "4px 10px", flex: "0 0 auto", whiteSpace: "nowrap" }}>
                        {cat.status === "in_bearbeitung" && <span style={{ width: 6, height: 6, borderRadius: 99, background: tc.c, animation: "nxLive 1.8s ease infinite" }} />}
                        {cmeta.label}
                      </span>
                    </Pressable>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {canAssignInventurTask && !locked && (
                        <Pressable onClick={() => { setInventurAssignForm({ catId: cat.id, assignedUserIds: cat.assignedUserIds, assignedUserNames: cat.assignedUserNames }); setSheet("inventurAssign"); }} style={{ padding: "7px 11px", borderRadius: 99, fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.card, border: `1.5px solid ${P.line}`, color: P.ink }}>👤 {t("inventur.assignBtn", "Attribuer")}</Pressable>
                      )}
                      {(canFinishInventurCategory || cat.assignedUserIds.includes(String(user?.proId || user?.id || ""))) && !locked && cat.status !== "nicht_zugewiesen" && (
                        <Pressable onClick={() => { if (confirm(t("inventur.confirmCategoryDone", "Nous confirmons que cette catégorie a été entièrement comptée."))) setInventurCategoryStatus(cat.id, "fertig"); }} style={{ padding: "7px 11px", borderRadius: 99, fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: "rgba(30,184,95,.14)", color: A.green }}>✓ {t("inventur.categoryStatus.fertig", "Terminé")}</Pressable>
                      )}
                      {canVerifyInventurCategory && cat.status === "fertig" && (
                        <Pressable onClick={() => setInventurCategoryStatus(cat.id, "geprueft")} style={{ padding: "7px 11px", borderRadius: 99, fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: `${A.purple}22`, color: A.purple }}>🔎 {t("inventur.categoryStatus.geprueft", "Vérifié")}</Pressable>
                      )}
                      {canLockInventurCategory && ["fertig", "geprueft"].includes(cat.status) && (
                        <Pressable onClick={() => setInventurCategoryStatus(cat.id, "gesperrt")} style={{ padding: "7px 11px", borderRadius: 99, fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.card, border: `1.5px solid ${P.line}`, color: P.ink }}>🔒 {t("inventur.lockBtn", "Verrouiller")}</Pressable>
                      )}
                      {canReopenInventurCategory && locked && (
                        <Pressable onClick={() => { setInventurReopenTarget({ kind: "category", catId: cat.id }); setInventurReopenReason(""); setSheet("inventurReopen"); }} style={{ padding: "7px 11px", borderRadius: 99, fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: "rgba(239,68,68,.12)", color: A.red }}>🔓 {t("inventur.reopenShortBtn", "Rouvrir")}</Pressable>
                      )}
                      {canManageInventurCategories && !cat.entryCount && inv.status !== "abgeschlossen" && (
                        <Pressable onClick={() => removeInventurCategoryFromInventory(cat.id)} style={{ padding: "7px 11px", borderRadius: 99, fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: "rgba(239,68,68,.1)", color: A.red }}>{t("inventur.removeBtn", "Retirer")}</Pressable>
                      )}
                    </div>
                  </div>
                );
              })}

              {canExportInventur && (
                <div style={{ background: P.card, border: `1.5px dashed ${P.line}`, borderRadius: 16, padding: "16px 18px", marginTop: 16 }}>
                  <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, marginBottom: 4 }}>📤 {t("inventur.export.cardTitle", "Export & impression")}</strong>
                  <p style={{ margin: "0 0 12px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{t("inventur.export.cardHint", "Un seul export ici, pour tout l'inventaire — fini le bouton CSV par catégorie qui exportait déjà tout à la fois et prêtait à confusion.")}</p>
                  <div style={{ display: "flex", gap: 8 }}>
                    <a href={`/api/inventur/inventories/${encodeURIComponent(inv.id)}/export.csv`} download style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: P.bg, border: `1.5px solid ${P.line}`, color: P.ink, textDecoration: "none" }}>⬇ CSV</a>
                    <a href={`/api/inventur/inventories/${encodeURIComponent(inv.id)}/export.xlsx`} download style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: P.bg, border: `1.5px solid ${P.line}`, color: P.ink, textDecoration: "none" }}>📊 {t("inventur.exportExcelBtn", "Exporter en Excel")}</a>
                    <Pressable onClick={() => printReportWithTitle(buildInventurPrintHtml(inv), `Inventur ${inv.name || inv.id} - Filiale ${inv.storeId}`)} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: P.bg, border: `1.5px solid ${P.line}`, color: P.ink }}>🖨️ {t("inventur.printBtn", "Imprimer")}</Pressable>
                  </div>
                </div>
              )}

              {inv.status !== "abgeschlossen" && canCloseInventur && (
                <Pressable onClick={() => { if (confirm(t("inventur.confirmCloseTemplate", { done, total }, `Articles contrôlés : ${done}/${total} catégories terminées.\n\nClôturer l'inventaire ?`))) patchInventurInventoryStatus("abgeschlossen"); }} style={{ width: "100%", ...btnSolid(A.green), marginTop: 16 }}>{t("inventur.closeBtn", "Clôturer l'inventaire")}</Pressable>
              )}
              {canCloseInventur && (
                <Pressable onClick={deleteInventurInventory} style={{ width: "100%", padding: "12px 0", borderRadius: 99, textAlign: "center", fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`, background: "rgba(239,68,68,.1)", color: A.red, marginTop: 8 }}>🗑️ {t("inventur.deleteBtn", "Supprimer l'inventaire")}</Pressable>
              )}
            </>
          );
        })()}

        {/* ================= INVENTURERFASSUNG — détail d'une catégorie (saisie, "Comptage") =================
             Refonte 2026-08-10, portée depuis l'artefact validé : bandeau de statut compact,
             formule toujours visible (jamais une boîte noire) pour la méthode "Kartons", et
             les lignes déjà saisies présentées en vrai tableau dense (Art.Nr/Bezeichnung/
             Méthode/Menge/Erfasst von) plutôt qu'en cartes empilées — plus facile à scanner
             visuellement pendant une saisie à haut volume. La gestion des doublons d'Art.Nr
             existait déjà (Sheet inventurDuplicateState avec les 4 choix : additionner /
             modifier / garder séparé / annuler) et reste inchangée. */}
        {screen === "inventur" && inventurScreen === "erfassung_category" && inventurActiveInventory && activeInventurCategory() && (() => {
          const cat = activeInventurCategory();
          const isBooks = cat.entryType === "buecher";
          const locked = ["fertig", "geprueft", "gesperrt"].includes(cat.status);
          const cmeta = INVENTUR_CATEGORY_STATUS_META[cat.status] || {};
          const q = inventurEntrySearch.trim().toLowerCase();
          const visible = inventurCategoryEntries
            .filter((e) => !q || e.artNr.toLowerCase().includes(q) || (e.name || "").toLowerCase().includes(q))
            .slice()
            .sort((a, b) => isBooks ? (a.artNr === b.artNr ? Number(a.price) - Number(b.price) : a.artNr.localeCompare(b.artNr)) : String(b.createdAt).localeCompare(String(a.createdAt)));
          const thStyle = { textAlign: "left", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".03em", padding: "0 8px 7px", borderBottom: `1.5px solid ${P.line}`, whiteSpace: "nowrap" };
          const tdStyle = { padding: "9px 8px", borderBottom: `1px solid ${P.soft}`, verticalAlign: "top" };
          return (
            <>
              <div style={{ ...card, padding: 14, marginBottom: 14, flexDirection: "column", alignItems: "stretch" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{t("inventur.linesCountShort", { n: inventurCategoryEntries.length }, `${inventurCategoryEntries.length} Zeile(n)`)}</strong>
                  <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: cmeta.color || P.muted, borderRadius: 99, padding: "4px 10px" }}>{cmeta.label || cat.status}</span>
                </div>
                {cat.assignedUserNames.length > 0 && <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 6 }}>{t("kontrolle.assignedEmployeeLabel", "Collaborateur affecté")} : {cat.assignedUserNames.join(", ")}</div>}
              </div>

              {locked && (
                <div style={{ borderRadius: 14, padding: 12, marginBottom: 14, background: P.soft, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700, textAlign: "center" }}>
                  {t("inventur.categoryLockedTemplate", { label: (INVENTUR_CATEGORY_STATUS_META[cat.status] || {}).label }, `Cette catégorie est ${(INVENTUR_CATEGORY_STATUS_META[cat.status] || {}).label} — aucune nouvelle saisie possible.`)}
                </div>
              )}

              {!locked && canCreateInventurEntry && (
                <div style={{ ...card, padding: 14, marginBottom: 16, flexDirection: "column", alignItems: "stretch" }}>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 8, marginBottom: 11 }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 5 }}>Art.Nr</span>
                      <input ref={inventurArtNrInputRef} style={inputStyle} inputMode="numeric" placeholder="Art.Nr" value={inventurEntryForm.artNr} onChange={(e) => setInventurEntryForm({ ...inventurEntryForm, artNr: e.target.value })} />
                    </div>
                    <Pressable onClick={() => doScan("inventur")} aria-label={t("common.scanAria", "Scanner")} style={{
                      flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6, padding: "12px 14px", borderRadius: 14,
                      background: themeId === "sombre" ? "#2a2a2e" : A.navy, color: "#fff",
                      fontWeight: 700, fontSize: `calc(12px * var(--nx-font-scale, 1))`, animation: scanning ? "nxPulse 1s ease infinite" : "none",
                    }}>📷</Pressable>
                  </div>
                  <Field label={t("inventur.designationOptionalLabel", "Désignation (facultatif)")}>
                    <input style={inputStyle} value={inventurEntryForm.name} onChange={(e) => setInventurEntryForm({ ...inventurEntryForm, name: e.target.value })} />
                  </Field>

                  {isBooks ? (
                    <>
                      <Field label={t("inventur.priceLabel", "Prix (€)")}>
                        <input style={inputStyle} inputMode="decimal" placeholder="0,00" value={inventurEntryForm.price} onChange={(e) => setInventurEntryForm({ ...inventurEntryForm, price: e.target.value })} />
                      </Field>
                      <Field label={t("inventur.countLabel", "Nombre")}>
                        <QtyStepper value={inventurEntryForm.totalQty} onChange={(v) => setInventurEntryForm({ ...inventurEntryForm, totalQty: v })} />
                      </Field>
                    </>
                  ) : (
                    <>
                      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                        {[{ id: "direct", l: t("inventur.methodDirect", "Direct") }, { id: "cartons", l: t("inventur.methodCartons", "Cartons") }].map((m) => (
                          <Pressable key={m.id} onClick={() => setInventurEntryForm({ ...inventurEntryForm, entryMethod: m.id })} style={{
                            flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                            background: inventurEntryForm.entryMethod === m.id ? A.brown : P.soft, color: inventurEntryForm.entryMethod === m.id ? "#fff" : P.ink,
                          }}>{m.l}</Pressable>
                        ))}
                      </div>
                      {inventurEntryForm.entryMethod === "cartons" ? (
                        <>
                          <Field label={t("inventur.methodCartons", "Cartons")}><QtyStepper value={inventurEntryForm.cartons} onChange={(v) => setInventurEntryForm({ ...inventurEntryForm, cartons: v })} /></Field>
                          <Field label={t("inventur.piecesPerCartonLabel", "Pièces par carton")}><QtyStepper value={inventurEntryForm.piecesPerCarton} onChange={(v) => setInventurEntryForm({ ...inventurEntryForm, piecesPerCarton: v })} /></Field>
                          <Field label={t("inventur.singlePiecesLabel", "Pièces à l'unité")}><QtyStepper value={inventurEntryForm.singlePieces} onChange={(v) => setInventurEntryForm({ ...inventurEntryForm, singlePieces: v })} /></Field>
                          {/* Correction apportée (voir artefact) : la formule "Kartons" est
                              désormais toujours affichée en clair avec son calcul complet —
                              plus jamais un total qui apparaît sans qu'on sache d'où il vient. */}
                          <div style={{ border: `1.5px dashed ${A.brown}66`, background: "rgba(139,94,52,.08)", borderRadius: 12, padding: "13px 14px", marginBottom: 12 }}>
                            <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.brown, textTransform: "uppercase", letterSpacing: ".03em", display: "block", marginBottom: 6 }}>{t("inventur.formula.totalLabel", "Formule — Quantité totale")}</span>
                            <div style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>
                              {t("inventur.methodCartons", "Cartons")} <span style={{ color: P.muted, fontWeight: 600 }}>×</span> {t("inventur.piecesPerCartonLabel", "Pièces par carton")} <span style={{ color: P.muted, fontWeight: 600 }}>+</span> {t("inventur.singlePiecesLabel", "Pièces à l'unité")} <span style={{ color: P.muted, fontWeight: 600 }}>=</span>{" "}
                              {Number(inventurEntryForm.cartons || 0)} <span style={{ color: P.muted, fontWeight: 600 }}>×</span> {Number(inventurEntryForm.piecesPerCarton || 0)} <span style={{ color: P.muted, fontWeight: 600 }}>+</span> {Number(inventurEntryForm.singlePieces || 0)}
                            </div>
                            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6 }}>
                              <strong style={{ fontSize: `calc(22px * var(--nx-font-scale, 1))`, color: A.brown }}>{(Number(inventurEntryForm.cartons || 0) * Number(inventurEntryForm.piecesPerCarton || 0)) + Number(inventurEntryForm.singlePieces || 0)}</strong>
                              <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>{t("inventur.formula.unitsSuffix", "pièces au total")}</span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <Field label={t("inventur.totalQtyLabel", "Quantité totale")}><QtyStepper value={inventurEntryForm.totalQty} onChange={(v) => setInventurEntryForm({ ...inventurEntryForm, totalQty: v })} /></Field>
                      )}
                    </>
                  )}
                  <Field label={t("inventur.noteOptionalLabel", "Remarque (facultatif)")}>
                    <input style={inputStyle} value={inventurEntryForm.note} onChange={(e) => setInventurEntryForm({ ...inventurEntryForm, note: e.target.value })} />
                  </Field>
                  <Pressable onClick={() => submitInventurEntry(false)} style={{ width: "100%", ...btnSolid(A.brown) }}>{t("inventur.recordBtn", "Enregistrer la saisie")}</Pressable>
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", gap: 8, ...card, padding: "10px 14px", marginBottom: 16 }}>
                <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: P.muted }}>🔎</span>
                <input value={inventurEntrySearch} onChange={(e) => setInventurEntrySearch(e.target.value)} placeholder={t("inventur.searchPlaceholder", "Art.Nr ou désignation…")} style={{ flex: 1, border: "none", outline: "none", background: "none", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: P.ink }} />
              </div>

              <div style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, margin: "0 4px 10px", display: "flex", alignItems: "center", gap: 8 }}>
                <span>{t("inventur.table.linesHeadingTemplate", { n: visible.length }, `Erfasste Zeilen (${visible.length})`)}</span><span style={{ flex: 1, height: 1, background: P.line }} />
              </div>

              {visible.length === 0 ? (
                <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, textAlign: "center", marginTop: 10 }}>{t("inventur.noActivity", "Aucune activité.")}</p>
              ) : (
                <div style={{ overflowX: "auto", marginBottom: 8 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: `calc(12px * var(--nx-font-scale, 1))`}}>
                    <thead><tr>
                      <th style={thStyle}>Art.Nr</th>
                      <th style={thStyle}>{t("inventur.table.designationHeader", "Désignation")}</th>
                      <th style={thStyle}>{isBooks ? t("inventur.priceLabel", "Prix (€)") : t("inventur.table.methodHeader", "Méthode")}</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>{t("controlRun.quantityLabel", "Quantité")}</th>
                      <th style={thStyle}>{t("inventur.table.enteredByHeader", "Saisi par")}</th>
                      {!locked && canEditInventurEntry && <th style={{ ...thStyle, width: 60 }} />}
                    </tr></thead>
                    <tbody>
                      {visible.map((e) => (
                        <tr key={e.id}>
                          <td style={{ ...tdStyle, fontVariantNumeric: "tabular-nums", fontWeight: 700, color: P.muted }}>{e.artNr}</td>
                          <td style={tdStyle}>
                            {e.name || "—"}
                            {e.note && <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 2 }}>💬 {e.note}</div>}
                          </td>
                          <td style={{ ...tdStyle, color: P.muted, fontSize: `calc(11px * var(--nx-font-scale, 1))`}}>
                            {isBooks ? `${Number(e.price).toFixed(2)} €` : (e.entryMethod === "cartons" ? `${e.cartons}×${e.piecesPerCarton}+${e.singlePieces}` : t("inventur.methodDirect", "Direct"))}
                          </td>
                          <td style={{ ...tdStyle, textAlign: "right", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{e.totalQty}</td>
                          <td style={{ ...tdStyle, color: P.muted, fontSize: `calc(11px * var(--nx-font-scale, 1))`}}>{e.createdByName} · {new Date(e.createdAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</td>
                          {!locked && canEditInventurEntry && (
                            <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                              <Pressable onClick={() => { setInventurEditEntry(e); setSheet("inventurEditEntry"); }} aria-label={t("common.editAria", "Modifier")} style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, padding: "2px 5px" }}>✏️</Pressable>
                              <Pressable onClick={() => deleteInventurEntry(e.id)} aria-label={t("common.delete", "Supprimer")} style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, padding: "2px 5px", color: A.red }}>✕</Pressable>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!isBooks && (
                <section style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: "18px 20px", marginTop: 18 }}>
                  <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, marginBottom: 12 }}>📐 {t("inventur.legend.title", "Formeln — toujours visibles, jamais une boîte noire")}</strong>
                  {[
                    [t("inventur.methodCartons", "Cartons"), t("inventur.legend.cartons", "Cartons × Pièces par carton + Pièces à l'unité = Quantité totale — la composition réelle du total reste toujours vérifiable à l'œil.")],
                    [t("inventur.methodDirect", "Direct"), t("inventur.legend.direct", "Saisie manuelle directe de la quantité totale — utilisée quand compter par cartons n'a pas de sens (ex. articles à l'unité).")],
                  ].map(([k2, f]) => (
                    <div key={k2} style={{ display: "flex", gap: 10, padding: "9px 0", borderTop: `1px solid ${P.soft}` }}>
                      <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, flex: "0 0 130px" }}>{k2}</span>
                      <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{f}</span>
                    </div>
                  ))}
                </section>
              )}
            </>
          );
        })()}

        {/* ================= INVENTURERFASSUNG — Meine Inventuraufgaben ================= */}
        {screen === "inventur" && inventurScreen === "erfassung_my_tasks" && (
          <>
            {inventurMyTasks.length === 0 && (
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 30 }}>
                <span style={{ fontSize: `calc(34px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>✅</span>
                <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{t("inventur.noTaskAssigned", "Aucune tâche attribuée.")}</strong>
              </section>
            )}
            {inventurMyTasks.map((tk) => {
              const cmeta = INVENTUR_CATEGORY_STATUS_META[tk.category.status] || INVENTUR_CATEGORY_STATUS_META.nicht_zugewiesen;
              return (
                <Pressable key={tk.category.id} onClick={() => openInventurInventory(tk.inventoryId).then((inv) => inv && openInventurCategory(tk.category.id, inv.id))} style={{ ...card, padding: 15, marginBottom: 9 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{tk.category.name}</strong>
                    <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: cmeta.color, borderRadius: 99, padding: "3px 9px" }}>{cmeta.label}</span>
                  </div>
                  <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{tk.inventoryName} · {t("inventur.linesCountShort", { n: tk.category.entryCount }, `${tk.category.entryCount} Zeile(n)`)}{tk.category.lastEntryAt ? ` · ${t("inventur.lastEntryTemplate", { date: new Date(tk.category.lastEntryAt).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) }, `zuletzt ${new Date(tk.category.lastEntryAt).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}`)}` : ""}</div>
                </Pressable>
              );
            })}
          </>
        )}

        {/* ================= INVENTURERFASSUNG — historique ================= */}
        {screen === "inventur" && inventurScreen === "erfassung_history" && (
          <>
            {inventurHistory.length === 0 && <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, textAlign: "center", marginTop: 20 }}>{t("inventur.noActivity", "Aucune activité.")}</p>}
            {inventurHistory.map((h) => (
              <div key={h.id} style={{ ...card, padding: 12, marginBottom: 7, flexDirection: "column", alignItems: "stretch" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{h.byName}</strong>
                  <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>{new Date(h.at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 2 }}>{h.message}</span>
              </div>
            ))}
          </>
        )}

        {/* ================= BESTANDSKONTROLLE — liste + détail en accordéon =================
             Refonte 2026-08-09, portée depuis l'artefact validé : déplier une carte EST
             l'écran de détail (plus de navigation séparée), coloration légère selon le vrai
             statut/résultat, chaque formule visible avec ses vrais chiffres, export CSV/PDF
             par article ou pour tous, formulaire papier vierge pour un suivi hors application,
             et une zone GR pour clôturer définitivement un contrôle avant la fin de sa période. */}
        {screen === "inventur" && inventurScreen === "kontrolle_list" && (() => {
          const formulaCard = (tint) => ({ border: `1.5px dashed ${tint ? KONTROLLE_TINT[tint].c + "66" : A.blue + "66"}`, background: tint ? KONTROLLE_TINT[tint].bg : "rgba(54,79,231,.08)", borderRadius: 12, padding: "13px 14px", marginBottom: 12 });
          return (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[{ id: "active", l: t("kontrolle.tabActive", "Actif") }, { id: "mine", l: t("kontrolle.tabMine", "Mes contrôles") }, { id: "archived", l: t("kontrolle.tabArchive", "Archive") }].map((tb) => (
                <Pressable key={tb.id} onClick={() => { loadInventurKontrollen(tb.id); setKontrolleExpandedId(null); }} style={{
                  flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                  background: kontrolleListMode === tb.id ? A.blue : P.soft, color: kontrolleListMode === tb.id ? "#fff" : P.ink,
                  border: `1.5px solid ${kontrolleListMode === tb.id ? A.blue : P.line}`,
                }}>{tb.l}</Pressable>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <Pressable onClick={() => { setKontrolleHistoryFilters({ artNr: "", reason: "", negativeOnly: false }); loadKontrolleHistory(); setInventurScreen("kontrolle_history"); }} style={{ flex: 1, padding: "11px 0", borderRadius: 99, textAlign: "center", fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>📊 {t("arbeitszeitliste.history.open", "Historique")}</Pressable>
              {canCreateKontrolle && (
                <Pressable onClick={() => { setKontrolleCreateForm(emptyKontrolleCreateForm); setInventurScreen("kontrolle_create"); }} style={{ flex: 1, padding: "11px 0", borderRadius: 99, textAlign: "center", fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: A.blue, color: "#fff" }}>＋ {t("kontrolle.newTitle", "Nouveau contrôle")}</Pressable>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <Pressable onClick={() => setKontrolleExportIds(inventurKontrollen.map((k) => k.id))} disabled={!inventurKontrollen.length} style={{ flex: 1, padding: "11px 0", borderRadius: 99, textAlign: "center", fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: P.soft, color: inventurKontrollen.length ? P.ink : P.muted }}>⬇ {t("kontrolle.export.allBtnTemplate", { n: inventurKontrollen.length }, `Exporter tout (${inventurKontrollen.length})`)}</Pressable>
              <Pressable onClick={() => setKontrolleBlankFormOpen(true)} style={{ flex: 1, padding: "11px 0", borderRadius: 99, textAlign: "center", fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>🖶 {t("kontrolle.blankForm.btn", "Formulaire vierge")}</Pressable>
            </div>

            {inventurKontrollen.length === 0 && (
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 30 }}>
                <span style={{ fontSize: `calc(34px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>🔍</span>
                <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{t("kontrolle.noneYet", "Aucun contrôle pour l'instant.")}</strong>
              </section>
            )}

            {inventurKontrollen.map((k) => {
              const tint = kontrolleTint(k);
              const tc = KONTROLLE_TINT[tint];
              const cumul = kontrolleCumulDiff(k);
              const isExpanded = kontrolleExpandedId === k.id;
              const isHighlighted = kontrolleHighlightId === k.id;
              const closedCount = k.cycles.filter((c) => c.status === "abgeschlossen").length;
              const currentClosed = k.currentCycle.status === "abgeschlossen";
              const zyklenLabel = !currentClosed ? `(${closedCount} + 1 ${t("kontrolle.activeWord", "actif")})` : k.archived ? `(${t("kontrolle.allClosedWord", "tous clos")})` : `(${closedCount} ${t("kontrolle.closedWord", "clos")})`;
              const metaLine = k.archived
                ? `${t("kontrolle.closedOnLabel", "Clôturé le")} ${fmtFR((k.archivedAt || "").slice(0, 10))}`
                : k.currentCycle.plannedEndAt
                  ? `${t("kontrolle.nextDueLabel", "Prochaine échéance")} ${fmtFR(k.currentCycle.plannedEndAt)}`
                  : `${t("kontrolle.cycleStartedOnLabel", "Zyklus démarré le")} ${fmtFR((k.currentCycle.startAt || "").slice(0, 10))}`;
              const forceClosed = (k.history || []).some((h) => h.action === "FORCE_CLOSED");
              const chipEmoji = forceClosed ? "⛔" : k.archived ? "✓" : k.reason === "diebstahlverdacht" ? "🔎" : (k.reason === "bestandsauffaelligkeit" || k.reason === "ungewoehnlich_hoher_verkauf") ? "⚠" : "🕵️";
              const chipLabel = forceClosed ? t("kontrolle.chip.forceClosed", "Clôturé anticipé (GR)") : k.archived ? t("kontrolle.status.abgeschlossen", "Terminé") : KONTROLLE_REASON_META[k.reason];
              return (
                <div key={k.id} style={{ marginBottom: 10 }}>
                  <div
                    role="button" tabIndex={0} onClick={() => { setKontrolleExpandedId(isExpanded ? null : k.id); setKontrolleExpandedCycleId(k.currentCycle.id); setKontrolleEditInfoOpen(false); }}
                    style={{
                      ...card, background: tc.bg, cursor: "pointer", padding: 14,
                      borderRadius: isExpanded ? "18px 18px 4px 4px" : 24,
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                      border: isHighlighted ? `1.5px solid ${A.blue}` : (card.border || "1.5px solid transparent"),
                      animation: isHighlighted ? "nxNextArticlePulse 1s ease-in-out 3" : "none",
                      transition: "border-color .4s ease",
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4, flexWrap: "wrap" }}>
                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>{k.artNr}</span>
                        <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, padding: "3px 9px", borderRadius: 99, color: tc.c, background: tc.chipBg, whiteSpace: "nowrap" }}>{chipEmoji} {chipLabel}</span>
                      </div>
                      <strong style={{ fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 2 }}>{k.name || k.artNr}</strong>
                      <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block" }}>{k.categoryName ? k.categoryName + " · " : ""}{k.assignedUserName || t("kontrolle.unassigned", "Non attribué")}</span>
                      <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, opacity: .75, display: "block", marginTop: 2 }}>{metaLine}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
                      <div style={{ textAlign: "right" }}>
                        <strong style={{ fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, display: "block", color: closedCount ? tc.c : P.muted }}>{closedCount ? `${cumul > 0 ? "+" : ""}${cumul}` : "—"}</strong>
                        <span style={{ fontSize: `calc(8.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, textTransform: "uppercase" }}>{t("kontrolle.cumulShortLabel", "Cumul")}</span>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <strong style={{ fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, display: "block" }}>{k.cycles.length}</strong>
                        <span style={{ fontSize: `calc(8.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, textTransform: "uppercase" }}>{t("kontrolle.zyklenShortLabel", "Zyklen")}</span>
                      </div>
                      <Pressable onClick={(e) => { e.stopPropagation(); setKontrolleExportIds([k.id]); }} aria-label={t("kontrolle.export.thisArticleAria", "Exporter cet article")} style={{
                        width: 30, height: 30, borderRadius: 99, border: `1px solid ${P.line}`, background: P.card,
                        display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, flexShrink: 0,
                      }}>⬇</Pressable>
                      <span style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`, color: P.muted, transition: "transform .2s", transform: isExpanded ? "rotate(90deg)" : "none", flexShrink: 0 }}>›</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ ...card, borderTop: `1px dashed ${P.line}`, borderRadius: "4px 4px 18px 18px", marginTop: 2, padding: "16px 16px 4px", flexDirection: "column", alignItems: "stretch" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 1, background: P.line, borderRadius: 12, overflow: "hidden", border: `1px solid ${P.line}`, marginBottom: 16 }}>
                        {[
                          [kontrolleDurationLabel(k), t("kontrolle.kpi.sinceLabel", "Sous contrôle depuis")],
                          [String(k.cycles.length), `${t("kontrolle.zyklenShortLabel", "Zyklen")} ${zyklenLabel}`],
                          [closedCount ? `${cumul > 0 ? "+" : ""}${cumul}` : "—", t("kontrolle.stat.cumulative", "Cumul Differenz"), closedCount ? tc.c : P.muted],
                          [k.archived ? fmtFR((k.archivedAt || "").slice(0, 10)) : (k.currentCycle.plannedEndAt ? fmtFR(k.currentCycle.plannedEndAt) : "—"), k.archived ? t("kontrolle.closedOnLabel", "Clôturé le") : t("kontrolle.nextDueLabel", "Prochaine échéance")],
                        ].map(([v, l, vColor], i) => (
                          <div key={i} style={{ background: P.card, padding: "12px 8px", textAlign: "center" }}>
                            <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`, display: "block", color: vColor || P.ink, letterSpacing: "-.01em" }}>{v}</strong>
                            <span style={{ fontSize: `calc(8px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, textTransform: "uppercase", letterSpacing: ".03em", display: "block", marginTop: 2 }}>{l}</span>
                          </div>
                        ))}
                      </div>

                      {canEditKontrolle && (
                        <div style={{ marginBottom: 16 }}>
                          <Pressable onClick={() => setKontrolleEditInfoOpen(!kontrolleEditInfoOpen)} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>
                            <span>✏️ {t("kontrolle.editInfo.toggle", "Modifier les informations de l'article")}</span>
                            <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, transition: "transform .2s", transform: kontrolleEditInfoOpen ? "rotate(90deg)" : "none" }}>›</span>
                          </Pressable>
                          {kontrolleEditInfoOpen && (
                            <div style={{ background: P.soft, borderRadius: 12, padding: "12px 13px", marginTop: 8 }}>
                              <Field label={t("kontrolle.designationLabel", "Désignation")}>
                                <input style={inputStyle} defaultValue={k.name || ""} onBlur={(e) => e.target.value !== (k.name || "") && patchInventurKontrolle({ name: e.target.value })} />
                              </Field>
                              <Field label={t("inventur.categoryLabel", "Catégorie")}>
                                <input style={inputStyle} defaultValue={k.categoryName || ""} onBlur={(e) => e.target.value !== (k.categoryName || "") && patchInventurKontrolle({ categoryName: e.target.value })} />
                              </Field>
                              <Field label={t("kontrolle.assignedEmployeeLabel", "Collaborateur affecté")}>
                                <select style={inputStyle} defaultValue={k.assignedUserId || ""} onChange={(e) => { const u = inventurTeamRoster.find((x) => x.employeeId === e.target.value); patchInventurKontrolle({ assignedUserId: e.target.value, assignedUserName: u?.name || "" }); }}>
                                  <option value="">—</option>
                                  {inventurTeamRoster.map((u) => <option key={u.employeeId} value={u.employeeId}>{u.name}</option>)}
                                </select>
                              </Field>
                              <Field label={t("kontrolle.reasonLabel", "Motif")}>
                                <select style={inputStyle} defaultValue={k.reason} onChange={(e) => patchInventurKontrolle({ reason: e.target.value })}>
                                  {Object.entries(KONTROLLE_REASON_META).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                                </select>
                              </Field>
                              <div style={{ marginBottom: 0 }}>
                                <Field label={t("kontrolle.noteLabel", "Remarque")}>
                                  <input style={inputStyle} defaultValue={k.note || ""} onBlur={(e) => e.target.value !== (k.note || "") && patchInventurKontrolle({ note: e.target.value })} />
                                </Field>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      <div style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, margin: "0 4px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                        <span>{t("kontrolle.timelineLabel", "Zyklen")}</span><span style={{ flex: 1, height: 1, background: P.line }} />
                      </div>

                      <div style={{ position: "relative", paddingLeft: 2 }}>
                        {k.cycles.slice().reverse().map((c) => {
                          const isCurrent = c.id === k.currentCycle.id;
                          const locked = c.status === "abgeschlossen";
                          const cycleExpanded = kontrolleExpandedCycleId === c.id;
                          const cycleTint = !locked ? "active" : (c.differenz == null ? "muted" : c.differenz === 0 ? "good" : c.differenz > 0 ? "warn" : "bad");
                          const cct = KONTROLLE_TINT[cycleTint];
                          return (
                            <div key={c.id} style={{ marginBottom: 12 }}>
                              <div role="button" tabIndex={0} onClick={() => setKontrolleExpandedCycleId(cycleExpanded ? null : c.id)}
                                style={{ ...card, borderColor: isCurrent ? cct.c + "73" : P.line, cursor: "pointer", padding: "13px 15px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, borderRadius: cycleExpanded ? "16px 16px 4px 4px" : 16 }}>
                                <div style={{ display: "flex", alignItems: "baseline", gap: 9, minWidth: 0 }}>
                                  <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{t("kontrolle.cycleTemplate", { n: c.cycleNumber }, `Zyklus ${c.cycleNumber}`)}</strong>
                                  <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {fmtFR((c.startAt || "").slice(0, 10))} → {locked ? fmtFR((c.closedAt || "").slice(0, 10)) : (c.plannedEndAt ? `${fmtFR(c.plannedEndAt)} (${t("kontrolle.plannedWord", "geplant")})` : "—")}
                                  </span>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                                  {locked
                                    ? <strong style={{ fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, color: cct.c }}>{c.differenz != null ? `${c.differenz > 0 ? "+" : ""}${c.differenz}` : "—"}</strong>
                                    : <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>{t("kontrolle.export.ongoing", "en cours")}</span>}
                                  <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, transform: cycleExpanded ? "rotate(90deg)" : "none", transition: "transform .2s" }}>▸</span>
                                </div>
                              </div>

                              {cycleExpanded && (
                                <div style={{ ...card, borderTop: `1px dashed ${P.line}`, borderRadius: "4px 4px 16px 16px", marginTop: 2, padding: "14px 15px 6px", flexDirection: "column", alignItems: "stretch" }}>
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, padding: "4px 10px", borderRadius: 99, marginBottom: 14, color: cct.c, background: cct.chipBg, alignSelf: "flex-start" }}>
                                    {locked ? (c.differenz === 0 ? "✓" : c.differenz != null ? "⚠" : "⛔") : "●"}{" "}
                                    {locked
                                      ? c.differenz === 0
                                        ? t("kontrolle.cycle.closedGood", "Terminé — aucun écart")
                                        : c.differenz > 0
                                        ? t("kontrolle.cycle.closedSurplus", "Clôturé — excédent constaté")
                                        : c.differenz != null
                                        ? t("kontrolle.cycle.closedBad", "Terminé — écart constaté")
                                        : t("kontrolle.cycle.closedIncomplete", "Clôturé — cycle incomplet")
                                      : (KONTROLLE_STATUS_META[c.status]?.label || c.status)}
                                  </span>

                                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                                    <div style={{ background: P.soft, borderRadius: 12, padding: "11px 12px" }}>
                                      <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{t("kontrolle.startdatumLabel", "Startdatum")}</span>
                                      {isCurrent && canEditKontrolle ? (
                                        <input type="date" style={{ ...dateTimeInputStyle, marginTop: 2, padding: "3px 4px", fontSize: `calc(14px * var(--nx-font-scale, 1))`, fontWeight: 800, height: "auto" }} defaultValue={(c.startAt || "").slice(0, 10)} onBlur={(e) => e.target.value && e.target.value !== (c.startAt || "").slice(0, 10) && patchInventurKontrolle({ startAt: e.target.value })} />
                                      ) : (
                                        <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, display: "block", marginTop: 2 }}>{fmtFR((c.startAt || "").slice(0, 10))}</strong>
                                      )}
                                    </div>
                                    <div style={{ background: P.soft, borderRadius: 12, padding: "11px 12px" }}>
                                      <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{locked ? t("kontrolle.enddatumLabel", "Enddatum") : t("kontrolle.plannedEndLabel", "Geplantes Enddatum")}</span>
                                      {isCurrent && canEditKontrolle ? (
                                        locked ? (
                                          <input type="date" style={{ ...dateTimeInputStyle, marginTop: 2, padding: "3px 4px", fontSize: `calc(14px * var(--nx-font-scale, 1))`, fontWeight: 800, height: "auto" }} defaultValue={(c.closedAt || "").slice(0, 10)} onBlur={(e) => e.target.value && e.target.value !== (c.closedAt || "").slice(0, 10) && patchInventurKontrolle({ closedAt: e.target.value })} />
                                        ) : (
                                          <input type="date" style={{ ...dateTimeInputStyle, marginTop: 2, padding: "3px 4px", fontSize: `calc(14px * var(--nx-font-scale, 1))`, fontWeight: 800, height: "auto" }} defaultValue={c.plannedEndAt || ""} onChange={(e) => e.target.value !== c.plannedEndAt && patchInventurKontrolle({ plannedEndAt: e.target.value })} />
                                        )
                                      ) : (
                                        <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, display: "block", marginTop: 2 }}>{locked ? fmtFR((c.closedAt || "").slice(0, 10)) : (c.plannedEndAt ? fmtFR(c.plannedEndAt) : "—")}</strong>
                                      )}
                                    </div>
                                  </div>
                                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                                    <div style={{ background: P.soft, borderRadius: 12, padding: "11px 12px" }}><span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>Anfangsbestand</span><strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, display: "block", marginTop: 2 }}>{c.anfangsbestand}</strong></div>
                                    <div style={{ background: P.soft, borderRadius: 12, padding: "11px 12px" }}><span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>Liefermenge</span><strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, display: "block", marginTop: 2 }}>{c.liefermenge}</strong><span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("kontrolle.deliveriesCountTemplate", { n: c.deliveries.length }, `${c.deliveries.length} livraison(s)`)}</span></div>
                                  </div>
                                  {(c.anfangsbestand != null && c.liefermenge != null) && (
                                    <div style={{ background: A.blue + "14", border: `1px dashed ${A.blue}55`, borderRadius: 12, padding: "9px 12px", marginBottom: 10 }}>
                                      <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.blue, textTransform: "uppercase" }}>{t("kontrolle.gesamtLabel", "Gesamt")}</span>
                                      <div style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, marginTop: 2 }}>
                                        Anfangsbestand <span style={{ color: P.muted, fontWeight: 600 }}>+</span> Liefermenge <span style={{ color: P.muted, fontWeight: 600 }}>=</span>{" "}
                                        {c.anfangsbestand} <span style={{ color: P.muted, fontWeight: 600 }}>+</span> {c.liefermenge} <span style={{ color: P.muted, fontWeight: 600 }}>=</span>{" "}
                                        <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{c.anfangsbestand + c.liefermenge}</strong>
                                      </div>
                                    </div>
                                  )}

                                  <strong style={{ display: "block", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, margin: "4px 2px 8px" }}>{t("kontrolle.deliveriesHeadingTemplate", { n: c.cycleNumber }, `Lieferungen (Zyklus ${c.cycleNumber})`)}</strong>
                                  {c.deliveries.length > 0 && (
                                    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10, fontSize: `calc(12px * var(--nx-font-scale, 1))`}}>
                                      <thead><tr>
                                        <th style={{ textAlign: "left", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", paddingBottom: 6, borderBottom: `1.5px solid ${P.line}` }}>{t("reporting.dateLabel", "Date")}</th>
                                        <th style={{ textAlign: "right", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", paddingBottom: 6, borderBottom: `1.5px solid ${P.line}` }}>{t("controlRun.quantityLabel", "Quantité")}</th>
                                        <th style={{ textAlign: "right", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", paddingBottom: 6, borderBottom: `1.5px solid ${P.line}` }}>{t("inventur.noteOptionalLabel", "Remarque")}</th>
                                        {isCurrent && canEditKontrolle && <th style={{ width: 54, borderBottom: `1.5px solid ${P.line}` }} />}
                                      </tr></thead>
                                      <tbody>
                                        {c.deliveries.map((d) => (
                                          <tr key={d.id}>
                                            <td style={{ padding: "8px 6px 8px 0", borderBottom: `1px solid ${P.soft}` }}>{fmtFR(d.date)}</td>
                                            <td style={{ padding: "8px 0", borderBottom: `1px solid ${P.soft}`, textAlign: "right", fontWeight: 800 }}>{d.qty}</td>
                                            <td style={{ padding: "8px 0 8px 6px", borderBottom: `1px solid ${P.soft}`, textAlign: "right", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{d.note || "—"}</td>
                                            {isCurrent && canEditKontrolle && (
                                              <td style={{ padding: "8px 0 8px 6px", borderBottom: `1px solid ${P.soft}`, textAlign: "right", whiteSpace: "nowrap" }}>
                                                <Pressable onClick={() => openKontrolleDeliveryEdit(d)} aria-label={t("common.edit", "Modifier")} style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, padding: "2px 5px" }}>✎</Pressable>
                                                <Pressable onClick={() => deleteKontrolleDelivery(d)} aria-label={t("common.delete", "Supprimer")} style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, padding: "2px 5px", color: A.red }}>✕</Pressable>
                                              </td>
                                            )}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                  {isCurrent && canEditKontrolle && (
                                    <Pressable onClick={() => { setKontrolleDeliveryEditId(null); setKontrolleDeliveryForm({ date: todayISO(), qty: "", note: "" }); setSheet("kontrolleDelivery"); }} style={{ width: "100%", padding: "9px 0", borderRadius: 99, border: `1.5px dashed ${P.line}`, textAlign: "center", fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 14 }}>＋ {t("kontrolle.addDeliveryBtn", "Ajouter une livraison")}</Pressable>
                                  )}

                                  {isCurrent && canEditKontrolle && (
                                    <Field label={t("kontrolle.anfangsbestandCountedLabel", "Anfangsbestand (compté)")}>
                                      <input key={`ab-${c.id}`} style={inputStyle} inputMode="numeric" defaultValue={c.anfangsbestand ?? ""} onBlur={(e) => e.target.value !== "" && patchInventurKontrolle({ anfangsbestand: Number(e.target.value) })} />
                                    </Field>
                                  )}
                                  {isCurrent && canEditKontrolle ? (
                                    <Field label={t("kontrolle.verkaufsmengeManualLabel", "Verkaufsmenge (manuell aus Norma)")}>
                                      <input key={`vk-${c.id}`} style={inputStyle} inputMode="numeric" defaultValue={c.verkaufsmenge ?? ""} onBlur={(e) => e.target.value !== "" && patchInventurKontrolle({ verkaufsmenge: Number(e.target.value) })} />
                                    </Field>
                                  ) : null}

                                  {/* Cartes formules : utiles pendant la saisie (expliquer le calcul avant qu'il
                                      soit figé), superflues une fois le cycle clôturé — retour utilisateur
                                      2026-08-09, "n'ont pas besoin d'apparaître dans historique ni archives".
                                      Les chiffres finaux restent visibles ailleurs (badge Differenz, KPI). */}
                                  {!locked && (
                                    <div style={formulaCard(c.verkaufsmenge != null ? "active" : "muted")}>
                                      <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: c.verkaufsmenge != null ? A.blue : P.muted, textTransform: "uppercase", letterSpacing: ".03em", display: "block", marginBottom: 6 }}>{t("kontrolle.formula.erwarteterLabel", "Formule — Erwarteter Endbestand")}</span>
                                      <div style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>
                                        Anfangsbestand <span style={{ color: P.muted, fontWeight: 600 }}>+</span> Liefermenge <span style={{ color: P.muted, fontWeight: 600 }}>−</span> Verkaufsmenge <span style={{ color: P.muted, fontWeight: 600 }}>=</span>{" "}
                                        {c.anfangsbestand} <span style={{ color: P.muted, fontWeight: 600 }}>+</span> {c.liefermenge} <span style={{ color: P.muted, fontWeight: 600 }}>−</span> {c.verkaufsmenge != null ? c.verkaufsmenge : <span style={{ color: P.muted }}>?</span>}
                                      </div>
                                      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6 }}>
                                        <strong style={{ fontSize: `calc(22px * var(--nx-font-scale, 1))`, color: c.erwarteterEndbestand != null ? P.ink : P.muted }}>{c.erwarteterEndbestand != null ? c.erwarteterEndbestand : "—"}</strong>
                                        {c.erwarteterEndbestand == null && <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, padding: "2px 9px", borderRadius: 99, background: KONTROLLE_TINT.warn.chipBg, color: A.orange }}>{t("kontrolle.formula.waitingVerkaufsmenge", "en attente de la Verkaufsmenge")}</span>}
                                      </div>
                                    </div>
                                  )}

                                  {isCurrent && canEditKontrolle ? (
                                    <Field label={t("kontrolle.endbestandCountedLabel", "Endbestand (compté)")}>
                                      <input key={`eb-${c.id}`} style={inputStyle} inputMode="numeric" defaultValue={c.endbestand ?? ""} onBlur={(e) => e.target.value !== "" && patchInventurKontrolle({ endbestand: Number(e.target.value) })} />
                                    </Field>
                                  ) : c.endbestand != null ? (
                                    <div style={{ background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 12, padding: "11px 12px", marginBottom: 12 }}>
                                      <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{t("kontrolle.endbestandCountedLabel", "Endbestand (compté)")}</span>
                                      <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, display: "block", marginTop: 2 }}>{c.endbestand}</strong>
                                      <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("kontrolle.formula.countedOnlyHint", "Compté physiquement — la seule valeur ici qui n'est jamais calculée")}</span>
                                    </div>
                                  ) : null}

                                  {!locked && c.differenz != null && (() => {
                                    // Trois états, pas deux : 0 = aucun écart ; négatif = manquant (Fehlbestand, rouge) ;
                                    // positif = excédent (Überbestand, orange) — un vrai bug trouvé ici : tout écart
                                    // non nul s'affichait "Fehlbestand" même quand le compte physique était SUPÉRIEUR
                                    // à l'attendu, ce qui est l'inverse d'un manque.
                                    const diffTint = c.differenz === 0 ? "good" : c.differenz > 0 ? "warn" : "bad";
                                    const diffColor = c.differenz === 0 ? A.green : c.differenz > 0 ? A.orange : A.red;
                                    const diffTag =
                                      c.differenz === 0
                                        ? t("kontrolle.formula.noDiffTag", "Aucun écart")
                                        : c.differenz > 0
                                        ? t("kontrolle.formula.surplusTag", "Excédent")
                                        : t("kontrolle.formula.diffTag", "Manquant");
                                    return (
                                      <div style={formulaCard(diffTint)}>
                                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: diffColor, textTransform: "uppercase", letterSpacing: ".03em", display: "block", marginBottom: 6 }}>{t("kontrolle.formula.differenzLabel", "Formule — Differenz")}</span>
                                        <div style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>Endbestand gezählt <span style={{ color: P.muted, fontWeight: 600 }}>−</span> Erwarteter Endbestand <span style={{ color: P.muted, fontWeight: 600 }}>=</span> {c.endbestand} <span style={{ color: P.muted, fontWeight: 600 }}>−</span> {c.erwarteterEndbestand}</div>
                                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6 }}>
                                          <strong style={{ fontSize: `calc(22px * var(--nx-font-scale, 1))`, color: diffColor }}>{c.differenz > 0 ? "+" : ""}{c.differenz}</strong>
                                          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, padding: "2px 9px", borderRadius: 99, background: KONTROLLE_TINT[diffTint].chipBg, color: diffColor }}>{diffTag}</span>
                                        </div>
                                      </div>
                                    );
                                  })()}

                                  <Field label={t("kontrolle.conclusionLabel", "Conclusion")}>
                                    {isCurrent && canEditKontrolle
                                      ? <input style={inputStyle} defaultValue={c.conclusion} onBlur={(e) => e.target.value !== c.conclusion && patchInventurKontrolle({ conclusion: e.target.value })} />
                                      : <div style={{ ...inputStyle, color: c.conclusion ? P.ink : P.muted, fontStyle: c.conclusion ? "normal" : "italic" }}>{c.conclusion || "—"}</div>}
                                  </Field>

                                  {locked && isCurrent && canEditKontrolle && (
                                    <p style={{ margin: "-6px 2px 12px", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                                      ✏️ {t("kontrolle.correctionHint", "Cycle clôturé — les corrections restent possibles (erreur de saisie), mais sont tracées de façon visible dans l'historique.")}
                                    </p>
                                  )}

                                  {isCurrent && canCloseKontrolle && c.status === "auswertung" && (
                                    <Pressable onClick={() => setKontrolleStatus("abgeschlossen")} style={{ width: "100%", ...btnSolid(A.green), marginTop: 4, marginBottom: 14 }}>{t("kontrolle.closeCycleBtn", "Clôturer le cycle")}</Pressable>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {currentClosed && !k.archived && canEditKontrolle && (
                        <div style={{ background: P.card, border: `1.5px dashed ${P.line}`, borderRadius: 18, padding: "16px 18px", marginTop: 6, marginBottom: 16 }}>
                          <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, marginBottom: 4 }}>🔁 {t("kontrolle.newCycleCardTitleTemplate", { n: k.currentCycle.cycleNumber + 1 }, `Démarrer le Zyklus ${k.currentCycle.cycleNumber + 1}`)}</strong>
                          <p style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 0 14px", lineHeight: 1.5 }}>{t("kontrolle.newCycleHint", "Der vorherige Zyklus bleibt gespeichert und im Verlauf sichtbar — es wird kein neuer Kontrolle-Datensatz erstellt.")}</p>
                          <Field label={t("kontrolle.startdatumLabel", "Startdatum")}>
                            <input type="date" style={dateTimeInputStyle} value={inventurCycleStartForm.startAt} onChange={(e) => setInventurCycleStartForm({ ...inventurCycleStartForm, startAt: e.target.value })} />
                          </Field>
                          <Field label={t("kontrolle.plannedEndOptionalLabel", "Fin prévue (facultatif)")}>
                            <input type="date" style={dateTimeInputStyle} value={inventurCycleStartForm.plannedEndAt} onChange={(e) => setInventurCycleStartForm({ ...inventurCycleStartForm, plannedEndAt: e.target.value })} />
                          </Field>
                          <Field label={t("kontrolle.anfangsbestandCountedLabel", "Anfangsbestand (compté)")}>
                            <input style={inputStyle} inputMode="numeric" value={inventurCycleStartForm.anfangsbestand !== "" ? inventurCycleStartForm.anfangsbestand : (k.currentCycle.endbestand ?? "")} onChange={(e) => setInventurCycleStartForm({ ...inventurCycleStartForm, anfangsbestand: e.target.value })} />
                          </Field>
                          <Pressable onClick={() => { if (inventurCycleStartForm.anfangsbestand === "") setInventurCycleStartForm((f) => ({ ...f, anfangsbestand: k.currentCycle.endbestand ?? "" })); startInventurKontrolleCycle(); }} style={{ width: "100%", ...btnSolid(A.blue) }}>{t("kontrolle.startCycleBtnTemplate", { n: k.currentCycle.cycleNumber + 1 }, `Zyklus ${k.currentCycle.cycleNumber + 1} starten`)}</Pressable>
                        </div>
                      )}

                      {canCloseKontrolle && !k.archived && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10, background: KONTROLLE_TINT.bad.bg, border: `1.5px dashed ${A.red}73`, borderRadius: 14, padding: "14px 15px", margin: "4px 0 18px" }}>
                          <div>
                            <strong style={{ display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.red, marginBottom: 3 }}>⛔ {t("kontrolle.forceClose.title", "Clôture anticipée (GR)")}</strong>
                            <p style={{ margin: 0, fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{t("kontrolle.forceClose.hint", "Arrête définitivement ce contrôle — y compris le Zyklus en cours — même si sa période prévue n'est pas terminée. Réservé aux responsables de secteur. Action irréversible.")}</p>
                          </div>
                          <Pressable onClick={() => forceCloseKontrolle(k)} style={{ alignSelf: "flex-start", padding: "9px 18px", borderRadius: 99, background: A.red, color: "#fff", fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`}}>{t("kontrolle.forceClose.btn", "Clôturer définitivement")}</Pressable>
                        </div>
                      )}

                      {canCloseKontrolle && (
                        <Pressable onClick={() => deleteInventurKontrolle(k)} style={{ width: "100%", padding: "12px 0", borderRadius: 99, textAlign: "center", fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`, background: "rgba(239,68,68,.1)", color: A.red, marginBottom: 16 }}>🗑️ {t("kontrolle.deleteBtn", "Supprimer le contrôle")}</Pressable>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* ---- Légende des formules — toujours visible, jamais une boîte noire ---- */}
            <section style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: "18px 20px", marginTop: 10 }}>
              <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, marginBottom: 12 }}>📐 {t("kontrolle.legend.title", "Formules — toujours visibles, jamais une boîte noire")}</strong>
              {[
                ["Anfangsbestand", t("kontrolle.legend.anfangsbestand", "Stock au tout début du cycle — jamais deviné, toujours repris tel quel de l'Endbestand (gezählt) du cycle précédent.")],
                ["Liefermenge", t("kontrolle.legend.liefermenge", "Somme de toutes les livraisons ajoutées dans le cycle en cours.")],
                ["Verkaufsmenge", t("kontrolle.legend.verkaufsmenge", "Saisie manuelle depuis Norma (jamais calculée) — ce qui a été vendu pendant le cycle.")],
                ["Erwarteter Endbestand", t("kontrolle.legend.erwartet", "Ce que le stock DEVRAIT être si rien n'a disparu : Anfangsbestand + Liefermenge − Verkaufsmenge.")],
                ["Endbestand (gezählt)", t("kontrolle.legend.endbestand", "Saisie manuelle (jamais calculée) — le stock réellement compté à la main à la clôture du cycle.")],
                ["Differenz", t("kontrolle.legend.differenz", "L'écart réel entre ce qui a été compté et ce qui était attendu. Négatif = manquant : Endbestand − Erwarteter Endbestand.")],
                ["Cumul Differenz", t("kontrolle.legend.cumul", "Somme des Differenz de tous les cycles déjà clôturés de ce contrôle (pas le cycle actif, encore incomplet).")],
              ].map(([k2, f]) => (
                <div key={k2} style={{ display: "flex", gap: 10, padding: "9px 0", borderTop: `1px solid ${P.soft}` }}>
                  <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, flex: "0 0 150px" }}>{k2}</span>
                  <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{f}</span>
                </div>
              ))}
            </section>
          </>
          );
        })()}

        {/* ================= BESTANDSKONTROLLE — export (par article ou pour tous) ================= */}
        {screen === "inventur" && kontrolleExportIds != null && (() => {
          const list = inventurKontrollen.filter((k) => kontrolleExportIds.includes(k.id));
          const maxCycles = list.reduce((m, k) => Math.max(m, k.cycles.length), 0);
          const closeExport = () => setKontrolleExportIds(null);
          const downloadCsv = () => {
            const header = ["Numero article", "Designation", "Periode", ...Array.from({ length: maxCycles }, (_, i) => `Zyklus ${i + 1}`), "Cumul Differenz"];
            const esc = (v) => '"' + String(v ?? "").replace(/"/g, '""') + '"';
            const rows = list.map((k) => {
              const period = `${fmtFR((k.cycles[0].startAt || "").slice(0, 10))} - ${k.archived ? fmtFR((k.cycles[k.cycles.length - 1].closedAt || "").slice(0, 10)) : "en cours"}`;
              const cells = Array.from({ length: maxCycles }, (_, i) => {
                const c = k.cycles[i];
                if (!c) return "—";
                return c.status !== "abgeschlossen" ? "en cours" : (c.differenz != null ? `${c.differenz > 0 ? "+" : ""}${c.differenz}` : "—");
              });
              const cumul = kontrolleCumulDiff(k);
              return [k.artNr, k.name || "", period, ...cells, `${cumul > 0 ? "+" : ""}${cumul}`];
            });
            const csv = [header, ...rows].map((r) => r.map(esc).join(";")).join("\r\n");
            const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url; link.download = list.length === 1 ? `bestandskontrolle-${list[0].artNr}.csv` : "bestandskontrolle-export.csv";
            document.body.appendChild(link); link.click(); document.body.removeChild(link);
            URL.revokeObjectURL(url);
          };
          return (
            <Sheet open={true} onClose={closeExport} P={P} maxH="80%">
              <SheetHead kicker="Bestandskontrolle" title={list.length === 1 ? `${list[0].artNr}${list[0].name ? " · " + list[0].name : ""}` : t("kontrolle.export.allTitleTemplate", { n: list.length }, `Export — ${list.length} articles`)} onClose={closeExport} />
              <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 0 14px", lineHeight: 1.5 }}>{t("kontrolle.export.hint", "Une ligne par article : numéro, désignation, période du contrôle, résultat de chaque cycle, Cumul Differenz à part.")}</p>
              <div style={{ overflowX: "auto", marginBottom: 16 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`}}>
                  <thead><tr>
                    {[t("kontrolle.export.number", "Numéro"), t("kontrolle.export.designation", "Désignation"), t("kontrolle.export.period", "Période"), ...Array.from({ length: maxCycles }, (_, i) => `Zyklus ${i + 1}`), t("kontrolle.export.cumul", "Cumul Differenz")].map((h) => (
                      <th key={h} style={{ textAlign: "left", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", padding: "0 8px 8px", borderBottom: `1.5px solid ${P.line}`, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {list.map((k) => {
                      const period = `${fmtFR((k.cycles[0].startAt || "").slice(0, 10))} – ${k.archived ? fmtFR((k.cycles[k.cycles.length - 1].closedAt || "").slice(0, 10)) : t("kontrolle.export.ongoing", "en cours")}`;
                      const cumul = kontrolleCumulDiff(k);
                      return (
                        <tr key={k.id}>
                          <td style={{ padding: "9px 8px", borderBottom: `1px solid ${P.soft}`, fontWeight: 800 }}>{k.artNr}</td>
                          <td style={{ padding: "9px 8px", borderBottom: `1px solid ${P.soft}` }}>{k.name || "—"}</td>
                          <td style={{ padding: "9px 8px", borderBottom: `1px solid ${P.soft}`, whiteSpace: "nowrap" }}>{period}</td>
                          {Array.from({ length: maxCycles }, (_, i) => {
                            const c = k.cycles[i];
                            const label = !c ? "—" : (c.status !== "abgeschlossen" ? t("kontrolle.export.ongoing", "en cours") : (c.differenz != null ? `${c.differenz > 0 ? "+" : ""}${c.differenz}` : "—"));
                            return <td key={i} style={{ padding: "9px 8px", borderBottom: `1px solid ${P.soft}`, fontWeight: 800 }}>{label}</td>;
                          })}
                          <td style={{ padding: "9px 8px", borderBottom: `1px solid ${P.soft}`, fontWeight: 800 }}>{cumul > 0 ? "+" : ""}{cumul}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <Pressable onClick={() => printReportWithTitle(buildKontrollePrintHtml(list), list.length === 1 ? `Bestandskontrolle ${list[0].artNr} - Filiale ${user?.store}` : `Bestandskontrolle - Export - Filiale ${user?.store}`)} style={{ flex: 1, padding: "12px 0", borderRadius: 99, textAlign: "center", fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>🖨️ PDF</Pressable>
                <Pressable onClick={downloadCsv} style={{ flex: 1, ...btnSolid(A.blue) }}>⬇ CSV</Pressable>
              </div>
            </Sheet>
          );
        })()}

        {/* ================= BESTANDSKONTROLLE — formulaire papier vierge (aperçu à l'écran) =================
             L'aperçu direct est le vrai livrable (window.print() peut être bloqué selon
             l'environnement) — imprimer reste une action secondaire depuis cet aperçu. */}
        {screen === "inventur" && kontrolleBlankFormOpen && (
          <Sheet open={true} onClose={() => setKontrolleBlankFormOpen(false)} P={P}>
            <SheetHead kicker="Bestandskontrolle" title={t("kontrolle.blankForm.title", "Formulaire papier vierge")} onClose={() => setKontrolleBlankFormOpen(false)} />
            <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 0 16px", lineHeight: 1.5 }}>{t("kontrolle.blankForm.hint", "Pour un article suivi à la main, sans accès à l'application. Le même dossier papier peut courir sur plusieurs mois — un bloc par cycle, comme dans l'app.")}</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
              {[t("kontrolle.export.number", "Numéro d'article"), t("inventur.designationOptionalLabel", "Désignation"), t("kontrolle.categoryOptionalLabel", "Catégorie"), t("kontrolle.assignedEmployeeLabel", "Attribué à")].map((l) => (
                <div key={l}><span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".03em", display: "block", marginBottom: 5 }}>{l}</span><div style={{ ...inputStyle, color: P.muted }}>—</div></div>
              ))}
            </div>
            {[1, 2, 3].map((n) => (
              <div key={n} style={{ background: P.card, border: `1.5px dashed ${P.line}`, borderRadius: 16, padding: "16px 18px", marginBottom: 14 }}>
                <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, marginBottom: 12 }}>{t("kontrolle.cycleTemplate", { n }, `Zyklus ${n}`)}</strong>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                  {[t("kontrolle.startdatumLabel", "Startdatum"), t("kontrolle.plannedEndLabel", "Geplantes Enddatum")].map((l) => (
                    <div key={l}><span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{l}</span><div style={{ ...inputStyle, color: P.muted, marginTop: 4 }}>— . — . ——</div></div>
                  ))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                  {["Anfangsbestand", "Liefermenge"].map((l) => (
                    <div key={l}><span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{l}</span><div style={{ ...inputStyle, color: P.muted, marginTop: 4 }}>—</div></div>
                  ))}
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10, fontSize: `calc(11px * var(--nx-font-scale, 1))`}}>
                  <thead><tr>{[t("reporting.dateLabel", "Date"), t("controlRun.quantityLabel", "Quantité"), t("inventur.noteOptionalLabel", "Remarque")].map((h) => <th key={h} style={{ textAlign: "left", fontSize: `calc(8.5px * var(--nx-font-scale, 1))`, color: P.muted, textTransform: "uppercase", paddingBottom: 4, borderBottom: `1px solid ${P.line}` }}>{h}</th>)}</tr></thead>
                  <tbody>{[1, 2, 3].map((r) => <tr key={r}><td style={{ height: 20, borderBottom: `1px solid ${P.soft}` }}></td><td style={{ borderBottom: `1px solid ${P.soft}` }}></td><td style={{ borderBottom: `1px solid ${P.soft}` }}></td></tr>)}</tbody>
                </table>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                  {[t("kontrolle.verkaufsmengeManualLabel", "Verkaufsmenge (Norma)"), t("kontrolle.endbestandCountedLabel", "Endbestand (compté)")].map((l) => (
                    <div key={l}><span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{l}</span><div style={{ ...inputStyle, color: P.muted, marginTop: 4 }}>—</div></div>
                  ))}
                </div>
                <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, border: `1px dashed ${P.line}`, borderRadius: 10, padding: "8px 11px", marginBottom: 8 }}>Erwarteter Endbestand = Anfangsbestand + Liefermenge − Verkaufsmenge = ________</div>
                <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink, border: `1px dashed ${P.line}`, borderRadius: 10, padding: "8px 11px" }}>Differenz (ce cycle) = Endbestand (gezählt) − Erwarteter Endbestand = ________</div>
              </div>
            ))}
            <div style={{ border: `1.5px solid ${P.ink}`, borderRadius: 12, padding: "13px 15px", marginBottom: 16, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("kontrolle.legend.cumulTitle", "Cumul Differenz")} = ________</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 18 }}>
              {[t("kontrolle.blankForm.filledBy", "Rempli par"), t("reporting.dateLabel", "Date"), t("kontrolle.blankForm.signature", "Signature")].map((l) => (
                <div key={l}><span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{l}</span><div style={{ ...inputStyle, color: P.muted, marginTop: 4 }}>—</div></div>
              ))}
            </div>
            <Pressable onClick={() => printReportWithTitle(buildKontrolleBlankFormHtml(), "Bestandskontrolle - Formulaire vierge")} style={{ width: "100%", ...btnSolid(A.blue) }}>🖨️ {t("kontrolle.blankForm.printBtn", "Imprimer / PDF")}</Pressable>
          </Sheet>
        )}

        {/* ================= BESTANDSKONTROLLE — création (plusieurs articles à la fois) ================= */}
        {screen === "inventur" && inventurScreen === "kontrolle_create" && (
          <>
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>{t("kontrolle.articlesLabel", "Articles")}</span>
            {kontrolleCreateForm.articles.map((row, i) => (
              <div key={i} style={{ ...card, padding: 12, marginBottom: 9, flexDirection: "column", alignItems: "stretch" }}>
                <div style={{ display: "flex", gap: 8, marginBottom: row.artNr || kontrolleCreateForm.articles.length > 1 ? 8 : 0 }}>
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="Art.Nr" value={row.artNr} onChange={(e) => { const articles = [...kontrolleCreateForm.articles]; articles[i] = { ...row, artNr: e.target.value }; setKontrolleCreateForm({ ...kontrolleCreateForm, articles }); }} />
                  <input style={{ ...inputStyle, flex: "0 0 90px" }} inputMode="numeric" placeholder="Anfangsb." value={row.anfangsbestand} onChange={(e) => { const articles = [...kontrolleCreateForm.articles]; articles[i] = { ...row, anfangsbestand: e.target.value }; setKontrolleCreateForm({ ...kontrolleCreateForm, articles }); }} />
                  {kontrolleCreateForm.articles.length > 1 && (
                    <Pressable onClick={() => setKontrolleCreateForm({ ...kontrolleCreateForm, articles: kontrolleCreateForm.articles.filter((_, x) => x !== i) })} style={{ width: 38, height: 38, borderRadius: 12, background: "rgba(239,68,68,.12)", color: A.red, display: "grid", placeItems: "center", flex: "0 0 auto" }}>✕</Pressable>
                  )}
                </div>
                <input style={inputStyle} placeholder={t("inventur.designationOptionalLabel", "Désignation (facultatif)")} value={row.name} onChange={(e) => { const articles = [...kontrolleCreateForm.articles]; articles[i] = { ...row, name: e.target.value }; setKontrolleCreateForm({ ...kontrolleCreateForm, articles }); }} />
              </div>
            ))}
            <Pressable onClick={() => setKontrolleCreateForm({ ...kontrolleCreateForm, articles: [...kontrolleCreateForm.articles, { ...emptyKontrolleArticleRow }] })} style={{ padding: "9px 4px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.blue, marginBottom: 16 }}>＋ {t("kontrolle.addArticleBtn", "Ajouter un autre article")}</Pressable>

            <Field label={t("kontrolle.categoryOptionalLabel", "Catégorie (facultatif)")}>
              <select style={inputStyle} value={kontrolleCreateForm.categoryId} onChange={(e) => { const c = inventurCategoryTemplates.find((x) => x.id === e.target.value); setKontrolleCreateForm({ ...kontrolleCreateForm, categoryId: e.target.value, categoryName: c?.name || "" }); }}>
                <option value="">—</option>
                {inventurCategoryTemplates.filter((c) => c.active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label={t("kontrolle.plannedEndOptionalLabel", "Fin prévue (facultatif)")}><input type="date" style={dateTimeInputStyle} value={kontrolleCreateForm.plannedEndAt} onChange={(e) => setKontrolleCreateForm({ ...kontrolleCreateForm, plannedEndAt: e.target.value })} /></Field>
            <Field label={t("kontrolle.assignedEmployeeLabel", "Collaborateur affecté")}>
              <select style={inputStyle} value={kontrolleCreateForm.assignedUserId} onChange={(e) => { const u = inventurTeamRoster.find((x) => x.employeeId === e.target.value); setKontrolleCreateForm({ ...kontrolleCreateForm, assignedUserId: e.target.value, assignedUserName: u?.name || "" }); }}>
                <option value="">—</option>
                {inventurTeamRoster.map((u) => <option key={u.employeeId} value={u.employeeId}>{u.name}</option>)}
              </select>
            </Field>
            <Field label={t("arbeitszeitliste.external.reasonLabel", "Motif")}>
              <select style={inputStyle} value={kontrolleCreateForm.reason} onChange={(e) => setKontrolleCreateForm({ ...kontrolleCreateForm, reason: e.target.value })}>
                {Object.entries(KONTROLLE_REASON_META).map(([id, l]) => <option key={id} value={id}>{l}</option>)}
              </select>
            </Field>
            <Field label={t("inventur.noteOptionalLabel", "Remarque (facultatif)")}><input style={inputStyle} value={kontrolleCreateForm.note} onChange={(e) => setKontrolleCreateForm({ ...kontrolleCreateForm, note: e.target.value })} /></Field>
            <Pressable onClick={createInventurKontrolle} style={{ width: "100%", ...btnSolid(A.blue), marginTop: 8 }}>{kontrolleCreateForm.articles.filter((a) => a.artNr.trim()).length > 1 ? t("kontrolle.createManyBtnTemplate", { n: kontrolleCreateForm.articles.filter((a) => a.artNr.trim()).length }, `${kontrolleCreateForm.articles.filter((a) => a.artNr.trim()).length} Kontrollen erstellen`) : t("kontrolle.createOneBtn", "Créer le contrôle")}</Pressable>
          </>
        )}

        {/* ================= BESTANDSKONTROLLE — historique par article ================= */}
        {screen === "inventur" && inventurScreen === "kontrolle_history" && (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <input style={{ ...inputStyle, flex: 1 }} placeholder="Art.Nr…" value={kontrolleHistoryFilters.artNr} onChange={(e) => setKontrolleHistoryFilters({ ...kontrolleHistoryFilters, artNr: e.target.value })} />
              <Pressable onClick={loadKontrolleHistory} style={{ padding: "0 16px", borderRadius: 12, background: A.blue, color: "#fff", fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>OK</Pressable>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
              <select style={{ ...inputStyle, flex: 1 }} value={kontrolleHistoryFilters.reason} onChange={(e) => { setKontrolleHistoryFilters({ ...kontrolleHistoryFilters, reason: e.target.value }); }}>
                <option value="">{t("kontrolle.allReasonsOption", "Tous les motifs")}</option>
                {Object.entries(KONTROLLE_REASON_META).map(([id, l]) => <option key={id} value={id}>{l}</option>)}
              </select>
              <Pressable onClick={() => setKontrolleHistoryFilters({ ...kontrolleHistoryFilters, negativeOnly: !kontrolleHistoryFilters.negativeOnly })} style={{
                padding: "10px 13px", borderRadius: 12, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                background: kontrolleHistoryFilters.negativeOnly ? A.red : P.soft, color: kontrolleHistoryFilters.negativeOnly ? "#fff" : P.ink,
              }}>{t("kontrolle.negativesOnlyBtn", "Négatifs")}</Pressable>
            </div>
            {inventurKontrolleHistorySummary && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 7, marginBottom: 16 }}>
                {[{ v: inventurKontrolleHistorySummary.total, l: t("common.total", "Total") }, { v: inventurKontrolleHistorySummary.withoutDiff, l: t("kontrolle.stat.withoutDiff", "Sans écart") }, { v: inventurKontrolleHistorySummary.withDiff, l: t("kontrolle.stat.withDiff", "Avec écart") }, { v: inventurKontrolleHistorySummary.cumulativeDiff, l: t("kontrolle.stat.cumulative", "Cumul") }].map((s) => (
                  <div key={s.l} style={{ textAlign: "center", background: P.soft, borderRadius: 12, padding: "9px 3px" }}><strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, display: "block" }}>{s.v}</strong><span style={{ fontSize: `calc(8.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 800 }}>{s.l}</span></div>
                ))}
              </div>
            )}
            {/* Une ligne par CYCLE clôturé (pas par contrôle) — un même Art.Nr peut apparaître
                plusieurs fois, une fois par round terminé, pour un vrai suivi dans le temps. */}
            {inventurKontrolleHistory.map((row) => (
              <Pressable key={row.id} onClick={() => openInventurKontrolle(row.kontrolleId)} style={{ ...card, padding: 13, marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{row.artNr}{row.name ? " · " + row.name : ""} <span style={{ fontWeight: 400, color: P.muted }}>· {t("kontrolle.cycleTemplate", { n: row.cycleNumber }, `Zyklus ${row.cycleNumber}`)}</span></strong>
                  {row.differenz != null && <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: row.differenz === 0 ? A.green : row.differenz < 0 ? A.red : A.blue }}>{row.differenz > 0 ? "+" : ""}{row.differenz}</strong>}
                </div>
                <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 2 }}>{fmtFR((row.closedAt || row.startAt || "").slice(0, 10))} · {KONTROLLE_REASON_META[row.reason]}{row.assignedUserName ? " · " + row.assignedUserName : ""}</div>
              </Pressable>
            ))}
          </>
        )}

        {/* ================= INVENTUR — Rendez-vous d'inventaire (côté filiale) =================
             Un seul rendez-vous "actif" possible par filiale (voir server.js) — le reste de la
             liste renvoyée par GET /api/inventur/appointments est de l'historique (confirmé ou
             annulé), affiché en dessous pour consultation seulement. */}
        {screen === "inventur" && inventurScreen === "rdv" && (() => {
          const active = inventurAppointments.find((a) => !a.confirmed && !a.cancelled);
          const history = inventurAppointments.filter((a) => a !== active);
          return (
            <>
              {!active && (
                <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 30, marginBottom: 16 }}>
                  <span style={{ fontSize: `calc(34px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>📅</span>
                  <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{t("inventur.appointment.noneText", "Aucun rendez-vous d'inventaire proposé pour l'instant.")}</strong>
                </section>
              )}
              {active && (
                <>
                  <section style={{ ...colorCard(active.storeAgreed ? A.green : A.teal), ...tile, flexDirection: "column", alignItems: "stretch", padding: 20, marginBottom: 14 }}>
                    <span style={onColorTag}>{active.proposedBySide === "bl" ? t("inventur.appointment.proposedByBl", "Proposé par ton responsable réseau") : t("inventur.appointment.proposedByStore", "Ta contre-proposition")}</span>
                    <h2 style={{ margin: "6px 0 4px", fontSize: `calc(22px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{fmtFR(active.proposedDate)}</h2>
                    {active.storeAgreed && <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("inventur.appointment.waitingConfirm", "Acceptée — en attente de validation définitive par ton responsable réseau.")}</p>}
                  </section>

                  {active.proposedBySide === "bl" && !active.storeAgreed && (
                    <Pressable onClick={() => acceptInventurAppointment(active.id)} disabled={inventurAppointmentBusy} style={{ width: "100%", ...btnSolid(A.green), marginBottom: 10 }}>
                      ✓ {t("inventur.appointment.acceptBtn", "Accepter cette date")}
                    </Pressable>
                  )}

                  {!active.storeAgreed && (
                    <>
                      <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: 6 }}>{t("inventur.appointment.counterLabel", "Proposer une autre date")}</span>
                      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                        <input type="date" style={{ ...dateTimeInputStyle, flex: 1 }} value={inventurAppointmentCounterDate} onChange={(e) => setInventurAppointmentCounterDate(e.target.value)} />
                        <Pressable onClick={() => counterInventurAppointment(active.id)} disabled={!inventurAppointmentCounterDate || inventurAppointmentBusy} style={{
                          padding: "0 18px", borderRadius: 15, background: A.blue, color: "#fff", fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                          display: "grid", placeItems: "center", flex: "0 0 auto", opacity: (!inventurAppointmentCounterDate || inventurAppointmentBusy) ? .5 : 1,
                        }}>{t("inventur.appointment.counterBtn", "Envoyer")}</Pressable>
                      </div>
                    </>
                  )}

                  <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: 8 }}>{t("pilotage.appointment.chatLabel", "Échanges")}</span>
                  <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 10 }}>
                    {active.messages.length === 0 && (
                      <p style={{ color: P.muted, fontSize: `calc(12px * var(--nx-font-scale, 1))`, textAlign: "center", padding: "10px 0" }}>{t("pilotage.appointment.chatEmpty", "Aucun échange pour l'instant.")}</p>
                    )}
                    {active.messages.map((m) => (
                      <div key={m.id} style={{ display: "flex", justifyContent: m.side === "store" ? "flex-end" : "flex-start", marginBottom: 8 }}>
                        <div style={{ maxWidth: "78%", padding: "9px 13px", borderRadius: 16, background: m.side === "store" ? A.blue : P.soft, color: m.side === "store" ? "#fff" : P.ink }}>
                          <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{m.text}</span>
                          <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, opacity: .75, display: "block", marginTop: 3 }}>{m.authorName} · {new Date(m.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                    <input style={{ ...inputStyle, flex: 1 }} placeholder={t("messages.composerPlaceholder", "Écrire un message...")} value={inventurAppointmentChatInput}
                      onChange={(e) => setInventurAppointmentChatInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") sendInventurAppointmentMessage(active.id); }} />
                    <Pressable onClick={() => sendInventurAppointmentMessage(active.id)} aria-label={t("messages.sendAria", "Envoyer")} style={{
                      width: 46, height: 46, borderRadius: 15, background: A.blue, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                    }}>➤</Pressable>
                  </div>
                </>
              )}

              {history.length > 0 && (
                <>
                  <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: 6 }}>{t("inventur.appointment.historyTitle", "Historique")}</span>
                  {history.map((a) => (
                    <div key={a.id} style={{ ...card, padding: 13, marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{fmtFR(a.proposedDate)}</strong>
                        <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "3px 9px", color: "#fff", background: (a.confirmed && !a.cancelled) ? A.green : P.muted }}>
                          {(a.confirmed && !a.cancelled) ? t("inventur.appointment.statusConfirmed", "Confirmé") : t("inventur.appointment.statusCancelled", "Annulé")}
                        </span>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          );
        })()}

        {/* ================= DASHBOARD ================= */}
        {screen === "dashboard" && (() => {
          // CA réel — Umsatz Ist du jour (repli sur Plan si pas encore saisi), même
          // famille daily-entries "stundenleistung" que l'écran Reporting/Stundenleistung.
          // stundenDays couvre tout le mois (Plan connu dès l'import) — on filtre ici aux
          // jours déjà écoulés, jamais le Plan des jours futurs (sinon "CA du mois" deviendrait
          // une projection du mois entier au lieu du réalisé à date).
          const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
          const elapsedStundenDays = stundenDays.filter((d) => d.date <= todayISO);
          // dayCA : le Plan du dimanche (isClosed) est la somme Lu-Sa de la semaine
          // imprimée par Norma, jamais une cible pour CE jour — ne doit jamais servir de
          // repli d'estimation journalière (retour utilisateur 2026-08-09).
          // Retour utilisateur explicite (2026-08-11) : le Dashboard affichait le Plan
          // (l'objectif Norma) à la place d'un jour pas encore saisi, sans aucune étiquette
          // "estimation" — un chiffre qui ressemble à du réel alors que rien n'a été entré,
          // et qui grossit tout seul chaque jour qui passe sans la moindre saisie. Plus de
          // repli sur Plan ici : un jour non fermé sans Ist réel vaut null (pas 0, pas Plan),
          // jamais compté dans les totaux tant qu'il n'est pas réellement saisi.
          const dayCA = (d) => d.isClosed ? 0 : (d.umsatzIstTag ?? null);
          const weekCA = reportingWeekDates.map((date) => {
            if (date > todayISO) return 0;
            const d = stundenDays.find((x) => x.date === date);
            return d ? (dayCA(d) ?? 0) : 0;
          });
          const weekHasData = reportingWeekDates.some((date) => {
            if (date > todayISO) return false;
            const d = stundenDays.find((x) => x.date === date);
            return d && !d.isClosed && d.umsatzIstTag != null;
          });
          const monthHasData = elapsedStundenDays.some((d) => !d.isClosed && d.umsatzIstTag != null);
          const monthTotal = elapsedStundenDays.reduce((s, d) => s + dayCA(d), 0);
          const weekTotal = weekCA.reduce((s, v) => s + v, 0);
          const weekDelta = weekHasData && caCompare.lastWeekTotal > 0 ? Math.round(((weekTotal - caCompare.lastWeekTotal) / caCompare.lastWeekTotal) * 100) : null;
          const monthDelta = monthHasData && caCompare.lastMonthTotal > 0 ? Math.round(((monthTotal - caCompare.lastMonthTotal) / caCompare.lastMonthTotal) * 100) : null;
          // vs même mois, année dernière (YoY) — comparaison demandée par l'utilisateur
          // (2026-08-09), en plus de "vs mois dernier".
          const yoyDelta = monthHasData && caCompare.lastYearMonthTotal > 0 ? Math.round(((monthTotal - caCompare.lastYearMonthTotal) / caCompare.lastYearMonthTotal) * 100) : null;
          // Objectif Umsatz de la semaine — Plan à date (jours écoulés du Lu-Sa, jamais le
          // dimanche dont le Plan est le sous-total hebdo, jamais les jours futurs) vs Ist à
          // date : "l'objectif est-il atteint en fonction des chiffres déjà effectués ?"
          const weekPlanToDate = reportingWeekDates.reduce((s, date) => {
            if (date > todayISO) return s;
            const d = stundenDays.find((x) => x.date === date);
            return (d && !d.isClosed && d.umsatzPlanTag != null) ? s + d.umsatzPlanTag : s;
          }, 0);
          const weekGoalPct = weekPlanToDate > 0 ? Math.round((weekTotal / weekPlanToDate) * 100) : null;
          const maxDay = Math.max(1, ...weekCA);
          const wasteTotal = wasteByCat.reduce((s, [, v]) => s + v, 0);
          const wasteColors = {
            "Kühlung": A.frost, "Trocken": A.orange, "Frischfleisch": A.red, "Frischfisch": A.blue,
            "Obst/Gemüse": A.green, "Schnittblumen/Pflanzen": A.purple, "H-Bereich": A.brown,
            "Prebake": A.teal, "Frischbrot / Brot": A.navy,
          };

          /* Verdict de la semaine — fidèle au vrai buildVerdict() : 3 signaux
             (Umsatz, Leistung, Gaspillage) chacun note -1/0/+1, score ≥2 = bon,
             ≤-2 = à surveiller, sinon demi-teinte. Phrase synthétisée, jamais
             un texte fixe. */
          const stundenRows = stundenComputed();
          // leistungIstCum s'arrête au dernier Ist réellement saisi (plus de calcul au-delà,
          // voir stundenComputed) — donc jamais [length-1] (souvent un jour futur, null).
          const lastStundenIstRow = [...stundenRows].reverse().find((r) => r.leistungIstCum != null);
          const currentLeistung = lastStundenIstRow?.leistungIstCum ?? null;
          // Comparaisons "vs semaine dernière" — vraies données réelles (caCompare, voir
          // loadCaCompare), jamais une base inventée à côté d'un vrai total : null quand la
          // vraie comparaison n'est pas calculable (rien saisi la semaine dernière), plutôt
          // qu'un pourcentage fabriqué qui aurait pu induire un employé en erreur.
          const leistungDelta = (currentLeistung != null && caCompare.lastWeekLeistung)
            ? Math.round(((currentLeistung - caCompare.lastWeekLeistung) / caCompare.lastWeekLeistung) * 100) : null;
          const wasteDelta = caCompare.lastWeekWasteTotal
            ? Math.round(((wasteTotal - caCompare.lastWeekWasteTotal) / caCompare.lastWeekWasteTotal) * 100) : null;
          let verdictScore = weekDelta != null && weekDelta > 0 ? 1 : weekDelta != null && weekDelta < 0 ? -1 : 0;
          if (leistungDelta != null) verdictScore += leistungDelta > 0 ? 1 : leistungDelta < 0 ? -1 : 0;
          if (wasteDelta != null) verdictScore += wasteDelta < 0 ? 1 : wasteDelta > 0 ? -1 : 0;
          // Rien saisi cette semaine → aucun verdict synthétisé à partir de zéros (retour
          // utilisateur explicite : ça ferait dire "on vend moins" alors que rien n'a
          // simplement encore été entré, pas que les ventes ont baissé).
          const verdictColor = !weekHasData ? A.blue : verdictScore >= 2 ? A.green : verdictScore <= -2 ? A.red : A.orange;
          const verdictHeadline = !weekHasData ? t("dashboard.verdict.noEntryWeek", "Pas encore de saisie cette semaine.")
            : verdictScore >= 2 ? t("dashboard.verdict.allGood", "Tout va dans le bon sens.")
            : verdictScore <= -2 ? t("dashboard.verdict.watchClosely", "Une semaine à surveiller de près.")
            : t("dashboard.verdict.mixed", "Une semaine en demi-teinte.");
          const verdictParts = weekDelta != null ? [weekDelta >= 0 ? t("dashboard.verdict.sellingMore", "on vend plus") : t("dashboard.verdict.sellingLess", "on vend moins")] : [];
          if (leistungDelta != null) verdictParts.push(leistungDelta >= 0 ? t("dashboard.verdict.teamMoreEfficient", "l'équipe est plus efficace") : t("dashboard.verdict.teamLessEfficient", "l'équipe est moins efficace"));
          if (wasteDelta != null) verdictParts.push(wasteDelta <= 0 ? t("dashboard.verdict.wastingLess", "on jette moins") : t("dashboard.verdict.wastingMore", "on jette plus"));
          const verdictJoined = verdictParts.join(", ");
          const verdictBody = !weekHasData
            ? t("dashboard.verdict.fillReporting", { week: isoWeek(now) }, `Remplis Umsatz, Heures et Abschriften dans le Reporting Center pour voir le comparatif de KW ${isoWeek(now)}.`)
            : verdictParts.length === 0
            ? t("dashboard.verdict.notEnoughDataLastWeekFull", "Pas encore assez de données saisies la semaine dernière pour comparer.")
            : `${verdictJoined.charAt(0).toUpperCase()}${verdictJoined.slice(1)} ${t("dashboard.verdict.vsLastWeekSuffix", { week: isoWeek(now) - 1 }, `que la semaine dernière (KW ${isoWeek(now) - 1}).`)}`;

          // Prévision fin de semaine/mois — extrapolation du rythme actuel (Ist réel à date
          // ÷ jours ouvrés déjà écoulés × jours ouvrés totaux de la période), jamais un
          // chiffre inventé — reste null tant qu'aucun jour n'est encore écoulé, ET tant
          // qu'aucune vraie saisie n'existe cette semaine (sinon extrapoler 0€ n'a aucun sens).
          const elapsedTradingDaysWeek = reportingWeekDates.filter((d) => d <= todayISO).length;
          const weekPlanFull = reportingWeekDates.reduce((s, date) => {
            const d = stundenDays.find((x) => x.date === date);
            return (d && !d.isClosed && d.umsatzPlanTag != null) ? s + d.umsatzPlanTag : s;
          }, 0);
          const weekForecast = weekHasData && elapsedTradingDaysWeek > 0 ? Math.round((weekTotal / elapsedTradingDaysWeek) * 6) : null;
          const weekForecastPct = weekForecast != null && weekPlanFull > 0 ? Math.round((weekForecast / weekPlanFull) * 100) : null;

          // Jour le plus fort / le plus faible de la semaine (parmi les jours déjà écoulés
          // seulement — comparer un jour futur à 0€ n'aurait aucun sens, ni comparer des
          // zéros entre eux quand rien n'a encore été saisi).
          const elapsedDayEntries = weekCA.map((v, i) => ({ v, i })).filter(({ i }) => reportingWeekDates[i] && reportingWeekDates[i] <= todayISO);
          const strongestDay = weekHasData && elapsedDayEntries.length > 1 ? elapsedDayEntries.reduce((a, b) => (b.v > a.v ? b : a)) : null;
          const weakestDay = weekHasData && elapsedDayEntries.length > 1 ? elapsedDayEntries.reduce((a, b) => (b.v < a.v ? b : a)) : null;

          // Ratio Krank/effectif — même source réelle (krankPersons, planTeam) que
          // Planning/Reporting, jamais un chiffre séparé recalculé différemment.
          const teamSize = planTeam?.length || 0;
          const krankRatioPct = teamSize > 0 ? Math.round((krankPersons / teamSize) * 100) : null;

          return (
            <>
              {/* Plan du mois suivant pas encore importé — seulement affiché dans les 5
                  derniers jours du mois (loadDashboardExtras), jamais en plein milieu de
                  mois où l'alerte n'aurait aucun sens. */}
              {nextMonthPlanMissing && (
                <section style={{ ...colorCard(A.orange), ...tile, padding: 14, marginBottom: 10 }}>
                  <span style={{ fontSize: `calc(20px * var(--nx-font-scale, 1))`}}>📥</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: "#fff", display: "block" }}>{t("dashboard.nextMonthPlanMissingTitle", "Plan du mois prochain pas encore importé")}</strong>
                    <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("dashboard.nextMonthPlanMissingBody", "Pense à importer le fichier Norma avant le 1er.")}</span>
                  </div>
                </section>
              )}

              {/* Bilan de la semaine — verdict synthétique */}
              <section style={{ ...colorCard(verdictColor), ...tile, padding: 19, alignItems: "flex-start" }}>
                <span style={{ ...onColorChip, fontSize: `calc(24px * var(--nx-font-scale, 1))`}}>{!weekHasData ? "📋" : verdictScore >= 2 ? "🟢" : verdictScore <= -2 ? "🔴" : "🟠"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={onColorTag}>{t("dashboard.verdictEyebrow", "Bilan de la semaine")} · KW {isoWeek(now)}</span>
                  <h2 style={{ margin: "4px 0 5px", fontSize: `calc(17.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{verdictHeadline}</h2>
                  <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, lineHeight: 1.5, ...onColorSub }}>{verdictBody}</p>
                  {dayTasks.length > 0 && <p style={{ margin: "8px 0 0", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("dashboard.remainingTasksTodayTemplate", { n: dayTasks.length }, `Reste ${dayTasks.length} tâche${dayTasks.length > 1 ? "s" : ""} aujourd'hui.`)}</p>}
                </div>
              </section>

              {/* Grille desktop Phase 2 — sur téléphone, empilement vertical strictement
                  inchangé (marginBottom de `tile` fait déjà l'espacement). Sur desktop, les
                  widgets se répartissent en colonnes (largeur variable selon leur contenu,
                  d'où align-items:"start" pour ne pas étirer un petit widget à la hauteur
                  d'un grand voisin) au lieu de rester empilés dans 520-1100px de large. */}
              <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fit, minmax(420px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 12 : 0, alignItems: "start" }}>
              {/* Cette semaine : CA + mini graphique Lu→Sa — rien saisi = message honnête,
                  jamais un "0,00 €" qui pourrait passer pour un vrai chiffre plat. */}
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 17, marginBottom: isWide ? 0 : 12 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: weekHasData ? 4 : 0 }}>
                  <strong style={{ fontSize: `calc(14.5px * var(--nx-font-scale, 1))`}}>{t("dashboard.thisWeek", "Cette semaine")}</strong>
                  {weekDelta != null && (
                    <span style={{
                      fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 10px",
                      background: weekDelta >= 0 ? "rgba(30,184,95,.14)" : "rgba(239,68,68,.14)",
                      color: weekDelta >= 0 ? "#15803d" : A.red,
                    }}>{weekDelta >= 0 ? "▲" : "▼"} {Math.abs(weekDelta)} % {t("dashboard.vsKwShortTemplate", { week: isoWeek(now) - 1 }, `vs KW ${isoWeek(now) - 1}`)}</span>
                  )}
                </div>
                {!weekHasData ? (
                  <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("dashboard.weekNoDataText", "Rien saisi cette semaine — le CA s'affichera dès la première saisie Umsatz.")}</p>
                ) : (
                  <>
                    <strong style={{ fontSize: `calc(26px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", marginBottom: 12 }}>{truncate2(weekTotal).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</strong>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 7, height: 74 }}>
                      {weekCA.map((v, i) => (
                        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                          <div style={{
                            width: "100%", borderRadius: 8, height: `${Math.round((v / maxDay) * 58)}px`,
                            background: i === TODAY_IDX ? A.blue : strongestDay?.i === i ? "rgba(30,184,95,.35)" : weakestDay?.i === i ? "rgba(239,68,68,.3)" : P.soft,
                            border: `1px solid ${i === TODAY_IDX ? A.blue : strongestDay?.i === i ? A.green : weakestDay?.i === i ? A.red : P.line}`,
                            boxShadow: i === TODAY_IDX ? "0 8px 16px rgba(54,79,231,.3)" : "none",
                            transition: "height .5s ease",
                          }} />
                          <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>{weekLabelShort(i)}</span>
                        </div>
                      ))}
                    </div>
                    {(weekForecastPct != null || (strongestDay && weakestDay && strongestDay.i !== weakestDay.i)) && (
                      <p style={{ margin: "10px 0 0", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                        {weekForecastPct != null && t("dashboard.weekForecastTemplate", { value: truncate2(weekForecast).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }), pct: weekForecastPct }, `Au rythme actuel : ~${truncate2(weekForecast).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € en fin de semaine (${weekForecastPct} % de l'objectif).`)}
                        {weekForecastPct != null && strongestDay && weakestDay && strongestDay.i !== weakestDay.i && " · "}
                        {strongestDay && weakestDay && strongestDay.i !== weakestDay.i && t("dashboard.strongestWeakestDayTemplate", { strong: weekLabelShort(strongestDay.i), weak: weekLabelShort(weakestDay.i) }, `${weekLabelShort(strongestDay.i)} = meilleur jour, ${weekLabelShort(weakestDay.i)} = plus faible.`)}
                      </p>
                    )}
                  </>
                )}
              </section>

              {/* Tendance 4 semaines — Umsatz Ist réel, jamais une projection, pour éviter
                  qu'une semaine exceptionnelle (bonne ou mauvaise) fausse la lecture isolée. */}
              {weekTrend.length > 1 && (
                <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 16, marginBottom: isWide ? 0 : 12 }}>
                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, marginBottom: 10, display: "block" }}>{t("dashboard.trend4WeeksTitle", "Tendance 4 semaines")}</strong>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 9, height: 56 }}>
                    {(() => {
                      const maxTrend = Math.max(1, ...weekTrend.map((w) => w.total));
                      return weekTrend.map((w) => (
                        <div key={w.week} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                          <div style={{
                            width: "100%", borderRadius: 6, height: `${Math.round((w.total / maxTrend) * 40)}px`,
                            background: w.week === isoWeek(now) ? A.teal : P.soft,
                            border: `1px solid ${w.week === isoWeek(now) ? A.teal : P.line}`,
                          }} />
                          <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>KW{w.week}</span>
                        </div>
                      ));
                    })()}
                  </div>
                </section>
              )}

              {/* Objectif & efficacité — "l'objectif de la semaine est-il atteint" (Ist à
                  date / Plan à date, jamais le Plan de la semaine ENTIÈRE tant qu'elle n'est
                  pas terminée) + "l'équipe est-elle efficace" (Leistung réelle vs Leistung
                  Plan visée, même base cumulée) — retour utilisateur explicite 2026-08-09 :
                  le Dashboard ne doit pas se limiter à afficher l'Umsatz brut. */}
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 16, marginBottom: isWide ? 0 : 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: `calc(20px * var(--nx-font-scale, 1))`}}>🎯</span>
                  <span style={eyebrow(A.teal)}>{t("dashboard.goalEyebrow", "Objectif & efficacité")}</span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{
                    flex: 1, textAlign: "center", borderRadius: 14, padding: "11px 6px",
                    background: weekGoalPct == null ? P.soft : weekGoalPct >= 100 ? "rgba(30,184,95,.1)" : weekGoalPct >= 85 ? "rgba(231,138,18,.1)" : "rgba(239,68,68,.1)",
                    border: `1px solid ${weekGoalPct == null ? P.line : weekGoalPct >= 100 ? "rgba(30,184,95,.25)" : weekGoalPct >= 85 ? "rgba(231,138,18,.25)" : "rgba(239,68,68,.25)"}`,
                  }}>
                    <strong style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`, display: "block", color: weekGoalPct == null ? P.muted : weekGoalPct >= 100 ? A.green : weekGoalPct >= 85 ? A.orange : A.red }}>
                      {weekGoalPct != null ? `${weekGoalPct} %` : "—"}
                    </strong>
                    <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{t("dashboard.weekGoalLabel", "Objectif semaine")}</span>
                    <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 3 }}>
                      {weekPlanToDate > 0 ? t("dashboard.vsPlanToDateTemplate", { value: truncate2(weekPlanToDate).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }, `vs ${truncate2(weekPlanToDate).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € visés à date`) : t("dashboard.noPlanYetText", "Plan pas encore importé")}
                    </span>
                  </div>
                  <div style={{
                    flex: 1, textAlign: "center", borderRadius: 14, padding: "11px 6px",
                    background: currentLeistung == null || !lastStundenIstRow?.leistungPlanCum ? P.soft : currentLeistung >= lastStundenIstRow.leistungPlanCum ? "rgba(30,184,95,.1)" : "rgba(239,68,68,.1)",
                    border: `1px solid ${currentLeistung == null || !lastStundenIstRow?.leistungPlanCum ? P.line : currentLeistung >= lastStundenIstRow.leistungPlanCum ? "rgba(30,184,95,.25)" : "rgba(239,68,68,.25)"}`,
                  }}>
                    <strong style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`, display: "block", color: currentLeistung == null ? P.muted : (!lastStundenIstRow?.leistungPlanCum ? P.ink : currentLeistung >= lastStundenIstRow.leistungPlanCum ? A.green : A.red) }}>
                      {currentLeistung != null ? `${fmt2(currentLeistung)} €/h` : "—"}
                    </strong>
                    <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{t("dashboard.teamEfficiencyLabel", "Équipe efficace")}</span>
                    <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 3 }}>
                      {lastStundenIstRow?.leistungPlanCum != null ? t("dashboard.vsPlanLeistungTemplate", { value: fmt2(lastStundenIstRow.leistungPlanCum) }, `vs ${fmt2(lastStundenIstRow.leistungPlanCum)} €/h visés`) : t("dashboard.noIstYetText", "Aucun Ist saisi")}
                    </span>
                  </div>
                </div>
                {krankRatioPct != null && krankPersons > 0 && (
                  <p style={{ margin: "10px 0 0", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, textAlign: "center" }}>
                    {t("dashboard.krankRatioTemplate", { n: krankPersons, pct: krankRatioPct }, `🤒 ${krankPersons} personne${krankPersons > 1 ? "s" : ""} en arrêt (${krankRatioPct} % de l'équipe)`)}
                  </p>
                )}
              </section>

              {/* Stundenleistung — Soll toujours disponible (préchargé, jamais nul par
                  défaut) ; Ist arrive de Planning une fois synchronisé */}
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 16, marginBottom: isWide ? 0 : 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: `calc(22px * var(--nx-font-scale, 1))`}}>⏱️</span>
                  <span style={eyebrow(A.frost)}>Stundenleistung</span>
                  <Pressable onClick={() => { setPlanScreen("week"); setScreen("planning"); }} style={{
                    marginLeft: "auto", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.frost,
                    background: "rgba(45,127,240,.12)", borderRadius: 99, padding: "7px 11px",
                  }}>{t("dashboard.planningLinkLabel", "Planning ›")}</Pressable>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1, textAlign: "center", background: "rgba(54,79,231,.08)", border: "1px solid rgba(54,79,231,.2)", borderRadius: 14, padding: "10px 4px" }}>
                    <strong style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`, display: "block", color: A.blue }}>{weekSollHours} h</strong>
                    <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{t("planning.statSollWeek", "Soll semaine")}</span>
                  </div>
                  <div style={{ flex: 1, textAlign: "center", background: A.frost + "12", border: `1px solid ${A.frost}30`, borderRadius: 14, padding: "10px 4px" }}>
                    <strong style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`, display: "block", color: A.frost }}>{syncedHours[0] != null ? `${syncedHours[0]} h` : "—"}</strong>
                    <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{t("planning.statIstWeek", "Ist semaine")}</span>
                  </div>
                  <div style={{
                    flex: 1, textAlign: "center", borderRadius: 14, padding: "10px 4px",
                    background: syncedHours[0] == null ? P.soft : (syncedHours[0] - weekSollHours >= 0 ? "rgba(30,184,95,.1)" : "rgba(239,68,68,.1)"),
                    border: `1px solid ${syncedHours[0] == null ? P.line : (syncedHours[0] - weekSollHours >= 0 ? "rgba(30,184,95,.25)" : "rgba(239,68,68,.25)")}`,
                  }}>
                    <strong style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`, display: "block", color: syncedHours[0] == null ? P.muted : (syncedHours[0] - weekSollHours >= 0 ? A.green : A.red) }}>
                      {syncedHours[0] != null ? `${syncedHours[0] - weekSollHours >= 0 ? "+" : ""}${Math.round((syncedHours[0] - weekSollHours) * 10) / 10} h` : "—"}
                    </strong>
                    <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{t("planning.statDiffWeek", "Différence")}</span>
                  </div>
                </div>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 9 }}>
                  {syncedHours[0] != null
                    ? t("dashboard.leistungTargetSyncedText", { value: weekSollHours > 0 ? Math.round(weekTotal / weekSollHours) : "—" }, `≈ ${weekSollHours > 0 ? Math.round(weekTotal / weekSollHours) : "—"} €/h de Leistung visée · synchronisé depuis Planning`)
                    : t("dashboard.leistungNotSyncedText", "Le Soll vient de Stundenleistung (Reporting) ; publie et synchronise le plan dans Planning pour voir l'Ist.")}
                </span>
              </section>

              {/* Pertes de la semaine — vraie saisie manuelle entsorgt (Reporting) */}
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 17, marginBottom: isWide ? 0 : 12 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
                  <strong style={{ fontSize: `calc(14.5px * var(--nx-font-scale, 1))`}}>{t("dashboard.weeklyWriteoffs", "Pertes de la semaine")}</strong>
                  <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>{truncate2(wasteTotal).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                </div>
                {wasteByCat.length === 0 && (
                  <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("dashboard.noWasteEnteredThisWeekText", "Rien saisi cette semaine dans Reporting > Abschriften.")}</p>
                )}
                {wasteByCat.map(([cat, v]) => (
                  <div key={cat} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 9 }}>
                    {/* Retour utilisateur explicite (2026-08-18, capture à l'appui) : un
                        libellé sans espace ("Schnittblumen/Pflanzen") n'a aucun point de
                        rupture par défaut — le texte débordait donc de la colonne de 92px
                        et se mélangeait visuellement avec la barre de progression juste à
                        côté, plutôt que de passer à la ligne comme "Frischbrot / Brot"
                        (qui a de vrais espaces). overflowWrap:"anywhere" force une rupture
                        même en l'absence d'espace. */}
                    <span style={{ width: 92, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, flex: "0 0 auto", overflowWrap: "anywhere", lineHeight: 1.25 }}>{cat}</span>
                    <div style={{ flex: 1, height: 12, borderRadius: 99, background: P.soft, overflow: "hidden" }}>
                      <div style={{
                        width: `${Math.round((v / (wasteByCat[0][1] || 1)) * 100)}%`, height: "100%",
                        borderRadius: 99, background: wasteColors[cat] || A.navy, transition: "width .5s ease",
                      }} />
                    </div>
                    <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, width: 44, textAlign: "right" }}>{truncate2(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</strong>
                  </div>
                ))}
                <p style={{ margin: "4px 0 0", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                  {t("dashboard.wasteDailyEntryNoteText", 'Saisie quotidienne "entsorgt" par rayon (Reporting) — mise à jour en direct.')}
                </p>
              </section>

              {/* À faire aujourd'hui — même source que l'assistant */}
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 17, marginBottom: isWide ? 0 : 12 }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: dayTasks.length ? 10 : 0 }}>
                  <strong style={{ fontSize: `calc(14.5px * var(--nx-font-scale, 1))`}}>{t("dashboard.todayTasks", "À faire aujourd'hui")}</strong>
                  <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>{t("dashboard.tasksCountShortTemplate", { n: dayTasks.length }, `${dayTasks.length} tâche${dayTasks.length > 1 ? "s" : ""}`)}</span>
                </div>
                {dayTasks.length === 0 && (
                  <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("dashboard.nothingPendingAllDoneText", "Rien en attente — tout est fait ✅")}</p>
                )}
                {dayTasks.map((dt) => (
                  <Pressable key={dt.chip} onClick={() => goToDayTask(dt)} style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                    padding: "11px 12px", borderRadius: 14, background: P.soft, marginBottom: 7, color: P.ink,
                  }}>
                    <span style={{ flex: 1, fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{dt.chip.replace(" ›", "")}</span>
                    <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, textTransform: "none", fontWeight: 600 }}>{dt.label}</span>
                    <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted }}>›</span>
                  </Pressable>
                ))}
              </section>
              </div>

              {/* Ce mois + synchro */}
              <div style={{ display: "flex", gap: 10 }}>
                <section style={{ flex: 1, ...card, borderRadius: 20, padding: 15 }}>
                  <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".06em" }}>{t("dashboard.thisMonth", "Ce mois")}</span>
                  {!monthHasData ? (
                    <p style={{ margin: "6px 0 0", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.4 }}>{t("dashboard.monthNoDataText", "Rien saisi ce mois.")}</p>
                  ) : (
                    <>
                      <strong style={{ display: "block", fontSize: `calc(19px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", margin: "5px 0 3px" }}>{truncate2(monthTotal).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</strong>
                      {monthDelta != null && (
                        <span style={{
                          display: "block", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800,
                          color: monthDelta >= 0 ? "#15803d" : A.red,
                        }}>{monthDelta >= 0 ? "▲" : "▼"} {Math.abs(monthDelta)} % {t("dashboard.vsLastMonthTemplate", "vs mois dernier")}</span>
                      )}
                      {yoyDelta != null && (
                        <span style={{
                          display: "block", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, marginTop: 2,
                          color: yoyDelta >= 0 ? "#15803d" : A.red,
                        }}>{yoyDelta >= 0 ? "▲" : "▼"} {Math.abs(yoyDelta)} % {t("dashboard.vsLastYearTemplate", { month: MONTH_NAMES_FR[now.getMonth()], year: now.getFullYear() - 1 }, `vs ${MONTH_NAMES_FR[now.getMonth()]} ${now.getFullYear() - 1}`)}</span>
                      )}
                    </>
                  )}
                </section>
                <section style={{ flex: 1, ...card, borderRadius: 20, padding: 15 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".06em" }}>
                    {t("dashboard.liveMode", "Mode live")} <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
                  </span>
                  <strong style={{ display: "block", fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, margin: "7px 0 3px" }}>{t("dashboard.autoSync", "Synchro auto")}</strong>
                  <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>{t("dashboard.lastSync", "Dernière sync")} : {timeLabel}</span>
                </section>
              </div>
            </>
          );
        })()}

        {/* ================= PLANNING ================= */}
        {screen === "planning" && (
          <>
            {/* --- HUB : Qu'est-ce qu'on organise aujourd'hui ? --- */}
            {planScreen === "hub" && (
              <>
                <p style={{ margin: "0 2px 14px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                  {t("planning.hub.title", "Qu'est-ce qu'on organise aujourd'hui ?")} {t("planning.hub.subtitleTail", "Tout ce qui concerne l'équipe et le planning se trouve ici.")}
                </p>
                {[
                  { s: "week", bg: A.teal, emoji: "🗓️", tag: "Einsatzplan", t: t("planning.hub.arbeitsplan.title", "Plan de travail"), d: t("planning.hub.arbeitsplan.desc", "Créer, modifier et publier le planning de la semaine."), badge: isPublished ? t("planning.control.published", "Publié") : t("planning.control.draft", "Brouillon") },
                  { s: "published", bg: A.teal, emoji: "📢", tag: t("planning.published.eyebrow", "Historique"), t: t("planning.menu.published", "Plan de travail publié"), d: t("planning.hub.published.desc", "Historique de tous les plans publiés — consulter et télécharger."), badge: publishedWeeksList.length ? String(publishedWeeksList.length) : null },
                  { s: "requests", bg: A.purple, emoji: "🙋", tag: "Urlaub · Frei", t: t("planning.hub.requests.title", "Demandes de planning"), d: t("planning.hub.requests.desc", "Demander ou traiter les demandes de l'équipe."), badge: reqPending.length ? t("planning.requests.pendingCountBadge", { n: reqPending.length }, `${reqPending.length} en attente`) : null },
                  { s: "vacation", bg: A.orange, emoji: "🏖️", tag: "Congés", t: t("planning.hub.vacation.title", "Resturlaub"), d: t("planning.hub.vacation.desc", "Jours de congés attribués et restants par employé.") },
                  { s: "leistungsfrei", bg: A.frost, emoji: "⏱️", tag: "Heures", t: "Leistungsfrei", d: t("planning.hub.leistungsfrei.desc", "Solde d'heures et attribution par employé.") },
                  { s: "holidays", bg: A.red, emoji: "🎌", tag: "Calendrier", t: t("planning.hub.holidays.title", "Feiertage"), d: t("planning.hub.holidays.desc", "Jours fériés pris en compte dans les congés et le planning.") },
                ].map((x) => (
                  <Pressable key={x.s} onClick={() => setPlanScreen(x.s)} style={{ ...colorCard(x.bg), ...tile }}>
                    <div style={onColorChip}><EmojiIcon e={x.emoji} /></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={onColorTag}>{x.tag}</span>
                      <h2 style={{ margin: "3px 0 3px", fontSize: `calc(16.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{x.t}</h2>
                      <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, lineHeight: 1.35, ...onColorSub }}>{x.d}</p>
                    </div>
                    {x.badge ? (
                      <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, background: "rgba(255,255,255,.25)", color: "#fff", borderRadius: 99, padding: "4px 9px", whiteSpace: "nowrap" }}>{x.badge}</span>
                    ) : (
                      <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(19px * var(--nx-font-scale, 1))`, fontWeight: 300 }}>›</span>
                    )}
                  </Pressable>
                ))}
                {[
                  { s: "calendar", bg: A.orange, emoji: "🗓️", tag: "Congés", t: t("planning.hub.calendar.title", "Calendrier annuel"), d: t("planning.hub.calendar.desc", "Vue d'ensemble des congés approuvés sur toute l'année.") },
                  { s: "aushilfe", bg: A.brown, emoji: "👥", tag: "Équipe", t: t("planning.hub.aushilfe.title", "Disponibilités des extras"), d: t("planning.hub.aushilfe.desc", "Déclarer ou consulter les disponibilités des extras.") },
                ].map((x) => (
                  <Pressable key={x.t} onClick={() => setPlanScreen(x.s)} style={{ ...colorCard(x.bg), ...tile, padding: 13 }}>
                    <div style={onColorChip}><EmojiIcon e={x.emoji} /></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={onColorTag}>{x.tag}</span>
                      <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", margin: "2px 0 2px", color: "#fff" }}>{x.t}</strong>
                      <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, lineHeight: 1.3, ...onColorSub }}>{x.d}</div>
                    </div>
                    {x.badge ? (
                      <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: "rgba(255,255,255,.25)", borderRadius: 99, padding: "4px 9px", whiteSpace: "nowrap" }}>{x.badge}</span>
                    ) : (
                      <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(17px * var(--nx-font-scale, 1))`, fontWeight: 300 }}>›</span>
                    )}
                  </Pressable>
                ))}
              </>
            )}

            {/* --- PLAN DE TRAVAIL (semaine) --- */}
            {planScreen === "week" && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 13 }}>
                  <Pressable feedback onClick={() => setPlanWeekOffset((o) => o - 1)} aria-label={t("planning.control.prevWeek", "Semaine précédente")} style={{
                    width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
                  }}>‹</Pressable>
                  <div style={{ flex: 1, textAlign: "center" }}>
                    <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`}}>KW {isoWeek(now) + planWeekOffset}</strong>
                    <span style={{
                      display: "block", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, marginTop: 2,
                      color: isPublished ? "#15803d" : "#b56a00",
                    }}>{isPublished ? `● ${t("planning.control.published", "Publié")}` : `○ ${t("planning.control.draft", "Brouillon")}`}</span>
                  </div>
                  <Pressable feedback onClick={() => setPlanWeekOffset((o) => o + 1)} aria-label={t("planning.control.nextWeek", "Semaine suivante")} style={{
                    width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
                  }}>›</Pressable>
                  <Pressable onClick={exportArbeitsplan} aria-label={t("planning.menu.print", "Exporter l'Arbeitsplan")} style={{
                    width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`,
                  }}>🖨️</Pressable>
                </div>

                {/* Bascule Jours (détaillé) / Semaine (grille, écriture directe) */}
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  {[{ id: "days", l: `📋 ${t("planning.vacationDetail.daysPlaceholder", "Jours")}` }, { id: "grid", l: `🗓️ ${t("planning.control.week", "Semaine")}` }].map((v) => (
                    <Pressable key={v.id} onClick={() => setPlanWeekView(v.id)} style={{
                      flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                      background: planWeekView === v.id ? A.teal : P.soft, color: planWeekView === v.id ? "#fff" : P.ink,
                      border: `1.5px solid ${planWeekView === v.id ? A.teal : P.line}`,
                    }}>{v.l}</Pressable>
                  ))}
                </div>
                {planWeekReadOnly ? (
                  canPublishPlanning() ? (
                    <Pressable onClick={() => setPlanEditUnlocked(true)} style={{
                      width: "100%", textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, marginBottom: 13,
                      background: `${A.orange}1a`, border: `1.5px solid ${A.orange}`, color: A.orange,
                    }}>✏️ {t("planning.editPlanBtn", "Modifier le plan")}</Pressable>
                  ) : (
                    <p style={{ display: "flex", alignItems: "center", gap: 8, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, background: P.soft, borderRadius: 14, padding: "10px 12px", marginBottom: 13 }}>
                      <span>🔒</span> {t("planning.publishedReadOnlyText", "Plan publié — consultable uniquement.")}
                    </p>
                  )
                ) : (
                  <div style={{ display: "flex", gap: 8, marginBottom: 13 }}>
                    <Pressable onClick={copyPreviousWeek} style={{
                      flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                      background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                    }}>⤵ {t("planning.actions.copyPrevious", "Copier semaine précédente")}</Pressable>
                    <Pressable onClick={() => { if (window.confirm(t("planning.clearPlanningConfirm", "Vider entièrement ce planning ? Tous les créneaux de la semaine seront supprimés."))) resetWeekPlanning(); }} style={{
                      flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                      background: "rgba(239,68,68,.1)", border: `1.5px solid rgba(239,68,68,.3)`, color: A.red,
                    }}>🗑️ {t("planning.clearPlanningBtn", "Vider le planning")}</Pressable>
                  </div>
                )}

                {planWeekView === "grid" ? (
                  <div style={{ marginBottom: 4 }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      {/* Colonne noms — délibérément HORS du conteneur overflow-x:auto ci-dessous,
                          c'est précisément ce qui évite le conflit de geste qui a fait planter
                          l'ancienne implémentation (voir commentaire sur PLAN_GRID_ROW_H plus haut). */}
                      <div style={{ display: "flex", flexDirection: "column", gap: PLAN_GRID_GAP, flex: "0 0 auto", width: 92 }}>
                        <div style={{ height: PLAN_GRID_HEADER_H }} />
                        {dragPreviewTeam.map((emp) => {
                          const isDragging = dragRowState?.emp === emp;
                          return (
                            <div key={emp} style={{
                              height: PLAN_GRID_ROW_H, display: "flex", alignItems: "center", gap: 4, fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800,
                              overflow: "hidden", borderRadius: 10,
                              background: isDragging ? P.soft : "transparent",
                              boxShadow: isDragging ? `0 0 0 1.5px ${A.teal}` : "none",
                            }}>
                              {!planWeekReadOnly && (
                                <span
                                  onPointerDown={(e) => startRowDrag(emp, e)}
                                  onPointerMove={moveRowDrag}
                                  onPointerUp={endRowDrag}
                                  onPointerCancel={endRowDrag}
                                  onLostPointerCapture={endRowDrag}
                                  aria-label={`Réordonner ${emp}`}
                                  style={{
                                    flex: "0 0 auto", width: 16, height: 20, display: "grid", placeItems: "center",
                                    fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, touchAction: "none", cursor: "grab",
                                  }}
                                >⠿</span>
                              )}
                              <span style={{ width: 20, height: 20, borderRadius: 99, background: EMP_COLORS[emp] || A.navy, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>{emp.charAt(0)}</span>
                              {/* Retour utilisateur 2026-08-04 : rôle en haut (ex. FLA), nom civilité
                                  en bas (ex. "M. Dupont") — remplace l'ancien affichage prénom seul. */}
                              <span style={{ display: "flex", flexDirection: "column", overflow: "hidden", lineHeight: 1.2, minWidth: 0 }}>
                                <strong style={{ fontSize: `calc(7.5px * var(--nx-font-scale, 1))`, fontWeight: 900, color: P.muted, textTransform: "uppercase", letterSpacing: ".02em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{planEmpRole(emp) || "—"}</strong>
                                <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{planEmpDisplayLabel(emp)}</span>
                              </span>
                            </div>
                          );
                        })}
                        <div style={{ height: PLAN_GRID_FOOTER_H, display: "flex", alignItems: "center", fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>Total</div>
                      </div>

                      <div style={{ overflowX: "auto", overscrollBehaviorX: "contain", WebkitOverflowScrolling: "touch", flex: 1, minWidth: 0 }}>
                        {/* Ossature desktop Phase 2 : colonnes jour à largeur fixe 70px (avec
                            scroll horizontal forcé par minWidth) sur téléphone — inchangé. Sur
                            desktop, les 6 colonnes jour se répartissent l'espace disponible du
                            shell élargi (1fr) plutôt que de rester à 70px chacune avec du vide
                            à droite ; aucun calcul de geste ne dépend de cette largeur (seul
                            PLAN_GRID_ROW_H, une hauteur, est utilisé par le drag vertical des
                            lignes). */}
                        <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(6, 1fr) 56px 60px" : "repeat(6, 70px) 56px 60px", gap: PLAN_GRID_GAP, minWidth: isWide ? "auto" : 6 * 70 + 56 + 60 + 7 * PLAN_GRID_GAP }}>
                          {WEEK_LABELS_SHORT.map((_, i) => (
                            <div key={i} style={{ height: PLAN_GRID_HEADER_H, textAlign: "center", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>{weekLabelShort(i)}</div>
                          ))}
                          <div style={{ height: PLAN_GRID_HEADER_H, textAlign: "center", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>LF</div>
                          <div style={{ height: PLAN_GRID_HEADER_H, textAlign: "center", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>Total</div>
                          {dragPreviewTeam.map((emp) => {
                        const empId = resolveEmployeeId(emp);
                        const empShiftsAll = weekShifts.filter((s) => s.emp === emp);
                        const empRole = planEmpRole(emp);
                        const netTotal = empShiftsAll.reduce((sum, s) => sum + Math.max((s.hours || 0) - (s.leistungsfreiHours || 0), 0) * azubiNetFactor(s, empRole), 0);
                        const override = weekPlan?.totalOverrides?.[empId];
                        const displayTotal = (typeof override === "number" && Number.isFinite(override)) ? override : netTotal;
                        const lfTotal = empShiftsAll.reduce((sum, s) => sum + (s.leistungsfreiHours || 0), 0);
                        return (
                          <Fragment key={emp}>
                            {WEEK_LABELS.map((_, day) => {
                              const s = weekShifts.find((x) => x.day === day && x.emp === emp);
                              const meta = shiftStatusMeta(s);
                              const unavailable = isUnavailable(emp, day);
                              const keepsHours = s && shiftStatusKeepsHours(s.status);
                              return (
                                <Pressable key={day}
                                  onClick={planWeekReadOnly ? undefined : () => handleShiftCellTap(empId, day)}
                                  style={{
                                    position: "relative",
                                    borderRadius: 12, height: PLAN_GRID_ROW_H, padding: "4px 3px", textAlign: "center",
                                    background: s ? meta.bg : (unavailable ? "rgba(239,68,68,.06)" : P.soft),
                                    border: `1.5px solid ${s ? meta.fg : "transparent"}`,
                                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1,
                                  }}>
                                  {/* Repère discret triple-tap FL/FLA (2026-08-17) — case avec au
                                      moins une donnée forcée à la main (voir openShiftOverridePrompt),
                                      distincte visuellement mais sans gêner la lecture normale. */}
                                  {s && s.hoursOverride != null && (
                                    <span aria-hidden="true" style={{
                                      position: "absolute", top: 3, right: 3, width: 5, height: 5,
                                      borderRadius: 99, background: A.amber,
                                    }} />
                                  )}
                                  {s ? (
                                    <>
                                      <strong style={{ fontSize: `calc(8px * var(--nx-font-scale, 1))`, color: meta.fg, lineHeight: 1.15 }}>
                                        {keepsHours && s.start ? `${s.start}–${s.end}` : meta.badge}
                                      </strong>
                                      {keepsHours && pauseRangeLabel(s) && (
                                        <span style={{ fontSize: `calc(6.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.1 }}>P {pauseRangeLabel(s)}</span>
                                      )}
                                      {(keepsHours || (s.leistungsfreiHours || 0) > 0) && (
                                        <span style={{ display: "flex", alignItems: "baseline", gap: 3, lineHeight: 1.1 }}>
                                          {keepsHours && (() => {
                                            // Retour utilisateur 2026-08-17 : le rappel "8h → 4h Ist" affiché en
                                            // permanence sur chaque case Azubi ne laissait plus de place pour un
                                            // futur badge LF sur la même case — le calcul ×0,5 doit rester INTERNE
                                            // (déjà appliqué au Total hebdo), la case n'affiche que le résultat
                                            // final, jamais l'explication de la conversion. Remplace l'ancien
                                            // hint "→Xh Ist" (retour utilisateur 2026-08-05, désormais retiré).
                                            const isAzubiFactored = isAzubiRole(empRole) && s.status !== "BS+Arbeit";
                                            const displayHours = isAzubiFactored ? Math.round(shiftHours(s) * 0.5 * 100) / 100 : shiftHours(s);
                                            return <span style={{ fontSize: `calc(7.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink }}>{displayHours}h</span>;
                                          })()}
                                          {(s.leistungsfreiHours || 0) > 0 && (
                                            <span style={{ fontSize: `calc(7px * var(--nx-font-scale, 1))`, color: A.amber, fontWeight: 800 }}>LF {s.leistungsfreiHours}h</span>
                                          )}
                                        </span>
                                      )}
                                    </>
                                  ) : <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: unavailable ? A.red : P.muted }}>{unavailable ? "✕" : "＋"}</span>}
                                </Pressable>
                              );
                            })}
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                              {lfTotal > 0 ? `${lfTotal.toLocaleString("fr-FR")}h` : "–"}
                            </div>
                            <Pressable
                              onClick={planWeekReadOnly ? undefined : () => handleTotalCellTap(empId, netTotal)}
                              style={{
                                display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 10,
                                fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink,
                                background: typeof override === "number" ? "rgba(231,138,18,.14)" : P.soft,
                              }}>{Math.round(displayTotal * 100) / 100}h</Pressable>
                          </Fragment>
                        );
                      })}
                      {WEEK_LABELS.map((_, day) => {
                            const dayNet = weekShifts.filter((s) => s.day === day).reduce((sum, s) => sum + Math.max((s.hours || 0) - (s.leistungsfreiHours || 0), 0) * azubiNetFactor(s, s.role), 0);
                            return <div key={day} style={{ height: PLAN_GRID_FOOTER_H, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, textAlign: "center" }}>{Math.round(dayNet * 100) / 100}h</div>;
                          })}
                          <div style={{ height: PLAN_GRID_FOOTER_H }} />
                          <div style={{ height: PLAN_GRID_FOOTER_H, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, textAlign: "center", color: A.teal }}>{weekTotalHours}h</div>
                        </div>
                      </div>
                    </div>
                    <p style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, margin: "9px 2px 0" }}>{t("planning.gridCaption", "Vue synthétique — tape une case pour écrire directement le créneau, double-tape le Total pour le forcer manuellement. Pour plusieurs créneaux par personne le même jour, passe en vue Jours.")}</p>
                  </div>
                ) : (
                  <>
                  {/* Sélecteur d'employé ciblé par le "+" ci-dessous — sans lui, le bouton
                      devinait (retour utilisateur 2026-08-13 : tombait parfois sur un
                      Aushilfe indisponible ce jour-là, double-tap de confirmation requis).
                      "Auto" garde l'ancienne devinette (première personne sans créneau ce
                      jour, en évitant désormais les indisponibles quand c'est possible). */}
                  {!!planTeam.length && (
                    <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 12, paddingBottom: 2 }}>
                      <Pressable onClick={() => setPlanDayFocusEmp("")} style={{
                        flex: "0 0 auto", padding: "8px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                        background: !planDayFocusEmp ? A.teal : P.soft, color: !planDayFocusEmp ? "#fff" : P.ink,
                        border: `1.5px solid ${!planDayFocusEmp ? A.teal : P.line}`,
                      }}>{t("planning.dayFocus.auto", "Auto")}</Pressable>
                      {planTeam.map((emp) => {
                        const sel = planDayFocusEmp === emp;
                        return (
                          <Pressable key={emp} onClick={() => setPlanDayFocusEmp(emp)} style={{
                            flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6, padding: "7px 13px 7px 7px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                            background: sel ? A.teal : P.soft, color: sel ? "#fff" : P.ink,
                            border: `1.5px solid ${sel ? A.teal : P.line}`,
                          }}>
                            <span style={{ width: 18, height: 18, borderRadius: 99, background: sel ? "rgba(255,255,255,.3)" : (EMP_COLORS[emp] || A.navy), color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(8.5px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>{emp.charAt(0)}</span>
                            {emp}
                          </Pressable>
                        );
                      })}
                    </div>
                  )}
                  {WEEK_LABELS.map((_, day) => {
                    const label = weekLabel(day);
                    const dayShifts = weekShifts.filter((s) => s.day === day).sort((a, b) => (a.start || "").localeCompare(b.start || ""));
                    return (
                      <section key={day} style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 13, marginBottom: 9 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: dayShifts.length ? 8 : 0 }}>
                          <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{label}</strong>
                          {!planWeekReadOnly && (
                            <Pressable onClick={() => {
                              // Bug réel (retour utilisateur 2026-08-05) : ciblait toujours
                              // resolveEmployeeId(user?.firstname) — vide dès que le compte
                              // connecté (FL/FLA, GR...) n'a pas sa propre ligne dans
                              // taskEmployees, donc openShiftSheet(employeeId="", ...) sortait
                              // immédiatement (!emp) et le bouton ne faisait rien. On cible
                              // maintenant un vrai membre de l'équipe (le premier sans créneau
                              // ce jour, sinon le premier tout court) — la sheet permet ensuite
                              // de changer d'employé via ses chips. Priorité au choix explicite
                              // (planDayFocusEmp) ; sinon devinette qui évite les Aushilfen
                              // indisponibles ce jour-là (retour utilisateur 2026-08-13).
                              if (!planTeam.length) { showToast(t("toast.noTeamMemberYet", "Aucun employé dans l'équipe pour l'instant.")); return; }
                              const available = planTeam.filter((emp) => !dayShifts.some((s) => s.emp === emp));
                              const target = planDayFocusEmp
                                || available.find((emp) => !isUnavailable(emp, day)) || available[0]
                                || planTeam.find((emp) => !isUnavailable(emp, day)) || planTeam[0];
                              handleShiftCellTap(resolveEmployeeId(target), day);
                            }}
                              aria-label={t("planning.addShiftAriaTemplate", { day: label }, `Ajouter un créneau ${label}`)} style={{
                                width: 28, height: 28, borderRadius: 99, background: "rgba(13,148,136,.13)", color: A.teal,
                                display: "grid", placeItems: "center", fontSize: `calc(15px * var(--nx-font-scale, 1))`, fontWeight: 800,
                              }}>＋</Pressable>
                          )}
                        </div>
                        {dayShifts.length === 0 && <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("planning.noSlotText", "Aucun créneau")}</span>}
                        {dayShifts.map((s) => {
                          const mine = s.emp === user?.firstname;
                          const meta = shiftStatusMeta(s);
                          return (
                            <Pressable key={s.id} onClick={planWeekReadOnly ? undefined : () => handleShiftEntryTap(s)} style={{
                              display: "flex", alignItems: "center", gap: 10, padding: "10px 11px",
                              borderRadius: 13, marginBottom: 6, color: P.ink,
                              background: mine ? meta.bg : P.soft,
                              border: `1.5px solid ${mine ? meta.fg : "transparent"}`,
                            }}>
                              <span style={{
                                width: 26, height: 26, borderRadius: 99, background: meta.fg, color: "#fff",
                                display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, flex: "0 0 auto",
                              }}>{s.emp.charAt(0)}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{s.emp}{mine ? t("planning.youSuffix", " (toi)") : ""}</strong>
                                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>
                                  {SHIFT_STATUS_LABELS[s.status] || s.status}
                                  {shiftStatusKeepsHours(s.status) ? ` · ${pauseRangeLabel(s) ? `P ${pauseRangeLabel(s)}` : t("planning.noBreakSuffix", "sans pause")} · ${shiftHours(s)}h` : ""}
                                  {shiftStatusKeepsHours(s.status) && isAzubiRole(planEmpRole(s.emp)) && s.status !== "BS+Arbeit"
                                    ? ` · ${Math.round(shiftHours(s) * 0.5 * 100) / 100}h ${t("planning.azubiIstShortLabel", "Ist")}`
                                    : ""}
                                  {s.leistungsfreiHours ? <span style={{ color: A.amber, fontWeight: 800 }}> · LF {s.leistungsfreiHours}h</span> : ""}
                                </span>
                              </div>
                              {shiftStatusKeepsHours(s.status) && s.start && <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: meta.fg }}>{s.start} – {s.end}</span>}
                            </Pressable>
                          );
                        })}
                      </section>
                    );
                  })}
                  </>
                )}

                {/* --- DASHBOARD DE CONFORMITÉ : connecté à Stundenleistung (Reporting) --- */}
                <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 16, marginTop: 4 }}>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 11 }}>
                    <strong style={{ fontSize: `calc(14.5px * var(--nx-font-scale, 1))`}}>{t("planning.complianceSectionTitle", "Résumé & conformité")}</strong>
                    <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>KW {isoWeek(now) + planWeekOffset}</span>
                  </div>
                  {/* Soll (Stundenleistung) / Ist (plan) / Différence — comme sur le plan imprimé */}
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    {[
                      { v: `${weekSollHours} h`, l: t("planning.statSollWeek", "Soll semaine"), sub: "Stundenleistung", c: A.blue },
                      { v: `${weekTotalHours} h`, l: t("planning.statIstWeek", "Ist semaine"), sub: t("planning.statIstSub", "hors Leistungsfrei"), c: A.frost },
                      { v: `${weekDiffHours >= 0 ? "+" : ""}${weekDiffHours} h`, l: t("planning.statDiffWeek", "Différence"), sub: t("planning.statDiffSub", "Ist − Soll"), c: weekDiffHours >= 0 ? A.green : A.red },
                    ].map((s) => (
                      <div key={s.l} style={{ flex: 1, textAlign: "center", background: `${s.c}12`, border: `1px solid ${s.c}30`, borderRadius: 14, padding: "9px 4px" }}>
                        <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, display: "block", lineHeight: 1.1, color: s.c }}>{s.v}</strong>
                        <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".03em", display: "block" }}>{s.l}</span>
                        <span style={{ fontSize: `calc(8.5px * var(--nx-font-scale, 1))`, color: P.muted }}>({s.sub})</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8, marginBottom: weekCompliance.length ? 12 : 4 }}>
                    {[
                      { v: weekShifts.length, l: t("planning.statShifts", "Créneaux") },
                      { v: new Set(weekShifts.map((s) => s.emp)).size, l: t("planning.control.employees", "Employés") },
                    ].map((s) => (
                      <div key={s.l} style={{ flex: 1, textAlign: "center", background: P.soft, borderRadius: 14, padding: "9px 0" }}>
                        <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`, display: "block", lineHeight: 1.1 }}>{s.v}</strong>
                        <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".04em" }}>{s.l}</span>
                      </div>
                    ))}
                  </div>

                  {weekCompliance.length === 0 ? (
                    <div style={{ display: "flex", gap: 9, alignItems: "center", background: "rgba(30,184,95,.09)", border: "1px solid rgba(30,184,95,.22)", borderRadius: 14, padding: 12 }}>
                      <span style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>✓</span>
                      <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: "#15803d" }}>{t("planning.complianceOkText", "Aucune anomalie — durée, pauses et repos respectent les règles légales.")}</span>
                    </div>
                  ) : (
                    <>
                      <strong style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: A.red, display: "block", marginBottom: 8 }}>{t("planning.anomalyCountTemplate", { n: weekCompliance.length }, `⚠️ ${weekCompliance.length} anomalie${weekCompliance.length > 1 ? "s" : ""} à corriger`)}</strong>
                      {weekCompliance.map((v, i) => (
                        <div key={i} style={{ display: "flex", gap: 8, padding: "8px 10px", borderRadius: 12, background: "rgba(239,68,68,.08)", marginBottom: 6 }}>
                          <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{v.type === "duration" ? "⏰" : v.type === "break" ? "☕" : "🌙"}</span>
                          <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.ink, lineHeight: 1.4 }}>{v.msg}</span>
                        </div>
                      ))}
                    </>
                  )}

                  <p style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "11px 2px 0", lineHeight: 1.5 }}>
                    {t("planning.legalRulesText", "Règles : modèle par équipes obligatoire, repos ≥ 11h entre deux créneaux · journée < 10h ; pause 30 min dès 6h de travail (45 min au-delà de 9h), mineur : 30 min dès 4h30 (60 min au-delà de 6h), jamais après 20h · Azubi = heures comptées ×0,5 dans le Ist (sauf École + Travail, jamais compté).")}
                  </p>
                  <div style={{ display: "flex", gap: 8, marginTop: 9, background: "rgba(139,47,214,.07)", border: "1px solid rgba(139,47,214,.2)", borderRadius: 12, padding: 10 }}>
                    <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>ℹ️</span>
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.55 }}>
                      {t("planning.remindersText", "Rappels (informatifs) : le plan doit être publié au plus tard le vendredi de la semaine précédente. Pendant une grossesse : journée ≤ 8,5h, jamais avant 6h ni après 20h, ≤ 90h par quinzaine, jamais un service isolé entre deux jours de repos.")}
                    </span>
                  </div>

                  {!planWeekReadOnly && (
                    <>
                      <Pressable onClick={syncStundenleistung} style={{
                        marginTop: 13, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                        background: A.frost, color: "#fff", boxShadow: "0 10px 22px rgba(45,127,240,.32)",
                      }}>🔄 {t("planning.syncStundenleistungBtn", "Synchroniser vers Stundenleistung (Reporting)")}</Pressable>
                      {syncedHours[planWeekOffset] != null && (
                        <span style={{ display: "block", textAlign: "center", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 7, fontWeight: 700 }}>
                          {t("planning.lastSyncTemplate", { hours: syncedHours[planWeekOffset] }, `Dernière synchro : ${syncedHours[planWeekOffset]} h — visible dans le Dashboard`)}
                        </span>
                      )}
                    </>
                  )}
                </section>

                {!planWeekReadOnly && (
                  <>
                    {!canPublishPlanning() && !isPublished && (
                      <p style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, textAlign: "center", margin: "8px 2px 0" }}>{t("planning.publish.roleGuard", "Seuls les rôles FL ou FLA peuvent publier ce planning.")}</p>
                    )}
                    <Pressable
                      onClick={publishForcePending ? publishWeekForced : publishWeek}
                      disabled={isPublished || weekShifts.length === 0 || !canPublishPlanning() || weekCompliance.length > 0}
                      style={{
                        width: "100%", ...btnSolid(publishForcePending ? A.orange : A.teal), marginTop: 10,
                        opacity: isPublished || weekShifts.length === 0 || !canPublishPlanning() || weekCompliance.length > 0 ? .5 : 1,
                      }}>{isPublished ? t("planning.planPublishedBtn", "✓ Plan publié") : publishForcePending ? t("planning.forcePublishBtn", "⚠️ Forcer la publication") : t("planning.publishToTeamBtn", "📢 Publier le plan à l'équipe")}</Pressable>
                  </>
                )}
              </>
            )}

            {/* --- PLANUNGSANFRAGEN --- */}
            {planScreen === "requests" && (
              <>
                <Pressable onClick={() => { setReqForm({ type: "freiwunsch", date: "", endDate: "", comment: "" }); setSheet("newRequest"); }}
                  style={{ width: "100%", ...btnSolid(A.purple), marginBottom: 14 }}>＋ {t("planning.requests.newBtn", "Nouvelle demande")} (Urlaub / Frei)</Pressable>
                {planRequests.filter((r) => !r.deleted).length === 0 && (
                  <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("planning.requests.emptyListText", "Aucune demande pour l'instant.")}</p>
                )}
                <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(400px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
                {planRequests.filter((r) => !r.deleted).map((r, rIdx) => {
                  const mine = r.emp === user?.firstname;
                  const thread = r.messages || [];
                  const statusMeta = REQ_STATUS_META[r.status] || REQ_STATUS_META.pending;
                  return (
                    <div key={r.id} className="nx-list-item-in" style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 9, alignItems: "flex-start", animationDelay: `${Math.min(rIdx, 14) * 28}ms` }}>
                      <span style={{
                        flex: "0 0 auto", width: 42, height: 42, borderRadius: 14, fontSize: `calc(19px * var(--nx-font-scale, 1))`,
                        background: r.type === "urlaub" ? "rgba(231,138,18,.14)" : "rgba(13,148,136,.14)",
                        display: "grid", placeItems: "center",
                      }}>{r.type === "urlaub" ? "🏖️" : "🌿"}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{r.emp}{mine ? t("planning.youSuffix", " (toi)") : ""} · {r.type === "urlaub" ? "Urlaub" : "Freiwunsch"}</strong>
                        <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 2 }}>
                          {reqDateLabel(r)}{r.comment ? ` · ${r.comment}` : ""}
                        </div>
                        <div style={{ display: "flex", gap: 7, marginTop: 9, flexWrap: "wrap" }}>
                          {r.status === "pending" && !mine && (
                            <>
                              <Pressable onClick={() => approveRequest(r.id)} style={{
                                padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                                background: A.green, color: "#fff",
                              }}>✓ {t("planning.requests.approveBtn", "Approuver")}</Pressable>
                              <Pressable onClick={() => rejectRequest(r.id)} style={{
                                padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                                background: "rgba(239,68,68,.13)", color: A.red,
                              }}>{t("planning.requests.rejectBtn", "Refuser")}</Pressable>
                            </>
                          )}
                          {r.status === "pending" && mine && (
                            <Pressable onClick={() => cancelRequest(r.id)} style={{
                              padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                              background: "rgba(239,68,68,.13)", color: A.red,
                            }}>{t("planning.requests.cancelBtn", "Annuler la demande")}</Pressable>
                          )}
                          <Pressable onClick={() => openReqThread(r.id)} style={{
                            padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                            background: thread.length ? "rgba(54,79,231,.14)" : P.soft, color: thread.length ? A.blue : P.muted,
                          }}>💬 {thread.length ? t("planning.requests.discussBtnCount", { n: thread.length }, `Discuter (${thread.length})`) : t("planning.requests.discussBtn", "Discuter")}</Pressable>
                        </div>
                      </div>
                      <span style={{
                        fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 10px", whiteSpace: "nowrap",
                        background: statusMeta.bg, color: statusMeta.fg,
                      }}>{statusMeta.label}</span>
                    </div>
                  );
                })}
                </div>
              </>
            )}

            {/* --- RESTURLAUB : solde de jours, réservé FL/FLA côté serveur --- */}
            {planScreen === "vacation" && (
              <>
                {!canManageVacation ? (
                  <div style={{ display: "flex", gap: 10, alignItems: "center", background: "rgba(231,138,18,.09)", border: "1px solid rgba(231,138,18,.25)", borderRadius: 16, padding: 14 }}>
                    <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>🔒</span>
                    <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: "#b56a00", lineHeight: 1.5 }}>{t("planning.vacation.restrictedText", "Réservé aux responsables de filiale (FL/FLA) — même l'employé concerné n'a pas accès à son propre solde ici.")}</span>
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                      <Pressable onClick={() => openLedgerHistory("urlaub", null, null)} style={{
                        fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                        borderRadius: 99, padding: "9px 14px",
                      }}>📜 {t("planning.lfDetail.historyTitle", "Historique")}</Pressable>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(340px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
                    {planTeam.map((emp, empIdx) => {
                      const employeeId = resolveEmployeeId(emp);
                      const acc = vacationAccountFor(employeeId);
                      const left = vacationDaysLeft(employeeId);
                      const total = acc?.creditedDays || 0;
                      const p = total > 0 ? Math.round(((total - left) / total) * 100) : 0;
                      const c = EMP_COLORS[emp] || A.navy;
                      return (
                        <section key={emp} className="nx-list-item-in" style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 15, marginBottom: isWide ? 0 : 9, animationDelay: `${Math.min(empIdx, 14) * 28}ms` }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 9 }}>
                            <span style={{ width: 30, height: 30, borderRadius: 99, background: c, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800 }}>{emp.charAt(0)}</span>
                            <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, flex: 1 }}>{emp}{emp === user?.firstname ? t("planning.youSuffix", " (toi)") : ""}</strong>
                            <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, color: left <= 3 ? A.red : P.ink }}>{t("planning.vacation.daysLeftTemplate", { n: left }, `${left} j restants`)}</strong>
                          </div>
                          <div style={{ height: 9, borderRadius: 99, background: P.soft, overflow: "hidden" }}>
                            <div style={{ width: `${p}%`, height: "100%", borderRadius: 99, background: c }} />
                          </div>
                          <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 6, display: "block" }}>
                            {total > 0 ? t("planning.vacation.totalAttributedTemplate", { total, used: acc.usedDays }, `${total} j attribués au total, ${acc.usedDays} j déjà pris`) : t("planning.vacation.noneAttributedText", "Aucun jour attribué — utilise « Attribuer des jours ».")}
                          </span>
                          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                            <Pressable onClick={() => { setAllocForm({ employeeId, emp, amount: 0, mode: "credit" }); setSheet("allocVacation"); }} style={{
                              flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                              background: `${c}1a`, color: c,
                            }}>⚙️ {t("planning.vacation.allocateBtn", "Attribuer des jours")}</Pressable>
                            <Pressable onClick={() => { setAllocForm({ employeeId, emp, amount: 0, mode: "restore" }); setSheet("allocVacation"); }} style={{
                              flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                              background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                            }}>↩️ {t("planning.vacation.restoreBtn", "Rétablir des jours")}</Pressable>
                          </div>
                        </section>
                      );
                    })}
                    </div>
                  </>
                )}
              </>
            )}

            {/* --- LEISTUNGSFREI : compte crédit/débit en heures — le serveur s'auto-filtre sur
                 le compte de l'employé courant pour un non-manager (jamais celui des autres),
                 donc on n'itère planTeam que pour un manager ; sinon juste "toi". */}
            {planScreen === "leistungsfrei" && (
              <>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                  <Pressable onClick={() => openLedgerHistory("leistungsfrei", isManager(user) ? null : resolveEmployeeId(user?.firstname), isManager(user) ? null : user?.firstname)} style={{
                    fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                    borderRadius: 99, padding: "9px 14px",
                  }}>📜 {t("planning.lfDetail.historyTitle", "Historique")}</Pressable>
                </div>
                <p style={{ margin: "0 2px 12px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                  {t("planning.leistungsfrei.introText", "Compte d'heures crédité (heures supp.) et débité (heures utilisées) par employé.")}
                </p>
                <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(320px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
                {(isManager(user) ? planTeam : [user?.firstname]).map((emp, empIdx) => {
                  const employeeId = isManager(user) ? resolveEmployeeId(emp) : (user?.proId || user?.id);
                  const bal = lfBalance(employeeId);
                  return (
                    <div key={emp} className="nx-list-item-in" style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 8, animationDelay: `${Math.min(empIdx, 14) * 28}ms` }}>
                      <span style={{ width: 30, height: 30, borderRadius: 99, background: EMP_COLORS[emp] || A.navy, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, flex: "0 0 auto" }}>{emp.charAt(0)}</span>
                      <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, flex: 1 }}>{emp}{emp === user?.firstname ? t("planning.youSuffix", " (toi)") : ""}</strong>
                      <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, color: bal > 0 ? A.frost : bal < 0 ? A.red : P.muted }}>{bal.toLocaleString("fr-FR")} h</strong>
                      {canManageLf && (
                        <div style={{ display: "flex", gap: 6, marginLeft: 10 }}>
                          <Pressable onClick={() => { setLeiForm({ employeeId, emp, type: "credit", amount: 1, note: "" }); setSheet("leiEntry"); }} aria-label={t("planning.leistungsfrei.creditAria", "Créditer")} style={{
                            width: 30, height: 30, borderRadius: 99, background: "rgba(30,184,95,.14)", color: A.green,
                            display: "grid", placeItems: "center", fontSize: `calc(15px * var(--nx-font-scale, 1))`, fontWeight: 800,
                          }}>＋</Pressable>
                          <Pressable onClick={() => { setLeiForm({ employeeId, emp, type: "debit", amount: 1, note: "" }); setSheet("leiEntry"); }} aria-label={t("planning.leistungsfrei.debitAria", "Débiter")} style={{
                            width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.13)", color: A.red,
                            display: "grid", placeItems: "center", fontSize: `calc(15px * var(--nx-font-scale, 1))`, fontWeight: 800,
                          }}>−</Pressable>
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              </>
            )}

            {/* --- FEIERTAGE : jours fériés réels de la filiale + jours personnalisés --- */}
            {planScreen === "holidays" && (
              <>
                <p style={{ margin: "0 2px 12px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                  {t("planning.holidays.introText", "Jours fériés nationaux et régionaux calculés pour l'État (Bundesland) de la filiale.")}
                </p>
                <Pressable onClick={() => { setHolidayForm({ date: "", name: "" }); setSheet("newHoliday"); }} style={{
                  width: "100%", ...btnSolid(A.red), marginBottom: 14,
                }}>＋ {t("planning.holidays.addCustomBtn", "Ajouter un jour férié personnalisé")}</Pressable>
                {activeHolidays.length === 0 && <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("planning.holidays.emptyText", "Aucun jour férié à venir.")}</p>}
                <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(340px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
                {activeHolidays.map((h, hIdx) => {
                  const isCustom = h.source !== "auto";
                  return (
                    <div key={h.date + h.label} className="nx-list-item-in" style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 8, animationDelay: `${Math.min(hIdx, 14) * 28}ms` }}>
                      <span style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`}}>{isCustom ? "📌" : "🎌"}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{isCustom ? h.label : t(`planning.holidayName.${HOLIDAY_NAME_KEYS[h.label] || "unknown"}`, h.label)}</strong>
                        <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                          {fmtDay(h.date)} {fmtFR(h.date)} · dans {daysLeft(h.date)} j · {isCustom ? t("planning.holidays.addedTag", "ajouté") : h.land ? h.land : t("planning.holidays.nationalTag", "national")}
                        </div>
                      </div>
                      {isCustom && (
                        <Pressable onClick={() => deleteCustomHoliday(h.date)} aria-label={t("common.delete", "Supprimer")} style={{
                          width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.13)",
                          display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                        }}>🗑️</Pressable>
                      )}
                    </div>
                  );
                })}
                </div>
              </>
            )}
          </>
        )}

        {/* ================= PLAN DE TRAVAIL PUBLIÉ (historique) ================= */}
        {screen === "planning" && planScreen === "published" && (
          <div className="nx-screen-enter">
            <p style={{ margin: "0 2px 14px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
              {t("planning.published.introText", "Historique de tous les plans publiés — consulte et retrouve les heures de chaque semaine.")}
            </p>
            {publishedWeeksList.length === 0 && (
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 30 }}>
                <span style={{ fontSize: `calc(34px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>🗓️</span>
                <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`}}>{t("planning.published.emptyTitle", "Aucun plan publié pour l'instant.")}</strong>
                <p style={{ margin: "5px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("planning.published.emptyHint", "Publie une semaine depuis « Plan de travail » pour la voir apparaître ici.")}</p>
              </section>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
            {publishedWeeksList.map((h, hIdx) => (
              <Pressable key={h.id} feedback className="nx-list-item-in" onClick={() => openPublishedWeek(h.weekStart)} style={{ ...card, ...tile, padding: 15, marginBottom: isWide ? 0 : 9, alignItems: "flex-start", animationDelay: `${Math.min(hIdx, 14) * 28}ms` }}>
                <span style={{
                  flex: "0 0 auto", width: 44, height: 44, borderRadius: 15, background: "rgba(13,148,136,.13)",
                  display: "grid", placeItems: "center", fontSize: `calc(18px * var(--nx-font-scale, 1))`,
                }}>📢</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{h.weekKey || h.weekStart}</strong>
                  <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 2 }}>
                    {t("planning.published.publishedAtTimeTemplate", { date: fmtFR((h.publishedAt || "").slice(0, 10)), time: (h.publishedAt || "").slice(11, 16) }, `Publié le ${fmtFR((h.publishedAt || "").slice(0, 10))} à ${(h.publishedAt || "").slice(11, 16)}`)}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 7 }}>
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, background: P.soft, borderRadius: 99, padding: "3px 9px" }}>{h.totals?.hours ?? 0} h</span>
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, background: P.soft, borderRadius: 99, padding: "3px 9px" }}>{h.totals?.shifts ?? 0} {t("planning.shiftsCountSuffix", "créneaux")}</span>
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, background: P.soft, borderRadius: 99, padding: "3px 9px" }}>{h.totals?.employees ?? 0} {t("planning.employeesCountSuffix", "employés")}</span>
                  </div>
                </div>
                {canPublishPlanning() && (
                  <Pressable onClick={(e) => { e.stopPropagation(); deletePublishedWeek(h); }}
                    aria-label={t("planning.published.unpublishBtn", "Retirer de l'archive")} style={{
                      width: 32, height: 32, borderRadius: 99, background: "rgba(239,68,68,.13)", flex: "0 0 auto",
                      display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                    }}>🗑️</Pressable>
                )}
                <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted }}>›</span>
              </Pressable>
            ))}
            </div>
          </div>
        )}

        {/* ================= CALENDRIER ANNUEL (congés approuvés) — vraie vue calendrier
             mensuelle, tous les employés superposés sur la même grille, pas un résumé par
             employé. ================= */}
        {screen === "planning" && planScreen === "calendar" && (
          <>
            <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 12, paddingBottom: 2 }}>
              {[THIS_YEAR - 1, THIS_YEAR, THIS_YEAR + 1].map((y) => {
                const sel = calYear === y;
                return (
                  <Pressable key={y} onClick={() => setCalYear(y)} style={{
                    flex: "0 0 auto", padding: "9px 15px", borderRadius: 99, fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                    background: sel ? A.teal : P.soft, color: sel ? "#fff" : P.ink,
                    border: `1.5px solid ${sel ? A.teal : P.line}`,
                  }}>{y}</Pressable>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              {[{ id: "month", l: `🗓️ ${t("reporting.mois", "Mois")}` }, { id: "year", l: `📅 ${t("reporting.annee", "Année")}` }].map((v) => (
                <Pressable key={v.id} onClick={() => setCalViewMode(v.id)} style={{
                  flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: calViewMode === v.id ? A.orange : P.soft, color: calViewMode === v.id ? "#fff" : P.ink,
                  border: `1.5px solid ${calViewMode === v.id ? A.orange : P.line}`,
                }}>{v.l}</Pressable>
              ))}
            </div>

            {calViewMode === "month" ? (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <Pressable feedback onClick={() => changeCalMonth(-1)} aria-label={t("planning.calendar.prevMonthAria", "Mois précédent")} style={{
                    width: 36, height: 36, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`, fontWeight: 800,
                  }}>‹</Pressable>
                  <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{monthLabelShort(calMonth)} {calYear}</strong>
                  <Pressable feedback onClick={() => changeCalMonth(1)} aria-label={t("planning.calendar.nextMonthAria", "Mois suivant")} style={{
                    width: 36, height: 36, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`, fontWeight: 800,
                  }}>›</Pressable>
                </div>

                {canPublishPlanning() && (
                  <p style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, margin: "-6px 2px 10px", lineHeight: 1.4 }}>
                    {t("planning.calendar.flHintText", "💡 En tant que FL/FLA, tape un jour à venir pour poser directement ton Urlaub.")}
                  </p>
                )}

                <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 12, marginBottom: 14 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3, marginBottom: 6 }}>
                    {[0, 1, 2, 3, 4, 5].map((d) => weekLabelShort(d)).concat(t("weekday.short.sun", "Di")).map((l) => (
                      <div key={l} style={{ textAlign: "center", fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>{l}</div>
                    ))}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3 }}>
                    {/* FL/FLA peuvent poser directement leur propre Urlaub/Frei en tapant un
                        jour à venir — réutilise la vraie sheet "Nouvelle demande" (même
                        auto-approbation instantanée que Planungsanfragen), jamais une
                        logique séparée. Jours passés et employés non-manager restent en
                        simple consultation. */}
                    {calMonthDays.map((day, i) => {
                      const vacs = day ? vacationsOnDay(day) : [];
                      const iso = day ? calDayISO(day) : null;
                      const isToday = iso === todayISO();
                      const clickable = day && canPublishPlanning() && iso >= todayISO();
                      const CellTag = clickable ? Pressable : "div";
                      return (
                        <CellTag key={i}
                          {...(clickable ? { onClick: () => { setReqForm({ type: "urlaub", date: iso, endDate: iso, comment: "" }); setSheet("newRequest"); } } : {})}
                          style={{
                            minHeight: 46, borderRadius: 10, padding: "4px 2px", position: "relative",
                            background: isToday ? "rgba(13,148,136,.12)" : "transparent",
                            border: `1.5px solid ${isToday ? A.teal : "transparent"}`,
                            display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                          }}>
                          {day && <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: isToday ? 800 : 700, color: P.ink }}>{day}</span>}
                          {vacs.slice(0, 3).map((r) => (
                            <span key={r.id} title={r.emp} style={{
                              width: 14, height: 14, borderRadius: 99, background: EMP_COLORS[r.emp] || A.navy, color: "#fff",
                              fontSize: `calc(7px * var(--nx-font-scale, 1))`, fontWeight: 800, display: "grid", placeItems: "center", flex: "0 0 auto",
                            }}>{r.emp.charAt(0)}</span>
                          ))}
                          {vacs.length > 3 && <span style={{ fontSize: `calc(7px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 800 }}>+{vacs.length - 3}</span>}
                          {clickable && vacs.length === 0 && (
                            <span style={{ position: "absolute", top: 2, right: 3, fontSize: `calc(8px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 800 }}>+</span>
                          )}
                        </CellTag>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 }}>
                {Array.from({ length: 12 }, (_, m) => monthLabelShort(m)).map((mLabel, m) => {
                  const days = monthGridDays(calYear, m);
                  return (
                    <Pressable key={mLabel} onClick={() => { setCalMonth(m); setCalViewMode("month"); }} style={{
                      ...card, flexDirection: "column", alignItems: "stretch", padding: 8,
                      border: `1.5px solid ${m === new Date().getMonth() && calYear === new Date().getFullYear() ? A.teal : P.line}`,
                    }}>
                      <strong style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, textAlign: "center", display: "block", marginBottom: 4 }}>{mLabel.replace(".", "")}</strong>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}>
                        {days.map((day, i) => {
                          const hasVac = monthHasVacation(calYear, m, day);
                          return (
                            <div key={i} style={{
                              aspectRatio: "1", borderRadius: 2, display: "grid", placeItems: "center",
                              background: hasVac ? A.orange : "transparent",
                            }}>
                              {day && <span style={{ fontSize: `calc(5.5px * var(--nx-font-scale, 1))`, fontWeight: hasVac ? 800 : 600, color: hasVac ? "#fff" : P.muted }}>{day}</span>}
                            </div>
                          );
                        })}
                      </div>
                    </Pressable>
                  );
                })}
              </div>
            )}

            <div style={{ display: "flex", flexWrap: "wrap", gap: 9, marginBottom: 14 }}>
              {planTeam.map((emp) => (
                <span key={emp} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                  <span style={{ width: 9, height: 9, borderRadius: 99, background: EMP_COLORS[emp] || A.navy, flex: "0 0 auto" }} />
                  {emp}
                </span>
              ))}
            </div>

            <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", margin: "0 2px 8px" }}>
              {calViewMode === "month" ? t("planning.calendar.vacationsOfMonthTemplate", { month: monthLabelShort(calMonth).replace(".", "") }, `Congés de ${monthLabelShort(calMonth).replace(".", "")}`) : t("planning.calendar.vacationsOfYearTemplate", { year: calYear }, `Congés ${calYear}`)}
            </strong>
            {(calViewMode === "month" ? calMonthApproved : yearApproved).length === 0 && (
              <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{calViewMode === "month" ? t("planning.calendar.noneThisMonth", "Aucun congé ce mois-ci.") : t("planning.calendar.noneThisYear", "Aucun congé cette année.")}</p>
            )}
            {(calViewMode === "month" ? calMonthApproved : [...yearApproved].sort((a, b) => a.startDate.localeCompare(b.startDate))).map((r) => (
              <div key={r.id} style={{ ...card, ...tile, padding: 12, marginBottom: 7 }}>
                <span style={{
                  width: 26, height: 26, borderRadius: 99, background: EMP_COLORS[r.emp] || A.navy, color: "#fff",
                  display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, flex: "0 0 auto",
                }}>{r.emp.charAt(0)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{r.emp}{r.emp === user?.firstname ? t("planning.youSuffix", " (toi)") : ""}</strong>
                  <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>
                    🏖️ {r.startDate === r.endDate ? fmtFR(r.startDate) : `${fmtFR(r.startDate)} → ${fmtFR(r.endDate)}`}{r.comment ? ` · ${r.comment}` : ""}
                  </div>
                </div>
                {canPublishPlanning() && (
                  <Pressable onClick={() => deleteApprovedVacation(r)} aria-label={t("planning.calendar.deleteAria", "Supprimer ce congé")} style={{
                    width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.1)", color: A.red,
                    display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                  }}>🗑️</Pressable>
                )}
              </div>
            ))}
          </>
        )}

        {/* ================= AUSHILFE-VERFÜGBARKEIT ================= */}
        {screen === "planning" && planScreen === "aushilfe" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 13 }}>
              <Pressable feedback onClick={() => setAvailWeekOffset((o) => o - 1)} aria-label={t("planning.control.prevWeek", "Semaine précédente")} style={{
                width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
              }}>‹</Pressable>
              <div style={{ flex: 1, textAlign: "center" }}>
                <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`}}>KW {isoWeek(now) + availWeekOffset}</strong>
              </div>
              <Pressable feedback onClick={() => setAvailWeekOffset((o) => o + 1)} aria-label={t("planning.control.nextWeek", "Semaine suivante")} style={{
                width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
              }}>›</Pressable>
            </div>

            {isAushilfe ? (
              <>
                <p style={{ margin: "0 2px 14px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                  {t("planning.aushilfe.introOwnText", "Déclare tes disponibilités pour cette semaine — modifiable jusqu'au mercredi 12:00, utile pour composer le plan sans te solliciter à côté.")}
                </p>
                {availabilityEditLocked(mondayOfWeekOffset(availWeekOffset)) && (
                  <p style={{ display: "flex", alignItems: "center", gap: 8, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: "#b56a00", background: "rgba(231,138,18,.09)", border: "1px solid rgba(231,138,18,.25)", borderRadius: 14, padding: "10px 12px", marginBottom: 13 }}>
                    <span>🔒</span> {t("planning.aushilfe.lockedBannerText", "Semaine verrouillée — modifications possibles jusqu'au mercredi 12:00 de cette semaine.")}
                  </p>
                )}
                {ownAvailDays && WEEK_LABELS.map((_, day) => {
                  const label = weekLabel(day);
                  const key = PLAN_DAY_KEYS[day];
                  const d = ownAvailDays[key] || { mode: "unavailable", ranges: [] };
                  const state = d.mode;
                  return (
                    <Pressable key={day} onClick={() => { setAvailDayEdit({ day }); setSheet("availEdit"); }} style={{
                      display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                      padding: "10px 11px", borderRadius: 13, marginBottom: 6, color: P.ink,
                      background: state === "full" ? "rgba(30,184,95,.09)" : state === "custom" ? "rgba(13,148,136,.09)" : "rgba(239,68,68,.08)",
                    }}>
                      <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{state === "full" ? "🟢" : state === "custom" ? "🕐" : "🔴"}</span>
                      <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, flex: 1 }}>{label}</strong>
                      <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>
                        {state === "full" ? t("planning.aushilfe.fullDayShort", "Toute la journée") : state === "custom" ? (d.ranges[0] ? `${d.ranges[0].start}–${d.ranges[0].end}` : t("planning.aushilfe.preciseHoursShort", "Heures précises")) : t("planning.aushilfe.unavailableShort", "Non disponible")}
                      </span>
                      <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>›</span>
                    </Pressable>
                  );
                })}
                <div style={{ display: "flex", gap: 8, marginTop: 13 }}>
                  <Pressable onClick={copyPreviousAvailability} style={{
                    flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                    background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                  }}>⤵ {t("planning.aushilfe.copyPreviousBtnShort", "Copier semaine précédente")}</Pressable>
                  <Pressable onClick={openAvailClearChoice} style={{
                    flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                    background: "rgba(239,68,68,.1)", border: "1.5px solid rgba(239,68,68,.3)", color: A.red,
                  }}>🗑️ {t("planning.aushilfe.clearWeekBtnShort", "Vider la semaine")}</Pressable>
                </div>
                <Pressable onClick={saveOwnAvailability} style={{
                  width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
                  background: A.teal, color: "#fff", marginTop: 9,
                }}>{t("common.save", "Enregistrer")}</Pressable>
              </>
            ) : canManageAushilfeAvailability ? (
              <>
                <p style={{ margin: "0 2px 14px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                  {t("planning.aushilfe.managerIntroText", "Disponibilités déclarées par les Aushilfen, jour par jour — consultable uniquement ici.")}
                </p>
                {availabilityList.length === 0 && (
                  <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("planning.aushilfe.emptyListText", "Aucune disponibilité déclarée pour cette semaine.")}</p>
                )}
                {availabilityList.map((entry) => (
                  <section key={entry.id} style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 15, marginBottom: 9 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 11 }}>
                      <span style={{ width: 30, height: 30, borderRadius: 99, background: EMP_COLORS[entry.employeeName?.split(" ")[0]] || A.navy, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800 }}>{(entry.employeeName || "?").charAt(0)}</span>
                      <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, flex: 1 }}>{entry.employeeName}</strong>
                    </div>
                    {WEEK_LABELS.map((_, day) => {
                      const label = weekLabel(day);
                      const key = PLAN_DAY_KEYS[day];
                      const d = entry.days?.[key] || { mode: "unavailable", ranges: [] };
                      const state = d.mode;
                      return (
                        <div key={day} style={{
                          display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                          padding: "10px 11px", borderRadius: 13, marginBottom: 6, color: P.ink,
                          background: state === "full" ? "rgba(30,184,95,.09)" : state === "custom" ? "rgba(13,148,136,.09)" : "rgba(239,68,68,.08)",
                        }}>
                          <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{state === "full" ? "🟢" : state === "custom" ? "🕐" : "🔴"}</span>
                          <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, flex: 1 }}>{label}</strong>
                          <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>
                            {state === "full" ? t("planning.aushilfe.fullDayShort", "Toute la journée") : state === "custom" ? (d.ranges[0] ? `${d.ranges[0].start}–${d.ranges[0].end}` : t("planning.aushilfe.preciseHoursShort", "Heures précises")) : t("planning.aushilfe.unavailableShort", "Non disponible")}
                          </span>
                        </div>
                      );
                    })}
                  </section>
                ))}
              </>
            ) : (
              <div style={{ display: "flex", gap: 10, alignItems: "center", background: "rgba(231,138,18,.09)", border: "1px solid rgba(231,138,18,.25)", borderRadius: 16, padding: 14 }}>
                <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>🔒</span>
                <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: "#b56a00", lineHeight: 1.5 }}>{t("planning.aushilfe.restrictedText", "Réservé aux Aushilfen (leur propre disponibilité) et aux responsables de filiale.")}</span>
              </div>
            )}
          </>
        )}

        {/* ================= TÂCHES =================
             Toujours pas de choix "Missions réseau" ici (retour utilisateur explicite,
             2026-08-02) — Missions réseau vit exclusivement dans son propre écran dédié
             (screen==="missions"), taskScope reste "store" partout ci-dessous.
             Plus de page "hub" séparée (retour utilisateur explicite, 2026-08-07 : "il y a
             une page tâche filiale qui apparaît souvent, supprime-la et affecte son contenu
             aux différentes sections") — les 4 catégories (mine/assigned/team/delegated, voir
             TASK_FILTER_PREDICATES) sont des chips de sélection directement en haut de
             l'écran liste, jamais une page intermédiaire à traverser. L'icône du module
             (accueil) atterrit directement sur la liste, catégorie "mine" par défaut. Les
             deep-links (notification, "Assistant du jour") continuent d'utiliser
             taskFilter=null (vue non filtrée, aucune chip sélectionnée) puisqu'ils pointent
             déjà vers une tâche précise plutôt que vers une catégorie.
             Tuile "Historique" SUPPRIMÉE (retour utilisateur explicite, 2026-08-07 : une fois
             que les 4 autres catégories ont chacune leurs 3 onglets de statut, Historique
             faisait doublon avec "To Do"/"Confiées" — seul le cas "mes propres tâches équipe"
             n'avait nulle part où vivre, puisque "Tâches de l'équipe" les exclut exprès de sa
             propre vue. Ce cas vit maintenant DANS "Tâches de l'équipe" via teamSubView
             ("Reçues"/"Créées par moi"), pas dans une tuile séparée. */}
        {screen === "tasks" && tasksScreen === "list" && (
          <>
            {/* Sélecteur de catégorie — remplace l'ancienne page hub (voir commentaire
                ci-dessus). Chips compactes, toujours visibles en haut de la liste.
                paddingTop + marginTop négatif compensant (retour utilisateur 2026-08-28) :
                overflowX:"auto" sans overflowY explicite force implicitement overflowY:"auto"
                aussi (règle CSS Overflow — un axe non-visible force l'autre à le devenir),
                donc le badge notification qui dépasse en haut du chip (top:-6) se faisait
                rogner par CE conteneur. On lui réserve la place plutôt que de changer
                overflowY (impossible à repasser à "visible" tant que overflowX est "auto",
                le navigateur l'ignorerait silencieusement). */}
            <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginTop: -8, marginBottom: 14, paddingTop: 8, paddingBottom: 2 }}>
              {[
                { id: "mine", c: A.green, emoji: "🙋", l: t("tasks.hub.mine.title", "To Do liste personnelle") },
                { id: "assigned", c: A.orange, emoji: "📥", l: t("tasks.hub.assigned.title", "Mes tâches") },
                { id: "team", c: A.blue, emoji: "👥", l: t("tasks.hub.team.title", "Tâches de l'équipe") },
                { id: "delegated", c: A.purple, emoji: "📤", l: t("tasks.hub.delegated.title", "Tâches confiées") },
              ].map((x) => {
                const sel = taskFilter === x.id;
                return (
                  <Pressable key={x.id} onClick={() => { setTaskFilter(x.id); setTaskTab("open"); setTeamSubView("received"); setTaskSectionsOpen({ done: false, archived: false }); }} style={{
                    position: "relative", flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                    background: sel ? x.c : P.soft, color: sel ? "#fff" : P.ink,
                    border: `1.5px solid ${sel ? x.c : P.line}`,
                  }}>
                    <EmojiIcon e={x.emoji} />
                    {x.l}
                    {/* Badge notification (retour utilisateur 2026-08-28, artefact "Badge de
                        tâches" validé) — remplace la pastille discrète collée au texte, qui se
                        remarquait mal sur un onglet non sélectionné. Même patron déjà établi
                        ailleurs dans l'app (ex. Krankmeldung "Mes déclarations", nx-notif-dot) :
                        détaché en haut à droite du chip, jamais fondu dans le flux du texte. */}
                    {taskFilterCounts[x.id] > 0 && (
                      <span className="nx-notif-dot" style={{
                        position: "absolute", top: -6, right: -6, minWidth: 20, height: 20, padding: "0 5px", borderRadius: 99,
                        background: A.red, border: `2px solid ${P.bg}`, color: "#fff", fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800,
                        display: "grid", placeItems: "center",
                      }}>{taskFilterCounts[x.id]}</span>
                    )}
                  </Pressable>
                );
              })}
            </div>

            {/* Second niveau, "Tâches de l'équipe" seulement — remplace la tuile "Historique"
                pour le seul cas qu'elle couvrait sans doublon (retour utilisateur explicite,
                2026-08-07). */}
            {taskFilter === "team" && (
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                {[
                  { id: "received", l: t("tasks.team.receivedTab", "Reçues"), n: taskFilterCounts.team },
                  { id: "mine", l: t("tasks.team.mineTab", "Créées par moi"), n: taskFilterCounts.teamMine },
                ].map((s) => (
                  <Pressable key={s.id} onClick={() => { setTeamSubView(s.id); setTaskTab("open"); setTaskSectionsOpen({ done: false, archived: false }); }} style={{
                    flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                    background: teamSubView === s.id ? A.blue : P.soft, color: teamSubView === s.id ? "#fff" : P.ink,
                    border: `1.5px solid ${teamSubView === s.id ? A.blue : P.line}`,
                  }}>{s.l}{s.n > 0 ? ` · ${s.n}` : ""}</Pressable>
                ))}
              </div>
            )}

            {/* Design porté depuis l'artefact "Tableau de service" (retour utilisateur
                explicite, implémentation à 100%) — remplace les 3 onglets pleine-largeur qui
                forçaient à choisir UNE SEULE vue à la fois. "En cours" reste toujours affichée ;
                Terminées/Archivées sont repliées par défaut (juste l'en-tête + compteur),
                cliquables pour se déplier, sans jamais être pré-affichées sur la page. Aucune
                tâche n'y perd de fonctionnalité : mêmes prédicats, mêmes actions, même contrôle
                de création (jamais sur "Mes tâches" reçues). */}
            {openTasks.length === 0 && (
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 28 }}>
                <span style={{ fontSize: `calc(32px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>🎉</span>
                <strong style={{ fontSize: `calc(14.5px * var(--nx-font-scale, 1))`}}>{t("tasks.emptyOpenText", "Rien en cours — tout est fait.")}</strong>
              </section>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
              {openTasks.map(renderTaskCard)}
            </div>

            {doneTasks.length > 0 && (
              <>
                <Pressable onClick={() => setTaskSectionsOpen((s) => ({ ...s, done: !s.done }))} style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 4px",
                  fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".06em",
                }}>
                  <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, width: 9 }}>{taskSectionsOpen.done ? "▾" : "▸"}</span>
                  {t("tasks.tabDone", "Terminées")}
                  <span style={{ background: P.soft, borderRadius: 99, padding: "1px 8px", color: P.ink }}>{doneTasks.length}</span>
                </Pressable>
                {taskSectionsOpen.done && (
                  <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
                    {doneTasks.map(renderTaskCard)}
                  </div>
                )}
              </>
            )}

            {archivedTasks.length > 0 && (
              <>
                <Pressable onClick={() => setTaskSectionsOpen((s) => ({ ...s, archived: !s.archived }))} style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 4px", marginTop: doneTasks.length > 0 ? 0 : 8,
                  fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".06em",
                }}>
                  <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, width: 9 }}>{taskSectionsOpen.archived ? "▾" : "▸"}</span>
                  {t("trash.title", "Archives")}
                  <span style={{ background: P.soft, borderRadius: 99, padding: "1px 8px", color: P.ink }}>{archivedTasks.length}</span>
                </Pressable>
                {taskSectionsOpen.archived && (
                  <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
                    {archivedTasks.map(renderTaskCard)}
                  </div>
                )}
              </>
            )}

            {/* "+" flottant — même patron que le FAB de l'artefact (position fixe, toujours
                accessible, au-dessus de la barre de navigation réelle). "Mes tâches" (assigned)
                n'a jamais de bouton de création — ce sont des tâches reçues, jamais créées
                depuis cette tuile (retour utilisateur explicite, 2026-08-07). */}
            {taskFilter !== "assigned" && (
              <Pressable onClick={() => {
                setTaskForm(presetFormForTile(taskFilter));
                setTaskQueue([]);
                loadTaskEmployees();
                setTasksScreen("new");
              }} aria-label={t("tasks.newTaskLabel", "Nouvelle tâche")} style={{
                position: "fixed", right: 18, bottom: "calc(92px + env(safe-area-inset-bottom))", zIndex: 33,
                width: 58, height: 58, borderRadius: 20, color: "#fff",
                display: "grid", placeItems: "center", fontSize: `calc(30px * var(--nx-font-scale, 1))`, fontWeight: 300,
                ...premiumTileStyle(A.green),
              }}>＋</Pressable>
            )}
          </>
        )}

        {/* "Nouvelle tâche" — Sheet (popup), plus une page séparée (retour utilisateur
            explicite, en référence à l'artefact "Tableau de service" : "le clic sur + dans
            l'artefact ouvre un popup mais dans l'app une page, c'est pas correct"). Même
            contenu/logique qu'avant à l'identique (type, panier, destinataire, cuisson, PLU,
            date/priorité, note, enregistrement) — seul le CONTENEUR change (Sheet flottante
            plutôt qu'un écran plein avec son propre en-tête). Position:fixed du composant
            Sheet échappe déjà au flux de <main> (même principe que le "+" flottant ou la
            barre d'action fixe ailleurs dans l'app), donc pas besoin de la déplacer hors de
            ce bloc pour qu'elle s'affiche correctement par-dessus tout le reste. */}
        <Sheet open={screen === "tasks" && tasksScreen === "new"} onClose={() => { setTasksScreen("list"); setTaskQueue([]); }} P={P}>
          <SheetHead kicker={t("tasks.hub.title", "Organisation")} title={t("tasks.newTaskLabel", "Nouvelle tâche")} onClose={() => { setTasksScreen("list"); setTaskQueue([]); }} />
          {screen === "tasks" && tasksScreen === "new" && (
            <>
            {/* Indicateur visible de la destination réelle de la tâche — ajouté après un bug
                réel rapporté 2 fois le même jour (2026-08-07) : le destinataire est déterminé
                par taskFilter (état ambiant, invisible une fois sur l'écran "Nouvelle tâche"),
                et peut rester sur "team" d'une visite précédente (ex. routé automatiquement
                par le popup "Tâches à faire" quand aucune tâche perso n'était en cours) sans
                que l'utilisateur s'en rende compte avant de valider. Ce bandeau rend la
                destination certaine AVANT la création, plutôt qu'après coup dans la liste. */}
            {taskFilter !== null && (
              <div style={{
                display: "flex", alignItems: "center", gap: 8, padding: "10px 13px", borderRadius: 12,
                background: P.soft, border: `1.5px solid ${P.line}`, marginBottom: 14,
              }}>
                <span style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>{taskFilter === "mine" ? "👤" : taskFilter === "team" ? "👥" : "➡️"}</span>
                <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink }}>
                  {t("tasks.creatingForLabel", "Créée pour :")}{" "}
                  {taskFilter === "mine" ? t("tasks.filter.mineTitle", "To Do liste personnelle")
                    : taskFilter === "team" ? t("tasks.filter.teamTitle", "Tâches de l'équipe")
                    : t("tasks.filter.delegatedTitle", "Tâches confiées")}
                </span>
              </div>
            )}
            {/* Panier — entrées déjà ajoutées via "+ Ajouter à la liste", de types
                éventuellement différents (standard/cuisson/plu), chacune avec sa propre
                assignation pour "confiée" (retour utilisateur explicite, 2026-08-07). Le
                formulaire ci-dessous compose toujours la PROCHAINE entrée, jamais une
                entrée déjà dans le panier — pour la modifier, la retirer puis la refaire. */}
            {taskQueue.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 6 }}>
                  {t("tasks.queueHeadingTemplate", { n: taskQueue.length }, `Dans cette liste (${taskQueue.length})`)}
                </span>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  {taskQueue.map((item) => (
                    <div key={item.id} style={{
                      display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 12,
                      background: P.soft, border: `1.5px solid ${P.line}`,
                    }}>
                      <span style={{ flex: 1, minWidth: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</span>
                      <Pressable onClick={() => removeQueuedTask(item.id)} aria-label={t("tasks.removeFromQueueAria", "Retirer de la liste")} style={{
                        width: 28, height: 28, borderRadius: 99, background: "rgba(239,68,68,.13)", color: A.red,
                        display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                      }}>✕</Pressable>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Types disponibles selon la tuile d'origine (retour utilisateur explicite,
                2026-08-06) : "équipe"/"mes tâches" jamais d'examen PLU (un GR ne fait jamais
                partie d'une liste de personnes qui doit passer un PLU) ; "confiée" et le
                formulaire générique (taskFilter===null) gardent les 3 types. */}
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 6 }}>{t("tasks.taskTypeLabel", "Type de tâche")}</span>
            <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
              {(taskFilter === "team" || taskFilter === "mine" ? ["standard", "bakery"] : ["standard", "bakery", "pluExam"]).map((tp) => {
                const sel = taskForm.type === tp;
                return (
                  <Pressable key={tp} onClick={() => setTaskForm({ ...taskForm, type: tp, recipient: tp === "pluExam" && taskFilter === null ? "specific" : taskForm.recipient })} style={{
                    flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                    background: sel ? A.green : P.soft, color: sel ? "#fff" : P.ink,
                    border: `1.5px solid ${sel ? A.green : P.line}`,
                  }}>{typeMeta[tp].emoji} {typeMeta[tp].label}</Pressable>
                );
              })}
            </div>

            {/* Le choix "équipe entière / employé spécifique" ne s'affiche que pour le
                formulaire générique hors-tuile (taskFilter===null, ex. deep-link) — depuis
                une tuile, la tuile détermine déjà tout : "équipe" force recipient=team,
                "mes tâches" force recipient=specific sur soi-même, "confiée" force
                recipient=specific avec sélection directe des employés juste en dessous
                (retour utilisateur explicite, 2026-08-06 : plus de libellé "Employé
                spécifique" à choisir, on sélectionne directement). Une mission réseau
                (taskScope==="network") part vers l'équipe de CHAQUE filiale ciblée — cibler
                un employé précis n'a pas de sens cross-filiales, sélecteur masqué aussi. */}
            {taskFilter === null && taskScope !== "network" && !(taskForm.type === "bakery" && taskForm.bakeryMode === "personal") && (
              <>
                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 6 }}>{t("tasks.recipientLabel", "Destinataire")}</span>
                <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 12, paddingBottom: 2 }}>
                  {(taskForm.type === "pluExam" ? [{ id: "specific", l: t("tasks.specificEmployeeBtn", "Employé spécifique") }] : [{ id: "team", l: t("tasks.allEmployeesBtn", "Tous les employés") }, { id: "specific", l: t("tasks.specificEmployeeBtn", "Employé spécifique") }]).map((r) => {
                    const sel = taskForm.recipient === r.id;
                    return (
                      <Pressable key={r.id} onClick={() => setTaskForm({ ...taskForm, recipient: r.id })} style={{
                        flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                        background: sel ? A.blue : P.soft, color: sel ? "#fff" : P.ink,
                        border: `1.5px solid ${sel ? A.blue : P.line}`,
                      }}>{r.l}</Pressable>
                    );
                  })}
                </div>
              </>
            )}
            {taskForm.recipient === "specific" && taskFilter !== "mine" && (
              <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
                {/* Vraie équipe filiale (GET /api/tasks/store-employees) — comparée par
                    prénom pour rester compatible avec resolveEmployeeId. Depuis la tuile
                    "confiée", sélection MULTIPLE (une tâche par employé choisi, voir
                    submitTask) ; sinon mono-sélection comme avant. Un GR n'apparaît jamais
                    dans cette liste pour un examen PLU (retour utilisateur explicite,
                    2026-08-06 — un responsable réseau ne passe jamais de PLU). */}
                {taskEmployees.filter((e) => taskForm.type !== "pluExam" || !NETWORK_LEADER_TASK_ROLES.includes(e.role)).map((e) => {
                  const first = e.name.split(" ")[0];
                  const isMulti = taskFilter === "delegated";
                  const sel = isMulti ? taskForm.assignees.includes(first) : taskForm.assignee === first;
                  const c = EMP_COLORS[first] || A.navy;
                  return (
                    <Pressable key={e.employeeId} onClick={() => setTaskForm({
                      ...taskForm,
                      ...(isMulti
                        ? { assignees: taskForm.assignees.includes(first) ? taskForm.assignees.filter((n) => n !== first) : [...taskForm.assignees, first] }
                        : { assignee: first }),
                    })} style={{
                      flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                      background: sel ? c : P.soft, color: sel ? "#fff" : P.ink,
                      border: `1.5px solid ${sel ? c : P.line}`,
                    }}>{first}</Pressable>
                  );
                })}
              </div>
            )}

            {taskForm.type === "pluExam" && (
              <div style={{ ...card, borderRadius: 18, padding: 14, marginBottom: 14 }}>
                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 6 }}>{t("tasks.pluListHeading", "Liste PLU")}</strong>
                {pluActiveList ? (
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("tasks.pluListWillBeUsedTemplate", { label: pluActiveList.label || `KW ${pluActiveList.kw}/${pluActiveList.year}` }, `${pluActiveList.label || `KW ${pluActiveList.kw}/${pluActiveList.year}`} sera utilisée — c'est la seule liste active pour l'instant.`)}</span>
                ) : (
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: A.red }}>{t("tasks.pluNoActiveListWarning", "⚠️ Aucune liste PLU active — active une liste dans PLU Quiz > Administration avant de créer cette tâche.")}</span>
                )}
              </div>
            )}

            {taskForm.type === "standard" && (
              <div style={{ ...card, borderRadius: 18, padding: 14, marginBottom: 14 }}>
                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 10 }}>{t("tasks.contentHeading", "Contenu de la tâche")}</strong>
                {taskForm.lines.map((l, i) => (
                  <div key={l.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                    <input style={{ ...inputStyle, flex: 1, padding: "10px 12px" }} placeholder={i === 0 ? t("tasks.lineFirstPlaceholder", "Ex. Ranger la réserve") : t("tasks.lineExtraPlaceholder", "Ligne supplémentaire...")}
                      value={l.title} onChange={(e) => updateTaskLine(l.id, e.target.value)} />
                    {taskForm.lines.length > 1 && (
                      <Pressable onClick={() => removeTaskLine(l.id)} aria-label={t("tasks.removeLineAria", "Retirer la ligne")} style={{
                        width: 36, height: 36, borderRadius: 99, background: "rgba(239,68,68,.13)", color: A.red,
                        display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                      }}>✕</Pressable>
                    )}
                  </div>
                ))}
                <Pressable onClick={addTaskLine} style={{
                  width: "100%", textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: P.soft, border: `1.5px dashed ${P.line}`, color: P.muted,
                }}>＋ {t("tasks.addLineBtn", "Ajouter une ligne")}</Pressable>
              </div>
            )}

            {taskForm.type === "bakery" && (
              <div style={{ ...card, borderRadius: 18, padding: 14, marginBottom: 14, flexDirection: "column", alignItems: "stretch" }}>
                {/* "Pour moi" n'a pas de sens depuis "Tâches de l'équipe" (déjà, par
                    définition, pour toute l'équipe) ; "Équipe" n'a pas de sens depuis "To Do
                    liste personnelle" (déjà, par définition, exclusivement pour moi). Et
                    depuis "Tâches confiées", NI L'UN NI L'AUTRE n'a plus de sens non plus
                    (retour utilisateur explicite, 2026-08-07, après coup) : le destinataire y
                    est déjà déterminé par la sélection multi-employés directe juste en dessous
                    (taskForm.assignees), jamais par "toute l'équipe" ni "moi-même" — ce choix
                    est un reliquat d'avant le modèle multi-employés de "confiée". Ne reste donc
                    affiché que pour le formulaire générique hors-tuile (taskFilter===null,
                    seul contexte où recipient est encore "team" OU "specific" au sens large). */}
                {taskFilter === null && (
                  <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    {[{ id: "team", l: t("tasks.bakeryTeamModeShort", "Équipe") }, { id: "personal", l: t("tasks.forMeBtn", "Pour moi") }].map((m) => (
                      <Pressable key={m.id} onClick={() => setTaskForm({ ...taskForm, bakeryMode: m.id })} style={{
                        flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                        background: taskForm.bakeryMode === m.id ? A.green : P.soft, color: taskForm.bakeryMode === m.id ? "#fff" : P.ink,
                        border: `1.5px solid ${taskForm.bakeryMode === m.id ? A.green : P.line}`,
                      }}>{m.l}</Pressable>
                    ))}
                  </div>
                )}
                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 3 }}>{t("tasks.articlesToBakeHeading", "Articles à cuire")}</strong>
                <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginBottom: 10 }}>
                  {taskForm.bakeryMode === "personal" ? t("tasks.bakeryPersonalHint", "Ta propre liste, sans notification ni échéance forcée.") : t("tasks.bakeryTeamHint", "Liste synchronisée avec Reporting (famille Prebake, articles actifs).")}
                </span>
                {taskForm.bakeryMode === "team" && (
                  <p style={{ margin: "0 0 10px", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: A.red, fontWeight: 700 }}>{t("tasks.bakeryTeamUrgentNote", "⚡ Priorité immédiate · échéance automatique 15 min après l'envoi.")}</p>
                )}
                {catalogArticlesFor("Prebake").length === 0 && (
                  <p style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 4px" }}>{t("tasks.noPrebakeArticleShort", "Aucun article Prebake actif — vérifie le Catalogue (Reporting).")}</p>
                )}
                <div style={{ maxHeight: 320, overflowY: "auto", margin: "0 -2px" }}>
                  {catalogArticlesFor("Prebake").map((a) => {
                    const qty = taskForm.bakeryQty[a.id] || 0;
                    return (
                      <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 2px", borderTop: `1px solid ${P.line}` }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block" }}>{a.name}</strong>
                          {a.program && <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>{a.program}</span>}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", borderRadius: 99, background: P.soft, border: `1.5px solid ${qty > 0 ? A.green : P.line}`, overflow: "hidden" }}>
                          <HoldButton onTap={() => { setBakeryQty(a.id, -1); }} onRepeat={() => setBakeryQty(a.id, -1)} label={t("common.decreaseHoldAria", "Moins (maintenir pour accélérer)")} style={{ width: 54, height: 54, display: "grid", placeItems: "center", fontSize: `calc(23px * var(--nx-font-scale, 1))`, fontWeight: 800, background: "none" }}>−</HoldButton>
                          <strong style={{ minWidth: 26, textAlign: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{qty}</strong>
                          <HoldButton onTap={() => { setBakeryQty(a.id, 1); }} onRepeat={() => setBakeryQty(a.id, 1)} label={t("common.increaseHoldAria", "Plus (maintenir pour accélérer)")} style={{ width: 54, height: 54, display: "grid", placeItems: "center", fontSize: `calc(23px * var(--nx-font-scale, 1))`, fontWeight: 800, background: "none" }}>＋</HoldButton>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {taskForm.type !== "bakery" && !taskForm.immediate && (
              <>
                <Field label={t("tasks.deadlineDateLabel", "Date limite")}>
                  <input style={dateTimeInputStyle} type="date" min={todayISO()} value={taskForm.date}
                    onChange={(e) => setTaskForm({ ...taskForm, date: e.target.value })} />
                </Field>
                <Field label={t("tasks.deadlineTimeLabel", "Heure")}>
                  {/* TimeSelect (voir son commentaire, Planning) au lieu du picker natif —
                      retour utilisateur 2026-08-28 : "pas de 15 minutes... au lieu de montrer
                      toutes les minutes comme dans Planning" (Planning l'a déjà, jamais un
                      second système). */}
                  <TimeSelect value={taskForm.time}
                    onChange={(v) => setTaskForm({ ...taskForm, time: v })} />
                </Field>
              </>
            )}

            {taskForm.type !== "bakery" && (
              <>
                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 6 }}>{t("tasks.priorityLabelField", "Priorité")}</span>
                <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 12, paddingBottom: 2 }}>
                  {["normal", "high", "urgent"].map((p) => {
                    const sel = taskForm.priority === p && !taskForm.immediate;
                    const pm = priorityMeta[p];
                    return (
                      <Pressable key={p} onClick={() => setTaskForm({ ...taskForm, priority: p, immediate: false })} style={{
                        flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                        background: sel ? pm.color : P.soft, color: sel ? "#fff" : P.ink,
                        border: `1.5px solid ${sel ? pm.color : P.line}`,
                      }}>{pm.label}</Pressable>
                    );
                  })}
                </div>

                <Pressable onClick={() => setTaskForm({ ...taskForm, immediate: !taskForm.immediate })} style={{
                  display: "flex", gap: 11, alignItems: "flex-start", width: "100%", boxSizing: "border-box",
                  padding: 13, borderRadius: 16, marginBottom: 15,
                  background: taskForm.immediate ? "rgba(239,68,68,.1)" : P.soft,
                  border: `1.5px solid ${taskForm.immediate ? A.red : P.line}`,
                }}>
                  <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>{taskForm.immediate ? "⚡" : "○"}</span>
                  <div>
                    <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: taskForm.immediate ? A.red : P.ink, display: "block" }}>{t("tasks.immediatePriorityTitle", "Priorité immédiate")}</strong>
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.4 }}>
                      {t("tasks.immediatePriorityTextShort", "Échéance automatique : 15 minutes après l'envoi. Rappel automatique si pas encore marquée accomplie.")}
                    </span>
                  </div>
                </Pressable>
              </>
            )}

            <Field label={t("tasks.noteOptionalLabel", "Remarque (facultatif)")}>
              <input style={inputStyle} placeholder={t("tasks.notePlaceholder", "Précision pour l'équipe...")} value={taskForm.note}
                onChange={(e) => setTaskForm({ ...taskForm, note: e.target.value })} />
            </Field>

            {/* "+ Ajouter à la liste" — met de côté le formulaire actuel (figé, requêtes déjà
                calculées) et en repropose un vierge pour composer un autre type (standard/
                cuisson/plu) avant un seul enregistrement groupé (retour utilisateur
                explicite, 2026-08-07 : "je crée des tâches standard, je vais dans cuisson je
                crée une tâche et dans plu une tâche... et j'enregistre tout en une fois"). */}
            <Pressable onClick={queueCurrentTask} style={{
              width: "100%", textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
              background: "rgba(54,79,231,.09)", border: `1.5px solid ${A.blue}`, color: A.blue, marginTop: 4,
            }}>
              ➕ {t("tasks.addToQueueBtn", "Ajouter à la liste (un autre type)")}
            </Pressable>
            <Pressable onClick={submitTask} style={{ width: "100%", ...btnSolid(A.green), marginTop: 10 }}>
              {taskQueue.length > 0
                ? t("tasks.saveQueueBtnTemplate", { n: taskQueue.length + (taskFormIsFilled(taskForm) ? 1 : 0) }, `✓ Enregistrer tout (${taskQueue.length + (taskFormIsFilled(taskForm) ? 1 : 0)})`)
                : taskForm.immediate ? t("tasks.sendImmediateBtn", "⚡ Envoyer en priorité immédiate") : t("tasks.createTaskBtn", "Créer la tâche")}
            </Pressable>
            </>
          )}
        </Sheet>

        {/* ================= HACCP ================= */}
        {screen === "haccp" && haccpScreen === "hub" && (
          <>
            {haccpViewDate !== todayISO() && (
              <div style={{ display: "flex", alignItems: "center", gap: 9, borderRadius: 14, padding: 12, marginBottom: 14, background: "rgba(231,138,18,.1)", border: "1px solid rgba(231,138,18,.28)" }}>
                <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>📅</span>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: "#b56a00", lineHeight: 1.5, flex: 1 }}>{t("haccp.viewingDateTemplate", { date: fmtFR(haccpViewDate) }, `Tu consultes le ${fmtFR(haccpViewDate)}, pas aujourd'hui.`)}</span>
                <Pressable onClick={() => openHaccpDay(todayISO())} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.orange, background: "#fff", borderRadius: 99, padding: "6px 11px", flexShrink: 0 }}>{t("haccp.today", "Aujourd'hui")}</Pressable>
              </div>
            )}
            {haccpStatus !== "open" && (
              <div style={{ display: "flex", gap: 9, borderRadius: 14, padding: 12, marginBottom: 14, background: "rgba(54,79,231,.08)", border: "1px solid rgba(54,79,231,.22)" }}>
                <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>ℹ️</span>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.ink, lineHeight: 1.5 }}>{haccpStatus === "closed_sunday" ? t("haccp.closedSundayFullText", "Magasin fermé (dimanche) — aucune saisie nécessaire ce jour-là.") : t("haccp.closedHolidayFullText", "Jour férié — aucune saisie nécessaire ce jour-là.")}</span>
              </div>
            )}
            <section style={{ ...card, ...tile, justifyContent: "space-between", padding: 19 }}>
              <div style={{ minWidth: 0 }}>
                <span style={eyebrow(P.muted)}>{haccpViewDate === todayISO() ? t("haccp.today", "Aujourd'hui") : fmtFR(haccpViewDate)}</span>
                <h2 style={{ margin: "4px 0 4px", fontSize: `calc(19px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em" }}>{t("haccp.countOfTotalTemplate", { done: dailyFilledCount, total: allDailyItems.length }, `${dailyFilledCount} sur ${allDailyItems.length} contrôles`)}</h2>
                <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.4 }}>
                  {haccpRecord?.validation ? t("haccp.validatedAtByTemplate", { time: haccpRecord.validation.validatedAt.slice(11, 16), name: haccpRecord.validation.validatedByName }, `Validée à ${haccpRecord.validation.validatedAt.slice(11, 16)} par ${haccpRecord.validation.validatedByName}`) :
                    haccpAnomalyCount > 0 ? t("haccp.heroAnomalyCount", { n: haccpAnomalyCount }, `${haccpAnomalyCount} anomalie${haccpAnomalyCount > 1 ? "s" : ""} signalée${haccpAnomalyCount > 1 ? "s" : ""}`) :
                    t("haccp.checkEachZoneText", "Contrôle chaque rayon dans l'ordre.")}
                </p>
              </div>
              <Ring pct={dailyPct} P={P} />
            </section>

            {/* Grille de zones façon "contrôle éclair" (remplace l'accordéon) : tap = ouvre la
                sheet haccpZone avec le flux pas-à-pas. Même carte colorée (colorCard/style
                HACCP_GROUP_STYLE) et même badge nx-notif-dot que le reste de l'app — jamais
                une palette inventée. */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
              {haccpConfig.dailyGroups.map((g) => {
                const style = HACCP_GROUP_STYLE[g.id] || { emoji: "🧪", color: A.navy };
                const complete = groupComplete(g);
                const remaining = g.items.length - groupFilledCount(g);
                return (
                  <Pressable key={g.id} onClick={() => openHaccpZone(g)} style={{
                    aspectRatio: "1", borderRadius: 18, position: "relative", overflow: "hidden", boxSizing: "border-box",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5, padding: 6,
                    background: `linear-gradient(155deg, rgba(255,255,255,.18), rgba(255,255,255,0) 45%), ${style.color}`,
                    border: "1px solid rgba(255,255,255,.14)",
                    boxShadow: `0 3px 8px rgba(0,0,0,.10), 0 10px 20px -8px ${style.color}99`,
                  }}>
                    <EmojiIcon e={style.emoji} size={22} />
                    <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", textAlign: "center", lineHeight: 1.15 }}>{t(`haccp.group.${g.id}.label`, g.label)}</span>
                    {complete ? (
                      <span style={{
                        position: "absolute", top: 6, right: 6, width: 16, height: 16, borderRadius: 99,
                        background: "rgba(255,255,255,.9)", color: style.color, fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 900,
                        display: "grid", placeItems: "center",
                      }}>✓</span>
                    ) : (
                      <span className="nx-notif-dot" style={{
                        position: "absolute", top: 5, right: 5, minWidth: 16, height: 16, padding: "0 4px", borderRadius: 99,
                        background: A.red, border: `2px solid ${style.color}`, color: "#fff", fontSize: `calc(8px * var(--nx-font-scale, 1))`, fontWeight: 800,
                        display: "grid", placeItems: "center",
                      }}>{remaining}</span>
                    )}
                  </Pressable>
                );
              })}
            </div>

            {haccpRemainingChecks.length > 0 && haccpConfig.capabilities.fill && haccpStatus === "open" && (
              <Pressable onClick={() => setSheet("haccpQuickChecks")} style={{ ...colorCard(A.orange), ...tile, padding: 15 }}>
                <span style={onColorChip}>⚡</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", color: "#fff" }}>{t("haccp.quickChecksBtn", "Valider les contrôles simples")}</strong>
                  <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, ...onColorSub }}>
                    {t("haccp.quickChecksSub", { n: haccpRemainingChecks.length }, `${haccpRemainingChecks.length} point${haccpRemainingChecks.length > 1 ? "s" : ""} restant${haccpRemainingChecks.length > 1 ? "s" : ""}`)}
                  </span>
                </div>
                <span style={{ marginLeft: "auto", color: "rgba(255,255,255,.8)", fontSize: `calc(18px * var(--nx-font-scale, 1))`, fontWeight: 300 }}>›</span>
              </Pressable>
            )}

            {dailyFilledCount === allDailyItems.length && allDailyItems.length > 0 && !haccpRecord?.validation && haccpConfig.capabilities.validateDaily && (
              <Pressable onClick={validateDaily} style={{ width: "100%", ...btnSolid(A.green), marginBottom: 14 }}>✓ {t("haccp.validateDayBtn", "Valider la journée")}</Pressable>
            )}

            <Pressable onClick={() => setHaccpScreen("weekly")} style={{ ...colorCard(A.teal), ...tile, padding: 17, marginBottom: 10 }}>
              <div style={onColorChip}><EmojiIcon e="🗓️" /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={onColorTag}>{t("haccp.weeklyTagShort", "Hebdomadaire")}</span>
                <h2 style={{ margin: "3px 0 3px", fontSize: `calc(16.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{t("haccp.weeklyControlsTitle", "Contrôles hebdomadaires")}</h2>
                <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, lineHeight: 1.35, ...onColorSub }}>{t("haccp.weeklyCardProgressTemplate", { done: weeklyFilledCount, total: allWeeklyItems.length }, `${weeklyFilledCount}/${allWeeklyItems.length} faits`)}{haccpWeeklyRecord?.validation ? t("haccp.validatedSuffix", " · validée") : ""}</p>
              </div>
              <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(19px * var(--nx-font-scale, 1))`, fontWeight: 300 }}>›</span>
            </Pressable>

            <Pressable onClick={openHaccpMonth} style={{ ...colorCard(A.navy), ...tile, padding: 17 }}>
              <div style={onColorChip}><EmojiIcon e="📅" /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={onColorTag}>{t("haccp.calendarTagShort", "Calendrier")}</span>
                <h2 style={{ margin: "3px 0 3px", fontSize: `calc(16.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{t("haccp.monthViewTitle", "Vue mensuelle")}</h2>
                <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, lineHeight: 1.35, ...onColorSub }}>{t("haccp.monthlyViewDesc", "Consulter, corriger et imprimer tout le mois.")}</p>
              </div>
              <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(19px * var(--nx-font-scale, 1))`, fontWeight: 300 }}>›</span>
            </Pressable>
          </>
        )}

        {screen === "haccp" && haccpScreen === "weekly" && (
          <>
            <section style={{ ...card, ...tile, justifyContent: "space-between", padding: 19 }}>
              <div style={{ minWidth: 0 }}>
                <span style={eyebrow(P.muted)}>{t("haccp.thisWeekEyebrow", "Cette semaine")}</span>
                <h2 style={{ margin: "4px 0 4px", fontSize: `calc(19px * var(--nx-font-scale, 1))`}}>{t("haccp.countOfTotalTemplate", { done: weeklyFilledCount, total: allWeeklyItems.length }, `${weeklyFilledCount} sur ${allWeeklyItems.length}`)}</h2>
                <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                  {haccpWeeklyRecord?.validation ? t("haccp.validatedAtByTemplate", { time: haccpWeeklyRecord.validation.validatedAt.slice(11, 16), name: haccpWeeklyRecord.validation.validatedByName }, `Validée à ${haccpWeeklyRecord.validation.validatedAt.slice(11, 16)} par ${haccpWeeklyRecord.validation.validatedByName}`) : t("haccp.weeklyChecksOnceText", "Contrôles à faire une fois par semaine.")}
                </p>
              </div>
              <Ring pct={weeklyPct} P={P} />
            </section>
            {/* Grille de zones façon "contrôle éclair" (même patron que le quotidien) — tous les
                items hebdo sont de simples "check" (aucune température), donc pas de clavier
                ici, juste le flux Fait/Anomalie pas-à-pas. */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginBottom: 16 }}>
              {haccpConfig.weeklyGroups.map((g) => {
                const style = HACCP_GROUP_STYLE[g.id] || { emoji: "🧪", color: A.navy };
                const complete = weeklyGroupComplete(g);
                const remaining = g.items.length - weeklyGroupFilledCount(g);
                return (
                  <Pressable key={g.id} onClick={() => openHaccpWeeklyZone(g)} style={{
                    aspectRatio: "1.6", borderRadius: 18, position: "relative", overflow: "hidden", boxSizing: "border-box",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: 10,
                    background: `linear-gradient(155deg, rgba(255,255,255,.18), rgba(255,255,255,0) 45%), ${style.color}`,
                    border: "1px solid rgba(255,255,255,.14)",
                    boxShadow: `0 3px 8px rgba(0,0,0,.10), 0 10px 20px -8px ${style.color}99`,
                  }}>
                    <EmojiIcon e={style.emoji} size={24} />
                    <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", textAlign: "center", lineHeight: 1.2 }}>{t(`haccp.group.${g.id}.label`, g.label)}</span>
                    {complete ? (
                      <span style={{
                        position: "absolute", top: 8, right: 8, width: 18, height: 18, borderRadius: 99,
                        background: "rgba(255,255,255,.9)", color: style.color, fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 900,
                        display: "grid", placeItems: "center",
                      }}>✓</span>
                    ) : (
                      <span className="nx-notif-dot" style={{
                        position: "absolute", top: 7, right: 7, minWidth: 18, height: 18, padding: "0 4px", borderRadius: 99,
                        background: A.red, border: `2px solid ${style.color}`, color: "#fff", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800,
                        display: "grid", placeItems: "center",
                      }}>{remaining}</span>
                    )}
                  </Pressable>
                );
              })}
            </div>

            {haccpRemainingWeeklyChecks.length > 0 && haccpCanValidateWeekly && (
              <Pressable onClick={() => setSheet("haccpWeeklyQuickChecks")} style={{ ...colorCard(A.orange), ...tile, padding: 15 }}>
                <span style={onColorChip}>⚡</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", color: "#fff" }}>{t("haccp.quickChecksBtn", "Valider les contrôles simples")}</strong>
                  <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, ...onColorSub }}>
                    {t("haccp.quickChecksSub", { n: haccpRemainingWeeklyChecks.length }, `${haccpRemainingWeeklyChecks.length} point${haccpRemainingWeeklyChecks.length > 1 ? "s" : ""} restant${haccpRemainingWeeklyChecks.length > 1 ? "s" : ""}`)}
                  </span>
                </div>
                <span style={{ marginLeft: "auto", color: "rgba(255,255,255,.8)", fontSize: `calc(18px * var(--nx-font-scale, 1))`, fontWeight: 300 }}>›</span>
              </Pressable>
            )}

            {weeklyFilledCount === allWeeklyItems.length && allWeeklyItems.length > 0 && !haccpWeeklyRecord?.validation && haccpConfig.capabilities.validateWeekly && (
              <Pressable onClick={validateWeekly} style={{ width: "100%", ...btnSolid(A.teal) }}>✓ {t("haccp.validateWeekBtn", "Valider la semaine")}</Pressable>
            )}
          </>
        )}

        {screen === "haccp" && haccpScreen === "month" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <Pressable onClick={() => changeHaccpMonth(-1)} style={{ width: 38, height: 38, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>‹</Pressable>
              <strong style={{ flex: 1, textAlign: "center", fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>
                {t(`haccp.month.${haccpMonthNum}`, ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"][haccpMonthNum - 1])} {haccpMonthYear}
              </strong>
              <Pressable onClick={() => changeHaccpMonth(1)} style={{ width: 38, height: 38, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>›</Pressable>
            </div>

            {!haccpMonthData ? (
              <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{t("haccp.loadingText", "Chargement...")}</p>
            ) : (
              <>
                <strong style={{ display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 8px" }}>{t("haccp.daysHeading", "Jours")}</strong>
                {haccpMonthData.days.map((day) => {
                  const d = new Date(day.date + "T00:00:00");
                  const dow = t(`haccp.dow.${d.getDay()}`, ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"][d.getDay()]);
                  const closed = day.status !== "open";
                  const tag = closed
                    ? { l: day.status === "closed_sunday" ? t("haccp.statusClosedSunday", "Fermé") : t("haccp.statusClosedHoliday", "Férié"), bg: P.soft, c: P.muted }
                    : day.validated ? { l: `✓ ${t("haccp.statusValidated", "Validé")}`, bg: A.green, c: "#fff" }
                    : !day.filled ? { l: t("haccp.statusNotStarted", "Non commencé"), bg: P.soft, c: P.muted }
                    : day.missingCount > 0 ? { l: t("haccp.missingCount", { n: day.missingCount }, `${day.missingCount} manquant${day.missingCount > 1 ? "s" : ""}`), bg: "rgba(231,138,18,.16)", c: "#b56a00" }
                    : { l: t("haccp.statusComplete", "Complet"), bg: "rgba(30,184,95,.16)", c: "#15803d" };
                  return (
                    <Pressable key={day.date} onClick={() => openHaccpDay(day.date)} style={{
                      ...card, ...tile, padding: 12, marginBottom: 7, opacity: closed ? .6 : 1,
                    }}>
                      <div style={{ width: 40, textAlign: "center", flexShrink: 0 }}>
                        <strong style={{ display: "block", fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{d.getDate()}</strong>
                        <span style={{ display: "block", fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{dow}</span>
                      </div>
                      <span style={{ flex: 1, fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "5px 10px", background: tag.bg, color: tag.c, textAlign: "center" }}>{tag.l}</span>
                      <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, color: P.muted }}>›</span>
                    </Pressable>
                  );
                })}

                <strong style={{ display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "16px 2px 8px" }}>{t("haccp.weeksLabel", "Semaines")}</strong>
                {haccpMonthData.weeks.map((week) => {
                  const anyOpenDay = haccpMonthData.days.find((d) => d.week === week.week && d.status === "open");
                  const done = 8 - week.missingCount;
                  const tag = week.validated ? { l: `✓ ${t("haccp.weekStatusValidatedFem", "Validée")}`, bg: A.green, c: "#fff" }
                    : done === 0 ? { l: t("haccp.weekStatusNotStarted", "Non commencée"), bg: P.soft, c: P.muted }
                    : { l: `${done}/8`, bg: done === 8 ? "rgba(30,184,95,.16)" : "rgba(231,138,18,.16)", c: done === 8 ? "#15803d" : "#b56a00" };
                  return (
                    <Pressable key={week.week} onClick={() => anyOpenDay && openHaccpDay(anyOpenDay.date)} style={{ ...card, ...tile, padding: 12, marginBottom: 7, opacity: anyOpenDay ? 1 : .5 }}>
                      <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>🛡️</span>
                      <strong style={{ flex: 1, fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{t("haccp.weekNumberLabel", { n: week.week }, `Semaine ${week.week}`)}</strong>
                      <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "5px 10px", background: tag.bg, color: tag.c }}>{tag.l}</span>
                      <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, color: P.muted }}>›</span>
                    </Pressable>
                  );
                })}
              </>
            )}
          </>
        )}

        {/* ================= RÉCLAMATIONS ================= */}
        {screen === "claims" && claimsScreen === "hub" && (
          <>
            <section style={{ ...colorCard(A.red), ...tile, flexDirection: "column", alignItems: "stretch", padding: 19 }}>
              <span style={onColorTag}>{t("claims.deliveryTagShort", "Livraison")}</span>
              <h2 style={{ margin: "4px 0 4px", fontSize: `calc(18px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{t("claims.hub.title", "Un défaut de livraison à signaler ?")}</h2>
              <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, lineHeight: 1.4, ...onColorSub }}>{t("claims.hub.desc", "Déclare rapidement les articles concernés — traçable, envoyé à la centrale.")}</p>
            </section>
            {/* Retour utilisateur explicite (2026-08-19) : "quand on sort de la page tout est
                supprimé" — ce bouton remettait TOUJOURS le brouillon à zéro, même en y
                revenant juste après avoir ajouté un article (retour à l'accueil puis re-clic
                ici). Ne réinitialise plus que s'il n'y a PAS déjà un brouillon en cours —
                reprend le brouillon existant sinon, jamais de perte silencieuse de saisie
                (même logique que le reste de l'app, voir Plan de travail). submitClaim() reste
                le seul autre endroit qui vide draftClaimItems, une fois la réclamation
                réellement envoyée. */}
            <Pressable onClick={() => { if (draftClaimItems.length === 0) setDraftClaimType("fresh"); setClaimsScreen("new"); }} style={{ ...colorCard(A.red), ...tile, padding: 16 }}>
              <div style={onColorChip}><EmojiIcon e="📝" /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={onColorTag}>{t("claims.menu.newTag", "Créer")}</span>
                <div style={{ margin: "2px 0 2px", fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff" }}>{t("claims.menu.new", "Nouvelle réclamation")}</div>
                <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("claims.card.new.desc", "Fruits & légumes ou Food / Non-Food — articles, défaut, photos.")}</div>
              </div>
              <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(17px * var(--nx-font-scale, 1))`, fontWeight: 300 }}>›</span>
            </Pressable>
            <Pressable onClick={() => { setClaimsHistoryTab("open"); setClaimsScreen("history"); }} style={{ ...colorCard(A.blue), ...tile, padding: 16 }}>
              <div style={onColorChip}><EmojiIcon e="🗂️" /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={onColorTag}>{t("claims.menu.historyTag", "Suivi")}</span>
                <div style={{ margin: "2px 0 2px", fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff" }}>{t("claims.menu.history", "Historique")}</div>
                <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("claims.card.history.desc", "Réclamations ouvertes, envoyées, traitées — et corbeille.")}</div>
              </div>
              {openClaimsCount > 0 && <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: "rgba(255,255,255,.28)", borderRadius: 99, padding: "4px 9px" }}>{openClaimsCount}</span>}
            </Pressable>
            <Pressable onClick={() => setClaimsScreen("settings")} style={{ ...card, ...tile, padding: 16 }}>
              <div style={{ flex: "0 0 auto", width: 46, height: 46, borderRadius: 16, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(21px * var(--nx-font-scale, 1))`}}>📮</div>
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: `calc(14.5px * var(--nx-font-scale, 1))`}}>{t("claims.menu.settings", "Destination d'envoi")}</strong>
                <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("claims.card.settings.desc", "E-mail, fax ou autre — modifiable à tout moment.")}</div>
              </div>
              <span style={{ color: P.muted, fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>›</span>
            </Pressable>
          </>
        )}

        {screen === "claims" && claimsScreen === "new" && (
          <>
            <div className="nx-chips" style={{ display: "flex", gap: 8, marginBottom: 15 }}>
              {[{ id: "fresh", l: `🍎 ${t("claims.type.fresh", "Fruits & légumes")}` }, { id: "food", l: `📦 ${t("claims.type.food", "Food / Non-Food")}` }].map((tp) => (
                <Pressable key={tp.id} onClick={() => setDraftClaimType(tp.id)} style={{
                  flex: 1, textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                  background: draftClaimType === tp.id ? A.red : P.soft, color: draftClaimType === tp.id ? "#fff" : P.ink,
                  border: `1.5px solid ${draftClaimType === tp.id ? A.red : P.line}`,
                }}>{tp.l}</Pressable>
              ))}
            </div>

            {draftClaimItems.length > 0 && (
              <section style={{ ...colorCard(A.red), ...tile, flexDirection: "column", alignItems: "stretch", padding: "15px 18px" }}>
                <div style={{ display: "flex", gap: 8 }}>
                  {[
                    { v: draftClaimItems.length, l: t("claims.new.statArticles", "Articles") },
                    { v: new Set(draftClaimItems.map((it) => it.defect).filter(Boolean)).size, l: t("claims.new.statDefects", "Défauts différents") },
                  ].map((s) => (
                    <div key={s.l} style={{ flex: 1, textAlign: "center", background: "rgba(255,255,255,.18)", borderRadius: 14, padding: "9px 0" }}>
                      <strong style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, color: "#fff", display: "block", lineHeight: 1.1 }}>{s.v}</strong>
                      <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "rgba(255,255,255,.8)" }}>{s.l}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {draftClaimItems.map((it) => (
              <Pressable key={it.id} onClick={() => { setClaimItemError(null); setClaimItemForm(it); setSheet("claimItem"); }} style={{ ...card, ...tile, padding: 13, marginBottom: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{it.name || it.artNr}</strong>
                  <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlRun.artNrPrefix", "Art.Nr")} {it.artNr} · {it.qty} {it.qtyType === "pieces" ? t("claims.unitPieces", "pièces") : it.qtyType === "cartons" ? t("claims.unitCartons", "cartons") : t("claims.unitKg", "kg")} · {it.defect}</div>
                </div>
                <Pressable onClick={(e) => { e.stopPropagation(); removeDraftItem(it.id); }} aria-label={t("claims.action.remove", "Retirer")} style={{
                  width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.13)", color: A.red, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                }}>✕</Pressable>
              </Pressable>
            ))}

            <Pressable onClick={() => { setClaimItemError(null); setClaimItemForm({ ...emptyClaimItem, qtyType: draftClaimType === "food" ? "cartons" : "pieces" }); setSheet("claimItem"); }} style={{
              width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
              background: P.soft, border: `1.5px dashed ${P.line}`, color: P.muted, marginBottom: 16,
            }}>＋ {t("claims.addItem", "Ajouter un article")}</Pressable>

            <Pressable onClick={submitClaim} style={{ width: "100%", ...btnSolid(A.red) }}>{t("claims.submit", "Créer la réclamation")}</Pressable>
          </>
        )}

        {screen === "claims" && claimsScreen === "history" && (
          <div className="nx-screen-enter">
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[
                { id: "open", l: `${t("claims.tab.open", "Ouvertes")} · ${claims.filter((c) => c.status === "open" && !c.trashed).length}` },
                { id: "sent", l: `${t("claims.tab.sent", "Envoyées")} · ${claims.filter((c) => (c.status === "sent" || c.status === "done") && !c.trashed).length}` },
                { id: "trash", l: `${t("claims.tab.trash", "Corbeille")} · ${claims.filter((c) => c.trashed).length}` },
              ].map((tb) => (
                <Pressable key={tb.id} onClick={() => setClaimsHistoryTab(tb.id)} style={{
                  flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(11px * var(--nx-font-scale, 1))`,
                  background: claimsHistoryTab === tb.id ? A.red : P.soft, color: claimsHistoryTab === tb.id ? "#fff" : P.ink,
                  border: `1.5px solid ${claimsHistoryTab === tb.id ? A.red : P.line}`,
                }}>{tb.l}</Pressable>
              ))}
            </div>
            {claimsHistoryTab === "open" && claims.filter((c) => c.status === "open" && !c.trashed).length > 1 && (
              <Pressable onClick={sendAllOpenClaims} style={{ width: "100%", ...btnSolid(A.blue), marginBottom: 14 }}>
                {t("claims.sendAllBtn", { n: claims.filter((c) => c.status === "open" && !c.trashed).length }, `📤 Envoyer les ${claims.filter((c) => c.status === "open" && !c.trashed).length} réclamations ouvertes`)}
              </Pressable>
            )}
            {claims.filter((c) => claimsHistoryTab === "trash" ? c.trashed : !c.trashed && (claimsHistoryTab === "open" ? c.status === "open" : c.status !== "open")).length === 0 && (
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 28 }}>
                <span style={{ fontSize: `calc(30px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>{claimsHistoryTab === "trash" ? "🗑️" : "📭"}</span>
                <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{t("claims.emptyHereText", "Rien ici pour l'instant.")}</strong>
              </section>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(420px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
            {claims.filter((c) => claimsHistoryTab === "trash" ? c.trashed : !c.trashed && (claimsHistoryTab === "open" ? c.status === "open" : c.status !== "open")).map((c, cIdx) => (
              <Pressable key={c.id} onClick={() => { setClaimDetail(c); setSheet("claimDetail"); }} className="nx-list-item-in" style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 9, alignItems: "flex-start", textAlign: "left", animationDelay: `${Math.min(cIdx, 14) * 28}ms` }}>
                <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>{c.type === "fresh" ? "🍎" : "📦"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{c.type === "fresh" ? t("claims.type.fresh", "Fruits & légumes") : t("claims.type.food", "Food / Non-Food")} · {t("claims.itemsCount", { n: c.items.length }, `${c.items.length} article${c.items.length > 1 ? "s" : ""}`)}</strong>
                  <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 2 }}>{t("claims.byCreatedAtTemplate", { name: c.createdBy, date: fmtFR(c.createdAt) }, `Par ${c.createdBy} · ${fmtFR(c.createdAt)}`)}</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                    {c.items.map((it) => (
                      <span key={it.id} style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink, background: P.soft, borderRadius: 99, padding: "3px 9px" }}>{it.name || it.artNr} · {it.defect}</span>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 7, marginTop: 9, flexWrap: "wrap" }}>
                    {c.status === "open" && !c.trashed && <Pressable onClick={(e) => { e.stopPropagation(); sendClaim(c.id); }} style={{ padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: A.blue, color: "#fff" }}>{t("claims.sendBtn", "📤 Envoyer")}</Pressable>}
                    {c.status === "sent" && !c.trashed && <Pressable onClick={(e) => { e.stopPropagation(); markClaimDone(c.id); }} style={{ padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: A.green, color: "#fff" }}>✓ {t("claims.action.markDone", "Marquer traitée")}</Pressable>}
                    {!c.trashed && <Pressable onClick={(e) => { e.stopPropagation(); trashClaim(c.id); }} style={{ padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: "rgba(239,68,68,.12)", color: A.red }}>🗑️ {t("common.delete", "Supprimer")}</Pressable>}
                    {c.trashed && <Pressable onClick={(e) => { e.stopPropagation(); restoreClaim(c.id); }} style={{ padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: "rgba(54,79,231,.12)", color: A.blue }}>{t("reductions.restoreBtn", "↩︎ Restaurer")}</Pressable>}
                    {c.trashed && <Pressable onClick={(e) => { e.stopPropagation(); deleteClaimForever(c.id); }} style={{ padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: "rgba(239,68,68,.12)", color: A.red }}>{t("claims.action.purge", "Supprimer définitivement")}</Pressable>}
                  </div>
                </div>
                <span style={{
                  fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 10px", whiteSpace: "nowrap",
                  background: c.status === "done" ? A.green : c.status === "sent" ? A.blue : "rgba(231,138,18,.16)",
                  color: c.status === "open" ? "#b56a00" : "#fff",
                }}>{c.status === "open" ? t("claims.status.open", "Ouverte") : c.status === "sent" ? t("claims.status.sent", "Envoyée") : t("claims.status.done", "Traitée")}</span>
              </Pressable>
            ))}
            </div>
          </div>
        )}

        {screen === "claims" && claimsScreen === "settings" && (() => {
          const toggleClaimsSection = (id) => setClaimsSettingsOpenSection((cur) => (cur === id ? null : id));
          const accHead = (id, icon, iconBg, iconColor, title, subtitle) => (
            <Pressable onClick={() => toggleClaimsSection(id)} style={{ display: "flex", alignItems: "center", gap: 11, width: "100%", padding: "13px 14px", textAlign: "left" }}>
              <span style={{ width: 36, height: 36, borderRadius: 12, display: "grid", placeItems: "center", fontSize: `calc(15px * var(--nx-font-scale, 1))`, flex: "0 0 auto", background: iconBg, color: iconColor }}>{icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block" }}>{title}</strong>
                <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</span>
              </div>
              <span style={{ color: claimsSettingsOpenSection === id ? P.ink : P.muted, fontSize: `calc(14px * var(--nx-font-scale, 1))`, flex: "0 0 auto", display: "inline-block", transform: claimsSettingsOpenSection === id ? "rotate(90deg)" : "none", transition: "transform .25s ease" }}>›</span>
            </Pressable>
          );
          const methodLabel = claimDestination.method === "email" ? t("claims.settings.methodEmail", "E-mail")
            : claimDestination.method === "fax" ? t("claims.settings.methodFax", "Fax")
            : claimDestination.method === "other" ? t("claims.settings.methodOther", "Autre")
            : t("claims.settings.methodUndefined", "Méthode non définie");
          return (
          <>
            <p style={{ margin: "0 2px 14px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
              {t("claims.settings.note", "Choisis comment les réclamations de cette filiale doivent être transmises à la centrale. Modifiable à tout moment.")}
            </p>

            <div style={{ ...card, borderRadius: 18, marginBottom: 10, overflow: "hidden" }}>
              {accHead("method", "📮", "rgba(54,79,231,.12)", A.blue, t("claims.settings.sectionMethod", "Méthode d'envoi"), claimDestination.method && claimDestination.value ? `${methodLabel} · ${claimDestination.value}` : methodLabel)}
              {claimsSettingsOpenSection === "method" && (
                <div style={{ padding: "0 14px 15px", borderTop: `1px solid ${P.line}`, paddingTop: 13 }}>
                  <div className="nx-chips" style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                    {[{ id: "email", l: t("claims.settings.methodEmail", "E-mail") }, { id: "fax", l: t("claims.settings.methodFax", "Fax") }, { id: "other", l: t("claims.settings.methodOther", "Autre") }].map((mm) => (
                      <Pressable key={mm.id} onClick={() => setClaimDestination({ ...claimDestination, method: mm.id })} style={{
                        flex: 1, textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                        background: claimDestination.method === mm.id ? A.blue : P.soft, color: claimDestination.method === mm.id ? "#fff" : P.ink,
                        border: `1.5px solid ${claimDestination.method === mm.id ? A.blue : P.line}`,
                      }}>{mm.l}</Pressable>
                    ))}
                  </div>
                  {claimDestination.method && claimDestination.method !== "other" && (
                    <Field label={claimDestination.method === "email" ? t("claims.emailFieldLabel", "Adresse e-mail") : t("claims.faxFieldLabel", "Numéro de fax")}>
                      <input style={inputStyle} placeholder={claimDestination.method === "email" ? "qualite@centrale.de" : "+49 ..."} value={claimDestination.value}
                        onChange={(e) => setClaimDestination({ ...claimDestination, value: e.target.value })} />
                    </Field>
                  )}
                  {claimDestination.method === "other" && (
                    <Field label={t("claims.precisionFieldLabel", "Précision")}>
                      <input style={inputStyle} placeholder={t("claims.precisionPlaceholder", "Ex. Portail fournisseur")} value={claimDestination.value}
                        onChange={(e) => setClaimDestination({ ...claimDestination, value: e.target.value })} />
                    </Field>
                  )}
                  {!claimDestination.method && (
                    <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 14px" }}>{t("claims.settings.methodUndefined", "Méthode non définie")}</p>
                  )}
                  <Pressable onClick={saveDestination} style={{ width: "100%", ...btnSolid(A.blue) }}>{t("common.save", "Enregistrer")}</Pressable>
                </div>
              )}
            </div>

            <div style={{ ...card, borderRadius: 18, marginBottom: 10, overflow: "hidden" }}>
              {accHead("freshDefects", "🍎", "rgba(239,68,68,.12)", A.red, t("claims.defects.freshTitle", "Liste des défauts — Fruits & légumes"), t("claims.defects.countTemplate", { n: claimDefects.fresh.length }, `${claimDefects.fresh.length} défaut${claimDefects.fresh.length > 1 ? "s" : ""} configuré${claimDefects.fresh.length > 1 ? "s" : ""}`))}
              {claimsSettingsOpenSection === "freshDefects" && (
                <div style={{ padding: "0 14px 15px", borderTop: `1px solid ${P.line}`, paddingTop: 13 }}>
                  <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 10, paddingBottom: 2 }}>
                    {claimDefects.fresh.map((d, i) => (
                      <span key={d} style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, background: P.soft, borderRadius: 99, padding: "7px 8px 7px 12px" }}>
                        {d}
                        <Pressable onClick={() => removeDefectEntry("fresh", i)} aria-label={t("claims.action.remove", "Retirer")} style={{ width: 18, height: 18, borderRadius: 99, background: "rgba(239,68,68,.16)", color: A.red, display: "grid", placeItems: "center", fontSize: `calc(10px * var(--nx-font-scale, 1))`}}>✕</Pressable>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input style={{ ...inputStyle, flex: 1 }} placeholder={t("claims.defects.addPlaceholder", "Nouveau défaut...")} value={newDefectInput.fresh} onChange={(e) => setNewDefectInput({ ...newDefectInput, fresh: e.target.value })} />
                    <Pressable onClick={() => addDefectEntry("fresh")} style={{ ...btnSolid(A.blue), padding: "0 18px", flex: "0 0 auto" }}>＋</Pressable>
                  </div>
                </div>
              )}
            </div>

            <div style={{ ...card, borderRadius: 18, marginBottom: 10, overflow: "hidden" }}>
              {accHead("foodDefects", "📦", "rgba(139,47,214,.12)", A.purple, t("claims.defects.foodTitle", "Liste des défauts — Food / Non-Food"), t("claims.defects.countTemplate", { n: claimDefects.food.length }, `${claimDefects.food.length} défaut${claimDefects.food.length > 1 ? "s" : ""} configuré${claimDefects.food.length > 1 ? "s" : ""}`))}
              {claimsSettingsOpenSection === "foodDefects" && (
                <div style={{ padding: "0 14px 15px", borderTop: `1px solid ${P.line}`, paddingTop: 13 }}>
                  <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 10, paddingBottom: 2 }}>
                    {claimDefects.food.map((d, i) => (
                      <span key={d} style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, background: P.soft, borderRadius: 99, padding: "7px 8px 7px 12px" }}>
                        {d}
                        <Pressable onClick={() => removeDefectEntry("food", i)} aria-label={t("claims.action.remove", "Retirer")} style={{ width: 18, height: 18, borderRadius: 99, background: "rgba(239,68,68,.16)", color: A.red, display: "grid", placeItems: "center", fontSize: `calc(10px * var(--nx-font-scale, 1))`}}>✕</Pressable>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input style={{ ...inputStyle, flex: 1 }} placeholder={t("claims.defects.addPlaceholder", "Nouveau défaut...")} value={newDefectInput.food} onChange={(e) => setNewDefectInput({ ...newDefectInput, food: e.target.value })} />
                    <Pressable onClick={() => addDefectEntry("food")} style={{ ...btnSolid(A.blue), padding: "0 18px", flex: "0 0 auto" }}>＋</Pressable>
                  </div>
                </div>
              )}
            </div>

            <div style={{ ...card, borderRadius: 18, marginBottom: 10, overflow: "hidden" }}>
              {accHead("articles", "🗂️", P.soft, P.ink, t("claims.articles.title", "Articles — Fruits & légumes"), t("claims.importedArticlesCountTemplate", { n: freshArticles.length }, `${freshArticles.length} article${freshArticles.length > 1 ? "s" : ""} importé${freshArticles.length > 1 ? "s" : ""}`))}
              {claimsSettingsOpenSection === "articles" && (
                <div style={{ padding: "0 14px 15px", borderTop: `1px solid ${P.line}`, paddingTop: 13 }}>
                  {freshArticles.length === 0 ? (
                    <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 12px" }}>{t("claims.empty.noArticles", "Aucun article importé pour l'instant.")}</p>
                  ) : (
                    <div style={{ background: P.soft, borderRadius: 16, padding: 4, marginBottom: 12 }}>
                      {freshArticles.map((a) => (
                        <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderBottom: `1px solid ${P.line}` }}>
                          <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, width: 56 }}>{a.artNr}</strong>
                          <span style={{ flex: 1, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{a.name}</span>
                          <Pressable onClick={() => removeFreshArticle(a.id)} aria-label={t("common.delete", "Supprimer")} style={{ width: 26, height: 26, borderRadius: 99, background: "rgba(239,68,68,.12)", color: A.red, display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`}}>✕</Pressable>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
                    <input style={{ ...inputStyle, flex: 1 }} placeholder={t("claims.artNrPlaceholderShort", "Art.Nr")} value={newArticleForm.artNr} onChange={(e) => setNewArticleForm({ ...newArticleForm, artNr: e.target.value })} />
                    <input style={{ ...inputStyle, flex: 2 }} placeholder={t("claims.item.name", "Désignation")} value={newArticleForm.name} onChange={(e) => setNewArticleForm({ ...newArticleForm, name: e.target.value })} />
                    <Pressable onClick={addFreshArticleEntry} style={{ ...btnSolid(A.blue), padding: "0 16px", flex: "0 0 auto" }}>＋</Pressable>
                  </div>

                  <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, margin: "4px 2px 9px" }}>{t("claims.bulkImportHeading", "Import en masse")}</strong>
                  <p style={{ margin: "0 0 8px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("claims.bulkImportDesc", 'Une ligne par article, format "Art.Nr ; Désignation". Remplace toute la liste actuelle.')}</p>
                  <textarea rows={4} style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: 10, resize: "vertical" }}
                    placeholder={"70211 ; Pommes Elstar\n70330 ; Bananes"} value={articlesImportText} onChange={(e) => setArticlesImportText(e.target.value)} />
                  <Pressable onClick={importFreshArticlesFromText} style={{ width: "100%", ...btnSolid(A.purple) }}>{t("claims.importReplaceBtnShort", "Importer (remplace la liste)")}</Pressable>
                </div>
              )}
            </div>
          </>
          );
        })()}

        {/* ================= PLU QUIZ ================= */}
        {screen === "inventurmassnahme" && imScreen === "pluquiz" && pluScreen === "hub" && (
          <>
            <section style={{ ...colorCard(A.teal), ...tile, flexDirection: "column", alignItems: "stretch", padding: 20 }}>
              <span style={onColorTag}>{t("pluQuiz.qualityEyebrow", "Qualité produit")}</span>
              <h2 style={{ margin: "5px 0 6px", fontSize: `calc(19px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{t("pluQuiz.hero.title", "Maîtrise tous les codes PLU.")}</h2>
              <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, lineHeight: 1.45, ...onColorSub }}>{t("pluQuiz.hero.desc", "Entraîne-toi librement, puis valide tes connaissances avec un examen officiel chronométré.")}</p>
            </section>
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              <Pressable onClick={() => pluActiveList ? setPluScreen("trainingHub") : showToast(pluAccessDenied ? t("pluQuiz.accessDeniedToast", "Tu n'as pas la permission d'utiliser PLU Quiz.") : t("pluQuiz.noActiveListToast", "Aucune liste PLU active."))} style={{ flex: 1, ...colorCard(A.blue), display: "flex", flexDirection: "column", alignItems: "stretch", padding: 15, opacity: pluActiveList ? 1 : .6 }}>
                <span style={onColorTag}>{t("pluQuiz.mode.trainingKicker", "Libre")}</span>
                <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, margin: "3px 0 3px", color: "#fff" }}>{t("pluQuiz.mode.trainingTitle", "Training")}</strong>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, lineHeight: 1.35, ...onColorSub }}>{t("pluQuiz.mode.trainingDescShort", "Entraîne-toi librement pour mémoriser les codes PLU.")}</span>
              </Pressable>
              <Pressable onClick={() => pluActiveList ? setPluScreen("examIntro") : showToast(pluAccessDenied ? t("pluQuiz.accessDeniedToast", "Tu n'as pas la permission d'utiliser PLU Quiz.") : t("pluQuiz.noActiveListToast", "Aucune liste PLU active."))} style={{ flex: 1, ...colorCard(A.red), display: "flex", flexDirection: "column", alignItems: "stretch", padding: 15, opacity: pluActiveList ? 1 : .6 }}>
                <span style={onColorTag}>{t("pluQuiz.mode.examKicker", "Officiel")}</span>
                <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, margin: "3px 0 3px", color: "#fff" }}>{t("pluQuiz.mode.examTitle", "Examen")}</strong>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, lineHeight: 1.35, ...onColorSub }}>{t("pluQuiz.mode.examDesc", "Passe un examen officiel chronométré.")}</span>
              </Pressable>
            </div>

            {!pluActiveList && (
              <div style={{ display: "flex", gap: 9, borderRadius: 14, padding: 12, marginBottom: 14, background: pluAccessDenied ? "rgba(239,68,68,.1)" : "rgba(231,138,18,.1)", border: `1px solid ${pluAccessDenied ? "rgba(239,68,68,.28)" : "rgba(231,138,18,.28)"}` }}>
                <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{pluAccessDenied ? "🔒" : "⚠️"}</span>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: pluAccessDenied ? A.red : "#b56a00", lineHeight: 1.5 }}>{pluAccessDenied ? t("pluQuiz.accessDeniedBanner", "Tu n'as pas la permission d'utiliser PLU Quiz — contacte un administrateur.") : t("pluQuiz.noActiveListBanner", "Aucune liste PLU active pour l'instant — demande à un responsable d'en activer une.")}</span>
              </div>
            )}

            {/* Retour utilisateur explicite (2026-08-19) : "les tests d'entraînement aussi
                [doivent] donner des scores... pour qu'il puisse connaître son niveau" — le
                score s'affichait déjà en fin de session (finishQuiz calcule un report quel
                que soit le mode) mais n'était sauvegardé nulle part, jamais consultable après
                coup. Historique séparé de "Tes derniers examens" ci-dessous : coloré par
                palier de score (pas de langage réussi/non réussi, le training n'est pas un
                pass/fail comme l'Examen officiel). */}
            <strong style={{ display: "block", fontSize: `calc(14px * var(--nx-font-scale, 1))`, margin: "2px 2px 9px" }}>{t("pluQuiz.trainingRecent.label", "Ton niveau (training)")}</strong>
            {pluTrainingAttempts.length === 0 && <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 14px" }}>{t("pluQuiz.trainingRecent.empty", "Aucune session de training pour l'instant.")}</p>}
            {pluTrainingAttempts.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                {pluTrainingAttempts.map((e) => {
                  const tierColor = e.percentage >= 80 ? A.green : e.percentage >= 50 ? A.orange : A.red;
                  const paceLabel = e.pace === "timed" ? t("pluQuiz.pace.timed", "Avec chrono") : e.pace === "simulation" ? t("pluQuiz.pace.simulation", "Simulation examen") : t("pluQuiz.pace.free", "Sans chrono");
                  return (
                    <div key={e.id} style={{ ...card, ...tile, padding: 13, marginBottom: 8 }}>
                      <span style={{
                        width: 36, height: 36, borderRadius: 12, display: "grid", placeItems: "center", flexShrink: 0,
                        fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: tierColor,
                      }}>{e.percentage}%</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{e.categoryLabel || t("articles.filterAll", "Tous")} · {paceLabel}</strong>
                        <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pluQuiz.attemptSummaryTemplate", { date: fmtFR(e.finishedAt?.slice(0, 10)), correct: e.correctCount, incorrect: e.incorrectCount }, `${fmtFR(e.finishedAt?.slice(0, 10))} · ${e.correctCount} bonnes / ${e.incorrectCount} mauvaises`)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <strong style={{ display: "block", fontSize: `calc(14px * var(--nx-font-scale, 1))`, margin: "2px 2px 9px" }}>{t("pluQuiz.recent.label", "Tes derniers examens")}</strong>
            {pluAttempts.length === 0 && <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 9px" }}>{t("pluQuiz.recent.empty", "Aucun examen passé pour l'instant.")}</p>}
            {pluAttempts.slice(0, 5).map((e) => (
              <div key={e.id} style={{ ...card, ...tile, padding: 13, marginBottom: 8 }}>
                <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>{e.passed ? "✅" : "❌"}</span>
                <div style={{ flex: 1 }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{e.percentage}%</strong>
                  <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pluQuiz.attemptSummaryTemplate", { date: fmtFR(e.finishedAt?.slice(0, 10)), correct: e.correctCount, incorrect: e.incorrectCount }, `${fmtFR(e.finishedAt?.slice(0, 10))} · ${e.correctCount} bonnes / ${e.incorrectCount} mauvaises`)}</div>
                </div>
                <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 9px", color: "#fff", background: e.passed ? A.green : A.red }}>{e.passed ? t("pluQuiz.recent.passed", "Réussi") : t("pluQuiz.recent.failed", "Non réussi")}</span>
              </div>
            ))}

            {canManagePluQuiz && (
              <Pressable onClick={() => { setPluListDraft(null); loadPluLists(); setPluScreen("admin"); }} style={{ ...colorCard(A.navy), ...tile, padding: 14, marginTop: 4 }}>
                <span style={onColorChip}><EmojiIcon e="⚙️" /></span>
                <div style={{ flex: 1 }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: "#fff" }}>{t("pluQuiz.admin.eyebrow", "Administration")}</strong>
                  <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("pluQuiz.admin.title", "Listes PLU hebdomadaires.")}</div>
                </div>
                <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>›</span>
              </Pressable>
            )}
          </>
        )}

        {screen === "inventurmassnahme" && imScreen === "pluquiz" && pluScreen === "trainingHub" && (
          <>
            <p style={{ margin: "0 2px 14px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
              {t("pluQuiz.trainingHub.title", "Choisis une catégorie.")} {t("pluQuiz.trainingHub.desc", "Les articles que tu maîtrises reviennent moins souvent ; ceux que tu rates reviennent plus vite.")}
            </p>
            <div className="nx-chips" style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 16, paddingBottom: 2 }}>
              {[{ id: "all", label: t("articles.filterAll", "Tous") }, ...(pluActiveList?.categories || [])].map((c) => (
                <Pressable key={c.id} onClick={() => setQuizCategory(c.id)} style={{
                  flex: "0 0 auto", padding: "10px 15px", borderRadius: 99, fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                  background: quizCategory === c.id ? A.teal : P.soft, color: quizCategory === c.id ? "#fff" : P.ink,
                  border: `1.5px solid ${quizCategory === c.id ? A.teal : P.line}`,
                }}>{c.label}</Pressable>
              ))}
            </div>
            <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 9px" }}>{t("pluQuiz.rhythmHeading", "Rythme")}</strong>
            {[
              { id: "free", emoji: "🧘", bg: A.teal, l: t("pluQuiz.pace.free", "Sans chrono"), d: t("pluQuiz.pace.freeDesc", "Correction affichée après chaque question.") },
              { id: "timed", emoji: "⏱️", bg: A.amber, l: t("pluQuiz.pace.timed", "Avec chrono"), d: t("pluQuiz.pace.timedDesc", "15 secondes par question.") },
              { id: "simulation", emoji: "🎯", bg: A.red, l: t("pluQuiz.pace.simulation", "Simulation examen"), d: t("pluQuiz.pace.simulationDesc", "Conditions réelles, correction à la fin.") },
            ].map((p) => (
              <Pressable key={p.id} onClick={() => startTraining(quizCategory, p.id)} style={{ ...colorCard(p.bg), ...tile, padding: 14, marginBottom: 9 }}>
                <span style={onColorChip}><EmojiIcon e={p.emoji} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: "#fff" }}>{p.l}</strong>
                  <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{p.d}</div>
                </div>
                <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(16px * var(--nx-font-scale, 1))`, fontWeight: 300 }}>›</span>
              </Pressable>
            ))}
          </>
        )}

        {screen === "inventurmassnahme" && imScreen === "pluquiz" && pluScreen === "examIntro" && (
          <>
            <section style={{ ...colorCard(A.red), ...tile, flexDirection: "column", alignItems: "stretch", padding: 20 }}>
              <span style={onColorTag}>{t("pluQuiz.examIntro.eyebrow", "Examen officiel")}</span>
              <h2 style={{ margin: "5px 0 6px", fontSize: `calc(19px * var(--nx-font-scale, 1))`, color: "#fff" }}>{t("pluQuiz.examIntro.title", "Prêt à démarrer ?")}</h2>
              <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, lineHeight: 1.45, ...onColorSub }}>{t("pluQuiz.examIntro.desc", "Une fois lancé, aucune correction ne sera affichée avant la fin. Le résultat est définitif.")}</p>
            </section>
            {[
              { i: "❓", t: t("pluQuiz.examIntro.questionsCountTemplate", { n: pluAllArticles.length, kw: pluActiveList?.kw ?? "?", year: pluActiveList?.year ?? "?" }, `${pluAllArticles.length} questions — tous les articles PLU de la liste (KW ${pluActiveList?.kw ?? "?"}/${pluActiveList?.year ?? "?"}), mélangés aléatoirement`) },
              { i: "⏱️", t: t("pluQuiz.examIntro.durationCountTemplate", { n: pluActiveList?.examDurationMinutes ?? "?" }, `${pluActiveList?.examDurationMinutes ?? "?"} minutes — verrouillage automatique à zéro`) },
              { i: "🎯", t: t("pluQuiz.examIntro.thresholdValueTemplate", "Seuil de réussite officiel : 80 %") },
            ].map((r, i) => (
              <div key={i} style={{ ...card, ...tile, padding: 14, marginBottom: 9 }}>
                <span style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`}}>{r.i}</span>
                <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: 1 }}>{r.t}</span>
              </div>
            ))}
            <Pressable onClick={openExamCertify} style={{ width: "100%", ...btnSolid(A.red), marginTop: 6 }}>{t("pluQuiz.examIntro.startBtn", "Commencer l'examen")}</Pressable>
          </>
        )}

        {screen === "inventurmassnahme" && imScreen === "pluquiz" && pluScreen === "quiz" && currentQ && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1, height: 8, borderRadius: 99, background: P.soft, overflow: "hidden" }}>
                <div style={{ width: `${Math.round((quizIndex / quizQuestions.length) * 100)}%`, height: "100%", borderRadius: 99, background: A.teal, transition: "width .4s ease" }} />
              </div>
              <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>{quizIndex + 1}/{quizQuestions.length}</span>
              {quizTimeLeft != null && (
                <span style={{
                  fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "5px 11px",
                  color: quizTimeLeft <= 5 ? "#fff" : P.ink, background: quizTimeLeft <= 5 ? A.red : P.soft,
                }}>⏱ {Math.floor(quizTimeLeft / 60)}:{String(quizTimeLeft % 60).padStart(2, "0")}</span>
              )}
            </div>
            <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 20 }}>
              <span style={eyebrow(A.teal)}>{currentQ.categoryLabel}</span>
              <h1 style={{ margin: "6px 0 18px", fontSize: `calc(21px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em" }}>{currentQ.prompt}</h1>
              {currentQ.mode === "choice" && currentQ.choices.map((c) => {
                const isSel = quizSelected === c;
                const isCorrect = c === currentQ.correctAnswer;
                let bg = P.soft, bd = P.line, fg = P.ink;
                if (revealed) {
                  if (isCorrect) { bg = "rgba(30,184,95,.15)"; bd = A.green; fg = "#15803d"; }
                  else if (isSel) { bg = "rgba(239,68,68,.13)"; bd = A.red; fg = A.red; }
                } else if (isSel) { bg = "rgba(54,79,231,.12)"; bd = A.blue; }
                return (
                  <Pressable key={c} onClick={() => !revealed && setQuizSelected(c)} style={{
                    width: "100%", boxSizing: "border-box", textAlign: "left", padding: "13px 15px", borderRadius: 16,
                    background: bg, border: `1.5px solid ${bd}`, color: fg, fontWeight: 700, fontSize: `calc(14px * var(--nx-font-scale, 1))`, marginBottom: 9,
                  }}>{c}</Pressable>
                );
              })}
              {currentQ.mode === "truefalse" && ["vrai", "faux"].map((c) => {
                const isSel = quizSelected === c;
                const isCorrect = c === currentQ.correctAnswer;
                let bg = P.soft, bd = P.line, fg = P.ink;
                if (revealed) {
                  if (isCorrect) { bg = "rgba(30,184,95,.15)"; bd = A.green; fg = "#15803d"; }
                  else if (isSel) { bg = "rgba(239,68,68,.13)"; bd = A.red; fg = A.red; }
                } else if (isSel) { bg = "rgba(54,79,231,.12)"; bd = A.blue; }
                return (
                  <Pressable key={c} onClick={() => !revealed && setQuizSelected(c)} style={{
                    width: "100%", boxSizing: "border-box", textAlign: "center", padding: "16px 15px", borderRadius: 16,
                    background: bg, border: `1.5px solid ${bd}`, color: fg, fontWeight: 800, fontSize: `calc(15px * var(--nx-font-scale, 1))`, marginBottom: 9, textTransform: "capitalize",
                  }}>{c}</Pressable>
                );
              })}
              {currentQ.mode === "text" && (
                <>
                  <input
                    style={{ ...inputStyle, fontSize: `calc(16px * var(--nx-font-scale, 1))`, textAlign: currentQ.inputMode === "numeric" ? "center" : "left" }}
                    inputMode={currentQ.inputMode === "numeric" ? "numeric" : "text"}
                    autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                    placeholder={currentQ.placeholder} value={quizTextAnswer} disabled={revealed}
                    onChange={(e) => setQuizTextAnswer(e.target.value)}
                  />
                  {/* Suggestions tapées à la volée pour les réponses en nom d'article (jamais
                      pour le PLU numérique) — évite les erreurs de frappe qui compteraient à
                      tort comme faux. Source = pluAllArticles (dérivé de pluActiveList), donc
                      toujours à jour avec la vraie liste active, y compris ses futurs ajouts. */}
                  {!revealed && currentQ.inputMode !== "numeric" && quizTextAnswer.trim().length > 0 && (() => {
                    const q = normalizeText(quizTextAnswer);
                    const suggestions = pluAllArticles.filter((a) => normalizeText(a.name).includes(q)).slice(0, 5);
                    if (!suggestions.length) return null;
                    return (
                      <div style={{ marginTop: 8, borderRadius: 16, border: `1.5px solid ${P.line}`, background: P.card, overflow: "hidden" }}>
                        {suggestions.map((a, i) => (
                          <Pressable key={a.plu} onClick={() => setQuizTextAnswer(a.name)} style={{
                            display: "block", width: "100%", textAlign: "left", padding: "11px 14px", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, fontWeight: 600, color: P.ink,
                            borderBottom: i === suggestions.length - 1 ? "none" : `1px solid ${P.line}`,
                          }}>{a.name}</Pressable>
                        ))}
                      </div>
                    );
                  })()}
                  {revealed && (
                    <p style={{ margin: "10px 2px 0", fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.green }}>{t("pluQuiz.quiz.answerColonTemplate", { answer: currentQ.correctAnswer }, `Réponse : ${currentQ.correctAnswer}`)}</p>
                  )}
                </>
              )}
            </section>
            {revealed ? (
              <Pressable onClick={nextQuestion} style={{ width: "100%", ...btnSolid(A.teal) }}>{t("pluQuiz.quiz.nextBtn", "Suivant")}</Pressable>
            ) : (
              <Pressable onClick={validateAnswer} style={{ width: "100%", ...btnSolid(A.teal) }}>{t("pluQuiz.quiz.validateBtn", "Valider")}</Pressable>
            )}
          </>
        )}

        {screen === "inventurmassnahme" && imScreen === "pluquiz" && pluScreen === "report" && lastReport && (() => {
          const byCat = {};
          lastReport.detail.forEach((a) => { (byCat[a.category] ||= []).push(a); });
          return (
            <>
              <section style={{ ...colorCard(lastReport.passed ? A.green : A.red), ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 28 }}>
                <strong style={{ fontSize: `calc(40px * var(--nx-font-scale, 1))`, color: "#fff" }}>{lastReport.percentage}%</strong>
                <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 700, marginTop: 4, ...onColorSub }}>
                  {lastReport.mode === "exam" ? (lastReport.passed ? t("pluQuiz.report.passedSubtitle", "Seuil de 80 % atteint.") : t("pluQuiz.report.failedSubtitle", "Seuil de 80 % non atteint.")) : t("pluQuiz.report.trainingDoneText", "Session de training terminée.")}
                </span>
              </section>
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                {[
                  { v: lastReport.correct, l: t("pluQuiz.report.statCorrect", "Bonnes"), c: A.green },
                  { v: lastReport.incorrect, l: t("pluQuiz.report.statIncorrect", "Mauvaises"), c: A.red },
                  { v: lastReport.unanswered, l: t("pluQuiz.report.statUnanswered", "Sans réponse"), c: P.muted },
                  { v: `${Math.floor(lastReport.durationSec / 60)}:${String(lastReport.durationSec % 60).padStart(2, "0")}`, l: t("pluQuiz.report.statTime", "Temps utilisé"), c: A.blue },
                ].map((s) => (
                  <div key={s.l} style={{ flex: 1, textAlign: "center", background: P.soft, borderRadius: 14, padding: "9px 3px" }}>
                    <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, color: s.c, display: "block" }}>{s.v}</strong>
                    <span style={{ fontSize: `calc(8.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{s.l}</span>
                  </div>
                ))}
              </div>
              {lastReport.mode === "exam" && examSignature && (
                <div style={{ ...card, borderRadius: 16, padding: 12, marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
                  <img src={examSignature.dataUrl} alt="Signature" style={{ width: 84, height: 34, objectFit: "contain", background: "#fffefb", borderRadius: 8, border: `1px solid ${P.line}` }} />
                  <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.4 }}>
                    {t("pluQuiz.report.certifiedByTemplate", { name: user?.firstname || "Toi" }, `Certifié et signé par ${user?.firstname || "Toi"}`)}<br />{t("pluQuiz.report.onDateTemplate", { date: new Date(examSignature.at).toLocaleString(INTL_LOCALE_BY_LANG[lang] || "fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) }, `le ${new Date(examSignature.at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}`)}
                  </span>
                </div>
              )}
              {lastReport.mode === "exam" && lastReport.attemptId && (
                <div style={{ display: "flex", gap: 9, marginBottom: 16 }}>
                  <Pressable onClick={sendReportToManager} disabled={!!lastReport.sentToManagerAt} style={{ flex: 1, ...btnSolid(lastReport.sentToManagerAt ? P.muted : A.blue), opacity: lastReport.sentToManagerAt ? .6 : 1 }}>
                    {lastReport.sentToManagerAt ? t("pluQuiz.report.alreadySentBtn", "✓ Déjà envoyé") : t("pluQuiz.report.sendToManagerBtn", "📤 Envoyer au responsable")}
                  </Pressable>
                  <Pressable onClick={() => showToast(t("pluQuiz.report.printSimulatedToast", "🖨️ Impression du rapport (simulée)"))} style={{
                    flex: "0 0 auto", width: 46, height: 46, borderRadius: 99, background: P.soft, border: `1.5px solid ${P.line}`,
                    display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
                  }}>🖨️</Pressable>
                </div>
              )}
              <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, margin: "4px 2px 9px" }}>{t("pluQuiz.report.detailTitle", "Détail des réponses")}</strong>
              {Object.entries(byCat).map(([cat, answers]) => (
                <div key={cat} style={{ ...card, borderRadius: 18, padding: 13, marginBottom: 9 }}>
                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginBottom: 8 }}>{cat}</strong>
                  {answers.map((a, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
                      <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{a.correct ? "✓" : a.given ? "✗" : "—"}</span>
                      <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, flex: 1 }}>{a.article} <span style={{ color: P.muted }}>· PLU {a.plu}</span></span>
                      {!a.correct && <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: A.red }}>{a.given || t("pluQuiz.report.noAnswerShort", "sans réponse")}</span>}
                    </div>
                  ))}
                </div>
              ))}
              <Pressable onClick={() => setPluScreen("hub")} style={{ width: "100%", ...btnSolid(A.blue), marginTop: 6 }}>{t("pluQuiz.report.backBtn", "Retour à l'accueil")}</Pressable>
            </>
          );
        })()}

        {screen === "inventurmassnahme" && imScreen === "pluquiz" && pluScreen === "admin" && (
          <div className="nx-screen-enter">
            <p style={{ margin: "0 2px 14px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
              {t("pluQuiz.admin.desc", "Une seule liste active à la fois — c'est elle que le Training et l'Examen utilisent.")}
            </p>

            {!pluListDraft ? (
              <>
                <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                  <Pressable onClick={duplicateActivePluList} style={{ flex: 1, ...card, textAlign: "center", padding: 13, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("pluQuiz.admin.duplicateBtn", "Dupliquer la semaine active")}</Pressable>
                  <Pressable onClick={startNewPluListDraft} style={{ flex: 1, ...card, textAlign: "center", padding: 13, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("pluQuiz.admin.newListBtn", "Liste vierge")}</Pressable>
                </div>
                <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "4px 2px 9px" }}>{t("pluQuiz.admin.allListsHeading", "Toutes les listes")}</strong>
                {pluLists.length === 0 && <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px" }}>{t("haccp.loadingText", "Chargement...")}</p>}
                <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(340px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
                {pluLists.map((l, lIdx) => {
                  const articleCount = (l.categories || []).reduce((n, c) => n + c.articles.length, 0);
                  return (
                    <div key={l.id} className="nx-list-item-in" style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 13, marginBottom: isWide ? 0 : 8, animationDelay: `${Math.min(lIdx, 14) * 28}ms` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, flex: 1 }}>{l.label || `KW ${l.kw}/${l.year}`}</strong>
                        <span style={{
                          fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "3px 8px",
                          color: l.status === "active" ? "#fff" : P.muted, background: l.status === "active" ? A.green : P.soft,
                        }}>{l.status === "active" ? t("pluQuiz.admin.activeShort", "Active") : t("pluQuiz.admin.archivedShort", "Archivée")}</span>
                      </div>
                      <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 8 }}>{t("pluQuiz.admin.listSummaryTemplate", { kw: l.kw, year: l.year, n: articleCount, min: l.examDurationMinutes }, `KW ${l.kw}/${l.year} · ${articleCount} articles · examen ${l.examDurationMinutes} min`)}</span>
                      <div style={{ display: "flex", gap: 8 }}>
                        {l.status !== "active" && <Pressable onClick={() => activatePluList(l.id)} style={{ flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: "rgba(30,184,95,.12)", color: A.green }}>{t("pluQuiz.admin.activateBtn", "Activer")}</Pressable>}
                        <Pressable onClick={() => setPluListDraft({ ...l })} style={{ flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>{t("pluQuiz.admin.editBtn", "Modifier")}</Pressable>
                        <Pressable onClick={() => deletePluList(l.id)} style={{ flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: "rgba(239,68,68,.12)", color: A.red }}>{t("pluQuiz.admin.deleteBtn", "Supprimer")}</Pressable>
                      </div>
                    </div>
                  );
                })}
                </div>
              </>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <Field label={t("pluQuiz.admin.kwLabel", "KW")}><input style={inputStyle} type="number" value={pluListDraft.kw} onChange={(e) => setPluListDraft({ ...pluListDraft, kw: Number(e.target.value) })} /></Field>
                  <Field label={t("pluQuiz.admin.yearLabel", "Année")}><input style={inputStyle} type="number" value={pluListDraft.year} onChange={(e) => setPluListDraft({ ...pluListDraft, year: Number(e.target.value) })} /></Field>
                </div>
                <Field label={t("pluQuiz.admin.labelField", "Libellé")}>
                  <input style={inputStyle} placeholder={`KW ${pluListDraft.kw}/${pluListDraft.year}`} value={pluListDraft.label} onChange={(e) => setPluListDraft({ ...pluListDraft, label: e.target.value })} />
                </Field>
                <Field label={t("pluQuiz.admin.durationLabel", "Durée examen (min)")}>
                  <input style={inputStyle} type="number" value={pluListDraft.examDurationMinutes} onChange={(e) => setPluListDraft({ ...pluListDraft, examDurationMinutes: Number(e.target.value) })} />
                </Field>
                <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "10px 2px 9px" }}>{t("pluQuiz.admin.categoriesTotalTemplate", { n: (pluListDraft.categories || []).reduce((n, c) => n + c.articles.length, 0) }, `Catégories et articles (${(pluListDraft.categories || []).reduce((n, c) => n + c.articles.length, 0)} au total)`)}</strong>
                {(pluListDraft.categories || []).map((c, ci) => (
                  <div key={c.id || ci} style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 13, marginBottom: 8 }}>
                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      <input style={{ ...inputStyle, flex: 1 }} placeholder={t("pluQuiz.admin.categoryIdPlaceholder", "Identifiant (ex. obst)")} value={c.id}
                        onChange={(e) => setPluListDraft((d) => ({ ...d, categories: d.categories.map((cc, i) => i === ci ? { ...cc, id: e.target.value } : cc) }))} />
                      <input style={{ ...inputStyle, flex: 1 }} placeholder={t("pluQuiz.admin.categoryLabelPlaceholder", "Libellé (ex. Obst)")} value={c.label}
                        onChange={(e) => setPluListDraft((d) => ({ ...d, categories: d.categories.map((cc, i) => i === ci ? { ...cc, label: e.target.value } : cc) }))} />
                    </div>
                    <textarea
                      style={{ ...inputStyle, minHeight: 90, fontFamily: "monospace", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, resize: "vertical" }}
                      placeholder={t("pluQuiz.admin.articlesTextareaPlaceholder", "Un article par ligne : plu;nom")}
                      defaultValue={c.articles.map((a) => `${a.plu};${a.name}`).join("\n")}
                      onBlur={(e) => {
                        const articles = e.target.value.split("\n").map((line) => line.split(";")).filter((p) => p[0]?.trim())
                          .map(([plu, name]) => ({ plu: plu.trim(), name: (name || "").trim(), unit: "Stück" }));
                        setPluListDraft((d) => ({ ...d, categories: d.categories.map((cc, i) => i === ci ? { ...cc, articles } : cc) }));
                      }}
                    />
                    <Pressable onClick={() => setPluListDraft((d) => ({ ...d, categories: d.categories.filter((_, i) => i !== ci) }))} style={{ marginTop: 8, fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.red, textAlign: "left" }}>{t("pluQuiz.admin.removeCategoryBtn", "Retirer cette catégorie")}</Pressable>
                  </div>
                ))}
                <Pressable onClick={() => setPluListDraft((d) => ({ ...d, categories: [...(d.categories || []), { id: "", label: "", articles: [] }] }))} style={{
                  width: "100%", textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, marginBottom: 14,
                }}>{t("pluQuiz.admin.addCategoryBtn", "+ Ajouter une catégorie")}</Pressable>
                <div style={{ display: "flex", gap: 8 }}>
                  <Pressable onClick={savePluListDraft} style={{ flex: 1, ...btnSolid(A.teal) }}>{t("pluQuiz.admin.saveBtn", "Enregistrer la liste")}</Pressable>
                  <Pressable onClick={() => setPluListDraft(null)} style={{ flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>{t("pluQuiz.admin.cancelBtn", "Annuler")}</Pressable>
                </div>
              </>
            )}
          </div>
        )}

        {/* ================= KRANKMELDUNG ================= */}
        {screen === "krank" && krankScreen === "hub" && (
          <>
            <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 19 }}>
              <span style={eyebrow(A.orange)}>{t("krankmeldung.heroEyebrow", "Krankmeldung")}</span>
              <h2 style={{ margin: "5px 0 6px", fontSize: `calc(18px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em" }}>{t("krankmeldung.heroTitle", "Déclaration d'arrêt maladie")}</h2>
              <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.45 }}>
                {t("krankmeldung.heroSubtitle", "La déclaration ici est complémentaire aux appels obligatoires — elle ne les remplace pas.")}
              </p>
            </section>

            {activeKrank && (
              <div style={{ ...colorCard(A.orange), ...tile, padding: 16 }}>
                <span style={onColorChip}><EmojiIcon e="🤒" /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={onColorTag}>{t("krankmeldung.activeDefaultTitle", "Déclaration active")}</span>
                  <strong style={{ display: "block", fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, margin: "3px 0 2px", color: "#fff" }}>
                    {t("krankmeldung.activeSinceShortTemplate", { date: fmtFR(activeKrank.startDate) }, `Depuis le ${fmtFR(activeKrank.startDate)}`)}{activeKrank.endDateUnknown ? t("krankmeldung.endUnknownInlineSuffix", " · fin inconnue") : activeKrank.expectedEndDate ? ` → ${fmtFR(activeKrank.expectedEndDate)}` : ""}
                  </strong>
                  <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{activeKrank.contactStatus === "COMPLETE" ? t("krankmeldung.contactsCompleteBadge", "Contacts confirmés") : t("krankmeldung.contactsPendingBadge", "Contacts en attente")}</span>
                </div>
                <Pressable onClick={() => openKrankDetail(activeKrank.id)} style={{
                  fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.orange, background: "#fff", borderRadius: 99, padding: "8px 13px",
                }}>{t("krankmeldung.activeOpenBtn", "Ouvrir")}</Pressable>
              </div>
            )}

            <Pressable onClick={startKrankNew} style={{ ...colorCard(A.orange), ...tile, padding: 15 }}>
              <span style={onColorChip}><EmojiIcon e="📝" /></span>
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: "#fff" }}>{t("krankmeldung.newCardTitle", "Nouvelle déclaration")}</strong>
                <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("krankmeldung.newCardText", "Absence, contacts obligatoires, AU.")}</div>
              </div>
              <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>›</span>
            </Pressable>
            <Pressable onClick={() => setKrankScreen("mine")} style={{ ...colorCard(A.blue), ...tile, padding: 15, position: "relative" }}>
              <span style={onColorChip}><EmojiIcon e="📂" /></span>
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: "#fff" }}>{t("krankmeldung.mineCardTitle", "Mes déclarations")}</strong>
                <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("krankmeldung.mineCardText", "Historique et suivi personnel.")}</div>
              </div>
              {krankmeldungen.some((k) => k.generalStatus === "ACTIVE" && ["PENDING", "REJECTED"].includes(k.auStatus)) && (
                <span className="nx-notif-dot" style={{
                  position: "absolute", top: -6, right: -6, minWidth: 20, height: 20, padding: "0 5px", borderRadius: 99,
                  background: A.red, border: `2px solid ${P.bg}`, color: "#fff", fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800,
                  display: "grid", placeItems: "center",
                }}>{krankmeldungen.filter((k) => k.generalStatus === "ACTIVE" && ["PENDING", "REJECTED"].includes(k.auStatus)).length}</span>
              )}
              <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>›</span>
            </Pressable>
            {isManager(user) && (
              <Pressable onClick={() => setKrankScreen("store")} style={{ ...colorCard(A.navy), ...tile, padding: 15 }}>
                <span style={onColorChip}><EmojiIcon e="🏬" /></span>
                <div style={{ flex: 1 }}>
                  <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: "#fff" }}>{t("krankmeldung.storeCardTitle", "Dossiers filiale")}</strong>
                  <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("krankmeldung.storeCardText", "Absences en cours, contacts, AU.")}</div>
                </div>
                <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>›</span>
              </Pressable>
            )}
          </>
        )}

        {screen === "krank" && krankScreen === "new" && (
          <>
            {krankStep === 1 && (
              <>
                <strong style={{ display: "block", fontSize: `calc(15px * var(--nx-font-scale, 1))`, margin: "0 2px 14px" }}>{t("krankmeldung.stepTitle1", "Informations")}</strong>
                <Field label={t("krankmeldung.step1.startDateLabel", "Premier jour d'absence")}>
                  <input style={dateTimeInputStyle} type="date" min={todayISO()} value={krankForm.startDate} onChange={(e) => setKrankForm({ ...krankForm, startDate: e.target.value })} />
                </Field>
                {!krankForm.endDateUnknown && (
                  <Field label={t("krankmeldung.step1.endDateLabel", "Dernier jour estimé")}>
                    <input style={dateTimeInputStyle} type="date" min={krankForm.startDate} value={krankForm.expectedEndDate} onChange={(e) => setKrankForm({ ...krankForm, expectedEndDate: e.target.value })} />
                  </Field>
                )}
                <Pressable onClick={() => setKrankForm({ ...krankForm, endDateUnknown: !krankForm.endDateUnknown, expectedEndDate: "" })} style={{
                  display: "flex", alignItems: "center", gap: 9, padding: "11px 13px", borderRadius: 14, marginBottom: 13,
                  background: krankForm.endDateUnknown ? "rgba(231,138,18,.1)" : P.soft, border: `1.5px solid ${krankForm.endDateUnknown ? A.orange : P.line}`,
                }}>
                  <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{krankForm.endDateUnknown ? "☑️" : "⬜️"}</span>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{t("krankmeldung.step1.endUnknownLabel", "Fin encore inconnue")}</span>
                </Pressable>
                <Pressable onClick={() => setKrankForm({ ...krankForm, absenceScope: krankForm.absenceScope === "PARTIAL" ? "FULL_DAY" : "PARTIAL" })} style={{
                  display: "flex", alignItems: "center", gap: 9, padding: "11px 13px", borderRadius: 14, marginBottom: krankForm.absenceScope === "PARTIAL" ? 13 : 15,
                  background: krankForm.absenceScope === "PARTIAL" ? "rgba(231,138,18,.1)" : P.soft, border: `1.5px solid ${krankForm.absenceScope === "PARTIAL" ? A.orange : P.line}`,
                }}>
                  <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{krankForm.absenceScope === "PARTIAL" ? "☑️" : "⬜️"}</span>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{t("krankmeldung.step1.partialLabel", "Absence partielle (pas une journée complète)")}</span>
                </Pressable>
                {krankForm.absenceScope === "PARTIAL" && (
                  <Field label={t("krankmeldung.step1.partialTimeLabel", "Heure de début de l'absence")}>
                    <input style={dateTimeInputStyle} type="time" value={krankForm.partialStartTime} onChange={(e) => setKrankForm({ ...krankForm, partialStartTime: e.target.value })} />
                  </Field>
                )}
                <Field label={t("krankmeldung.step1.commentLabel", "Commentaire (facultatif, non médical)")}>
                  <input style={inputStyle} placeholder={t("krankmeldung.step1.commentPlaceholderShort", "Ex. Je préviens dès que possible...")} value={krankForm.employeeComment} onChange={(e) => setKrankForm({ ...krankForm, employeeComment: e.target.value })} />
                </Field>
                <Pressable onClick={krankGoStep2} style={{ width: "100%", ...btnSolid(A.orange), marginTop: 4, opacity: krankSubmitting ? .6 : 1 }}>{krankSubmitting ? t("krankmeldung.savingBtn", "Enregistrement...") : t("krankmeldung.continueBtn", "Continuer")}</Pressable>
              </>
            )}
            {krankStep === 2 && (
              <>
                <strong style={{ display: "block", fontSize: `calc(15px * var(--nx-font-scale, 1))`, margin: "0 2px 8px" }}>{t("krankmeldung.stepTitle2", "Services concernés")}</strong>
                <p style={{ margin: "0 2px 16px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                  {t("krankmeldung.step2.intro", "Services concernés par cette absence, d'après le planning déjà publié.")}
                </p>
                {krankPlanImpact.length === 0 && (
                  <div style={{ ...card, ...tile, padding: 15, marginBottom: 16 }}>
                    <span style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`}}>🗓️</span>
                    <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: 1 }}>{t("krankmeldung.noShiftsFoundShort", "Aucun créneau publié sur cette période pour l'instant.")}</span>
                  </div>
                )}
                {krankPlanImpact.map((s) => (
                  <div key={s.entryId} style={{ ...card, ...tile, padding: 13, marginBottom: 8 }}>
                    <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>🗓️</span>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{fmtFR(s.date)}</strong>
                      <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{s.start}–{s.end}</span>
                    </div>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                  <Pressable onClick={() => setKrankStep(1)} style={{ flex: 1, textAlign: "center", padding: "14px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink }}>{t("krankmeldung.backBtn", "Retour")}</Pressable>
                  <Pressable onClick={krankGoStep3} style={{ flex: 1, ...btnSolid(A.orange), opacity: krankSubmitting ? .6 : 1 }}>{krankSubmitting ? "..." : t("krankmeldung.continueBtn", "Continuer")}</Pressable>
                </div>
              </>
            )}
            {krankStep === 3 && (
              <>
                <strong style={{ display: "block", fontSize: `calc(15px * var(--nx-font-scale, 1))`, margin: "0 2px 8px" }}>{t("krankmeldung.stepTitle3", "Contacts obligatoires")}</strong>
                <section style={{ ...colorCard(A.orange), ...tile, flexDirection: "column", alignItems: "stretch", padding: 15, marginBottom: 14 }}>
                  <span style={onColorTag}>{t("krankmeldung.step3.roleTemplate", { role: user?.role || "Employé" }, `Selon ton poste · ${user?.role || "Employé"}`)}</span>
                  <p style={{ margin: "5px 0 0", fontSize: `calc(12px * var(--nx-font-scale, 1))`, lineHeight: 1.5, color: "#fff" }}>
                    {t("krankmeldung.step3.callRequiredText", "Un appel téléphonique est obligatoire — l'app détermine automatiquement qui doit être joint selon ton poste. Ton responsable confirmera l'appel depuis le détail du dossier une fois qu'il aura eu lieu.")}
                  </p>
                </section>
                {krankContactsPreview.length === 0 && <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 14px" }}>{t("krankmeldung.noMandatoryContactShort", "Aucun contact obligatoire déterminé pour ton poste.")}</p>}
                {krankContactsPreview.map((c, i) => {
                  const contextText = krankContactContextText(c);
                  return (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box", padding: 14, borderRadius: 16, marginBottom: 9,
                    background: P.soft, border: `1.5px solid ${P.line}`,
                  }}>
                    <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>{c.userId ? "☎️" : "⚠️"}</span>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, fontWeight: 700, display: "block" }}>{c.name || c.role}</span>
                      <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: c.userId ? P.muted : A.orange }}>{c.userId ? c.role : t("krankmeldung.noAccountForRole", "Aucun compte trouvé pour ce rôle")}</span>
                      {contextText && (
                        <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.orange, display: "block", marginTop: 2 }}>🔄 {contextText}</span>
                      )}
                    </div>
                  </div>
                  );
                })}
                <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                  <Pressable onClick={() => setKrankStep(2)} style={{ flex: 1, textAlign: "center", padding: "14px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink }}>{t("krankmeldung.backBtn", "Retour")}</Pressable>
                  <Pressable onClick={() => setKrankStep(4)} style={{ flex: 1, ...btnSolid(A.orange) }}>{t("krankmeldung.continueBtn", "Continuer")}</Pressable>
                </div>
              </>
            )}
            {krankStep === 4 && (
              <>
                <strong style={{ display: "block", fontSize: `calc(15px * var(--nx-font-scale, 1))`, margin: "0 2px 8px" }}>{t("krankmeldung.stepTitle4", "Attestation médicale (AU)")}</strong>
                {[
                  { id: "now", l: t("krankmeldung.au.nowChoice", "Déposer l'AU maintenant") }, { id: "later", l: t("krankmeldung.au.laterChoice", "Déposer l'AU plus tard") },
                  { id: "none", l: t("krankmeldung.au.noneChoice", "Aucune AU nécessaire (pour l'instant)") }, { id: "eau", l: t("krankmeldung.au.eauChoice", "eAU / AU électronique") },
                ].map((o) => (
                  <Pressable key={o.id} onClick={() => setKrankForm({ ...krankForm, auChoice: o.id })} style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box", padding: 14, borderRadius: 16, marginBottom: 9,
                    background: krankForm.auChoice === o.id ? "rgba(231,138,18,.1)" : P.soft, border: `1.5px solid ${krankForm.auChoice === o.id ? A.orange : P.line}`,
                  }}>
                    <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{krankForm.auChoice === o.id ? "🔘" : "⚪️"}</span>
                    <span style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, fontWeight: 700, flex: 1 }}>{o.l}</span>
                  </Pressable>
                ))}
                {(krankForm.auChoice === "now" || krankForm.auChoice === "eau") && (
                  <>
                    <input ref={krankAuFileInputRef} type="file" accept="application/pdf,image/jpeg,image/png,image/heic" multiple style={{ display: "none" }}
                      onChange={(e) => { krankUploadAu(e.target.files); e.target.value = ""; }} />
                    <Pressable onClick={() => krankAuFileInputRef.current?.click()} style={{
                      display: "flex", alignItems: "center", gap: 9, padding: "12px 14px", borderRadius: 14, marginBottom: 8,
                      background: krankForm.auFiles.length ? "rgba(54,79,231,.1)" : P.soft, border: `1.5px solid ${krankForm.auFiles.length ? A.blue : P.line}`,
                    }}>
                      <span style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>{krankForm.auFiles.length ? "✅" : "📎"}</span>
                      <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: krankForm.auFiles.length ? A.blue : P.ink }}>
                        {krankSubmitting ? t("krankmeldung.uploadingBtn", "Dépôt en cours...") : krankForm.auFiles.length ? krankForm.auFiles.join(", ") : t("krankmeldung.attachFilesBtn", "Joindre le(s) fichier(s)")}
                      </span>
                    </Pressable>
                    <p style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 14px" }}>{t("krankmeldung.au.uploadHint", "PDF, JPG, PNG ou HEIC — 10 Mo max par fichier, 3 fichiers max.")}</p>
                  </>
                )}
                <div style={{ display: "flex", gap: 10 }}>
                  <Pressable onClick={() => setKrankStep(3)} style={{ flex: 1, textAlign: "center", padding: "14px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink }}>{t("krankmeldung.backBtn", "Retour")}</Pressable>
                  <Pressable onClick={() => setKrankStep(5)} style={{ flex: 1, ...btnSolid(A.orange) }}>{t("krankmeldung.continueBtn", "Continuer")}</Pressable>
                </div>
              </>
            )}
            {krankStep === 5 && (
              <>
                <strong style={{ display: "block", fontSize: `calc(15px * var(--nx-font-scale, 1))`, margin: "0 2px 14px" }}>{t("krankmeldung.stepTitle5", "Récapitulatif")}</strong>
                {[
                  { l: t("krankmeldung.summary.absenceLabel", "Absence"), v: `${fmtFR(krankForm.startDate)} → ${krankForm.endDateUnknown ? t("krankmeldung.endUnknownShort", "Fin inconnue") : fmtFR(krankForm.expectedEndDate)}` },
                  { l: t("krankmeldung.summary.type", "Type"), v: krankForm.absenceScope === "PARTIAL" ? t("krankmeldung.summary.partialFromTemplate", { time: krankForm.partialStartTime }, `Partielle dès ${krankForm.partialStartTime}`) : t("krankmeldung.summary.typeFull", "Journée complète") },
                  { l: t("krankmeldung.summary.contacts", "Contacts"), v: t("krankmeldung.summary.contactsIdentifiedTemplate", { done: krankContactsPreview.filter((c) => c.userId).length, total: krankContactsPreview.length }, `${krankContactsPreview.filter((c) => c.userId).length}/${krankContactsPreview.length} identifiés`) },
                  { l: t("krankmeldung.summary.au", "AU"), v: { now: t("krankmeldung.au.nowShortLabel", "Déposée maintenant"), later: t("krankmeldung.au.laterShortLabel", "Plus tard"), none: t("krankmeldung.au.noneShortLabel", "Aucune nécessaire"), eau: t("krankmeldung.au.eauShortLabel", "eAU électronique") }[krankForm.auChoice] },
                ].map((r) => (
                  <div key={r.l} style={{ display: "flex", justifyContent: "space-between", padding: "10px 2px", borderBottom: `1px solid ${P.line}` }}>
                    <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{r.l}</span>
                    <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{r.v}</strong>
                  </div>
                ))}
                <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "16px 2px", lineHeight: 1.5 }}>{t("krankmeldung.step5.confirmHint", "En envoyant, tu confirmes l'exactitude des informations saisies.")}</p>
                <Pressable onClick={submitKrankmeldung} style={{ width: "100%", ...btnSolid(A.orange), opacity: krankSubmitting ? .6 : 1 }}>{krankSubmitting ? t("settings.photo.uploading", "Envoi...") : t("krankmeldung.finalSubmitBtn", "Envoyer la Krankmeldung définitivement")}</Pressable>
                <Pressable onClick={() => setKrankStep(4)} style={{ width: "100%", textAlign: "center", padding: "13px 0", fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("krankmeldung.backBtn", "Retour")}</Pressable>
              </>
            )}
          </>
        )}

        {screen === "krank" && krankScreen === "mine" && (
          <div className="nx-screen-enter">
            {krankmeldungen.length === 0 && <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("krankmeldung.noDeclarationsYet", "Aucune déclaration pour l'instant.")}</p>}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
            {krankmeldungen.map((k, kIdx) => (
              <Pressable key={k.id} feedback className="nx-list-item-in" onClick={() => openKrankDetail(k.id)} style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 9, alignItems: "flex-start", animationDelay: `${Math.min(kIdx, 14) * 28}ms` }}>
                <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>🤒</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{fmtFR(k.startDate)}{k.endDateUnknown ? t("krankmeldung.endUnknownInlineSuffix", " · fin inconnue") : k.expectedEndDate ? ` → ${fmtFR(k.expectedEndDate)}` : ""}</strong>
                  <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 2 }}>{k.employeeComment || t("krankmeldung.noCommentShort", "Sans commentaire")}</div>
                </div>
                <span style={{
                  fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 9px", whiteSpace: "nowrap",
                  color: k.generalStatus === "ACTIVE" ? "#b56a00" : "#fff",
                  background: k.generalStatus === "ACTIVE" ? "rgba(231,138,18,.16)" : k.generalStatus === "CLOSED" ? A.green : A.muted || P.muted,
                }}>{k.generalStatus === "ACTIVE" ? t("krankmeldung.statusActiveShort", "En cours") : k.generalStatus === "DRAFT" ? t("krankmeldung.status.draft", "Brouillon") : k.generalStatus === "CLOSED" ? t("krankmeldung.status.closed", "Clôturée") : t("krankmeldung.status.cancelled", "Annulée")}</span>
              </Pressable>
            ))}
            </div>
          </div>
        )}

        {screen === "krank" && krankScreen === "store" && (
          <div className="nx-screen-enter">
            {canManageKrank && (
              <Pressable onClick={() => window.open(`/api/krankmeldungen/export.csv?_=${Date.now()}`, "_blank")} style={{ width: "100%", ...btnSolid(A.blue), marginBottom: 14 }}>{t("krankmeldung.exportCsvBtn", "Exporter CSV")}</Pressable>
            )}
            {krankDashboardRows.length > 0 && (
              <section style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 15, marginBottom: 16 }}>
                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 10 }}>{t("krankmeldung.sectorViewTitle", "Vue secteur")}</strong>
                {krankDashboardRows.map((row) => (
                  <div key={row.storeId} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: `1px solid ${P.line}` }}>
                    <strong style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, flex: 1 }}>{row.storeId}</strong>
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{row.active} {t("krankmeldung.activeCountSuffix", "en cours")}</span>
                    {row.planPending > 0 && <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.orange }}>{row.planPending} {t("krankmeldung.planBadgeShort", "planning")}</span>}
                    {row.auMissing > 0 && <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.red }}>{row.auMissing} {t("krankmeldung.auBadgeShort", "AU")}</span>}
                  </div>
                ))}
              </section>
            )}
            {krankStoreList.length === 0 && <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("krankmeldung.noDossiersYet", "Aucun dossier pour l'instant.")}</p>}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(420px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
            {krankStoreList.map((k, kIdx) => (
              <div key={k.id} className="nx-list-item-in" style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 14, marginBottom: isWide ? 0 : 9, animationDelay: `${Math.min(kIdx, 14) * 28}ms` }}>
                <Pressable feedback onClick={() => openKrankDetail(k.id)} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, width: "100%" }}>
                  <span style={{ width: 28, height: 28, borderRadius: 99, background: krankAvatarColor(k.employeeNameSnapshot), color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, flexShrink: 0 }}>{(k.employeeNameSnapshot || "?").charAt(0)}</span>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, flex: 1, textAlign: "left" }}>{k.employeeNameSnapshot}</strong>
                  <span style={{
                    fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 9px", whiteSpace: "nowrap",
                    color: k.contactStatus === "COMPLETE" ? "#fff" : "#b56a00", background: k.contactStatus === "COMPLETE" ? A.green : "rgba(231,138,18,.16)",
                  }}>{k.contactStatus === "COMPLETE" ? t("krankmeldung.confirmedShort", "Confirmés") : t("krankmeldung.pendingShort", "En attente")}</span>
                </Pressable>
                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 8 }}>{fmtFR(k.startDate)}{k.endDateUnknown ? t("krankmeldung.endUnknownInlineSuffix", " · fin inconnue") : k.expectedEndDate ? ` → ${fmtFR(k.expectedEndDate)}` : ""} · {t(`krankmeldung.auStatus.${{ NOT_REQUIRED: "notRequired", PENDING: "pending", UPLOADED: "uploaded", VERIFIED: "verified", REJECTED: "rejected" }[k.auStatus]}`, AU_STATUS_LABELS[k.auStatus])}</span>
                {k.requiredContacts.map((c, i) => {
                  const contextText = krankContactContextText(c);
                  return (
                  <Pressable key={i} onClick={() => (c.status !== "pending") ? null : openContactConfirm(k.id, c.userId, c.name || c.role)} style={{
                    display: "flex", alignItems: "center", gap: 9, padding: "9px 11px", borderRadius: 12, marginBottom: 6,
                    background: (c.status !== "pending") ? "rgba(30,184,95,.09)" : P.soft, opacity: (c.status !== "pending") ? .85 : 1,
                  }}>
                    <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{(c.status !== "pending") ? "☑️" : "⬜️"}</span>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, display: "block" }}>{c.name || c.role}</span>
                      {contextText && <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.orange, display: "block" }}>🔄 {contextText}</span>}
                      {(c.status !== "pending") && <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: "#15803d" }}>{t(`krankmeldung.contactStatus.${{ pending: "pending", attempted: "attempted", reached: "reached", message_left: "messageLeft", unreachable: "unreachable", not_in_system: "notInSystem" }[c.status]}`, CONTACT_STATUS_LABELS[c.status])}</span>}
                    </div>
                  </Pressable>
                  );
                })}
              </div>
            ))}
            </div>
          </div>
        )}

        {/* ================= COMMANDE MATÉRIEL ================= */}
        {screen === "material" && moScreen === "hub" && (
          <>
            <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 19 }}>
              <span style={eyebrow(A.brown)}>{t("materialOrder.hero.eyebrow", "NORMA — bons de commande filiale")}</span>
              <h2 style={{ margin: "5px 0 6px", fontSize: `calc(18px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em" }}>{t("materialOrder.hero.title", "Que veux-tu commander ?")}</h2>
              <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.45 }}>
                {t("materialOrder.hero.desc", "Indique les quantités, valide, le bon est transmis directement à la destination prévue.")}
              </p>
            </section>

            {Object.values(materialCatalogs).map((cat) => {
              const articleCount = cat.groups.reduce((s, g) => s + g.articles.length, 0);
              const bg = cat.id === "technique" ? A.navy : A.purple;
              return (
                <Pressable key={cat.id} onClick={() => openMoCatalog(cat.id)} style={{ ...colorCard(bg), ...tile, padding: 17 }}>
                  <div style={onColorChip}><EmojiIcon e={cat.id === "technique" ? "📠" : "✉️"} /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={onColorTag}>{cat.id === "technique" ? t("materialOrder.catalog.fax", "Fax") : t("materialOrder.catalog.mail", "Mail")} — {cat.destination}</span>
                    <h2 style={{ margin: "3px 0 3px", fontSize: `calc(16.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{t(`materialOrder.catalog.${cat.id}.label`, cat.label)}</h2>
                    <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("materialOrder.catalog.articles", { n: articleCount }, `${articleCount} article${articleCount > 1 ? "s" : ""} disponible${articleCount > 1 ? "s" : ""}`)}</p>
                  </div>
                  <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(19px * var(--nx-font-scale, 1))`, fontWeight: 300 }}>›</span>
                </Pressable>
              );
            })}

            <Pressable onClick={() => setMoScreen("history")} style={{ ...colorCard(A.brown), ...tile, padding: 15, marginTop: 4 }}>
              <div style={onColorChip}><EmojiIcon e="🗂️" /></div>
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: "#fff" }}>{t("materialOrder.history.eyebrow", "Historique")}</strong>
                <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("materialOrder.history.desc", "Suis le statut d'envoi de chaque bon de commande.")}</div>
              </div>
              {pendingOrdersCount > 0 && <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: "rgba(255,255,255,.25)", borderRadius: 99, padding: "4px 9px" }}>{pendingOrdersCount}</span>}
            </Pressable>
          </>
        )}

        {screen === "material" && moScreen === "form" && moActiveCatalog && (() => {
          const cat = materialCatalogs[moActiveCatalog];
          const items = moSelectedItems();
          const totalQty = items.reduce((s, it) => s + it.qty, 0);
          return (
            <>
              <div style={{ ...card, ...tile, padding: 14, marginBottom: 14 }}>
                <span style={{ fontSize: `calc(20px * var(--nx-font-scale, 1))`}}>{cat.id === "technique" ? "📠" : "✉️"}</span>
                <div style={{ flex: 1 }}>
                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{cat.destination}</strong>
                  <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t(`materialOrder.catalog.${cat.id}.destinationNote`, cat.destinationNote)}</div>
                </div>
              </div>

              {cat.groups.map((g) => (
                <section key={g.id} style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 15, marginBottom: 9 }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, marginBottom: g.note ? 3 : 10 }}>{t(`materialOrder.catalog.${cat.id}.group.${g.id}.label`, g.label)}</strong>
                  {g.note && <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 10, display: "block" }}>{t(`materialOrder.catalog.${cat.id}.group.${g.id}.note`, g.note)}</span>}
                  {g.articles.map((a) => {
                    const qty = moQuantities[a.id] || 0;
                    return (
                      <div key={a.id} style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "10px 0",
                        borderTop: `1px solid ${P.line}`,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{a.name}</strong>
                          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                            {a.artNr}{a.ve ? ` · VE ${a.ve}` : ""}{a.restricted ? ` · ${t("materialOrder.restrictedTag", "Sur demande BL")}` : ""}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", borderRadius: 99, background: P.soft, border: `1.5px solid ${qty > 0 ? A.brown : P.line}`, overflow: "hidden" }}>
                          <HoldButton onTap={() => { setMoQty(a.id, -1); }} onRepeat={() => setMoQty(a.id, -1)} label={t("common.decreaseHoldAria", "Moins (maintenir pour accélérer)")} style={{ width: 54, height: 54, display: "grid", placeItems: "center", fontSize: `calc(22px * var(--nx-font-scale, 1))`, fontWeight: 800, background: "none" }}>−</HoldButton>
                          <strong style={{ minWidth: 24, textAlign: "center", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{qty}</strong>
                          <HoldButton onTap={() => { setMoQty(a.id, 1); }} onRepeat={() => setMoQty(a.id, 1)} label={t("common.increaseHoldAria", "Plus (maintenir pour accélérer)")} style={{ width: 54, height: 54, display: "grid", placeItems: "center", fontSize: `calc(22px * var(--nx-font-scale, 1))`, fontWeight: 800, background: "none" }}>＋</HoldButton>
                        </div>
                      </div>
                    );
                  })}
                </section>
              ))}
              {cat.excludedNote && <p style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 14px", lineHeight: 1.5 }}>{t(`materialOrder.catalog.${cat.id}.excludedNote`, cat.excludedNote)}</p>}

              <Field label={t("materialOrder.note.label", "Remarque (facultatif)")}>
                <input style={inputStyle} placeholder={t("materialOrder.note.placeholderShort", "Précision pour la destination...")} value={moNote} onChange={(e) => setMoNote(e.target.value)} />
              </Field>

              <div style={{ ...card, borderRadius: 20, padding: 15, marginTop: 4 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>
                    {items.length === 0 ? t("materialOrder.submit.empty", "Aucun article sélectionné") : `${t("materialOrder.summary.articles", { n: items.length }, `${items.length} article${items.length > 1 ? "s" : ""}`)} · ${t("materialOrder.summary.units", { n: totalQty }, `${totalQty} unité${totalQty > 1 ? "s" : ""}`)}`}
                  </span>
                </div>
                <Pressable onClick={submitMaterialOrder} style={{ width: "100%", ...btnSolid(A.brown), opacity: items.length ? 1 : .5 }}>{t("materialOrder.submit.btn", "Valider et envoyer")}</Pressable>
              </div>
            </>
          );
        })()}

        {screen === "material" && moScreen === "history" && (
          <div className="nx-screen-enter">
            {materialOrders.length === 0 && <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("materialOrder.history.emptyShort", "Aucun bon de commande pour l'instant.")}</p>}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
            {materialOrders.map((o, oIdx) => {
              const cat = materialCatalogs[o.catalogId];
              const totalQty = o.items.reduce((s, it) => s + it.qty, 0);
              return (
                <Pressable key={o.id} feedback className="nx-list-item-in" onClick={() => { setMoDetail(o); setSheet("moDetail"); }} style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 9, alignItems: "flex-start", animationDelay: `${Math.min(oIdx, 14) * 28}ms` }}>
                  <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>{o.catalogId === "technique" ? "📠" : "✉️"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{t(`materialOrder.catalog.${cat.id}.label`, cat.label)} · {t("materialOrder.summary.articles", { n: o.items.length }, `${o.items.length} article${o.items.length > 1 ? "s" : ""}`)} ({totalQty})</strong>
                    <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 2 }}>{t("materialOrder.byCreatedAtTemplate", { name: o.createdBy, date: fmtFR(o.createdAt) }, `Par ${o.createdBy} · ${fmtFR(o.createdAt)}`)}</div>
                  </div>
                  <span style={{
                    fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 9px", whiteSpace: "nowrap",
                    color: o.status === "sent" ? "#fff" : "#b56a00", background: o.status === "sent" ? A.green : "rgba(231,138,18,.16)",
                  }}>{o.status === "sent" ? t("materialOrder.sentAtShortTemplate", { date: fmtFR(o.sentAt.slice(0, 10)) }, `Envoyée · ${fmtFR(o.sentAt.slice(0, 10))}`) : t("materialOrder.status.pending", "En attente d'envoi")}</span>
                </Pressable>
              );
            })}
            </div>
          </div>
        )}

        {/* ================= COMMANDES DE MARCHANDISES ================= */}
        {screen === "merchandise" && merchScreen === "hub" && (
          <>
            <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 19 }}>
              <span style={eyebrow(A.amber)}>{t("merch.hub.eyebrow", "Un article à la fois — vitesse MDE")}</span>
              <h2 style={{ margin: "5px 0 6px", fontSize: `calc(18px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em" }}>{t("merch.hub.title", "Quel rayon commander ?")}</h2>
              <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.45 }}>
                {t("merch.hub.desc", "Choisis la catégorie, indique la date de livraison, puis article par article.")}
              </p>
            </section>

            {/* Mode démo — commandes fictives en mémoire, jamais écrites au serveur (l'app
                n'est pas encore branchée à de vraies données de vente). Permet de tester tout
                le parcours Kühlung/Faktor/Empfehlung avec un Abverkauf fictif non-nul. */}
            <Pressable onClick={() => setMerchDemoMode((v) => !v)} style={{
              display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 13px", borderRadius: 12,
              background: merchDemoMode ? "rgba(231,138,18,.13)" : P.soft, border: `1.5px solid ${merchDemoMode ? A.orange : P.line}`,
              marginBottom: 12,
            }}>
              <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>🧪</span>
              <span style={{ flex: 1, textAlign: "left", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: merchDemoMode ? A.orange : P.ink }}>
                {merchDemoMode ? t("merch.demo.activeLabel", "MODE DÉMO actif — aucune donnée réelle touchée") : t("merch.demo.toggleLabel", "Mode démo (tester sans données réelles)")}
              </span>
              <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: merchDemoMode ? A.orange : P.muted }}>{merchDemoMode ? "ON" : "OFF"}</span>
            </Pressable>

            {MERCH_CATEGORIES.map((cat) => {
              const meta = merchCategories.find((c) => c.zone === cat.id);
              const articleCount = meta?.articleCount ?? 0;
              const activeOrder = merchActiveOrders[cat.id];
              return (
                <div key={cat.id} style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", gap: 8 }}>
                    {/* Tap la tuile = TOUJOURS démarrer une nouvelle commande (retour utilisateur
                        explicite : "quand je clique sur kuhlung que je puisse toujours faire une
                        nouvelle commande, valable pour toutes les catégories") — jamais détourné
                        vers une reprise. */}
                    <Pressable
                      onClick={() => canPrepareMerch ? openMerchCategory(cat.id) : showToast(t("err.actionRefused", "Action refusée."))}
                      style={{ ...colorCard(cat.color), ...tile, flex: 1, minWidth: 0, padding: 17, marginBottom: 0 }}
                    >
                      <div style={onColorChip}><EmojiIcon e={cat.emoji} /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h2 style={{ margin: "3px 0 3px", fontSize: `calc(16.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{cat.label}</h2>
                        <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>
                          {articleCount > 0 ? t("merch.hub.articleCountTemplate", { n: articleCount }, `${articleCount} article${articleCount > 1 ? "s" : ""}`) : t("merch.hub.noArticlesYet", "Aucun article pour l'instant")}
                        </p>
                      </div>
                      <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(19px * var(--nx-font-scale, 1))`, fontWeight: 300 }}>›</span>
                    </Pressable>
                    {/* Bouton "↕️ Réorganiser" retiré de la Hub (retour utilisateur explicite,
                        2026-08-26 : "je n'en ai pas besoin pour le moment") — écran/logique
                        de réorganisation (openMerchReorder, merchReorderZone/List,
                        PATCH /api/merchandise-orders/store-order/:zone) laissés intacts,
                        seul ce point d'entrée est retiré. */}
                  </div>
                  {/* Badge "Commande en cours" — SEUL point d'entrée qui rouvre directement le
                      centre de commande (écran article) sur la commande déjà débutée (retour
                      utilisateur explicite : "au clic d'une commande en cours que je me retrouve
                      encore dans l'interface de commande pour une commande deja debutee"). */}
                  {activeOrder && (
                    <Pressable onClick={() => resumeMerchOrder(activeOrder)} style={{
                      width: "100%", display: "flex", alignItems: "center", gap: 8, marginTop: 6,
                      padding: "9px 13px", borderRadius: 14, background: `${cat.color}17`, border: `1.5px solid ${cat.color}55`,
                    }}>
                      <span style={{ width: 7, height: 7, borderRadius: 99, background: cat.color, flexShrink: 0, animation: "nxLive 2s ease infinite" }} />
                      <span style={{ flex: 1, fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: cat.color, textAlign: "left" }}>
                        {t("merch.hub.resumeTemplate", { date: fmtFR(activeOrder.deliveryDate) }, `▶ Commande en cours — reprendre (livraison ${fmtFR(activeOrder.deliveryDate)})`)}
                      </span>
                    </Pressable>
                  )}
                </div>
              );
            })}
          </>
        )}

        {/* Rappel de commande — planning récurrent "quel rayon quel jour, à partir de quelle
            heure" (2026-08-31, retour utilisateur explicite). canManageMerchCatalog seulement
            (même palier que la gestion du catalogue) — décider le rythme de commande d'une
            filiale n'est pas un geste laissé à n'importe quel employé qui prend une commande.
            Enregistrement immédiat par toggle/select (voir patchMerchCategoryDays), pas de
            bouton "Enregistrer" séparé. Récurrent par défaut — reste valable chaque semaine
            jusqu'à modification, "nouveau plan de commande" = revenir ici et re-cocher. */}
        {screen === "merchandise" && merchScreen === "schedule" && canManageMerchCatalog && (
          <>
            <section style={{ ...card, padding: "14px 16px", marginBottom: 14 }}>
              <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                {t("merch.schedule.desc", "Choisis les jours (et, si besoin, l'heure) où chaque rayon doit être commandé. L'app le rappelle automatiquement ces jours-là — jamais de commande créée toute seule, juste un rappel vers ce module.")}
              </p>
            </section>
            {MERCH_CATEGORIES.map((cat) => {
              const meta = merchCategories.find((c) => c.zone === cat.id);
              const orderDays = meta?.orderDays || [];
              const orderHour = Number.isInteger(meta?.orderHour) ? meta.orderHour : null;
              return (
                <section key={cat.id} style={{ ...card, padding: 14, marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 11 }}>
                    <span style={{ width: 34, height: 34, borderRadius: 11, background: `${cat.color}1f`, display: "grid", placeItems: "center", fontSize: `calc(15px * var(--nx-font-scale, 1))`, flexShrink: 0 }}>{cat.emoji}</span>
                    <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{cat.label}</strong>
                  </div>
                  <div style={{ display: "flex", gap: 6, marginBottom: orderDays.length > 0 ? 11 : 0, flexWrap: "wrap" }}>
                    {MERCH_SCHEDULE_DAY_IDS.map((day, i) => {
                      const sel = orderDays.includes(day);
                      return (
                        <Pressable key={day} onClick={() => toggleMerchOrderDay(cat.id, day)} style={{
                          flex: "0 0 auto", width: 38, textAlign: "center", padding: "9px 0", borderRadius: 12, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                          background: sel ? cat.color : P.soft, color: sel ? "#fff" : P.muted,
                          border: `1.5px solid ${sel ? cat.color : P.line}`,
                        }}>{MERCH_SCHEDULE_DAY_LABELS[i]}</Pressable>
                      );
                    })}
                  </div>
                  {orderDays.length > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>{t("merch.schedule.hourLabel", "À partir de")}</span>
                      <select style={{ ...inputStyle, width: "auto", padding: "7px 10px" }} value={orderHour === null ? "" : String(orderHour)} onChange={(e) => patchMerchCategoryDays(cat.id, { orderHour: e.target.value === "" ? null : Number(e.target.value) })}>
                        <option value="">{t("merch.schedule.hourAnytime", "N'importe quelle heure")}</option>
                        {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}h</option>)}
                      </select>
                    </div>
                  )}
                </section>
              );
            })}
          </>
        )}

        {screen === "merchandise" && merchScreen === "date" && (
          <>
            <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 19, marginBottom: 16 }}>
              <span style={{ fontSize: `calc(26px * var(--nx-font-scale, 1))`}}>{merchCategoryMeta(merchActiveZone).emoji}</span>
              <h2 style={{ margin: "8px 0 6px", fontSize: `calc(18px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em" }}>{t("merch.date.titleTemplate", { zone: merchCategoryMeta(merchActiveZone).label }, `Nouvelle commande — ${merchCategoryMeta(merchActiveZone).label}`)}</h2>
              <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.45 }}>
                {t("merch.date.desc", "Choisis toi-même la date de livraison — jamais suggérée automatiquement. Tu pourras la modifier avant l'envoi final.")}
              </p>
            </section>
            <Field label={t("merch.date.fieldLabel", "Date de livraison")}>
              {/* min={todayISO()} + reclamp défensif dans onChange (2026-08-31, retour
                  utilisateur explicite : "le calendrier de Bestellung ne doit jamais prendre
                  en compte des anciennes dates") — le picker natif iOS n'affiche pas
                  toujours les dates passées comme visiblement désactivées, donc jamais faire
                  confiance uniquement à l'attribut min : toute date antérieure à aujourd'hui
                  est ignorée ici, quoi qu'il arrive. */}
              <input style={dateTimeInputStyle} type="date" min={todayISO()} value={merchDeliveryDateInput}
                onChange={(e) => { if (e.target.value && e.target.value < todayISO()) return; setMerchDeliveryDateInput(e.target.value); }} />
            </Field>
            <Pressable onClick={continueFromMerchDate} style={{ width: "100%", ...btnSolid(A.amber), marginTop: 10, opacity: merchDeliveryDateInput ? 1 : .5 }}>
              {t("merch.date.continueBtn", "Continuer")}
            </Pressable>
          </>
        )}

        {/* Étape Faktor — TOUS les rayons depuis le retour utilisateur du 2026-08-25 (avant
            cela, Kühlung uniquement), seulement au démarrage d'une commande NEUVE (jamais à
            la reprise, voir resumeMerchOrder/continueFromMerchDate). Un seul champ, jamais
            de valeur codée en dur — voir computeKuhlungRecommendation. Case "sans facteur" :
            seule échappatoire propre pour une commande sans recommandation (jamais un
            Faktor à 0, voir confirmMerchFaktor). */}
        {screen === "merchandise" && merchScreen === "faktor" && (
          <>
            <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 19, marginBottom: 16 }}>
              <span style={{ fontSize: `calc(26px * var(--nx-font-scale, 1))`}}>❄️</span>
              <h2 style={{ margin: "8px 0 6px", fontSize: `calc(18px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em" }}>{t("merch.faktor.title", "Mit welchem Faktor soll bestellt werden?")}</h2>
              <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.45 }}>
                {t("merch.faktor.desc", "Sert à calculer la recommandation de commande pour chaque article. Valable pour cette commande uniquement — modifiable ensuite article par article.")}
              </p>
            </section>
            {!merchNoFaktor && (
              <Field label={t("merch.faktor.fieldLabel", "Faktor")}>
                <input style={inputStyle} type="number" inputMode="decimal" min="0.1" step="0.1" placeholder={t("merch.faktor.placeholder", "ex. 5")}
                  value={merchFaktorInput} onChange={(e) => setMerchFaktorInput(e.target.value)} />
              </Field>
            )}
            <Pressable onClick={() => setMerchNoFaktor((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{merchNoFaktor ? "☑️" : "⬜️"}</span>
              <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{t("merch.faktor.noFaktorToggle", "Commander sans facteur (pas de recommandation)")}</span>
            </Pressable>
            <Pressable onClick={confirmMerchFaktor} style={{ width: "100%", ...btnSolid(A.amber), marginTop: 10, opacity: merchNoFaktor || merchFaktorInput ? 1 : .5 }}>
              {t("merch.date.continueBtn", "Continuer")}
            </Pressable>
          </>
        )}

        {screen === "merchandise" && merchScreen === "article" && merchSession && merchSession.articles[merchIndex] && (() => {
          const article = merchSession.articles[merchIndex];
          // Aperçu en direct pendant la saisie du numéro d'article (préfixe le plus proche
          // dans le catalogue du rayon) — voir findMerchArticleByArtNr/selectMerchArticleByArtNr.
          const artNrPreview = merchArtNrEditing ? findMerchArticleByArtNr(merchArtNrDraft) : null;
          const meta = merchCategoryMeta(merchActiveZone);
          const weekdayOrder = merchWeekdayOrder();
          const hist = merchSession.history[article.id] || {};
          const allowedUnits = merchAllowedUnits(merchActiveZone);
          const onBadge = { fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, background: "rgba(255,255,255,.22)", borderRadius: 99, padding: "5px 12px", color: "#fff" };
          return (
            <>
            {/* Héros clair — même formule que la carte de couleur du Contrôle Kühlung
                (colorCard), repris à l'identique de l'artefact validé par l'utilisatrice
                ("un truc dans le style contrôle kuhlung"). Art.Nr éditable en tapant dessus
                (clavier numérique, retour utilisateur explicite), prix optionnel (bascule
                artPriceVisible dans la topbar). */}
            <section style={{ ...colorCard(meta.color), flexDirection: "column", alignItems: "center", textAlign: "center", padding: "20px 20px 18px", marginBottom: 14 }}>
              <span style={onColorTag}>{t("merch.article.positionTemplate", { n: merchIndex + 1, zone: meta.label }, `Position ${merchIndex + 1} · Rayon ${meta.label}`)}</span>
              {merchArtNrEditing ? (
                <Pressable
                  disabled={!artNrPreview}
                  onClick={() => artNrPreview && selectMerchArticleByArtNr(artNrPreview)}
                  style={{
                    margin: "9px 0 12px", padding: 0, display: "block", width: "100%",
                    fontSize: `calc(21px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", fontWeight: 800, lineHeight: 1.18,
                    color: "#fff", textWrap: "balance", textAlign: "center",
                    opacity: artNrPreview ? 1 : .55, cursor: artNrPreview ? "pointer" : "default",
                  }}
                >
                  <span style={{ display: "block" }}>{artNrPreview ? artNrPreview.name : article.name}</span>
                  {artNrPreview && (
                    <span style={{ display: "block", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, opacity: .85, marginTop: 3, letterSpacing: 0 }}>
                      {t("merch.article.tapNameToSelectHint", "Toucher pour choisir cet article")}
                    </span>
                  )}
                </Pressable>
              ) : (
                <h1 style={{ margin: "9px 0 12px", fontSize: `calc(21px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", fontWeight: 800, lineHeight: 1.18, color: "#fff", textWrap: "balance" }}>{article.name}</h1>
              )}
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", justifyContent: "center" }}>
                {merchArtNrEditing ? (
                  <span style={{ ...onBadge, display: "inline-flex", alignItems: "center", gap: 5 }}>
                    {t("merch.article.artNrLabel", "Art.Nr")}
                    <input
                      autoFocus type="tel" inputMode="numeric" pattern="[0-9]*" autoComplete="off"
                      value={merchArtNrDraft}
                      onChange={(e) => {
                        const v = e.target.value;
                        setMerchArtNrDraft(v);
                        const exact = (merchSession.articles || []).find((a) => a.artNr === v.trim());
                        if (exact) selectMerchArticleByArtNr(exact);
                      }}
                      onBlur={commitMerchArtNrEdit}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitMerchArtNrEdit(); } }}
                      style={{ width: 58, border: "none", background: "rgba(255,255,255,.28)", borderRadius: 6, padding: "1px 4px", color: "#fff", font: "inherit", fontWeight: 700, outline: "none", textAlign: "center" }}
                    />
                  </span>
                ) : (
                  <Pressable onClick={startMerchArtNrEdit} style={{ ...onBadge, display: "inline-flex", alignItems: "center", gap: 5 }}>
                    {t("merch.article.artNrLabel", "Art.Nr")} {article.artNr} <span style={{ opacity: .75, fontSize: `calc(10px * var(--nx-font-scale, 1))`}}>✎</span>
                  </Pressable>
                )}
                {article.ve ? <span style={onBadge}>{t("merch.article.veLabel", "VE")} {article.ve}</span> : null}
                {/* Prix jamais affiché en clair (2026-08-17, retour utilisateur explicite) —
                    même traitement que le module Articles : "existe" reste visible via cette
                    puce, jamais la valeur elle-même. */}
                {isValidArtPrice(article.price) && (
                  <span style={{ ...onBadge, fontWeight: 800 }}>••••</span>
                )}
                {/* Puce Faktor — tous les rayons désormais (retour utilisateur 2026-08-25),
                    éditable en place (jamais réinterposé via l'écran "faktor" au resume,
                    voir commitMerchFaktorEdit). Absente pour une commande "sans facteur". */}
                {merchSession.order?.faktor != null && (
                  merchFaktorEditing ? (
                    <span style={{ ...onBadge, display: "inline-flex", alignItems: "center", gap: 5 }}>
                      {t("merch.kuhlung.faktorLabel", "Faktor")}
                      <input
                        autoFocus type="number" inputMode="decimal" min="0.1" step="0.1" autoComplete="off"
                        value={merchFaktorEditDraft} onChange={(e) => setMerchFaktorEditDraft(e.target.value)}
                        onBlur={commitMerchFaktorEdit}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitMerchFaktorEdit(); } }}
                        style={{ width: 40, border: "none", background: "rgba(255,255,255,.28)", borderRadius: 6, padding: "1px 4px", color: "#fff", font: "inherit", fontWeight: 700, outline: "none", textAlign: "center" }}
                      />
                    </span>
                  ) : (
                    <Pressable onClick={() => { setMerchFaktorEditDraft(String(merchSession.order.faktor)); setMerchFaktorEditing(true); }} style={{ ...onBadge, display: "inline-flex", alignItems: "center", gap: 5 }}>
                      {t("merch.kuhlung.faktorLabel", "Faktor")} {merchSession.order.faktor} <span style={{ opacity: .75, fontSize: `calc(10px * var(--nx-font-scale, 1))`}}>✎</span>
                    </Pressable>
                  )
                )}
              </div>
            </section>

            <section style={{ ...card, padding: 14, marginBottom: 14 }}>
              <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: P.muted, display: "block", marginBottom: 8 }}>{t("merch.article.historyLabel", "Historique du rayon")}</span>
              <table style={{ width: "100%", borderCollapse: "collapse", fontVariantNumeric: "tabular-nums" }}>
                <tbody>
                  <tr>
                    <td style={{ width: 62 }}></td>
                    {weekdayOrder.map((d, i) => <td key={d} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: i === 0 ? meta.color : P.ink, padding: "4px 4px", textAlign: "center" }}>{d}</td>)}
                    {/* Colonne Ø Abverkauf (2026-08-31, retour utilisateur explicite, capture à
                        l'appui) — même valeur que article.abverkauf déjà affichée sous
                        Empfehlung ("Ø Abverkauf X Stück/j"), rendue visible ICI dans le
                        tableau, à côté des jours dont elle est la moyenne. Séparée par une
                        bordure gauche pour rester lisible comme un résumé, pas un 7e jour.
                        Couleur meta.color (identité du rayon, déjà utilisée pour "Lun" dans
                        cette même rangée) plutôt que l'orange des valeurs "Vendu" — retour
                        utilisateur explicite : une moyenne doit se distinguer visuellement
                        des valeurs journalières dont elle est la moyenne, jamais s'y fondre. */}
                    <td style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: meta.color, padding: "4px 4px", textAlign: "center", borderLeft: `1px solid ${P.line}` }}>{t("merch.article.avgColumnLabel", "Ø")}</td>
                  </tr>
                  <tr>
                    <td style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, padding: "6px 4px" }}>{t("merch.article.orderedRow", "Commandé")}</td>
                    {weekdayOrder.map((d) => <td key={d} style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800, padding: "6px 4px", textAlign: "center", color: hist[d] !== undefined ? P.ink : P.line }}>{hist[d] !== undefined ? hist[d] : "—"}</td>)}
                    <td style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, padding: "6px 4px", textAlign: "center", color: P.line, borderLeft: `1px solid ${P.line}` }}>—</td>
                  </tr>
                  <tr>
                    <td style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, padding: "6px 4px", borderTop: `1px solid ${P.line}` }}>{t("merch.article.soldRow", "Vendu")}</td>
                    {/* En usage réel, aucune donnée d'Abverkauf n'existe encore (voir
                        computeKuhlungRecommendation) — "—" partout, rien d'inventé. En mode
                        démo, soldHistory (fictif, voir buildMerchDemoSession) rend visible
                        d'où vient la moyenne utilisée dans Empfehlung. */}
                    {weekdayOrder.map((d) => <td key={d} style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: article.soldHistory ? 800 : 400, padding: "6px 4px", textAlign: "center", color: article.soldHistory && article.soldHistory[d] !== undefined ? A.orange : P.line, borderTop: `1px solid ${P.line}` }}>{article.soldHistory && article.soldHistory[d] !== undefined ? article.soldHistory[d] : "—"}</td>)}
                    <td style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: article.abverkauf != null ? 800 : 400, padding: "6px 4px", textAlign: "center", color: article.abverkauf != null ? meta.color : P.line, borderTop: `1px solid ${P.line}`, borderLeft: `1px solid ${P.line}` }}>{article.abverkauf != null ? article.abverkauf : "—"}</td>
                  </tr>
                </tbody>
              </table>
            </section>

            {/* Carte Commande — gros chiffre, champ libre (retour utilisateur explicite
                2026-08-25 : plus de flèches −/+ dans ce module pour le moment — la quantité
                proposée par Nexora se pré-remplit directement, voir primeMerchLineInputs/
                l'effet de suivi juste après, et reste modifiable en tapant directement).
                Unité : Karton fixe pour toutes les catégories sauf Trocken, qui garde un
                menu déroulant (retour utilisateur explicite). */}
            <section style={{ ...card, padding: "16px 14px 14px", textAlign: "center" }}>
              <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, letterSpacing: ".02em", display: "block", marginBottom: 12 }}>{t("merch.article.orderLabel", "Commande")}</span>
              {/* Bestand + Empfehlung — tous les rayons désormais (retour utilisateur
                  2026-08-25, auparavant Kühlung uniquement). Champ compact (Stück) + bascule
                  exact/≈, jamais obligatoire de compter précisément (retour utilisateur).
                  Empfehlung affiche "—" tant qu'aucune donnée d'Abverkauf n'existe (voir
                  computeKuhlungRecommendation) — jamais une recommandation inventée. */}
              {merchSession.order?.faktor != null && (
                <>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink, background: P.soft, borderRadius: 99, padding: "7px 12px" }}>
                      {t("merch.kuhlung.bestandLabel", "Bestand")} {merchIstBestandApprox ? "≈" : ""}
                      <input
                        type="number" inputMode="numeric" min="0" placeholder="—" autoComplete="off"
                        value={merchIstBestand} onChange={(e) => setMerchIstBestand(e.target.value)}
                        style={{ width: 46, border: "none", background: "none", textAlign: "center", fontWeight: 800, color: P.ink, font: "inherit", outline: "none" }}
                      />
                      {t("merch.kuhlung.stuckUnit", "Stück")}
                    </span>
                    <Pressable onClick={() => setMerchIstBestandApprox((v) => !v)} style={{
                      fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, padding: "7px 11px", borderRadius: 99,
                      background: merchIstBestandApprox ? A.blue : P.soft, color: merchIstBestandApprox ? "#fff" : P.muted,
                      border: `1.5px solid ${merchIstBestandApprox ? A.blue : P.line}`,
                    }}>≈ {t("merch.kuhlung.approxToggle", "Approximatif")}</Pressable>
                  </div>
                  {(() => {
                    const rec = computeKuhlungRecommendation({
                      abverkauf: article.abverkauf, faktor: merchSession.order?.faktor,
                      istBestand: merchIstBestand === "" ? null : Number(merchIstBestand), ve: article.ve,
                    });
                    // Petite tuile (pas une grande carte) — retour utilisateur : garder le
                    // système "premium colorée" et la petite lampe, mais compact, et une
                    // couleur qui se démarque des couleurs déjà présentes sur l'écran (héros +
                    // puce Faktor sont dans meta.color = bleu Kühlung ici) — ambre choisi
                    // délibérément (association visuelle "idée/suggestion", jamais réutilisé
                    // ailleurs sur cet écran). Reste purement informatif : le stepper
                    // ci-dessous demeure seul maître de la quantité réellement commandée.
                    const proposalStyle = rec ? premiumTileStyle(A.amber) : {};
                    return (
                      <div style={{ marginBottom: 14, display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                        <div style={{
                          display: "inline-flex", alignItems: "center", gap: 8,
                          ...proposalStyle, background: rec ? proposalStyle.background : P.soft,
                          borderRadius: 99, padding: "7px 14px",
                          color: rec ? "#fff" : P.muted, border: rec ? "none" : `1.5px dashed ${P.line}`,
                        }}>
                          <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>💡</span>
                          <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800 }}>
                            {t("merch.kuhlung.recommendationLabel", "Empfehlung")} : {rec ? t("merch.kuhlung.recommendationValueTemplate", { n: rec.recommendedQty }, `${rec.recommendedQty} Karton`) : "—"}
                          </span>
                        </div>
                        {/* Ø Abverkauf visible seulement quand une valeur existe (mode démo
                            aujourd'hui) — rend explicite d'où vient le calcul affiché juste
                            au-dessus (ligne "Vendu" de l'historique), jamais affiché en usage
                            réel puisque toujours absent (voir computeKuhlungRecommendation). */}
                        {article.abverkauf != null && (
                          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                            {t("merch.kuhlung.avgAbverkaufTemplate", { n: article.abverkauf }, `Ø Abverkauf ${article.abverkauf} Stück/j × Faktor ${merchSession.order?.faktor ?? "—"}`)}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}
              {/* Flèches −/+ réintégrées (retour utilisateur explicite, 2026-08-26 — retirées
                  puis redemandées dans la même session) : gros boutons tactiles (retour
                  utilisateur d'origine : "bouton - et + gros adaptés aux doigts"). Comme le
                  champ libre juste à côté, toute interaction ici désarme le suivi automatique
                  de la recommandation pour cet article (merchQtyAutoRef, voir
                  primeMerchLineInputs/l'effet de suivi), tant qu'on ne change pas d'article. */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginBottom: 14 }}>
                <HoldButton onTap={() => { merchQtyAutoRef.current = false; setMerchQty((q) => Math.max(0, q - 1)); }} onRepeat={() => { merchQtyAutoRef.current = false; setMerchQty((q) => Math.max(0, q - 1)); }} label={t("common.decreaseHoldAria", "Moins (maintenir pour accélérer)")} style={{
                  width: 62, height: 62, borderRadius: "50%", background: P.soft, border: `1.5px solid ${P.line}`, display: "grid", placeItems: "center", fontSize: `calc(27px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink,
                }}>−</HoldButton>
                <input
                  type="number" inputMode="numeric" value={merchQty}
                  onChange={(e) => { merchQtyAutoRef.current = false; setMerchQty(Math.max(0, Math.round(Number(e.target.value) || 0))); }}
                  style={{ width: 92, textAlign: "center", fontSize: `calc(34px * var(--nx-font-scale, 1))`, fontWeight: 800, border: "none", background: "none", color: P.ink, fontFamily: `var(--nx-font, ${FONT})`, fontVariantNumeric: "tabular-nums" }}
                />
                <HoldButton onTap={() => { merchQtyAutoRef.current = false; setMerchQty((q) => q + 1); }} onRepeat={() => { merchQtyAutoRef.current = false; setMerchQty((q) => q + 1); }} label={t("common.increaseHoldAria", "Plus (maintenir pour accélérer)")} style={{
                  width: 62, height: 62, borderRadius: "50%", background: meta.color, border: `1.5px solid ${meta.color}`, display: "grid", placeItems: "center", fontSize: `calc(27px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff",
                }}>＋</HoldButton>
              </div>
              {allowedUnits.length > 1 ? (
                <div style={{ display: "flex", justifyContent: "center", position: "relative" }}>
                  <Pressable onClick={() => setMerchUnitMenuOpen((v) => !v)} style={{
                    display: "inline-flex", alignItems: "center", gap: 7, fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800,
                    padding: "10px 16px", borderRadius: 99, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                  }}>{merchUnit} <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>▾</span></Pressable>
                  {merchUnitMenuOpen && (
                    <>
                      <div onClick={() => setMerchUnitMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 49 }} />
                      <div style={{ position: "absolute", top: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", background: P.card, border: `1px solid ${P.line}`, borderRadius: 16, padding: 6, boxShadow: "0 16px 32px -12px rgba(20,22,50,.28)", minWidth: 190, zIndex: 50 }}>
                        {allowedUnits.map((u) => (
                          <Pressable key={u} onClick={() => { setMerchUnit(u); setMerchUnitMenuOpen(false); }} style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 12px", borderRadius: 10, fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink, width: "100%", textAlign: "left" }}>
                            <span style={{ width: 15, flexShrink: 0, color: meta.color, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`}}>{u === merchUnit ? "✓" : ""}</span>{u}
                          </Pressable>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>{allowedUnits[0]}</span>
              )}
            </section>

            {/* Navigation — barre FIXE en bas d'écran (même patron éprouvé que la barre
                d'action du Contrôle Kühlung, ‹ précédent | suivant › — jamais un flux normal
                poussé par le contenu). 2 chevrons seulement (retour utilisateur explicite :
                "bouton retour et suivant représentées par <>", "le clic sur suivant valide
                directement la quantité, donc plus besoin du bouton valider"). */}
            <nav style={{
              position: "fixed", left: 14, right: 14, bottom: "calc(12px + env(safe-area-inset-bottom))",
              zIndex: 40, maxWidth: 492, margin: "0 auto",
              display: "flex", alignItems: "center", gap: 10,
              background: P.tabBg, backdropFilter: "blur(26px) saturate(1.6)", WebkitBackdropFilter: "blur(26px) saturate(1.6)",
              border: `1px solid ${P.tabBorder}`, borderRadius: 26, padding: "9px",
              boxShadow: P.shadow,
            }}>
              <Pressable onClick={goMerchBack} aria-label={t("merch.article.backAria", "Article précédent")} style={{
                width: 76, height: 76, flexShrink: 0, borderRadius: 18, background: P.soft,
                display: "grid", placeItems: "center", fontSize: `calc(32px * var(--nx-font-scale, 1))`, fontWeight: 300, color: P.ink,
              }}>‹</Pressable>
              <Pressable feedback onClick={advanceMerchArticle} aria-label={t("merch.article.nextAria", "Valider et article suivant")} style={{
                flex: 1, height: 76, borderRadius: 18, background: meta.color,
                display: "grid", placeItems: "center", fontSize: `calc(32px * var(--nx-font-scale, 1))`, fontWeight: 300, color: "#fff",
                boxShadow: `0 12px 24px -10px ${meta.color}99`,
              }}>›</Pressable>
            </nav>
            </>
          );
        })()}

        {screen === "merchandise" && merchScreen === "summary" && merchSession && (() => {
          const stats = merchSummaryStats();
          return (
            <>
              <section style={{ ...colorCard(merchCategoryMeta(merchActiveZone).color), ...tile, flexDirection: "column", alignItems: "stretch", padding: 20 }}>
                <span style={onColorTag}>{t("merch.summary.doneTemplate", { zone: merchCategoryMeta(merchActiveZone).label }, `Commande ${merchCategoryMeta(merchActiveZone).label} terminée`)}</span>
                <h2 style={{ margin: "5px 0 2px", fontSize: `calc(20px * var(--nx-font-scale, 1))`, color: "#fff" }}>{t("merch.summary.orderedOfTotalTemplate", { n: stats.commandes, total: stats.total }, `${stats.commandes} article${stats.commandes > 1 ? "s" : ""} commandé${stats.commandes > 1 ? "s" : ""} sur ${stats.total}`)}</h2>
                <p style={{ margin: "4px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("merch.summary.deliveryDateTemplate", { date: fmtFR(merchSession.order?.deliveryDate) }, `Livraison le ${fmtFR(merchSession.order?.deliveryDate)}`)}</p>
              </section>

              <div style={{ display: "flex", gap: 8, margin: "12px 0" }}>
                {[
                  { l: t("merch.summary.treatedLabel", "Traités"), v: stats.traites },
                  { l: t("merch.summary.orderedLabel", "Commandés"), v: stats.commandes },
                  { l: t("merch.summary.skippedLabel", "Ignorés"), v: stats.ignores },
                ].map((s) => (
                  <div key={s.l} style={{ ...card, flex: 1, flexDirection: "column", alignItems: "center", padding: 13, borderRadius: 16 }}>
                    <strong style={{ fontSize: `calc(20px * var(--nx-font-scale, 1))`, fontVariantNumeric: "tabular-nums" }}>{s.v}</strong>
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{s.l}</span>
                  </div>
                ))}
              </div>

              <Pressable onClick={merchModifyOrder} style={{ width: "100%", ...card, ...tile, padding: 14, marginBottom: 8, justifyContent: "center" }}>
                <span style={{ fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{t("merch.summary.editBtn", "✏️ Modifier la commande")}</span>
              </Pressable>
              <Pressable onClick={() => merchConclude(false)} style={{ width: "100%", ...card, ...tile, padding: 14, marginBottom: 8, justifyContent: "center" }}>
                <span style={{ fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{t("merch.summary.saveBtn", "💾 Enregistrer")}</span>
              </Pressable>
              <Pressable onClick={() => merchConclude(true)} style={{ width: "100%", ...card, ...tile, padding: 14, marginBottom: 8, justifyContent: "center" }}>
                <span style={{ fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{t("merch.summary.sendLaterBtn", "🕓 Envoyer plus tard")}</span>
              </Pressable>
              {canValidateMerch && (
                <Pressable onClick={merchSend} style={{ width: "100%", ...btnSolid(A.green) }}>
                  {t("merch.summary.sendBtn", "📤 Envoyer")}
                </Pressable>
              )}
            </>
          );
        })()}

        {screen === "merchandise" && merchScreen === "manage" && (
          <div className="nx-screen-enter">
            <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
              {[{ id: "", l: t("merch.manage.allFilter", "Toutes") }, ...Object.keys(MERCH_STATUS_META).map((id) => ({ id, l: MERCH_STATUS_META[id].label }))].map((f) => (
                <Pressable key={f.id || "all"} onClick={() => setMerchManageStatusFilter(f.id)} style={{
                  flex: "0 0 auto", padding: "8px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  background: merchManageStatusFilter === f.id ? A.amber : P.soft, color: merchManageStatusFilter === f.id ? "#fff" : P.ink,
                  border: `1.5px solid ${merchManageStatusFilter === f.id ? A.amber : P.line}`,
                }}>{f.l}</Pressable>
              ))}
            </div>
            {merchOrders.filter((o) => !merchManageStatusFilter || o.status === merchManageStatusFilter).length === 0 && (
              <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("merch.manage.empty", "Aucune commande pour l'instant.")}</p>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
            {merchOrders.filter((o) => !merchManageStatusFilter || o.status === merchManageStatusFilter).map((o, oIdx) => {
              const meta = merchCategoryMeta(o.zone);
              const sm = MERCH_STATUS_META[o.status] || MERCH_STATUS_META.brouillon;
              const orderedCount = o.lines.filter((l) => !l.skipped && l.quantity > 0).length;
              return (
                <Pressable key={o.id} feedback className="nx-list-item-in" onClick={() => openMerchOrderDetail(o)} style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 9, alignItems: "flex-start", animationDelay: `${Math.min(oIdx, 14) * 28}ms` }}>
                  <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>{meta.emoji}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{meta.label} · {orderedCount} article{orderedCount > 1 ? "s" : ""}</strong>
                    <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 2 }}>{t("merch.manage.deliveryOnTemplate", { date: fmtFR(o.deliveryDate) }, `Livraison ${fmtFR(o.deliveryDate)}`)}</div>
                  </div>
                  <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 9px", whiteSpace: "nowrap", color: sm.color, background: sm.bg }}>{sm.label}</span>
                </Pressable>
              );
            })}
            </div>
          </div>
        )}

        {screen === "merchandise" && merchScreen === "reorder" && (
          <>
            <p style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 14px", lineHeight: 1.5 }}>
              {t("merch.reorder.desc", "Réorganise l'ordre d'affichage des articles pour qu'il corresponde à l'agencement réel de ton rayon.")}
            </p>
            {merchReorderList.map((a, i) => (
              <div key={a.id} style={{ ...card, padding: 12, marginBottom: 7, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, width: 20, textAlign: "center" }}>{i + 1}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{a.name}</strong>
                  <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("merch.article.artNrLabel", "Art.Nr")} {a.artNr}</span>
                </div>
                <Pressable onClick={() => moveMerchReorderItem(i, -1)} aria-label={t("merch.reorder.upAria", "Monter")} style={{
                  width: 34, height: 34, borderRadius: 10, background: P.soft, border: `1.5px solid ${P.line}`, display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, opacity: i === 0 ? .35 : 1,
                }}>↑</Pressable>
                <Pressable onClick={() => moveMerchReorderItem(i, 1)} aria-label={t("merch.reorder.downAria", "Descendre")} style={{
                  width: 34, height: 34, borderRadius: 10, background: P.soft, border: `1.5px solid ${P.line}`, display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, opacity: i === merchReorderList.length - 1 ? .35 : 1,
                }}>↓</Pressable>
              </div>
            ))}
            <Pressable onClick={saveMerchReorder} style={{ width: "100%", ...btnSolid(A.amber), marginTop: 4 }}>{t("merch.reorder.saveBtn", "✓ Enregistrer l'ordre")}</Pressable>
          </>
        )}

        {/* ================= FABRICATION D'ÉTIQUETTES ================= */}
        {screen === "labels" && labelsScreen === "hub" && (
          <div className="nx-screen-enter">
            <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 18, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: canManageLabels ? 13 : 0 }}>
                <span style={{ width: 44, height: 44, borderRadius: 14, flexShrink: 0, display: "grid", placeItems: "center", fontSize: `calc(19px * var(--nx-font-scale, 1))`, color: "#fff", ...premiumTileStyle(A.yellow) }}>🏷️</span>
                <div style={{ minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, display: "block" }}>
                    {t("labels.hub.weekCountTemplate", { n: labelsList.filter((l) => l.createdAt && Date.now() - new Date(l.createdAt).getTime() < 7 * 86400000).length }, "{n} étiquette(s) cette semaine")}
                  </strong>
                  <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.4 }}>
                    {t("labels.hub.desc", "Crée une étiquette, prévisualise-la, puis imprime-la ou exporte-la en PDF au format exact.")}
                  </span>
                </div>
              </div>
              {canManageLabels && (
                <Pressable onClick={startNewLabel} style={{ width: "100%", ...btnSolid(A.yellow) }}>
                  {t("labels.hub.newBtn", "+ Nouvelle étiquette")}
                </Pressable>
              )}
            </section>

            {labelsLoading && <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "20px 0" }}>{t("common.loading", "Chargement…")}</p>}
            {!labelsLoading && labelsList.length === 0 && (
              <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "20px 0" }}>{t("labels.hub.empty", "Aucune étiquette enregistrée pour l'instant.")}</p>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(320px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
            {labelsList.map((l, lIdx) => {
              const tpl = getLabelTemplate(l.templateId);
              const savedLogos = Array.isArray(l.data.logos) ? l.data.logos.filter((k) => LABEL_LOGO_BADGES[k]) : [];
              const swatch = savedLogos.length ? LABEL_LOGO_BADGES[savedLogos[0]].bandColor : (LABEL_COLOR_SWATCHES[l.data.couleur] || LABEL_FLAT_COLOR).bg;
              return (
                <Pressable key={l.id} feedback className="nx-list-item-in" onClick={() => openSavedLabel(l)} style={{ ...card, padding: 13, marginBottom: isWide ? 0 : 8, display: "flex", alignItems: "center", gap: 11, minWidth: 0, animationDelay: `${Math.min(lIdx, 14) * 28}ms` }}>
                  <span style={{ width: 38, height: 38, flexShrink: 0, borderRadius: 11, background: swatch, border: `1.5px solid ${P.line}` }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.data.designation || t("labels.untitled", "Sans désignation")}</strong>
                    <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t(`labels.templateName.${tpl.id}`, tpl.label)}{l.data.prix ? ` · ${l.data.prix} €` : ""}</span>
                  </div>
                  <span style={{ color: P.muted, fontSize: `calc(17px * var(--nx-font-scale, 1))`, fontWeight: 300 }}>›</span>
                </Pressable>
              );
            })}
            </div>
          </div>
        )}

        {screen === "labels" && labelsScreen === "template" && (() => {
          const tplOrder = ["flat5x5", "aktionA4", "flatA4", "flat15x5", "aktion15x5", "rayon15x7", "niimbot5x3"];
          const tplCardHeight = { flat5x5: 96, aktionA4: 150, flatA4: 150, flat15x5: 70, aktion15x5: 70, rayon15x7: 70, niimbot5x3: 118 };
          const tplFullWidth = { flat15x5: true, aktion15x5: true, rayon15x7: true };
          return (
            <>
              <p style={{ margin: "0 2px 14px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                {t("labels.template.desc", "Chaque modèle reproduit fidèlement une étiquette réelle de la filiale.")}
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
                {tplOrder.map((id) => {
                  const tpl = getLabelTemplate(id);
                  const name = t(`labels.templateName.${tpl.id}`, tpl.label);
                  return (
                    <Pressable
                      key={tpl.id}
                      onClick={() => pickLabelTemplate(tpl.id)}
                      style={{
                        ...colorCard(tpl.swatch),
                        gridColumn: tplFullWidth[tpl.id] ? "1 / 3" : undefined,
                        height: tplCardHeight[tpl.id],
                        padding: 12, display: "flex", flexDirection: "column", justifyContent: "flex-end", overflow: "hidden",
                      }}
                    >
                      <span style={onColorTag}>{name.split(" ")[0]}</span>
                      <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", margin: "3px 0 1px", color: "#fff" }}>{name}</strong>
                      <span style={{ ...onColorSub, fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontVariantNumeric: "tabular-nums" }}>
                        {tpl.format.widthMm} × {tpl.format.heightMm} mm
                        {tpl.id === "niimbot5x3" ? " · imprimante Bluetooth" : ""}
                      </span>
                    </Pressable>
                  );
                })}
              </div>
            </>
          );
        })()}

        {screen === "labels" && labelsScreen === "form" && labelActiveTemplateId && (() => {
          const tpl = getLabelTemplate(labelActiveTemplateId);
          // Aperçu en direct pendant la saisie (maquette artefact validée, écran 3 "Saisie —
          // aperçu en direct") — même moteur de rendu EXACT que l'écran preview plein écran
          // (buildLabelBodyHtml + même calcul d'échelle), jamais une seconde reconstruction :
          // se met à jour tout seul à chaque frappe puisque labelDraft est déjà l'état React.
          const MM_TO_PX = 96 / 25.4;
          const naturalWidthPx = tpl.format.widthMm * MM_TO_PX;
          const naturalHeightPx = tpl.format.heightMm * MM_TO_PX;
          const previewBudgetPx = isWide ? 340 : Math.min(260, (typeof window !== "undefined" ? window.innerWidth : 390) - 90);
          const scale = Math.min(1, previewBudgetPx / naturalWidthPx);
          return (
            <>
              <style>{LABEL_ENGINE_CSS}</style>
              <div style={{ ...card, padding: 16, marginBottom: 14, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{
                  width: naturalWidthPx * scale, height: naturalHeightPx * scale,
                  boxShadow: "0 14px 34px -14px rgba(20,22,50,.35)", borderRadius: 4, overflow: "hidden", flexShrink: 0,
                }}>
                  <div style={{ width: naturalWidthPx, height: naturalHeightPx, transform: `scale(${scale})`, transformOrigin: "top left" }}
                    dangerouslySetInnerHTML={{ __html: buildLabelBodyHtml(labelActiveTemplateId, labelDraft) }} />
                </div>
                <span style={{ marginTop: 10, fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("labels.form.liveCaption", "↑ se met à jour pendant la saisie ↓")}</span>
              </div>
              <p style={{ margin: "0 2px 14px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                {t("labels.form.desc", "Seuls les champs utiles à ce modèle sont affichés.")}
              </p>
              {tpl.fields.map((f) => {
                const meta = LABEL_FIELD_META[f];
                if (meta.swatches) {
                  const active = labelDraft[f] || "jaune";
                  return (
                    <Field key={f} label={t(meta.labelKey, meta.label)}>
                      <div style={{ display: "flex", gap: 12 }}>
                        {Object.entries(LABEL_COLOR_SWATCHES).map(([id, sw]) => {
                          const on = active === id;
                          return (
                            <Pressable key={id} onClick={() => updateLabelField(f, id)} aria-label={id} style={{
                              width: 38, height: 38, borderRadius: "50%", flexShrink: 0, background: sw.bg,
                              border: `2px solid ${on ? A.blue : P.line}`, display: "grid", placeItems: "center",
                              boxShadow: on ? `0 0 0 2px ${P.card}, 0 0 0 4px ${A.blue}` : "none",
                            }}>
                              {on && <span style={{ color: sw.ink, fontSize: 15, fontWeight: 900 }}>✓</span>}
                            </Pressable>
                          );
                        })}
                      </div>
                    </Field>
                  );
                }
                if (meta.toggle) {
                  const active = labelDraft[f] !== false;
                  return (
                    <Field key={f} label={t(meta.labelKey, meta.label)}>
                      <Pressable onClick={() => updateLabelField(f, !active)} style={{
                        ...inputStyle, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
                      }}>
                        <span>{active ? t("labels.form.toggleOn", "Activé") : t("labels.form.toggleOff", "Désactivé")}</span>
                        <span style={{ width: 38, height: 22, borderRadius: 99, background: active ? A.green : P.line, position: "relative", flexShrink: 0 }}>
                          <span style={{ position: "absolute", top: 2, left: active ? 18 : 2, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left .15s" }} />
                        </span>
                      </Pressable>
                    </Field>
                  );
                }
                if (meta.multiSwatches) {
                  const active = Array.isArray(labelDraft[f]) ? labelDraft[f] : [];
                  return (
                    <Field key={f} label={t(meta.labelKey, meta.label)}>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {Object.entries(LABEL_LOGO_BADGES).map(([id, b]) => {
                          const on = active.includes(id);
                          return (
                            <Pressable key={id} onClick={() => updateLabelField(f, on ? active.filter((k) => k !== id) : [...active, id])} style={{
                              padding: "8px 14px", borderRadius: 99, fontWeight: 700, fontSize: 13,
                              background: on ? A.green : P.soft, color: on ? "#fff" : P.ink,
                              border: `1.5px solid ${on ? A.green : P.line}`,
                            }}>
                              {t(b.labelKey, b.label)}
                            </Pressable>
                          );
                        })}
                      </div>
                    </Field>
                  );
                }
                return (
                  <Field key={f} label={t(meta.labelKey, meta.label)}>
                    {meta.multiline ? (
                      <textarea rows={2} style={{ ...inputStyle, minHeight: 56, resize: "vertical", fontFamily: `var(--nx-font, ${FONT})` }}
                        placeholder={meta.placeholder} value={labelDraft[f]} onChange={(e) => updateLabelField(f, e.target.value)} />
                    ) : f === "ean" ? (
                      // Scan inline (chantier 2026-08-28) : soit on scanne au téléphone, soit
                      // on tape le EAN à la main — les deux restent toujours possibles, jamais
                      // un remplacement de l'un par l'autre.
                      <div style={{ display: "flex", gap: 8 }}>
                        <input
                          style={{ ...inputStyle, flex: 1 }} placeholder={meta.placeholder} value={labelDraft[f]} onChange={(e) => updateLabelField(f, e.target.value)}
                          type="text" inputMode={meta.keyboard || "text"}
                        />
                        <Pressable onClick={() => doScan("labels")} aria-label={t("labels.form.scanEanAria", "Scanner le code-barres")} style={{
                          width: 48, height: 48, flexShrink: 0, borderRadius: 16, display: "grid", placeItems: "center",
                          background: A.blue, color: "#fff", fontSize: 19,
                        }}>
                          📷
                        </Pressable>
                      </div>
                    ) : (
                      <input
                        style={inputStyle} placeholder={meta.placeholder} value={labelDraft[f]} onChange={(e) => updateLabelField(f, e.target.value)}
                        type="text" inputMode={meta.keyboard || "text"}
                      />
                    )}
                  </Field>
                );
              })}
              <Pressable onClick={goToLabelPreview} style={{ width: "100%", ...btnSolid(A.yellow), marginTop: 6 }}>
                {t("labels.form.previewBtn", "Continuer")}
              </Pressable>
            </>
          );
        })()}

        {screen === "labels" && labelsScreen === "preview" && labelActiveTemplateId && (() => {
          const tpl = getLabelTemplate(labelActiveTemplateId);
          // L'étiquette est construite à sa taille mm RÉELLE (voir buildLabelBodyHtml) — sur
          // téléphone, 150/210mm dépasse largement la largeur d'écran. Sans mise à l'échelle,
          // un conteneur overflowX:auto centré sur un contenu plus large que lui montre un
          // recadrage centré des DEUX côtés par défaut (bug réel trouvé : rien de la vraie
          // étiquette n'était visible, juste une tranche du milieu) — jamais acceptable pour
          // une prévisualisation. On réduit donc uniformément (transform:scale, mêmes
          // proportions exactes que l'impression, jamais de recadrage) pour que l'étiquette
          // ENTIÈRE tienne toujours dans la largeur disponible ; l'impression réelle (portail
          // #lblPrintRoot) n'est jamais concernée, elle reste toujours à 100%.
          const MM_TO_PX = 96 / 25.4;
          const naturalWidthPx = tpl.format.widthMm * MM_TO_PX;
          const naturalHeightPx = tpl.format.heightMm * MM_TO_PX;
          const previewBudgetPx = isWide ? 460 : Math.min(340, (typeof window !== "undefined" ? window.innerWidth : 390) - 50);
          const scale = Math.min(1, previewBudgetPx / naturalWidthPx);
          return (
            <>
              <style>{LABEL_ENGINE_CSS}</style>
              <div style={{ ...card, padding: 16, marginBottom: 14, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{
                  width: naturalWidthPx * scale, height: naturalHeightPx * scale,
                  boxShadow: "0 14px 34px -14px rgba(20,22,50,.35)", borderRadius: 4, overflow: "hidden", flexShrink: 0,
                }}>
                  <div style={{ width: naturalWidthPx, height: naturalHeightPx, transform: `scale(${scale})`, transformOrigin: "top left" }}
                    dangerouslySetInnerHTML={{ __html: buildLabelBodyHtml(labelActiveTemplateId, labelDraft) }} />
                </div>
                <span style={{ marginTop: 10, fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("labels.preview.fidelityCaption", "Aperçu fidèle à l'impression — couleurs et police réelles")}</span>
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                {canManageLabels && (
                  <Pressable onClick={editCurrentLabel} style={{ flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(14px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink }}>
                    {t("labels.preview.editBtn", "Modifier")}
                  </Pressable>
                )}
                <Pressable onClick={printLabel} style={{ flex: 1, ...btnSolid(A.navy), opacity: labelExporting ? .6 : 1 }}>
                  {labelExporting ? t("labels.preview.exportingBtn", "Génération du PDF…") : t("labels.preview.printBtn", "📄 Exporter / Partager le PDF")}
                </Pressable>
              </div>
              {/* Chantier Niimbot Étape 2, Partie C (2026-08-28) — convertLabelToTemplate ne
                  touche jamais labelDraft, seulement labelActiveTemplateId (voir la fonction) :
                  dans N'IMPORTE QUEL SENS entre les modèles existants, pas seulement vers
                  niimbot5x3 (demande explicite : "n'importe quel format vers un autre"). */}
              {canManageLabels && (
                <Pressable onClick={() => setSheet("convertLabelTemplate")} style={{ width: "100%", textAlign: "center", padding: "13px 0", marginBottom: 8, borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink }}>
                  {t("labels.preview.convertBtn", "🔁 Convertir vers un autre format")}
                </Pressable>
              )}
              {/* Chantier impression Niimbot (2026-08-28), ÉTAPE 2 Partie B — vrai bouton
                  d'impression branché sur le contenu réel (printLabelViaBluetooth), connexion
                  automatique si besoin. Jamais affiché sur Safari/Chrome iOS (Web Bluetooth
                  absent, confirmé en test réel) — uniquement Chrome/Edge desktop ou Android. */}
              {typeof navigator !== "undefined" && navigator.bluetooth && (
                <Pressable onClick={printLabelViaBluetooth} disabled={niimbotBusy} style={{ width: "100%", marginBottom: 8, ...btnSolid(A.blue), opacity: niimbotBusy ? .6 : 1 }}>
                  {niimbotBusy ? (niimbotStatus || t("labels.niimbotPrint.starting", "Préparation…")) : t("labels.niimbotPrint.printBtn", `🖨️ Imprimer sur Niimbot${niimbotDeviceName ? ` (${niimbotDeviceName})` : ""}`)}
                </Pressable>
              )}
              {niimbotStatus && !niimbotBusy && (
                <p style={{ margin: "-4px 2px 8px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{niimbotStatus}</p>
              )}
              {typeof navigator !== "undefined" && navigator.bluetooth && (
                <div style={{ ...card, padding: 14, marginBottom: 8, border: `1.5px dashed ${P.line}` }}>
                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 8 }}>
                    🔧 Test imprimante Niimbot (Bluetooth)
                  </strong>
                  {!niimbotClient ? (
                    <Pressable onClick={connectNiimbot} disabled={niimbotBusy} style={{ width: "100%", textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink, opacity: niimbotBusy ? .6 : 1 }}>
                      {niimbotBusy ? t("labels.niimbotTest.connecting", "Recherche de l'imprimante…") : "Connecter l'imprimante"}
                    </Pressable>
                  ) : (
                    <div style={{ display: "flex", gap: 8 }}>
                      <Pressable onClick={testPrintNiimbot} disabled={niimbotBusy} style={{ flex: 1, textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink, opacity: niimbotBusy ? .6 : 1 }}>
                        {niimbotBusy ? "…" : `Imprimer un test (${niimbotDeviceName})`}
                      </Pressable>
                      <Pressable onClick={disconnectNiimbot} style={{ textAlign: "center", padding: "12px 14px", borderRadius: 99, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: A.red }}>
                        ✕
                      </Pressable>
                    </div>
                  )}
                </div>
              )}
              {canManageLabels && (
                <Pressable onClick={saveLabel} style={{ width: "100%", ...btnSolid(A.yellow), marginBottom: 8, opacity: labelSaving ? .6 : 1 }}>
                  {labelSaving ? t("common.saving", "Enregistrement…") : (labelEditingId ? t("labels.preview.updateBtn", "✓ Mettre à jour") : t("labels.preview.saveBtn", "✓ Enregistrer cette étiquette"))}
                </Pressable>
              )}
              {canManageLabels && labelEditingId && (
                labelPendingDeleteId === labelEditingId ? (
                  <div style={{ display: "flex", gap: 8 }}>
                    <Pressable onClick={() => setLabelPendingDeleteId(null)} style={{ flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>
                      {t("common.cancel", "Annuler")}
                    </Pressable>
                    <Pressable onClick={() => deleteLabelConfirmed(labelEditingId)} style={{ flex: 1, ...btnSolid(A.red) }}>
                      {t("labels.preview.confirmDeleteBtn", "Confirmer la suppression")}
                    </Pressable>
                  </div>
                ) : (
                  <Pressable onClick={() => setLabelPendingDeleteId(labelEditingId)} style={{ width: "100%", textAlign: "center", padding: "11px 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.red }}>
                    {t("labels.preview.deleteBtn", "Supprimer cette étiquette")}
                  </Pressable>
                )
              )}
            </>
          );
        })()}

        {/* ================= KASSENPOST (nouveau module, 2026-09-18) =================
            Signalements reçus de la Niederlassung — jamais créés par la filiale. Deux
            catégories : "qm" (Qualitätsmeldung, champs propres + photos probantes
            obligatoires avant de clôturer) et "infos" (Infos importantes, texte libre).
            Kassenpost reste le vrai canal officiel de réponse, ce module centralise/suit
            côté filiale, ne le remplace pas. */}
        {screen === "kassenpost" && kassenpostScreen === "hub" && (() => {
          const filtered = kassenpostList.filter((k) => kassenpostCategoryFilter === "all" || k.category === kassenpostCategoryFilter);
          const KP_CAT_META = {
            qm: { label: t("kassenpost.cat.qm", "Qualitätsmeldung"), emoji: "⚠️", color: A.red },
            infos: { label: t("kassenpost.cat.infos", "Infos importantes"), emoji: "📌", color: A.blue },
          };
          return (
          <>
            <section style={{
              ...card, padding: 18, marginBottom: 16, color: "#fff", border: "none",
              background: `linear-gradient(155deg, rgba(255,255,255,.18), rgba(255,255,255,0) 55%), linear-gradient(135deg, ${A.red}, #7a1512)`,
            }}>
              <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.75)" }}>
                {t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`)}
              </span>
              <h1 style={{ margin: "4px 0 5px", fontSize: `calc(17px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{t("kassenpost.hub.heading", "Signalements Kassenpost")}</h1>
              <p style={{ margin: "0 0 12px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: "rgba(255,255,255,.85)", lineHeight: 1.5 }}>
                {t("kassenpost.hub.desc", "Reçus de la Niederlassung — Kassenpost reste le vrai canal de réponse, ceci ne fait que centraliser le suivi côté filiale.")}
              </p>
              {canManageKassenpost && (
                <div style={{ display: "flex", gap: 8 }}>
                  <Pressable onClick={() => startNewKassenpost("qm")} style={{ flex: 1, ...btnGhostOnColor, textAlign: "center" }}>+ {t("kassenpost.cat.qm", "Qualitätsmeldung")}</Pressable>
                  <Pressable onClick={() => startNewKassenpost("infos")} style={{ flex: 1, ...btnGhostOnColor, textAlign: "center" }}>+ {t("kassenpost.cat.infos", "Infos importantes")}</Pressable>
                </div>
              )}
            </section>

            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[{ id: "all", l: t("kassenpost.tabAll", "Tout") }, { id: "qm", l: KP_CAT_META.qm.label }, { id: "infos", l: KP_CAT_META.infos.label }].map((tb) => (
                <Pressable key={tb.id} onClick={() => setKassenpostCategoryFilter(tb.id)} style={{
                  flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(11px * var(--nx-font-scale, 1))`,
                  background: kassenpostCategoryFilter === tb.id ? A.red : P.soft, color: kassenpostCategoryFilter === tb.id ? "#fff" : P.ink,
                  border: `1.5px solid ${kassenpostCategoryFilter === tb.id ? A.red : P.line}`,
                }}>{tb.l}</Pressable>
              ))}
            </div>

            {kassenpostLoading && <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "20px 0" }}>{t("common.loading", "Chargement…")}</p>}
            {!kassenpostLoading && filtered.length === 0 && (
              <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "20px 0" }}>{t("kassenpost.hub.empty", "Aucun message pour l'instant.")}</p>
            )}
            {filtered.map((item, idx) => {
              const meta = KP_CAT_META[item.category] || KP_CAT_META.infos;
              return (
                <Pressable key={item.id} feedback className="nx-list-item-in" onClick={() => openKassenpostDetail(item)} style={{
                  ...card, padding: 13, marginBottom: 8, display: "flex", alignItems: "center", gap: 11, minWidth: 0, animationDelay: `${Math.min(idx, 14) * 28}ms`,
                }}>
                  <span style={{ width: 38, height: 38, flexShrink: 0, borderRadius: 11, background: `${meta.color}22`, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))` }}>{meta.emoji}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.numero ? `#${item.numero} · ` : ""}{item.title || t("kassenpost.untitled", "Sans titre")}
                    </strong>
                    <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {[item.sender, item.receivedAt ? fmtFR(item.receivedAt) : "", item.category === "qm" && item.artNr ? `Art.Nr ${item.artNr}` : ""].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                  <span style={{
                    fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 900, padding: "4px 9px", borderRadius: 99, whiteSpace: "nowrap", flexShrink: 0,
                    background: item.status === "answered" ? `${A.green}22` : `${A.red}22`, color: item.status === "answered" ? A.green : A.red,
                  }}>{item.status === "answered" ? t("kassenpost.statusAnswered", "Répondu") : t("kassenpost.statusPending", "À traiter")}</span>
                </Pressable>
              );
            })}
          </>
          );
        })()}

        {screen === "kassenpost" && kassenpostScreen === "form" && kassenpostForm && (
          <>
            {kassenpostForm.category === "qm" && (
              <div style={{ ...card, border: `1.5px solid ${A.red}55`, background: `${A.red}14`, padding: 14, marginBottom: 14 }}>
                <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: A.red, marginBottom: 3 }}>⚠️ {t("kassenpost.qm.noDestroyTitle", "Ne pas détruire la marchandise")}</strong>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.4 }}>{t("kassenpost.qm.noDestroyText", "Photos probantes obligatoires avant de marquer ce signalement comme répondu.")}</span>
              </div>
            )}

            <Field label={t("kassenpost.field.numero", "Numéro Kassenpost")}>
              <input style={inputStyle} value={kassenpostForm.numero} onChange={(e) => updateKassenpostField("numero", e.target.value)} placeholder={t("kassenpost.field.numeroPh", "ex. 450")} />
            </Field>
            <Field label={t("kassenpost.field.receivedAt", "Date reçue")}>
              <input type="date" style={dateTimeInputStyle} value={kassenpostForm.receivedAt} onChange={(e) => updateKassenpostField("receivedAt", e.target.value)} />
            </Field>
            <Field label={t("kassenpost.field.sender", "Expéditeur")}>
              <input style={inputStyle} value={kassenpostForm.sender} onChange={(e) => updateKassenpostField("sender", e.target.value)} placeholder={t("kassenpost.field.senderPh", "ex. Johanna Schoellhorn")} />
            </Field>
            <Field label={t("kassenpost.field.title", "Titre")}>
              <input style={inputStyle} value={kassenpostForm.title} onChange={(e) => updateKassenpostField("title", e.target.value)} placeholder={t("kassenpost.field.titlePh", "ex. Qualitätsmeldung #44354 — Verfärbung")} />
            </Field>
            <Field label={t("kassenpost.field.body", "Texte du message")}>
              <textarea style={{ ...inputStyle, minHeight: 90, resize: "vertical" }} value={kassenpostForm.body} onChange={(e) => updateKassenpostField("body", e.target.value)} />
            </Field>

            {kassenpostForm.category === "qm" && (
              <>
                <Field label={t("kassenpost.field.artNr", "Art.Nr")}>
                  <input style={inputStyle} value={kassenpostForm.artNr} onChange={(e) => updateKassenpostField("artNr", e.target.value)} placeholder={t("kassenpost.field.artNrPh", "ex. 44354")} />
                </Field>
                <Field label={t("kassenpost.field.artName", "Désignation")}>
                  <input style={inputStyle} value={kassenpostForm.artName} onChange={(e) => updateKassenpostField("artName", e.target.value)} placeholder={t("kassenpost.field.artNamePh", "ex. NOTHWANG gerauchte Schinkenwurst")} />
                </Field>
                <Field label={t("kassenpost.field.mhd", "MHD")}>
                  <input type="date" style={dateTimeInputStyle} value={kassenpostForm.mhd} onChange={(e) => updateKassenpostField("mhd", e.target.value)} />
                </Field>
                <Field label={t("kassenpost.field.defectType", "Type de défaut")}>
                  <input style={inputStyle} value={kassenpostForm.defectType} onChange={(e) => updateKassenpostField("defectType", e.target.value)} placeholder={t("kassenpost.field.defectTypePh", "ex. Verfärbung")} />
                </Field>
                <Field label={t("kassenpost.field.quantityPieces", "Quantité en pièces")}>
                  <input type="number" inputMode="numeric" style={inputStyle} value={kassenpostForm.quantityPieces} onChange={(e) => updateKassenpostField("quantityPieces", e.target.value)} placeholder="0" />
                </Field>
                <Field label={t("kassenpost.field.deadline", "Échéance")}>
                  <input type="date" style={dateTimeInputStyle} value={kassenpostForm.deadline} onChange={(e) => updateKassenpostField("deadline", e.target.value)} />
                </Field>

                <Field label={t("kassenpost.field.photos", "Photos probantes")}>
                  <input ref={kassenpostPhotoInputRef} type="file" accept="image/*" multiple hidden
                    onChange={(e) => { uploadKassenpostPhotos(e.target.files); e.target.value = ""; }} />
                  <Pressable onClick={() => kassenpostPhotoInputRef.current?.click()} style={{ ...inputStyle, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontWeight: 700, marginBottom: kassenpostForm.photos.length ? 10 : 0 }}>
                    📷 {t("kassenpost.field.addPhotoBtn", "Ajouter une photo")}
                  </Pressable>
                  {kassenpostForm.photos.length > 0 && (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {kassenpostForm.photos.map((url, pIdx) => (
                        <div key={url} style={{ position: "relative", width: 72, height: 72, flexShrink: 0 }}>
                          <img src={url} alt="" style={{ width: 72, height: 72, borderRadius: 12, objectFit: "cover", border: `1.5px solid ${P.line}` }} />
                          <Pressable onClick={() => updateKassenpostField("photos", kassenpostForm.photos.filter((_, i2) => i2 !== pIdx))} aria-label={t("common.delete", "Supprimer")} style={{
                            position: "absolute", top: -6, right: -6, width: 22, height: 22, borderRadius: 99, background: A.red, color: "#fff",
                            display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, boxShadow: "0 3px 8px rgba(0,0,0,.3)",
                          }}>✕</Pressable>
                        </div>
                      ))}
                    </div>
                  )}
                </Field>
              </>
            )}

            {kassenpostActiveId && (
              <Field label={t("kassenpost.field.answerNote", "Note de réponse")}>
                <textarea style={{ ...inputStyle, minHeight: 70, resize: "vertical" }} value={kassenpostForm.answerNote} onChange={(e) => updateKassenpostField("answerNote", e.target.value)} />
              </Field>
            )}

            {kassenpostForm.status === "answered" && (
              <div style={{ ...card, padding: 12, marginBottom: 12, background: `${A.green}18`, border: `1.5px solid ${A.green}55` }}>
                <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: A.green }}>
                  ✓ {t("kassenpost.answeredOn", { date: fmtFR(kassenpostForm.answeredAt), name: kassenpostForm.answeredBy }, `Répondu le ${fmtFR(kassenpostForm.answeredAt)} par ${kassenpostForm.answeredBy}`)}
                </strong>
              </div>
            )}

            {canManageKassenpost && (
              <Pressable onClick={saveKassenpost} style={{ width: "100%", ...btnSolid(A.red), marginBottom: 8, opacity: kassenpostSaving ? .6 : 1 }}>
                {kassenpostSaving ? t("common.saving", "Enregistrement…") : (kassenpostActiveId ? t("kassenpost.updateBtn", "✓ Mettre à jour") : t("kassenpost.saveBtn", "✓ Enregistrer ce message"))}
              </Pressable>
            )}

            {canManageKassenpost && kassenpostActiveId && kassenpostForm.status !== "answered" && (
              <Pressable onClick={markKassenpostAnswered} style={{ width: "100%", ...btnSolid(A.green), marginBottom: 8 }}>
                ✓ {t("kassenpost.markAnsweredBtn", "Marqué comme répondu (dans Kassenpost)")}
              </Pressable>
            )}

            {canManageKassenpost && kassenpostActiveId && (
              kassenpostPendingDeleteId === kassenpostActiveId ? (
                <div style={{ display: "flex", gap: 8 }}>
                  <Pressable onClick={() => setKassenpostPendingDeleteId(null)} style={{ flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>
                    {t("common.cancel", "Annuler")}
                  </Pressable>
                  <Pressable onClick={() => deleteKassenpostConfirmed(kassenpostActiveId)} style={{ flex: 1, ...btnSolid(A.red) }}>
                    {t("kassenpost.confirmDeleteBtn", "Confirmer la suppression")}
                  </Pressable>
                </div>
              ) : (
                <Pressable onClick={() => setKassenpostPendingDeleteId(kassenpostActiveId)} style={{ width: "100%", textAlign: "center", padding: "11px 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.red }}>
                  {t("kassenpost.deleteBtn", "Supprimer ce message")}
                </Pressable>
              )
            )}
          </>
        )}

        {/* ================= INVENTURMASSNAHME ================= */}
        {screen === "inventurmassnahme" && imScreen === "hub" && (
          <>
            <section style={{
              ...card, padding: 18, marginBottom: 16, color: "#fff", border: "none",
              background: `linear-gradient(155deg, rgba(255,255,255,.18), rgba(255,255,255,0) 55%), linear-gradient(135deg, ${A.navy}, #1a2a4d)`,
            }}>
              <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.75)" }}>
                {t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`)}
              </span>
              <h1 style={{ margin: "4px 0 5px", fontSize: `calc(17px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{t("inventurmassnahme.hub.title", "Contrôles & évaluations")}</h1>
              <p style={{ margin: 0, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: "rgba(255,255,255,.8)", lineHeight: 1.5 }}>
                {t("inventurmassnahme.hub.desc", "Tests de livraison et évaluations d'achats-tests, remplis directement et exportés en PDF.")}
              </p>
            </section>

            <Pressable onClick={() => { setLsForm(emptyLsForm); setLsMitSearch(""); setImScreen("lieferschein"); }} style={{ ...card, ...tile, marginBottom: 10 }}>
              <span style={{ width: 46, height: 46, borderRadius: 15, display: "grid", placeItems: "center", fontSize: `calc(20px * var(--nx-font-scale, 1))`, flexShrink: 0, background: `${A.blue}1f`, color: A.blue }}>📋</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: "block", fontSize: `calc(14px * var(--nx-font-scale, 1))`, letterSpacing: "-.01em" }}>{t("inventurmassnahme.hub.lsTitle", "Lieferschein Test")}</strong>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("inventurmassnahme.hub.lsDesc", "Test de contrôle livraison par rayon")}</span>
              </div>
              <span style={{ color: P.muted, fontSize: `calc(15px * var(--nx-font-scale, 1))`, marginLeft: "auto" }}>›</span>
            </Pressable>
            <Pressable onClick={() => { setBwForm(emptyBwForm); setBwMitSearch(""); setImScreen("testkauf"); }} style={{ ...card, ...tile, marginBottom: 10 }}>
              <span style={{ width: 46, height: 46, borderRadius: 15, display: "grid", placeItems: "center", fontSize: `calc(20px * var(--nx-font-scale, 1))`, flexShrink: 0, background: `${A.purple}1f`, color: A.purple }}>🛒</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: "block", fontSize: `calc(14px * var(--nx-font-scale, 1))`, letterSpacing: "-.01em" }}>{t("inventurmassnahme.hub.bwTitle", "Bewertungsbogen für Testkäufe")}</strong>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("inventurmassnahme.hub.bwDesc", "Évaluation d'un achat-test (NORMA)")}</span>
              </div>
              <span style={{ color: P.muted, fontSize: `calc(15px * var(--nx-font-scale, 1))`, marginLeft: "auto" }}>›</span>
            </Pressable>

            <Pressable onClick={() => { setPluScreen("hub"); setImScreen("pluquiz"); }} style={{ ...card, ...tile, marginBottom: 10 }}>
              <span style={{ width: 46, height: 46, borderRadius: 15, display: "grid", placeItems: "center", fontSize: `calc(20px * var(--nx-font-scale, 1))`, flexShrink: 0, background: `${A.teal}1f`, color: A.teal }}>🎯</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: "block", fontSize: `calc(14px * var(--nx-font-scale, 1))`, letterSpacing: "-.01em" }}>{t("plu.eyebrow", "PLU Quiz")}</strong>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("inventurmassnahme.hub.pluDesc", "Training libre et examen chronométré")}</span>
              </div>
              <span style={{ color: P.muted, fontSize: `calc(15px * var(--nx-font-scale, 1))`, marginLeft: "auto" }}>›</span>
            </Pressable>

            <Pressable onClick={() => { setImScreen("archive"); loadImArchive(); }} style={{ ...card, ...tile, marginBottom: 10 }}>
              <span style={{ width: 46, height: 46, borderRadius: 15, display: "grid", placeItems: "center", fontSize: `calc(20px * var(--nx-font-scale, 1))`, flexShrink: 0, background: P.soft }}>📁</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: "block", fontSize: `calc(14px * var(--nx-font-scale, 1))`, letterSpacing: "-.01em" }}>{t("inventurmassnahme.hub.archiveTitle", "Archives hebdomadaires")}</strong>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("inventurmassnahme.hub.archiveDesc", "Tous les documents remplis, classés par semaine")}</span>
              </div>
              <span style={{ color: P.muted, fontSize: `calc(15px * var(--nx-font-scale, 1))`, marginLeft: "auto" }}>›</span>
            </Pressable>
          </>
        )}

        {screen === "inventurmassnahme" && imScreen === "archive" && (
          <div className="nx-screen-enter">
            {imArchiveLoading && <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "20px 0" }}>{t("common.loading", "Chargement…")}</p>}
            {!imArchiveLoading && imRenderWeekFolders(imArchiveWeeks, {
              expandedWeek: imArchiveExpandedWeek, setExpandedWeek: setImArchiveExpandedWeek,
              onExportItem: (it) => it.kind === "plu" ? printPluAttempt(it.raw) : it.kind === "bw" ? exportTestkaufPdf(it.raw) : exportLieferscheinTestPdf(it.raw),
              onEditItem: (it) => editImRecord(it.raw),
              onDeleteItem: (it) => deleteImRecord(it.id),
            })}
          </div>
        )}

        {screen === "inventurmassnahme" && imScreen === "lieferschein" && (() => {
          const RAYONS_IM = ["Trocken", "Kühlung", "Frischfleisch", "Zuteilungen", "O/G"];
          const fieldLabelStyle = { fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".04em", margin: "0 2px 7px", display: "block" };
          const jn = (value, onChange) => (
            <div style={{ display: "flex", gap: 8 }}>
              <Pressable onClick={() => onChange(true)} style={{
                flex: 1, textAlign: "center", padding: "11px 0", borderRadius: 12, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                border: `1.5px solid ${value ? A.green : P.line}`, background: value ? A.green : P.card, color: value ? "#fff" : P.muted,
              }}>✓ {t("inventurmassnahme.ja", "Ja")}</Pressable>
              <Pressable onClick={() => onChange(false)} style={{
                flex: 1, textAlign: "center", padding: "11px 0", borderRadius: 12, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                border: `1.5px solid ${!value ? A.red : P.line}`, background: !value ? A.red : P.card, color: !value ? "#fff" : P.muted,
              }}>✕ {t("inventurmassnahme.nein", "Nein")}</Pressable>
            </div>
          );
          const filteredEmployees = taskEmployees.filter((e) => e.name.toLowerCase().includes(lsMitSearch.trim().toLowerCase()));
          return (
            <>
              <span style={fieldLabelStyle}>{t("inventurmassnahme.filialeLabel", "Filiale")}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: P.soft, border: `1.5px solid ${P.line}`, borderRadius: 99, padding: "8px 13px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, marginBottom: 14 }}>
                🏬 {t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`)}
              </span>

              <span style={fieldLabelStyle}>{t("inventurmassnahme.ls.rayonLabel", "Rayon testé")}</span>
              <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
                {RAYONS_IM.map((r) => (
                  <Pressable key={r} onClick={() => setLsForm({ ...lsForm, rayon: r })} style={{
                    flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                    background: lsForm.rayon === r ? A.navy : P.soft, color: lsForm.rayon === r ? "#fff" : P.ink,
                    border: `1.5px solid ${lsForm.rayon === r ? A.navy : P.line}`,
                  }}>{r}</Pressable>
                ))}
              </div>

              <Field label={t("inventurmassnahme.ls.numberLabel", "Lieferscheinnummer")}>
                <input style={inputStyle} placeholder={t("inventurmassnahme.ls.numberPlaceholder", "ex. 2510841")} value={lsForm.lieferscheinnummer} onChange={(e) => setLsForm({ ...lsForm, lieferscheinnummer: e.target.value })} autoComplete="off" />
              </Field>
              <Field label={t("inventurmassnahme.ls.datumLabel", "Datum")}>
                <input type="date" style={dateTimeInputStyle} value={lsForm.datum} onChange={(e) => setLsForm({ ...lsForm, datum: e.target.value })} />
              </Field>

              <span style={fieldLabelStyle}>{t("inventurmassnahme.mitarbeiterLabel", "Mitarbeiter")}</span>
              {lsForm.mitarbeiterId ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                  <span style={{ flex: 1, display: "inline-flex", alignItems: "center", gap: 6, background: P.soft, border: `1.5px solid ${P.line}`, borderRadius: 99, padding: "8px 13px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>👤 {lsForm.mitarbeiterName}</span>
                  <Pressable onClick={() => setLsForm({ ...lsForm, mitarbeiterId: "", mitarbeiterName: "" })} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, padding: "8px 10px" }}>{t("messages.changeBtn", "Changer")}</Pressable>
                </div>
              ) : (
                <div style={{ marginBottom: 14 }}>
                  <input style={inputStyle} placeholder={t("inventurmassnahme.searchEmployeePlaceholder", "Chercher un collègue...")} value={lsMitSearch} onChange={(e) => setLsMitSearch(e.target.value)} autoComplete="off" />
                  {lsMitSearch.trim() !== "" && (
                    <div style={{ marginTop: 6 }}>
                      {filteredEmployees.length === 0 && <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "8px 2px 0" }}>{t("inventurmassnahme.noEmployeeMatch", "Aucun collègue trouvé.")}</p>}
                      {filteredEmployees.slice(0, 6).map((e) => (
                        <Pressable key={e.employeeId} onClick={() => { setLsForm({ ...lsForm, mitarbeiterId: e.employeeId, mitarbeiterName: e.name }); setLsMitSearch(""); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", borderRadius: 12, background: P.soft, marginBottom: 4 }}>
                          {e.name}
                        </Pressable>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <span style={fieldLabelStyle}>{t("inventurmassnahme.ls.testBestandenLabel", "Test bestanden")}</span>
              <div style={{ marginBottom: 14 }}>{jn(lsForm.testBestanden, (v) => setLsForm({ ...lsForm, testBestanden: v }))}</div>

              <Field label={t("inventurmassnahme.ls.verlustLabel", "Verlust en €")}>
                <input type="number" inputMode="decimal" style={inputStyle} placeholder="0,00" value={lsForm.verlust} onChange={(e) => setLsForm({ ...lsForm, verlust: e.target.value })} />
              </Field>

              <span style={fieldLabelStyle}>{t("inventurmassnahme.belegLabel", "Beleg (Foto/PDF du bon)")}</span>
              {imBelegField(lsForm, setLsForm, lsBelegInputRef)}

              <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "18px 0 10px" }}>
                <span style={{ width: 22, height: 22, borderRadius: 8, background: A.navy, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, flexShrink: 0 }}>✍️</span>
                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{t("inventurmassnahme.signaturesLabel", "Signatures")}</strong>
              </div>
              <span style={fieldLabelStyle}>{t("inventurmassnahme.ls.sigMitLabel", "Unterschrift Mitarbeiter")}</span>
              {/* Champ de signature masqué une fois signée (retour utilisateur explicite,
                  2026-08-28 : "dès que la signature est apposée et validée alors le champ de
                  signature doit disparaître") — jamais laisser le pad ouvert à un tracé
                  accidentel une fois une vraie signature enregistrée (locale OU reçue à
                  distance). "🗑️ Effacer" vide seulement le brouillon local (jamais envoyé au
                  serveur tant que "Speichern" n'est pas recliqué) pour rouvrir le pad si un
                  nouveau tracé est vraiment voulu — le serveur refuse de toute façon
                  d'écraser une signature déjà enregistrée par un envoi vide (voir
                  server/inventurmassnahme/routes.js). */}
              {lsForm.sigMitarbeiter ? (
                <div style={{ marginBottom: 14 }}>
                  <img src={lsForm.sigMitarbeiter} alt="" style={{ display: "block", height: 60, borderRadius: 8, border: `1px solid ${P.line}`, background: "#fff", marginBottom: 6 }} />
                  <Pressable onClick={() => setLsForm({ ...lsForm, sigMitarbeiter: "" })} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                    {t("inventurmassnahme.clearSigToResign", "🗑️ Effacer pour signer à nouveau")}
                  </Pressable>
                </div>
              ) : (
                <>
                  {renderRemoteSigButton("lieferschein_test", lsForm.id, "sigMitarbeiter", t("inventurmassnahme.ls.sigMitLabel", "Unterschrift Mitarbeiter"), lsForm.sigMitarbeiter)}
                  <div ref={lsSigMitMountRef} style={{ marginBottom: 14 }} />
                </>
              )}

              <span style={fieldLabelStyle}>{t("inventurmassnahme.ls.sigFlLabel", "Unterschrift FL")}</span>
              {lsForm.sigFl ? (
                <div style={{ marginBottom: 14 }}>
                  <img src={lsForm.sigFl} alt="" style={{ display: "block", height: 60, borderRadius: 8, border: `1px solid ${P.line}`, background: "#fff", marginBottom: 6 }} />
                  <Pressable onClick={() => setLsForm({ ...lsForm, sigFl: "" })} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                    {t("inventurmassnahme.clearSigToResign", "🗑️ Effacer pour signer à nouveau")}
                  </Pressable>
                </div>
              ) : (
                <>
                  {renderRemoteSigButton("lieferschein_test", lsForm.id, "sigFl", t("inventurmassnahme.ls.sigFlLabel", "Unterschrift FL"), lsForm.sigFl)}
                  <div ref={lsSigFlMountRef} style={{ marginBottom: 14 }} />
                </>
              )}

              <span style={fieldLabelStyle}>{t("inventurmassnahme.ls.sigBlLabel", "Unterschrift BL")}</span>
              {isNetworkLeader ? (
                lsForm.sigBl ? (
                  <div style={{ marginBottom: 14 }}>
                    <img src={lsForm.sigBl} alt="" style={{ display: "block", height: 60, borderRadius: 8, border: `1px solid ${P.line}`, background: "#fff", marginBottom: 6 }} />
                    <Pressable onClick={() => setLsForm({ ...lsForm, sigBl: "" })} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                      {t("inventurmassnahme.clearSigToResign", "🗑️ Effacer pour signer à nouveau")}
                    </Pressable>
                  </div>
                ) : (
                  <div ref={lsSigBlMountRef} style={{ marginBottom: 14 }} />
                )
              ) : lsForm.sigBl ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${A.green}1f`, border: `1.5px solid ${A.green}`, borderRadius: 14, padding: "10px 13px", marginBottom: 14 }}>
                  <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>✓</span>
                  <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: A.green, fontWeight: 700 }}>{t("inventurmassnahme.sigBlDoneNote", "Signature BL enregistrée")}</span>
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, background: P.soft, border: `1.5px solid ${P.line}`, borderRadius: 14, padding: "10px 13px", marginBottom: 8 }}>
                    <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>⏳</span>
                    <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("inventurmassnahme.sigBlPendingNote", "En attente de la signature du Bereichsleiter (depuis son propre compte)")}</span>
                  </div>
                  {/* Filet pour un BL pas immédiatement disponible sur son propre compte
                      (retour utilisateur explicite, 2026-08-28 : "même si la personne qui
                      doit signer n'est pas sur place on peut quand même la faire signer") —
                      même mécanisme de lien à distance que Mitarbeiter/FL ci-dessus, jamais
                      un remplacement de la signature "depuis son propre compte" (qui reste
                      possible et prioritaire), juste une option de secours. */}
                  {renderRemoteSigButton("lieferschein_test", lsForm.id, "sigBl", t("inventurmassnahme.ls.sigBlLabel", "Unterschrift BL"), lsForm.sigBl)}
                </>
              )}

              <span style={fieldLabelStyle}>{t("inventurmassnahme.ermahnungMuendlichLabel", "Ermahnung durch BL mündlich")}</span>
              <div style={{ marginBottom: 14 }}>{jn(lsForm.ermahnungMuendlich, (v) => setLsForm({ ...lsForm, ermahnungMuendlich: v }))}</div>

              <div className="nx-chips" style={{ display: "flex", gap: 7, marginBottom: 18 }}>
                <Pressable onClick={() => setLsForm({ ...lsForm, ermahnung: !lsForm.ermahnung })} style={{
                  flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  background: lsForm.ermahnung ? A.navy : P.soft, color: lsForm.ermahnung ? "#fff" : P.muted, border: `1.5px solid ${lsForm.ermahnung ? A.navy : P.line}`,
                }}>{lsForm.ermahnung ? "☑" : "☐"} {t("inventurmassnahme.ermahnungPill", "Ermahnung")}</Pressable>
                <Pressable onClick={() => setLsForm({ ...lsForm, abmahnung: !lsForm.abmahnung })} style={{
                  flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  background: lsForm.abmahnung ? A.navy : P.soft, color: lsForm.abmahnung ? "#fff" : P.muted, border: `1.5px solid ${lsForm.abmahnung ? A.navy : P.line}`,
                }}>{lsForm.abmahnung ? "☑" : "☐"} {t("inventurmassnahme.abmahnungPill", "Abmahnung durch VKS")}</Pressable>
              </div>

              <Pressable onClick={saveLieferscheinTest} style={{ width: "100%", ...btnSolid(A.navy), opacity: imSaving ? .6 : 1, marginBottom: lsForm.id ? 10 : 0 }}>
                {imSaving ? t("common.saving", "Enregistrement…") : t("inventurmassnahme.saveBtn", "💾 Enregistrer")}
              </Pressable>
              {/* Retour utilisateur explicite (2026-08-20) : "Enregistrer" ne quitte plus la
                  fiche ni n'exporte plus automatiquement — l'export devient une action
                  séparée, volontaire, disponible dès que la fiche existe côté serveur. */}
              {lsForm.id && (
                <Pressable onClick={() => exportLieferscheinTestPdf(lsForm)} style={{
                  width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700,
                  fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                }}>📄 {t("inventurmassnahme.exportPdfBtn", "Exporter le PDF")}</Pressable>
              )}
            </>
          );
        })()}

        {screen === "inventurmassnahme" && imScreen === "testkauf" && (() => {
          const fieldLabelStyle = { fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".04em", margin: "0 2px 7px", display: "block" };
          const jn = (value, onChange) => (
            <div style={{ display: "flex", gap: 8 }}>
              <Pressable onClick={() => onChange(true)} style={{
                flex: 1, textAlign: "center", padding: "11px 0", borderRadius: 12, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                border: `1.5px solid ${value ? A.green : P.line}`, background: value ? A.green : P.card, color: value ? "#fff" : P.muted,
              }}>✓ {t("inventurmassnahme.ja", "Ja")}</Pressable>
              <Pressable onClick={() => onChange(false)} style={{
                flex: 1, textAlign: "center", padding: "11px 0", borderRadius: 12, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                border: `1.5px solid ${!value ? A.red : P.line}`, background: !value ? A.red : P.card, color: !value ? "#fff" : P.muted,
              }}>✕ {t("inventurmassnahme.nein", "Nein")}</Pressable>
            </div>
          );
          const qrow = (label, value, onChange) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 10, background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 14, padding: "10px 13px", marginBottom: 7 }}>
              <span style={{ flex: 1, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, lineHeight: 1.35 }}>{label}</span>
              <div style={{ flex: "0 0 auto", width: 108, display: "flex", gap: 6 }}>
                <Pressable onClick={() => onChange(true)} style={{ flex: 1, textAlign: "center", padding: "7px 0", borderRadius: 9, fontWeight: 800, fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, border: `1.5px solid ${value ? A.green : P.line}`, background: value ? A.green : P.card, color: value ? "#fff" : P.muted }}>{t("inventurmassnahme.ja", "Ja")}</Pressable>
                <Pressable onClick={() => onChange(false)} style={{ flex: 1, textAlign: "center", padding: "7px 0", borderRadius: 9, fontWeight: 800, fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, border: `1.5px solid ${!value ? A.red : P.line}`, background: !value ? A.red : P.card, color: !value ? "#fff" : P.muted }}>{t("inventurmassnahme.nein", "Nein")}</Pressable>
              </div>
            </div>
          );
          const sectionTag = (num, label) => (
            <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "18px 0 10px" }}>
              <span style={{ width: 22, height: 22, borderRadius: 8, background: A.navy, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, flexShrink: 0 }}>{num}</span>
              <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{label}</strong>
            </div>
          );
          const filteredEmployees = taskEmployees.filter((e) => e.name.toLowerCase().includes(bwMitSearch.trim().toLowerCase()));
          const bwMitarbeiterIsAzubi = taskEmployees.find((e) => e.employeeId === bwForm.mitarbeiterId)?.role === "Azubi";
          const probewagen = bwForm.probewagenBetrag === "" ? null : Number(bwForm.probewagenBetrag);
          const richtiger = bwForm.richtigerBetrag === "" ? null : Number(bwForm.richtigerBetrag);
          const differenz = (probewagen != null && richtiger != null && !Number.isNaN(probewagen) && !Number.isNaN(richtiger)) ? Math.round((probewagen - richtiger) * 100) / 100 : null;
          return (
            <>
              <span style={fieldLabelStyle}>{t("inventurmassnahme.filialeLabel", "Filiale")}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: P.soft, border: `1.5px solid ${P.line}`, borderRadius: 99, padding: "8px 13px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, marginBottom: 14 }}>
                🏬 {t("home.store.label", { id: user?.store }, `Filiale ${user?.store}`)}
              </span>

              <Field label={t("inventurmassnahme.bw.datumLabel", "Datum & Uhrzeit des Testkaufs")}>
                <input type="datetime-local" style={dateTimeInputStyle} value={bwForm.datum} onChange={(e) => setBwForm({ ...bwForm, datum: e.target.value })} />
              </Field>

              <span style={fieldLabelStyle}>{t("inventurmassnahme.mitarbeiterLabel", "Mitarbeiter")}</span>
              {bwForm.mitarbeiterId ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                  <span style={{ flex: 1, display: "inline-flex", alignItems: "center", gap: 6, background: P.soft, border: `1.5px solid ${P.line}`, borderRadius: 99, padding: "8px 13px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>👤 {bwForm.mitarbeiterName}</span>
                  <Pressable onClick={() => setBwForm({ ...bwForm, mitarbeiterId: "", mitarbeiterName: "" })} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, padding: "8px 10px" }}>{t("messages.changeBtn", "Changer")}</Pressable>
                </div>
              ) : (
                <div style={{ marginBottom: 14 }}>
                  <input style={inputStyle} placeholder={t("inventurmassnahme.searchEmployeePlaceholder", "Chercher un collègue...")} value={bwMitSearch} onChange={(e) => setBwMitSearch(e.target.value)} autoComplete="off" />
                  {bwMitSearch.trim() !== "" && (
                    <div style={{ marginTop: 6 }}>
                      {filteredEmployees.length === 0 && <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "8px 2px 0" }}>{t("inventurmassnahme.noEmployeeMatch", "Aucun collègue trouvé.")}</p>}
                      {filteredEmployees.slice(0, 6).map((e) => (
                        <Pressable key={e.employeeId} onClick={() => { setBwForm({ ...bwForm, mitarbeiterId: e.employeeId, mitarbeiterName: e.name }); setBwMitSearch(""); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", borderRadius: 12, background: P.soft, marginBottom: 4 }}>
                          {e.name}
                        </Pressable>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Retour utilisateur explicite (2026-08-20) : jusqu'ici "Azubi" était un
                  Ja/Nein choisi à la main, alors que le rôle réel du collaborateur (choisi
                  juste au-dessus) le dit déjà — "Azubi" est un vrai rôle Nexora (voir
                  canonicalRoleServer côté serveur). Champ déduit automatiquement du rôle,
                  plus aucune saisie manuelle, jamais désynchronisable de la fiche employé. */}
              {bwForm.mitarbeiterId && (
                <>
                  <span style={fieldLabelStyle}>{t("inventurmassnahme.bw.azubiLabel", "Azubi")}</span>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8, marginBottom: 14, padding: "10px 13px", borderRadius: 14,
                    background: bwMitarbeiterIsAzubi ? `${A.blue}1f` : P.soft, border: `1.5px solid ${bwMitarbeiterIsAzubi ? A.blue : P.line}`,
                  }}>
                    <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{bwMitarbeiterIsAzubi ? "🎓" : "—"}</span>
                    <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: bwMitarbeiterIsAzubi ? A.blue : P.muted, fontWeight: 700 }}>
                      {bwMitarbeiterIsAzubi ? t("inventurmassnahme.bw.azubiAutoYes", "Oui — d'après le rôle du collaborateur") : t("inventurmassnahme.bw.azubiAutoNo", "Non — d'après le rôle du collaborateur")}
                    </span>
                  </div>
                </>
              )}

              <span style={fieldLabelStyle}>{t("inventurmassnahme.bw.arbeitskleidungLabel", "Arbeitskleidung")}</span>
              <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
                {["ordentlich", "ja", "nein"].map((v) => (
                  <Pressable key={v} onClick={() => setBwForm({ ...bwForm, arbeitskleidung: v })} style={{
                    flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                    background: bwForm.arbeitskleidung === v ? A.navy : P.soft, color: bwForm.arbeitskleidung === v ? "#fff" : P.ink,
                    border: `1.5px solid ${bwForm.arbeitskleidung === v ? A.navy : P.line}`,
                  }}>{{ ordentlich: t("inventurmassnahme.bw.arbeitskleidungOrdentlich", "Ordentlich"), ja: t("inventurmassnahme.ja", "Ja"), nein: t("inventurmassnahme.nein", "Nein") }[v]}</Pressable>
                ))}
              </div>

              <Field label={t("inventurmassnahme.bw.bemerkungenLabel", "Bemerkungen")}>
                <textarea style={{ ...inputStyle, minHeight: 60, resize: "none" }} placeholder={t("common.optionalPlaceholder", "Optionnel...")} value={bwForm.bemerkungen} onChange={(e) => setBwForm({ ...bwForm, bemerkungen: e.target.value })} />
              </Field>

              {sectionTag("A", t("inventurmassnahme.bw.sectionA", "Verhalten gegenüber Kunden"))}
              {qrow(t("inventurmassnahme.bw.q.begruessung", "Begrüßung und Verabschiedung durch Kassierer/in (Testeinkäufer wartet auf Begrüßung)"), bwForm.begruessung, (v) => setBwForm({ ...bwForm, begruessung: v }))}

              {sectionTag("B", t("inventurmassnahme.bw.sectionB", "Kassiervorgang"))}
              {qrow(t("inventurmassnahme.bw.q.wareAufBand", "Ware auf das Band"), bwForm.wareAufBand, (v) => setBwForm({ ...bwForm, wareAufBand: v }))}
              {qrow(t("inventurmassnahme.bw.q.blickEinkaufswagen", "Blick in den Einkaufswagen"), bwForm.blickEinkaufswagen, (v) => setBwForm({ ...bwForm, blickEinkaufswagen: v }))}
              {qrow(t("inventurmassnahme.bw.q.anhebenWare", "Anheben der Ware"), bwForm.anhebenWare, (v) => setBwForm({ ...bwForm, anhebenWare: v }))}
              {qrow(t("inventurmassnahme.bw.q.warenbewegung", "Warenbewegung auf Kassenband"), bwForm.warenbewegung, (v) => setBwForm({ ...bwForm, warenbewegung: v }))}
              {qrow(t("inventurmassnahme.bw.q.wechselgeldBestueckt", "Ist die Kasse ausreichend mit Wechselgeld bestückt?"), bwForm.wechselgeldBestueckt, (v) => setBwForm({ ...bwForm, wechselgeldBestueckt: v }))}
              {qrow(t("inventurmassnahme.bw.q.plusAppGefragt", "Wird nach der NORMA Plus-App gefragt?"), bwForm.plusAppGefragt, (v) => setBwForm({ ...bwForm, plusAppGefragt: v }))}
              {qrow(t("inventurmassnahme.bw.q.kundeWechselgeldGefragt", "Wird der Kunde nach Wechselgeld gefragt?"), bwForm.kundeWechselgeldGefragt, (v) => setBwForm({ ...bwForm, kundeWechselgeldGefragt: v }))}
              {qrow(t("inventurmassnahme.bw.q.pluSicherheit", "PLU-Sicherheit"), bwForm.pluSicherheit, (v) => setBwForm({ ...bwForm, pluSicherheit: v }))}

              <Field label={t("inventurmassnahme.bw.allgemeineAngabenLabel", "Allgemeine Angaben (z.B. Schnelligkeit)")}>
                <textarea style={{ ...inputStyle, minHeight: 60, resize: "none" }} placeholder={t("common.optionalPlaceholder", "Optionnel...")} value={bwForm.allgemeineAngaben} onChange={(e) => setBwForm({ ...bwForm, allgemeineAngaben: e.target.value })} />
              </Field>

              {sectionTag("C", t("inventurmassnahme.bw.sectionC", "Wertmäßige Fehler beim Abkassieren"))}
              <span style={fieldLabelStyle}>{t("inventurmassnahme.bw.fehlerArtikelLabel", "Artikel fehlerhaft registriert")}</span>
              {bwForm.fehlerhaftArtikel.map((a, idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
                  <span style={{ width: 20, fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, flexShrink: 0 }}>{idx + 1})</span>
                  <input style={{ ...inputStyle, flex: 1 }} placeholder={t("inventurmassnahme.bw.articlePlaceholder", "Article...")} value={a} onChange={(e) => updateBwFehlerArtikel(idx, e.target.value)} />
                  <Pressable onClick={() => removeBwFehlerArtikel(idx)} aria-label={t("claims.action.remove", "Retirer")} style={{ width: 30, height: 30, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, flexShrink: 0 }}>✕</Pressable>
                </div>
              ))}
              {bwForm.fehlerhaftArtikel.length < 6 && (
                <div style={{ textAlign: "center", margin: "6px 0 4px" }}>
                  <Pressable onClick={addBwFehlerArtikel} style={{ display: "inline-block", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.muted }}>
                    {t("inventurmassnahme.bw.addArticleBtn", "＋ Ajouter un article (max 6)")}
                  </Pressable>
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8, marginBottom: 14 }}>
                <div style={{ background: P.soft, borderRadius: 14, padding: "11px 12px" }}>
                  <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", display: "block", marginBottom: 4 }}>{t("inventurmassnahme.bw.probewagenLabel", "Probewagen")}</span>
                  <input type="number" inputMode="decimal" style={{ border: "none", background: "none", fontSize: `calc(15px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink, width: "100%", fontFamily: "inherit", padding: 0 }} placeholder="0,00 €" value={bwForm.probewagenBetrag} onChange={(e) => setBwForm({ ...bwForm, probewagenBetrag: e.target.value })} />
                </div>
                <div style={{ background: P.soft, borderRadius: 14, padding: "11px 12px" }}>
                  <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", display: "block", marginBottom: 4 }}>{t("inventurmassnahme.bw.richtigerBetragLabel", "Richtiger Betrag")}</span>
                  <input type="number" inputMode="decimal" style={{ border: "none", background: "none", fontSize: `calc(15px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink, width: "100%", fontFamily: "inherit", padding: 0 }} placeholder="0,00 €" value={bwForm.richtigerBetrag} onChange={(e) => setBwForm({ ...bwForm, richtigerBetrag: e.target.value })} />
                </div>
                {differenz != null && (
                  <div style={{ gridColumn: "span 2", background: "rgba(231,138,18,.14)", border: "1.5px solid rgba(231,138,18,.3)", borderRadius: 14, padding: "11px 12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.orange, textTransform: "uppercase" }}>{t("inventurmassnahme.bw.differenzLabel", "Differenz")}</span>
                    <strong style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`, color: A.orange }}>{differenz >= 0 ? "+" : ""}{fmt2(differenz)} €</strong>
                  </div>
                )}
              </div>

              <span style={fieldLabelStyle}>{t("inventurmassnahme.bw.testkaufBestandenLabel", "Testkauf bestanden")}</span>
              <div style={{ marginBottom: 14 }}>{jn(bwForm.testkaufBestanden, (v) => setBwForm({ ...bwForm, testkaufBestanden: v }))}</div>

              <span style={fieldLabelStyle}>{t("inventurmassnahme.belegLabel", "Beleg (Foto/PDF du bon)")}</span>
              {imBelegField(bwForm, setBwForm, bwBelegInputRef)}

              <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "18px 0 10px" }}>
                <span style={{ width: 22, height: 22, borderRadius: 8, background: A.navy, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, flexShrink: 0 }}>✍️</span>
                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{t("inventurmassnahme.signaturesLabel", "Signatures")}</strong>
              </div>
              <span style={fieldLabelStyle}>{t("inventurmassnahme.bw.sigTkLabel", "Name und Unterschrift des Testkäufers")}</span>
              {bwForm.sigTestkaeufer ? (
                <div style={{ marginBottom: 14 }}>
                  <img src={bwForm.sigTestkaeufer} alt="" style={{ display: "block", height: 60, borderRadius: 8, border: `1px solid ${P.line}`, background: "#fff", marginBottom: 6 }} />
                  <Pressable onClick={() => setBwForm({ ...bwForm, sigTestkaeufer: "" })} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                    {t("inventurmassnahme.clearSigToResign", "🗑️ Effacer pour signer à nouveau")}
                  </Pressable>
                </div>
              ) : (
                <>
                  {renderRemoteSigButton("testkauf", bwForm.id, "sigTestkaeufer", t("inventurmassnahme.bw.sigTkLabel", "Name und Unterschrift des Testkäufers"), bwForm.sigTestkaeufer)}
                  <div ref={bwSigTkMountRef} style={{ marginBottom: 14 }} />
                </>
              )}
              <span style={fieldLabelStyle}>{t("inventurmassnahme.bw.sigBlLabel", "Unterschrift der Bereichsleitung")}</span>
              {isNetworkLeader ? (
                bwForm.sigBereichsleitung ? (
                  <div style={{ marginBottom: 14 }}>
                    <img src={bwForm.sigBereichsleitung} alt="" style={{ display: "block", height: 60, borderRadius: 8, border: `1px solid ${P.line}`, background: "#fff", marginBottom: 6 }} />
                    <Pressable onClick={() => setBwForm({ ...bwForm, sigBereichsleitung: "" })} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                      {t("inventurmassnahme.clearSigToResign", "🗑️ Effacer pour signer à nouveau")}
                    </Pressable>
                  </div>
                ) : (
                  <div ref={bwSigBlMountRef} style={{ marginBottom: 14 }} />
                )
              ) : bwForm.sigBereichsleitung ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${A.green}1f`, border: `1.5px solid ${A.green}`, borderRadius: 14, padding: "10px 13px", marginBottom: 14 }}>
                  <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>✓</span>
                  <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: A.green, fontWeight: 700 }}>{t("inventurmassnahme.sigBlDoneNote", "Signature BL enregistrée")}</span>
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, background: P.soft, border: `1.5px solid ${P.line}`, borderRadius: 14, padding: "10px 13px", marginBottom: 8 }}>
                    <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>⏳</span>
                    <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("inventurmassnahme.sigBlPendingNote", "En attente de la signature du Bereichsleiter (depuis son propre compte)")}</span>
                  </div>
                  {renderRemoteSigButton("testkauf", bwForm.id, "sigBereichsleitung", t("inventurmassnahme.bw.sigBlLabel", "Unterschrift der Bereichsleitung"), bwForm.sigBereichsleitung)}
                </>
              )}

              <span style={fieldLabelStyle}>{t("inventurmassnahme.ermahnungMuendlichLabel", "Ermahnung durch BL mündlich")}</span>
              <div style={{ marginBottom: 10 }}>{jn(bwForm.ermahnungMuendlich, (v) => setBwForm({ ...bwForm, ermahnungMuendlich: v }))}</div>
              {bwForm.ermahnungMuendlich && (
                <Field label={t("inventurmassnahme.bw.ermahnungDatumLabel", "Datum")}>
                  <input type="date" style={dateTimeInputStyle} value={bwForm.ermahnungDatum} onChange={(e) => setBwForm({ ...bwForm, ermahnungDatum: e.target.value })} />
                </Field>
              )}

              <div className="nx-chips" style={{ display: "flex", gap: 7, marginBottom: 14 }}>
                <Pressable onClick={() => setBwForm({ ...bwForm, ermahnung: !bwForm.ermahnung })} style={{
                  flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  background: bwForm.ermahnung ? A.navy : P.soft, color: bwForm.ermahnung ? "#fff" : P.muted, border: `1.5px solid ${bwForm.ermahnung ? A.navy : P.line}`,
                }}>{bwForm.ermahnung ? "☑" : "☐"} {t("inventurmassnahme.ermahnungPill", "Ermahnung")}</Pressable>
                <Pressable onClick={() => setBwForm({ ...bwForm, abmahnung: !bwForm.abmahnung })} style={{
                  flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  background: bwForm.abmahnung ? A.navy : P.soft, color: bwForm.abmahnung ? "#fff" : P.muted, border: `1.5px solid ${bwForm.abmahnung ? A.navy : P.line}`,
                }}>{bwForm.abmahnung ? "☑" : "☐"} {t("inventurmassnahme.abmahnungPill", "Abmahnung durch VKS")}</Pressable>
              </div>

              <Field label={t("inventurmassnahme.bw.bemerkungLabel", "Bemerkung")}>
                <textarea style={{ ...inputStyle, minHeight: 60, resize: "none" }} placeholder={t("common.optionalPlaceholder", "Optionnel...")} value={bwForm.bemerkung} onChange={(e) => setBwForm({ ...bwForm, bemerkung: e.target.value })} />
              </Field>

              <Pressable onClick={saveTestkauf} style={{ width: "100%", ...btnSolid(A.navy), opacity: imSaving ? .6 : 1, marginBottom: bwForm.id ? 10 : 0 }}>
                {imSaving ? t("common.saving", "Enregistrement…") : t("inventurmassnahme.saveBtn", "💾 Enregistrer")}
              </Pressable>
              {bwForm.id && (
                <Pressable onClick={() => exportTestkaufPdf(bwForm)} style={{
                  width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700,
                  fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                }}>📄 {t("inventurmassnahme.exportPdfBtn", "Exporter le PDF")}</Pressable>
              )}
            </>
          );
        })()}

        {/* ================= RR =================
             Bon de vérification de stock Niederlassung → Filiale (2026-08-29). Un seul hub
             (jamais 3 onglets comme Warenaustausch) : GET /api/rr renvoie déjà les bons créés
             ET reçus mélangés, triés par date — le statut coloré suffit à s'y retrouver. */}
        {screen === "rr" && rrScreen === "hub" && (() => {
          const RR_STATUS_META = {
            brouillon: { label: t("rr.status.brouillon", "Brouillon"), color: P.muted },
            envoye: { label: t("rr.status.envoye", "À traiter"), color: A.orange },
            confirme: { label: t("rr.status.confirme", "À valider"), color: A.blue },
            valide: { label: t("rr.status.valide", "Validé"), color: A.green },
            annule: { label: t("rr.status.annule", "Annulé"), color: A.red },
          };
          return (
            <div className="nx-screen-enter">
              {rrIsNiederlassung() && (
                <Pressable onClick={openRrCreate} style={{
                  width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                  background: A.amber, color: "#fff", marginBottom: 14, boxShadow: `0 10px 22px ${A.amber}4d`,
                }}>＋ {t("rr.create.btn", "Créer un bon RR")}</Pressable>
              )}
              {/* Mode démo — bons fictifs en mémoire, jamais écrits au serveur (même patron
                  que merchDemoMode, voir toggleRrDemoMode/buildRrDemoBons). */}
              <Pressable onClick={toggleRrDemoMode} style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 13px", borderRadius: 12,
                background: rrDemoMode ? "rgba(231,138,18,.13)" : P.soft, border: `1.5px solid ${rrDemoMode ? A.orange : P.line}`,
                marginBottom: 14,
              }}>
                <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))` }}>🧪</span>
                <span style={{ flex: 1, textAlign: "left", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: rrDemoMode ? A.orange : P.ink }}>
                  {rrDemoMode ? t("rr.demo.activeLabel", "MODE DÉMO actif — aucune donnée réelle touchée") : t("rr.demo.toggleLabel", "Mode démo (bons fictifs à consulter)")}
                </span>
                <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: rrDemoMode ? A.orange : P.muted }}>{rrDemoMode ? "ON" : "OFF"}</span>
              </Pressable>
              {rrLoading ? (
                <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))` }}>{t("common.loading", "Chargement…")}</p>
              ) : rrList.length === 0 ? (
                <div style={{ ...card, textAlign: "center", padding: 30, color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))` }}>{t("rr.hub.emptyText", "Aucun bon RR pour l'instant.")}</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {rrList.map((b) => {
                    const meta = RR_STATUS_META[b.status] || RR_STATUS_META.brouillon;
                    const outgoing = user?.store === b.fromStoreId;
                    return (
                      <Pressable key={b.id} onClick={() => openRrDetail(b.id)} style={{ ...card, display: "flex", alignItems: "center", gap: 10, padding: 14, width: "100%" }}>
                        <span style={{ width: 38, height: 38, borderRadius: 12, background: `${A.amber}22`, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>🧮</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{b.number}</strong>
                          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{outgoing ? `→ ${waStoreLabel(b.toStoreId)}` : `${waStoreLabel(b.fromStoreId)} →`}</span>
                        </div>
                        <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: meta.color, borderRadius: 99, padding: "4px 10px", flex: "0 0 auto" }}>{meta.label}</span>
                      </Pressable>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {screen === "rr" && rrScreen === "create" && (() => {
          const targetStores = storesCatalog.filter((s) => s.kind !== "niederlassung");
          const setLine = (i, field, value) => setRrCreateForm((f) => ({ ...f, lines: f.lines.map((l, li) => li === i ? { ...l, [field]: value } : l) }));
          const addLine = () => setRrCreateForm((f) => ({ ...f, lines: [...f.lines, { ...emptyRrLine }] }));
          const removeLine = (i) => setRrCreateForm((f) => ({ ...f, lines: f.lines.filter((_, li) => li !== i) }));
          return (
            <div className="nx-screen-enter">
              <Field label={t("rr.create.targetStoreLabel", "Filiale destinataire")}>
                <select style={inputStyle} value={rrCreateForm.toStoreId} onChange={(e) => setRrCreateForm((f) => ({ ...f, toStoreId: e.target.value }))}>
                  <option value="">{t("rr.create.targetStorePlaceholder", "Choisir…")}</option>
                  {targetStores.map((s) => <option key={s.id} value={s.id}>{s.label || s.id}</option>)}
                </select>
              </Field>
              <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".04em", display: "block", margin: "16px 0 8px" }}>{t("rr.create.articlesTitle", "Articles")}</span>
              {rrCreateForm.lines.map((l, i) => (
                <div key={i} style={{ ...card, padding: 12, marginBottom: 8 }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input style={{ ...inputStyle, flex: 1 }} placeholder={t("common.artNrLabel", "Art.Nr")} value={l.artNr} onChange={(e) => setLine(i, "artNr", e.target.value)} />
                    <Pressable onClick={() => removeLine(i)} aria-label={t("claims.action.remove", "Retirer")} style={{ width: 32, height: 32, borderRadius: 99, background: "rgba(239,68,68,.1)", color: A.red, display: "grid", placeItems: "center", flex: "0 0 auto" }}>✕</Pressable>
                  </div>
                  <input style={{ ...inputStyle, marginBottom: 8 }} placeholder={t("warenaustausch.doc.designationLabel", "Désignation")} value={l.name} onChange={(e) => setLine(i, "name", e.target.value)} />
                  <div style={{ display: "flex", gap: 8 }}>
                    <input style={{ ...inputStyle, flex: 1 }} type="number" placeholder={t("rr.doc.expectedQtyLabel", "Qté attendue")} value={l.expectedQty} onChange={(e) => setLine(i, "expectedQty", e.target.value)} />
                    <input style={{ ...inputStyle, flex: 1 }} type="number" step="0.01" placeholder={t("warenaustausch.doc.unitPriceLabel", "Prix unitaire")} value={l.unitPrice} onChange={(e) => setLine(i, "unitPrice", e.target.value)} />
                  </div>
                </div>
              ))}
              <Pressable onClick={addLine} style={{ width: "100%", textAlign: "center", padding: "11px 0", borderRadius: 14, border: `1.5px dashed ${P.line}`, color: P.muted, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, marginBottom: 16 }}>＋ {t("rr.create.addLineBtn", "Ajouter un article")}</Pressable>
              <Pressable disabled={rrCreateBusy} onClick={createRrBon} style={{ width: "100%", ...btnSolid(A.amber), opacity: rrCreateBusy ? .6 : 1 }}>
                {rrCreateBusy ? t("common.sending", "Envoi...") : t("rr.create.sendBtn", "Envoyer le bon RR")}
              </Pressable>
            </div>
          );
        })()}

        {screen === "rr" && rrScreen === "detail" && rrDetailId && (() => {
          const bon = rrFindBon(rrDetailId);
          if (!bon) return null;
          const totals = bon.totals || {};
          const isFilialeSide = user?.store === bon.toStoreId;
          const isNlSide = user?.store === bon.fromStoreId;
          return (
            <div className="nx-screen-enter">
              <div style={{ ...card, padding: 14, marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 6 }}>
                  <span>{waStoreLabel(bon.fromStoreId)} → {waStoreLabel(bon.toStoreId)}</span>
                  <span>{fmtFR(bon.sentAt || bon.createdAt)}</span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Pressable disabled={rrExporting} onClick={() => exportRrTracking(bon)} style={{ flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>📄 {t("rr.doc.exportTrackingBtn", "Bon complet")}</Pressable>
                  <Pressable disabled={rrExporting} onClick={() => exportRrSticker(bon)} style={{ flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>🏷️ {t("rr.doc.exportStickerBtn", "Étiquette palette")}</Pressable>
                </div>
              </div>

              <div style={{ ...card, overflow: "hidden", marginBottom: 14 }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", minWidth: 540, borderCollapse: "collapse", fontSize: `calc(11px * var(--nx-font-scale, 1))` }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${P.line}` }}>
                        <th style={{ padding: "8px 10px", textAlign: "left", color: P.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{t("rr.doc.colArtNr", "N° art.")}</th>
                        <th style={{ padding: "8px 10px", textAlign: "left", color: P.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{t("rr.doc.colName", "Désignation")}</th>
                        <th style={{ padding: "8px 10px", textAlign: "right", color: P.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{t("rr.doc.colSoll", "Soll")}</th>
                        <th style={{ padding: "8px 10px", textAlign: "right", color: P.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{t("rr.doc.colIst", "Ist")}</th>
                        <th style={{ padding: "8px 10px", textAlign: "right", color: P.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{t("rr.doc.colPrice", "Verkaufspreis")}</th>
                        <th style={{ padding: "8px 10px", textAlign: "right", color: P.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{t("rr.doc.colTotal", "Gesamtpreis")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bon.lines.map((l) => {
                        const lineQty = l.foundQty != null ? l.foundQty : l.expectedQty;
                        const lineTotal = lineQty * l.unitPrice;
                        return (
                          <tr key={l.id} style={{ borderTop: `1px solid ${P.line}` }}>
                            <td style={{ padding: "8px 10px", whiteSpace: "nowrap", color: P.muted }}>{l.artNr}</td>
                            <td style={{ padding: "8px 10px", fontWeight: 700 }}>{l.name}</td>
                            <td style={{ padding: "8px 10px", textAlign: "right", whiteSpace: "nowrap" }}>{l.expectedQty}</td>
                            <td style={{ padding: "8px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                              {isFilialeSide && bon.status === "envoye" ? (
                                <input style={{ ...inputStyle, width: 56, padding: "5px 6px", textAlign: "right", fontSize: "inherit" }} type="number"
                                  defaultValue={l.foundQty != null ? l.foundQty : ""}
                                  onBlur={(e) => saveRrLineFound(bon.id, l.id, e.target.value)} />
                              ) : (l.foundQty != null ? l.foundQty : "—")}
                            </td>
                            <td style={{ padding: "8px 10px", textAlign: "right", whiteSpace: "nowrap" }}>{l.unitPrice.toFixed(2)} €</td>
                            <td style={{ padding: "8px 10px", textAlign: "right", whiteSpace: "nowrap", fontWeight: 800 }}>{l.foundQty != null ? lineTotal.toFixed(2) + " €" : "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ ...card, padding: 14, marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: `calc(12px * var(--nx-font-scale, 1))`, marginBottom: 4 }}>
                  <span style={{ color: P.muted }}>{t("rr.doc.totalExpectedLabel", "Total attendu")}</span><strong>{totals.totalExpected?.toFixed(2)} €</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: `calc(12px * var(--nx-font-scale, 1))`, marginBottom: 4 }}>
                  <span style={{ color: P.muted }}>{t("rr.doc.totalFoundLabel", "Total trouvé")}</span><strong>{totals.totalFound != null ? totals.totalFound.toFixed(2) + " €" : "—"}</strong>
                </div>
                {totals.diff != null && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: `calc(13px * var(--nx-font-scale, 1))` }}>
                    <span style={{ color: P.muted }}>{t("rr.doc.diffLabel", "Écart")}</span>
                    <strong style={{ color: totals.diff === 0 ? A.green : totals.diff > 0 ? A.blue : A.red }}>{totals.diff >= 0 ? "+" : ""}{totals.diff.toFixed(2)} €</strong>
                  </div>
                )}
              </div>

              {isFilialeSide && bon.status === "envoye" && (
                <>
                  <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                    <Field label={t("warenaustausch.doc.cartonsLabel", "Cartons")}>
                      <input style={inputStyle} type="number" value={rrCartonsDraft} onChange={(e) => setRrCartonsDraft(e.target.value)} onBlur={() => saveRrLogistics(bon.id)} />
                    </Field>
                    <Field label={t("warenaustausch.doc.palletsLabel", "Palettes")}>
                      <input style={inputStyle} type="number" value={rrPalletsDraft} onChange={(e) => setRrPalletsDraft(e.target.value)} onBlur={() => saveRrLogistics(bon.id)} />
                    </Field>
                  </div>
                  <section style={{ ...card, borderRadius: 18, padding: 14, marginBottom: 14 }}>
                    <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 8 }}>✍️ {t("rr.doc.filialeSignatureLabel", "Signature filiale")}</strong>
                    {bon.filialeSignatureUrl ? (
                      <img src={bon.filialeSignatureUrl.startsWith("data:") ? bon.filialeSignatureUrl : `/api/rr/${bon.id}/signature/filiale?_=${bon.updatedAt}`} alt={t("rr.doc.filialeSignatureLabel", "Signature filiale")} style={{ height: 80, objectFit: "contain" }} />
                    ) : (
                      <>
                        <div ref={rrSigMountRef} style={{ marginBottom: 10 }} />
                        <Pressable disabled={!rrSigReady || rrBusy} onClick={submitRrSignature} style={{ width: "100%", ...btnSolid(A.amber), padding: "11px 0", opacity: rrSigReady && !rrBusy ? 1 : .5 }}>
                          {rrBusy ? t("common.saving", "Enregistrement…") : t("rr.detail.signBtn", "Valider la signature")}
                        </Pressable>
                      </>
                    )}
                  </section>
                  <Pressable disabled={rrBusy || !totals.allFilled || !bon.filialeSignatureUrl || (!bon.cartonsCount && !bon.palletsCount)} onClick={() => confirmRrBon(bon.id)} style={{ width: "100%", ...btnSolid(A.amber), opacity: (rrBusy || !totals.allFilled || !bon.filialeSignatureUrl || (!bon.cartonsCount && !bon.palletsCount)) ? .5 : 1, marginBottom: 10 }}>
                    {rrBusy ? t("common.sending", "Envoi...") : t("rr.detail.confirmBtn", "Confirmer le bon")}
                  </Pressable>
                </>
              )}

              {isNlSide && bon.status === "confirme" && (
                <>
                  <section style={{ ...card, borderRadius: 18, padding: 14, marginBottom: 14 }}>
                    <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 8 }}>✍️ {t("rr.doc.niederlassungSignatureLabel", "Signature Niederlassung")}</strong>
                    {bon.niederlassungSignatureUrl ? (
                      <img src={bon.niederlassungSignatureUrl.startsWith("data:") ? bon.niederlassungSignatureUrl : `/api/rr/${bon.id}/signature/niederlassung?_=${bon.updatedAt}`} alt={t("rr.doc.niederlassungSignatureLabel", "Signature Niederlassung")} style={{ height: 80, objectFit: "contain" }} />
                    ) : (
                      <>
                        <div ref={rrNlSigMountRef} style={{ marginBottom: 10 }} />
                        <Pressable disabled={!rrNlSigReady || rrBusy} onClick={submitRrNlSignature} style={{ width: "100%", ...btnSolid(A.green), padding: "11px 0", opacity: rrNlSigReady && !rrBusy ? 1 : .5 }}>
                          {rrBusy ? t("common.saving", "Enregistrement…") : t("rr.detail.signBtn", "Valider la signature")}
                        </Pressable>
                      </>
                    )}
                  </section>
                  <Pressable disabled={rrBusy || !bon.niederlassungSignatureUrl} onClick={() => validateRrBon(bon.id)} style={{ width: "100%", ...btnSolid(A.green), opacity: (rrBusy || !bon.niederlassungSignatureUrl) ? .5 : 1, marginBottom: 10 }}>
                    {rrBusy ? t("common.sending", "Envoi...") : t("rr.detail.validateBtn", "Valider le bon")}
                  </Pressable>
                </>
              )}

              {["brouillon", "envoye"].includes(bon.status) && (user?.store === bon.fromStoreId) && (
                <Pressable onClick={() => cancelRrBon(bon.id)} style={{ width: "100%", textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: A.red }}>{t("rr.detail.cancelBtn", "Annuler ce bon")}</Pressable>
              )}
            </div>
          );
        })()}

        {/* ================= KONTOAUSZUG =================
             Contrôle de conformité (2026-08-30) — la filiale compare le relevé envoyé par la
             Niederlassung à ses propres transactions et déclare elle-même conforme/non,
             jamais déduit automatiquement d'un écart de montant. */}
        {screen === "kontoauszug" && kontoScreen === "hub" && (() => {
          const KONTO_STATUS_META = {
            conforme: { label: t("konto.status.conforme", "Conforme"), color: A.green },
            anomalie: { label: t("konto.status.anomalie", "Anomalie"), color: A.orange },
            resolue: { label: t("konto.status.resolue", "Résolue"), color: A.blue },
          };
          return (
            <div className="nx-screen-enter">
              <Pressable onClick={openKontoCreate} style={{
                width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                background: A.navy, color: "#fff", marginBottom: 14, boxShadow: `0 10px 22px ${A.navy}4d`,
              }}>＋ {t("konto.create.btn", "Nouveau contrôle")}</Pressable>
              {/* Mode démo — contrôles fictifs en mémoire, jamais écrits au serveur (même
                  patron que rrDemoMode, voir toggleKontoDemoMode/buildKontoDemoChecks). */}
              <Pressable onClick={toggleKontoDemoMode} style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 13px", borderRadius: 12,
                background: kontoDemoMode ? "rgba(231,138,18,.13)" : P.soft, border: `1.5px solid ${kontoDemoMode ? A.orange : P.line}`,
                marginBottom: 14,
              }}>
                <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))` }}>🧪</span>
                <span style={{ flex: 1, textAlign: "left", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: kontoDemoMode ? A.orange : P.ink }}>
                  {kontoDemoMode ? t("konto.demo.activeLabel", "MODE DÉMO actif — aucune donnée réelle touchée") : t("konto.demo.toggleLabel", "Mode démo (contrôles fictifs à consulter)")}
                </span>
                <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: kontoDemoMode ? A.orange : P.muted }}>{kontoDemoMode ? "ON" : "OFF"}</span>
              </Pressable>
              {kontoLoading ? (
                <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))` }}>{t("common.loading", "Chargement…")}</p>
              ) : kontoChecks.length === 0 ? (
                <div style={{ ...card, textAlign: "center", padding: 30, color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))` }}>{t("konto.hub.emptyText", "Aucun contrôle pour l'instant.")}</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {kontoChecks.map((c) => {
                    const meta = KONTO_STATUS_META[c.status] || KONTO_STATUS_META.conforme;
                    return (
                      <Pressable key={c.id} onClick={() => openKontoDetail(c.id)} style={{ ...card, display: "flex", alignItems: "center", gap: 10, padding: 14, width: "100%" }}>
                        <span style={{ width: 38, height: 38, borderRadius: 12, background: `${A.navy}22`, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>🏦</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{fmtFR(c.periodStart)} → {fmtFR(c.periodEnd)}</strong>
                          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("konto.hub.declaredLabel", "Déclaré")} {c.declaredAmount.toFixed(2)} €</span>
                        </div>
                        <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: meta.color, borderRadius: 99, padding: "4px 10px", flex: "0 0 auto" }}>{meta.label}</span>
                      </Pressable>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {screen === "kontoauszug" && kontoScreen === "create" && (() => {
          const f = kontoCreateForm;
          const setField = (field, value) => setKontoCreateForm((prev) => ({ ...prev, [field]: value }));
          const setDeclaredForEntry = (entryId, value) => setKontoCreateForm((prev) => ({ ...prev, declaredByEntry: { ...prev.declaredByEntry, [entryId]: value } }));
          // fieldLabelStyle est local à chaque écran IIFE ailleurs dans ce fichier (jamais une
          // constante globale) — bug réel trouvé en vidéo (ReferenceError, écran blanc) :
          // réutilisé ici sans être défini dans CE scope précis.
          const fieldLabelStyle = { fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".04em", margin: "0 2px 7px", display: "block" };
          const KONTO_MOTIF_FALLBACK = {
            warenaustausch: "Warenaustausch", warenrueckfuehrung: "Retour marchandise",
            rr: "RR", preisanderung: "Preisänderung",
          };
          const groups = kontoPreview ? kontoGroupByCategory(kontoPreview.entriesSnapshot, (e) => f.declaredByEntry[e.id]) : [];
          const calculatedTotal = kontoPreview ? kontoPreview.calculatedAmount : 0;
          const declaredTotal = round2ClientKonto(groups.reduce((s, g) => s + g.declaredSubtotal, 0));
          const diffTotal = round2ClientKonto(calculatedTotal - declaredTotal);
          return (
            <div className="nx-screen-enter">
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                <Field label={t("konto.create.periodStartLabel", "Début période")}>
                  <input type="date" style={dateTimeInputStyle} value={f.periodStart} onChange={(e) => setField("periodStart", e.target.value)} onBlur={() => loadKontoPreview(f.periodStart, f.periodEnd)} />
                </Field>
                <Field label={t("konto.create.periodEndLabel", "Fin période")}>
                  <input type="date" style={dateTimeInputStyle} value={f.periodEnd} onChange={(e) => setField("periodEnd", e.target.value)} onBlur={() => loadKontoPreview(f.periodStart, f.periodEnd)} />
                </Field>
              </div>
              <p style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 0 14px" }}>{t("konto.create.hint", "Pour chaque bon ci-dessous, saisis le montant indiqué par la Niederlassung sur son relevé — compare catégorie par catégorie, jamais un seul total.")}</p>

              {kontoPreviewLoading && (
                <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12px * var(--nx-font-scale, 1))` }}>{t("common.loading", "Chargement…")}</p>
              )}
              {kontoPreview && groups.length === 0 && (
                <div style={{ ...card, textAlign: "center", padding: 24, color: P.muted, fontSize: `calc(12px * var(--nx-font-scale, 1))`, marginBottom: 14 }}>{t("konto.detail.breakdownEmpty", "Aucune écriture trouvée sur cette période.")}</div>
              )}
              {groups.length > 0 && (() => {
                const allEntries = groups.flatMap((g) => g.rows);
                const allVerified = allEntries.every((e) => f.verdictsByEntry[e.id]);
                const rowDiffOf = (e) => {
                  const declaredVal = f.declaredByEntry[e.id];
                  return declaredVal == null || declaredVal === "" ? null : round2ClientKonto(e.betrag - Number(declaredVal));
                };
                const verdictColor = (v) => v === "conforme" ? A.green : v === "accepte" ? A.blue : v === "anomalie" ? A.red : P.line;
                return (
                  <div style={{ ...card, overflow: "hidden", marginBottom: 14 }}>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", minWidth: 680, borderCollapse: "collapse", fontSize: `calc(10.5px * var(--nx-font-scale, 1))` }}>
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${P.line}` }}>
                            <th style={{ padding: "8px 10px", textAlign: "left", color: P.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{t("konto.detail.colCategory", "Catégorie")}</th>
                            <th style={{ padding: "8px 10px", textAlign: "left", color: P.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{t("konto.detail.colBon", "Bon")}</th>
                            <th style={{ padding: "8px 10px", textAlign: "right", color: A.green, fontWeight: 700, whiteSpace: "nowrap" }}>{t("konto.detail.gutschrift", "Gutschrift")}</th>
                            <th style={{ padding: "8px 10px", textAlign: "right", color: A.red, fontWeight: 700, whiteSpace: "nowrap" }}>{t("konto.detail.belastung", "Belastung")}</th>
                            <th style={{ padding: "8px 10px", textAlign: "right", color: P.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{t("konto.create.colDeclared", "Niederlassung")}</th>
                            <th style={{ padding: "8px 10px", textAlign: "center", color: P.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{t("konto.create.colVerify", "Vérification")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groups.map((g) => (
                            <Fragment key={g.motif}>
                              {g.rows.map((e) => {
                                const declaredVal = f.declaredByEntry[e.id];
                                const verdict = f.verdictsByEntry[e.id];
                                const rowDiff = verdict ? rowDiffOf(e) : null;
                                return (
                                  <Fragment key={e.id}>
                                    <tr style={{ borderTop: `1px solid ${P.line}` }}>
                                      <td style={{ padding: "7px 10px", whiteSpace: "nowrap", color: P.muted }}>{t(`konto.motif.${g.motif}`, KONTO_MOTIF_FALLBACK[g.motif] || g.motif)}</td>
                                      <td style={{ padding: "7px 10px", whiteSpace: "nowrap", fontWeight: 700 }}>{e.relatedTransferId || e.id}</td>
                                      <td style={{ padding: "7px 10px", textAlign: "right", whiteSpace: "nowrap", fontWeight: 700, color: A.green }}>{e.art === "gutschrift" ? `${e.betrag.toFixed(2)} €` : "—"}</td>
                                      <td style={{ padding: "7px 10px", textAlign: "right", whiteSpace: "nowrap", fontWeight: 700, color: A.red }}>{e.art === "belastung" ? `${e.betrag.toFixed(2)} €` : "—"}</td>
                                      <td style={{ padding: "7px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                                        <input type="number" step="0.01" placeholder="0.00" value={declaredVal ?? ""} onChange={(ev) => setDeclaredForEntry(e.id, ev.target.value)}
                                          style={{ ...inputStyle, width: 62, padding: "5px 6px", textAlign: "right", fontSize: "inherit" }} />
                                      </td>
                                      <td style={{ padding: "6px 8px" }}>
                                        <div style={{ display: "flex", gap: 3, justifyContent: "center" }}>
                                          <Pressable onClick={() => setKontoVerdict(e.id, "conforme")} title={t("konto.create.verdictConforme", "Conforme")} style={{ width: 22, height: 22, borderRadius: 6, display: "grid", placeItems: "center", fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, background: verdict === "conforme" ? A.green : P.soft, color: verdict === "conforme" ? "#fff" : P.muted, border: `1px solid ${verdict === "conforme" ? A.green : P.line}` }}>✓</Pressable>
                                          <Pressable onClick={() => setKontoVerdict(e.id, "accepte")} title={t("konto.create.verdictAccepte", "Accepter malgré l'écart")} style={{ width: 22, height: 22, borderRadius: 6, display: "grid", placeItems: "center", fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, background: verdict === "accepte" ? A.blue : P.soft, color: verdict === "accepte" ? "#fff" : P.muted, border: `1px solid ${verdict === "accepte" ? A.blue : P.line}` }}>≈</Pressable>
                                          <Pressable onClick={() => setKontoVerdict(e.id, "anomalie")} title={t("konto.create.verdictAnomalie", "Anomalie")} style={{ width: 22, height: 22, borderRadius: 6, display: "grid", placeItems: "center", fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, background: verdict === "anomalie" ? A.red : P.soft, color: verdict === "anomalie" ? "#fff" : P.muted, border: `1px solid ${verdict === "anomalie" ? A.red : P.line}` }}>✗</Pressable>
                                        </div>
                                        {verdict && (
                                          <div style={{ textAlign: "center", marginTop: 3, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: rowDiff === 0 ? A.green : verdictColor(verdict) }}>
                                            {rowDiff == null ? "—" : `${rowDiff >= 0 ? "+" : ""}${rowDiff.toFixed(2)} €`}
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                    {verdict === "anomalie" && (
                                      <tr style={{ background: `${A.red}0d` }}>
                                        <td colSpan={6} style={{ padding: "6px 10px 9px" }}>
                                          <input value={f.notesByEntry[e.id] || ""} onChange={(ev) => setKontoEntryNote(e.id, ev.target.value)}
                                            placeholder={t("konto.create.entryNotePlaceholder", "Décris l'anomalie pour ce bon (destiné à la Niederlassung)...")}
                                            style={{ ...inputStyle, width: "100%", fontSize: `calc(11px * var(--nx-font-scale, 1))` }} />
                                        </td>
                                      </tr>
                                    )}
                                  </Fragment>
                                );
                              })}
                              <tr style={{ borderTop: `1px solid ${P.line}`, background: P.soft }}>
                                <td colSpan={2} style={{ padding: "6px 10px", fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".03em" }}>{t("konto.create.subtotal", "Sous-total")}</td>
                                <td colSpan={2} style={{ padding: "6px 10px", textAlign: "right", fontWeight: 800 }}>{g.calculatedSubtotal.toFixed(2)} €</td>
                                <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 800 }}>{g.declaredSubtotal.toFixed(2)} €</td>
                                <td style={{ padding: "6px 10px", textAlign: "center", fontWeight: 800, color: g.rows.every((e) => f.verdictsByEntry[e.id]) ? (g.calculatedSubtotal === g.declaredSubtotal ? A.green : A.red) : P.muted }}>
                                  {g.rows.every((e) => f.verdictsByEntry[e.id]) ? `${(g.calculatedSubtotal - g.declaredSubtotal >= 0 ? "+" : "")}${round2ClientKonto(g.calculatedSubtotal - g.declaredSubtotal).toFixed(2)} €` : "—"}
                                </td>
                              </tr>
                            </Fragment>
                          ))}
                          <tr style={{ borderTop: `2px solid ${P.line}` }}>
                            <td colSpan={2} style={{ padding: "9px 10px", fontWeight: 800 }}>{t("konto.create.grandTotal", "Total")}</td>
                            <td colSpan={2} style={{ padding: "9px 10px", textAlign: "right", fontWeight: 800 }}>{calculatedTotal.toFixed(2)} €</td>
                            <td style={{ padding: "9px 10px", textAlign: "right", fontWeight: 800 }}>{declaredTotal.toFixed(2)} €</td>
                            <td style={{ padding: "9px 10px", textAlign: "center", fontWeight: 800, color: allVerified ? (diffTotal === 0 ? A.green : A.red) : P.muted }}>
                              {allVerified ? `${diffTotal >= 0 ? "+" : ""}${diffTotal.toFixed(2)} €` : "—"}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    {!allVerified && (
                      <p style={{ margin: 0, padding: "8px 14px 12px", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("konto.create.verifyHint", "✓ conforme · ≈ accepter malgré l'écart · ✗ anomalie — vérifie chaque bon pour révéler son écart.")}</p>
                    )}
                  </div>
                );
              })()}

              <Pressable disabled={kontoCreateBusy} onClick={createKontoCheck} style={{ width: "100%", ...btnSolid(A.navy), opacity: kontoCreateBusy ? .5 : 1, marginTop: 6 }}>
                {kontoCreateBusy ? t("common.sending", "Envoi...") : t("konto.create.submitBtn", "Enregistrer le contrôle")}
              </Pressable>
            </div>
          );
        })()}

        {screen === "kontoauszug" && kontoScreen === "detail" && kontoDetailId && (() => {
          const check = kontoFindCheck(kontoDetailId);
          if (!check) return null;
          const canManage = isCheckAccessibleClient(check);
          // Motif "warenrueckfuehrung" = catégorie interne à Warenaustausch ("RR (manquant)",
          // retour filiale→Niederlassung) — jamais confondue avec le motif "rr" du module RR
          // (Niederlassung→filiale, sens inverse), d'où des libellés délibérément distincts.
          const KONTO_MOTIF_FALLBACK = {
            warenaustausch: "Warenaustausch", warenrueckfuehrung: "Retour marchandise",
            rr: "RR", preisanderung: "Preisänderung",
          };
          return (
            <div className="nx-screen-enter">
              <div style={{ ...card, padding: 14, marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 10 }}>
                  <span>{fmtFR(check.periodStart)} → {fmtFR(check.periodEnd)}</span>
                  <span>{waStoreLabel(check.storeId)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: `calc(12px * var(--nx-font-scale, 1))`, marginBottom: 4 }}>
                  <span style={{ color: P.muted }}>{t("konto.detail.declaredLabel", "Montant déclaré (Niederlassung)")}</span><strong>{check.declaredAmount.toFixed(2)} €</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: `calc(12px * var(--nx-font-scale, 1))`, marginBottom: 4 }}>
                  <span style={{ color: P.muted }}>{t("konto.detail.calculatedLabel", "Montant calculé (nos données)")}</span><strong>{check.calculatedAmount.toFixed(2)} €</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: `calc(13px * var(--nx-font-scale, 1))` }}>
                  <span style={{ color: P.muted }}>{t("konto.detail.diffLabel", "Écart")}</span>
                  <strong style={{ color: check.diff === 0 ? A.green : check.diff > 0 ? A.blue : A.red }}>{check.diff >= 0 ? "+" : ""}{check.diff.toFixed(2)} €</strong>
                </div>
              </div>

              {/* Détail PAR CATÉGORIE puis par bon (retour utilisateur explicite, 2026-08-30) :
                  jamais un seul chiffre opaque, jamais tous les bons mélangés — chaque
                  catégorie a son propre groupe, chaque bon compare nous vs Niederlassung.
                  entriesSnapshot (déclaré inclus) est figé à la création, jamais recalculé. */}
              {(() => {
                const groups = kontoGroupByCategory(check.entriesSnapshot, (e) => e.declaredBetrag);
                if (groups.length === 0) {
                  return <div style={{ ...card, textAlign: "center", padding: 24, color: P.muted, fontSize: `calc(12px * var(--nx-font-scale, 1))`, marginBottom: 14 }}>{t("konto.detail.breakdownEmpty", "Aucune écriture trouvée sur cette période.")}</div>;
                }
                return (
                  <div style={{ ...card, overflow: "hidden", marginBottom: 14 }}>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", minWidth: 620, borderCollapse: "collapse", fontSize: `calc(10.5px * var(--nx-font-scale, 1))` }}>
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${P.line}` }}>
                            <th style={{ padding: "8px 10px", textAlign: "left", color: P.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{t("konto.detail.colCategory", "Catégorie")}</th>
                            <th style={{ padding: "8px 10px", textAlign: "left", color: P.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{t("konto.detail.colBon", "Bon")}</th>
                            <th style={{ padding: "8px 10px", textAlign: "right", color: A.green, fontWeight: 700, whiteSpace: "nowrap" }}>{t("konto.detail.gutschrift", "Gutschrift")}</th>
                            <th style={{ padding: "8px 10px", textAlign: "right", color: A.red, fontWeight: 700, whiteSpace: "nowrap" }}>{t("konto.detail.belastung", "Belastung")}</th>
                            <th style={{ padding: "8px 10px", textAlign: "right", color: P.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{t("konto.create.colDeclared", "Niederlassung")}</th>
                            <th style={{ padding: "8px 10px", textAlign: "center", color: P.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{t("konto.create.colVerify", "Vérification")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groups.map((g) => (
                            <Fragment key={g.motif}>
                              {g.rows.map((e) => {
                                const rowDiff = e.declaredBetrag == null ? null : round2ClientKonto(e.betrag - e.declaredBetrag);
                                const VERDICT_META = {
                                  conforme: { label: t("konto.create.verdictConforme", "Conforme"), color: A.green },
                                  accepte: { label: t("konto.create.verdictAccepte", "Accepté"), color: A.blue },
                                  anomalie: { label: t("konto.create.verdictAnomalie", "Anomalie"), color: A.red },
                                };
                                const vm = VERDICT_META[e.verdict];
                                return (
                                  <Fragment key={e.id}>
                                    <tr style={{ borderTop: `1px solid ${P.line}` }}>
                                      <td style={{ padding: "7px 10px", whiteSpace: "nowrap", color: P.muted }}>{t(`konto.motif.${g.motif}`, KONTO_MOTIF_FALLBACK[g.motif] || g.motif)}</td>
                                      <td style={{ padding: "7px 10px", whiteSpace: "nowrap", fontWeight: 700 }}>{e.relatedTransferId || e.id}</td>
                                      <td style={{ padding: "7px 10px", textAlign: "right", whiteSpace: "nowrap", fontWeight: 700, color: A.green }}>{e.art === "gutschrift" ? `${e.betrag.toFixed(2)} €` : "—"}</td>
                                      <td style={{ padding: "7px 10px", textAlign: "right", whiteSpace: "nowrap", fontWeight: 700, color: A.red }}>{e.art === "belastung" ? `${e.betrag.toFixed(2)} €` : "—"}</td>
                                      <td style={{ padding: "7px 10px", textAlign: "right", whiteSpace: "nowrap" }}>{e.declaredBetrag == null ? "—" : `${e.declaredBetrag.toFixed(2)} €`}</td>
                                      <td style={{ padding: "6px 8px", textAlign: "center" }}>
                                        {vm && <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: vm.color, borderRadius: 99, padding: "3px 7px", whiteSpace: "nowrap" }}>{vm.label}</span>}
                                        {rowDiff != null && <div style={{ marginTop: 3, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: rowDiff === 0 ? A.green : (vm ? vm.color : A.red) }}>{rowDiff >= 0 ? "+" : ""}{rowDiff.toFixed(2)} €</div>}
                                      </td>
                                    </tr>
                                    {e.verdict === "anomalie" && e.entryNote && (
                                      <tr style={{ background: `${A.red}0d` }}>
                                        <td colSpan={6} style={{ padding: "5px 10px 8px", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.ink }}>💬 {e.entryNote}</td>
                                      </tr>
                                    )}
                                  </Fragment>
                                );
                              })}
                              <tr style={{ borderTop: `1px solid ${P.line}`, background: P.soft }}>
                                <td colSpan={2} style={{ padding: "6px 10px", fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".03em" }}>{t("konto.create.subtotal", "Sous-total")}</td>
                                <td colSpan={2} style={{ padding: "6px 10px", textAlign: "right", fontWeight: 800 }}>{g.calculatedSubtotal.toFixed(2)} €</td>
                                <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 800 }}>{g.declaredSubtotal.toFixed(2)} €</td>
                                <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 800, color: g.calculatedSubtotal === g.declaredSubtotal ? A.green : A.red }}>{(g.calculatedSubtotal - g.declaredSubtotal >= 0 ? "+" : "")}{round2ClientKonto(g.calculatedSubtotal - g.declaredSubtotal).toFixed(2)} €</td>
                              </tr>
                            </Fragment>
                          ))}
                          <tr style={{ borderTop: `2px solid ${P.line}` }}>
                            <td colSpan={2} style={{ padding: "9px 10px", fontWeight: 800 }}>{t("konto.create.grandTotal", "Total")}</td>
                            <td colSpan={2} style={{ padding: "9px 10px", textAlign: "right", fontWeight: 800 }}>{check.calculatedAmount.toFixed(2)} €</td>
                            <td style={{ padding: "9px 10px", textAlign: "right", fontWeight: 800 }}>{check.declaredAmount.toFixed(2)} €</td>
                            <td style={{ padding: "9px 10px", textAlign: "right", fontWeight: 800, color: check.diff === 0 ? A.green : A.red }}>{check.diff >= 0 ? "+" : ""}{check.diff.toFixed(2)} €</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              <div style={{ ...card, padding: 14, marginBottom: 14 }}>
                <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 8 }}>📄 {t("konto.detail.pdfLabel", "Relevé PDF de la Niederlassung")}</strong>
                {check.pdfUrl ? (
                  <a href={`/api/kontoauszug/checks/${encodeURIComponent(check.id)}/pdf`} target="_blank" rel="noreferrer" style={{ fontWeight: 700, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: A.navy }}>{t("konto.detail.pdfOpenBtn", "Ouvrir le PDF joint")}</a>
                ) : canManage ? (
                  <>
                    <input type="file" accept="application/pdf" disabled={kontoUploadBusy} onChange={(e) => uploadKontoPdfToCheck(check.id, e.target.files?.[0])} />
                    <p style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "6px 0 0" }}>{t("konto.detail.pdfHint", "Optionnel — juste pour référence, le montant reste saisi à la main.")}</p>
                  </>
                ) : (
                  <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("konto.detail.pdfNone", "Aucun PDF joint")}</span>
                )}
              </div>

              {check.status !== "conforme" && (
                <div style={{ ...card, padding: 14, marginBottom: 14 }}>
                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 10 }}>💬 {t("konto.detail.notesLabel", "Échanges")}</strong>
                  {check.notes.length === 0 ? (
                    <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: 0 }}>{t("konto.detail.noNotes", "Aucun message pour l'instant.")}</p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
                      {check.notes.map((n, i) => (
                        <div key={i} style={{ background: P.soft, borderRadius: 12, padding: "8px 11px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                            <strong style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))` }}>{n.authorName}</strong>
                            <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{fmtFR(n.createdAt)}</span>
                          </div>
                          <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))` }}>{n.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {canManage && (
                    <div style={{ display: "flex", gap: 8 }}>
                      <input style={{ ...inputStyle, flex: 1 }} value={kontoNoteDraft} onChange={(e) => setKontoNoteDraft(e.target.value)} placeholder={t("konto.detail.notePlaceholder", "Écrire un message...")} />
                      <Pressable disabled={kontoBusy || !kontoNoteDraft.trim()} onClick={() => addKontoNote(check.id)} style={{ padding: "0 16px", borderRadius: 12, background: A.navy, color: "#fff", fontWeight: 800, opacity: (kontoBusy || !kontoNoteDraft.trim()) ? .5 : 1 }}>{t("common.send", "Envoyer")}</Pressable>
                    </div>
                  )}
                </div>
              )}

              {check.status === "anomalie" && canManage && (
                <Pressable disabled={kontoBusy} onClick={() => resolveKontoCheck(check.id)} style={{ width: "100%", ...btnSolid(A.blue), opacity: kontoBusy ? .5 : 1 }}>
                  {kontoBusy ? t("common.saving", "Enregistrement…") : t("konto.detail.resolveBtn", "Marquer résolu")}
                </Pressable>
              )}
              {check.status === "resolue" && check.resolvedBy && (
                <p style={{ textAlign: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("konto.detail.resolvedByLabel", "Résolu par")} {check.resolvedBy.name} · {fmtFR(check.resolvedAt)}</p>
              )}
            </div>
          );
        })()}

        {/* ================= WARENAUSTAUSCH =================
             Transferts de marchandises filiale↔filiale et filiale↔Niederlassung (2026-08-23).
             3 onglets : Sortants (mes brouillons/envoyés), Réception (ce qui m'arrive à
             vérifier), Kontoauszug (relevé financier dérivé, lecture seule). */}
        {screen === "warenaustausch" && waScreen === "hub" && (() => {
          // storesCatalog (jamais storesRegistry, réservé aux comptes GR — voir
          // hasModuleAccess/isNetworkLeader) est déjà chargé pour TOUT compte connecté
          // (GET /api/stores ouvert), donc le seul registre fiable pour un FLA normal ici.
          const waStoreLabelFor = waStoreLabel;
          const WA_TABS = [
            { id: "outgoing", l: t("warenaustausch.tab.outgoing", "Sortants"), n: waOutgoing.filter((tr) => tr.status === "envoyee").length },
            { id: "inbox", l: t("warenaustausch.tab.inbox", "Réception"), n: waInbox.filter((tr) => tr.status === "envoyee").length },
            { id: "konto", l: t("warenaustausch.tab.konto", "Kontoauszug"), n: 0 },
          ];
          const myKontoStore = waKonto?.stores?.find((s) => s.storeId === user?.store);
          return (
            <>
              <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
                {WA_TABS.map((tb) => (
                  <Pressable key={tb.id} onClick={() => setWaTab(tb.id)} style={{
                    flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                    background: waTab === tb.id ? A.amber : P.soft, color: waTab === tb.id ? "#fff" : P.muted,
                  }}>
                    {tb.l}
                    {tb.n > 0 && (
                      <span style={{
                        fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, minWidth: 16, height: 16, padding: "0 4px", display: "grid", placeItems: "center",
                        background: waTab === tb.id ? "rgba(255,255,255,.28)" : "rgba(231,138,18,.15)", color: waTab === tb.id ? "#fff" : A.amber,
                      }}>{tb.n}</span>
                    )}
                  </Pressable>
                ))}
              </div>

              {waTab === "outgoing" && (
                <>
                  {waOutgoing.length === 0 && (
                    <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "20px 0" }}>{t("warenaustausch.emptyOutgoing", "Aucun transfert sortant.")}</p>
                  )}
                  {waOutgoing.map((tr) => {
                    const meta = WA_STATUS_META[tr.status];
                    return (
                      <Pressable key={tr.id} onClick={() => openWarenaustauschDetail(tr.id, false)} style={{ ...card, padding: 14, marginBottom: 9, display: "block", textAlign: "left" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                          <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, flex: 1, minWidth: 0 }}>
                            {tr.kind === "warenrueckfuehrung" ? `↩️ ${WA_CATEGORY_LABEL[tr.category] || t("warenaustausch.returnLabel", "Retour")}` : `🚚 → ${waStoreLabelFor(tr.toStoreId)}`}
                          </strong>
                          <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: meta.c, background: meta.bg, borderRadius: 99, padding: "3px 8px", whiteSpace: "nowrap" }}>{meta.l}</span>
                        </div>
                        <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>
                          {t("warenaustausch.lineCountTemplate", { n: tr.lines.length }, `${tr.lines.length} article${tr.lines.length > 1 ? "s" : ""}`)} · {fmtFR(tr.createdAt)}
                        </div>
                      </Pressable>
                    );
                  })}
                </>
              )}

              {waTab === "inbox" && (
                <>
                  {waInbox.length === 0 && (
                    <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "20px 0" }}>{t("warenaustausch.emptyInbox", "Rien à recevoir pour l'instant.")}</p>
                  )}
                  {waInbox.map((tr) => {
                    const meta = WA_STATUS_META[tr.status];
                    return (
                      <Pressable key={tr.id} onClick={() => openWarenaustauschDetail(tr.id, true)} style={{ ...card, padding: 14, marginBottom: 9, display: "block", textAlign: "left" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                          <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, flex: 1, minWidth: 0 }}>
                            {tr.kind === "warenrueckfuehrung" ? `↩️ ${WA_CATEGORY_LABEL[tr.category] || t("warenaustausch.returnLabel", "Retour")}` : `🚚 ${waStoreLabelFor(tr.fromStoreId)} →`}
                          </strong>
                          <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: meta.c, background: meta.bg, borderRadius: 99, padding: "3px 8px", whiteSpace: "nowrap" }}>{meta.l}</span>
                        </div>
                        <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>
                          {t("warenaustausch.lineCountTemplate", { n: tr.lines.length }, `${tr.lines.length} article${tr.lines.length > 1 ? "s" : ""}`)} · {fmtFR(tr.createdAt)}
                        </div>
                      </Pressable>
                    );
                  })}
                </>
              )}

              {waTab === "konto" && (
                <>
                  <div style={{ ...card, padding: 16, marginBottom: 14, textAlign: "center", background: "linear-gradient(155deg, rgba(231,138,18,.11), rgba(231,138,18,.04))", border: `1.5px solid rgba(231,138,18,.28)` }}>
                    <p style={{ margin: "0 0 4px", ...eyebrow(A.amber) }}>{t("warenaustausch.konto.balanceLabel", "Solde")}</p>
                    <strong style={{ fontSize: `calc(28px * var(--nx-font-scale, 1))`, fontWeight: 800, color: (myKontoStore?.solde ?? 0) >= 0 ? A.green : A.red }}>
                      {(myKontoStore?.solde ?? 0).toFixed(2)} €
                    </strong>
                  </div>
                  {(myKontoStore?.entries || []).length === 0 && (
                    <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "20px 0" }}>{t("warenaustausch.konto.empty", "Aucune écriture pour l'instant.")}</p>
                  )}
                  {(myKontoStore?.entries || []).map((e) => (
                    <div key={e.id} style={{ ...card, padding: 13, marginBottom: 8, display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{e.art === "gutschrift" ? t("warenaustausch.konto.gutschrift", "Gutschrift") : t("warenaustausch.konto.belastung", "Belastung")} — {e.counterpartStoreId}</strong>
                        <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 1 }}>{fmtFR(e.createdAt)}</div>
                      </div>
                      <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: e.art === "gutschrift" ? A.green : A.red }}>
                        {e.art === "gutschrift" ? "+" : "−"}{e.betrag.toFixed(2)} €
                      </strong>
                    </div>
                  ))}
                </>
              )}

              {waTab !== "konto" && (
                <Pressable onClick={openWarenaustauschNew} aria-label={t("warenaustausch.newAria", "Nouveau transfert")} style={{
                  position: "fixed", right: 18, bottom: "calc(86px + env(safe-area-inset-bottom))", width: 56, height: 56, borderRadius: 99,
                  background: A.amber, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(26px * var(--nx-font-scale, 1))`,
                  boxShadow: "0 12px 26px rgba(231,138,18,.4)", zIndex: 5,
                }}>＋</Pressable>
              )}
            </>
          );
        })()}

        {/* ================= WARENAUSTAUSCH — NOUVEAU / DÉTAIL SORTANT =================
             Refonte écran (2026-08-26, ex-Sheets "waNew"+"waDetail" fusionnées) : tant que
             waDetailId est vide, formulaire de création (kind + destinataire/catégorie) ;
             une fois le brouillon créé, scan d'articles + cartons/palettes + signature +
             export des documents + envoi, tout sur le même écran (jamais un popup — patron
             Rapport de filiale/Étiquettes). */}
        {screen === "warenaustausch" && waScreen === "new" && (() => {
          const candidateStores = storesCatalog.filter((s) => s.kind !== "niederlassung" && s.id !== user?.store);
          if (!waDetailId) {
            return (
              <>
                <div style={{ display: "flex", gap: 4, padding: 3, background: P.soft, borderRadius: 15, marginBottom: 16 }}>
                  {[
                    { id: "filiale_transfer", l: t("warenaustausch.new.tabFiliale", "Vers une filiale") },
                    { id: "warenrueckfuehrung", l: t("warenaustausch.new.tabReturn", "Retour marchandise") },
                  ].map((tb) => (
                    <Pressable key={tb.id} onClick={() => setWaNewForm({ ...emptyWaForm, kind: tb.id })} style={{
                      flex: 1, textAlign: "center", padding: "9px 4px", borderRadius: 12, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                      background: waNewForm.kind === tb.id ? P.card : "transparent", color: waNewForm.kind === tb.id ? P.ink : P.muted,
                      boxShadow: waNewForm.kind === tb.id ? "0 1px 4px rgba(0,0,0,.08)" : "none",
                    }}>{tb.l}</Pressable>
                  ))}
                </div>

                {waNewForm.kind === "filiale_transfer" ? (
                  <>
                    <label style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>{t("warenaustausch.new.destinationLabel", "Filiale destinataire")}</label>
                    <section style={{ ...card, borderRadius: 20, overflow: "hidden", marginBottom: 18, maxHeight: 300, overflowY: "auto" }}>
                      {candidateStores.map((s, i) => (
                        <Pressable key={s.id} onClick={() => setWaNewForm((f) => ({ ...f, toStoreId: s.id }))} style={{
                          display: "flex", alignItems: "center", gap: 10, padding: "12px 15px", borderTop: i > 0 ? `1px solid ${P.line}` : "none",
                        }}>
                          <strong style={{ flex: 1, fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{s.label || s.id}</strong>
                          <span style={{
                            width: 22, height: 22, borderRadius: 99, flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "center",
                            background: waNewForm.toStoreId === s.id ? A.amber : P.soft, color: "#fff", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800,
                          }}>{waNewForm.toStoreId === s.id ? "✓" : ""}</span>
                        </Pressable>
                      ))}
                    </section>
                  </>
                ) : (
                  <>
                    <label style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>{t("warenaustausch.new.categoryLabel", "Catégorie de retour")}</label>
                    <section style={{ marginBottom: 18, display: "flex", flexDirection: "column", gap: 8 }}>
                      {Object.entries(WA_CATEGORY_LABEL).map(([id, label]) => (
                        <Pressable key={id} onClick={() => setWaNewForm((f) => ({ ...f, category: id }))} style={{
                          ...card, padding: 14, display: "flex", alignItems: "center", justifyContent: "space-between",
                          border: waNewForm.category === id ? `1.5px solid ${A.amber}` : `1.5px solid ${P.line}`,
                        }}>
                          <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{label}</strong>
                          {waNewForm.category === id && <span style={{ color: A.amber, fontWeight: 800 }}>✓</span>}
                        </Pressable>
                      ))}
                    </section>
                  </>
                )}

                <Pressable onClick={createWarenaustauschTransfer} style={{ width: "100%", ...btnSolid(A.amber), padding: "13px 0" }}>
                  {t("warenaustausch.new.createBtn", "Créer le brouillon")}
                </Pressable>
              </>
            );
          }

          const tr = waFindTransfer(waDetailId);
          if (!tr) return <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("warenaustausch.notFound", "Transfert introuvable.")}</p>;
          const meta = WA_STATUS_META[tr.status];
          const isDraft = tr.status === "brouillon";
          return (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 16 }}>
                <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, flex: 1, minWidth: 0 }}>
                  {tr.kind === "warenrueckfuehrung" ? (WA_CATEGORY_LABEL[tr.category] || t("warenaustausch.returnLabel", "Retour")) : `→ ${waStoreLabel(tr.toStoreId)}`}
                </strong>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: meta.c, borderRadius: 99, padding: "5px 11px" }}>{meta.l}</span>
              </div>
              {!isDraft && <p style={{ margin: "-10px 2px 16px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{fmtFR(tr.sentAt || tr.createdAt)}</p>}

              {isDraft && (
                <section style={{ ...card, borderRadius: 18, padding: 14, marginBottom: 16 }}>
                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 8 }}>📷 {t("warenaustausch.scanTitle", "Scanner un article")}</strong>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input value={waScanCode} onChange={(e) => setWaScanCode(e.target.value)} placeholder={t("warenaustausch.codePlaceholder", "Code article (EAN/Art.Nr)")} style={{ ...inputStyle, flex: 1 }} />
                    <Pressable onClick={() => doScan("warenaustausch")} aria-label={t("warenaustausch.scanAria", "Scanner")} style={{
                      width: 46, height: 46, ...card, borderRadius: 14, display: "grid", placeItems: "center", fontSize: `calc(18px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                    }}>📷</Pressable>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input type="number" min="1" value={waScanQty} onChange={(e) => setWaScanQty(e.target.value)} placeholder={t("warenaustausch.qtyPlaceholder", "Quantité")} style={{ ...inputStyle, flex: 1 }} />
                    <Pressable disabled={waScanBusy} onClick={addWarenaustauschLine} style={{ flex: "0 0 auto", ...btnSolid(A.amber), padding: "0 18px", opacity: waScanBusy ? .6 : 1 }}>
                      {waScanBusy ? "…" : t("common.add", "Ajouter")}
                    </Pressable>
                  </div>
                </section>
              )}

              <label style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>
                {t("warenaustausch.lineCountTemplate", { n: tr.lines.length }, `${tr.lines.length} article${tr.lines.length > 1 ? "s" : ""}`)}
              </label>
              <section style={{ ...card, borderRadius: 20, overflow: "hidden", marginBottom: 18 }}>
                {tr.lines.length === 0 && (
                  <p style={{ margin: 0, padding: 16, textAlign: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("warenaustausch.noLines", "Aucun article scanné pour l'instant.")}</p>
                )}
                {tr.lines.map((l, i) => (
                  <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 15px", borderTop: i > 0 ? `1px solid ${P.line}` : "none" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{l.name || l.artNr}</strong>
                      <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 1 }}>
                        {t("warenaustausch.qtySentTemplate", { n: l.quantitySent }, `Envoyé : ${l.quantitySent}`)}{l.quantityReceived != null ? ` · ${t("warenaustausch.qtyReceivedTemplate", { n: l.quantityReceived }, `Reçu : ${l.quantityReceived}`)}` : ""} · {(l.unitPriceAtSend || 0).toFixed(2)} €
                      </div>
                    </div>
                    {isDraft && (
                      <Pressable onClick={() => removeWarenaustauschLine(tr.id, l.id)} aria-label={t("common.delete", "Supprimer")} style={{
                        width: 28, height: 28, borderRadius: 99, background: "rgba(239,68,68,.12)", color: A.red, display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                      }}>✕</Pressable>
                    )}
                  </div>
                ))}
              </section>

              {/* Cartons/palettes (retour utilisateur explicite, 2026-08-26) — sauvegardés
                  au blocage du champ ; lecture seule une fois le transfert envoyé. */}
              <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                <Field label={t("warenaustausch.cartonsLabel", "Nombre de cartons")}>
                  {isDraft ? (
                    <input type="number" min="0" value={waCartonsDraft} onChange={(e) => setWaCartonsDraft(e.target.value)} onBlur={() => saveWaCartonsPallets(tr.id)} style={inputStyle} />
                  ) : (
                    <p style={{ margin: 0, ...inputStyle, background: P.soft, display: "flex", alignItems: "center" }}>{tr.cartonsCount}</p>
                  )}
                </Field>
                <Field label={t("warenaustausch.palletsLabel", "Nombre de palettes")}>
                  {isDraft ? (
                    <input type="number" min="0" value={waPalletsDraft} onChange={(e) => setWaPalletsDraft(e.target.value)} onBlur={() => saveWaCartonsPallets(tr.id)} style={inputStyle} />
                  ) : (
                    <p style={{ margin: 0, ...inputStyle, background: P.soft, display: "flex", alignItems: "center" }}>{tr.palletsCount}</p>
                  )}
                </Field>
              </div>

              {/* Signature — condition d'envoi (voir POST /:id/send côté serveur), même
                  patron que Rapport de filiale (createSignaturePad). */}
              {isDraft && (
                <section style={{ ...card, borderRadius: 18, padding: 14, marginBottom: 16 }}>
                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 8 }}>✍️ {t("warenaustausch.signatureLabel", "Signature de l'expéditeur")}</strong>
                  {tr.signatureUrl ? (
                    <img src={`/api/warenaustausch/${tr.id}/signature?_=${tr.updatedAt}`} alt={t("warenaustausch.signatureLabel", "Signature de l'expéditeur")} style={{ height: 80, objectFit: "contain" }} />
                  ) : (
                    <>
                      <div ref={waSigMountRef} style={{ marginBottom: 10 }} />
                      <Pressable disabled={!waSigReady || waBusy} onClick={submitWaSignature} style={{ width: "100%", ...btnSolid(A.amber), padding: "11px 0", opacity: waSigReady && !waBusy ? 1 : .5 }}>
                        {waBusy ? t("common.saving", "Enregistrement…") : t("warenaustausch.signBtn", "Valider la signature")}
                      </Pressable>
                    </>
                  )}
                </section>
              )}

              {!isDraft && tr.signatureUrl && (
                <section style={{ ...card, borderRadius: 18, padding: 14, marginBottom: 16 }}>
                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 8 }}>✍️ {t("warenaustausch.signatureLabel", "Signature de l'expéditeur")}</strong>
                  <img src={`/api/warenaustausch/${tr.id}/signature`} alt={t("warenaustausch.signatureLabel", "Signature de l'expéditeur")} style={{ height: 70, objectFit: "contain" }} />
                </section>
              )}

              {/* Documents (correctifs demandés, 2026-08-26) — disponibles dès qu'il y a une
                  signature, avant même l'envoi (utile pour relire avant de finaliser). */}
              {tr.signatureUrl && (
                <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                  <Pressable disabled={waExporting} onClick={() => exportWaTracking(tr)} style={{ flex: 1, textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink, opacity: waExporting ? .6 : 1 }}>
                    📄 {t("warenaustausch.exportTrackingBtn", "Fiche de suivi")}
                  </Pressable>
                  <Pressable disabled={waExporting} onClick={() => exportWaSticker(tr)} style={{ flex: 1, textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink, opacity: waExporting ? .6 : 1 }}>
                    🏷️ {t("warenaustausch.exportStickerBtn", "Étiquette d'envoi")}
                  </Pressable>
                </div>
              )}

              {isDraft ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                  <Pressable disabled={!tr.lines.length || !tr.signatureUrl} onClick={() => sendWarenaustauschTransfer(tr.id)} style={{ width: "100%", ...btnSolid(A.amber), padding: "13px 0", opacity: tr.lines.length && tr.signatureUrl ? 1 : .5 }}>
                    📤 {t("warenaustausch.sendBtn", "Envoyer")}
                  </Pressable>
                  <Pressable onClick={() => deleteWarenaustauschTransfer(tr.id)} style={{
                    width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: "rgba(239,68,68,.1)", color: A.red,
                  }}>🗑️ {t("warenaustausch.deleteDraftBtn", "Supprimer le brouillon")}</Pressable>
                </div>
              ) : tr.status === "envoyee" ? (
                <Pressable onClick={() => cancelWarenaustauschTransfer(tr.id)} style={{
                  width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: "rgba(239,68,68,.1)", color: A.red,
                }}>✕ {t("warenaustausch.cancelBtn", "Annuler le transfert")}</Pressable>
              ) : null}
            </>
          );
        })()}

        {/* ================= WARENAUSTAUSCH — RÉCEPTION =================
             Vérification ligne par ligne (destinataire) — chaque ligne reçoit sa propre
             quantité vérifiée ; le montant final se calcule sur CES quantités, jamais
             celles envoyées (retour utilisateur explicite). Le contrôleur = l'identité
             connectée qui valide, automatique (voir receivedBy côté serveur), jamais saisi
             manuellement. */}
        {screen === "warenaustausch" && waScreen === "receive" && waDetailId && (() => {
          const tr = waFindTransfer(waDetailId);
          if (!tr) return <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("warenaustausch.notFound", "Transfert introuvable.")}</p>;
          const meta = WA_STATUS_META[tr.status];
          const allVerified = tr.lines.length > 0 && tr.lines.every((l) => l.quantityReceived != null);
          const displayAmount = tr.lines.reduce((sum, l) => sum + (l.quantityReceived != null ? l.quantityReceived * (l.unitPriceAtSend || 0) : 0), 0);
          return (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 16 }}>
                <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, flex: 1, minWidth: 0 }}>
                  {tr.kind === "warenrueckfuehrung" ? (WA_CATEGORY_LABEL[tr.category] || t("warenaustausch.returnLabel", "Retour")) : `${waStoreLabel(tr.fromStoreId)} →`}
                </strong>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: meta.c, borderRadius: 99, padding: "5px 11px" }}>{meta.l}</span>
              </div>
              <p style={{ margin: "-10px 2px 16px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{fmtFR(tr.sentAt || tr.createdAt)}</p>

              <section style={{ ...card, borderRadius: 20, overflow: "hidden", marginBottom: 18 }}>
                {tr.lines.map((l, i) => (
                  <div key={l.id} style={{ padding: "13px 15px", borderTop: i > 0 ? `1px solid ${P.line}` : "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: tr.status === "envoyee" ? 8 : 0 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{l.name || l.artNr}</strong>
                        <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 1 }}>
                          {t("warenaustausch.qtySentTemplate", { n: l.quantitySent }, `Envoyé : ${l.quantitySent}`)} · {(l.unitPriceAtSend || 0).toFixed(2)} €
                        </div>
                      </div>
                      {l.quantityReceived != null && (
                        <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: l.quantityReceived === l.quantitySent ? A.green : A.red, background: l.quantityReceived === l.quantitySent ? "rgba(30,184,95,.12)" : "rgba(239,68,68,.12)", borderRadius: 99, padding: "4px 10px" }}>
                          {l.quantityReceived === l.quantitySent ? "✓" : "⚠️"} {l.quantityReceived}
                        </span>
                      )}
                    </div>
                    {tr.status === "envoyee" && (
                      <div style={{ display: "flex", gap: 7 }}>
                        <input type="number" min="0" value={waVerifyDraft[l.id] ?? ""} onChange={(e) => setWaVerifyDraft((m) => ({ ...m, [l.id]: e.target.value }))}
                          placeholder={t("warenaustausch.qtyReceivedPlaceholder", "Quantité reçue")} style={{ ...inputStyle, flex: 1, padding: "9px 12px" }} />
                        <Pressable onClick={() => verifyWarenaustauschLine(tr.id, l.id)} style={{ flex: "0 0 auto", ...btnSolid(A.blue), padding: "0 16px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>
                          {t("warenaustausch.verifyBtn", "Vérifier")}
                        </Pressable>
                      </div>
                    )}
                  </div>
                ))}
              </section>

              {tr.status === "envoyee" && (
                <Pressable disabled={!allVerified} onClick={() => receiveWarenaustauschTransfer(tr.id)} style={{ width: "100%", ...btnSolid(A.green), padding: "13px 0", opacity: allVerified ? 1 : .5 }}>
                  ✅ {t("warenaustausch.receiveBtnTemplate", { amount: displayAmount.toFixed(2) }, `Valider la réception${allVerified ? ` — ${displayAmount.toFixed(2)} €` : ""}`)}
                </Pressable>
              )}

              {tr.status === "receptionnee" && (
                <div style={{ display: "flex", gap: 8 }}>
                  <Pressable disabled={waExporting} onClick={() => exportWaTracking(tr)} style={{ flex: 1, textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink, opacity: waExporting ? .6 : 1 }}>
                    📄 {t("warenaustausch.exportTrackingBtn", "Fiche de suivi")}
                  </Pressable>
                </div>
              )}
            </>
          );
        })()}

        {/* ================= RAPPORT DE FILIALE =================
             Visite GR : sections texte + photos + to-do optionnelle + signature, envoyé à
             la chaîne de gestion réelle de la filiale visitée (voir
             GET /api/filiale-reports/:id/recipients-candidates côté serveur). */}
        {screen === "filialeReport" && frScreen === "hub" && (() => {
          const storeLabelFor = (storeId) => storesCatalog.find((s) => s.id === storeId)?.label || storeId;
          // Correctifs #1/#2 — une filiale (FL/FLA) ne crée jamais de rapport et ne voit
          // jamais l'onglet de création : seul un GR (isNetworkLeader — Bereichsleiter/
          // Verkaufsleiter/Niederlassungsleiter) voit "Mes rapports" et le "+". Un compte
          // filiale n'a de toute façon que "Reçus" comme onglet possible.
          const FR_TABS = isNetworkLeader ? [
            { id: "mine", l: t("filialeReport.tab.mine", "Mes rapports"), n: 0 },
            { id: "received", l: t("filialeReport.tab.received", "Reçus"), n: frReceived.filter((r) => {
              const mine = r.recipientStatuses?.[user?.proId];
              return mine && mine.status !== "traite";
            }).length },
          ] : [];
          const activeTab = isNetworkLeader ? frTab : "received";
          const list = activeTab === "mine" ? frMine : frReceived;
          return (
            <>
              {isNetworkLeader && (
                <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
                  {FR_TABS.map((tb) => (
                    <Pressable key={tb.id} onClick={() => setFrTab(tb.id)} style={{
                      flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                      background: activeTab === tb.id ? A.teal : P.soft, color: activeTab === tb.id ? "#fff" : P.muted,
                    }}>
                      {tb.l}
                      {tb.n > 0 && (
                        <span style={{
                          fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, minWidth: 16, height: 16, padding: "0 4px", display: "grid", placeItems: "center",
                          background: activeTab === tb.id ? "rgba(255,255,255,.28)" : "rgba(13,148,136,.15)", color: activeTab === tb.id ? "#fff" : A.teal,
                        }}>{tb.n}</span>
                      )}
                    </Pressable>
                  ))}
                </div>
              )}

              {list.length === 0 && (
                <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "20px 0" }}>
                  {activeTab === "mine" ? t("filialeReport.emptyMine", "Aucun rapport pour l'instant.") : t("filialeReport.emptyReceived", "Aucun rapport reçu pour l'instant.")}
                </p>
              )}
              {list.map((r) => {
                const mineStatus = activeTab === "received" ? (r.recipientStatuses?.[user?.proId]?.status || "pending") : null;
                const todoOpen = (r.todoItems || []).filter((i) => !i.done).length;
                return (
                  <Pressable key={r.id} onClick={() => activeTab === "mine" ? (r.status === "brouillon" ? resumeFrDraft(r) : openFrDetail(r.id)) : openFrDetail(r.id)} style={{ ...card, padding: 14, marginBottom: 9, display: "block", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                      <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, flex: 1, minWidth: 0 }}>🗒️ N°{r.reportNumber} — {storeLabelFor(r.storeId)}</strong>
                      <span style={{
                        fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "3px 8px", whiteSpace: "nowrap",
                        color: r.status === "brouillon" ? P.muted : (mineStatus === "traite" ? A.green : A.teal),
                        background: r.status === "brouillon" ? P.soft : (mineStatus === "traite" ? `${A.green}1f` : `${A.teal}1f`),
                      }}>
                        {r.status === "brouillon" ? t("filialeReport.status.draft", "Brouillon") : mineStatus === "traite" ? t("filialeReport.status.traite", "Traité") : mineStatus === "viewed" ? t("filialeReport.status.viewed", "Vu") : t("filialeReport.status.pending", "À traiter")}
                      </span>
                    </div>
                    <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>
                      {activeTab === "mine" ? t("filialeReport.card.recipientCountTemplate", { n: r.targetIds.length }, `${r.targetIds.length} destinataire${r.targetIds.length > 1 ? "s" : ""}`) : `${t("filialeReport.card.byTemplate", { name: r.createdByName }, `Par ${r.createdByName}`)}`}
                      {todoOpen > 0 && ` · ${t("filialeReport.card.todoOpenTemplate", { n: todoOpen }, `${todoOpen} tâche${todoOpen > 1 ? "s" : ""} à faire`)}`}
                      {` · ${fmtFR(r.createdAt)}`}
                    </div>
                  </Pressable>
                );
              })}

              {isNetworkLeader && activeTab === "mine" && (
                <Pressable onClick={startFrNew} aria-label={t("filialeReport.newAria", "Nouveau rapport")} style={{
                  position: "fixed", right: 18, bottom: "calc(86px + env(safe-area-inset-bottom))", width: 56, height: 56, borderRadius: 99,
                  background: A.teal, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(26px * var(--nx-font-scale, 1))`,
                  boxShadow: "0 12px 26px rgba(13,148,136,.4)", zIndex: 5,
                }}>＋</Pressable>
              )}
            </>
          );
        })()}

        {screen === "filialeReport" && frScreen === "new" && (
          <>
            {!frDraft ? (
              <>
                <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 19, marginBottom: 16 }}>
                  <span style={{ fontSize: `calc(26px * var(--nx-font-scale, 1))`}}>🗒️</span>
                  <h2 style={{ margin: "8px 0 6px", fontSize: `calc(18px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em" }}>{t("filialeReport.new.title", "Quelle filiale visites-tu ?")}</h2>
                  <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.45 }}>
                    {t("filialeReport.new.desc", "Sections, photos et to-do se construisent ensuite librement — la filiale détermine seulement qui pourra recevoir le rapport.")}
                  </p>
                </section>
                {/* Correctif #8 — choix UNIQUEMENT parmi les filiales réellement attribuées à
                    ce GR (+ celles couvertes par une Vertretung active aujourd'hui), jamais
                    un champ libre — voir loadFrMyStores/GET /api/filiale-reports/my-stores. */}
                {frMyStoresError ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 9, marginBottom: 16 }}>
                    <p style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: A.red, margin: "0 2px", fontWeight: 700 }}>{frMyStoresError}</p>
                    <Pressable onClick={() => loadFrMyStores()} style={{ padding: "9px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink }}>
                      {t("common.retry", "Réessayer")}
                    </Pressable>
                  </div>
                ) : frMyStores.length === 0 ? (
                  <p style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 14px" }}>{t("filialeReport.new.noStores", "Aucune filiale disponible pour l'instant.")}</p>
                ) : (
                  <div style={{ ...card, padding: 12, marginBottom: 16, flexDirection: "column", alignItems: "stretch", gap: 4 }}>
                    {frMyStores.map((sid) => (
                      <Pressable key={sid} onClick={() => setFrNewStoreId(sid)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 4px" }}>
                        <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{frNewStoreId === sid ? "🔵" : "⚪"}</span>
                        <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{storesCatalog.find((s) => s.id === sid)?.label || sid}</span>
                      </Pressable>
                    ))}
                  </div>
                )}
                <Pressable disabled={frBusy} onClick={createFrDraft} style={{ width: "100%", ...btnSolid(A.teal), marginTop: 10, opacity: frNewStoreId ? 1 : .5 }}>
                  {t("filialeReport.new.createBtn", "Créer le brouillon")}
                </Pressable>
              </>
            ) : (
              <>
                <section style={{ ...card, padding: 14, marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: `calc(22px * var(--nx-font-scale, 1))`}}>🗒️</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block" }}>{t("filialeReport.card.numberedStoreTemplate", { n: frDraft.reportNumber, store: frDraft.storeId }, `N°${frDraft.reportNumber} — Filiale ${frDraft.storeId}`)}</strong>
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("filialeReport.new.draftHint", "Brouillon — rien n'est encore envoyé")}</span>
                  </div>
                  <Pressable onClick={deleteFrDraft} aria-label={t("common.deleteAria", "Supprimer")} style={{ width: 36, height: 36, borderRadius: 99, background: "rgba(239,68,68,.12)", display: "grid", placeItems: "center", color: A.red, fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>🗑️</Pressable>
                </section>

                {/* Retour utilisateur explicite (2026-08-26) — "le rapport doit contenir une
                    zone où le GR peut rédiger, et non juste créer une to-do liste" / "tout
                    doit être optionnel" : renommé pour lever toute ambiguïté (ce N'ÉTAIT pas
                    absent, juste pas assez explicite sous "Constat / sections") — aucune
                    validation, ni ici ni côté serveur (POST /:id/send), n'exige de contenu
                    dans cette zone ni dans la to-do : les deux restent facultatifs. */}
                <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "14px 2px 3px" }}>{t("filialeReport.sections.title", "Rapport écrit (optionnel)")}</strong>
                <p style={{ margin: "0 2px 9px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.4 }}>
                  {t("filialeReport.sections.desc", "Rédige librement tes observations sur la visite — un ou plusieurs paragraphes, à ton rythme.")}
                </p>
                {frDraft.sections.map((s) => (
                  <div key={s.id} style={{ ...card, padding: 12, marginBottom: 8, display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <p style={{ margin: 0, flex: 1, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, whiteSpace: "pre-wrap" }}>{s.text}</p>
                    <Pressable onClick={() => removeFrSection(s.id)} aria-label={t("common.deleteAria", "Supprimer")} style={{ color: A.red, fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>✕</Pressable>
                  </div>
                ))}
                <div style={{ ...card, padding: 12, marginBottom: 16, flexDirection: "column", alignItems: "stretch" }}>
                  <textarea value={frSectionDraft} onChange={(e) => setFrSectionDraft(e.target.value)} placeholder={t("filialeReport.sections.placeholder", "Ex. Rayon Kühlung : rangement à revoir, propreté correcte...")} rows={3}
                    style={{ width: "100%", border: "none", outline: "none", background: "none", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.ink, fontFamily: `var(--nx-font, ${FONT})`, resize: "vertical" }} />
                  <Pressable onClick={addFrSection} style={{ alignSelf: "flex-end", marginTop: 6, padding: "7px 14px", borderRadius: 99, background: frSectionDraft.trim() ? A.teal : P.soft, color: frSectionDraft.trim() ? "#fff" : P.muted, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`}}>{t("common.addBtn", "Ajouter")}</Pressable>
                </div>

                {/* To-do list optionnelle — AVANT les photos exprès (correctif #3) : on crée
                    d'abord les tâches, pour pouvoir ensuite rattacher chaque photo à
                    "l'événement" qu'elle illustre. */}
                <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "14px 2px 9px" }}>{t("filialeReport.todo.title", "To-do avant la prochaine visite (optionnel)")}</strong>
                {/* Correctif #3 — une seule échéance pour toute la liste, ou une par tâche. */}
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <Pressable onClick={() => setFrTodoDateMode("shared")} style={{
                    flex: 1, textAlign: "center", padding: "8px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                    background: frDraft.todoDateMode === "shared" ? A.teal : P.soft, color: frDraft.todoDateMode === "shared" ? "#fff" : P.muted,
                  }}>{t("filialeReport.todo.modeShared", "Une date pour toute la liste")}</Pressable>
                  <Pressable onClick={() => setFrTodoDateMode("perItem")} style={{
                    flex: 1, textAlign: "center", padding: "8px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                    background: frDraft.todoDateMode === "perItem" ? A.teal : P.soft, color: frDraft.todoDateMode === "perItem" ? "#fff" : P.muted,
                  }}>{t("filialeReport.todo.modePerItem", "Une date par tâche")}</Pressable>
                </div>
                {frDraft.todoDateMode === "shared" && (
                  <Field label={t("filialeReport.todo.sharedDateLabel", "Échéance pour toute la liste")}>
                    <input type="date" min={todayISO()} value={frDraft.todoSharedDueDate || ""} onChange={(e) => setFrSharedDueDate(e.target.value)} style={dateTimeInputStyle} />
                  </Field>
                )}
                {frDraft.todoItems.map((i) => {
                  const urgency = frDraft.todoDateMode === "perItem" ? frDueUrgency(i.dueDate, false) : null;
                  return (
                    <div key={i.id} style={{ ...card, padding: 12, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, display: "block", marginBottom: urgency ? 4 : 0 }}>{i.text}</span>
                        {urgency && (
                          <span style={{ display: "inline-block", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: urgency.c, background: urgency.bg, borderRadius: 99, padding: "3px 9px" }}>⏰ {urgency.label}</span>
                        )}
                      </div>
                      <Pressable onClick={() => removeFrTodo(i.id)} aria-label={t("common.deleteAria", "Supprimer")} style={{ color: A.red, fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>✕</Pressable>
                    </div>
                  );
                })}
                <div style={{ ...card, padding: 12, marginBottom: 16, flexDirection: "column", alignItems: "stretch", gap: 8 }}>
                  <input type="text" value={frTodoDraft.text} onChange={(e) => setFrTodoDraft((f) => ({ ...f, text: e.target.value }))} placeholder={t("filialeReport.todo.placeholder", "Ex. Refaire le facing du rayon Obst & Gemüse")}
                    style={{ width: "100%", border: "none", outline: "none", background: "none", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.ink, fontFamily: `var(--nx-font, ${FONT})` }} />
                  {/* Le champ date doit être SEUL sur sa ligne, jamais partager une ligne flex
                      avec un bouton — dateTimeInputStyle fixe une largeur calc(100% - 31px)
                      (contournement iOS Safari, voir storeflow_ios_date_input_boxsizing_bug) :
                      partagé avec le bouton "Ajouter", ça écrasait/masquait le sélecteur de
                      date (retour utilisateur explicite, 2026-08-26 : "il n'existe aucun
                      sélecteur de date pour ce bouton"). */}
                  {frDraft.todoDateMode === "perItem" && (
                    <Field label={t("filialeReport.todo.itemDateLabel", "Échéance de cette tâche (optionnel)")}>
                      <input type="date" min={todayISO()} value={frTodoDraft.dueDate} onChange={(e) => setFrTodoDraft((f) => ({ ...f, dueDate: e.target.value }))} style={dateTimeInputStyle} />
                    </Field>
                  )}
                  <Pressable onClick={addFrTodo} style={{ alignSelf: "flex-end", padding: "7px 14px", borderRadius: 99, background: frTodoDraft.text.trim() ? A.teal : P.soft, color: frTodoDraft.text.trim() ? "#fff" : P.muted, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`}}>{t("common.addBtn", "Ajouter")}</Pressable>
                </div>

                {/* Photos — chacune peut être rattachée à une tâche précise (correctif #3) */}
                <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "14px 2px 9px" }}>{t("filialeReport.photos.title", "Photos")}</strong>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 10 }}>
                  {frDraft.photos.map((p) => (
                    <div key={p.id} style={{ position: "relative", aspectRatio: "1", borderRadius: 12, overflow: "hidden", background: P.soft }}>
                      <img src={`/api/filiale-reports/${frDraft.id}/photos/${p.id}`} alt={p.caption || ""} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      <Pressable onClick={() => removeFrPhoto(p.id)} aria-label={t("common.deleteAria", "Supprimer")} style={{ position: "absolute", top: 4, right: 4, width: 24, height: 24, borderRadius: 99, background: "rgba(0,0,0,.55)", color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`}}>✕</Pressable>
                      {p.linkedTodoId && (
                        <span style={{ position: "absolute", top: 4, left: 4, width: 20, height: 20, borderRadius: 99, background: A.teal, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(10px * var(--nx-font-scale, 1))`}}>🔗</span>
                      )}
                      {p.caption && <span style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "3px 6px", background: "rgba(0,0,0,.55)", color: "#fff", fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{p.caption}</span>}
                    </div>
                  ))}
                </div>
                <div style={{ ...card, padding: 12, marginBottom: 16, flexDirection: "column", alignItems: "stretch", gap: 8 }}>
                  {frDraft.todoItems.length > 0 && (
                    <select value={frPhotoLinkedTodoId} onChange={(e) => setFrPhotoLinkedTodoId(e.target.value)} style={{ ...inputStyle, padding: "9px 12px" }}>
                      <option value="">{t("filialeReport.photos.generalOption", "Photo générale (non liée à une tâche)")}</option>
                      {frDraft.todoItems.map((i) => <option key={i.id} value={i.id}>{i.text}</option>)}
                    </select>
                  )}
                  <div style={{ display: "flex", gap: 8 }}>
                    <input type="text" value={frPhotoCaptionDraft} onChange={(e) => setFrPhotoCaptionDraft(e.target.value)} placeholder={t("filialeReport.photos.captionPlaceholder", "Légende optionnelle (ex. Rayon Trocken)")}
                      style={{ flex: 1, border: "none", outline: "none", background: "none", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.ink, fontFamily: `var(--nx-font, ${FONT})` }} />
                    <input ref={frPhotoInputRef} type="file" accept="image/*" capture="environment" multiple style={{ display: "none" }} onChange={(e) => { uploadFrPhoto(e.target.files); e.target.value = ""; }} />
                    <Pressable onClick={() => frPhotoInputRef.current?.click()} style={{ padding: "7px 14px", borderRadius: 99, background: A.teal, color: "#fff", fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, whiteSpace: "nowrap" }}>📷 {t("filialeReport.photos.addBtn", "Ajouter")}</Pressable>
                  </div>
                </div>

                {/* Destinataires — chaîne de gestion réelle de la filiale, moins le créateur.
                    Retour utilisateur explicite (2026-08-26) — "la case à cocher tous semble
                    vague, à qui est-elle adressée" : une phrase d'intro précise maintenant
                    explicitement à qui s'adresse la liste en dessous, et "Tous" est reformulé
                    pour dire clairement CE qu'il sélectionne (tout le monde dans CETTE liste). */}
                <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "14px 2px 3px" }}>{t("filialeReport.recipients.title", "Destinataires")}</strong>
                {frCandidates.length === 0 ? (
                  <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 14px" }}>{t("filialeReport.recipients.none", "Aucun destinataire possible n'a été trouvé pour cette filiale.")}</p>
                ) : (
                  <>
                    <p style={{ margin: "0 2px 9px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.4 }}>
                      {t("filialeReport.recipients.desc", "Choisis à qui envoyer ce rapport, parmi les responsables de cette filiale (liste ci-dessous).")}
                    </p>
                    <div style={{ ...card, padding: 12, marginBottom: 16, flexDirection: "column", alignItems: "stretch", gap: 4 }}>
                      <Pressable onClick={() => setFrSelectedTargets(frSelectedTargets.length === frCandidates.length ? [] : frCandidates.map((c) => c.employeeId))} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 2px", borderBottom: `1px solid ${P.line}`, marginBottom: 4 }}>
                        <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{frSelectedTargets.length === frCandidates.length ? "☑️" : "⬜️"}</span>
                        <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800 }}>{t("filialeReport.recipients.allToggleTemplate", { n: frCandidates.length }, `Tout le monde ci-dessous (${frCandidates.length})`)}</span>
                      </Pressable>
                      {frCandidates.map((c) => (
                        <Pressable key={c.employeeId} onClick={() => toggleFrTarget(c.employeeId)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 2px" }}>
                          <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{frSelectedTargets.includes(c.employeeId) ? "☑️" : "⬜️"}</span>
                          <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, flex: 1 }}>{c.name}</span>
                          <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, background: P.soft, borderRadius: 99, padding: "2px 8px" }}>{roleLabel(c.role)}</span>
                        </Pressable>
                      ))}
                    </div>
                  </>
                )}

                {/* Signature */}
                <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "14px 2px 9px" }}>{t("filialeReport.signature.title", "Signature")}</strong>
                {frDraft.signatureUrl ? (
                  <div style={{ ...card, padding: 14, marginBottom: 16, flexDirection: "column", alignItems: "center", gap: 8 }}>
                    <img src={`/api/filiale-reports/${frDraft.id}/signature?_=${frDraft.updatedAt}`} alt={t("filialeReport.signature.title", "Signature")} style={{ height: 80, objectFit: "contain" }} />
                    <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.green }}>✓ {t("filialeReport.signature.doneLabel", "Signé")}</span>
                  </div>
                ) : (
                  <div style={{ marginBottom: 16 }}>
                    <div ref={frSigMountRef} />
                    <Pressable disabled={!frSigReady || frBusy} onClick={submitFrSignature} style={{ width: "100%", ...btnSolid(A.navy), marginTop: 10, opacity: frSigReady ? 1 : .5 }}>
                      {t("filialeReport.signature.confirmBtn", "Enregistrer la signature")}
                    </Pressable>
                  </div>
                )}

                <Pressable disabled={frBusy} onClick={sendFrReport} style={{ width: "100%", ...btnSolid(A.teal), marginBottom: 30, opacity: (frDraft.signatureUrl && frSelectedTargets.length) ? 1 : .5 }}>
                  {t("filialeReport.sendBtn", "Envoyer le rapport")}
                </Pressable>
              </>
            )}
          </>
        )}

        {screen === "filialeReport" && frScreen === "detail" && frDetail && (() => {
          const isFrCreator = String(user?.proId || "") === String(frDetail.createdBy);
          // Retour utilisateur explicite (2026-08-27) : un GR destinataire (ex. transmis à son
          // supérieur/subordonné) lit le rapport, il ne réalise pas les tâches de la filiale —
          // seuls le créateur et la filiale (FL/FLA) cochent la to-do. Même règle appliquée
          // côté serveur (POST /:id/todo/:todoId/toggle) — la VRAIE barrière, ceci n'est que
          // l'affichage cohérent avec elle.
          const canToggleFrTodo = isFrCreator || isFlOrFlaRole;
          // Correctif #4 — vue réduite (filiale store-tier, ni créateur ni GR) : le serveur
          // n'envoie déjà QUE {id, reportNumber, storeId, createdByName, createdAt, sentAt,
          // status, todoDateMode, todoSharedDueDate, todoItems[+photos liées]} — sections/
          // photos générales/signature/destinataires sont alors simplement absents ici,
          // jamais masqués côté client seul (voir shapeForViewer, server/filialeReports/
          // routes.js) : ces `|| []`/optional chaining ne sont donc PAS de la prudence
          // défensive gratuite, ils reflètent une vraie forme de réponse réduite.
          const targetIds = frDetail.targetIds || [];
          const myTargetId = targetIds.find((id) => String(id) === String(user?.proId));
          const myStatus = myTargetId ? frDetail.recipientStatuses?.[myTargetId]?.status : null;
          return (
            <>
              <section style={{ ...card, padding: 14, marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: `calc(22px * var(--nx-font-scale, 1))`}}>🗒️</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block" }}>{t("filialeReport.card.numberedStoreTemplate", { n: frDetail.reportNumber, store: frDetail.storeId }, `N°${frDetail.reportNumber} — Filiale ${frDetail.storeId}`)}</strong>
                  <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("filialeReport.detail.byTemplate", { name: frDetail.createdByName, date: fmtFR(frDetail.sentAt) }, `Par ${frDetail.createdByName} — ${fmtFR(frDetail.sentAt)}`)}</span>
                </div>
                {/* Correctif #9 — export/impression (patron buildXPrintHtml + window.print()
                    déjà établi partout ailleurs dans l'app). Non proposé à une filiale
                    store-tier — rien à imprimer de riche dans sa vue réduite to-do-only. */}
                {frDetail.signatureUrl && (
                  <Pressable onClick={() => exportFrReportPdf(frDetail)} aria-label={t("filialeReport.detail.exportAria", "Exporter / imprimer")} style={{ width: 36, height: 36, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(15px * var(--nx-font-scale, 1))`, opacity: frExporting ? .6 : 1 }}>{frExporting ? "…" : "🖨️"}</Pressable>
                )}
                {/* Correctif #7 — suppression réservée au créateur, quel que soit le statut. */}
                {isFrCreator && (
                  <Pressable onClick={deleteFrReportFromDetail} aria-label={t("common.deleteAria", "Supprimer")} style={{ width: 36, height: 36, borderRadius: 99, background: "rgba(239,68,68,.12)", display: "grid", placeItems: "center", color: A.red, fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>🗑️</Pressable>
                )}
              </section>

              {(frDetail.sections || []).map((s) => (
                <p key={s.id} style={{ ...card, padding: 12, marginBottom: 8, display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, whiteSpace: "pre-wrap" }}>{s.text}</p>
              ))}

              {(frDetail.photos || []).length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 }}>
                  {frDetail.photos.map((p) => (
                    <div key={p.id} style={{ position: "relative", aspectRatio: "1", borderRadius: 12, overflow: "hidden", background: P.soft }}>
                      <img src={`/api/filiale-reports/${frDetail.id}/photos/${p.id}`} alt={p.caption || ""} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      {p.caption && <span style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "3px 6px", background: "rgba(0,0,0,.55)", color: "#fff", fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{p.caption}</span>}
                    </div>
                  ))}
                </div>
              )}

              {frDetail.signatureUrl && (
                <div style={{ ...card, padding: 14, marginBottom: 16, flexDirection: "column", alignItems: "center" }}>
                  <img src={`/api/filiale-reports/${frDetail.id}/signature`} alt={t("filialeReport.signature.title", "Signature")} style={{ height: 70, objectFit: "contain" }} />
                </div>
              )}

              {isFrCreator && (
                <>
                  <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "14px 2px 9px" }}>{t("filialeReport.detail.recipientsTitle", "Destinataires")}</strong>
                  {targetIds.map((id) => {
                    const st = frDetail.recipientStatuses?.[id]?.status || "pending";
                    return (
                      <div key={id} style={{ ...card, padding: 12, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ flex: 1, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{frDetail.targetNames?.[id] || id}</span>
                        <span style={{
                          fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "3px 8px",
                          color: st === "traite" ? A.green : st === "viewed" ? A.teal : P.muted,
                          background: st === "traite" ? `${A.green}1f` : st === "viewed" ? `${A.teal}1f` : P.soft,
                        }}>{st === "traite" ? t("filialeReport.status.traite", "Traité") : st === "viewed" ? t("filialeReport.status.viewed", "Vu") : t("filialeReport.status.pending", "À traiter")}</span>
                      </div>
                    );
                  })}
                </>
              )}

              {(frDetail.todoItems || []).length > 0 && (
                <>
                  {/* Correctif #4 — pour une filiale store-tier, c'est ICI que commence
                      réellement l'écran (tout ce qui précède est vide dans sa vue réduite) :
                      "elle ne reçoit que la liste des tâches à effectuer". */}
                  <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "14px 2px 9px" }}>{t("filialeReport.todo.title", "To-do avant la prochaine visite (optionnel)")}</strong>
                  {/* Retour utilisateur explicite (2026-08-26) — "c'est très important la date
                      maximale d'accomplissement des tâches" : en mode "shared", l'échéance
                      unique s'affiche en GROS bandeau au-dessus de toute la liste, jamais une
                      simple ligne discrète. */}
                  {frDetail.todoDateMode === "shared" && frDetail.todoSharedDueDate && (() => {
                    const sharedUrgency = frDueUrgency(frDetail.todoSharedDueDate, false);
                    return sharedUrgency ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, borderRadius: 14, padding: "10px 14px", marginBottom: 10, background: sharedUrgency.bg, border: `1.5px solid ${sharedUrgency.c}55` }}>
                        <span style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>⏰</span>
                        <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: sharedUrgency.c }}>{t("filialeReport.todo.sharedBannerTemplate", "Toute la liste — ") + sharedUrgency.label}</span>
                      </div>
                    ) : null;
                  })()}
                  {frDetail.todoItems.map((i, idx) => {
                    const due = frDetail.todoDateMode === "shared" ? frDetail.todoSharedDueDate : i.dueDate;
                    const urgency = frDetail.todoDateMode === "perItem" ? frDueUrgency(due, i.done) : null;
                    return (
                      <div key={i.id} style={{ ...card, padding: 12, marginBottom: 8, flexDirection: "column", alignItems: "stretch", gap: 0 }}>
                        {/* Interactif seulement pour le créateur et la filiale (FL/FLA) qui
                            réalise réellement la to-do — un GR destinataire (rapport transmis à
                            son supérieur/subordonné) reste en lecture seule, voir
                            canToggleFrTodo plus haut. canSeeReport + le contrôle de rôle côté
                            serveur (POST /:id/todo/:todoId/toggle) restent la VRAIE barrière,
                            jamais ce simple onClick. */}
                        {canToggleFrTodo ? (
                          <Pressable onClick={() => toggleFrTodoItem(i.id)} style={{ display: "flex", alignItems: "center", gap: 10, textAlign: "left", width: "100%" }}>
                            <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>{i.done ? "✅" : "⬜️"}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, display: "block", textDecoration: i.done ? "line-through" : "none", color: i.done ? P.muted : P.ink, marginBottom: (urgency || i.done) ? 4 : 0 }}>{idx + 1}. {i.text}</span>
                              {i.done ? (
                                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("filialeReport.todo.doneByTemplate", { name: i.doneByName, date: fmtFR(i.doneAt) }, `Fait par ${i.doneByName} — ${fmtFR(i.doneAt)}`)}</span>
                              ) : urgency ? (
                                <span style={{ display: "inline-block", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: urgency.c, background: urgency.bg, borderRadius: 99, padding: "3px 9px" }}>⏰ {urgency.label}</span>
                              ) : null}
                            </div>
                          </Pressable>
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%" }}>
                            <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`, opacity: .7 }}>{i.done ? "✅" : "⬜️"}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, display: "block", textDecoration: i.done ? "line-through" : "none", color: i.done ? P.muted : P.ink, marginBottom: (urgency || i.done) ? 4 : 0 }}>{idx + 1}. {i.text}</span>
                              {i.done ? (
                                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("filialeReport.todo.doneByTemplate", { name: i.doneByName, date: fmtFR(i.doneAt) }, `Fait par ${i.doneByName} — ${fmtFR(i.doneAt)}`)}</span>
                              ) : urgency ? (
                                <span style={{ display: "inline-block", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: urgency.c, background: urgency.bg, borderRadius: 99, padding: "3px 9px" }}>⏰ {urgency.label}</span>
                              ) : null}
                            </div>
                          </div>
                        )}
                        {(i.photos || []).length > 0 && (
                          <div style={{ display: "flex", gap: 6, marginTop: 8, paddingLeft: 28, overflowX: "auto" }}>
                            {i.photos.map((p) => (
                              <img key={p.id} src={`/api/filiale-reports/${frDetail.id}/photos/${p.id}`} alt={p.caption || ""} style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              {myTargetId && myStatus !== "traite" && (
                <Pressable onClick={markFrTraite} style={{ width: "100%", ...btnSolid(A.green), marginTop: 6, marginBottom: 30 }}>
                  {t("filialeReport.detail.markTraiteBtn", "✓ Marquer le rapport traité")}
                </Pressable>
              )}
            </>
          );
        })()}

        {/* ================= PREISÄNDERUNG =================
             Cahier des charges explicite (2026-08-16) : LA CENTRALE DÉCIDE, NEXORA CALCULE,
             LE SALARIÉ COMPTE ET SAISIT. Le seul champ éditable par ligne est Bestand — tout
             le reste (artNr/alterPreis/neuerPreis/preisdifferenz/art) vient du serveur et
             n'est jamais modifiable ici, ni même affiché comme s'il l'était. */}
        {screen === "preisanderung" && paScreen === "hub" && (() => {
          const PA_STATUS_META = {
            offen: { l: t("preisanderung.status.offen", "Offen"), c: P.muted, bg: P.soft },
            in_bearbeitung: { l: t("preisanderung.status.inBearbeitung", "In Bearbeitung"), c: A.orange, bg: `${A.orange}1f` },
            vollstaendig: { l: t("preisanderung.status.vollstaendig", "Vollständig"), c: A.blue, bg: `${A.blue}1f` },
            abgeschlossen: { l: t("preisanderung.status.abgeschlossen", "Abgeschlossen"), c: A.green, bg: `${A.green}1f` },
          };
          const openList = preisanderungen.filter((p) => p.status !== "abgeschlossen");
          const doneList = preisanderungen.filter((p) => p.status === "abgeschlossen");
          const row = (p) => {
            const meta = PA_STATUS_META[p.status];
            const counted = p.lines.filter((l) => l.bestand != null).length;
            const pct = p.lines.length ? Math.round((counted / p.lines.length) * 100) : 0;
            return (
              <Pressable key={p.id} onClick={() => openPreisanderung(p.id)} style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 15, marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: p.status === "abgeschlossen" ? 0 : 10 }}>
                  <span style={{ width: 40, height: 40, borderRadius: 13, display: "grid", placeItems: "center", fontSize: `calc(18px * var(--nx-font-scale, 1))`, flexShrink: 0, background: `${A.amber}1f`, color: A.amber }}>💶</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ display: "flex", alignItems: "center", gap: 6, fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>
                      {p.reference}
                      {p.isDemo && <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.purple, background: `${A.purple}1f`, borderRadius: 99, padding: "2px 7px" }}>🎲 DEMO</span>}
                    </strong>
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("preisanderung.hub.articleCountTemplate", { n: p.lines.length }, `${p.lines.length} article(s)`)} · {fmtFR((p.createdAt || "").slice(0, 10))}</span>
                  </div>
                  <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: meta.c, background: meta.bg, borderRadius: 99, padding: "4px 10px", flexShrink: 0 }}>{meta.l}</span>
                </div>
                {p.status !== "abgeschlossen" && (
                  <div style={{ height: 5, borderRadius: 99, background: P.soft, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: A.amber, transition: "width .2s ease" }} />
                  </div>
                )}
              </Pressable>
            );
          };
          return (
            <>
              {preisanderungen.length === 0 && (
                <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, marginTop: 30 }}>{t("preisanderung.hub.emptyText", "Aucune Preisänderung pour l'instant.")}</p>
              )}
              {openList.length > 0 && (
                <>
                  <h3 style={{ margin: "0 2px 10px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800 }}>{t("preisanderung.hub.openSectionTitle", "À traiter")}</h3>
                  {openList.map(row)}
                </>
              )}
              {doneList.length > 0 && (
                <>
                  <h3 style={{ margin: "20px 2px 10px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>{t("preisanderung.hub.doneSectionTitle", "Terminées")}</h3>
                  {doneList.map(row)}
                </>
              )}
            </>
          );
        })()}

        {screen === "preisanderung" && paScreen === "detail" && (() => {
          const doc = preisanderungen.find((p) => p.id === paActiveId);
          if (!doc) return <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, marginTop: 30 }}>{t("common.loading", "Chargement...")}</p>;
          const PA_STATUS_META = {
            offen: { l: t("preisanderung.status.offen", "Offen"), c: P.muted, bg: P.soft },
            in_bearbeitung: { l: t("preisanderung.status.inBearbeitung", "In Bearbeitung"), c: A.orange, bg: `${A.orange}1f` },
            vollstaendig: { l: t("preisanderung.status.vollstaendig", "Vollständig"), c: A.blue, bg: `${A.blue}1f` },
            abgeschlossen: { l: t("preisanderung.status.abgeschlossen", "Abgeschlossen"), c: A.green, bg: `${A.green}1f` },
          };
          const meta = PA_STATUS_META[doc.status];
          const total = doc.lines.length;
          const counted = doc.lines.filter((l) => l.bestand != null).length;
          const locked = doc.status === "abgeschlossen";
          const fmtEuro = (v) => v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
          // Gesamt Gutschrift/Belastung : simple somme des `betrag` DÉJÀ calculés serveur —
          // jamais un second calcul divergent depuis bestand×preisdifferenz ici.
          const gesamtGutschrift = doc.lines.filter((l) => l.art === "gutschrift").reduce((s, l) => s + (l.betrag || 0), 0);
          const gesamtBelastung = doc.lines.filter((l) => l.art === "belastung").reduce((s, l) => s + (l.betrag || 0), 0);

          return (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: meta.c, background: meta.bg, borderRadius: 99, padding: "5px 12px" }}>{meta.l}</span>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("preisanderung.detail.countedTemplate", { n: counted, total }, `${counted}/${total} comptés`)}</span>
                {doc.isDemo && <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.purple, background: `${A.purple}1f`, borderRadius: 99, padding: "4px 10px" }}>🎲 DEMO</span>}
              </div>
              <div style={{ height: 6, borderRadius: 99, background: P.soft, overflow: "hidden", marginBottom: 18 }}>
                <div style={{ height: "100%", width: `${total ? Math.round((counted / total) * 100) : 0}%`, background: locked ? A.green : A.amber, transition: "width .2s ease" }} />
              </div>

              {doc.lines.map((line) => {
                const draft = paBestandDraft[line.id];
                const value = draft !== undefined ? draft : (line.bestand == null ? "" : String(line.bestand));
                // Resynchronise le ref à chaque rendu (saisie clavier, rechargement serveur...)
                // — la mutation SYNCHRONE dans stepPaBestand ci-dessus reste la garantie
                // réelle pendant un appui maintenu, ceci ne fait que garder la base correcte
                // pour la PROCHAINE pression.
                paBestandLiveRef.current[line.id] = value === "" ? 0 : Number(value);
                const isDone = line.bestand != null;
                return (
                  <div key={line.id} style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 15, marginBottom: 10, border: `1.5px solid ${isDone ? `${A.green}40` : P.line}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, gap: 8 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{line.name}</strong>
                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{line.artNr}</span>
                      </div>
                      <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: line.preisdifferenz >= 0 ? A.green : A.red, flexShrink: 0 }}>
                        {line.preisdifferenz >= 0 ? "+" : ""}{line.preisdifferenz.toFixed(2)} €
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                      <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("preisanderung.line.alterPreisLabel", "Alter Preis")} <strong style={{ color: P.ink }}>{line.alterPreis.toFixed(2)} €</strong></span>
                      <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("preisanderung.line.neuerPreisLabel", "Neuer Preis")} <strong style={{ color: P.ink }}>{line.neuerPreis.toFixed(2)} €</strong></span>
                    </div>
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".03em", display: "block", marginBottom: 5 }}>Bestand</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {/* HoldButton (retour utilisateur : "activer le son... et vibration
                              aussi", "comme sur les autres boutons de l'app") — même
                              composant que les +/- de quantité déjà utilisés ailleurs
                              (hapticTap : vibration réelle Android, tic sonore synthétisé en
                              secours iOS) + maintien appuyé = défilement accéléré, jamais un
                              Pressable nu pour ce type de bouton. */}
                          <HoldButton disabled={locked} label="−1"
                            onTap={() => stepPaBestand(doc.id, line.id, line.bestand, -1)}
                            onRepeat={() => stepPaBestand(doc.id, line.id, line.bestand, -1)}
                            style={{
                              width: 36, height: 42, borderRadius: 12, background: P.soft, color: P.ink, fontSize: `calc(18px * var(--nx-font-scale, 1))`, fontWeight: 800,
                              display: "grid", placeItems: "center", flexShrink: 0, opacity: locked ? .5 : 1,
                            }}>−</HoldButton>
                          <input type="number" min="0" inputMode="numeric" disabled={locked} value={value}
                            onChange={(e) => setPaBestandDraft((m) => ({ ...m, [line.id]: e.target.value }))}
                            onBlur={(e) => { if (e.target.value !== "" && Number(e.target.value) === line.bestand) return; savePaBestand(doc.id, line.id, e.target.value); }}
                            style={{ ...inputStyle, flex: 1, minWidth: 0, fontSize: `calc(18px * var(--nx-font-scale, 1))`, fontWeight: 800, textAlign: "center", opacity: locked ? .6 : 1 }} placeholder="—" />
                          <HoldButton disabled={locked} label="+1"
                            onTap={() => stepPaBestand(doc.id, line.id, line.bestand, 1)}
                            onRepeat={() => stepPaBestand(doc.id, line.id, line.bestand, 1)}
                            style={{
                              width: 36, height: 42, borderRadius: 12, background: P.soft, color: P.ink, fontSize: `calc(18px * var(--nx-font-scale, 1))`, fontWeight: 800,
                              display: "grid", placeItems: "center", flexShrink: 0, opacity: locked ? .5 : 1,
                            }}>+</HoldButton>
                        </div>
                      </div>
                      <div style={{ flex: 1, textAlign: "right" }}>
                        <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".03em", display: "block", marginBottom: 5 }}>
                          {line.art === "gutschrift" ? "Gutschrift" : "Belastung"}
                        </span>
                        <strong style={{ display: "block", fontSize: `calc(18px * var(--nx-font-scale, 1))`, color: line.art === "gutschrift" ? A.green : A.red }}>
                          {line.betrag != null ? fmtEuro(line.betrag) : "—"}
                        </strong>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div style={{ position: "sticky", bottom: 0, marginTop: 18, paddingTop: 12, background: P.bg }}>
                <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 15, marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>{t("preisanderung.detail.gesamtGutschrift", "Gesamt Gutschrift")}</span>
                    <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: A.green }}>{fmtEuro(gesamtGutschrift)}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>{t("preisanderung.detail.gesamtBelastung", "Gesamt Belastung")}</span>
                    <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: A.red }}>{fmtEuro(gesamtBelastung)}</strong>
                  </div>
                </div>
                {!locked && (
                  <Pressable disabled={counted < total} onClick={() => setPaSummarySheet(true)} style={{ width: "100%", ...btnSolid(A.amber), opacity: counted < total ? .5 : 1 }}>
                    {counted < total
                      ? t("preisanderung.detail.missingBtnTemplate", { n: total - counted }, `${total - counted} article(s) restant(s)`)
                      : t("preisanderung.detail.finishBtn", "✓ Terminer la Preisänderung")}
                  </Pressable>
                )}
              </div>
            </>
          );
        })()}

        {/* ================= SCANNER (ASSOCIER EAN) ================= */}
        {screen === "scanner" && scanScreen === "hub" && (
          <>
            <p style={{ margin: "0 2px 14px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
              {t("scanner.hubText", "Lie ou délie un code-barres à tout moment, dans n'importe quel rayon.")}
            </p>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {[
                { v: articles.length, l: t("articles.statTotal", "Total"), c: P.ink },
                { v: scanLinkedCount, l: t("scanner.statLinked", "Liés"), c: A.green },
                { v: articles.length - scanLinkedCount, l: t("scanner.statUnlinked", "Non liés"), c: A.red },
              ].map((s) => (
                <div key={s.l} style={{ flex: 1, textAlign: "center", background: P.soft, borderRadius: 14, padding: "10px 3px" }}>
                  <strong style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, color: s.c, display: "block" }}>{s.v}</strong>
                  <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{s.l}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {SCANNER_ZONES.map((z) => {
                const zoneArticles = z.id === "kuehlung" ? articles : [];
                const linked = zoneArticles.filter((a) => scanArticleEan(a.artNr)).length;
                return (
                  <Pressable key={z.id} onClick={() => { setScanZone(z.id); setScanScreen("list"); }} style={{
                    ...colorCard(z.bg), padding: 15, position: "relative", minHeight: 110,
                    display: "flex", flexDirection: "column", justifyContent: "flex-end",
                  }}>
                    <div style={{ ...onColorChip, width: 40, height: 40, marginBottom: 9 }}><EmojiIcon e={z.emoji} size={19} /></div>
                    <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: "#fff" }}>{scannerZoneLabel(z.id)}</strong>
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("scanner.zoneCountTemplate", { n: zoneArticles.length }, `${zoneArticles.length} article${zoneArticles.length > 1 ? "s" : ""}`)} · {t("scanner.zoneLinkedTemplate", { n: linked }, `${linked} lié${linked > 1 ? "s" : ""}`)}</span>
                  </Pressable>
                );
              })}
            </div>
          </>
        )}

        {screen === "scanner" && scanScreen === "list" && (() => {
          const zone = SCANNER_ZONES.find((z) => z.id === scanZone) || SCANNER_ZONES[0];
          // Retour utilisateur explicite (2026-08-12) : ordre EXACT du Kühlung (sortOrder),
          // jamais l'ordre brut de récupération — même tri que le module Articles
          // (sortArtByOrder), pour que Scanner et Articles montrent toujours la même liste
          // dans le même ordre.
          const zoneArticlesAll = scanZone === "kuehlung" ? [...articles].sort(sortArtByOrder) : [];
          // Même correctif que la sheet "Liste" du Contrôle Kühlung : jusqu'à 376
          // articles réels rendus d'un coup sans aucune recherche ni plafond ici — un
          // des postes de mémoire trouvés lors du diagnostic du crash PWA (voir
          // storeflow_pwa_stale_bundle_crash).
          const q = scanListSearch.trim().toLowerCase();
          const zoneArticles = q ? zoneArticlesAll.filter((a) => a.name.toLowerCase().includes(q) || a.artNr.includes(q)) : zoneArticlesAll;
          const CAP = 60;
          const shown = scanListBatches * CAP;
          const capped = !q && zoneArticles.length > shown;
          const visibleZoneArticles = !q && zoneArticles.length > shown ? zoneArticles.slice(0, shown) : zoneArticles;
          return (
            <div className="nx-screen-enter">
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700, flex: 1 }}>{t("scanner.zoneBarHint", "Rayon en cours")} : {zone.emoji} {scannerZoneLabel(zone.id)}</span>
                <Pressable onClick={() => { setScanScreen("hub"); setScanListSearch(""); }} style={{
                  fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, ...card, borderRadius: 99, padding: "8px 13px",
                }}>{t("scanner.changeZoneBtn", "Changer")}</Pressable>
              </div>
              <input style={{ ...inputStyle, marginBottom: 12 }} placeholder={t("controlRun.searchPlaceholder", "Rechercher Art.Nr ou nom...")}
                value={scanListSearch} onChange={(e) => setScanListSearch(e.target.value)} />
              {zoneArticles.length === 0 && (
                <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 26 }}>
                  <span style={{ fontSize: `calc(30px * var(--nx-font-scale, 1))`, marginBottom: 6 }}>📭</span>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{q ? t("scanner.noArticleSearchText", "Aucun article ne correspond à ta recherche.") : t("scanner.noArticleInZoneText", "Aucun article dans ce rayon pour l'instant.")}</strong>
                </section>
              )}
              <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
              {visibleZoneArticles.map((a, aIdx) => {
                const code = scanArticleEan(a.artNr);
                return (
                  <Pressable key={a.artNr} feedback className="nx-list-item-in" onClick={() => openScanLink(a)} style={{ ...card, ...tile, padding: 13, marginBottom: isWide ? 0 : 8, animationDelay: `${Math.min(aIdx, 14) * 28}ms` }}>
                    <span style={{
                      flex: "0 0 auto", width: 38, height: 38, borderRadius: 13, color: code ? A.green : P.muted,
                      background: code ? "rgba(30,184,95,.13)" : P.soft, display: "grid", placeItems: "center",
                    }}><EmojiIcon e={code ? "🔗" : "📦"} size={18} /></span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block" }}>{a.name}</strong>
                      <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlRun.artNrPrefix", "Art.Nr")} {a.artNr}</span>
                    </div>
                    <span style={{
                      fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 9px", whiteSpace: "nowrap",
                      color: code ? "#fff" : P.muted, background: code ? A.green : P.soft,
                    }}>{code ? `…${code.slice(-5)}` : t("scanner.notLinkedBadge", "Non lié")}</span>
                  </Pressable>
                );
              })}
              </div>
              {capped && (
                <>
                  <Pressable onClick={() => setScanListBatches((n) => n + 1)} style={{
                    width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, marginTop: 4,
                    background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                  }}>{t("scanner.showMoreBtnTemplate", { shown: Math.min(shown, zoneArticles.length), total: zoneArticles.length }, `Afficher plus (${Math.min(shown, zoneArticles.length)}/${zoneArticles.length})`)}</Pressable>
                  <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, margin: "6px 0 0" }}>
                    {t("scanner.cappedHintSearchText", "Ou tape pour rechercher un article précis.")}
                  </p>
                </>
              )}
            </div>
          );
        })()}

        {/* ================= MESSAGES ================= */}
        {screen === "messages" && msgScreen === "list" && (
          <div className="nx-screen-enter" style={{ position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, ...card, borderRadius: 16, padding: "12px 15px", marginBottom: 12 }}>
              <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, opacity: .5 }}>🔎</span>
              <input value={msgListQuery} onChange={(e) => setMsgListQuery(e.target.value)} placeholder={t("messages.listSearchPlaceholder", "Rechercher une conversation…")} style={{
                flex: 1, border: "none", outline: "none", background: "none", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: P.ink,
              }} />
              {msgListQuery && <Pressable onClick={() => setMsgListQuery("")} style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted }}>✕</Pressable>}
            </div>
            {/* Refonte 2026-08-17 (retour utilisateur, artefact validé) : fondu sur le bord
                droit de la rangée de filtres — signale qu'on peut glisser au lieu d'une
                coupure nette qui donnait l'impression d'un écran cassé/tronqué. */}
            <div style={{ position: "relative", marginBottom: 15 }}>
              <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", paddingBottom: 2, paddingRight: 28 }}>
                {[
                  { id: "all", l: t("messages.filter.all", "Toutes") }, { id: "unread", l: t("messages.filter.unread", "Non lues") }, { id: "private", l: t("messages.filter.private", "Privées") },
                  { id: "group", l: t("messages.filter.group", "Groupes") }, { id: "branch", l: t("messages.filter.branch", "Filiale") },
                  { id: "archived", l: t("messages.filter.archived", "Archivées") },
                ].map((f) => {
                  const on = msgFilter === f.id;
                  return (
                    <Pressable key={f.id} onClick={() => setMsgFilter(f.id)} style={{
                      flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: on ? "#fff" : P.ink,
                      ...(on ? { background: `linear-gradient(160deg, ${nxMixHex(A.frost, "#ffffff", .1)} 0%, ${A.frost} 60%)`, boxShadow: `0 6px 14px ${A.frost}4d` } : { background: P.soft, border: `1.5px solid ${P.line}` }),
                    }}>{f.l}</Pressable>
                  );
                })}
              </div>
              <div style={{ position: "absolute", right: 0, top: 0, bottom: 2, width: 30, background: `linear-gradient(90deg, transparent, ${P.bg})`, pointerEvents: "none" }} />
            </div>
            {msgLoading && filteredConversations.length === 0 && (
              <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("haccp.loadingText", "Chargement...")}</p>
            )}
            {!msgLoading && filteredConversations.length === 0 && (
              <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("messages.emptyHereText", "Aucune conversation ici.")}</p>
            )}
            {/* minmax(0,1fr), jamais juste "1fr" — piège CSS Grid déjà documenté sur ce
                projet : sans le 0 explicite, une piste de grille ne se contracte jamais
                sous la largeur minimale de son contenu, donc le texte non cassable d'une
                carte (ex. l'aperçu du dernier message) pousse toute la grille en dehors de
                l'écran au lieu d'être tronqué par l'ellipsis déjà posé dessus. */}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 9 }}>
            {filteredConversations.map((c, cIdx) => {
              const unread = c.unread || 0;
              const typeMeta = {
                private: { emoji: "💬", bg: A.blue }, group: { emoji: "👥", bg: A.purple },
                multi_branch: { emoji: "🏢", bg: A.teal },
              }[c.type] || { emoji: "📢", bg: A.orange };
              // Initiales pour les conversations privées (repère plus lisible qu'une bulle
              // générique — retour utilisateur, artefact validé) ; icône dédiée conservée
              // pour groupe/filiale/annonce, où il n'y a pas UN interlocuteur à initialer.
              const initials = c.type === "private"
                ? String(c.title || "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()
                : null;
              const lastText = c.lastMessagePreview || t("messages.noMessageFallback", "Aucun message");
              const isArchivedTab = msgFilter === "archived";
              const row = (
                <Pressable feedback onClick={() => isArchivedTab ? unarchiveConv(c.id) : openThread(c.id)} style={{
                  ...card, ...tile, padding: 13, marginBottom: 0,
                  ...(unread > 0 ? { boxShadow: `0 2px 6px rgba(20,24,38,.04), ${P.shadow}, 0 0 0 1.5px ${A.frost}29` } : {}),
                }}>
                  <span style={{
                    flex: "0 0 auto", width: 48, height: 48, borderRadius: 16, color: "#fff",
                    border: "1px solid rgba(255,255,255,.16)",
                    display: "grid", placeItems: "center", fontSize: `calc(${(initials ? 15 : 19)}px * var(--nx-font-scale, 1))`, fontWeight: initials ? 800 : 400, letterSpacing: initials ? "-.02em" : 0,
                    ...premiumTileStyle(typeMeta.bg),
                  }}>{initials || typeMeta.emoji}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 6 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(13.8px * var(--nx-font-scale, 1))`, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.title}</strong>
                        {c.muted && <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>🔕</span>}
                      </div>
                      {c.lastMessageAt && <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: unread > 0 ? A.frost : P.muted, fontWeight: 800, flex: "0 0 auto", fontVariantNumeric: "tabular-nums" }}>{new Date(c.lastMessageAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 3 }}>
                      <span style={{
                        fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: unread > 0 ? P.ink : P.muted, fontWeight: unread > 0 ? 700 : 400,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0,
                      }}>{lastText}</span>
                      {!isArchivedTab && unread > 0 && <span style={{ width: 8, height: 8, borderRadius: 99, background: A.frost, flex: "0 0 auto" }} />}
                    </div>
                  </div>
                  {isArchivedTab ? (
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.frost, flex: "0 0 auto" }}>↩️ {t("messages.unarchiveBtn", "Désarchiver")}</span>
                  ) : unread > 1 && (
                    <span style={{
                      fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: A.red, borderRadius: 99,
                      minWidth: 20, height: 20, display: "grid", placeItems: "center", padding: "0 6px", flex: "0 0 auto",
                      boxShadow: "0 2px 6px rgba(239,68,68,.4)",
                    }}>{unread}</span>
                  )}
                </Pressable>
              );
              // SwipeToDelete retiré ici (retour utilisateur explicite, bug réel vécu :
              // un tap normal pour OUVRIR une conversation était intercepté comme un swipe
              // et déclenchait "quitter" silencieusement — même piège déjà connu sur
              // Admin/Planning, voir storeflow_pwa_stale_bundle_crash). "Quitter" reste
              // disponible via un vrai bouton explicite dans la sheet Infos (ℹ️), jamais
              // perdu, juste plus de geste ambigu sur la ligne elle-même.
              return <div key={c.id} className="nx-list-item-in" style={{ animationDelay: `${Math.min(cIdx, 14) * 28}ms` }}>{row}</div>;
            })}
            </div>

            {/* Bouton "nouveau message" flottant — remplace l'icône crayon discrète de
                l'en-tête (retour utilisateur, artefact validé : l'action la plus fréquente
                doit être la plus visible). */}
            <Pressable
              onClick={() => { setNewConvType("private"); setNewConvMembers([]); setNewConvTitle(""); setNewConvStores([]); setNewConvQuery(""); setMsgScreen("new"); }}
              aria-label={t("messages.newMessageAria", "Nouveau message")}
              style={{
                position: "fixed", right: isWide ? 40 : 20, bottom: `calc(${isWide ? 28 : 96}px + env(safe-area-inset-bottom))`,
                width: 56, height: 56, borderRadius: 18, color: "#fff", fontSize: `calc(22px * var(--nx-font-scale, 1))`,
                display: "grid", placeItems: "center", zIndex: 20,
                ...premiumTileStyle(A.blue),
              }}
            >✎</Pressable>
          </div>
        )}

        {screen === "messages" && msgScreen === "thread" && (() => {
          const conv = conversations.find((c) => c.id === msgActiveId);
          if (!conv) return null;
          const myId = user?.proId || user?.id;
          const AUTHOR_COLORS = [A.blue, A.purple, A.green, A.orange, A.teal, A.brown, A.frost];
          const colorForAuthor = (id) => {
            let h = 0; for (const ch of String(id)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
            return AUTHOR_COLORS[h % AUTHOR_COLORS.length];
          };
          let lastDateLabel = "";
          return (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {msgThreadLoading && msgThreadMessages.length === 0 && (
                <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, marginTop: 30 }}>{t("haccp.loadingText", "Chargement...")}</p>
              )}
              {!msgThreadLoading && msgThreadMessages.length === 0 && (
                <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, marginTop: 30 }}>{t("messages.noMessagesSayHiText", "Aucun message — dis bonjour 👋")}</p>
              )}
              {msgThreadMessages.map((m, i) => {
                const isMe = String(m.authorId || "").trim().toLowerCase() === String(myId || "").trim().toLowerCase();
                const dateLabel = new Date(m.createdAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "long" });
                const showDateSep = dateLabel !== lastDateLabel;
                lastDateLabel = dateLabel;
                const prevSameAuthor = i > 0 && msgThreadMessages[i - 1].authorId === m.authorId && !showDateSep;
                const authorColor = colorForAuthor(m.authorId);
                return (
                  <Fragment key={m.id}>
                    {showDateSep && (
                      <div style={{ textAlign: "center", margin: "14px 0 10px" }}>
                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, background: P.soft, borderRadius: 99, padding: "4px 12px" }}>{dateLabel}</span>
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: prevSameAuthor ? 3 : 12, flexDirection: isMe ? "row-reverse" : "row" }}>
                      {!isMe && conv.type !== "private" && (
                        <span style={{
                          flex: "0 0 auto", width: 26, height: 26, borderRadius: 99, fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff",
                          background: authorColor, display: "grid", placeItems: "center", visibility: prevSameAuthor ? "hidden" : "visible",
                        }}>{(m.authorName || "?").charAt(0)}</span>
                      )}
                      <div style={{ display: "flex", flexDirection: "column", alignItems: isMe ? "flex-end" : "flex-start", maxWidth: "78%" }}>
                        {!isMe && conv.type !== "private" && !prevSameAuthor && (
                          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: authorColor, margin: "0 4px 3px" }}>{m.authorName}</span>
                        )}
                        <Pressable onClick={() => !m.deleted && startReply(m)} style={{
                          padding: m.deleted ? "9px 14px" : "10px 14px", borderRadius: 18,
                          borderBottomRightRadius: isMe && !m.deleted ? 5 : 18, borderBottomLeftRadius: !isMe && !m.deleted ? 5 : 18,
                          background: m.deleted ? "transparent" : isMe ? `linear-gradient(155deg, rgba(255,255,255,.16), rgba(255,255,255,0) 55%), ${A.frost}` : P.card,
                          color: m.deleted ? P.muted : isMe ? "#fff" : P.ink,
                          border: m.deleted ? `1px dashed ${P.line}` : isMe ? "none" : `1px solid ${P.line}`,
                          boxShadow: m.deleted ? "none" : isMe ? `0 4px 12px ${A.frost}40` : P.shadow,
                          fontStyle: m.deleted ? "italic" : "normal",
                        }}>
                          {m.replyTo && !m.deleted && (
                            <div style={{
                              borderLeft: `2.5px solid ${isMe ? "rgba(255,255,255,.6)" : A.frost}`, paddingLeft: 8, marginBottom: 6,
                              opacity: isMe ? .85 : .7,
                            }}>
                              <strong style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, display: "block" }}>{m.replyTo.authorName}</strong>
                              <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`}}>{(m.replyTo.preview || "").slice(0, 60)}</span>
                            </div>
                          )}
                          {m.text && <span style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, lineHeight: 1.4 }}>{m.deleted ? t("messages.deletedPlaceholder", "Message supprimé") : m.text}</span>}
                          {!m.deleted && (m.attachments || []).length > 0 && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: m.text ? 8 : 0 }}>
                              {m.attachments.map((a) => {
                                const url = `/api/messages/${conv.id}/attachments/${a.id}`;
                                const isImage = (a.mimeType || "").startsWith("image/");
                                return isImage ? (
                                  <img key={a.id} src={url} alt={a.originalName} onClick={(e) => { e.stopPropagation(); setMissionPhotoLightbox(url); }} style={{
                                    width: 160, maxWidth: "100%", borderRadius: 12, display: "block", cursor: "pointer",
                                  }} />
                                ) : (
                                  <a key={a.id} href={url} download={a.originalName} onClick={(e) => e.stopPropagation()} style={{
                                    display: "flex", alignItems: "center", gap: 6, fontSize: `calc(12px * var(--nx-font-scale, 1))`, textDecoration: "underline",
                                    color: isMe ? "#fff" : A.frost,
                                  }}>📎 {a.originalName}</a>
                                );
                              })}
                            </div>
                          )}
                        </Pressable>
                        <div style={{ display: "flex", alignItems: "center", gap: 7, margin: "3px 4px 0" }}>
                          <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                            {new Date(m.createdAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                          {m.editedAt && !m.deleted && <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, color: P.muted, fontStyle: "italic" }}>{t("messages.edited", "Modifié")}</span>}
                          {isMe && !m.deleted && (
                            <>
                              <Pressable onClick={() => startEditMessage(m)} style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("common.editAria", "Modifier")}</Pressable>
                              <Pressable onClick={() => deleteMessage(conv.id, m.id)} style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: A.red, fontWeight: 700 }}>{t("common.delete", "Supprimer")}</Pressable>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </Fragment>
                );
              })}
            </div>
          );
        })()}

        {screen === "messages" && msgScreen === "new" && (() => {
          const TYPE_DEFS = [
            { id: "private", icon: "💬", l: t("messages.new.private", "Message privé"), d: t("messages.new.privateDesc", "Une conversation avec une personne"), c: A.frost },
            ...(msgCanCreateGroup ? [{ id: "group", icon: "👥", l: t("messages.new.group", "Nouveau groupe"), d: t("messages.new.groupDesc", "Créer un groupe personnalisé"), c: A.purple }] : []),
            // Communication multi-filiale — le point central de la demande GR (un
            // responsable réseau doit pouvoir écrire à toutes ses filiales/employés
            // d'un coup), server déjà réel (POST /api/messages/multi-branch), jamais
            // exposé côté client avant l'audit du 2026-08-02.
            ...(msgCanCreateCrossBranch ? [{ id: "multi-branch", icon: "🏢", l: t("messages.new.multiBranch", "Communication multi-filiale"), d: t("messages.new.multiBranchDescShort", "Écrire à plusieurs filiales à la fois"), c: A.orange }] : []),
          ];
          const q = newConvQuery.trim().toLowerCase();
          const filteredRecipients = !q ? msgRecipients : msgRecipients.filter((r) =>
            [r.name, r.role, r.storeId].some((f) => (f || "").toLowerCase().includes(q)));
          // Groupe/multi-filiale gardent un bouton explicite (sélection multiple, pas
          // d'action immédiate possible) — mais collé en bas d'écran (sticky), jamais à
          // atteindre en défilant toute la liste des destinataires (retour utilisateur
          // explicite 2026-08-18).
          const createValid = newConvType === "group"
            ? newConvMembers.length >= 2 && newConvTitle.trim().length > 0
            : newConvStores.length > 0 || newConvMembers.length > 0;
          const createLabel = newConvType === "group"
            ? t("messages.new.createGroupCountTemplate", { n: newConvMembers.length }, `Créer le groupe (${newConvMembers.length})`)
            : t("messages.new.create", "Créer");
          return (
            <>
              <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".04em" }}>{t("messages.typeHeading", "Type de conversation")}</span>
              <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
                {TYPE_DEFS.map((tp) => {
                  const on = newConvType === tp.id;
                  return (
                    <Pressable key={tp.id} onClick={() => { setNewConvType(tp.id); setNewConvMembers([]); setNewConvStores([]); setNewConvQuery(""); }} style={{
                      flex: 1, minWidth: 0, ...card, flexDirection: "column", alignItems: "flex-start", gap: 8, padding: 13, position: "relative",
                      border: `1.5px solid ${on ? tp.c : P.line}`, background: on ? `${tp.c}14` : P.card,
                    }}>
                      <span style={{ width: 34, height: 34, borderRadius: 11, display: "grid", placeItems: "center", fontSize: `calc(15px * var(--nx-font-scale, 1))`, flexShrink: 0, ...premiumTileStyle(tp.c) }}>{tp.icon}</span>
                      <div style={{ minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, display: "block", lineHeight: 1.25 }}>{tp.l}</strong>
                        <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 3, display: "block", lineHeight: 1.3 }}>{tp.d}</span>
                      </div>
                      {on && <span style={{ position: "absolute", top: 10, right: 10, fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: tp.c }}>✓</span>}
                    </Pressable>
                  );
                })}
              </div>
              {(newConvType === "group" || newConvType === "multi-branch") && (
                <Field label={newConvType === "multi-branch" ? t("messages.multiBranchNameLabel", "Nom de la communication (facultatif)") : t("messages.groupNameLabel", "Nom du groupe")}>
                  <input style={inputStyle} placeholder={newConvType === "multi-branch" ? t("messages.multiBranchNamePlaceholder", "Ex. Direction réseau") : t("messages.groupNamePlaceholderShort", "Ex. Équipe caisse")} value={newConvTitle} onChange={(e) => setNewConvTitle(e.target.value)} />
                </Field>
              )}
              {newConvType === "multi-branch" && (
                <>
                  <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>{t("messages.storesSelectedCountTemplate", { n: newConvStores.length }, `Filiales (${newConvStores.length} sélectionnée${newConvStores.length > 1 ? "s" : ""})`)}</span>
                  <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 16 }}>
                    {msgAvailableStores.length === 0 && (
                      <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px 10px" }}>{t("messages.noBranchAvailable", "Aucune filiale disponible.")}</p>
                    )}
                    {msgAvailableStores.map((s) => {
                      const sel = newConvStores.includes(s);
                      return (
                        <Pressable key={s} onClick={() => toggleNewConvStore(s)} style={{
                          padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                          background: sel ? A.frost : P.soft, color: sel ? "#fff" : P.ink,
                          border: `1.5px solid ${sel ? A.frost : P.line}`,
                        }}>{s}</Pressable>
                      );
                    })}
                  </div>
                </>
              )}
              <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>
                {newConvType === "multi-branch" ? t("messages.multiBranchRecipientsTitle", "Destinataires individuels (facultatif, en plus des filiales)") : t("messages.new.recipientsTitle", "Destinataires")}
              </span>
              {msgRecipients.length > 6 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, ...card, padding: "9px 13px", marginBottom: 12 }}>
                  <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted }}>🔎</span>
                  <input value={newConvQuery} onChange={(e) => setNewConvQuery(e.target.value)} placeholder={t("messages.new.recipientsPlaceholder", "Nom, rôle ou filiale...")} style={{
                    flex: 1, minWidth: 0, border: "none", outline: "none", background: "none", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.ink,
                  }} />
                  {newConvQuery && <Pressable onClick={() => setNewConvQuery("")} style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>✕</Pressable>}
                </div>
              )}
              {msgRecipients.length === 0 && (
                <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px 10px" }}>{t("messages.noRecipientAvailable", "Aucun destinataire disponible.")}</p>
              )}
              {msgRecipients.length > 0 && filteredRecipients.length === 0 && (
                <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px 10px" }}>{t("messages.noResultsShort", "Aucun résultat.")}</p>
              )}
              {filteredRecipients.map((r) => {
                const sel = newConvMembers.includes(r.employeeId);
                const isPrivate = newConvType === "private";
                const creating = newConvCreatingId === r.employeeId;
                return (
                  <Pressable key={r.employeeId} disabled={isPrivate && !!newConvCreatingId} onClick={() => {
                    if (isPrivate) { createPrivateConversationWith(r.employeeId); return; }
                    toggleNewConvMember(r.employeeId);
                  }} style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box", padding: 12, borderRadius: 14, marginBottom: 8,
                    background: sel ? "rgba(45,127,240,.1)" : P.soft, border: `1.5px solid ${sel ? A.frost : P.line}`, opacity: isPrivate && newConvCreatingId && !creating ? .5 : 1,
                  }}>
                    <span style={{ width: 34, height: 34, borderRadius: 99, color: "#fff", border: "1px solid rgba(255,255,255,.16)", display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800, flexShrink: 0, ...premiumTileStyle(A.frost) }}>{(r.name || "?").charAt(0)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{r.name}</strong>
                      {(newConvType === "multi-branch" || (!isPrivate && (r.role || r.storeId))) && <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{r.role}{r.role && r.storeId ? " · " : ""}{r.storeId}</span>}
                    </div>
                    {isPrivate
                      ? <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, color: P.muted }}>{creating ? "…" : "›"}</span>
                      : (sel && <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, color: A.frost }}>✓</span>)}
                  </Pressable>
                );
              })}
              {(newConvType === "group" || newConvType === "multi-branch") && (
                <div style={{ position: "sticky", bottom: 0, marginTop: 10, paddingTop: 10, background: P.bg }}>
                  <Pressable disabled={!createValid} onClick={createConversation} style={{ width: "100%", ...btnSolid(A.frost), opacity: createValid ? 1 : .5 }}>{createLabel}</Pressable>
                </div>
              )}
            </>
          );
        })()}

        {/* Recherche dans les messages — GET /api/messages/search, déjà réel côté serveur
            (jamais borné qu'à MES conversations), jamais exposé côté client avant l'audit
            du 2026-08-02. */}
        {screen === "messages" && msgScreen === "search" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, ...card, padding: "10px 14px", marginBottom: 16 }}>
              <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: P.muted }}>🔎</span>
              <input autoFocus value={msgSearchQuery} onChange={(e) => runMessageSearch(e.target.value)} placeholder={t("messages.searchInMessagesPlaceholder", "Rechercher un mot dans tes messages…")} style={{
                flex: 1, border: "none", outline: "none", background: "none", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: P.ink,
              }} />
              {msgSearchQuery && <Pressable onClick={() => runMessageSearch("")} style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted }}>✕</Pressable>}
            </div>
            {msgSearchLoading && <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("messages.searchingText", "Recherche...")}</p>}
            {!msgSearchLoading && msgSearchQuery.trim() && msgSearchResults.length === 0 && (
              <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("messages.noResultsShort", "Aucun résultat.")}</p>
            )}
            {msgSearchResults.map((r) => (
              <Pressable key={r.messageId} onClick={() => { setMsgScreen("list"); openThread(r.conversationId); }} style={{ ...card, ...tile, padding: 13, marginBottom: 9, flexDirection: "column", alignItems: "stretch" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{r.conversationTitle}</strong>
                  <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted, flex: "0 0 auto" }}>{new Date(r.createdAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}</span>
                </div>
                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{r.authorName} · {r.preview}</span>
              </Pressable>
            ))}
          </>
        )}

        {/* ================= INFOS IMPORTANTES ================= */}
        {screen === "infos" && infoScreen === "hub" && (
          <div className="nx-screen-enter">
            <section style={{ ...colorCard(infoCategoryFilter === "rundschreiben" ? A.navy : A.red), ...tile, flexDirection: "column", alignItems: "stretch", padding: 19 }}>
              <span style={onColorTag}>{infoCategoryFilter === "rundschreiben" ? t("rundschreiben.hero.eyebrow", "Pont Niederlassung ↔ filiales") : t("importantInfo.hero.eyebrow", "Canal officiel")}</span>
              <h2 style={{ margin: "5px 0 6px", fontSize: `calc(18px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{infoCategoryFilter === "rundschreiben" ? t("rundschreiben.hero.title", "Rundschreiben") : t("importantInfo.hero.title", "Consignes & rappels importants")}</h2>
              <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, lineHeight: 1.45, ...onColorSub }}>{infoCategoryFilter === "rundschreiben" ? t("rundschreiben.hero.desc", "Circulaires hebdomadaires de la Niederlassung — lecture certifiée et signature.") : t("importantInfo.hero.desc", "Certaines nécessitent une lecture certifiée et une signature.")}</p>
            </section>

            {(infoCategoryFilter === "rundschreiben" ? rundschreibenMandatoryPending : infoMandatoryPending) > 0 && (
              <div style={{ display: "flex", gap: 9, borderRadius: 14, padding: 12, marginBottom: 14, background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.28)" }}>
                <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>⚠️</span>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: A.red, lineHeight: 1.5, fontWeight: 700 }}>
                  {infoCategoryFilter === "rundschreiben"
                    ? t("rundschreiben.mandatoryPendingCountTemplate", { n: rundschreibenMandatoryPending }, `${rundschreibenMandatoryPending} Rundschreiben en attente de signature.`)
                    : t("importantInfo.mandatoryPendingCountTemplate", { n: infoMandatoryPending }, `${infoMandatoryPending} information${infoMandatoryPending > 1 ? "s" : ""} obligatoire${infoMandatoryPending > 1 ? "s" : ""} en attente de signature.`)}
                </span>
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[
                { id: "toread", l: `${t("importantInfo.tab.toread", "À traiter")} · ${infosByTab("toread").length}` },
                { id: "done", l: `${t("importantInfo.tab.done", "Terminées")} · ${infosByTab("done").length}` },
                { id: "archived", l: `${t("importantInfo.tab.archived", "Archives")} · ${infosByTab("archived").length}` },
              ].map((tb) => (
                <Pressable key={tb.id} onClick={() => setInfoTab(tb.id)} style={{
                  flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                  background: infoTab === tb.id ? A.red : P.soft, color: infoTab === tb.id ? "#fff" : P.ink,
                  border: `1.5px solid ${infoTab === tb.id ? A.red : P.line}`,
                }}>{tb.l}</Pressable>
              ))}
            </div>

            {infosByTab(infoTab).length === 0 && (
              <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("claims.emptyHereText", "Rien ici pour l'instant.")}</p>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
            {infosByTab(infoTab).map((i, iIdx) => {
              const lm = levelMeta[i.importanceLevel];
              return (
                <Pressable key={i.id} feedback className="nx-list-item-in" onClick={() => openInfoDetail(i.id)} style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 9, alignItems: "flex-start", animationDelay: `${Math.min(iIdx, 14) * 28}ms` }}>
                  <span style={{ flex: "0 0 auto", width: 40, height: 40, borderRadius: 14, background: `${lm.color}22`, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>
                    {i.importanceLevel === "urgent" ? "🚨" : i.importanceLevel === "important" ? "❗" : i.importanceLevel === "attention" ? "⚠️" : "📌"}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{i.title}</strong>
                      {i.type === "mandatory" && <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: A.red, borderRadius: 99, padding: "2px 7px" }}>{t("importantInfo.signatureRequiredBadge", "Signature requise")}</span>}
                      {i.status === "cancelled" && <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, background: P.soft, borderRadius: 99, padding: "2px 7px" }}>{t("importantInfo.manage.status.cancelled", "Annulée")}</span>}
                    </div>
                    <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 2 }}>{i.summary}</span>
                    <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: lm.color, marginTop: 5, display: "block" }}>{lm.label} · {i.authorName} · {fmtFR((i.publishedAt || "").slice(0, 10))}</span>
                  </div>
                </Pressable>
              );
            })}
            </div>

            {(infoCategoryFilter === "rundschreiben" ? infoScope.canCreateRundschreiben : infoScope.canCreate) && (
              <Pressable onClick={() => { setInfoScreen("manage"); loadInfoManage(); }} style={{ ...colorCard(A.navy), ...tile, padding: 14, marginTop: 4 }}>
                <span style={onColorChip}><EmojiIcon e="🗞️" /></span>
                <div style={{ flex: 1 }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: "#fff" }}>{infoCategoryFilter === "rundschreiben" ? t("rundschreiben.manage.pageTitle", "Rundschreiben publiés") : t("importantInfo.manage.pageTitle", "Mes publications")}</strong>
                  <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, ...onColorSub }}>{infoCategoryFilter === "rundschreiben" ? t("rundschreiben.manage.myPostsDesc", "Publier et suivre la réception par filiale.") : t("importantInfo.myPostsDesc", "Créer et gérer les informations diffusées.")}</div>
                </div>
                <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>›</span>
              </Pressable>
            )}
          </div>
        )}

        {screen === "infos" && infoScreen === "detail" && (() => {
          if (!infoDetail) return <p style={{ color: P.muted, fontSize: `calc(13px * var(--nx-font-scale, 1))`, textAlign: "center", marginTop: 40 }}>{t("krankmeldung.loading", "Chargement...")}</p>;
          const { info, version, recipient, signature } = infoDetail;
          const lm = levelMeta[info.importanceLevel];
          const expired = Boolean(info.expiresAt && info.expiresAt < todayISO());
          const inactive = info.status !== "published" || expired;
          const needsSig = info.type === "mandatory" && !inactive && !signature;
          const needsRead = info.type === "normal" && !inactive && recipient && recipient.status !== "read";
          return (
            <>
              <section style={{ ...colorCard(lm.color), ...tile, flexDirection: "column", alignItems: "stretch", padding: 19 }}>
                <span style={onColorTag}>{lm.label}{info.type === "mandatory" ? t("importantInfo.signatureRequiredSuffix", " · signature requise") : ""}</span>
                <h2 style={{ margin: "5px 0 6px", fontSize: `calc(18px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{info.title}</h2>
                <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("importantInfo.publishedByTemplate", { name: info.authorName, date: fmtFR((info.publishedAt || "").slice(0, 10)) }, `Publié par ${info.authorName} · ${fmtFR((info.publishedAt || "").slice(0, 10))}`)}{info.expiresAt ? t("importantInfo.expiresOnSuffix", { date: fmtFR(info.expiresAt) }, ` · expire le ${fmtFR(info.expiresAt)}`) : ""}</span>
              </section>
              {info.status === "cancelled" && (
                <div style={{ borderRadius: 14, padding: 12, marginBottom: 14, background: P.soft, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("importantInfo.cancelledByAuthorText", "Cette information a été annulée par son auteur.")}</div>
              )}
              {expired && info.status === "published" && (
                <div style={{ borderRadius: 14, padding: 12, marginBottom: 14, background: P.soft, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("importantInfo.expiredOnTemplate", { date: fmtFR(info.expiresAt) }, `Cette information a expiré le ${fmtFR(info.expiresAt)}.`)}</div>
              )}
              <div style={{ ...card, borderRadius: 18, padding: 16, marginBottom: 16 }}>
                <p style={{ margin: 0, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{version?.content}</p>
              </div>
              {(version?.attachments || []).map((a) => (
                <Pressable key={a.id} onClick={() => window.open(`/api/important-info/${info.id}/attachment/${a.id}`, "_blank")} style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box", padding: 13, borderRadius: 16, marginBottom: 14, background: P.soft,
                }}>
                  <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>📎</span>
                  <span style={{ flex: 1, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.fileName}</span>
                  <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>›</span>
                </Pressable>
              ))}
              {signature && (
                <div style={{ ...card, borderRadius: 16, padding: 12, marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
                  <img src={`/api/important-info/${info.id}/signature-image/${signature.id}`} alt="Signature" style={{ width: 84, height: 34, objectFit: "contain", background: "#fffefb", borderRadius: 8, border: `1px solid ${P.line}` }} />
                  <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.4 }}>
                    {t("importantInfo.signedByYouOnTemplate", "Lu et signé par toi")}<br />{t("importantInfo.onDatePrefix", { date: new Date(signature.signedAt).toLocaleString(INTL_LOCALE_BY_LANG[lang] || "fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) }, `le ${new Date(signature.signedAt).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}`)}
                  </span>
                </div>
              )}
              {needsSig && (
                <>
                  <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 7 }}>
                    {t("importantInfo.signatureOfTemplate", { name: user?.firstname || "Toi" }, `Signature de ${user?.firstname || "Toi"} — certifie ta lecture en signant`)}
                  </span>
                  <div style={{ position: "relative", borderRadius: 16, background: "#fffefb", border: `1.5px dashed ${infoHasSignature ? A.green : P.line}`, marginBottom: 10, overflow: "hidden", touchAction: "none" }}>
                    <canvas ref={infoSigCanvasRef} width={520} height={180} style={{ width: "100%", height: 150, display: "block", touchAction: "none", cursor: "crosshair" }}
                      onPointerDown={infoSigStart} onPointerMove={infoSigMove} onPointerUp={infoSigEnd} onPointerLeave={infoSigEnd}
                      onTouchStart={infoSigStart} onTouchMove={infoSigMove} onTouchEnd={infoSigEnd} />
                    {!infoHasSignature && <span style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, pointerEvents: "none" }}>{t("importantInfo.signHereText", "✍️ Signe ici")}</span>}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <Pressable onClick={clearInfoSignature} style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, background: P.soft, borderRadius: 99, padding: "8px 14px" }}>{t("importantInfo.signClearBtn", "Effacer")}</Pressable>
                    {infoHasSignature && <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: "#15803d", fontWeight: 700 }}>{t("importantInfo.signatureTracedText", "✓ Signature tracée")}</span>}
                  </div>
                  <Pressable onClick={() => markInfoDone(info.id, true)} style={{ width: "100%", ...btnSolid(A.red), opacity: infoHasSignature ? 1 : .5 }}>{t("importantInfo.readAndCertifyBtn", "J'ai lu et je certifie")}</Pressable>
                </>
              )}
              {needsRead && (
                <Pressable onClick={() => markInfoDone(info.id, false)} style={{ width: "100%", ...btnSolid(A.blue) }}>{t("importantInfo.markAsReadBtnFull", "✓ Marquer comme lu")}</Pressable>
              )}
            </>
          );
        })()}

        {screen === "infos" && infoScreen === "manage" && (() => {
          const scopedManageList = infoManageList.filter((i) => (i.category || "general") === infoCategoryFilter);
          return (
          <div className="nx-screen-enter">
            {/* Retour utilisateur explicite (2026-10-14) : "le rundschreiben n'est pas créé
                par un GR mais directement à la Niederlassung" — malentendu de conception
                corrigé, la création n'a jamais sa place côté app pour cette catégorie (pas
                un rôle manquant, juste un flux qui n'existe pas encore). Bouton "+" retiré
                uniquement pour rundschreiben ; la liste des publications/suivi de réception
                juste en dessous reste inchangée. */}
            {infoCategoryFilter === "rundschreiben" ? (
              <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5, margin: "0 2px 14px", fontStyle: "italic" }}>
                {t("rundschreiben.manage.creationUnavailable", "Les Rundschreiben proviennent directement de la Niederlassung — la création depuis l'app n'est pas encore disponible.")}
              </p>
            ) : (
              <Pressable onClick={() => { setInfoForm({ ...emptyInfoForm, category: infoCategoryFilter }); setInfoEditingId(null); setInfoEditingAttachments([]); setInfoScreen("editor"); }} style={{ width: "100%", ...btnSolid(A.red), marginBottom: 14 }}>
                ＋ {t("importantInfo.editor.title", "Nouvelle information")}
              </Pressable>
            )}
            {scopedManageList.length === 0 && (
              <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("importantInfo.manage.empty.all", "Aucune publication pour l'instant.")}</p>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
            {scopedManageList.map((i, iIdx) => {
              const lm = levelMeta[i.importanceLevel];
              return (
                <Pressable key={i.id} feedback className="nx-list-item-in" onClick={() => openManageDetail(i.id)} style={{ ...card, ...tile, padding: 13, marginBottom: isWide ? 0 : 8, animationDelay: `${Math.min(iIdx, 14) * 28}ms` }}>
                  <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>{i.importanceLevel === "urgent" ? "🚨" : i.importanceLevel === "important" ? "❗" : i.importanceLevel === "attention" ? "⚠️" : "📌"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{i.title}</strong>
                    <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                      {lm.label} · {fmtFR((i.createdAt || "").slice(0, 10))}
                      {i.recipientsSummary && ` · ${t("importantInfo.recipientsCompletedInlineTemplate", { done: i.recipientsSummary.done, total: i.recipientsSummary.total }, `${i.recipientsSummary.done}/${i.recipientsSummary.total} terminé`)}`}
                    </div>
                  </div>
                  <span style={{
                    fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "3px 8px", whiteSpace: "nowrap",
                    color: i.status === "draft" ? P.muted : "#fff",
                    background: i.status === "draft" ? P.soft : i.status === "published" ? A.green : i.status === "cancelled" ? A.red : P.muted,
                  }}>{{ draft: t("importantInfo.manage.status.draft", "Brouillon"), published: t("importantInfo.manage.status.published", "Publiée"), cancelled: t("importantInfo.manage.status.cancelled", "Annulée"), archived: t("importantInfo.manage.status.archived", "Archivée") }[i.status]}</span>
                </Pressable>
              );
            })}
            </div>
          </div>
          );
        })()}

        {screen === "infos" && infoScreen === "manageDetail" && (() => {
          if (!infoManageDetail) return <p style={{ color: P.muted, fontSize: `calc(13px * var(--nx-font-scale, 1))`, textAlign: "center", marginTop: 40 }}>{t("krankmeldung.loading", "Chargement...")}</p>;
          const { info, version } = infoManageDetail;
          const lm = levelMeta[info.importanceLevel];
          const RECIPIENT_LABEL = {
            not_opened: t("importantInfo.recipientStatusF.notOpened", "Non ouverte"), opened: t("importantInfo.recipientStatusF.opened", "Ouverte"),
            read: t("importantInfo.recipientStatusF.read", "Lue"), certified: t("importantInfo.recipientStatusF.certified", "Certifiée"), signed: t("importantInfo.recipientStatusF.signed", "Signée"),
          };
          const RECIPIENT_COLOR = { not_opened: P.muted, opened: A.orange, read: A.blue, certified: A.purple, signed: A.green };
          const rows = infoManageRecipients?.rows || [];
          const summary = infoManageRecipients?.summary || { total: 0, done: 0 };
          const pct = summary.total ? Math.round((summary.done / summary.total) * 100) : 0;
          return (
            <>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 14 }}>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: lm.color, borderRadius: 99, padding: "5px 11px" }}>{lm.label}</span>
                {info.type === "mandatory" && <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: A.red, borderRadius: 99, padding: "5px 11px" }}>{t("importantInfo.pill.mandatory", "Obligatoire")}</span>}
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, background: P.soft, borderRadius: 99, padding: "5px 11px" }}>{fmtFR((info.createdAt || "").slice(0, 10))}</span>
              </div>
              <h2 style={{ margin: "0 0 8px", fontSize: `calc(18px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em" }}>{info.title}</h2>
              {info.category === "rundschreiben" && (info.rundschreibenNr || info.rundschreibenWeek) && (
                <p style={{ margin: "-4px 0 10px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                  {[info.rundschreibenNr ? `N° ${info.rundschreibenNr}` : "", info.rundschreibenWeek].filter(Boolean).join(" · ")}
                </p>
              )}
              {/* Onglet caché (retour utilisateur explicite 2026-10-14) — le suivi de
                  réception n'apparaît jamais par défaut, même pour un GR autorisé ; bascule
                  explicite requise. Uniquement Rundschreiben (Infos importantes générales
                  gardent leur "Suivi des destinataires" affiché directement, inchangé). */}
              {info.category === "rundschreiben" && info.status !== "draft" && (
                <div style={{ display: "flex", gap: 6, padding: 4, background: P.soft, border: `1px solid ${P.line}`, borderRadius: 14, marginBottom: 14 }}>
                  <Pressable onClick={() => setRundschreibenDetailTab("content")} style={{ flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 11, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: rundschreibenDetailTab === "content" ? P.ink : "none", color: rundschreibenDetailTab === "content" ? P.card : P.muted }}>{t("rundschreiben.manage.tabContent", "Contenu")}</Pressable>
                  <Pressable onClick={() => setRundschreibenDetailTab("tracking")} style={{ flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 11, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: rundschreibenDetailTab === "tracking" ? P.ink : "none", color: rundschreibenDetailTab === "tracking" ? P.card : P.muted }}>👁️ {t("rundschreiben.manage.tabTracking", "Suivi de réception")}</Pressable>
                </div>
              )}
              {(info.category !== "rundschreiben" || rundschreibenDetailTab === "content") && (
                <>
                  <p style={{ margin: "0 0 12px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, lineHeight: 1.55, color: P.ink, whiteSpace: "pre-wrap" }}>{version?.content}</p>
                  {(version?.attachments || []).map((a) => (
                    <Pressable key={a.id} onClick={() => window.open(`/api/important-info/${info.id}/attachment/${a.id}`, "_blank")} style={{
                      display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box", padding: 13, borderRadius: 16, marginBottom: 14, background: P.soft,
                    }}>
                      <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>📎</span>
                      <span style={{ flex: 1, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.fileName}</span>
                      <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>›</span>
                    </Pressable>
                  ))}
                </>
              )}

              {info.status !== "draft" && info.category !== "rundschreiben" && (
                <section style={{ ...card, borderRadius: 18, padding: 15, marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 9 }}>
                    <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{t("importantInfo.manage.recipientsTitle", "Suivi des destinataires")}</strong>
                    <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: A.green }}>{t("importantInfo.manage.recipientsSummary", { done: summary.done, total: summary.total }, `${summary.done} / ${summary.total} ont terminé`)}</strong>
                  </div>
                  <div style={{ height: 7, borderRadius: 99, background: P.soft, overflow: "hidden", marginBottom: 13 }}>
                    <div style={{ width: `${pct}%`, height: "100%", borderRadius: 99, background: A.green, transition: "width .5s ease" }} />
                  </div>
                  {rows.map((r) => (
                    <div key={r.userId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: `1px solid ${P.line}` }}>
                      <span style={{ width: 26, height: 26, borderRadius: 99, background: EMP_COLORS[r.userName] || P.muted, color: "#fff", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, display: "grid", placeItems: "center", flex: "0 0 auto" }}>{r.userName.charAt(0)}</span>
                      <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, flex: 1 }}>{r.userName}{r.isAuthor ? t("importantInfo.authorSuffix", " · Auteur") : ""}</strong>
                      <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{(r.signedAt || r.readAt) ? fmtFR((r.signedAt || r.readAt).slice(0, 10)) : "—"}</span>
                      <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: RECIPIENT_COLOR[r.status], borderRadius: 99, padding: "3px 9px" }}>{RECIPIENT_LABEL[r.status]}</span>
                    </div>
                  ))}
                </section>
              )}

              {/* Accusé de réception par FILIALE (2026-08-31, retour utilisateur explicite) —
                  dérivé, jamais un nouveau champ : la première signature venant de FL/FLA/
                  Stellvertreter(in) pour une filiale donnée suffit à la marquer "reçue", sans
                  vérification de hiérarchie/présence (voir la discussion — volontairement pas
                  de rollup complexe). Uniquement pour Rundschreiben, jamais Infos importantes
                  générales (pas demandé pour celles-ci). */}
              {info.category === "rundschreiben" && info.status !== "draft" && rundschreibenDetailTab === "tracking" && (() => {
                const RS_ACK_ROLES = ["FL", "FLA", "Stellvertreter", "Stellvertreterin"];
                const byStore = {};
                rows.forEach((r) => {
                  const sid = r.storeId || "—";
                  if (!byStore[sid]) byStore[sid] = { storeId: sid, ackRow: null, members: [] };
                  byStore[sid].members.push(r);
                  if (!byStore[sid].ackRow && r.status === "signed" && RS_ACK_ROLES.includes(r.userRole)) byStore[sid].ackRow = r;
                });
                const storeList = Object.values(byStore).sort((a, b) => a.storeId.localeCompare(b.storeId));
                const ackedCount = storeList.filter((g) => g.ackRow).length;
                return (
                  <section style={{ ...card, borderRadius: 18, padding: 15, marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 9 }}>
                      <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{t("rundschreiben.manage.storeAckTitle", "Réception par filiale")}</strong>
                      <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: A.green }}>{t("rundschreiben.manage.storeAckSummaryTemplate", { done: ackedCount, total: storeList.length }, `${ackedCount} / ${storeList.length} filiales`)}</strong>
                    </div>
                    {/* Chaque filiale dépliable individuellement (retour utilisateur explicite
                        2026-10-14 : "pas sur la page avec une centaine de travailleurs") — tape
                        une filiale pour voir SES employés, jamais les ~100 du réseau d'un coup. */}
                    {storeList.map((g) => {
                      const expanded = rundschreibenExpandedStoreId === g.storeId;
                      return (
                        <div key={g.storeId} style={{ borderTop: `1px solid ${P.line}` }}>
                          <Pressable onClick={() => setRundschreibenExpandedStoreId(expanded ? null : g.storeId)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", width: "100%" }}>
                            <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, flex: 1, textAlign: "left" }}>{storeLabelFor(g.storeId)}</strong>
                            {g.ackRow ? (
                              <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: A.green, fontWeight: 700 }}>✓ {g.ackRow.userName}</span>
                            ) : (
                              <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, background: P.soft, borderRadius: 99, padding: "3px 9px" }}>{t("rundschreiben.manage.storeAckPending", "En attente")}</span>
                            )}
                            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, transform: expanded ? "rotate(90deg)" : "none", transition: "transform .2s ease" }}>›</span>
                          </Pressable>
                          {expanded && (
                            <div style={{ padding: "0 0 10px 4px" }}>
                              {g.members.map((r) => (
                                <div key={r.userId} style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 0" }}>
                                  <span style={{ width: 22, height: 22, borderRadius: 99, background: EMP_COLORS[r.userName] || P.muted, color: "#fff", fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, display: "grid", placeItems: "center", flex: "0 0 auto" }}>{r.userName.charAt(0)}</span>
                                  <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, flex: 1 }}>{r.userName} <span style={{ color: P.muted, fontWeight: 600 }}>· {r.userRole}</span></span>
                                  <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: RECIPIENT_COLOR[r.status], borderRadius: 99, padding: "2px 8px" }}>{RECIPIENT_LABEL[r.status]}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {/* Échappatoire volontaire pour la liste nominative complète — jamais
                        affichée par défaut, repliée derrière ce lien discret. */}
                    <Pressable onClick={() => setRundschreibenShowFullList((v) => !v)} style={{ width: "100%", textAlign: "center", padding: "10px 0 2px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                      {rundschreibenShowFullList ? t("rundschreiben.manage.hideFullListBtn", "Masquer la liste complète") : t("rundschreiben.manage.showFullListBtnTemplate", { n: rows.length }, `Voir la liste complète (${rows.length})`)}
                    </Pressable>
                    {rundschreibenShowFullList && (
                      <div style={{ marginTop: 6 }}>
                        {rows.map((r) => (
                          <div key={r.userId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: `1px solid ${P.line}` }}>
                            <span style={{ width: 26, height: 26, borderRadius: 99, background: EMP_COLORS[r.userName] || P.muted, color: "#fff", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, display: "grid", placeItems: "center", flex: "0 0 auto" }}>{r.userName.charAt(0)}</span>
                            <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, flex: 1 }}>{r.userName} <span style={{ color: P.muted, fontWeight: 600 }}>· {storeLabelFor(r.storeId)}</span></strong>
                            <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{(r.signedAt || r.readAt) ? fmtFR((r.signedAt || r.readAt).slice(0, 10)) : "—"}</span>
                            <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: RECIPIENT_COLOR[r.status], borderRadius: 99, padding: "3px 9px" }}>{RECIPIENT_LABEL[r.status]}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                );
              })()}

              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                {info.status === "draft" && (
                  <>
                    <Pressable onClick={() => { setInfoForm({ title: info.title, summary: info.summary, content: version?.content || "", level: info.importanceLevel, mandatory: info.type === "mandatory", expiresAt: info.expiresAt || "", targetBranches: info.targetBranches || [], category: info.category || "general", rundschreibenNr: info.rundschreibenNr || "", rundschreibenWeek: info.rundschreibenWeek || "" }); setInfoEditingId(info.id); setInfoEditingAttachments(version?.attachments || []); setInfoScreen("editor"); }} style={{
                      width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                    }}>{t("common.editAria", "Modifier")}</Pressable>
                    <Pressable onClick={() => publishDraftInfo(info.id)} style={{ width: "100%", ...btnSolid(A.green) }}>📢 {t("importantInfo.manage.actionPublish", "Publier")}</Pressable>
                  </>
                )}
                {info.status === "published" && info.type === "mandatory" && !rows.some((r) => r.isAuthor && r.status === "signed") && (
                  <Pressable onClick={() => openInfoDetail(info.id)} style={{ width: "100%", ...btnSolid(A.purple) }}>
                    {t("importantInfo.signNowBtn", "✍️ Signer maintenant")}
                  </Pressable>
                )}
                {info.status === "published" && info.category === "rundschreiben" && (
                  <Pressable onClick={() => shareInfoViaMessage(info, version)} style={{ width: "100%", ...btnSolid(A.navy) }}>
                    📤 {t("rundschreiben.manage.shareViaMessageBtn", "Partager via message")}
                  </Pressable>
                )}
                {info.status === "published" && info.category === "rundschreiben" && (
                  <Pressable onClick={() => openMissionFromRundschreiben(info)} style={{ width: "100%", ...btnSolid(A.purple) }}>
                    🎯 {t("rundschreiben.manage.createMissionBtn", "Créer une mission liée")}
                  </Pressable>
                )}
                {info.status === "published" && (
                  <Pressable onClick={exportInfoPrint} style={{
                    width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                  }}>{t("importantInfo.printContentSignaturesBtn", "🖨️ Imprimer (contenu + signatures)")}</Pressable>
                )}
                {info.status === "published" && (
                  <Pressable onClick={() => cancelInfoManage(info.id)} style={{
                    width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: "rgba(239,68,68,.12)", color: A.red,
                  }}>{t("importantInfo.manage.actionCancel", "Annuler")}</Pressable>
                )}
                {(info.status === "draft" || info.status === "published" || info.status === "cancelled") && (
                  <Pressable onClick={() => archiveInfoManage(info.id)} style={{
                    width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                  }}>📦 {t("importantInfo.manage.actionArchive", "Archiver")}</Pressable>
                )}
                <Pressable onClick={() => deleteInfoManage(info.id)} style={{
                  width: "100%", textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: A.red,
                }}>{t("lieferschein.detail.purgeBtn", "Supprimer définitivement")}</Pressable>
              </div>
            </>
          );
        })()}

        {screen === "infos" && infoScreen === "editor" && (
          <>
            {/* Jamais de titre/résumé/contenu à RÉDIGER pour un Rundschreiben (retour
                utilisateur explicite, 2026-09-12 : "il ne crée jamais le contenu du
                Rundschreiben" — le vrai contenu, c'est le PDF reçu de la Niederlassung, joint
                juste en dessous ; un GR ne fait que le relayer, jamais l'écrire). title/
                content restent needed côté serveur (POST /api/important-info) — dérivés du
                N°/de la semaine dans publishInfo, jamais tapés ici. */}
            {infoForm.category !== "rundschreiben" && (
              <>
                <Field label={t("importantInfo.editor.fieldTitle", "Titre")}>
                  <input style={inputStyle} placeholder={t("importantInfo.titlePlaceholderEx", "Ex. Nouvelle procédure caisse")} value={infoForm.title} onChange={(e) => setInfoForm({ ...infoForm, title: e.target.value })} />
                </Field>
                <Field label={t("importantInfo.editor.fieldSummary", "Résumé (facultatif)")}>
                  <input style={inputStyle} placeholder={t("importantInfo.summaryPlaceholder", "Une phrase d'accroche")} value={infoForm.summary} onChange={(e) => setInfoForm({ ...infoForm, summary: e.target.value })} />
                </Field>
                <Field label={t("importantInfo.editor.fieldContent", "Contenu complet")}>
                  <textarea style={{ ...inputStyle, minHeight: 110, resize: "vertical", fontFamily: `var(--nx-font, ${FONT})` }} placeholder={t("importantInfo.contentPlaceholder", "Détaille l'information...")} value={infoForm.content} onChange={(e) => setInfoForm({ ...infoForm, content: e.target.value })} />
                </Field>
              </>
            )}
            {/* Nr./KW — étiquettes facultatives, jamais utilisées pour calculer une échéance
                (vrai Rundschreiben examiné en session : formats de date trop hétérogènes,
                toujours saisies à la main). Le PDF lui-même se joint comme pièce jointe de
                version, même mécanisme qu'Infos importantes (voir attachInfoVersionFile). */}
            {infoForm.category === "rundschreiben" && (
              <div style={{ display: "flex", gap: 12, marginBottom: 4 }}>
                <div style={{ flex: 1 }}>
                  <Field label={t("rundschreiben.editor.fieldNr", "N° (ex. 33/2026)")}>
                    <input style={inputStyle} placeholder="33/2026" value={infoForm.rundschreibenNr} onChange={(e) => setInfoForm({ ...infoForm, rundschreibenNr: e.target.value })} />
                  </Field>
                </div>
                <div style={{ flex: 1 }}>
                  <Field label={t("rundschreiben.editor.fieldWeek", "Semaine (ex. KW 34)")}>
                    <input style={inputStyle} placeholder="KW 34" value={infoForm.rundschreibenWeek} onChange={(e) => setInfoForm({ ...infoForm, rundschreibenWeek: e.target.value })} />
                  </Field>
                </div>
              </div>
            )}
            {/* PDF joint — réservé aux brouillons (voir POST /:id/attachment) : un premier
                enregistrement en brouillon est nécessaire avant de pouvoir joindre le
                fichier (infoEditingId doit exister). */}
            {infoForm.category === "rundschreiben" && (
              <div style={{ marginBottom: 16 }}>
                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 6 }}>{t("rundschreiben.editor.fieldPdf", "Document PDF")}</span>
                {infoEditingAttachments.map((a) => (
                  <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 12, background: P.soft, marginBottom: 6 }}>
                    <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>📎</span>
                    <span style={{ flex: 1, fontSize: `calc(12px * var(--nx-font-scale, 1))`, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.fileName}</span>
                  </div>
                ))}
                {infoEditingId ? (
                  <>
                    <input ref={infoAttachmentInputRef} type="file" accept="application/pdf" style={{ display: "none" }}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadInfoAttachment(f); e.target.value = ""; }} />
                    <Pressable disabled={infoAttachmentUploading} onClick={() => infoAttachmentInputRef.current?.click()} style={{
                      width: "100%", textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                      background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink, opacity: infoAttachmentUploading ? .6 : 1,
                    }}>{infoAttachmentUploading ? t("common.saving", "Enregistrement…") : `📎 ${t("rundschreiben.editor.attachPdfBtn", "Joindre le PDF")}`}</Pressable>
                  </>
                ) : (
                  <p style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, margin: 0 }}>{t("rundschreiben.editor.saveDraftFirstHint", "Enregistre d'abord le brouillon pour pouvoir joindre le PDF.")}</p>
                )}
              </div>
            )}
            {infoMultiTarget && (
              <>
                <label style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>
                  {t("importantInfo.targetBranchesCountTemplate", { n: infoForm.targetBranches.length }, `Filiales destinataires (${infoForm.targetBranches.length})`)}
                </label>
                <div className="nx-chips" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 8, paddingBottom: 2 }}>
                  <Pressable onClick={() => setInfoForm((f) => ({ ...f, targetBranches: f.targetBranches.length === infoScope.allowedStores.length ? [] : infoScope.allowedStores.slice() }))} style={{
                    flex: "0 0 auto", padding: "7px 12px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11px * var(--nx-font-scale, 1))`, background: P.soft, color: P.muted,
                  }}>{infoForm.targetBranches.length === infoScope.allowedStores.length ? t("importantInfo.deselectAllBtn", "Tout désélectionner") : t("importantInfo.selectAllBtn", "Tout sélectionner")}</Pressable>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                  {infoScope.allowedStores.map((sid) => {
                    const on = infoForm.targetBranches.includes(sid);
                    return (
                      <Pressable key={sid} onClick={() => setInfoForm((f) => ({ ...f, targetBranches: on ? f.targetBranches.filter((x) => x !== sid) : [...f.targetBranches, sid] }))} style={{
                        padding: "7px 12px", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                        background: on ? A.red : P.soft, color: on ? "#fff" : P.muted,
                      }}>{on ? "✓ " : ""}{storeLabelFor(sid)}</Pressable>
                    );
                  })}
                </div>
                <p style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "-8px 2px 16px", lineHeight: 1.4 }}>
                  {t("importantInfo.broadcastDisclaimerText", 'Diffusée à tous les travailleurs des filiales choisies — tu n\'en fais pas partie, tu peux la signer toi-même en plus si tu veux depuis "Mes publications".')}
                </p>
              </>
            )}
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 6 }}>{t("importantInfo.editor.fieldLevel", "Niveau d'importance")}</span>
            <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
              {Object.entries(levelMeta).map(([id, lm]) => (
                <Pressable key={id} onClick={() => setInfoForm({ ...infoForm, level: id })} style={{
                  flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: infoForm.level === id ? lm.color : P.soft, color: infoForm.level === id ? "#fff" : P.ink,
                  border: `1.5px solid ${infoForm.level === id ? lm.color : P.line}`,
                }}>{lm.label}</Pressable>
              ))}
            </div>
            {infoForm.category === "rundschreiben" ? (
              <div style={{ display: "flex", gap: 11, alignItems: "flex-start", width: "100%", boxSizing: "border-box", padding: 13, borderRadius: 16, marginBottom: 15, background: "rgba(239,68,68,.1)", border: `1.5px solid ${A.red}` }}>
                <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>☑️</span>
                <div>
                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: A.red, display: "block" }}>{t("importantInfo.mandatoryReadTitle", "Lecture certifiée obligatoire")}</strong>
                  <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.4 }}>{t("rundschreiben.editor.alwaysMandatoryDesc", "Toujours activée pour un Rundschreiben — jamais désactivable.")}</span>
                </div>
              </div>
            ) : (
              <Pressable onClick={() => setInfoForm({ ...infoForm, mandatory: !infoForm.mandatory })} style={{
                display: "flex", gap: 11, alignItems: "flex-start", width: "100%", boxSizing: "border-box", padding: 13, borderRadius: 16, marginBottom: 15,
                background: infoForm.mandatory ? "rgba(239,68,68,.1)" : P.soft, border: `1.5px solid ${infoForm.mandatory ? A.red : P.line}`,
              }}>
                <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>{infoForm.mandatory ? "☑️" : "⬜️"}</span>
                <div>
                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: infoForm.mandatory ? A.red : P.ink, display: "block" }}>{t("importantInfo.mandatoryReadTitle", "Lecture certifiée obligatoire")}</strong>
                  <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.4 }}>{t("importantInfo.mandatoryReadDescShort", "Bloque le passage à « Terminée » pour chaque destinataire jusqu'à lecture, certification et signature.")}</span>
                </div>
              </Pressable>
            )}
            <Field label={t("importantInfo.editor.fieldExpires", "Date d'expiration (facultative)")}>
              <input style={dateTimeInputStyle} type="date" min={todayISO()} value={infoForm.expiresAt} onChange={(e) => setInfoForm({ ...infoForm, expiresAt: e.target.value })} />
            </Field>
            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              <Pressable onClick={() => publishInfo(true)} style={{
                flex: 1, textAlign: "center", padding: "14px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
                background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
              }}>{t("importantInfo.editor.saveDraftBtn", "Enregistrer le brouillon")}</Pressable>
              <Pressable onClick={() => publishInfo(false)} style={{ flex: 1, ...btnSolid(infoForm.category === "rundschreiben" ? A.navy : A.red) }}>{t("importantInfo.editor.publishBtn", "Publier")}</Pressable>
            </div>
          </>
        )}

        {/* ================= REPORTING ================= */}
        {screen === "reporting" && repScreen === "hub" && (
          <>
            <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 19 }}>
              <span style={eyebrow(A.green)}>{t("reporting.hub.autoEyebrow", "Automatique")}</span>
              <h2 style={{ margin: "5px 0 6px", fontSize: `calc(18px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em" }}>{t("reporting.hub.title", "Les rapports se remplissent automatiquement")}</h2>
              <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.45 }}>
                {t("reporting.hub.subtitle", "Tu saisis les pertes chaque jour, Nexora calcule les totaux. Choisis une section ci-dessous.")}
              </p>
            </section>

            {[
              { s: "daily", bg: A.green, emoji: "📝", tag: t("reporting.hub.dailyTagFull", "Chaque jour · à saisir"), t: t("reporting.hub.dailyTitle", "Saisie quotidienne"), d: t("reporting.hub.dailyDescShort", "Pertes du jour : Prebake, Brot, autres Abschriften — et un onglet Semaine pour le récap.") },
              { s: "stunden", bg: A.frost, emoji: "⏱️", tag: t("reporting.hub.stundenEyebrow", "Chaque mois"), t: "Stundenleistung", d: t("reporting.hub.stundenDescShort", "Umsatz ÷ heures travaillées, Plan importé vs Ist.") },
              { s: "month", bg: A.purple, emoji: "🔒", tag: t("reporting.hub.monthEyebrow", "Clôture"), t: t("reporting.month.kicker", "Clôture mensuelle"), d: t("reporting.hub.monthDesc", "Semaines cumulées du mois, krank, calculs finaux.") },
              { s: "history", bg: A.navy, emoji: "📚", tag: t("reporting.hub.historyEyebrow", "Consultation"), t: t("reporting.hub.historyTitle", "Historique"), d: t("reporting.hub.historyDescShort", "Vue hebdomadaire ou mensuelle (Monatsmeldung).") },
            ].map((x) => (
              <Pressable key={x.s} onClick={() => setRepScreen(x.s)} style={{ ...colorCard(x.bg), ...tile, padding: 15 }}>
                <div style={onColorChip}><EmojiIcon e={x.emoji} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={onColorTag}>{x.tag}</span>
                  <h2 style={{ margin: "3px 0 3px", fontSize: `calc(15.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{x.t}</h2>
                  <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, ...onColorSub }}>{x.d}</p>
                </div>
                <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(18px * var(--nx-font-scale, 1))`, fontWeight: 300 }}>›</span>
              </Pressable>
            ))}

            {isManager(user) && (
              <>
                {/* "Responsable" (Rapports KW réseau + PLU Quiz) retirée d'ici sur demande
                    explicite 2026-08-02 — sera reconstruite dans la vue GR (Pilotage
                    réseau), avec les vrais rapports réseau, pas ici dans Reporting. */}
                <Pressable onClick={() => { setCatalogTab("all"); setCatalogStatusFilter("active"); setRepScreen("catalog"); }} style={{ ...colorCard(A.brown), ...tile, padding: 15 }}>
                  <div style={onColorChip}><EmojiIcon e="📦" /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={onColorTag}>{t("reporting.hub.catalogEyebrow", "Administration")}</span>
                    <h2 style={{ margin: "3px 0 3px", fontSize: `calc(15.5px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{t("reporting.hub.catalogTitle", "Catalogue")}</h2>
                    <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("reporting.hub.catalogDescShort", "Tous, Prebake, Brot, suspendus, archivés.")}</p>
                  </div>
                  <span style={{ color: "rgba(255,255,255,.8)", fontSize: `calc(18px * var(--nx-font-scale, 1))`, fontWeight: 300 }}>›</span>
                </Pressable>
              </>
            )}
          </>
        )}

        {screen === "reporting" && repScreen === "daily" && (() => {
          const dayIdx = dailyDayIdx ?? TODAY_IDX;
          const locked = isFamilyDayLocked(dailyFamily, dayIdx);
          const familyLabel = { prebake: "Prebake", brot: "Brot", manual_waste: t("reporting.daily.familyManualLabel", "Autres Abschriften") }[dailyFamily];
          // Vrai effet carrousel (retour utilisateur explicite 2026-08-21 : "le comportement
          // qu'on avait sur ces Dashboard [l'ancien ring de tableaux de bord]... mais en plus
          // premium... je ne vois rien de tel") — la V1 changeait le jour silencieusement au
          // relâchement, sans jamais RIEN bouger à l'écran pendant le geste, d'où l'impression
          // que rien n'était implémenté malgré un code fonctionnel. Ici le contenu suit le
          // doigt en direct (manipulation DOM via ref, jamais de setState par pixel — un
          // setState à chaque pointermove jetterait des dizaines de re-renders/seconde),
          // résistance élastique aux bords (première/dernière filiale du jour navigable),
          // et au relâchement : glissement complet hors champ puis entrée depuis le bord
          // opposé une fois le nouveau jour appliqué — jamais un vrai triple-panneau (le
          // contenu diffère trop par jour pour pré-rendre 3 jours à la fois), mais l'illusion
          // de pagination est la même que l'ancien ring.
          const goToDailyDay = (nextIdx, opts = {}) => {
            if (nextIdx < 0 || nextIdx > TODAY_IDX || nextIdx === dayIdx) return;
            setDailyDayIdx(nextIdx);
            if (!opts.silent) hapticTap();
            setDailySwipeHint(`${weekLabelShort(nextIdx)} ${fmtFR(reportingWeekDates[nextIdx]).slice(0, 5)}`);
            clearTimeout(dailySwipeHintTimerRef.current);
            dailySwipeHintTimerRef.current = setTimeout(() => setDailySwipeHint(null), 550);
          };
          const DRAG_THRESHOLD = 70;
          const SLIDE_MS = 320;
          // Ressort (retour utilisateur explicite 2026-08-21 : "trop mécanique") — même
          // courbe que le retour tactile de Pressable/HoldButton dans toute l'app
          // (cubic-bezier(.34,1.4,.64,1), léger dépassement puis retour), jamais la courbe
          // plate d'avant.
          //
          // Bug réel corrigé (2026-08-21, retour utilisateur explicite après déploiement :
          // "encore plus gâté... pas du tout bon") — le premier essai ajoutait un
          // `scale(...)` sur ce même élément en plus de translateX. Ce conteneur mesure la
          // hauteur de TOUTE la liste d'articles (souvent >2500px, bien plus que l'écran) ;
          // `scale()` se centre par défaut sur le milieu de la boîle entière (transform-
          // origin: 50% 50%), donc réduire à .96 déplaçait le haut ET le bas du contenu
          // visible de plusieurs dizaines de pixels VERTICALEMENT pendant tout le
          // glissement horizontal — un vrai artefact de warp/saut, pas un effet "premium".
          // Seul le fondu (opacity, sans effet de mise en page) est sans danger ; le zoom
          // est purement retiré, jamais réintroduit avec un transform-origin "corrigé" sans
          // revalidation visuelle explicite.
          const SPRING = "cubic-bezier(.34,1.4,.64,1)";
          const onDailySwipeStart = (e) => {
            dailySwipeStartRef.current = { x: e.clientX, y: e.clientY };
            dailyDragActiveRef.current = false;
            dailyDirLockRef.current = null;
          };
          const onDailySwipeMove = (e) => {
            const start = dailySwipeStartRef.current;
            const el = dailyContentRef.current;
            if (!start || !el) return;
            // Cédé au scroll vertical natif pour ce doigt — plus rien à faire jusqu'au
            // prochain pointerdown.
            if (dailyDirLockRef.current === "y") return;
            const dx = e.clientX - start.x, dy = e.clientY - start.y;
            if (!dailyDragActiveRef.current) {
              const adx = Math.abs(dx), ady = Math.abs(dy);
              // Bug réel corrigé (2026-08-21, retour utilisateur explicite : "ne marche pas
              // à tous les coups, parfois faut essayer plusieurs fois") — l'ancienne
              // décision `dy > dx` se prenait sur UN SEUL échantillon dès 10px de mouvement
              // ET était irréversible (dailySwipeStartRef mis à null). Un vrai doigt ne
              // bouge jamais en ligne parfaitement droite : un tremblement initial très
              // légèrement diagonal (ex. dx:9 dy:11) suffisait à tuer tout le geste
              // définitivement, avant même qu'il ait commencé — obligeant à relever le
              // doigt et réessayer. Désormais : sous 12px dans les deux axes, on attend
              // encore (pas assez de signal) ; entre 12px et une dominance nette (ratio
              // 1.2), on continue d'attendre plutôt que de trancher sur un cas ambigu ; seul
              // un signal VRAIMENT majoritairement vertical cède définitivement au scroll
              // natif (dailyDirLockRef="y"). dailySwipeStartRef n'est plus jamais annulé
              // prématurément : tant que rien n'est tranché, chaque nouveau pointermove a sa
              // chance.
              if (adx < 12 && ady < 12) return;
              if (ady > adx * 1.2) { dailyDirLockRef.current = "y"; return; }
              if (adx <= ady * 1.2) return; // encore ambigu (proche diagonale) — repasser au prochain move
              dailyDirLockRef.current = "x";
              dailyDragActiveRef.current = true;
              el.style.transition = "none";
            }
            // Résistance élastique aux extrémités (jour 0 ou TODAY_IDX) — jamais un blocage
            // sec, juste un tiers du mouvement réel, comme un vrai scroll iOS en butée.
            const atStart = dayIdx === 0 && dx > 0;
            const atEnd = dayIdx === TODAY_IDX && dx < 0;
            const realDx = (atStart || atEnd) ? dx * .35 : dx;
            // Fondu proportionnel à la distance parcourue (jamais brutal, plafonné) —
            // AUCUN scale (voir commentaire sur SPRING ci-dessus, artefact de saut vertical
            // corrigé).
            const dist = Math.min(Math.abs(realDx) / 260, 1);
            el.style.transform = `translateX(${realDx}px)`;
            el.style.opacity = String(1 - dist * .2);
          };
          const onDailySwipeEnd = (e) => {
            const start = dailySwipeStartRef.current;
            const wasActive = dailyDragActiveRef.current;
            dailySwipeStartRef.current = null;
            dailyDragActiveRef.current = false;
            dailyDirLockRef.current = null;
            const el = dailyContentRef.current;
            if (!start || !el || !wasActive) return;
            const dx = e.clientX - start.x;
            el.style.transition = `transform ${SLIDE_MS}ms ${SPRING}, opacity ${SLIDE_MS}ms ease`;
            const dir = dx < 0 ? 1 : -1; // glisser vers la gauche => jour suivant
            const nextIdx = dayIdx + dir;
            if (Math.abs(dx) < DRAG_THRESHOLD || nextIdx < 0 || nextIdx > TODAY_IDX) {
              el.style.transform = "translateX(0px)";
              el.style.opacity = "1";
              return;
            }
            el.style.transform = `translateX(${dir * -100}%)`;
            el.style.opacity = "0";
            setTimeout(() => {
              goToDailyDay(nextIdx, { silent: true });
              hapticTap();
              el.style.transition = "none";
              el.style.transform = `translateX(${dir * 100}%)`;
              el.style.opacity = "0";
              requestAnimationFrame(() => {
                el.style.transition = `transform ${SLIDE_MS}ms ${SPRING}, opacity ${SLIDE_MS}ms ease`;
                el.style.transform = "translateX(0px)";
                el.style.opacity = "1";
              });
            }, SLIDE_MS);
          };
          const onDailySwipeCancel = () => {
            dailySwipeStartRef.current = null;
            dailyDirLockRef.current = null;
            if (dailyDragActiveRef.current && dailyContentRef.current) {
              dailyDragActiveRef.current = false;
              dailyContentRef.current.style.transition = `transform ${SLIDE_MS}ms ${SPRING}, opacity ${SLIDE_MS}ms ease`;
              dailyContentRef.current.style.transform = "translateX(0px)";
              dailyContentRef.current.style.opacity = "1";
            }
          };
          return (
            <>
              {/* Bascule Jour/Semaine (2026-09-20, maquette validée — voir
                  storeflow_wochenbericht_table_redesign_mockup) : remplace l'ancienne tuile
                  "Rapport hebdomadaire" du hub, retirée. Ne fait QUE changer repScreen —
                  "daily" et "weekly" restent deux écrans séparés (code déjà testé/déployé
                  chacun de son côté), jamais fusionnés en un seul bloc JSX. */}
              <div style={{ display: "flex", gap: 6, padding: 4, background: P.soft, border: `1px solid ${P.line}`, borderRadius: 14, marginBottom: 14 }}>
                <Pressable onClick={() => {}} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 11, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: P.ink, color: P.card }}>📝 {t("reporting.modeJourBtn", "Jour")}</Pressable>
                <Pressable onClick={() => setRepScreen("weekly")} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 11, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: "none", color: P.muted }}>📊 {t("reporting.modeSemaineBtn", "Semaine")}</Pressable>
              </div>

              <p style={{ margin: "0 2px 14px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{t("reporting.daily.introText", "Les valeurs alimentent automatiquement Abschriften KW.")}</p>

              {/* Navigation entre semaines — retour utilisateur explicite (2026-08-06) :
                  aucun moyen avant de rattraper un jour manqué la semaine passée. Même
                  patron que Plan de travail (planWeekOffset) — jamais au-delà de la
                  semaine réelle en cours (0), une saisie de pertes future n'a pas de sens. */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <Pressable feedback onClick={() => setReportingWeekOffset((o) => o - 1)} aria-label={t("planning.control.prevWeek", "Semaine précédente")} style={{
                  width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
                }}>‹</Pressable>
                <div style={{ flex: 1, textAlign: "center" }}>
                  <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`}}>KW {reportingWeekNumber}{reportingWeekOffset === 0 ? ` · ${t("reporting.weekly.currentWeekBtn", "Semaine en cours")}` : ""}</strong>
                </div>
                <Pressable feedback onClick={() => reportingWeekOffset < 0 && setReportingWeekOffset((o) => o + 1)} aria-label={t("planning.control.nextWeek", "Semaine suivante")} disabled={reportingWeekOffset >= 0} style={{
                  width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`, opacity: reportingWeekOffset >= 0 ? .4 : 1,
                }}>›</Pressable>
              </div>

              <div className="nx-chips" style={{ display: "flex", gap: 8, marginBottom: 10, overflowX: "auto", paddingBottom: 2 }}>
                {[{ id: "prebake", l: "Prebake" }, { id: "brot", l: "Brot" }, { id: "manual_waste", l: t("reporting.daily.familyManualLabel", "Autres Abschriften") }].map((m) => (
                  <Pressable key={m.id} onClick={() => { setDailyFamily(m.id); setDailyReorderActive(false); }} style={{
                    flex: "0 0 auto", textAlign: "center", padding: "10px 16px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                    background: dailyFamily === m.id ? A.green : P.soft, color: dailyFamily === m.id ? "#fff" : P.ink,
                    border: `1.5px solid ${dailyFamily === m.id ? A.green : P.line}`,
                  }}>{m.l}</Pressable>
                ))}
              </div>

              {/* Sélecteur de jour — rattrapage d'un jour manqué de la semaine en cours
                  (retour utilisateur explicite 2026-08-02 : "aucun sélecteur de date").
                  Jamais un jour futur, qui n'a par construction pas encore de réalité à
                  saisir (même règle que Stundenleistung, voir loadStundenleistung). Chevrons
                  ‹ › ajoutés (retour utilisateur explicite 2026-08-21, captures d'écran :
                  "rendre la liste défilante... pour naviguer entre les jours") — le
                  défilement tactile existait déjà (overflowX + touch-action:pan-x sur
                  .nx-chips, même patron que partout ailleurs dans l'app), mais rien ne
                  signalait qu'il y avait plus de jours à voir ; un pas explicite, comme le
                  stepper ‹ › de semaine juste au-dessus, est sans ambiguïté. */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14 }}>
                <Pressable onClick={() => goToDailyDay(dayIdx - 1)} aria-label={t("reporting.daily.prevDayAria", "Jour précédent")} style={{
                  flex: "0 0 auto", width: 28, height: 28, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center",
                  fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800, color: dayIdx > 0 ? P.ink : P.muted, opacity: dayIdx > 0 ? 1 : .35,
                }}>‹</Pressable>
                <div className="nx-chips" style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 2, flex: 1 }}>
                  {WEEK_DAY_LABELS.map((_, i) => {
                    const future = i > TODAY_IDX;
                    const on = dayIdx === i;
                    return (
                      <Pressable key={i} onClick={() => !future && goToDailyDay(i)} style={{
                        flex: "0 0 auto", textAlign: "center", padding: "7px 11px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11px * var(--nx-font-scale, 1))`,
                        background: on ? A.navy : P.soft, color: future ? P.muted : (on ? "#fff" : P.ink),
                        border: `1.5px solid ${on ? A.navy : P.line}`, opacity: future ? .45 : 1,
                      }}>{weekLabelShort(i)} {fmtFR(reportingWeekDates[i]).slice(0, 5)}</Pressable>
                    );
                  })}
                </div>
                <Pressable onClick={() => goToDailyDay(dayIdx + 1)} aria-label={t("reporting.daily.nextDayAria", "Jour suivant")} style={{
                  flex: "0 0 auto", width: 28, height: 28, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center",
                  fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800, color: dayIdx < TODAY_IDX ? P.ink : P.muted, opacity: dayIdx < TODAY_IDX ? 1 : .35,
                }}>›</Pressable>
              </div>

              {/* Zone glissable (retour utilisateur explicite 2026-08-21) — pointerdown/up
                  bruts, jamais preventDefault, donc le scroll vertical natif de la liste reste
                  intact ; seul un geste net et majoritairement horizontal (voir onDailySwipeEnd)
                  change de jour. position:relative pour ancrer l'aperçu ci-dessous.
                  Bug réel corrigé (2026-08-21, retour utilisateur : "ce n'est pas le
                  comportement... pas ce que tu as implanté" après un premier essai sur son
                  vrai téléphone) — touchAction par défaut ("auto") laissait iOS décider que
                  N'IMPORTE QUEL geste tactile ici, même franchement horizontal, pouvait être
                  capturé pour le scroll natif de la page (verticale) ; dès que ça arrivait,
                  iOS envoie pointercancel au lieu d'un vrai pointerup, donc onDailySwipeEnd
                  ne se déclenchait JAMAIS sur un vrai doigt (seul mon test Playwright, en
                  souris simulée, ne reproduisait pas ce piège — d'où l'impression que
                  "rien n'était implémenté"). touchAction:"pan-y" dit explicitement au
                  navigateur : le scroll vertical natif reste permis, mais un mouvement
                  horizontal m'appartient, jamais annulé par un geste de scroll concurrent. */}
              <div onPointerDown={onDailySwipeStart} onPointerMove={onDailySwipeMove} onPointerUp={onDailySwipeEnd} onPointerCancel={onDailySwipeCancel} style={{ position: "relative", overflowX: "hidden", touchAction: "pan-y" }}>
              <div ref={dailyContentRef}>
              {locked ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, borderRadius: 16, padding: 14, marginBottom: 14, background: "rgba(30,184,95,.09)", border: "1px solid rgba(30,184,95,.25)" }}>
                  <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>✓</span>
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: "#15803d", display: "block" }}>{t("reporting.daily.dayClosedTemplate", { family: familyLabel, day: dayIdx === TODAY_IDX ? t("reporting.daily.dayShort", "journée") : weekLabelShort(dayIdx) }, `${familyLabel} — ${dayIdx === TODAY_IDX ? "journée" : weekLabelShort(dayIdx)} clôturée`)}</strong>
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("reporting.daily.totalSentText", "Total envoyé dans Abschriften KW.")}</span>
                  </div>
                  <Pressable onClick={() => unlockDailyFamily(dailyFamily, dayIdx)} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "8px 12px" }}>{t("common.editAria", "Modifier")}</Pressable>
                </div>
              ) : (
                <div style={{ borderRadius: 16, padding: 13, marginBottom: 14, background: P.soft }}>
                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block" }}>{t("reporting.daily.fillThenSaveTemplate", { day: dayIdx === TODAY_IDX ? t("reporting.daily.theDay", "la journée") : t("reporting.daily.theDayTemplate", { day: weekLabelShort(dayIdx).toLowerCase() }, `le ${weekLabelShort(dayIdx).toLowerCase()}`) }, `Renseigne les quantités puis enregistre pour clôturer ${dayIdx === TODAY_IDX ? "la journée" : `le ${weekLabelShort(dayIdx).toLowerCase()}`}.`)}</strong>
                </div>
              )}

              {dailyFamily === "manual_waste" ? (
                <>
                  {MANUAL_CATEGORIES.map((cat) => {
                    const v = manualCellValue(cat.id, dayIdx);
                    return (
                      <Fragment key={cat.id}>
                        <div style={{ ...card, borderRadius: 18, padding: 14, marginBottom: 9 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                            <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{reportCategoryLabel(cat.id)}</strong>
                            <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: A.red }}>{fmt2(v.pu20 + v.entsorgt)}</strong>
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            {WASTE_TYPES.map((wt) => (
                              <div key={wt.id} style={{ flex: 1 }}>
                                <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", display: "block", marginBottom: 4 }}>{wasteTypeLabel(wt.id)}</span>
                                {/* type="text" + valeur brute lue directement dans weeklyGrid (pas v[wt.id],
                                    qui est déjà parsé par manualCellValue) — un input "number" rejette la
                                    virgule, et réafficher à chaque frappe une valeur déjà arrondie efface
                                    ce qui vient d'être tapé (retour utilisateur explicite : "0,x ne
                                    fonctionne pas"). Le parsing virgule→point n'a lieu qu'à la lecture
                                    (parseCommaAmount), jamais ici. */}
                                <input
                                  type="text" inputMode="decimal" placeholder="0,0" disabled={locked}
                                  value={weeklyGrid[cat.id]?.[dayIdx]?.[wt.id] ?? ""}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    setWeeklyGrid((g) => ({ ...g, [cat.id]: { ...g[cat.id], [dayIdx]: { ...g[cat.id]?.[dayIdx], [wt.id]: raw } } }));
                                  }}
                                  style={{ ...inputStyle, padding: "9px 10px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, opacity: locked ? .6 : 1 }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Retour utilisateur (2026-08-18) : les 2 lignes Frischbrot/Brot
                            doivent apparaître juste après Frischfleisch, pas en toute fin de
                            liste — insérées ici plutôt qu'après la boucle MANUAL_CATEGORIES. */}
                        {/* Retour utilisateur (2026-08-18) : les 2 cartes Frischbrot/Brot
                            fusionnées en une seule, même patron PÜ/20%|ENTSORGT que les
                            autres rayons — PÜ/20% reste éditable, ENTSORGT reste lecture
                            seule (repris de familyDayTotal("brot", dayIdx)/categoryDayValue,
                            jamais un second calcul, une seule source de vérité). */}
                        {cat.id === "frischfleisch" && (() => {
                          const brotCat = REPORT_CATEGORIES.find((c) => c.id === "frischbrot_brot");
                          const bv = categoryDayValue(brotCat, dayIdx);
                          return (
                            <div key="frischbrot_brot" style={{ ...card, borderRadius: 18, padding: 14, marginBottom: 9 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{reportCategoryLabel(brotCat.id)}</strong>
                                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: A.red }}>{fmt2(bv.pu20 + bv.entsorgt)}</strong>
                              </div>
                              <div style={{ display: "flex", gap: 8 }}>
                                <div style={{ flex: 1 }}>
                                  <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", display: "block", marginBottom: 4 }}>{wasteTypeLabel("pu20")}</span>
                                  <input
                                    type="text" inputMode="decimal" placeholder="0,0" disabled={locked}
                                    value={weeklyGrid.frischbrot_brot?.[dayIdx]?.pu20 ?? ""}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      setWeeklyGrid((g) => ({ ...g, frischbrot_brot: { ...g.frischbrot_brot, [dayIdx]: { ...g.frischbrot_brot?.[dayIdx], pu20: raw } } }));
                                    }}
                                    style={{ ...inputStyle, padding: "9px 10px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, opacity: locked ? .6 : 1 }}
                                  />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", display: "block", marginBottom: 4 }}>{wasteTypeLabel("entsorgt")}</span>
                                  <input type="number" disabled value={bv.entsorgt || 0} readOnly
                                    style={{ ...inputStyle, padding: "9px 10px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, opacity: .6, background: P.soft }} />
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </Fragment>
                    );
                  })}
                  {/* Retour utilisateur (2026-08-09) : Prebake doit aussi apparaître ici, pas
                      seulement dans Abschriften KW — mais c'est un rayon AUTOMATIQUE (source
                      auto_prebake dans REPORT_CATEGORIES), donc categoryDayValue reste la seule
                      source de vérité pour l'affichage (jamais manualCellValue, réservé aux 7
                      rayons réellement manuels ci-dessus). */}
                  {(() => {
                    const prebakeCat = REPORT_CATEGORIES.find((c) => c.id === "prebake");
                    const v = categoryDayValue(prebakeCat, dayIdx);
                    // Pas de champ PÜ/20% pour Prebake (retour utilisateur explicite 2026-09-24 :
                    // "elle n'existe pas de toutes les façons") — categoryDayValue force déjà
                    // pu20:0 pour ce rayon (voir son implémentation, source "auto_prebake"),
                    // seul entsorgt est une vraie donnée ici. Même règle déjà appliquée côté
                    // écran Wochenbericht (showPu, ligne ~33319) et export PDF hebdomadaire.
                    return (
                      <div style={{ ...card, borderRadius: 18, padding: 14, marginBottom: 9, opacity: .75 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                          <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{reportCategoryLabel(prebakeCat.id)}</strong>
                          <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: A.red }}>{fmt2(v.entsorgt)}</strong>
                        </div>
                        <div>
                          <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", display: "block", marginBottom: 4 }}>{wasteTypeLabel("entsorgt")}</span>
                          <input type="number" disabled value={v.entsorgt || 0} readOnly
                            style={{ ...inputStyle, padding: "9px 10px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, opacity: .6, background: P.soft }} />
                        </div>
                      </div>
                    );
                  })()}
                  {!locked && <Pressable onClick={() => saveDailyFamily("manual_waste", dayIdx)} style={{ width: "100%", ...btnSolid(A.green), marginTop: 6 }}>{t("reporting.saveEntry", "Enregistrer la saisie")}</Pressable>}
                </>
              ) : (
                <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 15 }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    <Pressable onClick={exportDailyReporting} style={{
                      flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                      background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                    }}>🖨️ {t("reporting.daily.exportBtn", "Exporter / Imprimer")}</Pressable>
                    <Pressable onClick={dailyReorderActive ? () => setDailyReorderActive(false) : startDailyReorder} style={{
                      flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                      background: dailyReorderActive ? P.soft : "rgba(30,184,95,.12)", border: `1.5px solid ${dailyReorderActive ? P.line : "transparent"}`, color: dailyReorderActive ? P.ink : A.green,
                    }}>{dailyReorderActive ? t("common.cancel", "Annuler") : `🔀 ${t("reporting.daily.reorderBtn", "Réorganiser")}`}</Pressable>
                  </div>

                  {dailyReorderActive ? (
                    <>
                      <p style={{ margin: "0 0 12px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                        {t("reporting.daily.reorderDesc", "Réorganise l'ordre d'affichage des articles pour qu'il corresponde à l'agencement réel du poste.")}
                      </p>
                      {dailyReorderList.map((a, i) => (
                        <div key={a.id} style={{ ...card, padding: 12, marginBottom: 7, display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, width: 20, textAlign: "center" }}>{i + 1}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{a.name}</strong>
                          </div>
                          <Pressable onClick={() => moveDailyReorderItem(i, -1)} aria-label={t("merch.reorder.upAria", "Monter")} style={{
                            width: 34, height: 34, borderRadius: 10, background: P.soft, border: `1.5px solid ${P.line}`, display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, opacity: i === 0 ? .35 : 1,
                          }}>↑</Pressable>
                          <Pressable onClick={() => moveDailyReorderItem(i, 1)} aria-label={t("merch.reorder.downAria", "Descendre")} style={{
                            width: 34, height: 34, borderRadius: 10, background: P.soft, border: `1.5px solid ${P.line}`, display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, opacity: i === dailyReorderList.length - 1 ? .35 : 1,
                          }}>↓</Pressable>
                        </div>
                      ))}
                      <Pressable onClick={saveDailyReorder} style={{ width: "100%", ...btnSolid(A.green), marginTop: 4 }}>{t("merch.reorder.saveBtn", "✓ Enregistrer l'ordre")}</Pressable>
                    </>
                  ) : (
                    <>
                      <p style={{ margin: "0 0 12px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                        {dailyFamily === "prebake"
                          ? t("reporting.daily.subtitlePrebake", "Seule la ligne entsorgt est saisie ici. La ligne PÜ/20% reste toujours à 0,00 € dans Abschriften KW.")
                          : t("reporting.daily.subtitleBrot", "Les pertes entsorgt se saisissent ici. La ligne PÜ/20% de Frischbrot/Brot se saisit directement dans Abschriften KW.")}
                      </p>
                      {catalogArticlesFor(dailyFamily === "prebake" ? "Prebake" : "Brot").length === 0 && <p style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 10px" }}>{t("reporting.daily.noActiveArticlesFamily", "Aucun article actif dans cette famille — vérifie le Catalogue.")}</p>}
                      {catalogArticlesFor(dailyFamily === "prebake" ? "Prebake" : "Brot").map((a) => {
                        const qty = dayQty(dailyFamily, dayIdx, a.id);
                        return (
                          <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderTop: `1px solid ${P.line}` }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{a.name}</strong>
                              <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("reporting.daily.perUnitTemplate", { price: a.price.toFixed(2) }, `${a.price.toFixed(2)} € / unité`)}</span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", borderRadius: 99, background: P.soft, border: `1.5px solid ${qty > 0 ? A.green : P.line}`, overflow: "hidden" }}>
                              <HoldButton disabled={locked} onTap={() => setDayQty(dailyFamily, dayIdx, a.id, -1)} onRepeat={() => setDayQty(dailyFamily, dayIdx, a.id, -1)} label={t("common.decreaseHoldAria", "Moins (maintenir pour accélérer)")} style={{ width: 54, height: 54, display: "grid", placeItems: "center", fontSize: `calc(23px * var(--nx-font-scale, 1))`, fontWeight: 800, opacity: locked ? .5 : 1, background: "none" }}>−</HoldButton>
                              <strong style={{ minWidth: 26, textAlign: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{qty}</strong>
                              <HoldButton disabled={locked} onTap={() => setDayQty(dailyFamily, dayIdx, a.id, 1)} onRepeat={() => setDayQty(dailyFamily, dayIdx, a.id, 1)} label={t("common.increaseHoldAria", "Plus (maintenir pour accélérer)")} style={{ width: 54, height: 54, display: "grid", placeItems: "center", fontSize: `calc(23px * var(--nx-font-scale, 1))`, fontWeight: 800, opacity: locked ? .5 : 1, background: "none" }}>＋</HoldButton>
                            </div>
                          </div>
                        );
                      })}
                      <div style={{ display: "flex", justifyContent: "space-between", margin: "12px 2px" }}>
                        <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>{t("reporting.daily.totalOfDayShort", "Total du jour")}</span>
                        <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{`${familyDayTotal(dailyFamily, dayIdx).toFixed(2)} €`}</strong>
                      </div>
                      {/* Ombre resserrée (pas btnSolid par défaut) — retour utilisateur explicite
                          (2026-08-21, capture à l'appui) : "l'espace entre le bouton et le bord de
                          la carte" semblait trop grand/injustifié. Mesuré en réel : l'écart
                          structurel carte↔bouton n'est que de 16px (padding:15 de la carte
                          juste au-dessus) — le vrai coupable est l'ombre par défaut de btnSolid
                          (12px de décalage + 26px de flou, pensée pour un bouton qui flotte sur
                          fond de page ouvert) qui bave visuellement bien au-delà de ces 16px,
                          fondue dans le fond clair de la carte, sans bord net. Ombre bien plus
                          resserrée UNIQUEMENT ici (jamais touché btnSolid globalement, utilisé
                          partout ailleurs dans l'app avec assez d'air autour pour que la grande
                          ombre reste un effet "flottant" voulu, pas une bavure). */}
                      {!locked && <Pressable onClick={() => saveDailyFamily(dailyFamily, dayIdx)} style={{ width: "100%", ...btnSolid(A.green), boxShadow: "0 4px 12px rgba(30,184,95,.28)" }}>{t("reporting.saveEntry", "Enregistrer la saisie")}</Pressable>}
                    </>
                  )}
                </section>
              )}
              </div>
              </div>
            </>
          );
        })()}

        {screen === "reporting" && repScreen === "weekly" && (() => {
          const locked = isWeekLocked();
          const viewingPast = weeklyViewWeek !== null;
          const distinctPastWeeks = [...new Set(reportingHistory.map((w) => w.kw))].sort((a, b) => b - a).slice(0, 8);
          const rowTotalFor = (cat) => viewingPast ? (pastWeekSrc?.rowTotal(cat.id) || 0) : categoryRowTotal(cat);
          const dayValueFor = (cat, i) => viewingPast ? (pastWeekSrc?.value(cat.id, i) || { pu20: 0, entsorgt: 0 }) : categoryDayValue(cat, i);
          const weekTotalDisplay = viewingPast ? (pastWeekSrc?.total || 0) : weekTotal();
          return (
            <>
              {/* Bascule Jour/Semaine — miroir exact de celle de l'écran "daily" (même
                  commentaire d'intention, voir plus haut dans le fichier). Remplace l'ancien
                  bandeau "Lecture seule — modifie depuis Saisie quotidienne" : ce bandeau
                  n'avait plus de sens une fois le popup d'édition ajouté directement sur cette
                  grille (2026-09-20, maquette validée). */}
              <div style={{ display: "flex", gap: 6, padding: 4, background: P.soft, border: `1px solid ${P.line}`, borderRadius: 14, marginBottom: 14 }}>
                <Pressable onClick={() => setRepScreen("daily")} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 11, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: "none", color: P.muted }}>📝 {t("reporting.modeJourBtn", "Jour")}</Pressable>
                <Pressable onClick={() => {}} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 11, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: P.ink, color: P.card }}>📊 {t("reporting.modeSemaineBtn", "Semaine")}</Pressable>
              </div>

              <p style={{ margin: "0 2px 8px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{viewingPast ? t("reporting.weeklyIntroTextReadonly", "Bereich par Bereich, PÜ/20% et entsorgt — vue d'ensemble en lecture seule.") : t("reporting.weeklyIntroTextEditable", "Bereich par Bereich, PÜ/20% et entsorgt — tape une case pour la modifier.")}</p>
              <p style={{ margin: "0 2px 14px", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontStyle: "italic" }}>{t("reporting.weeklyV1NoteText", "V1 : Gutschrift, Inventurverlust et Inventurprognose sont ignorés.")}</p>

              {/* Navigation entre semaines — retour utilisateur explicite (2026-08-06) :
                  avant, seules les semaines déjà clôturées (distinctPastWeeks) étaient
                  atteignables, impossible de rejoindre une semaine jamais commencée.
                  ‹/› pilotent reportingWeekOffset, qui bascule automatiquement vers la vue
                  Historique à prix figés dès qu'on quitte la semaine réelle en cours (voir
                  l'effet juste au-dessus). Jamais au-delà de la semaine en cours (0). */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <Pressable feedback onClick={() => setReportingWeekOffset((o) => o - 1)} aria-label={t("planning.control.prevWeek", "Semaine précédente")} style={{
                  width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
                }}>‹</Pressable>
                <div style={{ flex: 1, textAlign: "center" }}>
                  <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`}}>KW {reportingWeekNumber}</strong>
                </div>
                <Pressable feedback onClick={() => reportingWeekOffset < 0 && setReportingWeekOffset((o) => o + 1)} aria-label={t("planning.control.nextWeek", "Semaine suivante")} disabled={reportingWeekOffset >= 0} style={{
                  width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`, opacity: reportingWeekOffset >= 0 ? .4 : 1,
                }}>›</Pressable>
              </div>

              {/* Sélecteur de semaine — consultation en lecture seule d'une semaine passée
                  (retour utilisateur explicite 2026-08-02 : "il manque également le
                  sélecteur de date"). La semaine en cours reste seule éditable. */}
              {/* Toujours visible, même sans historique — cacher toute la ligne quand
                  distinctPastWeeks est vide masquait le sélecteur en entier pour un vrai
                  compte sans encore d'historique réel (retour utilisateur explicite : "il
                  n'y a aucun sélecteur de date"), ce qui revenait à ne jamais l'afficher. */}
              <div className="nx-chips" style={{ display: "flex", gap: 6, marginBottom: 14, overflowX: "auto", paddingBottom: 2 }}>
                <Pressable onClick={() => setReportingWeekOffset(0)} style={{
                  flex: "0 0 auto", textAlign: "center", padding: "8px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                  background: !viewingPast ? A.blue : P.soft, color: !viewingPast ? "#fff" : P.ink,
                  border: `1.5px solid ${!viewingPast ? A.blue : P.line}`,
                }}>{t("reporting.currentWeekBtn", "Semaine en cours")}</Pressable>
                {distinctPastWeeks.length === 0 && (
                  <span style={{ alignSelf: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, fontStyle: "italic" }}>{t("reporting.noPastWeekYetText", "Pas encore de semaine passée enregistrée")}</span>
                )}
                {distinctPastWeeks.map((wk) => (
                  <Pressable key={wk} onClick={() => setReportingWeekOffset(wk - isoWeek(now))} style={{
                    flex: "0 0 auto", textAlign: "center", padding: "8px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                    background: weeklyViewWeek === wk ? A.blue : P.soft, color: weeklyViewWeek === wk ? "#fff" : P.ink,
                    border: `1.5px solid ${weeklyViewWeek === wk ? A.blue : P.line}`,
                  }}>KW {wk}</Pressable>
                ))}
              </div>

              {viewingPast ? (
                <section style={{ ...card, ...tile, justifyContent: "space-between", padding: 16, marginBottom: 14 }}>
                  <div>
                    <span style={eyebrow(P.muted)}>KW {weeklyViewWeek} {t("reporting.consultationSuffix", "— consultation")}</span>
                    <h2 style={{ margin: "4px 0 0", fontSize: `calc(20px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em" }}>{pastWeekLoading ? "…" : `${fmt2(weekTotalDisplay)} €`}</h2>
                  </div>
                  <Pressable onClick={exportWeeklyReport} disabled={pastWeekLoading} style={{
                    fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "8px 12px",
                  }}>{t("reporting.exportPdf", "Export PDF")}</Pressable>
                </section>
              ) : locked ? (
                // Bug réel trouvé (retour utilisateur 2026-09-20 : "il manque l'export") —
                // cette bannière "semaine verrouillée" n'avait qu'un bouton "Modifier", jamais
                // d'export : une semaine clôturée est justement celle qu'on a le plus de
                // raisons d'exporter (pas juste une semaine encore en cours). Ajouté sans
                // toucher au reste — même exportWeeklyReport que les deux autres états.
                <div style={{ display: "flex", flexDirection: "column", gap: 10, borderRadius: 16, padding: 14, marginBottom: 14, background: "rgba(30,184,95,.09)", border: "1px solid rgba(30,184,95,.25)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>✓</span>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: "#15803d", display: "block" }}>{t("reporting.weekEntryDoneTemplate", { week: isoWeek(now) }, `KW ${isoWeek(now)} — saisie terminée`)}</strong>
                      <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("reporting.weekTotalTemplate", { total: fmt2(weekTotal()) }, `Total semaine ${fmt2(weekTotal())} €.`)}</span>
                    </div>
                    <Pressable onClick={unlockWeek} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "8px 12px" }}>{t("common.editAria", "Modifier")}</Pressable>
                  </div>
                  <Pressable onClick={exportWeeklyReport} style={{ width: "100%", textAlign: "center", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 99, padding: "9px 0" }}>{t("reporting.exportPdf", "Export PDF")}</Pressable>
                </div>
              ) : (
                <section style={{ ...card, ...tile, justifyContent: "space-between", padding: 16, marginBottom: 14 }}>
                  <div>
                    <span style={eyebrow(P.muted)}>{t("reporting.totalWeekLabel", "Total semaine")}</span>
                    <h2 style={{ margin: "4px 0 0", fontSize: `calc(20px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em" }}>{`${fmt2(weekTotal())} €`}</h2>
                  </div>
                  <Pressable onClick={exportWeeklyReport} style={{
                    fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, background: P.soft, borderRadius: 99, padding: "8px 12px",
                  }}>{t("reporting.exportPdf", "Export PDF")}</Pressable>
                </section>
              )}

              {/* Vrai tableau croisé catégorie × jour (maquette validée 2026-09-20, voir
                  storeflow_wochenbericht_table_redesign_mockup) — remplace les anciennes
                  cartes+puces "tape un jour pour le modifier". PÜ/20% et Entsorgt sont lisibles
                  directement dans chaque case ; taper une case éditable (semaine en cours,
                  catégorie manuelle ou frischbrot_brot) rouvre le popup gridEdit — un vrai POST
                  derrière, jamais un simple affichage local. Couleur par rayon
                  (REPORT_CATEGORY_COLORS) = simple repère visuel, jamais une donnée. */}
              <div style={{ ...card, borderRadius: 18, padding: 0, marginBottom: 14, overflow: "hidden" }}>
                <div style={{ height: 3, background: `linear-gradient(90deg, ${A.green}, ${A.blue} 55%, ${A.red})` }} />
                <div className="nx-wk-scroll">
                  {/* CSS Grid, jamais <table>+position:sticky sur des <th>/<td> (retour utilisateur
                      2026-09-20, capture à l'appui : "les chiffres et les noms se mélangent entre
                      eux") — position:sticky sur des cellules de tableau est connu pour se
                      désynchroniser de sa ligne pendant le défilement sur Safari/WebKit (moteur
                      réel de l'app, iPhone), à cause de l'algorithme de calcul de colonnes en deux
                      passes propre à <table>. Une grille CSS place chaque cellule sur une piste de
                      ligne fixe, sans ce risque. Fonds systématiquement OPAQUES (P.card, jamais
                      P.soft qui est un rgba à faible alpha prévu pour un survol, pas une cellule
                      collée) — l'utilisateur avait raison : la transparence contribuait aussi au
                      mélange visuel. */}
                  <div className={`nx-wk-grid${isWide ? " nx-wk-wide" : ""}`} style={{
                    // Retour utilisateur (2026-10-14, 2e capture) : des colonnes desktop en px
                    // fixes (220+118×6+134=1062px) débordaient du conteneur réel sur un écran
                    // plus étroit que prévu — colonne Total coupée, scroll horizontal imposé.
                    // Unités fr (flexibles) : la grille remplit exactement la largeur
                    // réellement disponible, quelle qu'elle soit, jamais plus.
                    gridTemplateColumns: isWide ? "1.9fr repeat(6, 1fr) 1.15fr" : "126px repeat(6, 62px) 76px",
                    minWidth: isWide ? 760 : 574, width: isWide ? "100%" : undefined, background: P.card,
                  }}>
                    <div className="nx-wk-cell nx-wk-head nx-wk-cat-h" style={{ background: P.card, color: P.muted, borderBottom: `1.5px solid ${P.line}`, boxShadow: `2px 0 6px -2px rgba(0,0,0,.1), 0 1.5px 0 ${P.line}` }}>{t("reporting.weeklyTableBereichHeader", "Bereich")}</div>
                    {WEEK_DAY_LABELS.map((_, i) => (
                      <div key={i} className="nx-wk-cell nx-wk-head" style={{ background: P.card, color: P.muted, borderBottom: `1.5px solid ${P.line}` }}>{weekLabelShort(i)}</div>
                    ))}
                    <div className="nx-wk-cell nx-wk-head" style={{ background: P.card, color: P.ink, borderBottom: `1.5px solid ${P.line}` }}>{t("reporting.weeklyTableTotalHeader", "Total")}</div>

                    {REPORT_CATEGORIES.map((cat) => {
                      const catLabel = reportCategoryLabel(cat.id);
                      // Retour utilisateur (2026-09-20) : colonne redevenue étroite (comme
                      // avant), donc un libellé long ("Schnittblumen/Pflanzen" DE/FR,
                      // "Flores cortadas/Plantas" ES) réduit sa propre taille de police au lieu
                      // d'élargir la colonne pour tout le monde. Le filet min-height (voir
                      // .nx-wk-cell plus haut) reste en place, donc même si ce libellé passe
                      // quand même sur 2 lignes à cette taille réduite, aucun chevauchement
                      // n'est possible avec la valeur du jour voisin.
                      const catFontSize = isWide ? (catLabel.length > 16 ? 10 : 11) : (catLabel.length > 16 ? 9.5 : 11);
                      return (
                      <Fragment key={cat.id}>
                        <div className="nx-wk-cell nx-wk-cat" style={{ background: P.card, color: P.ink, borderBottom: `1px solid ${P.line}`, boxShadow: "2px 0 6px -2px rgba(0,0,0,.06)", fontSize: `calc(${catFontSize}px * var(--nx-font-scale, 1))` }}>
                          <span className="nx-wk-cat-bar" style={{ background: REPORT_CATEGORY_COLORS[cat.id] || P.muted }} />
                          {catLabel}
                          {cat.source !== "manual" && <span className="nx-wk-cat-tag" style={{ color: P.muted }}>{t("reporting.autoTag", "Auto")}</span>}
                        </div>
                        {WEEK_DAY_LABELS.map((_, i) => {
                          const v = dayValueFor(cat, i);
                          const total = v.pu20 + v.entsorgt;
                          const showPu = cat.source !== "auto_prebake";
                          // Éditable seulement semaine en cours + catégorie manuelle ou
                          // frischbrot_brot (PU/20 seul) — Prebake reste un reflet calculé de
                          // l'onglet Prebake, jamais éditable ici (même règle que l'ancienne
                          // fiche, jamais assouplie).
                          const editableHere = !viewingPast && !locked && (cat.source === "manual" || cat.id === "frischbrot_brot");
                          return (
                            <div key={i} className="nx-wk-cell" onClick={() => editableHere && openGridEdit(cat.id, i)} style={{ background: P.card, borderBottom: `1px solid ${P.line}`, cursor: editableHere ? "pointer" : "default" }}>
                              {total === 0 ? (
                                <span className="nx-wk-en" style={{ color: P.muted, fontWeight: 600 }}>—</span>
                              ) : (
                                <>
                                  {showPu && <span className="nx-wk-pu" style={{ color: P.muted }}>{fmt2(v.pu20)}</span>}
                                  <span className="nx-wk-en" style={{ color: P.ink }}>{fmt2(v.entsorgt)}</span>
                                </>
                              )}
                            </div>
                          );
                        })}
                        <div className="nx-wk-cell nx-wk-total-cell" style={{ background: P.card, borderBottom: `1px solid ${P.line}`, color: A.red }}>
                          <span className="nx-wk-total-pill" style={{ background: "rgba(239,68,68,.1)" }} />
                          {fmt2(rowTotalFor(cat))}
                        </div>
                      </Fragment>
                      );
                    })}

                    <div className="nx-wk-cell nx-wk-cat" style={{ background: P.ink, color: P.card, boxShadow: "2px 0 6px -2px rgba(0,0,0,.2)" }}>{t("reporting.weeklyTableDayTotalRow", "Total jour")}</div>
                    {WEEK_DAY_LABELS.map((_, i) => {
                      const dayTotal = REPORT_CATEGORIES.reduce((s, cat) => { const v = dayValueFor(cat, i); return s + v.pu20 + v.entsorgt; }, 0);
                      return <div key={i} className="nx-wk-cell" style={{ background: P.soft, color: P.ink }}>{fmt2(dayTotal)}</div>;
                    })}
                    <div className="nx-wk-cell" style={{ background: A.red, color: "#fff", fontSize: `calc(${isWide ? 12 : 13}px * var(--nx-font-scale, 1))` }}>{fmt2(weekTotalDisplay)}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 12, padding: "10px 15px 11px", fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted, borderTop: `1px solid ${P.line}`, background: P.soft }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 5 }}><i style={{ width: 8, height: 8, borderRadius: 3, display: "inline-block", background: P.muted }} />{t("reporting.legendPu20", "PÜ/20%")}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 5 }}><i style={{ width: 8, height: 8, borderRadius: 3, display: "inline-block", background: P.ink }} />{t("reporting.legendEntsorgt", "Entsorgt")}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 5 }}><i style={{ width: 8, height: 8, borderRadius: 3, display: "inline-block", background: A.red }} />{t("reporting.legendTotalWeek", "Total Woche")}</span>
                </div>
              </div>

              {!viewingPast && !locked && <Pressable onClick={saveWeek} style={{ width: "100%", ...btnSolid(A.blue), marginTop: 6 }}>{t("reporting.weekDoneSaveBtn", "Semaine terminée — Enregistrer")}</Pressable>}
            </>
          );
        })()}

        {screen === "reporting" && repScreen === "stunden" && (() => {
          const computed = stundenComputed();
          const last = computed[computed.length - 1];
          // leistungIstCum s'arrête au dernier Ist réellement saisi (voir stundenComputed) —
          // jamais `last` (souvent un jour futur, null) pour le résumé "Cumulé du mois".
          const lastIstRow = [...computed].reverse().find((r) => r.leistungIstCum != null);
          const todayISOStunden = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
          const stundenViewedMonth = new Date(now.getFullYear(), now.getMonth() + stundenMonthOffset, 1);
          const stundenMonthLabel = stundenViewedMonth.toLocaleDateString(intlLocale, { month: "long", year: "numeric" });
          return (
            <>
              <p style={{ margin: "0 2px 14px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{t("reporting.stundenIntroText", "Le plan change chaque mois : on importe donc le fichier Norma reçu en début ou fin de mois.")}</p>

              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <Pressable feedback onClick={() => setStundenMonthOffset((o) => o - 1)} aria-label={t("reporting.prevMonth", "Mois précédent")} style={{
                  width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
                }}>‹</Pressable>
                <div style={{ flex: 1, textAlign: "center" }}>
                  <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`, textTransform: "capitalize" }}>{stundenMonthLabel}{stundenMonthOffset === 0 ? ` · ${t("reporting.currentMonthBtn", "Mois en cours")}` : ""}</strong>
                </div>
                <Pressable feedback onClick={() => stundenMonthOffset < 0 && setStundenMonthOffset((o) => o + 1)} aria-label={t("reporting.nextMonth", "Mois suivant")} disabled={stundenMonthOffset >= 0} style={{
                  width: 40, height: 40, ...card, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`, opacity: stundenMonthOffset >= 0 ? .4 : 1,
                }}>›</Pressable>
              </div>

              {computed.length > 0 && (
                <Pressable onClick={exportStundenleistung} style={{ width: "100%", ...card, borderRadius: 14, padding: "11px 14px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
                  📄 {t("reporting.exportStundenBtn", "Exporter ce mois")}
                </Pressable>
              )}

              {!stundenImported || computed.length === 0 ? (
                <Pressable onClick={importStundenFile} style={{ width: "100%", ...btnSolid(A.frost) }}>{t("reporting.importNormaFileBtn", "📥 Importer le fichier NORMA")}</Pressable>
              ) : (
                <>
                  <section style={{ ...colorCard(A.frost), ...tile, flexDirection: "column", alignItems: "stretch", padding: 18, marginBottom: 14 }}>
                    <span style={onColorTag}>{t("reporting.monthCumulTag", "Cumulé du mois")}</span>
                    <h2 style={{ margin: "5px 0 3px", fontSize: `calc(19px * var(--nx-font-scale, 1))`, color: "#fff" }}>{last.leistungPlanCum != null ? `${fmt2(last.leistungPlanCum)} €/h` : "—"} {t("reporting.planSuffix", "Plan")}</h2>
                    <h2 style={{ margin: "0 0 8px", fontSize: `calc(19px * var(--nx-font-scale, 1))`, color: "#fff" }}>{lastIstRow?.leistungIstCum != null ? `${fmt2(lastIstRow.leistungIstCum)} €/h` : "—"} {t("reporting.istSuffix", "Ist")}</h2>
                    <p style={{ margin: 0, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("reporting.leistungFormulaText", "Leistung = Umsatz ÷ heures travaillées, cumulé depuis le 1er du mois.")}</p>
                  </section>

                  <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "0 2px 9px" }}>{t("reporting.dayByDayHeading", "Jour par jour")}</strong>
                  <p style={{ margin: "0 2px 8px", fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("reporting.stundenTableHint", "Fais glisser horizontalement pour voir toutes les colonnes (comme le document Norma).")}</p>
                  <p style={{ margin: "0 2px 8px", fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("reporting.stundenRowTapHint", "Touche un jour déjà passé pour saisir ou corriger son Ist.")}</p>

                  {/* Légende couleurs — 3 origines de donnée, jamais mélangées visuellement
                      (retour utilisateur) : tout le côté Plan — Tag, ∑ et Stundenleistung —
                      existe déjà sur le document Norma, jamais rempli par le travailleur
                      (frost) ; seuls Umsatz/Stunden Ist Tag sont réellement saisis à la main
                      (vert) ; tout le reste du côté Ist (∑, Stundenleistung Ist) est calculé
                      par l'app à partir de cette saisie (violet). */}
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "0 2px 10px" }}>
                    {[
                      [A.frost, t("reporting.legendImported", "Sur le document (Plan)")],
                      [A.green, t("reporting.legendUserEntered", "Saisi par toi (Ist)")],
                      [A.purple, t("reporting.legendCalculated", "Calculé par l'app depuis ta saisie")],
                    ].map(([c, label]) => (
                      <span key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                        <span style={{ width: 8, height: 8, borderRadius: 99, background: c, flex: "0 0 auto" }} />
                        {label}
                      </span>
                    ))}
                  </div>

                  {(() => {
                    const fmtE = (v) => v != null ? `${truncate2(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : "—";
                    const fmtH = (v) => v != null ? `${truncate2(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h` : "—";
                    const fmtL = (v) => v != null ? `${fmt2(v)} €/h` : "—";
                    const planColor = A.frost, istColor = A.green, calcColor = A.purple;
                    const th = { padding: "6px 7px", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, textTransform: "uppercase", textAlign: "right", whiteSpace: "nowrap", borderBottom: `1.5px solid ${P.line}` };
                    const thGroup = { padding: "6px 7px", fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink, textAlign: "center", whiteSpace: "nowrap", borderBottom: `1px solid ${P.line}` };
                    const td = { padding: "6px 7px", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
                    const planTh = { ...th, color: planColor }, istTh = { ...th, color: istColor }, calcTh = { ...th, color: calcColor };
                    const planTd = (v) => ({ ...td, color: planColor, opacity: v != null ? 1 : .4 });
                    const istTd = (v) => ({ ...td, color: istColor, opacity: v != null ? 1 : .4 });
                    const calcTd = { ...td, color: calcColor, opacity: .85 };
                    return (
                      <div style={{ ...card, overflowX: "auto", marginBottom: 8, WebkitOverflowScrolling: "touch" }}>
                        <table style={{ borderCollapse: "collapse", width: "100%" }}>
                          <thead>
                            <tr>
                              <th style={{ ...thGroup, position: "sticky", left: 0, background: P.card, zIndex: 1 }}>{t("reporting.dayCol", "Jour")}</th>
                              <th style={thGroup} colSpan={4}>{t("reporting.umsatzCol", "Umsatz")}</th>
                              <th style={thGroup} colSpan={4}>{t("reporting.stundenCol", "Stunden")}</th>
                              <th style={thGroup} colSpan={4}>{t("reporting.stundenleistungCol", "Stundenleistung")}</th>
                            </tr>
                            <tr>
                              <th style={{ ...th, position: "sticky", left: 0, background: P.card, zIndex: 1, textAlign: "left" }}></th>
                              <th style={planTh}>{t("reporting.planTagCol", "Plan")}</th>
                              <th style={istTh}>{t("reporting.istTagCol", "Ist")}</th>
                              <th style={planTh}>{t("reporting.planCumCol", "Plan ∑")}</th>
                              <th style={calcTh}>{t("reporting.istCumCol", "Ist ∑")}</th>
                              <th style={planTh}>{t("reporting.planTagCol", "Plan")}</th>
                              <th style={istTh}>{t("reporting.istTagCol", "Ist")}</th>
                              <th style={planTh}>{t("reporting.planCumCol", "Plan ∑")}</th>
                              <th style={calcTh}>{t("reporting.istCumCol", "Ist ∑")}</th>
                              <th style={planTh}>{t("reporting.planTagCol", "Plan")}</th>
                              <th style={calcTh}>{t("reporting.istTagCol", "Ist")}</th>
                              <th style={planTh}>{t("reporting.planCumCol", "Plan ∑")}</th>
                              <th style={calcTh}>{t("reporting.istCumCol", "Ist ∑")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {computed.map((d) => {
                              const editable = d.date <= todayISOStunden;
                              const isEditing = stundenIstEditDate === d.date;
                              const rowBg = isEditing ? "rgba(74,144,217,.12)" : d.isClosed ? "rgba(107,117,136,.09)" : "transparent";
                              return (
                                <tr
                                  key={d.day}
                                  onClick={editable ? () => {
                                    setStundenIstForm({ umsatz: d.umsatzIstTag != null ? String(d.umsatzIstTag).replace(".", ",") : "", heures: d.stundenIstTag != null ? String(d.stundenIstTag).replace(".", ",") : "" });
                                    setStundenIstEditDate(d.date);
                                  } : undefined}
                                  style={{ borderBottom: `1px solid ${P.line}`, background: rowBg, cursor: editable ? "pointer" : "default" }}
                                >
                                  <td style={{ ...td, textAlign: "left", fontWeight: 800, fontStyle: d.isClosed ? "italic" : "normal", color: d.isClosed ? P.muted : P.ink, position: "sticky", left: 0, background: rowBg === "transparent" ? P.card : rowBg }}>
                                    {d.day}{d.isClosed ? ` · ${t("reporting.closedDayTag", "fermé")}` : ""}
                                  </td>
                                  <td style={planTd(d.umsatzPlanTag)}>{fmtE(d.umsatzPlanTag)}</td>
                                  <td style={istTd(d.umsatzIstTag)}>{fmtE(d.umsatzIstTag)}</td>
                                  <td style={planTd(d.umsatzPlanCum)}>{fmtE(d.umsatzPlanCum)}</td>
                                  <td style={calcTd}>{fmtE(d.umsatzIstCum)}</td>
                                  <td style={planTd(d.stundenPlanTag)}>{fmtH(d.stundenPlanTag)}</td>
                                  <td style={istTd(d.stundenIstTag)}>{fmtH(d.stundenIstTag)}</td>
                                  <td style={planTd(d.stundenPlanCum)}>{fmtH(d.stundenPlanCum)}</td>
                                  <td style={calcTd}>{fmtH(d.stundenIstCum)}</td>
                                  <td style={planTd(d.leistungPlanTag)}>{fmtL(d.leistungPlanTag)}</td>
                                  <td style={calcTd}>{fmtL(d.leistungIstTag)}</td>
                                  <td style={planTd(d.leistungPlanCum)}>{fmtL(d.leistungPlanCum)}</td>
                                  <td style={calcTd}>{fmtL(d.leistungIstCum)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </>
              )}
            </>
          );
        })()}

        {screen === "reporting" && repScreen === "month" && (
          <>
            <p style={{ margin: "0 2px 6px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{t("reporting.monthIntroText", "Semaines du mois affiché, y compris la semaine en cours.")}</p>
            <p style={{ margin: "0 2px 14px", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontStyle: "italic" }}>{t("reporting.monthSplitNoteText", "Une semaine à cheval sur deux mois est coupée en KWxxa / KWxxb.")}</p>

            <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 15, marginBottom: 14 }}>
              <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, marginBottom: 3 }}>{t("reporting.krankCurrentWeekHeading", "Krank — semaine en cours")}</strong>
              <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 10 }}>{t("reporting.krankPersonsSummaryTemplate", { n: krankPersons }, `${krankPersons} personne${krankPersons > 1 ? "s" : ""} en arrêt (calculé depuis Planning) · heures à saisir manuellement`)}</span>
              <div style={{ display: "flex", gap: 8 }}>
                <input type="number" step="0.5" inputMode="decimal" placeholder="0" value={krankHours || ""} onChange={(e) => { krankHoursDirtyRef.current = true; setKrankHours(parseFloat(e.target.value) || 0); }} onBlur={saveKrankHours} style={{ ...inputStyle, flex: 1 }} />
                <span style={{ ...inputStyle, flex: "0 0 auto", width: 60, textAlign: "center", background: P.soft }}>{t("reporting.hoursUnitShort", "heures")}</span>
              </div>
            </section>

            {monthWeeks().map((w) => (
              <div key={`${w.kw}-${w.month || "current"}`} style={{ ...card, ...tile, padding: 14, marginBottom: 8 }}>
                <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>{w.current ? "🟢" : "✅"}</span>
                <div style={{ flex: 1 }}>
                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>KW {w.kwLabel || w.kw}</strong>
                  <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                    {w.current ? t("reporting.currentWeekShort", "Semaine en cours") : t("reporting.closedShort", "Clôturée")}{w.krankHours > 0 ? ` · ${t("reporting.krankShortTemplate", { h: w.krankHours }, `Krank ${w.krankHours} h`)}` : ""}{w.krankPersons > 0 ? ` ${t("reporting.personsCountShort", { n: w.krankPersons }, `(${w.krankPersons} pers.)`)}` : ""}
                  </div>
                </div>
                <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: A.red }}>{`−${fmt2(w.total)} €`}</strong>
              </div>
            ))}
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <Pressable onClick={exportMonthClosure} style={{
                flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
              }}>{t("reporting.exportShort", "Export")}</Pressable>
              {/* "Vider ce mois" ouvre le vrai modal "zone sensible" (déjà réel, confirmation
                  "SUPPRIMER" obligatoire), catégories du mois pré-cochées — jamais une
                  suppression directe sans confirmation pour une action destructive irréversible. */}
              <Pressable onClick={() => { setWipeSelections(["prebake", "brot", "manual", "krank"]); setWipeConfirmText(""); setSheet("wipeModal"); }} style={{
                flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                background: "rgba(239,68,68,.12)", color: A.red,
              }}>{t("reporting.wipeMonthBtn", "Vider ce mois")}</Pressable>
            </div>
          </>
        )}

        {screen === "reporting" && repScreen === "history" && (
          <div className="nx-screen-enter">
            <p style={{ margin: "0 2px 14px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
              {historyView === "weekly" ? t("reporting.history.subtitle", "Registre continu depuis KW1 de l'année, jusqu'à la semaine en cours — lecture seule.") : t("reporting.history.monthlyViewBody", "Un mois = sa dernière semaine de Monatsmeldung, depuis janvier.")}
            </p>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[{ id: "weekly", l: t("reporting.weeklyViewLabel", "Vue hebdomadaire") }, { id: "monthly", l: t("reporting.monthlyViewLabel", "Vue mensuelle") }].map((v) => (
                <Pressable key={v.id} onClick={() => setHistoryView(v.id)} style={{
                  flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  background: historyView === v.id ? A.navy : P.soft, color: historyView === v.id ? "#fff" : P.ink,
                  border: `1.5px solid ${historyView === v.id ? A.navy : P.line}`,
                }}>{v.l}</Pressable>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(320px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
            {historyView === "weekly" ? (
              [{ kw: isoWeek(now), total: weekTotal(), current: true }, ...reportingHistory].map((w, wIdx) => (
                <div key={`${w.kw}-${w.month || "current"}`} className="nx-list-item-in" style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 8, animationDelay: `${Math.min(wIdx, 14) * 28}ms` }}>
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>KW {w.kwLabel || w.kw} · {THIS_YEAR}</strong>
                    <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{w.current ? t("reporting.currentWeekShort", "Semaine en cours") : t("reporting.archivedShort", "Archivée")}</div>
                  </div>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: A.red }}>{`−${fmt2(w.total)} €`}</strong>
                </div>
              ))
            ) : (
              monthlyHistory().map((m, mIdx) => (
                <div key={m.month} className="nx-list-item-in" style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 8, animationDelay: `${Math.min(mIdx, 14) * 28}ms` }}>
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{m.month} {THIS_YEAR}</strong>
                    <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>Monatsmeldung · KW {m.kwLabel || m.kw}</div>
                  </div>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: A.red }}>{`−${fmt2(m.total)} €`}</strong>
                </div>
              ))
            )}
            </div>
          </div>
        )}

        {screen === "reporting" && repScreen === "catalog" && (
          <div className="nx-screen-enter">
            <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 10, paddingBottom: 2 }}>
              {[{ id: "all", l: t("reporting.allShort", "Tous") }, { id: "Prebake", l: "Prebake" }, { id: "Brot", l: "Brot" }].map((c) => (
                <Pressable key={c.id} onClick={() => setCatalogTab(c.id)} style={{
                  flex: "0 0 auto", padding: "8px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  background: catalogTab === c.id ? A.brown : P.soft, color: catalogTab === c.id ? "#fff" : P.ink,
                  border: `1.5px solid ${catalogTab === c.id ? A.brown : P.line}`,
                }}>{c.l}</Pressable>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[{ id: "active", l: t("reporting.status.activeShort", "Actifs") }, { id: "suspended", l: t("reporting.catalog.filterSuspended", "Suspendus") }, { id: "archived", l: t("reporting.catalog.filterArchived", "Archivés") }].map((s) => (
                <Pressable key={s.id} onClick={() => setCatalogStatusFilter(s.id)} style={{
                  flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                  background: catalogStatusFilter === s.id ? A.navy : P.soft, color: catalogStatusFilter === s.id ? "#fff" : P.ink,
                  border: `1.5px solid ${catalogStatusFilter === s.id ? A.navy : P.line}`,
                }}>{s.l}</Pressable>
              ))}
            </div>
            <Pressable onClick={() => openArticleModal(null)} style={{ width: "100%", ...btnSolid(A.brown), marginBottom: 14 }}>＋ {t("reporting.catalog.addArticleBtn", "Article")}</Pressable>

            {catalogFiltered().length === 0 && <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px 14px" }}>{t("reporting.noArticleInFilterText", "Aucun article dans ce filtre.")}</p>}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(320px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
            {catalogFiltered().map((a, aIdx) => (
              <Pressable key={a.id} feedback className="nx-list-item-in" onClick={() => openArticleModal(a)} style={{ ...card, ...tile, padding: 13, marginBottom: isWide ? 0 : 8, animationDelay: `${Math.min(aIdx, 14) * 28}ms` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{a.name}</strong>
                  <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{a.category}{a.program ? ` · ${a.program}` : ""} · {a.price.toFixed(2)} €</div>
                </div>
                <span style={{
                  fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 9px",
                  color: a.status === "active" ? "#fff" : P.muted,
                  background: a.status === "active" ? A.green : a.status === "suspended" ? "rgba(231,138,18,.16)" : P.soft,
                }}>{{ active: t("reporting.status.active", "Actif"), suspended: t("reporting.status.suspended", "Suspendu"), archived: t("reporting.status.archived", "Archivé") }[a.status]}</span>
              </Pressable>
            ))}
            </div>

            <section style={{ ...card, borderRadius: 20, padding: 16, marginTop: 10, border: `1.5px solid rgba(239,68,68,.3)` }}>
              <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: A.red, display: "block", marginBottom: 5 }}>{t("reporting.dangerZoneTitleFull", "⚠️ Zone sensible")}</strong>
              <p style={{ margin: "0 0 12px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                {t("reporting.catalog.dangerZoneBody", "Choisis quoi supprimer définitivement parmi les saisies entrées dans les fiches, pour toutes les filiales. Le catalogue d'articles n'est jamais touché.")}
              </p>
              <Pressable onClick={() => { setWipeSelections([]); setWipeConfirmText(""); setSheet("wipeModal"); }} style={{
                width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                background: "rgba(239,68,68,.12)", color: A.red,
              }}>{t("reporting.catalog.wipeEntriesBtn", "Vider des saisies")}</Pressable>
            </section>
          </div>
        )}

        {/* ================= ADMIN ================= */}
        {/* Refonte 2026-08-11 (retour utilisateur explicite, artefact validé point par
            point) : stats en cartes séparées avec icône, cartes de navigation avec un vrai
            badge de compte à traiter. Aucune nouvelle donnée — adminPending/adminResetRequests
            étaient déjà chargés globalement, jamais affichés au niveau du hub avant. Le garde
            (NEXORA_ADMIN || isNetworkLeader) sur "Filiales" est inchangé : un FL/FLA ne voit
            toujours que sa propre filiale, jamais les autres. */}
        {screen === "admin" && adminScreen === "hub" && (() => {
          const usersNeedingAttention = adminPending.length + adminResetRequests.length;
          // Redesign 2026-08-30 (retour utilisateur explicite) : la rangée de stats passe en
          // bandeau unique (voir statItems ci-dessous), déjà validé. Les 4 tuiles de
          // navigation, elles, sont revenues à la couleur pleine — retour utilisateur
          // explicite après un premier essai en carte crème + icône : "tous les autres
          // modules sont colorés... reste un peu pas cohérent" — abandonne donc l'essai
          // crème/icône au profit du VRAI système établi app-wide (colorCard/onColorChip/
          // onColorTag/onColorSub, déjà utilisé sur des dizaines d'écrans, voir Control/
          // Delivery/Inventur...), jamais une variante inventée à part.
          // minHeight (retour utilisateur explicite : "toutes les tuiles [doivent avoir] les
          // mêmes longueurs") — les 4 sous-titres n'ont pas la même longueur (celui de
          // Face-ID passe sur 2 lignes, les autres sur 1), sans hauteur commune les cartes
          // auraient des tailles visuellement différentes.
          const navCard = (hex) => ({ ...colorCard(hex), display: "flex", alignItems: "center", gap: 13, padding: 14, marginBottom: 10, minHeight: 82 });
          const statItems = [
            { v: activeUsersCount, l: t("admin.stat.activeUsers", "Actifs"), c: A.green },
            { v: disabledUsersCount, l: t("admin.stat.disabledAccounts", "Désactivés"), c: A.red },
            { v: usersNeedingAttention, l: t("admin.stat.pending", "En attente"), c: A.orange },
            { v: ROLE_HIERARCHY.length, l: t("admin.stat.configuredRoles", "Rôles"), c: A.purple },
          ];
          return (
          <>
            <div style={{ ...card, display: "flex", alignItems: "stretch", borderRadius: 18, padding: "14px 4px", marginBottom: 16 }}>
              {statItems.map((s, i) => (
                <Fragment key={s.l}>
                  <div style={{ flex: 1, textAlign: "center", padding: "0 4px" }}>
                    <strong style={{ display: "block", fontSize: `calc(19px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: "-.02em", marginBottom: 3, color: i === 0 ? P.ink : s.c }}>{s.v}</strong>
                    <span style={{ fontSize: `calc(8.3px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".03em", lineHeight: 1.25, display: "block" }}>{s.l}</span>
                  </div>
                  {i < statItems.length - 1 && <div style={{ width: 1, background: P.line, margin: "2px 0" }} />}
                </Fragment>
              ))}
            </div>
            <Pressable onClick={() => setAdminScreen("users")} style={navCard(A.blue)}>
              <span style={onColorChip}><EmojiIcon e="👥" /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: "block", fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: "-.01em", color: "#fff" }}>{t("admin.usersTitle", "Utilisateurs")}</strong>
                <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, lineHeight: 1.35, ...onColorSub }}>{t("admin.usersSubtitle", "Profils, rôles, filiales et accès collaborateurs.")}</span>
              </div>
              {usersNeedingAttention > 0 && (
                <span style={{ background: "rgba(255,255,255,.22)", color: "#fff", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 900, padding: "5px 10px", borderRadius: 99, flexShrink: 0 }}>{usersNeedingAttention}</span>
              )}
              <span style={{ color: "rgba(255,255,255,.7)", fontSize: `calc(15px * var(--nx-font-scale, 1))`, flexShrink: 0 }}>›</span>
            </Pressable>
            <Pressable onClick={() => setAdminScreen("roles")} style={navCard(A.orange)}>
              <span style={onColorChip}><EmojiIcon e="🛡️" /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: "block", fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: "-.01em", color: "#fff" }}>{t("admin.rolesTitle", "Rôles & permissions")}</strong>
                <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, lineHeight: 1.35, ...onColorSub }}>{t("admin.rolesSubtitleTemplate", { n: PERMISSION_GROUPS.length }, `Droits par rôle, ${PERMISSION_GROUPS.length} modules.`)}</span>
              </div>
              <span style={{ color: "rgba(255,255,255,.7)", fontSize: `calc(15px * var(--nx-font-scale, 1))`, flexShrink: 0 }}>›</span>
            </Pressable>
            {/* Supervision Face ID/Touch ID (retour utilisateur explicite, 2026-08-29) —
                chaque compte gère librement SES propres appareils depuis ses propres
                Réglages (inchangé, voir sheet "webauthnManage") ; voir la liste de TOUS les
                comptes/appareils reste réservé à l'admin, jamais "n'importe qui" — même
                gate que isNexoraAdmin (NEXORA_ADMIN ou super-admin personnel). */}
            {isNexoraAdmin && (
            <Pressable onClick={() => { loadAdminWebauthnDevices(); setAdminScreen("webauthnDevices"); }} style={navCard(A.purple)}>
              <span style={onColorChip}><EmojiIcon e="🔐" /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: "block", fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: "-.01em", color: "#fff" }}>{t("admin.webauthnDevicesTitle", "Appareils Face ID")}</strong>
                <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, lineHeight: 1.35, ...onColorSub }}>{t("admin.webauthnDevicesSubtitle", "Supervision réseau — biométrie enregistrée par compte.")}</span>
              </div>
              <span style={{ color: "rgba(255,255,255,.7)", fontSize: `calc(15px * var(--nx-font-scale, 1))`, flexShrink: 0 }}>›</span>
            </Pressable>
            )}
            {/* Preisänderung retirée du Hub Administration (retour utilisateur explicite,
                2026-08-16 : "l'admin n'en a pas besoin puisqu'il dispose des 2 vues compte
                normal et compte GR" — n'a rien à faire ici comme 3e destination séparée).
                Accès géré depuis l'écran Preisänderung normal, bouton ⚙️ dans son topbar,
                voir ~ligne 15340. */}
            {(user?.role === "NEXORA_ADMIN" || isNetworkLeader) && (
            <Pressable onClick={() => setAdminScreen("stores")} style={navCard(A.green)}>
              <span style={onColorChip}><EmojiIcon e="🏬" /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: "block", fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: "-.01em", color: "#fff" }}>{t("admin.storesTitle", "Filiales")}</strong>
                <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, lineHeight: 1.35, ...onColorSub }}>{t("admin.storesSubtitleTemplate", { n: storesCatalog.length }, `${storesCatalog.length} filiale${storesCatalog.length > 1 ? "s" : ""} créée${storesCatalog.length > 1 ? "s" : ""} dans le registre.`)}</span>
              </div>
              <span style={{ color: "rgba(255,255,255,.7)", fontSize: `calc(15px * var(--nx-font-scale, 1))`, flexShrink: 0 }}>›</span>
            </Pressable>
            )}
          </>
          );
        })()}

        {/* Admin > Filiales — création/édition réelle réservée à NEXORA_ADMIN (retour
            utilisateur explicite : un BL ne crée jamais de filiale, il se la voit
            attribuer). La liste reste lisible pour tout compte ayant accès à ce module
            (GET /api/stores est ouvert), seules les actions de mutation sont bloquées
            côté serveur pour un non-admin — ce garde client évite juste de proposer des
            boutons qui échoueraient. */}
        {screen === "admin" && adminScreen === "stores" && (
          <div className="nx-screen-enter">
            {user?.role === "NEXORA_ADMIN" && (
              <Pressable onClick={openAdminStoreCreate} style={{
                width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                background: A.green, color: "#fff", marginBottom: 14, boxShadow: "0 10px 22px rgba(30,184,95,.3)",
              }}>＋ {t("admin.createStoreBtn", "Créer une filiale")}</Pressable>
            )}
            {storesCatalog.length === 0 && (
              <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("admin.noStoresText", "Aucune filiale dans le registre.")}</p>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
            {storesCatalog.map((s, sIdx) => (
              <div key={s.id} className="nx-list-item-in" style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 8, width: "100%", boxSizing: "border-box", animationDelay: `${Math.min(sIdx, 14) * 28}ms` }}>
                <Pressable feedback={user?.role === "NEXORA_ADMIN"} onClick={() => user?.role === "NEXORA_ADMIN" && openAdminStoreEdit(s)} style={{ display: "flex", alignItems: "center", gap: 14, flex: 1, minWidth: 0 }}>
                  <span style={{ width: 38, height: 38, borderRadius: 12, background: "rgba(30,184,95,.13)", display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>🏬</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{s.label || s.id}</strong>
                    <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>
                      {s.id}{s.address ? ` · ${s.address}` : ""}{storeRegionsMap[s.id] ? ` · ${germanLander[storeRegionsMap[s.id]] || storeRegionsMap[s.id]}` : ""}
                    </div>
                  </div>
                  {user?.role === "NEXORA_ADMIN" && <span style={{ color: P.muted, fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>›</span>}
                </Pressable>
                {user?.role === "NEXORA_ADMIN" && (
                  <Pressable onClick={() => deleteAdminStore(s)} aria-label={`Supprimer ${s.id}`} style={{
                    width: 32, height: 32, borderRadius: 99, background: "rgba(239,68,68,.13)", color: A.red, flex: "0 0 auto",
                    display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, marginLeft: 8,
                  }}>🗑️</Pressable>
                )}
              </div>
            ))}
            </div>
          </div>
        )}

        {/* Admin > Appareils Face ID — supervision réseau, avec suppression possible depuis
            2026-08-31 (retour utilisateur explicite, revient sur "lecture seule" du
            2026-08-29). isNexoraAdmin re-vérifié ici, jamais seulement la navigation, même
            principe que Preisänderung admin juste en dessous — le vrai gate reste
            GET/DELETE /api/admin/webauthn-devices (server.js), ceci évite juste d'afficher
            un écran vide/en erreur à qui n'aurait pas dû l'atteindre. */}
        {screen === "admin" && adminScreen === "webauthnDevices" && isNexoraAdmin && (
          adminWebauthnDevices.length === 0 ? (
            <div className="nx-screen-enter" style={{ ...card, textAlign: "center", padding: 30, color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))` }}>
              {t("admin.webauthnDevices.emptyText", "Aucun compte n'a encore activé Face ID.")}
            </div>
          ) : (
            <div className="nx-screen-enter" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {adminWebauthnDevices.map((acc) => (
                <div key={acc.userId} style={{ ...card, padding: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <span style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(139,47,214,.13)", display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>🔐</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{acc.name}</strong>
                      <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{roleLabel(acc.role)}{acc.storeId ? ` · ${acc.storeId}` : ""}</span>
                    </div>
                    <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, background: P.soft, borderRadius: 99, padding: "3px 9px" }}>{t("admin.webauthnDevices.countTemplate", { n: acc.devices.length }, `${acc.devices.length} appareil${acc.devices.length > 1 ? "s" : ""}`)}</span>
                  </div>
                  {acc.devices.map((d) => (
                    <div key={d.credentialId} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0 7px 42px", borderTop: `1px solid ${P.line}` }}>
                      <span style={{ flex: 1, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`}}>{d.deviceLabel || t("settings.webauthn.unnamedDevice", "Appareil")}</span>
                      <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>{fmtFR((d.createdAt || "").slice(0, 10))}</span>
                      <Pressable onClick={() => deleteAdminWebauthnDevice(acc, d)} aria-label={t("admin.webauthnDevices.deleteAria", "Supprimer cet appareil")} style={{
                        width: 26, height: 26, borderRadius: 99, background: "rgba(239,68,68,.13)", color: A.red, flex: "0 0 auto",
                        display: "grid", placeItems: "center", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                      }}>🗑️</Pressable>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )
        )}

        {/* Preisänderung > gestion réseau — création strictement réservée à NEXORA_ADMIN
            (jamais une filiale, jamais même un GR — voir server/preisanderung/routes.js).
            Rattaché à l'écran Preisänderung normal (bouton ⚙️ dans son topbar) depuis le
            2026-08-16 — plus une destination séparée dans Administration (retour utilisateur :
            "l'admin n'en a pas besoin puisqu'il dispose des 2 vues compte normal et compte
            GR"). Gate isNexoraAdmin redondant volontairement ici pour ne jamais dépendre
            uniquement de la navigation pour protéger cet écran. */}
        {screen === "preisanderung" && paScreen === "admin" && isNexoraAdmin && paAdminScreen === "list" && (() => {
          const batches = new Map();
          paAdminList.forEach((p) => {
            if (!batches.has(p.batchId)) batches.set(p.batchId, { batchId: p.batchId, reference: p.reference, createdAt: p.createdAt, isDemo: p.isDemo, docs: [] });
            batches.get(p.batchId).docs.push(p);
          });
          const batchList = Array.from(batches.values()).sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
          return (
            <div className="nx-screen-enter">
              <Pressable onClick={() => setPaAdminScreen("create")} style={{
                width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                background: A.amber, color: "#fff", marginBottom: 10, boxShadow: `0 10px 22px ${A.amber}4d`,
              }}>＋ {t("preisanderung.admin.newBtn", "Nouvelle Preisänderung")}</Pressable>
              {/* Données démo (2026-08-21, retour utilisateur explicite) — vrais documents,
                  clairement distincts des vrais envois (isDemo:true), un seul geste les
                  remplace intégralement par un nouvel échantillon. Cible les filiales cochées
                  dans le formulaire "Nouvelle Preisänderung" (même state, jamais dupliqué). */}
              <Pressable disabled={paDemoBusy} onClick={regeneratePaDemo} style={{
                width: "100%", textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                background: P.soft, color: P.ink, border: `1.5px dashed ${P.line}`, marginBottom: 16, opacity: paDemoBusy ? .6 : 1,
              }}>🎲 {paDemoBusy ? t("common.sending", "Envoi...") : t("preisanderung.admin.demoRefreshBtn", "Régénérer les données démo")}</Pressable>
              {batchList.length === 0 && (
                <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("preisanderung.admin.noneYetText", "Aucune Preisänderung envoyée pour l'instant.")}</p>
              )}
              {batchList.map((b) => {
                const doneCount = b.docs.filter((d) => d.status === "abgeschlossen").length;
                return (
                  <div key={b.batchId} style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 15, marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                      <span style={{ width: 38, height: 38, borderRadius: 12, background: `${A.amber}1f`, color: A.amber, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`, flexShrink: 0 }}>💶</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ display: "flex", alignItems: "center", gap: 6, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>
                          {b.reference}
                          {b.isDemo && <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.purple, background: `${A.purple}1f`, borderRadius: 99, padding: "2px 7px" }}>🎲 DEMO</span>}
                        </strong>
                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{fmtFR((b.createdAt || "").slice(0, 10))}</span>
                      </div>
                      <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: doneCount === b.docs.length ? A.green : P.muted, background: doneCount === b.docs.length ? `${A.green}1f` : P.soft, borderRadius: 99, padding: "4px 10px", flexShrink: 0 }}>
                        {t("preisanderung.admin.storesDoneTemplate", { n: doneCount, total: b.docs.length }, `${doneCount}/${b.docs.length} filiales terminées`)}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {b.docs.map((d) => (
                        <span key={d.id} style={{
                          fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 700, borderRadius: 99, padding: "3px 9px",
                          color: d.status === "abgeschlossen" ? A.green : P.muted,
                          background: d.status === "abgeschlossen" ? `${A.green}1f` : P.soft,
                        }}>{d.storeId}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {screen === "preisanderung" && paScreen === "admin" && isNexoraAdmin && paAdminScreen === "create" && (
          <div className="nx-screen-enter">
            <Field label={t("preisanderung.admin.referenceLabel", "Référence")}>
              <input style={inputStyle} placeholder={t("preisanderung.admin.referencePlaceholder", "Ex. PA-2026-08-16")} value={paCreateForm.reference} onChange={(e) => setPaCreateForm({ ...paCreateForm, reference: e.target.value })} />
            </Field>

            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", margin: "16px 2px 8px" }}>{t("preisanderung.admin.targetStoresLabel", "Filiales cibles")}</span>
            <div className="nx-chips" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 18, paddingBottom: 2 }}>
              {storesCatalog.map((s) => {
                const sel = paCreateForm.targetStoreIds.includes(s.id);
                return (
                  <Pressable key={s.id} onClick={() => togglePaTargetStore(s.id)} style={{
                    flex: "0 0 auto", padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                    background: sel ? A.amber : P.soft, color: sel ? "#fff" : P.muted,
                  }}>{s.label || s.id}</Pressable>
                );
              })}
            </div>

            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", margin: "0 2px 8px" }}>{t("preisanderung.admin.linesLabel", "Articles")}</span>
            {paCreateForm.lines.map((line, idx) => (
              <div key={idx} style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 14, marginBottom: 10 }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <input style={{ ...inputStyle, flex: 1 }} placeholder={t("preisanderung.admin.artNrPlaceholder", "Artikelnummer")} value={line.artNr} onChange={(e) => updatePaLine(idx, "artNr", e.target.value)} />
                  {paCreateForm.lines.length > 1 && (
                    <Pressable onClick={() => removePaLine(idx)} aria-label={t("common.delete", "Supprimer")} style={{
                      width: 40, height: 40, borderRadius: 12, background: "rgba(239,68,68,.1)", color: A.red, display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, flexShrink: 0,
                    }}>✕</Pressable>
                  )}
                </div>
                <input style={{ ...inputStyle, marginBottom: 8 }} placeholder={t("preisanderung.admin.namePlaceholder", "Désignation")} value={line.name} onChange={(e) => updatePaLine(idx, "name", e.target.value)} />
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <input style={{ ...inputStyle, flex: 1 }} type="number" step="0.01" placeholder={t("preisanderung.line.alterPreisLabel", "Alter Preis")} value={line.alterPreis} onChange={(e) => updatePaLine(idx, "alterPreis", e.target.value)} />
                  <input style={{ ...inputStyle, flex: 1 }} type="number" step="0.01" placeholder={t("preisanderung.line.neuerPreisLabel", "Neuer Preis")} value={line.neuerPreis} onChange={(e) => updatePaLine(idx, "neuerPreis", e.target.value)} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {["gutschrift", "belastung"].map((art) => (
                    <Pressable key={art} onClick={() => updatePaLine(idx, "art", art)} style={{
                      flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 12, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                      background: line.art === art ? (art === "gutschrift" ? A.green : A.red) : P.soft,
                      color: line.art === art ? "#fff" : P.ink,
                    }}>{art === "gutschrift" ? "Gutschrift" : "Belastung"}</Pressable>
                  ))}
                </div>
              </div>
            ))}
            <Pressable onClick={addPaLine} style={{ width: "100%", textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink, marginBottom: 18 }}>
              ＋ {t("preisanderung.admin.addLineBtn", "Ajouter un article")}
            </Pressable>

            <Pressable disabled={paCreateBusy} onClick={submitPaCreate} style={{ width: "100%", ...btnSolid(A.amber), opacity: paCreateBusy ? .6 : 1 }}>
              {paCreateBusy ? t("common.sending", "Envoi...") : t("preisanderung.admin.sendBtn", "Envoyer aux filiales")}
            </Pressable>
          </div>
        )}

        {/* ================= VERTRETUNG ENTRE GRANDS RESPONSABLES =================
             Concept validé en conversation complète (2026-08-22) : un GR (BL/VKL/NL) qui
             part en congés ou tombe malade désigne un PAIR DE MÊME NIVEAU (jamais un niveau
             différent) qui reçoit temporairement l'accès à ses filiales — vérifié serveur
             (identity.stores, voir sfRequestIdentity), jamais fait confiance au client. Une
             même personne peut couvrir plusieurs pairs en même temps (confirmé
             explicitement). */}
        {screen === "vertretung" && vertretungScreen === "hub" && (() => {
          // user?.employeeId n'existe pas côté client (le champ canonique est proId, voir
          // partout ailleurs dans ce fichier — myId = user?.proId || user?.id) — bug réel
          // trouvé en testant en direct : sans ce correctif, myKey valait "undefined" et
          // aucune section (à valider/mes remplacements/je couvre) ne s'affichait jamais.
          const myKey = String(user?.proId || user?.id || "");
          const toApprove = vertretungList.filter((v) => v.status === "PENDING" && String(v.approverEmployeeId) === myKey);
          const mine = vertretungList.filter((v) => String(v.absentEmployeeId) === myKey);
          const covering = vertretungList.filter((v) => String(v.deputyEmployeeId) === myKey);
          const STATUS_META = {
            PENDING: { l: t("vertretung.status.pending", "En attente"), c: A.orange, bg: `${A.orange}1f` },
            APPROVED: { l: t("vertretung.status.approved", "Approuvée"), c: A.green, bg: `${A.green}1f` },
            REJECTED: { l: t("vertretung.status.rejected", "Refusée"), c: A.red, bg: `${A.red}1f` },
            CANCELLED: { l: t("vertretung.status.cancelled", "Annulée"), c: P.muted, bg: P.soft },
          };
          const card2 = (v, { showCancel }) => {
            const meta = STATUS_META[v.status];
            return (
              <div key={v.id} style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 14, marginBottom: 9 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <span style={{ width: 36, height: 36, borderRadius: 12, background: `${A.navy}1f`, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>🔄</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{v.absentName} → {v.deputyName}</strong>
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                      {v.reason === "krankheit" ? t("vertretung.reasonKrankheit", "Maladie") : t("vertretung.reasonUrlaub", "Congés")} · {fmtFR(v.startDate)} → {v.endDateUnknown ? t("vertretung.endUnknown", "fin inconnue") : fmtFR(v.endDate)}
                    </span>
                  </div>
                  <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: meta.c, background: meta.bg, borderRadius: 99, padding: "4px 10px", flexShrink: 0 }}>{meta.l}</span>
                </div>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("vertretung.storesCountTemplate", { n: v.stores.length }, `${v.stores.length} filiale(s) concernée(s)`)}</span>
                {showCancel && ["PENDING", "APPROVED"].includes(v.status) && (
                  <Pressable onClick={() => cancelVertretung(v.id)} style={{ marginTop: 10, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: A.red, background: "rgba(239,68,68,.1)" }}>
                    {t("vertretung.cancelBtn", "Annuler")}
                  </Pressable>
                )}
              </div>
            );
          };
          return (
            <div className="nx-screen-enter">
              <Pressable onClick={() => { setVertretungForm(emptyVertretungForm); setVertretungScreen("create"); }} style={{
                width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                background: A.navy, color: "#fff", marginBottom: 20, boxShadow: `0 10px 22px ${A.navy}4d`,
              }}>＋ {t("vertretung.declareBtn", "Déclarer mon absence")}</Pressable>

              {toApprove.length > 0 && (
                <>
                  <h3 style={{ margin: "0 2px 10px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800 }}>{t("vertretung.toApproveTitle", "À valider")}</h3>
                  {toApprove.map((v) => (
                    <div key={v.id} style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 14, marginBottom: 9 }}>
                      <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, marginBottom: 4 }}>{v.absentName} {t("vertretung.wantsToBeReplacedBy", "souhaite être remplacé(e) par")} {v.deputyName}</strong>
                      <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginBottom: 10 }}>
                        {v.reason === "krankheit" ? t("vertretung.reasonKrankheit", "Maladie") : t("vertretung.reasonUrlaub", "Congés")} · {fmtFR(v.startDate)} → {v.endDateUnknown ? t("vertretung.endUnknown", "fin inconnue") : fmtFR(v.endDate)} · {t("vertretung.storesCountTemplate", { n: v.stores.length }, `${v.stores.length} filiale(s)`)}
                      </span>
                      <div style={{ display: "flex", gap: 8 }}>
                        <Pressable onClick={() => decideVertretung(v.id, "reject")} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: A.red, background: "rgba(239,68,68,.1)" }}>{t("common.refuse", "Refuser")}</Pressable>
                        <Pressable onClick={() => decideVertretung(v.id, "approve")} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: "#fff", background: A.green }}>{t("common.approve", "Approuver")}</Pressable>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {covering.length > 0 && (
                <>
                  <h3 style={{ margin: "20px 2px 10px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800 }}>{t("vertretung.coveringTitle", "Tu couvres actuellement")}</h3>
                  {covering.map((v) => card2(v, { showCancel: false }))}
                </>
              )}

              <h3 style={{ margin: "20px 2px 10px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800 }}>{t("vertretung.mineTitle", "Mes remplacements")}</h3>
              {mine.length === 0 && <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, marginTop: 10 }}>{t("vertretung.noneYetText", "Aucune demande pour l'instant.")}</p>}
              {mine.map((v) => card2(v, { showCancel: true }))}
            </div>
          );
        })()}

        {screen === "vertretung" && vertretungScreen === "create" && (
          <div className="nx-screen-enter">
            <span style={{ ...eyebrow(P.muted), display: "block", marginBottom: 8 }}>{t("vertretung.reasonLabel", "Motif")}</span>
            <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
              {["urlaub", "krankheit"].map((r) => (
                <Pressable key={r} onClick={() => setVertretungForm({ ...vertretungForm, reason: r, endDateUnknown: false })} style={{
                  flex: 1, textAlign: "center", padding: "11px 0", borderRadius: 14, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: vertretungForm.reason === r ? A.navy : P.soft, color: vertretungForm.reason === r ? "#fff" : P.ink,
                }}>{r === "urlaub" ? t("vertretung.reasonUrlaub", "Congés") : t("vertretung.reasonKrankheit", "Maladie")}</Pressable>
              ))}
            </div>

            <span style={{ ...eyebrow(P.muted), display: "block", marginBottom: 8 }}>{t("vertretung.deputyLabel", "Qui te remplace")}</span>
            {vertretungPeers.length === 0 && <p style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 14px" }}>{t("vertretung.noPeersText", "Aucun autre compte du même niveau trouvé.")}</p>}
            <div className="nx-chips" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 18, paddingBottom: 2 }}>
              {vertretungPeers.map((p) => {
                const sel = vertretungForm.deputyEmployeeId === p.employeeId;
                return (
                  <Pressable key={p.employeeId} onClick={() => setVertretungForm({ ...vertretungForm, deputyEmployeeId: p.employeeId })} style={{
                    flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                    background: sel ? A.navy : P.soft, color: sel ? "#fff" : P.ink,
                  }}>{p.name}</Pressable>
                );
              })}
            </div>

            <Field label={t("vertretung.startDateLabel", "Date de début")}>
              <input style={dateTimeInputStyle} type="date" min={todayISO()} value={vertretungForm.startDate} onChange={(e) => setVertretungForm({ ...vertretungForm, startDate: e.target.value })} />
            </Field>

            {vertretungForm.reason === "krankheit" && (
              <Pressable onClick={() => setVertretungForm({ ...vertretungForm, endDateUnknown: !vertretungForm.endDateUnknown })} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "9px 13px", borderRadius: 99, background: P.soft, border: `1.5px solid ${P.line}`,
                fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, marginBottom: 14, width: "fit-content",
              }}>
                <span style={{ width: 30, height: 18, borderRadius: 99, background: vertretungForm.endDateUnknown ? A.green : P.line, position: "relative", flex: "0 0 auto" }}>
                  <span style={{ position: "absolute", top: 2, left: vertretungForm.endDateUnknown ? 14 : 2, width: 14, height: 14, borderRadius: 99, background: "#fff", transition: "left .15s ease" }} />
                </span>
                <span>{t("vertretung.endUnknownToggle", "Date de fin inconnue pour l'instant")}</span>
              </Pressable>
            )}

            {!vertretungForm.endDateUnknown && (
              <Field label={t("vertretung.endDateLabel", "Date de fin")}>
                <input style={dateTimeInputStyle} type="date" min={vertretungForm.startDate || todayISO()} value={vertretungForm.endDate} onChange={(e) => setVertretungForm({ ...vertretungForm, endDate: e.target.value })} />
              </Field>
            )}

            <Pressable disabled={vertretungBusy} onClick={submitVertretungRequest} style={{ width: "100%", ...btnSolid(A.navy), opacity: vertretungBusy ? .6 : 1, marginTop: 8 }}>
              {vertretungBusy ? t("common.sending", "Envoi...") : t("vertretung.sendBtn", "Envoyer pour validation")}
            </Pressable>
            <Pressable onClick={() => setVertretungScreen("hub")} style={{ width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 8 }}>
              {t("common.cancel", "Annuler")}
            </Pressable>
          </div>
        )}

        {screen === "flTeam" && (() => {
          // Même liste que FL_MANAGEABLE_ROLES côté serveur (server/teamManage/routes.js) —
          // purement informatif ici (le serveur revalide tout via sfCanApproveRole), jamais
          // la vraie barrière de sécurité.
          const FL_MANAGEABLE_ROLES = ["Employé", "Kassierer", "Kassiererin", "Azubi", "Aushilfe", "EQJ", "Stellvertreter", "Stellvertreterin", "Teamleiter", "FLA"];
          const manageableWorkers = flTeamWorkers.filter((w) => String(w.id) !== String(user?.id) && FL_MANAGEABLE_ROLES.includes(w.role));
          const tchip = (active) => ({
            flex: 1, textAlign: "center", padding: "10px 4px", borderRadius: 10, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
            background: active ? P.card : "none", color: active ? P.ink : P.muted, boxShadow: active ? P.shadow : "none",
          });
          const emptyPanel = (text) => (
            <div style={{ ...card, textAlign: "center", padding: 30, color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))` }}>{text}</div>
          );
          return (
            <div className="nx-screen-enter">
              <div style={{ display: "flex", gap: 5, background: P.soft, borderRadius: 13, padding: 4, marginBottom: 15 }}>
                <Pressable onClick={() => setFlTeamTab("pending")} style={tchip(flTeamTab === "pending")}>{t("flTeam.tab.pending", "Demandes")} · {adminPending.length}</Pressable>
                <Pressable onClick={() => setFlTeamTab("workers")} style={tchip(flTeamTab === "workers")}>{t("flTeam.tab.workers", "Équipe")} · {manageableWorkers.length}</Pressable>
                <Pressable onClick={() => setFlTeamTab("resets")} style={tchip(flTeamTab === "resets")}>{t("flTeam.tab.resets", "Mots de passe")} · {adminResetRequests.length}</Pressable>
              </div>

              {flTeamTab === "pending" && (
                adminPending.length === 0 ? emptyPanel(t("flTeam.pending.empty", "Aucune demande d'ouverture de compte en attente.")) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {adminPending.map((req) => (
                      <div key={req.id} style={{ ...card, padding: 14 }}>
                        <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block" }}>{req.professionalId}</strong>
                        <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{roleLabel(req.requestedRole)}</span>
                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                          <Pressable onClick={() => approveUser(req.id)} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: A.green, color: "#fff" }}>{t("flTeam.approveBtn", "✓ Autoriser")}</Pressable>
                          <Pressable onClick={() => rejectUser(req.id)} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: "rgba(239,68,68,.1)", color: A.red }}>{t("flTeam.rejectBtn", "✕ Refuser")}</Pressable>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}

              {flTeamTab === "workers" && (
                flTeamWorkersLoading ? emptyPanel(t("common.loading", "Chargement…")) :
                manageableWorkers.length === 0 ? emptyPanel(t("flTeam.workers.empty", "Aucun membre d'équipe pour l'instant.")) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {manageableWorkers.map((w) => {
                      const isDisabled = w.disabled || w.status === "disabled";
                      return (
                        <div key={w.id} style={{ ...card, display: "flex", alignItems: "center", gap: 10, padding: 13 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block", opacity: isDisabled ? .5 : 1 }}>{w.firstname} {w.lastname}</strong>
                            <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{roleLabel(w.role)}{isDisabled ? ` · ${t("flTeam.workers.disabledTag", "Désactivé")}` : ""}</span>
                          </div>
                          <Pressable onClick={() => openFlWorkerEdit(w)} aria-label={t("flTeam.editAria", "Modifier")} style={{ width: 32, height: 32, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))` }}>✎</Pressable>
                          <Pressable onClick={() => toggleFlWorkerStatus(w)} style={{ padding: "8px 12px", borderRadius: 99, fontWeight: 800, fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, background: isDisabled ? "rgba(30,184,95,.13)" : "rgba(239,68,68,.1)", color: isDisabled ? A.green : A.red }}>
                            {isDisabled ? t("flTeam.reactivateBtn", "Réactiver") : t("flTeam.disableBtn", "Désactiver")}
                          </Pressable>
                          <Pressable
                            onClick={() => { if (window.confirm(t("admin.confirmDeleteUserTemplate", { name: `${w.firstname} ${w.lastname}` }, `Supprimer définitivement ${w.firstname} ${w.lastname} ?`))) deleteFlWorker(w); }}
                            aria-label={t("flTeam.deleteAria", "Supprimer")}
                            style={{ width: 32, height: 32, borderRadius: 99, background: "rgba(239,68,68,.1)", color: A.red, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))` }}
                          >🗑️</Pressable>
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {flTeamTab === "resets" && (
                adminResetRequests.length === 0 ? emptyPanel(t("flTeam.resets.empty", "Aucune demande de réinitialisation en attente.")) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {adminResetRequests.map((req) => (
                      <div key={req.id} style={{ ...card, display: "flex", alignItems: "center", gap: 10, padding: 13 }}>
                        <strong style={{ flex: 1, fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{req.professionalId}</strong>
                        <Pressable onClick={() => generateResetCode(req)} style={{ padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: A.purple, color: "#fff" }}>
                          {t("flTeam.generateCodeBtn", "Générer un code")}
                        </Pressable>
                      </div>
                    ))}
                  </div>
                )
              )}

              <Sheet open={!!flWorkerEditForm} onClose={() => setFlWorkerEditForm(null)} P={P}>
                {flWorkerEditForm && (
                  <div>
                    <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 14 }}>{t("flTeam.editTitle", "Modifier le membre")}</strong>
                    <Field label={t("common.firstNameLabel", "Prénom")}>
                      <input style={inputStyle} value={flWorkerEditForm.firstname} onChange={(e) => setFlWorkerEditForm({ ...flWorkerEditForm, firstname: e.target.value })} />
                    </Field>
                    <Field label={t("common.lastNameLabel", "Nom")}>
                      <input style={inputStyle} value={flWorkerEditForm.lastname} onChange={(e) => setFlWorkerEditForm({ ...flWorkerEditForm, lastname: e.target.value })} />
                    </Field>
                    <Field label={t("common.phoneLabel", "Téléphone")}>
                      <input style={inputStyle} value={flWorkerEditForm.phone} onChange={(e) => setFlWorkerEditForm({ ...flWorkerEditForm, phone: e.target.value })} />
                    </Field>
                    <Field label={t("common.emailLabel", "E-mail")}>
                      <input style={inputStyle} value={flWorkerEditForm.email} onChange={(e) => setFlWorkerEditForm({ ...flWorkerEditForm, email: e.target.value })} />
                    </Field>
                    <Field label={t("flTeam.roleFieldLabel", "Rôle")}>
                      <select style={inputStyle} value={flWorkerEditForm.role} onChange={(e) => setFlWorkerEditForm({ ...flWorkerEditForm, role: e.target.value })}>
                        {FL_MANAGEABLE_ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                      </select>
                    </Field>
                    <Pressable disabled={flWorkerEditBusy} onClick={commitFlWorkerEdit} style={{ width: "100%", ...btnSolid(A.purple), opacity: flWorkerEditBusy ? .6 : 1, marginTop: 8 }}>
                      {flWorkerEditBusy ? t("common.saving", "Enregistrement…") : t("common.save", "Enregistrer")}
                    </Pressable>
                  </div>
                )}
              </Sheet>
            </div>
          );
        })()}

        {screen === "admin" && adminScreen === "users" && (() => {
          const q = adminUserSearch.trim().toLowerCase();
          const matchesSearch = (u) => !q || `${u?.firstname || ""} ${u?.lastname || ""} ${u?.role || ""}`.toLowerCase().includes(q);
          const pendingRows = adminPending.map((req) => ({ req, u: adminUsers.find((u) => String(u.id) === String(req.userId)) })).filter(({ req, u }) => matchesSearch(u) || String(req.professionalId || "").toLowerCase().includes(q));
          const resetRows = adminResetRequests.map((req) => ({ req, u: adminUsers.find((u) => String(u.id) === String(req.userId)) })).filter(({ req, u }) => matchesSearch(u) || String(req.professionalId || "").toLowerCase().includes(q));
          const others = adminUsers.filter((u) => u.approvalStatus !== "PENDING_APPROVAL" && matchesSearch(u))
            .filter((u) => adminUserStatusFilter === "all" || (adminUserStatusFilter === "active" ? (!u.disabled && u.status !== "disabled") : (u.disabled || u.status === "disabled")));
          // Responsables réseau (GR) — carte à part, jamais noyés dans la filiale de
          // rattachement (retour utilisateur explicite : "creer aussi une section pour
          // les GR avec leur poste chacun") : même liste de rôles que isNetworkLeader.
          const NETWORK_LEADER_ROLES = ["Bereichsleiter", "Verkaufsleiter", "Niederlassungsleiter", "NEXORA_ADMIN"];
          const leaders = others.filter((u) => NETWORK_LEADER_ROLES.includes(u.role));
          const regular = others.filter((u) => !NETWORK_LEADER_ROLES.includes(u.role));
          // Remonté ici (au lieu de rester local à la pyramide plus bas) — réutilisé aussi
          // par le regroupement des cartes filiale par BL, voir plus bas (retour utilisateur
          // explicite 2026-08-16 : "les filiales d'un VKL doivent toujours être regroupées
          // en fonction de ses BL").
          const nls = leaders.filter((u) => u.role === "Niederlassungsleiter");
          const vkls = leaders.filter((u) => u.role === "Verkaufsleiter");
          const bls = leaders.filter((u) => u.role === "Bereichsleiter");
          const storeLabelById = new Map(storesCatalog.map((s) => [s.id, s.label || s.id]));
          const groups = new Map();
          regular.forEach((u) => {
            const storeId = u.storeId || (u.stores || [])[0] || "—";
            if (!groups.has(storeId)) groups.set(storeId, []);
            groups.get(storeId).push(u);
          });
          const allStoreIds = Array.from(groups.keys()).sort((a, b) => a.localeCompare(b));
          // Le chip de filtre filiale ne s'affiche que s'il y a réellement plus d'une
          // filiale dans les données déjà scopées par le serveur — jamais pour un FL/FLA
          // mono-filiale (retour utilisateur explicite : ne jamais donner l'impression
          // qu'il pourrait voir d'autres filiales que la sienne).
          const showFilialeFilter = allStoreIds.length > 1;
          const filialeQ = adminUserFilialeSearch.trim().toLowerCase();
          const sortedStoreIds = (showFilialeFilter && adminUserFilialeFilter !== "all"
            ? allStoreIds.filter((id) => id === adminUserFilialeFilter)
            : allStoreIds
          ).filter((id) => !filialeQ || `${id} ${storeLabelById.get(id) || ""}`.toLowerCase().includes(filialeQ));
          // Refonte 2026-08-16 (retour utilisateur explicite, artefact validé "reproduis à
          // l'identique" — https://claude.ai/code/artifact/331ae9ed-a365-4104-bd05-47768c8c00d9) :
          // badges de section colorés (au lieu d'un emoji flottant dans un libellé uppercase),
          // états vides regroupés dans une vraie carte (panel) au lieu d'un simple paragraphe
          // hors carte, pyramide avec connecteurs d'arbre + avatars ronds (jamais de nom
          // tronqué), filtre filiale et filtre statut sur 2 lignes séparées. Logique de
          // données 100% inchangée — uniquement la présentation.
          const fchip = (active) => ({
            flex: "0 0 auto", padding: "7px 14px", borderRadius: 99, fontWeight: 700, fontSize: `calc(11px * var(--nx-font-scale, 1))`,
            background: active ? P.ink : P.card, color: active ? P.bg : P.muted, border: `1.5px solid ${active ? P.ink : P.line}`,
          });
          const panelStyle = { background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 20, overflow: "hidden" };
          const sectionHead = (emoji, accent, label, count) => (
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "22px 2px 11px" }}>
              <span style={{ width: 26, height: 26, borderRadius: 9, flexShrink: 0, background: `${accent}24`, color: accent, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{emoji}</span>
              <span style={{ flex: 1, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink }}>{label}</span>
              {count != null && <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, background: P.soft, padding: "3px 9px", borderRadius: 99 }}>{count}</span>}
            </div>
          );
          const emptyState = (text) => (
            <div style={{ ...panelStyle, display: "flex", alignItems: "center", gap: 10, padding: "16px 16px" }}>
              <span style={{ width: 22, height: 22, borderRadius: 99, flexShrink: 0, background: "rgba(30,184,95,.14)", color: A.green, display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`}}>✓</span>
              <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 600, color: P.muted }}>{text}</span>
            </div>
          );
          return (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 9, background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 15, padding: "0 14px", height: 46, marginBottom: 11 }}>
                <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted }}>🔎</span>
                <input value={adminUserSearch} onChange={(e) => setAdminUserSearch(e.target.value)}
                  placeholder={t("admin.searchPlaceholder", "Chercher un nom, un rôle...")}
                  style={{ border: "none", outline: "none", background: "none", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: P.ink, width: "100%" }} />
                {adminUserSearch && <Pressable onClick={() => setAdminUserSearch("")} aria-label={t("search.clearAria", "Effacer")} style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted }}>✕</Pressable>}
              </div>
              {showFilialeFilter && (
                <div className="nx-chips" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 8, paddingBottom: 2 }}>
                  <Pressable onClick={() => setAdminUserFilialeFilter("all")} style={fchip(adminUserFilialeFilter === "all")}>{t("admin.allStoresChip", "Toutes filiales")}</Pressable>
                  {allStoreIds.map((id) => (
                    <Pressable key={id} onClick={() => setAdminUserFilialeFilter(id)} style={fchip(adminUserFilialeFilter === id)}>{storeLabelById.get(id) || id}</Pressable>
                  ))}
                </div>
              )}
              <div className="nx-chips" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 8, paddingBottom: 2 }}>
                <Pressable onClick={() => setAdminUserStatusFilter(adminUserStatusFilter === "active" ? "all" : "active")} style={fchip(adminUserStatusFilter === "active")}>{t("admin.stat.activeUsers", "Actifs")}</Pressable>
                <Pressable onClick={() => setAdminUserStatusFilter(adminUserStatusFilter === "disabled" ? "all" : "disabled")} style={fchip(adminUserStatusFilter === "disabled")}>{t("admin.stat.disabledAccounts", "Désactivés")}</Pressable>
              </div>

              {sectionHead("📥", A.orange, t("admin.pendingApprovalTitle", "En attente d'ouverture de compte"), pendingRows.length)}
              {pendingRows.length === 0 && emptyState(t("admin.noPendingRequestsText", "Aucune demande en attente."))}
              {pendingRows.length > 0 && (
                <div style={panelStyle}>
                  {pendingRows.map(({ req, u }, reqIdx) => {
                    // Jamais de rôle présélectionné (retour utilisateur explicite 2026-08-16 :
                    // "il ne doit jamais avoir un rôle préselectionné par l'app... sinon il
                    // pourra attribuer le mauvais rôle... parce qu'il a oublié de choisir") —
                    // avant, `|| ROLE_HIERARCHY[0]` retombait silencieusement sur "Kassierer" si
                    // l'admin cliquait "Approuver" sans jamais toucher les puces de rôle. Le
                    // bouton reste désormais désactivé tant qu'aucun choix explicite n'a été fait.
                    const chosenRole = pendingApprovalRole[req.id] || null;
                    return (
                      <div key={req.id} style={{ padding: 15, borderTop: reqIdx > 0 ? `1px solid ${P.line}` : "none" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 12 }}>
                          <span style={{ width: 38, height: 38, borderRadius: 12, color: "#fff", border: "1px solid rgba(255,255,255,.16)", display: "grid", placeItems: "center", fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`, flexShrink: 0, ...premiumTileStyle(A.orange) }}>{(u?.firstname || req.professionalId || "?").charAt(0)}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{u ? `${u.firstname} ${u.lastname}` : req.professionalId}</strong>
                            <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 600 }}>{req.accountScope === "network" ? t("admin.networkTag", "Réseau") : t("admin.scopeStoreTemplate", { id: req.branchId }, `Filiale ${req.branchId}`)} · {fmtFR((req.createdAt || "").slice(0, 10))}</span>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 6, marginBottom: 11 }}>
                          {ROLE_HIERARCHY.map((r) => (
                            <Pressable key={r} onClick={() => setPendingApprovalRole((m) => ({ ...m, [req.id]: r }))} style={{
                              flex: "0 0 auto", padding: "6px 12px", borderRadius: 99, fontWeight: 700, fontSize: `calc(10.5px * var(--nx-font-scale, 1))`,
                              background: chosenRole === r ? A.orange : P.soft, color: chosenRole === r ? "#fff" : P.ink,
                              border: "1.5px solid transparent",
                            }}>{roleLabel(r)}</Pressable>
                          ))}
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <Pressable onClick={() => { if (chosenRole) approveUser(req.id, chosenRole); }} style={{
                            flex: 1, textAlign: "center", padding: "11px 0", borderRadius: 12, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                            background: chosenRole ? A.green : P.soft, color: chosenRole ? "#fff" : P.muted,
                            opacity: chosenRole ? 1 : 0.7,
                          }}>{chosenRole ? t("admin.approveAsBtnTemplate", { role: roleLabel(chosenRole) }, `✓ Approuver comme ${roleLabel(chosenRole)}`) : t("admin.pickRoleFirstBtn", "Choisis d'abord un rôle ci-dessus")}</Pressable>
                          <Pressable onClick={() => rejectUser(req.id)} style={{ flex: 1, textAlign: "center", padding: "11px 0", borderRadius: 12, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: P.card, color: A.red, border: `1.5px solid ${A.red}33` }}>{t("admin.rejectBtn", "Refuser")}</Pressable>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Demandes "mot de passe oublié" (retour utilisateur 2026-08-05 — voir
                  generateResetCode/loadAdminResetRequests). Un responsable ne génère JAMAIS
                  le mot de passe lui-même, seulement un code à usage unique qu'il communique
                  en personne — l'employé finalise seul depuis l'écran de connexion. */}
              {sectionHead("🔑", A.blue, t("admin.resetRequestsTitle", "Mot de passe oublié"), resetRows.length)}
              {resetRows.length === 0 && emptyState(t("admin.noResetRequestsText", "Aucune demande en attente."))}
              {resetRows.length > 0 && (
                <div style={panelStyle}>
                  {resetRows.map(({ req, u }, reqIdx) => (
                    <div key={req.id} style={{ display: "flex", alignItems: "center", gap: 11, padding: 14, borderTop: reqIdx > 0 ? `1px solid ${P.line}` : "none" }}>
                      <span style={{ width: 38, height: 38, borderRadius: 12, color: "#fff", border: "1px solid rgba(255,255,255,.16)", display: "grid", placeItems: "center", fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`, flexShrink: 0, ...premiumTileStyle(A.blue) }}>{(u?.firstname || req.professionalId || "?").charAt(0)}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{u ? `${u.firstname} ${u.lastname}` : req.professionalId}</strong>
                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 600 }}>{t("admin.scopeStoreTemplate", { id: req.branchId }, `Filiale ${req.branchId}`)} · {fmtFR((req.createdAt || "").slice(0, 10))}</span>
                      </div>
                      <Pressable onClick={() => generateResetCode(req)} style={{ flexShrink: 0, padding: "9px 14px", borderRadius: 12, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: A.blue, color: "#fff" }}>{t("admin.generateCodeBtn", "Générer un code")}</Pressable>
                    </div>
                  ))}
                </div>
              )}

              {/* Pyramide hiérarchique (retour utilisateur explicite : "les BL et leur
                  Filiales respectives, et toutes ces filiales appartiennent a quel vkl et a
                  quelle niderlassungleiter et tout en haut le geschaftfuhrer... toute la
                  pyramide"). Chaque palier se rattache au suivant via reportsTo. Rien
                  d'inventé : un palier sans personne réelle assignée l'affiche honnêtement
                  plutôt que de le cacher — le rôle de chacun est déjà visible inline (pas de
                  sous-titres de palier, comme l'artefact).
                  Règles 2026-08-16 (retour utilisateur explicite) : le compte NEXORA_ADMIN
                  n'apparaît JAMAIS ici (ni comme sommet, ni ailleurs — un admin technique
                  n'est pas un "responsable réseau" au sens métier) ; AUCUN responsable réseau
                  (NL/VKL/BL) n'est supprimable depuis cette pyramide, quel que soit le compte
                  qui regarde l'écran (admin ou un GR consultant sa propre chaîne) — la
                  suppression d'un GR reste possible ailleurs (fiche utilisateur), jamais ici
                  d'un geste anodin sur un maillon de la hiérarchie. */}
              {sectionHead("🌳", A.purple, t("admin.pyramidTitle", "Pyramide réseau"), null)}
              {(() => {
                // nls/vkls/bls calculés plus haut (réutilisés par le regroupement des cartes
                // filiale par BL ci-dessous).
                // personRow — refonte 2026-08-16 : avatar rond (initiales, accent du palier)
                // + vrai connecteur d'arbre (segment vertical + coude horizontal, jamais un
                // simple point) + nom/rôle qui peuvent revenir à la ligne (plus jamais tronqués
                // au milieu, voir le bug "Lea..." signalé sur la capture d'écran). Bouton
                // supprimer réservé à NEXORA_ADMIN (retour utilisateur explicite : "pour le
                // compte admin lui peut avoir le droit de supprimer n'importe qui, tu ne peux
                // pas lui retirer cette autorisation") — jamais visible pour un GR consultant
                // sa propre chaîne au-dessus de lui.
                const personRow = (u, depth, accent, isRoot) => {
                  const scope = u.role === "Bereichsleiter"
                    ? (u.stores && u.stores.length ? u.stores : [u.storeId]).filter(Boolean).map((id) => storeLabelById.get(id) || id).join(", ")
                    : "";
                  const initials = `${(u.firstname || "?").charAt(0)}${(u.lastname || "").charAt(0)}`.toUpperCase();
                  const avatarSize = isRoot ? 30 : 28;
                  return (
                    <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, position: "relative", padding: "8px 0", marginLeft: depth * 22 }}>
                      {!isRoot && (
                        <>
                          <span style={{ position: "absolute", left: -avatarSize / 2 - 1, top: -8, bottom: "50%", width: 1.5, background: P.line }} />
                          <span style={{ position: "absolute", left: -avatarSize / 2 - 1, top: "50%", width: 10, height: 1.5, background: P.line }} />
                        </>
                      )}
                      <span style={{
                        width: avatarSize, height: avatarSize, borderRadius: 99, flexShrink: 0, background: accent, color: "#fff",
                        display: "grid", placeItems: "center", fontSize: `calc(${(isRoot ? 11 : 10)}px * var(--nx-font-scale, 1))`, fontWeight: 800,
                        border: `2px solid ${P.card}`, boxShadow: `0 0 0 1.5px ${accent}59`,
                      }}>{initials}</span>
                      <Pressable onClick={() => openUserEdit(u)} style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, lineHeight: 1.3 }}>{u.firstname} {u.lastname}</span>
                        <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700, lineHeight: 1.4, wordBreak: "break-word" }}>{u.role}{scope ? ` · ${scope}` : ""}</span>
                      </Pressable>
                      {isNexoraAdmin && (
                        <>
                          <Pressable onClick={() => openReplaceLeader(u)}
                            aria-label={t("admin.replaceLeaderBtnAria", "Remplacer ce responsable")} style={{
                              width: 22, height: 22, borderRadius: 99, background: "transparent", color: P.muted, flexShrink: 0,
                              display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, marginLeft: "auto",
                            }}>🔁</Pressable>
                          <Pressable onClick={() => { if (window.confirm(t("admin.confirmDeleteUserTemplate", { name: `${u.firstname} ${u.lastname}` }, `Supprimer définitivement ${u.firstname} ${u.lastname} ?`))) deleteUserPermanently(u.id); }}
                            aria-label={t("claims.action.remove", "Retirer")} style={{
                              width: 22, height: 22, borderRadius: 99, background: "transparent", color: P.muted, flexShrink: 0,
                              display: "grid", placeItems: "center", fontSize: `calc(10px * var(--nx-font-scale, 1))`,
                            }}>✕</Pressable>
                        </>
                      )}
                    </div>
                  );
                };
                const unassigned = (list, label) => list.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, background: P.soft, borderRadius: 12, padding: "9px 12px", margin: "4px 0 8px" }}>
                    <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`}}>⚠️</span>
                    <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("admin.unassignedTemplate", { n: list.length, role: label }, `${list.length} ${label} sans rattachement.`)}</span>
                  </div>
                );
                const empty = nls.length === 0 && vkls.length === 0 && bls.length === 0;
                return (
                  <div style={{ ...panelStyle, borderRadius: 20, padding: 16, marginBottom: 4 }}>
                    {empty ? (
                      <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("admin.noNetworkLeadersText", "Aucun responsable réseau.")}</p>
                    ) : (
                      <>
                        {nls.map((nl, nlIdx) => (
                          <div key={nl.id}>
                            {personRow(nl, 0, A.red, nlIdx === 0)}
                            {vkls.filter((v) => v.reportsTo === nl.employeeId).map((vkl) => (
                              <div key={vkl.id}>
                                {personRow(vkl, 1, A.orange, false)}
                                {bls.filter((b) => b.reportsTo === vkl.employeeId).map((bl) => personRow(bl, 2, A.purple, false))}
                              </div>
                            ))}
                          </div>
                        ))}
                        {unassigned(vkls.filter((v) => !v.reportsTo || !nls.some((nl) => nl.employeeId === v.reportsTo)), "Verkaufsleiter")}
                        {vkls.filter((v) => !v.reportsTo || !nls.some((nl) => nl.employeeId === v.reportsTo)).map((vkl, i) => (
                          <div key={vkl.id}>
                            {personRow(vkl, 0, A.orange, nls.length === 0 && i === 0)}
                            {bls.filter((b) => b.reportsTo === vkl.employeeId).map((bl) => personRow(bl, 1, A.purple, false))}
                          </div>
                        ))}
                        {unassigned(bls.filter((b) => !b.reportsTo || !vkls.some((v) => v.employeeId === b.reportsTo)), "Bereichsleiter")}
                        {bls.filter((b) => !b.reportsTo || !vkls.some((v) => v.employeeId === b.reportsTo)).map((bl, i) => personRow(bl, 0, A.purple, nls.length === 0 && vkls.length === 0 && i === 0))}
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Une carte par filiale — jamais une liste mélangée (retour utilisateur
                  explicite : "eviter tout melange"). Le serveur (GET /api/users) ne renvoie
                  déjà que les filiales du périmètre de l'appelant (admin = toutes, GR = son
                  périmètre, employé = la sienne). Refonte 2026-08-16 : carte dépliable au clic
                  (adminUsersExpandedStores, tout replié par défaut) + recherche dédiée
                  (adminUserFilialeSearch) — jamais une simple rangée de puces à l'échelle
                  réseau (1200+ filiales visées, voir storeflow_nexora_multi_store_scale).
                  Bug réel corrigé (2026-08-16) : ce champ de recherche était gated sur
                  showFilialeFilter (>1 filiale) — un environnement de dev n'ayant des vrais
                  employés que sur UNE seule filiale (ex. N236) le faisait disparaître
                  entièrement, alors qu'un champ de recherche reste utile/anodin dès qu'il y a
                  au moins une filiale, jamais un gate ">1" comme les puces. */}
              {allStoreIds.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 9, background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 15, padding: "0 14px", height: 42, margin: "22px 0 9px" }}>
                  <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>🏬</span>
                  <input value={adminUserFilialeSearch} onChange={(e) => setAdminUserFilialeSearch(e.target.value)}
                    placeholder={t("admin.searchFilialePlaceholder", "Chercher une filiale...")}
                    style={{ border: "none", outline: "none", background: "none", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.ink, width: "100%" }} />
                  {adminUserFilialeSearch && <Pressable onClick={() => setAdminUserFilialeSearch("")} aria-label={t("search.clearAria", "Effacer")} style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>✕</Pressable>}
                </div>
              )}
              {sortedStoreIds.length === 0 && pendingRows.length === 0 && leaders.length === 0 && (
                <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "16px 2px 0" }}>{t("admin.noAccountsText", "Aucun compte.")}</p>
              )}
              {allStoreIds.length > 0 && sortedStoreIds.length === 0 && (
                <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "16px 2px 0" }}>{t("admin.noFilialeMatchText", "Aucune filiale ne correspond à cette recherche.")}</p>
              )}
              {(() => {
                // Regroupement des cartes filiale par BL (retour utilisateur explicite
                // 2026-08-16 : "sur la vue Admin toujours les filiales d'un VKL doivent
                // toujours être regroupées en fonctions de ses BL") — une filiale sans BL
                // propriétaire (cas courant, ex. une seule filiale gérée directement par un
                // FL) reste affichée telle quelle, sans en-tête de groupe superflu.
                const blOwnerForStore = (storeId) => bls.find((b) => (b.stores && b.stores.length ? b.stores : [b.storeId]).includes(storeId));
                const blGroupsMap = new Map();
                const unassignedIds = [];
                sortedStoreIds.forEach((storeId) => {
                  const owner = blOwnerForStore(storeId);
                  if (!owner) { unassignedIds.push(storeId); return; }
                  if (!blGroupsMap.has(owner.id)) blGroupsMap.set(owner.id, { bl: owner, storeIds: [] });
                  blGroupsMap.get(owner.id).storeIds.push(storeId);
                });
                const blGroups = Array.from(blGroupsMap.values())
                  .sort((a, b) => `${a.bl.firstname}${a.bl.lastname}`.localeCompare(`${b.bl.firstname}${b.bl.lastname}`));

                const storeCard = (storeId) => {
                  const isExpanded = adminUsersExpandedStores.has(storeId);
                  return (
                    <div key={storeId} style={{ ...panelStyle, padding: 0, marginBottom: 9 }}>
                      <Pressable onClick={() => toggleAdminStoreExpanded(storeId)} style={{
                        display: "flex", alignItems: "center", gap: 9, width: "100%", boxSizing: "border-box",
                        padding: "13px 16px", background: `linear-gradient(135deg, ${A.blue}1a, ${A.blue}08)`,
                      }}>
                        <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: A.blue }}>🏬 {storeLabelById.get(storeId) || storeId}</strong>
                        <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700, marginLeft: "auto" }}>{t("admin.accountCountTemplate", { n: groups.get(storeId).length }, `${groups.get(storeId).length} compte${groups.get(storeId).length > 1 ? "s" : ""}`)}</span>
                        <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: A.blue, transition: "transform .18s ease", transform: isExpanded ? "rotate(180deg)" : "none" }}>▾</span>
                      </Pressable>
                      {isExpanded && groups.get(storeId).map((u, uIdx) => {
                        const isActive = !u.disabled && u.status !== "disabled";
                        return (
                          // Pas de SwipeToDelete ici (position:relative + transform/transition
                          // permanents sur CHAQUE ligne) : même famille de bug qu'un vrai crash
                          // WebKit déjà résolu sur Planning (explosion de calques de composition
                          // GPU sur iOS/PWA installée, voir storeflow_plan_de_travail_crash_local_storage) —
                          // en plus de casser le clic imbriqué (setPointerCapture, voir
                          // storeflow_admin_module_real_data). Suppression = bouton explicite +
                          // confirmation, comme le Plan de travail a été reconstruit sans geste
                          // tactile personnalisé.
                          <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderTop: uIdx > 0 ? `1px solid ${P.line}` : "none" }}>
                            <Pressable onClick={() => openUserEdit(u)} style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                              <span style={{ width: 34, height: 34, borderRadius: 99, background: EMP_COLORS[u.firstname] || A.navy, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, flexShrink: 0 }}>{(u.firstname || "?").charAt(0)}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{u.firstname} {u.lastname}</strong>
                                <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>{u.role}</span>
                              </div>
                            </Pressable>
                            <Pressable onClick={() => toggleUserStatus(u)} style={{
                              fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 9px", flexShrink: 0,
                              color: isActive ? A.green : A.red,
                              background: isActive ? `${A.green}22` : `${A.red}22`,
                            }}>{isActive ? t("admin.activeStatus", "Actif") : t("admin.disabledStatus", "Désactivé")}</Pressable>
                            <Pressable onClick={() => { if (window.confirm(t("admin.confirmDeleteUserTemplate", { name: `${u.firstname} ${u.lastname}` }, `Supprimer définitivement ${u.firstname} ${u.lastname} ?`))) deleteUserPermanently(u.id); }}
                              aria-label={t("common.delete", "Supprimer")} style={{
                                width: 26, height: 26, borderRadius: 99, background: "transparent", color: P.muted, flexShrink: 0,
                                display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                              }}>✕</Pressable>
                          </div>
                        );
                      })}
                    </div>
                  );
                };

                return (
                  <>
                    {blGroups.map(({ bl, storeIds }) => {
                      const vklOwner = vkls.find((v) => v.employeeId === bl.reportsTo);
                      return (
                        <div key={bl.id} style={{ marginBottom: 16 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 2px 8px" }}>
                            <span style={{ width: 20, height: 20, borderRadius: 99, background: A.purple, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, flexShrink: 0 }}>{(bl.firstname || "?").charAt(0)}{(bl.lastname || "").charAt(0)}</span>
                            <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink }}>{t("admin.blGroupTitleTemplate", { name: `${bl.firstname} ${bl.lastname}` }, `Filiales de ${bl.firstname} ${bl.lastname}`)}</span>
                            {vklOwner && <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>· {t("admin.reportsToVklTemplate", { name: `${vklOwner.firstname} ${vklOwner.lastname}` }, `sous ${vklOwner.firstname} ${vklOwner.lastname}`)}</span>}
                            <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, background: P.soft, padding: "3px 8px", borderRadius: 99, marginLeft: "auto", flexShrink: 0 }}>{storeIds.length}</span>
                          </div>
                          {storeIds.map(storeCard)}
                        </div>
                      );
                    })}
                    {unassignedIds.map(storeCard)}
                  </>
                );
              })()}
            </>
          );
        })()}

        {/* Refonte 2026-08-11 (retour utilisateur explicite, artefact validé point par
            point) : anneau de couverture (X/90 réel, aucune donnée inventée — juste compté
            depuis PERMISSION_GROUPS/rolePerms déjà là), recherche dans les ~90 permissions,
            et de vrais interrupteurs visuels à la place des cases ☑️/⬜️ emoji. */}
        {screen === "admin" && adminScreen === "roles" && (() => {
          const allPermCount = PERMISSION_GROUPS.reduce((s, g) => s + g.permissions.length, 0);
          const activePermCount = (rolePerms[activeRole] || []).length;
          const coveragePct = allPermCount > 0 ? Math.round((activePermCount / allPermCount) * 100) : 0;
          const pq = adminPermSearch.trim().toLowerCase();
          const visibleGroups = !pq ? PERMISSION_GROUPS : PERMISSION_GROUPS.map((g) => {
            const groupLabel = t(`permGroup.${g.id}`, g.label);
            if (groupLabel.toLowerCase().includes(pq)) return g;
            const filtered = g.permissions.filter((p) => t(`perm.${p.id}`, p.label).toLowerCase().includes(pq));
            return filtered.length ? { ...g, permissions: filtered } : null;
          }).filter(Boolean);
          return (
          <>
            {/* "Par utilisateur" — cacher/afficher un module pour UNE personne précise
                (distinct des permissions par rôle ci-dessous). Onglet admin-only : jamais
                visible pour un compte qui n'est pas NEXORA_ADMIN, même avec "roles.edit"
                (retour utilisateur explicite : "jamais pour les autres quelque soit la
                fonction" — roles.edit/users.edit sont des permissions assignables à
                n'importe quel rôle, pas un vrai gate admin, voir isModuleHiddenForUser). */}
            {(isNexoraAdmin || isAdminModuleOverride) && (
              <div className="nx-chips" style={{ display: "flex", gap: 6, marginBottom: 14 }}>
                <Pressable onClick={() => setRolesScreenMode("role")} style={{
                  flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  background: rolesScreenMode === "role" ? A.purple : P.soft, color: rolesScreenMode === "role" ? "#fff" : P.ink,
                }}>{t("admin.roles.modeByRole", "Par rôle")}</Pressable>
                <Pressable onClick={() => setRolesScreenMode("user")} style={{
                  flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  background: rolesScreenMode === "user" ? A.purple : P.soft, color: rolesScreenMode === "user" ? "#fff" : P.ink,
                }}>{t("admin.roles.modeByUser", "Par utilisateur")}</Pressable>
                {/* "Modules par rôle" (2026-08-15) — distinct du chip "Par rôle" ci-dessus
                    (celui-là = permissions ordinaires PERMISSION_GROUPS, assignables à
                    n'importe qui) : ici, même gate admin-only que "Par utilisateur", mais
                    s'applique à TOUS les comptes d'un rôle, y compris dès leur toute
                    première connexion — comble le trou identifié par l'utilisatrice
                    ("une personne inexistante dans l'app... je ne pourrai cacher les
                    modules qu'après coup"). Fusionné avec "Par utilisateur" côté serveur
                    uniquement (normalizeServerUser), jamais recalculé ici. */}
                <Pressable onClick={() => setRolesScreenMode("roleModules")} style={{
                  flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  background: rolesScreenMode === "roleModules" ? A.purple : P.soft, color: rolesScreenMode === "roleModules" ? "#fff" : P.ink,
                }}>{t("admin.roles.modeByRoleModules", "Modules par rôle")}</Pressable>
                {/* "Mis en avant" (2026-11-02, retour utilisateur explicite : "un favoris
                    pour tout les utilisateurs groupée") — UNE SEULE liste pour tous les
                    comptes normaux (jamais l'écran GR), voir highlightedModuleIds plus haut
                    et server/homeHighlights/routes.js. Même gate admin-only que les deux
                    chips précédents. */}
                <Pressable onClick={() => setRolesScreenMode("highlighted")} style={{
                  flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  background: rolesScreenMode === "highlighted" ? A.purple : P.soft, color: rolesScreenMode === "highlighted" ? "#fff" : P.ink,
                }}>{t("admin.roles.modeByHighlighted", "Mis en avant")}</Pressable>
              </div>
            )}
            {rolesScreenMode === "user" && (isNexoraAdmin || isAdminModuleOverride) ? (() => {
              const uq = hiddenModulesUserSearch.trim().toLowerCase();
              const matchingUsers = !uq ? adminUsers : adminUsers.filter((u) =>
                `${u.firstname} ${u.lastname} ${u.employeeId}`.toLowerCase().includes(uq));
              const targetUser = adminUsers.find((u) => String(u.id) === String(hiddenModulesTargetUserId));
              const allModules = [...HOME_MODULES, ...NETWORK_HOME_MODULES.filter((nm) => !HOME_MODULES.some((hm) => hm.id === nm.id))];
              return (
                <>
                  {!targetUser ? (
                    <>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 14, padding: "0 12px", height: 40, marginBottom: 14 }}>
                        <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>🔎</span>
                        <input value={hiddenModulesUserSearch} onChange={(e) => setHiddenModulesUserSearch(e.target.value)}
                          placeholder={t("admin.roles.searchUserPlaceholder", "Chercher un utilisateur...")}
                          style={{ border: "none", outline: "none", background: "none", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.ink, width: "100%" }} />
                      </div>
                      {matchingUsers.length === 0 && (
                        <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("admin.roles.noUserMatchText", "Aucun utilisateur ne correspond à cette recherche.")}</p>
                      )}
                      {matchingUsers.map((u) => (
                        <Pressable key={u.id} onClick={() => setHiddenModulesTargetUserId(u.id)} style={{
                          display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                          padding: "11px 13px", background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 14, marginBottom: 8,
                        }}>
                          <span style={{ width: 32, height: 32, borderRadius: 99, color: "#fff", border: "1px solid rgba(255,255,255,.16)", display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, flex: "0 0 auto", ...premiumTileStyle(A.navy) }}>{(u.firstname?.[0] || "") + (u.lastname?.[0] || "")}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{u.firstname} {u.lastname}</strong>
                            <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{roleLabel(u.role)} · {u.employeeId}</span>
                          </div>
                          {u.personalHiddenModules?.length > 0 && (
                            <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.red, background: "rgba(239,68,68,.12)", borderRadius: 99, padding: "3px 9px" }}>{t("admin.roles.hiddenCountTemplate", { n: u.personalHiddenModules.length }, `${u.personalHiddenModules.length} caché(s)`)}</span>
                          )}
                        </Pressable>
                      ))}
                    </>
                  ) : (
                    <>
                      <Pressable onClick={() => setHiddenModulesTargetUserId(null)} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.purple }}>
                        ‹ {t("admin.roles.backToUserListBtn", "Retour à la liste")}
                      </Pressable>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 16, padding: 14, marginBottom: 15 }}>
                        <span style={{ width: 40, height: 40, borderRadius: 99, color: "#fff", border: "1px solid rgba(255,255,255,.16)", display: "grid", placeItems: "center", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, fontWeight: 800, flex: "0 0 auto", ...premiumTileStyle(A.navy) }}>{(targetUser.firstname?.[0] || "") + (targetUser.lastname?.[0] || "")}</span>
                        <div style={{ minWidth: 0 }}>
                          <strong style={{ display: "block", fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{targetUser.firstname} {targetUser.lastname}</strong>
                          <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{roleLabel(targetUser.role)} · {targetUser.employeeId}</span>
                        </div>
                      </div>
                      <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 12px", lineHeight: 1.5 }}>
                        {t("admin.roles.hiddenModulesHint", "Un module désactivé ici disparaît complètement de l'Accueil de cette personne, quels que soient ses rôles/permissions.")}
                      </p>
                      {allModules.map((m) => {
                        // hiddenByRole = caché via "Modules par rôle" (roleHiddenModules,
                        // déjà chargé) — bug réel corrigé le 2026-08-16 : cet écran affichait
                        // et permettait de "décocher" un module caché par le rôle, sans
                        // aucun effet réel (le PATCH n'écrit que le champ personnel). Le
                        // toggle est désormais verrouillé/expliqué dans ce cas précis,
                        // jamais un interrupteur qui semble fonctionner sans rien changer.
                        const hiddenByRole = (roleHiddenModules[targetUser.role] || []).includes(m.id);
                        const hiddenPersonal = (targetUser.personalHiddenModules || []).includes(m.id);
                        const hidden = hiddenPersonal || hiddenByRole;
                        return (
                          <Pressable key={m.id} onClick={() => { if (!hiddenByRole) toggleHiddenModuleForUser(targetUser, m.id); }} style={{
                            display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                            padding: "10px 13px", background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 12, marginBottom: 6,
                            opacity: hiddenByRole ? 0.65 : 1,
                          }}>
                            <span style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}><EmojiIcon e={m.emoji} size={16} /></span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: hidden ? P.muted : P.ink, textDecoration: hidden ? "line-through" : "none" }}>{t(HOME_MODULES.some((hm) => hm.id === m.id) ? `home.module.${m.id}` : `home.netModule.${m.id}`, m.label)}</span>
                              {hiddenByRole && (
                                <span style={{ display: "block", fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: A.purple, fontWeight: 700, marginTop: 1 }}>
                                  {t("admin.roles.hiddenByRoleNoteTemplate", { role: roleLabel(targetUser.role) }, `Caché par le rôle ${roleLabel(targetUser.role)} — voir "Modules par rôle"`)}
                                </span>
                              )}
                            </div>
                            <span style={{
                              width: 34, height: 20, borderRadius: 99, flex: "0 0 auto", position: "relative",
                              background: !hidden ? A.purple : P.soft, border: !hidden ? "none" : `1.5px solid ${P.line}`,
                              transition: "background .15s ease",
                            }}>
                              <span style={{
                                position: "absolute", top: 2, width: 14, height: 14, borderRadius: 99, background: !hidden ? "#fff" : P.muted,
                                boxShadow: "0 1px 3px rgba(0,0,0,.25)", left: !hidden ? 18 : 2, transition: "left .15s ease",
                              }} />
                            </span>
                          </Pressable>
                        );
                      })}
                    </>
                  )}
                </>
              );
            })() : rolesScreenMode === "roleModules" && (isNexoraAdmin || isAdminModuleOverride) ? (() => {
              const targetRole = hiddenModulesTargetRole;
              const allModules = [...HOME_MODULES, ...NETWORK_HOME_MODULES.filter((nm) => !HOME_MODULES.some((hm) => hm.id === nm.id))];
              return (
                <>
                  {!targetRole ? (
                    <>
                      <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 12px", lineHeight: 1.5 }}>
                        {t("admin.roles.roleModulesIntro", "S'applique à TOUS les comptes de ce rôle, y compris dès leur toute première connexion — se cumule avec les restrictions par utilisateur.")}
                      </p>
                      {ROLE_HIERARCHY.map((r) => (
                        <Pressable key={r} onClick={() => setHiddenModulesTargetRole(r)} style={{
                          display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                          padding: "11px 13px", background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 14, marginBottom: 8,
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{roleLabel(r)}</strong>
                          </div>
                          {(roleHiddenModules[r] || []).length > 0 && (
                            <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.red, background: "rgba(239,68,68,.12)", borderRadius: 99, padding: "3px 9px" }}>{t("admin.roles.hiddenCountTemplate", { n: roleHiddenModules[r].length }, `${roleHiddenModules[r].length} caché(s)`)}</span>
                          )}
                        </Pressable>
                      ))}
                    </>
                  ) : (
                    <>
                      <Pressable onClick={() => setHiddenModulesTargetRole(null)} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.purple }}>
                        ‹ {t("admin.roles.backToRoleListBtn", "Retour à la liste des rôles")}
                      </Pressable>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 16, padding: 14, marginBottom: 15 }}>
                        <strong style={{ display: "block", fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{roleLabel(targetRole)}</strong>
                      </div>
                      <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 12px", lineHeight: 1.5 }}>
                        {t("admin.roles.roleModulesHint", "S'applique à TOUS les comptes de ce rôle, y compris dès leur toute première connexion — se cumule avec les modules cachés par utilisateur.")}
                      </p>
                      {allModules.map((m) => {
                        const hidden = (roleHiddenModules[targetRole] || []).includes(m.id);
                        return (
                          <Pressable key={m.id} onClick={() => toggleHiddenModuleForRole(targetRole, m.id)} style={{
                            display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                            padding: "10px 13px", background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 12, marginBottom: 6,
                          }}>
                            <span style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}><EmojiIcon e={m.emoji} size={16} /></span>
                            <span style={{ flex: 1, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: hidden ? P.muted : P.ink, textDecoration: hidden ? "line-through" : "none" }}>{t(HOME_MODULES.some((hm) => hm.id === m.id) ? `home.module.${m.id}` : `home.netModule.${m.id}`, m.label)}</span>
                            <span style={{
                              width: 34, height: 20, borderRadius: 99, flex: "0 0 auto", position: "relative",
                              background: !hidden ? A.purple : P.soft, border: !hidden ? "none" : `1.5px solid ${P.line}`,
                              transition: "background .15s ease",
                            }}>
                              <span style={{
                                position: "absolute", top: 2, width: 14, height: 14, borderRadius: 99, background: !hidden ? "#fff" : P.muted,
                                boxShadow: "0 1px 3px rgba(0,0,0,.25)", left: !hidden ? 18 : 2, transition: "left .15s ease",
                              }} />
                            </span>
                          </Pressable>
                        );
                      })}
                    </>
                  )}
                </>
              );
            })() : rolesScreenMode === "highlighted" && (isNexoraAdmin || isAdminModuleOverride) ? (() => {
              return (
                <>
                  <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 12px", lineHeight: 1.5 }}>
                    {t("admin.roles.highlightedIntro", "Réglage de repli réseau — s'applique à toute filiale qui n'a pas encore choisi sa propre liste (FL/FLA/Stellvertreter·in peuvent la personnaliser depuis leur Accueil). Jamais l'écran GR. Le reste des modules reste accessible, simplement replié sous \"Tous les modules\".")}
                  </p>
                  {HOME_MODULES.map((m) => {
                    const on = highlightedModuleIds.includes(m.id);
                    return (
                      <Pressable key={m.id} onClick={() => toggleHighlightedModule(m.id)} style={{
                        display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                        padding: "10px 13px", background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 12, marginBottom: 6,
                      }}>
                        <span style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}><EmojiIcon e={m.emoji} size={16} /></span>
                        <span style={{ flex: 1, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink }}>{t(`home.module.${m.id}`, m.label)}</span>
                        <span style={{
                          width: 34, height: 20, borderRadius: 99, flex: "0 0 auto", position: "relative",
                          background: on ? A.purple : P.soft, border: on ? "none" : `1.5px solid ${P.line}`,
                          transition: "background .15s ease",
                        }}>
                          <span style={{
                            position: "absolute", top: 2, width: 14, height: 14, borderRadius: 99, background: on ? "#fff" : P.muted,
                            boxShadow: "0 1px 3px rgba(0,0,0,.25)", left: on ? 18 : 2, transition: "left .15s ease",
                          }} />
                        </span>
                      </Pressable>
                    );
                  })}
                </>
              );
            })() : (
            <>
            {!canManageRoles && (
              <div style={{ borderRadius: 14, padding: 12, marginBottom: 14, background: "rgba(231,138,18,.1)", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>
                {t("admin.roles.readOnlyBanner", "Lecture seule — la modification des permissions nécessite le droit \"roles.edit\".")}
              </div>
            )}
            <div className="nx-chips" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
              {ROLE_HIERARCHY.map((r) => (
                <Pressable key={r} onClick={() => setActiveRole(r)} style={{
                  flex: "0 0 auto", padding: "9px 15px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  background: activeRole === r ? A.purple : P.card, color: activeRole === r ? "#fff" : P.muted,
                  border: `1.5px solid ${activeRole === r ? A.purple : P.line}`,
                }}>{roleLabel(r)}</Pressable>
              ))}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 14, background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 18, padding: 15, marginBottom: 15 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 999, flex: "0 0 auto", display: "grid", placeItems: "center",
                background: `conic-gradient(${A.purple} ${coveragePct}%, ${P.soft} 0)`,
              }}>
                <div style={{ width: 44, height: 44, borderRadius: 999, background: P.card, display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800 }}>{coveragePct}%</div>
              </div>
              <div style={{ minWidth: 0 }}>
                <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{t("admin.roles.coverageTemplate", { n: activePermCount }, `${activePermCount} permissions activées`)}</strong>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("admin.roles.coverageOfTemplate", { total: allPermCount, groups: PERMISSION_GROUPS.length }, `sur ${allPermCount} réparties en ${PERMISSION_GROUPS.length} modules`)}</span>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 14, padding: "0 12px", height: 40, marginBottom: 14 }}>
              <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>🔎</span>
              <input value={adminPermSearch} onChange={(e) => setAdminPermSearch(e.target.value)}
                placeholder={t("admin.roles.searchPermissionsPlaceholder", "Chercher une permission...")}
                style={{ border: "none", outline: "none", background: "none", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.ink, width: "100%" }} />
              {adminPermSearch && <Pressable onClick={() => setAdminPermSearch("")} aria-label={t("search.clearAria", "Effacer")} style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>✕</Pressable>}
            </div>

            {visibleGroups.length === 0 && (
              <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("admin.roles.noPermissionMatchText", "Aucune permission ne correspond à cette recherche.")}</p>
            )}
            {visibleGroups.map((g) => {
              const icon = PERMISSION_GROUP_ICON[g.id] || { emoji: "🔧", color: "navy" };
              const iconColor = A[icon.color] || A.navy;
              const activeInGroup = g.permissions.filter((p) => (rolePerms[activeRole] || []).includes(p.id)).length;
              return (
                <section key={g.id} style={{ background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 16, marginBottom: 8, overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "12px 13px" }}>
                    <span style={{ width: 28, height: 28, borderRadius: 9, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, flexShrink: 0, background: `${iconColor}22`, color: iconColor }}><EmojiIcon e={icon.emoji} size={13} /></span>
                    <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, flex: 1 }}>{t(`permGroup.${g.id}`, g.label)}</strong>
                    <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{activeInGroup}/{g.permissions.length}</span>
                  </div>
                  {g.permissions.map((p) => {
                    const checked = (rolePerms[activeRole] || []).includes(p.id);
                    return (
                      <Pressable key={p.id} onClick={() => canManageRoles && togglePermission(activeRole, p.id)} style={{
                        display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                        padding: "9px 13px 9px 50px", borderTop: `1px solid ${P.line}`, opacity: canManageRoles ? 1 : .6,
                      }}>
                        <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, flex: 1 }}>{t(`perm.${p.id}`, p.label)}</span>
                        <span style={{
                          width: 34, height: 20, borderRadius: 99, flex: "0 0 auto", position: "relative",
                          background: checked ? A.purple : P.soft, border: checked ? "none" : `1.5px solid ${P.line}`,
                          transition: "background .15s ease",
                        }}>
                          <span style={{
                            position: "absolute", top: 2, width: 14, height: 14, borderRadius: 99, background: checked ? "#fff" : P.muted,
                            boxShadow: "0 1px 3px rgba(0,0,0,.25)", left: checked ? 18 : 2, transition: "left .15s ease",
                          }} />
                        </span>
                      </Pressable>
                    );
                  })}
                </section>
              );
            })}
            </>
            )}
          </>
          );
        })()}

        {/* ================= ARTIKELFINDER ================= */}
        {screen === "finder" && finderScreen === "hub" && (
          <>
            <input ref={finderRecognizeInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
              onChange={(e) => { handleFinderRecognizePhoto(e.target.files?.[0]); e.target.value = ""; }} />
            <section style={{ ...colorCard(A.purple), ...tile, flexDirection: "column", alignItems: "stretch", padding: 20 }}>
              <span style={onColorTag}>{t("finder.eyebrow", "Reconnaissance photo")}</span>
              <h2 style={{ margin: "5px 0 6px", fontSize: `calc(18px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{t("finder.heroTitle", "Trouve n'importe quel article")}</h2>
              <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, lineHeight: 1.45, ...onColorSub }}>{t("finder.heroSubtitle", "Prends une photo ou cherche par nom — la fiche donne le prix et la catégorie.")}</p>
            </section>
            <Pressable onClick={runPhotoRecognition} style={{
              width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 9,
              padding: "16px 0", borderRadius: 99, background: themeId === "sombre" ? "#2a2a2e" : A.navy, color: "#fff",
              fontWeight: 800, fontSize: `calc(15px * var(--nx-font-scale, 1))`, marginBottom: 14, boxShadow: "0 14px 30px rgba(13,23,48,.35)",
              animation: finderPhotoScan ? "nxPulse 1s ease infinite" : "none",
            }}>📸 {finderPhotoScan ? t("finder.analyzingBtn", "Analyse en cours...") : t("finder.takePhotoBtn", "Prendre une photo")}</Pressable>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <input style={{ ...inputStyle, flex: 1 }} placeholder={t("finder.searchPlaceholder", "Chercher un article...")} value={finderQuery}
                onChange={(e) => setFinderQuery(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") runManualSearch(); }} />
              <Pressable onClick={runManualSearch} aria-label={t("finder.searchAria", "Rechercher")} style={{
                width: 46, height: 46, borderRadius: 15, flex: "0 0 auto", background: A.purple, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(18px * var(--nx-font-scale, 1))`,
              }}>🔎</Pressable>
            </div>
            <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", paddingBottom: 2, marginBottom: 14 }}>
              {[{ id: "all", l: t("finder.allCategoriesChip", "Toutes") }, ...finderCategories.map((c) => ({ id: c.label, l: c.label }))].map((c) => (
                <Pressable key={c.id} onClick={() => setFinderCategory(c.id)} style={{
                  flex: "0 0 auto", padding: "8px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  background: finderCategory === c.id ? A.purple : P.soft, color: finderCategory === c.id ? "#fff" : P.ink,
                  border: `1.5px solid ${finderCategory === c.id ? A.purple : P.line}`,
                }}>{c.l}</Pressable>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Pressable onClick={openFinderCatalog} style={{
                flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                background: P.soft, color: P.ink,
              }}>📚 {t("finder.catalogBtn", "Catalogue")}</Pressable>
              <Pressable onClick={openFinderCreate} style={{
                flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                background: P.soft, border: `1.5px dashed ${P.line}`, color: P.ink,
              }}>＋ {t("finder.createCardBtn", "Ajouter une fiche article")}</Pressable>
            </div>
          </>
        )}

        {screen === "finder" && finderScreen === "results" && (
          <div className="nx-screen-enter">
            {finderResults.length === 0 && (
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 30 }}>
                <span style={{ fontSize: `calc(34px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>🤷</span>
                <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{t("finder.noMatchTitle", "Aucune correspondance trouvée.")}</strong>
                <p style={{ margin: "5px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("finder.noMatchText", "Essaie une autre photo, ou affine ta recherche manuelle.")}</p>
              </section>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
            {finderResults.map((m, mIdx) => {
              const confMeta = { high: { l: t("finder.confidenceHigh", "Correspondance forte"), c: A.green }, medium: { l: t("finder.confidenceMedium", "Correspondance possible"), c: A.orange }, low: { l: t("finder.confidenceLow", "Correspondance incertaine"), c: A.red } }[m.confidenceLevel];
              return (
                <Pressable key={m.item.id} feedback className="nx-list-item-in" onClick={() => openFinderDetail(m.item.id)} style={{ ...card, ...tile, padding: 13, marginBottom: isWide ? 0 : 8, alignItems: "flex-start", animationDelay: `${Math.min(mIdx, 14) * 28}ms` }}>
                  {m.item.images?.[0]
                    ? <img src={m.item.images[0].thumbUrl || m.item.images[0].url} alt="" style={{ flex: "0 0 auto", width: 42, height: 42, borderRadius: 14, objectFit: "cover" }} />
                    : <span style={{ flex: "0 0 auto", width: 42, height: 42, borderRadius: 14, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(19px * var(--nx-font-scale, 1))`}}>📦</span>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: confMeta.c, textTransform: "uppercase", letterSpacing: ".05em" }}>{confMeta.l}</span>
                    <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", margin: "2px 0" }}>{m.item.designation}</strong>
                    <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{m.item.category}{m.item.priceStale ? ` · ${t("finder.priceToCheckSuffix", "⚠️ Prix à vérifier")}` : ""}</span>
                  </div>
                  <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{Number.isFinite(m.item.price?.amount) ? `${m.item.price.amount.toFixed(2)} €` : "—"}</strong>
                </Pressable>
              );
            })}
            </div>
          </div>
        )}

        {screen === "finder" && finderScreen === "detail" && (() => {
          const item = finderItems.find((i) => i.id === finderDetailId);
          if (!item) return null;
          return (
            <>
              <section style={{ ...colorCard(A.purple), ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 26 }}>
                {item.images?.[0]
                  ? <img src={item.images[0].thumbUrl || item.images[0].url} alt="" style={{ width: 72, height: 72, borderRadius: 20, objectFit: "cover", marginBottom: 8 }} />
                  : <span style={{ fontSize: `calc(40px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>📦</span>}
                <h2 style={{ margin: "0 0 4px", fontSize: `calc(18px * var(--nx-font-scale, 1))`, color: "#fff" }}>{item.designation}</h2>
                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, ...onColorSub }}>{item.category}</span>
              </section>
              {/* Galerie — TOUTES les photos de la fiche, pas seulement la première (retour
                  utilisateur explicite : "je veux pouvoir agrandir les photos d'un article
                  ajouter et les consulter toutes si plusieurs"). Tap = visualiseur plein
                  écran avec navigation ‹ › entre les photos. */}
              {item.images?.length > 0 && (
                <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
                  {item.images.map((img, i) => (
                    <Pressable key={img.id || i} onClick={() => setFinderPhotoViewer({ urls: item.images.map((x) => x.url), index: i })} style={{
                      width: 84, height: 84, borderRadius: 16, flex: "0 0 auto", overflow: "hidden", border: `1px solid ${P.line}`,
                    }}>
                      {/* Miniature ici (2026-08-22) — le zoom plein écran déclenché par ce tap
                          (setFinderPhotoViewer ci-dessus) reste sur img.url, jamais thumbUrl :
                          "je veux toujours pouvoir agrandir les photos" reste garanti. */}
                      <img src={img.thumbUrl || img.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </Pressable>
                  ))}
                </div>
              )}
              <div style={{ ...card, borderRadius: 18, padding: 16, marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: `1px solid ${P.line}` }}>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("finder.priceFieldLabel", "Prix")}</span>
                  {finderEditingPrice ? (
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input style={{ ...inputStyle, width: 90, padding: "6px 9px", fontSize: `calc(13px * var(--nx-font-scale, 1))`}} type="text" inputMode="decimal" autoFocus
                        value={finderPriceDraft} onChange={(e) => setFinderPriceDraft(e.target.value)} />
                      <Pressable onClick={() => saveFinderPrice(item.id)} aria-label={t("finder.priceSaveAria", "Enregistrer le prix")} style={{
                        width: 30, height: 30, borderRadius: 99, background: A.green, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                      }}>✓</Pressable>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{Number.isFinite(item.price?.amount) ? `${item.price.amount.toFixed(2)} €${item.priceStale ? " ⚠️" : ""}` : t("finder.priceNotSet", "Non renseigné")}</strong>
                      {finderAccess.hasValidateTier && (
                        <Pressable onClick={() => startEditFinderPrice(item)} aria-label={t("finder.priceEditAria", "Modifier le prix")} style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted }}>✏️</Pressable>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "9px 0" }}>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("finder.categoryFieldLabel", "Catégorie")}</span>
                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{item.category}</strong>
                </div>
              </div>
              {finderAccess.hasValidateTier && (
                <>
                  <input ref={finderAddPhotoInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                    onChange={(e) => { addFinderPhoto(item.id, e.target.files?.[0]); e.target.value = ""; }} />
                  <Pressable onClick={() => finderAddPhotoInputRef.current?.click()} style={{
                    width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                    background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink, marginBottom: 9,
                  }}>📷 {t("finder.addPhotoBtn", "Ajouter/actualiser une photo")}</Pressable>
                </>
              )}
              {/* Bouton "Signaler un problème" retiré (retour utilisateur explicite : pouvoir
                  actualiser/modifier directement la fiche — photo, prix, champs — suffit,
                  plus besoin d'un circuit de signalement séparé vers le FL/FLA). */}
              {item.priceStale && (
                <Pressable onClick={() => verifyPriceFinderItem(item.id)} style={{ width: "100%", ...btnSolid(A.green), marginBottom: 9 }}>{t("finder.confirmPriceUpToDateBtn", "✓ Confirmer que le prix est à jour")}</Pressable>
              )}
              {/* Bouton "Archiver" retiré (retour utilisateur explicite) — Supprimer
                  définitivement (ci-dessous) le remplace. "Restaurer" reste disponible pour
                  d'anciennes fiches déjà archivées avant ce changement. */}
              {item.status === "archived" && (
                <Pressable onClick={() => restoreFinderItem(item.id)} style={{
                  width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink, marginBottom: 9,
                }}>{t("finder.restoreSheetBtn", "↩︎ Restaurer la fiche")}</Pressable>
              )}
              {/* Suppression DÉFINITIVE — retour utilisateur explicite : "je veux pouvoir
                  supprimer completement et non seulement archiver une fiche". Irréversible,
                  réservée au palier validate. */}
              {finderAccess.hasValidateTier && (
                <Pressable onClick={() => deleteFinderItem(item.id)} style={{
                  width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: A.red, color: "#fff",
                }}>{t("finder.deleteSheetBtn", "🗑️ Supprimer définitivement")}</Pressable>
              )}
            </>
          );
        })()}

        {screen === "finder" && finderScreen === "admin" && (
          <div className="nx-screen-enter">
            <p style={{ margin: "0 2px 14px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{t("finder.adminQueueIntro", "Fiches proposées par l'équipe — valide avant qu'elles apparaissent en recherche.")}</p>
            {finderAdminItems.length === 0 && <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("finder.adminQueueEmpty", "Aucune fiche en attente.")}</p>}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
            {finderAdminItems.map((i, iIdx) => (
              <div key={i.id} className="nx-list-item-in" style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 14, marginBottom: isWide ? 0 : 9, animationDelay: `${Math.min(iIdx, 14) * 28}ms` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 9 }}>
                  {i.images?.[0]
                    ? <img src={i.images[0].thumbUrl || i.images[0].url} alt="" style={{ width: 34, height: 34, borderRadius: 12, objectFit: "cover" }} />
                    : <span style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`}}>📦</span>}
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{i.designation}</strong>
                    <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{i.category} · {Number.isFinite(i.price?.amount) ? `${i.price.amount.toFixed(2)} €` : t("finder.priceMissing", "prix manquant")} · {t("finder.proposedByPrefix", "proposé par")} {i.createdByName}{!i.images?.length ? ` · ${t("finder.noPhotoRejectedSuffix", "⚠️ sans photo (validation refusée)")}` : ""}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Pressable onClick={() => validateFinderItem(i.id)} style={{ flex: 1, ...btnSolid(A.green) }}>{t("finder.validateBtn", "✓ Valider")}</Pressable>
                  <Pressable onClick={() => rejectFinderItem(i.id)} style={{
                    flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                    background: "rgba(239,68,68,.12)", color: A.red,
                  }}>{t("finder.rejectBtn", "Rejeter")}</Pressable>
                </div>
              </div>
            ))}
            </div>
          </div>
        )}

        {screen === "finder" && finderScreen === "catalog" && (
          <div className="nx-screen-enter">
            <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 12, paddingBottom: 2 }}>
              {[{ id: "all", l: t("finder.allCategoriesChip", "Toutes") }, ...finderCategories.map((c) => ({ id: c.label, l: c.label }))].map((c) => (
                <Pressable key={c.id} onClick={() => { setFinderCategory(c.id); loadFinderCatalog(); }} style={{
                  flex: "0 0 auto", padding: "8px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  background: finderCategory === c.id ? A.purple : P.soft, color: finderCategory === c.id ? "#fff" : P.ink,
                  border: `1.5px solid ${finderCategory === c.id ? A.purple : P.line}`,
                }}>{c.l}</Pressable>
              ))}
            </div>
            {finderCatalogLoading && <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("common.loading", "Chargement...")}</p>}
            {!finderCatalogLoading && finderCatalog.length === 0 && (
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 30 }}>
                <span style={{ fontSize: `calc(34px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>📚</span>
                <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{t("finder.catalog.emptyTitle", "Aucun article dans le catalogue.")}</strong>
                <p style={{ margin: "5px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("finder.catalog.emptyText", "Les fiches validées apparaîtront ici.")}</p>
              </section>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
            {finderCatalog.map((item, itemIdx) => (
              <Pressable key={item.id} feedback className="nx-list-item-in" onClick={() => openFinderDetail(item.id, "catalog")} style={{ ...card, ...tile, padding: 13, marginBottom: isWide ? 0 : 8, alignItems: "flex-start", animationDelay: `${Math.min(itemIdx, 14) * 28}ms` }}>
                {item.images?.[0]
                  ? <img src={item.images[0].thumbUrl || item.images[0].url} alt="" style={{ flex: "0 0 auto", width: 42, height: 42, borderRadius: 14, objectFit: "cover" }} />
                  : <span style={{ flex: "0 0 auto", width: 42, height: 42, borderRadius: 14, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(19px * var(--nx-font-scale, 1))`}}>📦</span>}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", margin: "2px 0" }}>{item.designation}</strong>
                  <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{item.category}{item.status === "price_review_required" ? ` · ${t("finder.priceToCheckSuffix", "⚠️ Prix à vérifier")}` : ""}{item.status === "archived" ? ` · ${t("finder.catalog.archivedSuffix", "archivée")}` : ""}</span>
                </div>
                <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{Number.isFinite(item.price?.amount) ? `${item.price.amount.toFixed(2)} €` : "—"}</strong>
              </Pressable>
            ))}
            </div>
          </div>
        )}

        {screen === "finder" && finderScreen === "create" && (
          <>
            {/* Plusieurs photos possibles dès la création (retour utilisateur explicite) —
                grille de vignettes retirables + tuile "+" pour en ajouter d'autres (jusqu'à
                5), au lieu d'une seule zone de dépôt. Chaque vignette ouvre le visualiseur
                plein écran avec navigation ‹ › entre les photos déjà sélectionnées. */}
            <input ref={finderCreatePhotoInputRef} type="file" accept="image/*" multiple capture="environment" style={{ display: "none" }}
              onChange={(e) => { pickFinderCreatePhotos(e.target.files); e.target.value = ""; }} />
            {finderCreatePhotos.length > 0 ? (
              <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
                {finderCreatePhotos.map((p, i) => (
                  <div key={i} style={{ position: "relative", flex: "0 0 auto" }}>
                    <Pressable onClick={() => setFinderPhotoViewer({ urls: finderCreatePhotos.map((x) => x.preview), index: i })} style={{
                      width: 92, height: 92, borderRadius: 18, overflow: "hidden",
                    }}>
                      <img src={p.preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </Pressable>
                    <Pressable onClick={() => removeFinderCreatePhoto(i)} aria-label={t("finder.create.removePhotoAria", "Retirer cette photo")} style={{
                      position: "absolute", top: -6, right: -6, width: 24, height: 24, borderRadius: 99, background: A.red, color: "#fff",
                      display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, boxShadow: "0 2px 6px rgba(0,0,0,.3)",
                    }}>✕</Pressable>
                  </div>
                ))}
                {finderCreatePhotos.length < 5 && (
                  <Pressable onClick={() => finderCreatePhotoInputRef.current?.click()} aria-label={t("finder.create.addMorePhotosAria", "Ajouter d'autres photos")} style={{
                    width: 92, height: 92, borderRadius: 18, flex: "0 0 auto", border: `2px dashed ${P.line}`, background: P.soft,
                    display: "grid", placeItems: "center", fontSize: `calc(26px * var(--nx-font-scale, 1))`, color: P.muted,
                  }}>＋</Pressable>
                )}
              </div>
            ) : (
              <Pressable onClick={() => finderCreatePhotoInputRef.current?.click()} style={{
                width: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                padding: "26px 16px", borderRadius: 22, border: `2px dashed ${P.line}`, background: P.soft,
                marginBottom: 14, textAlign: "center",
              }}>
                <span style={{
                  width: 50, height: 50, borderRadius: 16, display: "grid", placeItems: "center", fontSize: `calc(22px * var(--nx-font-scale, 1))`, color: "#fff",
                  background: "radial-gradient(circle at 32% 26%, #c4b5fd, #4c1d95 82%)", marginBottom: 2,
                }}>📷</span>
                <strong style={{ fontSize: `calc(14.5px * var(--nx-font-scale, 1))`}}>{t("finder.create.photoDropzoneTitle", "Ajouter des photos")}</strong>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("finder.create.photoDropzoneDesc", "Optionnel, jusqu'à 5 — la fiche existe dès sa création, avec ou sans photo.")}</span>
              </Pressable>
            )}
            <div style={{ ...card, borderRadius: 18, padding: 14, marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, marginBottom: 6 }}>{t("finder.create.designationLabel", "Désignation")}</label>
              <input style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: 12 }}
                placeholder={t("finder.create.designationPlaceholder", "Ex. Boîte de conserve verte, logo rond rouge")}
                value={finderCreateForm.designation} onChange={(e) => setFinderCreateForm((f) => ({ ...f, designation: e.target.value }))} />
              <label style={{ display: "block", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, marginBottom: 6 }}>{t("finder.create.categoryLabel", "Catégorie")}</label>
              <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 12, paddingBottom: 2 }}>
                {finderCategories.map((c) => {
                  const sel = finderCreateForm.category === c.label;
                  return (
                    <Pressable key={c.id} onClick={() => setFinderCreateForm((f) => ({ ...f, category: c.label }))} style={{
                      flex: "0 0 auto", padding: "8px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                      background: sel ? A.purple : P.soft, color: sel ? "#fff" : P.ink,
                      border: `1.5px solid ${sel ? A.purple : P.line}`,
                    }}>{c.label}</Pressable>
                  );
                })}
              </div>
              <label style={{ display: "block", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, marginBottom: 6 }}>{t("finder.create.priceLabel", "Prix (optionnel)")}</label>
              <input style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: 12 }} type="text" inputMode="decimal"
                placeholder="0,00 €" value={finderCreateForm.price} onChange={(e) => setFinderCreateForm((f) => ({ ...f, price: e.target.value }))} />
              {/* EAN + numéro d'article — tous les deux facultatifs (retour utilisateur
                  2026-08-04), jamais exigés côté serveur non plus. Le scan réutilise le même
                  décodeur photo que Scanner/Contrôle magasin/Inventur (doScan/handleScanFileChange,
                  ctx="finder") plutôt qu'une nouvelle implémentation caméra. */}
              <label style={{ display: "block", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, marginBottom: 6 }}>{t("finder.create.eanLabel", "EAN (optionnel)")}</label>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <input style={{ ...inputStyle, flex: 1, boxSizing: "border-box" }} type="text" inputMode="numeric"
                  placeholder={t("finder.create.eanPlaceholder", "Scanner ou saisir le code-barres")}
                  value={finderCreateForm.ean} onChange={(e) => setFinderCreateForm((f) => ({ ...f, ean: e.target.value }))} />
                <Pressable onClick={() => doScan("finder")} aria-label={t("finder.create.scanAria", "Scanner le code-barres")} style={{
                  width: 46, height: 46, borderRadius: 15, flex: "0 0 auto", background: A.purple, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(18px * var(--nx-font-scale, 1))`,
                }}>📷</Pressable>
              </div>
              <label style={{ display: "block", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, marginBottom: 6 }}>{t("finder.create.articleNumberLabel", "Numéro d'article (optionnel)")}</label>
              <input style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} type="text"
                placeholder={t("finder.create.articleNumberPlaceholder", "Ex. 12345")}
                value={finderCreateForm.articleNumber} onChange={(e) => setFinderCreateForm((f) => ({ ...f, articleNumber: e.target.value }))} />
            </div>
            <Pressable onClick={submitFinderCreate} style={{ width: "100%", ...btnSolid(A.purple), opacity: finderCreateSaving ? .6 : 1 }}>
              {finderCreateSaving ? t("finder.create.savingBtn", "Enregistrement...") : t("finder.create.submitBtn", "✓ Créer la fiche")}
            </Pressable>
          </>
        )}

        {/* ================= CENTRE DOCUMENTAIRE (Document Intelligence Center) ================= */}
        {screen === "docintel" && (
          <>
            <section style={{ ...colorCard(A.purple), ...tile, flexDirection: "column", alignItems: "stretch", padding: 20, marginBottom: 15 }}>
              <span style={onColorTag}>{t("documentIntel.hero.eyebrow", "Smart Import")}</span>
              <strong style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, display: "block", margin: "8px 0 4px" }}>{t("documentIntel.hero.title", "Un seul geste pour tous tes documents")}</strong>
              <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, opacity: .9 }}>{t("documentIntel.hero.desc", "Dépose un fichier ou prends une photo — Nexora s'occupe de le classer.")}</span>
            </section>

            <input ref={docIntelFileInputRef} type="file" multiple className="hidden" style={{ display: "none" }}
              accept="application/pdf,image/*,.csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => { uploadDocIntelFiles(e.target.files); e.target.value = ""; }} />
            <input ref={docIntelCameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
              onChange={(e) => { uploadDocIntelFiles(e.target.files); e.target.value = ""; }} />

            <Pressable onClick={() => docIntelFileInputRef.current?.click()} style={{
              width: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
              padding: "26px 16px", borderRadius: 22, border: `2px dashed ${P.line}`, background: P.soft, marginBottom: 10, textAlign: "center",
            }}>
              <span style={{
                width: 50, height: 50, borderRadius: 16, display: "grid", placeItems: "center", fontSize: `calc(22px * var(--nx-font-scale, 1))`, color: "#fff",
                background: "radial-gradient(circle at 32% 26%, #c4b5fd, #4c1d95 82%)", marginBottom: 2,
              }}>⬆️</span>
              <strong style={{ fontSize: `calc(14.5px * var(--nx-font-scale, 1))`}}>{t("documentIntel.dropzone.title", "Importer un document")}</strong>
              <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("documentIntel.dropzoneDescFull", "Touche pour choisir un fichier (PDF, image, CSV, Excel)")}</span>
            </Pressable>
            <Pressable onClick={() => docIntelCameraInputRef.current?.click()} style={{
              width: "100%", boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              padding: 13, borderRadius: 16, background: P.soft, color: P.ink, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, marginBottom: 14,
            }}>📷 {t("documentIntel.cameraBtn", "Prendre une photo")}</Pressable>

            {docIntelError && (
              <div style={{ padding: "11px 13px", borderRadius: 14, background: "rgba(239,68,68,.1)", color: A.red, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, marginBottom: 12 }}>
                {docIntelError}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
              {[
                { l: t("documentIntel.stat.today", "Aujourd'hui"), v: docIntelSummary.todayCount || 0 },
                { l: t("documentIntel.stat.pending", "En attente"), v: docIntelSummary.pending || 0 },
                { l: t("documentIntel.stat.unsupported", "Non pris en charge"), v: docIntelSummary.unsupported || 0 },
              ].map((s) => (
                <div key={s.l} style={{ ...card, flexDirection: "column", alignItems: "center", gap: 2, padding: "12px 6px", textAlign: "center" }}>
                  <strong style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`, fontWeight: 800 }}>{s.v}</strong>
                  <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{s.l}</span>
                </div>
              ))}
            </div>

            <strong style={{ display: "block", fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, margin: "0 2px 10px" }}>{t("documentIntel.recentTitle", "Documents récents")}</strong>
            {docIntelDocs.length === 0 && <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("documentIntel.empty", "Aucun document déposé pour l'instant.")}</p>}
            {docIntelDocs.map((doc) => {
              const meta = DOC_INTEL_STATUS_META[doc.status] || DOC_INTEL_STATUS_META.pending;
              const isAnalyzing = docIntelAnalyzingIds.includes(doc.id);
              const canAnalyze = doc.status === "pending" && !isAnalyzing;
              const lastWarning = (doc.warnings || [])[doc.warnings.length - 1];
              return (
                <div key={doc.id} style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 13, marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <span style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`}}>{meta.emoji}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.originalFilename || t("documentIntel.docFallbackName", "Document")}</strong>
                      <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{fmtFR((doc.uploadedAt || "").slice(0, 10))} · {doc.uploadedByName || ""}</span>
                      {doc.status === "classified" && (
                        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginTop: 5 }}>
                          <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 900, textTransform: "uppercase", letterSpacing: ".03em", padding: "2px 8px", borderRadius: 99, background: "rgba(54,79,231,.1)", color: A.blue }}>{doc.detectedType || "unknown"}</span>
                          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>{Math.round((doc.confidence || 0) * 100)}%</span>
                          {doc.extraction?.summary && <p style={{ flexBasis: "100%", margin: 0, fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{doc.extraction.summary}</p>}
                        </div>
                      )}
                      {lastWarning && <p style={{ margin: "5px 0 0", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: A.orange }}>{lastWarning}</p>}
                    </div>
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: meta.color, flex: "0 0 auto", whiteSpace: "nowrap" }}>{meta.label}</span>
                  </div>
                  {canAnalyze && (
                    <Pressable onClick={() => analyzeDocIntelDoc(doc.id)} style={{
                      alignSelf: "flex-end", marginTop: 9, border: 0, padding: "7px 14px", borderRadius: 99,
                      background: "linear-gradient(135deg, #5b7dff, #364fe7)", color: "#fff", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800,
                    }}>{t("documentIntel.analyzeBtn", "Analyser")}</Pressable>
                  )}
                  {isAnalyzing && <span style={{ alignSelf: "flex-end", marginTop: 9, fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>{t("documentIntel.analyzing", "Analyse en cours...")}</span>}
                </div>
              );
            })}
          </>
        )}

        {/* ================= ARBEITSZEITLISTE ================= */}
        {screen === "azl" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <div style={{ ...card, display: "flex", alignItems: "center", gap: 10, padding: "8px 6px", flex: 1 }}>
                <Pressable feedback onClick={() => azlShiftMonth(-1)} aria-label={t("haccp.prevMonthAria", "Mois précédent")} style={{ width: 32, height: 32, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(15px * var(--nx-font-scale, 1))`, color: P.ink }}>‹</Pressable>
                <strong style={{ flex: 1, textAlign: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{azlMonthLabel}</strong>
                <Pressable feedback onClick={() => azlShiftMonth(1)} aria-label={t("planning.calendar.nextMonthAria", "Mois suivant")} style={{ width: 32, height: 32, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(15px * var(--nx-font-scale, 1))`, color: P.ink }}>›</Pressable>
              </div>
              {user?.stores?.length > 1 && (
                <select value={azlStoreId} onChange={(e) => setAzlStoreId(e.target.value)} style={{ ...inputStyle, width: "auto", padding: "10px 12px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>
                  {user?.stores.map((s) => <option key={s} value={s}>{t("admin.scopeStoreTemplate", { id: s }, `Filiale ${s}`)}</option>)}
                </select>
              )}
            </div>

            {azlLoading && <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("common.loading", "Chargement...")}</p>}
            {azlError && <p style={{ textAlign: "center", color: A.red, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{azlError}</p>}

            {azlData && (
              <>
                {azlData.document.status !== "draft" && (
                  <section style={{
                    ...colorCard(azlData.document.status === "archived" ? A.green : A.orange),
                    ...tile, flexDirection: "column", alignItems: "stretch", padding: 16, marginBottom: 14,
                  }}>
                    <span style={onColorTag}>{azlData.document.status === "archived" ? t("arbeitszeitliste.archive.archivedTitle", "Document archivé et verrouillé") : t("arbeitszeitliste.archive.validatedTitleShort", "Validé — archivage PDF en attente")}</span>
                    <span style={{ ...onColorSub, fontSize: `calc(12px * var(--nx-font-scale, 1))`, margin: "6px 0 12px", display: "block" }}>
                      {azlData.document.status === "archived"
                        ? `${t("arbeitszeitliste.archive.archivedAt", { date: new Date(azlData.document.archivedAt).toLocaleString("de-DE") }, `Archivé le ${new Date(azlData.document.archivedAt).toLocaleString("de-DE")}`)} — ${t("arbeitszeitliste.archive.hash", { hash: (azlData.document.pdfIntegrityHash || "").slice(0, 12) }, `hash ${(azlData.document.pdfIntegrityHash || "").slice(0, 12)}`)}`
                        : t("arbeitszeitliste.archive.validatedHint", "Les données sont figées ; le PDF officiel n'a pas encore pu être généré.")}
                    </span>
                    <div style={{ display: "flex", gap: 8 }}>
                      {azlData.document.status === "archived"
                        ? <Pressable onClick={downloadAzlArchive} style={{ flex: 1, ...btnGhostOnColor }}>{t("arbeitszeitliste.archive.download", "Télécharger le PDF officiel")}</Pressable>
                        : <Pressable onClick={retryAzlArchive} style={{ flex: 1, ...btnGhostOnColor }}>{t("arbeitszeitliste.archive.retry", "Réessayer l'archivage")}</Pressable>}
                      <Pressable onClick={openAzlHistory} style={{ flex: 1, ...btnGhostOnColor }}>{t("arbeitszeitliste.history.open", "Historique")}</Pressable>
                    </div>
                  </section>
                )}

                {(() => {
                  const sigByRole = new Map((azlData.validation?.signatures || []).map((s) => [s.validationRole, s]));
                  const azlBadge = (code) => (
                    <span style={{ display: "inline-block", fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 6, padding: "1px 6px", background: AZL_BADGE_FILL[code] || P.soft, color: AZL_BADGE_COLOR[code] || P.muted }}>{code}</span>
                  );
                  // 3 lignes par case (Phase AZL Phase 4, fidèle au document papier réel) :
                  // haut = Vertretungsgeld manuel OU LF+astérisque ; milieu = heures travaillées
                  // (décimal) OU code d'absence journée entière ; bas = pause (décimal), affichée
                  // seulement si renseignée. Z (Zuschlagspflichtig) en exposant à côté des heures
                  // — un modificateur, jamais un remplacement du code d'absence.
                  // Mélange RGB opaque (jamais rgba) — retour utilisateur explicite 2026-08-16 :
                  // "au défilement horizontal les données [sont] mélangées avec les noms". La
                  // colonne Mitarbeiter reste position:sticky pendant que les cellules jour
                  // défilent EN DESSOUS dans le même flux de peinture ; un fond translucide
                  // (P.soft/rgba) laisse ces cellules transparaître à travers la colonne figée.
                  // Un vrai rgb() opaque, calculé à partir des jetons de thème existants, réglait
                  // le problème sans ajouter de nouvelles couleurs codées en dur par thème.
                  const azlBlend = (hex, tint, ratio) => {
                    const h = (c) => [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
                    const [r1, g1, b1] = h(hex); const [r2, g2, b2] = h(tint);
                    const mix = (a, b) => Math.round(a * (1 - ratio) + b * ratio);
                    return `rgb(${mix(r1, r2)}, ${mix(g1, g2)}, ${mix(b1, b2)})`;
                  };
                  const AZL_FULL_DAY_CODES = ["K", "KK", "U", "UU", "SU", "F", "MS", "ERZ"];
                  const azlHoursDecimal = (minutes) => (minutes > 0 ? azlFmtHoursDe(minutes / 60) : "");
                  const azlDayCellContent = (day) => {
                    if (!day) return null;
                    const code = day.absenceCode;
                    const topText = day.vertretungsgeldNote || (day.lfMinutes > 0 ? `${azlHoursDecimal(day.lfMinutes)}*` : "");
                    const isFullDayCode = AZL_FULL_DAY_CODES.includes(code);
                    const middleText = isFullDayCode ? code : (code === "V" ? azlHoursDecimal(day.vertretungMinutes) : azlHoursDecimal(day.workedMinutes));
                    const middleColor = isFullDayCode ? (AZL_BADGE_COLOR[code] || P.ink) : (code === "V" ? A.purple : P.ink);
                    const bottomText = day.pauseMinutes > 0 ? azlHoursDecimal(day.pauseMinutes) : "";
                    return (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.2, minHeight: 30, justifyContent: "center" }}>
                        <span style={{ fontSize: `calc(8px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.orange, minHeight: 10 }}>{topText}</span>
                        <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: middleColor }}>
                          {middleText}
                          {day.zuschlagspflichtig && middleText && <sup style={{ fontSize: `calc(7px * var(--nx-font-scale, 1))`, marginLeft: 1 }}>Z</sup>}
                        </span>
                        <span style={{ fontSize: `calc(8px * var(--nx-font-scale, 1))`, color: P.muted, minHeight: 10 }}>{bottomText}</span>
                      </div>
                    );
                  };
                  // Fermeture hebdomadaire (dimanche) — trait diagonal "rien à remplir", fidèle au
                  // document papier. Les jours fériés réels restent signalés par le code F
                  // existant (déjà visible en ligne du milieu), jamais un double marquage.
                  // Thème-agnostique (retour utilisateur explicite, 2026-08-16 : "Azl doit
                  // pouvoir s'adapter à tous les thèmes") — P.line flip déjà clair/sombre
                  // selon le thème, au lieu d'un rgba(33,29,22,...) fixe (invisible/à
                  // contresens en Sombre/Nuit).
                  const azlSundayCellStyle = { backgroundImage: `repeating-linear-gradient(135deg, ${P.line} 0px, ${P.line} 1px, transparent 1px, transparent 9px)` };
                  const isWeekendDate = (dateISO) => [0, 6].includes(new Date(dateISO + "T00:00:00").getDay());
                  const isSundayDate = (dateISO) => new Date(dateISO + "T00:00:00").getDay() === 0;
                  return (
                    <>
                      {/* Tableau interactif — remplace la grille SVG A3 (retour utilisateur
                          explicite 2026-08-03 : "à peine lisible sauf au zoom"). Nom sticky à
                          gauche, jours scrollables horizontalement, colonnes de synthèse déjà
                          calculées côté serveur (dataAggregation.js). Chaque cellule jour est un
                          vrai <td onClick>, plus de hit-testing pixel. */}
                      <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", marginBottom: 20, borderRadius: 14, border: `1px solid ${P.line}`, background: P.card }}>
                        <table style={{ borderCollapse: "collapse", fontSize: `calc(12px * var(--nx-font-scale, 1))`, width: "100%" }}>
                          <thead>
                            <tr>
                              <th style={{ position: "sticky", left: 0, zIndex: 2, background: A.navy, color: "#fff", padding: "9px 10px", textAlign: "left", minWidth: 150, fontSize: `calc(10.5px * var(--nx-font-scale, 1))`}}>{t("arbeitszeitliste.table.employee", "Mitarbeiter")}</th>
                              {azlData.dates.map((dateISO) => {
                                const d = new Date(dateISO + "T00:00:00");
                                const weekend = isWeekendDate(dateISO);
                                return (
                                  <th key={dateISO} style={{ background: weekend ? "#26345f" : A.navy, color: "#fff", padding: "6px 3px", minWidth: 36, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>
                                    <div>{d.getDate()}</div>
                                    <div style={{ fontSize: `calc(8px * var(--nx-font-scale, 1))`, opacity: .8 }}>{["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][d.getDay()]}</div>
                                  </th>
                                );
                              })}
                              {[t("arbeitszeitliste.table.sumWork", "Σ Arbeit"), t("arbeitszeitliste.table.sumLf", "Σ LF"), t("arbeitszeitliste.table.sumV", "Σ V"), t("arbeitszeitliste.table.sumK", "Σ K"), t("arbeitszeitliste.table.sumU", "Σ U"), t("arbeitszeitliste.table.contract", "Vertrag"), t("arbeitszeitliste.table.restUrlaub", "Rest-Urlaub"), t("arbeitszeitliste.table.carryover", "Übertrag")].map((h) => (
                                <th key={h} style={{ background: A.navy, color: "#fff", padding: "6px 6px", minWidth: 54, fontSize: `calc(9px * var(--nx-font-scale, 1))`}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {azlData.employees.map((emp, r) => {
                              // Idem — fonds de ligne fixes (#fff/#fff9ec/#fbfaf6) remplacés par
                              // des jetons de thème opaques (P.bg/P.card/azlBlend), jamais une
                              // couleur translucide ici (colonne Mitarbeiter en position:sticky —
                              // voir azlBlend ci-dessus pour le pourquoi).
                              const rowBg = emp.isExternal ? azlBlend(P.card, A.amber, 0.14) : (r % 2 ? P.bg : P.card);
                              return (
                                <tr key={emp.employeeId}>
                                  <td style={{ position: "sticky", left: 0, zIndex: 1, background: rowBg, padding: "8px 10px", borderTop: `1px solid ${P.line}` }}>
                                    <strong style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, display: "block" }}>{emp.displayName}</strong>
                                    <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>{emp.roleLabel}{emp.isExternal ? ` · EXTERN (${emp.homeStoreId})` : ""}</span>
                                    {/* Personal-Nr (AZL Phase 6) — colonne de droite du document papier
                                        confirmée hors périmètre app pour tout le reste (rempli par le
                                        siège), SAUF celle-ci : simple affichage de l'employeeId déjà
                                        existant, aucun nouveau champ ni saisie. */}
                                    <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", opacity: .75 }}>{t("arbeitszeitliste.table.personalNr", "Personal-Nr")} {emp.employeeId}</span>
                                  </td>
                                  {emp.days.map((day, idx) => {
                                    const dateISO = azlData.dates[idx];
                                    const weekend = isWeekendDate(dateISO);
                                    const sunday = isSundayDate(dateISO);
                                    return (
                                      <td key={idx} onClick={() => openAzlDay(emp, dateISO, day)} style={{
                                        textAlign: "center", padding: "6px 2px", borderTop: `1px solid ${P.line}`, cursor: "pointer",
                                        background: weekend ? P.soft : rowBg,
                                        ...(sunday ? azlSundayCellStyle : {}),
                                      }}>
                                        {azlDayCellContent(day)}
                                      </td>
                                    );
                                  })}
                                  <td style={{ textAlign: "center", fontWeight: 800, fontSize: `calc(11px * var(--nx-font-scale, 1))`, borderTop: `1px solid ${P.line}`, background: rowBg }}>{azlFmtMinutes(emp.workedMinutes)}</td>
                                  <td style={{ textAlign: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, borderTop: `1px solid ${P.line}`, background: rowBg }}>{azlFmtMinutes(emp.lfMinutes)}</td>
                                  <td style={{ textAlign: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, borderTop: `1px solid ${P.line}`, background: rowBg }}>{azlFmtMinutes(emp.vertretungMinutes)}</td>
                                  <td style={{ textAlign: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, borderTop: `1px solid ${P.line}`, background: rowBg }}>{emp.krankDays}</td>
                                  <td style={{ textAlign: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, borderTop: `1px solid ${P.line}`, background: rowBg }}>{emp.urlaubDays}</td>
                                  <td style={{ textAlign: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, borderTop: `1px solid ${P.line}`, background: rowBg }}>{emp.weeklyContractedHours != null ? `${emp.weeklyContractedHours} h` : "—"}</td>
                                  <td style={{ textAlign: "center", fontWeight: 800, fontSize: `calc(11px * var(--nx-font-scale, 1))`, borderTop: `1px solid ${P.line}`, background: rowBg }}>
                                    {emp.restUrlaubDays != null ? `${emp.restUrlaubDays} j` : "—"}
                                  </td>
                                  <td style={{
                                    textAlign: "center", fontWeight: 800, fontSize: `calc(11px * var(--nx-font-scale, 1))`, borderTop: `1px solid ${P.line}`, background: rowBg,
                                    // Toujours en rouge sur le vrai document papier (pas une alerte
                                    // "négatif = mauvais" comme l'était l'ancien Δ) — reste neutre
                                    // uniquement à 0 (rien à reporter).
                                    color: emp.hourCarryoverMinutes ? A.red : P.ink,
                                  }}>
                                    {emp.hourCarryoverMinutes ? `${emp.hourCarryoverMinutes >= 0 ? "+" : "-"}${azlFmtMinutes(Math.abs(emp.hourCarryoverMinutes))}` : "—"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Légende — les 10 vrais codes du document papier (photos NORMA,
                          retour utilisateur 2026-08-04), plus Z (modificateur, pas un code
                          d'absence à part). "EZ" (jamais réel) retiré — corrigé en ERZ. */}
                      <section style={{ ...card, borderRadius: 18, padding: 16, marginBottom: 14 }}>
                        <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 10 }}>{t("arbeitszeitliste.legend.title", "Legende")}</strong>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
                          {[["K", "Krank"], ["KK", "Kinderkrank"], ["U", "Urlaub"], ["UU", "Unbez. Urlaub"], ["SU", "Sonderurlaub"], ["F", "Feiertag"], ["V", "Vertretung"], ["MS", "Mutterschutz"], ["ERZ", "Erziehungsurlaub"], ["Z", "Zuschlagspflichtig"]].map(([code, label]) => (
                            <div key={code} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              {azlBadge(code)}
                              <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{azlLegendLabel(code, label)}</span>
                            </div>
                          ))}
                        </div>
                      </section>

                      {/* Zusammenfassung — branchTotals déjà calculés côté serveur. */}
                      <section style={{ ...card, borderRadius: 18, padding: 16, marginBottom: 14 }}>
                        <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 10 }}>{t("arbeitszeitliste.summary.title", "Zusammenfassung")}</strong>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                          {[
                            [t("arbeitszeitliste.summary.sumWork", "Σ Arbeitsstunden"), azlFmtMinutes(azlData.branchTotals.workedMinutes)],
                            [t("arbeitszeitliste.summary.sumLf", "Σ Leistungsfrei"), azlFmtMinutes(azlData.branchTotals.lfMinutes)],
                            [t("arbeitszeitliste.summary.sumV", "Σ Vertretung"), azlFmtMinutes(azlData.branchTotals.vertretungMinutes)],
                            [t("arbeitszeitliste.summary.sumK", "Σ Krank"), String(azlData.branchTotals.krankDays)],
                            [t("arbeitszeitliste.summary.sumU", "Σ Urlaubstage"), String(azlData.branchTotals.urlaubDays)],
                            [t("arbeitszeitliste.summary.sumExt", "Σ Einsatz extern"), String(azlData.branchTotals.externalCount)],
                          ].map(([label, value]) => (
                            <div key={label}>
                              <div style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 3 }}>{label}</div>
                              <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{value}</strong>
                            </div>
                          ))}
                        </div>
                      </section>

                      {/* Performance — jamais une valeur inventée quand Reporting n'a rien pour
                          ce mois (voir calculateBranchPerformance, dataAggregation.js). */}
                      <section style={{ ...card, borderRadius: 18, padding: 16, marginBottom: 14 }}>
                        <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 10 }}>{t("arbeitszeitliste.performance.title", "Performance")}</strong>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                          {[
                            [t("arbeitszeitliste.performance.umsatz", "Σ Umsatz"), azlData.performance.umsatzTotal === null ? null : `${azlData.performance.umsatzTotal.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`],
                            [t("arbeitszeitliste.performance.leistungsstunden", "Σ Leistungsstunden"), azlData.performance.leistungsstundenTotal === null ? null : azlFmtMinutes(azlData.performance.leistungsstundenTotal * 60)],
                            [t("arbeitszeitliste.performance.stundenleistung", "Ø Stundenleistung"), azlData.performance.stundenleistung === null ? null : `${azlData.performance.stundenleistung.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/h`],
                          ].map(([label, value]) => (
                            <div key={label}>
                              <div style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 3 }}>{label}</div>
                              {value === null
                                ? <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontStyle: "italic" }}>{t("arbeitszeitliste.performance.unavailable", "Non disponible")}</span>
                                : <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{value}</strong>}
                            </div>
                          ))}
                        </div>
                      </section>

                      {/* Anomalien — mêmes fonctions déjà réelles (openAzlAnomalyDetail). */}
                      <section style={{ ...card, borderRadius: 18, padding: 16, marginBottom: 14 }}>
                        <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 10 }}>{t("arbeitszeitliste.anomalies.title", "Anomalien")}</strong>
                        {azlData.anomalies.length === 0 ? (
                          <p style={{ margin: 0, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: A.green, fontWeight: 700 }}>{t("arbeitszeitliste.anomalies.none", "Aucune anomalie détectée")}</p>
                        ) : azlData.anomalies.map((a, i) => (
                          <Pressable key={a.id || i} onClick={() => openAzlAnomalyDetail(a)} style={{
                            display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left",
                            padding: "9px 10px", borderRadius: 10, background: P.soft, marginBottom: 6,
                          }}>
                            <span style={{ width: 4, alignSelf: "stretch", borderRadius: 2, background: { critical: A.red, warning: A.orange, info: A.blue }[a.severity] || P.muted, flex: "0 0 auto" }} />
                            <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.ink }}>{a.message}</span>
                          </Pressable>
                        ))}
                      </section>

                      {/* Validierung (Phase AZL Phase 5) — fidèle au document papier : une
                          case de signature PAR EMPLOYÉ (jamais un rôle "employee" unique
                          partagé), plus 2 emplacements fixes en bas (Filialverantwortlicher/
                          FL-FLA, Bereichsleiter). requiredRoles/eligibility déjà calculés
                          côté serveur à partir du roster réel du mois — jamais une liste de
                          3 cases statique comme avant. */}
                      <section style={{ ...card, borderRadius: 18, padding: 16, marginBottom: 14, flexDirection: "column", alignItems: "stretch" }}>
                        <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 10 }}>{t("arbeitszeitliste.validation.title", "Validierung")}</strong>
                        {(() => {
                          const allRoles = azlData.validation?.requiredRoles || [];
                          const employeeRoles = allRoles.filter((r) => r.startsWith("employee:"));
                          const fixedRoles = allRoles.filter((r) => !r.startsWith("employee:"));
                          const orderedRoles = [...employeeRoles, ...fixedRoles];
                          const employeesById = new Map((azlData.validation?.employees || []).map((e) => [e.employeeId, e.name]));
                          const azlValidationRoleLabel = (role) => {
                            if (role === "branchManager") return t("arbeitszeitliste.validation.roleBranchManager", "Filialleiter / FLA");
                            if (role === "areaManager") return t("arbeitszeitliste.validation.roleAreaManager", "Bereichsleiter");
                            const empId = role.startsWith("employee:") ? role.slice("employee:".length) : null;
                            return empId ? (employeesById.get(empId) || empId) : role;
                          };
                          return orderedRoles.map((role) => {
                            const sig = sigByRole.get(role);
                            const eligible = Boolean(azlData.validation?.eligibility?.[role]);
                            const clickable = !sig && !azlData.validation?.locked && eligible;
                            const Wrap = clickable ? Pressable : "div";
                            return (
                              <Wrap key={role} {...(clickable ? { onClick: () => openAzlValidation(role) } : {})} style={{
                                display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                                borderRadius: 12, padding: "10px 12px", marginTop: 6, textAlign: "left",
                                background: sig ? "rgba(30,184,95,.06)" : P.soft,
                              }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{azlValidationRoleLabel(role)}</div>
                                  {sig && (
                                    <div style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 2 }}>{sig.userName} · {new Date(sig.signedAt).toLocaleDateString("de-DE")} {new Date(sig.signedAt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</div>
                                  )}
                                </div>
                                {sig ? (
                                  <span style={{ color: A.green, fontSize: `calc(17px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>✓</span>
                                ) : (
                                  <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: clickable ? A.blue : P.muted, fontWeight: 700, flex: "0 0 auto" }}>
                                    {clickable ? t("arbeitszeitliste.validation.signBtn", "Signer") : t("arbeitszeitliste.validation.pending", "En attente")}
                                  </span>
                                )}
                              </Wrap>
                            );
                          });
                        })()}
                      </section>
                    </>
                  );
                })()}
              </>
            )}
          </>
        )}

        {/* ================= PILOTAGE RÉSEAU ================= */}
        {screen === "home" && isNetworkLeader && (() => {
          const SEVERITY_META = {
            danger: { c: A.red, l: t("notifications.tab.urgent", "Urgent") }, warning: { c: A.orange, l: t("arbeitszeitliste.anomalies.severity.warning", "Attention") }, info: { c: A.blue, l: t("arbeitszeitliste.anomalies.severity.info", "Info") },
          };
          const feed = managerOverview?.feed || [];
          return (
            <>
              {/* Pas de barre d'onglets ici — la navigation entre Aujourd'hui/Filiales/
                  Alertes/Missions se fait déjà via NETWORK_TABS (nav du bas) et Analyse
                  via la tuile CTA d'Aujourd'hui, fidèle au fichier source. La barre de
                  chips que j'avais ajoutée ici dupliquait cette navigation pour rien —
                  retirée sur demande de l'utilisateur. */}
              {managerOverviewError && (
                <p style={{ textAlign: "center", color: A.red, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, margin: "20px 0" }}>{managerOverviewError}</p>
              )}

              {!managerOverviewError && !managerOverview && (
                <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "20px 0" }}>{t("common.loading", "Chargement...")}</p>
              )}

              {/* ================= PILOTAGE RÉSEAU — AUJOURD'HUI =================
                   Reconstruit (retour utilisateur explicite, 2026-08-09) : l'ancienne version
                   dupliquait Analyse (bande de 3 stats + flux d'urgences à plat) — "un module
                   pointe déjà sur un dashboard avec beaucoup de détails, en remettre un ici
                   fait trop". Remplacé par la même grammaire que l'Accueil compte normal :
                   bannière "Ton Assistant" (repliée par défaut, glisse/tap pour déplier une
                   phrase avec les priorités du jour en pastilles cliquables) puis grille
                   Modules — jamais de CA/pertes/Stundenleistung ici, Analyse reste le seul
                   dashboard réseau détaillé. Les 3 sources d'alertes/relances/validations
                   restent réelles (feed serveur, missingReportingDays, missions a_valider),
                   juste reformulées en actions ("Traiter"/"Relancer"/"Valider") plutôt qu'en
                   indicateurs à interpréter. */}
              {managerOverview && pilotScreen === "today" && (() => {
                const openFeed = feed.filter((f) => !f.resolved);
                const openIssue = (f) => setAlertActionId(alertKeyFor(f));
                const openFiliale = (storeId) => { setPilotFilialeId(storeId); setPilotFilialeSection(null); setPilotScreen("filiale-detail"); };
                // Bug réel trouvé (2026-08-24, retour utilisateur : "le module doit toujours
                // afficher un badge quand une mission arrive sur les profils") — ce compteur
                // ne comptait que le statut filiale "a_valider", un statut LEGACY que plus
                // aucun code n'a jamais produit depuis le 2026-08-02 ("le responsable ne
                // doit plus valider quoi que ce soit") — il retombait donc TOUJOURS à 0,
                // aucun badge jamais affiché, même pour une vraie mission fraîchement
                // arrivée. Ajoute le vrai cas actif : une mission ciblée directement sur MOI
                // (targetLeaderIds, voir le ciblage en cascade NL→VKL/BL) pas encore soumise
                // de mon côté. Le filtre "a_valider" reste en place par sécurité (compat
                // silencieuse si un très vieux enregistrement l'avait encore), jamais retiré.
                const missionsAValider = networkMissions
                  .filter((m) => !m.archived)
                  .reduce((sum, m) => {
                    const filialeCount = (m.filiales || []).filter((fid) => m.filialeStatuses?.[fid]?.status === "a_valider").length;
                    const myLeaderStatus = (m.targetLeaderIds || []).includes(user?.proId) ? (m.leaderStatuses || {})[user?.proId] : null;
                    const isMyPendingLeaderTarget = myLeaderStatus && !["validee", "refusee"].includes(myLeaderStatus.status);
                    // Dialogue non lu (retour utilisateur explicite, 2026-08-23 : "il faut des
                    // notifications et des badges pour qu'on soit avertis à chaque fois") —
                    // s'ajoute même si la mission est déjà accomplie de mon côté, une nouvelle
                    // question/réponse mérite toujours d'attirer l'attention.
                    return sum + filialeCount + (isMyPendingLeaderTarget ? 1 : 0) + (m.hasUnread ? 1 : 0);
                  }, 0);
                const perStoreLate = (managerOverview.perStore || []).filter((p) => p.missingReportingDays > 0);

                const dangerFeed = openFeed.filter((f) => f.severity === "danger");
                const warningFeed = openFeed.filter((f) => f.severity !== "danger");

                // Plafond de chips individuels par catégorie avant agrégation (2026-08-23,
                // retour utilisateur explicite : "pour un NL ça devient beaucoup... imagine
                // avec plusieurs notifications") — Nexora vise 1200+ filiales
                // (storeflow_nexora_multi_store_scale) : un chip par filiale par catégorie,
                // sans plafond, transformait la phrase de l'assistant en mur de pastilles
                // illisible dès qu'un NL/VKL avec beaucoup de filiales avait plusieurs alertes
                // du même type. Au-delà de ce plafond, les filiales restantes de CETTE
                // catégorie sont regroupées en un seul chip cliquable ("Relancer 9 filiales")
                // qui ouvre la liste correspondante plutôt qu'un cas précis — jamais un plafond
                // silencieux : le nombre réel reste toujours visible dans le chip agrégé ET
                // dans le texte replié (voir totalPriorityCount plus bas, jamais juste le
                // nombre de chips affichés).
                const PRIORITY_CHIP_CAP = 3;
                const buildPriorityChips = (items, priority, chipTextFor, onClickFor, aggregateTextFor, aggregateOnClick) => {
                  if (!items.length) return [];
                  const shown = items.slice(0, PRIORITY_CHIP_CAP).map((item, i) => ({
                    key: `${priority}-${i}-${item.key}`, priority, chip: chipTextFor(item), onClick: () => onClickFor(item),
                  }));
                  const remaining = items.length - shown.length;
                  if (remaining > 0) {
                    shown.push({ key: `${priority}-aggregate`, priority, chip: aggregateTextFor(remaining), onClick: aggregateOnClick });
                  }
                  return shown;
                };

                const priorities = [
                  ...buildPriorityChips(
                    dangerFeed.map((f) => ({ key: alertKeyFor(f), f })), "urgent",
                    (item) => t("pilotage.today.chipTreatTemplate", { store: storeLabelFor(item.f.storeId) }, `Traiter ${storeLabelFor(item.f.storeId)}`),
                    (item) => openIssue(item.f),
                    (n) => t("pilotage.today.chipTreatMoreTemplate", { n }, `Traiter ${n} autre${n > 1 ? "s" : ""} filiale${n > 1 ? "s" : ""}`),
                    () => setPilotScreen("alertes"),
                  ),
                  ...buildPriorityChips(
                    warningFeed.map((f) => ({ key: alertKeyFor(f), f })), "medium",
                    (item) => t("pilotage.today.chipWatchTemplate", { store: storeLabelFor(item.f.storeId) }, `Surveiller ${storeLabelFor(item.f.storeId)}`),
                    (item) => openIssue(item.f),
                    (n) => t("pilotage.today.chipWatchMoreTemplate", { n }, `Surveiller ${n} autre${n > 1 ? "s" : ""} filiale${n > 1 ? "s" : ""}`),
                    () => setPilotScreen("alertes"),
                  ),
                  ...buildPriorityChips(
                    perStoreLate.map((p) => ({ key: p.storeId, p })), "medium",
                    (item) => t("pilotage.today.chipNudgeTemplate", { store: storeLabelFor(item.p.storeId) }, `Relancer ${storeLabelFor(item.p.storeId)}`),
                    (item) => openFiliale(item.p.storeId),
                    (n) => t("pilotage.today.chipNudgeMoreTemplate", { n }, `Relancer ${n} autre${n > 1 ? "s" : ""} filiale${n > 1 ? "s" : ""}`),
                    () => setPilotScreen("filiales"),
                  ),
                  ...(missionsAValider > 0 ? [{
                    key: "missions-avalider", priority: "low",
                    chip: t("pilotage.today.chipValidateMissionsTemplate", { n: missionsAValider }, `Valider ${missionsAValider} mission${missionsAValider > 1 ? "s" : ""}`),
                    onClick: () => setPilotScreen("missions"),
                  }] : []),
                ];
                // Compte RÉEL de priorités (jamais le nombre de chips après agrégation) — sert
                // à la fois à la couleur d'humeur et au texte replié, pour ne jamais minimiser
                // silencieusement l'ampleur réelle derrière un chip agrégé.
                const totalPriorityCount = dangerFeed.length + warningFeed.length + perStoreLate.length + (missionsAValider > 0 ? 1 : 0);
                const netMoodColor = totalPriorityCount === 0 ? A.blue : totalPriorityCount <= 3 ? A.orange : A.red;

                return (
                  <div className="nx-screen-enter">
                    {/* ASSISTANT RÉSEAU — même grammaire que l'Assistant compte normal
                        (App.jsx ~13338 et suivants) : bannière repliée par défaut, tap pour
                        déplier avec effet de frappe puis minuteur de fermeture auto 8s. */}
                    <section style={{ ...card, borderRadius: 22, marginBottom: 16, overflow: "hidden", transition: "box-shadow .3s ease" }}>
                      <div style={{ display: "flex", gap: 13, alignItems: "flex-start", padding: 16, paddingBottom: netAssistantOpen ? 10 : 16 }}>
                        <span className="nx-bot" style={{
                          flex: "0 0 auto", width: 50, height: 50, borderRadius: 17,
                          display: "grid", placeItems: "center", fontSize: `calc(26px * var(--nx-font-scale, 1))`,
                          animation: "nxBot 2.6s ease-in-out infinite, nxBotRing 2.6s ease-out infinite",
                          transition: "background .4s ease",
                          ...premiumTileStyle(netMoodColor),
                        }}>🤖</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "flex", alignItems: "center", gap: 6, ...eyebrow(netMoodColor) }}>
                            {t("pilotage.today.assistantEyebrow", "Ton assistant réseau")}
                            <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, animation: "nxLive 2s ease infinite" }} />
                          </span>
                          {netAssistantOpen && netAssistantTyping ? (
                            <div style={{ display: "flex", gap: 4, padding: "8px 0 2px" }}>
                              {[0, 1, 2].map((i) => <span key={i} className="nx-typing-dot" style={{ width: 7, height: 7, borderRadius: 99, background: P.muted, animationDelay: `${i * 0.15}s` }} />)}
                            </div>
                          ) : netAssistantOpen ? (
                            <p className="nx-insight-in" style={{ margin: "5px 0 0", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, lineHeight: 1.6, fontWeight: 600 }}>
                              {greetWord} {user?.firstname} ! {priorities.length === 0 ? (
                                t("pilotage.today.assistantAllClear", "Tout est sous contrôle sur ton réseau aujourd'hui 🎉")
                              ) : (
                                <>
                                  {t("home.assistant.remainingPrefix", "Aujourd'hui il te reste à")}{" "}
                                  {priorities.map((p, i) => {
                                    const pColor = { urgent: A.red, medium: A.orange, low: A.blue }[p.priority] || A.blue;
                                    return (
                                      <Fragment key={p.key}>
                                        <Pressable onClick={p.onClick} style={{
                                          display: "inline-block", color: "#fff", fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                                          background: pColor, borderRadius: 8, padding: "1.5px 8px", margin: "0 1px",
                                          lineHeight: 1.5, verticalAlign: "-1px",
                                        }}>{p.chip}</Pressable>
                                        {i < priorities.length - 2 ? ", " : i === priorities.length - 2 ? t("home.assistant.and", " et ") : ""}
                                      </Fragment>
                                    );
                                  })}
                                  {t("home.assistant.remainingSuffix", ". Courage, à très bientôt !")} 💪
                                </>
                              )}
                            </p>
                          ) : (
                            <p style={{ margin: "5px 0 0", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, lineHeight: 1.4, fontWeight: 600 }}>
                              {totalPriorityCount === 0 ? t("pilotage.today.assistantAllUpToDate", "Rien à signaler sur ton réseau aujourd'hui 🎉") : t("pilotage.today.assistantPeekCount", { n: totalPriorityCount }, `${totalPriorityCount} priorité${totalPriorityCount > 1 ? "s" : ""} t'attend${totalPriorityCount > 1 ? "ent" : ""} aujourd'hui`)}
                            </p>
                          )}
                        </div>
                        {netAssistantOpen && (
                          <Pressable onClick={closeNetAssistant} aria-label={t("home.assistant.collapseAria", "Replier l'assistant")} style={{
                            flex: "0 0 auto", width: 26, height: 26, borderRadius: 99, background: P.soft,
                            display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 1,
                          }}>✕</Pressable>
                        )}
                      </div>

                      {netAssistantOpen ? (
                        <div className="nx-assistant-timer" style={{ height: 2.5, background: netMoodColor, opacity: .4, animationDuration: `${netAssistantAutoCloseMs}ms` }} />
                      ) : (
                        <div className="nx-assistant-peek" onClick={openNetAssistant} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "2px 0 12px", cursor: "pointer" }}>
                          <svg className="nx-assistant-chevron" width="18" height="9" viewBox="0 0 18 9" fill="none">
                            <path d="M1 1L9 8L17 1" stroke={P.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>{t("pilotage.today.assistantSwipeHint", "Glisse pour voir les priorités du réseau")}</span>
                        </div>
                      )}
                    </section>

                    {/* Modules — mêmes icônes/couleurs que l'Accueil compte normal (voir
                        NETWORK_HOME_MODULES). Filiales/Analyse/Missions/Alertes couvrent la
                        navigation déjà offerte par la nav du bas + la tuile Analyse d'avant ;
                        les 4 derniers reprennent tels quels les accès rapides existants. */}
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", margin: "2px 2px 11px" }}>
                      <h3 style={{ margin: 0, fontSize: `calc(16.5px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: "-.02em" }}>{t("home.section.modules", "Modules")}</h3>
                      <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>{t("home.section.accessCount", { n: NETWORK_HOME_MODULES.length }, `${NETWORK_HOME_MODULES.length} accès`)}</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(110px, 1fr))" : "1fr 1fr 1fr 1fr", gap: 10 }}>
                      {NETWORK_HOME_MODULES.filter((m) => !isModuleHiddenForUser(m.id)).map((m) => {
                        const badge = m.id === "netMissions" ? missionsAValider
                          : m.id === "netAlertes" ? openFeed.length
                          : m.id === "netInfos" ? infoMandatoryPending
                          : m.id === "netRundschreiben" ? rundschreibenMandatoryPending
                          : m.id === "netAppointments" ? pilotAppointments.filter((a) => !a.confirmed && !a.cancelled && (a.storeAgreed || a.proposedBySide === "store")).length
                          : m.id === "netVertretung" ? vertretungList.filter((v) => v.status === "PENDING" && String(v.approverEmployeeId) === String(user?.proId || user?.id || "")).length
                          // Retour utilisateur explicite (2026-08-27) — même calcul que le
                          // compte normal (pendingModuleCounts.filialeReport plus haut) :
                          // rapports reçus non "traité" ET dont la to-do n'est pas déjà
                          // entièrement cochée.
                          : m.id === "netFilialeReport" ? pendingModuleCounts.filialeReport
                          : 0;
                        return (
                          <Pressable key={m.id}
                            onClick={() =>
                              m.id === "netFiliales" ? setPilotScreen("filiales")
                              : m.id === "netAnalyse" ? setPilotScreen("analyse")
                              : m.id === "netMissions" ? setPilotScreen("missions")
                              : m.id === "netAlertes" ? setPilotScreen("alertes")
                              : m.id === "netPromo" ? setScreen("store")
                              : m.id === "netInfos" ? (setInfoCategoryFilter("general"), setInfoScreen("hub"), setScreen("infos"))
                              : m.id === "netRundschreiben" ? (setInfoCategoryFilter("rundschreiben"), setInfoScreen("hub"), setScreen("infos"))
                              // Retour utilisateur explicite (2026-08-16) : "la messagerie doit
                              // toujours s'ouvrir sur l'historique et non directement dans le
                              // tiroir nouvelle conversation" — avant ce fix, la tuile Messages
                              // de l'Accueil réseau sautait directement dans le tiroir "Nouvelle
                              // communication" (préremplie multi-branch), jamais l'historique.
                              // Même patron que la tuile Messages du compte normal (HOME_MODULES
                              // ci-dessus, déjà correcte : setMsgScreen("list")).
                              : m.id === "netMessages" ? (setMsgScreen("list"), setScreen("messages"))
                              : m.id === "netAppointments" ? setPilotScreen("appointments")
                              : m.id === "netVertretung" ? (setVertretungScreen("hub"), loadVertretungList(), loadVertretungPeers(), setScreen("vertretung"))
                              : m.id === "netFilialeReport" ? (setFrTab("mine"), loadFrMine(), loadFrReceived(), setScreen("filialeReport"))
                              : null
                            }
                            style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "4px 0", position: "relative" }}>
                            <span style={{
                              width: 54, height: 54, borderRadius: 18, color: "#fff",
                              border: "1px solid rgba(255,255,255,.16)",
                              display: "grid", placeItems: "center", fontSize: `calc(24px * var(--nx-font-scale, 1))`,
                              ...premiumTileStyle(m.color),
                            }}><EmojiIcon e={m.emoji} size={24} /></span>
                            {badge > 0 && (
                              <span className="nx-notif-dot" style={{
                                position: "absolute", top: -2, right: "calc(50% - 32px)", minWidth: 18, height: 18, padding: "0 4px",
                                borderRadius: 99, background: A.red, border: `2px solid ${P.bg}`, color: "#fff",
                                fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, display: "grid", placeItems: "center",
                              }}>{badge}</span>
                            )}
                            <span style={{
                              fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink, textAlign: "center", lineHeight: 1.15,
                              maxWidth: 74, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box",
                              WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                            }}>{t(`home.netModule.${m.id}`, m.label)}</span>
                          </Pressable>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* ---------- Rendez-vous d'inventaire (page dédiée, GR) ---------- retour
                   utilisateur explicite (2026-08-03) : une date PAR filiale (jamais un lot
                   visible par plusieurs filiales, voir POST /api/inventur/appointments côté
                   serveur). Une ligne par filiale du périmètre, peu importe qu'elle ait déjà
                   un rendez-vous ou non — le but est de voir d'un coup d'œil lesquelles n'ont
                   encore rien de proposé. */}
              {managerOverview && pilotScreen === "appointments" && (() => {
                const storesInScope = managerOverview.stores || [];
                return <div className="nx-screen-enter" style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(340px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>{storesInScope.map((storeId, storeIdx) => {
                  const activeApt = pilotAppointments.find((a) => a.storeId === storeId && !a.confirmed && !a.cancelled);
                  const lastConfirmed = pilotAppointments.find((a) => a.storeId === storeId && a.confirmed && !a.cancelled);
                  const apt = activeApt || lastConfirmed || null;
                  let statusLabel, statusColor;
                  if (!apt) { statusLabel = t("pilotage.appointment.statusNone", "Aucune date proposée"); statusColor = P.muted; }
                  else if (apt.confirmed) { statusLabel = t("pilotage.appointment.statusConfirmedTemplate", { date: fmtFR(apt.proposedDate) }, `Confirmé · ${fmtFR(apt.proposedDate)}`); statusColor = A.green; }
                  else if (apt.storeAgreed) { statusLabel = t("pilotage.appointment.statusAgreedTemplate", { date: fmtFR(apt.proposedDate) }, `Acceptée par la filiale · ${fmtFR(apt.proposedDate)} — à valider`); statusColor = A.orange; }
                  else if (apt.proposedBySide === "store") { statusLabel = t("pilotage.appointment.statusCounteredTemplate", { date: fmtFR(apt.proposedDate) }, `Contre-proposition · ${fmtFR(apt.proposedDate)}`); statusColor = A.orange; }
                  else { statusLabel = t("pilotage.appointment.statusPendingTemplate", { date: fmtFR(apt.proposedDate) }, `En attente de la filiale · ${fmtFR(apt.proposedDate)}`); statusColor = A.blue; }
                  return (
                    <Pressable key={storeId} feedback className="nx-list-item-in" onClick={() => openPilotAppointment(storeId)} style={{ ...card, ...tile, padding: 13, marginBottom: isWide ? 0 : 8, alignItems: "flex-start", animationDelay: `${Math.min(storeIdx, 14) * 28}ms` }}>
                      <span style={{ width: 36, height: 36, borderRadius: 12, background: "rgba(54,79,231,.1)", display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>📅</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{storeLabelFor(storeId)}</strong>
                        <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: statusColor, fontWeight: 700, marginTop: 2 }}>{statusLabel}</div>
                      </div>
                      <span style={{ color: P.muted, fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>›</span>
                    </Pressable>
                  );
                })}</div>;
              })()}

              {managerOverview && pilotScreen === "alertes" && (
                <div className="nx-screen-enter">
                  <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
                    {[{ id: "all", l: t("notifications.tab.all", "Toutes") }, { id: "danger", l: t("notifications.tab.urgent", "Urgent") }, { id: "warning", l: t("arbeitszeitliste.anomalies.severity.warning", "Attention") }, { id: "info", l: t("arbeitszeitliste.anomalies.severity.info", "Info") }].map((s) => (
                      <Pressable key={s.id} onClick={() => setAlertesSeverityFilter(s.id)} style={{
                        flex: "0 0 auto", padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                        background: alertesSeverityFilter === s.id ? A.blue : P.soft, color: alertesSeverityFilter === s.id ? "#fff" : P.muted,
                      }}>{s.l}</Pressable>
                    ))}
                  </div>
                  {feed.filter((f) => !f.resolved && (alertesSeverityFilter === "all" || f.severity === alertesSeverityFilter)).length === 0 && (
                    <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, marginTop: 30 }}>{t("pilotage.alertes.emptyQueueText", "Rien dans cette file pour l'instant.")}</p>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(360px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
                  {feed.filter((f) => !f.resolved && (alertesSeverityFilter === "all" || f.severity === alertesSeverityFilter)).map((f, fIdx) => {
                    const meta = SEVERITY_META[f.severity] || SEVERITY_META.info;
                    return (
                      <Pressable key={alertKeyFor(f)} feedback className="nx-list-item-in" onClick={() => setAlertActionId(alertKeyFor(f))} style={{ ...card, ...tile, padding: 13, marginBottom: isWide ? 0 : 8, alignItems: "flex-start", width: "100%", boxSizing: "border-box", animationDelay: `${Math.min(fIdx, 14) * 28}ms` }}>
                        <span style={{
                          width: 36, height: 36, borderRadius: 12, flex: "0 0 auto", display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`,
                          background: `${meta.c}22`,
                        }}><EmojiIcon e={f.icon || "📌"} /></span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{storeLabelFor(f.storeId)}</strong>
                          <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 2 }}>{f.title}</div>
                          {f.assignedToName && <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: A.blue, fontWeight: 700, marginTop: 2 }}>👤 {f.assignedToName}</div>}
                        </div>
                        <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: meta.c, borderRadius: 99, padding: "4px 10px", flex: "0 0 auto" }}>{meta.l}</span>
                      </Pressable>
                    );
                  })}
                  </div>
                </div>
              )}

              {/* Refonte visuelle — même patron que le module Tâches (retour utilisateur
                  explicite, 2026-08-20 : "Refaire l'interface comme le module Tâches").
                  "En cours" toujours affichée ; Terminées/Archivées repliées par défaut
                  (en-tête + compteur, cliquable pour déplier) au lieu des 3 onglets pleine
                  largeur d'avant. "+ Nouvelle mission" devient un FAB flottant, même
                  position/taille que celui de Tâches. Aucune logique changée : mêmes
                  listes, mêmes actions par carte, même bouton "Tout supprimer" sur les
                  archives (déplacé sous l'en-tête dépliée). */}
              {pilotScreen === "missions" && (() => {
                const isLeaderMission = (m) => (m.targetLeaderIds || []).length > 0;
                const typedMissions = networkMissions.filter((m) => isLeaderMission(m) === (missionTypeTab === "leader"));
                const nonArchived = typedMissions.filter((m) => !m.archived);
                const enCoursList = nonArchived.filter((m) => missionEffectiveStatus(m) === "en_cours");
                const termineesList = nonArchived.filter((m) => missionEffectiveStatus(m) === "terminee");
                const archiveesList = typedMissions.filter((m) => m.archived);
                // Badges = missions EN COURS uniquement (retour utilisateur explicite,
                // 2026-08-24 : "des missions terminées ne doivent pas alimenter les
                // badges") — jamais un total brut qui inclurait ce qui est déjà réglé.
                const leaderCount = networkMissions.filter((m) => !m.archived && isLeaderMission(m) && missionEffectiveStatus(m) === "en_cours").length;
                const filialeCount = networkMissions.filter((m) => !m.archived && !isLeaderMission(m) && missionEffectiveStatus(m) === "en_cours").length;

                const renderMissionCard = (m) => {
                  const myStatus = missionEffectiveStatus(m);
                  const meta = MISSION_OVERALL_META[myStatus];
                  const resolvedCount = m.filiales.filter((f) => ["validee", "refusee"].includes(m.filialeStatuses[f]?.status)).length;
                  const remainingLabel = missionRemainingLabel(m);
                  // Coloration — retour utilisateur explicite (2026-08-23) : même principe
                  // que le module Tâches pour "Terminées" (voir renderTaskCard/doneFade,
                  // ligne ~6581) — grisé (opacity) plutôt qu'un fond vert, pour rester
                  // cohérent d'un module à l'autre et distinguer visuellement "encore à
                  // l'œil" (en cours) de "déjà traité" (grisé, plus besoin d'attention).
                  // Rouge clair pour archivée conservé tel quel — signale explicitement
                  // "rangée", un état distinct de "terminée", jamais concerné par ce
                  // changement.
                  const cardTint = m.archived
                    ? { background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.25)" }
                    : myStatus === "terminee"
                    ? { opacity: .62 }
                    : {};
                  return (
                    <div key={m.id} style={{ ...card, ...cardTint, padding: 14, marginBottom: 9, display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <Pressable onClick={() => openMissionDetail(m.id)} style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                          <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, textDecoration: (myStatus === "terminee" && !m.archived) ? "line-through" : "none", ...(m.archived ? { color: P.muted } : {}) }}>{m.title}</strong>
                          {!m.archived && (
                            <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: meta.c, borderRadius: 99, padding: "3px 8px", whiteSpace: "nowrap", flex: "0 0 auto" }}>{meta.l}</span>
                          )}
                          {m.hasUnread && (
                            <span aria-label={t("missions.detail.unreadDialogueAria", "Nouveau message dans le dialogue")} title={t("missions.detail.unreadDialogueAria", "Nouveau message dans le dialogue")} style={{
                              width: 9, height: 9, borderRadius: 99, background: A.red, flex: "0 0 auto", boxShadow: "0 0 0 2px rgba(239,68,68,.2)",
                            }} />
                          )}
                        </div>
                        <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>
                          {(m.targetLeaderIds || []).length
                            ? `👤 ${m.targetLeaderIds.map((id) => (m.targetLeaderNames || {})[id] || id).join(", ")}`
                            : t("pilotage.missions.storesCountTemplate", { n: m.filiales.length, resolved: resolvedCount }, `${resolvedCount}/${m.filiales.length} filiale${m.filiales.length > 1 ? "s" : ""}`)}
                          {remainingLabel ? ` — ${remainingLabel}` : ""}{(m.targetLeaderIds || []).length ? "" : ` · ${m.destinataires}`}{m.echeanceDate ? ` · ${t("pilotage.missions.deadlineLabelTemplate", { date: fmtFR(m.echeanceDate) }, `échéance ${fmtFR(m.echeanceDate)}`)}${m.echeanceTime ? ` ${t("pilotage.missions.deadlineTimeSuffixTemplate", { time: m.echeanceTime }, `à ${m.echeanceTime}`)}` : ""}` : ""}
                        </div>
                        {m.relatedRundschreibenId && (
                          <div style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: A.navy, fontWeight: 700, marginTop: 2 }}>
                            📰 {t("rundschreiben.mission.linkedLabelTemplate", { title: m.relatedRundschreibenTitle }, `Issue de : ${m.relatedRundschreibenTitle}`)}
                          </div>
                        )}
                      </Pressable>
                      {/* Gestion réservée au CRÉATEUR (retour utilisateur explicite, 2026-08-23,
                          capture d'écran à l'appui : "Une mission reçue ne doit jamais comporter
                          ces boutons") — même garde-fou que le bloc d'actions de la fiche détail
                          (2026-08-24), qui manquait ici sur la carte de LISTE : un Bereichsleiter (BL) voyait
                          Modifier/Archiver/Supprimer sur "Tout", une mission créée par un autre
                          responsable, simplement parce que sa propre filiale y est concernée. */}
                      {String(user?.proId || "") === String(m.createdBy) && (
                      <div style={{ display: "flex", gap: 5, flex: "0 0 auto" }}>
                        {!m.archived && (
                          <Pressable onClick={() => openMissionEdit(m)} aria-label={t("missions.editAriaTemplate", { title: m.title }, `Modifier ${m.title}`)} style={{
                            width: 30, height: 30, borderRadius: 99, background: "rgba(54,79,231,.1)", color: A.blue, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                          }}>✏️</Pressable>
                        )}
                        <Pressable onClick={() => archiveMission(m.id, !m.archived)} aria-label={m.archived ? t("missions.restoreAriaTemplate", { title: m.title }, `Restaurer ${m.title}`) : t("missions.archiveAriaTemplate", { title: m.title }, `Archiver ${m.title}`)} style={{
                          width: 30, height: 30, borderRadius: 99, background: P.soft, color: P.muted, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                        }}>{m.archived ? "↩️" : "🗄️"}</Pressable>
                        <Pressable onClick={() => deleteMission(m)} aria-label={t("missions.deleteAriaTemplate", { title: m.title }, `Supprimer ${m.title}`)} style={{
                          width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.1)", color: A.red, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                        }}>🗑️</Pressable>
                      </div>
                      )}
                      {/* Retrait personnel — retour utilisateur explicite, 2026-08-24 : "chaque
                          destinataire peut retirer SA PROPRE copie" une fois terminée.
                          Jamais le créateur (déjà géré ci-dessus), jamais avant que SA part
                          à lui soit terminée (myStatus, voir missionEffectiveStatus). */}
                      {String(user?.proId || "") !== String(m.createdBy) && !m.archived && myStatus === "terminee" && (
                        <Pressable onClick={() => dismissMission(m)} aria-label={t("missions.dismissAriaTemplate", { title: m.title }, `Retirer ${m.title} de ma liste`)} style={{
                          width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.1)", color: A.red, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                        }}>🗑️</Pressable>
                      )}
                    </div>
                  );
                };

                return (
                  <>
                    {/* Deux natures de mission totalement différentes (retour utilisateur
                        explicite, 2026-08-24) — deux onglets séparés, chacun avec sa PROPRE
                        structure En cours/Terminées/Archivées, jamais mélangées dans une
                        même liste. */}
                    <div className="nx-chips" style={{ display: "flex", gap: 7, marginBottom: 14 }}>
                      {[
                        { id: "filiales", l: t("pilotage.missions.tabFiliales", "Filiales"), n: filialeCount },
                        { id: "leader", l: t("pilotage.missions.tabResponsables", "Responsables"), n: leaderCount },
                      ].map((tb) => (
                        <Pressable key={tb.id} onClick={() => setMissionTypeTab(tb.id)} style={{
                          flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 4px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                          background: missionTypeTab === tb.id ? A.purple : P.soft, color: missionTypeTab === tb.id ? "#fff" : P.muted,
                        }}>
                          {tb.l}
                          {tb.n > 0 && (
                            <span style={{
                              fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, minWidth: 16, height: 16, padding: "0 4px", display: "grid", placeItems: "center",
                              background: missionTypeTab === tb.id ? "rgba(255,255,255,.28)" : "rgba(139,47,214,.15)", color: missionTypeTab === tb.id ? "#fff" : A.purple,
                            }}>{tb.n}</span>
                          )}
                        </Pressable>
                      ))}
                    </div>

                    {/* Bascule Liste/Calendrier (2026-08-31, retour utilisateur — le
                        calendrier montre la charge par jour et distingue échéance filiale
                        (pastille pleine) vs échéance de synthèse GR (anneau creux), jamais
                        deux écrans séparés à maintenir : la liste reste le mode par défaut,
                        le calendrier est une lecture alternative des MÊMES données. */}
                    <Pressable onClick={() => setMissionsCalendarView((v) => !v)} style={{
                      width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "9px 4px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                      background: P.soft, color: A.purple, marginBottom: 14,
                    }}>
                      {missionsCalendarView ? `📋 ${t("missions.calendar.toggleList", "Vue liste")}` : `📅 ${t("missions.calendar.toggleCalendar", "Vue calendrier")}`}
                    </Pressable>

                    {!missionsCalendarView && (
                      <>
                        {enCoursList.length === 0 && (
                          <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 28 }}>
                            <span style={{ fontSize: `calc(32px * var(--nx-font-scale, 1))`, marginBottom: 8 }}>🎯</span>
                            <strong style={{ fontSize: `calc(14.5px * var(--nx-font-scale, 1))`}}>{t("missions.emptyOngoing", "Aucune mission en cours.")}</strong>
                          </section>
                        )}
                        {enCoursList.map(renderMissionCard)}

                        {termineesList.length > 0 && (
                          <>
                            <Pressable onClick={() => setMissionSectionsOpen((s) => ({ ...s, done: !s.done }))} style={{
                              width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 4px", marginTop: 8,
                              fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".06em",
                            }}>
                              <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, width: 9 }}>{missionSectionsOpen.done ? "▾" : "▸"}</span>
                              {t("tasks.tabDone", "Terminées")}
                              <span style={{ background: P.soft, borderRadius: 99, padding: "1px 8px", color: P.ink }}>{termineesList.length}</span>
                            </Pressable>
                            {missionSectionsOpen.done && termineesList.map(renderMissionCard)}
                          </>
                        )}

                        {archiveesList.length > 0 && (
                          <>
                            <Pressable onClick={() => setMissionSectionsOpen((s) => ({ ...s, archived: !s.archived }))} style={{
                              width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 4px", marginTop: termineesList.length > 0 ? 0 : 8,
                              fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".06em",
                            }}>
                              <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, width: 9 }}>{missionSectionsOpen.archived ? "▾" : "▸"}</span>
                              {t("messages.tabArchived", "Archivées")}
                              <span style={{ background: P.soft, borderRadius: 99, padding: "1px 8px", color: P.ink }}>{archiveesList.length}</span>
                            </Pressable>
                            {missionSectionsOpen.archived && (
                              <>
                                <Pressable onClick={deleteAllArchivedMissions} style={{
                                  width: "100%", textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                                  background: "rgba(239,68,68,.1)", color: A.red, marginTop: 8, marginBottom: 14,
                                }}>🗑️ {t("deliveryNotes.deleteAllBtn", "Tout supprimer")}</Pressable>
                                {archiveesList.map(renderMissionCard)}
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}

                    {missionsCalendarView && (() => {
                      const year = pilotMissionsCalMonth.getFullYear();
                      const month = pilotMissionsCalMonth.getMonth() + 1;
                      const cellsISO = buildMonthGridISO(year, month);
                      const weeks = [];
                      for (let i = 0; i < cellsISO.length; i += 7) weeks.push(cellsISO.slice(i, i + 7));
                      const todayStr = todayISO();
                      const CAL_WEEKDAYS = [
                        t("missions.calendar.weekday.mon", "Lu"), t("missions.calendar.weekday.tue", "Ma"), t("missions.calendar.weekday.wed", "Me"),
                        t("missions.calendar.weekday.thu", "Je"), t("missions.calendar.weekday.fri", "Ve"), t("missions.calendar.weekday.sat", "Sa"), t("missions.calendar.weekday.sun", "Di"),
                      ];
                      // Ratio d'accomplissement par jour — branche sur leaderStatuses ou
                      // filialeStatuses selon la nature de la mission (retour utilisateur
                      // explicite : "il y a 2 possibilités, un pour responsable et un pour
                      // les filiales"), jamais uniquement filialeStatuses. L'anneau creux de
                      // synthèse (echeanceSyntheseDate) ne compte JAMAIS dans ce ratio — même
                      // règle que dans l'exploration en artefact.
                      const dayStats = (dayISO) => {
                        const dueHere = typedMissions.filter((m) => !m.archived && m.echeanceDate === dayISO);
                        const syntheseHere = typedMissions.filter((m) => !m.archived && m.sourceType === "rundschreiben" && m.echeanceSyntheseDate && m.echeanceSyntheseDate === dayISO);
                        let total = 0, resolved = 0;
                        dueHere.forEach((m) => {
                          if (isLeaderMission(m)) {
                            total += (m.targetLeaderIds || []).length;
                            resolved += (m.targetLeaderIds || []).filter((id) => ["validee", "refusee"].includes((m.leaderStatuses || {})[id]?.status)).length;
                          } else {
                            total += m.filiales.length;
                            resolved += m.filiales.filter((f) => ["validee", "refusee"].includes((m.filialeStatuses || {})[f]?.status)).length;
                          }
                        });
                        const state = dueHere.length === 0 ? null : (total > 0 && resolved >= total) ? "done" : dayISO < todayStr ? "overdue" : "pending";
                        return { dueHere, syntheseHere, total, resolved, state };
                      };
                      const selStats = dayStats(pilotMissionsCalSelectedDate);
                      const selMissions = [...new Set([...selStats.dueHere, ...selStats.syntheseHere])];
                      const STATE_COLOR = { done: A.green, overdue: A.red, pending: A.orange };

                      return (
                        <>
                          <div style={{ ...card, padding: 14, marginBottom: 12 }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                              <Pressable onClick={() => setPilotMissionsCalMonth((d) => { const n = new Date(d); n.setMonth(n.getMonth() - 1); return n; })} aria-label={t("common.previous", "Précédent")} style={{
                                width: 32, height: 32, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`,
                              }}>‹</Pressable>
                              <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{MONTH_NAMES_FR[pilotMissionsCalMonth.getMonth()]} {year}</strong>
                              <Pressable onClick={() => setPilotMissionsCalMonth((d) => { const n = new Date(d); n.setMonth(n.getMonth() + 1); return n; })} aria-label={t("common.next", "Suivant")} style={{
                                width: 32, height: 32, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`,
                              }}>›</Pressable>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "22px repeat(7, 1fr)", gap: 3, marginBottom: 4 }}>
                              <span />
                              {CAL_WEEKDAYS.map((w, i) => (
                                <span key={i} style={{ textAlign: "center", fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{w}</span>
                              ))}
                            </div>

                            {weeks.map((week, wi) => (
                              <div key={wi} style={{ display: "grid", gridTemplateColumns: "22px repeat(7, 1fr)", gap: 3, marginBottom: 3 }}>
                                <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, color: P.muted, display: "grid", placeItems: "center", fontWeight: 700 }}>{isoWeekNumber(week[0])}</span>
                                {week.map((dayISO) => {
                                  const inMonth = Number(dayISO.slice(5, 7)) === month;
                                  const isToday = dayISO === todayStr;
                                  const isSelected = dayISO === pilotMissionsCalSelectedDate;
                                  const stats = dayStats(dayISO);
                                  const color = stats.state ? STATE_COLOR[stats.state] : null;
                                  return (
                                    <Pressable key={dayISO} onClick={() => setPilotMissionsCalSelectedDate(dayISO)} style={{
                                      position: "relative", borderRadius: 10, padding: "4px 2px 5px", minHeight: 40,
                                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2,
                                      opacity: inMonth ? 1 : .32,
                                      background: color ? `${color}1f` : "transparent",
                                      border: isSelected ? `2px solid ${A.purple}` : color ? `1.5px solid ${color}` : "1.5px solid transparent",
                                      boxSizing: "border-box",
                                    }}>
                                      {isToday && (
                                        <span style={{ position: "absolute", top: 2, right: 2, width: 5, height: 5, borderRadius: 99, background: A.purple }} />
                                      )}
                                      <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: isSelected ? 900 : 700, color: inMonth ? P.ink : P.muted }}>{Number(dayISO.slice(8, 10))}</span>
                                      {stats.total > 0 && (
                                        <span style={{ fontSize: `calc(8.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color }}>{stats.resolved}/{stats.total}</span>
                                      )}
                                      {stats.syntheseHere.length > 0 && (
                                        <span style={{ width: 6, height: 6, borderRadius: 99, border: `1.5px solid ${A.blue}`, position: "absolute", bottom: 3, right: 3 }} />
                                      )}
                                    </Pressable>
                                  );
                                })}
                              </div>
                            ))}

                            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${P.line}` }}>
                              <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}><span style={{ width: 8, height: 8, borderRadius: 99, background: A.green }} />{t("missions.calendar.legendDone", "Accompli")}</span>
                              <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}><span style={{ width: 8, height: 8, borderRadius: 99, background: A.orange }} />{t("missions.calendar.legendPending", "En attente")}</span>
                              <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}><span style={{ width: 8, height: 8, borderRadius: 99, background: A.red }} />{t("missions.calendar.legendOverdue", "En retard")}</span>
                              <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}><span style={{ width: 8, height: 8, borderRadius: 99, border: `1.5px solid ${A.blue}` }} />{t("missions.calendar.legendSynthese", "Échéance de synthèse")}</span>
                            </div>
                          </div>

                          <div style={{ marginBottom: 90 }}>
                            <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 8 }}>
                              {fmtFR(pilotMissionsCalSelectedDate)}{pilotMissionsCalSelectedDate === todayStr ? ` · ${t("dashboard.eyebrow", "Aujourd'hui")}` : ""}
                            </strong>
                            {selMissions.length === 0 ? (
                              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 22 }}>
                                <span style={{ fontSize: `calc(24px * var(--nx-font-scale, 1))`, marginBottom: 6 }}>📭</span>
                                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("missions.calendar.emptyDay", "Aucune échéance ce jour-là.")}</span>
                              </section>
                            ) : selMissions.map(renderMissionCard)}
                          </div>
                        </>
                      );
                    })()}

                    {/* Un Bereichsleiter n'a aucun subordonné responsable à cibler (voir la
                        même garde ci-dessous sur le sélecteur de responsables, ~40939) —
                        le "+" reste visible sur l'onglet Filiales mais disparaît sur
                        l'onglet Responsables pour ce rôle (retour utilisateur explicite,
                        capture d'écran à l'appui : le bouton apparaissait quand même sur
                        "Responsables" pour un profil BL). */}
                    {(missionTypeTab === "filiales" || ["Verkaufsleiter", "Niederlassungsleiter"].includes(user?.role)) && (
                      <Pressable onClick={() => {
                        if (missionsCalendarView) {
                          setMissionForm({ ...emptyMissionForm, targetMode: missionTypeTab, echeanceDate: pilotMissionsCalSelectedDate });
                          setMissionEditId(null); setMissionEmployeeOptions([]); setSheet("missionForm");
                        } else {
                          openMissionNew(missionTypeTab);
                        }
                      }} aria-label={t("tasks.newMissionLabel", "Nouvelle mission")} style={{
                        position: "fixed", right: 18, bottom: "calc(92px + env(safe-area-inset-bottom))", zIndex: 33,
                        width: 58, height: 58, borderRadius: 20, color: "#fff",
                        display: "grid", placeItems: "center", fontSize: `calc(30px * var(--nx-font-scale, 1))`, fontWeight: 300,
                        ...premiumTileStyle(A.purple),
                      }}>＋</Pressable>
                    )}
                  </>
                );
              })()}

              {/* Fiche mission — style tableau premium (retour utilisateur explicite :
                  "vraiment premium et style tableau"). Chaque filiale ciblée a sa propre
                  ligne avec son PROPRE statut (server/missions/repository.js) — jamais un
                  statut global appliqué à toutes, et ça se met à jour en direct dès qu'un
                  responsable valide/refuse ou qu'une filiale soumet son résultat
                  (loadMissions() re-fetch à chaque action). */}
              {/* Retour utilisateur explicite (2026-09-01) : ce sélecteur restait affiché
                  quel que soit le sous-onglet d'Analyse, mais Évolution (fenêtre glissante
                  pilotée par evolutionWeekOffset, voir son propre stepper ‹ ›) et
                  Comparaison (son propre sélecteur de période local, voir "réservés aux
                  autres onglets" plus bas) n'en tiennent JAMAIS compte — un contrôle qui
                  semblait s'appliquer à toute la page mais n'avait aucun effet sur ces deux
                  écrans précis, perçu à raison comme un bug ("le choix des 12 semaines ne
                  change jamais"). Masqué désormais sur les onglets où il est réellement
                  sans effet, jamais retiré là où il agit vraiment (Résumé/Filiales). */}
              {(pilotScreen === "filiales" || (pilotScreen === "analyse" && ["resume", "filiales"].includes(analyseTab))) && (
                <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 10, paddingBottom: 2 }}>
                  {[{ id: "jour", l: t("pilotage.period.day", "Aujourd'hui") }, { id: "semaine", l: t("pilotage.period.week", "Cette semaine") }, { id: "mois", l: t("pilotage.period.month", "Ce mois") }, { id: "trimestre", l: t("pilotage.period.quarter", "Ce trimestre") }, { id: "annee", l: t("pilotage.period.year", "Cette année") }].map((p) => (
                    <Pressable key={p.id} onClick={() => setPilotPeriod(p.id)} style={{
                      flex: "0 0 auto", padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                      background: pilotPeriod === p.id ? A.blue : P.soft, color: pilotPeriod === p.id ? "#fff" : P.muted,
                    }}>{p.l}</Pressable>
                  ))}
                </div>
              )}

              {/* Stepper ‹ › — décale periodOffset en arrière/avant pour la granularité
                  pilotPeriod choisie ci-dessus. Demande utilisatrice (2026-08-15), maquette
                  https://claude.ai/code/artifact/c627bbf9-3964-42c1-b42b-51ea6b143937.
                  Jamais dans le futur (bouton › désactivé si periodOffset >= 0). */}
              {(pilotScreen === "filiales" || pilotScreen === "analyse") && (() => {
                const d = new Date();
                if (pilotPeriod === "jour") d.setDate(d.getDate() + periodOffset);
                else if (pilotPeriod === "semaine") d.setDate(d.getDate() + periodOffset * 7);
                else if (pilotPeriod === "mois") { d.setDate(1); d.setMonth(d.getMonth() + periodOffset); }
                else if (pilotPeriod === "trimestre") { d.setDate(1); d.setMonth(d.getMonth() + periodOffset * 3); }
                else d.setFullYear(d.getFullYear() + periodOffset);
                let label;
                if (pilotPeriod === "jour") label = d.toLocaleDateString(intlLocale, { weekday: "short", day: "numeric", month: "long", year: "numeric" });
                else if (pilotPeriod === "semaine") label = `${t("pilotage.periodStepper.weekPrefix", "Semaine")} ${isoWeek(d)} · ${d.getFullYear()}`;
                else if (pilotPeriod === "mois") label = d.toLocaleDateString(intlLocale, { month: "long", year: "numeric" });
                else if (pilotPeriod === "trimestre") label = `T${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
                else label = `${d.getFullYear()}`;
                return (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 6 }}>
                    <Pressable onClick={() => setPeriodOffset((o) => o - 1)} aria-label={t("pilotage.periodStepper.prevAria", "Période précédente")} style={{
                      width: 30, height: 30, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink, flexShrink: 0,
                    }}>‹</Pressable>
                    <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, textTransform: "capitalize", minWidth: 120, textAlign: "center" }}>{label}</span>
                    <Pressable onClick={() => periodOffset < 0 && setPeriodOffset((o) => o + 1)} aria-label={t("pilotage.periodStepper.nextAria", "Période suivante")} style={{
                      width: 30, height: 30, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, fontWeight: 800, color: periodOffset < 0 ? P.ink : P.muted, opacity: periodOffset < 0 ? 1 : 0.35, flexShrink: 0,
                    }}>›</Pressable>
                  </div>
                );
              })()}
              {(pilotScreen === "filiales" || pilotScreen === "analyse") && periodOffset !== 0 && (
                <div style={{ textAlign: "center", marginBottom: 14 }}>
                  <Pressable onClick={() => setPeriodOffset(0)} style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.blue, padding: "4px 10px", borderRadius: 99, display: "inline-block" }}>{t("pilotage.periodStepper.backToToday", "↺ Revenir à aujourd'hui")}</Pressable>
                </div>
              )}

              {/* ================= PILOTAGE RÉSEAU — FILIALES =================
                   Fidèle à nexora-app_1.zip (source lignes ~4794-4893) : par défaut
                   groupées par vrai Bezirk (registre server/stores/*, Phase 1) et
                   triées par sévérité réelle, avec badge Critique/À surveiller/
                   Conforme — 7 options de tri au total (le tri "Attention" repasse en
                   vue groupée par Bezirk, les 6 autres en liste plate classée). Jamais
                   la simple liste à 4 tris sans groupement que j'avais construite ici
                   avant — ce n'était pas fidèle au fichier. */}
              {pilotScreen === "filiales" && (() => {
                const ATTENTION_META = { critique: { c: A.red, l: t("arbeitszeitliste.anomalies.severity.critical", "Critique") }, attention: { c: A.orange, l: t("pilotage.today.toWatch", "À surveiller") }, conforme: { c: A.green, l: t("haccp.labelCompliant", "Conforme") } };
                const filialeAttention = (storeId) => {
                  const storeFeed = feed.filter((f) => f.storeId === storeId);
                  if (storeFeed.some((f) => f.severity === "danger")) return "critique";
                  if (storeFeed.length > 0) return "attention";
                  return "conforme";
                };
                const SORTS = [
                  { id: "attention", l: t("pilotage.filiales.sort.attention", "Attention") },
                  { id: "perf", l: t("pilotage.filiales.sort.perf", "Meilleure performance") },
                  { id: "progression", l: t("pilotage.filiales.sort.progression", "Plus forte progression") },
                  { id: "pertesplus", l: t("pilotage.filiales.sort.pertesplus", "Pertes les plus élevées") },
                  { id: "pertesmoins", l: t("pilotage.filiales.sort.pertesmoins", "Pertes les plus faibles") },
                  { id: "objectif", l: t("pilotage.filiales.sort.objectif", "Objectif non atteint") },
                  { id: "stunden", l: t("pilotage.filiales.sort.stunden", "Stundenleistung la plus faible") },
                ];
                // Recherche + regroupement par BL/VKL (retour utilisateur explicite
                // 2026-08-16, suite à sa propre remarque sur l'échelle : "pour un VKL qui
                // dirige plusieurs BL... la liste deviendra très longue, idem pour un NL") —
                // le périmètre hérité (voir sfRequestIdentity côté serveur) reste correct
                // pour l'ACCÈS aux données, mais ne doit jamais se traduire par une liste
                // plate à l'affichage dès qu'un VKL/NL a de vrais subordonnés réseau.
                const searchQ = pilotFilialeSearch.trim().toLowerCase();
                const matchesSearch = (storeId) => !searchQ || `${storeId} ${storeLabelFor(storeId)}`.toLowerCase().includes(searchQ);
                const directReports = adminUsers.filter((u) => u.reportsTo === user?.proId);
                const rows = (managerOverview?.stores || []).filter(matchesSearch).map((storeId) => ({ storeId, s: pilotStoreStats[storeId] })).filter((r) => r.s);
                const withMetric = (getVal, getLabel) => rows.map((r) => ({ ...r, metric: getVal(r.s), metricLabel: getLabel(r.s) }));
                const openFiliale = (storeId) => { setPilotFilialeId(storeId); setPilotFilialeSection(null); setPilotTeamContactId(null); setPilotClaimOpenId(null); setPilotScreen("filiale-detail"); };

                let flatRows = null;
                if (pilotFilialeSort === "perf") flatRows = withMetric((s) => s.umsatzPlan > 0 ? s.umsatzIst / s.umsatzPlan : 0, (s) => s.umsatzPlan > 0 ? t("pilotage.filiales.metric.objectivePctTemplate", { pct: Math.round((s.umsatzIst / s.umsatzPlan) * 100) }, `${Math.round((s.umsatzIst / s.umsatzPlan) * 100)}% de l'objectif`) : t("pilotage.filiales.metric.noObjectiveSet", "Pas d'objectif saisi")).sort((a, b) => b.metric - a.metric);
                else if (pilotFilialeSort === "progression") {
                  const withHist = rows.filter((r) => (pilotHistory[r.storeId] || []).length >= 2);
                  flatRows = withHist.map((r) => {
                    const h = pilotHistory[r.storeId];
                    const cur = h[h.length - 1], prev = h[h.length - 2];
                    const pct = prev > 0 ? ((cur - prev) / prev) * 100 : 0;
                    return { ...r, metric: pct, metricLabel: t("pilotage.filiales.metric.vsLastWeekTemplate", { pct: `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}` }, `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}% vs sem. préc.`) };
                  }).sort((a, b) => b.metric - a.metric);
                } else if (pilotFilialeSort === "pertesplus") flatRows = withMetric((s) => s.pertes, (s) => t("pilotage.filiales.metric.lossesTemplate", { amount: s.pertes.toFixed(2) }, `${s.pertes.toFixed(2)} € de pertes`)).sort((a, b) => b.metric - a.metric);
                else if (pilotFilialeSort === "pertesmoins") flatRows = withMetric((s) => s.pertes, (s) => t("pilotage.filiales.metric.lossesTemplate", { amount: s.pertes.toFixed(2) }, `${s.pertes.toFixed(2)} € de pertes`)).sort((a, b) => a.metric - b.metric);
                else if (pilotFilialeSort === "objectif") flatRows = withMetric((s) => s.umsatzIst - s.umsatzPlan, (s) => t("pilotage.filiales.metric.vsObjectiveTemplate", { amount: `${s.umsatzIst - s.umsatzPlan >= 0 ? "+" : ""}${(s.umsatzIst - s.umsatzPlan).toFixed(0)}` }, `${s.umsatzIst - s.umsatzPlan >= 0 ? "+" : ""}${(s.umsatzIst - s.umsatzPlan).toFixed(0)} € vs objectif`)).filter((r) => r.s.umsatzPlan > 0 && r.metric < 0).sort((a, b) => a.metric - b.metric);
                else if (pilotFilialeSort === "stunden") flatRows = withMetric((s) => s.stundenleistung ?? Infinity, (s) => s.stundenleistung === null ? t("pilotage.filiales.metric.noHoursEntered", "Aucune heure saisie") : t("pilotage.filiales.metric.stundenleistungTemplate", { amount: s.stundenleistung.toFixed(1) }, `Stundenleistung ${s.stundenleistung.toFixed(1)} €/h`)).sort((a, b) => a.metric - b.metric);

                const bezirkeInScope = [...new Set((managerOverview?.stores || []).filter(matchesSearch).map((sId) => storesRegistry.find((s) => s.id === sId)?.bezirkId).filter(Boolean))];

                return (
                  <div className="nx-screen-enter">
                    {(managerOverview?.stores || []).length > 0 && (
                      <div style={{ display: "flex", alignItems: "center", gap: 9, background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 15, padding: "0 14px", height: 42, marginBottom: 10 }}>
                        <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>🏬</span>
                        <input value={pilotFilialeSearch} onChange={(e) => setPilotFilialeSearch(e.target.value)}
                          placeholder={t("admin.searchFilialePlaceholder", "Chercher une filiale...")}
                          style={{ border: "none", outline: "none", background: "none", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.ink, width: "100%" }} />
                        {pilotFilialeSearch && <Pressable onClick={() => setPilotFilialeSearch("")} aria-label={t("search.clearAria", "Effacer")} style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>✕</Pressable>}
                      </div>
                    )}
                    <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
                      {SORTS.map((s) => (
                        <Pressable key={s.id} onClick={() => setPilotFilialeSort(s.id)} style={{
                          flex: "0 0 auto", padding: "7px 13px", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                          background: pilotFilialeSort === s.id ? A.purple : P.soft, color: pilotFilialeSort === s.id ? "#fff" : P.muted,
                        }}>{s.l}</Pressable>
                      ))}
                    </div>
                    {pilotStatsLoading && <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("common.loading", "Chargement...")}</p>}

                    {flatRows ? (
                      <>
                        {!pilotStatsLoading && flatRows.length === 0 && (
                          <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, marginTop: 20 }}>
                            {pilotFilialeSort === "objectif" ? t("pilotage.filiales.emptyBelowTarget", "Aucune filiale sous son objectif.") : t("pilotage.filiales.emptyForSortPeriod", "Aucune donnée pour ce tri sur cette période.")}
                          </p>
                        )}
                        <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(320px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
                        {flatRows.map((r, idx) => {
                          const meta = ATTENTION_META[filialeAttention(r.storeId)];
                          return (
                            <Pressable key={r.storeId} feedback className="nx-list-item-in" onClick={() => openFiliale(r.storeId)} style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 8, animationDelay: `${Math.min(idx, 14) * 28}ms` }}>
                              <span style={{ width: 22, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, flex: "0 0 auto" }}>#{idx + 1}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{storeLabelFor(r.storeId)}</strong>
                                <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{r.metricLabel}</div>
                              </div>
                              <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: meta.c, borderRadius: 99, padding: "4px 10px" }}>{meta.l}</span>
                            </Pressable>
                          );
                        })}
                        </div>
                      </>
                    ) : (() => {
                      const filialeCard = (sId, sIdx) => {
                        const meta = ATTENTION_META[filialeAttention(sId)];
                        const nbIssues = feed.filter((f) => f.storeId === sId).length;
                        return (
                          <Pressable key={sId} feedback className="nx-list-item-in" onClick={() => openFiliale(sId)} style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 8, animationDelay: `${Math.min(sIdx, 14) * 28}ms` }}>
                            <span style={{ width: 10, height: 10, borderRadius: 99, background: meta.c, flex: "0 0 auto" }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{storeLabelFor(sId)}</strong>
                              <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{sId} · {nbIssues > 0 ? t("pilotage.filiales.attentionPointsTemplate", { n: nbIssues }, `${nbIssues} point${nbIssues > 1 ? "s" : ""} d'attention`) : t("pilotage.filiales.nothingToReportShort", "Rien à signaler")}</div>
                            </div>
                            <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: meta.c, borderRadius: 99, padding: "4px 10px" }}>{meta.l}</span>
                          </Pressable>
                        );
                      };
                      const sortByAttention = (ids) => ids.slice().sort((a, b2) => {
                        const order = { critique: 0, attention: 1, conforme: 2 };
                        return order[filialeAttention(a)] - order[filialeAttention(b2)];
                      });

                      // Regroupement par subordonné direct (BL pour un VKL, VKL pour un NL)
                      // AU LIEU du Bezirk géographique — retour utilisateur explicite
                      // 2026-08-16, sa propre remarque sur l'échelle réseau (1200+ filiales,
                      // plusieurs VKL/NL) : un BL garde son périmètre naturel (5-8 filiales,
                      // "encore gérable"), mais un VKL/NL sans ce regroupement se
                      // retrouverait avec une liste plate de dizaines/centaines de filiales.
                      // N'active ce mode QUE si le compte a de vrais subordonnés réseau —
                      // un BL (jamais de subordonné, reportsTo n'est posé que sur BL/VKL)
                      // garde le comportement Bezirk existant, inchangé, sans régression.
                      if (directReports.length > 0) {
                        const ownerGroups = directReports.map((dr) => ({
                          owner: dr,
                          storeIds: sortByAttention(effectiveStoresFor(dr).filter((sId) => (managerOverview?.stores || []).includes(sId) && matchesSearch(sId))),
                        })).filter((g) => g.storeIds.length > 0);
                        const ownedIds = new Set(ownerGroups.flatMap((g) => g.storeIds));
                        const unassignedIds = sortByAttention((managerOverview?.stores || []).filter((sId) => matchesSearch(sId) && !ownedIds.has(sId)));
                        if (ownerGroups.length === 0 && unassignedIds.length === 0) {
                          return <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, marginTop: 20 }}>{t("pilotage.filiales.emptyForSortPeriod", "Aucune donnée pour ce tri sur cette période.")}</p>;
                        }
                        return (
                          <>
                            {ownerGroups.map(({ owner, storeIds }) => (
                              <div key={owner.id} style={{ marginBottom: 18 }}>
                                <strong style={{ display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 8px" }}>
                                  {t("pilotage.filiales.ownerGroupTemplate", { name: `${owner.firstname} ${owner.lastname}`, n: storeIds.length }, `${owner.firstname} ${owner.lastname} · ${storeIds.length} filiale${storeIds.length > 1 ? "s" : ""}`)}
                                </strong>
                                <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(320px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
                                  {storeIds.map((sId, sIdx) => filialeCard(sId, sIdx))}
                                </div>
                              </div>
                            ))}
                            {unassignedIds.length > 0 && (
                              <div style={{ marginBottom: 18 }}>
                                <strong style={{ display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 8px" }}>{t("pilotage.filiales.noOwnerLabel", "Sans responsable direct")}</strong>
                                <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(320px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
                                  {unassignedIds.map((sId, sIdx) => filialeCard(sId, sIdx))}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      }

                      const groups = bezirkeInScope.length > 0 ? bezirkeInScope : [null];
                      return groups.map((bId) => {
                        const b = bId ? bezirkeRegistry.find((x) => x.id === bId) : null;
                        const storeIds = sortByAttention((managerOverview?.stores || []).filter((sId) => {
                          if (!matchesSearch(sId)) return false;
                          const st = storesRegistry.find((s) => s.id === sId);
                          return bId ? st?.bezirkId === bId : !st?.bezirkId;
                        }));
                        if (storeIds.length === 0) return null;
                        return (
                          <div key={bId || "sans-bezirk"} style={{ marginBottom: 18 }}>
                            {(bezirkeInScope.length > 1 || !bId) && (
                              <strong style={{ display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 8px" }}>
                                {b ? b.label : t("pilotage.filiales.noBezirkLabel", "Sans Bezirk")}
                              </strong>
                            )}
                            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(320px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
                              {storeIds.map((sId, sIdx) => filialeCard(sId, sIdx))}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                );
              })()}

              {/* ================= PILOTAGE RÉSEAU — FICHE FILIALE (13 rubriques en accordéon) =================
                   Fidèle à nexora-app_1.zip (source lignes ~4896-4959). Chaque rubrique croise le vrai feed
                   /api/manager/overview de cette filiale (claims/krankmeldung/reporting) + les vraies Missions
                   la ciblant — jamais de DEMO_NETWORK_ISSUES ; les rubriques sans signal réel aujourd'hui
                   affichent honnêtement "Rien à signaler" plutôt qu'une donnée inventée. */}
              {pilotScreen === "filiale-detail" && pilotFilialeId && (() => {
                const FILIALE_SECTIONS = [
                  { id: "equipe", emoji: "👥", label: t("pilotage.filiale.section.equipe", "Équipe") },
                  { id: "planning", emoji: "📅", label: t("permGroup.planning", "Planning") },
                  { id: "krank", emoji: "🤒", label: "Krankmeldung" },
                  { id: "azl", emoji: "⏱️", label: "AZL" },
                  { id: "bestellung", emoji: "📦", label: t("pilotage.filiale.section.bestellung", "Commande") },
                  { id: "haccp", emoji: "📋", label: "HACCP" },
                  { id: "liefer", emoji: "🚚", label: t("pilotage.filiale.section.livraisons", "Livraisons") },
                  { id: "claims", emoji: "⚠️", label: t("claims.title", "Réclamations"), feedType: "claims" },
                  { id: "inventaire", emoji: "📊", label: t("pilotage.filiale.section.inventaires", "Inventaires") },
                  { id: "inventurmassnahme", emoji: "🧾", label: t("inventurmassnahme.eyebrow", "Inventurmaßnahme") },
                  { id: "missions", emoji: "🎯", label: t("nav.missions", "Missions") },
                  { id: "reports", emoji: "🗒️", label: t("filialeReport.hubTitle", "Rapport de filiale") },
                  { id: "warenaustausch", emoji: "🚚", label: t("home.module.warenaustausch", "Warenaustausch") },
                  { id: "reporting", emoji: "📈", label: "Reporting", feedType: "reporting" },
                  { id: "performance", emoji: "🏆", label: t("pilotage.filiale.section.performance", "Performance") },
                ];
                const storeFeed = (managerOverview?.feed || []).filter((f) => f.storeId === pilotFilialeId);
                // Rétention 7 jours pour les missions TERMINÉES (retour utilisateur
                // explicite, 2026-08-24, capture d'écran à l'appui : "sinon on aura une
                // tonne de missions qui s'accumulent avec le temps") — une mission encore
                // en cours pour cette filiale reste toujours visible ici ; une fois
                // terminée, elle ne reste qu'une semaine dans ce résumé (basé sur la date
                // de validation DE CETTE FILIALE précisément, jamais la date de création de
                // la mission) puis disparaît — elle reste entièrement consultable dans
                // l'écran Missions complet (onglet Terminées/Archivées), jamais perdue.
                const MISSION_FICHE_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
                const storeMissions = networkMissions.filter((m) => {
                  if (m.archived || !m.filiales.includes(pilotFilialeId)) return false;
                  const fs = m.filialeStatuses[pilotFilialeId];
                  const isTerminal = fs && ["validee", "refusee"].includes(fs.status);
                  if (!isTerminal) return true;
                  const completedAt = fs.updatedAt ? new Date(fs.updatedAt).getTime() : 0;
                  return completedAt > 0 && (Date.now() - completedAt) <= MISSION_FICHE_RETENTION_MS;
                });
                return (
                  <div>
                    {FILIALE_SECTIONS.map((s) => {
                      const open = pilotFilialeSection === s.id;
                      const sectionIssues = s.feedType ? storeFeed.filter((f) => f.type === s.feedType) : [];
                      const missionCount = s.id === "missions" ? storeMissions.length : 0;
                      // AU (certificat médical) en attente ou refusée = seul vrai point d'attention
                      // d'un arrêt en cours — un arrêt avec AU déjà déposée/vérifiée n'a rien à
                      // signaler de plus au GR (même règle qu'ailleurs dans l'app, ex. chip "AU à
                      // déposer" du Dashboard employé).
                      const krankAuIssue = s.id === "krank" && pilotKrank.some((k) => ["PENDING", "REJECTED"].includes(k.auStatus));
                      // AZL : seul le mois EN COURS compte pour le badge (les mois passés sont déjà
                      // clos/archivés, jamais une action à mener maintenant) — critique = alerte,
                      // warning/info = neutre mais visible.
                      const azlCurrentMonth = s.id === "azl" ? pilotAzlMonths[0] : null;
                      const azlAnomalyCount = azlCurrentMonth ? azlCurrentMonth.anomalies.length : 0;
                      const azlCritical = azlCurrentMonth ? azlCurrentMonth.anomalies.some((a) => a.severity === "critical") : false;
                      // HACCP : jours ouverts (ni dimanche ni férié) pas encore validés ce mois-ci —
                      // aujourd'hui non à jour = seul vrai point d'attention immédiat, les jours
                      // passés en retard restent un décompte informatif (déjà loupés, pas "urgents").
                      const haccpPendingDays = s.id === "haccp" && pilotHaccpMonth ? pilotHaccpMonth.days.filter((d) => d.status === "open" && !d.validated) : [];
                      const haccpTodayPending = s.id === "haccp" && pilotHaccpMonth ? pilotHaccpMonth.days.some((d) => d.date === todayISO() && d.status === "open" && !d.validated) : false;
                      // Inventaires : un cycle de Bestandskontrolle clôturé avec une différence non
                      // nulle est le seul vrai point d'attention (écart de stock potentiel) — un
                      // inventaire ou un contrôle simplement en cours n'est qu'informatif, jamais
                      // une alerte (même principe que Krankmeldung/AZL/HACCP ci-dessus).
                      const inventurDiscrepancy = s.id === "inventaire" && pilotInventurKontrollen.some((k) => k.currentCycle?.status === "abgeschlossen" && k.currentCycle?.differenz != null && k.currentCycle.differenz !== 0);
                      // PLU Quiz : un minimum hebdomadaire d'examens non atteint (3/filiale/semaine,
                      // voir PLU_WEEKLY_MINIMUM_PER_STORE côté serveur) est une attention à avoir,
                      // pas une urgence — jamais pulsé rouge, juste visible (orange) tant que
                      // pilotPluWeekly existe et !met ; neutre sinon (y compris avant tout chargement).
                      const pluNotMet = s.id === "inventurmassnahme" && pilotPluWeekly && !pilotPluWeekly.met;
                      // Reporting — mêmes chiffres et mêmes seuils que l'écran Analyse (tauxPertes
                      // </1.2 maîtrisé/<1.8 à surveiller/≥1.8 élevé, Stundenleistung ≥190 bon), pour
                      // qu'un même écart se lise comme le même niveau d'alerte partout dans le
                      // pilotage réseau — jamais une échelle inventée propre à cette rubrique.
                      const reportingStats = s.id === "reporting" ? pilotStoreStats[pilotFilialeId] : null;
                      const reportingTauxPertes = reportingStats && reportingStats.umsatzIst > 0 ? (reportingStats.pertes / reportingStats.umsatzIst) * 100 : null;
                      const reportingConcernCount = reportingStats ? [
                        reportingTauxPertes !== null && reportingTauxPertes >= 1.8,
                        reportingStats.stundenleistung !== null && reportingStats.stundenleistung < 190,
                        reportingStats.umsatzPlan > 0 && reportingStats.umsatzIst < reportingStats.umsatzPlan * 0.95,
                      ].filter(Boolean).length : 0;
                      // Équipe/Planning : le badge est un décompte, pas une alerte — jamais coloré
                      // rouge/orange ni pulsé (retour utilisateur explicite : la pulsation est
                      // réservée aux notifications qui méritent vraiment attention). Krankmeldung :
                      // neutre s'il y a des arrêts en cours mais aucune AU en souffrance, sinon alerte.
                      // Warenaustausch : seul un transfert ENTRANT "envoyé" (à vérifier côté
                      // cette filiale) est un vrai point d'attention — un transfert sortant
                      // déjà expédié ou déjà réceptionné n'a rien de plus à faire ici.
                      const waNeedsVerify = s.id === "warenaustausch" ? pilotFilialeWA.filter((tr) => tr.toStoreId === pilotFilialeId && tr.status === "envoyee").length : 0;
                      const isNeutralBadge = s.id === "equipe" || s.id === "planning" || s.id === "reports" || (s.id === "krank" && !krankAuIssue) || (s.id === "azl" && !azlCritical) || (s.id === "haccp" && !haccpTodayPending) || (s.id === "inventaire" && !inventurDiscrepancy) || (s.id === "inventurmassnahme" && !pluNotMet) || (s.id === "warenaustausch" && !waNeedsVerify);
                      const badgeCount = s.id === "missions" ? missionCount
                        : s.id === "equipe" ? pilotTeam.length : s.id === "planning" ? pilotPlanningWeeks.length
                        : s.id === "krank" ? pilotKrank.length : s.id === "azl" ? azlAnomalyCount
                        : s.id === "haccp" ? haccpPendingDays.length : s.id === "inventaire" ? pilotInventurInventories.length + pilotInventurKontrollen.length
                        : s.id === "inventurmassnahme" ? (pilotPluWeekly ? pilotPluWeekly.examCount : pilotPluAttempts.length)
                        : s.id === "reporting" ? reportingConcernCount
                        : s.id === "reports" ? pilotFilialeReports.filter((r) => r.status === "envoye").length
                        : s.id === "warenaustausch" ? (waNeedsVerify || pilotFilialeWA.length)
                        : sectionIssues.length;
                      const hasDanger = sectionIssues.some((f) => f.severity === "danger") || krankAuIssue || azlCritical || haccpTodayPending || inventurDiscrepancy || reportingConcernCount > 0;
                      return (
                        <section key={s.id} style={{ borderRadius: 18, marginBottom: 8, overflow: "hidden", boxShadow: P.shadow }}>
                          <Pressable onClick={() => setPilotFilialeSection(open ? null : s.id)} style={{
                            display: "flex", alignItems: "center", gap: 12, width: "100%", boxSizing: "border-box", padding: "13px 14px",
                            background: P.card,
                          }}>
                            <span style={{
                              width: 34, height: 34, borderRadius: 11, flex: "0 0 auto", display: "grid", placeItems: "center",
                              background: !badgeCount ? P.soft : isNeutralBadge ? P.soft : hasDanger ? "rgba(239,68,68,.12)" : "rgba(231,138,18,.14)",
                            }}><EmojiIcon e={s.emoji} size={16} /></span>
                            <strong style={{ flex: 1, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{s.label}</strong>
                            {badgeCount > 0 && (
                              <span className={isNeutralBadge ? "" : "nx-notif-dot"} style={{
                                minWidth: 18, height: 18, padding: "0 4px", borderRadius: 99,
                                background: isNeutralBadge ? P.soft : hasDanger ? A.red : A.orange,
                                color: isNeutralBadge ? P.muted : "#fff",
                                fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, display: "grid", placeItems: "center",
                              }}>{badgeCount}</span>
                            )}
                            <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
                          </Pressable>
                          {open && (
                            <div style={{ padding: "4px 14px 14px", background: P.card }}>
                              {s.id === "planning" ? (
                                <>
                                {pilotPlanningWeeks.length > 0 ? pilotPlanningWeeks.map((w) => {
                                  const weekOpen = pilotPlanningDetail?.weekStart === w.weekStart;
                                  return (
                                    <div key={w.weekStart} style={{ borderRadius: 14, marginTop: 6, background: P.soft, overflow: "hidden" }}>
                                      <Pressable onClick={() => openPilotPlanningWeek(w.weekStart)} style={{
                                        display: "flex", alignItems: "center", gap: 10, padding: 12, width: "100%", boxSizing: "border-box",
                                      }}>
                                        <span style={{
                                          width: 30, height: 30, borderRadius: 10, flex: "0 0 auto", display: "grid", placeItems: "center",
                                          fontSize: `calc(14px * var(--nx-font-scale, 1))`, background: w.status === "published" ? "rgba(30,184,95,.13)" : "rgba(231,138,18,.14)",
                                        }}>{w.status === "published" ? "📢" : "📝"}</span>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{w.weekKey || w.weekStart}{w.isCurrent ? t("pilotage.filiale.planning.currentWeekSuffix", " · Semaine en cours") : ""}</strong>
                                          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block" }}>
                                            {w.status === "published" ? t("planning.control.published", "Publié") : t("planning.control.draft", "Brouillon")} · {t("pilotage.filiale.planning.hoursEmployeesTemplate", { hours: w.totals?.hours ?? 0, employees: w.totals?.employees ?? 0 }, `${w.totals?.hours ?? 0} h · ${w.totals?.employees ?? 0} employés`)}
                                          </span>
                                        </div>
                                        <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, transform: weekOpen ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
                                      </Pressable>
                                      {weekOpen && (
                                        <div style={{ padding: "0 12px 12px" }}>
                                          {pilotPlanningDetailLoading ? (
                                            <p style={{ margin: 0, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.planning.loadingEllipsis", "Chargement…")}</p>
                                          ) : pilotPlanningDetail ? (
                                            <>
                                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                {(pilotPlanningDetail.employees || []).length === 0 && (
                                                  <p style={{ margin: 0, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.planning.noEmployeePlanned", "Aucun employé planifié cette semaine-là.")}</p>
                                                )}
                                                {(pilotPlanningDetail.employees || []).map((emp) => {
                                                  const empEntries = (pilotPlanningDetail.entries || []).filter((e) => e.employeeId === emp.employeeId && e.status !== "Leer");
                                                  return (
                                                    <div key={emp.employeeId} style={{ background: P.card, borderRadius: 10, padding: "8px 10px" }}>
                                                      <strong style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 3 }}>{emp.name}</strong>
                                                      {empEntries.length === 0 ? (
                                                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.planning.nothingPlanned", "Rien de planifié")}</span>
                                                      ) : (
                                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                                                          {empEntries.map((e) => (
                                                            <span key={e.id} style={{
                                                              fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, background: P.soft, borderRadius: 99, padding: "3px 8px",
                                                            }}>
                                                              {weekLabelShort(PLAN_DAY_KEYS.indexOf(e.day)) || e.day} · {e.status === "Arbeit" && e.start ? `${e.start}–${e.end}` : (SHIFT_STATUS_LABELS[e.status] || e.status)}
                                                            </span>
                                                          ))}
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                              <Pressable onClick={() => exportPilotPlanningWeek(pilotPlanningDetail, pilotFilialeId)} style={{
                                                marginTop: 10, width: "100%", textAlign: "center", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff",
                                                background: A.purple, borderRadius: 99, padding: "9px 12px",
                                              }}>🖨️ {t("pilotage.filiale.planning.exportWeekBtn", "Exporter cette semaine")}</Pressable>
                                            </>
                                          ) : (
                                            <p style={{ margin: 0, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.planning.loadFailedText", "Impossible de charger ce plan.")}</p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }) : (
                                  <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.planning.noPublishedPlan", "Aucun plan publié pour cette filiale pour l'instant.")}</p>
                                )}
                                {/* Recherche d'un plan antérieur — retour utilisateur explicite, 2026-08-24 :
                                    "toujours afficher le dernier plan et donner la possibilité au GR de
                                    chercher un plan antérieur et de le recharger au besoin". */}
                                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                                  <input type="date" value={pilotPlanningSearchDate} onChange={(e) => setPilotPlanningSearchDate(e.target.value)} style={{ ...inputStyle, flex: 1, padding: "9px 12px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}} />
                                  <Pressable disabled={!pilotPlanningSearchDate || pilotPlanningSearchBusy} onClick={searchOlderPilotPlanningWeek} style={{
                                    flex: "0 0 auto", ...btnSolid(A.teal), padding: "0 16px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, opacity: (!pilotPlanningSearchDate || pilotPlanningSearchBusy) ? .5 : 1,
                                  }}>
                                    {pilotPlanningSearchBusy ? "…" : `🔍 ${t("pilotage.filiale.planning.searchBtn", "Charger")}`}
                                  </Pressable>
                                </div>
                                </>
                              ) : s.id === "krank" ? (
                                pilotKrank.length > 0 ? pilotKrank.map((k) => {
                                  const auIssue = ["PENDING", "REJECTED"].includes(k.auStatus);
                                  return (
                                    <div key={k.id} style={{
                                      padding: 12, borderRadius: 14, marginTop: 6,
                                      background: auIssue ? "rgba(239,68,68,.08)" : P.soft,
                                      border: auIssue ? "1px solid rgba(239,68,68,.25)" : "none",
                                    }}>
                                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                                        <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{k.employeeNameSnapshot || k.employeeId}</strong>
                                        <span style={{
                                          fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "3px 9px", whiteSpace: "nowrap",
                                          color: auIssue ? A.red : P.muted, background: auIssue ? "rgba(239,68,68,.13)" : P.card,
                                        }}>{t(`krankmeldung.auStatus.${{ NOT_REQUIRED: "notRequired", PENDING: "pending", UPLOADED: "uploaded", VERIFIED: "verified", REJECTED: "rejected" }[k.auStatus]}`, AU_STATUS_LABELS[k.auStatus])}</span>
                                      </div>
                                      <p style={{ margin: "4px 0 0", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                                        {t("krankmeldung.activeSinceShortTemplate", { date: fmtFR(k.startDate) }, `Depuis le ${fmtFR(k.startDate)}`)}{k.endDateUnknown ? t("krankmeldung.endUnknownInlineSuffix", " · fin inconnue") : k.expectedEndDate ? ` → ${fmtFR(k.expectedEndDate)}` : ""}
                                      </p>
                                      {k.planStatus !== "NOT_APPLICABLE" && (
                                        <p style={{ margin: "3px 0 0", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>
                                          {t("pilotage.filiale.krank.replacementTemplate", { status: k.planStatus === "COVERED" ? t("pilotage.filiale.krank.coveredStatus", "couvert") : k.planStatus === "PARTIALLY_COVERED" ? t("pilotage.filiale.krank.partiallyCoveredStatus", "partiellement couvert") : t("haccp.weekStatusPending", "en attente") }, `Remplacement : ${k.planStatus === "COVERED" ? "couvert" : k.planStatus === "PARTIALLY_COVERED" ? "partiellement couvert" : "en attente"}`)}
                                        </p>
                                      )}
                                      {(k.attachments || []).length > 0 && (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 7 }}>
                                          {k.attachments.map((a) => (
                                            <a key={a.id} href={`/api/krankmeldungen/${k.id}/attachments/${a.id}`} target="_blank" rel="noreferrer" style={{
                                              display: "flex", alignItems: "center", gap: 6, fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.blue, textDecoration: "none",
                                            }}>📎 {a.originalName}</a>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }) : (
                                  <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>✓ {t("pilotage.filiale.krank.noneActiveText", "Aucun arrêt en cours dans cette filiale.")}</p>
                                )
                              ) : s.id === "azl" ? (
                                pilotAzlMonths.length > 0 ? pilotAzlMonths.map((m) => {
                                  const key = `${m.year}-${m.month}`;
                                  const monthOpen = pilotAzlOpenKey === key;
                                  const monthLabel = new Date(m.year, m.month - 1, 1).toLocaleDateString(INTL_LOCALE_BY_LANG[lang] || "fr-FR", { month: "long", year: "numeric" });
                                  const statusLabel = m.status === "archived" ? t("reporting.status.archived", "Archivé") : m.status === "validated" ? t("haccp.statusValidated", "Validé") : t("planning.control.draft", "Brouillon");
                                  const critical = m.anomalies.some((a) => a.severity === "critical");
                                  return (
                                    <div key={key} style={{
                                      borderRadius: 14, marginTop: 6, overflow: "hidden",
                                      background: critical ? "rgba(239,68,68,.08)" : P.soft,
                                      border: critical ? "1px solid rgba(239,68,68,.25)" : "none",
                                    }}>
                                      <Pressable onClick={() => setPilotAzlOpenKey(monthOpen ? null : key)} style={{
                                        display: "flex", alignItems: "center", gap: 10, padding: 12, width: "100%", boxSizing: "border-box",
                                      }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, textTransform: "capitalize" }}>{monthLabel}</strong>
                                          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block" }}>
                                            {statusLabel} · {t("pilotage.filiale.azl.signedCountTemplate", { n: m.signatures.length, total: m.requiredRoles.length }, `${m.signatures.length}/${m.requiredRoles.length} signé`)}{m.anomalies.length > 0 ? ` · ${t("pilotage.filiale.azl.anomalyCountTemplate", { n: m.anomalies.length }, `${m.anomalies.length} anomalie${m.anomalies.length > 1 ? "s" : ""}`)}` : ""}
                                          </span>
                                        </div>
                                        {m.anomalies.length > 0 && (
                                          <span style={{
                                            fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "3px 9px", whiteSpace: "nowrap",
                                            color: critical ? A.red : P.muted, background: critical ? "rgba(239,68,68,.13)" : P.card,
                                          }}>{critical ? t("arbeitszeitliste.anomalies.severity.critical", "Critique") : t("lieferschein.itemStatus.toVerify", "À vérifier")}</span>
                                        )}
                                        <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, transform: monthOpen ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
                                      </Pressable>
                                      {monthOpen && (
                                        <div style={{ padding: "0 12px 12px" }}>
                                          {m.anomalies.length > 0 ? (
                                            <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: m.hasArchivedPdf ? 10 : 0 }}>
                                              {m.anomalies.map((a) => (
                                                <p key={a.id} style={{
                                                  margin: 0, fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: a.severity === "critical" ? A.red : P.muted,
                                                }}>{a.severity === "critical" ? "⚠️ " : "· "}{a.message}</p>
                                              ))}
                                            </div>
                                          ) : (
                                            <p style={{ margin: "0 0 10px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>✓ {t("pilotage.filiale.azl.noAnomalyThisMonth", "Aucune anomalie détectée ce mois-là.")}</p>
                                          )}
                                          {m.hasArchivedPdf ? (
                                            <a href={`/api/arbeitszeitliste/${encodeURIComponent(pilotFilialeId)}/${m.year}/${m.month}/archive`} target="_blank" rel="noreferrer" style={{
                                              display: "block", textAlign: "center", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff",
                                              background: A.purple, borderRadius: 99, padding: "9px 12px", textDecoration: "none",
                                            }}>🖨️ {t("pilotage.filiale.azl.downloadArchivedPdfBtn", "Télécharger le PDF archivé")}</a>
                                          ) : (
                                            <p style={{ margin: 0, fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.azl.noPdfYetTemplate", { status: statusLabel.toLowerCase() }, `Pas encore de PDF archivé pour ce mois (document ${statusLabel.toLowerCase()}).`)}</p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }) : (
                                  <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.planning.loadingEllipsis", "Chargement…")}</p>
                                )
                              ) : s.id === "haccp" ? (
                                pilotHaccpMonth ? (() => {
                                  const openDays = pilotHaccpMonth.days.filter((d) => d.status === "open");
                                  const validatedCount = openDays.filter((d) => d.validated).length;
                                  const pending = openDays.filter((d) => !d.validated).sort((a, b) => a.date.localeCompare(b.date));
                                  const today = todayISO();
                                  return (
                                    <>
                                      <p style={{ margin: "6px 0 10px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                                        {t("pilotage.filiale.haccp.validatedDaysTemplate", { validated: validatedCount, total: openDays.length }, `${validatedCount}/${openDays.length} jours validés ce mois-ci.`)}
                                      </p>
                                      {pending.length > 0 ? pending.map((d) => {
                                        const isToday = d.date === today;
                                        return (
                                          <div key={d.date} style={{
                                            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                                            padding: 10, borderRadius: 12, marginTop: 6,
                                            background: isToday ? "rgba(239,68,68,.08)" : P.soft,
                                            border: isToday ? "1px solid rgba(239,68,68,.25)" : "none",
                                          }}>
                                            <strong style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: isToday ? A.red : P.ink }}>{fmtFR(d.date)}{isToday ? t("pilotage.filiale.haccp.todaySuffix", " · aujourd'hui") : ""}</strong>
                                            <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: isToday ? A.red : P.muted, fontWeight: isToday ? 800 : 700 }}>
                                              {d.missingCount > 0 ? t("pilotage.filiale.haccp.missingPointsTemplate", { n: d.missingCount }, `${d.missingCount} point${d.missingCount > 1 ? "s" : ""} manquant${d.missingCount > 1 ? "s" : ""}`) : t("pilotage.filiale.haccp.toValidateLabel", "À valider")}
                                            </span>
                                          </div>
                                        );
                                      }) : (
                                        <p style={{ margin: "0 0 10px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>✓ {t("pilotage.filiale.haccp.allValidatedText", "Tous les jours du mois sont validés.")}</p>
                                      )}
                                      <Pressable onClick={() => exportPilotHaccpMonth(pilotFilialeId, pilotHaccpMonth.year, pilotHaccpMonth.month)} style={{
                                        marginTop: 10, width: "100%", textAlign: "center", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff",
                                        background: A.purple, borderRadius: 99, padding: "9px 12px",
                                      }}>🖨️ {t("pilotage.filiale.haccp.exportMonthBtn", "Exporter le mois")}</Pressable>
                                    </>
                                  );
                                })() : (
                                  <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.planning.loadingEllipsis", "Chargement…")}</p>
                                )
                              ) : s.id === "equipe" ? (
                                pilotTeam.length > 0 ? pilotTeam.map((emp) => {
                                  const contactOpen = pilotTeamContactId === emp.employeeId;
                                  return (
                                    <div key={emp.employeeId} style={{ borderRadius: 14, marginTop: 6, background: P.soft, overflow: "hidden" }}>
                                      <Pressable onClick={() => setPilotTeamContactId(contactOpen ? null : emp.employeeId)} style={{
                                        display: "flex", alignItems: "center", gap: 10, padding: 12, width: "100%", boxSizing: "border-box",
                                      }}>
                                        {emp.photoUrl ? (
                                          <img src={emp.photoUrl} alt="" style={{ width: 30, height: 30, borderRadius: 99, flex: "0 0 auto", objectFit: "cover" }} />
                                        ) : (
                                          <span style={{
                                            width: 30, height: 30, borderRadius: 99, flex: "0 0 auto", display: "grid", placeItems: "center",
                                            fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff",
                                            background: emp.isResponsable ? A.purple : P.muted,
                                          }}>{emp.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}</span>
                                        )}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block" }}>{emp.name}</strong>
                                          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                                            {emp.roleLabel}{emp.isResponsable ? t("pilotage.filiale.equipe.responsableSuffix", " · Responsable") : ""}{emp.weeklyContractedHours ? ` · ${t("pilotage.filiale.equipe.weeklyHoursTemplate", { hours: emp.weeklyContractedHours }, `${emp.weeklyContractedHours} h/sem.`)}` : ""}
                                          </span>
                                        </div>
                                        <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, transform: contactOpen ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
                                      </Pressable>
                                      {contactOpen && (
                                        <div style={{ padding: "0 12px 12px" }}>
                                          {/* Fiche employé complète (retour utilisateur 2026-08-04 : "toutes ses
                                              infos" au tap dans Équipe) — civilité, type de contrat, soldes,
                                              déjà renvoyés par GET /api/stores/:storeId/team. */}
                                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                                            {[
                                              emp.gender === "homme" ? t("common.titleMr", "M.") : emp.gender === "femme" ? t("common.titleMrs", "Mme") : null,
                                              emp.contractType,
                                              emp.leistungsfreiAvailableHours != null ? `${t("planning.lfShort", "LF")} ${emp.leistungsfreiAvailableHours} h` : null,
                                              emp.restUrlaubDays != null ? `${t("arbeitszeitliste.table.restUrlaub", "Rest-Urlaub")} ${emp.restUrlaubDays} j` : null,
                                            ].filter(Boolean).map((chip, i) => (
                                              <span key={i} style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, background: P.card, color: P.ink, borderRadius: 99, padding: "5px 10px", border: `1px solid ${P.line}` }}>{chip}</span>
                                            ))}
                                          </div>
                                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                            {emp.phone ? (
                                              <a href={`tel:${emp.phone}`} style={{
                                                flex: "1 1 auto", textAlign: "center", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff",
                                                background: A.green, borderRadius: 99, padding: "8px 12px", textDecoration: "none",
                                              }}>📞 {emp.phone}</a>
                                            ) : null}
                                            {emp.email ? (
                                              <a href={`mailto:${emp.email}`} style={{
                                                flex: "1 1 auto", textAlign: "center", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink,
                                                background: P.card, borderRadius: 99, padding: "8px 12px", textDecoration: "none", border: `1px solid ${P.line}`,
                                              }}>✉️ {emp.email}</a>
                                            ) : null}
                                            {!emp.phone && !emp.email ? (
                                              <p style={{ margin: 0, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.equipe.noContactInfo", "Aucune coordonnée renseignée pour cet employé.")}</p>
                                            ) : null}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                }) : (
                                  <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.equipe.noneAssigned", "Aucun employé rattaché à cette filiale pour l'instant.")}</p>
                                )
                              ) : s.id === "missions" ? (
                                storeMissions.length > 0 ? storeMissions.map((m) => {
                                  const fMeta = FILIALE_STATUS_META[m.filialeStatuses[pilotFilialeId]?.status || "en_attente"];
                                  return (
                                    <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: 12, borderRadius: 14, marginTop: 6, background: P.soft }}>
                                      <strong style={{ flex: 1, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{m.title}</strong>
                                      <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: fMeta.c, background: fMeta.bg, borderRadius: 99, padding: "4px 9px", whiteSpace: "nowrap" }}>{fMeta.l}</span>
                                    </div>
                                  );
                                }) : (
                                  <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>✓ {t("pilotage.filiale.missions.noneForStore", "Aucune mission en cours pour cette filiale.")}</p>
                                )
                              ) : s.id === "reports" ? (
                                pilotFilialeReports.length > 0 ? pilotFilialeReports.map((r) => {
                                  const openTodos = r.todoItems.filter((i) => !i.done).length;
                                  return (
                                    <Pressable key={r.id} onClick={() => { setScreen("filialeReport"); r.status === "brouillon" ? resumeFrDraft(r) : openFrDetail(r.id); }} style={{
                                      display: "flex", alignItems: "center", gap: 10, padding: 12, borderRadius: 14, marginTop: 6, background: P.soft, width: "100%", boxSizing: "border-box", textAlign: "left",
                                    }}>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block" }}>N°{r.reportNumber} — {r.createdByName} — {fmtFR(r.createdAt)}</strong>
                                        {openTodos > 0 && <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: A.orange }}>{t("filialeReport.card.todoOpenTemplate", { n: openTodos }, `${openTodos} tâche${openTodos > 1 ? "s" : ""} à faire`)}</span>}
                                      </div>
                                      <span style={{
                                        fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 9px", whiteSpace: "nowrap",
                                        color: r.status === "brouillon" ? P.muted : A.teal, background: r.status === "brouillon" ? P.card : `${A.teal}1f`,
                                      }}>{r.status === "brouillon" ? t("filialeReport.status.draft", "Brouillon") : t("filialeReport.status.sent", "Envoyé")}</span>
                                    </Pressable>
                                  );
                                }) : (
                                  <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("filialeReport.emptyMine", "Aucun rapport pour l'instant.")}</p>
                                )
                              ) : s.id === "warenaustausch" ? (
                                // Retour utilisateur explicite (2026-08-27) : "juste donner la
                                // possibilité au GR d'enregistrer ou imprimer le bon" — même
                                // principe déjà établi pour HACCP dans cette fiche
                                // (exportPilotHaccpMonth, export PDF seul, jamais l'écran
                                // haccp.fill live) : un GR en oversight consulte/exporte le
                                // document réel, il n'ouvre jamais l'écran opérationnel de
                                // préparation/réception (qui appartient à la vraie filiale
                                // concernée — risque réel d'y modifier/valider par erreur un
                                // transfert qui n'est pas le sien).
                                pilotFilialeWA.length > 0 ? pilotFilialeWA.map((tr) => {
                                  const meta = WA_STATUS_META[tr.status];
                                  const outgoing = tr.fromStoreId === pilotFilialeId;
                                  return (
                                    <Pressable key={tr.id} disabled={waExporting} onClick={() => exportWaTracking(tr)} style={{
                                      display: "flex", alignItems: "center", gap: 10, padding: 12, borderRadius: 14, marginTop: 6, background: P.soft, width: "100%", boxSizing: "border-box", textAlign: "left",
                                      opacity: waExporting ? .6 : 1,
                                    }}>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block" }}>
                                          {tr.kind === "warenrueckfuehrung" ? `↩️ ${WA_CATEGORY_LABEL[tr.category] || t("warenaustausch.returnLabel", "Retour")}` : outgoing ? `🚚 → ${waStoreLabel(tr.toStoreId)}` : `🚚 ← ${waStoreLabel(tr.fromStoreId)}`}
                                        </strong>
                                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("warenaustausch.lineCountTemplate", { n: tr.lines.length }, `${tr.lines.length} article${tr.lines.length > 1 ? "s" : ""}`)} · {fmtFR(tr.createdAt)}</span>
                                      </div>
                                      <span style={{
                                        fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 9px", whiteSpace: "nowrap",
                                        color: meta.c, background: meta.bg,
                                      }}>{meta.l}</span>
                                      <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }} aria-label={t("warenaustausch.exportTrackingBtn", "Fiche de suivi")}>📄</span>
                                    </Pressable>
                                  );
                                }) : (
                                  <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("warenaustausch.emptyOutgoing", "Aucun transfert sortant.")}</p>
                                )
                              ) : s.id === "claims" ? (
                                pilotClaims.length > 0 ? pilotClaims.map((c) => {
                                  const typeLabel = c.type === "food" ? t("pilotage.filiale.claims.typeFood", "Épicerie") : t("pilotage.filiale.claims.typeFresh", "Frais");
                                  const itemsSummary = c.items.length > 1 ? t("pilotage.filiale.claims.itemsCountTemplate", { n: c.items.length }, `${c.items.length} articles`) : (c.items[0]?.name || c.items[0]?.defect || t("pilotage.filiale.claims.itemsCountTemplate", { n: 1 }, "1 article"));
                                  const daysOpen = Math.max(0, Math.floor((Date.now() - new Date(c.createdAt).getTime()) / 86400000));
                                  const claimOpen = pilotClaimOpenId === c.id;
                                  return (
                                    <div key={c.id} style={{ borderRadius: 14, marginTop: 6, overflow: "hidden", background: P.soft }}>
                                      <Pressable onClick={() => setPilotClaimOpenId(claimOpen ? null : c.id)} style={{
                                        display: "flex", alignItems: "center", gap: 10, padding: 12, width: "100%", boxSizing: "border-box",
                                      }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{typeLabel} · {itemsSummary}</strong>
                                          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block" }}>
                                            {t("pilotage.filiale.claims.openSinceTemplate", { n: daysOpen }, `Ouverte depuis ${daysOpen} jour${daysOpen > 1 ? "s" : ""}`)}{c.createdBy ? ` · ${c.createdBy}` : ""}
                                          </span>
                                        </div>
                                        <span style={{
                                          fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "3px 9px", whiteSpace: "nowrap",
                                          color: c.status === "open" ? A.orange : P.muted, background: c.status === "open" ? "rgba(231,138,18,.14)" : P.card,
                                        }}>{c.status === "open" ? t("pilotage.filiale.claims.toSendStatus", "À envoyer") : t("claims.status.sent", "Envoyée")}</span>
                                        <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, transform: claimOpen ? "rotate(90deg)" : "none", transition: "transform .2s" }}>›</span>
                                      </Pressable>
                                      {claimOpen && (
                                        <div style={{ padding: "0 12px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                                          {c.items.map((it) => (
                                            <div key={it.id} style={{ background: P.card, borderRadius: 10, padding: "8px 10px" }}>
                                              <strong style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, display: "block" }}>{it.name || it.artNr || t("pilotage.filiale.claims.articleFallbackName", "Article")}</strong>
                                              <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 2 }}>
                                                {[it.artNr && t("pilotage.filiale.claims.artNrTemplate", { n: it.artNr }, `Art. ${it.artNr}`), it.pieces !== "" && it.pieces != null && t("pilotage.filiale.claims.piecesTemplate", { n: it.pieces }, `${it.pieces} pièce(s)`), it.kilos !== "" && it.kilos != null && t("pilotage.filiale.claims.kilosTemplate", { n: it.kilos }, `${it.kilos} kg`), it.cartons !== "" && it.cartons != null && t("pilotage.filiale.claims.cartonsTemplate", { n: it.cartons }, `${it.cartons} carton(s)`)].filter(Boolean).join(" · ")}
                                              </span>
                                              {it.defect && <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: A.orange, fontWeight: 700, display: "block", marginTop: 3 }}>⚠️ {it.defect}</span>}
                                              {it.note && <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 3, fontStyle: "italic" }}>« {it.note} »</span>}
                                              {(it.photos || []).length > 0 && (
                                                <div style={{ display: "flex", gap: 6, marginTop: 7, flexWrap: "wrap" }}>
                                                  {it.photos.map((url, i) => (
                                                    <Pressable key={i} onClick={() => setMissionPhotoLightbox(url)}>
                                                      <img src={url} alt={t("pilotage.filiale.claims.photoAlt", "Photo réclamation")} style={{ width: 48, height: 48, borderRadius: 8, objectFit: "cover", display: "block" }} />
                                                    </Pressable>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }) : (
                                  <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>✓ {t("pilotage.filiale.claims.noneForStore", "Aucune réclamation en cours pour cette filiale.")}</p>
                                )
                              ) : s.id === "inventaire" ? (
                                pilotInventurInventories.length === 0 && pilotInventurKontrollen.length === 0 ? (
                                  <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>✓ {t("pilotage.filiale.inventur.noneActive", "Aucun inventaire ni contrôle de stock en cours pour cette filiale.")}</p>
                                ) : (
                                  <>
                                    <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".05em", display: "block", margin: "6px 0 4px" }}>{t("pilotage.filiale.inventur.subInventories", "Inventurerfassung")}</span>
                                    {pilotInventurInventories.length === 0 ? (
                                      <p style={{ margin: "0 0 8px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.inventur.noInventoryActive", "Aucun inventaire en cours.")}</p>
                                    ) : pilotInventurInventories.map((inv) => {
                                      const total = inv.categories.length;
                                      const done = inv.categories.filter((c) => ["fertig", "geprueft", "gesperrt"].includes(c.status)).length;
                                      const pct = total ? Math.round((done / total) * 100) : 0;
                                      const meta = INVENTUR_STATUS_META[inv.status] || INVENTUR_STATUS_META.vorbereitung;
                                      const invOpen = pilotInventurOpenId === inv.id;
                                      return (
                                        <div key={inv.id} style={{ borderRadius: 14, marginBottom: 6, overflow: "hidden", background: P.soft }}>
                                          <Pressable onClick={() => setPilotInventurOpenId(invOpen ? null : inv.id)} style={{ padding: 12, width: "100%", boxSizing: "border-box", display: "block", textAlign: "left" }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                                              <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{inv.name || (inv.type === "jahresinventur" ? "Jahresinventur" : "Inventur")}</strong>
                                              <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: meta.color, borderRadius: 99, padding: "3px 9px", whiteSpace: "nowrap" }}>{meta.label}</span>
                                            </div>
                                            <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 6 }}>{fmtFR(inv.date)} · {t("pilotage.filiale.inventur.categoriesDoneTemplate", { done, total }, `${done}/${total} catégories terminées`)}</div>
                                            <div style={{ height: 5, borderRadius: 99, background: P.card, overflow: "hidden" }}><div style={{ height: "100%", width: `${pct}%`, background: A.green }} /></div>
                                          </Pressable>
                                          {invOpen && (
                                            <div style={{ padding: "0 12px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
                                              <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".05em" }}>{t("inventur.categoriesLabel", "Catégories")}</span>
                                              {inv.categories.map((c) => {
                                                const cMeta = INVENTUR_CATEGORY_STATUS_META[c.status] || INVENTUR_CATEGORY_STATUS_META.nicht_zugewiesen;
                                                return (
                                                  <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: P.card, borderRadius: 10, padding: "8px 10px" }}>
                                                    <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`}}>{c.name || t("inventur.categoryFallback", "Catégorie")}</span>
                                                    <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: cMeta.color, borderRadius: 99, padding: "2px 8px", whiteSpace: "nowrap" }}>{cMeta.label}</span>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                    <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".05em", display: "block", margin: "10px 0 4px" }}>{t("pilotage.filiale.inventur.subKontrollen", "Bestandskontrolle")}</span>
                                    {pilotInventurKontrollen.length === 0 ? (
                                      <p style={{ margin: 0, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.inventur.noKontrolleActive", "Aucun contrôle de stock en cours.")}</p>
                                    ) : pilotInventurKontrollen.map((k) => {
                                      const meta = KONTROLLE_STATUS_META[k.status] || KONTROLLE_STATUS_META.vorbereitet;
                                      const cyc = k.currentCycle;
                                      const showDiff = cyc?.status === "abgeschlossen" && cyc?.differenz != null;
                                      const diffIssue = showDiff && cyc.differenz !== 0;
                                      const kOpen = pilotInventurOpenId === k.id;
                                      const row = (label, value) => value == null || value === "" ? null : (
                                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: `calc(11px * var(--nx-font-scale, 1))`}}>
                                          <span style={{ color: P.muted }}>{label}</span><strong>{value}</strong>
                                        </div>
                                      );
                                      return (
                                        <div key={k.id} style={{
                                          borderRadius: 14, marginBottom: 6, overflow: "hidden",
                                          background: diffIssue ? "rgba(239,68,68,.08)" : P.soft,
                                          border: diffIssue ? "1px solid rgba(239,68,68,.25)" : "none",
                                        }}>
                                          <Pressable onClick={() => setPilotInventurOpenId(kOpen ? null : k.id)} style={{ padding: 12, width: "100%", boxSizing: "border-box", display: "block", textAlign: "left" }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                              <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{k.name || k.artNr}</strong>
                                              <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: meta.color, borderRadius: 99, padding: "3px 9px", whiteSpace: "nowrap" }}>{meta.label}</span>
                                            </div>
                                            <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                                              {k.categoryName}{cyc ? ` · ${t("pilotage.filiale.inventur.cycleNumberTemplate", { n: cyc.cycleNumber }, `Cycle ${cyc.cycleNumber}`)}` : ""}
                                            </div>
                                            {showDiff && (
                                              <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: diffIssue ? A.red : A.green, marginTop: 4 }}>
                                                {t("pilotage.filiale.inventur.differenceTemplate", { n: cyc.differenz }, `Écart : ${cyc.differenz}`)}
                                              </div>
                                            )}
                                          </Pressable>
                                          {kOpen && cyc && (
                                            <div style={{ padding: "0 12px 12px", display: "flex", flexDirection: "column", gap: 5 }}>
                                              {row(t("kontrolle.assignedEmployeeLabel", "Collaborateur affecté"), k.assignedUserName || "—")}
                                              {row(t("pilotage.filiale.inventur.reasonLabel", "Motif"), KONTROLLE_REASON_META[k.reason])}
                                              {row(t("kontrolle.anfangsbestandCountedLabel", "Anfangsbestand (compté)"), cyc.anfangsbestand)}
                                              {row(t("pilotage.filiale.inventur.liefermengeLabel", "Liefermenge"), cyc.liefermenge)}
                                              {row(t("kontrolle.verkaufsmengeManualLabel", "Verkaufsmenge (saisie manuelle depuis Norma)"), cyc.verkaufsmenge)}
                                              {row(t("kontrolle.endbestandCountedLabel", "Endbestand (compté)"), cyc.endbestand)}
                                              {row(t("pilotage.filiale.inventur.erwarteterEndbestandLabel", "Erwarteter Endbestand"), cyc.erwarteterEndbestand)}
                                              {cyc.conclusion && row(t("kontrolle.conclusionLabel", "Conclusion"), cyc.conclusion)}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </>
                                )
                              ) : s.id === "inventurmassnahme" ? (
                                <>
                                  {pilotPluWeekly && (
                                    <div style={{
                                      padding: 12, borderRadius: 14, marginBottom: 10,
                                      background: pilotPluWeekly.met ? P.soft : "rgba(231,138,18,.09)",
                                      border: pilotPluWeekly.met ? "none" : "1px solid rgba(231,138,18,.3)",
                                    }}>
                                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                        <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("pilotage.filiale.pluquiz.weeklyTitle", "Cette semaine")}</strong>
                                        <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: pilotPluWeekly.met ? A.green : A.orange }}>
                                          {pilotPluWeekly.met ? t("pilotage.filiale.pluquiz.metText", "Minimum atteint") : t("pilotage.filiale.pluquiz.notMetText", "Minimum non atteint")}
                                        </span>
                                      </div>
                                      <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>
                                        {t("pilotage.filiale.pluquiz.examCountTemplate", { n: pilotPluWeekly.examCount, min: pilotPluWeekly.minimum }, `${pilotPluWeekly.examCount}/${pilotPluWeekly.minimum} examen(s) passé(s)`)}
                                      </span>
                                    </div>
                                  )}
                                  <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: 6 }}>{t("pilotage.filiale.pluquiz.recentTitle", "Examens de la semaine en cours")}</span>
                                  {pilotPluAttempts.length === 0 ? (
                                    <p style={{ margin: "0 0 12px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.pluquiz.noneYet", "Aucun examen passé cette semaine.")}</p>
                                  ) : pilotPluAttempts.map((a) => (
                                    <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, borderRadius: 14, background: P.soft, marginBottom: 6 }}>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{a.employeeName}</strong>
                                        <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{fmtFR((a.finishedAt || "").slice(0, 10))} · {a.percentage}%</div>
                                      </div>
                                      <span style={{
                                        fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "3px 9px", whiteSpace: "nowrap",
                                        color: a.passed ? "#fff" : A.red, background: a.passed ? A.green : "rgba(239,68,68,.13)",
                                      }}>{a.passed ? t("pluQuiz.recent.passed", "Réussi") : t("pluQuiz.recent.failed", "Non réussi")}</span>
                                      <Pressable onClick={() => printPluAttempt(a)} aria-label={t("pluQuiz.attempt.downloadBtn", "Télécharger ce test")} style={{ width: 30, height: 30, borderRadius: 99, background: P.line, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>🖨️</Pressable>
                                    </div>
                                  ))}
                                  <Pressable onClick={exportPilotPluWeekly} style={{
                                    width: "100%", textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                                    background: "transparent", color: P.muted, border: `1px solid ${P.line}`, marginTop: 6,
                                  }}>🖨️ {t("pilotage.filiale.pluquiz.downloadReportBtn", "Télécharger le rapport groupé de la semaine (optionnel)")}</Pressable>

                                  <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".05em", display: "block", margin: "16px 0 6px" }}>{t("inventurmassnahme.archive.ficheTitle", "Archive complète (Lieferschein Test · Bewertungsbogen · PLU Quiz)")}</span>
                                  {pilotImArchiveLoading ? (
                                    <p style={{ margin: "0 0 6px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.loadingEllipsis", "Chargement…")}</p>
                                  ) : imRenderWeekFolders(pilotImArchiveWeeks, {
                                    // readOnly retiré (retour utilisateur explicite,
                                    // 2026-08-29 : "pourquoi le GR ne signe pas
                                    // automatiquement dans son profil ?") — le GR est déjà
                                    // authentifié en consultant cette Fiche filiale, jamais
                                    // besoin d'un lien à distance pour SA PROPRE signature.
                                    // onDeleteItem volontairement absent (readOnly seulement
                                    // pour la suppression, jamais pour signer/éditer) —
                                    // reste réservé au compte de la filiale elle-même.
                                    // editImRecord() est le même point d'entrée que côté
                                    // filiale (screen "inventurmassnahme"/imScreen
                                    // "lieferschein"|"testkauf") : le pad BL y est déjà
                                    // affiché dès que isNetworkLeader est vrai (voir les
                                    // useEffect mountImSignaturePad plus haut) — aucun nouvel
                                    // écran à construire, juste ce branchement.
                                    expandedWeek: pilotImArchiveExpandedWeek, setExpandedWeek: setPilotImArchiveExpandedWeek,
                                    onExportItem: (it) => it.kind === "plu" ? printPluAttempt(it.raw) : it.kind === "bw" ? exportTestkaufPdf(it.raw) : exportLieferscheinTestPdf(it.raw),
                                    // editImRecord() seul ne suffit pas ici : il ne change que
                                    // imScreen, jamais le screen principal (qui reste "home"/
                                    // pilotScreen "filiale-detail") — sans ce setScreen, le clic
                                    // sur ✎ ne faisait visuellement rien (bug remonté par
                                    // l'utilisateur). imReturnToPilot fait revenir directement à
                                    // cette Fiche filiale au lieu de la chaîne normale hub→home.
                                    onEditItem: (it) => { editImRecord(it.raw); setScreen("inventurmassnahme"); setImReturnToPilot(true); },
                                  })}
                                </>
                              ) : s.id === "reporting" ? (
                                <>
                                  {pilotStatsLoading && !reportingStats ? (
                                    <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.loadingEllipsis", "Chargement…")}</p>
                                  ) : !reportingStats ? (
                                    <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.reporting.noDataText", "Pas encore de données Reporting pour cette filiale sur cette période.")}</p>
                                  ) : (() => {
                                    const fmtEuroFiche = (v) => v.toLocaleString("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " €";
                                    const periodLabelFiche = { semaine: t("pilotage.analyse.period.week", "cette semaine"), mois: t("pilotage.analyse.period.month", "ce mois-ci"), trimestre: t("pilotage.analyse.period.quarter", "ce trimestre"), annee: t("pilotage.analyse.period.year", "cette année") }[pilotPeriod];
                                    const ecartPctFiche = reportingStats.umsatzPlan > 0 ? Math.round(((reportingStats.umsatzIst - reportingStats.umsatzPlan) / reportingStats.umsatzPlan) * 100) : null;
                                    const kpis = [
                                      { l: t("pilotage.kpi.umsatz", "Umsatz"), v: fmtEuroFiche(reportingStats.umsatzIst), obj: reportingStats.umsatzPlan > 0 ? t("pilotage.kpi.objTemplate", { amount: fmtEuroFiche(reportingStats.umsatzPlan) }, `Obj. ${fmtEuroFiche(reportingStats.umsatzPlan)}`) : t("pilotage.kpi.noObjectiveSet", "Objectif non saisi"), status: ecartPctFiche === null ? "—" : ecartPctFiche >= 0 ? t("pilotage.kpi.status.reached", "Atteint") : ecartPctFiche > -5 ? t("pilotage.kpi.status.atRisk", "En risque") : t("pilotage.kpi.status.notReached", "Non atteint"), statusColor: ecartPctFiche === null ? P.muted : ecartPctFiche >= 0 ? A.green : ecartPctFiche > -5 ? A.orange : A.red },
                                      { l: t("pilotage.kpi.pertes", "Pertes"), v: fmtEuroFiche(reportingStats.pertes), obj: reportingTauxPertes !== null ? t("pilotage.kpi.pctOfRevenueTemplate", { pct: reportingTauxPertes.toFixed(1) }, `${reportingTauxPertes.toFixed(1)}% du CA`) : "—", status: reportingStats.pertes === 0 ? t("pilotage.kpi.status.none", "Aucune") : reportingTauxPertes < 1.2 ? t("pilotage.kpi.status.controlled", "Maîtrisé") : reportingTauxPertes < 1.8 ? t("pilotage.kpi.status.toWatch", "À surveiller") : t("pilotage.kpi.status.high", "Élevé"), statusColor: reportingStats.pertes === 0 ? A.green : reportingTauxPertes < 1.2 ? A.green : reportingTauxPertes < 1.8 ? A.orange : A.red },
                                      { l: "Stundenleistung", v: reportingStats.stundenleistung === null ? "—" : `${reportingStats.stundenleistung.toFixed(1)} €/h`, obj: t("pilotage.kpi.hoursWorkedTemplate", { hours: reportingStats.stundenIst.toFixed(0) }, `${reportingStats.stundenIst.toFixed(0)} h travaillées`), status: reportingStats.stundenleistung === null ? "—" : reportingStats.stundenleistung >= 190 ? t("pilotage.kpi.status.good", "Bon") : t("pilotage.kpi.status.low", "Faible"), statusColor: reportingStats.stundenleistung === null ? P.muted : reportingStats.stundenleistung >= 190 ? A.green : A.orange },
                                    ];
                                    return (
                                      <>
                                        <p style={{ margin: "0 0 10px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.reporting.periodTemplate", { period: periodLabelFiche }, `Chiffres Reporting réels — ${periodLabelFiche}`)}</p>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                          {kpis.map((k) => (
                                            <div key={k.l} style={{ background: P.soft, borderRadius: 14, padding: 12 }}>
                                              <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".03em" }}>{k.l}</span>
                                              <strong style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, display: "block", margin: "3px 0 1px", letterSpacing: "-.02em" }}>{k.v}</strong>
                                              <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{k.obj}</span>
                                              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
                                                <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: k.statusColor, borderRadius: 99, padding: "3px 8px" }}>{k.status}</span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </>
                                    );
                                  })()}
                                  {/* Détail hebdomadaire + les 3 vrais documents déjà téléchargeables par un compte
                                      normal pour SA filiale (voir buildWeeklyReportPrintHtml/buildStundenleistungPrintHtml/
                                      buildMonthClosurePrintHtml, tous acceptent désormais un storeId explicite) —
                                      toujours visible ici, indépendant du chargement des KPIs période ci-dessus
                                      (pilotStoreStats), jamais bloqué sur une saisie pas encore terminée. */}
                                  {(() => {
                                    const selectedWeek = pilotRepWeekView === "previous" ? isoWeek(now) - 1 : isoWeek(now);
                                    const weekSrc = pilotRepWeekSrcs[selectedWeek];
                                    return (
                                      <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${P.line}` }}>
                                        <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 8 }}>{t("pilotage.filiale.reporting.weeklyHeading", "Détail hebdomadaire")}</strong>
                                        <div className="nx-chips" style={{ display: "flex", gap: 6, marginBottom: 10, overflowX: "auto", paddingBottom: 2 }}>
                                          <Pressable onClick={() => setPilotRepWeekView("current")} style={{
                                            flex: "0 0 auto", textAlign: "center", padding: "7px 12px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11px * var(--nx-font-scale, 1))`,
                                            background: pilotRepWeekView === "current" ? A.blue : P.soft, color: pilotRepWeekView === "current" ? "#fff" : P.ink,
                                            border: `1.5px solid ${pilotRepWeekView === "current" ? A.blue : P.line}`,
                                          }}>{t("reporting.currentWeekBtn", "Semaine en cours")}</Pressable>
                                          <Pressable onClick={() => setPilotRepWeekView("previous")} style={{
                                            flex: "0 0 auto", textAlign: "center", padding: "7px 12px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11px * var(--nx-font-scale, 1))`,
                                            background: pilotRepWeekView === "previous" ? A.blue : P.soft, color: pilotRepWeekView === "previous" ? "#fff" : P.ink,
                                            border: `1.5px solid ${pilotRepWeekView === "previous" ? A.blue : P.line}`,
                                          }}>{t("planning.control.prevWeek", "Semaine précédente")}</Pressable>
                                        </div>
                                        {pilotRepWeekLoading && !weekSrc ? (
                                          <p style={{ margin: "0 0 10px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.loadingEllipsis", "Chargement…")}</p>
                                        ) : !weekSrc ? (
                                          <p style={{ margin: "0 0 10px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.filiale.reporting.noWeekDataYetText", "Pas encore de données pour cette semaine.")}</p>
                                        ) : (
                                          <div style={{ background: P.soft, borderRadius: 14, padding: 12, marginBottom: 12 }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                                              <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".03em" }}>KW {selectedWeek} · {t("reporting.totalWeekLabel", "Total semaine")}</span>
                                              <strong style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>{fmt2(weekSrc.total)} €</strong>
                                            </div>
                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                                              {REPORT_CATEGORIES.map((cat) => (
                                                <div key={cat.id} style={{ display: "flex", justifyContent: "space-between", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`}}>
                                                  <span style={{ color: P.muted }}>{reportCategoryLabel(cat.id)}</span>
                                                  <strong>{fmt2(weekSrc.rowTotal(cat.id))} €</strong>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", margin: "4px 0 8px" }}>{t("pilotage.filiale.reporting.downloadsHeading", "Documents à télécharger")}</strong>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                          <Pressable onClick={exportPilotWeeklyReport} disabled={!weekSrc} style={{
                                            width: "100%", ...card, borderRadius: 14, padding: "11px 14px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800,
                                            display: "flex", alignItems: "center", justifyContent: "center", gap: 7, opacity: !weekSrc ? .5 : 1,
                                          }}>🖨️ {t("pilotage.filiale.reporting.exportWeeklyBtn", "Télécharger Woche Abschriften")}</Pressable>
                                          <Pressable onClick={exportPilotStundenleistung} disabled={pilotStundenDays.length === 0} style={{
                                            width: "100%", ...card, borderRadius: 14, padding: "11px 14px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800,
                                            display: "flex", alignItems: "center", justifyContent: "center", gap: 7, opacity: pilotStundenDays.length === 0 ? .5 : 1,
                                          }}>🖨️ {t("pilotage.filiale.reporting.exportStundenBtn", "Télécharger Stundenleistung")}</Pressable>
                                          <Pressable onClick={exportPilotMonatsmeldung} disabled={pilotStundenDays.length === 0} style={{
                                            width: "100%", ...card, borderRadius: 14, padding: "11px 14px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800,
                                            display: "flex", alignItems: "center", justifyContent: "center", gap: 7, opacity: pilotStundenDays.length === 0 ? .5 : 1,
                                          }}>🖨️ {t("pilotage.filiale.reporting.exportMonatsmeldungBtn", "Télécharger Monatsmeldung")}</Pressable>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </>
                              ) : sectionIssues.length > 0 ? sectionIssues.map((f, i) => (
                                <div key={i} style={{
                                  padding: 12, borderRadius: 14, marginTop: 6,
                                  background: f.severity === "danger" ? "rgba(239,68,68,.08)" : "rgba(231,138,18,.09)",
                                  border: `1px solid ${f.severity === "danger" ? "rgba(239,68,68,.25)" : "rgba(231,138,18,.25)"}`,
                                }}>
                                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: f.severity === "danger" ? A.red : "#92620a" }}>{f.title}</strong>
                                </div>
                              )) : (
                                <p style={{ margin: "6px 0 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>✓ {t("pilotage.filiale.nothingInSectionText", "Rien à signaler dans cette rubrique.")}</p>
                              )}
                            </div>
                          )}
                        </section>
                      );
                    })}
                  </div>
                );
              })()}

              {pilotScreen === "analyse" && (() => {
                const stores = managerOverview?.stores || [];
                // Onglet Équipe — n'apparaît que pour un responsable ayant réellement des
                // subordonnés rattachés (reportsTo), quel que soit le palier : un VKL voit
                // ses BL (2026-08-16, retour utilisateur "il doit tout au moins hériter de
                // tout ce que possède un BL et plus que ça"), un NL voit ses VKL (retour
                // utilisateur 2026-08-23, "personnaliser chaque profil... un NL n'a pas de
                // vue comparative de ses VKL" — AVANT, ce filtre était figé sur
                // role==="Bereichsleiter", donc invisible pour un NL). Jamais pour un BL
                // lui-même (pas de subordonné réseau). adminUsers déjà chargé par l'effet
                // dédié ci-dessus.
                const directReports = adminUsers.filter((u) => u.reportsTo === user?.proId);
                // Tous les subordonnés directs d'un même responsable sont toujours du même
                // palier (voir POST /api/leaders, SUBORDINATE_ROLE_OF) — sert uniquement à
                // adapter le texte d'intro ci-dessous ("regroupé par VKL"/"par BL"), jamais
                // un vrai champ métier.
                const directReportsRoleLabel = directReports[0]?.role || t("pilotage.team.genericRoleLabel", "responsables");
                // Rapport intelligent réagencé par BL (vue VKL, item 8) — quand ce VKL a une
                // équipe, chaque filiale signalée dans les insights pointe aussi vers le BL
                // responsable, pas seulement la filiale isolée (retour utilisateur : "il doit
                // tout au moins hériter de tout ce que possède un BL et plus que ça").
                const blOwnerLabelFor = (storeId) => {
                  const owner = directReports.find((bl) => (bl.stores && bl.stores.length ? bl.stores : [bl.storeId]).includes(storeId));
                  return owner ? ` (${t("pilotage.team.ownedByTemplate", { name: `${owner.firstname} ${owner.lastname}` }, `chez ${owner.firstname} ${owner.lastname}`)})` : "";
                };
                const scope = pilotDashFiliale === "all" ? stores : [pilotDashFiliale];
                const agg = scope.reduce((acc, sId) => {
                  const s = pilotStoreStats[sId];
                  if (!s) return acc;
                  acc.umsatzIst += s.umsatzIst; acc.umsatzPlan += s.umsatzPlan; acc.stundenIst += s.stundenIst; acc.stundenPlan += s.stundenPlan || 0; acc.pertes += s.pertes;
                  return acc;
                }, { umsatzIst: 0, umsatzPlan: 0, stundenIst: 0, stundenPlan: 0, pertes: 0 });
                const stundenleistung = agg.stundenIst > 0 ? agg.umsatzIst / agg.stundenIst : null;
                const ecartPct = agg.umsatzPlan > 0 ? Math.round(((agg.umsatzIst - agg.umsatzPlan) / agg.umsatzPlan) * 100) : null;
                // Cible Stundenleistung réelle (plan sur plan, demande utilisatrice
                // 2026-08-15) — remplace STUNDENLEISTUNG_ALERT_THRESHOLD partout où
                // Stundenleistung est comparée à une cible ; null si aucun plan d'heures connu
                // sur le périmètre/la période (jamais une fausse cible à 0€/h).
                const stundenleistungPlan = agg.stundenPlan > 0 ? agg.umsatzPlan / agg.stundenPlan : null;
                const stundEcartPct = stundenleistungPlan > 0 && stundenleistung !== null ? Math.round(((stundenleistung - stundenleistungPlan) / stundenleistungPlan) * 100) : null;
                const fmtEuroPilot = (v) => v.toLocaleString("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " €";

                return (
                  <>
                    <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 16, paddingBottom: 2 }}>
                      <Pressable onClick={() => setPilotDashFiliale("all")} style={{
                        flex: "0 0 auto", padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                        background: pilotDashFiliale === "all" ? A.purple : P.soft, color: pilotDashFiliale === "all" ? "#fff" : P.muted,
                      }}>{t("pilotage.analyse.allStoresChip", "Toutes les filiales")}</Pressable>
                      {stores.map((sId) => (
                        <Pressable key={sId} onClick={() => setPilotDashFiliale(sId)} style={{
                          flex: "0 0 auto", padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                          background: pilotDashFiliale === sId ? A.purple : P.soft, color: pilotDashFiliale === sId ? "#fff" : P.muted,
                        }}>{storeLabelFor(sId)}</Pressable>
                      ))}
                    </div>

                    <div style={{ display: "flex", gap: 4, padding: 3, background: P.soft, borderRadius: 15, marginBottom: 16, overflowX: "auto" }}>
                      {[
                        { id: "resume", l: t("pilotage.analyse.tab.resume", "Résumé") },
                        { id: "evolution", l: t("pilotage.analyse.tab.evolution", "Évolution") },
                        { id: "filiales", l: t("pilotage.analyse.tab.filiales", "Filiales") },
                        { id: "comparaison", l: t("pilotage.analyse.tab.comparaison", "Comparaison") },
                        // Onglet toujours visible pour un VKL/NL (même sans subordonné
                        // encore créé — voir POST /api/leaders/openCreateLeader), plus
                        // seulement conditionné à "en avoir déjà" (retour utilisateur
                        // 2026-08-23 : sinon aucun moyen de créer SON PREMIER VKL/BL).
                        ...(["Verkaufsleiter", "Niederlassungsleiter"].includes(user?.role) ? [{ id: "equipe", l: t("pilotage.analyse.tab.equipe", "Équipe") }] : []),
                      ].map((tb) => (
                        <Pressable key={tb.id} onClick={() => setAnalyseTab(tb.id)} style={{
                          flex: 1, textAlign: "center", padding: "9px 4px", borderRadius: 12, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                          background: analyseTab === tb.id ? P.card : "transparent", color: analyseTab === tb.id ? P.ink : P.muted,
                          boxShadow: analyseTab === tb.id ? "0 1px 4px rgba(0,0,0,.08)" : "none",
                        }}>{tb.l}</Pressable>
                      ))}
                    </div>

                    {/* Gardé sur "aucune donnée encore chargée" (jamais sur pilotStatsLoading seul) —
                        sinon chaque poll d'actualisation en direct (15s) démonte puis remonte tout
                        ce bloc (KPI + graphique), ce qui se lit comme un rechargement de page et rend
                        les boutons ‹ › et le survol du graphique inutilisables (retour utilisateur). */}
                    {pilotStatsLoading && !scope.some((sId) => pilotStoreStats[sId]) && <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("common.loading", "Chargement...")}</p>}

                    {scope.some((sId) => pilotStoreStats[sId]) && (() => {
                      const rows = scope.map((sId) => ({ storeId: sId, s: pilotStoreStats[sId] })).filter((r) => r.s);
                      const tauxPertes = agg.umsatzIst > 0 ? (agg.pertes / agg.umsatzIst) * 100 : 0;
                      const nonAtteint = rows.filter((r) => r.s.umsatzPlan > 0 && r.s.umsatzIst < r.s.umsatzPlan);
                      const worstPertes = rows.slice().sort((a, b) => b.s.pertes - a.s.pertes)[0];
                      const bestStunden = rows.slice().sort((a, b) => (b.s.stundenleistung || 0) - (a.s.stundenleistung || 0))[0];

                      const trendOf = (hist) => {
                        let dir = null, count = 0;
                        for (let i = hist.length - 1; i > 0; i--) {
                          const d = hist[i] - hist[i - 1];
                          if (dir === null) { if (d === 0) break; dir = d < 0 ? "down" : "up"; count = 1; }
                          else if ((d < 0 && dir === "down") || (d > 0 && dir === "up")) count++;
                          else break;
                        }
                        return { dir, count };
                      };
                      const trendRows = rows.map((r) => ({ r, t: trendOf(pilotHistory[r.storeId] || []) })).filter((x) => x.t.count >= 3);
                      const worstTrend = trendRows.filter((x) => x.t.dir === "down").sort((a, b) => b.t.count - a.t.count)[0];

                      let vScore = 0;
                      vScore += ecartPct === null ? 0 : ecartPct >= 0 ? 1 : ecartPct > -5 ? 0 : -1;
                      vScore += tauxPertes < 1.2 ? 1 : tauxPertes < 1.8 ? 0 : -1;
                      vScore += nonAtteint.length === 0 ? 1 : nonAtteint.length <= rows.length / 2 ? 0 : -1;
                      const verdict = vScore >= 1 ? { label: t("pilotage.analyse.verdict.good", "Bonne période"), color: A.green } : vScore <= -1 ? { label: t("pilotage.today.toWatch", "À surveiller"), color: A.red } : { label: t("pilotage.analyse.verdict.mixed", "Période mitigée"), color: A.orange };

                      const Pill = ({ children, onClick, color }) => (
                        <Pressable onClick={onClick} style={{
                          display: "inline-block", color: "#fff", fontWeight: 800, background: color,
                          borderRadius: 8, padding: "1.5px 8px", margin: "0 2px", lineHeight: 1.6, verticalAlign: "-1px", fontSize: "0.94em",
                        }}>{children}</Pressable>
                      );
                      const goTo = (sId) => { setPilotFilialeId(sId); setPilotFilialeSection(null); setPilotScreen("filiale-detail"); };
                      const createActionMission = (targetRows, title) => {
                        setMissionForm({ ...emptyMissionForm, title, filiales: targetRows.map((r) => r.storeId), preuveRequise: true, note: "Généré depuis le rapport intelligent du Dashboard." });
                        setMissionEditId(null);
                        setPilotScreen("missions"); setSheet("missionForm");
                      };

                      const periodLabel = { semaine: t("pilotage.analyse.period.week", "cette semaine"), mois: t("pilotage.analyse.period.month", "ce mois-ci"), trimestre: t("pilotage.analyse.period.quarter", "ce trimestre"), annee: t("pilotage.analyse.period.year", "cette année") }[pilotPeriod];
                      const insights = [
                        {
                          tone: ecartPct === null ? A.blue : ecartPct >= 0 ? A.green : ecartPct > -5 ? A.orange : A.red, icon: "💰",
                          content: <>{t("pilotage.analyse.insight.revenuePrefix", "Le chiffre d'affaires atteint")} <strong>{(agg.umsatzIst / 1000).toFixed(1)}k €</strong> {periodLabel}{agg.umsatzPlan > 0 ? <> {t("pilotage.analyse.insight.revenueObjectivePrefix", "— objectif fixé à")} <strong>{(agg.umsatzPlan / 1000).toFixed(1)}k €</strong> ({ecartPct >= 0 ? "+" : ""}{ecartPct}%)</> : t("pilotage.analyse.insight.noRevenueObjective", " — aucun objectif Plan saisi sur cette période.")}</>,
                        },
                        worstTrend ? {
                          tone: A.red, icon: "📆",
                          content: <>{t("pilotage.analyse.insight.trendPrefix", "C'est la")} <strong>{t("pilotage.analyse.insight.trendWeekCountTemplate", { n: worstTrend.t.count }, `${worstTrend.t.count}ᵉ semaine consécutive de baisse`)}</strong> {t("pilotage.analyse.insight.trendAtSuffix", "à")} <Pill color={A.red} onClick={() => goTo(worstTrend.r.storeId)}>{storeLabelFor(worstTrend.r.storeId)}</Pill>{blOwnerLabelFor(worstTrend.r.storeId)} {t("pilotage.analyse.insight.trendSuffix", "— une vraie tendance, pas un accident isolé.")}</>,
                        } : null,
                        nonAtteint.length > 0 ? {
                          tone: nonAtteint.length > rows.length / 2 ? A.red : A.orange, icon: "⚠️",
                          content: <>
                            <strong>{nonAtteint.length}</strong> {t("pilotage.analyse.insight.notReachedPrefixTemplate", { n: nonAtteint.length }, nonAtteint.length > 1 ? "filiales n'ont pas atteint leur objectif :" : "filiale n'a pas atteint son objectif :")}{" "}
                            {nonAtteint.map((r, i) => <Fragment key={r.storeId}><Pill color={A.red} onClick={() => goTo(r.storeId)}>{storeLabelFor(r.storeId)}</Pill>{blOwnerLabelFor(r.storeId)}{i < nonAtteint.length - 1 ? " " : ""}</Fragment>)}.
                          </>,
                          action: { label: t("pilotage.analyse.insight.createFixMissionBtn", "🎯 Créer une mission de redressement"), onClick: () => createActionMission(nonAtteint, t("pilotage.analyse.insight.fixMissionTitle", "Plan d'action — objectif non atteint")) },
                        } : (rows.some((r) => r.s.umsatzPlan > 0) ? { tone: A.green, icon: "✅", content: <>{t("pilotage.analyse.insight.allReachedTemplate", { period: periodLabel }, `Toutes les filiales du périmètre ont atteint leur objectif ${periodLabel}.`)}</> } : null),
                        agg.pertes > 0 ? {
                          tone: tauxPertes < 1.2 ? A.green : tauxPertes < 1.8 ? A.orange : A.red, icon: "📉",
                          content: <>{t("pilotage.analyse.insight.lossesPrefix", "Les pertes s'élèvent à")} <strong>{agg.pertes.toFixed(0)} €</strong>{agg.umsatzIst > 0 ? <> {t("pilotage.analyse.insight.lossesPctTemplate", { pct: tauxPertes.toFixed(1) }, `(${tauxPertes.toFixed(1)}% du CA)`)}</> : ""}{worstPertes ? <>{t("pilotage.analyse.insight.lossesDrivenByPrefix", ", tirées surtout par")} <Pill color={A.orange} onClick={() => goTo(worstPertes.storeId)}>{storeLabelFor(worstPertes.storeId)}</Pill>{blOwnerLabelFor(worstPertes.storeId)} ({worstPertes.s.pertes.toFixed(0)} €)</> : ""}.</>,
                          action: tauxPertes >= 1.2 && worstPertes ? { label: t("pilotage.analyse.insight.requestFixBtn", "📮 Demander une correction"), onClick: () => createActionMission([worstPertes], t("pilotage.analyse.insight.reduceLossesTitleTemplate", { store: storeLabelFor(worstPertes.storeId) }, `Réduire les pertes — ${storeLabelFor(worstPertes.storeId)}`)) } : null,
                        } : null,
                        bestStunden && bestStunden.s.stundenleistung !== null ? {
                          tone: A.blue, icon: "⏱️",
                          content: <>{t("pilotage.analyse.insight.bestStundenTemplate", "La Stundenleistung la plus forte du périmètre est à")} <Pill color={A.blue} onClick={() => goTo(bestStunden.storeId)}>{storeLabelFor(bestStunden.storeId)}</Pill>{blOwnerLabelFor(bestStunden.storeId)} ({bestStunden.s.stundenleistung.toFixed(1)} €/h).</>,
                        } : null,
                      ].filter(Boolean);
                      const sevOf = (tone) => tone === A.red ? 3 : tone === A.orange ? 2 : tone === A.green ? 0 : 1;
                      insights.sort((a, b) => sevOf(b.tone) - sevOf(a.tone));

                      // Graphique "qui parle" — retour utilisateur explicite (2026-08-09) :
                      // une simple courbe muette n'apporte rien à un GR pressé. Chaque point
                      // porte maintenant sa semaine (KWxx), sa valeur, sa couleur (vert/rouge
                      // vs objectif), le point courant est mis en évidence, et un badge de
                      // tendance résume l'évolution sur les 6 semaines en une phrase.
                      const histSums = scope.length ? Array.from({ length: PILOT_HISTORY_WEEKS }, (_, i) => scope.reduce((a, sId) => a + ((pilotHistory[sId] || [])[i] || 0), 0)) : [];
                      // Objectif RÉEL par semaine — jamais agg.umsatzPlan (bug d'origine : ce
                      // total est re-découpé selon pilotPeriod, une comparaison hebdo contre un
                      // objectif de mois/trimestre/année n'a jamais eu de sens). Voir
                      // loadPilotHistory/pilotHistoryPlan.
                      const histPlanSums = scope.length ? Array.from({ length: PILOT_HISTORY_WEEKS }, (_, i) => scope.reduce((a, sId) => a + ((pilotHistoryPlan[sId] || [])[i] || 0), 0)) : [];
                      const hasHistory = histSums.some((v) => v > 0);
                      // Décalé de evolutionWeekOffset semaines — sinon les libellés KWxx
                      // resteraient calés sur "aujourd'hui" même quand la fenêtre chargée
                      // (loadPilotHistory) est décalée dans le passé (Phase 3, 2026-08-15).
                      const weekNumFor = (i) => {
                        const refDate = new Date(now); refDate.setDate(refDate.getDate() + evolutionWeekOffset * 7);
                        const w = isoWeek(refDate) - (PILOT_HISTORY_WEEKS - 1 - i); return w >= 1 ? w : w + 52;
                      };
                      const firstDataIdx = histSums.findIndex((v) => v > 0);
                      const lastDataIdx = histSums.length - 1 - [...histSums].reverse().findIndex((v) => v > 0);
                      const trendPct = (firstDataIdx !== -1 && lastDataIdx !== -1 && firstDataIdx !== lastDataIdx && histSums[firstDataIdx] > 0)
                        ? Math.round(((histSums[lastDataIdx] - histSums[firstDataIdx]) / histSums[firstDataIdx]) * 100) : null;
                      const fmtK = (v) => `${(v / 1000).toFixed(1)}k€`;
                      const themeAccent = PILOT_RHYTHM_ACCENT[themeId] || PILOT_RHYTHM_ACCENT.blanc;
                      const fmtEuroChart = (v) => Math.round(v).toLocaleString("de-DE") + " €";

                      // ===== Stundenleistung — évolution réseau (demande Verkaufsleiter,
                      // 2026-08-14) : moyenne réseau par semaine + bande min/max entre filiales
                      // du périmètre, dérivée de pilotStundenHistory (déjà calculée par
                      // loadPilotHistory à partir des mêmes entrées stundenleistung que Umsatz,
                      // aucun fetch supplémentaire). Une filiale sans donnée cette semaine-là
                      // (null) est exclue du calcul de CETTE semaine, jamais traitée comme 0.
                      const stundHistPerWeek = Array.from({ length: PILOT_HISTORY_WEEKS }, (_, i) =>
                        scope.map((sId) => (pilotStundenHistory[sId] || [])[i]).filter((v) => v !== null && v !== undefined && v > 0)
                      );
                      const stundAvgPerWeek = stundHistPerWeek.map((vals) => vals.length ? vals.reduce((a, v) => a + v, 0) / vals.length : null);
                      const stundMinPerWeek = stundHistPerWeek.map((vals) => vals.length ? Math.min(...vals) : null);
                      const stundMaxPerWeek = stundHistPerWeek.map((vals) => vals.length ? Math.max(...vals) : null);
                      const hasStundHistory = stundAvgPerWeek.some((v) => v !== null);
                      // Cible Stundenleistung réelle par semaine (demande utilisatrice
                      // 2026-08-15, "plan hebdomadaire") — Σ umsatzPlan (histPlanSums, déjà
                      // calculé) / Σ stundenPlan du périmètre pour CETTE semaine ; null si
                      // aucune heure planifiée connue cette semaine-là (jamais une fausse cible
                      // à 0€/h). Remplace STUNDENLEISTUNG_ALERT_THRESHOLD dans les 2 graphes
                      // Stundenleistung ci-dessous.
                      const stundPlanRawSums = scope.length ? Array.from({ length: PILOT_HISTORY_WEEKS }, (_, i) => scope.reduce((a, sId) => a + ((pilotStundenPlanHistory[sId] || [])[i] || 0), 0)) : [];
                      const stundPlanSumsPerWeek = histPlanSums.map((uPlan, i) => stundPlanRawSums[i] > 0 ? uPlan / stundPlanRawSums[i] : null);

                      // ===== Pertes — évolution réseau : total € par semaine + répartition par
                      // rayon de la semaine sélectionnée (tap), dérivée de pilotPertesHistory/
                      // pilotPertesCategoryHistory (nouveaux fetches manual_waste/prebake/brot
                      // par semaine dans loadPilotHistory, même garde anti-double-comptage que
                      // loadPilotStoreStats). =====
                      const pertesHistSums = Array.from({ length: PILOT_HISTORY_WEEKS }, (_, i) => scope.reduce((a, sId) => a + ((pilotPertesHistory[sId] || [])[i] || 0), 0));
                      const pertesHistCategorySums = Array.from({ length: PILOT_HISTORY_WEEKS }, (_, i) => {
                        const merged = {};
                        scope.forEach((sId) => {
                          const cats = (pilotPertesCategoryHistory[sId] || [])[i] || {};
                          Object.entries(cats).forEach(([catId, amount]) => { merged[catId] = (merged[catId] || 0) + amount; });
                        });
                        return merged;
                      });
                      const hasPertesHistory = pertesHistSums.some((v) => v > 0);

                      // ===== Rythme Umsatz — filiale unique, résolution JOURNALIÈRE réelle =====
                      // Mockup validé (Style B) : https://claude.ai/code/artifact/beab8649-2855-
                      // 48ea-b185-14e8035fcdef. Deux courbes cumulées (mois jamais remis à zéro,
                      // semaine remise à zéro chaque lundi) contre leur propre "rythme" — ici le
                      // cumul RÉEL du Plan (umsatzPlanCum de stundenComputed), plus fidèle qu'une
                      // diagonale linéaire puisque le document Norma donne déjà une cible propre à
                      // chaque jour (jamais uniforme sur le mois).
                      const rhythmSingleStore = pilotDashFiliale === "all" || !pilotRhythmDays.length ? null : (() => {
                        const days = pilotRhythmDays;
                        const computed = stundenComputed({ stundenDays: days });
                        const daysInMonth = days.length;
                        const lastIstDay = days.reduce((max, d) => (d.umsatzIstTag != null ? Math.max(max, d.day) : max), 0);
                        const viewedDate = new Date(now.getFullYear(), now.getMonth() + pilotRhythmMonthOffset, 1);
                        const viewedYear = viewedDate.getFullYear(), viewedMonth = viewedDate.getMonth() + 1;
                        const isCurrentMonthView = pilotRhythmMonthOffset === 0;
                        const dayNumIfSameMonth = (d) => (d.getFullYear() === viewedYear && d.getMonth() + 1 === viewedMonth) ? d.getDate() : null;

                        // Semaine ISO affichée — celle contenant "aujourd'hui" en vue mois en
                        // cours, sinon la DERNIÈRE semaine du mois consulté (retour utilisateur :
                        // simplification explicite, pas de sélecteur de semaine séparé).
                        const pivot = isCurrentMonthView ? now : new Date(viewedYear, viewedMonth - 1, daysInMonth);
                        const dow = pivot.getDay() || 7;
                        const mondayDate = new Date(pivot); mondayDate.setDate(pivot.getDate() - dow + 1);
                        const sundayDate = new Date(mondayDate); sundayDate.setDate(mondayDate.getDate() + 6);
                        const weekStartDay = dayNumIfSameMonth(mondayDate) || 1;
                        const weekEndDay = dayNumIfSameMonth(sundayDate) || daysInMonth;
                        const todayDay = isCurrentMonthView ? Math.min(now.getDate(), daysInMonth) : null;

                        const monthActual = computed.map((d) => d.umsatzIstCum);
                        const monthPace = computed.map((d) => d.umsatzPlanCum);
                        const monthObjective = computed.length ? computed[computed.length - 1].umsatzPlanCum : 0;

                        const weekActual = computed.map(() => null);
                        const weekPace = computed.map(() => null);
                        let cumWI = 0, cumWP = 0, weekBroken = false;
                        for (let day = weekStartDay; day <= weekEndDay; day++) {
                          const d = computed[day - 1];
                          if (!d) continue;
                          if (!d.isClosed && d.umsatzPlanTag != null) cumWP += d.umsatzPlanTag;
                          if (day > lastIstDay) weekBroken = true;
                          if (!weekBroken && d.umsatzIstTag != null) cumWI += d.umsatzIstTag;
                          weekPace[day - 1] = cumWP;
                          weekActual[day - 1] = weekBroken ? null : cumWI;
                        }
                        const weekObjective = weekPace[weekEndDay - 1] || 0;

                        // Badge ✓/✕ — une semaine ISO entièrement dans le mois affiché (lundi ET
                        // samedi entre 1 et daysInMonth), entièrement écoulée (samedi <=
                        // lastIstDay) et JAMAIS la semaine en cours affichée ci-dessus.
                        const weekBadges = [];
                        const firstOfMonth = new Date(viewedYear, viewedMonth - 1, 1);
                        const dow1 = firstOfMonth.getDay() || 7;
                        const cursorMonday = new Date(firstOfMonth); cursorMonday.setDate(firstOfMonth.getDate() - dow1 + 1);
                        for (let guard = 0; guard < 7; guard++) {
                          const mondayNum = dayNumIfSameMonth(cursorMonday);
                          const saturday = new Date(cursorMonday); saturday.setDate(cursorMonday.getDate() + 5);
                          const saturdayNum = dayNumIfSameMonth(saturday);
                          if (mondayNum && saturdayNum && mondayNum !== weekStartDay && saturdayNum <= lastIstDay) {
                            let wTotal = 0, wObj = 0;
                            for (let day = mondayNum; day <= saturdayNum; day++) {
                              const d = computed[day - 1];
                              if (!d) continue;
                              wTotal += d.umsatzIstTag || 0; wObj += d.umsatzPlanTag || 0;
                            }
                            if (wObj > 0) weekBadges.push({ day: saturdayNum, total: wTotal, objective: wObj, reached: wTotal >= wObj });
                          }
                          cursorMonday.setDate(cursorMonday.getDate() + 7);
                          if (cursorMonday.getFullYear() > viewedYear || (cursorMonday.getFullYear() === viewedYear && cursorMonday.getMonth() + 1 > viewedMonth)) break;
                        }

                        // Lundis du mois (repères visuels des frontières de semaine) — recalculé
                        // proprement plutôt que de réutiliser cursorMonday, épuisé par la boucle
                        // des badges ci-dessus.
                        const mondays = [];
                        const cursor2 = new Date(firstOfMonth); cursor2.setDate(firstOfMonth.getDate() - dow1 + 1);
                        for (let guard = 0; guard < 7; guard++) {
                          const n = dayNumIfSameMonth(cursor2);
                          if (n) mondays.push(n);
                          cursor2.setDate(cursor2.getDate() + 7);
                          if (cursor2.getFullYear() > viewedYear || (cursor2.getFullYear() === viewedYear && cursor2.getMonth() + 1 > viewedMonth)) break;
                        }

                        return {
                          daysInMonth, todayDay, weekStartDay, weekEndDay, mondays,
                          monthActual, monthPace, monthObjective,
                          weekActual, weekPace, weekObjective,
                          weekBadges,
                          monthLabel: viewedDate.toLocaleDateString(intlLocale, { month: "long", year: "numeric" }),
                        };
                      })();

                      return (
                        <>
                        {analyseTab === "resume" && (
                          <>
                          <section style={{
                            ...card, borderRadius: 22, padding: 16, marginBottom: 18,
                            border: `1px solid ${themeId === "sombre" ? "rgba(124,92,242,.25)" : "rgba(54,79,231,.14)"}`,
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                              <span style={{
                                width: 38, height: 38, borderRadius: 13, flex: "0 0 auto", color: "#fff",
                                background: `linear-gradient(155deg, rgba(255,255,255,.28), rgba(255,255,255,0) 55%), linear-gradient(135deg, ${A.blue}, #7c5cf2)`,
                                display: "grid", placeItems: "center", fontSize: `calc(18px * var(--nx-font-scale, 1))`,
                              }}>🤖</span>
                              <div style={{ flex: 1 }}>
                                <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{t("pilotage.analyse.smartReportTitle", "Rapport intelligent")}</strong>
                                <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.analyse.smartReportSubtitleTemplate", { period: periodLabel }, `Ce que ton périmètre te dit ${periodLabel}`)}</div>
                              </div>
                              {!dashTyping && (
                                <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: verdict.color, borderRadius: 99, padding: "5px 11px", whiteSpace: "nowrap" }}>{verdict.label}</span>
                              )}
                            </div>
                            {dashTyping && (
                              <div style={{ display: "flex", gap: 4, padding: "4px 0 8px" }}>
                                {[0, 1, 2].map((i) => <span key={i} className="nx-typing-dot" style={{ width: 7, height: 7, borderRadius: 99, background: P.muted, animationDelay: `${i * 0.15}s` }} />)}
                              </div>
                            )}
                            {!dashTyping && insights.length === 0 && (
                              <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.analyse.notEnoughDataText", "Pas encore assez de données Reporting sur cette période pour un vrai rapport.")}</p>
                            )}
                            {!dashTyping && insights.map((ins, i) => (
                              <div key={i} className="nx-insight-in" style={{ padding: "9px 0", borderTop: i > 0 ? `1px solid ${P.line}` : "none", animationDelay: `${i * 0.09}s` }}>
                                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                                  <span style={{
                                    width: 26, height: 26, borderRadius: 9, flex: "0 0 auto", display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                                    background: `${ins.tone}1a`,
                                  }}>{ins.icon}</span>
                                  <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, lineHeight: 1.55, color: P.ink }}>{ins.content}</p>
                                </div>
                                {ins.action && (
                                  <Pressable onClick={ins.action.onClick} style={{
                                    marginLeft: 36, marginTop: 6, display: "inline-block", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff",
                                    background: A.purple, borderRadius: 99, padding: "7px 13px",
                                  }}>{ins.action.label}</Pressable>
                                )}
                              </div>
                            ))}
                          </section>

                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 18 }}>
                            {[
                              { l: t("pilotage.kpi.umsatz", "Umsatz"), v: fmtEuroPilot(agg.umsatzIst), obj: agg.umsatzPlan > 0 ? t("pilotage.kpi.objTemplate", { amount: fmtEuroPilot(agg.umsatzPlan) }, `Obj. ${fmtEuroPilot(agg.umsatzPlan)}`) : t("pilotage.kpi.noObjectiveSet", "Objectif non saisi"), status: ecartPct === null ? "—" : ecartPct >= 0 ? t("pilotage.kpi.status.reached", "Atteint") : ecartPct > -5 ? t("pilotage.kpi.status.atRisk", "En risque") : t("pilotage.kpi.status.notReached", "Non atteint"), statusColor: ecartPct === null ? P.muted : ecartPct >= 0 ? A.green : ecartPct > -5 ? A.orange : A.red },
                              { l: t("pilotage.kpi.pertes", "Pertes"), v: fmtEuroPilot(agg.pertes), obj: agg.umsatzIst > 0 ? t("pilotage.kpi.pctOfRevenueTemplate", { pct: tauxPertes.toFixed(1) }, `${tauxPertes.toFixed(1)}% du CA`) : "—", status: agg.pertes === 0 ? t("pilotage.kpi.status.none", "Aucune") : tauxPertes < 1.2 ? t("pilotage.kpi.status.controlled", "Maîtrisé") : tauxPertes < 1.8 ? t("pilotage.kpi.status.toWatch", "À surveiller") : t("pilotage.kpi.status.high", "Élevé"), statusColor: agg.pertes === 0 ? A.green : tauxPertes < 1.2 ? A.green : tauxPertes < 1.8 ? A.orange : A.red },
                              { l: "Stundenleistung", v: stundenleistung === null ? "—" : `${stundenleistung.toFixed(1)} €/h`, obj: t("pilotage.kpi.hoursWorkedTemplate", { hours: agg.stundenIst.toFixed(0) }, `${agg.stundenIst.toFixed(0)} h travaillées`), status: (stundenleistung === null || stundenleistungPlan == null) ? "—" : stundenleistung >= stundenleistungPlan ? t("pilotage.kpi.status.good", "Bon") : t("pilotage.kpi.status.low", "Faible"), statusColor: (stundenleistung === null || stundenleistungPlan == null) ? P.muted : stundenleistung >= stundenleistungPlan ? A.green : A.orange },
                              { l: t("pilotage.kpi.objectifsAtteints", "Objectifs atteints"), v: rows.filter((r) => r.s.umsatzPlan > 0).length ? `${rows.filter((r) => r.s.umsatzPlan > 0).length - nonAtteint.length}/${rows.filter((r) => r.s.umsatzPlan > 0).length}` : "—", obj: t("pilotage.kpi.storesUnit", "filiales"), status: rows.length === 0 ? "—" : nonAtteint.length === 0 ? t("pilotage.kpi.status.all", "Toutes") : t("pilotage.kpi.status.partial", "Partiel"), statusColor: nonAtteint.length === 0 ? A.green : A.orange },
                            ].map((k) => (
                              <div key={k.l} style={{ ...card, borderRadius: 18, padding: 13 }}>
                                <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".03em" }}>{k.l}</span>
                                <strong style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`, display: "block", margin: "3px 0 1px", letterSpacing: "-.02em" }}>{k.v}</strong>
                                <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>{k.obj}</span>
                                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 7 }}>
                                  <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: k.statusColor, borderRadius: 99, padding: "3px 8px" }}>{k.status}</span>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* AZL/Bestellung : plus de tuile chacun + section vide chacun (retour
                              utilisateur explicite "meilleur artefact pour qu'on l'implémente mieux",
                              maquette ci-dessus) — une seule ligne discrète, honnête sur l'absence de
                              donnée réseau réelle sans peser sur le défilement. */}
                          <div style={{ display: "flex", alignItems: "center", gap: 9, background: P.soft, borderRadius: 13, padding: "9px 12px", marginBottom: 18, fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 600 }}>
                            <span>🔧</span>
                            <span>{t("pilotage.analyse.comingSoonNote", "AZL & Bestellung — pas encore de vraie donnée réseau, arrivent avec ces modules")}</span>
                          </div>
                          </>
                        )}

                        {analyseTab === "evolution" && (
                          <>
                          {/* ===== Stepper ‹ › — décale toute la fenêtre glissante de PILOT_HISTORY_WEEKS
                               semaines (Phase 3, 2026-08-15), distinct du stepper Résumé/Filiales qui
                               décale une PÉRIODE discrète. Un clic = 1 semaine, comme demandé
                               ("semaine précédente ou semaines précédentes"). */}
                          {(() => {
                            const refDate = new Date(); refDate.setDate(refDate.getDate() + evolutionWeekOffset * 7);
                            const firstW = isoWeek(new Date(refDate.getTime() - (PILOT_HISTORY_WEEKS - 1) * 7 * 86400000));
                            const lastW = isoWeek(refDate);
                            return (
                              <>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 4 }}>
                                  <Pressable onClick={() => setEvolutionWeekOffset((o) => o - 1)} aria-label={t("pilotage.periodStepper.prevAria", "Période précédente")} style={{
                                    width: 30, height: 30, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink, flexShrink: 0,
                                  }}>‹</Pressable>
                                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, minWidth: 150, textAlign: "center" }}>{t("pilotage.evolutionStepper.rangeTemplate", { first: firstW, last: lastW, year: refDate.getFullYear() }, `KW${firstW}–KW${lastW} · ${refDate.getFullYear()}`)}</span>
                                  <Pressable onClick={() => evolutionWeekOffset < 0 && setEvolutionWeekOffset((o) => o + 1)} aria-label={t("pilotage.periodStepper.nextAria", "Période suivante")} style={{
                                    width: 30, height: 30, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, fontWeight: 800, color: evolutionWeekOffset < 0 ? P.ink : P.muted, opacity: evolutionWeekOffset < 0 ? 1 : 0.35, flexShrink: 0,
                                  }}>›</Pressable>
                                </div>
                                {evolutionWeekOffset !== 0 && (
                                  <div style={{ textAlign: "center", marginBottom: 12 }}>
                                    <Pressable onClick={() => setEvolutionWeekOffset(0)} style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.blue, padding: "4px 10px", borderRadius: 99, display: "inline-block" }}>{t("pilotage.periodStepper.backToToday", "↺ Revenir à aujourd'hui")}</Pressable>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                          {/* ===== VUE D'ENSEMBLE — graphe combiné (demande utilisatrice, 2026-08-15) =====
                               Umsatz (barres, axe € gauche) + Objectif (ligne pointillée, même axe) +
                               Stundenleistung (ligne + bande min/max, axe €/h droit) + seuil d'alerte.
                               Réutilise histSums/histPlanSums/stundAvgPerWeek/stundMinPerWeek/
                               stundMaxPerWeek/agg/ecartPct/stundenleistung/tauxPertes déjà calculés
                               plus haut dans cette IIFE — AUCUNE nouvelle donnée chargée pour cette
                               carte. Le taux de pertes ne rentre pas dans ce graphe (mauvaise échelle
                               vs Umsatz, ~30x d'écart) — affiché en % ET en € dans la légende plutôt
                               que comme 3e série tracée (maquette
                               https://claude.ai/code/artifact/a6610aad-ce6e-4215-893b-9c7b05816d62). */}
                          <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 2px 10px" }}>
                            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em", color: P.muted, whiteSpace: "nowrap" }}>{t("pilotage.analyse.overview.label", "👁 Vue d'ensemble")}</span>
                            <span style={{ flex: 1, height: 1, background: P.line }} />
                          </div>
                          {hasHistory && hasStundHistory && (() => {
                            const w = 320, h = 190, padL = 40, padR = 36, padT = 12, padB = 22;
                            const chartW = w - padL - padR, chartH = h - padT - padB;
                            const maxU = Math.max(...histSums, ...histPlanSums, 1) * 1.1;
                            const minU = 0;
                            const definedStund = stundAvgPerWeek.filter((v) => v !== null);
                            const definedStundPlan = stundPlanSumsPerWeek.filter((v) => v !== null);
                            // STUNDENLEISTUNG_ALERT_THRESHOLD ne sert plus qu'à garder un axe Y
                            // sain quand AUCUNE semaine n'a de plan d'heures connu (jamais comme
                            // repère affiché/comparé — voir stundPlanSumsPerWeek).
                            const maxS = Math.max(...definedStund, ...definedStundPlan, STUNDENLEISTUNG_ALERT_THRESHOLD, 1) * 1.08;
                            const minS = Math.min(...definedStund, ...definedStundPlan, STUNDENLEISTUNG_ALERT_THRESHOLD) * 0.92;
                            const bw = (chartW / PILOT_HISTORY_WEEKS) * 0.44;
                            const xForCat = (i) => padL + (i + 0.5) * (chartW / PILOT_HISTORY_WEEKS);
                            const yForU = (v) => padT + (1 - (v - minU) / (maxU - minU || 1)) * chartH;
                            const yForS = (v) => padT + (1 - (v - minS) / (maxS - minS || 1)) * chartH;

                            const objPts = histPlanSums.map((v, i) => (v > 0 ? `${xForCat(i).toFixed(1)},${yForU(v).toFixed(1)}` : null)).filter(Boolean).join(" ");
                            const stundPlanPts = stundPlanSumsPerWeek.map((v, i) => (v !== null ? `${xForCat(i).toFixed(1)},${yForS(v).toFixed(1)}` : null)).filter(Boolean).join(" ");
                            const bandTop = [], bandBot = [];
                            stundMaxPerWeek.forEach((v, i) => { if (v !== null) bandTop.push(`${xForCat(i).toFixed(1)},${yForS(v).toFixed(1)}`); });
                            for (let i = PILOT_HISTORY_WEEKS - 1; i >= 0; i--) { if (stundMinPerWeek[i] !== null) bandBot.push(`${xForCat(i).toFixed(1)},${yForS(stundMinPerWeek[i]).toFixed(1)}`); }
                            const bandD = bandTop.length && bandBot.length ? `M${bandTop.join(" L")} L${bandBot.join(" L")} Z` : null;
                            const linePts = stundAvgPerWeek.map((v, i) => (v !== null ? `${xForCat(i).toFixed(1)},${yForS(v).toFixed(1)}` : null)).filter(Boolean).join(" ");
                            const gridFracs = [0, 0.25, 0.5, 0.75, 1];
                            // Survol tactile (2026-09-01, retour utilisateur explicite : "impossible
                            // d'interagir avec le doigt avec ce diagramme") — même mécanique que
                            // updateAggHover (graphe Détail juste en dessous), état dédié
                            // (pilotOverviewHoverIdx) pour ne jamais coupler les deux graphes.
                            const updateOverviewHover = (e) => {
                              const box = e.currentTarget.getBoundingClientRect();
                              const frac = box.width > 0 ? Math.max(0, Math.min(1, (e.clientX - box.left) / box.width)) : 0;
                              setPilotOverviewHoverIdx(Math.max(0, Math.min(PILOT_HISTORY_WEEKS - 1, Math.round(frac * (PILOT_HISTORY_WEEKS - 1)))));
                            };

                            // maxWidth plafonné sur desktop (retour utilisateur explicite 2026-09-24 :
                            // "les diagrammes... sur pc ils sont très grands et pas vraiment
                            // présentables") — ces cartes graphe (viewBox interne fixe, ex. 320×190,
                            // voir plus haut) n'avaient jamais de cap : width:"100%" les étire à toute
                            // la largeur du shell desktop (maxWidth:1100, voir ligne ~20493), donc le
                            // viewBox se met à l'échelle x3+ et tout grossit disproportionnellement
                            // (traits, points, texte) — jamais un problème sur mobile (shell à 520px,
                            // proche de l'échelle native du viewBox). Appliqué identiquement aux 6
                            // cartes de l'onglet Analyse (Vue d'ensemble, détail, Pertes, jauge,
                            // Stundenleistung, Travail et productivité) plutôt qu'une seule.
                            return (
                              <section style={{ ...card, borderRadius: 20, padding: 16, marginBottom: 18, maxWidth: isWide ? 640 : "none", marginLeft: isWide ? "auto" : undefined, marginRight: isWide ? "auto" : undefined }}>
                                <div style={{ display: "flex", gap: 16, marginBottom: 14, flexWrap: "wrap" }}>
                                  <div>
                                    <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}><span style={{ width: 9, height: 9, borderRadius: 3, background: A.frost, flexShrink: 0 }} />{t("pilotage.kpi.umsatz", "Umsatz")}</span>
                                    <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, display: "block", color: A.frost, fontVariantNumeric: "tabular-nums" }}>{fmtEuroPilot(agg.umsatzIst)}</strong>
                                    {agg.umsatzPlan > 0 && <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("pilotage.kpi.objTemplate", { amount: fmtEuroPilot(agg.umsatzPlan) }, `Obj. ${fmtEuroPilot(agg.umsatzPlan)}`)} · <span style={{ color: ecartPct >= 0 ? A.green : A.red }}>{ecartPct >= 0 ? "+" : ""}{ecartPct}%</span></span>}
                                  </div>
                                  <div>
                                    <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}><span style={{ width: 12, height: 2.5, borderRadius: 2, background: A.green, flexShrink: 0 }} />Stundenleistung</span>
                                    <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, display: "block", color: A.green, fontVariantNumeric: "tabular-nums" }}>{stundenleistung === null ? "—" : `${stundenleistung.toFixed(0)} €/h`}</strong>
                                    {stundenleistungPlan != null ? (
                                      <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("pilotage.kpi.objTemplate", { amount: `${stundenleistungPlan.toFixed(0)} €/h` }, `Obj. ${stundenleistungPlan.toFixed(0)} €/h`)} · <span style={{ color: stundEcartPct >= 0 ? A.green : A.red }}>{stundEcartPct >= 0 ? "+" : ""}{stundEcartPct}%</span></span>
                                    ) : (
                                      <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("pilotage.kpi.noObjectiveSet", "Objectif non saisi")}</span>
                                    )}
                                  </div>
                                  {agg.pertes > 0 && (
                                    <div>
                                      <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}><span style={{ width: 9, height: 9, borderRadius: 3, background: A.red, flexShrink: 0 }} />{t("pilotage.analyse.overview.lossRateLabel", "Taux de pertes")}</span>
                                      <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, display: "block", color: A.red }}>{tauxPertes.toFixed(1)} %</strong>
                                      <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("pilotage.analyse.overview.lossAmountTemplate", { amount: fmtEuroPilot(agg.pertes) }, `soit ${fmtEuroPilot(agg.pertes)}`)}</span>
                                    </div>
                                  )}
                                </div>
                                <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: "auto", display: "block" }}>
                                  {/* Raffraîchissement visuel (2026-10-14, retour utilisateur "plus premium
                                      et plus frais") — dégradé sur les barres Umsatz + léger halo sur la
                                      courbe Stundenleistung, jamais une refonte de la géométrie/logique de
                                      données (inchangée). A.frost (pas A.teal) pour Umsatz : validé via le
                                      script dataviz (scripts/validate_palette.js) — A.teal/A.green étaient
                                      à ΔE 13.8, sous le plancher de 15 en vision normale ; A.frost/A.green
                                      passe à ΔE 30.2. */}
                                  <defs>
                                    <linearGradient id="pilotOverviewBarGrad" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="0%" stopColor={A.frost} stopOpacity="0.95" />
                                      <stop offset="100%" stopColor={A.frost} stopOpacity="0.55" />
                                    </linearGradient>
                                    <filter id="pilotOverviewGlow" x="-40%" y="-40%" width="180%" height="180%">
                                      <feGaussianBlur stdDeviation="2.4" result="blur" />
                                      <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                                    </filter>
                                  </defs>
                                  {gridFracs.map((f) => {
                                    const y = padT + f * chartH;
                                    return (
                                      <Fragment key={f}>
                                        <line x1={padL} y1={y} x2={padL + chartW} y2={y} stroke={P.line} strokeWidth="1" strokeDasharray={f === 1 ? "0" : "2,3"} />
                                        <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="8.5" fontWeight="700" fill={P.muted}>{Math.round((maxU - f * (maxU - minU)) / 1000)}k</text>
                                        <text x={padL + chartW + 6} y={y + 3} textAnchor="start" fontSize="8.5" fontWeight="700" fill={P.muted}>{Math.round(maxS - f * (maxS - minS))}</text>
                                      </Fragment>
                                    );
                                  })}
                                  {bandD && <path d={bandD} fill={A.green} opacity={0.14} stroke="none" />}
                                  {histSums.map((v, i) => {
                                    if (v <= 0) return null;
                                    const x = xForCat(i) - bw / 2, y = yForU(v);
                                    const above = !(histPlanSums[i] > 0) || v >= histPlanSums[i];
                                    return <rect key={i} x={x} y={y} width={bw} height={Math.max(0, padT + chartH - y)} rx={5} fill={above ? "url(#pilotOverviewBarGrad)" : A.red} opacity={0.85} />;
                                  })}
                                  <polyline points={objPts} fill="none" stroke={P.muted} strokeWidth="1.4" strokeDasharray="4,3" />
                                  <polyline points={stundPlanPts} fill="none" stroke={A.green} strokeWidth="1.4" strokeDasharray="4,3" opacity={0.7} />
                                  <polyline points={linePts} fill="none" stroke={A.green} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" filter="url(#pilotOverviewGlow)" />
                                  {stundAvgPerWeek.map((v, i) => v !== null && <circle key={i} cx={xForCat(i)} cy={yForS(v)} r={3} fill={A.green} stroke={P.card} strokeWidth="1.4" />)}
                                  {Array.from({ length: PILOT_HISTORY_WEEKS }, (_, i) => (i % 3 === 0 || i === PILOT_HISTORY_WEEKS - 1) && (
                                    <text key={i} x={xForCat(i)} y={h - 6} textAnchor="middle" fontSize="8.5" fontWeight="700" fill={P.muted}>KW{weekNumFor(i)}</text>
                                  ))}
                                  <rect
                                    x={padL} y={padT} width={Math.max(0, chartW)} height={Math.max(0, chartH)}
                                    fill="transparent" pointerEvents="all" style={{ touchAction: "none", cursor: "pointer" }}
                                    onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); updateOverviewHover(e); }}
                                    onPointerMove={(e) => { if (e.buttons) updateOverviewHover(e); }}
                                    onPointerUp={() => setPilotOverviewHoverIdx(null)}
                                    onPointerCancel={() => setPilotOverviewHoverIdx(null)}
                                    onPointerLeave={(e) => { if (e.pointerType !== "touch") setPilotOverviewHoverIdx(null); }}
                                  />
                                  {pilotOverviewHoverIdx != null && (histSums[pilotOverviewHoverIdx] > 0 || stundAvgPerWeek[pilotOverviewHoverIdx] !== null) && (() => {
                                    const hi = pilotOverviewHoverIdx;
                                    const hu = histSums[hi], hs = stundAvgPerWeek[hi];
                                    const bw = 108, bh = 46;
                                    const hx = xForCat(hi);
                                    const bx = Math.min(Math.max(hx - bw / 2, padL), padL + chartW - bw);
                                    return (
                                      <g>
                                        <line x1={hx} y1={padT} x2={hx} y2={padT + chartH} stroke={P.ink} strokeWidth="1" strokeDasharray="3 2" opacity={0.4} />
                                        {hu > 0 && <circle cx={hx} cy={yForU(hu)} r="5" fill="none" stroke={A.frost} strokeWidth="1.75" opacity={0.9} />}
                                        {hs !== null && <circle cx={hx} cy={yForS(hs)} r="5" fill="none" stroke={A.green} strokeWidth="1.75" opacity={0.9} />}
                                        <g transform={`translate(${bx},2)`}>
                                          <rect width={bw} height={bh} rx={8} fill={P.card} stroke={P.line} strokeWidth="1" />
                                          <text x={8} y={14} fontSize="8.5" fontWeight="800" fill={P.muted}>KW{weekNumFor(hi)}</text>
                                          <text x={8} y={26} fontSize="9.5" fontWeight="800" fill={A.frost}>{hu > 0 ? fmtEuroChart(hu) : "—"}</text>
                                          <text x={8} y={38} fontSize="9.5" fontWeight="800" fill={A.green}>{hs !== null ? `${hs.toFixed(0)} €/h` : "—"}</text>
                                        </g>
                                      </g>
                                    );
                                  })()}
                                </svg>
                                <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                                  <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}><span style={{ width: 9, height: 9, borderRadius: 3, background: A.frost, flexShrink: 0 }} />{t("pilotage.kpi.umsatz", "Umsatz")}</span>
                                  <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}><span style={{ width: 12, height: 0, borderTop: `1.6px dashed ${P.muted}`, flexShrink: 0 }} />{t("pilotage.analyse.chart.objectiveLabel", "Objectif")}</span>
                                  <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}><span style={{ width: 12, height: 2.5, borderRadius: 2, background: A.green, flexShrink: 0 }} />Stundenleistung</span>
                                  <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}><span style={{ width: 12, height: 0, borderTop: `1.4px dashed ${A.green}`, flexShrink: 0, opacity: 0.7 }} />{t("pilotage.analyse.chart.objectiveLabel", "Objectif")}</span>
                                  <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}><span style={{ width: 9, height: 9, borderRadius: 3, background: "rgba(30,184,95,.14)", border: `1px solid ${A.green}`, flexShrink: 0 }} />{t("pilotage.analyse.stundEvolution.legendSpread", "Écart filiales")}</span>
                                </div>
                              </section>
                            );
                          })()}
                          <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 2px 10px" }}>
                            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em", color: P.muted, whiteSpace: "nowrap" }}>{t("pilotage.analyse.detail.label", "🔍 Détail")}</span>
                            <span style={{ flex: 1, height: 1, background: P.line }} />
                          </div>
                          <section style={{ ...card, borderRadius: 20, padding: 16, marginBottom: 18, maxWidth: isWide ? 640 : "none", marginLeft: isWide ? "auto" : undefined, marginRight: isWide ? "auto" : undefined }}>
                            {pilotDashFiliale === "all" ? (
                              <>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, gap: 8 }}>
                                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{t("pilotage.analyse.chart.titleTemplate", { n: PILOT_HISTORY_WEEKS }, `CA réel vs objectif — ${PILOT_HISTORY_WEEKS} dernières semaines`)}</strong>
                                  {trendPct != null && (
                                    <span style={{
                                      fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "3px 9px", whiteSpace: "nowrap",
                                      background: trendPct >= 0 ? "rgba(30,184,95,.14)" : "rgba(239,68,68,.14)", color: trendPct >= 0 ? "#15803d" : A.red,
                                    }}>{trendPct >= 0 ? "↗" : "↘"} {Math.abs(trendPct)}% {t("pilotage.analyse.chart.sinceWeeksTemplate", { n: PILOT_HISTORY_WEEKS }, `sur ${PILOT_HISTORY_WEEKS} sem.`)}</span>
                                  )}
                                </div>
                                {!hasHistory ? (
                                  <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.analyse.chart.noDataText", "Pas encore de saisie Reporting hebdomadaire sur cette période pour tracer une courbe réelle.")}</p>
                                ) : (() => {
                                  // Style B (mockup) re-scopé à PILOT_HISTORY_WEEKS points hebdo —
                                  // bandes d'écart réel vs objectif RÉEL de CHAQUE semaine
                                  // (histPlanSums, jamais agg.umsatzPlan redécoupé, voir plus haut).
                                  // ✓/✕ sur toute semaine déjà terminée (jamais la semaine en cours,
                                  // i===lastDataIdx).
                                  const chartW = 320, chartH = 172, padX = 20, padTop = 30, padBottom = 34;
                                  const maxV = Math.max(...histSums, ...histPlanSums, 1) * 1.14;
                                  const minV = Math.min(...histSums.filter((v) => v > 0), ...histPlanSums.filter((v) => v > 0), Infinity) * 0.9;
                                  const minVSafe = Number.isFinite(minV) ? minV : 0;
                                  const xFor = (i) => padX + (i / (PILOT_HISTORY_WEEKS - 1)) * (chartW - padX * 2);
                                  const yFor = (v) => chartH - padBottom - ((v - minVSafe) / (maxV - minVSafe || 1)) * (chartH - padTop - padBottom);
                                  const linePts = histSums.map((v, i) => (v > 0 ? `${xFor(i)},${yFor(v)}` : null)).filter(Boolean).join(" ");
                                  const gapBands = (() => {
                                    const bands = []; let i = 0;
                                    while (i < histSums.length) {
                                      if (!(histSums[i] > 0) || !(histPlanSums[i] > 0)) { i++; continue; }
                                      const sign = histSums[i] >= histPlanSums[i] ? 1 : -1;
                                      let j = i;
                                      while (j + 1 < histSums.length && histSums[j + 1] > 0 && histPlanSums[j + 1] > 0 && (histSums[j + 1] >= histPlanSums[j + 1] ? 1 : -1) === sign) j++;
                                      const top = [], bot = [];
                                      for (let k = i; k <= j; k++) { top.push(`${xFor(k)},${yFor(histSums[k])}`); bot.push(`${xFor(k)},${yFor(histPlanSums[k])}`); }
                                      bands.push({ sign, d: `M${top.join(" L")} L${bot.reverse().join(" L")} Z` });
                                      i = j + 1;
                                    }
                                    return bands;
                                  })();
                                  // Lecture au doigt/souris — glisser sur le graphique fait défiler la
                                  // semaine survolée (aimantée au point le plus proche, PILOT_HISTORY_WEEKS points fixes).
                                  const updateAggHover = (e) => {
                                    const box = e.currentTarget.getBoundingClientRect();
                                    const frac = box.width > 0 ? Math.max(0, Math.min(1, (e.clientX - box.left) / box.width)) : 0;
                                    setPilotAggHoverIdx(Math.max(0, Math.min(PILOT_HISTORY_WEEKS - 1, Math.round(frac * (PILOT_HISTORY_WEEKS - 1)))));
                                  };
                                  return (
                                    <>
                                      <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: "100%", height: "auto", display: "block" }}>
                                        <defs>
                                          <filter id="pilotDetailGlow" x="-40%" y="-40%" width="180%" height="180%">
                                            <feGaussianBlur stdDeviation="2.2" result="blur" />
                                            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                                          </filter>
                                        </defs>
                                        {gapBands.map((b, i) => <path key={i} d={b.d} fill={b.sign >= 0 ? A.green : A.red} opacity={0.22} stroke="none" />)}
                                        <polyline points={linePts} fill="none" stroke={themeAccent.week.c} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" filter="url(#pilotDetailGlow)" />
                                        {histSums.map((v, i) => {
                                          if (v <= 0) return null;
                                          const isLast = i === lastDataIdx;
                                          // À 6 semaines chaque point avait sa propre étiquette (assez de
                                          // place) — à PILOT_HISTORY_WEEKS=12 points, un label sur
                                          // chacun se chevauche. On n'étiquette qu'un point sur
                                          // labelStride (+ toujours le dernier) ; la valeur exacte de
                                          // n'importe quel point reste disponible via le survol/glisser
                                          // (tooltip ci-dessous), rien n'est perdu, juste moins dense.
                                          const labelStride = Math.max(1, Math.ceil(PILOT_HISTORY_WEEKS / 6));
                                          const showLabel = isLast || i % labelStride === 0;
                                          const hasObj = histPlanSums[i] > 0;
                                          const belowGoal = hasObj && v < histPlanSums[i];
                                          const dotColor = isLast ? themeAccent.week.c : (hasObj ? (belowGoal ? A.red : A.green) : P.muted);
                                          return (
                                            <Fragment key={i}>
                                              <circle cx={xFor(i)} cy={yFor(v)} r={isLast ? 5.5 : 3.2} fill={dotColor} stroke={P.card} strokeWidth={isLast ? 2 : 1.2} />
                                              {showLabel && (
                                                <>
                                                  <text x={xFor(i)} y={yFor(v) - (isLast ? 14 : 9)} textAnchor="middle" fontSize={isLast ? 10 : 8.5} fontWeight="800" fill={isLast ? themeAccent.week.ink : P.muted}>{fmtK(v)}</text>
                                                  {!isLast && hasObj && (
                                                    <text x={xFor(i)} y={yFor(v) + 15} textAnchor="middle" fontSize="9.5" fontWeight="900" fill={belowGoal ? A.red : A.green}>{belowGoal ? "✕" : "✓"}</text>
                                                  )}
                                                  <text x={xFor(i)} y={chartH - 8} textAnchor="middle" fontSize="8.5" fontWeight="700" fill={P.muted}>KW{weekNumFor(i)}</text>
                                                </>
                                              )}
                                            </Fragment>
                                          );
                                        })}
                                        <rect
                                          x={padX} y={padTop} width={Math.max(0, chartW - padX * 2)} height={Math.max(0, chartH - padTop - padBottom)}
                                          fill="transparent" pointerEvents="all" style={{ touchAction: "none", cursor: "pointer" }}
                                          onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); updateAggHover(e); }}
                                          onPointerMove={(e) => { if (e.buttons) updateAggHover(e); }}
                                          onPointerUp={() => setPilotAggHoverIdx(null)}
                                          onPointerCancel={() => setPilotAggHoverIdx(null)}
                                          onPointerLeave={(e) => { if (e.pointerType !== "touch") setPilotAggHoverIdx(null); }}
                                        />
                                        {pilotAggHoverIdx != null && histSums[pilotAggHoverIdx] > 0 && (() => {
                                          const hi = pilotAggHoverIdx;
                                          const hv = histSums[hi], hobj = histPlanSums[hi];
                                          const hEcart = hobj > 0 ? Math.round(((hv - hobj) / hobj) * 100) : null;
                                          const bw = 98, bh = hobj > 0 ? 46 : 30;
                                          const bx = Math.min(Math.max(xFor(hi) - bw / 2, padX), chartW - padX - bw);
                                          return (
                                            <g>
                                              <circle cx={xFor(hi)} cy={yFor(hv)} r="8.5" fill="none" stroke={themeAccent.week.c} strokeWidth="1.75" opacity={0.85} />
                                              <g transform={`translate(${bx},2)`}>
                                                <rect width={bw} height={bh} rx={8} fill={P.card} stroke={P.line} strokeWidth="1" />
                                                <text x={8} y={14} fontSize="8.5" fontWeight="800" fill={P.muted}>KW{weekNumFor(hi)}</text>
                                                <text x={8} y={26} fontSize="9.5" fontWeight="800" fill={themeAccent.week.ink}>{fmtEuroChart(hv)}</text>
                                                {hobj > 0 && (
                                                  <text x={8} y={38} fontSize="8.5" fontWeight="700" fill={hEcart >= 0 ? A.green : A.red}>{hEcart >= 0 ? "+" : ""}{hEcart}% {t("pilotage.rhythm.hoverVsObjective", "vs obj.")}</text>
                                                )}
                                              </g>
                                            </g>
                                          );
                                        })()}
                                      </svg>
                                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
                                        {[
                                          [themeAccent.week.c, t("pilotage.analyse.chart.legendWeekReal", "Semaine — réel")],
                                          [A.green, t("pilotage.analyse.chart.legendAhead", "En avance")],
                                          [A.red, t("pilotage.analyse.chart.legendBehind", "En retard")],
                                        ].map(([c, label]) => (
                                          <span key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                                            <span style={{ width: 8, height: 8, borderRadius: 99, background: c, flex: "0 0 auto" }} />
                                            {label}
                                          </span>
                                        ))}
                                      </div>
                                    </>
                                  );
                                })()}
                              </>
                            ) : (
                              <>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 8 }}>
                                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{t("pilotage.rhythm.title", "Rythme Umsatz")}</strong>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <Pressable feedback onClick={() => setPilotRhythmMonthOffset((o) => o - 1)} aria-label={t("pilotage.rhythm.monthPrevAria", "Mois précédent")} style={{
                                      width: 28, height: 28, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink,
                                    }}>‹</Pressable>
                                    <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, textTransform: "capitalize", minWidth: 84, textAlign: "center" }}>
                                      {rhythmSingleStore ? rhythmSingleStore.monthLabel : "—"}
                                    </span>
                                    <Pressable feedback onClick={() => pilotRhythmMonthOffset < 0 && setPilotRhythmMonthOffset((o) => o + 1)} disabled={pilotRhythmMonthOffset >= 0} aria-label={t("pilotage.rhythm.monthNextAria", "Mois suivant")} style={{
                                      width: 28, height: 28, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink, opacity: pilotRhythmMonthOffset >= 0 ? .4 : 1,
                                    }}>›</Pressable>
                                  </div>
                                </div>
                                {!rhythmSingleStore || !(rhythmSingleStore.monthObjective > 0) ? (
                                  <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.rhythm.noDataText", "Pas encore de Plan Stundenleistung importé pour ce mois — impossible de comparer au rythme réel.")}</p>
                                ) : (() => {
                                  const rs = rhythmSingleStore;
                                  const rW = 340, rH = 232, padL = 40, padR = 8, padTop = 40, padBottom = 24;
                                  const maxRV = Math.max(rs.monthObjective, ...rs.monthActual.filter((v) => v != null), 1) * 1.1;
                                  const xFor = (day) => padL + ((day - 1) / Math.max(1, rs.daysInMonth - 1)) * (rW - padL - padR);
                                  const yFor = (v) => rH - padBottom - (v / maxRV) * (rH - padTop - padBottom);
                                  const pathFor = (arr) => {
                                    const pts = [];
                                    arr.forEach((v, idx) => { if (v != null) pts.push(`${xFor(idx + 1).toFixed(1)},${yFor(v).toFixed(1)}`); });
                                    return pts.length ? `M${pts.join(" L")}` : "";
                                  };
                                  const gapBands = (actual, pace) => {
                                    const bands = []; let i = 0;
                                    while (i < actual.length) {
                                      if (actual[i] == null) { i++; continue; }
                                      const sign = actual[i] >= pace[i] ? 1 : -1;
                                      let j = i;
                                      while (j + 1 < actual.length && actual[j + 1] != null && (actual[j + 1] >= pace[j + 1] ? 1 : -1) === sign) j++;
                                      const top = [], bot = [];
                                      for (let k = i; k <= j; k++) { top.push(`${xFor(k + 1).toFixed(1)},${yFor(actual[k]).toFixed(1)}`); bot.push(`${xFor(k + 1).toFixed(1)},${yFor(pace[k]).toFixed(1)}`); }
                                      bands.push({ sign, d: `M${top.join(" L")} L${bot.reverse().join(" L")} Z` });
                                      i = j + 1;
                                    }
                                    return bands;
                                  };
                                  const monthBands = gapBands(rs.monthActual, rs.monthPace);
                                  const weekBands = gapBands(rs.weekActual, rs.weekPace);
                                  const monthHasToday = rs.todayDay && rs.monthActual[rs.todayDay - 1] != null;
                                  const weekHasToday = rs.todayDay && rs.weekActual[rs.todayDay - 1] != null;
                                  const labelRight = rs.todayDay ? xFor(rs.todayDay) < rW - 90 : true;
                                  const uniqDayTicks = [1, ...rs.mondays].filter((v, idx, arr) => arr.indexOf(v) === idx);
                                  // Lecture au doigt/souris — glisser sur le graphique fait défiler le
                                  // jour survolé (aimanté au jour le plus proche du mois affiché).
                                  const updateDayHover = (e) => {
                                    const box = e.currentTarget.getBoundingClientRect();
                                    const frac = box.width > 0 ? Math.max(0, Math.min(1, (e.clientX - box.left) / box.width)) : 0;
                                    setPilotChartHoverDay(Math.max(1, Math.min(rs.daysInMonth, Math.round(1 + frac * (rs.daysInMonth - 1)))));
                                  };
                                  const hoverDay = pilotChartHoverDay;

                                  return (
                                    <>
                                      <svg viewBox={`0 0 ${rW} ${rH}`} style={{ width: "100%", height: "auto", display: "block" }}>
                                        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
                                          const y = padTop + f * (rH - padTop - padBottom);
                                          return <line key={f} x1={padL} y1={y} x2={rW - padR} y2={y} stroke={P.line} strokeWidth="1" />;
                                        })}
                                        {rs.mondays.map((d) => (
                                          <line key={d} x1={xFor(d)} y1={padTop} x2={xFor(d)} y2={rH - padBottom} stroke={P.line} strokeWidth="1" strokeDasharray="2 3" />
                                        ))}
                                        {uniqDayTicks.map((d) => (
                                          <text key={d} x={xFor(d)} y={rH - 8} textAnchor="middle" fontSize="8.5" fontWeight="700" fill={P.muted}>{d}</text>
                                        ))}
                                        {monthBands.map((b, i) => <path key={`m${i}`} d={b.d} fill={b.sign >= 0 ? A.green : A.red} opacity={0.20} stroke="none" />)}
                                        {weekBands.map((b, i) => <path key={`w${i}`} d={b.d} fill={b.sign >= 0 ? A.green : A.red} opacity={0.30} stroke="none" />)}
                                        <defs>
                                          <filter id="pilotRhythmGlow" x="-40%" y="-40%" width="180%" height="180%">
                                            <feGaussianBlur stdDeviation="2.2" result="blur" />
                                            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                                          </filter>
                                        </defs>
                                        <path d={pathFor(rs.monthActual)} fill="none" stroke={themeAccent.month.c} strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" filter="url(#pilotRhythmGlow)" />
                                        <path d={pathFor(rs.weekActual)} fill="none" stroke={themeAccent.week.c} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" filter="url(#pilotRhythmGlow)" />
                                        {/* Objectifs écrits sur la courbe elle-même — jamais seulement dans les cartes KPI en dessous (retour utilisateur explicite). */}
                                        <circle cx={xFor(rs.daysInMonth)} cy={yFor(rs.monthObjective)} r="3" fill="none" stroke={themeAccent.month.c} strokeWidth="1.5" />
                                        <text x={xFor(rs.daysInMonth) - 6} y={yFor(rs.monthObjective) - 6} textAnchor="end" fontSize="9.5" fontWeight="800" fill={themeAccent.month.ink}>{t("pilotage.rhythm.monthObjectivePrefix", "Obj. mois")} {fmtEuroChart(rs.monthObjective)}</text>
                                        <circle cx={xFor(rs.weekEndDay)} cy={yFor(rs.weekObjective)} r="3" fill="none" stroke={themeAccent.week.c} strokeWidth="1.5" />
                                        <text x={xFor(rs.weekEndDay) - 6} y={yFor(rs.weekObjective) - 8} textAnchor="end" fontSize="9.5" fontWeight="800" fill={themeAccent.week.ink}>{t("pilotage.rhythm.weekObjectivePrefix", "Obj. semaine")} {fmtEuroChart(rs.weekObjective)}</text>
                                        {/* Badge ✓/✕ — uniquement une semaine ENTIÈREMENT écoulée, jamais la semaine en cours. */}
                                        {rs.weekBadges.map((b) => (
                                          <Fragment key={b.day}>
                                            <circle cx={xFor(b.day)} cy={yFor(b.total)} r="7" fill={b.reached ? A.green : A.red} stroke={P.card} strokeWidth="2" />
                                            <text x={xFor(b.day)} y={yFor(b.total) + 3.2} textAnchor="middle" fontSize="9" fontWeight="900" fill="#fff">{b.reached ? "✓" : "✕"}</text>
                                            <text x={xFor(b.day)} y={yFor(b.total) - 11} textAnchor="middle" fontSize="8.5" fontWeight="800" fill={b.reached ? A.green : A.red}>{fmtEuroChart(b.total)}</text>
                                          </Fragment>
                                        ))}
                                        {rs.todayDay && (
                                          <>
                                            <line x1={xFor(rs.todayDay)} y1={padTop} x2={xFor(rs.todayDay)} y2={rH - padBottom} stroke={P.muted} strokeWidth="1" strokeDasharray="2 3" />
                                            <text x={xFor(rs.todayDay)} y={padTop - 4} textAnchor="middle" fontSize="9" fontWeight="800" fill={P.muted}>{t("pilotage.rhythm.todayLabel", "Aujourd'hui")}</text>
                                            {monthHasToday && (
                                              <>
                                                <circle cx={xFor(rs.todayDay)} cy={yFor(rs.monthActual[rs.todayDay - 1])} r="4.5" fill={themeAccent.month.c} stroke={P.card} strokeWidth="2" />
                                                <text x={labelRight ? xFor(rs.todayDay) + 9 : xFor(rs.todayDay) - 9} y={yFor(rs.monthActual[rs.todayDay - 1]) + 3.5} textAnchor={labelRight ? "start" : "end"} fontSize="10" fontWeight="800" fill={themeAccent.month.ink}>{fmtEuroChart(rs.monthActual[rs.todayDay - 1])}</text>
                                              </>
                                            )}
                                            {weekHasToday && (
                                              <>
                                                <circle cx={xFor(rs.todayDay)} cy={yFor(rs.weekActual[rs.todayDay - 1])} r="4.5" fill={themeAccent.week.c} stroke={P.card} strokeWidth="2" />
                                                <text x={labelRight ? xFor(rs.todayDay) + 9 : xFor(rs.todayDay) - 9} y={yFor(rs.weekActual[rs.todayDay - 1]) + 3.5} textAnchor={labelRight ? "start" : "end"} fontSize="10" fontWeight="800" fill={themeAccent.week.ink}>{fmtEuroChart(rs.weekActual[rs.todayDay - 1])}</text>
                                              </>
                                            )}
                                          </>
                                        )}
                                        <rect
                                          x={padL} y={padTop} width={Math.max(0, rW - padL - padR)} height={Math.max(0, rH - padTop - padBottom)}
                                          fill="transparent" pointerEvents="all" style={{ touchAction: "none", cursor: "pointer" }}
                                          onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); updateDayHover(e); }}
                                          onPointerMove={(e) => { if (e.buttons) updateDayHover(e); }}
                                          onPointerUp={() => setPilotChartHoverDay(null)}
                                          onPointerCancel={() => setPilotChartHoverDay(null)}
                                          onPointerLeave={(e) => { if (e.pointerType !== "touch") setPilotChartHoverDay(null); }}
                                        />
                                        {hoverDay != null && (() => {
                                          const mv = rs.monthActual[hoverDay - 1];
                                          const wv = rs.weekActual[hoverDay - 1];
                                          if (mv == null && wv == null) return null;
                                          const bw = 112, bh = wv != null ? 46 : 30;
                                          const bx = Math.min(Math.max(xFor(hoverDay) - bw / 2, padL), rW - padR - bw);
                                          return (
                                            <g>
                                              <line x1={xFor(hoverDay)} y1={padTop} x2={xFor(hoverDay)} y2={rH - padBottom} stroke={P.ink} strokeWidth="1.25" strokeDasharray="3 2" opacity={0.5} />
                                              {mv != null && <circle cx={xFor(hoverDay)} cy={yFor(mv)} r="4.5" fill={themeAccent.month.c} stroke={P.card} strokeWidth="1.5" />}
                                              {wv != null && <circle cx={xFor(hoverDay)} cy={yFor(wv)} r="4.5" fill={themeAccent.week.c} stroke={P.card} strokeWidth="1.5" />}
                                              <g transform={`translate(${bx},${padTop - 2})`}>
                                                <rect width={bw} height={bh} rx={8} fill={P.card} stroke={P.line} strokeWidth="1" />
                                                <text x={8} y={14} fontSize="8.5" fontWeight="800" fill={P.muted}>{t("pilotage.rhythm.hoverDayTemplate", { day: hoverDay }, `Jour ${hoverDay}`)}</text>
                                                <text x={8} y={26} fontSize="9.5" fontWeight="800" fill={themeAccent.month.ink}>{t("pilotage.rhythm.kpi.monthLabel", "Mois")}: {mv != null ? fmtEuroChart(mv) : "—"}</text>
                                                {wv != null && (
                                                  <text x={8} y={38} fontSize="9.5" fontWeight="800" fill={themeAccent.week.ink}>{t("pilotage.rhythm.hoverWeekLabel", "Semaine")}: {fmtEuroChart(wv)}</text>
                                                )}
                                              </g>
                                            </g>
                                          );
                                        })()}
                                      </svg>
                                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8, marginBottom: 14 }}>
                                        {[
                                          [themeAccent.month.c, t("pilotage.rhythm.legendMonthReal", "Mois — réel")],
                                          [themeAccent.week.c, t("pilotage.rhythm.legendWeekReal", "Semaine — réel")],
                                          [A.green, t("pilotage.rhythm.legendAhead", "En avance")],
                                          [A.red, t("pilotage.rhythm.legendBehind", "En retard")],
                                        ].map(([c, label]) => (
                                          <span key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                                            <span style={{ width: 8, height: 8, borderRadius: 99, background: c, flex: "0 0 auto" }} />
                                            {label}
                                          </span>
                                        ))}
                                      </div>
                                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                        {[
                                          { c: themeAccent.week.c, l: t("pilotage.rhythm.kpi.weekLabelTemplate", { kw: isoWeek(now) }, `Semaine KW${isoWeek(now)}`), v: rs.weekActual[rs.todayDay ? rs.todayDay - 1 : rs.weekEndDay - 1], obj: rs.weekObjective },
                                          { c: themeAccent.month.c, l: t("pilotage.rhythm.kpi.monthLabel", "Mois"), v: rs.todayDay ? rs.monthActual[rs.todayDay - 1] : rs.monthActual[rs.monthActual.length - 1], obj: rs.monthObjective },
                                        ].map((k, i) => {
                                          const reached = k.v != null && k.obj > 0 && k.v >= k.obj;
                                          return (
                                            <div key={i} style={{ ...card, borderRadius: 14, padding: 12 }}>
                                              <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "flex", alignItems: "center", gap: 5 }}>
                                                <span style={{ width: 7, height: 7, borderRadius: 99, background: k.c, flex: "0 0 auto" }} />{k.l}
                                              </span>
                                              <strong style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`, display: "block", margin: "4px 0 1px" }}>{k.v != null ? fmtEuroChart(k.v) : "—"}</strong>
                                              <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.kpi.objTemplate", { amount: fmtEuroChart(k.obj) }, `Obj. ${fmtEuroChart(k.obj)}`)}</span>
                                              {k.v != null && k.obj > 0 && (
                                                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 7 }}>
                                                  <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: reached ? A.green : A.red, borderRadius: 99, padding: "3px 8px" }}>
                                                    {reached ? t("pilotage.kpi.status.reached", "Atteint") : t("pilotage.kpi.status.notReached", "Non atteint")}
                                                  </span>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </>
                                  );
                                })()}
                              </>
                            )}
                          </section>

                          {/* ===== PERTES — ÉVOLUTION RÉSEAU (demande Verkaufsleiter, 2026-08-14) =====
                               Complète (ne remplace pas) le donut "période en cours" juste en dessous :
                               barres hebdo sur PILOT_HISTORY_WEEKS semaines, taper une semaine affiche
                               son détail par rayon (mêmes LOSS_COLORS que le donut) sans le modifier. */}
                          {hasPertesHistory && (() => {
                            const LOSS_COLORS = { obst_gemuese: A.green, schnittblumen: A.purple, frischfleisch: A.red, prebake: A.orange, frischbrot_brot: "#b98b4e", frischfisch: A.blue, kuehlung: A.frost, h_bereich: "#8a8f9c", trocken: "#c9a13b" };
                            const chartW = 320, chartH = 120, padX = 12, padTop = 22, padBottom = 22;
                            const maxV = Math.max(...pertesHistSums, 1) * 1.18;
                            const bars = PILOT_HISTORY_WEEKS;
                            const bw = (chartW - padX * 2) / bars * 0.55;
                            const slotX = (i) => padX + (i / bars) * (chartW - padX * 2) + ((chartW - padX * 2) / bars - bw) / 2;
                            const peakIdx = pertesHistSums.indexOf(Math.max(...pertesHistSums));
                            const lastNonZero = pertesHistSums.length - 1 - [...pertesHistSums].reverse().findIndex((v) => v > 0);
                            const activeIdx = pilotPertesSelectedIdx != null ? pilotPertesSelectedIdx : lastNonZero;
                            const detailCats = Object.entries(pertesHistCategorySums[activeIdx] || {})
                              .map(([id, amount]) => ({ id, amount, color: LOSS_COLORS[id] || P.muted, label: reportCategoryLabel(id) }))
                              .sort((a, b) => b.amount - a.amount);
                            const detailTotal = detailCats.reduce((a, c) => a + c.amount, 0);
                            return (
                              <section style={{ ...card, borderRadius: 20, padding: 16, marginBottom: 18, maxWidth: isWide ? 640 : "none", marginLeft: isWide ? "auto" : undefined, marginRight: isWide ? "auto" : undefined }}>
                                <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 4 }}>{t("pilotage.analyse.pertesEvolution.titleTemplate", { n: PILOT_HISTORY_WEEKS }, `🗑️ Pertes — évolution (${PILOT_HISTORY_WEEKS} sem.)`)}</strong>
                                <p style={{ margin: "0 0 10px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{t("pilotage.analyse.pertesEvolution.subtitle", "Total réseau par semaine — tape une barre pour voir le détail par rayon")}</p>
                                <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: "100%", height: "auto", display: "block" }} onPointerLeave={() => setPilotPertesHoverIdx(null)}>
                                  <defs>
                                    <linearGradient id="pilotPertesBarGrad" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="0%" stopColor={A.red} stopOpacity="0.95" />
                                      <stop offset="100%" stopColor={A.red} stopOpacity="0.6" />
                                    </linearGradient>
                                  </defs>
                                  {pertesHistSums.map((v, i) => {
                                    const barH = (v / maxV) * (chartH - padTop - padBottom);
                                    const y = chartH - padBottom - barH;
                                    const active = i === activeIdx;
                                    return (
                                      <rect key={i} x={slotX(i)} y={y} width={bw} height={Math.max(1.5, barH)} rx={4}
                                        fill={active ? "url(#pilotPertesBarGrad)" : "rgba(239,68,68,.16)"} stroke={active ? "none" : A.red} strokeWidth={active ? 0 : 1}
                                        pointerEvents="none"
                                      />
                                    );
                                  })}
                                  {/* Cible tactile réelle : toute la colonne (pas juste la barre visuelle à
                                      55% de large — bien trop étroite pour un doigt sur 12 semaines dans 320
                                      unités). Même motif onPointerDown + touchAction:"none" que les capteurs
                                      des graphes Umsatz/Stundenleistung ci-dessus, qui répondent correctement
                                      au toucher — onClick/onPointerEnter seuls, essayés d'abord, ne
                                      déclenchaient pas de façon fiable sur mobile. */}
                                  {pertesHistSums.map((v, i) => {
                                    if (v <= 0) return null;
                                    const colW = (chartW - padX * 2) / bars;
                                    const colX = padX + i * colW;
                                    return (
                                      <rect key={`hit-${i}`} x={colX} y={padTop} width={colW} height={chartH - padTop - padBottom}
                                        fill="transparent" pointerEvents="all" style={{ touchAction: "none", cursor: "pointer" }}
                                        onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); setPilotPertesHoverIdx(i); setPilotPertesSelectedIdx(i); }}
                                        onPointerEnter={() => setPilotPertesHoverIdx(i)}
                                      />
                                    );
                                  })}
                                  {peakIdx >= 0 && pertesHistSums[peakIdx] > 0 && (
                                    <text x={slotX(peakIdx) + bw / 2} y={chartH - padBottom - (pertesHistSums[peakIdx] / maxV) * (chartH - padTop - padBottom) - 6}
                                      textAnchor="middle" fontSize="8.5" fontWeight="800" fill={A.red}>{t("pilotage.analyse.pertesEvolution.peakPrefix", "Pic")} {Math.round(pertesHistSums[peakIdx]).toLocaleString("fr-FR")} €</text>
                                  )}
                                  {pilotPertesHoverIdx != null && pertesHistSums[pilotPertesHoverIdx] > 0 && pilotPertesHoverIdx !== peakIdx && (
                                    <text x={slotX(pilotPertesHoverIdx) + bw / 2} y={chartH - padBottom - (pertesHistSums[pilotPertesHoverIdx] / maxV) * (chartH - padTop - padBottom) - 6}
                                      textAnchor="middle" fontSize="8.5" fontWeight="800" fill={P.ink}>{Math.round(pertesHistSums[pilotPertesHoverIdx]).toLocaleString("fr-FR")} €</text>
                                  )}
                                </svg>
                                {detailCats.length > 0 && (
                                  <>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "10px 0 6px" }}>
                                      <strong style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.ink }}>{t("pilotage.analyse.pertesEvolution.detailTitle", "Détail par rayon")}</strong>
                                      <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{activeIdx === pertesHistSums.length - 1 ? t("pilotage.analyse.pertesEvolution.mostRecentWeek", "Semaine la plus récente") : `KW${weekNumFor(activeIdx)}`}</span>
                                    </div>
                                    {detailCats.map((c) => (
                                      <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                        <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, width: 92, flex: "0 0 auto", color: P.muted, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.label}</span>
                                        <div style={{ flex: 1, height: 9, borderRadius: 5, background: P.soft, overflow: "hidden" }}>
                                          <div style={{ width: `${detailTotal > 0 ? (c.amount / detailTotal) * 100 : 0}%`, height: "100%", borderRadius: 5, background: c.color }} />
                                        </div>
                                        <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, width: 46, textAlign: "right", flex: "0 0 auto" }}>{Math.round(c.amount)} €</span>
                                      </div>
                                    ))}
                                  </>
                                )}
                              </section>
                            );
                          })()}
                          </>
                        )}

                        {analyseTab === "filiales" && (
                          <>
                          {/* ===== ANALYSE DES PERTES — vraie répartition par catégorie (Reporting manual_waste) =====
                               Fidèle à nexora-app_1.zip (source lignes ~5464-5559) pour la partie catégories, qui a
                               une vraie donnée derrière (REPORT_CATEGORIES). La répartition "par cause" du fichier
                               source n'a aucune donnée réelle équivalente dans StoreFlowProV2 aujourd'hui (aucun
                               champ "cause" n'est jamais saisi nulle part) — honnêtement omise plutôt qu'estimée. */}
                          {agg.pertes > 0 && (() => {
                            const LOSS_COLORS = { obst_gemuese: A.green, schnittblumen: A.purple, frischfleisch: A.red, prebake: A.orange, frischbrot_brot: "#b98b4e", frischfisch: A.blue, kuehlung: A.frost, h_bereich: "#8a8f9c", trocken: "#c9a13b" };
                            const catTotals = {};
                            rows.forEach((r) => Object.entries(r.s.pertesByCategory || {}).forEach(([catId, v]) => { catTotals[catId] = (catTotals[catId] || 0) + v; }));
                            const aggCategories = Object.entries(catTotals).map(([id, amount]) => ({
                              id, amount, color: LOSS_COLORS[id] || P.muted,
                              label: reportCategoryLabel(id),
                            })).sort((a, b) => b.amount - a.amount);
                            if (aggCategories.length === 0) return null;
                            const size = 96, stroke = 16, r2 = (size - stroke) / 2, cx = size / 2, cy = size / 2, circ = 2 * Math.PI * r2;
                            let offsetAcc = 0;
                            return (
                              <section style={{ ...card, borderRadius: 20, padding: 16, marginBottom: 18, maxWidth: isWide ? 640 : "none", marginLeft: isWide ? "auto" : undefined, marginRight: isWide ? "auto" : undefined }}>
                                <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 4 }}>{t("pilotage.analyse.losses.title", "Analyse des pertes")}</strong>
                                <p style={{ margin: "0 0 14px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.analyse.losses.totalTemplate", { amount: agg.pertes.toFixed(0), pctSuffix: agg.umsatzIst > 0 ? ` (${tauxPertes.toFixed(1)}% du CA)` : "" }, `${agg.pertes.toFixed(0)} € au total${agg.umsatzIst > 0 ? ` (${tauxPertes.toFixed(1)}% du CA)` : ""} — répartition par catégorie`)}</p>
                                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8 }}>
                                  <svg width={size} height={size} style={{ flex: "0 0 auto", transform: "rotate(-90deg)" }}>
                                    <circle cx={cx} cy={cy} r={r2} fill="none" stroke={P.soft} strokeWidth={stroke} />
                                    {aggCategories.map((c) => {
                                      const frac = c.amount / agg.pertes;
                                      const dash = frac * circ;
                                      {/* Petit espace entre segments (dash légèrement réduit, jamais le
                                          offset) — même esprit que le "surface gap" recommandé entre
                                          segments adjacents d'un anneau, sans recalculer d'angles. */}
                                      const el = <circle key={c.id} cx={cx} cy={cy} r={r2} fill="none" stroke={c.color} strokeWidth={stroke} strokeDasharray={`${Math.max(0, dash - 2.4)} ${circ - dash + 2.4}`} strokeDashoffset={-offsetAcc} />;
                                      offsetAcc += dash;
                                      return el;
                                    })}
                                  </svg>
                                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                                    {aggCategories.map((c) => (
                                      <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                        <span style={{ width: 9, height: 9, borderRadius: 99, background: c.color, flex: "0 0 auto" }} />
                                        <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, flex: 1, color: P.ink }}>{c.label}</span>
                                        <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800 }}>{Math.round((c.amount / agg.pertes) * 100)}%</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                {aggCategories.map((c) => (
                                  <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 0", borderTop: `1px solid ${P.line}` }}>
                                    <span style={{ width: 9, height: 9, borderRadius: 99, background: c.color, flex: "0 0 auto" }} />
                                    <strong style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, flex: 1 }}>{c.label}</strong>
                                    <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{c.amount.toFixed(0)} €</span>
                                    <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted, minWidth: 34, textAlign: "right" }}>{Math.round((c.amount / agg.pertes) * 100)}%</span>
                                  </div>
                                ))}
                              </section>
                            );
                          })()}
                          </>
                        )}

                        {/* AZL/Bestellung détaillés supprimés (2026-08-14) — remplacés par la note
                            compacte "AZL & Bestellung — pas encore de vraie donnée réseau" dans
                            l'onglet Résumé, ci-dessus. Aucune donnée réelle perdue : ces 2 blocs
                            n'affichaient déjà qu'un texte honnête d'absence de module. */}

                        {analyseTab === "evolution" && (
                          <>
                          {/* ===== STUNDENLEISTUNG — ÉVOLUTION RÉSEAU (demande Verkaufsleiter, 2026-08-14) =====
                               Complète (ne remplace pas) le classement instantané juste en dessous : moyenne
                               réseau par semaine + bande min/max entre filiales du périmètre, comparée au vrai
                               plan Stundenleistung hebdomadaire (stundPlanSumsPerWeek, demande utilisatrice
                               2026-08-15 — plus de seuil fixe). Même idiome visuel que le graphe Umsatz
                               ci-dessus (bandes, point courant mis en valeur, tooltip au survol/glisser). */}
                          {hasStundHistory && (() => {
                            const chartW = 320, chartH = 150, padX = 20, padTop = 26, padBottom = 30;
                            const definedAvg = stundAvgPerWeek.filter((v) => v !== null);
                            const definedPlan = stundPlanSumsPerWeek.filter((v) => v !== null);
                            const maxV = Math.max(...definedAvg, ...definedPlan, STUNDENLEISTUNG_ALERT_THRESHOLD, 1) * 1.12;
                            const minV = Math.min(...definedAvg, ...definedPlan, STUNDENLEISTUNG_ALERT_THRESHOLD) * 0.9;
                            const xFor = (i) => padX + (i / (PILOT_HISTORY_WEEKS - 1)) * (chartW - padX * 2);
                            const yFor = (v) => chartH - padBottom - ((v - minV) / (maxV - minV || 1)) * (chartH - padTop - padBottom);
                            const lastIdx = stundAvgPerWeek.length - 1 - [...stundAvgPerWeek].reverse().findIndex((v) => v !== null);
                            const linePts = stundAvgPerWeek.map((v, i) => (v !== null ? `${xFor(i)},${yFor(v)}` : null)).filter(Boolean).join(" ");
                            const planPts = stundPlanSumsPerWeek.map((v, i) => (v !== null ? `${xFor(i)},${yFor(v)}` : null)).filter(Boolean).join(" ");
                            const bandD = (() => {
                              const top = [], bot = [];
                              for (let i = 0; i < PILOT_HISTORY_WEEKS; i++) { if (stundMaxPerWeek[i] !== null) top.push(`${xFor(i)},${yFor(stundMaxPerWeek[i])}`); }
                              for (let i = PILOT_HISTORY_WEEKS - 1; i >= 0; i--) { if (stundMinPerWeek[i] !== null) bot.push(`${xFor(i)},${yFor(stundMinPerWeek[i])}`); }
                              if (!top.length || !bot.length) return null;
                              return `M${top.join(" L")} L${bot.join(" L")} Z`;
                            })();
                            const labelStride = Math.max(1, Math.ceil(PILOT_HISTORY_WEEKS / 6));
                            const updateStundHover = (e) => {
                              const box = e.currentTarget.getBoundingClientRect();
                              const frac = box.width > 0 ? Math.max(0, Math.min(1, (e.clientX - box.left) / box.width)) : 0;
                              setPilotStundHoverIdx(Math.max(0, Math.min(PILOT_HISTORY_WEEKS - 1, Math.round(frac * (PILOT_HISTORY_WEEKS - 1)))));
                            };
                            return (
                              <section style={{ ...card, borderRadius: 20, padding: 16, marginBottom: 18, maxWidth: isWide ? 640 : "none", marginLeft: isWide ? "auto" : undefined, marginRight: isWide ? "auto" : undefined }}>
                                <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 4 }}>{t("pilotage.analyse.stundEvolution.titleTemplate", { n: PILOT_HISTORY_WEEKS }, `📈 Stundenleistung — évolution (${PILOT_HISTORY_WEEKS} sem.)`)}</strong>
                                <p style={{ margin: "0 0 10px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{t("pilotage.analyse.stundEvolution.subtitleTemplate", "Moyenne réseau et écart entre filiales — comparé au plan Stundenleistung hebdomadaire")}</p>
                                <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: "100%", height: "auto", display: "block" }}>
                                  <defs>
                                    <filter id="pilotStundGlow" x="-40%" y="-40%" width="180%" height="180%">
                                      <feGaussianBlur stdDeviation="2.2" result="blur" />
                                      <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                                    </filter>
                                  </defs>
                                  {bandD && <path d={bandD} fill={A.green} opacity={0.14} stroke="none" />}
                                  <polyline points={planPts} fill="none" stroke={A.green} strokeWidth="1.4" strokeDasharray="4,3" opacity={0.7} />
                                  <polyline points={linePts} fill="none" stroke={A.green} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" filter="url(#pilotStundGlow)" />
                                  {stundAvgPerWeek.map((v, i) => {
                                    if (v === null) return null;
                                    const isLast = i === lastIdx;
                                    const showLabel = isLast || i % labelStride === 0;
                                    // null (pas de plan connu cette semaine-là) = pas de jugement,
                                    // jamais un faux "en dessous du seuil" (demande utilisatrice 2026-08-15).
                                    const belowPlan = stundPlanSumsPerWeek[i] != null && v < stundPlanSumsPerWeek[i];
                                    return (
                                      <Fragment key={i}>
                                        <circle cx={xFor(i)} cy={yFor(v)} r={isLast ? 5.5 : 3.2} fill={belowPlan ? A.red : A.green} stroke={P.card} strokeWidth={isLast ? 2 : 1.2} />
                                        {showLabel && (
                                          <>
                                            <text x={xFor(i)} y={yFor(v) - (isLast ? 14 : 9)} textAnchor="middle" fontSize={isLast ? 10 : 8.5} fontWeight="800" fill={isLast ? A.green : P.muted}>{v.toFixed(0)}</text>
                                            <text x={xFor(i)} y={chartH - 8} textAnchor="middle" fontSize="8.5" fontWeight="700" fill={P.muted}>KW{weekNumFor(i)}</text>
                                          </>
                                        )}
                                      </Fragment>
                                    );
                                  })}
                                  <rect
                                    x={padX} y={padTop} width={Math.max(0, chartW - padX * 2)} height={Math.max(0, chartH - padTop - padBottom)}
                                    fill="transparent" pointerEvents="all" style={{ touchAction: "none", cursor: "pointer" }}
                                    onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); updateStundHover(e); }}
                                    onPointerMove={(e) => { if (e.buttons) updateStundHover(e); }}
                                    onPointerUp={() => setPilotStundHoverIdx(null)}
                                    onPointerCancel={() => setPilotStundHoverIdx(null)}
                                    onPointerLeave={(e) => { if (e.pointerType !== "touch") setPilotStundHoverIdx(null); }}
                                  />
                                  {pilotStundHoverIdx != null && stundAvgPerWeek[pilotStundHoverIdx] !== null && (() => {
                                    const hi = pilotStundHoverIdx;
                                    const hv = stundAvgPerWeek[hi], hLo = stundMinPerWeek[hi], hHi = stundMaxPerWeek[hi], hPlan = stundPlanSumsPerWeek[hi];
                                    const bw = 96, bh = hPlan != null ? 50 : 40;
                                    const bx = Math.min(Math.max(xFor(hi) - bw / 2, padX), chartW - padX - bw);
                                    return (
                                      <g>
                                        <circle cx={xFor(hi)} cy={yFor(hv)} r="8.5" fill="none" stroke={A.green} strokeWidth="1.75" opacity={0.85} />
                                        <g transform={`translate(${bx},2)`}>
                                          <rect width={bw} height={bh} rx={8} fill={P.card} stroke={P.line} strokeWidth="1" />
                                          <text x={8} y={14} fontSize="8.5" fontWeight="800" fill={P.muted}>KW{weekNumFor(hi)}</text>
                                          <text x={8} y={26} fontSize="9.5" fontWeight="800" fill={A.green}>{hv.toFixed(1)} €/h</text>
                                          {hLo !== null && hHi !== null && (
                                            <text x={8} y={36} fontSize="7.5" fontWeight="700" fill={P.muted}>{t("pilotage.analyse.stundEvolution.hoverRangeTemplate", { lo: hLo.toFixed(0), hi: hHi.toFixed(0) }, `écart ${hLo.toFixed(0)}–${hHi.toFixed(0)}`)}</text>
                                          )}
                                          {hPlan != null && (
                                            <text x={8} y={46} fontSize="7.5" fontWeight="700" fill={P.muted}>{t("pilotage.kpi.objTemplate", { amount: `${hPlan.toFixed(0)} €/h` }, `Obj. ${hPlan.toFixed(0)} €/h`)}</text>
                                          )}
                                        </g>
                                      </g>
                                    );
                                  })()}
                                </svg>
                                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
                                  <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                                    <span style={{ width: 8, height: 8, borderRadius: 99, background: A.green, flex: "0 0 auto" }} />
                                    {t("pilotage.analyse.stundEvolution.legendAvg", "Moyenne réseau")}
                                  </span>
                                  <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                                    <span style={{ width: 8, height: 8, borderRadius: 99, background: "transparent", border: `1px solid ${A.green}`, flex: "0 0 auto" }} />
                                    {t("pilotage.analyse.stundEvolution.legendSpread", "Écart filiales")}
                                  </span>
                                  <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                                    <span style={{ width: 12, height: 0, borderTop: `1.4px dashed ${A.green}`, flex: "0 0 auto", opacity: 0.7 }} />
                                    {t("pilotage.analyse.chart.objectiveLabel", "Objectif")}
                                  </span>
                                </div>
                              </section>
                            );
                          })()}
                          </>
                        )}

                        {analyseTab === "filiales" && (
                          <>
                          {/* ===== BLOC TRAVAIL ET PRODUCTIVITÉ =====
                               Fidèle à nexora-app_1.zip (source lignes ~5625-5653) : classement par
                               Stundenleistung réelle, "absences" = vraies Krankmeldungen actives de la
                               filiale sur le périmètre (managerOverview.perStore), pas un champ démo. */}
                          {rows.length > 0 && (() => {
                            const workRows = rows.filter((r) => r.s.stundenleistung !== null).slice().sort((a, b) => a.s.stundenleistung - b.s.stundenleistung);
                            if (workRows.length === 0) return null;
                            const absencesFor = (storeId) => (managerOverview?.perStore || []).find((p) => p.storeId === storeId)?.krankmeldungActive || 0;
                            const weakest = workRows[0];
                            const maxStunden = Math.max(...workRows.map((x) => x.s.stundenleistung));
                            return (
                              <section style={{ ...card, borderRadius: 20, padding: 16, marginBottom: 18, maxWidth: isWide ? 640 : "none", marginLeft: isWide ? "auto" : undefined, marginRight: isWide ? "auto" : undefined }}>
                                <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 4 }}>{t("pilotage.analyse.productivity.title", "💪 Travail et productivité")}</strong>
                                <p style={{ margin: "0 0 12px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                                  <strong>{storeLabelFor(weakest.storeId)}</strong> {t("pilotage.analyse.productivity.weakestTemplate", { value: weakest.s.stundenleistung.toFixed(1) }, `a la Stundenleistung la plus faible (${weakest.s.stundenleistung.toFixed(1)} €/h)`)}{absencesFor(weakest.storeId) > 0 ? <>{t("pilotage.analyse.productivity.linkAbsencesTemplate", { n: absencesFor(weakest.storeId) }, `, à mettre en lien avec ses ${absencesFor(weakest.storeId)} Krankmeldung${absencesFor(weakest.storeId) > 1 ? "en" : ""} en cours.`)}</> : t("pilotage.analyse.productivity.noAbsenceText", " — sans absence en cours sur cette filiale, plutôt un signe de charge de travail à revoir.")}
                                </p>
                                {(pilotWorkRowsExpanded ? workRows : workRows.slice(0, PILOT_RANKING_CAP)).map((r) => (
                                  <div key={r.storeId} style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 0" }}>
                                    <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, width: 76, flex: "0 0 auto", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{storeLabelFor(r.storeId)}</span>
                                    <div style={{ flex: 1, height: 14, borderRadius: 7, background: P.soft, overflow: "hidden", position: "relative" }}>
                                      <div style={{ width: `${(r.s.stundenleistung / maxStunden) * 100}%`, height: "100%", borderRadius: 7, background: `linear-gradient(90deg, ${A.green}cc, ${A.green})` }} />
                                    </div>
                                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, width: 34, textAlign: "right", flex: "0 0 auto", fontWeight: 700 }}>{r.s.stundenleistung.toFixed(1)}</span>
                                    <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: absencesFor(r.storeId) > 0 ? A.red : P.muted, width: 30, textAlign: "right", flex: "0 0 auto" }}>{absencesFor(r.storeId)} {t("pilotage.analyse.productivity.absShort", "abs.")}</span>
                                  </div>
                                ))}
                                {workRows.length > PILOT_RANKING_CAP && (
                                  <Pressable onClick={() => setPilotWorkRowsExpanded((v) => !v)} style={{ display: "block", width: "100%", textAlign: "center", padding: "10px 0 2px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.green }}>
                                    {pilotWorkRowsExpanded ? t("pilotage.analyse.rankingCollapse", "Réduire") : t("pilotage.analyse.rankingShowAllTemplate", { n: workRows.length }, `Voir les ${workRows.length} filiales`)}
                                  </Pressable>
                                )}
                              </section>
                            );
                          })()}

                          {/* ===== CLASSEMENT UMSATZ (liste complète, pas seulement les KPI) =====
                               Fidèle à nexora-app_1.zip (source lignes ~5655-5668). */}
                          {rows.length > 0 && (
                            <>
                              <strong style={{ display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px 8px" }}>{t("pilotage.analyse.rankingUmsatzTemplate", { period: periodLabel }, `Classement Umsatz ${periodLabel}`)}</strong>
                              {(() => {
                                const umsatzRows = rows.slice().sort((a, b) => b.s.umsatzIst - a.s.umsatzIst);
                                const shown = pilotUmsatzRowsExpanded ? umsatzRows : umsatzRows.slice(0, PILOT_RANKING_CAP);
                                return shown.map((r, idx) => {
                                  const h = pilotHistory[r.storeId] || [];
                                  const hasDelta = h.length >= 2 && h[h.length - 2] > 0;
                                  const delta = hasDelta ? ((h[h.length - 1] - h[h.length - 2]) / h[h.length - 2]) * 100 : null;
                                  return (
                                    <Pressable key={r.storeId} onClick={() => goTo(r.storeId)} style={{ ...card, ...tile, padding: 12, marginBottom: 7 }}>
                                      <span style={{ width: 22, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, flex: "0 0 auto" }}>#{idx + 1}</span>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{storeLabelFor(r.storeId)}</strong>
                                        <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{(r.s.umsatzIst / 1000).toFixed(1)}k €{r.s.stundenleistung !== null ? ` · Stundenl. ${r.s.stundenleistung.toFixed(1)}` : ""}</div>
                                      </div>
                                      {delta !== null && (
                                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: delta >= 0 ? A.green : A.red }}>{delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}%</span>
                                      )}
                                    </Pressable>
                                  );
                                });
                              })()}
                              {rows.length > PILOT_RANKING_CAP && (
                                <Pressable onClick={() => setPilotUmsatzRowsExpanded((v) => !v)} style={{ display: "block", width: "100%", textAlign: "center", padding: "10px 0 2px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.green }}>
                                  {pilotUmsatzRowsExpanded ? t("pilotage.analyse.rankingCollapse", "Réduire") : t("pilotage.analyse.rankingShowAllTemplate", { n: rows.length }, `Voir les ${rows.length} filiales`)}
                                </Pressable>
                              )}
                            </>
                          )}
                          </>
                        )}

                        {analyseTab === "resume" && (
                          <>
                          {/* ===== RAPPORTS MANQUANTS =====
                               Adapté depuis nexora-app_1.zip (source lignes ~5670-5684) : le fichier source
                               parle de "Monatsmeldung"/"Inventaire", deux concepts qui n'existent pas
                               réellement dans StoreFlowProV2 — utilise à la place le vrai signal équivalent
                               qui existe déjà (jours de reporting quotidien manquants, managerOverview.perStore). */}
                          {(() => {
                            const missing = (managerOverview?.perStore || []).filter((p) => scope.includes(p.storeId) && p.missingReportingDays > 0);
                            if (missing.length === 0) return null;
                            return (
                              <>
                                <strong style={{ display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: A.orange, margin: "18px 2px 8px" }}>{t("pilotage.analyse.missingReportsTemplate", { n: missing.length }, `⚠️ Rapports manquants (${missing.length})`)}</strong>
                                {missing.map((p) => (
                                  <div key={p.storeId} style={{ ...card, ...tile, padding: 12, marginBottom: 7 }}>
                                    <div style={{ flex: 1 }}>
                                      <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{storeLabelFor(p.storeId)}</strong>
                                      <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.analyse.missingReportingDaysTemplate", { n: p.missingReportingDays }, `${p.missingReportingDays} jour${p.missingReportingDays > 1 ? "s" : ""} de reporting quotidien manquant${p.missingReportingDays > 1 ? "s" : ""}`)}</div>
                                    </div>
                                  </div>
                                ))}
                              </>
                            );
                          })()}

                          {/* ===== ALERTES ET OBSERVATIONS (signaux de performance dérivés) =====
                               Fidèle en esprit à nexora-app_1.zip (source lignes ~5686-5729) : signaux réels
                               calculés depuis pilotStoreStats/pilotHistory (pas de démo). Les signaux AZL/
                               Bestellung du fichier source sont omis ici (aucune donnée réelle derrière). */}
                          {(() => {
                            const dashAlerts = [];
                            rows.forEach((r) => {
                              const s = r.s;
                              if (s.umsatzPlan > 0 && s.umsatzIst < s.umsatzPlan * 0.9) dashAlerts.push({ storeId: r.storeId, indicateur: t("pilotage.analyse.alert.objectiveAtRisk", "Objectif en danger"), ecart: `${(((s.umsatzIst - s.umsatzPlan) / s.umsatzPlan) * 100).toFixed(0)}%`, action: t("pilotage.analyse.alert.actionPlanRequired", "Plan d'action requis avant la fin de période") });
                              if (s.stundenleistung !== null && s.stundenleistungPlan != null && s.stundenleistung < s.stundenleistungPlan) dashAlerts.push({ storeId: r.storeId, indicateur: t("pilotage.analyse.alert.insufficientStundenleistung", "Stundenleistung insuffisante"), ecart: `${s.stundenleistung.toFixed(1)} €/h`, action: t("pilotage.analyse.alert.reviewWorkOrg", "Revoir l'organisation du travail") });
                              const h = pilotHistory[r.storeId] || [];
                              if (h.length >= 4) {
                                let dir = null, count = 0;
                                for (let i = h.length - 1; i > 0; i--) {
                                  const d = h[i] - h[i - 1];
                                  if (dir === null) { if (d === 0) break; dir = d < 0 ? "down" : "up"; count = 1; }
                                  else if (d < 0 && dir === "down") count++;
                                  else break;
                                }
                                if (count >= 3 && dir === "down") dashAlerts.push({ storeId: r.storeId, indicateur: t("pilotage.analyse.alert.continuousDecline", "Dégradation continue"), ecart: t("pilotage.analyse.alert.weeksDeclineTemplate", { n: count }, `${count} semaines de baisse`), action: t("pilotage.analyse.alert.interventionRecommended", "Intervention recommandée sans délai") });
                              }
                            });
                            if (dashAlerts.length === 0) return null;
                            return (
                              <>
                                <strong style={{ display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "22px 2px 8px" }}>{t("pilotage.analyse.alertsObservationsTemplate", { n: dashAlerts.length }, `🔎 Alertes et observations (${dashAlerts.length})`)}</strong>
                                {dashAlerts.map((a, i) => (
                                  <div key={i} style={{ ...card, borderRadius: 16, padding: 13, marginBottom: 8 }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                                      <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{a.indicateur}</strong>
                                      <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.red }}>{a.ecart}</span>
                                    </div>
                                    <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 8 }}>{storeLabelFor(a.storeId)} · {periodLabel} · {a.action}</div>
                                    <Pressable onClick={() => goTo(a.storeId)} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.blue }}>{t("pilotage.analyse.viewDetailBtn", "Voir le détail →")}</Pressable>
                                  </div>
                                ))}
                              </>
                            );
                          })()}
                          </>
                        )}

                        {/* Vue VKL — Équipe (2026-08-16) : regroupement par BL de tout ce qui
                            précède, jamais un nouveau calcul serveur — pilotStoreStats et
                            managerOverview.perStore couvrent déjà tout le périmètre hérité
                            (voir sfRequestIdentity, server.js) depuis que le VKL hérite
                            automatiquement des filiales de ses BL. Repose sur adminUsers
                            (chargé par l'effet dédié, voir plus haut) pour savoir quel BL
                            possède quelle filiale (u.stores). */}
                        {analyseTab === "equipe" && (
                          <>
                            <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 14px", lineHeight: 1.5 }}>
                              {t("pilotage.team.introTemplate", { role: directReportsRoleLabel }, `Ton périmètre regroupé par ${directReportsRoleLabel} — mêmes chiffres qu'au-dessus, répartis par équipe.`)}
                            </p>
                            {/* Créer un subordonné (retour utilisateur 2026-08-23,
                                "personnaliser chaque profil... un NL devrait pouvoir créer
                                ses VKL, un VKL ses BL") — POST /api/leaders, réservé côté
                                serveur à VKL/NL, rôle du subordonné toujours déduit du
                                sien. Toujours visible ici, même sans aucun subordonné
                                encore créé. */}
                            {(() => {
                              const mySubordinateRole = user?.role === "Niederlassungsleiter" ? "Verkaufsleiter" : user?.role === "Verkaufsleiter" ? "Bereichsleiter" : null;
                              if (!mySubordinateRole) return null;
                              return (
                                <Pressable onClick={openCreateLeader} style={{
                                  width: "100%", textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                                  background: "rgba(124,58,237,.1)", color: A.purple, marginBottom: 14,
                                }}>➕ {t("pilotage.team.createLeaderBtnTemplate", { role: mySubordinateRole }, `Créer un ${mySubordinateRole}`)}</Pressable>
                              );
                            })()}
                            {(() => {
                              const blCards = directReports.map((bl) => {
                                const blStores = (bl.stores && bl.stores.length ? bl.stores : [bl.storeId]).filter((id) => stores.includes(id));
                                const blAgg = blStores.reduce((acc, sId) => {
                                  const s = pilotStoreStats[sId];
                                  if (!s) return acc;
                                  acc.umsatzIst += s.umsatzIst; acc.umsatzPlan += s.umsatzPlan; acc.stundenIst += s.stundenIst; acc.stundenPlan += s.stundenPlan || 0; acc.pertes += s.pertes;
                                  return acc;
                                }, { umsatzIst: 0, umsatzPlan: 0, stundenIst: 0, stundenPlan: 0, pertes: 0 });
                                const blStundenleistung = blAgg.stundenIst > 0 ? blAgg.umsatzIst / blAgg.stundenIst : null;
                                const blPerStore = (managerOverview?.perStore || []).filter((p) => blStores.includes(p.storeId));
                                const reactTotals = blPerStore.reduce((acc, p) => ({
                                  tasksDone: acc.tasksDone + p.tasksReactivity.doneOnTime, tasksTotal: acc.tasksTotal + p.tasksReactivity.total,
                                }), { tasksDone: 0, tasksTotal: 0 });
                                const tasksOnTimeRate = reactTotals.tasksTotal > 0 ? Math.round((reactTotals.tasksDone / reactTotals.tasksTotal) * 100) : null;
                                const alertsCount = (managerOverview?.feed || []).filter((f) => blStores.includes(f.storeId)).length;
                                const belowObjective = blAgg.umsatzPlan > 0 && blAgg.umsatzIst < blAgg.umsatzPlan * 0.9;
                                return { bl, blStores, blAgg, blStundenleistung, tasksOnTimeRate, alertsCount, belowObjective };
                              }).sort((a, b) => b.blAgg.umsatzIst - a.blAgg.umsatzIst);

                              const unassignedStores = stores.filter((sId) => !directReports.some((bl) => (bl.stores && bl.stores.length ? bl.stores : [bl.storeId]).includes(sId)));

                              if (blCards.length === 0) {
                                return <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.team.noneText", "Aucun responsable rattaché pour l'instant.")}</p>;
                              }

                              return (
                                <>
                                  {blCards.map(({ bl, blStores, blAgg, blStundenleistung, tasksOnTimeRate, alertsCount, belowObjective }, idx) => {
                                    const isExpanded = pilotTeamExpandedBL.has(bl.employeeId);
                                    const initials = `${(bl.firstname || "?").charAt(0)}${(bl.lastname || "").charAt(0)}`.toUpperCase();
                                    return (
                                      <div key={bl.id} style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 0, marginBottom: 10, overflow: "hidden" }}>
                                        <Pressable onClick={() => togglePilotTeamBL(bl.employeeId)} style={{ display: "flex", alignItems: "center", gap: 10, padding: 14, width: "100%", boxSizing: "border-box" }}>
                                          <span style={{ width: 34, height: 34, borderRadius: 99, color: "#fff", border: "1px solid rgba(255,255,255,.16)", display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, flexShrink: 0, ...premiumTileStyle(A.purple) }}>{initials}</span>
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                              <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{bl.firstname} {bl.lastname}</strong>
                                              {idx === 0 && blAgg.umsatzIst > 0 && <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.green, background: "rgba(30,184,95,.14)", borderRadius: 99, padding: "2px 7px" }}>🏆 {t("pilotage.team.topBadge", "1ère équipe")}</span>}
                                              {belowObjective && <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.red, background: "rgba(239,68,68,.12)", borderRadius: 99, padding: "2px 7px" }}>⚠️ {t("pilotage.team.belowObjectiveBadge", "Objectif en danger")}</span>}
                                            </div>
                                            <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>
                                              {t("pilotage.team.storesCountTemplate", { n: blStores.length }, `${blStores.length} filiale${blStores.length > 1 ? "s" : ""}`)}
                                              {alertsCount > 0 ? ` · ${t("pilotage.team.alertsCountTemplate", { n: alertsCount }, `${alertsCount} alerte${alertsCount > 1 ? "s" : ""}`)}` : ""}
                                            </span>
                                          </div>
                                          <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: A.purple, transition: "transform .18s ease", transform: isExpanded ? "rotate(180deg)" : "none" }}>▾</span>
                                        </Pressable>
                                        <div style={{ display: "flex", gap: 8, padding: "0 14px 14px", flexWrap: "wrap" }}>
                                          <div style={{ flex: "1 1 40%", background: P.soft, borderRadius: 12, padding: "9px 11px" }}>
                                            <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".04em", display: "block" }}>{t("pilotage.team.caLabel", "CA")}</span>
                                            <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{fmtEuroPilot(blAgg.umsatzIst)}</strong>
                                          </div>
                                          <div style={{ flex: "1 1 40%", background: P.soft, borderRadius: 12, padding: "9px 11px" }}>
                                            <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".04em", display: "block" }}>{t("pilotage.team.stundLabel", "Stundenleistung")}</span>
                                            <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{blStundenleistung != null ? `${blStundenleistung.toFixed(1)} €/h` : "—"}</strong>
                                          </div>
                                          <div style={{ flex: "1 1 40%", background: P.soft, borderRadius: 12, padding: "9px 11px" }}>
                                            <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".04em", display: "block" }}>{t("pilotage.team.pertesLabel", "Pertes")}</span>
                                            <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{fmtEuroPilot(blAgg.pertes)}</strong>
                                          </div>
                                          <div style={{ flex: "1 1 40%", background: P.soft, borderRadius: 12, padding: "9px 11px" }}>
                                            <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".04em", display: "block" }}>{t("pilotage.team.reactivityLabel", "Réactivité")}</span>
                                            <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{tasksOnTimeRate != null ? `${tasksOnTimeRate}%` : "—"}</strong>
                                          </div>
                                        </div>
                                        {isExpanded && (
                                          <div style={{ borderTop: `1px solid ${P.line}`, padding: 14 }}>
                                            {blStores.map((sId) => (
                                              <div key={sId} style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 0" }}>
                                                <Pressable onClick={() => goTo(sId)} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between", minWidth: 0 }}>
                                                  <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{storeLabelFor(sId)}</span>
                                                  <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{pilotStoreStats[sId] ? fmtEuroPilot(pilotStoreStats[sId].umsatzIst) : "—"}</span>
                                                </Pressable>
                                                {/* Réassignation (retour utilisateur 2026-08-24, "un point qui manque")
                                                    — seulement utile s'il y a au moins un AUTRE subordonné direct vers
                                                    qui déplacer. */}
                                                {directReports.length > 1 && (
                                                  <Pressable onClick={() => openReassignStore(sId, bl.employeeId)} aria-label={t("pilotage.team.reassignStoreAria", "Déplacer cette filiale")} style={{
                                                    flex: "0 0 auto", width: 26, height: 26, borderRadius: 99, background: P.soft, color: P.muted,
                                                    display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`,
                                                  }}>↔️</Pressable>
                                                )}
                                              </div>
                                            ))}
                                            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                                              <Pressable onClick={() => createTeamMission(bl)} style={{ flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: P.card, border: `1.5px solid ${P.line}`, color: P.ink }}>
                                                🎯 {t("pilotage.team.createMissionBtn", "Mission équipe")}
                                              </Pressable>
                                              {isNexoraAdmin && (
                                                <Pressable onClick={() => openReplaceLeader(bl)} style={{ flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: P.card, border: `1.5px solid ${P.line}`, color: P.ink }}>
                                                  🔁 {t("admin.replaceLeaderTitle", "Remplacer ce responsable")}
                                                </Pressable>
                                              )}
                                            </div>
                                            {/* Désactiver/réactiver — "Gestion de mon
                                                équipe" (retour utilisateur 2026-08-23).
                                                Chaque carte ici EST déjà un subordonné
                                                DIRECT (directReports filtre sur
                                                reportsTo===user?.proId) — pas besoin d'une
                                                garde de plus côté client, le serveur revérifie
                                                de toute façon (PATCH /api/leaders/:id/disable). */}
                                            <Pressable onClick={() => toggleLeaderDisabled(bl)} style={{
                                              width: "100%", textAlign: "center", padding: "9px 0", marginTop: 8, borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                                              background: bl.disabled ? "rgba(30,184,95,.1)" : "rgba(239,68,68,.1)", color: bl.disabled ? A.green : A.red,
                                            }}>{bl.disabled ? `↩️ ${t("pilotage.team.reenableBtn", "Réactiver ce compte")}` : `🚫 ${t("pilotage.team.disableBtn", "Désactiver ce compte")}`}</Pressable>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                  {unassignedStores.length > 0 && (
                                    <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 13, background: "rgba(231,138,18,.08)", border: "1px solid rgba(231,138,18,.25)" }}>
                                      <strong style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: "#92620a", marginBottom: 4 }}>⚠️ {t("pilotage.team.unassignedTitle", "Filiales sans BL")}</strong>
                                      <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 4 }}>{unassignedStores.map((id) => storeLabelFor(id)).join(", ")}</span>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </>
                        )}

                        {/* Onglet Comparaison (demande utilisatrice, 2026-08-17, complétée en
                            cours de route) : "comparer les données d'une filiale sur des
                            périodes qu'il souhaite [PAS juste 2] et entre filiales sur période
                            définie aussi" + "cela doit être avec des graphiques" — 2 modes
                            indépendants, chacun avec un vrai graphique en barres (même technique
                            SVG fait main que le reste de Pilotage réseau, ex. le graphe Umsatz de
                            l'onglet Évolution ci-dessus, A.frost/A.red au-dessus/en-dessous
                            objectif) plutôt qu'un simple tableau de chiffres. States/effets dédiés
                            déclarés plus haut (comparePeriods etc.) — jamais pilotStoreStats/
                            pilotPeriod, réservés aux 3 autres onglets. fmtEuroPilot/stores/
                            storeLabelFor/card réutilisés depuis la portée de ce même IIFE. */}
                        {analyseTab === "comparaison" && (() => {
                          const PERIOD_OPTIONS = [
                            { id: "jour", l: t("pilotage.period.day", "Aujourd'hui") },
                            { id: "semaine", l: t("pilotage.period.week", "Cette semaine") },
                            { id: "mois", l: t("pilotage.period.month", "Ce mois") },
                            { id: "trimestre", l: t("pilotage.period.quarter", "Ce trimestre") },
                            { id: "annee", l: t("pilotage.period.year", "Cette année") },
                          ];
                          const periodLabelFor = (period, offset) => {
                            const d = new Date();
                            if (period === "jour") d.setDate(d.getDate() + offset);
                            else if (period === "semaine") d.setDate(d.getDate() + offset * 7);
                            else if (period === "mois") { d.setDate(1); d.setMonth(d.getMonth() + offset); }
                            else if (period === "trimestre") { d.setDate(1); d.setMonth(d.getMonth() + offset * 3); }
                            else d.setFullYear(d.getFullYear() + offset);
                            if (period === "jour") return d.toLocaleDateString(intlLocale, { day: "numeric", month: "short", year: "numeric" });
                            if (period === "semaine") return `${t("pilotage.periodStepper.weekPrefix", "Semaine")} ${isoWeek(d)} · ${d.getFullYear()}`;
                            if (period === "mois") return d.toLocaleDateString(intlLocale, { month: "long", year: "numeric" });
                            if (period === "trimestre") return `T${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
                            return `${d.getFullYear()}`;
                          };
                          const periodShortLabelFor = (period, offset) => {
                            const d = new Date();
                            if (period === "jour") d.setDate(d.getDate() + offset);
                            else if (period === "semaine") d.setDate(d.getDate() + offset * 7);
                            else if (period === "mois") { d.setDate(1); d.setMonth(d.getMonth() + offset); }
                            else if (period === "trimestre") { d.setDate(1); d.setMonth(d.getMonth() + offset * 3); }
                            else d.setFullYear(d.getFullYear() + offset);
                            if (period === "jour") return d.toLocaleDateString(intlLocale, { day: "numeric", month: "short" });
                            if (period === "semaine") return `S${isoWeek(d)}`;
                            if (period === "mois") return d.toLocaleDateString(intlLocale, { month: "short", year: "2-digit" });
                            if (period === "trimestre") return `T${Math.floor(d.getMonth() / 3) + 1} ${String(d.getFullYear()).slice(-2)}`;
                            return `${d.getFullYear()}`;
                          };
                          const PeriodPicker = ({ period, setPeriod, offset, setOffset, accent }) => (
                            <div>
                              <div style={{ display: "flex", gap: 5, overflowX: "auto", marginBottom: 6, paddingBottom: 2 }}>
                                {PERIOD_OPTIONS.map((p) => (
                                  <Pressable key={p.id} onClick={() => { setPeriod(p.id); setOffset(0); }} style={{
                                    flex: "0 0 auto", padding: "5px 10px", borderRadius: 99, fontWeight: 800, fontSize: `calc(10.5px * var(--nx-font-scale, 1))`,
                                    background: period === p.id ? accent : P.soft, color: period === p.id ? "#fff" : P.muted,
                                  }}>{p.l}</Pressable>
                                ))}
                              </div>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                                <Pressable onClick={() => setOffset((o) => o - 1)} aria-label={t("pilotage.periodStepper.prevAria", "Période précédente")} style={{
                                  width: 24, height: 24, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink, flexShrink: 0,
                                }}>‹</Pressable>
                                <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, textTransform: "capitalize", minWidth: 92, textAlign: "center" }}>{periodLabelFor(period, offset)}</span>
                                <Pressable onClick={() => offset < 0 && setOffset((o) => o + 1)} aria-label={t("pilotage.periodStepper.nextAria", "Période suivante")} style={{
                                  width: 24, height: 24, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: offset < 0 ? P.ink : P.muted, opacity: offset < 0 ? 1 : 0.35, flexShrink: 0,
                                }}>›</Pressable>
                              </div>
                            </div>
                          );
                          const tauxPertesFor = (s) => (s && s.umsatzIst > 0 ? (s.pertes / s.umsatzIst) * 100 : null);

                          // Graphique en barres fait main — même idiome que le reste de Pilotage
                          // réseau (svg brut, rx arrondi, texte de valeur au-dessus de la barre,
                          // barre grisée plate + "—" quand la donnée est absente plutôt qu'une
                          // fausse barre à 0).
                          const MiniBarChart = ({ data, color, fmtValue }) => {
                            const w = Math.max(160, data.length * 58), h = 128, padTop = 20, padBottom = 30, padX = 12;
                            const vals = data.map((d) => d.value).filter((v) => v != null);
                            const maxV = vals.length ? Math.max(...vals, 0.0001) * 1.18 : 1;
                            const slot = data.length ? (w - padX * 2) / data.length : 0;
                            const barW = Math.max(18, Math.min(40, slot - 14));
                            return (
                              <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: "auto", display: "block" }}>
                                <line x1={padX} y1={h - padBottom} x2={w - padX} y2={h - padBottom} stroke={P.line} strokeWidth="1" />
                                {data.map((d, i) => {
                                  const cx = padX + slot * i + slot / 2;
                                  const x = cx - barW / 2;
                                  const bh = d.value != null ? Math.max(2, (d.value / maxV) * (h - padTop - padBottom)) : 0;
                                  const y = h - padBottom - bh;
                                  return (
                                    <Fragment key={d.key}>
                                      {d.value != null ? (
                                        <rect x={x} y={y} width={barW} height={bh} rx={5} fill={color} opacity={d.dim ? 0.4 : 1} />
                                      ) : (
                                        <rect x={x} y={h - padBottom - 3} width={barW} height={3} rx={1.5} fill={P.line} />
                                      )}
                                      <text x={cx} y={Math.max(11, y - 5)} textAnchor="middle" fontSize="8.5" fontWeight="800" fill={P.ink}>{d.value != null ? fmtValue(d.value) : "—"}</text>
                                      <text x={cx} y={h - padBottom + 13} textAnchor="middle" fontSize="8" fill={P.muted}>{d.label}</text>
                                    </Fragment>
                                  );
                                })}
                              </svg>
                            );
                          };

                          return (
                            <>
                              <div style={{ display: "flex", gap: 4, padding: 3, background: P.soft, borderRadius: 13, marginBottom: 16 }}>
                                {[
                                  { id: "periods", l: t("pilotage.comparaison.mode.periods", "Une filiale, plusieurs périodes") },
                                  { id: "stores", l: t("pilotage.comparaison.mode.stores", "Plusieurs filiales, 1 période") },
                                ].map((m) => (
                                  <Pressable key={m.id} onClick={() => setCompareMode(m.id)} style={{
                                    flex: 1, textAlign: "center", padding: "8px 4px", borderRadius: 10, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                                    background: compareMode === m.id ? P.card : "transparent", color: compareMode === m.id ? P.ink : P.muted,
                                    boxShadow: compareMode === m.id ? "0 1px 4px rgba(0,0,0,.08)" : "none",
                                  }}>{m.l}</Pressable>
                                ))}
                              </div>

                              {compareMode === "periods" && (
                                <>
                                  <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
                                    {stores.map((sId) => (
                                      <Pressable key={sId} onClick={() => setCompareFiliale(sId)} style={{
                                        flex: "0 0 auto", padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                                        background: compareFiliale === sId ? A.purple : P.soft, color: compareFiliale === sId ? "#fff" : P.muted,
                                      }}>{storeLabelFor(sId)}</Pressable>
                                    ))}
                                  </div>

                                  {!compareFiliale && (
                                    <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("pilotage.comparaison.periods.chooseFilialeHint", "Choisis une filiale pour comparer plusieurs périodes.")}</p>
                                  )}

                                  {compareFiliale && (() => {
                                    // Tri chronologique réel (jamais l'ordre d'ajout) — la même
                                    // pilotPeriodRange déjà utilisée pour le fetch donne un
                                    // startISO directement comparable.
                                    const sortedPeriods = comparePeriods.slice().sort((a, b) => pilotPeriodRange(a.period, a.offset).startISO.localeCompare(pilotPeriodRange(b.period, b.offset).startISO));
                                    return (
                                      <>
                                        <div style={{ display: "flex", gap: 10, overflowX: "auto", marginBottom: 12, paddingBottom: 4 }}>
                                          {comparePeriods.map((p) => (
                                            <div key={p.id} style={{ ...card, flexDirection: "column", alignItems: "stretch", borderRadius: 16, padding: 12, flex: "0 0 auto", width: 190, position: "relative" }}>
                                              {comparePeriods.length > 2 && (
                                                <Pressable onClick={() => setComparePeriods((prev) => prev.filter((x) => x.id !== p.id))} aria-label={t("pilotage.comparaison.periods.removeAria", "Retirer cette période")} style={{
                                                  position: "absolute", top: 8, right: 8, width: 20, height: 20, borderRadius: 99, background: "rgba(239,68,68,.12)", color: A.red, display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800,
                                                }}>✕</Pressable>
                                              )}
                                              <PeriodPicker
                                                period={p.period} offset={p.offset} accent={A.purple}
                                                setPeriod={(period) => setComparePeriods((prev) => prev.map((x) => x.id === p.id ? { ...x, period } : x))}
                                                setOffset={(fn) => setComparePeriods((prev) => prev.map((x) => x.id === p.id ? { ...x, offset: typeof fn === "function" ? fn(x.offset) : fn } : x))}
                                              />
                                            </div>
                                          ))}
                                          <Pressable onClick={() => { setComparePeriods((prev) => [...prev, { id: nextComparePeriodId.current++, period: "mois", offset: 0 }]); }} style={{
                                            flex: "0 0 auto", width: 60, borderRadius: 16, border: `1.5px dashed ${P.line}`, display: "grid", placeItems: "center", fontSize: `calc(22px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.purple,
                                          }}>＋</Pressable>
                                        </div>

                                        {compareLoading && Object.keys(comparePeriodsStats).length === 0 && <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("common.loading", "Chargement...")}</p>}

                                        {sortedPeriods.some((p) => comparePeriodsStats[p.id]) && (() => {
                                          const chartData = (valueFn) => sortedPeriods.map((p) => ({ key: p.id, label: periodShortLabelFor(p.period, p.offset), value: comparePeriodsStats[p.id] ? valueFn(comparePeriodsStats[p.id]) : null }));
                                          return (
                                            <>
                                              {[
                                                { l: t("pilotage.kpi.umsatz", "Umsatz"), color: A.frost, fmt: (v) => fmtEuroPilot(v), data: chartData((s) => s.umsatzIst) },
                                                { l: "Stundenleistung", color: A.green, fmt: (v) => `${v.toFixed(1)} €/h`, data: chartData((s) => s.stundenleistung) },
                                                { l: t("pilotage.comparaison.kpi.tauxPertes", "Taux de pertes"), color: A.red, fmt: (v) => `${v.toFixed(1)}%`, data: chartData((s) => tauxPertesFor(s)) },
                                              ].map((chart) => (
                                                <section key={chart.l} style={{ ...card, flexDirection: "column", alignItems: "stretch", borderRadius: 20, padding: 16, marginBottom: 12 }}>
                                                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 8 }}>{chart.l}</strong>
                                                  <MiniBarChart data={chart.data} color={chart.color} fmtValue={chart.fmt} />
                                                </section>
                                              ))}
                                            </>
                                          );
                                        })()}
                                      </>
                                    );
                                  })()}
                                </>
                              )}

                              {compareMode === "stores" && (
                                <>
                                  <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 10, paddingBottom: 2 }}>
                                    {stores.map((sId) => {
                                      const active = compareStoresSelected.includes(sId);
                                      return (
                                        <Pressable key={sId} onClick={() => setCompareStoresSelected((prev) => active ? prev.filter((id) => id !== sId) : [...prev, sId])} style={{
                                          flex: "0 0 auto", padding: "8px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                                          background: active ? A.purple : P.soft, color: active ? "#fff" : P.muted,
                                        }}>{active ? "✓ " : ""}{storeLabelFor(sId)}</Pressable>
                                      );
                                    })}
                                  </div>

                                  <div style={{ marginBottom: 14 }}>
                                    <PeriodPicker period={compareStoresPeriod} setPeriod={setCompareStoresPeriod} offset={compareStoresOffset} setOffset={setCompareStoresOffset} accent={A.purple} />
                                  </div>

                                  {compareStoresSelected.length < 2 && (
                                    <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("pilotage.comparaison.stores.chooseStoresHint", "Sélectionne au moins 2 filiales à comparer.")}</p>
                                  )}

                                  {compareLoading && compareStoresSelected.length >= 2 && Object.keys(compareStoresStats).length === 0 && <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("common.loading", "Chargement...")}</p>}

                                  {compareStoresSelected.length >= 2 && (() => {
                                    const rows = compareStoresSelected.map((sId) => ({ storeId: sId, s: compareStoresStats[sId] })).filter((r) => r.s);
                                    if (rows.length === 0) return null;
                                    const chartData = (valueFn) => rows.map((r) => ({ key: r.storeId, label: storeLabelFor(r.storeId), value: valueFn(r.s) }));
                                    return (
                                      <>
                                        {[
                                          { l: t("pilotage.kpi.umsatz", "Umsatz"), color: A.frost, fmt: (v) => fmtEuroPilot(v), data: chartData((s) => s.umsatzIst) },
                                          { l: "Stundenleistung", color: A.green, fmt: (v) => `${v.toFixed(1)} €/h`, data: chartData((s) => s.stundenleistung) },
                                          { l: t("pilotage.comparaison.kpi.tauxPertes", "Taux de pertes"), color: A.red, fmt: (v) => `${v.toFixed(1)}%`, data: chartData((s) => tauxPertesFor(s)) },
                                        ].map((chart) => (
                                          <section key={chart.l} style={{ ...card, flexDirection: "column", alignItems: "stretch", borderRadius: 20, padding: 16, marginBottom: 12 }}>
                                            <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 8 }}>{chart.l}</strong>
                                            <MiniBarChart data={chart.data} color={chart.color} fmtValue={chart.fmt} />
                                          </section>
                                        ))}
                                      </>
                                    );
                                  })()}
                                </>
                              )}
                            </>
                          );
                        })()}

                          <p style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px", lineHeight: 1.5 }}>
                            {t("pilotage.analyse.footerNote", "Chiffres réels agrégés depuis Reporting (Stundenleistung Ist/Plan, pertes par catégorie, Krankmeldung). Aucune valeur inventée : AZL/Bestellung affichent honnêtement l'absence de module réel tant qu'ils ne sont pas branchés.")}
                          </p>
                        </>
                      );
                    })()}
                  </>
                );
              })()}
            </>
          );
        })()}

        {/* ================= LIEFERSCHEIN ================= */}
        {screen === "liefer" && lfScreen === "hub" && (
          <div className="nx-screen-enter">
            <p style={{ margin: "0 2px 14px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{t("lieferschein.hub.desc", "Importe, contrôle et valide un bon multi-pages depuis le téléphone ou l'ordinateur.")}</p>
            <Pressable onClick={() => { setLfImportMethod(null); setLfScreen("import"); }} style={{ width: "100%", ...btnSolid(A.blue), marginBottom: 15 }}>＋ {t("lieferschein.importBon", "Importer un bon")}</Pressable>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[
                { id: "active", l: `${t("lieferschein.tab.active", "En cours")} · ${lieferscheine.filter((n) => !n.archived && !n.trashed).length}` },
                { id: "archived", l: `${t("lieferschein.tab.archived", "Archivés")} · ${lieferscheine.filter((n) => n.archived && !n.trashed).length}` },
                { id: "trash", l: `${t("controlStore.trashAria", "Corbeille")} · ${lieferscheine.filter((n) => n.trashed).length}` },
              ].map((tb) => (
                <Pressable key={tb.id} onClick={() => setLfTab(tb.id)} style={{
                  flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(11px * var(--nx-font-scale, 1))`,
                  background: lfTab === tb.id ? A.blue : P.soft, color: lfTab === tb.id ? "#fff" : P.ink,
                  border: `1.5px solid ${lfTab === tb.id ? A.blue : P.line}`,
                }}>{tb.l}</Pressable>
              ))}
            </div>
            {currentLfList().length === 0 && <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("claims.emptyHereText", "Rien ici pour l'instant.")}</p>}
            <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 9 : 0 }}>
            {currentLfList().map((n, nIdx) => {
              const reviewCount = n.items.filter((i) => i.needsReview).length;
              return (
                <Pressable key={n.id} feedback className="nx-list-item-in" onClick={() => openLfDetail(n.id)} style={{ ...card, ...tile, padding: 14, marginBottom: isWide ? 0 : 9, alignItems: "flex-start", animationDelay: `${Math.min(nIdx, 14) * 28}ms` }}>
                  <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>📄</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{n.supplier}</strong>
                    <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 2 }}>{n.deliveryNumber} · {fmtFR(n.deliveryDate)} · {t("lieferschein.pageCountTemplate", { n: n.pageCount }, `${n.pageCount} page${n.pageCount > 1 ? "s" : ""}`)} · {t("lieferschein.itemCountTemplate", { n: n.items.length }, `${n.items.length} article${n.items.length > 1 ? "s" : ""}`)}</div>
                  </div>
                  {reviewCount > 0 && <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: A.orange, borderRadius: 99, padding: "4px 9px" }}>{t("lieferschein.toVerifyCountBadge", { n: reviewCount }, `${reviewCount} à vérifier`)}</span>}
                </Pressable>
              );
            })}
            </div>
          </div>
        )}

        {screen === "liefer" && lfScreen === "import" && (
          <>
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 9 }}>{t("lieferschein.import.howQuestion", "Comment veux-tu importer ce bon ?")}</span>
            {[
              { id: "pdf", emoji: "📑", l: "PDF", d: t("lieferschein.import.pdfDesc", "Texte natif ou scanné, plusieurs pages") },
              { id: "photos", emoji: "📸", l: t("lieferschein.import.photosTitle", "Photos"), d: t("lieferschein.import.photosDesc", "Une photo par page, dans l'ordre") },
              { id: "manual", emoji: "✍️", l: t("lieferschein.import.manualTitle", "Saisie manuelle"), d: t("lieferschein.import.manualDesc", "En dernier recours") },
            ].map((m) => (
              <Pressable key={m.id} onClick={() => setLfImportMethod(m.id)} style={{
                display: "flex", alignItems: "center", gap: 12, width: "100%", boxSizing: "border-box", padding: 15, borderRadius: 18, marginBottom: 9,
                background: lfImportMethod === m.id ? "rgba(54,79,231,.09)" : P.card, border: `1.5px solid ${lfImportMethod === m.id ? A.blue : P.line}`, boxShadow: P.shadow,
              }}>
                <span style={{ fontSize: `calc(22px * var(--nx-font-scale, 1))`}}>{m.emoji}</span>
                <div style={{ flex: 1 }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{m.l}</strong>
                  <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{m.d}</div>
                </div>
              </Pressable>
            ))}
            <input ref={lfFileInputRef} type="file" accept={lfImportMethod === "photos" ? "image/*" : "application/pdf,image/*"} multiple style={{ display: "none" }}
              onChange={(e) => { analyzeLfImport(e.target.files); e.target.value = ""; }} />
            <Pressable onClick={() => lfImportMethod === "manual" ? analyzeLfImport(null) : lfFileInputRef.current?.click()} style={{ width: "100%", ...btnSolid(A.blue), marginTop: 8, opacity: lfImportMethod ? 1 : .5 }}>
              {lfAnalyzing ? t("lieferschein.import.analyzing", "Analyse en cours — lecture page par page...") : t("lieferschein.import.analyzeBtn", "Analyser")}
            </Pressable>
          </>
        )}

        {screen === "liefer" && lfScreen === "preview" && (
          <>
            <p style={{ margin: "0 2px 14px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{t("lieferschein.preview.introFull", "Vérification avant création — corrige ce que l'analyse aurait mal lu.")}</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label={t("lieferschein.preview.supplierLabel", "Fournisseur")}><input style={inputStyle} value={lfPreview.supplier} onChange={(e) => updateLfPreviewField("supplier", e.target.value)} /></Field>
              <Field label={t("lieferschein.preview.numberLabelShort", "N° de bon")}><input style={inputStyle} value={lfPreview.deliveryNumber} onChange={(e) => updateLfPreviewField("deliveryNumber", e.target.value)} /></Field>
            </div>
            <Field label={t("lieferschein.preview.dateLabel", "Date de livraison")}><input style={dateTimeInputStyle} type="date" value={lfPreview.deliveryDate} onChange={(e) => updateLfPreviewField("deliveryDate", e.target.value)} /></Field>

            <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, margin: "10px 2px 9px" }}>{t("lieferschein.preview.articlesHeading", "Articles")}</strong>
            {lfPreview.items.map((it) => (
              <div key={it.id} style={{ ...card, borderRadius: 16, padding: 12, marginBottom: 9, border: it.needsReview ? `1.5px solid ${A.orange}` : `1px solid ${P.line}` }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                  <input style={{ ...inputStyle, padding: "9px 11px", fontSize: `calc(13px * var(--nx-font-scale, 1))`}} placeholder="Art.Nr" value={it.artNr} onChange={(e) => updateLfPreviewItem(it.id, "artNr", e.target.value)} />
                  <input style={{ ...inputStyle, padding: "9px 11px", fontSize: `calc(13px * var(--nx-font-scale, 1))`}} placeholder="VE" value={it.ve} onChange={(e) => updateLfPreviewItem(it.id, "ve", e.target.value)} />
                </div>
                <input style={{ ...inputStyle, padding: "9px 11px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, marginBottom: 8 }} placeholder={t("claims.item.name", "Désignation")} value={it.name} onChange={(e) => updateLfPreviewItem(it.id, "name", e.target.value)} />
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input style={{ ...inputStyle, flex: 1, padding: "9px 11px", fontSize: `calc(13px * var(--nx-font-scale, 1))`}} inputMode="numeric" placeholder={t("lieferschein.preview.qtyPlaceholder", "Qté")} value={it.qty} onChange={(e) => updateLfPreviewItem(it.id, "qty", e.target.value)} />
                  {it.needsReview ? (
                    <Pressable onClick={() => markLfLineVerified(it.id)} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: A.orange, borderRadius: 99, padding: "9px 12px", whiteSpace: "nowrap" }}>{t("lieferschein.preview.markVerified", "Marquer vérifié")}</Pressable>
                  ) : (
                    <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.green, whiteSpace: "nowrap" }}>✓ {t("lieferschein.preview.verified", "Vérifié")}</span>
                  )}
                  <Pressable onClick={() => removeLfPreviewLine(it.id)} aria-label={t("common.delete", "Supprimer")} style={{ width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.13)", display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>✕</Pressable>
                </div>
                {it.needsReview && <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: A.orange, fontWeight: 700, display: "block", marginTop: 6 }}>⚠️ {it.reviewReason}</span>}
                {!it.catalogMatch && it.artNr === "" && <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 4 }}>{t("lieferschein.preview.notInCatalog", "Non trouvé au catalogue")}</span>}
              </div>
            ))}
            <Pressable onClick={addLfPreviewLine} style={{
              width: "100%", textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
              background: P.soft, border: `1.5px dashed ${P.line}`, color: P.muted, marginBottom: 16,
            }}>＋ {t("lieferschein.preview.addLine", "Ajouter une ligne")}</Pressable>
            <Pressable onClick={createLieferschein} style={{ width: "100%", ...btnSolid(A.blue) }}>{t("lieferschein.preview.createBtn", "Créer le bon")}</Pressable>
          </>
        )}

        {screen === "liefer" && lfScreen === "detail" && (() => {
          const note = lieferscheine.find((n) => n.id === lfDetailId);
          if (!note) return null;
          // Regroupe par section KOMGRU dans l'ordre du document (note.groups) ; les bons
          // sans groupe (saisie manuelle, ancien import) retombent sur un seul bloc "Articles".
          const groupOrder = (note.groups || []).length
            ? note.groups
            : [{ id: "", name: t("lieferschein.preview.articlesHeading", "Articles") }];
          const byGroup = new Map(groupOrder.map((g) => [g.id, []]));
          note.items.forEach((it) => {
            const gid = byGroup.has(it.groupId) ? it.groupId : groupOrder[0].id;
            byGroup.get(gid).push(it);
          });
          return (
            <>
              <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "stretch", padding: 17, marginBottom: 14 }}>
                <strong style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>{note.supplier}</strong>
                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{note.deliveryNumber} · {fmtFR(note.deliveryDate)} · {t("lieferschein.pageCountTemplate", { n: note.pageCount }, `${note.pageCount} page${note.pageCount > 1 ? "s" : ""}`)}</span>
                <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 4 }}>{t("lieferschein.detail.importedByTemplate", { name: note.createdBy, date: fmtFR(note.createdAt.slice(0, 10)) }, `Importé par ${note.createdBy} le ${fmtFR(note.createdAt.slice(0, 10))} · ${note.createdAt.slice(11, 16)}`)}</span>
              </section>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "0 2px 9px" }}>
                <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{t("lieferschein.preview.articlesHeading", "Articles")} ({note.items.length})</strong>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>
                  {t("lieferschein.detail.controlledCountTemplate", { done: note.items.filter((i) => i.status && i.status !== "pending").length, total: note.items.length }, `${note.items.filter((i) => i.status && i.status !== "pending").length}/${note.items.length} contrôlés`)}
                </span>
              </div>
              {groupOrder.map((g) => {
                const groupItems = byGroup.get(g.id) || [];
                if (!groupItems.length) return null;
                return (
                  <div key={g.id || "ungrouped"}>
                    {note.groups && note.groups.length > 0 && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "12px 2px 8px" }}>
                        <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.blue, textTransform: "uppercase", letterSpacing: ".02em" }}>{g.name}</span>
                        <span style={{ flex: 1, height: 1, background: P.line }} />
                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{groupItems.length}</span>
                      </div>
                    )}
                    {groupItems.map((it) => {
                      const st = LF_ITEM_STATUS[it.status || "pending"];
                      const stKey = { pending: "pending", complete: "complete", partial: "partial", missing: "missing", excess: "excess", damaged: "damaged", wrong_item: "wrongItem", unordered: "unordered", to_verify: "toVerify", other: "other" }[it.status || "pending"];
                      return (
                        <Pressable key={it.id} onClick={() => setLfStatusTarget({ noteId: note.id, itemId: it.id })} style={{ ...card, ...tile, padding: 13, marginBottom: 8, alignItems: "flex-start", width: "100%", boxSizing: "border-box" }}>
                          <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                            <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{it.name || t("lieferschein.item.unidentifiedName", "Article non identifié")}</strong>
                            <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{it.artNr ? `Art.Nr ${it.artNr} · ` : ""}{t("controlRun.qtyPrefix", "Qté")} {it.qty}{it.ve ? ` · ${it.ve}` : ""}</div>
                            {it.reviewReason && <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: A.orange, fontWeight: 700 }}>{it.reviewReason}</span>}
                          </div>
                          <span style={{
                            fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "5px 10px", whiteSpace: "nowrap",
                            color: "#fff", background: st.color,
                          }}>{st.emoji} {t(`lieferschein.itemStatus.${stKey}`, st.label)}</span>
                        </Pressable>
                      );
                    })}
                  </div>
                );
              })}
              <div style={{ display: "flex", gap: 9, marginTop: 6, marginBottom: 9 }}>
                <Pressable onClick={() => generateLfClaim(note)} style={{
                  flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: "rgba(239,68,68,.12)", color: A.red,
                }}>{t("lieferschein.detail.generateClaimBtnFull", "⚠️ Générer une réclamation")}</Pressable>
                <Pressable onClick={() => completeLfControl(note)} style={{ flex: 1, ...btnSolid(A.green) }}>✓ {t("lieferschein.detail.completeControlBtn", "Terminer le contrôle")}</Pressable>
              </div>
              <div style={{ display: "flex", gap: 9, marginTop: 15 }}>
                <Pressable onClick={() => showToast(t("lieferschein.detail.exportCsvSimulatedToast", "📤 Export CSV (simulé)"))} style={{
                  flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                  background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                }}>{t("lieferschein.detail.exportCsv", "Export CSV")}</Pressable>
                {!note.archived && !note.trashed && <Pressable onClick={() => archiveLf(note.id)} style={{ flex: 1, ...btnSolid(A.blue) }}>{t("lieferschein.detail.archiveBtnFull", "📦 Archiver")}</Pressable>}
                {note.trashed ? (
                  <>
                    <Pressable onClick={() => restoreLf(note.id)} style={{ flex: 1, ...btnSolid(A.green) }}>{t("reductions.restoreBtn", "↩︎ Restaurer")}</Pressable>
                  </>
                ) : (
                  <Pressable onClick={() => trashLf(note.id)} style={{
                    flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                    background: "rgba(239,68,68,.12)", color: A.red,
                  }}>{t("lieferschein.detail.deleteBtnFull", "🗑️ Supprimer")}</Pressable>
                )}
              </div>
              {note.trashed && (
                <Pressable onClick={() => deleteLfForever(note.id)} style={{
                  width: "100%", textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, marginTop: 9,
                  color: A.red,
                }}>{t("lieferschein.detail.purgeBtn", "Supprimer définitivement")}</Pressable>
              )}
            </>
          );
        })()}

        {/* ================= NOTIFICATIONS ================= */}
        {screen === "notifications" && (() => {
          const criticalCount = urgentItems.filter((t) => t.chip.includes("🚨") || t.chip.includes("⛔")).length;
          const heroTone = criticalCount > 0 ? A.red : (notifUnreadCount + urgentItems.length > 0 ? A.orange : A.green);
          const heroText = criticalCount > 0 ? t("notifications.hero.critical", "Des actions nécessitent une réaction immédiate.")
            : (notifUnreadCount + urgentItems.length > 0 ? t("notifications.hero.toReview", "Tu as des notifications à vérifier.") : t("notifications.hero.calm", "Tout est calme pour le moment."));
          const list = notifTab === "unread" ? notifications.filter((n) => !n.read)
            : notifTab === "all" ? notifications : [];
          return (
            <div className="nx-screen-enter">
              <section style={{ ...colorCard(heroTone), ...tile, flexDirection: "column", alignItems: "stretch", padding: 19 }}>
                <span style={onColorTag}>🔔 {t("notifications.hero.eyebrow", "Centre global")}</span>
                <h2 style={{ margin: "5px 0 6px", fontSize: `calc(18px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", color: "#fff" }}>{heroText}</h2>
                <p style={{ margin: 0, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, ...onColorSub }}>{t("common.updatedJustNow", "Mis à jour à l'instant")}</p>
              </section>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
                {[
                  { v: totalUnread, l: t("notifications.stat.messages", "Messages") },
                  { v: openStoreTeamTasks.length, l: t("notifications.stat.tasks", "Tâches") },
                  { v: urgentItems.length, l: t("notifications.stat.urgent", "Urgences") },
                  { v: articles.length - checkedCount, l: t("notifications.stat.control", "Contrôle") },
                ].map((s) => (
                  <div key={s.l} style={{ background: P.soft, borderRadius: 14, padding: "10px 4px", textAlign: "center" }}>
                    <strong style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`, display: "block" }}>{s.v}</strong>
                    <span style={{ fontSize: `calc(8.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{s.l}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                {[
                  { id: "unread", l: t("notifications.tab.unreadTemplate", { n: notifications.filter((n) => !n.read).length }, `Non lues · ${notifications.filter((n) => !n.read).length}`) },
                  { id: "urgent", l: t("notifications.tab.urgentTemplate", { n: urgentItems.length }, `Urgent · ${urgentItems.length}`) },
                  { id: "all", l: t("notifications.tab.all", "Toutes") },
                ].map((tb) => (
                  <Pressable key={tb.id} onClick={() => setNotifTab(tb.id)} style={{
                    flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(11px * var(--nx-font-scale, 1))`,
                    background: notifTab === tb.id ? A.orange : P.soft, color: notifTab === tb.id ? "#fff" : P.ink,
                    border: `1.5px solid ${notifTab === tb.id ? A.orange : P.line}`,
                  }}>{tb.l}</Pressable>
                ))}
              </div>

              {notifTab === "urgent" ? (
                <>
                  <p style={{ margin: "0 2px 12px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                    {t("notifications.urgent.desc", "Alertes réellement impératives, agrégées de tous les modules. Écarte-les une fois vues — elles reviennent d'elles-mêmes si le problème n'est pas réglé.")}
                  </p>
                  {urgentItems.length === 0 && (
                    <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 26 }}>
                      <span style={{ fontSize: `calc(30px * var(--nx-font-scale, 1))`, marginBottom: 6 }}>✓</span>
                      <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{t("notifications.urgent.empty", "Rien d'urgent pour l'instant.")}</strong>
                    </section>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
                  {urgentItems.map((ut, utIdx) => (
                    <div key={ut.label} className="nx-list-item-in" style={{ ...card, ...tile, padding: 13, marginBottom: isWide ? 0 : 8, animationDelay: `${Math.min(utIdx, 14) * 28}ms` }}>
                      <Pressable feedback onClick={() => goToDayTask(ut)} style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                        <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{ut.chip}</strong>
                        <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{ut.label}</span>
                      </Pressable>
                      <Pressable onClick={() => dismissUrgentItem(ut.label)} aria-label={t("common.dismissForTodayAria", "Écarter pour aujourd'hui")} style={{
                        width: 30, height: 30, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                      }}>✕</Pressable>
                    </div>
                  ))}
                  </div>
                </>
              ) : (
                <>
                  {list.length === 0 && (
                    <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 26 }}>
                      <span style={{ fontSize: `calc(30px * var(--nx-font-scale, 1))`, marginBottom: 6 }}>🔕</span>
                      <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{notifTab === "unread" ? t("notifications.empty.unread", "Aucune non lue.") : t("notifications.empty.all", "Aucune notification.")}</strong>
                    </section>
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: isWide ? "repeat(auto-fill, minmax(380px, 1fr))" : "minmax(0, 1fr)", gap: isWide ? 8 : 0 }}>
                  {list.map((n, nIdx) => (
                    <div key={n.id} className="nx-list-item-in" style={{ ...card, ...tile, padding: 13, marginBottom: isWide ? 0 : 8, alignItems: "flex-start", animationDelay: `${Math.min(nIdx, 14) * 28}ms` }}>
                      <span style={{
                        flex: "0 0 auto", width: 40, height: 40, borderRadius: 14, fontSize: `calc(18px * var(--nx-font-scale, 1))`,
                        display: "grid", placeItems: "center",
                        ...(n.read ? { background: P.soft } : premiumTileStyle(A.orange)),
                      }}>{NOTIF_ICON[n.type] || "🔔"}</span>
                      <Pressable feedback onClick={() => openNotification(n)} style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {!n.read && <span style={{ width: 6, height: 6, borderRadius: 99, background: A.orange, flex: "0 0 auto" }} />}
                          <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{notifTitle(n)}</strong>
                        </div>
                        <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 2 }}>{notifMessage(n)}</span>
                        <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 4, display: "block" }}>{fmtFR(n.createdAt.slice(0, 10))} · {n.createdAt.slice(11, 16)}</span>
                      </Pressable>
                      <Pressable onClick={() => deleteNotif(n.id)} aria-label={t("common.delete", "Supprimer")} style={{
                        width: 26, height: 26, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, flex: "0 0 auto", color: P.muted,
                      }}>✕</Pressable>
                    </div>
                  ))}
                  </div>
                  {notifications.some((n) => n.read) && (
                    <Pressable onClick={clearReadNotifs} style={{
                      width: "100%", textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, marginTop: 4,
                      background: P.soft, border: `1.5px solid ${P.line}`, color: P.muted,
                    }}>{t("notifications.clearReadBtn", "Effacer les lues")}</Pressable>
                  )}
                  {notifications.length > 0 && (
                    <Pressable onClick={() => { if (window.confirm(t("notifications.confirmDeleteAll", "Supprimer TOUTES les notifications ?"))) deleteAllNotifs(); }} style={{
                      width: "100%", textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, marginTop: 8,
                      background: "rgba(239,68,68,.1)", border: `1.5px solid ${A.red}40`, color: A.red,
                    }}>{t("notifications.clearAllBtn", "Tout supprimer")}</Pressable>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* ================= AUTRES ONGLETS (PLUS / RÉGLAGES) ================= */}
        {screen === "other" && (() => {
          // "Plus" — cartes dépliantes (retour utilisateur explicite, 2026-08-18, artefact
          // validé point par point) : remplace l'ancien long défilement de ~15 sections par
          // 5 cartes repliées par défaut (Apparence & texte, Notifications, Compte &
          // sécurité, Réseau, Administration), une seule ouverte à la fois — jamais
          // plusieurs en même temps, sinon l'écran redevient aussi long qu'avant. Chaque
          // sous-titre reflète l'état réel (thème/langue/taille, sessions en ligne, nombre
          // de filiales...), jamais un texte figé qu'il faudrait ouvrir la carte pour
          // vérifier.
          const openSection = settingsOpenSection;
          const toggleSection = (id) => setSettingsOpenSection((cur) => (cur === id ? null : id));
          const accWrap = (id) => ({ display: "grid", gridTemplateRows: openSection === id ? "1fr" : "0fr", transition: "grid-template-rows .32s cubic-bezier(.32,.72,0,1)" });
          const segChip = (active) => ({
            flex: "1 1 auto", textAlign: "center", padding: "8px 6px", borderRadius: 11, fontWeight: 800,
            fontSize: `calc(11px * var(--nx-font-scale, 1))`, background: active ? P.card : "none", color: active ? A.blue : P.ink,
            boxShadow: active ? "0 2px 7px rgba(0,0,0,.09)" : "none",
          });
          const fieldGroup = (first) => ({ padding: "13px 0", borderTop: first ? "none" : `1px solid ${P.line}` });
          const fieldLabel = { fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: 10 };

          // Icône colorée, fond neutre (2026-08-30, retour utilisateur explicite après
          // comparatif en artefact — "je sens que c'est to much avec les couleurs", 3 pistes
          // proposées, "Le 1" choisie) : remplace le fond plein saturé du 2026-08-24 (5
          // blocs pleins empilés, jugé "to much" une fois vu en vrai sur le téléphone) —
          // seule la puce-icône garde sa couleur (iconBg pour le fond tinté, iconColor pour
          // le glyphe), l'en-tête revient au fond neutre P.card comme le reste de l'app.
          // Hiérarchie titre/sous-titre (retour utilisateur explicite, 2026-08-24) conservée
          // à l'identique, juste repassée en encre normale (P.ink/P.muted) sur fond neutre.
          const renderAccHeader = (id, icon, iconBg, iconColor, title, subtitle) => (
            <Pressable onClick={() => toggleSection(id)} style={{
              display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "14px 15px", textAlign: "left",
            }}>
              <span style={{
                width: 42, height: 42, borderRadius: 14, display: "grid", placeItems: "center", fontSize: `calc(18px * var(--nx-font-scale, 1))`, flex: "0 0 auto", color: iconColor,
                background: iconBg,
              }}>{icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.ink, display: "block" }}>{title}</strong>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 500, color: P.muted, display: "block", marginTop: 2 }}>{subtitle}</span>
              </div>
              <span style={{ color: P.muted, fontSize: `calc(14px * var(--nx-font-scale, 1))`, flex: "0 0 auto", display: "inline-block", transform: openSection === id ? "rotate(90deg)" : "none", transition: "transform .25s ease" }}>›</span>
            </Pressable>
          );

          const subRow = (icon, iconBg, iconColor, title, subtitle, trailing, onClick) => (
            <Pressable onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 11, width: "100%", padding: "11px 0", textAlign: "left", borderTop: `1px solid ${P.line}` }}>
              <span style={{ width: 30, height: 30, borderRadius: 99, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: "0 0 auto", background: P.soft }}>{icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink, display: "block" }}>{title}</strong>
                <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 500, color: P.muted, display: "block", marginTop: 1 }}>{subtitle}</span>
              </div>
              {trailing}
            </Pressable>
          );

          const onlineCount = adminSessions.filter((s) => s.online).length;
          const storesCount = (user?.stores?.length ? user?.stores : [user?.store]).length;
          const adminRowCount = 4 + (user?.role === "NEXORA_ADMIN" || isNetworkLeader ? 1 : 0);

          const appearanceSubtitle = `${useSystemTheme ? t("auth.systemTheme", "Système") : t(`theme.${themeId}`, THEMES[themeId].name)} · ${lang.toUpperCase()} · ${t(`settings.textSize.${fontScaleId}`, FONT_SCALES[fontScaleId].label)}`;
          const notifSubtitle = pushEnabled && bannersEnabled
            ? t("settings.notifications.bothOnSummary", "Push et bannières activés")
            : pushEnabled
            ? t("settings.notifications.pushOnlySummary", "Push activé")
            : bannersEnabled
            ? t("settings.notifications.bannersOnlySummary", "Bannières activées")
            : t("settings.notifications.offSummary", "Désactivées");
          const accountSubtitle = t("settings.account.headerSubtitleTemplate", { n: onlineCount }, `Mot de passe · ${onlineCount} en ligne`);
          const networkSubtitle = isNetworkLeader
            ? t(`settings.network.leaderSubtitleTemplate.${storesCount > 1 ? "other" : "one"}`, { n: storesCount }, `${storesCount} filiale${storesCount > 1 ? "s" : ""} sous ta responsabilité`)
            : (myLeaderInfo
              ? (myLeaderInfo.covered
                ? t("settings.myLeader.coveredTemplate", { name: myLeaderInfo.name, role: myLeaderInfo.role, coveredFor: myLeaderInfo.coveredForName }, `${myLeaderInfo.name} · ${myLeaderInfo.role} (remplace ${myLeaderInfo.coveredForName})`)
                : `${myLeaderInfo.name} · ${myLeaderInfo.role}`)
              : t("settings.myLeader.unassigned", "Non assigné pour l'instant."));
          const adminSubtitle = t("settings.admin.headerSubtitleTemplate", { n: adminRowCount }, `${adminRowCount} réglages réseau`);

          return (
          <div style={{ paddingTop: 26 }}>
            <span style={eyebrow(A.orange)}>{t("settings.eyebrow", "Nexora")}</span>
            <h2 style={{ margin: "5px 0 4px", fontSize: `calc(24px * var(--nx-font-scale, 1))`, letterSpacing: "-.03em", fontWeight: 800 }}>{t("settings.hero.title", "Qu'est-ce que tu veux régler ?")}</h2>
            <p style={{ color: P.muted, fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "0 0 18px", lineHeight: 1.5 }}>{t("settings.hero.subtitle", "Ton compte, tes préférences et — si tu es administrateur — la gestion de la filiale, tout est ici.")}</p>

            {/* Carte profil "fond teinté doux" — refonte 2026-08-30, artefact validé avant
                implémentation (option "C" pour la carte + palette "1" pour les tuiles en
                dessous) : remplace le traitement neutre du 2026-08-22 (lui-même un retrait
                du dégradé plein d'origine) — avatar en badge dégradé premiumTileStyle,
                fond légèrement teinté bleu sur toute la carte, badge rôle plein bleu,
                statut de session en vraie pilule verte (pas juste un point). Photo réelle
                (upload/suppression réels, POST/DELETE /api/users/:id/photo) inchangée.*/}
            <section style={{
              ...tile, padding: 20, marginBottom: 14, alignItems: "center", borderRadius: 24,
              background: `linear-gradient(155deg, ${A.blue}1a, ${A.blue}08 60%)`, border: `1px solid ${A.blue}24`,
            }}>
              <Pressable onClick={() => profilePhotoInputRef.current?.click()} aria-label={t("settings.photo.editAria", "Changer la photo de profil")} style={{
                position: "relative", flex: "0 0 auto", width: 58, height: 58, borderRadius: 99,
              }}>
                {user?.photoUrl ? (
                  <img src={user?.photoUrl} alt="" style={{ width: 58, height: 58, borderRadius: 99, objectFit: "cover", boxShadow: `0 0 0 1.5px ${P.line}, 0 8px 20px rgba(0,0,0,.1)` }} />
                ) : (
                  <div style={{
                    width: 58, height: 58, borderRadius: 99, color: "#fff", ...premiumTileStyle(A.blue),
                    display: "grid", placeItems: "center", fontWeight: 800, fontSize: `calc(21px * var(--nx-font-scale, 1))`,
                  }}>{user?.firstname.charAt(0).toUpperCase()}</div>
                )}
                <span style={{
                  position: "absolute", bottom: -2, right: -2, width: 22, height: 22, borderRadius: 99,
                  background: "#fff", color: A.blue, display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`,
                  boxShadow: "0 2px 6px rgba(0,0,0,.25)", opacity: profilePhotoUploading ? .5 : 1,
                }}>{profilePhotoUploading ? "…" : "📷"}</span>
              </Pressable>
              {/* Jamais capture="user" ici (retour utilisateur explicite : "depuis mes
                  fichiers photos aussi") — cet attribut force l'ouverture directe de
                  l'appareil photo sur mobile et cache le choix "Galerie/Fichiers" que le
                  système propose nativement dès qu'il est absent. */}
              <input ref={profilePhotoInputRef} type="file" accept="image/*" onChange={uploadProfilePhoto} style={{ display: "none" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 3, color: P.ink }}>{user?.firstname} {user?.lastname}</strong>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 600 }}>Pro-ID {user?.proId} · {myStoresLabel}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, background: A.blue, color: "#fff", borderRadius: 99, padding: "4px 10px" }}>{user?.role || "Employé"}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(30,184,95,.13)", border: "1px solid rgba(30,184,95,.25)", borderRadius: 99, padding: "4px 9px 4px 7px" }}>
                    <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, boxShadow: "0 0 0 3px rgba(30,184,95,.2)" }} />
                    <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: "#1a8a52", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".03em" }}>{t("settings.session.active", "Session active")}</span>
                  </span>
                </div>
              </div>
              {user?.photoUrl && (
                <Pressable onClick={removeProfilePhoto} aria-label={t("settings.photo.remove", "Supprimer la photo")} style={{
                  fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.red, background: "rgba(239,68,68,.08)", borderRadius: 99, padding: "7px 11px", flex: "0 0 auto",
                }}>{t("settings.photo.removeButton", "Retirer")}</Pressable>
              )}
            </section>

            {user?.role === "NEXORA_ADMIN" && (
              <Pressable onClick={() => { setAdminViewAsEmployee((v) => !v); setScreen("home"); }} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "9px 13px", borderRadius: 99,
                background: P.soft, border: `1.5px solid ${P.line}`, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800,
                marginBottom: 14, width: "fit-content",
              }}>
                <span style={{ width: 30, height: 18, borderRadius: 99, background: adminViewAsEmployee ? A.green : P.line, position: "relative", flex: "0 0 auto" }}>
                  <span style={{ position: "absolute", top: 2, left: adminViewAsEmployee ? 14 : 2, width: 14, height: 14, borderRadius: 99, background: "#fff", transition: "left .15s ease" }} />
                </span>
                <span>{t("settings.adminView.title", "Vue employé (test)")}</span>
              </Pressable>
            )}

            {/* ===== Apparence & texte ===== */}
            <section style={{ ...card, borderRadius: 20, marginBottom: 9, overflow: "hidden" }}>
              {renderAccHeader("appearance", "🎨", "rgba(54,79,231,.14)", A.blue, t("settings.section.appearance.title", "Apparence & texte"), appearanceSubtitle)}
              <div style={accWrap("appearance")}><div style={{ overflow: "hidden" }}>
                <div style={{ padding: "14px 14px 15px", borderTop: `1px solid ${P.line}`, margin: "0 14px" }}>

                  {/* Aperçu vivant (2026-08-30, retour utilisateur explicite après 2 refus
                      d'itérations "pareil que l'existant" — direction structurellement
                      différente demandée) : reflète en direct taille/police/gras via P/
                      FONT_SCALES/FONT_CHOICES déjà appliqués globalement, jamais une
                      simulation séparée — remplace l'ancien encart "Aperçu" statique en bas
                      de section, qui n'existe donc plus. */}
                  <div style={{ borderRadius: 18, padding: 16, marginBottom: 18, position: "relative", overflow: "hidden", background: P.card, border: `1px solid ${P.line}`, boxShadow: `0 2px 8px rgba(20,24,38,.06)` }}>
                    <span style={{ position: "absolute", top: 12, right: 14, fontSize: `calc(8px * var(--nx-font-scale, 1))`, fontWeight: 900, color: A.blue, background: "rgba(54,79,231,.1)", padding: "3px 8px", borderRadius: 99, textTransform: "uppercase", letterSpacing: ".05em" }}>● {t("settings.livePreview.tag", "Live")}</span>
                    <div style={{ background: P.soft, borderRadius: 13, padding: 12, display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ width: 30, height: 30, borderRadius: 99, flexShrink: 0, color: "#fff", ...premiumTileStyle(A.blue) }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: boldText ? 900 : 800, fontSize: `calc(${(13 * FONT_SCALES[fontScaleId].value).toFixed(1)}px * var(--nx-font-scale, 1))`, fontFamily: FONT_CHOICES[fontChoiceId].stack, color: P.ink, marginBottom: 5 }}>Nexora</div>
                        <div style={{ height: 6, width: "45%", borderRadius: 3, background: P.muted, opacity: .35 }} />
                      </div>
                    </div>
                  </div>

                  <div style={fieldGroup(true)}>
                    <span style={fieldLabel}>{t("settings.theme.label", "Thème")}</span>
                    <div style={{ display: "flex", gap: 10, overflowX: "auto", margin: "0 -14px", padding: "0 14px 2px" }}>
                      {(() => {
                        const swatch = (top, bot, botAccent) => (
                          <span style={{ display: "block", width: 52, height: 66 }}>
                            <span style={{ display: "block", height: "42%", background: top }} />
                            <span style={{ display: "flex", alignItems: "flex-end", height: "58%", background: bot, padding: 6, boxSizing: "border-box" }}>
                              <span style={{ width: "60%", height: 5, borderRadius: 3, background: botAccent }} />
                            </span>
                          </span>
                        );
                        return (
                          <>
                            <div style={{ flex: "0 0 auto", width: 52 }}>
                              <Pressable onClick={() => { pickSystemTheme(); showToast(t("settings.theme.systemToast", "Thème système — suit ton appareil")); }} aria-label={t("settings.theme.systemAria", "Thème système")} style={{
                                display: "block", width: 52, height: 66, borderRadius: 14, overflow: "hidden",
                                border: useSystemTheme ? `2px solid ${A.blue}` : `1.5px solid ${P.line}`,
                                boxShadow: useSystemTheme ? `0 0 0 1.5px ${A.blue}, 0 6px 14px rgba(54,79,231,.3)` : "0 2px 6px rgba(0,0,0,.08)",
                              }}>{swatch(THEMES.blanc.bg, THEMES.sombre.card, THEMES.sombre.muted)}</Pressable>
                              <span style={{ display: "block", textAlign: "center", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: useSystemTheme ? A.blue : P.muted, marginTop: 6 }}>{t("settings.theme.auto", "Auto")}</span>
                            </div>
                            {THEME_ORDER.map((id) => {
                              const th = THEMES[id];
                              const thName = t(`theme.${id}`, th.name);
                              const active = !useSystemTheme && id === themeId;
                              return (
                                <div key={id} style={{ flex: "0 0 auto", width: 52 }}>
                                  <Pressable onClick={() => { pickTheme(id); showToast(`${t("settings.theme.label", "Thème")} ${thName} ${th.icon}`); }} aria-label={t("settings.theme.themeAria", { name: thName }, `Thème ${thName}`)} style={{
                                    display: "block", width: 52, height: 66, borderRadius: 14, overflow: "hidden",
                                    border: active ? `2px solid ${A.blue}` : `1.5px solid ${th.line}`,
                                    boxShadow: active ? `0 0 0 1.5px ${A.blue}, 0 6px 14px rgba(54,79,231,.3)` : "0 2px 6px rgba(0,0,0,.08)",
                                  }}>{swatch(th.bg, th.card, th.muted)}</Pressable>
                                  <span style={{ display: "block", textAlign: "center", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: active ? A.blue : P.muted, marginTop: 6 }}>{thName}</span>
                                </div>
                              );
                            })}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div style={fieldGroup()}>
                    <span style={fieldLabel}>{t("settings.textSize.label", "Taille du texte")}</span>
                    <div style={{ position: "relative", height: 40 }}>
                      <div style={{ position: "absolute", top: 19, left: 4, right: 4, height: 2, background: P.line, borderRadius: 2 }} />
                      <div style={{ position: "absolute", top: 19, left: 4, height: 2, background: A.blue, borderRadius: 2, width: `${(FONT_SCALE_ORDER.indexOf(fontScaleId) / (FONT_SCALE_ORDER.length - 1)) * 100}%`, transition: "width .18s ease" }} />
                      <div style={{ display: "flex", justifyContent: "space-between", position: "relative" }}>
                        {FONT_SCALE_ORDER.map((id, i) => {
                          const active = fontScaleId === id;
                          return (
                            <Pressable key={id} onClick={() => setFontScaleId(id)} aria-label={t(`settings.textSize.${id}`, FONT_SCALES[id].label)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, width: `${100 / FONT_SCALE_ORDER.length}%` }}>
                              <span style={{ width: 12, height: 12, borderRadius: 99, background: active ? A.blue : P.card, border: `2px solid ${active ? A.blue : P.line}`, boxShadow: active ? `0 0 0 4px rgba(54,79,231,.18)` : "none" }} />
                              <span style={{ fontWeight: 800, color: active ? A.blue : P.muted, fontSize: `calc(${11 + i * 2}px * var(--nx-font-scale, 1))` }}>Aa</span>
                            </Pressable>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div style={fieldGroup()}>
                    <span style={fieldLabel}>{t("settings.language.label", "Langue")}</span>
                    <div style={{ display: "flex", gap: 6 }}>
                      {LANGS.map((l) => {
                        const active = lang === l.id;
                        return (
                          <Pressable key={l.id} onClick={() => changeLang(l.id)} style={{
                            flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "9px 0", borderRadius: 12,
                            background: active ? A.blue : P.soft,
                          }}>
                            <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{l.flag}</span>
                            <span style={{ fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: active ? "#fff" : P.muted }}>{l.id.toUpperCase()}</span>
                          </Pressable>
                        );
                      })}
                    </div>
                  </div>

                  <div style={fieldGroup()}>
                    <span style={fieldLabel}>{t("settings.fontChoice.label", "Police d'écriture")}</span>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {FONT_CHOICE_ORDER.map((id) => {
                        const active = fontChoiceId === id;
                        return (
                          <Pressable key={id} onClick={() => setFontChoiceId(id)} style={{
                            display: "flex", alignItems: "center", gap: 12, padding: "11px 13px", borderRadius: 14,
                            background: active ? "rgba(54,79,231,.1)" : P.soft, border: `1.5px solid ${active ? A.blue : "transparent"}`,
                          }}>
                            <span style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`, fontWeight: 700, flexShrink: 0, width: 30, fontFamily: FONT_CHOICES[id].stack, color: active ? A.blue : P.ink }}>Aa</span>
                            <strong style={{ flex: 1, minWidth: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: active ? A.blue : P.ink }}>{t(`settings.fontChoice.${id}`, FONT_CHOICES[id].label)}</strong>
                            {active && <span style={{ color: A.blue, fontWeight: 900 }}>✓</span>}
                          </Pressable>
                        );
                      })}
                    </div>
                  </div>

                  <div style={fieldGroup()}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{t("settings.boldText.label", "Texte en gras")}</strong>
                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("settings.boldText.subtitle", "Renforce le contraste du texte dans toute l'app.")}</span>
                      </div>
                      <Pressable onClick={() => setBoldText((v) => !v)} aria-label={t("settings.boldText.label", "Texte en gras")} style={{
                        width: 44, height: 26, borderRadius: 99, background: boldText ? A.blue : P.line, position: "relative", flex: "0 0 auto",
                      }}>
                        <span style={{ position: "absolute", top: 2.5, left: boldText ? 20 : 3, width: 21, height: 21, borderRadius: 99, background: "#fff", transition: "left .18s ease", boxShadow: "0 1px 3px rgba(0,0,0,.3)" }} />
                      </Pressable>
                    </div>
                  </div>

                </div>
              </div></div>
            </section>

            {/* ===== Notifications ===== */}
            <section style={{ ...card, borderRadius: 20, marginBottom: 9, overflow: "hidden" }}>
              {renderAccHeader("notifs", "🔔", "rgba(231,138,18,.16)", A.orange, t("settings.notifications.title", "Notifications"), notifSubtitle)}
              <div style={accWrap("notifs")}><div style={{ overflow: "hidden" }}>
                <div style={{ padding: "2px 14px 15px", borderTop: `1px solid ${P.line}`, margin: "0 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "12px 0" }}>
                    <span style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(54,79,231,.12)", display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>🔔</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block" }}>{t("settings.notifications.title", "Notifications")}</strong>
                      <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("settings.notifications.subtitle", "Alertes push pour messages, tâches et infos importantes.")}</span>
                    </div>
                    <Pressable onClick={togglePush} style={{
                      width: 42, height: 25, borderRadius: 99, background: pushEnabled ? A.green : P.line, position: "relative", flex: "0 0 auto", opacity: pushBusy ? .6 : 1,
                    }}>
                      <span style={{ position: "absolute", top: 2.5, left: pushEnabled ? 19 : 3, width: 20, height: 20, borderRadius: 99, background: "#fff", transition: "left .18s ease", boxShadow: "0 1px 3px rgba(0,0,0,.3)" }} />
                    </Pressable>
                  </div>
                  {pushEnabled && (
                    <Pressable onClick={sendTestPush} style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.blue, background: "rgba(54,79,231,.1)", borderRadius: 99, padding: "7px 12px", marginTop: 2, marginBottom: 4, display: "inline-block" }}>{t("settings.push.testLabel", "Envoyer un test")}</Pressable>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "12px 0", borderTop: `1px solid ${P.line}` }}>
                    <span style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(231,138,18,.14)", display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>🎈</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block" }}>{t("settings.banners.title", "Bannières de notification")}</strong>
                      <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("settings.banners.subtitle", "Petit encart en haut de l'écran à l'arrivée d'une notification (en plus de la cloche).")}</span>
                    </div>
                    <Pressable onClick={toggleBanners} style={{
                      width: 42, height: 25, borderRadius: 99, background: bannersEnabled ? A.green : P.line, position: "relative", flex: "0 0 auto",
                    }}>
                      <span style={{ position: "absolute", top: 2.5, left: bannersEnabled ? 19 : 3, width: 20, height: 20, borderRadius: 99, background: "#fff", transition: "left .18s ease", boxShadow: "0 1px 3px rgba(0,0,0,.3)" }} />
                    </Pressable>
                  </div>
                </div>
              </div></div>
            </section>

            {/* ===== Compte & sécurité ===== */}
            <section style={{ ...card, borderRadius: 20, marginBottom: 9, overflow: "hidden" }}>
              {renderAccHeader("account", "🔒", "rgba(239,68,68,.14)", A.red, t("settings.section.account.headerTitle", "Compte & sécurité"), accountSubtitle)}
              <div style={accWrap("account")}><div style={{ overflow: "hidden" }}>
                <div style={{ padding: "2px 14px 4px", borderTop: `1px solid ${P.line}`, margin: "0 14px" }}>
                  {subRow("🔑", "rgba(54,79,231,.12)", A.blue, t("settings.changePassword.title", "Changer mon mot de passe"), t("settings.changePassword.subtitle", "Modifier le mot de passe du compte connecté"), <span style={{ color: P.muted, fontSize: `calc(15px * var(--nx-font-scale, 1))` }}>›</span>, openPasswordSheet)}
                  {subRow("👥", "rgba(30,184,95,.14)", A.green, t("settings.sessions.title", "Sessions actives"), t("settings.sessions.subtitle", "Qui est connecté en ce moment"),
                    adminSessions.length > 0 ? <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: A.green, borderRadius: 99, padding: "3px 8px", flex: "0 0 auto" }}>{t("settings.sessions.onlineCount", { n: onlineCount }, `${onlineCount} en ligne`)}</span> : null,
                    () => setSheet("sessions"))}
                  {/* Face ID/Touch ID (retour utilisateur explicite, 2026-08-29) — jusqu'ici
                      seulement proposé une fois juste après une connexion par mot de passe
                      (voir doLogin, sheet "webauthnOffer"), sans moyen d'y revenir ni de voir
                      quels appareils ont un accès actif. Toujours affiché (contrairement à
                      "Sessions actives", jamais gardé par un rôle — une capacité personnelle
                      de compte, pas une permission). */}
                  {subRow("🔐", "rgba(139,47,214,.13)", A.purple, t("settings.webauthn.title", "Face ID / Touch ID"),
                    webauthnHint?.login === user?.proId ? t("settings.webauthn.enabledSubtitle", "Activé sur cet appareil") : t("settings.webauthn.disabledSubtitle", "Non activé sur cet appareil"),
                    <span style={{ color: P.muted, fontSize: `calc(15px * var(--nx-font-scale, 1))` }}>›</span>,
                    () => { loadWebauthnDevices(); setSheet("webauthnManage"); })}
                </div>
              </div></div>
            </section>

            {/* ===== Réseau ===== */}
            <section style={{ ...card, borderRadius: 20, marginBottom: 9, overflow: "hidden" }}>
              {renderAccHeader("network", "🏬", "rgba(30,184,95,.16)", A.green, t("settings.section.network.kicker", "Réseau"), networkSubtitle)}
              <div style={accWrap("network")}><div style={{ overflow: "hidden" }}>
                <div style={{ padding: "2px 14px 4px", borderTop: `1px solid ${P.line}`, margin: "0 14px" }}>
                  {isNetworkLeader
                    ? subRow("🏬", "rgba(30,184,95,.14)", A.green, t("settings.myStores.title", "Mes filiales"), t("settings.myStores.subtitle", "Filiales sous ta responsabilité, ajouter/retirer au besoin."),
                        <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: A.blue, borderRadius: 99, padding: "3px 8px", flex: "0 0 auto" }}>{storesCount}</span>,
                        openMyStores)
                    : subRow("🧭", "rgba(139,47,214,.13)", A.purple, t("settings.myLeader.title", "Ton responsable réseau"), networkSubtitle, null, null)}
                  {/* "Mon équipe" (retour utilisateur 2026-08-23, "Gestion de mon équipe"
                      scopée pour BL/VKL/NL, jamais le panneau Administration de
                      NEXORA_ADMIN — voir server.js, la faille roles-permissions corrigée
                      juste avant) — VKL/NL uniquement, un BL n'a pas de subordonné réseau.
                      Raccourci vers l'onglet Équipe déjà construit (Pilotage réseau >
                      Analyse), jamais un écran dupliqué. */}
                  {["Verkaufsleiter", "Niederlassungsleiter"].includes(user?.role) && subRow(
                    "🧑‍💼", "rgba(139,47,214,.13)", A.purple, t("settings.myTeam.title", "Mon équipe"),
                    t("settings.myTeam.subtitle", "Créer/gérer tes responsables directs"),
                    <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: A.purple, borderRadius: 99, padding: "3px 8px", flex: "0 0 auto" }}>{adminUsers.filter((u) => u.reportsTo === user?.proId).length}</span>,
                    () => { setScreen("home"); setPilotScreen("analyse"); setAnalyseTab("equipe"); }
                  )}
                  {/* "Mon équipe filiale" (retour utilisateur explicite, 2026-08-29 : "juste
                      qu'il puisse faire ces choses", jamais un accès au module Admin même
                      partiel) — écran dédié, jamais le panneau Administration. */}
                  {isFlOrFlaRole && subRow(
                    "🧑‍💼", "rgba(139,47,214,.13)", A.purple, t("flTeam.settingsRow.title", "Mon équipe filiale"),
                    t("flTeam.settingsRow.subtitle", "Ouvertures de compte, mots de passe, équipe"),
                    adminPending.length > 0 ? <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: A.purple, borderRadius: 99, padding: "3px 8px", flex: "0 0 auto" }}>{adminPending.length}</span> : null,
                    () => setScreen("flTeam")
                  )}
                </div>
              </div></div>
            </section>

            {/* ===== Administration ===== */}
            {canViewUsers && (
              <section style={{ ...card, borderRadius: 20, marginBottom: 9, overflow: "hidden" }}>
                {renderAccHeader("admin", "🛡️", "rgba(139,47,214,.16)", A.purple, t("settings.section.admin.title", "Gestion Nexora"), adminSubtitle)}
                <div style={accWrap("admin")}><div style={{ overflow: "hidden" }}>
                  <div style={{ padding: "2px 14px 4px", borderTop: `1px solid ${P.line}`, margin: "0 14px" }}>
                    {subRow("👥", "rgba(54,79,231,.12)", A.blue, t("settings.users.title", "Utilisateurs"), t("settings.users.subtitle", "Comptes, rôles et filiales"), <span style={{ color: P.muted, fontSize: `calc(15px * var(--nx-font-scale, 1))` }}>›</span>, () => { setAdminScreen("users"); setScreen("admin"); })}
                    {subRow("🛡️", "rgba(231,138,18,.14)", A.orange, t("settings.roles.title", "Rôles & Permissions"), t("settings.roles.subtitle", "Contrôle des droits par rôle"), <span style={{ color: P.muted, fontSize: `calc(15px * var(--nx-font-scale, 1))` }}>›</span>, () => { setAdminScreen("roles"); setScreen("admin"); })}
                    {/* Filiales — jamais pour un simple compte "employé" même s'il a users.view
                        (ex. FL/FLA), réservé à NEXORA_ADMIN et à la vue GR (retour utilisateur
                        explicite : "supprimer la section filiale de administration, seulement
                        visible pour admin et vue GR"). */}
                    {(user?.role === "NEXORA_ADMIN" || isNetworkLeader) && subRow("🏬", "rgba(30,184,95,.13)", A.green, t("settings.section.network.storesTitle", "Filiales"), t("settings.stores.createdCount", { n: storesCatalog.length }, `${storesCatalog.length} filiale${storesCatalog.length > 1 ? "s" : ""} créée${storesCatalog.length > 1 ? "s" : ""} dans le registre.`), <span style={{ color: P.muted, fontSize: `calc(15px * var(--nx-font-scale, 1))` }}>›</span>, () => { setAdminScreen("stores"); setScreen("admin"); })}
                    {subRow("🎌", "rgba(239,68,68,.12)", A.red, t("settings.holidays.title", "Jours fériés"), t("settings.holidays.subtitle", "Land de la filiale et calendrier"), <span style={{ color: P.muted, fontSize: `calc(15px * var(--nx-font-scale, 1))` }}>›</span>, () => { setPlanScreen("holidays"); setScreen("planning"); })}
                    <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "11px 0", borderTop: `1px solid ${P.line}` }}>
                      <span style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(239,68,68,.12)", display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>🗑️</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block" }}>{t("settings.krankRetention.title", "Krankmeldung — rétention")}</strong>
                        <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("settings.krankRetention.subtitle", "Jours avant purge des dossiers clôturés/annulés")}</span>
                      </div>
                      <input type="number" min="30" max="3650" value={krankRetentionDays} onChange={(e) => setKrankRetentionDays(Number(e.target.value))}
                        style={{ ...inputStyle, width: 56, textAlign: "center", padding: "7px 3px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }} />
                      <Pressable onClick={saveKrankRetention} style={{
                        fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "7px 10px", whiteSpace: "nowrap", flex: "0 0 auto",
                        color: krankRetentionSaved ? "#fff" : A.orange, background: krankRetentionSaved ? A.green : "rgba(231,138,18,.13)",
                      }}>{krankRetentionSaved ? t("settings.krankRetention.saved", "Enregistré ✓") : t("settings.password.save", "Enregistrer")}</Pressable>
                    </div>
                  </div>
                </div></div>
              </section>
            )}

            <section style={{ ...card, ...tile, flexDirection: "column", alignItems: "center", textAlign: "center", padding: 18, marginBottom: 12, marginTop: 9 }}>
              <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{t("settings.about.version", "Nexora V4")}</strong>
              <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("settings.about.tagline", "Auth + Permissions stable")}</span>
            </section>
            <Pressable onClick={logout} style={{
              width: "100%", textAlign: "center", padding: "14px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
              color: A.red, background: "rgba(239,68,68,.12)",
            }}>{t("home.menu.logout", "Déconnexion")}</Pressable>
          </div>
          );
        })()}
      </main>

      {/* Aperçu du jour (glissement Tägliche Erfassung) — déclaré ICI, hors <main>, même
          raison que la popup Stundenleistung juste en dessous (voir son commentaire) :
          nichée dans <main> (-webkit-overflow-scrolling:touch), la bulle position:fixed
          était réinterprétée par WebKit iOS comme relative au conteneur défilant, donc
          invisible dès que la liste n'était plus tout en haut — EXACTEMENT le bug que ce
          repositionnement était censé corriger (retour utilisateur explicite, 2026-08-21 :
          "la notif du jour... s'affiche toujours qu'en haut" persistait après la 1ère
          tentative en position:fixed imbriquée dans <main>, qui ne suffisait pas sur
          WebKit réel — seul un vrai test navigateur desktop ne reproduit pas ce piège). */}
      {screen === "reporting" && repScreen === "daily" && dailySwipeHint && (
        <div style={{
          position: "fixed", top: "calc(74px + env(safe-area-inset-top))", left: "50%", transform: "translateX(-50%)", zIndex: 70,
          background: "rgba(13,23,48,.88)", color: "#fff", fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
          padding: "8px 18px", borderRadius: 99, boxShadow: "0 8px 20px rgba(0,0,0,.25)",
          animation: "nxDailySwipeHint .55s ease forwards", pointerEvents: "none",
        }}>{dailySwipeHint}</div>
      )}

      {/* Popup premium de saisie/correction Ist (Stundenleistung) — remplace l'ancien
          formulaire fixe en bas de page. Déclarée ICI (hors <main>), comme toutes les
          autres Sheet de l'app : une Sheet position:fixed imbriquée dans <main> (qui a
          -webkit-overflow-scrolling:touch) se fait réinterpréter par WebKit iOS comme
          positionnée par rapport au conteneur scrollable plutôt qu'au vrai viewport —
          la nav du bas semblait alors "cacher" le bouton Enregistrer (retour utilisateur). */}
      <Sheet open={stundenIstEditDate != null} onClose={() => { setStundenIstEditDate(null); setStundenIstForm({ umsatz: "", heures: "" }); }} P={P} maxH="70%">
        {stundenIstEditDate && (() => {
          const editedDay = stundenComputed().find((x) => x.date === stundenIstEditDate);
          const dateLabel = new Date(`${stundenIstEditDate}T12:00:00`).toLocaleDateString(intlLocale, { weekday: "long", day: "2-digit", month: "long" });
          return (
            <>
              <SheetHead kicker="Stundenleistung" title={dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1)}
                onClose={() => { setStundenIstEditDate(null); setStundenIstForm({ umsatz: "", heures: "" }); }} />
              <section style={{ ...colorCard(A.frost), ...tile, flexDirection: "column", alignItems: "stretch", padding: 16, marginBottom: 16 }}>
                <span style={onColorTag}>{t("reporting.planReferenceTag", "Repère — Plan du jour")}</span>
                <h2 style={{ margin: "5px 0 0", fontSize: `calc(20px * var(--nx-font-scale, 1))`, color: "#fff" }}>
                  {editedDay?.umsatzPlanTag != null ? `${editedDay.umsatzPlanTag.toLocaleString("fr-FR")} €` : "—"}
                  {" · "}
                  {editedDay?.stundenPlanTag != null ? `${editedDay.stundenPlanTag.toLocaleString("fr-FR")} h` : "—"}
                </h2>
              </section>
              <Field label={t("reporting.umsatzOfDayLabel", "Umsatz du jour (€)")}>
                <input style={inputStyle} inputMode="decimal" placeholder="0,00" value={stundenIstForm.umsatz} onChange={(e) => setStundenIstForm({ ...stundenIstForm, umsatz: e.target.value })} />
              </Field>
              <Field label={t("reporting.hoursWorkedLabel", "Heures travaillées")}>
                <input style={inputStyle} inputMode="decimal" placeholder="0" value={stundenIstForm.heures} onChange={(e) => setStundenIstForm({ ...stundenIstForm, heures: e.target.value })} />
              </Field>
              <Pressable onClick={() => saveStundenIst(stundenIstEditDate)} style={{ width: "100%", ...btnSolid(A.frost) }}>{t("reporting.saveEntry", "Enregistrer la saisie")}</Pressable>
            </>
          );
        })()}
      </Sheet>

      {/* ---------- BARRE DE COMPOSITION MESSAGES ---------- */}
      {screen === "messages" && msgScreen === "thread" && (
        <section style={keyboardOpen ? {
          // Comportement NATIF (façon WhatsApp), plus aucun calcul JS de position — voir le
          // commentaire complet sur messagesThreadActiveRef plus haut : --nx-vh suit
          // maintenant réellement le clavier sur cet écran, donc la colonne flex de l'app
          // (mainAppRef, display:flex/flexDirection:column) rétrécit à la vraie zone
          // visible, et ce <section>, redevenu un simple enfant flex normal juste après
          // <main>, se retrouve mécaniquement collé juste au-dessus du clavier — comme le
          // ferait le navigateur lui-même pour n'importe quelle barre de saisie native.
          // Pas besoin de maxWidth/margin:auto ici : mainAppRef (l'ancêtre flex-column)
          // plafonne déjà toute l'app à 520px et la centre, ce <section> hérite donc
          // naturellement de cette largeur déjà bornée.
          position: "static", flex: "0 0 auto", margin: "0 10px", zIndex: 30,
          background: P.card, border: `1px solid ${P.line}`, borderRadius: 22, boxShadow: P.deepShadow,
          overflow: "hidden", marginBottom: "env(safe-area-inset-bottom)",
        } : {
          position: "fixed", left: 10, right: 10,
          bottom: "calc(88px + env(safe-area-inset-bottom))",
          zIndex: 30, maxWidth: 500, margin: "0 auto", transition: "bottom .2s ease",
          background: P.card, border: `1px solid ${P.line}`, borderRadius: 22, boxShadow: P.deepShadow,
          overflow: "hidden",
        }}>
          {(msgReplyTo || msgEditId) && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderBottom: `1px solid ${P.line}`, background: P.soft }}>
              <span style={{ width: 3, alignSelf: "stretch", borderRadius: 99, background: A.frost, flex: "0 0 auto" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: A.frost, display: "block" }}>{msgEditId ? "Modifier le message" : `Répondre à ${msgReplyTo.from}`}</strong>
                {msgReplyTo && <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{msgReplyTo.preview}</span>}
              </div>
              <Pressable onClick={cancelComposerMode} aria-label={t("messages.cancelBtn", "Annuler")} style={{ width: 24, height: 24, borderRadius: 99, background: P.card, display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, flex: "0 0 auto" }}>✕</Pressable>
            </div>
          )}
          {msgPendingAttachments.length > 0 && (
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", padding: "9px 12px", borderBottom: `1px solid ${P.line}`, background: P.soft }}>
              {msgPendingAttachments.map((a) => (
                <span key={a.id} style={{ display: "flex", alignItems: "center", gap: 6, background: P.card, border: `1px solid ${P.line}`, borderRadius: 99, padding: "5px 8px 5px 10px", fontSize: `calc(11px * var(--nx-font-scale, 1))`}}>
                  📎 {a.originalName.length > 18 ? a.originalName.slice(0, 15) + "…" : a.originalName}
                  <Pressable onClick={() => removePendingAttachment(a.id)} aria-label={t("messages.removeBtn", "Retirer")} style={{ width: 16, height: 16, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(9px * var(--nx-font-scale, 1))`, color: P.muted }}>✕</Pressable>
                </span>
              ))}
            </div>
          )}
          <div style={{ padding: 10, display: "flex", gap: 8, alignItems: "center" }}>
            {!msgEditId && (
              <>
                <input ref={msgAttachmentInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,application/pdf" multiple style={{ display: "none" }}
                  onChange={(e) => { uploadMsgAttachment(e.target.files); e.target.value = ""; }} />
                <Pressable onClick={() => msgAttachmentInputRef.current?.click()} aria-label={t("messages.attachFileAria", "Joindre une pièce jointe")} disabled={msgUploadingAttachment} style={{
                  width: 40, height: 40, borderRadius: 99, flex: "0 0 auto", background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`, opacity: msgUploadingAttachment ? .5 : 1,
                }}>{msgUploadingAttachment ? "…" : "📎"}</Pressable>
              </>
            )}
            <input style={{ ...inputStyle, flex: 1, background: P.soft, border: "none" }} placeholder={t("messages.composerPlaceholder", "Écrire un message...")} value={msgDraft}
              onChange={(e) => setMsgDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendMessage(); }}
              onFocus={() => {
                // Filet de sécurité résiduel (bug initial 2026-08-06 "la page remonte") —
                // désormais surtout redondant avec le passage en flux normal (voir plus
                // haut, keyboardOpen ? position:"static") qui règle la cause probable de
                // fond, mais gardé au cas où un navigateur tenterait quand même un
                // scroll-into-view avant que --nx-vh n'ait fini de se mettre à jour.
                const el = mainScrollRef.current;
                if (!el) return;
                const y = el.scrollTop;
                requestAnimationFrame(() => { if (el.scrollTop !== y) el.scrollTop = y; });
                setTimeout(() => { if (el.scrollTop !== y) el.scrollTop = y; }, 350);
              }} />
            <Pressable onClick={sendMessage} aria-label={t("messages.sendAria", "Envoyer")} style={{
              width: 44, height: 44, borderRadius: 99, flex: "0 0 auto", color: "#fff",
              background: `linear-gradient(155deg, rgba(255,255,255,.22), rgba(255,255,255,0) 55%), ${A.frost}`,
              boxShadow: `0 4px 12px ${A.frost}55`,
              display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
            }}>{msgEditId ? "✓" : "➤"}</Pressable>
          </div>
        </section>
      )}

      {/* ---------- BARRE D'ACTION DU RUN — remplace la tab bar pendant le run,
          fidèle au vrai native-actionbar (‹ précédent | + Nouvelle MHD | suivant ›),
          boutons agrandis pour une bonne prise en main d'une seule main. ---------- */}
      {inRun && article && !keyboardOpen && (
        <nav style={{
          position: "fixed", left: 14, right: 14, bottom: "calc(12px + env(safe-area-inset-bottom))",
          zIndex: 32, maxWidth: 492, margin: "0 auto",
          display: "flex", alignItems: "center", gap: 9,
          background: P.tabBg, backdropFilter: "blur(26px) saturate(1.6)", WebkitBackdropFilter: "blur(26px) saturate(1.6)",
          border: `1px solid ${P.tabBorder}`, borderRadius: 30, padding: "9px",
          // P.shadow (pas deepShadow, bien plus large et opaque — 50px de flou visible
          // comme une tache sombre sous la pilule flottante, retour utilisateur
          // 2026-07-31, capture confirmée à l'écran) : gabarit déjà existant, plus discret.
          boxShadow: `${P.shadow}, inset 0 1px 0 rgba(255,255,255,${themeId === "sombre" ? ".07" : ".65"})`,
        }}>
          <Pressable feedback onClick={prevArticle} aria-label={t("controlRun.prevArticleAria", "Article précédent")} disabled={runIndex === 0} style={{
            width: 60, height: 60, borderRadius: 20, display: "grid", placeItems: "center", fontSize: `calc(26px * var(--nx-font-scale, 1))`, fontWeight: 700,
            background: P.soft, color: runIndex === 0 ? P.line : P.ink, flex: "0 0 auto",
          }}>‹</Pressable>
          <Pressable onClick={() => openEditSheet(null)} style={{
            flex: 1, textAlign: "center", padding: "18px 0", borderRadius: 20, fontWeight: 800, fontSize: `calc(14.5px * var(--nx-font-scale, 1))`,
            background: A.green, color: "#fff", boxShadow: "0 10px 22px rgba(30,184,95,.4)",
            animation: saveFlash ? "nxSaveFlash .5s ease" : "none",
          }}>＋ Nouvelle MHD</Pressable>
          <Pressable feedback onClick={nextArticle} aria-label={t("controlRun.nextArticleAria", "Article suivant")} style={{
            width: 60, height: 60, borderRadius: 20, display: "grid", placeItems: "center", fontSize: `calc(26px * var(--nx-font-scale, 1))`, fontWeight: 700,
            background: P.soft, color: P.ink, flex: "0 0 auto",
          }}>›</Pressable>
        </nav>
      )}

      {/* ---------- TAB BAR iOS PREMIUM — glissable au doigt, bulle discrète. Masquée pendant le run, et pendant
         la composition d'un message (remplacée par la barre de saisie, cf. plus haut) — PAS pour tout clavier
         ouvert sur n'importe quel écran comme avant : ce masquage global basé sur visualViewport.resize faisait
         disparaître la nav sur tout écran contenant un champ de saisie, et pouvait rester bloqué invisible en
         permanence si resize ne se redéclenchait pas proprement à la fermeture du clavier (WebView Android). ---------- */}
      {/* Commandes de marchandises — écran "article" masque aussi cette pilule flottante
          (retour utilisateur explicite : "page plein écran sans barre de navigation") ;
          bug réel trouvé par vérification : sans cette exclusion, la pilule reste au-dessus
          (zIndex 32) et intercepte le clic sur le gros chevron "›" en bas d'écran. */}
      {!inRun && !(keyboardOpen && screen === "messages" && msgScreen === "thread") && !(screen === "merchandise" && merchScreen === "article") && !(keyboardOpen && screen === "labels" && labelsScreen === "form") && !(keyboardOpen && screen === "inventurmassnahme" && imScreen !== "hub") && (() => {
        const activeTabIndex = TABS.findIndex((t) =>
          t.id === "home" ? screen === "home" :
          t.id === "netHome" ? screen === "home" && (pilotScreen === "today" || pilotScreen === "analyse") :
          t.id === "filiales" ? screen === "home" && pilotScreen === "filiales" :
          t.id === "alertes" ? screen === "home" && pilotScreen === "alertes" :
          t.id === "missions" ? screen === "home" && pilotScreen === "missions" :
          t.id === "control" ? inControl :
          t.id === "reductions" ? screen === "reductions" :
          t.id === "dashboard" ? screen === "dashboard" :
          screen === "other" && otherTab === t.id
        );
        // Pendant un glissement, la position ET la couleur suivent le doigt en continu ;
        // hors glissement, elles retombent sur l'onglet réellement actif (route courante).
        const dragProgress = tabDrag ? Math.min(TABS.length - 0.001, Math.max(0, (tabDrag.x / tabDrag.width) * TABS.length)) : null;
        const bubblePos = dragProgress != null ? dragProgress : (activeTabIndex === -1 ? 0 : activeTabIndex);
        const colorIndex = dragProgress != null ? Math.floor(dragProgress) : (activeTabIndex === -1 ? 0 : activeTabIndex);
        const bubbleColor = TABS[colorIndex]?.color || A.blue;
        const bubbleVisible = dragProgress != null || activeTabIndex !== -1;

        const navigateToTabIndex = (idx) => {
          const t = TABS[idx]; if (!t) return;
          // Retour utilisateur explicite (2026-08-16, "la barre de navigation est sans son") :
          // cette nav mobile ne passe jamais par Pressable (bulle glissante au doigt, gérée en
          // pointer events bruts sur le <nav> entier — voir onTabPointerDown/endTabDrag
          // ci-dessus), donc le flip par défaut de Pressable ne pouvait pas l'atteindre.
          // hapticTap() posé ici, au seul point réel de navigation.
          hapticTap();
          if (t.id === "home") setScreen("home");
          else if (t.id === "netHome") { setScreen("home"); setPilotScreen("today"); }
          else if (t.id === "filiales") { setScreen("home"); setPilotScreen("filiales"); }
          else if (t.id === "alertes") { setScreen("home"); setPilotScreen("alertes"); }
          else if (t.id === "missions") { setScreen("home"); setPilotScreen("missions"); }
          else if (t.id === "control") setScreen("hub");
          else if (t.id === "reductions") setScreen("reductions");
          else if (t.id === "dashboard") { setReportingWeekOffset(0); setPlanWeekOffset(0); loadStundenleistung(0); loadCaCompare(); loadDashboardExtras(); setScreen("dashboard"); }
          else { setOtherTab(t.id); setScreen("other"); }
        };
        const posFromEvent = (e) => {
          const rect = tabBarRef.current.getBoundingClientRect();
          const clientX = e.touches ? e.touches[0].clientX : e.clientX;
          return { x: Math.min(Math.max(clientX - rect.left, 0), rect.width), width: rect.width };
        };
        const onTabPointerDown = (e) => {
          try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {}
          setTabDrag(posFromEvent(e));
        };
        const onTabPointerMove = (e) => { if (tabDrag) setTabDrag(posFromEvent(e)); };
        const endTabDrag = (e) => {
          if (!tabDrag) return;
          const raw = (tabDrag.x / tabDrag.width) * TABS.length;
          navigateToTabIndex(Math.min(TABS.length - 1, Math.max(0, Math.floor(raw))));
          setTabDrag(null);
        };

        // Ossature desktop (Phase 1) : dock latéral vertical fixé au vrai bord gauche du
        // viewport, remplace la pilule glissante (aucun sens à la souris) — même source
        // TABS/activeTabIndex/navigateToTabIndex, juste un rendu différent.
        if (isWide) {
          return (
            <nav style={{
              position: "fixed", left: 0, top: 0, bottom: 0, width: SIDEBAR_WIDTH, zIndex: 32, boxSizing: "border-box",
              display: "flex", flexDirection: "column", alignItems: "stretch", gap: 3, padding: "18px 10px",
              background: P.tabBg, backdropFilter: "blur(26px) saturate(1.6)", WebkitBackdropFilter: "blur(26px) saturate(1.6)",
              borderRight: `1px solid ${P.tabBorder}`, overflowY: "auto",
            }}>
              {TABS.map((t, i) => {
                const highlighted = i === activeTabIndex;
                return (
                  <Pressable key={t.id} onClick={() => navigateToTabIndex(i)} style={{
                    display: "flex", alignItems: "center", gap: 11, padding: "10px 12px", borderRadius: 14,
                    background: highlighted ? `${t.color}1f` : "transparent",
                    border: `1px solid ${highlighted ? `${t.color}40` : "transparent"}`,
                  }}>
                    <span style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`, lineHeight: 1 }}>{t.id === "control" ? <ShieldIcon size={19} /> : <EmojiIcon e={t.emoji} size={19} />}</span>
                    <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: highlighted ? 800 : 650, color: highlighted ? t.color : P.ink }}>{t.label}</span>
                  </Pressable>
                );
              })}
            </nav>
          );
        }
        return (
          <nav ref={tabBarRef} className="nx-bottomnav"
            onPointerDown={onTabPointerDown} onPointerMove={onTabPointerMove}
            onPointerUp={endTabDrag} onPointerCancel={() => setTabDrag(null)}
            style={{
              position: "fixed", left: 14, right: 14, bottom: "calc(12px + env(safe-area-inset-bottom))",
              zIndex: 32, maxWidth: 492, margin: "0 auto", touchAction: "none",
              display: "flex", alignItems: "stretch", justifyContent: "space-between",
              // saturate(1.1), pas 1.6 (2026-08-31, retour utilisateur avec capture : un
              // bandeau plein très saturé — ex. Notifications — qui défile juste derrière
              // se voyait nettement à travers le flou et teintait visiblement toute la
              // pilule). 1.6 gardait le flou "vivant" plutôt que gris et plat, mais
              // amplifiait aussi n'importe quelle couleur saturée en dessous ; comparé en
              // artefact avant/après (curseur de saturation) — 1.1 atténue nettement l'effet
              // sans rendre le flou plat. Un seul endroit à corriger (le seul vrai
              // ".nx-bottomnav" de l'app) — les autres barres fixes translucides du même
              // patron visuel (Merchandise, etc.) sont des éléments distincts, hors du
              // signalement, jamais touchées ici.
              background: P.tabBg, backdropFilter: "blur(26px) saturate(1.1)", WebkitBackdropFilter: "blur(26px) saturate(1.1)",
              border: `1px solid ${P.tabBorder}`, borderRadius: 30, padding: "9px 10px",
              // P.shadow (pas deepShadow, bien plus large et opaque — 50px de flou visible
          // comme une tache sombre sous la pilule flottante, retour utilisateur
          // 2026-07-31, capture confirmée à l'écran) : gabarit déjà existant, plus discret.
          boxShadow: `${P.shadow}, inset 0 1px 0 rgba(255,255,255,${themeId === "sombre" ? ".07" : ".65"})`,
            }}>
            {/* Bulle glissante — discrète (pas de fond plein), se teinte de la couleur de l'élément survolé */}
            <div aria-hidden="true" style={{
              position: "absolute", top: 9, bottom: 9, left: 10,
              width: `calc((100% - 20px) / ${TABS.length})`,
              borderRadius: 22, pointerEvents: "none",
              background: `${bubbleColor}1f`,
              border: `1px solid ${bubbleColor}40`,
              opacity: bubbleVisible ? 1 : 0,
              transform: `translateX(${bubblePos * 100}%)`,
              transition: tabDrag ? "background .12s ease, border-color .12s ease" : "transform .45s cubic-bezier(.22,1,.36,1), background .3s ease, opacity .3s ease",
            }} />
            {TABS.map((t, i) => {
              const highlighted = bubbleVisible && i === colorIndex;
              return (
                <div key={t.id} style={{
                  flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3,
                  padding: "3px 0", position: "relative", zIndex: 1, pointerEvents: "none",
                }}>
                  <span style={{
                    fontSize: `calc(19px * var(--nx-font-scale, 1))`, lineHeight: 1, display: "block",
                    transform: highlighted ? "scale(1.1) translateY(-1px)" : "scale(1)",
                    transition: "transform .28s cubic-bezier(.34,1.4,.64,1)",
                  }}>{t.id === "control" ? <ShieldIcon size={19} /> : <EmojiIcon e={t.emoji} size={19} />}</span>
                  <span style={{
                    fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: highlighted ? 800 : 650, letterSpacing: ".01em",
                    color: highlighted ? t.color : P.muted, transition: "color .25s ease",
                  }}>{t.label}</span>
                </div>
              );
            })}
          </nav>
        );
      })()}

      {/* ---------- SHEETS ---------- */}
      <Sheet open={sheet === "edit"} onClose={() => setSheet(null)} P={P}>
        <SheetHead kicker={editId ? t("controlRun.editMhdKicker", "Modifier la MHD") : t("controlRun.newMhdKicker", "Nouvelle MHD")} title={editId ? t("controlRun.editMhdFormTitle", "Modifier la date") : t("controlRun.newMhdFormTitle", "Ajouter une date")} onClose={() => setSheet(null)} />
        <Field label={t("controlRun.dateMadLabel", "Date MAD")}>
          <input style={dateTimeInputStyle} type="date" min={todayISO()} value={editForm.mhd}
            onChange={(e) => setEditForm({ ...editForm, mhd: e.target.value })} />
        </Field>
        <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 15, paddingBottom: 2 }}>
          {QUICK_DAYS.map((n) => {
            const iso = addDays(n);
            const sel = editForm.mhd === iso;
            const u = urgency(iso);
            return (
              <Pressable key={n} onClick={() => setEditForm({ ...editForm, mhd: iso })} style={{
                flex: "0 0 auto", padding: "8px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                background: sel ? urgColor[u] : P.soft, color: sel ? "#fff" : P.ink,
                border: `1.5px solid ${sel ? urgColor[u] : P.line}`,
              }}>+{n}j</Pressable>
            );
          })}
        </div>
        <Field label={t("controlRun.quantityLabel", "Quantité")}>
          <input style={inputStyle} inputMode="numeric" placeholder={t("controlRun.qtyPlaceholder", "Qté")} value={editForm.qty}
            onChange={(e) => setEditForm({ ...editForm, qty: e.target.value })} />
        </Field>
        <Field label={t("controlRun.remarkLabel", "Remarque")}>
          <input style={inputStyle} placeholder={t("controlRun.remarkPlaceholder", "Remarque facultative")} value={editForm.note}
            onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} />
        </Field>
        {editId && (
          <Pressable onClick={deleteEdit} style={{
            width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, marginBottom: 10,
            background: "rgba(239,68,68,.14)", color: A.red, fontWeight: 700, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
          }}>{t("controlRun.deleteMhdEntryBtn", "Supprimer cette MHD")}</Pressable>
        )}
        <div style={{ display: "flex", gap: 10 }}>
          <Pressable onClick={() => setSheet(null)} style={{
            flex: 1, textAlign: "center", padding: "14px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14.5px * var(--nx-font-scale, 1))`,
            background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
          }}>{t("common.cancel", "Annuler")}</Pressable>
          <Pressable onClick={saveEdit} style={{ flex: 1, ...btnSolid(A.blue) }}>{t("common.save", "Enregistrer")}</Pressable>
        </div>
      </Sheet>

      <Sheet open={sheet === "newArtMhd"} onClose={() => setSheet(null)} P={P}>
        <SheetHead kicker={newArtForm.name} title={newArtForm.recordId ? t("controlRun.editDlcFormTitle", "Modifier la DLC") : t("controlRun.newMhdModeLabel", "Nouvelle DLC")} onClose={() => setSheet(null)} />
        <Field label={t("controlStore.fieldDateMhd", "Date DLC")}>
          <input style={dateTimeInputStyle} type="date" min={todayISO()} value={newArtForm.mhd}
            onChange={(e) => setNewArtForm({ ...newArtForm, mhd: e.target.value })} />
        </Field>
        <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 15, paddingBottom: 2 }}>
          {QUICK_DAYS.map((n) => {
            const iso = addDays(n);
            const sel = newArtForm.mhd === iso;
            const u = urgency(iso);
            return (
              <Pressable key={n} onClick={() => setNewArtForm({ ...newArtForm, mhd: iso })} style={{
                flex: "0 0 auto", padding: "8px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                background: sel ? urgColor[u] : P.soft, color: sel ? "#fff" : P.ink,
                border: `1.5px solid ${sel ? urgColor[u] : P.line}`,
              }}>+{n}j</Pressable>
            );
          })}
        </div>
        <Field label={t("controlRun.quantityLabel", "Quantité")}>
          <input style={inputStyle} inputMode="numeric" placeholder={t("controlRun.qtyPlaceholder", "Qté")} value={newArtForm.qty}
            onChange={(e) => setNewArtForm({ ...newArtForm, qty: e.target.value })} />
        </Field>
        <Field label={t("controlRun.remarkLabel", "Remarque")}>
          <input style={inputStyle} placeholder={t("controlRun.remarkPlaceholder", "Remarque facultative")} value={newArtForm.note}
            onChange={(e) => setNewArtForm({ ...newArtForm, note: e.target.value })} />
        </Field>
        {newArtForm.recordId && (
          <Pressable onClick={() => { deleteNewArtMhd(newArtForm.recordId); setSheet(null); }} style={{
            width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, marginBottom: 10,
            background: "rgba(239,68,68,.14)", color: A.red, fontWeight: 700, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
          }}>{t("controlRun.deleteThisMhdBtn", "Supprimer cette DLC")}</Pressable>
        )}
        <div style={{ display: "flex", gap: 10 }}>
          <Pressable onClick={() => setSheet(null)} style={{
            flex: 1, textAlign: "center", padding: "14px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14.5px * var(--nx-font-scale, 1))`,
            background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
          }}>{t("common.cancel", "Annuler")}</Pressable>
          <Pressable onClick={saveNewArtMhd} style={{ flex: 1, ...btnSolid(A.blue) }}>{t("common.save", "Enregistrer")}</Pressable>
        </div>
      </Sheet>

      <Sheet open={sheet === "newArtMhdFF"} onClose={() => setSheet(null)} P={P}>
        <SheetHead kicker={newArtFormFF.name} title={newArtFormFF.recordId ? t("controlRun.editDlcFormTitle", "Modifier la DLC") : t("controlRun.newMhdModeLabel", "Nouvelle DLC")} onClose={() => setSheet(null)} />
        <Field label={t("controlStore.fieldDateMhd", "Date DLC")}>
          <input style={dateTimeInputStyle} type="date" min={todayISO()} value={newArtFormFF.mhd}
            onChange={(e) => setNewArtFormFF({ ...newArtFormFF, mhd: e.target.value })} />
        </Field>
        <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 15, paddingBottom: 2 }}>
          {QUICK_DAYS.map((n) => {
            const iso = addDays(n);
            const sel = newArtFormFF.mhd === iso;
            const u = urgency(iso);
            return (
              <Pressable key={n} onClick={() => setNewArtFormFF({ ...newArtFormFF, mhd: iso })} style={{
                flex: "0 0 auto", padding: "8px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                background: sel ? urgColor[u] : P.soft, color: sel ? "#fff" : P.ink,
                border: `1.5px solid ${sel ? urgColor[u] : P.line}`,
              }}>+{n}j</Pressable>
            );
          })}
        </div>
        <Field label={t("controlRun.quantityLabel", "Quantité")}>
          <input style={inputStyle} inputMode="numeric" placeholder={t("controlRun.qtyPlaceholder", "Qté")} value={newArtFormFF.qty}
            onChange={(e) => setNewArtFormFF({ ...newArtFormFF, qty: e.target.value })} />
        </Field>
        <Field label={t("controlRun.remarkLabel", "Remarque")}>
          <input style={inputStyle} placeholder={t("controlRun.remarkPlaceholder", "Remarque facultative")} value={newArtFormFF.note}
            onChange={(e) => setNewArtFormFF({ ...newArtFormFF, note: e.target.value })} />
        </Field>
        {newArtFormFF.recordId && (
          <Pressable onClick={() => { deleteNewArtMhdFF(newArtFormFF.recordId); setSheet(null); }} style={{
            width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, marginBottom: 10,
            background: "rgba(239,68,68,.14)", color: A.red, fontWeight: 700, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
          }}>{t("controlRun.deleteThisMhdBtn", "Supprimer cette DLC")}</Pressable>
        )}
        <div style={{ display: "flex", gap: 10 }}>
          <Pressable onClick={() => setSheet(null)} style={{
            flex: 1, textAlign: "center", padding: "14px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14.5px * var(--nx-font-scale, 1))`,
            background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
          }}>{t("common.cancel", "Annuler")}</Pressable>
          <Pressable onClick={saveNewArtMhdFF} style={{ flex: 1, ...btnSolid(A.blue) }}>{t("common.save", "Enregistrer")}</Pressable>
        </div>
      </Sheet>

      <Sheet open={sheet === "newPromoArt"} onClose={() => setSheet(null)} P={P}>
        <SheetHead
          kicker={newPromoArtForm.family === "FRISCHFLEISCH" ? "Frisch Fleisch" : "Kühlung"}
          title={t("controlNewArt.newArticleTitle", "Nouvel article promotionnel")}
          onClose={() => setSheet(null)}
        />
        <Pressable onClick={() => doScan("newPromoArt")} aria-label={t("controlRun.scanBarcodeBtn", "Scanner le code-barres")} style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 7, width: "100%", padding: "12px 0", borderRadius: 99,
          background: themeId === "sombre" ? "#2a2a2e" : A.navy, color: "#fff", fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, marginBottom: 14,
          animation: scanning ? "nxPulse 1s ease infinite" : "none",
        }}>📷 {t("controlNewArt.scanToFillArtNr", "Scanner le code-barres (remplit Art.Nr)")}</Pressable>
        <Field label="Art.Nr">
          <input style={inputStyle} value={newPromoArtForm.artNr} onChange={(e) => setNewPromoArtForm({ ...newPromoArtForm, artNr: e.target.value })} />
        </Field>
        <Field label={t("claims.item.name", "Désignation")}>
          <input style={inputStyle} value={newPromoArtForm.name} onChange={(e) => setNewPromoArtForm({ ...newPromoArtForm, name: e.target.value })} />
        </Field>
        <Field label={t("controlStore.fieldDateMhd", "Date DLC")}>
          <input style={dateTimeInputStyle} type="date" min={todayISO()} value={newPromoArtForm.mhd}
            onChange={(e) => setNewPromoArtForm({ ...newPromoArtForm, mhd: e.target.value })} />
        </Field>
        <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 15, paddingBottom: 2 }}>
          {QUICK_DAYS.map((n) => {
            const iso = addDays(n);
            const sel = newPromoArtForm.mhd === iso;
            const u = urgency(iso);
            return (
              <Pressable key={n} onClick={() => setNewPromoArtForm({ ...newPromoArtForm, mhd: iso })} style={{
                flex: "0 0 auto", padding: "8px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                background: sel ? urgColor[u] : P.soft, color: sel ? "#fff" : P.ink,
                border: `1.5px solid ${sel ? urgColor[u] : P.line}`,
              }}>+{n}j</Pressable>
            );
          })}
        </div>
        <Field label={t("controlRun.quantityLabel", "Quantité")}>
          <input style={inputStyle} inputMode="numeric" placeholder={t("controlRun.qtyPlaceholder", "Qté")} value={newPromoArtForm.qty}
            onChange={(e) => setNewPromoArtForm({ ...newPromoArtForm, qty: e.target.value })} />
        </Field>
        <Field label={t("controlRun.remarkLabel", "Remarque")}>
          <input style={inputStyle} placeholder={t("controlRun.remarkPlaceholder", "Remarque facultative")} value={newPromoArtForm.note}
            onChange={(e) => setNewPromoArtForm({ ...newPromoArtForm, note: e.target.value })} />
        </Field>
        <Field label={t("reductions.optionalRuleLabel", "Règle de réduction (facultatif)")}>
          <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", paddingBottom: 2 }}>
            {["AUTO", ...ruleDefinitions.map((d) => d.id)].map((rule) => {
              const sel = newPromoArtForm.rule === rule;
              const label = rule === "AUTO" ? t("reductions.autoLabel", "Automatique") : reductionRuleLabel(rule);
              return (
                <Pressable key={rule} onClick={() => setNewPromoArtForm({ ...newPromoArtForm, rule })} style={{
                  flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: sel ? A.blue : P.soft, color: sel ? "#fff" : P.ink, border: `1.5px solid ${sel ? A.blue : P.line}`,
                }}>{label}</Pressable>
              );
            })}
            <Pressable onClick={() => openInlineRuleAdd("newPromoArtForm")} style={{
              flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
              background: P.soft, border: `1.5px dashed ${P.line}`, color: A.blue,
            }}>＋ {t("reductions.rulesAdmin.addBtn", "Ajouter une règle")}</Pressable>
          </div>
          {inlineRuleAddKey === "newPromoArtForm" && renderInlineRuleAddForm((newId) => setNewPromoArtForm((f) => ({ ...f, rule: newId })))}
        </Field>
        <input ref={newPromoArtPhotoInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handleNewPromoArtPhotoChange} />
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 15 }}>
          {newPromoArtForm.photo ? (
            <img src={newPromoArtForm.photo} alt="" style={{ width: 46, height: 46, borderRadius: 12, objectFit: "cover", flex: "0 0 auto" }} />
          ) : null}
          <Pressable onClick={() => newPromoArtPhotoInputRef.current?.click()} style={{
            flex: 1, textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
            background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink, opacity: newPromoArtPhotoUploading ? .6 : 1,
          }}>{newPromoArtPhotoUploading ? t("settings.photo.uploading", "Envoi...") : newPromoArtForm.photo ? t("controlStore.changePhotoBtn", "Changer la photo") : t("controlStore.addPhotoOptionalLabel", "Ajouter une photo (facultatif)")}</Pressable>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Pressable onClick={() => setSheet(null)} style={{
            flex: 1, textAlign: "center", padding: "14px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14.5px * var(--nx-font-scale, 1))`,
            background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
          }}>{t("common.cancel", "Annuler")}</Pressable>
          <Pressable onClick={saveNewPromoArt} style={{ flex: 1, ...btnSolid(A.blue) }}>{t("common.save", "Enregistrer")}</Pressable>
        </div>
      </Sheet>

      <Sheet open={sheet === "list"} onClose={() => { setSheet(null); setSearch(""); }} P={P}>
        <SheetHead kicker={t("control.listSheetKicker", "Liste Kühlung")} title={t("control.listSheetTitle", "Articles")}
          onClose={() => { setSheet(null); setSearch(""); }} />
        <input style={{ ...inputStyle, marginBottom: 12 }} placeholder={t("controlRun.searchPlaceholder", "Rechercher Art.Nr ou nom...")}
          value={search} onChange={(e) => setSearch(e.target.value)} />
        {(() => {
          // Sans recherche, filteredList = tout le catalogue (jusqu'à 376 articles réels
          // à N236) — les rendre TOUS d'un coup dans une sheet à chaque ouverture, même
          // pour juste sauter à un article, pèse inutilement sur la mémoire (retour
          // utilisateur 2026-07-31 : vrai crash natif iOS/PWA par manque de mémoire,
          // confirmé via l'inspecteur distant — aucune erreur JS, la page est tuée par
          // l'OS). Un plafond tant qu'aucune recherche n'est tapée ; la recherche
          // elle-même n'a jamais besoin d'afficher plus de quelques dizaines de résultats.
          const CAP = 60;
          const shown = kuhlungListBatches * CAP;
          const capped = !search.trim() && filteredList.length > shown;
          const visible = capped ? filteredList.slice(0, shown) : filteredList;
          return (
            <>
              {visible.map((a) => {
                const idx = articles.indexOf(a);
                const done = touched.has(a.artNr);
                return (
                  <Pressable key={a.artNr} onClick={() => { setRunIndex(idx); setFinish(false); setSheet(null); setSearch(""); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 11, width: "100%", boxSizing: "border-box",
                      padding: "12px 13px", borderRadius: 16, marginBottom: 6, color: P.ink,
                      background: idx === runIndex ? "rgba(54,79,231,.12)" : P.soft,
                      border: idx === runIndex ? `1.5px solid ${A.blue}` : "1.5px solid transparent",
                    }}>
                    <span style={{
                      width: 23, height: 23, borderRadius: 99, flex: "0 0 auto", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800,
                      display: "grid", placeItems: "center",
                      background: done ? A.green : P.soft, color: done ? "#fff" : P.muted,
                    }}>{done ? "✓" : a.position}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</strong>
                      <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>Art.Nr {a.artNr}</span>
                    </div>
                  </Pressable>
                );
              })}
              {capped && (
                <Pressable onClick={() => setKuhlungListBatches((n) => n + 1)} style={{
                  width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, marginTop: 4,
                  background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                }}>{t("scanner.showMoreBtnTemplate", { shown: Math.min(shown, filteredList.length), total: filteredList.length }, `Afficher plus (${Math.min(shown, filteredList.length)}/${filteredList.length})`)}</Pressable>
              )}
              {filteredList.length === 0 && <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{t("scanner.noArticleSearchText", "Aucun article ne correspond à ta recherche.")}</p>}
            </>
          );
        })()}
      </Sheet>

      <Sheet open={sheet === "newRequest"} onClose={() => setSheet(null)} P={P} maxH="70%">
        <SheetHead kicker={t("planning.hub.requests.title", "Planungsanfrage")} title={t("planning.requests.newSheetTitle", "Nouvelle demande")} onClose={() => setSheet(null)} />
        <div style={{ display: "flex", gap: 8, marginBottom: 13 }}>
          {[
            { id: "freiwunsch", label: "🌿 Freiwunsch", c: A.teal },
            { id: "urlaub", label: "🏖️ Urlaub", c: A.orange },
          ].map((tp) => (
            <Pressable key={tp.id} onClick={() => setReqForm({ ...reqForm, type: tp.id })} style={{
              flex: 1, textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
              background: reqForm.type === tp.id ? tp.c : P.soft,
              color: reqForm.type === tp.id ? "#fff" : P.ink,
              border: `1.5px solid ${reqForm.type === tp.id ? tp.c : P.line}`,
            }}>{tp.label}</Pressable>
          ))}
        </div>
        {reqForm.type === "urlaub" ? (
          <>
            <Field label={t("planning.requests.startDateLabel", "Du")}>
              <input style={dateTimeInputStyle} type="date" min={todayISO()} value={reqForm.date}
                onChange={(e) => setReqForm({ ...reqForm, date: e.target.value })} />
            </Field>
            <Field label={t("planning.requests.endDateOptionalLabel", "Au (optionnel)")}>
              <input style={dateTimeInputStyle} type="date" min={reqForm.date || todayISO()} value={reqForm.endDate}
                onChange={(e) => setReqForm({ ...reqForm, endDate: e.target.value })} />
            </Field>
          </>
        ) : (
          <>
            <Field label={t("planning.requests.whichDayLabel", "Quel jour ?")}>
              <input style={dateTimeInputStyle} type="date" min={todayISO()} value={reqForm.date}
                onChange={(e) => setReqForm({ ...reqForm, date: e.target.value })} />
            </Field>
            <p style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, margin: "-8px 2px 12px" }}>{t("planning.storeClosedSundayHintText", "La filiale étant fermée le dimanche, choisis un jour du lundi au samedi.")}</p>
          </>
        )}
        <Field label={t("planning.requests.commentLabel", "Remarque")}>
          <input style={inputStyle} placeholder={t("planning.requests.commentPlaceholder", "Remarque facultative")} value={reqForm.comment}
            onChange={(e) => setReqForm({ ...reqForm, comment: e.target.value })} />
        </Field>
        <div style={{ display: "flex", gap: 10 }}>
          <Pressable onClick={() => setSheet(null)} style={{
            flex: 1, textAlign: "center", padding: "14px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14.5px * var(--nx-font-scale, 1))`,
            background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
          }}>{t("common.cancel", "Annuler")}</Pressable>
          <Pressable onClick={submitRequest} style={{ flex: 1, ...btnSolid(A.purple) }}>{t("planning.requests.sendBtn", "Envoyer")}</Pressable>
        </div>
      </Sheet>

      <Sheet open={sheet === "shift"} onClose={() => { setSheet(null); setShiftForm(null); }} P={P} maxH="72%">
        <SheetHead kicker={`${t("planning.hub.arbeitsplan.title", "Plan de travail")} · ${shiftForm ? weekLabel(shiftForm.day) : ""}`}
          title={shiftForm?.id ? t("planning.week.editShiftTitle", "Modifier le créneau") : t("planning.week.newShiftTitle", "Nouveau créneau")}
          onClose={() => { setSheet(null); setShiftForm(null); }} />
        {shiftForm && (() => {
          const keepsHours = shiftStatusKeepsHours(shiftForm.status);
          const isMinor = isMinorEmployeeId(shiftForm.employeeId);
          // La pause doit être automatiquement remplie dès que la règle légale s'applique —
          // jamais laissée à "Sans pause" par défaut sur un créneau qui en exige une. Basé
          // sur la durée BRUTE du créneau (start→end), avant toute pause posée, ce qui
          // suffit à déterminer le bon palier (6h/9h sont très en dessous de la différence
          // entre durée brute et nette). Ne réduit jamais une pause déjà plus grande que le
          // minimum légal — seulement relève au minimum requis si insuffisante.
          const applyAutoPause = (f, start, end) => {
            if (!start || !end) return { pauseMin: f.pauseMin, pauseStart: f.pauseStart };
            const s = toMin(start), e = toMin(end);
            if (e <= s) return { pauseMin: f.pauseMin, pauseStart: f.pauseStart };
            const grossHours = Math.round(((e - s) / 60) * 100) / 100;
            const req = requiredBreak(isMinor, grossHours) || 0;
            const pauseMin = Math.max(f.pauseMin || 0, req);
            if (!pauseMin) return { pauseMin: 0, pauseStart: "" };
            const pauseStart = pauseMin === f.pauseMin && f.pauseStart ? f.pauseStart : suggestPauseStart(start, end, pauseMin);
            return { pauseMin, pauseStart };
          };
          return (
          <>
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 6 }}>{t("arbeitszeitliste.external.searchLabel", "Employé")}</span>
            <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 13, paddingBottom: 2 }}>
              {planTeam.map((emp) => {
                const sel = shiftForm.emp === emp;
                const c = EMP_COLORS[emp] || A.navy;
                const role = planEmpRole(emp);
                return (
                  <Pressable key={emp} onClick={() => openShiftSheet(resolveEmployeeId(emp), shiftForm.day)} style={{
                    flex: "0 0 auto", display: "flex", alignItems: "center", gap: 7, padding: "9px 14px",
                    borderRadius: 99, fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                    background: sel ? c : P.soft, color: sel ? "#fff" : P.ink,
                    border: `1.5px solid ${sel ? c : P.line}`,
                  }}>{emp}{role ? ` · ${role}` : ""}</Pressable>
                );
              })}
            </div>

            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 6 }}>Statut</span>
            <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
              {SHIFT_STATUSES.map((st) => {
                const sel = shiftForm.status === st;
                return (
                  <Pressable key={st} onClick={() => setShiftForm({ ...shiftForm, status: st, ...(shiftStatusKeepsHours(st) ? {} : { start: "", end: "", pauseMin: 0, pauseStart: "" }) })} style={{
                    flex: "0 0 auto", padding: "9px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                    background: sel ? A.teal : P.soft, color: sel ? "#fff" : P.ink,
                    border: `1.5px solid ${sel ? A.teal : P.line}`,
                  }}>{SHIFT_STATUS_LABELS[st]}</Pressable>
                );
              })}
            </div>

            {keepsHours && (
              <>
                {/* Créneaux rapides : évite les champs heure natifs côte à côte
                    (bug de chevauchement iOS) et couvre le cas d'usage courant en 1 tap —
                    alignés sur la classification réelle Früh/Mitte/Spät/Kurz (shiftStatusMeta). */}
                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 6 }}>{t("planning.quickSlotsLabel", "Créneaux rapides")}</span>
                <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
                  {[
                    { l: t("planning.shift.early", "Tôt"), s: "06:00", e: "14:30" },
                    { l: t("planning.shift.mid", "Milieu"), s: "09:00", e: "17:00" },
                    { l: t("planning.shift.late", "Tard"), s: "12:45", e: "21:15" },
                    { l: t("planning.shift.short", "Court"), s: "06:00", e: "10:00" },
                  ].map((p) => {
                    const sel = shiftForm.start === p.s && shiftForm.end === p.e;
                    return (
                      <Pressable key={p.l} onClick={() => setShiftForm((f) => ({ ...f, start: p.s, end: p.e, ...applyAutoPause(f, p.s, p.e) }))} style={{
                        flex: "0 0 auto", padding: "9px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                        background: sel ? A.teal : P.soft, color: sel ? "#fff" : P.ink,
                        border: `1.5px solid ${sel ? A.teal : P.line}`,
                      }}>{p.l} · {p.s}–{p.e}</Pressable>
                    );
                  })}
                </div>
                {/* Champs empilés (pleine largeur chacun) : jamais côte à côte,
                    donc plus aucun chevauchement possible avec le picker natif iOS. step=900
                    (15 min) — évite d'avoir à défiler minute par minute sur le picker natif. */}
                <Field label={t("planning.sheet.start", "Début")}>
                  <TimeSelect value={shiftForm.start}
                    onChange={(v) => setShiftForm((f) => ({ ...f, start: v, ...applyAutoPause(f, v, f.end) }))} />
                </Field>
                <Field label={t("planning.sheet.end", "Fin")}>
                  <TimeSelect value={shiftForm.end}
                    onChange={(v) => setShiftForm((f) => ({ ...f, end: v, ...applyAutoPause(f, f.start, v) }))} />
                </Field>

                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 6 }}>Pause</span>
                <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 12, paddingBottom: 2 }}>
                  {[0, 15, 30, 45, 60].map((m) => {
                    const sel = (shiftForm.pauseMin || 0) === m;
                    return (
                      <Pressable key={m} onClick={() => setShiftForm((f) => ({ ...f, pauseMin: m, pauseStart: m ? suggestPauseStart(f.start, f.end, m) : "" }))} style={{
                        flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                        background: sel ? A.teal : P.soft, color: sel ? "#fff" : P.ink,
                        border: `1.5px solid ${sel ? A.teal : P.line}`,
                      }}>{m === 0 ? "Sans pause" : `${m} min`}</Pressable>
                    );
                  })}
                </div>
                {shiftForm.pauseMin > 0 && (
                  <Field label={`Début de la pause (${shiftForm.pauseMin} min)`}>
                    <TimeSelect value={shiftForm.pauseStart || ""}
                      onChange={(v) => setShiftForm({ ...shiftForm, pauseStart: v })} />
                  </Field>
                )}
                {shiftForm.pauseMin > 0 && shiftForm.pauseStart && (
                  <p style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "-8px 2px 12px" }}>
                    Pause {shiftForm.pauseStart} – {minutesToTime(toMin(shiftForm.pauseStart) + shiftForm.pauseMin)}
                  </p>
                )}
              </>
            )}

            <Field label={t("planning.leistungsfreiHoursLabel", "Leistungsfrei (heures payées hors Leistung)")}>
              <input style={inputStyle} inputMode="decimal" placeholder="0" value={shiftForm.leistungsfreiHours || ""}
                onChange={(e) => setShiftForm({ ...shiftForm, leistungsfreiHours: e.target.value.replace(",", ".") })} />
            </Field>
            {/* FL/FLA ne sont pas soumis au solde crédité (voir saveShift) — le message
                l'indique explicitement, plutôt que d'afficher "Solde disponible : 0h" qui
                laisserait croire à un blocage qui n'existe plus pour ces rôles. */}
            <p style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "-8px 2px 12px" }}>
              {["FL", "FLA"].includes(planEmpRole(shiftForm.emp)) ? "Pas de limite de solde pour FL/FLA." : shiftLfHint.text}
            </p>

            {shiftForm.status === "Urlaub" && (
              <p style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "-4px 2px 12px" }}>{shiftVacationHint.text}</p>
            )}

            {/* Zuschlagspflichtig (Z) — case masquée (retour utilisateur explicite 2026-09-21) :
                la filiale n'a ni shift de nuit ni ouverture le dimanche, donc les 2 raisons
                qui justifiaient cette majoration ne s'appliquent jamais ici. Champ de données
                (shiftForm.zuschlagspflichtig) volontairement laissé intact — jours déjà
                enregistrés avec Z=true dans le passé continuent de s'afficher correctement en
                AZL, juste plus aucune nouvelle valeur ne peut être posée depuis cet écran. */}

            <Field label={t("inventur.noteOptionalLabel", "Remarque (facultatif)")}>
              <input style={inputStyle} placeholder={t("planning.shiftNotePlaceholder", "Précision...")} value={shiftForm.note || ""}
                onChange={(e) => setShiftForm({ ...shiftForm, note: e.target.value })} />
            </Field>

            {/* Alerte de disponibilité Aushilfe — non bloquante, comme dans le vrai app */}
            {isUnavailable(shiftForm.emp, shiftForm.day) && (
              <div style={{
                display: "flex", gap: 9, borderRadius: 14, padding: 11, marginBottom: 13,
                background: "rgba(231,138,18,.09)", border: "1px solid rgba(231,138,18,.28)",
              }}>
                <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>⚠️</span>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: "#b56a00", lineHeight: 1.5 }}>
                  {shiftForm.emp} a indiqué ne pas être disponible {weekLabel(shiftForm.day)?.toLowerCase()}. Tu peux quand même enregistrer, mais vérifie avec elle/lui avant de publier.
                </span>
              </div>
            )}

            {/* Rappel des règles légales, calculé en direct pour ce créneau */}
            {keepsHours && shiftForm.start && shiftForm.end && shiftForm.end > shiftForm.start && (() => {
              const h = shiftHours(shiftForm);
              const req = requiredBreak(isMinor, h);
              const curfewBreach = isMinor && toMin(shiftForm.end) > MINOR_LATEST_END_MIN;
              const ok = (!req || (shiftForm.pauseMin || 0) >= req) && !curfewBreach;
              return (
                <div style={{
                  display: "flex", gap: 9, borderRadius: 14, padding: 11, marginBottom: 13,
                  background: ok ? "rgba(30,184,95,.09)" : "rgba(239,68,68,.09)",
                  border: `1px solid ${ok ? "rgba(30,184,95,.25)" : "rgba(239,68,68,.28)"}`,
                }}>
                  <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{ok ? "✓" : "⚠️"}</span>
                  <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: ok ? "#15803d" : A.red, lineHeight: 1.5 }}>
                    {h}h de travail{isMinor ? " (mineur)" : ""}
                    {req ? ` — pause de ${req} min requise` : " — aucune pause légale requise"}
                    {req && (shiftForm.pauseMin || 0) < req ? `, seulement ${shiftForm.pauseMin || 0} min prévues` : ""}.
                    {h >= 10 ? " Attention : au-delà de 10h/jour." : ""}
                    {curfewBreach ? ` Un mineur ne doit pas travailler après 20h (fin prévue ${shiftForm.end}).` : ""}
                    {isAzubiRole(planEmpRole(shiftForm.emp)) && shiftForm.status !== "BS+Arbeit" ? ` Compte pour ${Math.round(h * 0.5 * 100) / 100}h dans le Ist (Azubi, ×0,5).` : ""}
                  </span>
                </div>
              );
            })()}

            {shiftForm.id && (
              <Pressable onClick={deleteShift} style={{
                width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, marginBottom: 10,
                background: "rgba(239,68,68,.14)", color: A.red, fontWeight: 700, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
              }}>{t("planning.deleteShiftBtn", "Supprimer ce créneau")}</Pressable>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <Pressable onClick={() => { setSheet(null); setShiftForm(null); }} style={{
                flex: 1, textAlign: "center", padding: "14px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14.5px * var(--nx-font-scale, 1))`,
                background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
              }}>{t("common.cancel", "Annuler")}</Pressable>
              <Pressable onClick={saveShift} style={{ flex: 1, ...btnSolid(A.teal) }}>{t("common.save", "Enregistrer")}</Pressable>
            </div>
          </>
          );
        })()}
      </Sheet>

      <Sheet open={sheet === "reqThread"} onClose={() => setSheet(null)} P={P} maxH="82%">
        {(() => {
          const req = planRequests.find((r) => r.id === activeReqId);
          if (!req) return null;
          const thread = req.messages || [];
          const mine = req.emp === user?.firstname;
          const statusMeta = REQ_STATUS_META[req.status] || REQ_STATUS_META.pending;
          return (
            <>
              <SheetHead kicker={`${req.emp} · ${req.type === "urlaub" ? "Urlaub" : "Freiwunsch"}`}
                title={reqDateLabel(req)}
                onClose={() => setSheet(null)} />
              <div style={{
                display: "inline-block", padding: "5px 11px", borderRadius: 99, fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800,
                background: statusMeta.bg, color: statusMeta.fg, marginBottom: 12,
              }}>{statusMeta.label}</div>
              {(req.originalStartDate || req.originalWeek) && (
                <p style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 10 }}>
                  {t("planning.requests.originalDateTemplate", { date: reqDateLabelFor(req.type, req.originalStartDate, req.originalEndDate, req.originalWeek, req.originalRequestedDay) }, `Date d'origine : ${reqDateLabelFor(req.type, req.originalStartDate, req.originalEndDate, req.originalWeek, req.originalRequestedDay)}`)}
                  {" → "}{t("planning.requests.newDateProposedLabel", "nouvelle date proposée :")} <strong style={{ color: P.ink }}>{reqDateLabel(req)}</strong>
                </p>
              )}
              <div style={{ maxHeight: 260, overflowY: "auto", marginBottom: 12 }}>
                {thread.length === 0 && (
                  <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, textAlign: "center", padding: "16px 0" }}>
                    {t("planning.requests.noMessagesYetText", "Aucun échange pour l'instant — demande une précision avant de décider.")}
                  </p>
                )}
                {thread.map((m, i) => {
                  const isMe = m.authorId === user?.proId || m.authorId === user?.id;
                  return (
                    <div key={m.id || i} style={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start", marginBottom: 8 }}>
                      <div style={{
                        maxWidth: "78%", padding: "9px 13px", borderRadius: 16,
                        background: isMe ? A.blue : P.soft, color: isMe ? "#fff" : P.ink,
                      }}>
                        <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{m.text}</span>
                        <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, opacity: .75, display: "block", marginTop: 3 }}>
                          {m.authorName} · {new Date(m.createdAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <input style={{ ...inputStyle, flex: 1 }} placeholder={t("messages.composerPlaceholder", "Écrire un message...")} value={reqMsgDraft}
                  onChange={(e) => setReqMsgDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") sendReqMessage(); }} />
                <Pressable onClick={sendReqMessage} aria-label={t("messages.sendAria", "Envoyer")} style={{
                  width: 46, height: 46, borderRadius: 15, background: A.blue, color: "#fff",
                  display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                }}>➤</Pressable>
              </div>
              {req.status === "pending" && !mine && (
                <div style={{ display: "flex", gap: 10 }}>
                  <Pressable onClick={() => { rejectRequest(req.id); setSheet(null); }} style={{
                    flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                    background: "rgba(239,68,68,.13)", color: A.red,
                  }}>{t("planning.requests.rejectBtn", "Refuser")}</Pressable>
                  <Pressable onClick={() => { approveRequest(req.id); setSheet(null); }} style={{ flex: 1, ...btnSolid(A.green) }}>✓ {t("planning.requests.approveBtn", "Approuver")}</Pressable>
                </div>
              )}
              {req.status === "pending" && mine && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {reqProposeForm ? (
                    <div style={{ ...card, padding: 12 }}>
                      <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>{t("planning.requests.proposeSectionTitle", "Nouvelle date proposée")}</span>
                      {req.type === "urlaub" ? (
                        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                          <input type="date" style={{ ...inputStyle, flex: 1 }} value={reqProposeForm.date}
                            onChange={(e) => setReqProposeForm((f) => ({ ...f, date: e.target.value }))} />
                          <input type="date" style={{ ...inputStyle, flex: 1 }} value={reqProposeForm.endDate}
                            onChange={(e) => setReqProposeForm((f) => ({ ...f, endDate: e.target.value }))} />
                        </div>
                      ) : (
                        <input type="date" style={{ ...inputStyle, width: "100%", marginBottom: 10 }} value={reqProposeForm.date}
                          onChange={(e) => setReqProposeForm((f) => ({ ...f, date: e.target.value }))} />
                      )}
                      <div style={{ display: "flex", gap: 8 }}>
                        <Pressable onClick={() => setReqProposeForm(null)} style={{
                          flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                          background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                        }}>{t("common.cancel", "Annuler")}</Pressable>
                        <Pressable onClick={submitProposeDate} style={{ flex: 1, ...btnSolid(A.blue) }}>{t("planning.requests.proposeBtn", "Proposer")}</Pressable>
                      </div>
                    </div>
                  ) : (
                    <Pressable onClick={() => setReqProposeForm({ date: "", endDate: "" })} style={{
                      textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                      background: "rgba(45,127,240,.12)", color: A.frost, border: `1.5px solid ${A.frost}`,
                    }}>📅 {t("planning.requests.proposeNewDateBtn", "Proposer une nouvelle date")}</Pressable>
                  )}
                  <Pressable onClick={() => { cancelRequest(req.id); setSheet(null); }} style={{
                    textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                    background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                  }}>{t("planning.requests.cancelBtn", "Annuler la demande")}</Pressable>
                </div>
              )}
              {req.status !== "pending" && (
                <p style={{ textAlign: "center", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>
                  {t("planning.requests.alreadyDecidedTemplate", { status: statusMeta.label.toLowerCase() }, `Demande déjà ${statusMeta.label.toLowerCase()} — la discussion reste consultable.`)}
                </p>
              )}
            </>
          );
        })()}
      </Sheet>

      <Sheet open={sheet === "pilotAppointment"} onClose={() => setSheet(null)} P={P} maxH="82%">
        {(() => {
          if (!pilotAppointmentStoreId) return null;
          const activeApt = pilotAppointments.find((a) => a.storeId === pilotAppointmentStoreId && !a.confirmed && !a.cancelled);
          const lastConfirmed = pilotAppointments.find((a) => a.storeId === pilotAppointmentStoreId && a.confirmed && !a.cancelled);
          const apt = activeApt || lastConfirmed || null;
          const canPropose = !apt?.confirmed;
          return (
            <>
              <SheetHead kicker={t("pilotage.appointment.sheetKicker", "Rendez-vous d'inventaire")} title={storeLabelFor(pilotAppointmentStoreId)} onClose={() => setSheet(null)} />
              {apt?.confirmed && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, borderRadius: 14, background: "rgba(30,184,95,.1)", marginBottom: 14 }}>
                  <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>✅</span>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.green }}>
                    {t("pilotage.appointment.confirmedBannerTemplate", { date: fmtFR(apt.proposedDate) }, `Rendez-vous confirmé pour le ${fmtFR(apt.proposedDate)}.`)}
                  </span>
                </div>
              )}
              {apt && !apt.confirmed && apt.storeAgreed && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, borderRadius: 14, background: "rgba(231,138,18,.09)", marginBottom: 14 }}>
                  <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>👍</span>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.orange }}>{t("pilotage.appointment.agreedBanner", "La filiale a accepté cette date — valide-la pour la rendre définitive.")}</span>
                </div>
              )}
              {apt && !apt.confirmed && !apt.storeAgreed && apt.proposedBySide === "store" && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, borderRadius: 14, background: "rgba(54,79,231,.09)", marginBottom: 14 }}>
                  <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>↩️</span>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.blue }}>{t("pilotage.appointment.counteredBanner", "La filiale propose une autre date — accepte-la en la reproposant, ou ajuste.")}</span>
                </div>
              )}

              {canPropose && (
                <>
                  <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: 6 }}>
                    {apt ? t("pilotage.appointment.proposeAgainLabel", "Proposer une autre date") : t("pilotage.appointment.proposeLabel", "Proposer une date")}
                  </span>
                  <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    <input type="date" style={{ ...dateTimeInputStyle, flex: 1 }} value={pilotAppointmentDateInput} onChange={(e) => setPilotAppointmentDateInput(e.target.value)} />
                    <Pressable onClick={proposePilotAppointment} disabled={!pilotAppointmentDateInput || pilotAppointmentBusy} style={{
                      padding: "0 18px", borderRadius: 15, background: A.blue, color: "#fff", fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                      display: "grid", placeItems: "center", flex: "0 0 auto", opacity: (!pilotAppointmentDateInput || pilotAppointmentBusy) ? .5 : 1,
                    }}>{t("pilotage.appointment.proposeBtn", "Envoyer")}</Pressable>
                  </div>
                </>
              )}

              {apt && !apt.confirmed && apt.storeAgreed && (
                <Pressable onClick={() => confirmPilotAppointment(apt.id)} disabled={pilotAppointmentBusy} style={{ width: "100%", ...btnSolid(A.green), marginBottom: 10 }}>
                  ✓ {t("pilotage.appointment.confirmBtn", "Valider définitivement")}
                </Pressable>
              )}
              {apt && !apt.confirmed && (
                <Pressable onClick={() => cancelPilotAppointment(apt.id, false)} style={{
                  width: "100%", textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: "rgba(239,68,68,.1)", color: A.red, marginBottom: 16,
                }}>{t("pilotage.appointment.cancelBtn", "Annuler la proposition")}</Pressable>
              )}

              {/* Rendez-vous déjà validé — retour utilisateur explicite (2026-08-06) : 2 actions
                  distinctes, jamais confondues. "Changement de plan" réutilise CE rendez-vous
                  (redevient en attente, notif dédiée) ; "nouveau rendez-vous indépendant" en crée
                  un second qui coexiste, celui déjà validé restant intact tant qu'il n'est pas
                  explicitement supprimé ci-dessous. */}
              {apt?.confirmed && (
                <>
                  <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: 6 }}>
                    {t("pilotage.appointment.rescheduleLabel", "Changement de plan — renvoyer une nouvelle date")}
                  </span>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input type="date" style={{ ...dateTimeInputStyle, flex: 1 }} value={pilotAppointmentDateInput} onChange={(e) => setPilotAppointmentDateInput(e.target.value)} />
                    <Pressable onClick={reschedulePilotAppointment} disabled={!pilotAppointmentDateInput || pilotAppointmentBusy} style={{
                      padding: "0 18px", borderRadius: 15, background: A.orange, color: "#fff", fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                      display: "grid", placeItems: "center", flex: "0 0 auto", opacity: (!pilotAppointmentDateInput || pilotAppointmentBusy) ? .5 : 1,
                    }}>{t("pilotage.appointment.rescheduleBtn", "Envoyer")}</Pressable>
                  </div>
                  <p style={{ margin: "0 0 16px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.appointment.rescheduleHint", "Ce rendez-vous redevient en attente de validation — la filiale reçoit une notification de changement de plan, pas une nouvelle demande.")}</p>

                  <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".05em", display: "block", marginBottom: 6 }}>
                    {t("pilotage.appointment.newIndependentLabel", "Ou proposer un nouveau rendez-vous indépendant")}
                  </span>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input type="date" style={{ ...dateTimeInputStyle, flex: 1 }} value={pilotAppointmentNewDateInput} onChange={(e) => setPilotAppointmentNewDateInput(e.target.value)} />
                    <Pressable onClick={proposeNewPilotAppointment} disabled={!pilotAppointmentNewDateInput || pilotAppointmentBusy} style={{
                      padding: "0 18px", borderRadius: 15, background: A.blue, color: "#fff", fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                      display: "grid", placeItems: "center", flex: "0 0 auto", opacity: (!pilotAppointmentNewDateInput || pilotAppointmentBusy) ? .5 : 1,
                    }}>{t("pilotage.appointment.proposeBtn", "Envoyer")}</Pressable>
                  </div>
                  <p style={{ margin: "0 0 16px", fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.appointment.newIndependentHint", "Le rendez-vous déjà validé reste inchangé — celui-ci s'ajoute et suit son propre circuit de validation.")}</p>

                  <Pressable onClick={() => cancelPilotAppointment(apt.id, true)} style={{
                    width: "100%", textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                    background: "rgba(239,68,68,.1)", color: A.red, marginBottom: 16,
                  }}>{t("pilotage.appointment.deleteConfirmedBtn", "Supprimer ce rendez-vous")}</Pressable>
                </>
              )}

              {apt && (
                <>
                  <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".05em", display: "block", margin: "4px 0 8px" }}>{t("pilotage.appointment.chatLabel", "Échanges")}</span>
                  <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 10 }}>
                    {apt.messages.length === 0 && (
                      <p style={{ color: P.muted, fontSize: `calc(12px * var(--nx-font-scale, 1))`, textAlign: "center", padding: "10px 0" }}>{t("pilotage.appointment.chatEmpty", "Aucun échange pour l'instant.")}</p>
                    )}
                    {apt.messages.map((m) => (
                      <div key={m.id} style={{ display: "flex", justifyContent: m.side === "bl" ? "flex-end" : "flex-start", marginBottom: 8 }}>
                        <div style={{ maxWidth: "78%", padding: "9px 13px", borderRadius: 16, background: m.side === "bl" ? A.blue : P.soft, color: m.side === "bl" ? "#fff" : P.ink }}>
                          <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{m.text}</span>
                          <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, opacity: .75, display: "block", marginTop: 3 }}>{m.authorName} · {new Date(m.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input style={{ ...inputStyle, flex: 1 }} placeholder={t("messages.composerPlaceholder", "Écrire un message...")} value={pilotAppointmentChatInput}
                      onChange={(e) => setPilotAppointmentChatInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") sendPilotAppointmentMessage(apt.id); }} />
                    <Pressable onClick={() => sendPilotAppointmentMessage(apt.id)} aria-label={t("messages.sendAria", "Envoyer")} style={{
                      width: 46, height: 46, borderRadius: 15, background: A.blue, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                    }}>➤</Pressable>
                  </div>
                </>
              )}

              {/* Historique — retour utilisateur explicite (2026-08-06) : le GR voit maintenant
                  la même chose que la filiale (voir écran inventurScreen "rdv" côté filiale),
                  pas seulement le rendez-vous en cours. pilotAppointments est déjà scopé réseau
                  et trié par date de mise à jour côté serveur, donc rien à re-trier ici. */}
              {(() => {
                const history = pilotAppointments.filter((a) => a.storeId === pilotAppointmentStoreId && a.id !== apt?.id);
                if (history.length === 0) return null;
                return (
                  <>
                    <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".05em", display: "block", margin: "16px 0 6px" }}>{t("inventur.appointment.historyTitle", "Historique")}</span>
                    {history.map((a) => (
                      <div key={a.id} style={{ ...card, padding: 13, marginBottom: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{fmtFR(a.proposedDate)}</strong>
                          <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "3px 9px", color: "#fff", background: (a.confirmed && !a.cancelled) ? A.green : P.muted }}>
                            {(a.confirmed && !a.cancelled) ? t("inventur.appointment.statusConfirmed", "Confirmé") : t("inventur.appointment.statusCancelled", "Annulé")}
                          </span>
                        </div>
                      </div>
                    ))}
                  </>
                );
              })()}
            </>
          );
        })()}
      </Sheet>

      {/* Popup "Tâches à faire" — retour utilisateur explicite, 2026-08-07. Réutilise Sheet
          (feuille du bas), pas un nouveau style de popup centré — décision explicite. Voir
          l'effet d'auto-ouverture juste après dayTasks pour le déclenchement. */}
      <Sheet open={sheet === "taskReminder"} onClose={() => setSheet(null)} P={P} maxH="70%">
        <SheetHead kicker={t("tasks.reminder.kicker", "Aujourd'hui")} title={t("tasks.reminder.title", { n: actionableTasks.length }, `${actionableTasks.length} tâche${actionableTasks.length > 1 ? "s" : ""} à faire`)} onClose={() => setSheet(null)} />
        <div style={{ marginBottom: 16 }}>
          {actionableTasks.slice(0, 5).map((tk) => {
            const tm = typeMeta[tk.type] || typeMeta.standard;
            return (
              <div key={tk.id} style={{ ...card, padding: 12, marginBottom: 8, alignItems: "center" }}>
                <span style={{ flex: "0 0 auto", width: 36, height: 36, borderRadius: 12, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>{tm.emoji}</span>
                <div style={{ flex: 1, minWidth: 0, marginLeft: 10 }}>
                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{translateAutoTaskTitle(tk.title)}</strong>
                  <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>
                    {TASK_FILTER_PREDICATES.assigned(tk) ? t("tasks.reminder.fromAssigned", "Assignée par ton responsable") : TASK_FILTER_PREDICATES.mine(tk) ? t("tasks.reminder.fromMine", "Ta to-do personnelle") : t("tasks.reminder.fromTeam", "Tâche équipe")}
                  </span>
                </div>
              </div>
            );
          })}
          {actionableTasks.length > 5 && (
            <p style={{ margin: "0 2px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, textAlign: "center" }}>
              {t("tasks.reminder.andMore", { n: actionableTasks.length - 5 }, `+ ${actionableTasks.length - 5} autre${actionableTasks.length - 5 > 1 ? "s" : ""}`)}
            </p>
          )}
        </div>
        <Pressable onClick={goToActionableTasks} style={{ width: "100%", ...btnSolid(A.green), marginBottom: 10 }}>
          {t("tasks.reminder.goBtn", "Y aller")}
        </Pressable>
        <Pressable onClick={() => setSheet(null)} style={{
          width: "100%", textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
          background: P.soft, color: P.muted,
        }}>{t("tasks.reminder.laterBtn", "Plus tard")}</Pressable>
      </Sheet>

      {/* Popup "Rayons à commander aujourd'hui" (2026-08-31, retour utilisateur explicite —
          système de rappel de commande) — même patron que "Tâches à faire" juste au-dessus
          (Sheet, jamais un nouveau style de popup), déclenché depuis le même tryAutoOpen en
          repli si aucune tâche n'a déjà pris ce créneau. Toujours "la première option" (retour
          utilisateur explicite) : un simple lien vers le Hub, jamais une commande pré-ouverte/
          pré-remplie toute seule. */}
      <Sheet open={sheet === "merchOrderReminder"} onClose={() => setSheet(null)} P={P} maxH="65%">
        <SheetHead kicker={t("merch.reminder.kicker", "Aujourd'hui")} title={t("merch.reminder.title", { n: merchOrderReminder.zones.length }, `${merchOrderReminder.zones.length} rayon${merchOrderReminder.zones.length > 1 ? "s" : ""} à commander`)} onClose={() => setSheet(null)} />
        <div style={{ marginBottom: 16 }}>
          {merchOrderReminder.zones.map((zone) => {
            const meta = merchCategoryMeta(zone);
            return (
              <div key={zone} style={{ ...card, padding: 12, marginBottom: 8, alignItems: "center" }}>
                <span style={{ flex: "0 0 auto", width: 36, height: 36, borderRadius: 12, background: `${meta.color}1f`, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>{meta.emoji}</span>
                <strong style={{ flex: 1, minWidth: 0, marginLeft: 10, fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{meta.label}</strong>
              </div>
            );
          })}
        </div>
        <Pressable onClick={() => { setSheet(null); setScreen("merchandise"); setMerchScreen("hub"); loadMerchCategories(); }} style={{ width: "100%", ...btnSolid(A.amber), marginBottom: 10 }}>
          {t("merch.reminder.goBtn", "Y aller")}
        </Pressable>
        <Pressable onClick={() => setSheet(null)} style={{
          width: "100%", textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
          background: P.soft, color: P.muted,
        }}>{t("tasks.reminder.laterBtn", "Plus tard")}</Pressable>
      </Sheet>

      <Sheet open={sheet === "allocVacation"} onClose={() => { setSheet(null); setAllocForm(null); }} P={P} maxH="62%">
        <SheetHead kicker={t("planning.hub.vacation.title", "Resturlaub")} title={allocForm ? `${allocForm.mode === "restore" ? "Rétablir pour" : "Attribuer à"} ${allocForm.emp}` : ""}
          onClose={() => { setSheet(null); setAllocForm(null); }} />
        {allocForm && (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 15 }}>
              {[{ id: "credit", l: "⚙️ Attribuer" }, { id: "restore", l: "↩️ Rétablir" }].map((m) => (
                <Pressable key={m.id} onClick={() => setAllocForm({ ...allocForm, mode: m.id })} style={{
                  flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: allocForm.mode === m.id ? A.orange : P.soft, color: allocForm.mode === m.id ? "#fff" : P.ink,
                  border: `1.5px solid ${allocForm.mode === m.id ? A.orange : P.line}`,
                }}>{m.l}</Pressable>
              ))}
            </div>
            <p style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, margin: "-8px 2px 14px" }}>
              {allocForm.mode === "restore" ? "Recrédite des jours non pris (ex. retour anticipé de vacances), sans toucher au planning." : "Ajoute au quota total attribué (nouvelle année, régularisation…)."}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", borderRadius: 16, background: P.soft, border: `1.5px solid ${P.line}`, overflow: "hidden" }}>
                <HoldButton onTap={() => { setAllocForm((f) => ({ ...f, amount: Math.max(0, f.amount - 1) })); }} onRepeat={() => setAllocForm((f) => ({ ...f, amount: Math.max(0, f.amount - 1) }))} label={t("common.decreaseHoldAria", "Moins (maintenir pour accélérer)")} style={{
                  width: 56, height: 56, display: "grid", placeItems: "center", fontSize: `calc(25px * var(--nx-font-scale, 1))`, fontWeight: 800, background: "none",
                }}>−</HoldButton>
                <div style={{ minWidth: 70, textAlign: "center" }}>
                  <strong style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`}}>{allocForm.amount}</strong>
                  <span style={{ display: "block", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>JOURS</span>
                </div>
                <HoldButton onTap={() => { setAllocForm((f) => ({ ...f, amount: f.amount + 1 })); }} onRepeat={() => setAllocForm((f) => ({ ...f, amount: f.amount + 1 }))} label={t("common.increaseHoldAria", "Plus (maintenir pour accélérer)")} style={{
                  width: 56, height: 56, display: "grid", placeItems: "center", fontSize: `calc(25px * var(--nx-font-scale, 1))`, fontWeight: 800, background: "none",
                }}>＋</HoldButton>
              </div>
              <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, flex: 1 }}>jours à {allocForm.mode === "restore" ? "rétablir" : "créditer"}</span>
            </div>
            <Pressable onClick={saveAllocation} style={{ width: "100%", ...btnSolid(A.orange) }}>
              {allocForm.mode === "restore" ? "Rétablir les jours" : "Créditer les jours"}
            </Pressable>
          </>
        )}
      </Sheet>

      <Sheet open={sheet === "leiEntry"} onClose={() => { setSheet(null); setLeiForm(null); }} P={P} maxH="60%">
        <SheetHead kicker={t("planning.hub.leistungsfrei.title", "Leistungsfrei")} title={leiForm ? `${leiForm.type === "credit" ? "Créditer" : "Débiter"} · ${leiForm.emp}` : ""}
          onClose={() => { setSheet(null); setLeiForm(null); }} />
        {leiForm && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", borderRadius: 16, background: P.soft, border: `1.5px solid ${P.line}`, overflow: "hidden" }}>
                <HoldButton onTap={() => { setLeiForm((f) => ({ ...f, amount: Math.max(0.5, f.amount - 0.5) })); }} onRepeat={() => setLeiForm((f) => ({ ...f, amount: Math.max(0.5, f.amount - 0.5) }))} label={t("common.decreaseHoldAria", "Moins (maintenir pour accélérer)")} style={{
                  width: 56, height: 56, display: "grid", placeItems: "center", fontSize: `calc(25px * var(--nx-font-scale, 1))`, fontWeight: 800, background: "none",
                }}>−</HoldButton>
                <div style={{ minWidth: 70, textAlign: "center" }}>
                  <strong style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`}}>{leiForm.amount}</strong>
                  <span style={{ display: "block", fontSize: `calc(9px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted }}>HEURES</span>
                </div>
                <HoldButton onTap={() => { setLeiForm((f) => ({ ...f, amount: f.amount + 0.5 })); }} onRepeat={() => setLeiForm((f) => ({ ...f, amount: f.amount + 0.5 }))} label={t("common.increaseHoldAria", "Plus (maintenir pour accélérer)")} style={{
                  width: 56, height: 56, display: "grid", placeItems: "center", fontSize: `calc(25px * var(--nx-font-scale, 1))`, fontWeight: 800, background: "none",
                }}>＋</HoldButton>
              </div>
            </div>
            <Field label={t("controlRun.remarkLabel", "Remarque")}>
              <input style={inputStyle} placeholder={t("controlRun.remarkPlaceholder", "Remarque facultative")} value={leiForm.note}
                onChange={(e) => setLeiForm({ ...leiForm, note: e.target.value })} />
            </Field>
            <Pressable onClick={saveLeiEntry} style={{ width: "100%", ...btnSolid(leiForm.type === "credit" ? A.green : A.red) }}>
              {leiForm.type === "credit" ? "Créditer les heures" : "Retirer les heures"}
            </Pressable>
          </>
        )}
      </Sheet>

      <Sheet open={sheet === "ledgerHistory"} onClose={() => setSheet(null)} P={P}>
        <SheetHead kicker={t("planning.lfDetail.historyTitle", "Historique")} title={historyKind === "urlaub" ? t("planning.hub.vacation.title", "Resturlaub") : t("planning.hub.leistungsfrei.title", "Leistungsfrei")} onClose={() => setSheet(null)} />
        {(historyKind === "urlaub" || isManager(user)) && (
          <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 13, paddingBottom: 2 }}>
            {planTeam.map((emp) => {
              const empId = resolveEmployeeId(emp);
              const sel = historyEmpId === empId;
              const c = EMP_COLORS[emp] || A.navy;
              return (
                <Pressable key={emp} onClick={() => { setHistoryEmpId(empId); setHistoryEmpName(emp); }} style={{
                  flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                  background: sel ? c : P.soft, color: sel ? "#fff" : P.ink,
                  border: `1.5px solid ${sel ? c : P.line}`,
                }}>{emp}</Pressable>
              );
            })}
          </div>
        )}
        {(() => {
          const canDelete = historyKind === "urlaub" ? canManageVacation : canManageLf;
          const list = (historyKind === "urlaub" ? vacationTransactions : lfTransactions)
            .filter((t) => t.employeeId === historyEmpId)
            .slice()
            .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
          if (list.length === 0) return <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{t("planning.lfDetail.emptyForEmployeeTemplate", { name: historyEmpName }, `Aucune écriture pour ${historyEmpName}.`)}</p>;
          return list.map((t) => {
            const meta = txMeta(historyKind, t);
            const positive = meta.sign === "+";
            const negative = meta.sign === "−";
            return (
              <div key={t.id} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "11px 12px", borderRadius: 16,
                background: positive ? "rgba(30,184,95,.09)" : negative ? "rgba(239,68,68,.09)" : P.soft, marginBottom: 7,
              }}>
                <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{positive ? "➕" : negative ? "➖" : "•"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: positive ? "#15803d" : negative ? A.red : P.ink }}>
                    {meta.sign}{meta.amount}{historyKind === "urlaub" ? " j" : " h"} · {meta.label}
                  </strong>
                  <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t.comment ? `${t.comment} · ` : ""}{fmtFR(String(t.createdAt).slice(0, 10))}</div>
                </div>
                {canDelete && (
                  <Pressable onClick={() => deleteLedgerEntry(t.id, historyKind)} aria-label={t("common.delete", "Supprimer")} style={{
                    width: 28, height: 28, borderRadius: 99, background: "rgba(33,29,22,.06)",
                    display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  }}>🗑️</Pressable>
                )}
              </div>
            );
          });
        })()}
      </Sheet>

      <Sheet open={sheet === "newHoliday"} onClose={() => setSheet(null)} P={P} maxH="55%">
        <SheetHead kicker={t("planning.hub.holidays.title", "Feiertage")} title={t("planning.holidaysDetail.submitBtn", "Ajouter un jour férié")} onClose={() => setSheet(null)} />
        <Field label={t("common.dateLabel", "Date")}>
          <input style={dateTimeInputStyle} type="date" value={holidayForm.date}
            onChange={(e) => setHolidayForm({ ...holidayForm, date: e.target.value })} />
        </Field>
        <Field label={t("common.nameLabel", "Nom")}>
          <input style={inputStyle} placeholder={t("planning.holiday.namePlaceholder", "Ex. Fermeture exceptionnelle")} value={holidayForm.name}
            onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })} />
        </Field>
        <div style={{ display: "flex", gap: 10 }}>
          <Pressable onClick={() => setSheet(null)} style={{
            flex: 1, textAlign: "center", padding: "14px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14.5px * var(--nx-font-scale, 1))`,
            background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
          }}>{t("common.cancel", "Annuler")}</Pressable>
          <Pressable onClick={saveCustomHoliday} style={{ flex: 1, ...btnSolid(A.red) }}>{t("planning.holidaysDetail.addBtn", "Ajouter")}</Pressable>
        </div>
      </Sheet>

      <Sheet open={sheet === "examCertify"} onClose={() => setSheet(null)} P={P} maxH="80%">
        <SheetHead kicker={t("pluQuiz.beforeStartKicker", "Avant de commencer")} title={t("pluQuiz.certifyTitle", "Certification de passage")} onClose={() => setSheet(null)} />
        <div style={{ ...card, borderRadius: 18, padding: 15, marginBottom: 15, background: "rgba(239,68,68,.06)", border: "1px solid rgba(239,68,68,.2)" }}>
          <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, lineHeight: 1.6, color: P.ink }}>
            {t("pluQuiz.examCertify.prefix", "Je certifie sur l'honneur que je vais réaliser cet examen PLU")} <strong>{t("pluQuiz.examCertify.bold1", "seul(e), sans aide extérieure ni support")}</strong> {t("pluQuiz.examCertify.middle", "(notes, téléphone, collègue),")} <strong>{t("pluQuiz.examCertify.bold2", "du début à la fin")}</strong>{t("pluQuiz.examCertify.suffix", ", dans les conditions réelles d'examen. Je comprends que le résultat sera enregistré et transmis à mon responsable, et que toute irrégularité constatée invalide l'examen.")}
          </p>
        </div>
        <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 7 }}>
          {t("pluQuiz.certify.signatureLabelTemplate", { name: user?.firstname || t("common.youFallback", "Toi") }, `Signature de ${user?.firstname || "Toi"} — trace ta signature avec le doigt`)}
        </span>
        <div style={{
          position: "relative", borderRadius: 16, background: "#fffefb", border: `1.5px dashed ${hasSignature ? A.green : P.line}`,
          marginBottom: 10, overflow: "hidden", touchAction: "none",
        }}>
          <canvas
            ref={sigCanvasRef} width={520} height={180}
            style={{ width: "100%", height: 150, display: "block", touchAction: "none", cursor: "crosshair" }}
            onPointerDown={sigStart} onPointerMove={sigMove} onPointerUp={sigEnd} onPointerLeave={sigEnd}
            onTouchStart={sigStart} onTouchMove={sigMove} onTouchEnd={sigEnd}
          />
          {!hasSignature && (
            <span style={{
              position: "absolute", inset: 0, display: "grid", placeItems: "center",
              fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, pointerEvents: "none",
            }}>{t("importantInfo.signHereText", "✍️ Signe ici")}</span>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <Pressable onClick={clearSignature} style={{
            fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, background: P.soft, borderRadius: 99, padding: "8px 14px",
          }}>{t("common.clearBtn", "Effacer")}</Pressable>
          {hasSignature && (
            <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: "#15803d", fontWeight: 700 }}>
              {t("importantInfo.signatureTracedText", "✓ Signature tracée")} · {new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
        <Pressable onClick={signAndStartExam} style={{
          width: "100%", ...btnSolid(A.red), opacity: hasSignature ? 1 : .5,
        }}>{t("pluQuiz.certify.startBtn", "Signer et commencer l'examen")}</Pressable>
      </Sheet>

      <Sheet open={sheet === "krankDetail"} onClose={() => { setSheet(null); setKrankDetail(null); }} P={P}>
        {(() => {
          const liveDetail = krankDetail;
          if (!liveDetail) return null;
          const isOwner = liveDetail.employeeId === user?.proId;
          return (
            <>
              <SheetHead kicker="Krankmeldung" title={fmtFR(liveDetail.startDate)} onClose={() => { setSheet(null); setKrankDetail(null); }} />
              <span style={{
                display: "inline-block", fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 10px", marginBottom: 12,
                color: liveDetail.generalStatus === "ACTIVE" ? "#b56a00" : "#fff",
                background: liveDetail.generalStatus === "ACTIVE" ? "rgba(231,138,18,.16)" : liveDetail.generalStatus === "CLOSED" ? A.green : liveDetail.generalStatus === "CANCELLED" ? A.red : P.muted,
              }}>{t(`krankmeldung.status.${String(liveDetail.generalStatus || "").toLowerCase()}`, KRANK_GENERAL_STATUS_LABELS[liveDetail.generalStatus])}</span>

              {liveDetail.contactStatus === "PENDING" && liveDetail.generalStatus === "ACTIVE" && (
                <div style={{ display: "flex", gap: 9, borderRadius: 14, padding: 12, marginBottom: 14, background: "rgba(231,138,18,.1)", border: "1px solid rgba(231,138,18,.28)" }}>
                  <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>⚠️</span>
                  <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: "#b56a00", lineHeight: 1.5 }}>{t("krankmeldung.contactsNotAllConfirmedText", "Contacts pas encore tous confirmés.")}</span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "9px 2px", borderBottom: `1px solid ${P.line}` }}>
                <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("krankmeldung.summary.period", "Période")}</span>
                <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{fmtFR(liveDetail.startDate)}{liveDetail.endDateUnknown ? ` → ${t("krankmeldung.endUnknownSuffix", "fin inconnue")}` : liveDetail.expectedEndDate ? ` → ${fmtFR(liveDetail.expectedEndDate)}` : ""}</strong>
              </div>
              {liveDetail.actualReturnDate && (
                <div style={{ display: "flex", justifyContent: "space-between", padding: "9px 2px", borderBottom: `1px solid ${P.line}` }}>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("krankmeldung.announcedReturnLabel", "Retour annoncé")}</span>
                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{fmtFR(liveDetail.actualReturnDate)}</strong>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 2px", borderBottom: `1px solid ${P.line}` }}>
                <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>AU</span>
                <strong style={{
                  fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  color: liveDetail.auStatus === "REJECTED" ? A.red : liveDetail.auStatus === "VERIFIED" ? A.green : P.ink,
                }}>{t(`krankmeldung.auStatus.${{ NOT_REQUIRED: "notRequired", PENDING: "pending", UPLOADED: "uploaded", VERIFIED: "verified", REJECTED: "rejected" }[liveDetail.auStatus]}`, AU_STATUS_LABELS[liveDetail.auStatus])}</strong>
              </div>
              {liveDetail.attachments.map((a) => (
                <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 2px" }}>
                  <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>📎</span>
                  <a href={`/api/krankmeldungen/${liveDetail.id}/attachments/${a.id}`} target="_blank" rel="noreferrer" style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: A.blue, flex: 1, textDecoration: "underline", wordBreak: "break-all" }}>{a.originalName}</a>
                  {a.status === "uploaded" && canManageKrank && (
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <Pressable onClick={() => setKrankAuStatus(liveDetail.id, a.id, "VERIFIED")} style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: A.green, borderRadius: 99, padding: "5px 9px" }}>✓</Pressable>
                      <Pressable onClick={() => setKrankAuStatus(liveDetail.id, a.id, "REJECTED")} style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.red, background: "rgba(239,68,68,.12)", borderRadius: 99, padding: "5px 9px" }}>✕</Pressable>
                    </div>
                  )}
                </div>
              ))}

              {liveDetail.employeeComment && (
                <div style={{ padding: "9px 2px", borderBottom: `1px solid ${P.line}` }}>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700, display: "block", marginBottom: 3 }}>{t("krankmeldung.employeeCommentLabel", "Commentaire du salarié")}</span>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{liveDetail.employeeComment}</span>
                </div>
              )}
              {liveDetail.managerComment && (
                <div style={{ padding: "9px 2px", borderBottom: `1px solid ${P.line}` }}>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700, display: "block", marginBottom: 3 }}>{t("krankmeldung.managerCommentLabel", "Commentaire manager")}</span>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{liveDetail.managerComment}</span>
                </div>
              )}

              <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "14px 2px 9px" }}>{t("krankmeldung.detail.contactsTitle", "Contacts")}</strong>
              {liveDetail.requiredContacts.length === 0 && <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 8px" }}>{t("krankmeldung.noRequiredContactText", "Aucun contact obligatoire pour ce poste.")}</p>}
              {liveDetail.requiredContacts.map((c, i) => {
                const contextText = krankContactContextText(c);
                return (
                <Pressable key={i} onClick={() => (c.status !== "pending" && c.status) || !canManageKrank ? null : openContactConfirm(liveDetail.id, c.userId, c.name || c.role)} style={{
                  display: "flex", alignItems: "center", gap: 9, width: "100%", boxSizing: "border-box", padding: "9px 11px", borderRadius: 12,
                  background: (c.status !== "pending") ? "rgba(30,184,95,.09)" : P.soft, marginBottom: 6, opacity: (c.status === "pending" && !canManageKrank) ? .7 : 1,
                }}>
                  <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{(c.status !== "pending") ? "☑️" : "⬜️"}</span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, display: "block" }}>{c.name || c.role}</span>
                    {contextText && <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.orange, display: "block" }}>🔄 {contextText}</span>}
                    <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: c.status !== "pending" ? "#15803d" : P.muted }}>{t(`krankmeldung.contactStatus.${{ pending: "pending", attempted: "attempted", reached: "reached", message_left: "messageLeft", unreachable: "unreachable", not_in_system: "notInSystem" }[c.status]}`, CONTACT_STATUS_LABELS[c.status])}</span>
                  </div>
                </Pressable>
                );
              })}

              {liveDetail.affectedShifts.length > 0 && (
                <>
                  <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "14px 2px 9px" }}>{t("krankmeldung.detail.shiftsTitle", "Services concernés")}</strong>
                  {liveDetail.affectedShifts.map((s) => (
                    <div key={s.entryId} style={{ ...card, padding: 12, marginBottom: 8, flexDirection: "column", alignItems: "stretch" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, flex: 1 }}>{fmtFR(s.date)} · {s.start}–{s.end}</span>
                        <span style={{
                          fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "3px 8px", whiteSpace: "nowrap",
                          color: s.planChangeStatus === "ASSIGNED" ? "#fff" : s.planChangeStatus === "LEFT_UNCOVERED" ? A.red : "#b56a00",
                          background: s.planChangeStatus === "ASSIGNED" ? A.green : s.planChangeStatus === "LEFT_UNCOVERED" ? "rgba(239,68,68,.12)" : "rgba(231,138,18,.16)",
                        }}>{s.planChangeStatus === "ASSIGNED" ? t("krankmeldung.shiftStatus.replaced", "Remplacé") : s.planChangeStatus === "LEFT_UNCOVERED" ? t("krankmeldung.shiftStatus.uncovered", "Non couvert") : t("krankmeldung.shiftStatus.toHandle", "À traiter")}</span>
                      </div>
                      {canManageKrank && liveDetail.generalStatus === "ACTIVE" && (
                        <Pressable onClick={() => openKrankReplacement(s.entryId)} style={{ marginTop: 8, fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.blue, textAlign: "left" }}>
                          {s.planChangeStatus === "ASSIGNED" ? t("krankmeldung.changeReplacementBtn", "Changer le remplaçant") : t("krankmeldung.assignReplacementBtn", "Assigner un remplaçant")}
                        </Pressable>
                      )}
                    </div>
                  ))}
                </>
              )}

              {krankReplacementFor && (
                <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 13, marginBottom: 12, background: "rgba(54,79,231,.06)" }}>
                  <strong style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, marginBottom: 8, display: "block" }}>{t("planning.chooseReplacementTitle", "Choisir un remplaçant")}</strong>
                  {krankReplacementCandidates.length === 0 && <p style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 0 8px" }}>{t("krankmeldung.detail.noCandidates", "Aucun candidat disponible ce jour-là.")}</p>}
                  {krankReplacementCandidates.map((cand) => (
                    <Pressable key={cand.employeeId} onClick={() => assignKrankReplacement(cand.employeeId)} style={{
                      display: "flex", justifyContent: "space-between", padding: "9px 11px", borderRadius: 12, marginBottom: 6, background: P.card, border: `1px solid ${P.line}`,
                    }}>
                      <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{cand.name}{cand.isMinor ? t("krankmeldung.detail.minorSuffix", " (mineur)") : ""}</span>
                      <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{cand.role}</span>
                    </Pressable>
                  ))}
                  <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                    <Pressable onClick={() => assignKrankReplacement(null)} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: "rgba(239,68,68,.12)", color: A.red }}>{t("krankmeldung.detail.leaveUncoveredBtn", "Laisser non couvert")}</Pressable>
                    <Pressable onClick={() => setKrankReplacementFor(null)} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>{t("common.cancel", "Annuler")}</Pressable>
                  </div>
                </div>
              )}

              {isOwner && liveDetail.generalStatus === "ACTIVE" && (
                <>
                  <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "14px 2px 9px" }}>{t("krankmeldung.extendReturnTitle", "Prolongation / retour")}</strong>
                  {krankExtendForm ? (
                    <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 13, marginBottom: 10 }}>
                      <Field label={t("krankmeldung.newExpectedEndDateLabel", "Nouvelle date de fin estimée")}>
                        <input style={dateTimeInputStyle} type="date" disabled={krankExtendForm.endDateUnknown} min={liveDetail.expectedEndDate || liveDetail.startDate} value={krankExtendForm.newExpectedEndDate} onChange={(e) => setKrankExtendForm({ ...krankExtendForm, newExpectedEndDate: e.target.value })} />
                      </Field>
                      <Pressable onClick={() => setKrankExtendForm({ ...krankExtendForm, endDateUnknown: !krankExtendForm.endDateUnknown })} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                        <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{krankExtendForm.endDateUnknown ? "☑️" : "⬜️"}</span><span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{t("krankmeldung.detail.extendUnknownLabel", "Fin toujours inconnue")}</span>
                      </Pressable>
                      <div style={{ display: "flex", gap: 8 }}>
                        <Pressable onClick={submitKrankExtend} style={{ flex: 1, ...btnSolid(A.orange) }}>{t("common.confirm", "Confirmer")}</Pressable>
                        <Pressable onClick={() => setKrankExtendForm(null)} style={{ flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>{t("common.cancel", "Annuler")}</Pressable>
                      </div>
                    </div>
                  ) : krankEarlyReturnForm ? (
                    <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 13, marginBottom: 10 }}>
                      <Field label={t("krankmeldung.returnDateLabel", "Date de retour")}>
                        <input style={dateTimeInputStyle} type="date" min={liveDetail.startDate} value={krankEarlyReturnForm.actualReturnDate} onChange={(e) => setKrankEarlyReturnForm({ ...krankEarlyReturnForm, actualReturnDate: e.target.value })} />
                      </Field>
                      <div style={{ display: "flex", gap: 8 }}>
                        <Pressable onClick={submitKrankEarlyReturn} style={{ flex: 1, ...btnSolid(A.teal) }}>{t("common.confirm", "Confirmer")}</Pressable>
                        <Pressable onClick={() => setKrankEarlyReturnForm(null)} style={{ flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>{t("common.cancel", "Annuler")}</Pressable>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                      <Pressable onClick={() => setKrankExtendForm({ newExpectedEndDate: liveDetail.expectedEndDate, endDateUnknown: liveDetail.endDateUnknown, employeeComment: "" })} style={{ flex: 1, textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}` }}>{t("krankmeldung.extendBtn", "Prolonger")}</Pressable>
                      <Pressable onClick={() => setKrankEarlyReturnForm({ actualReturnDate: todayISO(), employeeComment: "" })} style={{ flex: 1, textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}` }}>{t("krankmeldung.earlyReturnBtn", "Retour anticipé")}</Pressable>
                    </div>
                  )}
                </>
              )}

              {canManageKrank && liveDetail.generalStatus === "ACTIVE" && (
                <>
                  <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "14px 2px 9px" }}>{t("krankmeldung.managerActionsTitle", "Actions manager")}</strong>
                  {krankCorrectForm ? (
                    <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 13, marginBottom: 10 }}>
                      <Field label={t("krankmeldung.detail.correctStartLabel", "Premier jour d'absence")}><input style={dateTimeInputStyle} type="date" value={krankCorrectForm.startDate} onChange={(e) => setKrankCorrectForm({ ...krankCorrectForm, startDate: e.target.value })} /></Field>
                      {!krankCorrectForm.endDateUnknown && (
                        <Field label={t("krankmeldung.detail.correctEndLabel", "Date de fin prévue")}><input style={dateTimeInputStyle} type="date" min={krankCorrectForm.startDate} value={krankCorrectForm.expectedEndDate} onChange={(e) => setKrankCorrectForm({ ...krankCorrectForm, expectedEndDate: e.target.value })} /></Field>
                      )}
                      <Pressable onClick={() => setKrankCorrectForm({ ...krankCorrectForm, endDateUnknown: !krankCorrectForm.endDateUnknown })} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                        <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{krankCorrectForm.endDateUnknown ? "☑️" : "⬜️"}</span><span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{t("krankmeldung.detail.correctUnknownLabel", "Fin toujours inconnue")}</span>
                      </Pressable>
                      <div style={{ display: "flex", gap: 8 }}>
                        <Pressable onClick={submitKrankCorrect} style={{ flex: 1, ...btnSolid(A.navy) }}>{t("krankmeldung.detail.confirmCorrectBtn", "Enregistrer la correction")}</Pressable>
                        <Pressable onClick={() => setKrankCorrectForm(null)} style={{ flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>{t("common.cancel", "Annuler")}</Pressable>
                      </div>
                    </div>
                  ) : (
                    <Pressable onClick={() => setKrankCorrectForm({ startDate: liveDetail.startDate, expectedEndDate: liveDetail.expectedEndDate, endDateUnknown: liveDetail.endDateUnknown })} style={{
                      width: "100%", textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, marginBottom: 10,
                    }}>{t("krankmeldung.detail.correctDatesBtn", "Corriger les dates")}</Pressable>
                  )}
                  {canCloseKrank && (
                    <Pressable onClick={() => closeKrankmeldung(liveDetail.id)} style={{ width: "100%", ...btnSolid(A.green), marginBottom: 10 }}>{t("krankmeldung.closeCaseBtn", "Clôturer le dossier")}</Pressable>
                  )}
                </>
              )}

              {liveDetail.history.length > 0 && (
                <>
                  <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "14px 2px 9px" }}>{t("krankmeldung.detail.historyTitle", "Historique")}</strong>
                  {liveDetail.history.slice().reverse().slice(0, 10).map((h) => (
                    <div key={h.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 2px", fontSize: `calc(11px * var(--nx-font-scale, 1))`}}>
                      <span style={{ color: P.muted }}>{t(`krankmeldung.history.${{ created: "created", edited: "edited", submitted: "submitted", extended: "extended", early_return_requested: "earlyReturnRequested", contact_status: "contactStatusUpdated", au_uploaded: "auUploaded", au_accessed: "auAccessed", au_verified: "auVerified", au_reminder_sent: "auReminderSent", corrected: "corrected", closed: "closed", cancelled: "cancelled" }[h.eventType] || h.eventType}`, KRANK_HISTORY_LABELS[h.eventType] || h.eventType)} · {h.actorNameSnapshot}</span>
                      <span style={{ color: P.muted, flexShrink: 0 }}>{String(h.timestamp).slice(0, 16).replace("T", " ")}</span>
                    </div>
                  ))}
                </>
              )}

              {(isOwner || canManageKrank) && !["CLOSED", "CANCELLED"].includes(liveDetail.generalStatus) && (
                <Pressable onClick={() => cancelKrankmeldung(liveDetail.id)} style={{
                  width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, marginTop: 15, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                  background: "rgba(239,68,68,.12)", color: A.red,
                }}>{t("krankmeldung.cancelDeclarationBtn", "Annuler cette déclaration")}</Pressable>
              )}
              {canManageKrank && (
                <Pressable onClick={() => deleteKrankmeldung(liveDetail)} style={{
                  width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, marginTop: 10, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                  background: A.red, color: "#fff",
                }}>{t("krankmeldung.deleteDeclarationBtn", "🗑️ Supprimer définitivement")}</Pressable>
              )}
            </>
          );
        })()}
      </Sheet>

      <Sheet open={sheet === "mhdExport"} onClose={() => setSheet(null)} P={P} maxH="55%">
        <SheetHead kicker={t("control.newArticlesTag", "Kühlung")} title={t("control.kuhlung.exportTitle", "Exporter la liste des MHD")} onClose={() => setSheet(null)} />
        <p style={{ margin: "0 0 14px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{t("control.mhdPdfExportDescText", "Ouvre un PDF imprimable des MHD Kühlung, filtré par urgence.")}</p>
        {["all", "red", "orange", "green"].map((f) => (
          <Pressable key={f} onClick={() => exportMhdKuhlungPdf(f)} style={{
            display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box", padding: 14, borderRadius: 16, marginBottom: 8,
            background: P.soft, border: `1.5px solid ${P.line}`,
          }}>
            {f !== "all" && <span style={{ width: 9, height: 9, borderRadius: 99, background: urgColor[f] }} />}
            <span style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{t({ all: "controlRun.exportFilterAll", red: "controlRun.exportFilterUrgent", orange: "controlRun.exportFilterSoon", green: "controlRun.exportFilterOk" }[f], MHD_EXPORT_FILTER_LABELS[f])}</span>
          </Pressable>
        ))}
      </Sheet>

      <Sheet open={sheet === "adminStoreEdit"} onClose={() => { setSheet(null); setAdminStoreForm(null); }} P={P} maxH="75%">
        <SheetHead kicker={t("settings.networkKicker", "Réseau")} title={adminStoreForm?.isNew ? t("admin.createStoreTitle", "Créer une filiale") : t("admin.editStoreTitle", "Modifier la filiale")} onClose={() => { setSheet(null); setAdminStoreForm(null); }} />
        {adminStoreForm && (
          <>
            <Field label={t("admin.storeIdFieldLabel", "Identifiant filiale")}>
              <StoreIdInput value={adminStoreForm.id} placeholder={t("admin.storeIdPlaceholder", "Numéro")}
                disabled={!adminStoreForm.isNew}
                onChange={(v) => setAdminStoreForm((f) => ({ ...f, id: v }))} />
            </Field>
            <Field label={t("admin.storeLabelFieldLabel", "Label")}>
              <input style={inputStyle} value={adminStoreForm.label} placeholder={t("admin.storeDisplayNamePlaceholder", "Nom affiché de la filiale")}
                onChange={(e) => setAdminStoreForm((f) => ({ ...f, label: e.target.value }))} />
            </Field>
            <Field label={t("admin.storeAddressFieldLabel", "Adresse (facultatif)")}>
              <input style={inputStyle} value={adminStoreForm.address}
                onChange={(e) => setAdminStoreForm((f) => ({ ...f, address: e.target.value }))} />
            </Field>
            <Field label={t("admin.storeLandFieldLabel", "Land (jours fériés régionaux)")}>
              <select style={inputStyle} value={adminStoreForm.land}
                onChange={(e) => setAdminStoreForm((f) => ({ ...f, land: e.target.value }))}>
                <option value="">{t("common.notDefinedOption", "— Non défini —")}</option>
                {Object.entries(germanLander).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </Field>
            {/* Heures d'ouverture/fermeture (2026-08-31, retour utilisateur explicite) — une
                seule plage quotidienne valable tous les jours OUVRÉS de la filiale (dimanche +
                jours fériés déjà fermés via le Land ci-dessus, jamais dupliqué ici). Vide =
                compte à rebours désactivé pour cette filiale (Accueil/Réductions/popup
                Tâches gardent alors leur comportement d'avant, voir computeStoreOpenStatus). */}
            {/* dateTimeInputStyle, jamais inputStyle nu (bug réel iOS Safari déjà documenté,
                voir son commentaire à la déclaration) — input[type=time] ignore box-sizing:
                border-box sur WebKit iOS, ~31px de débordement sinon. minWidth:0 sur les deux
                colonnes flex, sans quoi chaque enfant refuse de rétrécir sous sa largeur
                intrinsèque et déborde du Sheet (bug réel signalé avec capture, 2026-08-31 :
                "le contenu du popup ne tient pas en place"). */}
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Field label={t("admin.storeOpenTimeFieldLabel", "Ouverture")}>
                  <input type="time" style={dateTimeInputStyle} value={adminStoreForm.openTime}
                    onChange={(e) => setAdminStoreForm((f) => ({ ...f, openTime: e.target.value }))} />
                </Field>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Field label={t("admin.storeCloseTimeFieldLabel", "Fermeture")}>
                  <input type="time" style={dateTimeInputStyle} value={adminStoreForm.closeTime}
                    onChange={(e) => setAdminStoreForm((f) => ({ ...f, closeTime: e.target.value }))} />
                </Field>
              </div>
            </div>
            <p style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 0 16px", lineHeight: 1.5 }}>
              Cette filiale démarre à zéro (retour utilisateur explicite) : aucune donnée
              héritée d'une autre filiale, catalogue et équipe à construire ensuite.
            </p>
            <Pressable onClick={saveAdminStore} style={{ width: "100%", ...btnSolid(A.green), opacity: adminStoreBusy ? .6 : 1 }}>
              {adminStoreBusy ? "Enregistrement..." : "Enregistrer"}
            </Pressable>
          </>
        )}
      </Sheet>

      <Sheet open={sheet === "password"} onClose={() => setSheet(null)} P={P} maxH="65%">
        <SheetHead kicker={t("settings.securityKicker", "Sécurité")} title={t("settings.password.title", "Changer le mot de passe")} onClose={() => setSheet(null)} />
        <Field label={t("settings.password.old", "Ancien mot de passe")}>
          <PasswordInput style={inputStyle} P={P} t={t} value={pwForm.old} onChange={(e) => setPwForm({ ...pwForm, old: e.target.value })} />
        </Field>
        <Field label={t("settings.password.new", "Nouveau mot de passe")}>
          <PasswordInput style={inputStyle} P={P} t={t} value={pwForm.new1} onChange={(e) => setPwForm({ ...pwForm, new1: e.target.value })} />
        </Field>
        <Field label={t("settings.password.confirm", "Confirmer")}>
          <PasswordInput style={inputStyle} P={P} t={t} value={pwForm.new2} onChange={(e) => setPwForm({ ...pwForm, new2: e.target.value })} />
        </Field>
        {pwMessage && <p style={{ color: A.red, fontSize: `calc(12px * var(--nx-font-scale, 1))`, margin: "0 2px 13px" }}>{pwMessage}</p>}
        <Pressable onClick={changePassword} style={{ width: "100%", ...btnSolid(A.orange) }}>{t("common.save", "Enregistrer")}</Pressable>
      </Sheet>

      <Sheet open={sheet === "myStores"} onClose={() => { setSheet(null); setMyStoresAdding(false); setMyStoresQuery(""); setMyStoreConfirm(null); }} P={P} maxH="80%">
        <SheetHead kicker={t("settings.networkKicker", "Réseau")} title={t("settings.myStores.title", "Mes filiales")} onClose={() => { setSheet(null); setMyStoresAdding(false); setMyStoresQuery(""); setMyStoreConfirm(null); }} />
        <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px 16px", lineHeight: 1.5 }}>
          Les filiales sous ta responsabilité — Pilotage réseau (Filiales, Analyse, réseau MHD) ne montre que celles-ci.
        </p>
        {(() => {
          const myStores = user?.stores?.length ? user?.stores : [user?.store];
          const storeLabelById = new Map(storesCatalog.map((s) => [s.id, s.label || s.id]));
          const q = myStoresQuery.trim().toUpperCase();
          // Périmètre Niederlassung — myAddableStores est déjà borné côté serveur (voir
          // GET /api/me/addable-stores), jamais storesCatalog (registre entier) ici.
          const candidates = myAddableStores.filter((s) => !myStores.includes(s.id) && (!q || s.id.includes(q) || (s.label || "").toUpperCase().includes(q)));
          return (
            <>
              {myStores.map((id) => (
                <div key={id} style={{ ...card, ...tile, padding: 13, marginBottom: 8 }}>
                  <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>🏬</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{storeLabelById.get(id) || id}</strong>
                    <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{id}{id === user?.store ? " · filiale principale" : ""}</div>
                  </div>
                  {myStores.length > 1 && (
                    <Pressable onClick={() => removeMyStore(id)} disabled={myStoresBusy} aria-label={t("settings.myStores.removeStoreAriaTemplate", { id }, `Retirer ${id}`)} style={{
                      fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.red, background: "rgba(239,68,68,.12)", borderRadius: 99, padding: "7px 12px", opacity: myStoresBusy ? .6 : 1,
                    }}>{t("settings.photo.removeButton", "Retirer")}</Pressable>
                  )}
                </div>
              ))}

              {!myStoresAdding ? (
                <Pressable onClick={() => { setMyStoresAdding(true); loadMyAddableStores(); }} style={{
                  width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                  background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink, marginTop: 6,
                }}>＋ Ajouter une filiale</Pressable>
              ) : !myStoresNlResolved ? (
                // Aucun Niederlassung identifiable dans la hiérarchie de ce compte (reportsTo
                // manquant/cassé) — jamais un repli permissif, on bloque et on explique
                // clairement plutôt que de laisser deviner (même principe que le blocage
                // serveur, voir sfUserNiederlassungId).
                <div style={{ ...card, padding: 14, marginTop: 6, background: "rgba(239,68,68,.07)", border: `1.5px solid ${A.red}40` }}>
                  <p style={{ color: A.red, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: 0, lineHeight: 1.5, fontWeight: 600 }}>
                    Aucun Niederlassungsleiter identifié dans ta hiérarchie — contacte un administrateur avant de pouvoir ajouter une filiale.
                  </p>
                </div>
              ) : myStoreConfirm ? (
                // Confirmation explicite avant tout ajout (retour utilisateur explicite,
                // 2026-08-22) — jamais un ajout direct au tap, y compris pour le cas d'usage
                // "je couvre un pair de ma Niederlassung pendant son absence" : un geste
                // volontaire, pas un mis-clic dans une liste.
                <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 16, marginTop: 6 }}>
                  <p style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, margin: "0 0 14px", lineHeight: 1.5 }}>
                    Ajouter <strong>{myStoreConfirm.label || myStoreConfirm.id}</strong> ({myStoreConfirm.id}) à ton périmètre ?
                  </p>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Pressable onClick={() => setMyStoreConfirm(null)} disabled={myStoresBusy} style={{
                      flex: 1, textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                      background: P.soft, color: P.ink,
                    }}>{t("common.cancel", "Annuler")}</Pressable>
                    <Pressable onClick={() => addMyStore(myStoreConfirm.id, myStoreConfirm.label)} disabled={myStoresBusy} style={{
                      flex: 1, textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                      background: A.blue, color: "#fff", opacity: myStoresBusy ? .6 : 1,
                    }}>{myStoresBusy ? t("common.sending", "Envoi...") : t("common.confirm", "Confirmer")}</Pressable>
                  </div>
                </div>
              ) : (
                <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 14, marginTop: 6 }}>
                  {/* Un BL choisit uniquement parmi les filiales déjà créées ET déjà dans SA
                      Niederlassung (voir myAddableStores, GET /api/me/addable-stores) — jamais
                      de création à la volée (retour utilisateur explicite historique : les
                      filiales existent déjà, un BL se les voit attribuer, il ne les invente
                      pas), et depuis 2026-08-22 jamais hors de son Niederlassung non plus.
                      Créer une filiale = Admin > Filiales, NEXORA_ADMIN. */}
                  <Field label={t("settings.myStores.searchExistingLabel", "Rechercher une filiale existante")}>
                    <input style={inputStyle} value={myStoresQuery} autoCapitalize="characters" placeholder={t("settings.myStores.searchPlaceholder", "Ex. N412")}
                      onChange={(e) => setMyStoresQuery(e.target.value.toUpperCase())} />
                  </Field>
                  <div style={{ maxHeight: 220, overflowY: "auto" }}>
                    {candidates.map((s) => (
                      <Pressable key={s.id} onClick={() => setMyStoreConfirm({ id: s.id, label: s.label })} disabled={myStoresBusy} style={{
                        display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                        padding: 12, borderRadius: 14, marginBottom: 6, background: P.soft,
                      }}>
                        <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>🏬</span>
                        <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                          <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{s.label || s.id}</strong>
                          <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{s.id}</div>
                        </div>
                      </Pressable>
                    ))}
                    {candidates.length === 0 && (
                      <p style={{ color: P.muted, fontSize: `calc(12px * var(--nx-font-scale, 1))`, margin: "6px 2px" }}>
                        {q ? "Aucune filiale de ta Niederlassung ne correspond." : "Toutes les filiales de ta Niederlassung sont déjà les tiennes."}
                        {" "}Une filiale manquante ? Demande à un administrateur (Admin &gt; Filiales).
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </Sheet>

      <Sheet open={sheet === "sessions"} onClose={() => setSheet(null)} P={P}>
        <SheetHead kicker={t("settings.supervisionKicker", "Supervision")} title={t("settings.sessions.title", "Sessions actives")} onClose={() => setSheet(null)} />
        {adminSessions.length === 0 ? (
          <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("admin.sessionsSupervisionSuperAdminOnlyText", "Rien à afficher — la supervision des sessions est réservée au compte super-admin.")}</p>
        ) : (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <div style={{ flex: 1, textAlign: "center", background: P.soft, borderRadius: 14, padding: "10px 0" }}>
                <strong style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, color: A.green, display: "block" }}>{adminSessions.filter((s) => s.online).length}</strong>
                <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{t("common.onlineTag", "En ligne")}</span>
              </div>
              <div style={{ flex: 1, textAlign: "center", background: P.soft, borderRadius: 14, padding: "10px 0" }}>
                <strong style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, color: P.muted, display: "block" }}>{adminSessions.filter((s) => !s.online).length}</strong>
                <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase" }}>{t("common.offlineTag", "Hors ligne")}</span>
              </div>
            </div>
            {adminSessions.map((s) => (
              <div key={s.employeeId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 2px", borderBottom: `1px solid ${P.line}` }}>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: s.online ? A.green : P.line, flex: "0 0 auto" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{s.name}</strong>
                  <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{s.role} · {s.storeId}</div>
                </div>
                <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, color: s.online ? "#15803d" : P.muted }}>{s.online ? t("common.onlineTag", "En ligne") : formatLastSeen(s.lastActivityAt ? Math.round((Date.now() - new Date(s.lastActivityAt).getTime()) / 60000) : null)}</span>
              </div>
            ))}
          </>
        )}
      </Sheet>

      {/* Proposition Face ID/Touch ID/Windows Hello (retour utilisateur explicite,
          2026-08-18) — affichée juste après une connexion par mot de passe réussie sur un
          appareil qui supporte la biométrie et n'a pas encore d'enregistrement pour ce
          compte (voir doLogin). Jamais forcée : "Plus tard" ferme simplement le sheet, rien
          n'est proposé de nouveau avant la prochaine connexion par mot de passe. */}
      <Sheet open={sheet === "webauthnOffer"} onClose={() => setSheet(null)} P={P}>
        <div style={{ textAlign: "center", padding: "8px 4px 4px" }}>
          <span style={{ fontSize: `calc(38px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 12 }}>🔐</span>
          <strong style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 6 }}>{t("auth.webauthn.offerTitle", "Activer Face ID pour la prochaine fois ?")}</strong>
          <p style={{ margin: "0 0 20px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{t("auth.webauthn.offerBody", "Connecte-toi la prochaine fois sans retaper ton Pro-ID ni ton mot de passe sur cet appareil.")}</p>
          <Pressable onClick={registerWebauthn} style={{ width: "100%", ...btnSolid(A.blue), padding: "14px 0", fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, marginBottom: 8, opacity: webauthnBusy ? .6 : 1 }}>
            {webauthnBusy ? t("auth.webauthn.registerBusy", "Activation...") : t("auth.webauthn.offerAccept", "Activer Face ID")}
          </Pressable>
          <Pressable onClick={() => setSheet(null)} style={{ width: "100%", textAlign: "center", padding: "12px 0", fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted }}>
            {t("auth.webauthn.offerDecline", "Plus tard")}
          </Pressable>
        </div>
      </Sheet>

      {/* Gestion Face ID/Touch ID depuis Réglages (retour utilisateur explicite, 2026-08-29)
          — activer à la demande (pas seulement l'offre unique post-connexion) + voir/retirer
          les appareils déjà enregistrés (GET/DELETE /api/webauthn/credentials). */}
      <Sheet open={sheet === "webauthnManage"} onClose={() => setSheet(null)} P={P}>
        <SheetHead kicker={t("settings.section.account.headerTitle", "Compte & sécurité")} title={t("settings.webauthn.title", "Face ID / Touch ID")} onClose={() => setSheet(null)} />
        {!webauthnAvailable ? (
          <p style={{ margin: "0 0 16px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{t("settings.webauthn.unavailableOnDevice", "Cet appareil ne supporte pas la biométrie (Face ID/Touch ID).")}</p>
        ) : webauthnHint?.login === user?.proId ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(30,184,95,.1)", border: `1px solid rgba(30,184,95,.25)`, borderRadius: 14, padding: "12px 14px", marginBottom: 16 }}>
            <span style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>✓</span>
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.green }}>{t("settings.webauthn.enabledSubtitle", "Activé sur cet appareil")}</span>
          </div>
        ) : (
          <Pressable onClick={async () => { await registerWebauthn(); setSheet("webauthnManage"); loadWebauthnDevices(); }} style={{ width: "100%", ...btnSolid(A.purple), padding: "13px 0", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, marginBottom: 16, opacity: webauthnBusy ? .6 : 1 }}>
            {webauthnBusy ? t("auth.webauthn.registerBusy", "Activation...") : t("settings.webauthn.enableOnDeviceBtn", "Activer sur cet appareil")}
          </Pressable>
        )}
        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".04em", display: "block", marginBottom: 8 }}>{t("settings.webauthn.devicesListTitle", "Appareils enregistrés")}</span>
        {webauthnDevicesLoading ? (
          <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12px * var(--nx-font-scale, 1))`}}>{t("common.loading", "Chargement…")}</p>
        ) : webauthnDevices.length === 0 ? (
          <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12px * var(--nx-font-scale, 1))`}}>{t("settings.webauthn.noDevicesText", "Aucun appareil enregistré.")}</p>
        ) : (
          webauthnDevices.map((d) => (
            <div key={d.credentialId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: `1px solid ${P.line}` }}>
              <span style={{ width: 32, height: 32, borderRadius: 10, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>📱</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block" }}>{d.deviceLabel || t("settings.webauthn.unnamedDevice", "Appareil")}</strong>
                <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>{fmtFR((d.createdAt || "").slice(0, 10))}</span>
              </div>
              <Pressable onClick={() => removeWebauthnDevice(d.credentialId)} aria-label={t("settings.webauthn.removeAria", "Retirer cet appareil")} style={{ width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.1)", display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: A.red }}>🗑</Pressable>
            </div>
          ))
        )}
      </Sheet>

      {/* Hauteur figée sur --nx-vh (gelée pendant la saisie, cf. maybeSetNxVh) plutôt qu'un
          "%" brut résolu contre le viewport visuel réel : avec 8+ champs à parcourir au
          clavier, chaque ouverture/fermeture du clavier entre deux champs redimensionnait
          la sheet elle-même (donc tout son contenu) — c'est ce qui faisait "bouger dans
          tous les sens" (retour utilisateur 2026-07-31), pas juste un focus perdu. */}
      <Sheet open={sheet === "userEdit"} onClose={() => { setSheet(null); setUserEditForm(null); }} P={P} maxH="calc(var(--nx-vh, 1vh) * 82)">
        {userEditForm && (() => {
          const roleColor = A.blue;
          const section = (label, emoji) => (
            <div style={{ display: "flex", alignItems: "center", gap: 7, margin: "18px 2px 9px" }}>
              <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{emoji}</span>
              <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".07em" }}>{label}</span>
            </div>
          );
          return (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
                <span style={{
                  width: 52, height: 52, borderRadius: 18, flex: "0 0 auto", color: "#fff",
                  background: `linear-gradient(155deg, rgba(255,255,255,.28), rgba(255,255,255,0) 55%), ${EMP_COLORS[userEditForm.firstname] || A.navy}`,
                  border: "1px solid rgba(255,255,255,.18)", boxShadow: `0 3px 8px rgba(0,0,0,.12), 0 10px 20px ${(EMP_COLORS[userEditForm.firstname] || A.navy)}40`,
                  display: "grid", placeItems: "center", fontSize: `calc(19px * var(--nx-font-scale, 1))`, fontWeight: 800,
                }}>{(userEditForm.firstname || "?").charAt(0)}{(userEditForm.lastname || "").charAt(0)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={eyebrow(roleColor)}>{t("admin.userEdit.eyebrow", "Fiche personnel")}</span>
                  <strong style={{ display: "block", fontSize: `calc(18px * var(--nx-font-scale, 1))`, marginTop: 1, letterSpacing: "-.02em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {userEditForm.firstname || "—"} {userEditForm.lastname || ""}
                  </strong>
                </div>
                <Pressable onClick={() => { setSheet(null); setUserEditForm(null); }} aria-label={t("common.close", "Fermer")} style={{
                  width: 32, height: 32, borderRadius: 99, background: P.soft, color: P.ink,
                  display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                }}>✕</Pressable>
              </div>

              {section(t("admin.userEdit.sectionIdentity", "Identité"), "🪪")}
              <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 14 }}>
                <Field label={t("common.nameLabel", "Nom")}>
                  <input style={inputStyle} value={userEditForm.lastname} onChange={(e) => setUserEditForm({ ...userEditForm, lastname: e.target.value })} />
                </Field>
                <Field label={t("auth.firstnameLabel", "Prénom")}>
                  <input style={inputStyle} value={userEditForm.firstname} onChange={(e) => setUserEditForm({ ...userEditForm, firstname: e.target.value })} />
                </Field>
                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>{t("auth.genderLabel", "Sexe")}</span>
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  {[{ id: "homme", l: t("auth.genderMale", "Homme") }, { id: "femme", l: t("auth.genderFemale", "Femme") }].map((g) => (
                    <Pressable key={g.id} onClick={() => setUserEditForm({ ...userEditForm, gender: userEditForm.gender === g.id ? "" : g.id })} style={{
                      flex: 1, textAlign: "center", padding: "11px 0", borderRadius: 14, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                      background: userEditForm.gender === g.id ? A.blue : P.soft, color: userEditForm.gender === g.id ? "#fff" : P.ink,
                    }}>{g.l}</Pressable>
                  ))}
                </div>
                <label style={{ display: "block" }}>
                  <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 5 }}>{t("admin.userEdit.birthdateLabel", "Date de naissance")}</span>
                  <input style={dateTimeInputStyle} type="date" value={userEditForm.birthdate} onChange={(e) => setUserEditForm({ ...userEditForm, birthdate: e.target.value })} />
                  <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 5, lineHeight: 1.4 }}>{t("planning.breakRuleAppliedText", "Règle de pause applicable dans Arbeitsplan (mineur/majeur).")}</span>
                </label>
              </div>

              {section(t("admin.userEdit.sectionContract", "Contrat"), "📄")}
              <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 14 }}>
                <label style={{ display: "block", marginBottom: 12 }}>
                  <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 5 }}>{t("admin.userEdit.weeklyHoursLabel", "Heures contractuelles / semaine")}</span>
                  <input style={inputStyle} type="number" min="1" max="60" step="0.5" placeholder={t("admin.weeklyHoursPlaceholderExample", "ex. 40")} value={userEditForm.weeklyContractedHours} onChange={(e) => setUserEditForm({ ...userEditForm, weeklyContractedHours: e.target.value })} />
                  <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 5, lineHeight: 1.4 }}>{t("admin.userEdit.weeklyHoursHint", "Objectif mensuel (Δ) de l'Arbeitszeitliste — vide si inconnu.")}</span>
                </label>
                <Field label={t("admin.employeeIdFieldLabel", "ID employé")}>
                  <NumericIdInput value={userEditForm.employeeId} onChange={(v) => setUserEditForm({ ...userEditForm, employeeId: v })} />
                </Field>
              </div>

              {section(t("admin.userEdit.sectionContact", "Contact"), "📞")}
              <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 14 }}>
                <Field label={t("ficheEmployee.phone", "Téléphone")}>
                  <input style={inputStyle} type="tel" placeholder={t("delivery.optionalPlaceholder", "Facultatif")} value={userEditForm.phone} onChange={(e) => setUserEditForm({ ...userEditForm, phone: e.target.value })} />
                </Field>
                <div style={{ marginBottom: 0 }}>
                  <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 5 }}>{t("admin.userEdit.emailLabel", "E-mail")}</span>
                  <input style={inputStyle} type="email" placeholder={t("delivery.optionalPlaceholder", "Facultatif")} value={userEditForm.email} onChange={(e) => setUserEditForm({ ...userEditForm, email: e.target.value })} />
                </div>
              </div>

              {section(t("admin.userEdit.sectionAccess", "Accès"), "🛡️")}
              <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 14 }}>
                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 7 }}>{t("admin.roleFieldLabel", "Rôle")}</span>
                <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 13, paddingBottom: 2 }}>
                  {ROLE_HIERARCHY.map((r) => (
                    <Pressable key={r} onClick={() => setUserEditForm({ ...userEditForm, role: r })} style={{
                      flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                      background: userEditForm.role === r ? roleColor : P.soft, color: userEditForm.role === r ? "#fff" : P.ink,
                      border: `1.5px solid ${userEditForm.role === r ? roleColor : P.line}`,
                    }}>{roleLabel(r)}</Pressable>
                  ))}
                </div>
                {["Bereichsleiter", "Verkaufsleiter", "Niederlassungsleiter"].includes(userEditForm.role) ? (() => {
                  // Recherche + plafond d'affichage (2026-08-22, retour utilisateur explicite :
                  // "et si on devait rechercher parmi 1200 filiales ??") — storesCatalog est déjà
                  // entièrement chargé côté client (loadStoresCatalog), donc la recherche filtre
                  // en mémoire, jamais un nouvel appel serveur. Les filiales déjà cochées restent
                  // TOUJOURS visibles même hors résultat de recherche/plafond — jamais un plafond
                  // silencieux qui ferait disparaître une sélection existante (même piège que le
                  // bug de recherche Messages corrigé plus tôt).
                  const storeQuery = userEditStoreSearch.trim().toLowerCase();
                  const matchingStores = storesCatalog.filter((s) =>
                    !storeQuery || s.id.toLowerCase().includes(storeQuery) || (s.label || "").toLowerCase().includes(storeQuery)
                  );
                  const RENDER_CAP = 60;
                  const checkedStores = matchingStores.filter((s) => userEditForm.stores.includes(s.id));
                  const uncheckedStores = matchingStores.filter((s) => !userEditForm.stores.includes(s.id));
                  const visibleStores = [...checkedStores, ...uncheckedStores.slice(0, RENDER_CAP)];
                  const hiddenCount = uncheckedStores.length - Math.min(uncheckedStores.length, RENDER_CAP);
                  return (
                  <div style={{ marginBottom: 13 }}>
                    <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 7 }}>
                      {t("admin.userEdit.storesManagedLabel", { n: userEditForm.stores.length }, `Filiales gérées (${userEditForm.stores.length})`)}
                    </span>
                    {storesCatalog.length > 6 && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, ...card, padding: "9px 13px", marginBottom: 8 }}>
                        <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted }}>🔎</span>
                        <input value={userEditStoreSearch} onChange={(e) => setUserEditStoreSearch(e.target.value)}
                          placeholder={t("admin.userEdit.storeSearchPlaceholder", "Chercher une filiale...")} style={{
                            flex: 1, minWidth: 0, border: "none", outline: "none", background: "none", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.ink,
                          }} />
                        {userEditStoreSearch && <Pressable onClick={() => setUserEditStoreSearch("")} style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>✕</Pressable>}
                      </div>
                    )}
                    <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 6, maxHeight: 260, overflowY: "auto" }}>
                      {storesCatalog.length === 0 && (
                        <p style={{ color: P.muted, fontSize: `calc(12px * var(--nx-font-scale, 1))`, margin: "8px 6px" }}>{t("messages.noBranchAvailable", "Aucune filiale disponible.")}</p>
                      )}
                      {storesCatalog.length > 0 && visibleStores.length === 0 && (
                        <p style={{ color: P.muted, fontSize: `calc(12px * var(--nx-font-scale, 1))`, margin: "8px 6px" }}>{t("messages.noResultsShort", "Aucun résultat.")}</p>
                      )}
                      {visibleStores.map((s) => {
                        const checked = userEditForm.stores.includes(s.id);
                        return (
                          <Pressable key={s.id} onClick={() => setUserEditForm({
                            ...userEditForm,
                            stores: checked ? userEditForm.stores.filter((id) => id !== s.id) : [...userEditForm.stores, s.id],
                          })} style={{
                            display: "flex", alignItems: "center", gap: 9, width: "100%", boxSizing: "border-box", padding: "9px 8px", borderRadius: 10,
                            background: checked ? "rgba(54,79,231,.08)" : "transparent",
                          }}>
                            <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{checked ? "☑️" : "⬜️"}</span>
                            <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, flex: 1, textAlign: "left" }}>{s.label && s.label !== s.id ? `${s.id} — ${s.label}` : s.id}</span>
                          </Pressable>
                        );
                      })}
                    </div>
                    {hiddenCount > 0 && (
                      <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: A.orange, fontWeight: 700, display: "block", marginTop: 6, lineHeight: 1.4 }}>
                        {t("admin.userEdit.storesHiddenCountTemplate", { n: hiddenCount }, `+ ${hiddenCount} autre(s) filiale(s) — affine la recherche pour les voir.`)}
                      </span>
                    )}
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 5, lineHeight: 1.4 }}>
                      {t("admin.userEdit.storesManagedHint", "Coche toutes les filiales que ce responsable gère — jamais héritées automatiquement, toujours choisies explicitement ici.")}
                    </span>
                  </div>
                  );
                })() : (
                <Field label={t("common.storeLabel", "Filiale")}>
                  <StoreIdInput value={userEditForm.storeId} placeholder="Numéro"
                    onChange={(v) => setUserEditForm({ ...userEditForm, storeId: v })} />
                  <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginTop: 5, lineHeight: 1.4 }}>
                    {t("admin.userEdit.storeIdHint", "Filiale existante suggérée automatiquement — un identifiant absent du registre est créé au moment d'enregistrer.")}
                  </span>
                </Field>
                )}
                {/* Pyramide hiérarchique (retour utilisateur explicite : "les BL et leur
                    Filiales respectives, et toutes ces filiales appartiennent a quel vkl
                    et a quelle niderlassungleiter et tout en haut le geschaftfuhrer") — un
                    BL rattaché à un Verkaufsleiter, un VKL à un Niederlassungsleiter, un NL
                    rattaché implicitement au sommet (compte NEXORA_ADMIN réel), jamais de
                    sélection pour lui. */}
                {userEditForm.role === "Bereichsleiter" && (
                  <Field label={t("admin.reportsToVerkaufsleiterLabel", "Rattaché à quel Verkaufsleiter ?")}>
                    <select style={inputStyle} value={userEditForm.reportsTo} onChange={(e) => setUserEditForm({ ...userEditForm, reportsTo: e.target.value })}>
                      <option value="">{t("common.unassignedOption", "— Non assigné —")}</option>
                      {adminUsers.filter((u) => u.role === "Verkaufsleiter").map((u) => (
                        <option key={u.id} value={u.employeeId}>{u.firstname} {u.lastname}</option>
                      ))}
                    </select>
                  </Field>
                )}
                {userEditForm.role === "Verkaufsleiter" && (
                  <Field label={t("admin.reportsToNiederlassungsleiterLabel", "Rattaché à quel Niederlassungsleiter ?")}>
                    <select style={inputStyle} value={userEditForm.reportsTo} onChange={(e) => setUserEditForm({ ...userEditForm, reportsTo: e.target.value })}>
                      <option value="">{t("common.unassignedOption", "— Non assigné —")}</option>
                      {adminUsers.filter((u) => u.role === "Niederlassungsleiter").map((u) => (
                        <option key={u.id} value={u.employeeId}>{u.firstname} {u.lastname}</option>
                      ))}
                    </select>
                  </Field>
                )}
                {userEditForm.role === "Niederlassungsleiter" && (
                  <p style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 0 13px", lineHeight: 1.4 }}>
                    {t("admin.userEdit.niederlassungsleiterHint", "Rattaché directement à la direction (Geschäftsführer) — aucune sélection nécessaire.")}
                  </p>
                )}
                {userEditForm.role === "Bereichsleiter" && (
                  <Pressable onClick={() => setUserEditForm({ ...userEditForm, ausbildungsbeauftragter: !userEditForm.ausbildungsbeauftragter })} style={{
                    display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "9px 2px 0",
                  }}>
                    <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{userEditForm.ausbildungsbeauftragter ? "☑️" : "⬜️"}</span>
                    <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, flex: 1, textAlign: "left", lineHeight: 1.4 }}>{t("admin.userEdit.ausbildungsbeauftragterLabel", "Responsable formation Azubis (contact Krankmeldung obligatoire des Azubis, tous rayons)")}</span>
                  </Pressable>
                )}
              </div>

              <Pressable onClick={saveUserEdit} style={{ width: "100%", ...btnSolid(roleColor), marginTop: 18 }}>{t("common.save", "Enregistrer")}</Pressable>
            </>
          );
        })()}
      </Sheet>

      {/* "Remplacer ce responsable" (2026-08-16) — succession : choisir qui reprend le
          poste, hériter le périmètre de filiales par défaut mais rester librement
          modifiable (retour utilisateur explicite : "le responsable peut hériter des
          filiales d'un autre mais pourrait en recevoir de plus ou de moins"). Deux étapes
          dans une seule Sheet : choix de la personne, puis checklist filiale éditable. */}
      <Sheet open={sheet === "replaceLeader"} onClose={() => { setSheet(null); setReplaceLeaderDraft(null); }} P={P} maxH="calc(var(--nx-vh, 1vh) * 82)">
        {replaceLeaderDraft && (() => {
          const d = replaceLeaderDraft;
          const target = adminUsers.find((u) => u.id === d.targetId);
          const originalStores = new Set((d.source.stores && d.source.stores.length ? d.source.stores : [d.source.storeId]).filter(Boolean));
          const tq = d.targetSearch.trim().toLowerCase();
          const candidates = adminUsers.filter((u) => u.id !== d.source.id && u.role !== "NEXORA_ADMIN" &&
            (!tq || `${u.firstname} ${u.lastname} ${u.employeeId}`.toLowerCase().includes(tq)));
          const sq = d.storeSearch.trim().toLowerCase();
          const filteredStores = !sq ? storesCatalog : storesCatalog.filter((s) =>
            s.id.toLowerCase().includes(sq) || (s.label || "").toLowerCase().includes(sq));
          return (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <span style={{ fontSize: `calc(20px * var(--nx-font-scale, 1))`}}>🔁</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={eyebrow(A.purple)}>{roleLabel(d.source.role)}</span>
                  <strong style={{ display: "block", fontSize: `calc(17px * var(--nx-font-scale, 1))`, marginTop: 1 }}>{t("admin.replaceLeaderTitle", "Remplacer ce responsable")}</strong>
                </div>
                <Pressable onClick={() => { setSheet(null); setReplaceLeaderDraft(null); }} aria-label={t("common.close", "Fermer")} style={{
                  width: 32, height: 32, borderRadius: 99, background: P.soft, color: P.ink, display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                }}>✕</Pressable>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 16, padding: 13, margin: "14px 0" }}>
                <span style={{ width: 36, height: 36, borderRadius: 99, color: "#fff", border: "1px solid rgba(255,255,255,.16)", display: "grid", placeItems: "center", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, flex: "0 0 auto", ...premiumTileStyle(A.purple) }}>{(d.source.firstname?.[0] || "") + (d.source.lastname?.[0] || "")}</span>
                <div style={{ minWidth: 0 }}>
                  <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{d.source.firstname} {d.source.lastname}</strong>
                  <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{roleLabel(d.source.role)} · {originalStores.size} filiale{originalStores.size > 1 ? "s" : ""}</span>
                </div>
              </div>

              {!target ? (
                <>
                  <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 12px", lineHeight: 1.5 }}>
                    {t("admin.replaceLeaderPickHint", "Choisissez qui reprend ce poste — le périmètre de filiales sera pré-rempli mais restera librement modifiable à l'étape suivante.")}
                  </p>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 14, padding: "0 12px", height: 40, marginBottom: 12 }}>
                    <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>🔎</span>
                    <input value={d.targetSearch} onChange={(e) => setReplaceLeaderDraft({ ...d, targetSearch: e.target.value })}
                      placeholder={t("admin.roles.searchUserPlaceholder", "Chercher un utilisateur...")}
                      style={{ border: "none", outline: "none", background: "none", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.ink, width: "100%" }} />
                  </div>
                  {candidates.length === 0 && (
                    <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("admin.roles.noUserMatchText", "Aucun utilisateur ne correspond à cette recherche.")}</p>
                  )}
                  {candidates.map((u) => (
                    <Pressable key={u.id} onClick={() => setReplaceLeaderDraft({ ...d, targetId: u.id })} style={{
                      display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                      padding: "11px 13px", background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 14, marginBottom: 8,
                    }}>
                      <span style={{ width: 32, height: 32, borderRadius: 99, color: "#fff", border: "1px solid rgba(255,255,255,.16)", display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, flex: "0 0 auto", ...premiumTileStyle(A.navy) }}>{(u.firstname?.[0] || "") + (u.lastname?.[0] || "")}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{u.firstname} {u.lastname}</strong>
                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{roleLabel(u.role)} · {u.employeeId}</span>
                      </div>
                    </Pressable>
                  ))}
                </>
              ) : (
                <>
                  <Pressable onClick={() => setReplaceLeaderDraft({ ...d, targetId: null })} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.purple }}>
                    ‹ {t("admin.replaceLeaderChangeTargetBtn", "Choisir une autre personne")}
                  </Pressable>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 16, padding: 14, marginBottom: 4 }}>
                    <span style={{ width: 40, height: 40, borderRadius: 99, color: "#fff", border: "1px solid rgba(255,255,255,.16)", display: "grid", placeItems: "center", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, fontWeight: 800, flex: "0 0 auto", ...premiumTileStyle(A.navy) }}>{(target.firstname?.[0] || "") + (target.lastname?.[0] || "")}</span>
                    <div style={{ minWidth: 0 }}>
                      <strong style={{ display: "block", fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{target.firstname} {target.lastname}</strong>
                      <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("admin.replaceLeaderBecomesTemplate", { role: roleLabel(d.source.role) }, `Devient ${roleLabel(d.source.role)}`)}</span>
                    </div>
                  </div>
                  <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "12px 2px 10px", lineHeight: 1.5 }}>
                    {t("admin.replaceLeaderStoresHint", "Périmètre de filiales — pré-rempli depuis l'ancien titulaire, ajoutez ou retirez librement.")}
                  </p>
                  {storesCatalog.length > 6 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 14, padding: "0 12px", height: 38, marginBottom: 10 }}>
                      <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>🏬</span>
                      <input value={d.storeSearch} onChange={(e) => setReplaceLeaderDraft({ ...d, storeSearch: e.target.value })}
                        placeholder={t("admin.searchFilialePlaceholder", "Chercher une filiale...")}
                        style={{ border: "none", outline: "none", background: "none", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.ink, width: "100%" }} />
                    </div>
                  )}
                  <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, margin: "0 2px 8px" }}>
                    {t("admin.replaceLeaderStoresCountTemplate", { n: d.stores.length }, `${d.stores.length} filiale${d.stores.length > 1 ? "s" : ""} sélectionnée${d.stores.length > 1 ? "s" : ""}`)}
                  </div>
                  {filteredStores.length === 0 && (
                    <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("admin.noFilialeMatchText", "Aucune filiale ne correspond à cette recherche.")}</p>
                  )}
                  {filteredStores.map((s) => {
                    const checked = d.stores.includes(s.id);
                    const inherited = originalStores.has(s.id);
                    return (
                      <Pressable key={s.id} onClick={() => toggleReplaceLeaderStore(s.id)} style={{
                        display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box",
                        padding: "10px 13px", background: P.card, border: `1.5px solid ${P.line}`, borderRadius: 12, marginBottom: 6,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{s.label && s.label !== s.id ? s.label : s.id}</span>
                          {inherited && <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.purple }}>{t("admin.inheritedTag", "hérité")}</span>}
                        </div>
                        <span style={{
                          width: 34, height: 20, borderRadius: 99, flex: "0 0 auto", position: "relative",
                          background: checked ? A.purple : P.soft, border: checked ? "none" : `1.5px solid ${P.line}`,
                          transition: "background .15s ease",
                        }}>
                          <span style={{
                            position: "absolute", top: 2, width: 14, height: 14, borderRadius: 99, background: checked ? "#fff" : P.muted,
                            boxShadow: "0 1px 3px rgba(0,0,0,.25)", left: checked ? 18 : 2, transition: "left .15s ease",
                          }} />
                        </span>
                      </Pressable>
                    );
                  })}
                  <Pressable disabled={d.stores.length === 0} onClick={confirmReplaceLeader} style={{
                    width: "100%", ...btnSolid(A.purple), marginTop: 16, opacity: d.stores.length === 0 ? 0.5 : 1,
                  }}>{t("admin.confirmReplaceLeaderBtn", "✓ Confirmer le remplacement")}</Pressable>
                </>
              )}
            </>
          );
        })()}
      </Sheet>

      {/* Preisänderung — résumé de validation avant de terminer (§9 du cahier des charges) :
          refusé tant qu'il reste un article sans Bestand, le vrai verrou étant côté serveur
          (POST /:id/complete). */}
      <Sheet open={paSummarySheet} onClose={() => setPaSummarySheet(false)} P={P} maxH="70%">
        {(() => {
          const doc = preisanderungen.find((p) => p.id === paActiveId);
          if (!doc) return null;
          const total = doc.lines.length;
          const counted = doc.lines.filter((l) => l.bestand != null).length;
          const missing = total - counted;
          const fmtEuro = (v) => v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
          const gesamtGutschrift = doc.lines.filter((l) => l.art === "gutschrift").reduce((s, l) => s + (l.betrag || 0), 0);
          const gesamtBelastung = doc.lines.filter((l) => l.art === "belastung").reduce((s, l) => s + (l.betrag || 0), 0);
          return (
            <>
              <strong style={{ display: "block", fontSize: `calc(17px * var(--nx-font-scale, 1))`, marginBottom: 14 }}>{t("preisanderung.summary.title", "Résumé avant de terminer")}</strong>
              {[
                [t("preisanderung.summary.totalArticles", "Articles au total"), total],
                [t("preisanderung.summary.countedArticles", "Articles comptés"), counted],
                [t("preisanderung.summary.missingArticles", "Articles manquants"), missing],
              ].map(([label, value], i, arr) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "12px 2px", borderBottom: i < arr.length - 1 ? `1px solid ${P.line}` : "none" }}>
                  <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{label}</span>
                  <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{value}</strong>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 2px" }}>
                <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("preisanderung.detail.gesamtGutschrift", "Gesamt Gutschrift")}</span>
                <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: A.green }}>{fmtEuro(gesamtGutschrift)}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 2px 18px" }}>
                <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("preisanderung.detail.gesamtBelastung", "Gesamt Belastung")}</span>
                <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: A.red }}>{fmtEuro(gesamtBelastung)}</strong>
              </div>
              {missing > 0 ? (
                <p style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: A.red, textAlign: "center", margin: "0 0 12px" }}>{t("preisanderung.summary.missingWarning", "Il reste des articles sans Bestand — impossible de terminer.")}</p>
              ) : (
                <Pressable onClick={() => completePreisanderung(doc.id)} style={{ width: "100%", ...btnSolid(A.green) }}>{t("preisanderung.summary.confirmBtn", "✓ Confirmer et terminer")}</Pressable>
              )}
            </>
          );
        })()}
      </Sheet>

      {/* Popup CENTRÉ (pas la Sheet ancrée en bas habituelle de l'app) — demande explicite
          utilisateur (2026-09-20, maquette approuvée) pour l'édition rapide d'une case du
          tableau Wochenbericht depuis la vue Semaine. Volontairement un composant à part,
          pas une réutilisation de <Sheet> : ce dernier est ancré en bas sur mobile (patron
          établi partout ailleurs dans l'app), alors que celui-ci doit rester centré même sur
          mobile, comme validé dans la maquette. */}
      <div onClick={() => { setSheet(null); setGridEditTarget(null); }} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 60,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
        opacity: sheet === "gridEdit" ? 1 : 0, pointerEvents: sheet === "gridEdit" ? "auto" : "none",
        transition: "opacity .18s ease",
      }}>
        {gridEditTarget && (() => {
          const cat = REPORT_CATEGORIES.find((c) => c.id === gridEditTarget.categoryId);
          const isBrot = gridEditTarget.categoryId === "frischbrot_brot";
          return (
            <div onClick={(e) => e.stopPropagation()} style={{
              width: "100%", maxWidth: 300, background: P.card, borderRadius: 20,
              boxShadow: "0 24px 60px rgba(0,0,0,.35)", padding: 18,
              transform: sheet === "gridEdit" ? "scale(1) translateY(0)" : "scale(.94) translateY(6px)",
              transition: "transform .18s ease",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
                <span style={{ width: 9, height: 9, borderRadius: 3, display: "inline-block", background: REPORT_CATEGORY_COLORS[gridEditTarget.categoryId] || P.muted }} />
                <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".05em" }}>{weekLabelShort(gridEditTarget.dayIdx)} {fmtFR(reportingWeekDates[gridEditTarget.dayIdx]).slice(0, 5)}</span>
              </div>
              <h2 style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`, fontWeight: 800, margin: "0 0 14px" }}>{cat ? reportCategoryLabel(cat.id) : ""}</h2>
              <Field label="PÜ/20%">
                {/* type="text" (jamais "number") — virgule rejetée par le clavier numérique
                    iOS en locale allemande (retour utilisateur explicite déjà rencontré sur
                    cette même saisie : "le 0,x ne fonctionne pas"). Conversion virgule→point
                    uniquement à l'enregistrement (saveGridEdit), jamais ici. */}
                <input style={inputStyle} type="text" inputMode="decimal" placeholder="0,0" value={gridEditDraft.pu20} onChange={(e) => setGridEditDraft({ ...gridEditDraft, pu20: e.target.value })} autoFocus />
              </Field>
              {!isBrot ? (
                <Field label="entsorgt">
                  <input style={inputStyle} type="text" inputMode="decimal" placeholder="0,0" value={gridEditDraft.entsorgt} onChange={(e) => setGridEditDraft({ ...gridEditDraft, entsorgt: e.target.value })} />
                </Field>
              ) : (
                <p style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 0 14px", lineHeight: 1.5 }}>{t("reporting.gridEdit.entsorgtBrotNote", "La ligne entsorgt de Frischbrot/Brot vient de la saisie quotidienne — elle n'est pas modifiable ici.")}</p>
              )}
              {/* onPointerDownCapture (capture, pas bubble — se déclenche avant même le
                  onPointerDown interne de Pressable) : ferme le clavier natif AVANT que le tap
                  soit traité, jamais après. Retour utilisateur explicite (2026-09-20) : "le
                  bouton terminer ne marche pas d'un coup... je me retrouve à faire plusieurs
                  clics" — un input encore focus au moment du tap fait qu'iOS consomme le
                  premier tap juste pour fermer le clavier, sans le transmettre au bouton
                  underneath ; le 2e tap (clavier déjà fermé) fonctionne enfin. blur() explicite
                  ici retire ce premier tap "perdu" du chemin. */}
              <div onPointerDownCapture={() => { if (document.activeElement instanceof HTMLInputElement) document.activeElement.blur(); }} style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <Pressable onClick={() => { setSheet(null); setGridEditTarget(null); }} style={{ flex: 1, textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>{t("common.cancel", "Annuler")}</Pressable>
                <Pressable onClick={saveGridEdit} style={{ flex: 1, textAlign: "center", padding: "12px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: A.green, color: "#fff", boxShadow: "0 6px 16px rgba(30,184,95,.28)" }}>{t("common.doneAria", "✓ Terminer")}</Pressable>
              </div>
            </div>
          );
        })()}
      </div>

      <Sheet open={sheet === "articleModal"} onClose={() => { setSheet(null); setArticleModalForm(null); }} P={P} maxH="75%">
        <SheetHead kicker={t("reporting.articleModal.kicker", "Catalogue dynamique")} title={t("reporting.articleModal.title", "Ajouter / modifier un article")} onClose={() => { setSheet(null); setArticleModalForm(null); }} />
        {articleModalForm && (
          <>
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 7 }}>{t("reporting.articleModal.familyLabel", "Famille")}</span>
            <div style={{ display: "flex", gap: 8, marginBottom: 13 }}>
              {["Prebake", "Brot"].map((c) => (
                <Pressable key={c} onClick={() => setArticleModalForm({ ...articleModalForm, category: c })} style={{
                  flex: 1, textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                  background: articleModalForm.category === c ? A.brown : P.soft, color: articleModalForm.category === c ? "#fff" : P.ink,
                  border: `1.5px solid ${articleModalForm.category === c ? A.brown : P.line}`,
                }}>{c === "Brot" ? t("reporting.articleModal.familyBrot", "Brot / Frischbrot") : c}</Pressable>
              ))}
            </div>
            <Field label="Art.-Nr">
              <input style={inputStyle} inputMode="numeric" placeholder={t("reporting.articleModal.artNrPlaceholder", "Ex. 40123")} value={articleModalForm.artNr} onChange={(e) => setArticleModalForm({ ...articleModalForm, artNr: e.target.value })} />
            </Field>
            <Field label={t("reporting.articleModal.nameLabel", "Article")}>
              <input style={inputStyle} placeholder={t("reporting.articleModal.namePlaceholder", "Ex. Baguette tradition")} value={articleModalForm.name} onChange={(e) => setArticleModalForm({ ...articleModalForm, name: e.target.value })} />
            </Field>
            <Field label={t("reporting.articleModal.programLabel", "Backprogramm / Groupe")}>
              <input style={inputStyle} placeholder={t("reporting.articleModal.programPlaceholder", "Ex : Brezel, Brötchen, Croissant...")} value={articleModalForm.program} onChange={(e) => setArticleModalForm({ ...articleModalForm, program: e.target.value })} />
            </Field>
            <Field label={t("reporting.articleModal.priceLabel", "Prix (€)")}>
              <input style={inputStyle} inputMode="decimal" placeholder="0,00" value={articleModalForm.price} onChange={(e) => setArticleModalForm({ ...articleModalForm, price: e.target.value })} />
            </Field>
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 7 }}>{t("reporting.articleModal.statusLabel", "Statut")}</span>
            <div className="nx-chips" style={{ display: "flex", gap: 7, marginBottom: 16 }}>
              {[{ id: "active", l: t("reporting.status.active", "Actif") }, { id: "suspended", l: t("reporting.status.suspended", "Suspendu") }, { id: "archived", l: t("reporting.status.archived", "Archivé") }].map((s) => (
                <Pressable key={s.id} onClick={() => setArticleModalForm({ ...articleModalForm, status: s.id })} style={{
                  flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: articleModalForm.status === s.id ? A.navy : P.soft, color: articleModalForm.status === s.id ? "#fff" : P.ink,
                  border: `1.5px solid ${articleModalForm.status === s.id ? A.navy : P.line}`,
                }}>{s.l}</Pressable>
              ))}
            </div>
            <Pressable onClick={saveArticleModal} style={{ width: "100%", ...btnSolid(A.brown) }}>{t("reporting.articleModal.saveBtn", "Enregistrer article")}</Pressable>
          </>
        )}
      </Sheet>

      <Sheet open={sheet === "articleEditor"} onClose={() => { setSheet(null); setArtForm(null); }} P={P}>
        <SheetHead kicker={artForm?.isPrebake ? "Prebake" : t("articles.eyebrow", "Base articles")} title={artForm?.mode === "create" ? t("articles.addArticleTitle", "Ajouter un article") : (artForm?.name || t("articles.editorTitleDefault", "Modifier l'article"))} onClose={() => { setSheet(null); setArtForm(null); }} />
        {artForm && (
          <>
            <Field label="Art.Nr">
              <input style={inputStyle} placeholder={t("reporting.articleModal.artNrPlaceholder", "Ex. 40123")} value={artForm.artNr} onChange={(e) => setArtForm({ ...artForm, artNr: e.target.value })} />
            </Field>
            <Field label={t("articles.fieldArticleName", "Nom de l'article")}>
              <input style={inputStyle} placeholder={t("reporting.articleModal.namePlaceholder", "Ex. Baguette tradition")} value={artForm.name} onChange={(e) => setArtForm({ ...artForm, name: e.target.value })} />
            </Field>
            {!artForm.isPrebake && (
              <Field label={t("articles.fieldSortOrder", "Ordre rayon")}>
                <input style={inputStyle} inputMode="numeric" placeholder={t("articles.sortOrderPlaceholderExample", "Ex. 10")} value={artForm.sortOrder} onChange={(e) => setArtForm({ ...artForm, sortOrder: e.target.value })} />
              </Field>
            )}
            <Field label={t("articles.fieldCurrentPrice", "Prix actuel (€)")}>
              <input style={inputStyle} inputMode="decimal" placeholder="0,00" value={artForm.price} onChange={(e) => setArtForm({ ...artForm, price: e.target.value })} />
            </Field>
            {!artForm.isPrebake && (
              <Field label={t("articles.fieldRegularPrice", "Prix normal (€) — facultatif")}>
                <input style={inputStyle} inputMode="decimal" placeholder="0,00" value={artForm.regularPrice} onChange={(e) => setArtForm({ ...artForm, regularPrice: e.target.value })} />
              </Field>
            )}
            <Field label={t("articles.fieldUnit", "Unité / colisage")}>
              <input style={inputStyle} placeholder={t("articles.unitPlaceholderExample", "Ex. 500g, VE 6")} value={artForm.unit} onChange={(e) => setArtForm({ ...artForm, unit: e.target.value })} />
            </Field>
            {!artForm.isPrebake && (
              <Field label={t("articles.fieldZone", "Zone / rayon")}>
                <select style={inputStyle} value={artForm.zone} onChange={(e) => setArtForm({ ...artForm, zone: e.target.value })}>
                  {ARTICLE_ZONES.filter((z) => z.id !== "Prebake").map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
                  {!ARTICLE_ZONES.some((z) => z.id === artForm.zone) && <option value={artForm.zone}>{artForm.zone} {t("articles.existingValueSuffix", "(valeur existante)")}</option>}
                </select>
              </Field>
            )}
            {/* Commandes de marchandises (nouveau module, 2026-08-07) — "gérer les unités de
                commande" reste réservé à l'admin (merchandiseOrders.manageCatalog, garde
                CONDITIONNELLE côté serveur sur ces 2 champs précis), donc masqué ici pour
                tout autre rôle même s'il peut éditer le reste de la fiche article. */}
            {!artForm.isPrebake && canManageMerchCatalog && (
              <>
                <Field label={t("merch.admin.defaultUnitLabel", "Unité de commande par défaut")}>
                  <select style={inputStyle} value={artForm.uniteParDefaut || "Karton"} onChange={(e) => setArtForm({ ...artForm, uniteParDefaut: e.target.value })}>
                    {MERCH_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </Field>
                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 7 }}>{t("merch.admin.allowedUnitsLabel", "Unités de commande autorisées")}</span>
                <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 13, paddingBottom: 2 }}>
                  {MERCH_UNITS.map((u) => {
                    const list = artForm.unitesAutorisees || ["Karton"];
                    const sel = list.includes(u);
                    return (
                      <Pressable key={u} onClick={() => setArtForm({
                        ...artForm,
                        unitesAutorisees: sel ? list.filter((x) => x !== u) : [...list, u],
                      })} style={{
                        flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                        background: sel ? A.amber : P.soft, color: sel ? "#fff" : P.ink,
                        border: `1.5px solid ${sel ? A.amber : P.line}`,
                      }}>{u}</Pressable>
                    );
                  })}
                </div>
              </>
            )}
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 7 }}>{t("articles.fieldStatus", "Statut")}</span>
            <div style={{ display: "flex", gap: 8, marginBottom: 13 }}>
              {[{ id: "active", l: t("articles.statusActive", "Actif") }, { id: "inactive", l: t("articles.statusInactive", "Inactif") }].map((s) => (
                <Pressable key={s.id} onClick={() => setArtForm({ ...artForm, status: s.id })} style={{
                  flex: 1, textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                  background: artForm.status === s.id ? A.navy : P.soft, color: artForm.status === s.id ? "#fff" : P.ink,
                  border: `1.5px solid ${artForm.status === s.id ? A.navy : P.line}`,
                }}>{s.l}</Pressable>
              ))}
            </div>
            {!artForm.isPrebake && (
              <Field label={t("articles.fieldNote", "Remarque interne")}>
                <input style={inputStyle} placeholder={t("delivery.optionalPlaceholder", "Facultatif")} value={artForm.note} onChange={(e) => setArtForm({ ...artForm, note: e.target.value })} />
              </Field>
            )}
            <Pressable onClick={saveArticleForm} style={{ width: "100%", ...btnSolid(A.green), marginBottom: artForm.mode === "edit" && canCreateArticles ? 8 : 0 }}>{t("common.save", "Enregistrer")}</Pressable>
            {artForm.mode === "edit" && canCreateArticles && (
              <Pressable onClick={duplicateArticleForm} style={{ width: "100%", padding: "12px 0", borderRadius: 99, textAlign: "center", fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>⧉ {t("articles.duplicateBtn", "Dupliquer")}</Pressable>
            )}
          </>
        )}
      </Sheet>

      <Sheet open={sheet === "articleMove"} onClose={() => { setSheet(null); setArtMoveState(null); }} P={P}>
        <SheetHead kicker={t("articles.moveKicker", "Déplacer")} title={artMoveState?.article?.name || t("reporting.articleModal.nameLabel", "Article")} onClose={() => { setSheet(null); setArtMoveState(null); }} />
        {artMoveState && (
          <>
            <p style={{ margin: "0 0 14px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
              Art.Nr {artMoveState.article.artNr} · {t("controlRun.positionPrefix", "Position")} {artMoveState.article.sortOrder ?? "-"} · {artZoneLabel(artMoveState.article.zone)}
            </p>
            <div style={{ display: "flex", gap: 7, marginBottom: 13, flexWrap: "wrap" }}>
              {[{ id: "before", l: t("articles.moveBefore", "Avant") }, { id: "after", l: t("articles.moveAfter", "Après") }, { id: "start", l: t("articles.moveStart", "Début du rayon") }, { id: "end", l: t("articles.moveEnd", "Fin du rayon") }].map((m) => (
                <Pressable key={m.id} onClick={() => setArtMoveState({ ...artMoveState, mode: m.id })} style={{
                  flex: "0 0 auto", padding: "9px 14px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                  background: artMoveState.mode === m.id ? A.green : P.soft, color: artMoveState.mode === m.id ? "#fff" : P.ink,
                  border: `1.5px solid ${artMoveState.mode === m.id ? A.green : P.line}`,
                }}>{m.l}</Pressable>
              ))}
            </div>
            {!artMoveState.article.isPrebake && (
              <Field label={t("articles.moveTargetZoneLabel", "Zone de destination")}>
                <select style={inputStyle} value={artMoveState.zone} onChange={(e) => setArtMoveState({ ...artMoveState, zone: e.target.value })}>
                  {ARTICLE_ZONES.filter((z) => z.id !== "Prebake").map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
                </select>
              </Field>
            )}
            {(artMoveState.mode === "before" || artMoveState.mode === "after") && (
              <>
                <Field label={t("articles.moveReferenceLabel", "Article repère")}>
                  <input style={inputStyle} placeholder={t("articles.moveSearchPlaceholder", "Rechercher un article…")} value={artMoveState.query} onChange={(e) => setArtMoveState({ ...artMoveState, query: e.target.value, referenceArtNr: null })} />
                </Field>
                <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 13 }}>
                  {artMoveCandidates().length === 0 ? (
                    <p style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, textAlign: "center", padding: "12px 0" }}>{t("articles.noReferenceArticleFound", "Aucun article repère trouvé.")}</p>
                  ) : artMoveCandidates().map((c) => (
                    <Pressable key={c.artNr} onClick={() => setArtMoveState({ ...artMoveState, referenceArtNr: c.artNr })} style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", boxSizing: "border-box",
                      padding: "10px 12px", borderRadius: 12, marginBottom: 6,
                      background: artMoveState.referenceArtNr === c.artNr ? "rgba(34,197,94,.12)" : P.soft,
                      border: `1.5px solid ${artMoveState.referenceArtNr === c.artNr ? A.green : P.line}`,
                    }}>
                      <span style={{ minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</strong>
                        <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>Art.Nr {c.artNr} · {t("controlRun.positionPrefix", "Position")} {c.sortOrder ?? "-"} · {artZoneLabel(c.zone)}</span>
                      </span>
                      {artMoveState.referenceArtNr === c.artNr && <span style={{ color: A.green, fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>✓</span>}
                    </Pressable>
                  ))}
                </div>
              </>
            )}
            <Pressable onClick={saveArticleMoveReal} style={{ width: "100%", ...btnSolid(A.green) }}>{t("articles.saveMoveBtn", "Enregistrer l'emplacement")}</Pressable>
          </>
        )}
      </Sheet>

      <Sheet open={sheet === "wipeModal"} onClose={() => setSheet(null)} P={P} maxH="80%">
        <SheetHead kicker={t("reporting.wipeModal.kicker", "Action irréversible")} title={t("reporting.wipeModal.title", "Vider des saisies ?")} onClose={() => setSheet(null)} />
        <p style={{ margin: "0 0 14px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>{t("reporting.wipeModal.checkWhatToWipeText", "Coche ce que tu veux supprimer définitivement, pour toutes les filiales.")}</p>
        {WIPE_TARGETS.map((wt) => (
          <Pressable key={wt.id} onClick={() => toggleWipeTarget(wt.id)} style={{
            display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box", padding: "11px 12px", borderRadius: 14, marginBottom: 7,
            background: wipeSelections.includes(wt.id) ? "rgba(239,68,68,.1)" : P.soft, border: `1.5px solid ${wipeSelections.includes(wt.id) ? A.red : P.line}`,
          }}>
            <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{wipeSelections.includes(wt.id) ? "☑️" : "⬜️"}</span>
            <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, flex: 1 }}>{wipeTargetLabel(wt.id)}</span>
          </Pressable>
        ))}
        <Field label={t("reporting.wipeModal.typeSupprimerLabel", 'Tape "SUPPRIMER" pour confirmer')}>
          <input style={inputStyle} placeholder="SUPPRIMER" value={wipeConfirmText} onChange={(e) => setWipeConfirmText(e.target.value)} />
        </Field>
        <Pressable onClick={confirmWipe} style={{ width: "100%", ...btnSolid(A.red) }}>{t("reporting.wipeModal.confirmBtn", "Vider la sélection")}</Pressable>
      </Sheet>

      <Sheet open={sheet === "convInfo"} onClose={() => { setSheet(null); setMsgRenameForm(null); setMsgAddMemberQuery(""); }} P={P}>
        {(() => {
          const conv = conversations.find((c) => c.id === msgActiveId);
          if (!conv) return null;
          const isOfficialChannel = conv.type === "branch" || conv.type === "branch_managers";
          const canRename = canManageMsgGroup && (conv.type === "group" || conv.type === "multi_branch");
          const myId = String(user?.proId || user?.id || "");
          const memberIds = new Set(msgConvMembers.map((m) => String(m.userId)));
          const addCandidates = canManageMsgMembers && conv.type !== "private" && !isOfficialChannel
            ? msgRecipients.filter((r) => !memberIds.has(String(r.employeeId)) &&
                (!msgAddMemberQuery.trim() || r.name.toLowerCase().includes(msgAddMemberQuery.trim().toLowerCase()))).slice(0, 15)
            : [];
          return (
            <>
              <SheetHead kicker={t("messages.infoKicker", "Infos")} title={conv.title} onClose={() => { setSheet(null); setMsgRenameForm(null); setMsgAddMemberQuery(""); }} />
              {canRename && (
                msgRenameForm ? (
                  <div style={{ ...card, padding: 13, marginBottom: 12, flexDirection: "column", alignItems: "stretch" }}>
                    <Field label={t("common.nameLabel", "Nom")}>
                      <input style={inputStyle} value={msgRenameForm.title} onChange={(e) => setMsgRenameForm({ ...msgRenameForm, title: e.target.value })} />
                    </Field>
                    <Field label={t("inventur.descriptionOptionalLabel", "Description (facultatif)")}>
                      <input style={inputStyle} value={msgRenameForm.description} onChange={(e) => setMsgRenameForm({ ...msgRenameForm, description: e.target.value })} />
                    </Field>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Pressable onClick={() => setMsgRenameForm(null)} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink }}>{t("common.cancel", "Annuler")}</Pressable>
                      <Pressable onClick={renameConversation} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: A.frost, color: "#fff" }}>{t("common.save", "Enregistrer")}</Pressable>
                    </div>
                  </div>
                ) : (
                  <Pressable onClick={() => setMsgRenameForm({ title: conv.title, description: conv.description || "" })} style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box", padding: 13, borderRadius: 14, marginBottom: 8, background: P.soft,
                  }}>
                    <span style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>✏️</span>
                    <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: 1 }}>{t("documentIntel.renameDescribeBtn", "Renommer / décrire")}</strong>
                  </Pressable>
                )
              )}
              <Pressable onClick={() => toggleMute(conv.id)} style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box", padding: 13, borderRadius: 14, marginBottom: 8, background: P.soft,
              }}>
                <span style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>{conv.muted ? "🔔" : "🔕"}</span>
                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: 1 }}>{conv.muted ? t("messages.unmuteBtn", "Réactiver les notifications") : t("messages.muteBtn", "Muet")}</strong>
              </Pressable>
              <Pressable onClick={() => archiveConv(conv.id)} style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box", padding: 13, borderRadius: 14, marginBottom: 8, background: P.soft,
              }}>
                <span style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>📦</span>
                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: 1 }}>{t("messages.archiveBtn", "Archiver")}</strong>
              </Pressable>
              {/* Retour utilisateur explicite 2026-08-24 : "archiver existe mais
                  définitivement supprimer non" — auparavant réservé aux groupes
                  (conv.type !== "private"), alors que la route /leave existante convient
                  exactement pour une conversation privée aussi ("supprimer pour moi",
                  définitif, jamais un vrai effacement partagé). */}
              {!isOfficialChannel && (
                <Pressable onClick={() => leaveConv(conv.id)} style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box", padding: 13, borderRadius: 14, marginBottom: 15, background: "rgba(239,68,68,.1)",
                }}>
                  <span style={{ fontSize: `calc(16px * var(--nx-font-scale, 1))`}}>{conv.type === "private" ? "🗑️" : "🚪"}</span>
                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: 1, color: A.red }}>{conv.type === "private" ? t("messages.deleteBtn", "Supprimer") : t("messages.leaveBtn", "Quitter")}</strong>
                </Pressable>
              )}
              <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "6px 2px 9px" }}>{t("messages.membersCountTemplate", { n: msgConvMembers.length }, `Membres (${msgConvMembers.length})`)}</strong>
              {msgConvMembers.map((m) => (
                <div key={m.userId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 2px" }}>
                  <span style={{ width: 28, height: 28, borderRadius: 99, color: "#fff", border: "1px solid rgba(255,255,255,.16)", display: "grid", placeItems: "center", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, ...premiumTileStyle(A.navy) }}>{(m.name || "?").charAt(0)}</span>
                  <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: 1 }}>{m.name}{String(m.userId) === myId ? ` ${t("messages.youSuffix", "(toi)")}` : ""}</span>
                  {canManageMsgMembers && conv.type !== "private" && !isOfficialChannel && String(m.userId) !== myId && (
                    <Pressable onClick={() => removeConvMember(m.userId)} aria-label={t("messages.removeMemberAria", "Retirer")} style={{ width: 26, height: 26, borderRadius: 99, background: "rgba(239,68,68,.12)", display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: A.red }}>✕</Pressable>
                  )}
                </div>
              ))}
              {canManageMsgMembers && conv.type !== "private" && !isOfficialChannel && (
                <>
                  <Field label={t("messages.addMemberLabel", "Ajouter un membre")}>
                    <input style={inputStyle} placeholder={t("messages.searchNamePlaceholder", "Rechercher un nom…")} value={msgAddMemberQuery} onChange={(e) => setMsgAddMemberQuery(e.target.value)} />
                  </Field>
                  {addCandidates.map((r) => (
                    <Pressable key={r.employeeId} onClick={() => { addConvMember(r.employeeId); setMsgAddMemberQuery(""); }} style={{
                      display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box", padding: "9px 2px",
                    }}>
                      <span style={{ width: 26, height: 26, borderRadius: 99, color: "#fff", border: "1px solid rgba(255,255,255,.16)", display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, ...premiumTileStyle(A.frost) }}>{(r.name || "?").charAt(0)}</span>
                      <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, flex: 1 }}>{r.name}</span>
                      <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: A.green }}>＋</span>
                    </Pressable>
                  ))}
                </>
              )}
            </>
          );
        })()}
      </Sheet>

      <Sheet open={sheet === "moDetail"} onClose={() => { setSheet(null); setMoDetail(null); }} P={P}>
        {moDetail && (() => {
          const cat = materialCatalogs[moDetail.catalogId];
          const totalQty = moDetail.items.reduce((s, it) => s + it.qty, 0);
          return (
            <>
              <SheetHead kicker={t(`materialOrder.catalog.${cat.id}.label`, cat.label)} title={t("materialOrder.detail.titleTemplate", { date: fmtFR(moDetail.createdAt) }, `Bon de commande · ${fmtFR(moDetail.createdAt)}`)} onClose={() => { setSheet(null); setMoDetail(null); }} />
              <div style={{ display: "flex", justifyContent: "space-between", padding: "9px 2px", borderBottom: `1px solid ${P.line}` }}>
                <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("materialOrder.detail.destinationLabel", "Destination")}</span>
                <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{cat.destination}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "9px 2px", borderBottom: `1px solid ${P.line}` }}>
                <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("articleFinderAdmin.fieldCreatedBy", "Créé par")}</span>
                <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{moDetail.createdBy}</strong>
              </div>
              {moDetail.note && (
                <div style={{ padding: "9px 2px", borderBottom: `1px solid ${P.line}` }}>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700, display: "block", marginBottom: 3 }}>{t("controlRun.remarkLabel", "Remarque")}</span>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{moDetail.note}</span>
                </div>
              )}
              <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "14px 2px 9px" }}>{t("materialOrder.detail.articlesHeadingTemplate", { n: totalQty }, `Articles (${totalQty} unités)`)}</strong>
              {moDetail.items.map((it) => (
                <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 11px", borderRadius: 12, background: P.soft, marginBottom: 6 }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block" }}>{it.name}</span>
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{it.artNr}{it.ve ? ` · VE ${it.ve}` : ""}</span>
                  </div>
                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>×{it.qty}</strong>
                </div>
              ))}
              <div style={{ display: "flex", gap: 10, marginTop: 15 }}>
                <Pressable onClick={() => showToast(t("toast.printPdfSimulated", "🖨️ Impression / PDF (simulée)"))} style={{
                  flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                  background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                }}>🖨️ Imprimer / PDF</Pressable>
                {moDetail.status === "pending" && (
                  <Pressable onClick={() => markOrderSent(moDetail.id)} style={{ flex: 1, ...btnSolid(A.green) }}>{t("materialOrder.detail.markSentBtn", "Marquer comme envoyé")}</Pressable>
                )}
              </div>
            </>
          );
        })()}
      </Sheet>

      {/* Détail commande de marchandises (écran Gestion) — édition quantité/unité/date/
          remarque, suppression de ligne, actions Envoyer/Marquer reçue/Annuler selon
          permission et statut (voir le plan : merchandiseOrders.modify pour rouvrir une
          commande déjà terminee/envoyee, .validate pour Envoyer/Annuler). */}
      <Sheet open={sheet === "merchOrderDetail"} onClose={() => { setSheet(null); setMerchOrderDetail(null); }} P={P}>
        {merchOrderDetail && (() => {
          const o = merchOrderDetail;
          const meta = merchCategoryMeta(o.zone);
          const sm = MERCH_STATUS_META[o.status] || MERCH_STATUS_META.brouillon;
          const canEditLines = o.status === "brouillon" || o.status === "en_cours" || canModifyMerch;
          const orderedLines = o.lines.filter((l) => !l.skipped && l.quantity > 0);
          return (
            <>
              <SheetHead kicker={meta.label} title={t("merch.detail.orderOnTemplate", { date: fmtFR(o.deliveryDate) }, `Commande · livraison ${fmtFR(o.deliveryDate)}`)} onClose={() => { setSheet(null); setMerchOrderDetail(null); }} />
              <span style={{ display: "inline-block", fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 10px", color: sm.color, background: sm.bg, marginBottom: 12 }}>{sm.label}</span>

              {canModifyMerch ? (
                <Field label={t("merch.detail.deliveryDateLabel", "Date de livraison")}>
                  {/* min manquant ici (bug réel trouvé, 2026-08-31 — retour utilisateur :
                      "le calendrier de Bestellung ne doit jamais prendre en compte des
                      anciennes dates") — cet écran modifie la date d'une commande DÉJÀ créée,
                      contrairement à l'écran de création (merch.date.fieldLabel) qui avait
                      déjà min={todayISO()}. Même garde défensive dans onChange que là-bas. */}
                  <input style={dateTimeInputStyle} type="date" min={todayISO()} value={o.deliveryDate}
                    onChange={(e) => { if (e.target.value && e.target.value < todayISO()) return; setMerchOrderDetail({ ...o, deliveryDate: e.target.value }); }}
                    onBlur={() => saveMerchOrderField({ deliveryDate: merchOrderDetail.deliveryDate }, t("toast.merchDateSaved", "✓ Date mise à jour"))} />
                </Field>
              ) : (
                <div style={{ display: "flex", justifyContent: "space-between", padding: "9px 2px", borderBottom: `1px solid ${P.line}` }}>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("merch.detail.deliveryDateLabel", "Date de livraison")}</span>
                  <strong style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{fmtFR(o.deliveryDate)}</strong>
                </div>
              )}

              <Field label={t("merch.detail.noteLabel", "Remarque générale")}>
                <input style={inputStyle} placeholder={t("merch.detail.notePlaceholder", "Précision pour cette commande...")} value={o.note || ""}
                  onChange={(e) => setMerchOrderDetail({ ...o, note: e.target.value })}
                  onBlur={() => saveMerchOrderField({ note: merchOrderDetail.note }, t("toast.merchNoteSaved", "✓ Remarque enregistrée"))}
                  disabled={!canModifyMerch && !["brouillon", "en_cours"].includes(o.status)} />
              </Field>

              <strong style={{ display: "block", fontSize: `calc(13px * var(--nx-font-scale, 1))`, margin: "14px 2px 9px" }}>{t("merch.detail.linesHeadingTemplate", { n: orderedLines.length }, `Articles commandés (${orderedLines.length})`)}</strong>
              {orderedLines.length === 0 && <p style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 10px" }}>{t("merch.detail.noOrderedLines", "Aucun article commandé sur cette commande.")}</p>}
              {orderedLines.map((line) => (
                <div key={line.articleId} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", borderRadius: 12, background: P.soft, marginBottom: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, display: "block" }}>{line.name}</span>
                    <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{line.artNr}{line.ve ? ` · VE ${line.ve}` : ""}</span>
                  </div>
                  {canEditLines ? (
                    <>
                      <input type="number" inputMode="numeric" value={line.quantity}
                        onChange={(e) => setMerchOrderDetail({ ...o, lines: o.lines.map((l) => l.articleId === line.articleId ? { ...l, quantity: Math.max(0, Math.round(Number(e.target.value) || 0)) } : l) })}
                        onBlur={() => updateMerchOrderLine(o.lines.find((l) => l.articleId === line.articleId))}
                        style={{ width: 46, textAlign: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 8, border: `1.5px solid ${P.line}`, background: P.card, color: P.ink, fontFamily: `var(--nx-font, ${FONT})`, padding: "6px 2px" }} />
                      <select value={line.unit} onChange={(e) => { const updated = { ...line, unit: e.target.value }; setMerchOrderDetail({ ...o, lines: o.lines.map((l) => l.articleId === line.articleId ? updated : l) }); updateMerchOrderLine(updated); }}
                        style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, borderRadius: 8, border: `1.5px solid ${P.line}`, background: P.card, color: P.ink, fontFamily: `var(--nx-font, ${FONT})`, padding: "6px 4px" }}>
                        {/* Même règle que l'écran article (retour utilisateur explicite : "je vois
                            encore le choix entre karton, lage, dd palette, euro palette dans une
                            commande kuhlung") — Karton seul sauf Trocken, jamais les 4 unités ici. */}
                        {merchAllowedUnits(o.zone).map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                      <Pressable onClick={() => removeMerchOrderLine(line.articleId)} aria-label={t("merch.detail.removeLineAria", "Retirer la ligne")} style={{
                        width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.13)", color: A.red, display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, flexShrink: 0,
                      }}>✕</Pressable>
                    </>
                  ) : (
                    <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>×{line.quantity} {line.unit}</strong>
                  )}
                </div>
              ))}

              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 15 }}>
                {o.status === "terminee" && canValidateMerch && (
                  <Pressable onClick={() => sendMerchOrderFromManage(o)} style={{ width: "100%", ...btnSolid(A.green) }}>{t("merch.detail.sendBtn", "📤 Envoyer")}</Pressable>
                )}
                {o.status === "envoyee" && canModifyMerch && (
                  <Pressable onClick={() => markMerchOrderDelivered(o)} style={{ width: "100%", ...btnSolid(A.blue) }}>{t("merch.detail.markDeliveredBtn", "📦 Marquer reçue")}</Pressable>
                )}
                {!["livree", "annulee"].includes(o.status) && canValidateMerch && (
                  <Pressable onClick={() => cancelMerchOrder(o)} style={{
                    width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                    background: "rgba(239,68,68,.1)", border: `1.5px solid ${A.red}`, color: A.red,
                  }}>{t("merch.detail.cancelBtn", "Annuler la commande")}</Pressable>
                )}
                {/* Suppression DÉFINITIVE (retour utilisateur explicite) — distincte d'Annuler,
                    qui garde la commande à l'historique. Toujours accessible (même une
                    commande déjà annulee/livree peut être nettoyée), même palier que
                    Envoyer/Annuler. */}
                {canValidateMerch && (
                  <Pressable onClick={() => deleteMerchOrder(o)} style={{
                    width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                    background: A.red, color: "#fff",
                  }}>{t("merch.detail.deleteBtn", "🗑️ Supprimer définitivement")}</Pressable>
                )}
              </div>
            </>
          );
        })()}
      </Sheet>

      {/* Liste de commande (☰, écran article) — accès direct à tous les articles du rayon en
          cours, quantité déjà saisie visible, tap = aller directement à cet article (retour
          utilisateur explicite : "je veux la possibilité d'accéder à la liste de commande...
          et pouvoir sélectionner dans la liste un article"). Distincte de la Sheet
          "merchOrderDetail" (Gestion des commandes, historique des commandes passées) — ici
          on navigue DANS la commande en cours de préparation. */}
      <Sheet open={sheet === "merchArticleList"} onClose={() => setSheet(null)} P={P} maxH="80%">
        {merchSession && (() => {
          const articles = merchSession.articles || [];
          const linesById = new Map((merchSession.order?.lines || []).map((l) => [l.articleId, l]));
          const done = articles.filter((a) => { const l = linesById.get(a.id); return l && !l.skipped && l.quantity > 0; }).length;
          return (
            <>
              <SheetHead kicker={merchCategoryMeta(merchActiveZone).label} title={t("merch.list.title", "Liste de commande")} onClose={() => setSheet(null)} />
              <p style={{ margin: "0 2px 10px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>
                {t("merch.list.summaryTemplate", { done, total: articles.length, remaining: articles.length - done }, `${done} commandé${done > 1 ? "s" : ""} sur ${articles.length} · ${articles.length - done} restant${articles.length - done > 1 ? "s" : ""}`)}
              </p>
              {articles.map((a, i) => {
                const line = linesById.get(a.id);
                const isDone = line && !line.skipped && line.quantity > 0;
                return (
                  <Pressable key={a.id} onClick={() => { setSheet(null); goToMerchArticle(i); }} style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "11px 8px", borderRadius: 14,
                    background: i === merchIndex ? "rgba(45,127,240,.08)" : "transparent",
                  }}>
                    <span style={{ width: 20, fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textAlign: "center", flexShrink: 0 }}>{i + 1}</span>
                    <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                      <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 700, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
                      <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("merch.article.artNrLabel", "Art.Nr")} {a.artNr}</span>
                    </div>
                    {isDone ? (
                      <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, padding: "5px 11px", borderRadius: 99, whiteSpace: "nowrap", flexShrink: 0, background: "rgba(30,184,95,.13)", color: A.green }}>×{line.quantity} {line.unit}</span>
                    ) : (
                      <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, padding: "5px 11px", borderRadius: 99, whiteSpace: "nowrap", flexShrink: 0, background: P.soft, color: P.muted }}>—</span>
                    )}
                  </Pressable>
                );
              })}
            </>
          );
        })()}
      </Sheet>

      {/* Nouvelle/Modifier mission — vrai popup premium (retour utilisateur explicite :
          "Ce la doit etre un popup premium digne d'une interface GR"), jamais un écran
          plein comme avant. Ordre des champs imposé : filiales destinataires D'ABORD, puis
          le titre mis en valeur (zone teintée à part, jamais un champ texte comme les
          autres), consigne, échéance (date+heure séparées), preuve, puis annuler/envoyer. */}
      <Sheet open={sheet === "missionForm"} onClose={() => setSheet(null)} P={P}>
        <SheetHead kicker={t("missions.form.kicker", "Pilotage réseau")} title={missionEditId ? t("missions.form.editTitle", "Modifier la mission") : t("missions.form.newTitle", "Nouvelle mission")} onClose={() => setSheet(null)} />

        {/* Ciblage direct d'un responsable (retour utilisateur 2026-08-24, "un NL doit
            pouvoir assigner des tâches à VKL et BL et VKL doit assigner des tâches à BL et
            filiales s'il souhaite") — VKL/NL uniquement, un BL n'a pas de subordonné réseau
            donc jamais ce choix pour lui. adminUsers est déjà scopé au périmètre effectif
            de l'appelant (même filtre serveur que GET /api/users), donc contient déjà
            exactement les bons candidats (VKL+BL pour un NL, BL seuls pour un VKL) sans
            recalcul supplémentaire. */}
        {!missionEditId && ["Verkaufsleiter", "Niederlassungsleiter"].includes(user?.role) && (() => {
          const leaderCandidates = adminUsers.filter((u) =>
            user?.role === "Niederlassungsleiter" ? ["Verkaufsleiter", "Bereichsleiter"].includes(u.role) : u.role === "Bereichsleiter"
          );
          if (!leaderCandidates.length) return null;
          const inLeaderMode = missionForm.targetMode === "leader";
          return (
            <div style={{ display: "flex", gap: 4, padding: 3, background: P.soft, borderRadius: 15, marginBottom: 16 }}>
              {[
                { id: "filiales", l: t("missions.form.targetFilialesTab", "Des filiales") },
                { id: "leader", l: t("missions.form.targetLeaderTab", "Des responsables") },
              ].map((tb) => (
                <Pressable key={tb.id} onClick={() => setMissionForm((f) => ({ ...f, targetMode: tb.id, targetLeaderIds: tb.id === "leader" ? f.targetLeaderIds : [], filiales: tb.id === "filiales" ? f.filiales : [] }))} style={{
                  flex: 1, textAlign: "center", padding: "9px 4px", borderRadius: 12, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: (tb.id === "leader") === inLeaderMode ? P.card : "transparent",
                  color: (tb.id === "leader") === inLeaderMode ? P.ink : P.muted,
                  boxShadow: (tb.id === "leader") === inLeaderMode ? "0 1px 4px rgba(0,0,0,.08)" : "none",
                }}>{tb.l}</Pressable>
              ))}
            </div>
          );
        })()}

        {missionForm.targetMode === "leader" ? (() => {
          // Multi-sélection (retour utilisateur explicite 2026-08-24 : "on ne peut pas
          // sélectionner plusieurs personnes à la fois") — même patron de checklist que le
          // sélecteur de filiales juste en dessous, jamais un choix unique.
          const leaderCandidates = adminUsers.filter((u) =>
            user?.role === "Niederlassungsleiter" ? ["Verkaufsleiter", "Bereichsleiter"].includes(u.role) : u.role === "Bereichsleiter"
          );
          // Groupé par palier (retour utilisateur explicite 2026-08-24 : "un sélecteur VKL
          // liste les vkl et BL les bl... et à chaque fois le badge s'incrémente au niveau
          // de vkl ou bl") — un NL choisit parmi 2 groupes distincts, chacun avec son propre
          // badge de compte ; un VKL n'a qu'un seul groupe (BL, jamais de section à afficher
          // pour un seul groupe non-vide).
          const groups = [
            { role: "Verkaufsleiter", label: t("common.roleVerkaufsleiter", "Verkaufsleiter") },
            { role: "Bereichsleiter", label: t("common.roleBereichsleiter", "Bereichsleiter") },
          ].map((g) => ({ ...g, items: leaderCandidates.filter((u) => u.role === g.role) })).filter((g) => g.items.length > 0);
          const renderLeaderRow = (u, i) => {
            const on = missionForm.targetLeaderIds.includes(u.employeeId);
            return (
              <Pressable key={u.id} onClick={() => setMissionForm((f) => ({
                ...f, targetLeaderIds: on ? f.targetLeaderIds.filter((id) => id !== u.employeeId) : [...f.targetLeaderIds, u.employeeId],
              }))} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "12px 15px",
                borderTop: i > 0 ? `1px solid ${P.line}` : "none",
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{u.firstname} {u.lastname}</strong>
                  <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{u.role}</div>
                </div>
                <span style={{
                  width: 22, height: 22, borderRadius: 99, flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "center",
                  background: on ? A.purple : P.soft, color: "#fff", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800,
                }}>{on ? "✓" : ""}</span>
              </Pressable>
            );
          };
          return (
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>
                {t("missions.form.targetLeaderLabelTemplate", { n: missionForm.targetLeaderIds.length }, `Responsables destinataires (${missionForm.targetLeaderIds.length})`)}
              </label>
              {groups.length > 1 ? groups.map((g) => {
                const selectedCount = g.items.filter((u) => missionForm.targetLeaderIds.includes(u.employeeId)).length;
                return (
                  <div key={g.role} style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 2px 8px" }}>
                      <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".04em" }}>{g.label}</span>
                      {selectedCount > 0 && (
                        <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, minWidth: 16, height: 16, padding: "0 5px", display: "grid", placeItems: "center", background: A.purple, color: "#fff" }}>{selectedCount}</span>
                      )}
                    </div>
                    <section style={{ ...card, borderRadius: 20, overflow: "hidden" }}>
                      {g.items.map(renderLeaderRow)}
                    </section>
                  </div>
                );
              }) : (
                <section style={{ ...card, borderRadius: 20, overflow: "hidden" }}>
                  {leaderCandidates.map(renderLeaderRow)}
                </section>
              )}
            </div>
          );
        })() : (
        <>
        <label style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>
          {t("missions.form.recipientStoresTemplate", { n: missionForm.filiales.length }, `Filiales destinataires (${missionForm.filiales.length})`)}
        </label>
        {(() => {
          // Périmètre borné (retour utilisateur explicite, 2026-08-23 : "un BL ne devrait
          // choisir que parmi ses filiales directes, un VKL parmi celles de ses BL") —
          // storesRegistry est le registre RÉSEAU ENTIER (1200+ à terme), jamais le bon
          // choix pour ce sélecteur. managerOverview.stores EST déjà identity.stores
          // (BFS-inclusif) côté serveur — même périmètre que la vérification faite dans
          // POST/PATCH /api/missions (assertFilialesInScope), jamais deux règles
          // différentes. NEXORA_ADMIN garde le registre entier (oversight réseau complet).
          const myMissionStores = (isNexoraAdmin || isAdminModuleOverride)
            ? storesRegistry
            : storesRegistry.filter((s) => (managerOverview?.stores || []).includes(s.id));
          return (
            <>
              <div className="nx-chips" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 8, paddingBottom: 2 }}>
                <Pressable onClick={() => setMissionForm((f) => ({ ...f, filiales: f.filiales.length === myMissionStores.length ? [] : myMissionStores.map((s) => s.id) }))} style={{
                  flex: "0 0 auto", padding: "7px 12px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11px * var(--nx-font-scale, 1))`, background: P.soft, color: P.muted,
                }}>{missionForm.filiales.length === myMissionStores.length ? t("missions.form.deselectAllBtn", "Tout désélectionner") : t("missions.form.selectAllBtn", "Tout sélectionner")}</Pressable>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 20 }}>
                {myMissionStores.map((s) => {
                  const on = missionForm.filiales.includes(s.id);
                  return (
                    <Pressable key={s.id} onClick={() => toggleMissionFiliale(s.id)} style={{
                      padding: "7px 12px", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                      background: on ? A.purple : P.soft, color: on ? "#fff" : P.muted,
                    }}>{on ? "✓ " : ""}{s.label}</Pressable>
                  );
                })}
              </div>
            </>
          );
        })()}
        </>
        )}

        {/* Titre — toujours mis en valeur, jamais un simple champ texte comme les autres
            (retour utilisateur explicite : "le titre de la mission doit etre toujours mis
            en valeur, autre couleur"). */}
        <div style={{
          background: "linear-gradient(155deg, rgba(139,47,214,.1), rgba(139,47,214,.04))",
          border: `1.5px solid rgba(139,47,214,.22)`, borderRadius: 18, padding: "12px 14px", marginBottom: 16,
        }}>
          <label style={{ ...eyebrow(A.purple), display: "block", marginBottom: 6 }}>🎯 {t("missions.form.titleLabel", "Titre de la mission")}</label>
          <input value={missionForm.title} onChange={(e) => setMissionForm((f) => ({ ...f, title: e.target.value }))}
            placeholder={t("tasks.titlePlaceholderExample", "ex. Vérifier l'affichage des prix")} style={{
              width: "100%", minWidth: 0, boxSizing: "border-box", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(17px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.purple,
              background: "transparent", border: "none", outline: "none", padding: 0,
            }} />
        </div>

        {/* Consigne — même principe que le titre : jamais un champ discret parmi d'autres,
            c'est l'instruction que les filiales concernées doivent pouvoir repérer d'un
            coup d'œil (retour utilisateur explicite : "elle est trop discrete, les
            interessees peuvent la reter visuellement change le style"). */}
        <div style={{
          background: "linear-gradient(155deg, rgba(231,138,18,.11), rgba(231,138,18,.04))",
          border: `1.5px solid rgba(231,138,18,.28)`, borderRadius: 18, padding: "12px 14px", marginBottom: 16,
        }}>
          <label style={{ ...eyebrow(A.orange), display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>📋 {t("missions.form.instructionLabel", "Consigne de la mission")}</label>
          <textarea value={missionForm.note} onChange={(e) => setMissionForm((f) => ({ ...f, note: e.target.value }))} rows={3}
            placeholder={t("tasks.descriptionPlaceholder", "Détaille précisément ce qui est attendu…")} style={{
              width: "100%", minWidth: 0, boxSizing: "border-box", fontFamily: `var(--nx-font, ${FONT})`, fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink,
              background: "transparent", border: "none", outline: "none", padding: 0, resize: "vertical", lineHeight: 1.5,
            }} />
        </div>

        {/* Destinataires — vrai choix structuré (retour utilisateur explicite : "on a
            juste pris en compte le FL... au cas où le FL ne serait pas là, c'est un
            probleme... ajouter d'autres employes pour avoir le choix entre un employé
            spécifique et toute le monde"). "Tous les employés" notifie et rend la mission
            actionnable pour TOUTE l'équipe active de chaque filiale ciblée, plus seulement
            le FL/FLA. */}
        {/* Destinataires (team/specific) — hors sujet en mode "responsable direct" (le
            destinataire EST le responsable choisi plus haut, jamais une équipe de filiale). */}
        {missionForm.targetMode !== "leader" && (
        <>
        <label style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>{t("missions.form.recipientsLabel", "Destinataires")}</label>
        <div style={{ display: "flex", gap: 8, marginBottom: missionForm.recipientMode === "specific" ? 10 : 16 }}>
          {[{ id: "team", l: `👥 ${t("missions.form.recipientModeTeam", "Tous les employés")}` }, { id: "specific", l: `👤 ${t("missions.form.recipientModeSpecific", "Employé(s) spécifique(s)")}` }].map((r) => {
            const sel = missionForm.recipientMode === r.id;
            return (
              <Pressable key={r.id} onClick={() => {
                setMissionForm((f) => ({ ...f, recipientMode: r.id, specificEmployeeIds: r.id === "team" ? [] : f.specificEmployeeIds }));
                // Rafraîchit toujours la liste au moment où elle devient utile — jamais une
                // liste en cache qui pourrait encore proposer un employé définitivement
                // supprimé entre-temps (retour utilisateur explicite).
                if (r.id === "specific") loadMissionEmployees(missionForm.filiales);
              }} style={{
                flex: 1, textAlign: "center", padding: "11px 6px", borderRadius: 14, fontWeight: 700, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                background: sel ? A.purple : P.soft, color: sel ? "#fff" : P.ink, border: `1.5px solid ${sel ? A.purple : P.line}`,
              }}>{r.l}</Pressable>
            );
          })}
        </div>
        {missionForm.recipientMode === "specific" && (
          <div style={{ marginBottom: 16 }}>
            {missionForm.filiales.length === 0 ? (
              <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px" }}>{t("missions.form.chooseStoreFirstText", "Choisis d'abord au moins une filiale.")}</p>
            ) : missionEmployeeOptions.length === 0 ? (
              <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px" }}>{t("missions.noActiveEmployeeInStoresText", "Aucun employé actif dans ces filiales.")}</p>
            ) : (
              <>
                <label style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, display: "block", marginBottom: 6 }}>
                  {t("missions.form.selectedCountTemplate", { n: missionForm.specificEmployeeIds.length }, `${missionForm.specificEmployeeIds.length} sélectionné${missionForm.specificEmployeeIds.length > 1 ? "s" : ""} — plusieurs possibles`)}
                </label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {missionEmployeeOptions.map((e) => {
                    const sel = missionForm.specificEmployeeIds.includes(e.employeeId);
                    return (
                      <Pressable key={e.employeeId} onClick={() => setMissionForm((f) => ({
                        ...f, specificEmployeeIds: sel ? f.specificEmployeeIds.filter((id) => id !== e.employeeId) : [...f.specificEmployeeIds, e.employeeId],
                      }))} style={{
                        padding: "7px 12px", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                        background: sel ? A.blue : P.soft, color: sel ? "#fff" : P.muted,
                      }}>{sel ? "✓ " : ""}{e.name} · {e.storeId}</Pressable>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
        </>
        )}

        <label style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>{t("tasks.priorityLabelField", "Priorité")}</label>
        <div style={{ display: "flex", gap: 7, marginBottom: 16 }}>
          {["normal", "high", "urgent"].map((p) => {
            const sel = missionForm.priority === p;
            const pm = priorityMeta[p];
            return (
              <Pressable key={p} onClick={() => setMissionForm((f) => ({ ...f, priority: p }))} style={{
                flex: 1, textAlign: "center", padding: "9px 6px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                background: sel ? pm.color : P.soft, color: sel ? "#fff" : P.ink, border: `1.5px solid ${sel ? pm.color : P.line}`,
              }}>{pm.label}</Pressable>
            );
          })}
        </div>

        {/* Source de l'échéance (2026-08-31, retour utilisateur explicite + vrai
            Rundschreiben joint en exemple) — "rundschreiben" : échéance imposée par une
            circulaire du siège, le créateur la transcrit, jamais un choix à optimiser.
            "initiative" : décision propre du GR — seul cas où une suggestion de date
            (calendrier, pas encore construit) aurait un sens. Change aussi si la 2e Frist
            (synthèse) est proposée juste en dessous : une initiative propre n'a normalement
            qu'un seul étage. */}
        <label style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>{t("missions.form.sourceTypeLabel", "D'où vient cette échéance ?")}</label>
        <div style={{ display: "flex", gap: 7, marginBottom: 16 }}>
          {["rundschreiben", "initiative"].map((s) => {
            const sel = missionForm.sourceType === s;
            return (
              <Pressable key={s} onClick={() => setMissionForm((f) => ({ ...f, sourceType: s, ...(s === "initiative" ? { echeanceSyntheseDate: "", echeanceSyntheseTime: "" } : {}) }))} style={{
                flex: 1, textAlign: "center", padding: "10px 6px", borderRadius: 14, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, lineHeight: 1.3,
                background: sel ? A.purple : P.soft, color: sel ? "#fff" : P.ink, border: `1.5px solid ${sel ? A.purple : P.line}`,
              }}>{s === "rundschreiben" ? t("missions.form.sourceRundschreibenLabel", "🔒 Rundschreiben (imposée)") : t("missions.form.sourceInitiativeLabel", "🎯 Initiative propre")}</Pressable>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <Field label={t("missions.form.deadlineDateLabel", "Échéance — date")}>
              <input style={dateTimeInputStyle} type="date" min={todayISO()} value={missionForm.echeanceDate}
                onChange={(e) => setMissionForm((f) => ({ ...f, echeanceDate: e.target.value }))} />
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label={t("missions.form.deadlineTimeLabel", "Échéance — heure")}>
              <TimeSelect value={missionForm.echeanceTime} minuteOptions={["00", "30"]}
                onChange={(v) => setMissionForm((f) => ({ ...f, echeanceTime: v }))} />
            </Field>
          </div>
        </div>

        {/* 2e échéance, facultative — vrai document : "melden Sie bis Dienstag 18.08 nach
            Geschäftsschluss" (filiales → BL) PUIS "Vollzugsmeldung Bereichsleitung...
            Mittwoch 19.08 10:00" (BL → réseau). L'échéance ci-dessus reste celle des
            FILIALES ; celle-ci porte la consolidation du créateur, jamais requise, jamais
            déduite automatiquement. Proposée seulement pour "rundschreiben" — une
            initiative propre du GR n'a normalement qu'un seul étage. */}
        {missionForm.sourceType === "rundschreiben" && (
          <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <Field label={t("missions.form.deadlineSyntheseDateLabel", "Échéance de synthèse — date (facultatif)")}>
                <input style={dateTimeInputStyle} type="date" min={missionForm.echeanceDate || todayISO()} value={missionForm.echeanceSyntheseDate}
                  onChange={(e) => setMissionForm((f) => ({ ...f, echeanceSyntheseDate: e.target.value }))} />
              </Field>
            </div>
            <div style={{ flex: 1 }}>
              <Field label={t("missions.form.deadlineSyntheseTimeLabel", "Échéance de synthèse — heure")}>
                <TimeSelect value={missionForm.echeanceSyntheseTime} minuteOptions={["00", "30"]}
                  onChange={(v) => setMissionForm((f) => ({ ...f, echeanceSyntheseTime: v }))} />
              </Field>
            </div>
          </div>
        )}

        <Pressable onClick={() => setMissionForm((f) => ({ ...f, preuveRequise: !f.preuveRequise }))} style={{
          display: "flex", alignItems: "center", gap: 12, width: "100%", boxSizing: "border-box", padding: 14, borderRadius: 16, background: P.soft, marginBottom: 22,
        }}>
          <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`}}>📷</span>
          <div style={{ flex: 1 }}>
            <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{t("missions.form.requireProofLabel", "Demander une preuve")}</strong>
            <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("missions.photoOrDocRequiredAtCloseText", "Photo ou document requis à la clôture.")}</span>
          </div>
          <span style={{
            width: 44, height: 26, borderRadius: 99, background: missionForm.preuveRequise ? A.purple : P.line, position: "relative", flex: "0 0 auto",
          }}>
            <span style={{ position: "absolute", top: 2.5, left: missionForm.preuveRequise ? 20 : 3, width: 21, height: 21, borderRadius: 99, background: "#fff", transition: "left .18s ease", boxShadow: "0 1px 3px rgba(0,0,0,.3)" }} />
          </span>
        </Pressable>

        <div style={{ display: "flex", gap: 9 }}>
          <Pressable onClick={() => setSheet(null)} style={{
            flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
          }}>{t("common.cancel", "Annuler")}</Pressable>
          <Pressable onClick={createMission} style={{ flex: 1, ...btnSolid(A.purple) }}>{missionEditId ? t("missions.form.saveBtn", "✓ Enregistrer") : t("missions.form.sendBtn", "🎯 Envoyer la mission")}</Pressable>
        </div>
      </Sheet>

      {/* Fiche mission — vrai popup (retour utilisateur explicite : "le clic sur une
          mission en cours doir ouvrir un popup et non une page"), style tableau premium.
          Chaque filiale ciblée a sa propre ligne avec son PROPRE statut
          (server/missions/repository.js) — jamais un statut global appliqué à toutes, et
          ça se met à jour en direct dès qu'un responsable valide/refuse ou qu'une filiale
          soumet son résultat (loadMissions() re-fetch à chaque action). */}
      <Sheet open={sheet === "missionDetail"} onClose={() => { setSheet(null); setMissionDetailId(null); }} P={P}>
        {missionDetailId && (() => {
          const m = networkMissions.find((x) => x.id === missionDetailId);
          if (!m) return <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("missions.detail.notFoundText", "Mission introuvable.")}</p>;
          const myStatus = missionEffectiveStatus(m);
          const meta = MISSION_OVERALL_META[myStatus];
          const Row = ({ label, children, strong: isStrong }) => (
            <div style={{ display: "flex", justifyContent: "space-between", gap: 14, padding: "11px 2px", borderBottom: `1px solid ${P.line}` }}>
              <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700, flex: "0 0 auto" }}>{label}</span>
              <span style={{ fontSize: `calc(${(isStrong ? 13.5 : 12.5)}px * var(--nx-font-scale, 1))`, fontWeight: isStrong ? 800 : 600, color: P.ink, textAlign: "right" }}>{children}</span>
            </div>
          );
          const pm = priorityMeta[m.priority || "normal"];
          // Noms résolus côté serveur à chaque réponse (m.specificEmployeeNames), jamais
          // depuis un cache client qui pourrait encore afficher un employé définitivement
          // supprimé entre-temps (retour utilisateur explicite).
          const destinatairesLabel = m.recipientMode === "specific"
            ? ((m.specificEmployeeNames && m.specificEmployeeNames.length ? m.specificEmployeeNames.join(", ") : null) || t("missions.detail.specificEmployeesFallback", "Employé(s) spécifique(s)"))
            : t("missions.detail.wholeTeamLabel", "Toute l'équipe");
          const threadByStore = m.filiales.map((fid) => ({ fid, entries: m.thread.filter((te) => te.storeId === fid) }));
          const remainingLabel = missionRemainingLabel(m);
          return (
            <>
              <SheetHead kicker={t("missions.detail.kicker", "Pilotage réseau · Mission")} title={m.title} onClose={() => { setSheet(null); setMissionDetailId(null); }} />
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 14, flexWrap: "wrap" }}>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: meta.c, borderRadius: 99, padding: "5px 11px" }}>{meta.l}</span>
                {m.priority !== "normal" && (
                  <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: pm.color, borderRadius: 99, padding: "5px 11px" }}>{pm.label}</span>
                )}
                {remainingLabel && <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>{remainingLabel}</span>}
              </div>

              {/* Consigne — sa propre carte teintée, jamais un sous-texte discret (retour
                  utilisateur explicite : "les interessees peuvent la reter visuellement
                  change le style"). Même traitement que dans le formulaire, pour que la
                  consigne se reconnaisse immédiatement entre les deux écrans. */}
              {m.note && (
                <div style={{
                  background: "linear-gradient(155deg, rgba(231,138,18,.11), rgba(231,138,18,.04))",
                  border: `1.5px solid rgba(231,138,18,.28)`, borderRadius: 18, padding: "12px 14px", marginBottom: 16,
                }}>
                  <label style={{ ...eyebrow(A.orange), display: "flex", alignItems: "center", gap: 6, marginBottom: 7 }}>📋 {t("missions.detail.instructionLabel", "Consigne")}</label>
                  <p style={{ margin: 0, fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink, lineHeight: 1.5 }}>{m.note}</p>
                </div>
              )}

              <section style={{ ...card, borderRadius: 20, padding: "4px 15px", marginBottom: 16 }}>
                {!(m.targetLeaderIds || []).length && (
                  <Row label={t("missions.form.recipientsLabel", "Destinataires")}>{m.recipientMode === "specific" ? "👤 " : "👥 "}{destinatairesLabel}</Row>
                )}
                <Row label={t("missions.form.deadlineLabel", "Échéance")} strong>
                  {m.echeanceDate ? `${fmtFR(m.echeanceDate)}${m.echeanceTime ? ` ${t("missions.detail.deadlineAtTemplate", { time: m.echeanceTime }, `à ${m.echeanceTime}`)}` : ""}` : t("missions.detail.noneText", "Aucune")}
                </Row>
                <Row label={t("missions.detail.proofRequestedLabel", "Preuve demandée")}>{m.preuveRequise ? `📷 ${t("common.yes", "Oui")}` : t("common.no", "Non")}</Row>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 14, padding: "11px 2px" }}>
                  <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("tasks.createdByLabel", "Créée par")}</span>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 600 }}>{m.createdByName}{m.relancedAt ? ` · ${t("missions.detail.remindedOnTemplate", { date: fmtFR(m.relancedAt) }, `relancée ${fmtFR(m.relancedAt)}`)}` : ""}</span>
                </div>
              </section>

              {/* Ciblage direct de responsables (retour utilisateur 2026-08-24, "on ne peut
                  pas sélectionner plusieurs personnes à la fois") — pas de liste de
                  filiales dans ce mode, un ou plusieurs destinataires, CHACUN avec son
                  propre statut/preuve et sa propre action "Marquer comme fait" (voir
                  POST /:id/submit-leader — chacun ne marque que SA part). */}
              {(m.targetLeaderIds || []).length > 0 ? (() => {
                return (
                  <>
                    <strong style={{ display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px 8px" }}>{t("missions.detail.targetLeaderTitleTemplate", { n: m.targetLeaderIds.length }, `Responsables ciblés (${m.targetLeaderIds.length})`)}</strong>
                    <section style={{ ...card, borderRadius: 20, overflow: "hidden", marginBottom: 18 }}>
                      {m.targetLeaderIds.map((leaderId, i) => {
                        const ls = (m.leaderStatuses || {})[leaderId] || { status: "en_attente" };
                        const lMeta = FILIALE_STATUS_META[ls.status];
                        const isMe = String(user?.proId || "") === String(leaderId);
                        return (
                          <div key={leaderId} style={{ padding: "13px 15px", borderTop: i > 0 ? `1px solid ${P.line}` : "none" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{(m.targetLeaderNames || {})[leaderId] || leaderId}</strong>
                                {ls.updatedByName && (
                                  <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 1 }}>{ls.updatedByName} · {fmtFR(ls.updatedAt)}</div>
                                )}
                              </div>
                              {ls.proofPhotoUrl && (
                                <Pressable onClick={() => setMissionPhotoLightbox(ls.proofPhotoUrl)} aria-label={t("missions.detail.viewProofPhotoAria", "Voir la photo de preuve")} style={{
                                  display: "flex", alignItems: "center", gap: 6, padding: "5px 8px 5px 5px", borderRadius: 99,
                                  background: "rgba(30,184,95,.12)", flex: "0 0 auto",
                                }}>
                                  <img src={ls.proofPhotoUrl} alt={t("missions.detail.proofPhotoAlt", "Preuve")} style={{ width: 26, height: 26, borderRadius: 99, objectFit: "cover" }} />
                                </Pressable>
                              )}
                              <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: lMeta.c, background: lMeta.bg, borderRadius: 99, padding: "5px 10px", whiteSpace: "nowrap", flex: "0 0 auto" }}>{lMeta.l}</span>
                            </div>
                            {isMe && !["validee", "refusee"].includes(ls.status) && (
                              <Pressable onClick={() => submitLeaderMission(m.id)} style={{ width: "100%", ...btnSolid(A.green), padding: "11px 0", marginTop: 12 }}>
                                ✓ {t("missions.detail.markDoneBtn", "Marquer comme fait")}
                              </Pressable>
                            )}
                            {(() => {
                              // Dialogue de CETTE part uniquement (retour utilisateur explicite,
                              // 2026-08-23 : "on va implémenter le dialogue sur les missions... et
                              // fais pareil dans la vue compte normal parce que tu ne l'as jamais
                              // implanté") — jamais visible pour un autre co-cible (confidentialité,
                              // même principe que threadRestricted côté filiale).
                              const isCreator = String(user?.proId || "") === String(m.createdBy);
                              const myEntries = (m.thread || []).filter((te) => te.leaderId === leaderId);
                              if (!isMe && !(isCreator && myEntries.length > 0)) return null;
                              return (
                                <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${P.line}` }}>
                                  {myEntries.length > 0 && (
                                    <div style={{ marginBottom: 8 }}>
                                      {myEntries.map((te) => (
                                        <div key={te.id} style={{ marginBottom: 7, paddingLeft: te.type === "reply" ? 0 : 9, borderLeft: te.type === "anomaly" ? `3px solid ${A.red}` : te.type === "question" ? `3px solid ${A.blue}` : "none" }}>
                                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                                            <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: te.type === "anomaly" ? A.red : te.type === "reply" ? A.purple : A.blue }}>
                                              {te.type === "anomaly" ? `⚠️ ${t("missions.detail.entryTypeAnomaly", "Anomalie")}` : te.type === "reply" ? `↩️ ${t("missions.detail.entryTypeReply", "Réponse")}` : `❓ ${t("missions.detail.entryTypeQuestion", "Question")}`}
                                            </span>
                                            <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{te.authorName} · {fmtFR(te.createdAt)}</span>
                                          </div>
                                          <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.ink, lineHeight: 1.4 }}>{te.text}</p>
                                          {te.photoUrl && (
                                            <img src={te.photoUrl} alt={t("missions.detail.attachedPhotoAlt", "Photo jointe")} onClick={() => setMissionPhotoLightbox(te.photoUrl)}
                                              style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 9, marginTop: 5, cursor: "pointer" }} />
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {isCreator ? (
                                    missionReplyLeaderId === leaderId ? (
                                      <div>
                                        <textarea value={missionThreadDraft} onChange={(e) => setMissionThreadDraft(e.target.value)} rows={2}
                                          placeholder={t("missions.yourReplyPlaceholder", "Ta réponse…")} style={{ ...inputStyle, marginBottom: 7, resize: "vertical", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}} />
                                        <div style={{ display: "flex", gap: 7 }}>
                                          <Pressable onClick={() => { setMissionReplyLeaderId(null); setMissionThreadDraft(""); }} style={{
                                            flex: 1, textAlign: "center", padding: "8px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink,
                                          }}>{t("common.cancel", "Annuler")}</Pressable>
                                          <Pressable onClick={postMissionLeaderReply} style={{ flex: 1, ...btnSolid(A.purple), padding: "8px 0", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`}}>{t("planning.requests.sendBtn", "Envoyer")}</Pressable>
                                        </div>
                                      </div>
                                    ) : (
                                      <Pressable onClick={() => { setMissionReplyLeaderId(leaderId); setMissionThreadDraft(""); }} style={{
                                        fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.purple,
                                      }}>💬 {t("missions.detail.replyToLeaderTemplate", { name: (m.targetLeaderNames || {})[leaderId] || leaderId }, `Répondre à ${(m.targetLeaderNames || {})[leaderId] || leaderId}`)}</Pressable>
                                    )
                                  ) : isMe && !["validee", "refusee"].includes(ls.status) ? (
                                    <div>
                                      <div style={{ display: "flex", gap: 7, marginBottom: 7 }}>
                                        {[{ id: "question", l: `❓ ${t("missions.threadTypeQuestion", "Question")}` }, { id: "anomaly", l: `⚠️ ${t("missions.threadTypeAnomaly", "Anomalie")}` }].map((opt) => (
                                          <Pressable key={opt.id} onClick={() => setLeaderThreadType(opt.id)} style={{
                                            flex: 1, textAlign: "center", padding: "7px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(11px * var(--nx-font-scale, 1))`,
                                            background: leaderThreadType === opt.id ? (opt.id === "anomaly" ? A.red : A.blue) : P.soft,
                                            color: leaderThreadType === opt.id ? "#fff" : P.ink,
                                          }}>{opt.l}</Pressable>
                                        ))}
                                      </div>
                                      <textarea value={leaderThreadDraft} onChange={(e) => setLeaderThreadDraft(e.target.value)} rows={2}
                                        placeholder={leaderThreadType === "anomaly" ? t("missions.describeAnomalyPlaceholder", "Décris l'anomalie…") : t("missions.writeQuestionPlaceholder", "Écris ta question…")} style={{ ...inputStyle, marginBottom: 7, resize: "vertical", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}} />
                                      <Pressable disabled={leaderThreadBusy} onClick={postLeaderThread} style={{ width: "100%", ...btnSolid(leaderThreadType === "anomaly" ? A.red : A.blue), padding: "9px 0", fontSize: `calc(12px * var(--nx-font-scale, 1))`, opacity: leaderThreadBusy ? .6 : 1 }}>
                                        {leaderThreadBusy ? t("common.saving", "Enregistrement…") : `💬 ${t("planning.requests.sendBtn", "Envoyer")}`}
                                      </Pressable>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </section>
                  </>
                );
              })() : (
                <>
              <strong style={{ display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px 8px" }}>{t("missions.detail.concernedStoresTemplate", { n: m.filiales.length }, `Filiales concernées (${m.filiales.length})`)}</strong>
              <section style={{ ...card, borderRadius: 20, overflow: "hidden", marginBottom: 18 }}>
                {m.filiales.map((fid, i) => {
                  const fs = m.filialeStatuses[fid] || { status: "en_attente" };
                  const fMeta = FILIALE_STATUS_META[fs.status];
                  return (
                    <div key={fid} style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "13px 15px",
                      borderTop: i > 0 ? `1px solid ${P.line}` : "none",
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{storeLabelFor(fid)}</strong>
                        {fs.updatedByName && (
                          <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 1 }}>{fs.updatedByName} · {fmtFR(fs.updatedAt)}</div>
                        )}
                      </div>
                      {fs.proofPhotoUrl && (
                        <Pressable onClick={() => setMissionPhotoLightbox(fs.proofPhotoUrl)} aria-label={t("missions.detail.viewProofPhotoAria", "Voir la photo de preuve")} style={{
                          display: "flex", alignItems: "center", gap: 6, padding: "5px 8px 5px 5px", borderRadius: 99,
                          background: "rgba(30,184,95,.12)", flex: "0 0 auto",
                        }}>
                          <img src={fs.proofPhotoUrl} alt={t("missions.detail.proofPhotoAlt", "Preuve")} style={{ width: 26, height: 26, borderRadius: 99, objectFit: "cover" }} />
                          <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.green }}>📷 {t("missions.detail.proofChipLabel", "Preuve")}</span>
                        </Pressable>
                      )}
                      <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: fMeta.c, background: fMeta.bg, borderRadius: 99, padding: "5px 10px", whiteSpace: "nowrap", flex: "0 0 auto" }}>{fMeta.l}</span>
                    </div>
                  );
                })}
              </section>
                </>
              )}

              {/* Dialogue par filiale — question/anomalie de l'employé, réponse du GR
                  (retour utilisateur explicite : "si il a une question... il creer un
                  dialogue dedie qui est transmis directement a GR qui peut repondre").
                  Jamais un fourre-tout : chaque filiale garde son propre échange. */}
              {/* Confidentialité (2026-08-23, retour utilisateur explicite) — un GR qui ne
                  voit cette filiale que par héritage (pas la sienne en propre, jamais
                  couverte par une Vertretung) ne reçoit plus le fil de discussion (voir
                  server/missions/routes.js, threadRestricted). Jamais un simple silence qui
                  laisserait croire qu'il n'y a aucun échange — la restriction reste visible. */}
              {m.threadRestricted && (
                <div style={{ ...card, padding: "12px 14px", marginBottom: 16, background: P.soft }}>
                  <p style={{ margin: 0, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.4 }}>
                    🔒 {t("missions.detail.threadRestrictedText", "Le dialogue détaillé de cette mission reste privé entre l'expéditeur et la filiale concernée.")}
                  </p>
                </div>
              )}
              {threadByStore.some((t) => t.entries.length > 0) && (
                <>
                  <strong style={{ display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px 8px" }}>💬 {t("missions.detail.dialogueTitle", "Dialogue")}</strong>
                  <section style={{ marginBottom: 18 }}>
                    {threadByStore.filter((ts) => ts.entries.length > 0).map(({ fid, entries }) => (
                      <div key={fid} style={{ ...card, borderRadius: 18, padding: 13, marginBottom: 9 }}>
                        <strong style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginBottom: 8 }}>{storeLabelFor(fid)}</strong>
                        {entries.map((te) => (
                          <div key={te.id} style={{ marginBottom: 8, paddingLeft: te.type === "reply" ? 0 : 10, borderLeft: te.type === "anomaly" ? `3px solid ${A.red}` : te.type === "question" ? `3px solid ${A.blue}` : "none" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                              <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: te.type === "anomaly" ? A.red : te.type === "reply" ? A.purple : A.blue }}>
                                {te.type === "anomaly" ? `⚠️ ${t("missions.detail.entryTypeAnomaly", "Anomalie")}` : te.type === "reply" ? `↩️ ${t("missions.detail.entryTypeReply", "Réponse")}` : `❓ ${t("missions.detail.entryTypeQuestion", "Question")}`}
                              </span>
                              <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>{te.authorName} · {fmtFR(te.createdAt)}</span>
                            </div>
                            <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.ink, lineHeight: 1.4 }}>{te.text}</p>
                            {te.photoUrl && (
                              <img src={te.photoUrl} alt={t("missions.detail.attachedPhotoAlt", "Photo jointe")} onClick={() => setMissionPhotoLightbox(te.photoUrl)}
                                style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 10, marginTop: 6, cursor: "pointer" }} />
                            )}
                          </div>
                        ))}
                        {missionReplyStoreId === fid ? (
                          <div style={{ marginTop: 6 }}>
                            <textarea value={missionThreadDraft} onChange={(e) => setMissionThreadDraft(e.target.value)} rows={2}
                              placeholder={t("missions.yourReplyPlaceholder", "Ta réponse…")} style={{ ...inputStyle, marginBottom: 7, resize: "vertical", fontSize: `calc(13px * var(--nx-font-scale, 1))`}} />
                            <div style={{ display: "flex", gap: 7 }}>
                              <Pressable onClick={() => { setMissionReplyStoreId(null); setMissionThreadDraft(""); }} style={{
                                flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink,
                              }}>{t("common.cancel", "Annuler")}</Pressable>
                              <Pressable onClick={postMissionReply} style={{ flex: 1, ...btnSolid(A.purple), padding: "9px 0", fontSize: `calc(12px * var(--nx-font-scale, 1))`}}>{t("planning.requests.sendBtn", "Envoyer")}</Pressable>
                            </div>
                          </div>
                        ) : (
                          <Pressable onClick={() => { setMissionReplyStoreId(fid); setMissionThreadDraft(""); }} style={{
                            fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.purple, marginTop: 2,
                          }}>{t("missions.detail.replyToTemplate", { store: storeLabelFor(fid) }, `Répondre à ${storeLabelFor(fid)}`)}</Pressable>
                        )}
                      </div>
                    ))}
                  </section>
                </>
              )}

              {/* Actions de gestion (Relancer/Partager/Modifier/Archiver/Supprimer) —
                  retour utilisateur explicite 2026-08-24, capture d'écran à l'appui : "cela
                  ne devrait pas du tout apparaître dans un profil qui reçoit la mission".
                  Réservées au CRÉATEUR uniquement — avant, seul "Partager avec…" avait ce
                  garde-fou ; un responsable qui reçoit la mission (targetLeaderIds/
                  sharedWith/filiale directement la sienne) voyait quand même tous ces
                  boutons, alors que le serveur (canManage) les aurait de toute façon
                  refusés. Jamais un simple "ça échoue silencieusement" — le bouton ne doit
                  même pas apparaître pour quelqu'un qui n'y a pas droit. */}
              {String(user?.proId || "") === String(m.createdBy) && (
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                {m.overallStatus !== "terminee" && !m.archived && (
                  <Pressable onClick={() => relanceMission(m.id)} style={{
                    width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                  }}>🔔 {(m.targetLeaderIds || []).length ? t("missions.detail.remindPendingLeadersBtn", "Relancer les responsables en attente") : t("missions.detail.remindPendingBtn", "Relancer les filiales en attente")}</Pressable>
                )}
                {/* Partage explicite — clause "sauf si [créateur] autorise" du verrouillage
                    réseau (retour utilisateur 2026-08-23). */}
                <Pressable onClick={() => openMissionShare(m)} style={{
                  width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                }}>🔓 {m.sharedWith && m.sharedWith.length ? t("missions.detail.shareBtnWithCountTemplate", { n: m.sharedWith.length }, `Partagée avec ${m.sharedWith.length} responsable${m.sharedWith.length > 1 ? "s" : ""}`) : t("missions.detail.shareBtn", "Partager avec…")}</Pressable>
                <div style={{ display: "flex", gap: 9 }}>
                  {!m.archived && (
                    <Pressable onClick={() => openMissionEdit(m)} style={{
                      flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                    }}>✏️ {t("common.edit", "Modifier")}</Pressable>
                  )}
                  <Pressable onClick={() => archiveMission(m.id, !m.archived)} style={{
                    flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
                  }}>{m.archived ? t("missions.detail.restoreBtn", "↩️ Restaurer") : t("missions.detail.archiveBtn", "🗄️ Archiver")}</Pressable>
                </div>
                <Pressable onClick={() => deleteMission(m)} style={{
                  width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, background: "rgba(239,68,68,.1)", color: A.red,
                }}>🗑️ {t("missions.detail.deleteMissionBtn", "Supprimer la mission")}</Pressable>
              </div>
              )}
            </>
          );
        })()}
      </Sheet>

      {/* Partage explicite d'une mission — clause "sauf si [créateur] autorise" du
          verrouillage réseau (retour utilisateur 2026-08-23). Liste = adminUsers déjà
          chargée pour Pilotage réseau (voir l'effet screen==="pilotage"), filtrée aux rôles
          responsables réseau, jamais l'utilisateur lui-même. Recherche dès qu'il y a
          plusieurs candidats — même principe que pilotFilialeSearch, un réseau à 1200
          filiales peut avoir des dizaines de BL/VKL/NL. */}
      <Sheet open={sheet === "missionShare"} onClose={() => setSheet("missionDetail")} P={P}>
        {missionShareId && (() => {
          const candidates = adminUsers
            .filter((u) => ["Bereichsleiter", "Verkaufsleiter", "Niederlassungsleiter"].includes(u.role))
            .filter((u) => String(u.employeeId) !== String(user?.proId || ""));
          const q = missionShareSearch.trim().toLowerCase();
          const shown = q ? candidates.filter((u) => `${u.name} ${u.employeeId}`.toLowerCase().includes(q)) : candidates;
          return (
            <>
              <SheetHead kicker={t("missions.share.kicker", "Pilotage réseau · Mission")} title={t("missions.share.title", "Partager avec…")} onClose={() => setSheet("missionDetail")} />
              <p style={{ margin: "0 0 14px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                {t("missions.share.explainText", "Par défaut, seuls toi et le ou les destinataires directs (filiale ou responsable ciblé) voient cette mission. Coche les responsables à qui l'ouvrir en plus.")}
              </p>
              {candidates.length > 8 && (
                <input value={missionShareSearch} onChange={(e) => setMissionShareSearch(e.target.value)}
                  placeholder={t("missions.share.searchPlaceholder", "Rechercher un responsable…")} style={{ ...inputStyle, marginBottom: 10 }} />
              )}
              <section style={{ ...card, borderRadius: 20, overflow: "hidden", marginBottom: 18, maxHeight: 360, overflowY: "auto" }}>
                {shown.length === 0 && (
                  <p style={{ margin: 0, padding: 16, textAlign: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("missions.share.noneFoundText", "Aucun responsable trouvé.")}</p>
                )}
                {shown.map((u, i) => {
                  const on = missionShareDraft.includes(String(u.employeeId));
                  return (
                    <Pressable key={u.id} onClick={() => toggleMissionShareId(String(u.employeeId))} style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "12px 15px",
                      borderTop: i > 0 ? `1px solid ${P.line}` : "none",
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{u.name}</strong>
                        <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{u.role}</div>
                      </div>
                      <span style={{
                        width: 22, height: 22, borderRadius: 99, flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "center",
                        background: on ? A.purple : P.soft, color: "#fff", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800,
                      }}>{on ? "✓" : ""}</span>
                    </Pressable>
                  );
                })}
              </section>
              <Pressable onClick={saveMissionShare} disabled={missionShareBusy} style={{ width: "100%", ...btnSolid(A.purple), padding: "13px 0", opacity: missionShareBusy ? .6 : 1 }}>
                {missionShareBusy ? t("common.saving", "Enregistrement…") : t("missions.share.saveBtnTemplate", { n: missionShareDraft.length }, missionShareDraft.length ? `Partager avec ${missionShareDraft.length}` : "Enregistrer (personne)")}
              </Pressable>
            </>
          );
        })()}
      </Sheet>


      {/* Créer un subordonné réseau — VKL crée un BL, NL crée un VKL (retour utilisateur
          explicite 2026-08-23, POST /api/leaders). Filiales candidates = périmètre
          EFFECTIF actuel (managerOverview.stores, identique à ce que le serveur vérifie),
          chacune annotée de son détenteur actuel s'il y en a un — le créateur voit
          clairement ce qu'il délègue avant de valider. */}
      <Sheet open={sheet === "createLeader"} onClose={() => setSheet(null)} P={P}>
        {(() => {
          const mySubordinateRole = user?.role === "Niederlassungsleiter" ? "Verkaufsleiter" : user?.role === "Verkaufsleiter" ? "Bereichsleiter" : null;
          const candidateStores = managerOverview?.stores || [];
          const holderFor = (storeId) => {
            const holder = adminUsers.find((u) => (u.stores || []).includes(storeId) && String(u.employeeId) !== String(user?.proId));
            return holder ? `${holder.firstname} ${holder.lastname}` : null;
          };
          const labelStyle = { fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 };
          return (
            <>
              <SheetHead kicker={t("pilotage.team.createLeaderKicker", "Pilotage réseau · Équipe")} title={t("pilotage.team.createLeaderTitleTemplate", { role: mySubordinateRole }, `Nouveau ${mySubordinateRole}`)} onClose={() => setSheet(null)} />
              <label style={labelStyle}>{t("common.firstname", "Prénom")}</label>
              <input value={createLeaderForm.firstname} onChange={(e) => setCreateLeaderForm((f) => ({ ...f, firstname: e.target.value }))} style={{ ...inputStyle, marginBottom: 14 }} />
              <label style={labelStyle}>{t("common.lastname", "Nom")}</label>
              <input value={createLeaderForm.lastname} onChange={(e) => setCreateLeaderForm((f) => ({ ...f, lastname: e.target.value }))} style={{ ...inputStyle, marginBottom: 14 }} />
              <label style={labelStyle}>{t("pilotage.team.assignedStoresTemplate", { n: createLeaderForm.storeIds.length }, `Filiales confiées (${createLeaderForm.storeIds.length})`)}</label>
              <section style={{ ...card, borderRadius: 20, overflow: "hidden", marginBottom: 20, maxHeight: 320, overflowY: "auto" }}>
                {candidateStores.length === 0 && (
                  <p style={{ margin: 0, padding: 16, textAlign: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.team.noAssignableStoresText", "Aucune filiale disponible dans ton périmètre.")}</p>
                )}
                {candidateStores.map((sId, i) => {
                  const on = createLeaderForm.storeIds.includes(sId);
                  const holder = holderFor(sId);
                  return (
                    <Pressable key={sId} onClick={() => toggleCreateLeaderStore(sId)} style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "12px 15px",
                      borderTop: i > 0 ? `1px solid ${P.line}` : "none",
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{storeLabelFor(sId)}</strong>
                        {holder && <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.team.currentlyWithTemplate", { name: holder }, `actuellement chez ${holder}`)}</div>}
                      </div>
                      <span style={{
                        width: 22, height: 22, borderRadius: 99, flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "center",
                        background: on ? A.purple : P.soft, color: "#fff", fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800,
                      }}>{on ? "✓" : ""}</span>
                    </Pressable>
                  );
                })}
              </section>
              <Pressable onClick={submitCreateLeader} disabled={createLeaderBusy} style={{ width: "100%", ...btnSolid(A.purple), padding: "13px 0", opacity: createLeaderBusy ? .6 : 1 }}>
                {createLeaderBusy ? t("common.saving", "Enregistrement…") : t("pilotage.team.createLeaderSubmitBtn", "Créer le compte")}
              </Pressable>
            </>
          );
        })()}
      </Sheet>

      {/* Confirmation avec identifiants — affichés UNE SEULE FOIS (jamais re-consultables
          ensuite, même règle qu'un vrai mot de passe côté serveur), le créateur doit les
          transmettre lui-même au nouveau responsable. */}
      <Sheet open={sheet === "createLeaderResult"} onClose={() => { setSheet(null); setCreateLeaderResult(null); }} P={P}>
        {createLeaderResult && (
          <>
            <SheetHead kicker={t("pilotage.team.createLeaderKicker", "Pilotage réseau · Équipe")} title={t("pilotage.team.createdTitle", "Compte créé")} onClose={() => { setSheet(null); setCreateLeaderResult(null); }} />
            <p style={{ margin: "0 0 16px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
              {t("pilotage.team.createdExplainTemplate", { name: createLeaderResult.name }, `${createLeaderResult.name} peut se connecter dès maintenant. Note ces identifiants — ils ne seront plus jamais affichés.`)}
            </p>
            <section style={{ ...card, borderRadius: 20, padding: 16, marginBottom: 20 }}>
              <div style={{ marginBottom: 14 }}>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".04em", display: "block", marginBottom: 3 }}>Pro-ID</span>
                <strong style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`, fontFamily: "monospace" }}>{createLeaderResult.employeeId}</strong>
              </div>
              <div>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: ".04em", display: "block", marginBottom: 3 }}>{t("pilotage.team.tempPasswordLabel", "Mot de passe provisoire")}</span>
                <strong style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`, fontFamily: "monospace" }}>{createLeaderResult.tempPassword}</strong>
              </div>
            </section>
            <Pressable onClick={() => { setSheet(null); setCreateLeaderResult(null); }} style={{ width: "100%", ...btnSolid(A.purple), padding: "13px 0" }}>
              {t("common.done", "Terminé")}
            </Pressable>
          </>
        )}
      </Sheet>

      {/* Réassignation de filiale entre subordonnés (retour utilisateur 2026-08-24) —
          siblings = autres subordonnés DIRECTS du même supérieur que le détenteur actuel,
          recalculé ici depuis adminUsers (directReports est local au bloc Équipe plus
          haut, pas accessible ici). */}
      <Sheet open={sheet === "reassignStore"} onClose={() => { setSheet(null); setReassignStoreDraft(null); }} P={P}>
        {reassignStoreDraft && (() => {
          const holder = adminUsers.find((u) => u.employeeId === reassignStoreDraft.fromEmployeeId);
          const siblings = adminUsers.filter((u) => u.reportsTo === holder?.reportsTo && u.employeeId !== reassignStoreDraft.fromEmployeeId);
          return (
            <>
              <SheetHead kicker={t("pilotage.team.reassignKicker", "Pilotage réseau · Équipe")} title={t("pilotage.team.reassignTitleTemplate", { store: storeLabelFor(reassignStoreDraft.storeId) }, `Déplacer ${storeLabelFor(reassignStoreDraft.storeId)}`)} onClose={() => { setSheet(null); setReassignStoreDraft(null); }} />
              <p style={{ margin: "0 0 14px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                {t("pilotage.team.reassignExplainTemplate", { name: holder ? `${holder.firstname} ${holder.lastname}` : "" }, `Actuellement chez ${holder ? `${holder.firstname} ${holder.lastname}` : "—"}. Choisis le nouveau responsable.`)}
              </p>
              <section style={{ ...card, borderRadius: 20, overflow: "hidden", marginBottom: 4 }}>
                {siblings.length === 0 && (
                  <p style={{ margin: 0, padding: 16, textAlign: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("pilotage.team.noSiblingsText", "Aucun autre subordonné disponible.")}</p>
                )}
                {siblings.map((u, i) => (
                  <Pressable key={u.id} disabled={reassignStoreBusy} onClick={() => submitReassignStore(u.employeeId)} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "12px 15px", opacity: reassignStoreBusy ? .6 : 1,
                    borderTop: i > 0 ? `1px solid ${P.line}` : "none",
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{u.firstname} {u.lastname}</strong>
                      <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{u.role} · {(u.stores || []).length} {t("pilotage.team.storesShortSuffix", "filiale(s)")}</div>
                    </div>
                    <span style={{ color: P.muted, fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>›</span>
                  </Pressable>
                ))}
              </section>
            </>
          );
        })()}
      </Sheet>

      {/* Fiche mission reçue (compte normal) — affichage entier, "mission accomplie",
          dialogue dédié (question/anomalie) transmis directement au créateur (retour
          utilisateur explicite). Scopé strictement à SA propre filiale (myStores[0]),
          jamais les autres filiales de la même mission. */}
      <Sheet open={sheet === "myMissionDetail"} onClose={() => setSheet(null)} P={P}>
        {myMissionDetailId && (() => {
          const m = myNetworkMissions.find((x) => x.id === myMissionDetailId);
          if (!m) return <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("missions.detail.notFoundText", "Mission introuvable.")}</p>;
          const myStore = m.myStores[0];
          const fs = m.filialeStatuses[myStore] || { status: "en_attente" };
          const fMeta = FILIALE_STATUS_META[fs.status];
          const pm = priorityMeta[m.priority || "normal"];
          const myThread = m.thread.filter((t) => t.storeId === myStore);
          const Row = ({ label, children, strong: isStrong }) => (
            <div style={{ display: "flex", justifyContent: "space-between", gap: 14, padding: "11px 2px", borderBottom: `1px solid ${P.line}` }}>
              <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700, flex: "0 0 auto" }}>{label}</span>
              <span style={{ fontSize: `calc(${(isStrong ? 13.5 : 12.5)}px * var(--nx-font-scale, 1))`, fontWeight: isStrong ? 800 : 600, color: P.ink, textAlign: "right" }}>{children}</span>
            </div>
          );
          return (
            <>
              <SheetHead kicker={t("tasks.networkMissionType", "Mission réseau")} title={m.title} onClose={() => setSheet(null)} />
              <div style={{ display: "flex", gap: 7, marginBottom: 14 }}>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: fMeta.c, background: fMeta.bg, borderRadius: 99, padding: "5px 11px" }}>{fMeta.l}</span>
                {m.priority !== "normal" && (
                  <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: pm.color, borderRadius: 99, padding: "5px 11px" }}>{pm.label}</span>
                )}
              </div>

              {m.note && (
                <div style={{
                  background: "linear-gradient(155deg, rgba(231,138,18,.11), rgba(231,138,18,.04))",
                  border: `1.5px solid rgba(231,138,18,.28)`, borderRadius: 18, padding: "12px 14px", marginBottom: 16,
                }}>
                  <label style={{ ...eyebrow(A.orange), display: "flex", alignItems: "center", gap: 6, marginBottom: 7 }}>📋 Consigne</label>
                  <p style={{ margin: 0, fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink, lineHeight: 1.5 }}>{m.note}</p>
                </div>
              )}

              <section style={{ ...card, borderRadius: 20, padding: "4px 15px", marginBottom: 18 }}>
                <Row label={t("missions.form.deadlineLabel", "Échéance")} strong>
                  {m.echeanceDate ? `${fmtFR(m.echeanceDate)}${m.echeanceTime ? ` à ${m.echeanceTime}` : ""}` : t("missions.detail.noDeadline", "Aucune")}
                </Row>
                <Row label={t("missions.detail.proofRequestedLabel", "Preuve demandée")}>{m.preuveRequise ? `📷 ${t("common.yes", "Oui")}` : t("common.no", "Non")}</Row>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 14, padding: "11px 2px" }}>
                  <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{t("missions.sentByLabel", "Envoyée par")}</span>
                  <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 600 }}>{m.createdByName}</span>
                </div>
              </section>

              {(fs.status === "en_attente" || fs.status === "refusee") && (
                <>
                  {fs.status === "refusee" && (
                    <div style={{ textAlign: "center", padding: "10px 0", borderRadius: 16, background: "rgba(239,68,68,.1)", color: A.red, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, marginBottom: 10 }}>
                      ✕ Résultat refusé — à reprendre
                    </div>
                  )}
                  {/* Capture photo directe — retour utilisateur explicite, 2026-08-02 :
                      "quand on coche envoie de photo pour preuve... il n'y a aucun moyen de
                      prendre des photos pour joindre directe en cliquant sur mission
                      accomplie". capture="environment" ouvre l'appareil photo natif. */}
                  <input ref={myMissionProofInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                    onChange={(e) => { pickMyMissionProofPhoto(e.target.files?.[0]); e.target.value = ""; }} />
                  {myMissionProofPreview ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, borderRadius: 16, background: P.soft, marginBottom: 10 }}>
                      <img src={myMissionProofPreview} alt="Preuve" style={{ width: 46, height: 46, borderRadius: 10, objectFit: "cover" }} />
                      <span style={{ flex: 1, fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink }}>{t("missions.photoReadyToSendLabel", "Photo prête à envoyer")}</span>
                      <Pressable onClick={() => { setMyMissionProofFile(null); setMyMissionProofPreview(""); }} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.red }}>{t("settings.photo.removeButton", "Retirer")}</Pressable>
                    </div>
                  ) : (
                    <Pressable onClick={() => myMissionProofInputRef.current?.click()} style={{
                      width: "100%", textAlign: "center", padding: "12px 0", borderRadius: 16, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, marginBottom: 10,
                      background: m.preuveRequise ? "rgba(231,138,18,.1)" : P.soft, color: m.preuveRequise ? A.orange : P.ink,
                      border: m.preuveRequise ? `1.5px dashed ${A.orange}` : "none",
                    }}>📷 {m.preuveRequise ? t("missions.takeProofPhotoRequiredBtn", "Prendre la photo de preuve (requise)") : t("missions.attachPhotoOptionalBtn", "Joindre une photo (optionnel)")}</Pressable>
                  )}
                  <Pressable disabled={myMissionPhotoBusy} onClick={() => completeMyMission(m.id, m.preuveRequise)} style={{ width: "100%", ...btnSolid(A.green), marginBottom: 18, opacity: myMissionPhotoBusy ? .6 : 1 }}>
                    {myMissionPhotoBusy ? "Envoi…" : fs.status === "refusee" ? "✅ Marquer accomplie à nouveau" : "✅ Mission accomplie"}
                  </Pressable>
                </>
              )}
              {fs.status === "a_valider" && (
                <div style={{ textAlign: "center", padding: "12px 0", borderRadius: 16, background: "rgba(54,79,231,.1)", color: A.blue, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, marginBottom: 18 }}>
                  ⏳ En attente de validation par {m.createdByName}
                </div>
              )}
              {fs.status === "validee" && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ textAlign: "center", padding: "12px 0", borderRadius: 16, background: "rgba(30,184,95,.12)", color: A.green, fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, marginBottom: fs.proofPhotoUrl ? 10 : 0 }}>
                    ✓ Validée
                  </div>
                  {fs.proofPhotoUrl && (
                    <img src={fs.proofPhotoUrl} alt="Preuve envoyée" onClick={() => setMissionPhotoLightbox(fs.proofPhotoUrl)}
                      style={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 16, cursor: "pointer" }} />
                  )}
                </div>
              )}

              {/* Dialogue — question ou anomalie, transmis directement au créateur (retour
                  utilisateur explicite). Jamais mélangé avec les autres filiales de la
                  mission (myThread déjà scopé à myStore). Une fois la mission validée, plus
                  aucune raison de poser une NOUVELLE question — retour utilisateur
                  explicite, 2026-08-02 : "la section... doivent automatiquement disparaitre
                  si une mission est validee". L'historique déjà échangé reste visible (rien
                  n'est perdu), seuls les contrôles de saisie disparaissent ; si aucun
                  échange n'a jamais eu lieu, toute la section disparaît (rien à montrer). */}
              {(myThread.length > 0 || fs.status !== "validee") && (
                <>
                  <strong style={{ display: "block", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px 8px" }}>💬 Une question ou une anomalie ?</strong>
                  <section style={{ ...card, borderRadius: 18, padding: 14, marginBottom: 18 }}>
                    {myThread.map((t) => (
                      <div key={t.id} style={{ marginBottom: 9, paddingLeft: t.type === "reply" ? 0 : 10, borderLeft: t.type === "anomaly" ? `3px solid ${A.red}` : t.type === "question" ? `3px solid ${A.blue}` : "none" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                          <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: t.type === "anomaly" ? A.red : t.type === "reply" ? A.purple : A.blue }}>
                            {t.type === "anomaly" ? "⚠️ Anomalie" : t.type === "reply" ? "↩️ Réponse" : "❓ Question"}
                          </span>
                          <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, color: P.muted }}>{t.authorName} · {fmtFR(t.createdAt)}</span>
                        </div>
                        <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.ink, lineHeight: 1.4 }}>{t.text}</p>
                        {t.photoUrl && (
                          <img src={t.photoUrl} alt="Photo jointe" onClick={() => setMissionPhotoLightbox(t.photoUrl)}
                            style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 10, marginTop: 6, cursor: "pointer" }} />
                        )}
                      </div>
                    ))}
                    {fs.status !== "validee" && (
                      <>
                        <div style={{ display: "flex", gap: 7, marginBottom: 8 }}>
                          {[{ id: "question", l: `❓ ${t("missions.threadTypeQuestion", "Question")}` }, { id: "anomaly", l: `⚠️ ${t("missions.threadTypeAnomaly", "Anomalie")}` }].map((opt) => (
                            <Pressable key={opt.id} onClick={() => setMyMissionThreadType(opt.id)} style={{
                              flex: 1, textAlign: "center", padding: "8px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                              background: myMissionThreadType === opt.id ? (opt.id === "anomaly" ? A.red : A.blue) : P.soft,
                              color: myMissionThreadType === opt.id ? "#fff" : P.ink,
                            }}>{opt.l}</Pressable>
                          ))}
                        </div>
                        <textarea value={myMissionThreadDraft} onChange={(e) => setMyMissionThreadDraft(e.target.value)} rows={2}
                          placeholder={myMissionThreadType === "anomaly" ? t("missions.describeAnomalyPlaceholder", "Décris l'anomalie…") : t("missions.writeQuestionPlaceholder", "Écris ta question…")} style={{ ...inputStyle, marginBottom: 8, resize: "vertical", fontSize: `calc(13px * var(--nx-font-scale, 1))`}} />
                        <input ref={myMissionThreadPhotoInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                          onChange={(e) => { pickMyMissionThreadPhoto(e.target.files?.[0]); e.target.value = ""; }} />
                        {myMissionThreadPhotoPreview ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 8, borderRadius: 14, background: P.soft, marginBottom: 8 }}>
                            <img src={myMissionThreadPhotoPreview} alt="Photo à joindre" style={{ width: 38, height: 38, borderRadius: 8, objectFit: "cover" }} />
                            <span style={{ flex: 1, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.ink }}>{t("missions.photoReadyToAttachLabel", "Photo prête à joindre")}</span>
                            <Pressable onClick={() => { setMyMissionThreadPhotoFile(null); setMyMissionThreadPhotoPreview(""); }} style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.red }}>{t("settings.photo.removeButton", "Retirer")}</Pressable>
                          </div>
                        ) : (
                          <Pressable onClick={() => myMissionThreadPhotoInputRef.current?.click()} style={{
                            width: "100%", textAlign: "center", padding: "9px 0", borderRadius: 14, fontWeight: 700, fontSize: `calc(12px * var(--nx-font-scale, 1))`, background: P.soft, color: P.ink, marginBottom: 8,
                          }}>📷 {t("missions.attachPhotoBtn", "Joindre une photo")}</Pressable>
                        )}
                        <Pressable disabled={myMissionPhotoBusy} onClick={postMyMissionThread} style={{ width: "100%", ...btnSolid(myMissionThreadType === "anomaly" ? A.red : A.blue), opacity: myMissionPhotoBusy ? .6 : 1 }}>
                          {myMissionPhotoBusy ? "Envoi…" : "Envoyer"}
                        </Pressable>
                      </>
                    )}
                  </section>
                </>
              )}
            </>
          );
        })()}
      </Sheet>

      <Sheet open={sheet === "scannerLink"} onClose={() => { setSheet(null); setScanLinkArticle(null); }} P={P} maxH="60%">
        <SheetHead kicker={t("scanner.pageTitle", "Associer EAN")} title={scanLinkArticle?.name || ""} onClose={() => { setSheet(null); setScanLinkArticle(null); }} />
        {scanLinkArticle && (() => {
          const liveArticle = articles.find((x) => x.artNr === scanLinkArticle.artNr) || scanLinkArticle;
          const linkedCodes = articleCodesFor(liveArticle);
          return (
            <>
              {linkedCodes.length > 0 && (
                <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, textTransform: "uppercase", letterSpacing: .3, marginBottom: 8 }}>
                  {t("scanner.linkedCodesCountTemplate", { count: linkedCodes.length }, `${linkedCodes.length} code(s) associé(s)`)}
                </div>
              )}
              {linkedCodes.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  {linkedCodes.map((c) => (
                    <div key={c} style={{ display: "flex", alignItems: "center", gap: 9, padding: "11px 13px", borderRadius: 14, marginBottom: 8, background: "rgba(30,184,95,.1)", border: `1.5px solid ${A.green}` }}>
                      <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>🔗</span>
                      <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: "#15803d", flex: 1 }}>…{c.slice(-5)}</span>
                      {scanLinkAllowUnlink && (
                        <Pressable onClick={() => removeScanLinkCode(c)} aria-label={t("claims.action.remove", "Retirer")} style={{
                          width: 26, height: 26, borderRadius: 99, background: P.card, display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, flexShrink: 0,
                        }}>✕</Pressable>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, marginBottom: 15 }}>
                <input style={{ ...inputStyle, flex: 1 }} inputMode="numeric" placeholder={t("controlStore.eanLabel", "Code-barres (EAN)")} value={scanLinkInput}
                  onChange={(e) => setScanLinkInput(e.target.value)} />
                <Pressable onClick={simulateScanForLink} aria-label={t("scanner.sheetScanBtn", "Scanner ce code")} style={{
                  width: 46, height: 46, borderRadius: 15, flex: "0 0 auto",
                  background: themeId === "sombre" ? "#2a2a2e" : A.navy, color: "#fff",
                  display: "grid", placeItems: "center", fontSize: `calc(19px * var(--nx-font-scale, 1))`,
                  animation: scanning ? "nxPulse 1s ease infinite" : "none",
                }}>📷</Pressable>
              </div>
              {linkedCodes.length > 0 && !scanLinkAllowUnlink && (
                <p style={{ margin: "0 0 10px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, textAlign: "center" }}>
                  {t("delivery.unlinkFromScannerOnlyText", "Pour délier cet article, ouvre le module Scanner.")}
                </p>
              )}
              <Pressable onClick={saveScanLink} style={{ width: "100%", ...btnSolid(A.navy) }}>{t("scanner.saveLinkBtn", "Enregistrer")}</Pressable>
            </>
          );
        })()}
      </Sheet>


      <Sheet open={sheet === "contactConfirm"} onClose={() => { setSheet(null); setContactConfirmTarget(null); }} P={P} maxH="70%">
        <SheetHead kicker={t("krankmeldung.contactConfirm.kicker", "Confirmation d'appel")} title={contactConfirmTarget?.name || ""} onClose={() => { setSheet(null); setContactConfirmTarget(null); }} />
        <div style={{ ...card, borderRadius: 18, padding: 14, marginBottom: 16, background: "rgba(231,138,18,.08)", border: "1px solid rgba(231,138,18,.25)" }}>
          <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, lineHeight: 1.6, color: P.ink }}>
            {t("krankmeldung.contactConfirm.instructionTemplate", { name: contactConfirmTarget?.name || "" }, `Choisis ce qui s'est réellement passé avec ${contactConfirmTarget?.name}. Ta réponse est horodatée et associée à ton compte — une fausse déclaration reste attribuable.`)}
          </p>
        </div>
        {["reached", "message_left", "attempted", "unreachable", "not_in_system"].map((s) => (
          <Pressable key={s} onClick={() => confirmContactCall(s)} style={{
            width: "100%", textAlign: "left", padding: "13px 15px", borderRadius: 16, marginBottom: 8,
            background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
          }}>{t(`krankmeldung.contactStatus.${{ pending: "pending", attempted: "attempted", reached: "reached", message_left: "messageLeft", unreachable: "unreachable", not_in_system: "notInSystem" }[s]}`, CONTACT_STATUS_LABELS[s])}</Pressable>
        ))}
        <Pressable onClick={() => { setSheet(null); setContactConfirmTarget(null); }} style={{
          width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 4,
        }}>{t("common.cancel", "Annuler")}</Pressable>
      </Sheet>

      <Sheet open={!!lfStatusTarget} onClose={() => setLfStatusTarget(null)} P={P} maxH="70%">
        <SheetHead kicker={t("lieferschein.articleControlKicker", "Contrôle de l'article")} title={t("lieferschein.receptionStatusTitle", "Statut de réception")} onClose={() => setLfStatusTarget(null)} />
        {Object.entries(LF_ITEM_STATUS).filter(([k]) => k !== "pending").map(([k, meta]) => (
          <Pressable key={k} onClick={() => setLfItemStatus(lfStatusTarget.noteId, lfStatusTarget.itemId, k)} style={{
            width: "100%", textAlign: "left", padding: "12px 15px", borderRadius: 16, marginBottom: 7,
            background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <span style={{ width: 26, height: 26, borderRadius: 99, background: meta.color, color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>{meta.emoji}</span>
            {t(`lieferschein.itemStatus.${LF_STATUS_KEY_MAP[k]}`, meta.label)}
          </Pressable>
        ))}
        <Pressable onClick={() => setLfStatusTarget(null)} style={{
          width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 4,
        }}>{t("common.cancel", "Annuler")}</Pressable>
      </Sheet>

      <Sheet open={sheet === "account"} onClose={() => setSheet(null)} P={P}>
        {/* Fiche employé du compte connecté (retour utilisateur 2026-08-04 : "sa fiche" au
            tap sur le badge/photo, pas juste un menu) — photo modifiable directement ici
            (réutilise uploadProfilePhoto/removeProfilePhoto, déjà réels côté Réglages),
            civilité + type de contrat + soldes Leistungsfrei/Rest-Urlaub (GET /api/me/profile,
            self-service, chargé par openAccountSheet à l'ouverture).
            Croix de fermeture ajoutée (2026-08-17, retour utilisateur explicite) — le Sheet
            générique ne fournit qu'un fond cliquable + une poignée décorative, jamais de ✕
            propre ; ce popup n'en avait aucun (fermeture uniquement par tap en dehors). */}
        <Pressable onClick={() => setSheet(null)} aria-label={t("common.close", "Fermer")} style={{
          position: "absolute", top: 14, right: 14, width: 30, height: 30, borderRadius: 99,
          background: P.soft, color: P.muted, display: "grid", placeItems: "center",
          fontSize: `calc(13px * var(--nx-font-scale, 1))`, zIndex: 1,
        }}>✕</Pressable>
        {/* Refonte "sobre + premium" (2026-08-22, retour utilisateur explicite après 2
            maquettes comparées — la 1ère version pleine de couleurs a été rejetée en bloc,
            celle-ci retenue) : plus aucune couleur décorative ajoutée. Le relief vient
            uniquement d'ombres douces (jamais un aplat) et d'un rythme d'espacement
            généreux ; un seul accent bleu, réservé exclusivement à ce qui est sélectionné
            (thème actif, langue active) — jamais dispersé sur les icônes de la liste comme
            avant (vert/orange/bleu par ligne, remplacé par un chip neutre uniforme). */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, margin: "12px 0 22px" }}>
          <Pressable onClick={() => profilePhotoInputRef.current?.click()} aria-label={t("settings.photo.change", "Changer la photo de profil")} style={{
            position: "relative", flex: "0 0 auto", width: 62, height: 62, borderRadius: 99,
          }}>
            {user?.photoUrl ? (
              <img src={user?.photoUrl} alt="" style={{ width: 62, height: 62, borderRadius: 99, objectFit: "cover", boxShadow: `0 0 0 1.5px ${P.line}, 0 8px 20px rgba(0,0,0,.1)` }} />
            ) : (
              <span style={{
                width: 62, height: 62, borderRadius: 99, color: P.ink, background: P.card,
                boxShadow: `0 1px 0 rgba(255,255,255,.6) inset, 0 0 0 1.5px ${P.line}, 0 8px 20px rgba(0,0,0,.1)`,
                display: "grid", placeItems: "center", fontSize: `calc(18px * var(--nx-font-scale, 1))`, fontWeight: 700, letterSpacing: "-.02em",
              }}>{userInitials}</span>
            )}
            <span style={{
              position: "absolute", bottom: -2, right: -2, width: 22, height: 22, borderRadius: 99,
              background: "#fff", color: A.blue, display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`,
              boxShadow: "0 2px 6px rgba(0,0,0,.25)", opacity: profilePhotoUploading ? .5 : 1,
            }}>{profilePhotoUploading ? "…" : "📷"}</span>
          </Pressable>
          <input ref={profilePhotoInputRef} type="file" accept="image/*" onChange={uploadProfilePhoto} style={{ display: "none" }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong style={{ fontSize: `calc(19px * var(--nx-font-scale, 1))`, letterSpacing: "-.02em", display: "block", marginBottom: 4 }}>{user?.firstname} {user?.lastname || ""}</strong>
            <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: 99, background: A.green, boxShadow: "0 0 0 3px rgba(30,184,95,.15)", flexShrink: 0 }} />
              {user?.role || "Employé"} · {myStoresLabel}
            </span>
          </div>
          {user?.photoUrl && (
            <Pressable onClick={removeProfilePhoto} aria-label={t("settings.photo.remove", "Supprimer la photo")} style={{
              fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.red, background: "rgba(239,68,68,.1)", borderRadius: 99, padding: "6px 10px", flex: "0 0 auto",
            }}>{t("settings.photo.removeButton", "Retirer")}</Pressable>
          )}
        </div>

        {myProfile && (
          <div style={{ display: "flex", flexWrap: "wrap", marginBottom: 28, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.ink }}>
            {[
              myProfile.gender === "homme" ? t("common.titleMr", "M.") : myProfile.gender === "femme" ? t("common.titleMrs", "Mme") : null,
              myProfile.contractType,
              myProfile.weeklyContractedHours != null ? `${myProfile.weeklyContractedHours} h/sem.` : null,
              myProfile.leistungsfreiAvailableHours != null ? `${t("planning.lfShort", "LF")} ${myProfile.leistungsfreiAvailableHours} h` : null,
              myProfile.restUrlaubDays != null ? `${t("arbeitszeitliste.table.restUrlaub", "Rest-Urlaub")} ${myProfile.restUrlaubDays} j` : null,
            ].filter(Boolean).map((chip, i, arr) => (
              <span key={i} style={{
                fontWeight: 600, fontVariantNumeric: "tabular-nums",
                paddingRight: i < arr.length - 1 ? 12 : 0, marginRight: i < arr.length - 1 ? 12 : 0,
                borderRight: i < arr.length - 1 ? `1px solid ${P.line}` : "none",
              }}>{chip}</span>
            ))}
          </div>
        )}

        {/* Les 3 raccourcis, groupés en une seule liste (façon Réglages iOS) au lieu de
            cartes séparées — icônes en chip neutre uniforme, plus de couleur par ligne. */}
        <div style={{
          borderRadius: 18, background: P.card, overflow: "hidden", marginBottom: 28,
          boxShadow: `0 1px 0 rgba(255,255,255,.6) inset, 0 0 0 1px ${P.line}, 0 10px 26px rgba(0,0,0,.06)`,
        }}>
          <Pressable onClick={openMyFiche} style={{
            width: "100%", boxSizing: "border-box", display: "flex", alignItems: "center", gap: 13, padding: "14px 15px",
            borderBottom: `1px solid ${P.line}`,
          }}>
            <span style={{ width: 32, height: 32, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>🪪</span>
            <strong style={{ flex: 1, fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 600 }}>{t("home.menu.myFiche", "Ma fiche complète")}</strong>
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>›</span>
          </Pressable>
          <Pressable onClick={() => { setSheet(null); setScreen("notifications"); }} style={{
            width: "100%", boxSizing: "border-box", display: "flex", alignItems: "center", gap: 13, padding: "14px 15px",
            borderBottom: `1px solid ${P.line}`,
          }}>
            <span style={{ width: 32, height: 32, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>🔔</span>
            <strong style={{ flex: 1, fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 600 }}>{t("home.menu.notifications", "Notifications")}</strong>
            {notifUnreadCount > 0 && <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: A.red, borderRadius: 99, padding: "2px 8px" }}>{notifUnreadCount}</span>}
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>›</span>
          </Pressable>
          <Pressable onClick={() => { setSheet(null); setScreen("other"); setOtherTab("more"); }} style={{
            width: "100%", boxSizing: "border-box", display: "flex", alignItems: "center", gap: 13, padding: "14px 15px",
          }}>
            <span style={{ width: 32, height: 32, borderRadius: 99, background: P.soft, display: "grid", placeItems: "center", fontSize: `calc(14.5px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>⚙️</span>
            <strong style={{ flex: 1, fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 600 }}>{t("home.menu.settingsLink", "Réglages")}</strong>
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>›</span>
          </Pressable>
        </div>

        {/* Thèmes en pastilles de vraie couleur (utile, pas décoratif) — sélection marquée
            par un anneau bleu + un badge ✓, jamais un panneau plein coloré. */}
        <span style={{ ...eyebrow(P.muted), display: "block", marginBottom: 12 }}>{t("home.menu.mode", "Mode")}</span>
        <div style={{ display: "flex", gap: 16, marginBottom: 30, overflowX: "auto", padding: "4px 2px 6px" }}>
          {[{ id: null, label: t("auth.systemTheme", "Système"), swatch: `linear-gradient(135deg, ${P.muted}, ${P.ink})` }, ...THEME_ORDER.map((id) => ({ id, label: t(`theme.${id}`, THEMES[id].name), swatch: THEMES[id].bg }))].map((opt) => {
            const active = opt.id === null ? useSystemTheme : (!useSystemTheme && opt.id === themeId);
            return (
              <Pressable key={opt.id || "system"} onClick={() => opt.id === null ? pickSystemTheme() : pickTheme(opt.id)} style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 7, flex: "0 0 auto", position: "relative",
              }}>
                {active && <span style={{
                  position: "absolute", top: -3, right: -3, width: 15, height: 15, borderRadius: 99, background: A.blue, color: "#fff",
                  fontSize: `calc(8px * var(--nx-font-scale, 1))`, display: "grid", placeItems: "center", boxShadow: `0 0 0 2px ${P.card}`, zIndex: 1,
                }}>✓</span>}
                <span style={{
                  width: 34, height: 34, borderRadius: 99, background: opt.swatch,
                  boxShadow: active
                    ? `0 1px 0 rgba(255,255,255,.5) inset, 0 0 0 2px ${P.card}, 0 0 0 3.5px ${A.blue}, 0 4px 12px rgba(54,79,231,.25)`
                    : `0 1px 0 rgba(255,255,255,.5) inset, 0 0 0 1px ${P.line}, 0 4px 10px rgba(0,0,0,.1)`,
                }} />
                <span style={{ fontSize: `calc(9.5px * var(--nx-font-scale, 1))`, fontWeight: active ? 700 : 600, color: active ? P.ink : P.muted, whiteSpace: "nowrap" }}>{opt.label}</span>
              </Pressable>
            );
          })}
        </div>

        <span style={{ ...eyebrow(P.muted), display: "block", marginBottom: 12 }}>{t("settings.language.label", "Langue")}</span>
        <div style={{ display: "flex", gap: 3, marginBottom: 30, borderRadius: 13, background: P.soft, padding: 4 }}>
          {LANGS.map((l) => {
            const active = lang === l.id;
            return (
              <Pressable key={l.id} onClick={() => changeLang(l.id)}
                style={{
                  flex: 1, textAlign: "center", padding: "9px 0", borderRadius: 10, fontWeight: 700, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                  color: active ? P.ink : P.muted, background: active ? P.card : "none",
                  boxShadow: active ? `0 1px 0 rgba(255,255,255,.6) inset, 0 2px 6px rgba(0,0,0,.14)` : "none",
                }}>{l.id.toUpperCase()}</Pressable>
            );
          })}
        </div>

        <Pressable onClick={logout} style={{
          width: "100%", textAlign: "center", padding: "14px 0", borderRadius: 13, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
          color: A.red, background: "rgba(239,68,68,.045)", border: "1px solid rgba(239,68,68,.18)", boxSizing: "border-box",
        }}>{t("home.menu.logoutButton", "Se déconnecter")}</Pressable>
      </Sheet>

      {/* Sheet HACCP "contrôle éclair" — flux pas-à-pas d'une zone. Item courant dérivé de
          haccpRecord (haccpZoneItem, plus haut dans le composant), jamais un index local. */}
      <Sheet open={sheet === "haccpZone"} onClose={closeHaccpZone} P={P} maxH="88%">
        {haccpZoneGroup && (() => {
          const zStyle = HACCP_GROUP_STYLE[haccpZoneGroup.id] || { emoji: "🧪", color: A.navy };
          const dKey = haccpZoneItem ? `${haccpZoneGroup.id}.${haccpZoneItem.id}` : null;
          const showDeliveryChoice = haccpZoneItem?.type === "delivery_temp" && !haccpDeliveryPending[dKey];
          const showTempEntry = haccpZoneItem && (haccpZoneItem.type === "install_temp" || (haccpZoneItem.type === "delivery_temp" && haccpDeliveryPending[dKey]));
          return (
            <>
              <SheetHead kicker={haccpZoneItem ? t("haccp.zone.stepTemplate", { n: haccpZoneIdx + 1, total: haccpZoneItems.length }, `Étape ${haccpZoneIdx + 1} / ${haccpZoneItems.length}`) : t("haccp.zone.doneTitle", "Zone terminée")}
                title={t(`haccp.group.${haccpZoneGroup.id}.label`, haccpZoneGroup.label)} onClose={closeHaccpZone} />
              <div style={{ display: "flex", gap: 4, margin: "0 0 20px" }}>
                {haccpZoneItems.map((it, i) => (
                  <span key={it.id} style={{
                    flex: 1, height: 4, borderRadius: 99,
                    background: i < haccpZoneIdx ? A.green : i === haccpZoneIdx ? zStyle.color : P.line,
                    transition: "background .2s ease",
                  }} />
                ))}
              </div>

              {!haccpZoneItem ? (
                <div style={{ textAlign: "center", padding: "30px 10px" }}>
                  <div style={{ fontSize: `calc(40px * var(--nx-font-scale, 1))`, marginBottom: 10 }}>✅</div>
                  <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 4 }}>{t("haccp.zone.doneTitle", "Zone terminée")}</strong>
                  <p style={{ margin: "0 0 16px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("haccp.zone.doneSub", "Tous les contrôles de cette zone sont enregistrés.")}</p>
                  <div style={{ textAlign: "left", marginBottom: 18 }}>
                    {haccpZoneItems.map((it) => {
                      const rec = haccpEntryFor(haccpZoneGroup.id, it.id);
                      const isAnomaly = it.type === "check" ? rec?.value === false : rec?.ok === false;
                      const valueLabel = it.type === "check" ? null
                        : it.type === "delivery_temp" && rec?.hasDelivery === false ? t("haccp.noDeliveryTodayText", "Pas de livraison aujourd'hui")
                        : it.type === "delivery_temp" ? `${rec?.temp}°C` : `${rec?.value}°C`;
                      return (
                        <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 4px", borderBottom: `1px solid ${P.line}` }}>
                          <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: isAnomaly ? A.red : A.green, flex: "0 0 auto" }}>{isAnomaly ? "⚠️" : "✓"}</span>
                          <span style={{ flex: 1, minWidth: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{t(`haccp.group.${haccpZoneGroup.id}.item.${it.id}.label`, it.label)}</span>
                          {valueLabel && <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 800, flex: "0 0 auto" }}>{valueLabel}</span>}
                        </div>
                      );
                    })}
                  </div>
                  <Pressable onClick={closeHaccpZone} style={{ width: "100%", ...btnSolid(A.green) }}>{t("common.close", "Fermer")}</Pressable>
                </div>
              ) : (
                <>
                  <div style={{
                    borderRadius: 22, padding: "18px 20px 16px", marginBottom: 16, textAlign: "center",
                    background: `linear-gradient(155deg, ${zStyle.color}22, ${P.card} 60%)`,
                    border: `1px solid ${zStyle.color}38`,
                  }}>
                    <div style={{
                      width: 48, height: 48, margin: "0 auto 10px", borderRadius: 99,
                      background: `linear-gradient(155deg, ${zStyle.color}4d, ${zStyle.color}24 60%)`,
                      boxShadow: "inset 0 1px 0 rgba(255,255,255,.4)",
                      display: "grid", placeItems: "center",
                    }}><EmojiIcon e={zStyle.emoji} size={22} /></div>
                    <p style={{ margin: "0 0 2px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                      {t(`haccp.group.${haccpZoneGroup.id}.item.${haccpZoneItem.id}.label`, haccpZoneItem.label)}
                    </p>
                    {showTempEntry && (
                      <>
                        <div style={{
                          fontSize: `calc(46px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: "-.03em",
                          minHeight: 54, display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                          color: haccpKpDigits ? P.ink : P.muted, opacity: haccpKpDigits ? 1 : .35,
                          fontVariantNumeric: "tabular-nums",
                        }}>
                          {haccpKpNeg && haccpKpDigits ? "−" : ""}{haccpKpDigits ? (parseInt(haccpKpDigits, 10) / 10).toFixed(1) : "0"}
                          <span style={{ fontSize: `calc(20px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>°C</span>
                        </div>
                        <span style={{
                          display: "inline-block", marginTop: 8, fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800,
                          background: `${zStyle.color}1f`, color: zStyle.color, borderRadius: 99, padding: "5px 13px",
                        }}>{t("haccp.zone.maxNote", { max: haccpZoneItem.max }, `Seuil : ≤ ${haccpZoneItem.max}°C`)}</span>
                      </>
                    )}
                  </div>

                  {showDeliveryChoice ? (
                    <div style={{ display: "flex", gap: 10 }}>
                      <Pressable disabled={!haccpCanFill} onClick={() => haccpCanFill && setDeliveryYes(haccpZoneGroup.id, haccpZoneItem)} style={{
                        flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                        background: P.card, border: `1.5px solid ${P.line}`, color: P.ink, opacity: haccpCanFill ? 1 : .5,
                      }}>{t("haccp.deliveryReceivedBtn", "Livraison reçue")}</Pressable>
                      <Pressable disabled={!haccpCanFill} onClick={() => haccpCanFill && setDeliveryNo(haccpZoneGroup.id, haccpZoneItem)} style={{
                        flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(13px * var(--nx-font-scale, 1))`,
                        background: P.card, border: `1.5px solid ${P.line}`, color: P.muted, opacity: haccpCanFill ? 1 : .5,
                      }}>{t("haccp.noDelivery", "Pas de livraison")}</Pressable>
                    </div>
                  ) : haccpZoneItem.type === "check" ? (
                    <div style={{ display: "flex", gap: 10 }}>
                      <Pressable disabled={!haccpCanFill} onClick={() => haccpCanFill && toggleCheck(haccpZoneGroup.id, haccpZoneItem, true)} style={{
                        flex: 1, ...btnSolid(A.green), opacity: haccpCanFill ? 1 : .5,
                      }}>✓ {t("haccp.labelDone", "Fait")}</Pressable>
                      <Pressable disabled={!haccpCanFill} onClick={() => haccpCanFill && toggleCheck(haccpZoneGroup.id, haccpZoneItem, false)} style={{
                        flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
                        background: "rgba(239,68,68,.12)", color: A.red, opacity: haccpCanFill ? 1 : .5,
                      }}>⚠️ {t("haccp.labelAnomaly", "Anomalie")}</Pressable>
                    </div>
                  ) : showTempEntry ? (
                    <>
                      <div style={{ position: "relative", margin: "0 0 12px" }}>
                        <div className="nx-chips" style={{
                          display: "grid", gridAutoFlow: "column", gridTemplateRows: "repeat(2, 1fr)", gridAutoColumns: 66,
                          gap: 6, overflowX: "auto", padding: "2px 2px 4px",
                        }}>
                          {haccpChipsFor(haccpZoneItem).map((v) => {
                            const over = v > haccpZoneItem.max;
                            return (
                              <Pressable key={v} disabled={!haccpCanFill} onClick={() => haccpCanFill && haccpCommitZoneTemp(String(v))} style={{
                                textAlign: "center", padding: "9px 2px", borderRadius: 13, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                                fontVariantNumeric: "tabular-nums", background: P.soft, color: over ? A.red : P.ink,
                                border: `1px solid ${over ? "rgba(239,68,68,.3)" : P.line}`, opacity: haccpCanFill ? 1 : .5,
                              }}>{v.toFixed(1)}°C</Pressable>
                            );
                          })}
                        </div>
                      </div>
                      <Pressable disabled={!haccpCanFill} onClick={() => haccpCanFill && haccpConfirmZoneTemp()} style={{
                        display: "block", width: "100%", marginBottom: 14, ...btnSolid(zStyle.color), opacity: haccpCanFill ? 1 : .5,
                      }}>{t("haccp.zone.confirmBtn", "Valider")}</Pressable>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0, maxWidth: 280, margin: "0 auto" }}>
                        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "−", "0", "⌫"].map((k) => (
                          <Pressable key={k} disabled={!haccpCanFill} feedback={false} onClick={() => {
                            if (!haccpCanFill) return;
                            if (k === "⌫") haccpKpBackspace();
                            else if (k === "−") haccpKpToggleNeg();
                            else haccpKpPressDigit(k);
                          }} style={{
                            aspectRatio: "1.8", borderRadius: 14, display: "grid", placeItems: "center",
                            fontSize: k === "−" || k === "⌫" ? `calc(22px * var(--nx-font-scale, 1))` : `calc(27px * var(--nx-font-scale, 1))`,
                            fontWeight: 700, color: k === "−" || k === "⌫" ? P.muted : P.ink, fontVariantNumeric: "tabular-nums",
                          }}>{k}</Pressable>
                        ))}
                      </div>
                    </>
                  ) : null}
                </>
              )}
            </>
          );
        })()}
      </Sheet>

      <Sheet open={sheet === "haccpQuickChecks"} onClose={() => setSheet(null)} P={P} maxH="80%">
        <SheetHead kicker="HACCP" title={t("haccp.quickChecksBtn", "Valider les contrôles simples")} onClose={() => setSheet(null)} />
        <p style={{ margin: "0 0 16px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
          {t("haccp.zone.quickChecksHint", "Un tap sur ⚠️ signale une anomalie précise avant de tout confirmer d'un coup.")}
        </p>
        {haccpRemainingChecks.map((it) => {
          const zStyle = HACCP_GROUP_STYLE[it.groupId] || { emoji: "🧪", color: A.navy };
          const g = haccpConfig.dailyGroups.find((gr) => gr.id === it.groupId);
          return (
            <div key={`${it.groupId}.${it.id}`} style={{ display: "flex", alignItems: "center", gap: 11, padding: "11px 4px", borderBottom: `1px solid ${P.line}` }}>
              <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}><EmojiIcon e={zStyle.emoji} size={18} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{t(`haccp.group.${it.groupId}.item.${it.id}.label`, it.label)}</strong>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{g ? t(`haccp.group.${g.id}.label`, g.label) : it.groupId}</span>
              </div>
              <Pressable disabled={haccpBulkRunning} onClick={() => toggleCheck(it.groupId, it, false)} style={{
                width: 34, height: 34, borderRadius: 99, background: "rgba(239,68,68,.12)", color: A.red,
                display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
              }}>⚠️</Pressable>
            </div>
          );
        })}
        <Pressable disabled={haccpBulkRunning || haccpRemainingChecks.length === 0} onClick={bulkConfirmHaccpChecks} style={{
          width: "100%", marginTop: 16, ...btnSolid(A.green), opacity: haccpBulkRunning ? .6 : 1,
        }}>
          {haccpBulkRunning ? t("common.saving", "Enregistrement...") : t("haccp.zone.confirmAllBtn", { n: haccpRemainingChecks.length }, `✓ Confirmer les ${haccpRemainingChecks.length} contrôle${haccpRemainingChecks.length > 1 ? "s" : ""} conformes`)}
        </Pressable>
      </Sheet>

      {/* Sheet HACCP hebdomadaire "contrôle éclair" — même patron que haccpZone, mais tous les
          items sont de simples "check" (aucun clavier/chip nécessaire). */}
      <Sheet open={sheet === "haccpWeeklyZone"} onClose={closeHaccpWeeklyZone} P={P} maxH="82%">
        {haccpWeeklyZoneGroup && (() => {
          const zStyle = HACCP_GROUP_STYLE[haccpWeeklyZoneGroup.id] || { emoji: "🧪", color: A.navy };
          return (
            <>
              <SheetHead kicker={haccpWeeklyZoneItem ? t("haccp.zone.stepTemplate", { n: haccpWeeklyZoneIdx + 1, total: haccpWeeklyZoneItems.length }, `Étape ${haccpWeeklyZoneIdx + 1} / ${haccpWeeklyZoneItems.length}`) : t("haccp.zone.doneTitle", "Zone terminée")}
                title={t(`haccp.group.${haccpWeeklyZoneGroup.id}.label`, haccpWeeklyZoneGroup.label)} onClose={closeHaccpWeeklyZone} />
              <div style={{ display: "flex", gap: 4, margin: "0 0 20px" }}>
                {haccpWeeklyZoneItems.map((it, i) => (
                  <span key={it.id} style={{
                    flex: 1, height: 4, borderRadius: 99,
                    background: i < haccpWeeklyZoneIdx ? A.green : i === haccpWeeklyZoneIdx ? zStyle.color : P.line,
                    transition: "background .2s ease",
                  }} />
                ))}
              </div>

              {!haccpWeeklyZoneItem ? (
                <div style={{ textAlign: "center", padding: "30px 10px" }}>
                  <div style={{ fontSize: `calc(40px * var(--nx-font-scale, 1))`, marginBottom: 10 }}>✅</div>
                  <strong style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 4 }}>{t("haccp.zone.doneTitle", "Zone terminée")}</strong>
                  <p style={{ margin: "0 0 16px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("haccp.zone.doneSub", "Tous les contrôles de cette zone sont enregistrés.")}</p>
                  <div style={{ textAlign: "left", marginBottom: 18 }}>
                    {haccpWeeklyZoneItems.map((it) => {
                      const rec = haccpWeeklyRecord?.entries?.[`${haccpWeeklyZoneGroup.id}.${it.id}`];
                      const isAnomaly = rec?.value === false;
                      return (
                        <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 4px", borderBottom: `1px solid ${P.line}` }}>
                          <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: isAnomaly ? A.red : A.green, flex: "0 0 auto" }}>{isAnomaly ? "⚠️" : "✓"}</span>
                          <span style={{ flex: 1, minWidth: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{t(`haccp.group.${haccpWeeklyZoneGroup.id}.item.${it.id}.label`, it.label)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <Pressable onClick={closeHaccpWeeklyZone} style={{ width: "100%", ...btnSolid(A.green) }}>{t("common.close", "Fermer")}</Pressable>
                </div>
              ) : (
                <>
                  <div style={{
                    borderRadius: 22, padding: "18px 20px 16px", marginBottom: 16, textAlign: "center",
                    background: `linear-gradient(155deg, ${zStyle.color}22, ${P.card} 60%)`,
                    border: `1px solid ${zStyle.color}38`,
                  }}>
                    <div style={{
                      width: 48, height: 48, margin: "0 auto 10px", borderRadius: 99,
                      background: `linear-gradient(155deg, ${zStyle.color}4d, ${zStyle.color}24 60%)`,
                      boxShadow: "inset 0 1px 0 rgba(255,255,255,.4)",
                      display: "grid", placeItems: "center",
                    }}><EmojiIcon e={zStyle.emoji} size={22} /></div>
                    <p style={{ margin: 0, fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted }}>
                      {t(`haccp.group.${haccpWeeklyZoneGroup.id}.item.${haccpWeeklyZoneItem.id}.label`, haccpWeeklyZoneItem.label)}
                    </p>
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <Pressable disabled={!haccpCanValidateWeekly} onClick={() => haccpCanValidateWeekly && toggleWeekly(haccpWeeklyZoneGroup.id, haccpWeeklyZoneItem, true)} style={{
                      flex: 1, ...btnSolid(A.green), opacity: haccpCanValidateWeekly ? 1 : .5,
                    }}>✓ {t("haccp.labelDone", "Fait")}</Pressable>
                    <Pressable disabled={!haccpCanValidateWeekly} onClick={() => haccpCanValidateWeekly && toggleWeekly(haccpWeeklyZoneGroup.id, haccpWeeklyZoneItem, false)} style={{
                      flex: 1, textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
                      background: "rgba(239,68,68,.12)", color: A.red, opacity: haccpCanValidateWeekly ? 1 : .5,
                    }}>⚠️ {t("haccp.labelAnomaly", "Anomalie")}</Pressable>
                  </div>
                </>
              )}
            </>
          );
        })()}
      </Sheet>

      <Sheet open={sheet === "haccpWeeklyQuickChecks"} onClose={() => setSheet(null)} P={P} maxH="80%">
        <SheetHead kicker="HACCP" title={t("haccp.quickChecksBtn", "Valider les contrôles simples")} onClose={() => setSheet(null)} />
        <p style={{ margin: "0 0 16px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
          {t("haccp.zone.quickChecksHint", "Un tap sur ⚠️ signale une anomalie précise avant de tout confirmer d'un coup.")}
        </p>
        {haccpRemainingWeeklyChecks.map((it) => {
          const zStyle = HACCP_GROUP_STYLE[it.groupId] || { emoji: "🧪", color: A.navy };
          const g = haccpConfig.weeklyGroups.find((gr) => gr.id === it.groupId);
          return (
            <div key={`${it.groupId}.${it.id}`} style={{ display: "flex", alignItems: "center", gap: 11, padding: "11px 4px", borderBottom: `1px solid ${P.line}` }}>
              <span style={{ fontSize: `calc(18px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}><EmojiIcon e={zStyle.emoji} size={18} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{t(`haccp.group.${it.groupId}.item.${it.id}.label`, it.label)}</strong>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 700 }}>{g ? t(`haccp.group.${g.id}.label`, g.label) : it.groupId}</span>
              </div>
              <Pressable disabled={haccpWeeklyBulkRunning} onClick={() => toggleWeekly(it.groupId, it, false)} style={{
                width: 34, height: 34, borderRadius: 99, background: "rgba(239,68,68,.12)", color: A.red,
                display: "grid", placeItems: "center", fontSize: `calc(14px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
              }}>⚠️</Pressable>
            </div>
          );
        })}
        <Pressable disabled={haccpWeeklyBulkRunning || haccpRemainingWeeklyChecks.length === 0} onClick={bulkConfirmHaccpWeeklyChecks} style={{
          width: "100%", marginTop: 16, ...btnSolid(A.green), opacity: haccpWeeklyBulkRunning ? .6 : 1,
        }}>
          {haccpWeeklyBulkRunning ? t("common.saving", "Enregistrement...") : t("haccp.zone.confirmAllBtn", { n: haccpRemainingWeeklyChecks.length }, `✓ Confirmer les ${haccpRemainingWeeklyChecks.length} contrôle${haccpRemainingWeeklyChecks.length > 1 ? "s" : ""} conformes`)}
        </Pressable>
      </Sheet>

      <Sheet open={sheet === "haccpNote"} onClose={() => { setSheet(haccpNoteEdit?.returnSheet || null); setHaccpNoteEdit(null); }} P={P} maxH="70%">
        <SheetHead kicker={t("haccp.labelAnomaly", "Anomalie")} title={haccpNoteEdit?.item.label || ""} onClose={() => { setSheet(haccpNoteEdit?.returnSheet || null); setHaccpNoteEdit(null); }} />
        <p style={{ margin: "0 0 12px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
          {t("haccp.noteRequired", "Une remarque est obligatoire")} {t("haccp.anomalySuffixShort", "pour enregistrer une anomalie")}{haccpNoteEdit?.value != null ? t("haccp.anomalyValueSuffixTemplate", { value: haccpNoteEdit.value }, ` (valeur : ${haccpNoteEdit.value}°C)`) : ""}.
        </p>
        <Field label={t("haccp.noteFallbackLabel", "Remarque")}>
          <input style={inputStyle} placeholder={t("haccp.notePlaceholder", "Décris le problème constaté (obligatoire)")} value={haccpNoteDraft.text}
            onChange={(e) => setHaccpNoteDraft({ ...haccpNoteDraft, text: e.target.value })} />
        </Field>
        <input ref={haccpPhotoInputRef} type="file" accept="image/*" style={{ display: "none" }}
          onChange={(e) => { uploadHaccpPhoto(e.target.files); e.target.value = ""; }} />
        <Pressable onClick={() => haccpPhotoInputRef.current?.click()} style={{
          display: "flex", alignItems: "center", gap: 9, padding: "11px 13px", borderRadius: 14, marginBottom: 15,
          background: haccpNoteDraft.photoUrl ? "rgba(54,79,231,.1)" : P.soft, border: `1.5px solid ${haccpNoteDraft.photoUrl ? A.blue : P.line}`,
        }}>
          <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{haccpNoteDraft.photoUrl ? "✅" : "📷"}</span>
          <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: haccpNoteDraft.photoUrl ? A.blue : P.ink }}>
            {haccpUploading ? t("haccp.uploadingPhoto", "Envoi de la photo...") : haccpNoteDraft.photoUrl ? t("haccp.photoAttachedLabel", "Photo jointe") : t("haccp.addPhotoOptional", "Ajouter une photo (optionnel)")}
          </span>
        </Pressable>
        <Pressable onClick={confirmHaccpAnomaly} style={{ width: "100%", ...btnSolid(A.red) }}>{t("haccp.saveAnomalyBtn", "Enregistrer l'anomalie")}</Pressable>
      </Sheet>

      <Sheet open={sheet === "haccpJournal"} onClose={() => setSheet(null)} P={P}>
        <SheetHead kicker="HACCP" title={t("haccp.historyModalTitle", "Historique des modifications")} onClose={() => setSheet(null)} />
        {haccpJournalEntries().length === 0 && <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{t("haccp.journalEmptyText", "Aucune modification pour l'instant.")}</p>}
        {haccpJournalEntries().map((h) => {
          const [groupId, itemId] = String(h.key || "").split(".");
          // newValue est soit un booléen (check), un nombre (install_temp), soit {hasDelivery,temp}
          // (delivery_temp) — seul le cas booléen false est une anomalie certaine à partir du seul
          // journal ; les dépassements de seuil température ne sont pas re-déductibles ici sans
          // revalider contre item.max, donc affichés neutres (✓) plutôt que mal étiquetés.
          const isAnomaly = h.newValue === false;
          return (
            <div key={h.id || `${h.key}_${h.at}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 12px", borderRadius: 16, background: P.soft, marginBottom: 7 }}>
              <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{isAnomaly ? "⚠️" : "✓"}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{haccpGroupLabel(h.scope, groupId)} · {haccpItemLabelIn(h.scope, groupId, itemId)}</strong>
                <div style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{h.scope === "weekly" ? t("haccp.weeklyTagShort", "Hebdomadaire") : t("haccp.dailyTagShort", "Quotidien")} · {h.userName} · {new Date(h.at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>
              </div>
            </div>
          );
        })}
      </Sheet>

      <Sheet open={sheet === "claimItem"} onClose={() => { setSheet(null); setClaimItemForm(null); }} P={P}>
        <SheetHead kicker={t(draftClaimType === "fresh" ? "claims.type.fresh" : "claims.type.food", draftClaimType === "fresh" ? "Fruits & légumes" : "Food / Non-Food")} title={claimItemForm?.id ? t("claims.item.editTitle", "Modifier l'article") : t("claims.addItem", "Ajouter un article")}
          onClose={() => { setSheet(null); setClaimItemForm(null); }} />
        {claimItemForm && (() => {
          // Retour utilisateur explicite (2026-08-19) : un simple toast pour "Défaut qualité
          // obligatoire" passait inaperçu — la fiche encadre maintenant en rouge la SECTION du
          // champ en faute (claimItemError, posé par saveClaimItem), en plus du toast.
          const formSection = (hasError) => ({ ...card, padding: 14, marginBottom: 12, ...(hasError ? { border: `1.5px solid ${A.red}`, boxShadow: `0 0 0 3px ${A.red}22` } : {}) });
          const sectionTitle = (dotColor, label) => (
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".08em", color: P.muted, marginBottom: 11 }}>
              <span style={{ width: 7, height: 7, borderRadius: 99, flexShrink: 0, background: dotColor }} />{label}
            </div>
          );
          const qtyOptions = draftClaimType === "food"
            ? [{ id: "pieces", l: t("claims.item.pieces", "Pièces") }, { id: "cartons", l: t("claims.item.cartons", "Cartons") }]
            : [{ id: "pieces", l: t("claims.item.pieces", "Pièces") }, { id: "kilos", l: t("claims.item.kilos", "Kilos") }];
          const qtyLabel = claimItemForm.qtyType === "pieces" ? t("claims.item.qtyPiecesLabel", "Nombre de pièces")
            : claimItemForm.qtyType === "cartons" ? t("claims.item.qtyCartonsLabel", "Nombre de cartons")
            : t("claims.item.qtyKilosLabel", "Poids (kg)");
          return (
          <>
            <div style={formSection(claimItemError === "artNr")}>
              {sectionTitle(A.red, t("claims.item.sectionArticle", "Article"))}
              <Field label={t("claims.item.artNr", "Numéro d'article")}>
                <input style={inputStyle} value={claimItemForm.artNr} onChange={(e) => { setClaimItemError(null); setClaimItemForm({ ...claimItemForm, artNr: e.target.value }); }} placeholder={t("claims.item.artNrPlaceholder", "Ex. 70211")} />
              </Field>
              {draftClaimType === "fresh" ? (
                <>
                  <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 6 }}>{t("claims.item.suggestionsLabel", "Article (suggestions)")}</span>
                  <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 13, paddingBottom: 2 }}>
                    {freshArticles.map((a) => (
                      <Pressable key={a.id} onClick={() => setClaimItemForm({ ...claimItemForm, name: a.name, artNr: a.artNr })} style={{
                        flex: "0 0 auto", padding: "8px 13px", borderRadius: 99, fontWeight: 700, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                        background: claimItemForm.name === a.name ? A.red : P.soft, color: claimItemForm.name === a.name ? "#fff" : P.ink,
                        border: `1.5px solid ${claimItemForm.name === a.name ? A.red : P.line}`,
                      }}>{a.name}</Pressable>
                    ))}
                  </div>
                </>
              ) : null}
              <Field label={t("claims.item.name", "Désignation")}>
                <input style={inputStyle} value={claimItemForm.name} onChange={(e) => setClaimItemForm({ ...claimItemForm, name: e.target.value })} placeholder={t("claims.item.namePlaceholder", "Nom de l'article")} />
              </Field>
            </div>

            <div style={formSection(claimItemError === "defect" || claimItemError === "qty")}>
              {sectionTitle(A.blue, t("claims.item.sectionQtyDefect", "Quantité & défaut"))}
              <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 6 }}>{t("controlRun.quantityLabel", "Quantité")}</span>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                {qtyOptions.map((q) => (
                  <Pressable key={q.id} onClick={() => setClaimItemForm({ ...claimItemForm, qtyType: q.id })} style={{
                    flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                    background: claimItemForm.qtyType === q.id ? A.blue : P.soft, color: claimItemForm.qtyType === q.id ? "#fff" : P.ink,
                    border: `1.5px solid ${claimItemForm.qtyType === q.id ? A.blue : P.line}`,
                  }}>{q.l}</Pressable>
                ))}
              </div>
              <Field label={qtyLabel}>
                <input style={inputStyle} inputMode="numeric" value={claimItemForm.qty} onChange={(e) => { setClaimItemError((cur) => cur === "qty" ? null : cur); setClaimItemForm({ ...claimItemForm, qty: e.target.value }); }} />
              </Field>
              <Field label={t("claims.item.chargeLossLabel", "N° charge / perte (optionnel)")}>
                <input style={inputStyle} value={claimItemForm.chargeLossNr} onChange={(e) => setClaimItemForm({ ...claimItemForm, chargeLossNr: e.target.value })} />
              </Field>
              <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 6 }}>{t("claims.item.defect", "Défaut qualité")}</span>
              <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 10, paddingBottom: 2 }}>
                {currentDefects.map((d) => (
                  <Pressable key={d} onClick={() => { setClaimItemError((cur) => cur === "defect" ? null : cur); setClaimItemForm({ ...claimItemForm, defect: d }); }} style={{
                    flex: "0 0 auto", padding: "8px 13px", borderRadius: 99, fontWeight: 700, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                    background: claimItemForm.defect === d ? A.red : P.soft, color: claimItemForm.defect === d ? "#fff" : P.ink,
                    border: `1.5px solid ${claimItemForm.defect === d ? A.red : P.line}`,
                  }}>{d}</Pressable>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input style={{ ...inputStyle, flex: 1 }} placeholder={t("claims.defects.addPlaceholder", "Nouveau défaut...")} value={newDefectDraft} onChange={(e) => setNewDefectDraft(e.target.value)} />
                <Pressable onClick={addDefectToList} style={{ padding: "0 16px", borderRadius: 16, background: P.soft, border: `1.5px solid ${P.line}`, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.ink }}>{t("claims.add", "Ajouter")}</Pressable>
              </div>
            </div>

            <div style={formSection(false)}>
              {sectionTitle(P.muted, t("claims.item.sectionProof", "Preuve & remarque"))}
              <input ref={claimPhotoInputRef} type="file" accept="image/*" multiple style={{ display: "none" }}
                onChange={(e) => { uploadClaimPhotos(e.target.files); e.target.value = ""; }} />
              <Pressable onClick={() => claimItemForm.photos.length ? setClaimItemForm({ ...claimItemForm, photos: [] }) : claimPhotoInputRef.current?.click()} style={{
                display: "flex", alignItems: "center", gap: 9, padding: "11px 13px", borderRadius: 14, marginBottom: 12,
                background: claimItemForm.photos.length ? "rgba(54,79,231,.1)" : P.soft, border: `1.5px solid ${claimItemForm.photos.length ? A.blue : P.line}`,
              }}>
                <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{claimItemForm.photos.length ? "✅" : "📷"}</span>
                <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: claimItemForm.photos.length ? A.blue : P.ink }}>{claimItemForm.photos.length ? t("claims.item.photoAttachedLabel", "Photo jointe") : t("claims.item.addPhotoLabel", "Ajouter une photo")}</span>
              </Pressable>
              <Field label={t("claims.item.note", "Remarque (optionnel)")}>
                <input style={inputStyle} placeholder={t("claims.item.notePlaceholder", "Détail utile pour la centrale")} value={claimItemForm.note} onChange={(e) => setClaimItemForm({ ...claimItemForm, note: e.target.value })} />
              </Field>
            </div>

            <Pressable onClick={saveClaimItem} style={{ width: "100%", ...btnSolid(A.red) }}>{t("claims.item.validate", "Valider")}</Pressable>
          </>
          );
        })()}
      </Sheet>

      {/* Fiche complète d'une réclamation — retour utilisateur explicite (2026-08-19) :
          cliquer une réclamation dans l'Historique n'ouvrait rien avant, seuls les tags
          "nom · défaut" étaient visibles dans la liste (jamais quantité/n° charge-perte/
          remarque/photos). Lecture seule — les actions (Envoyer/Supprimer/Restaurer...)
          restent sur la carte de la liste, déjà fonctionnelles, pas dupliquées ici. */}
      <Sheet open={sheet === "claimDetail"} onClose={() => { setSheet(null); setClaimDetail(null); }} P={P}>
        <SheetHead kicker={claimDetail ? (claimDetail.type === "fresh" ? t("claims.type.fresh", "Fruits & légumes") : t("claims.type.food", "Food / Non-Food")) : ""}
          title={t("claims.detail.title", "Fiche réclamation")}
          onClose={() => { setSheet(null); setClaimDetail(null); }} />
        {claimDetail && (() => {
          const c = claimDetail;
          const statusMeta = c.status === "done" ? { l: t("claims.status.done", "Traitée"), bg: A.green, fg: "#fff" }
            : c.status === "sent" ? { l: t("claims.status.sent", "Envoyée"), bg: A.blue, fg: "#fff" }
            : { l: t("claims.status.open", "Ouverte"), bg: "rgba(231,138,18,.16)", fg: "#b56a00" };
          const sentViaLabel = c.sentVia === "email" ? t("claims.settings.methodEmail", "E-mail")
            : c.sentVia === "fax" ? t("claims.settings.methodFax", "Fax")
            : c.sentVia === "other" ? t("claims.settings.methodOther", "Autre") : "";
          const unitLabel = (it) => it.qtyType === "pieces" ? t("claims.unitPieces", "pièces") : it.qtyType === "cartons" ? t("claims.unitCartons", "cartons") : t("claims.unitKg", "kg");
          return (
          <>
            <div style={{ ...card, padding: 14, marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{t("claims.itemsCount", { n: c.items.length }, `${c.items.length} article${c.items.length > 1 ? "s" : ""}`)}</strong>
                <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 10px", whiteSpace: "nowrap", background: statusMeta.bg, color: statusMeta.fg }}>{statusMeta.l}</span>
              </div>
              <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("claims.byCreatedAtTemplate", { name: c.createdBy, date: fmtFR(c.createdAt) }, `Par ${c.createdBy} · ${fmtFR(c.createdAt)}`)}</div>
              {c.sentAt && (
                <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, marginTop: 3 }}>
                  {t("claims.detail.sentAtPrefix", { date: fmtFR(c.sentAt) }, `Envoyée le ${fmtFR(c.sentAt)}`)}
                  {sentViaLabel ? ` · ${sentViaLabel}` : ""}{c.sentTo ? ` · ${c.sentTo}` : ""}
                </div>
              )}
            </div>

            {c.items.map((it, i) => (
              <div key={it.id} style={{ ...card, padding: 14, marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".08em", color: P.muted, marginBottom: 10 }}>
                  <span style={{ width: 7, height: 7, borderRadius: 99, flexShrink: 0, background: A.red }} />{t("claims.detail.itemNTemplate", { n: i + 1 }, `Article ${i + 1}`)}
                </div>
                <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 2 }}>{it.name || it.artNr}</strong>
                <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 10 }}>{t("controlRun.artNrPrefix", "Art.Nr")} {it.artNr} · {it.qty} {unitLabel(it)}{it.chargeLossNr ? ` · ${t("claims.detail.chargeLossShortLabel", "N° charge/perte")} ${it.chargeLossNr}` : ""}</div>
                {it.defect && (
                  <div style={{ display: "inline-block", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.red, background: "rgba(239,68,68,.1)", borderRadius: 99, padding: "5px 12px", marginBottom: it.note || it.photos.length ? 10 : 0 }}>⚠️ {it.defect}</div>
                )}
                {it.note && (
                  <p style={{ margin: "0 0 10px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.ink, background: P.soft, borderRadius: 12, padding: 11, fontStyle: "italic" }}>« {it.note} »</p>
                )}
                {it.photos.length > 0 && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {it.photos.map((url, pIdx) => (
                      <img key={pIdx} src={url} alt="" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 12, border: `1px solid ${P.line}` }} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
          );
        })()}
      </Sheet>

      {/* Fiche tâche — grand popup (jamais une nouvelle page), polices volontairement larges
          pour se lire d'un coup d'œil. Montre TOUT le contenu, jamais tronqué comme dans les
          petites puces de la liste. */}
      <Sheet open={sheet === "taskDetail"} onClose={() => { setSheet(null); setTaskDetail(null); }} P={P}>
        <SheetHead kicker={taskDetail ? typeMeta[taskDetail.type].label : ""} title={taskDetail ? translateAutoTaskTitle(taskDetail.title) : ""}
          onClose={() => { setSheet(null); setTaskDetail(null); }} />
        {taskDetail && (() => {
          const tm = typeMeta[taskDetail.type];
          const tc = TASK_TYPE_COLOR[taskDetail.type] || TASK_TYPE_COLOR.standard;
          const pm = priorityMeta[taskDetail.priority];
          const overdue = taskDetail.status === "late";
          return (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
                <span style={{ flex: "0 0 auto", width: 64, height: 64, borderRadius: 20, background: tc.bg, display: "grid", placeItems: "center", fontSize: `calc(32px * var(--nx-font-scale, 1))`}}>{tm.emoji}</span>
                <div style={{ flex: 1, minWidth: 0, fontSize: `calc(15px * var(--nx-font-scale, 1))`, color: P.ink, fontWeight: 700 }}>
                  {taskDetail.scope === "network" ? `${taskDetail.createdBy} · ${t("tasks.allStoresSuffix", "toutes filiales")}` : taskDetail.recipient === "specific" ? t("tasks.forAssigneeTemplate", { name: taskDetail.assignee }, `Pour ${taskDetail.assignee}`) : t("tasks.forTeamBtn", "Pour l'équipe")}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
                <span style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: pm.color, background: pm.bg, borderRadius: 99, padding: "8px 15px" }}>{pm.label}</span>
                <span style={{
                  fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, fontWeight: 700, borderRadius: 99, padding: "8px 15px",
                  color: overdue ? A.red : P.muted, background: overdue ? "rgba(239,68,68,.13)" : P.soft,
                }}>{overdue ? t("tasks.overdueLabel", "Pas fait · ") : t("tasks.deadlinePrefix", "Échéance ")}{fmtFR(taskDetail.deadline.slice(0, 10))} {taskDetail.deadline.slice(11, 16)}</span>
                {taskDetail.status === "done" && taskDetail.completedBy && (
                  <span style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: "#15803d", background: "rgba(30,184,95,.12)", borderRadius: 99, padding: "8px 15px" }}>{t("tasks.completedByTemplate", { name: taskDetail.completedBy }, `✓ Terminée par ${taskDetail.completedBy}`)}</span>
                )}
              </div>

              {taskDetail.note && (
                <p style={{ margin: "0 0 22px", fontSize: `calc(16px * var(--nx-font-scale, 1))`, lineHeight: 1.65, color: P.ink, background: P.soft, borderRadius: 18, padding: 17, fontWeight: 600 }}>{taskDetail.note}</p>
              )}

              {taskDetail.type === "bakery" && taskDetail.bakeryItems && (
                <>
                  <strong style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`, display: "block", marginBottom: 12 }}>{t("tasks.articlesToBakeHeadingTemplate", { n: taskDetail.bakeryItems.length }, `Articles à cuire (${taskDetail.bakeryItems.length})`)}</strong>
                  {taskDetail.bakeryItems.map((b, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 17px", borderRadius: 18, background: P.soft, marginBottom: 10 }}>
                      <span style={{ fontSize: `calc(26px * var(--nx-font-scale, 1))`}}>🥐</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(16.5px * var(--nx-font-scale, 1))`, display: "block" }}>{b.name}</strong>
                        {b.artNr && <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>Art.-Nr {b.artNr}</span>}
                      </div>
                      <strong style={{ fontSize: `calc(23px * var(--nx-font-scale, 1))`, color: A.brown }}>×{b.qty}</strong>
                    </div>
                  ))}
                </>
              )}
            </>
          );
        })()}
      </Sheet>

      <Sheet open={sheet === "pluConfirm"} onClose={() => { setSheet(null); setPluTask(null); setPluStep("confirm"); }} P={P} maxH="55%">
        <SheetHead kicker={t("pluQuiz.examKicker", "Examen PLU")} title={pluTask ? translateAutoTaskTitle(pluTask.title) : ""}
          onClose={() => { setSheet(null); setPluTask(null); setPluStep("confirm"); }} />
        {pluTask && pluStep === "confirm" && (
          <>
            <p style={{ margin: "0 0 16px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
              {t("pluQuiz.reminder.introText", "Tu es assigné à cet examen PLU. Tu veux le commencer maintenant, ou plus tard ?")}
            </p>
            <Pressable onClick={() => startPluNow(pluTask)} style={{ width: "100%", ...btnSolid(A.blue), marginBottom: 10 }}>{t("pluQuiz.reminder.startNowBtn", "Commencer maintenant")}</Pressable>
            <Pressable onClick={() => setPluStep("delay")} style={{
              width: "100%", textAlign: "center", padding: "14px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14.5px * var(--nx-font-scale, 1))`,
              background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
            }}>{t("tasks.reminder.laterBtn", "Plus tard")}</Pressable>
          </>
        )}
        {pluTask && pluStep === "delay" && (
          <>
            <p style={{ margin: "0 0 14px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
              Tu seras rappelé pour commencer l'examen.
            </p>
            {[{ m: 30, l: "Dans 30 min" }, { m: 60, l: "Dans 1 h" }, { m: 120, l: "Dans 2 h" }].map((opt) => (
              <Pressable key={opt.m} onClick={() => savePluDelay(opt.m)} style={{
                width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
                background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink, marginBottom: 9,
              }}>{opt.l}</Pressable>
            ))}
          </>
        )}
      </Sheet>

      <Sheet open={sheet === "availEdit"} onClose={() => { setSheet(null); setAvailDayEdit(null); }} P={P} maxH="65%">
        <SheetHead kicker={t("planning.hub.aushilfe.title", "Disponibilités des extras")} title={availDayEdit ? weekLabel(availDayEdit.day) : ""}
          onClose={() => { setSheet(null); setAvailDayEdit(null); }} />
        {availDayEdit && ownAvailDays && (() => {
          const key = PLAN_DAY_KEYS[availDayEdit.day];
          const d = ownAvailDays[key] || { mode: "unavailable", ranges: [] };
          const range = d.ranges[0] || { start: "09:00", end: "17:00" };
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {[
                { l: `🟢 ${t("planning.aushilfe.fullDayShort", "Toute la journée")}`, v: "full" },
                { l: `🔴 ${t("planning.aushilfe.unavailableShort", "Non disponible")}`, v: "unavailable" },
                { l: `🕐 ${t("planning.aushilfe.preciseHoursShort", "Heures précises")}`, v: "custom" },
              ].map((opt) => (
                <Pressable key={opt.v} onClick={() => setAvailDayMode(availDayEdit.day, opt.v)} style={{
                  textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
                  background: d.mode === opt.v ? `${A.teal}1a` : P.soft,
                  border: `1.5px solid ${d.mode === opt.v ? A.teal : P.line}`,
                  color: d.mode === opt.v ? A.teal : P.ink,
                }}>{opt.l}</Pressable>
              ))}
              {d.mode === "custom" && (
                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  <Field label={t("planning.sheet.start", "Début")}>
                    <TimeSelect value={range.start}
                      onChange={(v) => setAvailDayRange(availDayEdit.day, "start", v)} />
                  </Field>
                  <Field label={t("planning.sheet.end", "Fin")}>
                    <TimeSelect value={range.end}
                      onChange={(v) => setAvailDayRange(availDayEdit.day, "end", v)} />
                  </Field>
                </div>
              )}
              <Pressable onClick={() => { setSheet(null); setAvailDayEdit(null); }} style={{
                width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
                background: A.teal, color: "#fff", marginTop: 4,
              }}>OK</Pressable>
            </div>
          );
        })()}
      </Sheet>

      <Sheet open={sheet === "availClearChoice"} onClose={() => setSheet(null)} P={P}>
        <SheetHead kicker={t("planning.hub.aushilfe.title", "Disponibilités des extras")} title={t("planning.aushilfe.clearWeekBtnShort", "Vider la semaine")} onClose={() => setSheet(null)} />
        <p style={{ margin: "0 0 14px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
          Supprimer définitivement l'enregistrement de cette semaine, ou juste remettre tous les jours à « non disponible » (l'enregistrement reste, consultable plus tard) ?
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          <Pressable onClick={() => clearAvailabilityWeek("reset")} style={{
            textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
            background: P.soft, border: `1.5px solid ${P.line}`, color: P.ink,
          }}>{t("reporting.wipeModal.resetOnlyBtn", "↩️ Réinitialiser seulement")}</Pressable>
          <Pressable onClick={() => clearAvailabilityWeek("full")} style={{
            textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
            background: "rgba(239,68,68,.1)", border: "1.5px solid rgba(239,68,68,.3)", color: A.red,
          }}>{t("reporting.wipeModal.deletePermanentlyBtn", "🗑️ Supprimer définitivement")}</Pressable>
        </div>
      </Sheet>

      {/* Sélecteur de règle rapide — accessible depuis les 3 écrans Contrôle MHD Aktion
          (articles jamais présents dans articlesCatalog, donc jamais atteignables via la
          recherche de la sheet redRules ci-dessous). Même moteur (setArticleRule). */}
      <Sheet open={sheet === "quickRule"} onClose={closeQuickRule} P={P} maxH="55%">
        <SheetHead kicker={t("reductions.jRulesSheetTitle", "Règles de réduction")} title={quickRuleArt?.name || ""} onClose={closeQuickRule} />
        {quickRuleArt && (() => {
          // Clé effective — Art.Nr en priorité, EAN sinon (article Magasin en saisie libre
          // sans Art.Nr, retour utilisateur explicite 2026-08-21). Même clé que le serveur
          // (sfReductionOverrideKey) et que loadRuleOverrides/redTodo côté client.
          const key = quickRuleArt.artNr || quickRuleArt.ean;
          return (
          <>
            <p style={{ margin: "0 0 14px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>
              {quickRuleArt.artNr ? `${t("controlRun.artNrPrefix", "Art.Nr")} ${quickRuleArt.artNr}` : `EAN ${quickRuleArt.ean}`} · {t("reductions.currentRulePrefix", "règle actuelle :")} {reductionRuleLabel(redRuleFor(key))}
              {!ruleOverrides[key] ? ` (${t("reductions.autoSuffix", "automatique")})` : ""}
            </p>
            <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", paddingBottom: 2 }}>
              {["AUTO", ...ruleDefinitions.map((d) => d.id)].map((rule) => {
                const sel = rule === "AUTO" ? !ruleOverrides[key] : ruleOverrides[key] === rule;
                const label = rule === "AUTO" ? t("reductions.autoLabel", "Automatique") : reductionRuleLabel(rule);
                return (
                  <Pressable key={rule} onClick={() => {
                    setArticleRule(quickRuleArt.artNr, rule, quickRuleArt.name, quickRuleArt.ean);
                    closeQuickRule();
                  }} style={{
                    flex: "0 0 auto", padding: "10px 15px", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                    background: sel ? A.blue : P.soft, color: sel ? "#fff" : P.ink, border: `1.5px solid ${sel ? A.blue : P.line}`,
                  }}>{label}</Pressable>
                );
              })}
              <Pressable onClick={() => openInlineRuleAdd("quickRule")} style={{
                flex: "0 0 auto", padding: "10px 15px", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                background: P.soft, border: `1.5px dashed ${P.line}`, color: A.blue,
              }}>＋ {t("reductions.rulesAdmin.addBtn", "Ajouter une règle")}</Pressable>
            </div>
            {inlineRuleAddKey === "quickRule" && renderInlineRuleAddForm((newId) => {
              setArticleRule(quickRuleArt.artNr, newId, quickRuleArt.name, quickRuleArt.ean);
              closeQuickRule();
            })}
          </>
          );
        })()}
      </Sheet>

      <Sheet open={sheet === "redRules"} onClose={() => { setSheet(null); resetRedRulesNav(); }} P={P}>
        <SheetHead kicker={t("reductions.topbarTitle", "Réductions")} title={t("reductions.jRulesSheetTitle", "Règles de réduction")}
          onClose={() => { setSheet(null); resetRedRulesNav(); }} />

        {/* Écran de choix initial (2026-08-17, retour utilisateur explicite : "au lieu qu'il
            charge directement la liste des articles kühlung par défaut [...] d'abord donner
            le choix à l'utilisateur de choisir quel groupe d'article [...] et appliquer [une
            règle à] ce groupe" + "si l'utilisateur veut juste appliquer à des articles
            spécifiques il aura aussi ce choix de scanner ou d'entrer un numéro d'article").
            Plus de chargement direct de la liste Kühlung (380+ articles) — c'est ce qui
            rendait la gestion des règles quasi inatteignable (capture d'écran à l'appui). */}
        {redRulesMode === null && (
          <>
            <p style={{ margin: "0 0 16px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
              {t("reductions.rulesLandingIntro", "Choisis comment tu veux appliquer une règle de réduction.")}
            </p>
            <Pressable onClick={() => setRedRulesMode("group")} style={{ ...card, ...tile, padding: 16, marginBottom: 10, textAlign: "left" }}>
              <span style={{ fontSize: `calc(22px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>🗂️</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, display: "block" }}>{t("reductions.rulesLandingGroupTitle", "Appliquer à un groupe d'articles")}</strong>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("reductions.rulesLandingGroupDesc", "Change la règle par défaut de tout un rayon (Kühlung, Magasin, Trocken...).")}</span>
              </div>
              <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: P.muted }}>›</span>
            </Pressable>
            <Pressable onClick={() => setRedRulesMode("article")} style={{ ...card, ...tile, padding: 16, marginBottom: 10, textAlign: "left" }}>
              <span style={{ fontSize: `calc(22px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>🔍</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, display: "block" }}>{t("reductions.rulesLandingArticleTitle", "Appliquer à un article spécifique")}</strong>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("reductions.rulesLandingArticleDesc", "Cherche, scanne ou tape un numéro d'article — quel que soit son groupe.")}</span>
              </div>
              <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: P.muted }}>›</span>
            </Pressable>
            {isNexoraAdmin && (
              <Pressable onClick={() => setRedRulesMode("admin")} style={{
                width: "100%", textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                background: P.soft, color: P.ink, marginTop: 4,
              }}>⚙️ {t("reductions.rulesAdmin.sectionTitle", "Gérer les règles disponibles")}</Pressable>
            )}
          </>
        )}

        {/* Mode Groupe — édite la règle PAR DÉFAUT d'un rayon entier (GET/PUT
            /api/reductions/source-defaults), distinct de la dérogation par article. */}
        {redRulesMode === "group" && (
          <>
            <Pressable onClick={() => { setRedRulesGroup(null); setRedRulesMode(null); }} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14, color: A.blue, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))` }}>
              ‹ {t("common.back", "Retour")}
            </Pressable>
            {redRulesGroup === null ? (
              <>
                <p style={{ margin: "0 0 12px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
                  {t("reductions.rulesGroupPickIntro", "Choisis le groupe d'articles dont tu veux changer la règle par défaut.")}
                </p>
                {REDUCTION_CATEGORIES.map((cat) => (
                  <Pressable key={cat.id} onClick={() => setRedRulesGroup(cat.id)} style={{ ...card, ...tile, padding: 14, marginBottom: 8, textAlign: "left" }}>
                    <span style={{ width: 34, height: 34, borderRadius: 12, background: `${cat.color}22`, display: "grid", placeItems: "center", fontSize: `calc(16px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>{cat.emoji}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{cat.label}</strong>
                      <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{reductionRuleLabel(sourceDefaults[cat.id] || "J3_OPEN")}</span>
                    </div>
                    <span style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted }}>›</span>
                  </Pressable>
                ))}
              </>
            ) : (() => {
              const cat = REDUCTION_CATEGORIES.find((c) => c.id === redRulesGroup);
              const currentRule = sourceDefaults[redRulesGroup] || "J3_OPEN";
              return (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                    <span style={{ width: 38, height: 38, borderRadius: 12, background: `${cat?.color || A.blue}22`, display: "grid", placeItems: "center", fontSize: `calc(18px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>{cat?.emoji}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`, display: "block" }}>{cat?.label}</strong>
                      <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("reductions.currentRulePrefix", "règle actuelle :")} {reductionRuleLabel(currentRule)}</span>
                    </div>
                    <Pressable onClick={() => setRedRulesGroup(null)} aria-label={t("common.close", "Fermer")} style={{
                      width: 28, height: 28, borderRadius: 99, background: P.soft, color: P.ink,
                      display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`,
                    }}>✕</Pressable>
                  </div>
                  {isNexoraAdmin ? (
                    <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", paddingBottom: 2 }}>
                      {ruleDefinitions.map((d) => (
                        <Pressable key={d.id} onClick={() => saveSourceDefault(redRulesGroup, d.id)} style={{
                          flex: "0 0 auto", padding: "10px 15px", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                          background: currentRule === d.id ? A.blue : P.soft, color: currentRule === d.id ? "#fff" : P.ink,
                          border: `1.5px solid ${currentRule === d.id ? A.blue : P.line}`,
                        }}>{d.label}</Pressable>
                      ))}
                    </div>
                  ) : (
                    <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("reductions.rulesGroupAdminOnlyHint", "Seul un administrateur réseau peut changer la règle d'un groupe entier.")}</p>
                  )}
                </>
              );
            })()}
          </>
        )}

        {/* Mode Article spécifique — recherche/scan par Art.Nr ou nom, catalogue COMPLET
            (articlesCatalog, toutes zones — "peu importe son groupe"), jamais un chargement
            de liste par défaut : rien ne s'affiche tant que rien n'est tapé/scanné. */}
        {redRulesMode === "article" && (
          <>
            <Pressable onClick={() => { setRuleArt(null); setRuleSearch(""); setRedRulesMode(null); }} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14, color: A.blue, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))` }}>
              ‹ {t("common.back", "Retour")}
            </Pressable>

            {ruleArt && (
              <div style={{ ...card, borderRadius: 18, padding: 14, marginBottom: 12, border: `1.5px solid ${A.blue}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 11 }}>
                  <span style={{ fontSize: `calc(17px * var(--nx-font-scale, 1))`}}>📦</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ruleArt.name}</strong>
                    <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>Art.Nr {ruleArt.artNr} · {t("reductions.currentRulePrefix", "règle actuelle :")} {reductionRuleLabel(redRuleFor(ruleArt.artNr))}{!ruleOverrides[ruleArt.artNr] ? ` (${t("reductions.autoSuffix", "automatique")})` : ""}</span>
                  </div>
                  <Pressable onClick={() => setRuleArt(null)} aria-label={t("common.close", "Fermer")} style={{
                    width: 28, height: 28, borderRadius: 99, background: P.soft, color: P.ink,
                    display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`,
                  }}>✕</Pressable>
                </div>
                {/* La roue : toutes les règles disponibles (liste gérable, voir ruleDefinitions
                    — 2026-08-17) + "Automatique" pour retirer toute dérogation et revenir à la
                    règle par défaut de la source de l'article. */}
                <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", paddingBottom: 2 }}>
                  {["AUTO", ...ruleDefinitions.map((d) => d.id)].map((rule) => {
                    const sel = rule === "AUTO" ? !ruleOverrides[ruleArt.artNr] : ruleOverrides[ruleArt.artNr] === rule;
                    const label = rule === "AUTO" ? t("reductions.autoLabel", "Automatique") : reductionRuleLabel(rule);
                    return (
                      <Pressable key={rule} onClick={() => setArticleRule(ruleArt.artNr, rule)} style={{
                        flex: "0 0 auto", padding: "10px 15px", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                        background: sel ? A.blue : P.soft, color: sel ? "#fff" : P.ink,
                        border: `1.5px solid ${sel ? A.blue : P.line}`,
                      }}>{label}</Pressable>
                    );
                  })}
                  <Pressable onClick={() => openInlineRuleAdd("ruleArt")} style={{
                    flex: "0 0 auto", padding: "10px 15px", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                    background: P.soft, border: `1.5px dashed ${P.line}`, color: A.blue,
                  }}>＋ {t("reductions.rulesAdmin.addBtn", "Ajouter une règle")}</Pressable>
                </div>
                {inlineRuleAddKey === "ruleArt" && renderInlineRuleAddForm((newId) => setArticleRule(ruleArt.artNr, newId))}
              </div>
            )}

            {Object.keys(ruleOverrides).length > 0 && !ruleArt && (
              <>
                <strong style={{ display: "block", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 8px", textTransform: "uppercase", letterSpacing: ".08em" }}>{t("reductions.activeExceptionsTitle", "Exceptions actives")}</strong>
                {/* ruleOverrides est partagé avec les cartes Aktion EAN-only (⚙️, retour
                    utilisateur explicite 2026-08-21) — `key` n'est donc pas toujours un
                    vrai Art.Nr catalogue. "Modifier" masqué quand `art` est introuvable
                    (l'édition catalogue ne s'applique pas à ces articles ; ⚙️ sur leur
                    propre carte reste le seul point d'édition), jamais un bouton mort. */}
                {Object.entries(ruleOverrides).map(([key, rule]) => {
                  const art = articlesCatalog.find((a) => a.artNr === key);
                  return (
                    <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 12px", borderRadius: 16, background: "rgba(54,79,231,.08)", border: "1.5px solid rgba(54,79,231,.2)", marginBottom: 6 }}>
                      <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>⚙️</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{art ? art.name : `${t("reductions.eanPrefix", "EAN")} ${key}`}</strong>
                        <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>{reductionRuleLabel(rule)}</span>
                      </div>
                      {art && (
                        <Pressable onClick={() => setRuleArt(art)} style={{
                          fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.blue, background: "rgba(54,79,231,.12)", borderRadius: 99, padding: "7px 11px",
                        }}>{t("common.edit", "Modifier")}</Pressable>
                      )}
                      <Pressable onClick={() => setArticleRule(key, "AUTO")} aria-label={t("reductions.removeExceptionAria", "Retirer l'exception")} style={{
                        width: 28, height: 28, borderRadius: 99, background: "rgba(239,68,68,.13)", color: A.red,
                        display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`,
                      }}>✕</Pressable>
                    </div>
                  );
                })}
                <div style={{ height: 8 }} />
              </>
            )}

            {!ruleArt && (
              <>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <input style={{ ...inputStyle, flex: 1 }} placeholder={t("reductions.searchArticleToConfigurePlaceholder", "Rechercher un article à configurer...")}
                    value={ruleSearch} onChange={(e) => setRuleSearch(e.target.value)} />
                  <Pressable onClick={() => doScan("redRule")} aria-label={t("reductions.scanArticleAria", "Scanner un article")} style={{
                    width: 46, height: 46, borderRadius: 14, background: P.soft, color: P.ink, display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                  }}>📷</Pressable>
                </div>
                {ruleSearch.trim() === "" ? (
                  <p style={{ margin: 0, fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("reductions.searchArticleHint", "Tape un nom ou un numéro d'article, ou scanne-le directement.")}</p>
                ) : (
                  articlesCatalog.filter((a) => {
                    const q = ruleSearch.trim().toLowerCase();
                    return (a.name || "").toLowerCase().includes(q) || (a.artNr || "").includes(q);
                  }).slice(0, 40).map((a) => {
                    const hasOverride = Boolean(ruleOverrides[a.artNr]);
                    return (
                      <Pressable key={a.artNr} onClick={() => setRuleArt(a)} style={{
                        display: "flex", alignItems: "center", gap: 11, width: "100%", boxSizing: "border-box",
                        padding: "11px 12px", borderRadius: 16, background: P.soft, marginBottom: 6, color: P.ink,
                        border: ruleArt?.artNr === a.artNr ? `1.5px solid ${A.blue}` : "1.5px solid transparent",
                      }}>
                        <span style={{ fontSize: `calc(14px * var(--nx-font-scale, 1))`}}>{hasOverride ? "⚙️" : "📦"}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</strong>
                          <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>Art.Nr {a.artNr}</span>
                        </div>
                        <span style={{
                          fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "4px 10px",
                          background: hasOverride ? A.blue : P.soft,
                          color: hasOverride ? "#fff" : P.muted,
                        }}>{reductionRuleLabel(redRuleFor(a.artNr))}</span>
                      </Pressable>
                    );
                  })
                )}
              </>
            )}
          </>
        )}

        {/* Gestion des règles disponibles — NEXORA_ADMIN uniquement (2026-08-17, retour
            utilisateur explicite : "plusieurs choix au cas où les règles changeraient dans
            l'entreprise"). Décision structurelle d'entreprise, jamais une permission
            dynamique assignable à n'importe quel rôle (même logique que Preisänderung) —
            gate déjà appliqué côté serveur sur les 3 routes. Déplacée hors de la liste
            d'articles (retour utilisateur, capture à l'appui : "il est complètement en bas
            de la liste kühlung parce que la liste charge par défaut") — accès direct depuis
            l'écran de choix initial, plus jamais après un scroll interminable. */}
        {redRulesMode === "admin" && isNexoraAdmin && (
          <>
            <Pressable onClick={() => setRedRulesMode(null)} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14, color: A.blue, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))` }}>
              ‹ {t("common.back", "Retour")}
            </Pressable>
            <strong style={{ display: "block", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 10px", textTransform: "uppercase", letterSpacing: ".08em" }}>{t("reductions.rulesAdmin.sectionTitle", "Gérer les règles disponibles")}</strong>
            {ruleDefinitions.map((d) => (
              <div key={d.id} style={{ ...card, borderRadius: 16, padding: 12, marginBottom: 8, flexDirection: "column", alignItems: "stretch" }}>
                {ruleDefEditingId === d.id ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <input style={inputStyle} placeholder={t("reductions.rulesAdmin.labelPlaceholder", "Libellé")} value={ruleDefForm.label} onChange={(e) => setRuleDefForm({ ...ruleDefForm, label: e.target.value })} />
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input style={{ ...inputStyle, flex: 1 }} type="number" min="1" inputMode="numeric" placeholder="J-X" value={ruleDefForm.days} onChange={(e) => setRuleDefForm({ ...ruleDefForm, days: e.target.value })} />
                      <Pressable onClick={() => setRuleDefForm({ ...ruleDefForm, calendar: !ruleDefForm.calendar })} style={{
                        flex: "0 0 auto", padding: "10px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                        background: ruleDefForm.calendar ? A.blue : P.soft, color: ruleDefForm.calendar ? "#fff" : P.ink,
                      }}>{ruleDefForm.calendar ? t("reductions.rulesAdmin.calendarDays", "Calendaires") : t("reductions.rulesAdmin.openDays", "Ouvrés")}</Pressable>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Pressable onClick={cancelRuleDefForm} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.muted }}>{t("common.cancel", "Annuler")}</Pressable>
                      <Pressable onClick={submitRuleDefForm} style={{ flex: 1, ...btnSolid(A.blue), padding: "10px 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))` }}>{t("common.save", "Enregistrer")}</Pressable>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block" }}>{d.label}</strong>
                      <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted }}>J-{d.days} · {d.calendar ? t("reductions.rulesAdmin.calendarDays", "Calendaires") : t("reductions.rulesAdmin.openDays", "Ouvrés")}{d.isSeed ? ` · ${t("reductions.rulesAdmin.protectedBadge", "protégée")}` : ""}</span>
                    </div>
                    <Pressable onClick={() => startEditRuleDef(d)} style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.blue, background: "rgba(54,79,231,.12)", borderRadius: 99, padding: "7px 11px" }}>{t("common.edit", "Modifier")}</Pressable>
                    {!d.isSeed && (
                      <Pressable onClick={() => deleteRuleDef(d)} aria-label={t("common.delete", "Supprimer")} style={{
                        width: 28, height: 28, borderRadius: 99, background: "rgba(239,68,68,.13)", color: A.red,
                        display: "grid", placeItems: "center", fontSize: `calc(11px * var(--nx-font-scale, 1))`,
                      }}>✕</Pressable>
                    )}
                  </div>
                )}
              </div>
            ))}
            {ruleDefEditingId === null && (
              addingRuleDef ? (
                <div style={{ ...card, borderRadius: 16, padding: 12, marginBottom: 8, flexDirection: "column", alignItems: "stretch" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <input style={inputStyle} placeholder={t("reductions.rulesAdmin.labelPlaceholder", "Libellé")} value={ruleDefForm.label} onChange={(e) => setRuleDefForm({ ...ruleDefForm, label: e.target.value })} />
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input style={{ ...inputStyle, flex: 1 }} type="number" min="1" inputMode="numeric" placeholder="J-X" value={ruleDefForm.days} onChange={(e) => setRuleDefForm({ ...ruleDefForm, days: e.target.value })} />
                      <Pressable onClick={() => setRuleDefForm({ ...ruleDefForm, calendar: !ruleDefForm.calendar })} style={{
                        flex: "0 0 auto", padding: "10px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(11.5px * var(--nx-font-scale, 1))`,
                        background: ruleDefForm.calendar ? A.blue : P.soft, color: ruleDefForm.calendar ? "#fff" : P.ink,
                      }}>{ruleDefForm.calendar ? t("reductions.rulesAdmin.calendarDays", "Calendaires") : t("reductions.rulesAdmin.openDays", "Ouvrés")}</Pressable>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Pressable onClick={cancelRuleDefForm} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 99, fontWeight: 700, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, background: P.soft, color: P.muted }}>{t("common.cancel", "Annuler")}</Pressable>
                      <Pressable onClick={submitRuleDefForm} style={{ flex: 1, ...btnSolid(A.blue), padding: "10px 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))` }}>{t("reductions.rulesAdmin.createBtn", "Créer la règle")}</Pressable>
                    </div>
                  </div>
                </div>
              ) : (
                <Pressable onClick={() => { setRuleDefForm({ ...emptyRuleDefForm }); setAddingRuleDef(true); }} style={{
                  width: "100%", textAlign: "center", padding: "11px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: P.soft, border: `1.5px dashed ${P.line}`, color: A.blue,
                }}>＋ {t("reductions.rulesAdmin.addBtn", "Ajouter une règle")}</Pressable>
              )
            )}
          </>
        )}
      </Sheet>

      <Sheet open={sheet === "convertLabelTemplate"} onClose={() => setSheet(null)} P={P}>
        <SheetHead kicker={t("labels.convert.kicker", "Fabrication d'étiquettes")} title={t("labels.convert.title", "Convertir vers…")} onClose={() => setSheet(null)} />
        <p style={{ margin: "0 0 12px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
          {t("labels.convert.desc", "Les données déjà saisies sont conservées — seul l'affichage change.")}
        </p>
        {LABEL_TEMPLATES.filter((tpl) => tpl.id !== labelActiveTemplateId).map((tpl) => {
          const name = t(`labels.templateName.${tpl.id}`, tpl.label);
          return (
            <Pressable key={tpl.id} onClick={() => convertLabelToTemplate(tpl.id)} style={{
              display: "flex", alignItems: "center", gap: 11, width: "100%", boxSizing: "border-box",
              padding: "12px 13px", borderRadius: 16, background: P.soft, marginBottom: 6, color: P.ink,
            }}>
              <span style={{ width: 34, height: 34, flexShrink: 0, borderRadius: 11, background: tpl.swatch, border: `1.5px solid ${P.line}` }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</strong>
                <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, color: P.muted, fontVariantNumeric: "tabular-nums" }}>{tpl.format.widthMm} × {tpl.format.heightMm} mm</span>
              </div>
              <span style={{ color: P.muted, fontSize: `calc(17px * var(--nx-font-scale, 1))`, fontWeight: 300 }}>›</span>
            </Pressable>
          );
        })}
      </Sheet>

      <Sheet open={sheet === "linkCode"} onClose={() => { setSheet(null); setPendingCode(""); }} P={P}>
        <SheetHead kicker={t("scanner.unknownCodeKickerTemplate", { code: pendingCode }, `Code inconnu : ${pendingCode}`)} title={t("controlRun.chooseMatchingArticle", "Choisis l'article correspondant")}
          onClose={() => { setSheet(null); setPendingCode(""); }} />
        <p style={{ margin: "0 0 12px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
          Ce code EAN n'est associé à aucun article. Associe-le une fois — au prochain scan, l'article sera reconnu directement.
        </p>
        <input style={{ ...inputStyle, marginBottom: 12 }} placeholder={t("controlRun.searchPlaceholder", "Rechercher Art.Nr ou nom...")}
          value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)} />
        {articles.filter((a) => {
          const q = linkSearch.trim().toLowerCase();
          return !q || a.name.toLowerCase().includes(q) || a.artNr.includes(q);
        }).map((a) => {
          const already = articleCodesFor(a).length > 0;
          return (
            <Pressable key={a.artNr} onClick={() => linkPendingCode(a)} style={{
              display: "flex", alignItems: "center", gap: 11, width: "100%", boxSizing: "border-box",
              padding: "12px 13px", borderRadius: 16, background: P.soft, marginBottom: 6, color: P.ink,
            }}>
              <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>{already ? "🔗" : "📦"}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</strong>
                <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>Art.Nr {a.artNr} · Position {a.position}{already ? " · a déjà un code" : ""}</span>
              </div>
              <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.blue }}>Associer ›</span>
            </Pressable>
          );
        })}
      </Sheet>

      <Sheet open={sheet === "delMissing"} onClose={() => setSheet(null)} P={P}>
        <SheetHead kicker={t("control.kuhlung.deliveryTag", "Livraison")} title={t("control.itemsNotReceivedSheetTitle", "Articles non reçus")} onClose={() => setSheet(null)} />
        {delMissing.length === 0 && (
          <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{t("control.allReceivedText", "Tout est réceptionné — belle livraison ✅")}</p>
        )}
        {delMissing.map((a) => {
          const linkedCode = scanArticleEan(a.artNr);
          return (
          <Pressable key={a.artNr} onClick={() => { selectDelArt(a); setSheet(null); }} style={{
            display: "flex", alignItems: "center", gap: 11, width: "100%", boxSizing: "border-box",
            padding: "12px 13px", borderRadius: 16, background: P.soft, marginBottom: 6, color: P.ink,
          }}>
            <span style={{ fontSize: `calc(15px * var(--nx-font-scale, 1))`}}>📦</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</strong>
              <span style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>Art.Nr {a.artNr} · Position {a.position}</span>
            </div>
            {!linkedCode && (
              <Pressable onClick={(e) => { e.stopPropagation(); openScanLink(a, false); }} style={{
                fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 800, borderRadius: 99, padding: "5px 10px", whiteSpace: "nowrap",
                color: P.muted, background: P.card, border: `1.5px solid ${P.line}`,
              }}>{t("delivery.linkArticleBtn", "Lier")}</Pressable>
            )}
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.green }}>{t("control.receiveBtn", "Réceptionner ›")}</span>
          </Pressable>
          );
        })}
      </Sheet>

      <Sheet open={sheet === "storeHistory"} onClose={() => setSheet(null)} P={P}>
        <SheetHead kicker={t("control.storeTag", "Magasin")} title={t("controlStore.historyTitleFull", "Historique complet")} onClose={() => setSheet(null)} />
        {/* redStoreRecords (pas storeRecords) — retour utilisateur explicite (2026-08-20) :
            storeRecords est volontairement limité à controlDate=aujourd'hui (voir sa
            déclaration plus haut, "Saisies du jour" sous le formulaire), donc un article
            promotionnel saisi la veille disparaissait ici dès le changement de jour calendaire,
            alors que le titre "Historique complet" promet justement le contraire. redStoreRecords
            (déjà utilisé par Réductions, aucun filtre de date, includeExpiredRemoved=1) est la
            bonne source : reste visible jusqu'à sa réduction, ou jusqu'à apparaître dans l'onglet
            Expirées de Réductions si expiré sans avoir été réduit. */}
        {redStoreRecords.length === 0 && storeTrash.length === 0 && (
          <p style={{ textAlign: "center", color: P.muted, fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{t("controlStore.historyEmptyText", "Aucune saisie pour l'instant.")}</p>
        )}
        {redStoreRecords.map((r) => (
          <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 11, padding: "12px 13px", borderRadius: 16, marginBottom: 7, color: P.ink, ...urgRow(urgency(r.mhd)) }}>
            <span style={{ width: 9, height: 9, borderRadius: 99, background: urgColor[urgency(r.mhd)] }} />
            <div onClick={() => openArticleCard(r, "storeHistory")} style={{ flex: 1, cursor: "pointer" }}>
              <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>{r.name}</strong>
              <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>DLC {fmtFR(r.mhd)} · saisi le {fmtFR(r.controlDate)}</div>
              {/* Règle visible directement sur la carte (retour utilisateur explicite
                  2026-08-21), même patron que "Saisies du jour" (ligne ~18590). */}
              <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: ruleOverrides[r.artNr || r.ean] ? A.blue : P.muted, marginTop: 1 }}>
                ⚙️ {reductionRuleLabel(redRuleFor(r.artNr || r.ean, r.source))}
              </div>
            </div>
            {/* Même bouton ⚙️ que sur les cartes "Saisies du jour" (ligne ~18586) — retour
                utilisateur explicite (2026-08-21) : un article créé sans règle définie doit
                pouvoir en recevoir une plus tard depuis sa carte, y compris depuis Historique
                complet (seul endroit d'où un article saisi la veille reste atteignable, voir
                le commentaire redStoreRecords ci-dessus). Même openQuickRule/ruleOverrides
                que partout ailleurs, jamais une logique séparée. EAN en repli (même jour) —
                un article Magasin en saisie libre n'a souvent qu'un EAN, jamais d'Art.Nr.
                returnTo:"storeHistory" (retour utilisateur explicite 2026-08-21 : "faudrait
                pas que l'écran historique disparaisse") — sheet étant un état exclusif (pas
                une pile), quickRule écrasait "storeHistory" sans jamais y revenir ; voir
                closeQuickRule ci-dessous, seul point qui referme réellement le sélecteur. */}
            {((r.artNr && r.artNr !== "—") || r.ean) && (
              <Pressable onClick={(e) => { e.stopPropagation(); openQuickRule(r.artNr, r.name, r.ean, "storeHistory"); }} aria-label={t("reductions.ruleAria", "Règle de réduction")} style={{
                flex: "0 0 auto", width: 30, height: 30, borderRadius: 99, background: ruleOverrides[r.artNr || r.ean] ? "rgba(54,79,231,.15)" : P.soft, color: ruleOverrides[r.artNr || r.ean] ? A.blue : P.muted,
                display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
              }}>⚙️</Pressable>
            )}
            {/* Retour utilisateur explicite (2026-08-20) : pouvoir supprimer directement depuis
                Historique complet, pas seulement depuis Réductions — trashStore (pas
                dropExpired) pour atterrir dans LA Corbeille de cette même feuille
                (storeTrash/restoreStore juste en dessous), jamais dans celle de Réductions
                qui n'est pas visible d'ici. */}
            <Pressable onClick={(e) => { e.stopPropagation(); trashStore(r.id); }} aria-label={t("common.delete", "Supprimer")} style={{
              flex: "0 0 auto", width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.1)",
              display: "grid", placeItems: "center", fontSize: `calc(13px * var(--nx-font-scale, 1))`,
            }}>🗑️</Pressable>
          </div>
        ))}
        {storeTrash.length > 0 && (
          <>
            <strong style={{ display: "block", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, margin: "12px 2px 8px", textTransform: "uppercase", letterSpacing: ".08em" }}>Corbeille</strong>
            {storeTrash.map((r) => (
              <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 11, padding: "12px 13px", borderRadius: 16, background: "rgba(239,68,68,.1)", marginBottom: 7, color: P.ink }}>
                <div onClick={() => openArticleCard(r, "storeHistory")} style={{ flex: 1, opacity: .75, cursor: "pointer" }}>
                  <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, textDecoration: "line-through" }}>{r.name}</strong>
                  <div style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted }}>DLC {fmtFR(r.mhd)}</div>
                </div>
                <Pressable onClick={() => restoreStore(r.id)} style={{
                  fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.blue, background: "rgba(54,79,231,.1)",
                  borderRadius: 99, padding: "7px 13px",
                }}>Restaurer</Pressable>
                <Pressable onClick={() => deleteStorePermanently(r.id)} style={{
                  fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.red, background: "rgba(239,68,68,.12)",
                  borderRadius: 99, padding: "7px 13px",
                }}>{t("reductions.deletePermanentlyBtn", "Supprimer définitivement")}</Pressable>
              </div>
            ))}
          </>
        )}
      </Sheet>

      {/* Lien de signature à distance — voir requestRemoteSignature plus haut. Affiché une
          seule fois à la création (le lien n'est jamais rejournalisé après coup, pas de
          "revoir mon dernier lien" volontairement — en redemander un nouveau si perdu, même
          esprit que le code de réinitialisation mot de passe qui n'est montré qu'une fois). */}
      <Sheet open={sheet === "remoteSignatureLink"} onClose={() => { setSheet(null); setSignatureRequestLink(null); }} P={P}>
        <SheetHead kicker={t("inventurmassnahme.remoteSig.kicker", "Signature à distance")} title={t("inventurmassnahme.remoteSig.linkTitle", "Lien créé")} onClose={() => { setSheet(null); setSignatureRequestLink(null); }} />
        {signatureRequestLink && (
          <>
            <p style={{ margin: "0 0 16px", fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
              {t("inventurmassnahme.remoteSig.linkSubtitleTemplate", { field: signatureRequestLink.fieldLabel },
                `Envoie ce lien à la personne concernée (${signatureRequestLink.fieldLabel}) — valable 24h, aucun compte Nexora nécessaire.`)}
            </p>
            <div style={{ ...card, padding: 14, marginBottom: 14, wordBreak: "break-all", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.ink, fontFamily: "monospace" }}>
              {signatureRequestLink.url}
            </div>
            {typeof navigator !== "undefined" && navigator.share && (
              <Pressable onClick={() => navigator.share({ title: t("inventurmassnahme.remoteSig.shareTitle", "Signature Nexora"), url: signatureRequestLink.url }).catch(() => {})} style={{ width: "100%", ...btnSolid(A.blue), padding: "15px 0", fontSize: `calc(15px * var(--nx-font-scale, 1))`, marginBottom: 10 }}>
                📤 {t("inventurmassnahme.remoteSig.shareBtn", "Partager le lien")}
              </Pressable>
            )}
            <Pressable onClick={() => {
              navigator.clipboard?.writeText(signatureRequestLink.url).then(() => showToast(t("toast.linkCopied", "✓ Lien copié"))).catch(() => {});
            }} style={{ width: "100%", textAlign: "center", borderRadius: 99, fontWeight: 800, background: P.soft, color: P.ink, border: `1.5px solid ${P.line}`, padding: "15px 0", fontSize: `calc(15px * var(--nx-font-scale, 1))`, marginBottom: 10 }}>
              📋 {t("inventurmassnahme.remoteSig.copyBtn", "Copier le lien")}
            </Pressable>
            <Pressable onClick={() => { setSheet(null); setSignatureRequestLink(null); }} style={{
              width: "100%", textAlign: "center", padding: "12px 0", fontWeight: 700, fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.muted,
            }}>{t("common.close", "Fermer")}</Pressable>
          </>
        )}
      </Sheet>

      {/* Fiche article — Réductions (toutes catégories) + Historique DLC Magasin, voir
          openArticleCard(). Toutes les infos réelles de l'enregistrement, jamais de champ
          inventé : les champs absents (EAN, photo, note) sont simplement omis plutôt
          qu'affichés vides. */}
      <Sheet open={sheet === "articleCard"} onClose={() => { setSheet(articleCardReturnSheet); setArticleCardRecord(null); setArticleCardReturnSheet(null); }} P={P}>
        {articleCardRecord && (() => {
          const r = articleCardRecord;
          const u = urgency(r.mhd);
          return (
            <>
              <SheetHead kicker={r.source === "STORE" ? "Magasin · Article promotionnel" : redSourceMeta(r.source).label} title={r.name || "Article"} onClose={() => { setSheet(articleCardReturnSheet); setArticleCardRecord(null); setArticleCardReturnSheet(null); }} />
              {r.photo && (
                <img src={r.photo} alt="" style={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 18, marginBottom: 14 }} />
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <span style={{ width: 9, height: 9, borderRadius: 99, background: urgColor[u] }} />
                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: urgColor[u] }}>{urgLabel[u]}</strong>
              </div>
              <div style={{ ...card, borderRadius: 18, padding: 16, display: "flex", flexDirection: "column", gap: 11 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>DLC</span>
                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{fmtFR(r.mhd)}</strong>
                </div>
                {r.artNr && r.artNr !== "—" && (
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>Art.Nr</span>
                    <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{r.artNr}</strong>
                  </div>
                )}
                {r.ean && (
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>EAN</span>
                    <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{r.ean}</strong>
                  </div>
                )}
                {r.quantity !== undefined && r.quantity !== "" && (
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlRun.quantityLabel", "Quantité")}</span>
                    <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{r.quantity}</strong>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlStore.provenanceLabel", "Provenance")}</span>
                  <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{r.source === "STORE" ? t("controlStore.storePromoLabel", "Magasin (promotionnel)") : redSourceMeta(r.source).label}</strong>
                </div>
                {r.employee && (
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlStore.enteredByLabel", "Saisi par")}</span>
                    <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{r.employee}</strong>
                  </div>
                )}
                {r.controlDate && (
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("controlStore.entryDateLabel", "Date de saisie")}</span>
                    <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{fmtFR(r.controlDate)}</strong>
                  </div>
                )}
                {r.note && (
                  <div>
                    <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", marginBottom: 3 }}>{t("controlRun.remarkLabel", "Remarque")}</span>
                    <p style={{ margin: 0, fontSize: `calc(13px * var(--nx-font-scale, 1))`, lineHeight: 1.4 }}>{r.note}</p>
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </Sheet>

      <Sheet open={sheet === "azlDay"} onClose={() => { setSheet(null); setAzlDayDetail(null); }} P={P}>
        <SheetHead
          kicker={azlDayDetail ? new Date(azlDayDetail.dateISO + "T00:00:00").toLocaleDateString("de-DE") : "—"}
          title={azlDayDetail?.emp?.displayName || t("krankmeldung.detail.defaultTitle", "Détail")}
          onClose={() => { setSheet(null); setAzlDayDetail(null); }}
        />
        {azlDayDetail && (
          <>
            {azlDayDetail.anomalyMessage && (
              <div style={{ padding: "11px 13px", borderRadius: 14, background: "rgba(239,68,68,.1)", color: A.red, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, marginBottom: 12 }}>
                {azlDayDetail.anomalyMessage}
              </div>
            )}
            {azlDayDetail.day?.overridden && (
              <div style={{ padding: "9px 13px", borderRadius: 14, background: `${A.orange}1a`, color: A.orange, fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, marginBottom: 12 }}>
                {t("arbeitszeitliste.detail.correctedNotice", "Case corrigée manuellement — valeurs différentes du planning brut.")}
              </div>
            )}
            {azlDayDetail.day?.absenceCode && (
              <span style={{ display: "inline-block", fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff", background: A.blue, borderRadius: 99, padding: "5px 12px", marginBottom: 10 }}>
                {{ K: "Krank", KK: "Krankheit Kind", U: "Urlaub", UU: "Unbezahlter Urlaub", SU: "Sonderurlaub", F: "Feiertag", LF: "Leistungsfrei (partiel)", V: "Vertretung", MS: "Mutterschutz", ERZ: "Erziehungsurlaub" }[azlDayDetail.day.absenceCode] || azlDayDetail.day.absenceCode}
              </span>
            )}

            {azlData?.document?.frozen || !azlDayEditForm ? (
              <>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                  {[
                    [t("arbeitszeitliste.detail.worked", "Heures réellement travaillées"), azlFmtMinutes(azlDayDetail.day?.workedMinutes || 0)],
                    [t("arbeitszeitliste.detail.lf", "Leistungsfrei"), azlFmtMinutes(azlDayDetail.day?.lfMinutes || 0)],
                    [t("arbeitszeitliste.detail.vertretung", "Vertretung"), azlFmtMinutes(azlDayDetail.day?.vertretungMinutes || 0)],
                    [t("arbeitszeitliste.detail.credited", "Heures créditées"), azlFmtMinutes((azlDayDetail.day?.workedMinutes || 0) + (azlDayDetail.day?.lfMinutes || 0) + (azlDayDetail.day?.vertretungMinutes || 0))],
                  ].map(([label, value]) => (
                    <span key={label} style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, background: P.soft, color: P.ink, borderRadius: 99, padding: "7px 12px" }}>{label} : {value}</span>
                  ))}
                </div>
                <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                  {azlData?.document?.frozen
                    ? t("arbeitszeitliste.detail.frozenNotice", "Document figé — corrections indisponibles (rouvrir le document pour corriger).")
                    : t("arbeitszeitliste.detail.source", "Source : Arbeitsplan / Planänderung")}
                </p>
              </>
            ) : (
              <>
                <Field label={t("arbeitszeitliste.detail.workedEdit", "Heures réellement travaillées (h)")}>
                  <input style={inputStyle} inputMode="decimal" placeholder="0" value={azlDayEditForm.workedHours}
                    onChange={(e) => setAzlDayEditForm({ ...azlDayEditForm, workedHours: e.target.value.replace(",", ".") })} />
                </Field>
                <Field label={t("arbeitszeitliste.detail.lfEdit", "Leistungsfrei (h)")}>
                  <input style={inputStyle} inputMode="decimal" placeholder="0" value={azlDayEditForm.lfHours}
                    onChange={(e) => setAzlDayEditForm({ ...azlDayEditForm, lfHours: e.target.value.replace(",", ".") })} />
                </Field>
                <Field label={t("arbeitszeitliste.detail.pauseEdit", "Pause (h)")}>
                  <input style={inputStyle} inputMode="decimal" placeholder="0" value={azlDayEditForm.pauseHours}
                    onChange={(e) => setAzlDayEditForm({ ...azlDayEditForm, pauseHours: e.target.value.replace(",", ".") })} />
                </Field>
                <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 6 }}>{t("arbeitszeitliste.detail.absenceEdit", "Code d'absence")}</span>
                <div className="nx-chips" style={{ display: "flex", gap: 7, overflowX: "auto", marginBottom: 14, paddingBottom: 2 }}>
                  {AZL_DAY_ABSENCE_OPTIONS.map((code) => {
                    const sel = azlDayEditForm.absenceCode === code;
                    return (
                      <Pressable key={code || "none"} onClick={() => setAzlDayEditForm({ ...azlDayEditForm, absenceCode: code })} style={{
                        flex: "0 0 auto", padding: "9px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                        background: sel ? A.teal : P.soft, color: sel ? "#fff" : P.ink,
                        border: `1.5px solid ${sel ? A.teal : P.line}`,
                      }}>{code || t("arbeitszeitliste.detail.absenceNone", "Aucun")}</Pressable>
                    );
                  })}
                </div>
                <Field label={t("arbeitszeitliste.detail.vertretungsgeldEdit", "Vertretungsgeld (notation manuelle, ex. V4)")}>
                  <input style={inputStyle} placeholder="V4" value={azlDayEditForm.vertretungsgeldNote}
                    onChange={(e) => setAzlDayEditForm({ ...azlDayEditForm, vertretungsgeldNote: e.target.value })} />
                </Field>
                <Field label={t("arbeitszeitliste.detail.reasonEdit", "Motif de la correction (facultatif)")}>
                  <input style={inputStyle} placeholder={t("arbeitszeitliste.detail.reasonPlaceholder", "Ex : erreur de saisie planning")} value={azlDayEditForm.reason}
                    onChange={(e) => setAzlDayEditForm({ ...azlDayEditForm, reason: e.target.value })} />
                </Field>
                <Pressable onClick={saveAzlDayOverride} style={{ width: "100%", marginTop: 4, ...btnSolid(A.navy), opacity: azlDaySaving ? .6 : 1 }}>
                  {azlDaySaving ? t("articles.saving", "Enregistrement...") : t("common.save", "Enregistrer")}
                </Pressable>
              </>
            )}
          </>
        )}
      </Sheet>

      <Sheet open={sheet === "azlAnomalies"} onClose={() => setSheet(null)} P={P}>
        <SheetHead kicker={t("arbeitszeitliste.anomalies.eyebrow", "Contrôle qualité")} title={t("arbeitszeitliste.anomalies.title", "Anomalies détectées")} onClose={() => setSheet(null)} />
        {azlAnomalyList.map((a) => (
          <Pressable key={a.id} onClick={() => { setSheet(null); openAzlAnomalyDetail(a); }} style={{
            display: "flex", alignItems: "flex-start", gap: 10, width: "100%", boxSizing: "border-box",
            padding: 13, borderRadius: 14, background: P.soft, marginBottom: 8, textAlign: "left",
            borderLeft: `4px solid ${{ critical: A.red, warning: A.orange, info: A.blue }[a.severity] || P.muted}`,
          }}>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, fontWeight: 900, textTransform: "uppercase", letterSpacing: ".05em", color: { critical: A.red, warning: A.orange, info: A.blue }[a.severity] || P.muted }}>
                {{ critical: t("arbeitszeitliste.anomalies.severity.critical", "Critique"), warning: t("arbeitszeitliste.anomalies.severity.warning", "Attention"), info: t("arbeitszeitliste.anomalies.severity.info", "Info") }[a.severity] || t("arbeitszeitliste.anomalies.severity.info", "Info")}
              </span>
              <p style={{ margin: "3px 0 0", fontSize: `calc(13px * var(--nx-font-scale, 1))`, color: P.ink }}>{a.message}</p>
            </div>
          </Pressable>
        ))}
      </Sheet>

      <Sheet open={sheet === "azlValidate"} onClose={() => setSheet(null)} P={P} maxH="80%">
        <SheetHead
          kicker={
            azlValidationRole === "branchManager" ? t("arbeitszeitliste.validation.roleBranchManager", "Filialleiter / FLA")
              : azlValidationRole === "areaManager" ? t("arbeitszeitliste.validation.roleAreaManager", "Bereichsleiter")
              : (azlData?.validation?.employees || []).find((e) => `employee:${e.employeeId}` === azlValidationRole)?.name || "—"
          }
          title={t("arbeitszeitliste.validation.title", "Validation")} onClose={() => setSheet(null)}
        />
        <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, marginBottom: 14 }}>{t("arbeitszeitliste.validation.hintTuMulti", "Relis les données ci-dessus avant de signer — une fois toutes les signatures réunies (chaque employé + les 2 responsables), le document est figé.")}</p>
        <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 6 }}>{t("arbeitszeitliste.validation.signLabel", "Signature manuscrite")}</span>
        <div ref={azlSignPadMountRef} />
        {azlSignError && <p style={{ color: A.red, fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700, margin: "10px 0 0" }}>{azlSignError}</p>}
        <Pressable onClick={submitAzlSignature} style={{ width: "100%", marginTop: 14, ...btnSolid(A.navy), opacity: azlSignReady && !azlSignBusy ? 1 : .5 }}>
          {azlSignBusy ? t("articles.saving", "Enregistrement...") : t("arbeitszeitliste.validation.submit", "Confirmer et signer")}
        </Pressable>
      </Sheet>

      <Sheet open={sheet === "azlExternal"} onClose={() => setSheet(null)} P={P}>
        <SheetHead kicker={t("arbeitszeitliste.external.eyebrow", "Affectation temporaire")} title={t("arbeitszeitliste.external.title", "Employés externes")} onClose={() => setSheet(null)} />
        <Field label={t("arbeitszeitliste.external.searchLabel", "Employé")}>
          <input style={inputStyle} placeholder={t("arbeitszeitliste.external.searchPlaceholder", "Nom ou matricule…")} value={azlExternalSearch} onChange={(e) => scheduleAzlExternalSearch(e.target.value)} autoComplete="off" />
        </Field>
        {azlExternalResults.length > 0 && !azlExternalSelected && (
          <div style={{ marginTop: -6, marginBottom: 12 }}>
            {azlExternalResults.map((e) => (
              <Pressable key={e.employeeId} onClick={() => { setAzlExternalSelected(e); setAzlExternalSearch(e.name); setAzlExternalResults([]); }} style={{
                display: "block", width: "100%", textAlign: "left", padding: "9px 12px", borderRadius: 12, background: P.soft, marginBottom: 4,
              }}>
                <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{e.name}</strong>
                <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{e.role || ""} · {t("admin.scopeStoreTemplate", { id: e.homeStoreId }, `Filiale ${e.homeStoreId}`)}</div>
              </Pressable>
            ))}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label={t("arbeitszeitliste.external.startLabel", "Début")}><input type="date" style={dateTimeInputStyle} value={azlExternalForm.start} onChange={(e) => setAzlExternalForm((f) => ({ ...f, start: e.target.value }))} /></Field>
          <Field label={t("arbeitszeitliste.external.endLabel", "Fin (optionnel)")}><input type="date" style={dateTimeInputStyle} value={azlExternalForm.end} onChange={(e) => setAzlExternalForm((f) => ({ ...f, end: e.target.value }))} /></Field>
        </div>
        <Field label={t("arbeitszeitliste.external.reasonLabel", "Motif")}><input style={inputStyle} placeholder={t("arbeitszeitliste.external.reasonPlaceholder", "Renfort, formation…")} value={azlExternalForm.reason} onChange={(e) => setAzlExternalForm((f) => ({ ...f, reason: e.target.value }))} /></Field>
        {azlExternalError && <p style={{ color: A.red, fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{azlExternalError}</p>}
        <Pressable onClick={submitAzlExternal} style={{ width: "100%", ...btnSolid(A.blue), marginBottom: 18 }}>{t("arbeitszeitliste.external.submit", "Ajouter l'affectation")}</Pressable>

        <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, margin: "0 2px 9px" }}>{t("arbeitszeitliste.external.listTitle", "Affectations en cours")}</strong>
        {azlExternalList.length === 0 && <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px" }}>{t("arbeitszeitliste.external.empty", "Aucune affectation en cours.")}</p>}
        {azlExternalList.map((a) => (
          <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: 12, borderRadius: 14, background: P.soft, marginBottom: 7 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{a.employeeName}</strong>
              <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                {t("admin.scopeStoreTemplate", { id: a.homeBranchId }, `Filiale ${a.homeBranchId}`)} · {fmtFR(a.startDate)} — {a.endDate ? fmtFR(a.endDate) : t("arbeitszeitliste.external.ongoing", "en cours")}{a.reason ? ` · ${a.reason}` : ""}
              </div>
            </div>
            <Pressable onClick={() => deleteAzlExternal(a.id)} aria-label={t("common.delete", "Supprimer")} style={{ width: 30, height: 30, borderRadius: 99, background: "rgba(239,68,68,.13)", display: "grid", placeItems: "center", fontSize: `calc(12px * var(--nx-font-scale, 1))`, flex: "0 0 auto" }}>🗑️</Pressable>
          </div>
        ))}
      </Sheet>

      <Sheet open={sheet === "azlHistory"} onClose={() => setSheet(null)} P={P}>
        <SheetHead kicker={t("arbeitszeitliste.history.eyebrow", "Traçabilité")} title={t("arbeitszeitliste.history.title", "Historique des versions")} onClose={() => setSheet(null)} />
        {azlHistoryVersions.length === 0 && <p style={{ color: P.muted, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, margin: "0 2px 14px" }}>{t("arbeitszeitliste.history.empty", "Aucune version validée pour ce mois.")}</p>}
        {azlHistoryVersions.map((v) => (
          <div key={v.versionNumber} style={{ display: "flex", alignItems: "center", gap: 10, padding: 12, borderRadius: 14, background: P.soft, marginBottom: 7 }}>
            <div style={{ flex: 1 }}>
              <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`}}>{t("arbeitszeitliste.history.version", { n: v.versionNumber }, `Version ${v.versionNumber}`)}</strong>
              <div style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>
                {new Date(v.archivedAt || v.createdAt).toLocaleString("de-DE")}{v.pdfIntegrityHash ? ` — ${t("arbeitszeitliste.archive.hash", { hash: v.pdfIntegrityHash.slice(0, 12) }, `hash ${v.pdfIntegrityHash.slice(0, 12)}`)}` : ""}
              </div>
            </div>
            {v.hasArchivedPdf
              ? <Pressable onClick={() => downloadAzlVersion(v.versionNumber)} style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: A.blue, background: "rgba(54,79,231,.1)", borderRadius: 99, padding: "8px 13px", whiteSpace: "nowrap" }}>{t("planning.published.downloadBtn", "Télécharger")}</Pressable>
              : <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("arbeitszeitliste.history.noPdf", "PDF non disponible")}</span>}
          </div>
        ))}

        {canValidateAzl && azlData?.document?.status !== "draft" && (
          <>
            <strong style={{ display: "block", fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, margin: "16px 2px 9px" }}>{t("arbeitszeitliste.history.reopenTitle", "Rouvrir pour correction")}</strong>
            <p style={{ fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 10px" }}>{t("arbeitszeitliste.history.reopenHint", "Crée un nouveau cycle de validation ; la version actuelle reste conservée et consultable pour toujours.")}</p>
            <Field label={t("arbeitszeitliste.history.reasonLabel", "Motif (obligatoire)")}>
              <textarea rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder={t("arbeitszeitliste.history.reasonPlaceholder", "Ex. : erreur de saisie détectée après validation…")} value={azlReopenReason} onChange={(e) => setAzlReopenReason(e.target.value)} />
            </Field>
            {azlReopenError && <p style={{ color: A.red, fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 700 }}>{azlReopenError}</p>}
            <Pressable onClick={submitAzlReopen} style={{
              width: "100%", textAlign: "center", padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(14px * var(--nx-font-scale, 1))`,
              background: "rgba(239,68,68,.12)", color: A.red, opacity: azlReopenBusy ? .6 : 1,
            }}>{azlReopenBusy ? t("arbeitszeitliste.history.reopeningBtn", "Réouverture...") : t("arbeitszeitliste.history.reopenSubmit", "Rouvrir ce document")}</Pressable>
          </>
        )}
      </Sheet>

      <Sheet open={sheet === "inventurNewCategory"} onClose={() => { setSheet(null); setInventurNewCategoryForm(null); }} P={P} maxH="80%">
        <SheetHead kicker="Inventur" title={t("inventur.newCategoryTitle", "Nouvelle catégorie")} onClose={() => setSheet(null)} />
        {inventurNewCategoryForm && (
          <>
            <Field label={t("importantInfo.manage.colName", "Name")}>
              <input style={inputStyle} value={inventurNewCategoryForm.name} onChange={(e) => setInventurNewCategoryForm({ ...inventurNewCategoryForm, name: e.target.value })} />
            </Field>
            <Field label={t("inventur.descriptionOptionalLabel", "Description (facultatif)")}>
              <input style={inputStyle} value={inventurNewCategoryForm.description || ""} onChange={(e) => setInventurNewCategoryForm({ ...inventurNewCategoryForm, description: e.target.value })} />
            </Field>
            <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, fontWeight: 800, color: P.muted, display: "block", marginBottom: 8 }}>{t("importantInfo.editor.fieldType", "Type")}</span>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {[{ id: "standard", l: t("inventur.categoryTypeStandard", "Standard") }, { id: "buecher", l: t("inventur.categoryTypeBooks", "Livres") }].map((ct) => (
                <Pressable key={ct.id} onClick={() => setInventurNewCategoryForm({ ...inventurNewCategoryForm, entryType: ct.id })} style={{
                  flex: 1, textAlign: "center", padding: "11px 0", borderRadius: 14, fontWeight: 800, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`,
                  background: inventurNewCategoryForm.entryType === ct.id ? A.brown : P.soft, color: inventurNewCategoryForm.entryType === ct.id ? "#fff" : P.ink,
                }}>{ct.l}</Pressable>
              ))}
            </div>
            {inventurActiveInventory && (
              <Pressable onClick={() => setInventurNewCategoryForm({ ...inventurNewCategoryForm, scope: inventurNewCategoryForm.scope === "shared" ? "inventory_only" : "shared" })} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 4px", marginBottom: 16 }}>
                <span style={{
                  width: 22, height: 22, borderRadius: 7, border: `2px solid ${inventurNewCategoryForm.scope === "shared" ? A.brown : P.line}`,
                  background: inventurNewCategoryForm.scope === "shared" ? A.brown : "transparent", display: "grid", placeItems: "center", color: "#fff", fontSize: `calc(13px * var(--nx-font-scale, 1))`, flex: "0 0 auto",
                }}>{inventurNewCategoryForm.scope === "shared" ? "✓" : ""}</span>
                <span style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`}}>{t("inventur.saveForStoreLabel", "Enregistrer pour la filiale (réutilisable)")}</span>
              </Pressable>
            )}
            <Pressable onClick={createInventurCategoryTemplate} style={{ width: "100%", ...btnSolid(A.brown) }}>{t("inventur.createCategoryBtn", "Créer la catégorie")}</Pressable>
          </>
        )}
      </Sheet>

      <Sheet open={sheet === "inventurAssign"} onClose={() => { setSheet(null); setInventurAssignForm(null); }} P={P} maxH="80%">
        <SheetHead kicker="Inventur" title={t("inventur.assignTaskTitle", "Attribuer la tâche")} onClose={() => setSheet(null)} />
        {inventurAssignForm && (
          <>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 16 }}>
              {inventurTeamRoster.map((u) => {
                const sel = inventurAssignForm.assignedUserIds.includes(u.employeeId);
                return (
                  <Pressable key={u.employeeId} onClick={() => {
                    const ids = sel ? inventurAssignForm.assignedUserIds.filter((id) => id !== u.employeeId) : [...inventurAssignForm.assignedUserIds, u.employeeId];
                    const names = sel ? inventurAssignForm.assignedUserNames.filter((n) => n !== u.name) : [...inventurAssignForm.assignedUserNames, u.name];
                    setInventurAssignForm({ ...inventurAssignForm, assignedUserIds: ids, assignedUserNames: names });
                  }} style={{
                    padding: "8px 13px", borderRadius: 99, fontWeight: 800, fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                    background: sel ? A.brown : P.soft, color: sel ? "#fff" : P.ink, border: `1.5px solid ${sel ? A.brown : P.line}`,
                  }}>{u.name}</Pressable>
                );
              })}
            </div>
            <Pressable onClick={saveInventurAssignment} style={{ width: "100%", ...btnSolid(A.brown) }}>{t("common.save", "Enregistrer")}</Pressable>
          </>
        )}
      </Sheet>

      <Sheet open={sheet === "inventurEditEntry"} onClose={() => { setSheet(null); setInventurEditEntry(null); }} P={P} maxH="84%">
        <SheetHead kicker="Inventur" title={t("inventur.editLineTitle", "Modifier la ligne")} onClose={() => setSheet(null)} />
        {inventurEditEntry && (() => {
          const cat = activeInventurCategory();
          const isBooks = cat?.entryType === "buecher";
          return (
            <>
              <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 14px" }}>{inventurEditEntry.artNr}{inventurEditEntry.name ? " · " + inventurEditEntry.name : ""}</p>
              <Field label={t("inventur.designationOptionalLabel", "Désignation (facultatif)")}>
                <input style={inputStyle} value={inventurEditEntry.name || ""} onChange={(e) => setInventurEditEntry({ ...inventurEditEntry, name: e.target.value })} />
              </Field>
              {isBooks ? (
                <>
                  <Field label={t("inventur.priceLabel", "Prix (€)")}><input style={inputStyle} inputMode="decimal" value={inventurEditEntry.price ?? ""} onChange={(e) => setInventurEditEntry({ ...inventurEditEntry, price: e.target.value })} /></Field>
                  <Field label={t("inventur.countLabel", "Nombre")}><QtyStepper value={inventurEditEntry.totalQty ?? ""} onChange={(v) => setInventurEditEntry({ ...inventurEditEntry, totalQty: v })} /></Field>
                </>
              ) : inventurEditEntry.entryMethod === "cartons" ? (
                <>
                  <Field label={t("inventur.methodCartons", "Cartons")}><QtyStepper value={inventurEditEntry.cartons ?? ""} onChange={(v) => setInventurEditEntry({ ...inventurEditEntry, cartons: v })} /></Field>
                  <Field label={t("inventur.piecesPerCartonLabel", "Pièces par carton")}><QtyStepper value={inventurEditEntry.piecesPerCarton ?? ""} onChange={(v) => setInventurEditEntry({ ...inventurEditEntry, piecesPerCarton: v })} /></Field>
                  <Field label={t("inventur.singlePiecesLabel", "Pièces à l'unité")}><QtyStepper value={inventurEditEntry.singlePieces ?? ""} onChange={(v) => setInventurEditEntry({ ...inventurEditEntry, singlePieces: v })} /></Field>
                </>
              ) : (
                <Field label={t("inventur.totalQtyLabel", "Quantité totale")}><QtyStepper value={inventurEditEntry.totalQty ?? ""} onChange={(v) => setInventurEditEntry({ ...inventurEditEntry, totalQty: v })} /></Field>
              )}
              <Field label={t("inventur.noteOptionalLabel", "Remarque (facultatif)")}>
                <input style={inputStyle} value={inventurEditEntry.note || ""} onChange={(e) => setInventurEditEntry({ ...inventurEditEntry, note: e.target.value })} />
              </Field>
              <Pressable onClick={saveInventurEntryEdit} style={{ width: "100%", ...btnSolid(A.brown) }}>{t("common.save", "Enregistrer")}</Pressable>
            </>
          );
        })()}
      </Sheet>

      <Sheet open={!!inventurDuplicateState} onClose={() => setInventurDuplicateState(null)} P={P} maxH="60%">
        <SheetHead kicker="Inventur" title={t("inventur.duplicateTitle", "Art.Nr déjà saisi")} onClose={() => setInventurDuplicateState(null)} />
        {inventurDuplicateState && (
          <>
            <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, margin: "0 2px 16px" }}>
              {t("inventur.duplicateBodyTemplate", {
                artNr: inventurDuplicateState.existingEntry.artNr,
                qty: inventurDuplicateState.existingEntry.totalQty,
                createdBy: inventurDuplicateState.existingEntry.createdByName ? t("inventur.duplicateCreatedBySuffix", { name: inventurDuplicateState.existingEntry.createdByName }, ` — par ${inventurDuplicateState.existingEntry.createdByName}`) : "",
              }, `${inventurDuplicateState.existingEntry.artNr} a déjà été saisi (quantité ${inventurDuplicateState.existingEntry.totalQty}${inventurDuplicateState.existingEntry.createdByName ? ` — par ${inventurDuplicateState.existingEntry.createdByName}` : ""}). Que veux-tu faire ?`)}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              <Pressable onClick={() => resolveInventurDuplicate("add")} style={{ width: "100%", textAlign: "left", padding: "13px 15px", borderRadius: 16, background: "rgba(30,184,95,.12)", fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: A.green }}>➕ {t("inventur.dupAddQtyBtn", "Additionner la quantité")}</Pressable>
              <Pressable onClick={() => resolveInventurDuplicate("edit")} style={{ width: "100%", textAlign: "left", padding: "13px 15px", borderRadius: 16, background: P.soft, border: `1.5px solid ${P.line}`, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>✏️ {t("inventur.dupEditExistingBtn", "Modifier la ligne existante")}</Pressable>
              <Pressable onClick={() => resolveInventurDuplicate("separate")} style={{ width: "100%", textAlign: "left", padding: "13px 15px", borderRadius: 16, background: P.soft, border: `1.5px solid ${P.line}`, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`}}>➗ {t("inventur.dupKeepSeparateBtn", "Garder comme ligne séparée")}</Pressable>
              <Pressable onClick={() => resolveInventurDuplicate("cancel")} style={{ width: "100%", textAlign: "center", padding: "13px 15px", borderRadius: 16, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, color: A.red }}>{t("common.cancel", "Annuler")}</Pressable>
            </div>
          </>
        )}
      </Sheet>

      <Sheet open={sheet === "inventurReopen"} onClose={() => { setSheet(null); setInventurReopenTarget(null); setInventurReopenReason(""); }} P={P} maxH="55%">
        <SheetHead kicker="Inventur" title={t("inventur.reopenShortBtn", "Rouvrir")} onClose={() => setSheet(null)} />
        {inventurReopenTarget && (
          <>
            <Field label={t("arbeitszeitliste.history.reasonLabel", "Motif (obligatoire)")}>
              <textarea rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder={t("inventur.reopenReasonPlaceholder", "Ex. : erreur détectée après validation…")} value={inventurReopenReason} onChange={(e) => setInventurReopenReason(e.target.value)} />
            </Field>
            <Pressable onClick={() => {
              if (!inventurReopenReason.trim()) { showToast(t("inventur.toast.needReason", "Indique un motif.")); return; }
              if (inventurReopenTarget.kind === "inventory") reopenInventurInventory();
              else setInventurCategoryStatus(inventurReopenTarget.catId, "in_bearbeitung", inventurReopenReason.trim());
              if (inventurReopenTarget.kind === "category") { setSheet(null); setInventurReopenTarget(null); setInventurReopenReason(""); }
            }} style={{ width: "100%", ...btnSolid(A.red) }}>{t("inventur.reopenShortBtn", "Rouvrir")}</Pressable>
          </>
        )}
      </Sheet>

      <Sheet open={sheet === "kontrolleDelivery"} onClose={() => { setSheet(null); setKontrolleDeliveryEditId(null); }} P={P} maxH="55%">
        <SheetHead kicker="Bestandskontrolle" title={kontrolleDeliveryEditId ? t("kontrolle.editDeliveryTitle", "Corriger la livraison") : t("kontrolle.addDeliveryBtn", "Ajouter une livraison")} onClose={() => { setSheet(null); setKontrolleDeliveryEditId(null); }} />
        <Field label={t("reporting.dateLabel", "Date")}>
          <input type="date" style={dateTimeInputStyle} value={kontrolleDeliveryForm.date} onChange={(e) => setKontrolleDeliveryForm({ ...kontrolleDeliveryForm, date: e.target.value })} />
        </Field>
        <Field label={t("controlRun.quantityLabel", "Quantité")}>
          <input style={inputStyle} inputMode="numeric" value={kontrolleDeliveryForm.qty} onChange={(e) => setKontrolleDeliveryForm({ ...kontrolleDeliveryForm, qty: e.target.value })} />
        </Field>
        <Field label={t("inventur.noteOptionalLabel", "Remarque (facultatif)")}>
          <input style={inputStyle} value={kontrolleDeliveryForm.note} onChange={(e) => setKontrolleDeliveryForm({ ...kontrolleDeliveryForm, note: e.target.value })} />
        </Field>
        <Pressable onClick={addKontrolleDelivery} style={{ width: "100%", ...btnSolid(A.blue) }}>{kontrolleDeliveryEditId ? t("common.save", "Enregistrer") : t("claims.add", "Ajouter")}</Pressable>
      </Sheet>

      {/* kontrolleNewCycle (Sheet) retirée (2026-08-09) : le formulaire "démarrer le
          Zyklus suivant" est maintenant inline dans la carte dépliée elle-même (voir
          kontrolle_list), avec le vrai champ Startdatum qui manquait ici. */}

      {/* Visibilité Bestandskontrolle par employé — réservé FL/FLA (retour utilisateur
          2026-08-09). Liste de BLOCAGE : coché = actuellement MASQUÉ pour cet employé
          (donc décoché par défaut = visible, comme avant ce chantier). */}
      <Sheet open={kontrolleVisibilitySheetOpen} onClose={() => setKontrolleVisibilitySheetOpen(false)} P={P} maxH="80%">
        <SheetHead kicker="Bestandskontrolle" title={t("kontrolle.visibility.sheetTitle", "Qui voit ce module ?")} onClose={() => setKontrolleVisibilitySheetOpen(false)} />
        <p style={{ margin: "0 0 16px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, lineHeight: 1.5 }}>
          {t("kontrolle.visibility.sheetHint", "Décoche un employé pour lui masquer la tuile Bestandskontrolle sur son compte. Ses droits (créer/modifier/clôturer) ne changent pas — c'est uniquement une question de visibilité.")}
        </p>
        {inventurTeamRoster.length === 0 && (
          <p style={{ fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted, textAlign: "center", padding: "20px 0" }}>{t("kontrolle.visibility.noTeam", "Aucun employé trouvé pour cette filiale.")}</p>
        )}
        {inventurTeamRoster.map((u) => {
          const hidden = (kontrolleVisibility?.hiddenEmployeeIds || []).includes(u.employeeId);
          return (
            <Pressable key={u.employeeId} onClick={() => toggleKontrolleVisibility(u.employeeId)} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
              padding: "12px 14px", borderRadius: 14, marginBottom: 8,
              background: hidden ? "rgba(239,68,68,.08)" : P.soft, border: `1.5px solid ${hidden ? A.red + "55" : P.line}`,
            }}>
              <div style={{ minWidth: 0 }}>
                <strong style={{ fontSize: `calc(13.5px * var(--nx-font-scale, 1))`, display: "block" }}>{u.name}</strong>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{u.role}</span>
              </div>
              <span style={{ fontSize: `calc(11px * var(--nx-font-scale, 1))`, fontWeight: 800, padding: "4px 10px", borderRadius: 99, flexShrink: 0, background: hidden ? "rgba(239,68,68,.14)" : "rgba(30,184,95,.13)", color: hidden ? A.red : A.green }}>
                {hidden ? `🚫 ${t("kontrolle.visibility.hiddenBadge", "Masqué")}` : `✓ ${t("kontrolle.visibility.visibleBadge", "Visible")}`}
              </span>
            </Pressable>
          );
        })}
      </Sheet>

      <Sheet open={!!alertActionId} onClose={() => setAlertActionId(null)} P={P} maxH="80%">
        {(() => {
          const f = (managerOverview?.feed || []).find((x) => alertKeyFor(x) === alertActionId);
          if (!f) return null;
          return (
            <>
              <SheetHead kicker={storeLabelFor(f.storeId)} title={f.title} onClose={() => setAlertActionId(null)} />
              {f.assignedToName && (
                <p style={{ margin: "0 0 12px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: A.blue, fontWeight: 700 }}>👤 {t("pilotage.alertes.assignedToTemplate", { name: f.assignedToName }, `Attribuée à ${f.assignedToName}`)}</p>
              )}
              {f.resolved && (
                <p style={{ margin: "0 0 12px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: A.green, fontWeight: 700 }}>{t("pilotage.alertAlreadyClosedText", "✓ Déjà clôturée")}</p>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                {f.assignedToName ? (
                  <Pressable onClick={() => assignAlert(f, false)} style={{
                    width: "100%", textAlign: "left", padding: "13px 15px", borderRadius: 16, background: P.soft, border: `1.5px solid ${P.line}`, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                  }}>🚫 {t("pilotage.alertes.unassignBtn", "Retirer l'attribution")}</Pressable>
                ) : (
                  <Pressable onClick={() => assignAlert(f, true)} style={{
                    width: "100%", textAlign: "left", padding: "13px 15px", borderRadius: 16, background: P.soft, border: `1.5px solid ${P.line}`, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                  }}>👤 {t("pilotage.alertes.assignToFLTemplate", { store: storeLabelFor(f.storeId) }, `Attribuer au Filialleiter de ${storeLabelFor(f.storeId)}`)}</Pressable>
                )}
                <Pressable onClick={() => alertToMission(f)} style={{ width: "100%", ...btnSolid(A.purple) }}>🎯 {t("pilotage.alertes.convertToMissionBtn", "Transformer en mission")}</Pressable>
                {f.resolved ? (
                  <Pressable onClick={() => resolveAlert(f, false)} style={{
                    width: "100%", textAlign: "left", padding: "13px 15px", borderRadius: 16, background: P.soft, border: `1.5px solid ${P.line}`, fontWeight: 700, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
                  }}>↩️ {t("pilotage.alertes.reopenBtn", "Rouvrir cette alerte")}</Pressable>
                ) : (
                  <Pressable onClick={() => resolveAlert(f, true)} style={{ width: "100%", ...btnSolid(A.green) }}>{t("pilotage.closeThisAlertBtn", "✓ Clôturer cette alerte")}</Pressable>
                )}
              </div>
            </>
          );
        })()}
      </Sheet>

      {activeBanner && (() => {
        const n = activeBanner;
        const onBannerPointerDown = (e) => { e.currentTarget.dataset.startY = e.clientY; e.currentTarget.dataset.dy = 0; try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {} };
        const onBannerPointerMove = (e) => {
          if (e.currentTarget.dataset.startY === undefined) return;
          const dy = Math.min(0, e.clientY - Number(e.currentTarget.dataset.startY));
          e.currentTarget.dataset.dy = dy;
          e.currentTarget.style.transform = `translateY(${dy}px)`;
        };
        const onBannerPointerUp = (e) => {
          const dy = Number(e.currentTarget.dataset.dy || 0);
          e.currentTarget.style.transform = "";
          if (dy < -24) dismissBanner();
        };
        return (
          <div
            className={bannerLeaving ? "nx-banner-out" : "nx-banner-in"}
            onPointerDown={onBannerPointerDown} onPointerMove={onBannerPointerMove}
            onPointerUp={onBannerPointerUp} onPointerCancel={onBannerPointerUp}
            onClick={openBanner}
            style={{
              position: "fixed", top: "calc(10px + env(safe-area-inset-top))", left: 10, right: 10, zIndex: 90,
              maxWidth: 500, margin: "0 auto", background: P.tabBg, backdropFilter: "blur(26px) saturate(1.7)", WebkitBackdropFilter: "blur(26px) saturate(1.7)",
              border: `1px solid ${P.tabBorder}`, borderRadius: 20, boxShadow: P.deepShadow,
              padding: 12, display: "flex", gap: 10, alignItems: "flex-start", touchAction: "none",
            }}
          >
            <span style={{
              flex: "0 0 auto", width: 36, height: 36, borderRadius: 11, color: "#fff",
              border: "1px solid rgba(255,255,255,.16)",
              display: "grid", placeItems: "center", fontSize: `calc(17px * var(--nx-font-scale, 1))`,
              ...premiumTileStyle(A.blue),
            }}>{NOTIF_ICON[n.type] || "🔔"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <strong style={{ fontSize: `calc(10px * var(--nx-font-scale, 1))`, textTransform: "uppercase", letterSpacing: ".05em", color: P.muted, flex: 1 }}>Nexora</strong>
                <span style={{ fontSize: `calc(10.5px * var(--nx-font-scale, 1))`, color: P.muted, fontWeight: 600 }}>{t("common.justNowShort", "à l'instant")}</span>
              </div>
              <strong style={{ fontSize: `calc(13px * var(--nx-font-scale, 1))`, display: "block", marginTop: 1 }}>{notifTitle(n)}</strong>
              <span style={{ fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>{notifMessage(n)}</span>
            </div>
          </div>
        );
      })()}

      {toast && (
        <div style={{
          position: "fixed", bottom: 126, left: "50%", transform: "translateX(-50%)", zIndex: 60,
          background: themeId === "sombre" ? "#26262a" : A.navy, color: "#fff", padding: "11px 19px",
          borderRadius: 99, fontSize: `calc(13px * var(--nx-font-scale, 1))`, fontWeight: 600, boxShadow: P.deepShadow,
          whiteSpace: "nowrap", maxWidth: "88vw", overflow: "hidden", textOverflow: "ellipsis",
          animation: "nxToastIn .32s cubic-bezier(.34,1.4,.64,1)",
        }}>{toast}</div>
      )}

      {/* Écran de verrouillage par inactivité (voir IDLE_LOCK_MS) — zIndex au-dessus de TOUT,
          y compris les lightbox (300/301) : un Sheet ou une photo pourrait rester ouvert
          derrière, mais rien n'est manipulable avant déverrouillage. Fond opaque plein (pas le
          noir translucide des lightbox photo, ce n'est pas une visionneuse) et langage visuel
          de la porte "Infos importantes" (carte centrée, emoji 🔒, bouton plein). */}
      {locked && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 99999, background: P.bg,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
        }}>
          <div style={{ width: "100%", maxWidth: 360 }}>
            <div style={{ textAlign: "center", marginBottom: 22 }}>
              <div style={{ fontSize: `calc(40px * var(--nx-font-scale, 1))`, marginBottom: 10 }}>🔒</div>
              <h1 style={{ margin: "0 0 4px", fontSize: `calc(19px * var(--nx-font-scale, 1))`, fontWeight: 800, letterSpacing: "-.02em" }}>{t("auth.lockScreen.title", "Session verrouillée")}</h1>
              <p style={{ margin: 0, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, color: P.muted }}>{t("auth.lockScreen.subtitle", "30 minutes sans activité — entre ton mot de passe pour continuer.")}</p>
            </div>
            {/* Déverrouillage Face ID/Touch ID — visible uniquement si CET appareil a déjà un
                enregistrement réussi POUR CE COMPTE précis (webauthnHint.login === user?.proId
                — jamais l'indice d'un autre compte laissé sur un appareil partagé, voir
                unlockWithFaceId). Fonctionne en ligne (vraie vérification serveur, identique à
                une connexion classique) ET hors-ligne (porte locale, voir le commentaire de
                unlockWithFaceId). */}
            {webauthnHint && webauthnAvailable && webauthnHint.login === user?.proId && (
              <Pressable onClick={unlockWithFaceId} style={{
                width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                padding: "13px 0", borderRadius: 99, fontWeight: 800, fontSize: `calc(14px * var(--nx-font-scale, 1))`, color: "#fff",
                background: `linear-gradient(135deg, ${A.navy}, #1c2b52)`,
                boxShadow: `0 16px 34px -12px rgba(13,23,48,.55), 0 4px 10px rgba(0,0,0,.15)`,
                marginBottom: 14, opacity: webauthnBusy ? .6 : 1,
              }}>
                <span style={{
                  width: 25, height: 25, borderRadius: "50%", flexShrink: 0, display: "grid", placeItems: "center",
                  background: "linear-gradient(135deg, #f3d585, #d4a72c)", fontSize: `calc(12px * var(--nx-font-scale, 1))`,
                }}>🔐</span>
                {webauthnBusy ? t("auth.webauthn.loginBusy", "Vérification...") : t("auth.lockScreen.faceIdBtn", "Déverrouiller avec Face ID")}
              </Pressable>
            )}
            <div style={{ ...card, borderRadius: 18, padding: 18 }}>
              <Field label={t("auth.passwordLabel", "Mot de passe")}>
                <PasswordInput style={inputStyle} P={P} t={t} value={unlockPassword} placeholder="••••••••" autoFocus
                  onChange={(e) => { setUnlockPassword(e.target.value); setUnlockError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") submitUnlock(); }} />
              </Field>
              {unlockError && <p style={{ margin: "0 0 11px", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: A.red }}>{unlockError}</p>}
              <Pressable onClick={submitUnlock} style={{ width: "100%", ...btnSolid(A.blue), padding: "15px 0", fontSize: `calc(15px * var(--nx-font-scale, 1))`, opacity: unlockBusy ? .6 : 1 }}>
                {unlockBusy ? t("auth.lockScreen.checking", "Vérification...") : t("auth.lockScreen.unlockBtn", "Déverrouiller")}
              </Pressable>
            </div>
            <Pressable onClick={logout} style={{ display: "block", margin: "16px auto 0", fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 700, color: P.muted, textAlign: "center" }}>
              {t("auth.lockScreen.notYouLogout", "Pas toi ? Se déconnecter")}
            </Pressable>
          </div>
        </div>
      )}

      {/* Lightbox photo missions réseau — vrai visualiseur in-app, jamais window.open
          (retour utilisateur explicite : photo de preuve pas consultable — window.open
          échoue silencieusement en PWA installée, même famille de bug déjà corrigée sur
          l'export MHD). zIndex au-dessus de toute Sheet (40/50) pour rester utilisable
          quand ouvert depuis missionDetail/myMissionDetail. */}
      {missionPhotoLightbox && (
        <div onClick={() => setMissionPhotoLightbox("")} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,.92)", zIndex: 300,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
        }}>
          <Pressable onClick={() => setMissionPhotoLightbox("")} aria-label={t("missions.closePhotoAria", "Fermer la photo")} style={{
            position: "fixed", top: "calc(16px + env(safe-area-inset-top))", right: 16, width: 42, height: 42,
            borderRadius: 99, background: "rgba(255,255,255,.15)", color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(18px * var(--nx-font-scale, 1))`, zIndex: 301,
          }}>✕</Pressable>
          <img src={missionPhotoLightbox} alt="Photo en grand" onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 14, objectFit: "contain" }} />
        </div>
      )}

      {/* Visionneuse Beleg Inventurmaßnahme — image OU PDF, voir openImBeleg/imBelegViewer. */}
      {imBelegViewer && (
        <div onClick={() => setImBelegViewer(null)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,.92)", zIndex: 300,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, gap: 14,
        }}>
          <Pressable onClick={() => setImBelegViewer(null)} aria-label={t("common.close", "Fermer")} style={{
            position: "fixed", top: "calc(16px + env(safe-area-inset-top))", right: 16, width: 42, height: 42,
            borderRadius: 99, background: "rgba(255,255,255,.15)", color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(18px * var(--nx-font-scale, 1))`, zIndex: 301,
          }}>✕</Pressable>
          {imBelegViewer.mimeType.startsWith("image/") ? (
            <img src={imBelegViewer.url} alt="" onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "100%", maxHeight: "80%", borderRadius: 14, objectFit: "contain" }} />
          ) : (
            <iframe src={imBelegViewer.url} title="Beleg" onClick={(e) => e.stopPropagation()}
              style={{ width: "100%", maxWidth: 720, height: "72%", border: "none", borderRadius: 14, background: "#fff" }} />
          )}
          <a href={imBelegViewer.url} download onClick={(e) => e.stopPropagation()} style={{
            textAlign: "center", padding: "12px 22px", borderRadius: 99, fontWeight: 800, fontSize: `calc(13.5px * var(--nx-font-scale, 1))`,
            background: "#fff", color: "#111", textDecoration: "none",
          }}>{t("inventurmassnahme.belegDownloadBtn", "⬇ Télécharger le fichier")}</a>
        </div>
      )}

      {/* Visualiseur photo Artikelfinder — même patron que le lightbox missions, mais avec
          navigation ‹ › entre plusieurs photos (retour utilisateur explicite : "je veux
          pouvoir agrandir les photos d'un article ajouter et les consulter toutes si
          plusieurs"). Utilisé aussi bien pour les photos déjà attachées (détail) que pour
          les aperçus en cours de sélection (création). */}
      {finderPhotoViewer && (
        <div onClick={() => setFinderPhotoViewer(null)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,.92)", zIndex: 300,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
        }}>
          <Pressable onClick={() => setFinderPhotoViewer(null)} aria-label={t("finder.photoViewer.closeAria", "Fermer la photo")} style={{
            position: "fixed", top: "calc(16px + env(safe-area-inset-top))", right: 16, width: 42, height: 42,
            borderRadius: 99, background: "rgba(255,255,255,.15)", color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(18px * var(--nx-font-scale, 1))`, zIndex: 301,
          }}>✕</Pressable>
          {finderPhotoViewer.urls.length > 1 && (
            <span style={{
              position: "fixed", top: "calc(16px + env(safe-area-inset-top))", left: 16, fontSize: `calc(12.5px * var(--nx-font-scale, 1))`, fontWeight: 800, color: "#fff",
              background: "rgba(255,255,255,.15)", borderRadius: 99, padding: "9px 14px", zIndex: 301,
            }}>{finderPhotoViewer.index + 1} / {finderPhotoViewer.urls.length}</span>
          )}
          {finderPhotoViewer.urls.length > 1 && (
            <>
              <Pressable feedback onClick={(e) => { e.stopPropagation(); setFinderPhotoViewer((v) => ({ ...v, index: (v.index - 1 + v.urls.length) % v.urls.length })); }} aria-label={t("finder.photoViewer.prevAria", "Photo précédente")} style={{
                position: "fixed", left: 10, top: "50%", transform: "translateY(-50%)", width: 46, height: 46,
                borderRadius: 99, background: "rgba(255,255,255,.15)", color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(22px * var(--nx-font-scale, 1))`, zIndex: 301,
              }}>‹</Pressable>
              <Pressable feedback onClick={(e) => { e.stopPropagation(); setFinderPhotoViewer((v) => ({ ...v, index: (v.index + 1) % v.urls.length })); }} aria-label={t("finder.photoViewer.nextAria", "Photo suivante")} style={{
                position: "fixed", right: 10, top: "50%", transform: "translateY(-50%)", width: 46, height: 46,
                borderRadius: 99, background: "rgba(255,255,255,.15)", color: "#fff", display: "grid", placeItems: "center", fontSize: `calc(22px * var(--nx-font-scale, 1))`, zIndex: 301,
              }}>›</Pressable>
            </>
          )}
          <img src={finderPhotoViewer.urls[finderPhotoViewer.index]} alt="" onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 14, objectFit: "contain" }} />
        </div>
      )}

      {/* Révélation du code de réinitialisation — le serveur ne le renvoie qu'UNE fois
          (jamais journalisé, §14) : cet écran est la seule occasion de le voir, jamais
          récupérable après fermeture. Zéro auto-fermeture/toast qui l'effacerait par
          erreur — uniquement une fermeture explicite. */}
      {resetCodeReveal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,.75)", zIndex: 300,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
        }}>
          <div style={{ ...card, flexDirection: "column", alignItems: "stretch", padding: 24, maxWidth: 340, width: "100%", borderRadius: 22 }}>
            <span style={{ fontSize: `calc(30px * var(--nx-font-scale, 1))`, textAlign: "center", marginBottom: 8 }}>🔑</span>
            <strong style={{ fontSize: `calc(15.5px * var(--nx-font-scale, 1))`, textAlign: "center", display: "block", marginBottom: 4 }}>
              {t("admin.resetCode.title", { name: resetCodeReveal.request.professionalId }, `Code pour ${resetCodeReveal.request.professionalId}`)}
            </strong>
            <p style={{ margin: "0 0 16px", fontSize: `calc(12px * var(--nx-font-scale, 1))`, color: P.muted, textAlign: "center", lineHeight: 1.5 }}>
              {t("admin.resetCode.instructions", "Communique ce code de vive voix, en personne — jamais par écrit. Il n'est affiché qu'une seule fois.")}
            </p>
            <div style={{
              textAlign: "center", padding: "18px 0", borderRadius: 16, background: P.soft,
              fontSize: `calc(34px * var(--nx-font-scale, 1))`, fontWeight: 900, letterSpacing: ".12em", marginBottom: 10, fontVariantNumeric: "tabular-nums",
            }}>{resetCodeReveal.code}</div>
            <p style={{ margin: "0 0 18px", fontSize: `calc(11.5px * var(--nx-font-scale, 1))`, color: A.orange, fontWeight: 700, textAlign: "center" }}>
              {t("admin.resetCode.expiry", { n: resetCodeReveal.expiresInMinutes }, `Expire dans ${resetCodeReveal.expiresInMinutes} minutes`)}
            </p>
            <Pressable onClick={() => setResetCodeReveal(null)} style={{ width: "100%", ...btnSolid(A.blue) }}>{t("common.close", "Fermer")}</Pressable>
          </div>
        </div>
      )}

      {/* Capture réelle du code-barres (Scanner + Contrôle Kühlung + Livraison + DLC
          promo) — un seul input partagé, doScan(ctx) fixe le contexte avant de
          l'ouvrir. capture="environment" ouvre directement l'appareil photo arrière
          sur mobile, jamais un sélecteur de galerie. */}
      <input ref={scanFileInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handleScanFileChange} />
      {/* Photo réelle de l'article promotionnel (Magasin · Saisie libre), envoyée telle
          quelle à POST /api/store-photos — même patron que l'input scan ci-dessus. */}
      <input ref={storePhotoInputRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handleStorePhotoChange} />
      {/* Import réel du fichier Stundenleistung NORMA (photo ou PDF) — lecture IA vision
          côté serveur, voir handleStundenFileChange. */}
      <input ref={stundenFileInputRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={handleStundenFileChange} />
      {/* Beleg (photo/PDF) obligatoire des deux formulaires Inventurmaßnahme — même patron
          que stundenFileInputRef ci-dessus, voir imBelegField/uploadImBeleg. */}
      <input ref={lsBelegInputRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={(e) => uploadImBeleg(e.target.files?.[0], setLsForm)} />
      <input ref={bwBelegInputRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={(e) => uploadImBeleg(e.target.files?.[0], setBwForm)} />
    </div>
  );
}
